import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Camera, BarChart3, CheckSquare, FileText, Shield, Settings, Menu, X, ChevronRight, ChevronDown, Trash2, AlertTriangle, Search, Activity, Users, Clock, RefreshCw, Plus, Pencil, Trophy, AlertCircle, Bell, MessageSquare, Send, ArrowLeft, CheckCircle2, Bug, CalendarClock, ShieldAlert, Rocket, GitBranch, ListChecks, ArrowUpRight, TrendingUp } from 'lucide-react';
import { ReactFlow, Background, Controls, Handle, Position, useNodesState, applyNodeChanges } from '@xyflow/react';
import DOMPurify from 'dompurify';

// Single real sanitizer for every WYSIWYG-sourced HTML string rendered via
// dangerouslySetInnerHTML anywhere in the app. Replaces several duplicated
// hand-rolled blacklists (which only stripped <script>, on*= attributes, and
// javascript: URLs — not <iframe>/<object>/<embed>/<form>/srcdoc etc.) with an
// allowlist-based sanitizer so unknown-in-advance dangerous markup is dropped
// by default instead of needing to be enumerated.
const sanitizeHtml = (html) => (html ? DOMPurify.sanitize(html) : '');

// ============================================================================
// ROLE-BASED ACCESS CONTROL
// ============================================================================

// Pages always accessible regardless of role (bypass module restriction)
const ALWAYS_ALLOWED_PAGES = ['dashboard', 'modules', 'messages', 'orgchart', 'fastdtr', 'projectdashboard'];

// Admin-only nav items hidden from non-admin users
const ADMIN_ONLY_NAV = ['tasks', 'reports', 'audit', 'settings'];

// Returns the list of accessible module IDs for the user, or null (= unrestricted/admin)
const getAllowedModules = (user) => {
  const role = user?.role || user?.user_role || '';
  if (role === 'admin') return null; // admin always unrestricted
  const perms = user?.permissions;
  if (perms && Object.keys(perms).length > 0) return Object.keys(perms);
  return [];
};

// Returns the permission level for a specific module: 'view' | 'edit' | 'full' | null
const getUserPermission = (user, moduleId) => {
  const role = user?.role || user?.user_role || '';
  if (role === 'admin') return 'full';
  return user?.permissions?.[moduleId] || null;
};

const canAccessPage = (user, page) => {
  if (ALWAYS_ALLOWED_PAGES.includes(page)) return true;
  const role = user?.role || user?.user_role || '';
  if (role === 'admin') return true;
  const isManager = role === 'manager';
  const isDataOpsManager = role === 'dataops_manager';
  const isDevOpsManager = role === 'devops_manager';
  if ((page === 'tasks' || page === 'reports') && (isManager || isDataOpsManager || isDevOpsManager)) return true;
  if (page === 'audit' && isDevOpsManager) return true;
  const allowed = getAllowedModules(user);
  return !allowed || allowed.includes(page);
};

// ============================================================================
// MODELS (Data Layer)
// ============================================================================

const API_BASE = '/api'; // Cloudflare Workers endpoint

class BaseModel {
  static authHeaders() {
    const token = localStorage.getItem('orbit_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  // Read the response body even on failures so the caller sees the actual server
  // error ("projectId is required" etc.) instead of a generic "API request failed".
  static async _readError(response) {
    let payload = null;
    try { payload = await response.json(); } catch {}
    const msg = payload?.error || payload?.message || `Request failed (${response.status})`;
    const err = new Error(msg);
    err.status = response.status;
    err.payload = payload;
    return err;
  }

  static async fetch(endpoint) {
    const response = await fetch(`${API_BASE}${endpoint}`, {
      headers: { ...this.authHeaders() }
    });
    if (!response.ok) throw await this._readError(response);
    return response.json();
  }

  static async post(endpoint, data) {
    const response = await fetch(`${API_BASE}${endpoint}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...this.authHeaders() },
      body: JSON.stringify(data)
    });
    if (!response.ok) throw await this._readError(response);
    return response.json();
  }
}

class DashboardModel extends BaseModel {
  static async getStats() {
    try { return await this.fetch('/dashboard'); } catch { return { stats: [], activities: [], quickModules: [] }; }
  }
  static async getBurnCharts() {
    try { return await this.fetch('/dashboard/burncharts'); }
    catch { return null; }
  }
  static async getBurnChartRemainingTasks(module) {
    try { return await this.fetch(`/dashboard/burncharts/${module}/remaining`); }
    catch { return { items: [] }; }
  }
  static async getEnhancements() {
    try { return await this.fetch('/dashboard/enhancements'); }
    catch { return null; }
  }
}

class ModulesModel extends BaseModel {
  static async getModules() {
    try { return await this.fetch('/modules'); }
    catch { return []; }
  }

  static async getPlanTrackData() {
    try { return await this.fetch('/plantrack'); }
    catch { return { stats: { total: 0, active: 0, completed: 0, onHold: 0, planning: 0 }, projects: [], milestones: [], sprints: [], sprint: null }; }
  }

  static async getProjectDashboard() {
    try { return await this.fetch('/projects/dashboard'); }
    catch { return { statusDistribution: [], projects: [], totals: {} }; }
  }

  // ---- Marketing Workflow ----
  static async getMarketingWorkflow(filters = {}) {
    try {
      const qs = new URLSearchParams();
      const keys = ['search', 'requestedBy', 'createdBy', 'createdFrom', 'createdTo', 'status', 'vendor', 'tag', 'sort'];
      for (const k of keys) {
        if (filters[k]) qs.set(k, filters[k]);
      }
      const url = `/marketing-workflow${qs.toString() ? '?' + qs.toString() : ''}`;
      return await this.fetch(url);
    } catch { return { tasks: [], requesters: [], creators: [], vendors: [], qaStatuses: [], buildStatuses: [], deploymentStatuses: [], overallStatuses: [], tags: [], tagCounts: {} }; }
  }
  static async getMarketingTaskDetail(id) {
    try { return await this.fetch(`/marketing-workflow/tasks/${id}`); }
    catch { return null; }
  }
  static async createMarketingTask(data) {
    try { return await this.post('/marketing-workflow/tasks', data); }
    catch (e) { return { success: false, error: e.message }; }
  }
  static async updateMarketingTask(id, data) {
    const r = await fetch(`${API_BASE}/marketing-workflow/tasks/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json', ...this.authHeaders() }, body: JSON.stringify(data) });
    const payload = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(payload?.error || `Failed (${r.status})`);
    return payload;
  }
  static async deleteMarketingTask(id) {
    const r = await fetch(`${API_BASE}/marketing-workflow/tasks/${id}`, { method: 'DELETE', headers: this.authHeaders() });
    return r.json();
  }
  static async addMarketingRow(taskId, data) {
    try { return await this.post(`/marketing-workflow/tasks/${taskId}/rows`, data); }
    catch (e) { return { success: false, error: e.message }; }
  }
  static async updateMarketingRow(rowId, data) {
    const r = await fetch(`${API_BASE}/marketing-workflow/rows/${rowId}`, { method: 'PUT', headers: { 'Content-Type': 'application/json', ...this.authHeaders() }, body: JSON.stringify(data) });
    const payload = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(payload?.error || `Failed (${r.status})`);
    return payload;
  }
  static async deleteMarketingRow(rowId) {
    const r = await fetch(`${API_BASE}/marketing-workflow/rows/${rowId}`, { method: 'DELETE', headers: this.authHeaders() });
    return r.json();
  }
  static async duplicateMarketingRow(rowId) {
    try { return await this.post(`/marketing-workflow/rows/${rowId}/duplicate`, {}); }
    catch (e) { return { success: false, error: e.message }; }
  }

  // ---- Workflow Troubleshooting Logs ----
  static async getTroubleshootingLogs(filters = {}) {
    try {
      const qs = new URLSearchParams();
      if (filters.search) qs.set('search', filters.search);
      if (filters.createdBy) qs.set('createdBy', filters.createdBy);
      if (filters.status) qs.set('status', filters.status);
      if (filters.severity) qs.set('severity', filters.severity);
      if (filters.environment) qs.set('environment', filters.environment);
      if (filters.category) qs.set('category', filters.category);
      const url = `/troubleshooting-logs${qs.toString() ? '?' + qs.toString() : ''}`;
      return await this.fetch(url);
    } catch { return { logs: [], creators: [] }; }
  }
  static async getTroubleshootingLogDetail(id) {
    try { return await this.fetch(`/troubleshooting-logs/${id}`); }
    catch { return null; }
  }
  static async createTroubleshootingLog(data) {
    try { return await this.post('/troubleshooting-logs', data); }
    catch (e) { return { success: false, error: e.message }; }
  }
  static async updateTroubleshootingLog(id, data) {
    const r = await fetch(`${API_BASE}/troubleshooting-logs/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json', ...this.authHeaders() }, body: JSON.stringify(data) });
    const payload = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(payload?.error || `Failed (${r.status})`);
    return payload;
  }
  static async deleteTroubleshootingLog(id) {
    const r = await fetch(`${API_BASE}/troubleshooting-logs/${id}`, { method: 'DELETE', headers: this.authHeaders() });
    return r.json();
  }
  static async addTroubleshootingLogImage(logId, data) {
    try { return await this.post(`/troubleshooting-logs/${logId}/images`, data); }
    catch (e) { return { success: false, error: e.message }; }
  }
  static async deleteTroubleshootingLogImage(imageId) {
    const r = await fetch(`${API_BASE}/troubleshooting-logs/images/${imageId}`, { method: 'DELETE', headers: this.authHeaders() });
    return r.json();
  }
  static async verifyTroubleshootingLog(id) {
    try { return await this.post(`/troubleshooting-logs/${id}/verify`, {}); }
    catch (e) { return { success: false, error: e.message }; }
  }

  // ---- Deployments ----
  static async getDeployments(filters = {}) {
    try {
      const qs = new URLSearchParams();
      for (const k of ['search', 'status', 'application', 'requestedBy']) if (filters[k]) qs.set(k, filters[k]);
      const url = `/deployments${qs.toString() ? '?' + qs.toString() : ''}`;
      return await this.fetch(url);
    } catch { return { deployments: [], requesters: [], statuses: [], applications: [], stats: {} }; }
  }
  static async getDeploymentLookups() {
    try { return await this.fetch('/deployments/lookups'); }
    catch { return { crs: [], testPlans: [] }; }
  }
  static async getDeploymentDetail(id) {
    try { return await this.fetch(`/deployments/${id}`); }
    catch { return null; }
  }
  static async createDeployment(data) {
    try { return await this.post('/deployments', data); }
    catch (e) { return { success: false, error: e.message }; }
  }
  static async updateDeployment(id, data) {
    const r = await fetch(`${API_BASE}/deployments/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json', ...this.authHeaders() }, body: JSON.stringify(data) });
    const p = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(p?.error || `Failed (${r.status})`);
    return p;
  }
  static async deleteDeployment(id) {
    const r = await fetch(`${API_BASE}/deployments/${id}`, { method: 'DELETE', headers: this.authHeaders() });
    return r.json();
  }
  static async submitDeployment(id) { try { return await this.post(`/deployments/${id}/submit`, {}); } catch (e) { return { success: false, error: e.message }; } }
  static async reviewDeployment(id, decision, notes) { try { return await this.post(`/deployments/${id}/review`, { decision, notes }); } catch (e) { return { success: false, error: e.message }; } }
  static async markDeploymentDeployed(id, notes) { try { return await this.post(`/deployments/${id}/deployed`, { notes }); } catch (e) { return { success: false, error: e.message }; } }
  static async markDeploymentVerified(id, notes) { try { return await this.post(`/deployments/${id}/verify`, { notes }); } catch (e) { return { success: false, error: e.message }; } }
  static async completeDeployment(id, notes) { try { return await this.post(`/deployments/${id}/complete`, { notes }); } catch (e) { return { success: false, error: e.message }; } }
  static async cancelDeployment(id) { try { return await this.post(`/deployments/${id}/cancel`, {}); } catch (e) { return { success: false, error: e.message }; } }
  // v2 staged-workflow methods
  static async completeDeploymentStage(deploymentId, stageId, notes, validationOutcome) {
    try { return await this.post(`/deployments/${deploymentId}/stages/${stageId}/complete`, { notes: notes || '', validationOutcome }); }
    catch (e) { return { success: false, error: e.message }; }
  }
  static async rejectDeploymentStage(deploymentId, stageId, notes) {
    try { return await this.post(`/deployments/${deploymentId}/stages/${stageId}/reject`, { notes: notes || '' }); }
    catch (e) { return { success: false, error: e.message }; }
  }
  static async returnDeploymentStage(deploymentId, stageId, targetStageKey, reason) {
    try { return await this.post(`/deployments/${deploymentId}/stages/${stageId}/return`, { targetStageKey, reason }); }
    catch (e) { return { success: false, error: e.message }; }
  }
  static async requestDeploymentStageInfo(deploymentId, stageId, question) {
    try { return await this.post(`/deployments/${deploymentId}/stages/${stageId}/request-info`, { question }); }
    catch (e) { return { success: false, error: e.message }; }
  }
  static async provideDeploymentInfo(deploymentId, response) {
    try { return await this.post(`/deployments/${deploymentId}/provide-info`, { response }); }
    catch (e) { return { success: false, error: e.message }; }
  }
  static async toggleDeploymentChecklist(deploymentId, itemId, checked) {
    const r = await fetch(`${API_BASE}/deployments/${deploymentId}/checklist/${itemId}`, { method: 'PUT', headers: { 'Content-Type': 'application/json', ...this.authHeaders() }, body: JSON.stringify({ checked }) });
    const p = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(p?.error || `Failed (${r.status})`);
    return p;
  }
  static async addDeploymentAttachment(deploymentId, payload) {
    try { return await this.post(`/deployments/${deploymentId}/attachments`, payload); }
    catch (e) { return { success: false, error: e.message }; }
  }
  static async deleteDeploymentAttachment(attId) {
    const r = await fetch(`${API_BASE}/deployments/attachments/${attId}`, { method: 'DELETE', headers: this.authHeaders() });
    return r.json();
  }
  static async downloadDeploymentAttachment(attId) {
    try { return await this.fetch(`/deployments/attachments/${attId}/download`); }
    catch (e) { return { error: e?.message || 'Download failed' }; }
  }
  // v1.8 — risk scoring + MFA re-auth for high-risk final approvals
  static async verifyDeploymentMfa(deploymentId, password) {
    try { return await this.post(`/deployments/${deploymentId}/verify-mfa`, { password }); }
    catch (e) { return { success: false, error: e?.message || 'MFA verification failed' }; }
  }
  static async refreshDeploymentRisk(deploymentId) {
    try { return await this.fetch(`/deployments/${deploymentId}/risk`); }
    catch { return null; }
  }
  // Override completeDeploymentStage to accept an optional MFA nonce for high-risk approvals
  static async completeDeploymentStageWithMfa(deploymentId, stageId, notes, mfaNonce) {
    try { return await this.post(`/deployments/${deploymentId}/stages/${stageId}/complete`, { notes, mfaNonce }); }
    catch (e) { return { success: false, error: e?.message || 'Failed' }; }
  }
  // APM — SOC 2 reporting pipeline
  static async listSoc2Reports() {
    try { return await this.fetch('/apm/soc2/reports'); } catch { return { reports: [] }; }
  }
  static async createSoc2Report(data) {
    try { return await this.post('/apm/soc2/reports', data); } catch (e) { return { success: false, error: e?.message }; }
  }
  static async getSoc2Report(id) {
    try { return await this.fetch(`/apm/soc2/reports/${id}`); } catch { return null; }
  }
  static async updateSoc2Report(id, data) {
    const r = await fetch(`${API_BASE}/apm/soc2/reports/${id}`, {
      method: 'PUT', headers: { ...this.authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return r.json();
  }
  static async ingestSoc2AuditData(reportId) {
    try { return await this.post(`/apm/soc2/reports/${reportId}/ingest`, {}); } catch (e) { return { success: false, error: e?.message }; }
  }
  static async generateSoc2Narrative(reportId, controlId) {
    try { return await this.post(`/apm/soc2/reports/${reportId}/controls/${controlId}/generate`, {}); } catch (e) { return { success: false, error: e?.message }; }
  }
  static async listSoc2Evidence(reportId, controlId) {
    try { return await this.fetch(`/apm/soc2/reports/${reportId}/controls/${controlId}/evidence`); } catch { return { evidence: [] }; }
  }
  static async addSoc2Evidence(reportId, controlId, data) {
    try { return await this.post(`/apm/soc2/reports/${reportId}/controls/${controlId}/evidence`, data); } catch (e) { return { success: false, error: e?.message }; }
  }
  static async updateSoc2Narrative(narrativeId, data) {
    const r = await fetch(`${API_BASE}/apm/soc2/narratives/${narrativeId}`, {
      method: 'PUT', headers: { ...this.authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return r.json();
  }
  // DataFeed — read-only public API + admin key management
  static async getDataFeedSchema() {
    try { return await this.fetch('/datafeed/schema'); } catch { return { entities: [] }; }
  }
  static async listDataFeedKeys() {
    try { return await this.fetch('/datafeed/keys'); } catch { return { keys: [] }; }
  }
  // Reveal returns the stored key; the backend audit-logs every call.
  static async revealDataFeedKey(id) {
    try { return await this.post(`/datafeed/keys/${id}/reveal`, {}); } catch (e) { return { success: false, error: e?.message }; }
  }
  static async rotateDataFeedKey(id) {
    try { return await this.post(`/datafeed/keys/${id}/rotate`, {}); } catch (e) { return { success: false, error: e?.message }; }
  }
  static async createDataFeedKey(data) {
    try { return await this.post('/datafeed/keys', data); } catch (e) { return { success: false, error: e?.message }; }
  }
  static async updateDataFeedKey(id, data) {
    const r = await fetch(`${API_BASE}/datafeed/keys/${id}`, {
      method: 'PUT', headers: { ...this.authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return r.json();
  }
  static async deleteDataFeedKey(id) {
    const r = await fetch(`${API_BASE}/datafeed/keys/${id}`, {
      method: 'DELETE', headers: this.authHeaders(),
    });
    return r.json();
  }
  static async getDataFeedAccessLog(limit = 100) {
    try { return await this.fetch(`/datafeed/access-log?limit=${limit}`); } catch { return { log: [] }; }
  }
  static async getDeploymentKpis(rangeDays = 90) {
    try { return await this.fetch(`/deployments/kpis?rangeDays=${rangeDays}`); }
    catch { return null; }
  }
  // v1.6 freeze windows + conflict detection
  static async listFreezeWindows() {
    try { return await this.fetch('/deployments/freeze-windows'); }
    catch { return { windows: [] }; }
  }
  static async createFreezeWindow(data) {
    const r = await fetch(`${API_BASE}/deployments/freeze-windows`, {
      method: 'POST', headers: { ...this.authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return r.json();
  }
  static async updateFreezeWindow(id, data) {
    const r = await fetch(`${API_BASE}/deployments/freeze-windows/${id}`, {
      method: 'PUT', headers: { ...this.authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return r.json();
  }
  static async deleteFreezeWindow(id) {
    const r = await fetch(`${API_BASE}/deployments/freeze-windows/${id}`, {
      method: 'DELETE', headers: this.authHeaders(),
    });
    return r.json();
  }
  static async checkDeploymentConflicts({ plannedDate, application, changeType, excludeId }) {
    const params = new URLSearchParams();
    if (plannedDate)  params.set('plannedDate', plannedDate);
    if (application)  params.set('application', application);
    if (changeType)   params.set('changeType', changeType);
    if (excludeId)    params.set('excludeId', String(excludeId));
    try { return await this.fetch(`/deployments/check-conflicts?${params.toString()}`); }
    catch { return { freezeWindows: [], conflictingDeployments: [] }; }
  }
  // v1.4 — stage template admin endpoints
  static async listStageTemplates() {
    try { return await this.fetch('/deployments/stage-templates'); }
    catch { return { templates: [] }; }
  }
  static async updateStageTemplate(stageKey, data) {
    const r = await fetch(`${API_BASE}/deployments/stage-templates/${stageKey}`, {
      method: 'PUT', headers: { ...this.authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return r.json();
  }
  static async addStageChecklistItem(stageKey, data) {
    const r = await fetch(`${API_BASE}/deployments/stage-templates/${stageKey}/checklist`, {
      method: 'POST', headers: { ...this.authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return r.json();
  }
  static async updateStageChecklistItem(itemId, data) {
    const r = await fetch(`${API_BASE}/deployments/stage-templates/checklist/${itemId}`, {
      method: 'PUT', headers: { ...this.authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return r.json();
  }
  static async deleteStageChecklistItem(itemId) {
    const r = await fetch(`${API_BASE}/deployments/stage-templates/checklist/${itemId}`, {
      method: 'DELETE', headers: this.authHeaders(),
    });
    return r.json();
  }
  static async resetStageTemplate(stageKey) {
    const r = await fetch(`${API_BASE}/deployments/stage-templates/reset`, {
      method: 'POST', headers: { ...this.authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(stageKey ? { stageKey } : {}),
    });
    return r.json();
  }
  // TCMS test case links on a deployment — mirrors the CRF helpers
  static async addDeploymentTestLinks(id, testCaseIds) {
    try { return await this.post(`/deployments/${id}/test-links`, { testCaseIds }); }
    catch (e) { return { success: false, error: e.message }; }
  }
  static async removeDeploymentTestLink(linkId) {
    try {
      const r = await fetch(`${API_BASE}/deployments/test-links/${linkId}`, { method: 'DELETE', headers: this.authHeaders() });
      return r.json();
    } catch (e) { return { success: false, error: e.message }; }
  }
  // QMS requirement links on a deployment — mirrors the TCMS test-link helpers above
  static async addDeploymentRequirementLinks(id, requirementIds) {
    try { return await this.post(`/deployments/${id}/requirement-links`, { requirementIds }); }
    catch (e) { return { success: false, error: e.message }; }
  }
  static async removeDeploymentRequirementLink(linkId) {
    try {
      const r = await fetch(`${API_BASE}/deployments/requirement-links/${linkId}`, { method: 'DELETE', headers: this.authHeaders() });
      return r.json();
    } catch (e) { return { success: false, error: e.message }; }
  }
  static async getRequirementsForLinking() {
    try { return await this.fetch('/requirements-for-linking'); }
    catch { return { requirements: [], projects: [] }; }
  }
  // CRF links on a deployment — supports an original plus any number of addenda.
  static async getDeploymentCrLinks(id) {
    try { return await this.fetch(`/deployments/${id}/cr-links`); } catch { return { crLinks: [] }; }
  }
  static async addDeploymentCrLinks(id, crLinks) {
    try { return await this.post(`/deployments/${id}/cr-links`, { crLinks }); }
    catch (e) { return { success: false, error: e.message }; }
  }
  static async setDeploymentCrLinkType(linkId, linkType) {
    try {
      const r = await fetch(`${API_BASE}/deployments/cr-links/${linkId}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json', ...this.authHeaders() },
        body: JSON.stringify({ linkType }),
      });
      return r.json();
    } catch (e) { return { success: false, error: e.message }; }
  }
  static async removeDeploymentCrLink(linkId) {
    try {
      const r = await fetch(`${API_BASE}/deployments/cr-links/${linkId}`, { method: 'DELETE', headers: this.authHeaders() });
      return r.json();
    } catch (e) { return { success: false, error: e.message }; }
  }
  // Deployment approval chains — mirrors the CRF approval-chain settings API.
  static async getDeploymentApprovalChains(role = '') {
    try { return await this.fetch(`/deployments/approval-chains${role ? `?role=${encodeURIComponent(role)}` : ''}`); }
    catch { return null; }
  }
  static async saveDeploymentApprovalChains(approvalChains) {
    try {
      const r = await fetch(`${API_BASE}/deployments/approval-chains`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json', ...this.authHeaders() },
        body: JSON.stringify({ approvalChains }),
      });
      return r.json();
    } catch (e) { return { success: false, error: e?.message }; }
  }
  static async getDeploymentVolume() {
    try { return await this.fetch('/deployments/volume'); } catch { return null; }
  }

  static async getAppSettings() {
    try { return await this.fetch('/settings'); }
    catch { return {}; }
  }

  static async saveAppSettings(data) {
    try {
      const response = await fetch(`${API_BASE}/settings`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json', ...this.authHeaders() },
        body: JSON.stringify(data)
      });
      return response.json();
    } catch (e) { return { success: false, error: e.message }; }
  }

  static async getWorkflows() {
    try { return await this.fetch('/workflows'); } catch { return { workflows: [] }; }
  }
  static async getWorkflow(id) {
    try { return await this.fetch(`/workflows/${id}`); } catch { return null; }
  }
  static async createWorkflow(data) {
    try { return await this.post('/workflows', data); } catch (e) { return { success: false, error: e.message }; }
  }
  static async updateWorkflow(id, data) {
    const r = await fetch(`${API_BASE}/workflows/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json', ...this.authHeaders() }, body: JSON.stringify(data) });
    const p = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(p?.error || `Failed (${r.status})`);
    return p;
  }
  static async deleteWorkflow(id) {
    const r = await fetch(`${API_BASE}/workflows/${id}`, { method: 'DELETE', headers: this.authHeaders() });
    return r.json();
  }
  static async runWorkflow(id, triggerData) {
    try { return await this.post(`/workflows/${id}/run`, { triggerData: triggerData || {} }); } catch (e) { return { success: false, error: e.message }; }
  }
  static async regenerateWorkflowHook(id) {
    try { return await this.post(`/workflows/${id}/regenerate-hook`, {}); } catch (e) { return { success: false, error: e.message }; }
  }
  static async getWorkflowRuns(id) {
    try { return await this.fetch(`/workflows/${id}/runs`); } catch { return { runs: [] }; }
  }
  static async getWorkflowRunDetail(runId) {
    try { return await this.fetch(`/workflows/runs/${runId}`); } catch { return null; }
  }
  static async testWorkflowSteps(steps, uptoIndex, triggerData) {
    try { return await this.post('/workflows/test-steps', { steps, uptoIndex, triggerData }); } catch (e) { return { results: [{ status: 'failed', error: e.message }] }; }
  }

  static async sendEodSummary(module) {
    try {
      const response = await fetch(`${API_BASE}/eod/send`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ module })
      });
      return response.json();
    } catch (e) { return { success: false, error: e.message }; }
  }

  static async sendQualityEodSummary(userName) {
    try {
      const response = await fetch(`${API_BASE}/eod/send-quality`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_name: userName })
      });
      return response.json();
    } catch (e) { return { success: false, error: e.message }; }
  }

  static async getProjectDetail(projectId) {
    try {
      return await this.fetch(`/plantrack/projects/${projectId}`);
    } catch {
      // Fallback mock data for dev
      const allData = await this.getPlanTrackData();
      const project = allData.projects.find(p => p.id === projectId);
      return {
        project,
        tasks: [
          { id: 'TASK-2024-016', title: 'Design REST endpoint contracts', type: 'task', priority: 'P1', status: 'completed', assignee: 'John Developer', estimateHours: 16, storyPoints: 3, progress: 100 },
          { id: 'TASK-2024-017', title: 'Set up API gateway configuration', type: 'task', priority: 'P2', status: 'in_progress', assignee: 'Mike Fullstack', estimateHours: 8, storyPoints: 2, progress: 40 }
        ],
        comments: [
          { id: 1, userName: 'John Developer', content: 'Kicked off the API modernization. Starting with the authentication endpoints first.', createdAt: '2025-01-28T10:00:00' },
          { id: 2, userName: 'Jim Manager', content: 'Make sure we maintain backward compatibility with the existing clients during migration.', createdAt: '2025-01-29T14:00:00' },
          { id: 3, userName: 'Mike Fullstack', content: 'I can help with the gateway config once the endpoint contracts are finalized.', createdAt: '2025-02-01T09:00:00' }
        ],
        updates: [
          { id: 1, userName: 'John Developer', updateType: 'status_change', content: 'Project moved to active development', oldValue: 'planning', newValue: 'active', createdAt: '2025-01-28T08:00:00' },
          { id: 2, userName: 'John Developer', updateType: 'progress', content: 'API design phase completed, moving to implementation', oldValue: null, newValue: '35%', createdAt: '2025-02-04T16:00:00' }
        ],
        milestones: allData.milestones.filter(m => m.projectId === projectId)
      };
    }
  }

  static async createProject(data) {
    try { return await this.post('/plantrack/projects', data); }
    catch { return { success: true, projectId: `PROJ-${String(Date.now()).slice(-6)}` }; }
  }

  static async updateProject(projectId, data) {
    try {
      const response = await fetch(`${API_BASE}/plantrack/projects/${projectId}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      return response.json();
    } catch { return { success: true }; }
  }

  static async deleteProject(projectId, force = false) {
    try {
      const url = `${API_BASE}/plantrack/projects/${projectId}${force ? '?force=true' : ''}`;
      const r = await fetch(url, { method: 'DELETE', headers: this.authHeaders() });
      return r.json();
    } catch (e) { return { success: false, error: e.message || 'Network error' }; }
  }

  static async addComment(projectId, content) {
    try { return await this.post(`/plantrack/projects/${projectId}/comments`, { content }); }
    catch { return { success: true, id: Date.now() }; }
  }

  static async addUpdate(projectId, content, userName) {
    return await this.post(`/plantrack/projects/${projectId}/updates`, { content, updateType: 'general', userName });
  }
  static async editProjectUpdate(projectId, updateId, content) {
    try {
      const r = await fetch(`${API_BASE}/plantrack/projects/${projectId}/updates/${updateId}`, { method: 'PUT', headers: { 'Content-Type': 'application/json', ...this.authHeaders() }, body: JSON.stringify({ content }) });
      return r.json();
    } catch { return { success: false }; }
  }
  static async deleteProjectUpdate(projectId, updateId) {
    try {
      const r = await fetch(`${API_BASE}/plantrack/projects/${projectId}/updates/${updateId}`, { method: 'DELETE', headers: this.authHeaders() });
      return r.json();
    } catch { return { success: false }; }
  }

  static async postProjectUpdateReport(preparedBy, role) {
    try {
      const response = await fetch(`${API_BASE}/plantrack/post-update-report`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prepared_by: preparedBy, role })
      });
      return response.json();
    } catch (e) { return { success: false, error: e.message }; }
  }

  // ---- Polaris ----
  static async getPolarisData() {
    try { return await this.fetch('/polaris'); }
    catch { return { projects: [], archived: [], priorityLog: [], highlights: [], highlightsMeta: null }; }
  }
  static async createPolarisProject(data) {
    try { return await this.post('/polaris/projects', data); }
    catch (e) { return { success: false, error: e.message }; }
  }
  static async updatePolarisProject(id, data) {
    const r = await fetch(`${API_BASE}/polaris/projects/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json', ...this.authHeaders() }, body: JSON.stringify(data) });
    const payload = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(payload?.error || payload?.message || `Update failed (${r.status})`);
    return payload;
  }
  static async deletePolarisProject(id) {
    try {
      const r = await fetch(`${API_BASE}/polaris/projects/${id}`, { method: 'DELETE', headers: this.authHeaders() });
      return r.json();
    } catch (e) { return { success: false, error: e.message }; }
  }
  static async setPolarisArchive(id, archived, reason) {
    try {
      const r = await fetch(`${API_BASE}/polaris/projects/${id}/archive`, { method: 'POST', headers: { 'Content-Type': 'application/json', ...this.authHeaders() }, body: JSON.stringify({ archived, reason: reason || '' }) });
      return r.json();
    } catch (e) { return { success: false, error: e.message }; }
  }
  static async reorderPolarisProjects(orderedIds, reason) {
    try {
      const r = await fetch(`${API_BASE}/polaris/reorder`, { method: 'POST', headers: { 'Content-Type': 'application/json', ...this.authHeaders() }, body: JSON.stringify({ orderedIds, reason }) });
      return r.json();
    } catch (e) { return { success: false, error: e.message }; }
  }
  static async addPolarisBullet(projectId, data) {
    try { return await this.post(`/polaris/projects/${projectId}/bullets`, data); }
    catch (e) { return { success: false, error: e.message }; }
  }
  static async updatePolarisBullet(bulletId, data) {
    try {
      const r = await fetch(`${API_BASE}/polaris/bullets/${bulletId}`, { method: 'PUT', headers: { 'Content-Type': 'application/json', ...this.authHeaders() }, body: JSON.stringify(data) });
      return r.json();
    } catch (e) { return { success: false, error: e.message }; }
  }
  static async deletePolarisBullet(bulletId) {
    try {
      const r = await fetch(`${API_BASE}/polaris/bullets/${bulletId}`, { method: 'DELETE', headers: this.authHeaders() });
      return r.json();
    } catch (e) { return { success: false, error: e.message }; }
  }
  static async renamePolarisSubsection(projectId, from, to) {
    try {
      const r = await fetch(`${API_BASE}/polaris/projects/${projectId}/subsections/rename`, { method: 'POST', headers: { 'Content-Type': 'application/json', ...this.authHeaders() }, body: JSON.stringify({ from, to }) });
      return r.json();
    } catch (e) { return { success: false, error: e.message }; }
  }
  static async deletePolarisSubsection(projectId, label) {
    try {
      const r = await fetch(`${API_BASE}/polaris/projects/${projectId}/subsections/delete`, { method: 'POST', headers: { 'Content-Type': 'application/json', ...this.authHeaders() }, body: JSON.stringify({ label }) });
      return r.json();
    } catch (e) { return { success: false, error: e.message }; }
  }
  static async setPolarisHighlights(content) {
    try {
      const r = await fetch(`${API_BASE}/polaris/highlights`, { method: 'PUT', headers: { 'Content-Type': 'application/json', ...this.authHeaders() }, body: JSON.stringify({ content }) });
      return r.json();
    } catch (e) { return { success: false, error: e.message }; }
  }
  static async deletePolarisPriorityLogEntry(id) {
    try {
      const r = await fetch(`${API_BASE}/polaris/priority-log/${id}`, { method: 'DELETE', headers: this.authHeaders() });
      return r.json();
    } catch (e) { return { success: false, error: e.message }; }
  }
  static async clearPolarisPriorityLog() {
    try {
      const r = await fetch(`${API_BASE}/polaris/priority-log/clear`, { method: 'POST', headers: this.authHeaders() });
      return r.json();
    } catch (e) { return { success: false, error: e.message }; }
  }

  // ---- Change Request Form ----
  static async getChangeRequests(filters = {}) {
    try {
      const qs = new URLSearchParams();
      for (const [k, v] of Object.entries(filters)) if (v) qs.set(k, v);
      const url = `/change-requests${qs.toString() ? '?' + qs.toString() : ''}`;
      return await this.fetch(url);
    } catch { return { changeRequests: [], stats: {} }; }
  }
  static async getChangeRequest(id) {
    try { return await this.fetch(`/change-requests/${id}`); }
    catch { return null; }
  }
  static async createChangeRequest(data) {
    try { return await this.post('/change-requests', data); }
    catch (e) { return { success: false, error: e.message }; }
  }
  static async updateChangeRequest(id, data) {
    try {
      const r = await fetch(`${API_BASE}/change-requests/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json', ...this.authHeaders() }, body: JSON.stringify(data) });
      return r.json();
    } catch (e) { return { success: false, error: e.message }; }
  }
  static async deleteChangeRequest(id) {
    try {
      const r = await fetch(`${API_BASE}/change-requests/${id}`, { method: 'DELETE', headers: this.authHeaders() });
      return r.json();
    } catch (e) { return { success: false, error: e.message }; }
  }
  static async submitChangeRequest(id) {
    try { return await this.post(`/change-requests/${id}/submit`, {}); }
    catch (e) { return { success: false, error: e.message }; }
  }
  static async reviewChangeRequest(id, decision, notes) {
    try { return await this.post(`/change-requests/${id}/review`, { decision, notes }); }
    catch (e) { return { success: false, error: e.message }; }
  }
  static async completeChangeRequest(id, payload = {}) {
    try { return await this.post(`/change-requests/${id}/complete`, payload); }
    catch (e) { return { success: false, error: e.message }; }
  }
  static async addChangeRequestComment(id, content) {
    try { return await this.post(`/change-requests/${id}/comments`, { content }); }
    catch (e) { return { success: false, error: e.message }; }
  }
  static async updateChangeRequestComment(commentId, content) {
    try {
      const r = await fetch(`${API_BASE}/change-requests/comments/${commentId}`, { method: 'PUT', headers: { 'Content-Type': 'application/json', ...this.authHeaders() }, body: JSON.stringify({ content }) });
      return r.json();
    } catch (e) { return { success: false, error: e.message }; }
  }
  static async deleteChangeRequestComment(commentId) {
    try {
      const r = await fetch(`${API_BASE}/change-requests/comments/${commentId}`, { method: 'DELETE', headers: this.authHeaders() });
      return r.json();
    } catch (e) { return { success: false, error: e.message }; }
  }
  static async cancelChangeRequest(id) {
    try { return await this.post(`/change-requests/${id}/cancel`, {}); }
    catch (e) { return { success: false, error: e.message }; }
  }
  static async addChangeRequestAttachment(id, data) {
    try { return await this.post(`/change-requests/${id}/attachments`, data); }
    catch (e) { return { success: false, error: e.message }; }
  }
  static async deleteChangeRequestAttachment(attId) {
    try {
      const r = await fetch(`${API_BASE}/change-requests/attachments/${attId}`, { method: 'DELETE', headers: this.authHeaders() });
      return r.json();
    } catch (e) { return { success: false, error: e.message }; }
  }
  static async downloadChangeRequestAttachment(attId) {
    try { return await this.fetch(`/change-requests/attachments/${attId}`); }
    catch { return null; }
  }
  static async getChangeRequestSettings() {
    try { return await this.fetch('/change-requests/settings'); }
    catch { return null; }
  }
  static async saveChangeRequestSettings(data) {
    try {
      const r = await fetch(`${API_BASE}/change-requests/settings`, { method: 'PUT', headers: { 'Content-Type': 'application/json', ...this.authHeaders() }, body: JSON.stringify(data) });
      return r.json();
    } catch (e) { return { success: false, error: e.message }; }
  }
  static async bulkChangeRequestAction(action, ids, notes) {
    try { return await this.post('/change-requests/bulk', { action, ids, notes }); }
    catch (e) { return { success: false, error: e.message }; }
  }
  static async addChangeRequestTestLinks(id, testCaseIds) {
    try { return await this.post(`/change-requests/${id}/test-links`, { testCaseIds }); }
    catch (e) { return { success: false, error: e.message }; }
  }
  static async removeChangeRequestTestLink(linkId) {
    try {
      const r = await fetch(`${API_BASE}/change-requests/test-links/${linkId}`, { method: 'DELETE', headers: this.authHeaders() });
      return r.json();
    } catch (e) { return { success: false, error: e.message }; }
  }
  static async getTcmsForLinking() {
    try { return await this.fetch('/tcms'); }
    catch { return { testCases: [], projects: [] }; }
  }

  static async createSprint(data) {
    return await this.post('/plantrack/sprints', data);
  }

  static async updateSprint(sprintId, data) {
    try {
      const r = await fetch(`${API_BASE}/plantrack/sprints/${sprintId}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      return r.json();
    } catch { return { success: false }; }
  }

  static async deleteSprint(sprintId) {
    try {
      const r = await fetch(`${API_BASE}/plantrack/sprints/${sprintId}`, {
        method: 'DELETE', headers: this.authHeaders()
      });
      return r.json();
    } catch { return { success: false }; }
  }

  static async createProjectTask(projectId, data) {
    try { return await this.post(`/plantrack/projects/${projectId}/tasks`, data); }
    catch { return { success: true, taskId: `TASK-${String(Date.now()).slice(-7)}` }; }
  }
  static async deleteProjectTask(projectId, taskId) {
    try { const r = await fetch(`${API_BASE}/plantrack/projects/${projectId}/tasks/${taskId}`, { method: 'DELETE' }); return r.json(); }
    catch { return { success: false }; }
  }

  static async getLeaderboard(period = 'weekly', rangeStart = null, rangeEnd = null) {
    try {
      const params = new URLSearchParams({ period });
      if (rangeStart) params.set('rangeStart', rangeStart);
      if (rangeEnd) params.set('rangeEnd', rangeEnd);
      return await this.fetch(`/leaderboard?${params.toString()}`);
    } catch { return { devops: [], dataops: [] }; }
  }

  static async getProjectCharter(projectId) {
    try { return await this.fetch(`/plantrack/projects/${projectId}/charter`); } catch { return {}; }
  }
  static async saveProjectCharter(projectId, data) {
    try {
      const r = await fetch(`${API_BASE}/plantrack/projects/${projectId}/charter`, { method: 'PUT', headers: { 'Content-Type': 'application/json', ...this.authHeaders() }, body: JSON.stringify(data) });
      return r.json();
    } catch { return { success: false }; }
  }

  static async getRisks(projectId = null) {
    try { return await this.fetch(`/risks${projectId ? `?project_id=${projectId}` : ''}`); } catch { return { risks: [], summary: {} }; }
  }
  static async createRisk(data) {
    try { return await this.post('/risks', data); } catch { return { success: false }; }
  }
  static async updateRisk(id, data) {
    try {
      const r = await fetch(`${API_BASE}/risks/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json', ...this.authHeaders() }, body: JSON.stringify(data) });
      return r.json();
    } catch { return { success: false }; }
  }
  static async deleteRisk(id) {
    try {
      const r = await fetch(`${API_BASE}/risks/${id}`, { method: 'DELETE', headers: { ...this.authHeaders() } });
      return r.json();
    } catch { return { success: false }; }
  }

  static async getDevOpsPipeline(range = null) {
    try {
      const qs = range?.start && range?.end ? `?start=${encodeURIComponent(range.start)}&end=${encodeURIComponent(range.end)}` : '';
      return await this.fetch(`/devops${qs}`);
    } catch {
      return { sprint: null, capacity: { total: 0, committed: 0, completed: 0 }, tasks: [], bugs: [], timesheets: [], teamMembers: [] };
    }
  }

  static async getDevOpsKanban() {
    return await this.fetch('/devops/kanban');
  }

  static async createDevOpsTask(data) {
    try { return await this.post('/devops/tasks', data); }
    catch { return { success: true, taskId: `TASK-${String(Date.now()).slice(-7)}` }; }
  }

  static async createDataOpsTask(data) {
    try { return await this.post('/dataops/tasks', data); }
    catch { return { success: true, taskId: `DOP-${String(Date.now()).slice(-7)}` }; }
  }

  static async updateDevOpsTask(taskId, data) {
    try {
      const response = await fetch(`${API_BASE}/devops/tasks/${taskId}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json', ...this.authHeaders() },
        body: JSON.stringify(data)
      });
      return response.json();
    } catch { return { success: true }; }
  }

  static async deleteDevOpsTask(taskId) {
    try {
      const response = await fetch(`${API_BASE}/devops/tasks/${taskId}`, { method: 'DELETE', headers: { ...this.authHeaders() } });
      return response.json();
    } catch { return { success: true }; }
  }

  static async getDevOpsTask(taskId) {
    try { return await this.fetch(`/devops/tasks/${taskId}`); }
    catch { return null; }
  }

  // ---- DataOps Model Methods ----

  static async getDataOpsKanban() {
    try {
      return await this.fetch('/dataops/kanban');
    } catch {
      return { open: [], in_progress: [], done: [], total: 0 };
    }
  }

  static async getDataOpsData() {
    try {
      return await this.fetch('/dataops');
    } catch {
      return {
        metrics: { activePipelines: 3, dataProcessed: '19.9TB', reportsGenerated: 3, successRate: '67%' },
        pipelineStatus: [{ status: 'running', count: 3 }, { status: 'completed', count: 2 }, { status: 'failed', count: 1 }],
        powerbiReports: [
          { title: 'Executive KPI Dashboard', icon: '📊', lastUpdated: '2 hours ago', status: 'Published', views: 1234 },
          { title: 'Sales Performance', icon: '💰', lastUpdated: '5 hours ago', status: 'Published', views: 892 }
        ],
        activePipelines: [
          { name: 'Daily Users ETL', type: 'ETL', status: 'running', progress: 67, records: 1200000, started: '2 hours ago' },
          { name: 'Sales Dashboard Refresh', type: 'Report', status: 'running', progress: 45, records: 3500000, started: '90 min ago' }
        ],
        allPipelines: [
          { pipelineId: 'etl-001', pipelineName: 'Daily Users ETL', pipelineType: 'ETL', status: 'running', lastRun: '2025-02-15T10:00:00', durationSeconds: 7200, recordsProcessed: 1200000, progress: 67 },
          { pipelineId: 'rep-001', pipelineName: 'Sales Dashboard Refresh', pipelineType: 'Report', status: 'running', lastRun: '2025-02-15T10:30:00', durationSeconds: 5400, recordsProcessed: 3500000, progress: 45 },
          { pipelineId: 'etl-003', pipelineName: 'Customer Data Sync', pipelineType: 'ETL', status: 'failed', lastRun: '2025-02-15T04:00:00', durationSeconds: 600, recordsProcessed: 500000, progress: 45, errorMessage: 'Connection timeout' }
        ],
        allReports: [
          { reportId: 'pbi-exec-kpi', reportName: 'Executive KPI Dashboard', reportIcon: '📊', category: 'Executive', status: 'Published', lastUpdated: '2025-02-15T10:00:00', viewCount: 1234, isFavorite: 1 },
          { reportId: 'pbi-sales', reportName: 'Sales Performance', reportIcon: '💰', category: 'Sales', status: 'Published', lastUpdated: '2025-02-15T07:00:00', viewCount: 892, isFavorite: 0 }
        ]
      };
    }
  }

  static async getPipeline(pipelineId) {
    try { return await this.fetch(`/dataops/pipelines/${pipelineId}`); }
    catch { return null; }
  }

  static async createPipeline(data) {
    try { return await this.post('/dataops/pipelines', data); }
    catch { return { success: true }; }
  }

  static async updatePipeline(pipelineId, data) {
    try {
      const response = await fetch(`${API_BASE}/dataops/pipelines/${pipelineId}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) });
      return response.json();
    } catch { return { success: true }; }
  }

  static async deletePipeline(pipelineId) {
    try {
      const response = await fetch(`${API_BASE}/dataops/pipelines/${pipelineId}`, { method: 'DELETE' });
      return response.json();
    } catch { return { success: true }; }
  }

  static async getReport(reportId) {
    try { return await this.fetch(`/dataops/reports/${reportId}`); }
    catch { return null; }
  }

  static async createReport(data) {
    try { return await this.post('/dataops/reports', data); }
    catch { return { success: true }; }
  }

  static async updateReport(reportId, data) {
    try {
      const response = await fetch(`${API_BASE}/dataops/reports/${reportId}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) });
      return response.json();
    } catch { return { success: true }; }
  }

  static async deleteReport(reportId) {
    try {
      const response = await fetch(`${API_BASE}/dataops/reports/${reportId}`, { method: 'DELETE' });
      return response.json();
    } catch { return { success: true }; }
  }

  // ---- QMS Model Methods ----

  static async getQualityData(projectId) {
    try { return await this.fetch(`/quality${projectId ? '?projectId=' + projectId : ''}`); }
    catch {
      return { projects: [], project: null, requirements: [], testCases: [], bugs: [], metrics: { requirements: { total: 0, approved: 0, pending: 0 }, testCases: { total: 0, passed: 0, failed: 0, pending: 0 }, bugs: { total: 0, open: 0, inProgress: 0, resolved: 0 }, passRate: 0 } };
    }
  }

  static async enableProjectQa(projectId, data) {
    try { return await this.post(`/quality/projects/${projectId}/enable`, data); } catch { return { success: true }; }
  }

  static async getQaLeads() {
    try { return await this.fetch('/users/qa-leads'); } catch { return []; }
  }

  static async getProjectOverview(projectId) {
    try { return await this.fetch(`/projects/${projectId}/overview`); }
    catch { return null; }
  }

  static async clearData(modules) {
    try {
      const r = await fetch(`${API_BASE}/settings/data`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ modules })
      });
      return r.json();
    } catch { return { success: false, error: 'Network error' }; }
  }

  // ---- Milestone Engine Model Methods ----
  static async getMilestones(projectId = null) {
    try { return await this.fetch(`/plantrack/milestones${projectId ? `?projectId=${projectId}` : ''}`); } catch { return []; }
  }
  static async getMilestoneDetail(milestoneId) {
    try { return await this.fetch(`/plantrack/milestones/${milestoneId}`); } catch { return null; }
  }
  static async createMilestone(data) {
    try { return await this.post('/plantrack/milestones', data); } catch (e) { return { success: false, error: e.message }; }
  }
  static async updateMilestone(milestoneId, data) {
    try { const r = await fetch(`${API_BASE}/plantrack/milestones/${milestoneId}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) }); return r.json(); } catch (e) { return { success: false, error: e.message }; }
  }
  static async deleteMilestone(milestoneId) {
    try { const r = await fetch(`${API_BASE}/plantrack/milestones/${milestoneId}`, { method: 'DELETE' }); return r.json(); } catch (e) { return { success: false, error: e.message }; }
  }
  static async completeMilestone(milestoneId) {
    try { const r = await fetch(`${API_BASE}/plantrack/milestones/${milestoneId}/complete`, { method: 'PUT' }); return r.json(); } catch (e) { return { success: false, error: e.message }; }
  }
  static async getMilestoneTemplates() {
    try { return await this.fetch('/plantrack/milestones/templates'); } catch { return []; }
  }
  static async getTemplateItems(templateId) {
    try { return await this.fetch(`/plantrack/milestones/templates/${templateId}/items`); } catch { return []; }
  }
  static async generateMilestonesFromTemplate(data) {
    try { return await this.post('/plantrack/milestones/generate', data); } catch (e) { return { success: false, error: e.message }; }
  }
  static async createMilestoneTemplate(data) {
    try { return await this.post('/plantrack/milestones/templates', data); } catch (e) { return { success: false }; }
  }
  static async updateMilestoneTemplate(id, data) {
    try { const r = await fetch(`${API_BASE}/plantrack/milestones/templates/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) }); return r.json(); } catch (e) { return { success: false }; }
  }
  static async deleteMilestoneTemplate(id) {
    try { const r = await fetch(`${API_BASE}/plantrack/milestones/templates/${id}`, { method: 'DELETE' }); return r.json(); } catch (e) { return { success: false }; }
  }
  static async createTemplateItem(templateId, data) {
    try { return await this.post(`/plantrack/milestones/templates/${templateId}/items`, data); } catch (e) { return { success: false }; }
  }
  static async deleteTemplateItem(templateId, itemId) {
    try { const r = await fetch(`${API_BASE}/plantrack/milestones/templates/${templateId}/items/${itemId}`, { method: 'DELETE' }); return r.json(); } catch (e) { return { success: false }; }
  }
  static async getExecutiveSummary(projectId = null) {
    try { return await this.fetch(`/plantrack/milestones/executive-summary${projectId ? `?projectId=${projectId}` : ''}`); } catch { return { summary: {}, upcomingMilestones: [], delayedMilestones: [] }; }
  }
  static async createMilestoneDependency(data) {
    try { return await this.post('/plantrack/milestones/dependencies', data); } catch (e) { return { success: false, error: e.message }; }
  }
  static async deleteMilestoneDependency(predecessorId, successorId) {
    try { const r = await fetch(`${API_BASE}/plantrack/milestones/dependencies/${predecessorId}/${successorId}`, { method: 'DELETE' }); return r.json(); } catch (e) { return { success: false, error: e.message }; }
  }
  static async linkTaskToMilestone(milestoneId, taskId) {
    try { const r = await fetch(`${API_BASE}/plantrack/milestones/${milestoneId}/tasks/${taskId}`, { method: 'POST' }); return r.json(); } catch (e) { return { success: false, error: e.message }; }
  }
  static async unlinkTaskFromMilestone(milestoneId, taskId) {
    try { const r = await fetch(`${API_BASE}/plantrack/milestones/${milestoneId}/tasks/${taskId}`, { method: 'DELETE' }); return r.json(); } catch (e) { return { success: false, error: e.message }; }
  }
  static async calculateCriticalPath(projectId) {
    try { const r = await fetch(`${API_BASE}/plantrack/projects/${projectId}/critical-path`, { method: 'POST' }); return r.json(); } catch (e) { return { success: false, error: e.message }; }
  }

  static async updateQaProject(id, data) {
    try { const r = await fetch(`${API_BASE}/quality/projects/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) }); return r.json(); } catch (e) { console.error('updateQaProject failed:', e); return { success: false, error: e.message }; }
  }

  static async getReqTypes() {
    try { const r = await fetch(`${API_BASE}/quality/req-types`); return r.json(); } catch { return []; }
  }
  static async createReqType(data) {
    try { return await this.post('/quality/req-types', data); } catch (e) { return { success: false, error: e.message }; }
  }
  static async updateReqType(id, data) {
    try { const r = await fetch(`${API_BASE}/quality/req-types/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) }); return r.json(); } catch (e) { return { success: false, error: e.message }; }
  }
  static async deleteReqType(id) {
    try { const r = await fetch(`${API_BASE}/quality/req-types/${id}`, { method: 'DELETE' }); return r.json(); } catch { return { success: false }; }
  }

  static async getTcModules() {
    try { const r = await fetch(`${API_BASE}/quality/tc-modules`); return r.json(); } catch { return []; }
  }
  static async createTcModule(data) {
    try { return await this.post('/quality/tc-modules', data); } catch (e) { return { success: false, error: e.message }; }
  }
  static async updateTcModule(id, data) {
    try { const r = await fetch(`${API_BASE}/quality/tc-modules/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) }); return r.json(); } catch (e) { return { success: false, error: e.message }; }
  }
  static async deleteTcModule(id) {
    try { const r = await fetch(`${API_BASE}/quality/tc-modules/${id}`, { method: 'DELETE' }); return r.json(); } catch { return { success: false }; }
  }

  static async createRequirement(data) {
    try { return await this.post('/quality/requirements', data); } catch (e) { console.error('createRequirement failed:', e); return { success: false, error: e.message }; }
  }

  static async updateRequirement(id, data) {
    try { const r = await fetch(`${API_BASE}/quality/requirements/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json', ...this.authHeaders() }, body: JSON.stringify(data) }); return r.json(); } catch (e) { console.error('updateRequirement failed:', e); return { success: false, error: e.message }; }
  }
  static async deleteRequirement(id) {
    try { const r = await fetch(`${API_BASE}/quality/requirements/${id}`, { method: 'DELETE', headers: { ...this.authHeaders() } }); return r.json(); } catch { return { success: false }; }
  }
  static async renameReqCategory(projectId, oldCategory, newCategory) {
    try { const r = await fetch(`${API_BASE}/quality/req-categories/rename`, { method: 'PUT', headers: { 'Content-Type': 'application/json', ...this.authHeaders() }, body: JSON.stringify({ projectId, oldCategory, newCategory }) }); return r.json(); } catch { return { success: false }; }
  }

  static async createTestCase(data) {
    try { return await this.post('/quality/testcases', data); } catch (e) { console.error('createTestCase failed:', e); return { success: false, error: e.message }; }
  }

  static async updateTestCase(id, data) {
    try { const r = await fetch(`${API_BASE}/quality/testcases/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json', ...this.authHeaders() }, body: JSON.stringify(data) }); return r.json(); } catch (e) { console.error('updateTestCase failed:', e); return { success: false, error: e.message }; }
  }
  static async deleteTestCase(id) {
    try { const r = await fetch(`${API_BASE}/quality/testcases/${id}`, { method: 'DELETE', headers: this.authHeaders() }); return r.json(); } catch { return { success: false }; }
  }

  static async createBug(data) {
    try { return await this.post('/quality/bugs', data); } catch (e) { console.error('createBug failed:', e); return { success: false, error: e.message }; }
  }

  static async getBug(id) {
    try { return await this.fetch(`/quality/bugs/${id}`); } catch { return null; }
  }

  static async updateBug(id, data) {
    try { const r = await fetch(`${API_BASE}/quality/bugs/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json', ...this.authHeaders() }, body: JSON.stringify(data) }); return r.json(); } catch (e) { console.error('updateBug failed:', e); return { success: false, error: e.message }; }
  }

  static async getAllBugs(filters = {}) {
    try {
      const p = new URLSearchParams();
      if (filters.projectId) p.set('projectId', filters.projectId);
      if (filters.severity) p.set('severity', filters.severity);
      if (filters.status) p.set('status', filters.status);
      if (filters.search) p.set('search', filters.search);
      if (filters.sort) p.set('sort', filters.sort);
      if (filters.order) p.set('order', filters.order);
      const qs = p.toString();
      return await this.fetch(`/quality/bugs${qs ? '?' + qs : ''}`);
    } catch { return { bugs: [], metrics: {}, total: 0, projects: [] }; }
  }

  static async getBugDetail(id) {
    try { return await this.fetch(`/quality/bugs/${id}/detail`); }
    catch { return null; }
  }

  static async addBugComment(bugId, data) {
    try { return await this.post(`/quality/bugs/${bugId}/comments`, data); }
    catch { return { success: false }; }
  }

  static async deleteBugComment(commentId) {
    try {
      const r = await fetch(`${API_BASE}/quality/bugs/comments/${commentId}`, { method: 'DELETE', headers: this.authHeaders() });
      return r.json();
    } catch { return { success: false }; }
  }

  static async getBugAttachments(bugId) {
    try { return await this.fetch(`/quality/bugs/${bugId}/attachments`); } catch { return []; }
  }
  static async addBugAttachment(bugId, data) {
    try {
      const r = await fetch(`${API_BASE}/quality/bugs/${bugId}/attachments`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...this.authHeaders() },
        body: JSON.stringify(data),
      });
      return r.json();
    } catch { return { success: false }; }
  }
  static async deleteBugAttachment(attachmentId) {
    try {
      const r = await fetch(`${API_BASE}/quality/bugs/attachments/${attachmentId}`, {
        method: 'DELETE', headers: { ...this.authHeaders() },
      });
      return r.json();
    } catch { return { success: false }; }
  }

  // Environment Promotions
  static async promoteArtifacts(data) {
    try {
      const r = await fetch(`${API_BASE}/quality/promote`, { method: 'POST', headers: { 'Content-Type': 'application/json', ...this.authHeaders() }, body: JSON.stringify(data) });
      return r.json();
    } catch (e) { return { success: false, error: e.message }; }
  }
  static async getPromotionHistory(projectId, artifactType, artifactId) {
    try {
      const params = new URLSearchParams({ projectId });
      if (artifactType) params.set('artifactType', artifactType);
      if (artifactId) params.set('artifactId', artifactId);
      return await this.fetch(`/quality/promotions?${params}`);
    } catch { return []; }
  }

  // QA Workflow methods
  static async getTestPlans(projectId) {
    try { return await this.fetch(`/quality/test-plans?projectId=${projectId}`); } catch { return []; }
  }
  static async createTestPlan(data) {
    try {
      const r = await fetch(`${API_BASE}/quality/test-plans`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...this.authHeaders() },
        body: JSON.stringify(data),
      });
      const json = await r.json().catch(() => ({}));
      if (!r.ok) return { success: false, error: json.error || `HTTP ${r.status}` };
      return json;
    } catch (e) { return { success: false, error: e.message || 'Network error' }; }
  }
  static async updateTestPlan(id, data) {
    try {
      const r = await fetch(`${API_BASE}/quality/test-plans/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...this.authHeaders() },
        body: JSON.stringify(data),
      });
      const json = await r.json().catch(() => ({}));
      if (!r.ok) return { success: false, error: json.error || `HTTP ${r.status}` };
      return json;
    } catch (e) { return { success: false, error: e.message || 'Network error' }; }
  }

  static async deleteTestPlan(id, force = false) {
    try {
      const r = await fetch(`${API_BASE}/quality/test-plans/${id}${force ? '?force=true' : ''}`, {
        method: 'DELETE',
        headers: this.authHeaders(),
      });
      const json = await r.json().catch(() => ({}));
      if (!r.ok && !json.needsConfirmation) return { success: false, error: json.error || `HTTP ${r.status}` };
      return json;
    } catch (e) { return { success: false, error: e.message || 'Network error' }; }
  }
  static async getTestPlanDetail(id) {
    try { return await this.fetch(`/quality/test-plans/${id}`); } catch { return null; }
  }
  static async getExecutionSummary(planId) {
    try { return await this.fetch(`/quality/executions/summary?planId=${planId}`); } catch { return null; }
  }
  static async recordExecution(data) {
    try { return await this.post('/quality/executions', data); } catch { return { success: false }; }
  }
  static async getUatSignoffs(projectId) {
    try { return await this.fetch(`/quality/uat-signoffs?projectId=${projectId}`); } catch { return []; }
  }
  static async submitUatSignoff(data) {
    try { return await this.post('/quality/uat-signoffs', data); } catch { return { success: false }; }
  }
  static async getProdVerifications(projectId) {
    try { return await this.fetch(`/quality/prod-verifications?projectId=${projectId}`); } catch { return []; }
  }
  static async updateProdVerification(id, data) {
    try {
      const r = await fetch(`${API_BASE}/quality/prod-verifications/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json', ...this.authHeaders() }, body: JSON.stringify(data) });
      return r.json();
    } catch { return { success: false }; }
  }
  static async completeProdVerification(projectId) {
    try { return await this.post('/quality/prod-verifications/complete', { projectId }); } catch { return { success: false }; }
  }
  // ---- SupportBoard ----
  static async getSupportTickets(filters = {}) {
    try {
      const params = new URLSearchParams(filters).toString();
      return await this.fetch(`/support/tickets${params ? '?' + params : ''}`);
    } catch { return { tickets: [], stats: {} }; }
  }
  static async getSupportTicket(id) {
    try { return await this.fetch(`/support/tickets/${id}`); } catch { return null; }
  }
  static async createSupportTicket(data) {
    try { return await this.post('/support/tickets', data); } catch (e) { return { success: false, error: e.message }; }
  }
  static async updateSupportTicket(id, data) {
    try {
      const r = await fetch(`${API_BASE}/support/tickets/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json', ...this.authHeaders() }, body: JSON.stringify(data) });
      return r.json();
    } catch (e) { return { success: false, error: e.message }; }
  }
  static async assignSupportTicket(id, data) {
    try { return await this.post(`/support/tickets/${id}/assign`, data); } catch (e) { return { success: false, error: e.message }; }
  }
  static async resolveSupportTicket(id, resolution) {
    try { return await this.post(`/support/tickets/${id}/resolve`, { resolution }); } catch (e) { return { success: false, error: e.message }; }
  }
  static async addSupportTicketComment(ticketId, data) {
    try { return await this.post(`/support/tickets/${ticketId}/comments`, data); } catch (e) { return { success: false, error: e.message }; }
  }
  static async getSlaPolicies() {
    try { return await this.fetch('/support/sla-policies'); } catch { return []; }
  }
  static async upsertSlaPolicy(data) {
    try { return await this.post('/support/sla-policies', data); } catch (e) { return { success: false }; }
  }
  static async getRoutingRules() {
    try { return await this.fetch('/support/routing-rules'); } catch { return []; }
  }
  static async createRoutingRule(data) {
    try { return await this.post('/support/routing-rules', data); } catch (e) { return { success: false }; }
  }
  static async deleteRoutingRule(id) {
    try {
      const r = await fetch(`${API_BASE}/support/routing-rules/${id}`, { method: 'DELETE', headers: this.authHeaders() });
      return r.json();
    } catch { return { success: false }; }
  }
  static async getSlaStatus(ticketId) {
    try { return await this.fetch(`/support/tickets/${ticketId}/sla`); } catch { return null; }
  }
  static async escalateTicket(ticketId, level) {
    try { return await this.post(`/support/tickets/${ticketId}/escalate`, { level }); } catch { return { success: false }; }
  }
  static async getRtmData(projectId) {
    try { return await this.fetch(`/quality/rtm?projectId=${projectId}`); } catch { return { requirements: [], summary: {} }; }
  }
  static async getRequirementDetail(id) {
    try { return await this.fetch(`/quality/requirements/${id}/detail`); } catch { return null; }
  }
  static async generateSprintTasks(reqId, tasks, useTemplate = false, defaultAssignee = '') {
    try { return await this.post(`/quality/requirements/${reqId}/generate-tasks`, { tasks, useTemplate, defaultAssignee }); } catch { return { success: false }; }
  }
  static async getRequirementTemplate(reqId) {
    try { return await this.fetch(`/quality/requirements/${reqId}/template`); } catch { return { titles: [] }; }
  }
  static async updateTaskReviewStatus(taskId, reviewStatus) {
    try {
      const r = await fetch(`${API_BASE}/devops/tasks/${taskId}`, { method: 'PUT', headers: { 'Content-Type': 'application/json', ...this.authHeaders() }, body: JSON.stringify({ reviewStatus }) });
      return r.json();
    } catch { return { success: false }; }
  }

  static async getProjectAttachments(projectId) {
    try { return await this.fetch(`/plantrack/projects/${projectId}/attachments`); } catch { return []; }
  }
  static async addProjectAttachment(projectId, data) {
    try {
      const r = await fetch(`${API_BASE}/plantrack/projects/${projectId}/attachments`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...this.authHeaders() },
        body: JSON.stringify(data),
      });
      return r.json();
    } catch { return { success: false }; }
  }
  static async deleteProjectAttachment(attachmentId) {
    try {
      const r = await fetch(`${API_BASE}/plantrack/projects/attachments/${attachmentId}`, {
        method: 'DELETE', headers: { ...this.authHeaders() },
      });
      return r.json();
    } catch { return { success: false }; }
  }

  // ---- QuickTrack Model Methods ----

  static async getQuickTrackData() {
    try { return await this.fetch('/quicktrack'); }
    catch {
      return {
        tasks: [
          { id: 1, title: 'Sample task', priority: 'medium', status: 'open', category: 'general', assignedTo: 'John Developer', createdBy: 'Jim Manager', dueDate: null, createdAt: new Date().toISOString(), completedAt: null }
        ],
        metrics: { total: 1, open: 1, inProgress: 0, done: 0, overdue: 0 }
      };
    }
  }

  static async createQuickTask(data) {
    // Was silently reporting success on any thrown error, which would have hidden
    // backfill validation failures (missing/future completed date, wrong status) from
    // the user entirely — they'd see the modal close as if it worked.
    try { return await this.post('/quicktrack/tasks', data); }
    catch (e) { return { success: false, error: e?.message || 'Failed to create task' }; }
  }

  static async getQuickTask(taskId) {
    try { return await this.fetch(`/quicktrack/tasks/${taskId}`); } catch { return null; }
  }
  static async updateQuickTask(taskId, data) {
    try {
      const response = await fetch(`${API_BASE}/quicktrack/tasks/${taskId}`, { method: 'PUT', headers: { 'Content-Type': 'application/json', ...this.authHeaders() }, body: JSON.stringify(data) });
      return response.json();
    } catch { return { success: true }; }
  }

  static async deleteQuickTask(taskId) {
    try {
      const response = await fetch(`${API_BASE}/quicktrack/tasks/${taskId}`, { method: 'DELETE', headers: { ...this.authHeaders() } });
      return response.json();
    } catch { return { success: true }; }
  }

  // ---- Vendor Events Model Methods ----

  static async getVendorEventsData(filters = {}) {
    try {
      const params = new URLSearchParams();
      if (filters.vendor) params.set('vendor', filters.vendor);
      if (filters.workflow) params.set('workflow', filters.workflow);
      if (filters.status) params.set('status', filters.status);
      const qs = params.toString();
      return await this.fetch(`/vendorevents${qs ? '?' + qs : ''}`);
    } catch {
      return { vendors: [], events: [], metrics: { totalFired: 0, totalAccepted: 0, acceptanceRate: 0 } };
    }
  }

  static async createVendor(data) {
    try { return await this.post('/vendorevents/vendors', data); }
    catch { return { success: true }; }
  }

  static async createVendorEvent(data) {
    try { return await this.post('/vendorevents/events', data); }
    catch { return { success: true }; }
  }

  static async updateVendorEvent(eventId, data) {
    try {
      const response = await fetch(`${API_BASE}/vendorevents/events/${eventId}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) });
      return response.json();
    } catch { return { success: true }; }
  }

  static async deleteVendorEvent(eventId) {
    try {
      const response = await fetch(`${API_BASE}/vendorevents/events/${eventId}`, { method: 'DELETE' });
      return response.json();
    } catch { return { success: true }; }
  }

  static async receiveVendorWebhook(vendorId, payload) {
    try { return await this.post(`/vendorevents/vendors/${vendorId}/webhook`, payload); }
    catch (e) { return { success: false, error: e.message }; }
  }

  static async updateVendor(vendorId, data) {
    try {
      const r = await fetch(`${API_BASE}/vendorevents/vendors/${vendorId}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) });
      return r.json();
    } catch (e) { return { success: false, error: e.message }; }
  }

  static async deleteVendor(vendorId) {
    try {
      const r = await fetch(`${API_BASE}/vendorevents/vendors/${vendorId}`, { method: 'DELETE' });
      return r.json();
    } catch (e) { return { success: false, error: e.message }; }
  }

  // ---- LearnTrack Model Methods ----

  static async getLearnTrackData() {
    try {
      return await this.fetch('/learntrack');
    } catch {
      return {
        skills: [
          { id: 1, skillName: 'JavaScript', category: 'technical' },
          { id: 2, skillName: 'React', category: 'technical' },
          { id: 3, skillName: 'Python', category: 'technical' }
        ],
        courses: [
          { id: 1, courseName: 'React Fundamentals', difficulty: 'beginner', durationHours: 8, instructor: 'Sarah Frontend', targetGroup: 'developer', status: 'published', badgeIcon: '⚛️', skillName: 'React', enrollmentCount: 2, completedCount: 2 },
          { id: 2, courseName: 'Advanced JavaScript', difficulty: 'advanced', durationHours: 12, instructor: 'Mike Fullstack', targetGroup: 'developer', status: 'published', badgeIcon: '🟨', skillName: 'JavaScript', enrollmentCount: 1, completedCount: 0 }
        ],
        myEnrollments: [
          { id: 1, status: 'completed', progress: 100, courseName: 'React Fundamentals', badgeIcon: '⚛️', difficulty: 'beginner', skillName: 'React', score: 92 },
          { id: 2, status: 'in_progress', progress: 65, courseName: 'Advanced JavaScript', badgeIcon: '🟨', difficulty: 'advanced', skillName: 'JavaScript', score: null }
        ],
        myBadges: [{ id: 1, badgeName: 'React Starter', badgeIcon: '⚛️', description: 'Completed React Fundamentals', awardedAt: '2025-02-01' }],
        teamProgress: [
          { userId: 2, userName: 'John Developer', totalEnrollments: 3, completedCourses: 1, inProgressCourses: 1, badgeCount: 1 },
          { userId: 3, userName: 'Sarah Frontend', totalEnrollments: 2, completedCourses: 1, inProgressCourses: 1, badgeCount: 1 }
        ]
      };
    }
  }

  static async getCourseDetail(courseId) {
    try { return await this.fetch(`/learntrack/courses/${courseId}`); }
    catch { return null; }
  }

  static async createCourse(data) {
    try { return await this.post('/learntrack/courses', data); }
    catch { return { success: true }; }
  }

  static async updateCourse(courseId, data) {
    try {
      const response = await fetch(`${API_BASE}/learntrack/courses/${courseId}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) });
      return response.json();
    } catch { return { success: true }; }
  }

  static async deleteCourse(courseId) {
    try {
      const response = await fetch(`${API_BASE}/learntrack/courses/${courseId}`, { method: 'DELETE' });
      return response.json();
    } catch { return { success: true }; }
  }

  static async enrollUser(data) {
    try { return await this.post('/learntrack/enroll', data); }
    catch { return { success: true }; }
  }

  static async updateEnrollment(enrollmentId, data) {
    try {
      const response = await fetch(`${API_BASE}/learntrack/enrollments/${enrollmentId}`, { method: 'PUT', headers: { 'Content-Type': 'application/json', ...this.authHeaders() }, body: JSON.stringify(data) });
      return response.json();
    } catch { return { success: false }; }
  }

  static async deleteEnrollment(enrollmentId) {
    try {
      const response = await fetch(`${API_BASE}/learntrack/enrollments/${enrollmentId}`, { method: 'DELETE', headers: { ...this.authHeaders() } });
      return response.json();
    } catch { return { success: false }; }
  }

  // ---- Self profile (hire_date) ----

  static async updateMyHireDate(hireDate) {
    try {
      const r = await fetch(`${API_BASE}/users/me`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...this.authHeaders() },
        body: JSON.stringify({ hireDate })
      });
      return r.json();
    } catch (e) { return { success: false, error: e.message || 'Network error' }; }
  }

  static async updateMySchedule(schedule) {
    try {
      const r = await fetch(`${API_BASE}/users/me`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...this.authHeaders() },
        body: JSON.stringify({ schedule })
      });
      return r.json();
    } catch (e) { return { success: false, error: e.message || 'Network error' }; }
  }

  // Admin/manager — edit another user's hire_date or weekly schedule
  static async adminUpdateUserProfile(userId, data) {
    try {
      const r = await fetch(`${API_BASE}/users/${userId}/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...this.authHeaders() },
        body: JSON.stringify(data)
      });
      return r.json();
    } catch (e) { return { success: false, error: e.message || 'Network error' }; }
  }

  // ---- Learning Paths ----

  static async getLearningPaths() {
    try { return await this.fetch('/learntrack/paths'); } catch { return []; }
  }
  static async getLearningPath(pathId) {
    try { return await this.fetch(`/learntrack/paths/${pathId}`); } catch { return null; }
  }
  static async createLearningPath(data) {
    try { return await this.post('/learntrack/paths', data); } catch { return { success: false }; }
  }
  static async updateLearningPath(pathId, data) {
    try {
      const r = await fetch(`${API_BASE}/learntrack/paths/${pathId}`, { method: 'PUT', headers: { 'Content-Type': 'application/json', ...this.authHeaders() }, body: JSON.stringify(data) });
      return r.json();
    } catch { return { success: false }; }
  }
  static async deleteLearningPath(pathId) {
    try {
      const r = await fetch(`${API_BASE}/learntrack/paths/${pathId}`, { method: 'DELETE', headers: this.authHeaders() });
      return r.json();
    } catch { return { success: false }; }
  }
  static async setPathCourses(pathId, courseIds) {
    try {
      const r = await fetch(`${API_BASE}/learntrack/paths/${pathId}/courses`, { method: 'PUT', headers: { 'Content-Type': 'application/json', ...this.authHeaders() }, body: JSON.stringify({ courseIds }) });
      return r.json();
    } catch { return { success: false }; }
  }
  static async enrollInPath(pathId, userIds = null) {
    try { return await this.post(`/learntrack/paths/${pathId}/enroll`, userIds ? { userIds } : {}); }
    catch { return { success: false }; }
  }
  static async unenrollFromPath(pathId, userId) {
    try {
      const r = await fetch(`${API_BASE}/learntrack/paths/${pathId}/enroll/${userId}`, { method: 'DELETE', headers: this.authHeaders() });
      return r.json();
    } catch { return { success: false }; }
  }

  // ---- Manager Console Model Methods ----

  static async getManagerConsole() {
    try { return await this.fetch('/manager-console'); } catch { return null; }
  }
  static async mcUpdateSettings(data) {
    try {
      const r = await fetch(`${API_BASE}/manager-console/settings`, { method: 'PUT', headers: { 'Content-Type': 'application/json', ...this.authHeaders() }, body: JSON.stringify(data) });
      return r.json();
    } catch { return { success: false }; }
  }
  static async mcAddMember(data) {
    try { return await this.post('/manager-console/members', data); } catch { return { success: false }; }
  }
  static async mcUpdateMember(id, data) {
    try {
      const r = await fetch(`${API_BASE}/manager-console/members/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json', ...this.authHeaders() }, body: JSON.stringify(data) });
      return r.json();
    } catch { return { success: false }; }
  }
  static async mcRemoveMember(id) {
    try {
      const r = await fetch(`${API_BASE}/manager-console/members/${id}`, { method: 'DELETE', headers: { ...this.authHeaders() } });
      return r.json();
    } catch { return { success: false }; }
  }
  static async mcSyncMembersFromTeams() {
    try { return await this.post('/manager-console/members/sync', {}); } catch { return { success: false }; }
  }
  static async mcSaveOkrTemplate(okrTemplate) {
    try {
      const r = await fetch(`${API_BASE}/manager-console/okr-template`, { method: 'PUT', headers: { 'Content-Type': 'application/json', ...this.authHeaders() }, body: JSON.stringify({ okrTemplate }) });
      return r.json();
    } catch { return { success: false }; }
  }
  static async mcSaveEvaluation(memberId, month, data) {
    try {
      const r = await fetch(`${API_BASE}/manager-console/evaluations/${memberId}/${month}`, { method: 'PUT', headers: { 'Content-Type': 'application/json', ...this.authHeaders() }, body: JSON.stringify(data) });
      return r.json();
    } catch { return { success: false }; }
  }
  static async mcSavePipelineStages(stages) {
    try {
      const r = await fetch(`${API_BASE}/manager-console/pipeline-stages`, { method: 'PUT', headers: { 'Content-Type': 'application/json', ...this.authHeaders() }, body: JSON.stringify({ stages }) });
      return r.json();
    } catch { return { success: false }; }
  }
  static async mcAddCandidate(data) {
    try { return await this.post('/manager-console/candidates', data); } catch { return { success: false }; }
  }
  static async mcUpdateCandidate(id, data) {
    try {
      const r = await fetch(`${API_BASE}/manager-console/candidates/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json', ...this.authHeaders() }, body: JSON.stringify(data) });
      return r.json();
    } catch { return { success: false }; }
  }
  static async mcDeleteCandidate(id) {
    try {
      const r = await fetch(`${API_BASE}/manager-console/candidates/${id}`, { method: 'DELETE', headers: { ...this.authHeaders() } });
      return r.json();
    } catch { return { success: false }; }
  }
  static async mcAddHire(data) {
    try { return await this.post('/manager-console/hires', data); } catch { return { success: false }; }
  }
  static async mcUpdateHire(id, data) {
    try {
      const r = await fetch(`${API_BASE}/manager-console/hires/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json', ...this.authHeaders() }, body: JSON.stringify(data) });
      return r.json();
    } catch { return { success: false }; }
  }
  static async mcDeleteHire(id) {
    try {
      const r = await fetch(`${API_BASE}/manager-console/hires/${id}`, { method: 'DELETE', headers: { ...this.authHeaders() } });
      return r.json();
    } catch { return { success: false }; }
  }
  static async mcSaveHireProgress(hireId, progress) {
    try {
      const r = await fetch(`${API_BASE}/manager-console/hires/${hireId}/progress`, { method: 'PUT', headers: { 'Content-Type': 'application/json', ...this.authHeaders() }, body: JSON.stringify({ progress }) });
      return r.json();
    } catch { return { success: false }; }
  }
  static async mcConvertHireToMember(hireId) {
    try { return await this.post(`/manager-console/hires/${hireId}/convert-to-member`, {}); } catch { return { success: false }; }
  }
  static async mcSaveOnboardingTemplate(template) {
    try {
      const r = await fetch(`${API_BASE}/manager-console/onboarding-template`, { method: 'PUT', headers: { 'Content-Type': 'application/json', ...this.authHeaders() }, body: JSON.stringify({ template }) });
      return r.json();
    } catch { return { success: false }; }
  }
  static async mcAddCoachingLog(data) {
    try { return await this.post('/manager-console/coaching-logs', data); } catch { return { success: false }; }
  }
  static async mcUpdateCoachingLog(id, data) {
    try {
      const r = await fetch(`${API_BASE}/manager-console/coaching-logs/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json', ...this.authHeaders() }, body: JSON.stringify(data) });
      return r.json();
    } catch { return { success: false }; }
  }
  static async mcDeleteCoachingLog(id) {
    try {
      const r = await fetch(`${API_BASE}/manager-console/coaching-logs/${id}`, { method: 'DELETE', headers: { ...this.authHeaders() } });
      return r.json();
    } catch { return { success: false }; }
  }
  static async mcSaveOneOnOne(memberId, data) {
    try {
      const r = await fetch(`${API_BASE}/manager-console/one-on-ones/${memberId}`, { method: 'PUT', headers: { 'Content-Type': 'application/json', ...this.authHeaders() }, body: JSON.stringify(data) });
      return r.json();
    } catch { return { success: false }; }
  }
  static async mcSaveIdp(memberId, data) {
    try {
      const r = await fetch(`${API_BASE}/manager-console/idps/${memberId}`, { method: 'PUT', headers: { 'Content-Type': 'application/json', ...this.authHeaders() }, body: JSON.stringify(data) });
      return r.json();
    } catch { return { success: false }; }
  }
  static async mcUpsertOncall(data) {
    try { return await this.post('/manager-console/oncall', data); } catch { return { success: false }; }
  }
  static async mcAutoFillOncall(data) {
    try { return await this.post('/manager-console/oncall/auto-fill', data); } catch { return { success: false }; }
  }
  static async mcDeleteOncall(id) {
    try {
      const r = await fetch(`${API_BASE}/manager-console/oncall/${id}`, { method: 'DELETE', headers: { ...this.authHeaders() } });
      return r.json();
    } catch { return { success: false }; }
  }
  static async mcAddPto(data) {
    try { return await this.post('/manager-console/pto', data); } catch { return { success: false }; }
  }
  static async mcDeletePto(id) {
    try {
      const r = await fetch(`${API_BASE}/manager-console/pto/${id}`, { method: 'DELETE', headers: { ...this.authHeaders() } });
      return r.json();
    } catch { return { success: false }; }
  }
  static async mcAddPulse(data) {
    try { return await this.post('/manager-console/pulse', data); } catch { return { success: false }; }
  }
  static async mcUpdatePulse(id, data) {
    try {
      const r = await fetch(`${API_BASE}/manager-console/pulse/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json', ...this.authHeaders() }, body: JSON.stringify(data) });
      return r.json();
    } catch { return { success: false }; }
  }
  static async mcDeletePulse(id) {
    try {
      const r = await fetch(`${API_BASE}/manager-console/pulse/${id}`, { method: 'DELETE', headers: { ...this.authHeaders() } });
      return r.json();
    } catch { return { success: false }; }
  }
  static async mcSaveSkills(skills) {
    try {
      const r = await fetch(`${API_BASE}/manager-console/skills`, { method: 'PUT', headers: { 'Content-Type': 'application/json', ...this.authHeaders() }, body: JSON.stringify({ skills }) });
      return r.json();
    } catch { return { success: false }; }
  }
  static async mcAddSuccession(data) {
    try { return await this.post('/manager-console/succession', data); } catch { return { success: false }; }
  }
  static async mcUpdateSuccession(id, data) {
    try {
      const r = await fetch(`${API_BASE}/manager-console/succession/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json', ...this.authHeaders() }, body: JSON.stringify(data) });
      return r.json();
    } catch { return { success: false }; }
  }
  static async mcDeleteSuccession(id) {
    try {
      const r = await fetch(`${API_BASE}/manager-console/succession/${id}`, { method: 'DELETE', headers: { ...this.authHeaders() } });
      return r.json();
    } catch { return { success: false }; }
  }
  static async mcSaveComp(memberId, data) {
    try {
      const r = await fetch(`${API_BASE}/manager-console/comp/${memberId}`, { method: 'PUT', headers: { 'Content-Type': 'application/json', ...this.authHeaders() }, body: JSON.stringify(data) });
      return r.json();
    } catch { return { success: false }; }
  }
  static async mcSaveRetention(memberId, data) {
    try {
      const r = await fetch(`${API_BASE}/manager-console/retention/${memberId}`, { method: 'PUT', headers: { 'Content-Type': 'application/json', ...this.authHeaders() }, body: JSON.stringify(data) });
      return r.json();
    } catch { return { success: false }; }
  }

  // ---- Tech Debt Review ----
  static async getTechDebt() {
    try { return await this.fetch('/techdebt'); } catch { return { items: [], meetings: [] }; }
  }
  static async tdCreateItem(data) {
    try { return await this.post('/techdebt/items', data); } catch { return { success: false }; }
  }
  static async tdUpdateItem(id, data) {
    try {
      const r = await fetch(`${API_BASE}/techdebt/items/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json', ...this.authHeaders() }, body: JSON.stringify(data) });
      return r.json();
    } catch { return { success: false }; }
  }
  static async tdDeleteItem(id) {
    try {
      const r = await fetch(`${API_BASE}/techdebt/items/${id}`, { method: 'DELETE', headers: { ...this.authHeaders() } });
      return r.json();
    } catch { return { success: false }; }
  }
  static async tdCreateMeeting(data) {
    try { return await this.post('/techdebt/meetings', data); } catch { return { success: false }; }
  }
  static async tdUpdateMeeting(id, data) {
    try {
      const r = await fetch(`${API_BASE}/techdebt/meetings/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json', ...this.authHeaders() }, body: JSON.stringify(data) });
      return r.json();
    } catch { return { success: false }; }
  }
  static async tdDeleteMeeting(id) {
    try {
      const r = await fetch(`${API_BASE}/techdebt/meetings/${id}`, { method: 'DELETE', headers: { ...this.authHeaders() } });
      return r.json();
    } catch { return { success: false }; }
  }

  // ---- Product (Team Work Log) ----
  static async getProduct() {
    try { return await this.fetch('/product'); } catch { return { entries: [] }; }
  }
  static async createProductEntry(data) {
    try { return await this.post('/product/entries', data); } catch { return { success: false }; }
  }
  static async deleteProductEntry(id) {
    try {
      const r = await fetch(`${API_BASE}/product/entries/${id}`, { method: 'DELETE', headers: { ...this.authHeaders() } });
      return r.json();
    } catch { return { success: false }; }
  }

  // ---- TimeBoard Model Methods ----

  static async getTimeBoardData(weekStart) {
    try {
      const params = weekStart ? `?week=${weekStart}` : '';
      return await this.fetch(`/timeboard${params}`);
    } catch {
      const ws = weekStart || '2025-01-27';
      return {
        weekStart: ws,
        myTimesheet: { id: 4, userId: 2, userName: 'John Developer', weekStartDate: ws, status: 'draft', totalHours: 24, billableHours: 20, leaveHours: 0 },
        entries: [
          { id: 19, entryDate: `${ws}`, hours: 8, category: 'project', projectId: 'PROJ-001', description: 'Settlement API implementation', isBillable: 1 },
          { id: 20, entryDate: '2025-01-28', hours: 8, category: 'project', projectId: 'PROJ-001', description: 'REST endpoint development', isBillable: 1 },
          { id: 21, entryDate: '2025-01-29', hours: 6, category: 'project', projectId: 'PROJ-002', description: 'Worklist bulk actions', isBillable: 1 },
          { id: 22, entryDate: '2025-01-29', hours: 2, category: 'meeting', projectId: null, description: 'Sprint retrospective', isBillable: 0 }
        ],
        teamTimesheets: [
          { id: 4, userId: 2, userName: 'John Developer', weekStartDate: ws, status: 'draft', totalHours: 24, billableHours: 20, leaveHours: 0 },
          { id: 5, userId: 3, userName: 'Sarah Frontend', weekStartDate: ws, status: 'draft', totalHours: 16, billableHours: 12, leaveHours: 0 },
          { id: 6, userId: 4, userName: 'Mike Fullstack', weekStartDate: ws, status: 'draft', totalHours: 20, billableHours: 16, leaveHours: 8 },
          { id: 8, userId: 1, userName: 'Jim Manager', weekStartDate: ws, status: 'draft', totalHours: 32, billableHours: 8, leaveHours: 0 }
        ],
        leaveDays: [
          { userId: 4, userName: 'Mike Fullstack', leaveType: 'sick', startDate: '2025-01-28', endDate: '2025-01-29' }
        ]
      };
    }
  }

  static async createTimesheetEntry(data) {
    try { return await this.post('/timeboard/entries', data); }
    catch { return { success: true }; }
  }

  static async updateTimesheetEntry(id, data) {
    try {
      const response = await fetch(`${API_BASE}/timeboard/entries/${id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json', ...this.authHeaders() },
        body: JSON.stringify(data)
      });
      return response.json();
    } catch { return { success: true }; }
  }

  static async deleteTimesheetEntry(id) {
    try {
      const response = await fetch(`${API_BASE}/timeboard/entries/${id}`, { method: 'DELETE', headers: this.authHeaders() });
      return response.json();
    } catch { return { success: true }; }
  }

  static async submitTimesheet(id) {
    try {
      const response = await fetch(`${API_BASE}/timeboard/timesheet/${id}/submit`, { method: 'PUT', headers: this.authHeaders() });
      return response.json();
    } catch { return { success: true }; }
  }

  static async approveTimesheet(id) {
    try {
      const response = await fetch(`${API_BASE}/timeboard/timesheet/${id}/approve`, { method: 'PUT', headers: this.authHeaders() });
      return response.json();
    } catch { return { success: true }; }
  }

  static async syncCompletedTasks(defaultHours = 1) {
    try {
      const response = await fetch(`${API_BASE}/timeboard/sync-completed`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...ModulesModel.authHeaders() },
        body: JSON.stringify({ defaultHours }),
      });
      return response.json();
    } catch { return { success: false, synced: 0, skippedNoHours: 0 }; }
  }

  // ---- LeaveBoard Model Methods ----

  static async getLeaveBoardData() {
    try { return await this.fetch('/leaveboard'); }
    catch {
      return {
        myBalance: { vacation_total: 15, vacation_used: 5, vacation_remaining: 10, sick_total: 10, sick_used: 2, sick_remaining: 8, personal_total: 5, personal_used: 1, personal_remaining: 4 },
        requests: [
          { id: 1, userId: 2, userName: 'John Developer', leaveType: 'vacation', startDate: '2025-01-06', endDate: '2025-01-10', totalDays: 5, status: 'approved', reason: 'Family vacation', approvedBy: 'Jim Manager', createdAt: '2024-12-15' },
          { id: 4, userId: 2, userName: 'John Developer', leaveType: 'personal', startDate: '2025-02-14', endDate: '2025-02-14', totalDays: 1, status: 'approved', reason: 'Personal appointment', approvedBy: 'Jim Manager', createdAt: '2025-02-10' },
          { id: 5, userId: 3, userName: 'Sarah Frontend', leaveType: 'vacation', startDate: '2025-02-24', endDate: '2025-02-28', totalDays: 5, status: 'pending', reason: 'Spring break vacation', createdAt: '2025-02-01' },
          { id: 3, userId: 4, userName: 'Mike Fullstack', leaveType: 'vacation', startDate: '2025-02-03', endDate: '2025-02-05', totalDays: 3, status: 'approved', reason: 'Personal trip', approvedBy: 'Jim Manager', createdAt: '2025-01-18' },
          { id: 6, userId: 4, userName: 'Mike Fullstack', leaveType: 'sick', startDate: '2025-01-28', endDate: '2025-01-29', totalDays: 2, status: 'approved', reason: 'Flu', approvedBy: 'Jim Manager', createdAt: '2025-01-28' },
          { id: 7, userId: 1, userName: 'Jim Manager', leaveType: 'vacation', startDate: '2025-03-10', endDate: '2025-03-14', totalDays: 5, status: 'pending', reason: 'Annual leave', createdAt: '2025-02-01' }
        ],
        teamBalances: [
          { userName: 'Jim Manager', vacationRemaining: 17, sickRemaining: 9, personalRemaining: 5 },
          { userName: 'John Developer', vacationRemaining: 10, sickRemaining: 8, personalRemaining: 4 },
          { userName: 'Mike Fullstack', vacationRemaining: 11, sickRemaining: 7, personalRemaining: 5 },
          { userName: 'Sarah Frontend', vacationRemaining: 13, sickRemaining: 10, personalRemaining: 3 }
        ],
        upcomingLeave: [
          { userId: 4, userName: 'Mike Fullstack', leaveType: 'sick', startDate: '2025-01-28', endDate: '2025-01-29', totalDays: 2 },
          { userId: 4, userName: 'Mike Fullstack', leaveType: 'vacation', startDate: '2025-02-03', endDate: '2025-02-05', totalDays: 3 },
          { userId: 2, userName: 'John Developer', leaveType: 'personal', startDate: '2025-02-14', endDate: '2025-02-14', totalDays: 1 }
        ]
      };
    }
  }

  static async createLeaveRequest(data) {
    try { return await this.post('/leaveboard/requests', data); }
    catch { return { success: true }; }
  }

  static async deleteLeaveRequest(id) {
    try {
      const r = await fetch(`${API_BASE}/leaveboard/requests/${id}`, { method: 'DELETE', headers: this.authHeaders() });
      return r.json();
    } catch (e) { return { success: false, error: e.message || 'Network error' }; }
  }

  static async updateLeaveRequest(id, data) {
    try {
      const response = await fetch(`${API_BASE}/leaveboard/requests/${id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json', ...this.authHeaders() },
        body: JSON.stringify(data)
      });
      return response.json();
    } catch { return { success: true }; }
  }

  // ---- MeetingBoard Model Methods ----

  static async getMeetingBoardData() {
    try { return await this.fetch('/meetingboard'); }
    catch { return { upcoming: [], past: [], stats: { total: 0, upcoming: 0, completed: 0, cancelled: 0 } }; }
  }

  static async createMeeting(data) {
    try { return await this.post('/meetingboard/meetings', data); }
    catch { return { success: true }; }
  }

  static async updateMeeting(id, data) {
    try {
      const res = await fetch(`${API_BASE}/meetingboard/meetings/${id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json', ...this.authHeaders() },
        body: JSON.stringify(data)
      });
      return res.json();
    } catch { return { success: true }; }
  }

  static async deleteMeeting(id) {
    try {
      const res = await fetch(`${API_BASE}/meetingboard/meetings/${id}`, {
        method: 'DELETE', headers: this.authHeaders()
      });
      return res.json();
    } catch { return { success: true }; }
  }

  static async saveMeetingNotes(id, data) {
    try {
      const res = await fetch(`${API_BASE}/meetingboard/meetings/${id}/notes`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json', ...this.authHeaders() },
        body: JSON.stringify(data)
      });
      return res.json();
    } catch { return { success: false }; }
  }

  // ---- KnowledgeBoard Model Methods ----

  static async getKnowledgeData(params = {}) {
    try {
      const p = new URLSearchParams();
      if (params.categoryId) p.set('categoryId', params.categoryId);
      if (params.search) p.set('search', params.search);
      if (params.status) p.set('status', params.status);
      const qs = p.toString();
      return await this.fetch(`/knowledge/articles${qs ? '?' + qs : ''}`);
    } catch { return { categories: [], articles: [], metrics: { total: 0, published: 0, drafts: 0, categories: 0 } }; }
  }

  static async getKnowledgeArticle(id) {
    try { return await this.fetch(`/knowledge/articles/${id}`); }
    catch { return null; }
  }

  static async createKnowledgeArticle(data) {
    try { return await this.post('/knowledge/articles', data); }
    catch { return { success: false }; }
  }

  static async updateKnowledgeArticle(id, data) {
    try {
      const res = await fetch(`${API_BASE}/knowledge/articles/${id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json', ...this.authHeaders() },
        body: JSON.stringify(data)
      });
      const text = await res.text();
      try { return JSON.parse(text); } catch { return { success: false, error: `Server error (${res.status}): ${text.slice(0, 200)}` }; }
    } catch (e) { return { success: false, error: e.message }; }
  }

  static async deleteKnowledgeArticle(id) {
    try {
      const res = await fetch(`${API_BASE}/knowledge/articles/${id}`, {
        method: 'DELETE', headers: this.authHeaders()
      });
      return res.json();
    } catch { return { success: false }; }
  }

  static async createKnowledgeCategory(data) {
    try { return await this.post('/knowledge/categories', data); }
    catch { return { success: false }; }
  }

  static async updateKnowledgeCategory(id, data) {
    try {
      const res = await fetch(`${API_BASE}/knowledge/categories/${id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json', ...this.authHeaders() },
        body: JSON.stringify(data)
      });
      return res.json();
    } catch { return { success: false }; }
  }

  static async deleteKnowledgeCategory(id) {
    try {
      const res = await fetch(`${API_BASE}/knowledge/categories/${id}`, {
        method: 'DELETE', headers: this.authHeaders()
      });
      return res.json();
    } catch { return { success: false }; }
  }

  static async getArticleVersions(articleId) {
    try { return await this.fetch(`/knowledge/articles/${articleId}/versions`); }
    catch { return []; }
  }

  static async restoreArticleVersion(articleId, versionId) {
    try { return await this.post(`/knowledge/articles/${articleId}/versions/${versionId}/restore`, {}); }
    catch { return { success: false }; }
  }

  // ---- IncidentBoard Model Methods ----

  static async getIncidentsData(params = {}) {
    try {
      const p = new URLSearchParams();
      if (params.severity) p.set('severity', params.severity);
      if (params.status) p.set('status', params.status);
      if (params.team) p.set('team', params.team);
      if (params.search) p.set('search', params.search);
      const qs = p.toString();
      return await this.fetch(`/incidents${qs ? '?' + qs : ''}`);
    } catch { return { incidents: [], metrics: { total: 0, open: 0, critical: 0, resolvedToday: 0, bySeverity: {}, byStatus: {}, byTeam: {} } }; }
  }

  static async getIncident(id) {
    try { return await this.fetch(`/incidents/${id}`); }
    catch { return null; }
  }

  static async createIncident(data) {
    try { return await this.post('/incidents', data); }
    catch { return { success: false }; }
  }

  static async updateIncident(id, data) {
    try {
      const res = await fetch(`${API_BASE}/incidents/${id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json', ...this.authHeaders() },
        body: JSON.stringify(data)
      });
      return res.json();
    } catch { return { success: false }; }
  }

  static async addIncidentUpdate(incidentId, data) {
    try { return await this.post(`/incidents/${incidentId}/updates`, data); }
    catch { return { success: false }; }
  }

  static async getIncidentAnalytics() {
    try { return await this.fetch('/incidents/analytics'); }
    catch { return null; }
  }

  static async deleteIncident(id) {
    try {
      const res = await fetch(`${API_BASE}/incidents/${id}`, { method: 'DELETE', headers: this.authHeaders() });
      return res.json();
    } catch { return { success: false }; }
  }

  // ---- AutomationBoard Model Methods ----

  static async getAutomationRules() {
    try { return await this.fetch('/automation/rules'); }
    catch { return []; }
  }

  static async createAutomationRule(data) {
    try { return await this.post('/automation/rules', data); }
    catch { return { success: false }; }
  }

  static async updateAutomationRule(id, data) {
    try {
      const res = await fetch(`${API_BASE}/automation/rules/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json', ...this.authHeaders() }, body: JSON.stringify(data) });
      return res.json();
    } catch { return { success: false }; }
  }

  static async deleteAutomationRule(id) {
    try {
      const res = await fetch(`${API_BASE}/automation/rules/${id}`, { method: 'DELETE', headers: this.authHeaders() });
      return res.json();
    } catch { return { success: false }; }
  }

  static async getAutomationLogs() {
    try { return await this.fetch('/automation/logs'); }
    catch { return []; }
  }

  // ---- RequestBoard Model Methods ----

  static async getRequests(filters = {}) {
    try {
      const p = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) p.set(k, v); });
      const qs = p.toString();
      return await this.fetch(`/requests${qs ? '?' + qs : ''}`);
    } catch { return []; }
  }
  static async getRequest(id) {
    try { return await this.fetch(`/requests/${id}`); } catch { return null; }
  }
  static async getRequestStats() {
    try { return await this.fetch('/requests/stats'); }
    catch { return { total: 0, pending: 0, approved: 0, rejected: 0, inprogress: 0, completed: 0, todayCount: 0, byType: [], byDept: [] }; }
  }
  static async createRequest(data) {
    try { return await this.post('/requests', data); } catch { return { success: false }; }
  }
  static async updateRequest(id, data) {
    try {
      const res = await fetch(`${API_BASE}/requests/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json', ...this.authHeaders() }, body: JSON.stringify(data) });
      return res.json();
    } catch { return { success: false }; }
  }
  static async approveRequest(id, notes = '') {
    try { return await this.post(`/requests/${id}/approve`, { notes }); } catch { return { success: false }; }
  }
  static async rejectRequest(id, notes = '') {
    try { return await this.post(`/requests/${id}/reject`, { notes }); } catch { return { success: false }; }
  }
  static async convertRequest(id, data) {
    try { return await this.post(`/requests/${id}/convert`, data); } catch { return { success: false }; }
  }
  static async getRequestComments(id) {
    try { return await this.fetch(`/requests/${id}/comments`); } catch { return []; }
  }
  static async addRequestComment(id, data) {
    try { return await this.post(`/requests/${id}/comments`, data); } catch { return { success: false }; }
  }
  static async getRequestCategories() {
    try { return await this.fetch('/request-categories'); } catch { return []; }
  }

  // ---- AssetBoard Model Methods ----

  static async getAssets(filters = {}) {
    try {
      const p = new URLSearchParams();
      if (filters.search)      p.set('search', filters.search);
      if (filters.category_id) p.set('category_id', filters.category_id);
      if (filters.status)      p.set('status', filters.status);
      if (filters.location)    p.set('location', filters.location);
      const qs = p.toString();
      return await this.fetch(`/assets${qs ? '?' + qs : ''}`);
    } catch { return []; }
  }
  static async getAsset(id) {
    try { return await this.fetch(`/assets/${id}`); } catch { return null; }
  }
  static async getAssetStats() {
    try { return await this.fetch('/assets/stats'); }
    catch { return { total: 0, assigned: 0, available: 0, maintenance: 0, retired: 0, expiringCount: 0, expiringAssets: [] }; }
  }
  static async createAsset(data) {
    try { return await this.post('/assets', data); } catch { return { success: false }; }
  }
  static async updateAsset(id, data) {
    try {
      const res = await fetch(`${API_BASE}/assets/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json', ...this.authHeaders() }, body: JSON.stringify(data) });
      return res.json();
    } catch { return { success: false }; }
  }
  static async deleteAsset(id) {
    try {
      const res = await fetch(`${API_BASE}/assets/${id}`, { method: 'DELETE', headers: this.authHeaders() });
      return res.json();
    } catch { return { success: false }; }
  }
  static async assignAsset(data) {
    try { return await this.post('/assets/assign', data); } catch { return { success: false }; }
  }
  static async returnAsset(data) {
    try { return await this.post('/assets/return', data); } catch { return { success: false }; }
  }
  static async getAssetHistory(id) {
    try { return await this.fetch(`/assets/${id}/history`); } catch { return []; }
  }
  static async getAssetCategories() {
    try { return await this.fetch('/asset-categories'); } catch { return []; }
  }
  static async createAssetCategory(data) {
    try { return await this.post('/asset-categories', data); } catch { return { success: false }; }
  }

  // ---- Audit Trail Model Methods ----

  static async getAuditLogs(filters = {}) {
    try {
      const params = new URLSearchParams();
      if (filters.module) params.set('module', filters.module);
      if (filters.action) params.set('action', filters.action);
      if (filters.entityType) params.set('entityType', filters.entityType);
      if (filters.dateFrom) params.set('dateFrom', filters.dateFrom);
      if (filters.dateTo) params.set('dateTo', filters.dateTo);
      if (filters.search) params.set('search', filters.search);
      if (filters.offset) params.set('offset', filters.offset);
      params.set('limit', filters.limit || 50);
      const qs = params.toString();
      return await this.fetch(`/audit?${qs}`);
    } catch { return { logs: [], hasMore: false, offset: 0 }; }
  }

  static async getAuditStats() {
    try { return await this.fetch('/audit/stats'); }
    catch { return { byModule: [], byAction: [], total: 0, uniqueUsers: 0, today: 0, lastWeek: 0 }; }
  }
  static async getReportsData() {
    try { return await this.fetch('/reports'); }
    catch { return {}; }
  }
  static async getTaskManageData(filters = {}) {
    const p = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      const val = Array.isArray(v) ? v.join(',') : v;
      if (val) p.set(k, val);
    });
    try { return await this.fetch(`/tasks/manage?${p}`); }
    catch { return { tasks: [], summary: {}, assignees: [], projects: [] }; }
  }
  static async bulkUpdateTasks(tasks, updates) {
    try {
      const res = await fetch(`${API_BASE}/tasks/manage/bulk`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('orbit_token')}` },
        body: JSON.stringify({ tasks, updates }),
      });
      return await res.json();
    } catch { return { success: false }; }
  }

  static async getTeamMembers() {
    try {
      const teams = await this.fetch('/teams');
      return (teams || []).flatMap(team =>
        (team.members || []).map(m => ({ ...m, team_module: team.module, team_name: team.team_name }))
      );
    } catch { return []; }
  }

  static async getUserDirectory() {
    try { return await this.fetch('/users/directory'); }
    catch { return []; }
  }
}

// ============================================================================
// VIEWS (Presentation Components)
// ============================================================================

const NOTIF_ICONS = {
  task_assigned:   { icon: '📋', color: 'text-blue-600' },
  bug_assigned:    { icon: '🐛', color: 'text-red-600' },
  bug_commented:   { icon: '💬', color: 'text-teal-600' },
  comment_replied: { icon: '↩️', color: 'text-indigo-600' },
  meeting_invite:  { icon: '📹', color: 'text-sky-600' },
};

// ============================================================================
// MESSAGING PANEL
// ============================================================================

const MessagingPanel = ({ user, onClose, onUnreadChange, initialUserId, initialUserName }) => {
  const [conversations, setConversations] = useState([]);
  const [activeUserId, setActiveUserId] = useState(initialUserId || null);
  const [activeUserName, setActiveUserName] = useState(initialUserName || '');
  const [thread, setThread] = useState([]);
  const [messageText, setMessageText] = useState('');
  const [sending, setSending] = useState(false);
  const [loadingThread, setLoadingThread] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [search, setSearch] = useState('');
  const [otherTyping, setOtherTyping] = useState(false);
  const [showEmoji, setShowEmoji] = useState(false);
  const typingTimerRef = React.useRef(null);
  const threadEndRef = React.useRef(null);
  const threadContainerRef = React.useRef(null);
  const textareaRef = React.useRef(null);
  const prevThreadLenRef = React.useRef(0);
  const justSentRef = React.useRef(false);

  const EMOJIS = [
    ['😀','😂','😍','🥰','😎','🤔','😭','😡','🥺','😴','🤯','🤩','😏','🙄','😬','🤗','😇','🥳','😤','🤪'],
    ['👍','👎','👏','🙌','🤝','🤜','✌️','🤞','👌','🖐️','💪','🙏','👋','🤙','☝️','👆','🫶','❤️','🔥','💯'],
    ['😆','😅','🤣','😊','😋','😜','🤭','😶','🙃','😑','🫡','🥴','🤤','😮','😲','🫢','🤫','😈','👻','💀'],
    ['🎉','🎊','✨','🌟','💫','⭐','🌈','🎯','🚀','💡','💥','❗','❓','💬','📌','🔔','📣','🏆','🎁','🫠'],
  ];

  const insertEmoji = (emoji) => {
    const el = textareaRef.current;
    if (!el) { setMessageText(t => t + emoji); return; }
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const newText = messageText.slice(0, start) + emoji + messageText.slice(end);
    setMessageText(newText);
    setTimeout(() => { el.focus(); el.setSelectionRange(start + emoji.length, start + emoji.length); }, 0);
  };

  const token = () => localStorage.getItem('orbit_token');
  const ah = () => ({ Authorization: `Bearer ${token()}` });

  const loadConversations = async () => {
    try {
      const res = await fetch('/api/messages/conversations', { headers: ah() });
      if (res.ok) {
        const data = await res.json();
        const convs = data.conversations || [];
        setConversations(convs);
        // Only update parent badge — don't set to 0 if no convs yet (let parent polling handle it)
        if (convs.length > 0) {
          const total = convs.reduce((s, c) => s + (c.unread_count || 0), 0);
          onUnreadChange?.(total);
        }
      }
    } catch { /* ignore */ }
  };

  const loadUserDirectory = async () => {
    setLoadingUsers(true);
    try {
      const res = await fetch('/api/users/directory', { headers: ah() });
      if (res.ok) {
        const data = await res.json();
        setAllUsers((data.users || data || []).filter(u => u.user_id !== user?.id));
      }
    } catch { /* ignore */ }
    setLoadingUsers(false);
  };

  const loadThread = async (uid, uname) => {
    setLoadingThread(true);
    setActiveUserId(uid);
    setActiveUserName(uname);
    try {
      const res = await fetch(`/api/messages/thread/${uid}`, { headers: ah() });
      if (res.ok) {
        const data = await res.json();
        setThread(data.messages || []);
      }
      await fetch(`/api/messages/thread/${uid}`, { method: 'PUT', headers: ah() }).catch(() => {});
      loadConversations();
    } catch { /* ignore */ }
    setLoadingThread(false);
  };

  const refreshThread = async (uid) => {
    try {
      const res = await fetch(`/api/messages/thread/${uid}`, { headers: ah() });
      if (res.ok) { const data = await res.json(); setThread(data.messages || []); }
    } catch { /* ignore */ }
  };

  const checkTyping = async (uid) => {
    try {
      const res = await fetch(`/api/messages/typing/${uid}`, { headers: ah() });
      if (res.ok) { const data = await res.json(); setOtherTyping(data.isTyping || false); }
    } catch { /* ignore */ }
  };

  const sendTypingSignal = async (uid) => {
    await fetch(`/api/messages/typing/${uid}`, { method: 'PUT', headers: ah() }).catch(() => {});
  };

  useEffect(() => {
    loadUserDirectory();
    loadConversations();
    // If opened directly to a thread (from message dropdown), fetch the thread immediately
    if (initialUserId) {
      const ah = () => ({ Authorization: `Bearer ${localStorage.getItem('orbit_token')}` });
      fetch(`/api/messages/thread/${initialUserId}`, { headers: ah() })
        .then(r => r.ok ? r.json() : null)
        .then(d => { if (d) setThread(d.messages || []); })
        .catch(() => {});
      fetch(`/api/messages/thread/${initialUserId}`, { method: 'PUT', headers: ah() }).catch(() => {});
    }
    const iv = setInterval(() => {
      if (activeUserId) { refreshThread(activeUserId); checkTyping(activeUserId); loadConversations(); }
      else loadConversations();
    }, 3000);
    return () => clearInterval(iv);
  }, [activeUserId]);

  useEffect(() => {
    const container = threadContainerRef.current;
    const isInitialLoad = prevThreadLenRef.current === 0 && thread.length > 0;
    const hasNew = thread.length > prevThreadLenRef.current;
    const nearBottom = container
      ? container.scrollHeight - container.scrollTop - container.clientHeight < 120
      : true;

    const scrollToBottom = (instant) => {
      requestAnimationFrame(() => {
        const c = threadContainerRef.current;
        if (c) {
          if (instant) c.scrollTop = c.scrollHeight;
          else threadEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
      });
    };

    if (isInitialLoad || justSentRef.current) {
      scrollToBottom(true);
    } else if (hasNew && nearBottom) {
      scrollToBottom(false);
    }

    prevThreadLenRef.current = thread.length;
    justSentRef.current = false;
  }, [thread]);

  const handleSend = async () => {
    if (!messageText.trim() || !activeUserId || sending) return;
    setSending(true);
    try {
      await fetch('/api/messages', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...ah() },
        body: JSON.stringify({ recipientId: activeUserId, content: messageText.trim() }),
      });
      setMessageText('');
      setShowEmoji(false);
      justSentRef.current = true;
      // Use refreshThread (no loading flash) so container stays visible for scroll measurement
      await refreshThread(activeUserId);
    } catch { /* ignore */ }
    setSending(false);
  };

  const fmtTime = (ts) => {
    try {
      const d = new Date(ts.endsWith('Z') ? ts : ts + 'Z');
      const diff = Date.now() - d.getTime();
      if (diff < 60000) return 'just now';
      if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
      if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } catch { return ''; }
  };

  // Build a map of unread counts + last message keyed by user id
  const convMap = {};
  conversations.forEach(c => { convMap[c.other_id] = c; });

  const filteredUsers = allUsers.filter(u =>
    !search || u.user_name?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="fixed bottom-0 right-6 w-[380px] h-[520px] bg-white rounded-t-2xl shadow-2xl border border-gray-200 flex flex-col z-50 overflow-hidden">
      {/* Panel Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-blue-600 text-white">
        <div className="flex items-center gap-2">
          {activeUserId && (
            <button onClick={() => { setActiveUserId(null); setActiveUserName(''); setThread([]); prevThreadLenRef.current = 0; }} className="p-1 rounded hover:bg-blue-500">
              <ArrowLeft size={16} />
            </button>
          )}
          <MessageSquare size={16} />
          <span className="font-semibold text-sm">{activeUserId ? activeUserName : 'Messages'}</span>
        </div>
        <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-blue-500"><X size={16} /></button>
      </div>

      {/* Content area */}
      {!activeUserId ? (
        /* User list */
        <>
          <div className="px-3 py-2 border-b border-gray-100">
            <div className="relative">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search people..."
                className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto divide-y divide-gray-50">
            {loadingUsers ? (
              <div className="flex items-center justify-center h-full text-gray-400 text-sm">Loading...</div>
            ) : filteredUsers.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-gray-400 gap-2">
                <Users size={32} className="text-gray-200" />
                <p className="text-sm">No users found</p>
              </div>
            ) : filteredUsers.map(u => {
              const conv = convMap[u.user_id];
              const unread = conv?.unread_count || 0;
              const isActive = u.is_active === 1 || u.is_active === true;
              return (
                <button
                  key={u.user_id}
                  onClick={() => loadThread(u.user_id, u.user_name)}
                  className="w-full text-left px-4 py-3 hover:bg-gray-50 flex items-center gap-3 transition-colors"
                >
                  <div className="relative flex-shrink-0">
                    <div className="w-9 h-9 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-bold text-sm">
                      {(u.user_name || 'U').charAt(0).toUpperCase()}
                    </div>
                    {/* Active/inactive status dot */}
                    <span className={`absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full border-2 border-white ${isActive ? 'bg-green-500' : 'bg-red-400'}`} />
                    {unread > 0 && (
                      <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                        {unread}
                      </span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <p className={`text-sm font-semibold truncate ${unread > 0 ? 'text-gray-900' : 'text-gray-700'}`}>{u.user_name}</p>
                      {conv && <p className="text-[11px] text-gray-400 flex-shrink-0 ml-2">{fmtTime(conv.last_at)}</p>}
                    </div>
                    {conv ? (
                      <p className={`text-xs truncate mt-0.5 ${unread > 0 ? 'text-gray-700 font-medium' : 'text-gray-400'}`}>{conv.last_message}</p>
                    ) : (
                      <p className="text-xs text-gray-400 mt-0.5">No messages yet</p>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </>
      ) : (
        /* Chat thread */
        <>
          <div ref={threadContainerRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3 bg-gray-50">
            {loadingThread ? (
              <div className="flex items-center justify-center h-full text-gray-400 text-sm">Loading...</div>
            ) : thread.length === 0 ? (
              <div className="flex items-center justify-center h-full text-gray-400 text-sm">No messages yet. Say hi!</div>
            ) : thread.map(msg => {
              const isMe = Number(msg.senderId) === Number(user?.id);
              return (
                <div key={msg.id} className={`flex items-end gap-2 ${isMe ? 'justify-end' : 'justify-start'}`}>
                  {!isMe && (
                    <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-bold text-[10px] flex-shrink-0 mb-0.5">
                      {(activeUserName || '?').charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div className={`max-w-[72%] rounded-2xl px-3.5 py-2.5 text-sm shadow-sm ${isMe ? 'bg-blue-600 text-white rounded-br-none' : 'bg-white text-gray-800 rounded-bl-none border border-gray-100'}`}>
                    <p className="leading-snug break-words">{msg.content}</p>
                    <p className={`text-[10px] mt-1 ${isMe ? 'text-blue-200 text-right' : 'text-gray-400'}`}>{fmtTime(msg.createdAt)}</p>
                  </div>
                </div>
              );
            })}
            {/* Typing indicator */}
            {otherTyping && (
              <div className="flex items-end gap-2 justify-start">
                <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-bold text-[10px] flex-shrink-0">
                  {(activeUserName || '?').charAt(0).toUpperCase()}
                </div>
                <div className="bg-white border border-gray-100 rounded-2xl rounded-bl-none px-4 py-3 shadow-sm flex items-center gap-1">
                  <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            )}
            <div ref={threadEndRef} />
          </div>
          {/* Emoji Picker */}
          {showEmoji && (
            <div className="px-3 pb-1 bg-white border-t border-gray-100">
              <div className="bg-white border border-gray-200 rounded-xl shadow-lg p-2 space-y-1">
                <div className="flex items-center justify-between mb-1 px-1">
                  <span className="text-xs text-gray-400 font-medium">Pick an emoji</span>
                  <button
                    onClick={() => setShowEmoji(false)}
                    className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                    title="Close"
                  >
                    <X size={13} />
                  </button>
                </div>
                {EMOJIS.map((row, ri) => (
                  <div key={ri} className="flex flex-wrap gap-0.5">
                    {row.map(em => (
                      <button
                        key={em}
                        onClick={() => insertEmoji(em)}
                        className="w-8 h-8 flex items-center justify-center text-lg rounded-lg hover:bg-gray-100 transition-colors"
                      >
                        {em}
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          )}
          {/* Input */}
          <div className="px-3 py-3 bg-white border-t border-gray-100 flex items-end gap-2">
            <button
              onClick={() => setShowEmoji(s => !s)}
              className={`p-2 rounded-xl transition-colors flex-shrink-0 ${showEmoji ? 'bg-yellow-100 text-yellow-600' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'}`}
              title="Emoji"
            >
              😊
            </button>
            <textarea
              ref={textareaRef}
              value={messageText}
              onChange={e => {
                setMessageText(e.target.value);
                if (activeUserId && e.target.value.trim()) {
                  sendTypingSignal(activeUserId);
                  clearTimeout(typingTimerRef.current);
                }
              }}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
              placeholder="Type a message..."
              rows={1}
              className="flex-1 resize-none rounded-xl border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent max-h-24 overflow-y-auto"
              style={{ lineHeight: '1.4' }}
            />
            <button
              onClick={handleSend}
              disabled={!messageText.trim() || sending}
              className="p-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex-shrink-0"
            >
              <Send size={16} />
            </button>
          </div>
        </>
      )}
    </div>
  );
};

// ============================================================================
// MESSAGING MODULE (Slack-like)
// ============================================================================

const CHAT_EMOJIS = ['👍','👎','❤️','😂','😮','😢','🔥','✅','👀','🎉','🚀','💯','⚡','🙏','💪','😎','🤔','💡','⚠️','🎯'];
const chatAh = () => { const t = localStorage.getItem('orbit_token'); return t ? { Authorization: `Bearer ${t}` } : {}; };
const chatApi = {
  get: (p) => fetch(`/api/${p}`, { headers: chatAh() }).then(r => r.ok ? r.json() : null).catch(() => null),
  post: (p, b) => fetch(`/api/${p}`, { method: 'POST', headers: { 'Content-Type': 'application/json', ...chatAh() }, body: JSON.stringify(b) }).then(r => r.ok ? r.json() : null).catch(() => null),
  put: (p, b = {}) => fetch(`/api/${p}`, { method: 'PUT', headers: { 'Content-Type': 'application/json', ...chatAh() }, body: JSON.stringify(b) }).then(r => r.ok ? r.json() : null).catch(() => null),
  del: (p) => fetch(`/api/${p}`, { method: 'DELETE', headers: chatAh() }).then(r => r.ok ? r.json() : null).catch(() => null),
};
// Module-level channel navigation: set before navigating to messages page, read on mount
let _pendingChat = null; // { id, channel } — channel data from dropdown, no extra fetch needed
let _openChatChannelFn = null; // registered by mounted MessagingModule

const presenceDot = (s) => ({ online: 'bg-green-500', away: 'bg-yellow-400', offline: 'bg-gray-400' }[s] || 'bg-gray-400');
const presenceLabel = (s) => ({ online: 'Online', away: 'Away', offline: 'Offline' }[s] || 'Offline');

// ---- Create Channel Modal ----
const CreateChannelModal = ({ onClose, onCreate, allUsers, currentUserId }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState('public');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [saving, setSaving] = useState(false);
  const others = allUsers.filter(u => u.user_id !== currentUserId);
  const handleSubmit = async () => {
    if (!name.trim()) return;
    setSaving(true);
    await onCreate({ name: name.trim(), description, type, memberIds: selectedUsers });
    setSaving(false); onClose();
  };
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6">
        <div className="flex justify-between items-center mb-5">
          <h2 className="text-lg font-bold text-gray-900">Create a channel</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Channel name</label>
            <input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. announcements"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Description <span className="font-normal text-gray-400">(optional)</span></label>
            <input value={description} onChange={e => setDescription(e.target.value)} placeholder="What is this channel about?"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-2">Visibility</label>
            <div className="flex gap-3">
              {[['public','# Public','Anyone can join'],['private','🔒 Private','Invite only']].map(([v,label,sub]) => (
                <label key={v} className={`flex-1 border rounded-lg p-3 cursor-pointer ${type === v ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}>
                  <input type="radio" className="sr-only" checked={type === v} onChange={() => setType(v)} />
                  <div className="text-sm font-semibold text-gray-800">{label}</div>
                  <div className="text-xs text-gray-500 mt-0.5">{sub}</div>
                </label>
              ))}
            </div>
          </div>
          {type === 'private' && (
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Add members</label>
              <div className="max-h-36 overflow-y-auto border border-gray-200 rounded-lg p-2 space-y-1">
                {others.map(u => (
                  <label key={u.user_id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-50 cursor-pointer">
                    <input type="checkbox" checked={selectedUsers.includes(u.user_id)}
                      onChange={e => setSelectedUsers(p => e.target.checked ? [...p, u.user_id] : p.filter(id => id !== u.user_id))} />
                    <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-bold">{u.user_name.charAt(0).toUpperCase()}</div>
                    <span className="text-sm text-gray-700">{u.user_name}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
          <div className="flex justify-end gap-3 pt-2">
            <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
            <button onClick={handleSubmit} disabled={!name.trim() || saving}
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-40">
              {saving ? 'Creating…' : 'Create channel'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ---- New DM Modal ----
const NewDMModal = ({ onClose, onStart, allUsers, currentUserId }) => {
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState([]);
  const others = allUsers.filter(u => u.user_id !== currentUserId && (!search || u.user_name.toLowerCase().includes(search.toLowerCase())));
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm p-5">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-base font-bold text-gray-900">New Direct Message</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
        </div>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search people…"
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-blue-500" />
        <div className="max-h-52 overflow-y-auto space-y-0.5 mb-4">
          {others.map(u => (
            <label key={u.user_id} className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-50 cursor-pointer">
              <input type="checkbox" checked={selected.includes(u.user_id)}
                onChange={e => setSelected(p => e.target.checked ? [...p, u.user_id] : p.filter(id => id !== u.user_id))} />
              <div className="w-7 h-7 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-bold">{u.user_name.charAt(0).toUpperCase()}</div>
              <span className="text-sm text-gray-800">{u.user_name}</span>
            </label>
          ))}
        </div>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
          <button onClick={() => { if (selected.length) { onStart(selected); onClose(); } }} disabled={!selected.length}
            className="px-4 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-40">
            {selected.length > 1 ? 'Start Group DM' : 'Open DM'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ---- Reaction Bar ----
const ReactionBar = ({ reactions, currentUserId, onToggle }) => {
  const grouped = {};
  (reactions || []).forEach(r => {
    if (!grouped[r.emoji]) grouped[r.emoji] = { emoji: r.emoji, count: 0, users: [], mine: false };
    grouped[r.emoji].count++;
    grouped[r.emoji].users.push(r.userName);
    if (Number(r.userId) === Number(currentUserId)) grouped[r.emoji].mine = true;
  });
  const entries = Object.values(grouped);
  if (!entries.length) return null;
  return (
    <div className="flex flex-wrap gap-1 mt-1.5">
      {entries.map(g => (
        <button key={g.emoji} onClick={() => onToggle(g.emoji)} title={g.users.join(', ')}
          className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border transition-colors ${g.mine ? 'bg-blue-50 border-blue-300 text-blue-700' : 'bg-gray-50 border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-100'}`}>
          <span>{g.emoji}</span><span className="font-medium">{g.count}</span>
        </button>
      ))}
    </div>
  );
};

// ---- Chat Message Item ----
const ChatMessageItem = ({ msg, currentUserId, onReact, onEdit, onDelete, onThreadOpen, isThread = false, presenceMap = {} }) => {
  const [showActions, setShowActions] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editContent, setEditContent] = useState(msg.content);
  const isMe = Number(msg.senderId) === Number(currentUserId);
  const isDeleted = !!msg.deletedAt;
  const fmtTime = (ts) => { try { const d = new Date(ts.endsWith('Z') ? ts : ts + 'Z'); return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }); } catch { return ''; } };
  const handleSaveEdit = async () => { if (!editContent.trim() || editContent.trim() === msg.content) { setEditing(false); return; } await onEdit(msg.id, editContent.trim()); setEditing(false); };
  return (
    <div className="group relative flex gap-3 px-4 py-1.5 hover:bg-gray-50 rounded-lg"
      onMouseEnter={() => setShowActions(true)} onMouseLeave={() => { setShowActions(false); setShowEmojiPicker(false); }}>
      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-400 to-indigo-500 text-white flex items-center justify-center font-bold text-sm flex-shrink-0 mt-0.5 relative">
        {(msg.senderName || '?').charAt(0).toUpperCase()}
        {presenceMap[msg.senderId] && <span className={`absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full border-2 border-white ${presenceDot(presenceMap[msg.senderId])}`} />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="text-sm font-bold text-gray-900">{msg.senderName}</span>
          <span className="text-[11px] text-gray-400">{fmtTime(msg.createdAt)}</span>
          {msg.editedAt && !isDeleted && <span className="text-[10px] text-gray-400 italic">(edited)</span>}
        </div>
        {isDeleted ? (
          <p className="text-sm text-gray-400 italic">This message was deleted.</p>
        ) : editing ? (
          <div className="mt-1">
            <textarea value={editContent} onChange={e => setEditContent(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSaveEdit(); } if (e.key === 'Escape') setEditing(false); }}
              className="w-full border border-blue-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" rows={2} autoFocus />
            <div className="flex gap-2 mt-1 text-xs">
              <button onClick={handleSaveEdit} className="text-blue-600 hover:text-blue-800 font-medium">Save</button>
              <span className="text-gray-300">·</span>
              <button onClick={() => setEditing(false)} className="text-gray-500 hover:text-gray-700">Cancel</button>
            </div>
          </div>
        ) : (
          <p className="text-sm text-gray-800 leading-relaxed break-words whitespace-pre-wrap">{msg.content}</p>
        )}
        {(msg.files || []).length > 0 && (
          <div className="flex flex-wrap gap-2 mt-1.5">
            {msg.files.map(f => (
              <a key={f.id} href={`/api/chat/files/${f.id}`} target="_blank" rel="noreferrer"
                className="flex items-center gap-1.5 px-2.5 py-1.5 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-700 hover:bg-blue-100 transition-colors">
                📎 <span className="truncate max-w-[140px]">{f.fileName}</span>
              </a>
            ))}
          </div>
        )}
        {!isDeleted && <ReactionBar reactions={msg.reactions} currentUserId={currentUserId} onToggle={emoji => onReact(msg.id, emoji)} />}
        {!isThread && !isDeleted && msg.replyCount > 0 && (
          <button onClick={() => onThreadOpen(msg)} className="mt-1 flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium hover:underline">
            <MessageSquare size={11} /> {msg.replyCount} {msg.replyCount === 1 ? 'reply' : 'replies'}
          </button>
        )}
      </div>
      {showActions && !isDeleted && !editing && (
        <div className="absolute right-3 top-1 flex items-center gap-0.5 bg-white border border-gray-200 rounded-lg shadow-sm px-1 py-0.5 z-10">
          <div className="relative">
            <button onClick={() => setShowEmojiPicker(s => !s)} title="React" className="p-1.5 rounded hover:bg-gray-100 text-gray-500 text-sm">😊</button>
            {showEmojiPicker && (
              <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-xl shadow-xl p-2 z-20 flex flex-wrap gap-0.5 w-56">
                {CHAT_EMOJIS.map(em => (
                  <button key={em} onClick={() => { onReact(msg.id, em); setShowEmojiPicker(false); }}
                    className="w-8 h-8 flex items-center justify-center text-base rounded hover:bg-gray-100">{em}</button>
                ))}
              </div>
            )}
          </div>
          {!isThread && (
            <button onClick={() => onThreadOpen(msg)} title="Reply in thread" className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-gray-700"><MessageSquare size={13} /></button>
          )}
          {isMe && <>
            <button onClick={() => setEditing(true)} title="Edit" className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-gray-700"><Pencil size={13} /></button>
            <button onClick={() => onDelete(msg.id)} title="Delete" className="p-1.5 rounded hover:bg-gray-100 text-red-400 hover:text-red-600"><Trash2 size={13} /></button>
          </>}
        </div>
      )}
    </div>
  );
};

// ---- Thread Panel ----
const ChatThreadPanel = ({ parentMsg, currentUserId, presenceMap, onClose, onReact, onEdit, onDelete }) => {
  const [replies, setReplies] = useState([]);
  const [content, setContent] = useState('');
  const [sending, setSending] = useState(false);
  const endRef = React.useRef(null);
  const load = async () => { const d = await chatApi.get(`chat/messages/${parentMsg.id}/thread`); if (d?.replies) setReplies(d.replies); };
  useEffect(() => { load(); const iv = setInterval(load, 4000); return () => clearInterval(iv); }, [parentMsg.id]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [replies.length]);
  const handleSend = async () => {
    if (!content.trim() || sending) return;
    setSending(true);
    await chatApi.post(`chat/messages/${parentMsg.id}/thread`, { content: content.trim() });
    setContent(''); await load(); setSending(false);
  };
  return (
    <div className="w-72 border-l border-gray-200 bg-white flex flex-col flex-shrink-0">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 flex-shrink-0">
        <div className="flex items-center gap-2"><MessageSquare size={14} className="text-gray-500" /><span className="font-semibold text-gray-800 text-sm">Thread</span></div>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={15} /></button>
      </div>
      <div className="px-4 py-3 border-b border-gray-100 bg-gray-50 flex-shrink-0">
        <div className="flex items-center gap-1.5 mb-1">
          <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-bold">{(parentMsg.senderName || '?').charAt(0).toUpperCase()}</div>
          <span className="text-xs font-bold text-gray-800">{parentMsg.senderName}</span>
        </div>
        <p className="text-xs text-gray-600 leading-relaxed whitespace-pre-wrap line-clamp-3">{parentMsg.content}</p>
      </div>
      <div className="flex-1 overflow-y-auto py-2">
        {replies.length === 0 && <p className="text-xs text-gray-400 text-center py-6">No replies yet.</p>}
        {replies.map(r => (
          <ChatMessageItem key={r.id} msg={r} currentUserId={currentUserId} presenceMap={presenceMap}
            onReact={async (id, emoji) => { await onReact(id, emoji); load(); }}
            onEdit={async (id, c) => { await onEdit(id, c); load(); }}
            onDelete={async (id) => { await onDelete(id); load(); }}
            onThreadOpen={() => {}} isThread />
        ))}
        <div ref={endRef} />
      </div>
      <div className="px-3 py-3 border-t border-gray-100 flex-shrink-0 flex gap-2 items-end">
        <textarea value={content} onChange={e => setContent(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
          placeholder="Reply…" rows={2}
          className="flex-1 resize-none rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 max-h-20" />
        <button onClick={handleSend} disabled={!content.trim() || sending}
          className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-40 flex-shrink-0"><Send size={14} /></button>
      </div>
    </div>
  );
};

// ---- Chat Composer ----
const ChatComposer = ({ channelId, placeholder, onSent, onTyping }) => {
  const [content, setContent] = useState('');
  const [sending, setSending] = useState(false);
  const [showEmoji, setShowEmoji] = useState(false);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const textareaRef = React.useRef(null);
  const fileInputRef = React.useRef(null);
  const EMOJI_ROWS = [
    ['😀','😂','😍','🥰','😎','🤔','😭','😡','🥺','😴','🤯','🤩','😏','🙄','😬','🤗'],
    ['👍','👎','👏','🙌','🤝','✌️','🤞','👌','💪','🙏','👋','❤️','🔥','💯','🎉','🎊'],
    ['✨','🌟','🌈','🎯','🚀','💡','💥','❗','❓','📌','🔔','🏆','🎁','🎵','🍕','😴'],
  ];
  const insertEmoji = (em) => {
    const el = textareaRef.current;
    if (!el) { setContent(c => c + em); return; }
    const s = el.selectionStart, e2 = el.selectionEnd;
    const next = content.slice(0, s) + em + content.slice(e2);
    setContent(next);
    setTimeout(() => { el.focus(); el.setSelectionRange(s + em.length, s + em.length); }, 0);
  };
  const handleSend = async () => {
    if ((!content.trim() && !files.length) || sending) return;
    setSending(true);
    await chatApi.post(`chat/channels/${channelId}/messages`, { content: content.trim(), fileIds: files.map(f => f.id) });
    setContent(''); setFiles([]); setShowEmoji(false); setSending(false);
    onSent?.();
  };
  const handleFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (file.size > 2 * 1024 * 1024) { alert('Max file size is 2 MB'); return; }
    setUploading(true);
    const reader = new FileReader();
    reader.onload = async (ev) => {
      const result = await chatApi.post('chat/files', { channelId, fileName: file.name, fileType: file.type, fileSize: file.size, fileData: ev.target.result });
      if (result?.fileId) setFiles(p => [...p, { id: result.fileId, name: file.name }]);
      setUploading(false);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };
  return (
    <div className="px-4 py-3 border-t border-gray-200 bg-white flex-shrink-0">
      {files.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {files.map(f => (
            <div key={f.id} className="flex items-center gap-1.5 px-2.5 py-1.5 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-700">
              📎 <span className="max-w-[120px] truncate">{f.name}</span>
              <button onClick={() => setFiles(p => p.filter(x => x.id !== f.id))} className="ml-1 text-blue-400 hover:text-red-500"><X size={10} /></button>
            </div>
          ))}
        </div>
      )}
      {showEmoji && (
        <div className="mb-2 bg-white border border-gray-200 rounded-xl shadow-lg p-2">
          <div className="flex justify-between items-center mb-1 px-1">
            <span className="text-xs text-gray-400 font-medium">Emoji</span>
            <button onClick={() => setShowEmoji(false)} className="p-1 text-gray-400 hover:text-gray-600 rounded"><X size={11} /></button>
          </div>
          {EMOJI_ROWS.map((row, i) => (
            <div key={i} className="flex flex-wrap gap-0.5">
              {row.map(em => <button key={em} onClick={() => insertEmoji(em)} className="w-8 h-8 flex items-center justify-center text-base rounded hover:bg-gray-100">{em}</button>)}
            </div>
          ))}
        </div>
      )}
      <div className="flex items-end gap-2">
        <div className="flex gap-1 flex-shrink-0">
          <button onClick={() => setShowEmoji(s => !s)} className={`p-2 rounded-lg text-lg transition-colors ${showEmoji ? 'bg-yellow-50 text-yellow-500' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'}`} title="Emoji">😊</button>
          <button onClick={() => fileInputRef.current?.click()} disabled={uploading} className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 disabled:opacity-40 text-base" title="Attach file">{uploading ? '⏳' : '📎'}</button>
          <input ref={fileInputRef} type="file" className="hidden" onChange={handleFile} accept=".jpg,.jpeg,.png,.gif,.webp,.pdf,.txt,.doc,.docx,.xls,.xlsx,.csv" />
        </div>
        <textarea ref={textareaRef} value={content}
          onChange={e => { setContent(e.target.value); if (e.target.value.trim()) onTyping?.(); }}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
          placeholder={placeholder || 'Type a message…'} rows={1}
          className="flex-1 resize-none rounded-xl border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 max-h-32 overflow-y-auto"
          style={{ lineHeight: '1.5' }} />
        <button onClick={handleSend} disabled={(!content.trim() && !files.length) || sending}
          className="p-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-40 flex-shrink-0 transition-colors"><Send size={16} /></button>
      </div>
    </div>
  );
};

// ---- Channel View ----
const ChatChannelView = ({ channel, currentUser, presenceMap, allUsers, onChannelUpdated }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [threadMsg, setThreadMsg] = useState(null);
  const [showMembers, setShowMembers] = useState(false);
  const [typers, setTypers] = useState([]);
  const [members, setMembers] = useState(channel.members || []);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteSearch, setInviteSearch] = useState('');
  const endRef = React.useRef(null);
  const containerRef = React.useRef(null);
  const prevCountRef = React.useRef(0);
  const justSentRef = React.useRef(false);

  const loadMessages = async () => {
    const data = await chatApi.get(`chat/channels/${channel.id}/messages?limit=60`);
    if (data?.messages) { setMessages(data.messages); setLoading(false); }
  };
  const loadMembers = async () => {
    const data = await chatApi.get(`chat/channels/${channel.id}`);
    if (data?.channel?.members) setMembers(data.channel.members);
  };
  const loadTypers = async () => {
    const data = await chatApi.get(`chat/channels/${channel.id}/typing`);
    if (data?.typers) setTypers(data.typers.filter(n => n !== (currentUser?.user_name || currentUser?.name)));
  };

  useEffect(() => {
    setLoading(true); setMessages([]); prevCountRef.current = 0;
    loadMessages(); loadMembers();
    chatApi.put(`chat/channels/${channel.id}/read`);
    const iv = setInterval(() => { loadMessages(); loadTypers(); }, 3000);
    return () => clearInterval(iv);
  }, [channel.id]);

  useEffect(() => {
    const c = containerRef.current;
    const isNew = messages.length > prevCountRef.current;
    const nearBottom = c ? c.scrollHeight - c.scrollTop - c.clientHeight < 150 : true;
    if (isNew && (nearBottom || justSentRef.current || prevCountRef.current === 0)) {
      requestAnimationFrame(() => { if (prevCountRef.current === 0 && containerRef.current) containerRef.current.scrollTop = containerRef.current.scrollHeight; else endRef.current?.scrollIntoView({ behavior: 'smooth' }); });
    }
    prevCountRef.current = messages.length;
    justSentRef.current = false;
  }, [messages]);

  const handleReact = async (id, emoji) => { await chatApi.post(`chat/messages/${id}/reactions`, { emoji }); loadMessages(); };
  const handleEdit = async (id, content) => { await chatApi.put(`chat/messages/${id}`, { content }); loadMessages(); };
  const handleDelete = async (id) => { await chatApi.del(`chat/messages/${id}`); loadMessages(); };
  const handleSent = () => { justSentRef.current = true; loadMessages(); onChannelUpdated?.(); };

  const channelName = () => {
    if (channel.type === 'dm' || channel.type === 'group_dm') {
      const others = members.filter(m => Number(m.user_id) !== Number(currentUser?.user_id)).map(m => m.user_name);
      return others.join(', ') || channel.name || 'DM';
    }
    return channel.name;
  };
  const typingText = typers.length === 1 ? `${typers[0]} is typing…` : typers.length === 2 ? `${typers[0]} and ${typers[1]} are typing…` : typers.length > 2 ? 'Several people are typing…' : '';
  const composerPlaceholder = `Message ${channel.type === 'dm' ? '' : '#'}${channelName()}`;
  const nonMembers = allUsers.filter(u => !members.find(m => Number(m.user_id) === Number(u.user_id)) && (!inviteSearch || u.user_name.toLowerCase().includes(inviteSearch.toLowerCase())));

  return (
    <div className="flex flex-col flex-1 min-w-0 min-h-0">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3 bg-white border-b border-gray-200 flex-shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          {channel.type === 'dm' ? <span className="text-gray-400 font-bold text-base">@</span>
            : channel.type === 'group_dm' ? <Users size={14} className="text-gray-400" />
            : channel.type === 'private' ? <span className="text-sm">🔒</span>
            : <span className="text-gray-500 font-bold text-base">#</span>}
          <h2 className="font-bold text-gray-900 text-sm truncate">{channelName()}</h2>
          {channel.description && <span className="hidden lg:inline text-xs text-gray-400 truncate">· {channel.description}</span>}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {channel.type === 'private' && (
            <button onClick={() => setShowInvite(s => !s)} className={`flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg transition-colors ${showInvite ? 'bg-blue-50 text-blue-600' : 'text-gray-500 hover:bg-gray-100'}`}>
              <Plus size={13} /> Invite
            </button>
          )}
          <button onClick={() => { setShowMembers(s => !s); loadMembers(); }}
            className={`flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg transition-colors ${showMembers ? 'bg-gray-100 text-gray-800' : 'text-gray-500 hover:bg-gray-100'}`}>
            <Users size={13} /> {members.length}
          </button>
        </div>
      </div>
      <div className="flex flex-1 min-h-0">
        {/* Messages */}
        <div className="flex flex-col flex-1 min-w-0 min-h-0">
          <div ref={containerRef} className="flex-1 overflow-y-auto py-3">
            {loading ? (
              <div className="flex items-center justify-center h-full text-gray-400 text-sm">Loading…</div>
            ) : messages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full gap-3 text-gray-400">
                <div className="text-4xl">{channel.type === 'dm' ? '👋' : '#'}</div>
                <p className="text-sm font-medium">{channel.type === 'dm' ? `Start a conversation with ${channelName()}` : `Welcome to #${channelName()}`}</p>
                {channel.description && <p className="text-xs text-gray-300">{channel.description}</p>}
              </div>
            ) : messages.map(msg => (
              <ChatMessageItem key={msg.id} msg={msg} currentUserId={currentUser?.user_id} presenceMap={presenceMap}
                onReact={handleReact} onEdit={handleEdit} onDelete={handleDelete} onThreadOpen={setThreadMsg} />
            ))}
            {typingText && (
              <div className="px-4 py-1 text-xs text-gray-400 italic flex items-center gap-1.5">
                <span className="flex gap-0.5">
                  {[0,150,300].map(d => <span key={d} className="w-1 h-1 bg-gray-400 rounded-full animate-bounce" style={{animationDelay:`${d}ms`}} />)}
                </span>
                {typingText}
              </div>
            )}
            <div ref={endRef} />
          </div>
          <ChatComposer channelId={channel.id} placeholder={composerPlaceholder} onSent={handleSent}
            onTyping={() => chatApi.put(`chat/channels/${channel.id}/typing`)} />
        </div>
        {/* Thread panel */}
        {threadMsg && (
          <ChatThreadPanel parentMsg={threadMsg} currentUserId={currentUser?.user_id} presenceMap={presenceMap}
            onClose={() => setThreadMsg(null)}
            onReact={async (id, emoji) => { await chatApi.post(`chat/messages/${id}/reactions`, { emoji }); loadMessages(); }}
            onEdit={async (id, c) => { await chatApi.put(`chat/messages/${id}`, { content: c }); loadMessages(); }}
            onDelete={async (id) => { await chatApi.del(`chat/messages/${id}`); loadMessages(); }} />
        )}
        {/* Members panel */}
        {showMembers && (
          <div className="w-52 border-l border-gray-200 bg-white flex-shrink-0 flex flex-col">
            <div className="px-4 py-3 border-b border-gray-100">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Members · {members.length}</p>
            </div>
            <div className="flex-1 overflow-y-auto py-2">
              {members.map(m => (
                <div key={m.user_id} className="flex items-center gap-2.5 px-4 py-2 hover:bg-gray-50">
                  <div className="relative flex-shrink-0">
                    <div className="w-7 h-7 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-bold">{m.user_name.charAt(0).toUpperCase()}</div>
                    <span className={`absolute bottom-0 right-0 w-2 h-2 rounded-full border border-white ${presenceDot(presenceMap[m.user_id] || 'offline')}`} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-gray-800 truncate">{m.user_name}</p>
                    <p className="text-[10px] text-gray-400 capitalize">{m.role}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {/* Invite panel */}
        {showInvite && channel.type === 'private' && (
          <div className="w-52 border-l border-gray-200 bg-white flex-shrink-0 flex flex-col">
            <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
              <p className="text-xs font-semibold text-gray-700">Invite to channel</p>
              <button onClick={() => setShowInvite(false)} className="text-gray-400 hover:text-gray-600"><X size={13} /></button>
            </div>
            <div className="px-3 py-2 border-b border-gray-100">
              <input value={inviteSearch} onChange={e => setInviteSearch(e.target.value)} placeholder="Search…"
                className="w-full border border-gray-200 rounded-md px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500" />
            </div>
            <div className="flex-1 overflow-y-auto py-1">
              {nonMembers.map(u => (
                <button key={u.user_id} onClick={async () => { await chatApi.post(`chat/channels/${channel.id}/invite`, { userId: u.user_id }); loadMembers(); }}
                  className="w-full flex items-center gap-2 px-3 py-2 hover:bg-blue-50 text-left transition-colors">
                  <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-bold flex-shrink-0">{u.user_name.charAt(0).toUpperCase()}</div>
                  <span className="text-xs text-gray-700 truncate">{u.user_name}</span>
                  <Plus size={11} className="ml-auto text-blue-500 flex-shrink-0" />
                </button>
              ))}
              {nonMembers.length === 0 && <p className="text-xs text-gray-400 text-center py-4">All users are members</p>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ---- Main MessagingModule ----
const MessagingModule = ({ user, onUnreadChange }) => {
  const [channels, setChannels] = useState([]);
  // Initialise from any pending channel set by the header dropdown (no extra fetch needed)
  const [activeChannelId, setActiveChannelId] = useState(() => _pendingChat?.id ?? null);
  const [activeChannel, setActiveChannel] = useState(() =>
    _pendingChat ? { members: [], ..._pendingChat.channel } : null
  );
  const [allUsers, setAllUsers] = useState([]);
  const [presence, setPresence] = useState([]);
  const [search, setSearch] = useState('');
  const [myStatus, setMyStatus] = useState('online');
  const [showCreateChannel, setShowCreateChannel] = useState(false);
  const [showNewDM, setShowNewDM] = useState(false);
  const [sectionOpen, setSectionOpen] = useState({ channels: true, dms: true });

  const presenceMap = {};
  presence.forEach(p => { presenceMap[p.userId] = p.status; });

  const loadChannels = async () => {
    const data = await chatApi.get('chat/channels');
    if (data?.channels) {
      setChannels(data.channels);
      const total = (data.channels || []).reduce((s, c) => s + (c.unread_count || 0), 0);
      onUnreadChange?.(total);
    }
  };

  const loadPresence = async () => {
    const data = await chatApi.get('chat/presence');
    if (data?.presence) setPresence(data.presence);
  };

  const loadUsers = async () => {
    const res = await fetch('/api/users/directory', { headers: chatAh() });
    if (res.ok) { const d = await res.json(); setAllUsers(d.users || d || []); }
  };

  const openChannel = async (id) => {
    const data = await chatApi.get(`chat/channels/${id}`);
    if (data?.channel) {
      setActiveChannelId(data.channel.id);
      setActiveChannel(data.channel);
      chatApi.put(`chat/channels/${id}/read`);
      loadChannels();
    }
  };

  useEffect(() => {
    // Register opener so header dropdown can open a channel while module is mounted
    _openChatChannelFn = openChannel;
    // Clear any pending channel that was already consumed by useState initializer
    _pendingChat = null;

    loadChannels(); loadPresence(); loadUsers();
    chatApi.put('chat/presence', { status: 'online' });
    const iv = setInterval(() => { loadChannels(); loadPresence(); chatApi.put('chat/presence', { status: myStatus }); }, 10000);
    return () => { clearInterval(iv); _openChatChannelFn = null; };
  }, []);

  const handleCreateChannel = async (body) => {
    const result = await chatApi.post('chat/channels', body);
    if (result?.channelId) { await loadChannels(); openChannel(result.channelId); }
  };

  const handleStartDM = async (userIds) => {
    let result;
    if (userIds.length === 1) {
      result = await chatApi.post('chat/dm', { targetUserId: userIds[0] });
    } else {
      const names = userIds.map(id => allUsers.find(u => u.user_id === id)?.user_name).filter(Boolean);
      result = await chatApi.post('chat/group-dm', { name: names.join(', '), memberIds: userIds });
    }
    if (result?.channelId) { await loadChannels(); openChannel(result.channelId); }
  };

  const publicChannels = channels.filter(c => (c.type === 'public' || c.type === 'private') && (!search || c.name?.toLowerCase().includes(search.toLowerCase())));
  const dmChannels = channels.filter(c => c.type === 'dm' || c.type === 'group_dm');

  const SidebarItem = ({ ch }) => {
    const isDM = ch.type === 'dm' || ch.type === 'group_dm';
    const isActive = ch.id === activeChannelId;
    const unread = ch.unread_count || 0;
    const label = isDM ? (ch.name || ch.last_sender || 'DM') : ch.name;
    return (
      <button onClick={() => openChannel(ch.id)}
        className={`w-full flex items-center gap-2 px-3 py-1.5 rounded-md text-left transition-colors ${isActive ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-700 hover:text-white'}`}>
        <span className={`text-sm flex-shrink-0 ${isActive ? 'text-blue-200' : 'text-gray-500'}`}>
          {isDM ? '@' : ch.type === 'private' ? '🔒' : '#'}
        </span>
        <span className={`flex-1 truncate text-xs ${unread > 0 && !isActive ? 'font-bold text-white' : 'font-medium'}`}>{label}</span>
        {unread > 0 && !isActive && (
          <span className="flex-shrink-0 min-w-[18px] h-[18px] bg-blue-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1">{unread > 99 ? '99+' : unread}</span>
        )}
      </button>
    );
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <div className="flex bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" style={{ height: 'calc(100vh - 160px)' }}>

        {/* ── Sidebar ── */}
        <div className="w-56 bg-gray-900 flex flex-col flex-shrink-0">
          {/* Workspace header */}
          <div className="px-4 py-3.5 border-b border-gray-700 flex items-center justify-between flex-shrink-0">
            <div>
              <p className="text-white font-bold text-sm">Orbit</p>
              <p className="text-gray-400 text-[11px]">Workspace</p>
            </div>
            <div className="relative group">
              <button className="flex items-center gap-1 text-xs text-gray-400 hover:text-white p-1 rounded">
                <span className={`w-2 h-2 rounded-full ${presenceDot(myStatus)}`} />
                <ChevronDown size={10} />
              </button>
              <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl z-30 py-1 w-36 hidden group-hover:block">
                {[['online','🟢','Online'],['away','🟡','Away'],['offline','⚫','Offline']].map(([s,em,lbl]) => (
                  <button key={s} onClick={() => { setMyStatus(s); chatApi.put('chat/presence', { status: s }); }}
                    className={`w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 flex items-center gap-2 ${myStatus === s ? 'font-bold text-blue-600' : 'text-gray-700'}`}>
                    {em} {lbl}
                  </button>
                ))}
              </div>
            </div>
          </div>
          {/* Search */}
          <div className="px-3 py-2 flex-shrink-0">
            <div className="relative">
              <Search size={11} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-500" />
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search"
                className="w-full pl-7 pr-2 py-1.5 bg-gray-800 text-gray-200 text-xs rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 placeholder-gray-500" />
            </div>
          </div>
          {/* Nav */}
          <div className="flex-1 overflow-y-auto px-2 py-1 space-y-0.5">
            {/* Channels */}
            <div className="flex items-center justify-between px-1 py-1.5">
              <button onClick={() => setSectionOpen(s => ({ ...s, channels: !s.channels }))}
                className="flex items-center gap-1 text-gray-400 hover:text-white text-[11px] font-semibold uppercase tracking-wider">
                <ChevronDown size={11} className={`transition-transform ${sectionOpen.channels ? '' : '-rotate-90'}`} />
                Channels
              </button>
              <button onClick={() => setShowCreateChannel(true)} className="text-gray-500 hover:text-white p-0.5 rounded"><Plus size={13} /></button>
            </div>
            {sectionOpen.channels && publicChannels.map(ch => <SidebarItem key={ch.id} ch={ch} />)}

            <div className="flex items-center justify-between px-1 py-1.5 mt-1">
              <button onClick={() => setSectionOpen(s => ({ ...s, dms: !s.dms }))}
                className="flex items-center gap-1 text-gray-400 hover:text-white text-[11px] font-semibold uppercase tracking-wider">
                <ChevronDown size={11} className={`transition-transform ${sectionOpen.dms ? '' : '-rotate-90'}`} />
                Direct Messages
              </button>
              <button onClick={() => setShowNewDM(true)} className="text-gray-500 hover:text-white p-0.5 rounded"><Plus size={13} /></button>
            </div>
            {sectionOpen.dms && dmChannels.map(ch => <SidebarItem key={ch.id} ch={ch} />)}
          </div>
          {/* Footer */}
          <div className="px-3 py-3 border-t border-gray-700 flex items-center gap-2 flex-shrink-0">
            <div className="relative flex-shrink-0">
              <div className="w-7 h-7 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs font-bold">
                {(user?.name || user?.user_name || 'U').charAt(0).toUpperCase()}
              </div>
              <span className={`absolute bottom-0 right-0 w-2 h-2 rounded-full border border-gray-900 ${presenceDot(myStatus)}`} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white text-xs font-semibold truncate">{user?.name || user?.user_name}</p>
              <p className="text-gray-400 text-[10px]">{presenceLabel(myStatus)}</p>
            </div>
          </div>
        </div>

        {/* ── Main area ── */}
        {activeChannel ? (
          <ChatChannelView key={activeChannel.id} channel={activeChannel} currentUser={user}
            presenceMap={presenceMap} allUsers={allUsers} onChannelUpdated={loadChannels} />
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center bg-gray-50 text-gray-400 gap-4">
            <MessageSquare size={48} className="text-gray-200" />
            <div className="text-center">
              <p className="text-sm font-semibold text-gray-600">Welcome to Orbit Messages</p>
              <p className="text-xs text-gray-400 mt-1">Select a channel or start a direct message</p>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setShowCreateChannel(true)}
                className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors">
                <Plus size={14} /> New Channel
              </button>
              <button onClick={() => setShowNewDM(true)}
                className="flex items-center gap-1.5 px-4 py-2 bg-white border border-gray-200 text-gray-700 text-sm rounded-lg hover:bg-gray-50 transition-colors">
                <MessageSquare size={14} /> Direct Message
              </button>
            </div>
          </div>
        )}
      </div>

      {showCreateChannel && <CreateChannelModal onClose={() => setShowCreateChannel(false)} onCreate={handleCreateChannel} allUsers={allUsers} currentUserId={user?.user_id} />}
      {showNewDM && <NewDMModal onClose={() => setShowNewDM(false)} onStart={handleStartDM} allUsers={allUsers} currentUserId={user?.user_id} />}
    </div>
  );
};

const Header = ({ user, onMenuClick, onLogout, onChangePassword, notifications, unreadCount, onMarkRead, onMarkAllRead, onDismiss, unreadMessages, msgConversations, onOpenMessage, onToggleMessages, onNavigate, onGoTo }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [msgOpen, setMsgOpen] = useState(false);
  const [chatChannels, setChatChannels] = useState([]);
  const [chatLoading, setChatLoading] = useState(false);
  const initials = (user?.name || 'U').charAt(0).toUpperCase();

  useEffect(() => {
    if (!msgOpen) return;
    setChatLoading(true);
    chatApi.get('chat/channels').then(data => {
      setChatChannels(Array.isArray(data) ? data : (data?.channels || []));
      setChatLoading(false);
    });
  }, [msgOpen]);

  const fmtTime = (ts) => {
    try {
      const d = new Date(ts.endsWith('Z') ? ts : ts + 'Z');
      const diff = Date.now() - d.getTime();
      if (diff < 60000) return 'just now';
      if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
      if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } catch { return ''; }
  };

  return (
    <div className="bg-blue-600 text-white shadow-md relative z-40">
      <div className="max-w-7xl mx-auto px-4 py-3.5 flex justify-between items-center">
        <div className="flex items-center gap-4">
          <button onClick={onMenuClick} className="lg:hidden p-1">
            <Menu size={22} />
          </button>
          <div className="text-xl font-bold tracking-wide">🛰️ ORBIT</div>
        </div>

        <div className="flex items-center gap-2">
          {/* Messages Icon + Dropdown */}
          <div className="relative">
            <button
              onClick={() => { setMsgOpen(o => !o); setNotifOpen(false); setMenuOpen(false); }}
              className="relative p-2 rounded-lg hover:bg-blue-500 transition-colors"
              title="Messages"
            >
              <MessageSquare size={20} />
              {unreadMessages > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-green-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1 leading-none">
                  {unreadMessages > 99 ? '99+' : unreadMessages}
                </span>
              )}
            </button>

            {msgOpen && (
              <>
                <div className="fixed inset-0" onClick={() => setMsgOpen(false)} />
                <div className="absolute right-0 top-full mt-2 w-96 bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden z-50">
                  <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-100">
                    <div className="flex items-center gap-2">
                      <MessageSquare size={15} className="text-gray-500" />
                      <span className="font-semibold text-gray-800 text-sm">Messages</span>
                      {unreadMessages > 0 && (
                        <span className="px-1.5 py-0.5 bg-green-100 text-green-700 text-xs font-semibold rounded-full">{unreadMessages} new</span>
                      )}
                    </div>
                    <button onClick={() => { setMsgOpen(false); onGoTo?.('messages'); }} className="text-xs text-blue-600 hover:text-blue-800 font-medium">
                      Open Messages
                    </button>
                  </div>
                  <div className="max-h-96 overflow-y-auto divide-y divide-gray-50">
                    {chatLoading ? (
                      <div className="px-4 py-8 text-center text-sm text-gray-400">Loading…</div>
                    ) : chatChannels.length === 0 ? (
                      <div className="px-4 py-8 text-center text-sm text-gray-400">
                        <MessageSquare size={24} className="mx-auto mb-2 text-gray-300" />
                        No conversations yet
                      </div>
                    ) : chatChannels.map(ch => {
                      const isDm = ch.type === 'dm' || ch.type === 'group_dm';
                      const icon = isDm ? '💬' : ch.type === 'private' ? '🔒' : '#';
                      const hasUnread = (ch.unread_count || 0) > 0;
                      return (
                        <div
                          key={ch.id}
                          className={`group flex gap-3 px-4 py-3 transition-colors cursor-pointer ${hasUnread ? 'bg-blue-50 hover:bg-blue-100' : 'hover:bg-gray-50'}`}
                          onClick={() => { setMsgOpen(false); onGoTo?.('messages', ch.id, ch); }}
                        >
                          <div className="flex-shrink-0 text-lg leading-none mt-0.5">{icon}</div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className={`text-sm font-semibold truncate ${hasUnread ? 'text-gray-900' : 'text-gray-600'}`}>{ch.name}</p>
                              {hasUnread && (
                                <span className="flex-shrink-0 min-w-[18px] h-[18px] bg-green-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1">{ch.unread_count > 99 ? '99+' : ch.unread_count}</span>
                              )}
                            </div>
                            {ch.last_message && <p className="text-xs text-gray-500 mt-0.5 leading-snug truncate">{ch.last_message}</p>}
                            {ch.last_message_at && <p className="text-[11px] text-gray-400 mt-1">{fmtTime(ch.last_message_at)}</p>}
                          </div>
                          <div className="flex-shrink-0 self-start">
                            <span className="opacity-0 group-hover:opacity-100 transition-opacity text-xs text-blue-500 font-medium whitespace-nowrap">Open →</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Notification Bell */}
          <div className="relative">
            <button
              onClick={() => { setNotifOpen(o => !o); setMenuOpen(false); }}
              className="relative p-2 rounded-lg hover:bg-blue-500 transition-colors"
              title="Notifications"
            >
              <Bell size={20} />
              {unreadCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1 leading-none">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </button>

            {notifOpen && (
              <>
                <div className="fixed inset-0" onClick={() => setNotifOpen(false)} />
                <div className="absolute right-0 top-full mt-2 w-96 bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden z-50">
                  <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-100">
                    <div className="flex items-center gap-2">
                      <Bell size={15} className="text-gray-500" />
                      <span className="font-semibold text-gray-800 text-sm">Notifications</span>
                      {unreadCount > 0 && (
                        <span className="px-1.5 py-0.5 bg-red-100 text-red-600 text-xs font-semibold rounded-full">{unreadCount} new</span>
                      )}
                    </div>
                    {unreadCount > 0 && (
                      <button onClick={() => { onMarkAllRead?.(); }} className="text-xs text-blue-600 hover:text-blue-800 font-medium">Mark all read</button>
                    )}
                  </div>

                  <div className="max-h-96 overflow-y-auto divide-y divide-gray-50">
                    {notifications.length === 0 ? (
                      <div className="px-4 py-8 text-center text-sm text-gray-400">
                        <Bell size={24} className="mx-auto mb-2 text-gray-300" />
                        No notifications yet
                      </div>
                    ) : (
                      notifications.map(n => {
                        const style = NOTIF_ICONS[n.type] || { icon: '🔔', color: 'text-gray-500' };
                        const isNavigable = !!(n.entityType && n.entityId);
                        const handleClick = () => {
                          if (!n.isRead) onMarkRead?.(n.id);
                          if (isNavigable && onNavigate) { setNotifOpen(false); onNavigate(n); }
                        };
                        return (
                          <div
                            key={n.id}
                            className={`group flex gap-3 px-4 py-3 transition-colors cursor-pointer ${!n.isRead ? 'bg-blue-50 hover:bg-blue-100' : 'hover:bg-gray-50'}`}
                            onClick={handleClick}
                            title={isNavigable ? 'Click to view' : undefined}
                          >
                            <div className="flex-shrink-0 text-lg leading-none mt-0.5">{style.icon}</div>
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm font-semibold ${!n.isRead ? 'text-gray-900' : 'text-gray-600'}`}>{n.title}</p>
                              <p className="text-xs text-gray-500 mt-0.5 leading-snug">{n.message}</p>
                              <p className="text-[11px] text-gray-400 mt-1">{fmtTime(n.createdAt)}</p>
                            </div>
                            <div className="flex-shrink-0 flex flex-col items-end gap-1 self-start">
                              {isNavigable && (
                                <span className="opacity-0 group-hover:opacity-100 transition-opacity text-xs text-blue-500 font-medium whitespace-nowrap">View →</span>
                              )}
                              <button
                                onClick={e => { e.stopPropagation(); onDismiss?.(n.id); }}
                                className="p-1 text-gray-300 hover:text-gray-500 rounded"
                                title="Dismiss"
                              >
                                <X size={12} />
                              </button>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              </>
            )}
          </div>

          {/* User Menu */}
          <div className="relative">
            <button
              onClick={() => { setMenuOpen(o => !o); setNotifOpen(false); }}
              className="flex items-center gap-2.5 hover:bg-blue-500 rounded-lg px-2 py-1.5 transition-colors"
            >
              <span className="hidden sm:inline text-sm opacity-90">{user?.name}</span>
              {user?.avatar ? (
                <img src={user.avatar} alt={user?.name} className="w-8 h-8 rounded-full object-cover border-2 border-blue-300" />
              ) : (
                <div className="w-8 h-8 rounded-full bg-blue-400 flex items-center justify-center font-bold text-sm border-2 border-blue-300">
                  {initials}
                </div>
              )}
            </button>

            {menuOpen && (
              <>
                <div className="fixed inset-0" onClick={() => setMenuOpen(false)} />
                <div className="absolute right-0 top-full mt-1 bg-white rounded-xl shadow-xl border border-gray-100 min-w-[200px] overflow-hidden z-50">
                  <div className="px-4 py-3 bg-gray-50 border-b border-gray-100">
                    <p className="font-semibold text-gray-800 text-sm">{user?.name}</p>
                    <p className="text-xs text-gray-500 capitalize mt-0.5">{user?.role || 'User'}</p>
                    {user?.email && <p className="text-xs text-gray-400 mt-0.5 truncate">{user.email}</p>}
                  </div>
                  <div className="py-1">
                    <button
                      onClick={() => { setMenuOpen(false); onChangePassword?.(); }}
                      className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2.5"
                    >
                      <Shield size={14} className="text-gray-400" /> Change Password
                    </button>
                    <div className="my-1 border-t border-gray-100" />
                    <button
                      onClick={() => { setMenuOpen(false); onLogout?.(); }}
                      className="w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2.5"
                    >
                      <X size={14} /> Sign Out
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// RISK REGISTER MODULE
// ============================================================================

// ── Confirm Dialog (singleton) ──────────────────────────────────────────────
const _confirmRef = { show: null };
const requestConfirm = (message, onConfirm) => _confirmRef.show?.(message, onConfirm);

const ConfirmDialog = () => {
  const [state, setState] = useState(null);
  _confirmRef.show = (message, onConfirm) => setState({ message, onConfirm });
  if (!state) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-[9999] p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm">
        <div className="p-6">
          <div className="flex items-start gap-3 mb-5">
            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center shrink-0">
              <Trash2 size={18} className="text-red-600" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-gray-900 mb-1">Confirm Deletion</h3>
              <p className="text-sm text-gray-500">{state.message}</p>
            </div>
          </div>
          <div className="flex gap-3 justify-end">
            <button onClick={() => setState(null)}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50">
              Cancel
            </button>
            <button onClick={() => { state.onConfirm(); setState(null); }}
              className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700">
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
// ────────────────────────────────────────────────────────────────────────────

const RISK_CATEGORIES = ['technical', 'resource', 'schedule', 'budget', 'external', 'security', 'compliance'];
const RISK_LEVELS = ['low', 'medium', 'high'];
const RISK_STATUSES = ['open', 'mitigated', 'accepted', 'closed'];

const riskScoreColor = (score) => {
  if (score >= 6) return { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-300', label: 'High', dot: 'bg-red-500' };
  if (score >= 3) return { bg: 'bg-yellow-100', text: 'text-yellow-700', border: 'border-yellow-300', label: 'Medium', dot: 'bg-yellow-500' };
  return { bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-300', label: 'Low', dot: 'bg-green-500' };
};

const statusColor = (status) => {
  const map = {
    open: 'bg-blue-100 text-blue-700',
    mitigated: 'bg-green-100 text-green-700',
    accepted: 'bg-yellow-100 text-yellow-700',
    closed: 'bg-gray-100 text-gray-600',
  };
  return map[status] || 'bg-gray-100 text-gray-600';
};

const RiskFormModal = ({ risk, onSave, onClose }) => {
  const [form, setForm] = useState({
    title: risk?.title || '',
    description: risk?.description || '',
    category: risk?.category || 'technical',
    likelihood: risk?.likelihood || 'medium',
    impact: risk?.impact || 'medium',
    status: risk?.status || 'open',
    owner: risk?.owner || '',
    mitigation_plan: risk?.mitigation_plan || '',
    contingency_plan: risk?.contingency_plan || '',
    due_date: risk?.due_date || '',
  });
  const [saving, setSaving] = useState(false);

  const score = { low: 1, medium: 2, high: 3 };
  const previewScore = (score[form.likelihood] || 2) * (score[form.impact] || 2);
  const previewColor = riskScoreColor(previewScore);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    setSaving(true);
    await onSave(form);
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-lg font-semibold text-gray-900">{risk ? 'Edit Risk' : 'Add New Risk'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
            <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" required />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <MiniWysiwyg value={form.description} onChange={v => setForm(f => ({ ...f, description: v }))} placeholder="Describe the risk…" minHeight="60px" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                {RISK_CATEGORIES.map(c => <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
                className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                {RISK_STATUSES.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Likelihood</label>
              <select value={form.likelihood} onChange={e => setForm(f => ({ ...f, likelihood: e.target.value }))}
                className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                {RISK_LEVELS.map(l => <option key={l} value={l}>{l.charAt(0).toUpperCase() + l.slice(1)}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Impact</label>
              <select value={form.impact} onChange={e => setForm(f => ({ ...f, impact: e.target.value }))}
                className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                {RISK_LEVELS.map(l => <option key={l} value={l}>{l.charAt(0).toUpperCase() + l.slice(1)}</option>)}
              </select>
            </div>
          </div>
          <div className={`flex items-center gap-3 p-3 rounded-lg border ${previewColor.bg} ${previewColor.border}`}>
            <span className={`w-2.5 h-2.5 rounded-full ${previewColor.dot}`} />
            <span className={`text-sm font-medium ${previewColor.text}`}>Risk Score: {previewScore} — {previewColor.label} Risk</span>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Owner</label>
              <input value={form.owner} onChange={e => setForm(f => ({ ...f, owner: e.target.value }))}
                className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
              <input type="date" value={form.due_date} onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))}
                className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Mitigation Plan</label>
            <MiniWysiwyg value={form.mitigation_plan} onChange={v => setForm(f => ({ ...f, mitigation_plan: v }))} placeholder="How will this risk be mitigated?" minHeight="60px" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Contingency Plan</label>
            <MiniWysiwyg value={form.contingency_plan} onChange={v => setForm(f => ({ ...f, contingency_plan: v }))} placeholder="What's the fallback if mitigation fails?" minHeight="60px" />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 border rounded-lg hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={saving} className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-60">
              {saving ? 'Saving…' : risk ? 'Update Risk' : 'Add Risk'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const RiskHeatmap = ({ risks }) => {
  const levels = ['low', 'medium', 'high'];
  const grid = {};
  levels.forEach(l => levels.forEach(i => { grid[`${l}-${i}`] = []; }));
  risks.forEach(r => {
    const key = `${r.likelihood}-${r.impact}`;
    if (grid[key]) grid[key].push(r);
  });

  const cellColor = (l, i) => {
    const score = { low: 1, medium: 2, high: 3 };
    const s = score[l] * score[i];
    if (s >= 6) return 'bg-red-100 border-red-200';
    if (s >= 3) return 'bg-yellow-50 border-yellow-200';
    return 'bg-green-50 border-green-200';
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <h3 className="text-sm font-semibold text-gray-700 mb-4">Risk Heatmap</h3>
      <div className="flex gap-2">
        <div className="flex flex-col justify-around pr-2 text-xs text-gray-500 font-medium" style={{ width: 64 }}>
          {['High', 'Medium', 'Low'].map(l => <div key={l} className="text-right">{l}</div>)}
        </div>
        <div className="flex-1">
          <div className="grid grid-cols-3 gap-1.5">
            {['high', 'medium', 'low'].flatMap(likelihood =>
              ['low', 'medium', 'high'].map(impact => {
                const items = grid[`${likelihood}-${impact}`] || [];
                return (
                  <div key={`${likelihood}-${impact}`} className={`border rounded-lg p-2 min-h-[64px] ${cellColor(likelihood, impact)}`}>
                    {items.length > 0
                      ? items.map(r => (
                          <div key={r.id} className="text-xs text-gray-700 truncate mb-0.5" title={r.title}>• {r.title}</div>
                        ))
                      : <div className="text-xs text-gray-300 text-center mt-2">—</div>
                    }
                  </div>
                );
              })
            )}
          </div>
          <div className="grid grid-cols-3 gap-1.5 mt-1.5">
            {['Low', 'Medium', 'High'].map(l => (
              <div key={l} className="text-center text-xs text-gray-500 font-medium">{l}</div>
            ))}
          </div>
          <div className="text-center text-xs text-gray-400 mt-1">Impact →</div>
        </div>
      </div>
      <div className="mt-3 flex items-center gap-1 text-xs text-gray-400">← Likelihood</div>
    </div>
  );
};

// ============================================================================
// PROJECT CHARTER
// ============================================================================

const emptyCharter = () => ({
  department: '',
  project_purpose: '',
  biz_summary: '', biz_problem: '', biz_solution: '',
  benefits_qty: '', benefits_qlty: '',
  deliverables: ['', ''],
  strategic_alignment: '',
  exclusions: ['', ''],
  objectives: ['', ''],
  start_date: '',
  update_frequency: '',
  total_budget: 'No external budget allocated; project will utilize existing employee time and resources',
  budget_personnel: '', budget_hardware: '', budget_software: '', budget_services: '',
  sponsor: '',
  stakeholder_register: '',
  risk_biz: [{ risk: '', level: 'High' }, { risk: '', level: 'Medium' }],
  mitigating_strategies: '',
  risk_tech: [{ risk: '', level: 'Medium' }],
  risk_register: '',
  team_business: [
    { name: '', role: '', responsibilities: '' },
    { name: '', role: '', responsibilities: '' },
    { name: '', role: '', responsibilities: '' },
  ],
  team_it: [
    { name: 'Anthony Zoblescin', role: 'IT Director', responsibilities: 'Solution Architect' },
    { name: 'Gian Halili', role: 'IT Project Manager', responsibilities: 'Project Manager' },
    { name: 'Karen Jimenez', role: 'DevOps Manager', responsibilities: 'Lead Developer' },
    { name: 'Richard Estrelles', role: 'DataOps Manager', responsibilities: 'Data Architect' },
    { name: 'Rommel Acebedo', role: 'Database Administrator', responsibilities: 'Data Architect' },
    { name: 'Mary Joy Colas', role: 'BI Analyst', responsibilities: 'Data Architect' },
    { name: 'Jerald Tembrevilla', role: 'Fullstack Developer', responsibilities: 'Software Developer' },
    { name: 'Kamil Corpuz', role: 'Fullstack Developer', responsibilities: 'Software Developer' },
  ],
  steering: [
    { name: '', title: '', approval_date: '' },
    { name: '', title: '', approval_date: '' },
    { name: '', title: '', approval_date: '' },
  ],
  tech_infrastructure: 'Existing hosting environment',
  tech_software: '', tech_tools: '',
  int_deps: ['', ''],
  ext_deps: ['', ''],
  implementation_rec: '',
});

const ENGAGEMENT_STYLES = {
  unaware:    { bg: '#f3f4f6', color: '#6b7280', label: 'Unaware' },
  resistant:  { bg: '#fee2e2', color: '#dc2626', label: 'Resistant' },
  neutral:    { bg: '#fef9c3', color: '#ca8a04', label: 'Neutral' },
  supportive: { bg: '#dcfce7', color: '#16a34a', label: 'Supportive' },
  leading:    { bg: '#dbeafe', color: '#2563eb', label: 'Leading' },
};
const INFLUENCE_LABELS = { low: 'Low', medium: 'Medium', high: 'High' };
const INTEREST_LABELS  = { low: 'Low', medium: 'Medium', high: 'High' };
const FREQ_LABELS = { daily: 'Daily', weekly: 'Weekly', biweekly: 'Bi-weekly', monthly: 'Monthly', quarterly: 'Quarterly', asneeded: 'As Needed' };

const EMPTY_STAKEHOLDER = { name: '', title: '', organization: '', email: '', role: '', interest: 'medium', influence: 'medium', engagement: 'neutral', communicationFrequency: 'monthly', notes: '' };

const StakeholderFormModal = ({ initial = EMPTY_STAKEHOLDER, onClose, onSave }) => {
  const [form, setForm] = useState({ ...EMPTY_STAKEHOLDER, ...initial });
  const f = (k, v) => setForm(s => ({ ...s, [k]: v }));
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 sticky top-0 bg-white">
          <h2 className="text-lg font-bold text-gray-900">{initial.id ? 'Edit Stakeholder' : 'Add Stakeholder'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl font-bold">&times;</button>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
              <input value={form.name} onChange={e => f('name', e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Title / Position</label>
              <input value={form.title} onChange={e => f('title', e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Organization</label>
              <input value={form.organization} onChange={e => f('organization', e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input type="email" value={form.email} onChange={e => f('email', e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Role / Category</label>
              <input value={form.role} onChange={e => f('role', e.target.value)} placeholder="e.g. Sponsor, End User, Regulator…" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Interest Level</label>
              <select value={form.interest} onChange={e => f('interest', e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400">
                <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Influence / Power</label>
              <select value={form.influence} onChange={e => f('influence', e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400">
                <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Engagement</label>
              <select value={form.engagement} onChange={e => f('engagement', e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400">
                {Object.entries(ENGAGEMENT_STYLES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Communication Frequency</label>
            <select value={form.communicationFrequency} onChange={e => f('communicationFrequency', e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400">
              {Object.entries(FREQ_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes / Concerns</label>
            <MiniWysiwyg value={form.notes} onChange={v => f('notes', v)} placeholder="Add notes or concerns…" minHeight="80px" />
          </div>
        </div>
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 sticky bottom-0 bg-white">
          <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</button>
          <button onClick={() => { if (!form.name.trim()) return; onSave(form); }} className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700">{initial.id ? 'Save Changes' : 'Add Stakeholder'}</button>
        </div>
      </div>
    </div>
  );
};

const StakeholderRegisterTab = ({ projectId, user }) => {
  const [stakeholders, setStakeholders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [view, setView] = useState('list'); // 'list' | 'grid'

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`/api/plantrack/projects/${projectId}/stakeholders`);
      const data = await r.json();
      setStakeholders(Array.isArray(data) ? data : []);
    } catch { setStakeholders([]); }
    setLoading(false);
  };
  useEffect(() => { load(); }, [projectId]);

  const handleSave = async (form) => {
    if (editing) {
      await fetch(`/api/plantrack/projects/${projectId}/stakeholders/${editing.id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json', ...BaseModel.authHeaders() }, body: JSON.stringify(form) });
    } else {
      await fetch(`/api/plantrack/projects/${projectId}/stakeholders`, { method: 'POST', headers: { 'Content-Type': 'application/json', ...BaseModel.authHeaders() }, body: JSON.stringify(form) });
    }
    setShowForm(false); setEditing(null); load();
  };
  const handleDelete = async (id) => {
    await fetch(`/api/plantrack/projects/${projectId}/stakeholders/${id}`, { method: 'DELETE', headers: BaseModel.authHeaders() });
    load();
  };

  // Power/Interest grid: 3×3 (low/med/high influence × low/med/high interest)
  const gridLevels = ['high', 'medium', 'low'];
  const gridCell = (inf, int) => stakeholders.filter(s => s.influence === inf && s.interest === int);

  const gridQuadrantLabel = (inf, int) => {
    if (inf === 'high' && int === 'high') return { label: 'Manage Closely', bg: '#fef3c7', border: '#f59e0b' };
    if (inf === 'high' && int !== 'high') return { label: 'Keep Satisfied', bg: '#dbeafe', border: '#3b82f6' };
    if (inf !== 'high' && int === 'high') return { label: 'Keep Informed', bg: '#dcfce7', border: '#22c55e' };
    return { label: 'Monitor', bg: '#f3f4f6', border: '#d1d5db' };
  };

  return (
    <div>
      {(showForm || editing) && (
        <StakeholderFormModal initial={editing || EMPTY_STAKEHOLDER} onClose={() => { setShowForm(false); setEditing(null); }} onSave={handleSave} />
      )}

      <div className="flex justify-between items-center mb-5">
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-semibold text-gray-900">Stakeholder Register</h2>
          <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-xs font-medium">{stakeholders.length}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex border border-gray-200 rounded-lg overflow-hidden">
            <button onClick={() => setView('list')} className={`px-3 py-1.5 text-xs font-medium ${view === 'list' ? 'bg-teal-600 text-white' : 'text-gray-600 hover:bg-gray-50'}`}>List</button>
            <button onClick={() => setView('grid')} className={`px-3 py-1.5 text-xs font-medium ${view === 'grid' ? 'bg-teal-600 text-white' : 'text-gray-600 hover:bg-gray-50'}`}>Power/Interest Grid</button>
          </div>
          <button onClick={() => { setEditing(null); setShowForm(true); }} className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 flex items-center gap-2">
            <span className="text-lg leading-none">+</span> Add Stakeholder
          </button>
        </div>
      </div>

      {loading && <p className="text-sm text-gray-400 py-6 text-center">Loading…</p>}
      {!loading && stakeholders.length === 0 && (
        <div className="text-center py-12 border-2 border-dashed border-gray-200 rounded-xl">
          <p className="text-gray-400 text-sm mb-3">No stakeholders registered yet.</p>
          <button onClick={() => { setEditing(null); setShowForm(true); }} className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700">+ Add First Stakeholder</button>
        </div>
      )}

      {/* List View */}
      {!loading && stakeholders.length > 0 && view === 'list' && (
        <div className="overflow-x-auto rounded-xl border border-gray-200">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {['Name', 'Role', 'Interest', 'Influence', 'Engagement', 'Comm. Frequency', ''].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {stakeholders.map(s => {
                const eng = ENGAGEMENT_STYLES[s.engagement] || ENGAGEMENT_STYLES.neutral;
                return (
                  <tr key={s.id} className="hover:bg-gray-50 group">
                    <td className="px-4 py-3">
                      <p className="font-medium text-gray-900">{s.name}</p>
                      {s.title && <p className="text-xs text-gray-400">{s.title}{s.organization ? ` · ${s.organization}` : ''}</p>}
                      {s.email && <p className="text-xs text-teal-600">{s.email}</p>}
                    </td>
                    <td className="px-4 py-3 text-gray-600">{s.role || '—'}</td>
                    <td className="px-4 py-3"><span className="capitalize text-gray-700">{INTEREST_LABELS[s.interest] || s.interest}</span></td>
                    <td className="px-4 py-3"><span className="capitalize text-gray-700">{INFLUENCE_LABELS[s.influence] || s.influence}</span></td>
                    <td className="px-4 py-3">
                      <span className="px-2.5 py-1 rounded-full text-xs font-semibold" style={{ background: eng.bg, color: eng.color }}>{eng.label}</span>
                    </td>
                    <td className="px-4 py-3 text-gray-500">{FREQ_LABELS[s.communicationFrequency] || s.communicationFrequency}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button onClick={() => setEditing(s)} className="p-1.5 text-gray-400 hover:text-blue-600 rounded"><Pencil size={13} /></button>
                        <button onClick={() => requestConfirm(`Delete stakeholder "${s.name}"?`, () => handleDelete(s.id))} className="p-1.5 text-gray-400 hover:text-red-600 rounded"><Trash2 size={13} /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {/* Notes panel */}
          {stakeholders.some(s => s.notes) && (
            <div className="border-t border-gray-200 divide-y divide-gray-100">
              {stakeholders.filter(s => s.notes).map(s => (
                <div key={s.id} className="px-4 py-2.5 flex gap-3">
                  <span className="text-xs font-semibold text-gray-500 shrink-0 w-28 pt-0.5">{s.name}</span>
                  <p className="text-xs text-gray-500 prose prose-sm max-w-none [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:m-0" dangerouslySetInnerHTML={{ __html: sanitizeBugHtml(s.notes) }} />
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Power / Interest Grid */}
      {!loading && stakeholders.length > 0 && view === 'grid' && (
        <div>
          <div className="flex gap-4">
            {/* Y-axis label */}
            <div className="flex items-center">
              <span className="text-xs font-semibold text-gray-400 uppercase tracking-widest" style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>Influence / Power →</span>
            </div>
            <div className="flex-1">
              <div className="grid grid-cols-3 gap-2" style={{ gridTemplateRows: 'repeat(3, minmax(100px, auto))' }}>
                {gridLevels.map(inf => gridLevels.map(int => {
                  const cells = gridCell(inf, int);
                  const q = gridQuadrantLabel(inf, int);
                  return (
                    <div key={`${inf}-${int}`} className="rounded-lg border-2 p-3 min-h-24" style={{ background: q.bg, borderColor: q.border }}>
                      <p className="text-xs font-bold mb-2" style={{ color: q.border }}>{q.label}</p>
                      <div className="space-y-1">
                        {cells.map(s => {
                          const eng = ENGAGEMENT_STYLES[s.engagement] || ENGAGEMENT_STYLES.neutral;
                          return (
                            <div key={s.id} className="flex items-center gap-1.5 cursor-pointer" onClick={() => setEditing(s)}>
                              <span className="w-2 h-2 rounded-full shrink-0" style={{ background: eng.color }} />
                              <span className="text-xs font-medium text-gray-800 truncate">{s.name}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                }))}
              </div>
              {/* X-axis label */}
              <div className="text-center mt-2">
                <span className="text-xs font-semibold text-gray-400 uppercase tracking-widest">Interest →</span>
              </div>
              {/* Axis labels */}
              <div className="grid grid-cols-3 gap-2 mt-1 text-center">
                {['Low Interest', 'Medium Interest', 'High Interest'].map(l => <span key={l} className="text-xs text-gray-400">{l}</span>)}
              </div>
            </div>
          </div>
          {/* Legend */}
          <div className="flex flex-wrap gap-3 mt-4 pt-4 border-t border-gray-100">
            <span className="text-xs font-semibold text-gray-500 mr-1">Engagement:</span>
            {Object.entries(ENGAGEMENT_STYLES).map(([k, v]) => (
              <span key={k} className="flex items-center gap-1.5 text-xs">
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: v.color }} />
                {v.label}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const sanitizeCharterHtml = sanitizeHtml;

const CharterWysiwygField = ({ initialValue = '', onChange, placeholder = '' }) => {
  const editorRef = React.useRef(null);
  const [isEmpty, setIsEmpty] = React.useState(!initialValue);
  const mountedRef = React.useRef(false);

  React.useEffect(() => {
    if (editorRef.current && !mountedRef.current) {
      mountedRef.current = true;
      if (initialValue) { editorRef.current.innerHTML = initialValue; setIsEmpty(false); }
    }
  }, []);

  const syncValue = () => {
    const html = editorRef.current?.innerHTML || '';
    const empty = !html || html === '<br>' || html === '<div><br></div>' || html === '<p><br></p>';
    setIsEmpty(empty);
    onChange(empty ? '' : html);
  };

  const exec = (cmd) => { document.execCommand(cmd, false, null); editorRef.current?.focus(); syncValue(); };

  const handlePaste = (e) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, text);
  };

  const tbtn = (cmd, content, title) => (
    <button type="button" title={title} key={cmd} onMouseDown={e => { e.preventDefault(); exec(cmd); }}
      className="px-2 py-1 text-xs rounded hover:bg-gray-200 text-gray-700 select-none">{content}</button>
  );

  return (
    <div className="border border-gray-300 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500 bg-white">
      <div className="flex items-center gap-0.5 px-2 py-1 bg-gray-50 border-b border-gray-200 flex-wrap">
        {tbtn('bold', <strong>B</strong>, 'Bold')}
        {tbtn('italic', <em>I</em>, 'Italic')}
        {tbtn('underline', <u>U</u>, 'Underline')}
        <div className="w-px h-3.5 bg-gray-300 mx-0.5" />
        {tbtn('insertUnorderedList', '• List', 'Bullet list')}
        {tbtn('insertOrderedList', '1. List', 'Numbered list')}
        <div className="w-px h-3.5 bg-gray-300 mx-0.5" />
        {tbtn('removeFormat', 'Aa✕', 'Clear formatting')}
      </div>
      <div className="relative">
        {isEmpty && <div className="absolute top-2 left-3 text-sm text-gray-400 pointer-events-none select-none">{placeholder}</div>}
        <div ref={editorRef} contentEditable onInput={syncValue} onPaste={handlePaste}
          className="min-h-[64px] max-h-40 overflow-y-auto px-3 py-2 text-sm text-gray-800 focus:outline-none prose prose-sm max-w-none [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5"
          suppressContentEditableWarning />
      </div>
    </div>
  );
};

const ProjectCharterTab = ({ projectId, projectName, user }) => {
  const [charter, setCharter] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(emptyCharter());
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const role = user?.role || user?.user_role;
  const canEdit = ['admin', 'manager', 'project'].includes(role);

  const load = async () => {
    setLoading(true);
    const data = await ModulesModel.getProjectCharter(projectId);
    setCharter(data && Object.keys(data).length > 0 ? data : null);
    setLoading(false);
  };

  useEffect(() => { load(); }, [projectId]);

  const parseCharter = (data) => {
    if (!data) return emptyCharter();
    if (data.charter_json) {
      try {
        const p = typeof data.charter_json === 'string' ? JSON.parse(data.charter_json) : data.charter_json;
        const merged = { ...emptyCharter(), ...p };
        // Migrate old deliverables cards → simple array
        if (!p.deliverables && (p.del1_title !== undefined || p.del1_desc !== undefined)) {
          const items = [1,2,3,4].map(n => [p[`del${n}_title`], p[`del${n}_desc`], p[`del${n}_criteria`]].filter(Boolean).join(' — ')).filter(Boolean);
          merged.deliverables = items.length ? items : [''];
        }
        // Migrate old objectives cards → simple array
        if (!p.objectives && (p.obj1_title !== undefined || p.obj1_alignment !== undefined)) {
          const items = [1,2,3].map(n => [p[`obj${n}_title`], p[`obj${n}_alignment`], p[`obj${n}_metrics`]].filter(Boolean).join(' — ')).filter(Boolean);
          merged.objectives = items.length ? items : [''];
        }
        // Migrate old flat exclusion fields → array
        if (!p.exclusions && (p.exclusion_1 !== undefined || p.exclusion_2 !== undefined)) {
          merged.exclusions = [p.exclusion_1 || '', p.exclusion_2 || ''].filter(Boolean);
          if (!merged.exclusions.length) merged.exclusions = [''];
        }
        // Migrate old flat dependency fields → arrays
        if (!p.int_deps && (p.int_dep_1 !== undefined || p.int_dep_2 !== undefined)) {
          merged.int_deps = [p.int_dep_1 || '', p.int_dep_2 || ''].filter(Boolean);
          if (!merged.int_deps.length) merged.int_deps = [''];
        }
        if (!p.ext_deps && (p.ext_dep_1 !== undefined || p.ext_dep_2 !== undefined)) {
          merged.ext_deps = [p.ext_dep_1 || '', p.ext_dep_2 || ''].filter(Boolean);
          if (!merged.ext_deps.length) merged.ext_deps = [''];
        }
        return merged;
      } catch {}
    }
    // Legacy fallback
    return { ...emptyCharter(), sponsor: data.sponsor || '', biz_summary: data.executive_summary || '', start_date: data.timeline_start || '', total_budget: data.budget_estimated || emptyCharter().total_budget };
  };

  const startEdit = () => { setForm(parseCharter(charter)); setEditing(true); };

  const handleSave = async () => {
    setSaving(true);
    await ModulesModel.saveProjectCharter(projectId, { charter_json: JSON.stringify(form) });
    await load();
    setEditing(false);
    setSaving(false);
  };

  const updArr = (key, idx, field, val) => setForm(f => ({ ...f, [key]: f[key].map((r, i) => i === idx ? { ...r, [field]: val } : r) }));
  const addRow = (key, tpl) => setForm(f => ({ ...f, [key]: [...f[key], { ...tpl }] }));
  const delRow = (key, idx) => setForm(f => ({ ...f, [key]: f[key].filter((_, i) => i !== idx) }));

  const d = charter ? parseCharter(charter) : null;

  if (loading) return <div className="py-12 text-center text-gray-400 text-sm">Loading charter…</div>;

  const iCls = 'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';
  const lCls = 'block text-xs font-semibold text-gray-500 mb-1';
  const inp = (key, ph = '', type = 'text') => <input type={type} value={form[key] || ''} placeholder={ph} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} className={iCls} />;
  const ta = (key, ph = '') => <CharterWysiwygField key={`charter-${projectId}-${key}`} initialValue={form[key] || ''} onChange={v => setForm(f => ({ ...f, [key]: v }))} placeholder={ph} />;
  const lbl = (t) => <label className={lCls}>{t}</label>;
  const fld = (label, children) => <div>{lbl(label)}{children}</div>;
  const richText = (val) => val ? <div className="prose prose-sm max-w-none text-sm [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:my-0.5" dangerouslySetInnerHTML={{ __html: sanitizeCharterHtml(val) }} /> : null;

  // ── EDIT MODE ──────────────────────────────────────────────────────────────
  if (editing) return (
    <div className="space-y-6">
      <div className="flex items-center justify-between sticky top-0 bg-gray-50 z-10 py-2 border-b border-gray-200">
        <h2 className="text-lg font-semibold text-gray-900">Edit Project Charter</h2>
        <div className="flex gap-2">
          <button onClick={() => setEditing(false)} className="px-4 py-2 text-sm border rounded-lg text-gray-600 hover:bg-gray-50">Cancel</button>
          <button onClick={handleSave} disabled={saving} className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-60">{saving ? 'Saving…' : 'Save Charter'}</button>
        </div>
      </div>

      {/* Header */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-3">
        <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Header</h3>
        {fld('Department', inp('department', 'e.g. IT'))}
      </div>

      {/* Project Details */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
        <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Project Details</h3>
        {fld('Project Purpose', ta('project_purpose', 'State the purpose of this project…', 3))}
        <div className="space-y-2">
          {lbl('Business Case')}
          {fld('Executive Summary:', ta('biz_summary', '', 3))}
          {fld('Problem Statement:', ta('biz_problem', '', 2))}
          {fld('Proposed Solution:', ta('biz_solution', '', 2))}
        </div>
        <div className="grid grid-cols-2 gap-4">
          {fld('Quantitative Benefits', ta('benefits_qty', '', 2))}
          {fld('Qualitative Benefits', ta('benefits_qlty', '', 2))}
        </div>
        <div className="space-y-2">
          {lbl('Project Scope – Deliverables')}
          {(form.deliverables || []).map((item, i) => (
            <div key={i} className="flex gap-2 items-start">
              <span className="text-xs text-gray-400 w-4 flex-shrink-0 pt-2">{i + 1}.</span>
              <div className="flex-1 min-w-0">
                <CharterWysiwygField
                  key={`charter-${projectId}-deliverable-${i}`}
                  initialValue={item}
                  onChange={v => setForm(f => ({ ...f, deliverables: f.deliverables.map((val, j) => j === i ? v : val) }))}
                  placeholder="Describe this deliverable…"
                />
              </div>
              {form.deliverables.length > 1 && (
                <button type="button" onClick={() => setForm(f => ({ ...f, deliverables: f.deliverables.filter((_, j) => j !== i) }))} className="text-gray-400 hover:text-red-500 pt-2"><X size={14} /></button>
              )}
            </div>
          ))}
          <button type="button" onClick={() => setForm(f => ({ ...f, deliverables: [...f.deliverables, ''] }))}
            className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700"><Plus size={12} /> Add Deliverable</button>
        </div>
        {fld('Strategic Alignment', ta('strategic_alignment', '', 2))}
        <div className="space-y-2">
          {lbl('Out of Scope / Potential Enhancement in future')}
          {(form.exclusions || []).map((ex, i) => (
            <div key={i} className="flex gap-2 items-center">
              <span className="text-xs text-gray-400 w-4 flex-shrink-0">{i + 1}.</span>
              <input value={ex} onChange={e => setForm(f => ({ ...f, exclusions: f.exclusions.map((v, j) => j === i ? e.target.value : v) }))}
                placeholder="Exclusion or future enhancement…" className={iCls + ' flex-1'} />
              {form.exclusions.length > 1 && (
                <button type="button" onClick={() => setForm(f => ({ ...f, exclusions: f.exclusions.filter((_, j) => j !== i) }))} className="text-gray-400 hover:text-red-500"><X size={14} /></button>
              )}
            </div>
          ))}
          <button type="button" onClick={() => setForm(f => ({ ...f, exclusions: [...f.exclusions, ''] }))}
            className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700"><Plus size={12} /> Add Exclusion</button>
        </div>
        <div className="space-y-2">
          {lbl('Project Objectives')}
          {(form.objectives || []).map((item, i) => (
            <div key={i} className="flex gap-2 items-start">
              <span className="text-xs text-gray-400 w-4 flex-shrink-0 pt-2">{i + 1}.</span>
              <div className="flex-1 min-w-0">
                <CharterWysiwygField
                  key={`charter-${projectId}-objective-${i}`}
                  initialValue={item}
                  onChange={v => setForm(f => ({ ...f, objectives: f.objectives.map((val, j) => j === i ? v : val) }))}
                  placeholder="Describe this objective…"
                />
              </div>
              {form.objectives.length > 1 && (
                <button type="button" onClick={() => setForm(f => ({ ...f, objectives: f.objectives.filter((_, j) => j !== i) }))} className="text-gray-400 hover:text-red-500 pt-2"><X size={14} /></button>
              )}
            </div>
          ))}
          <button type="button" onClick={() => setForm(f => ({ ...f, objectives: [...f.objectives, ''] }))}
            className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700"><Plus size={12} /> Add Objective</button>
        </div>
        <div className="grid grid-cols-2 gap-4">
          {fld('Start Date', inp('start_date', '', 'date'))}
          {fld('Frequency of Updates', inp('update_frequency', 'e.g. Weekly'))}
        </div>
        {fld('Total Budget', inp('total_budget'))}
        <div className="space-y-2">
          {lbl('Budget Allocation')}
          <div className="grid grid-cols-2 gap-3">
            {fld('Personnel:', inp('budget_personnel'))}
            {fld('Hardware:', inp('budget_hardware'))}
            {fld('Software:', inp('budget_software'))}
            {fld('Services:', inp('budget_services'))}
          </div>
        </div>
        {fld('Project Sponsor', inp('sponsor'))}
      </div>

      {/* Team Members */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-5">
        <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Project Team Members</h3>
        {[['team_business','Business Users'],['team_it','IT & Vendor']].map(([key, title]) => (
          <div key={key}>
            <p className="text-sm font-semibold text-gray-700 mb-2">{title}</p>
            <div className="space-y-2">
              {form[key].map((m, i) => (
                <div key={i} className="grid grid-cols-3 gap-2 items-center">
                  <input value={m.name} onChange={e => updArr(key, i, 'name', e.target.value)} placeholder="Name" className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm" />
                  <input value={m.role} onChange={e => updArr(key, i, 'role', e.target.value)} placeholder="Role" className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm" />
                  <div className="flex gap-2">
                    <input value={m.responsibilities} onChange={e => updArr(key, i, 'responsibilities', e.target.value)} placeholder="Responsibilities" className="flex-1 border border-gray-300 rounded-lg px-3 py-1.5 text-sm" />
                    <button onClick={() => delRow(key, i)} className="text-gray-400 hover:text-red-500"><X size={14} /></button>
                  </div>
                </div>
              ))}
              <button onClick={() => addRow(key, { name: '', role: '', responsibilities: '' })} className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700"><Plus size={12} /> Add Member</button>
            </div>
          </div>
        ))}
      </div>

      {/* Steering Committee */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-3">
        <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Steering Committee</h3>
        {form.steering.map((m, i) => (
          <div key={i} className="grid grid-cols-3 gap-2 items-center">
            <input value={m.name} onChange={e => updArr('steering', i, 'name', e.target.value)} placeholder="Name" className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm" />
            <input value={m.title} onChange={e => updArr('steering', i, 'title', e.target.value)} placeholder="Title" className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm" />
            <div className="flex gap-2">
              <input type="date" value={m.approval_date} onChange={e => updArr('steering', i, 'approval_date', e.target.value)} className="flex-1 border border-gray-300 rounded-lg px-3 py-1.5 text-sm" />
              <button onClick={() => delRow('steering', i)} className="text-gray-400 hover:text-red-500"><X size={14} /></button>
            </div>
          </div>
        ))}
        <button onClick={() => addRow('steering', { name: '', title: '', approval_date: '' })} className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700"><Plus size={12} /> Add Member</button>
      </div>

      {/* Technical Specifications */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
        <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Technical Specifications</h3>
        <div className="space-y-2">
          {lbl('Resources')}
          {fld('Infrastructure:', inp('tech_infrastructure', 'e.g. Existing hosting environment'))}
          {fld('Software:', inp('tech_software'))}
          {fld('Tools:', inp('tech_tools'))}
        </div>
        <div className="space-y-2">
          {lbl('Dependencies')}
          <p className="text-xs font-medium text-gray-600">Internal Dependencies</p>
          {(form.int_deps || []).map((dep, i) => (
            <div key={i} className="flex gap-2 items-center">
              <span className="text-xs text-gray-400 w-4 flex-shrink-0">{i + 1}.</span>
              <input value={dep} onChange={e => setForm(f => ({ ...f, int_deps: f.int_deps.map((v, j) => j === i ? e.target.value : v) }))}
                placeholder="Internal dependency…" className={iCls + ' flex-1'} />
              {form.int_deps.length > 1 && (
                <button type="button" onClick={() => setForm(f => ({ ...f, int_deps: f.int_deps.filter((_, j) => j !== i) }))} className="text-gray-400 hover:text-red-500"><X size={14} /></button>
              )}
            </div>
          ))}
          <button type="button" onClick={() => setForm(f => ({ ...f, int_deps: [...f.int_deps, ''] }))}
            className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700"><Plus size={12} /> Add Internal Dependency</button>
          <p className="text-xs font-medium text-gray-600 mt-2">External Dependencies</p>
          {(form.ext_deps || []).map((dep, i) => (
            <div key={i} className="flex gap-2 items-center">
              <span className="text-xs text-gray-400 w-4 flex-shrink-0">{i + 1}.</span>
              <input value={dep} onChange={e => setForm(f => ({ ...f, ext_deps: f.ext_deps.map((v, j) => j === i ? e.target.value : v) }))}
                placeholder="External dependency…" className={iCls + ' flex-1'} />
              {form.ext_deps.length > 1 && (
                <button type="button" onClick={() => setForm(f => ({ ...f, ext_deps: f.ext_deps.filter((_, j) => j !== i) }))} className="text-gray-400 hover:text-red-500"><X size={14} /></button>
              )}
            </div>
          ))}
          <button type="button" onClick={() => setForm(f => ({ ...f, ext_deps: [...f.ext_deps, ''] }))}
            className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700"><Plus size={12} /> Add External Dependency</button>
        </div>
        {fld('Implementation Recommendations', ta('implementation_rec', '', 3))}
      </div>
    </div>
  );

  // ── EMPTY STATE ────────────────────────────────────────────────────────────
  if (!charter) return (
    <div className="text-center py-16 border-2 border-dashed border-gray-200 rounded-xl">
      <div className="text-4xl mb-3">📋</div>
      <p className="text-gray-500 font-medium mb-1">No charter created yet</p>
      <p className="text-sm text-gray-400 mb-4">Define the project scope, goals, team, and get stakeholder sign-off.</p>
      {canEdit && <button onClick={startEdit} className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">Create Charter</button>}
    </div>
  );

  // ── VIEW MODE ──────────────────────────────────────────────────────────────
  const DR = ({ label, children }) => (
    <div className="flex border-b border-gray-300 last:border-b-0 min-h-[2.5rem]">
      <div className="w-52 flex-shrink-0 bg-gray-100 border-r border-gray-300 px-3 py-2.5 font-bold text-sm text-gray-800 leading-tight">{label}</div>
      <div className="flex-1 px-3 py-2.5 text-sm text-gray-800">{children || <span className="text-gray-400 text-xs italic">—</span>}</div>
    </div>
  );
  const SH = ({ title }) => <div className="bg-gray-300 text-center font-bold py-2.5 text-sm text-gray-900 border-b border-gray-400">{title}</div>;
  const Tbl = ({ title, children }) => <div className="border-2 border-gray-400 rounded overflow-hidden">{title && <SH title={title} />}{children}</div>;
  const riskClr = (l) => ({ Critical: 'text-red-700 font-semibold', High: 'text-orange-600 font-semibold', Medium: 'text-yellow-700', Low: 'text-green-600' }[l] || '');

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Project Charter</h2>
          {charter.updated_at && <p className="text-xs text-gray-400 mt-0.5">Last updated {charter.updated_at.slice(0,10)}</p>}
        </div>
        {canEdit && <button onClick={startEdit} className="flex items-center gap-1.5 px-3 py-1.5 text-sm border rounded-lg text-gray-600 hover:bg-gray-50"><Pencil size={14}/> Edit</button>}
      </div>

      {/* Header Info */}
      <Tbl>
        {[['Project ID', projectId],['Project Name', projectName],['Department', d.department],['Created Date', charter.created_at?.slice(0,10)]].map(([l,v]) => (
          <div key={l} className="flex border-b border-gray-300 last:border-b-0">
            <div className="w-40 flex-shrink-0 bg-gray-100 border-r border-gray-300 px-3 py-2 font-bold text-sm text-gray-800">{l}</div>
            <div className="flex-1 px-3 py-2 text-sm text-gray-800">{v || ''}</div>
          </div>
        ))}
      </Tbl>

      {/* Project Details */}
      <Tbl title="Project Details">
        <DR label="Project Purpose">{richText(d.project_purpose)}</DR>
        <DR label="Business Case">
          {(d.biz_summary || d.biz_problem || d.biz_solution) ? <div className="space-y-2">
            {d.biz_summary && <div><span className="font-semibold">Executive Summary:</span><div className="mt-0.5">{richText(d.biz_summary)}</div></div>}
            {d.biz_problem && <div><span className="font-semibold">Problem Statement:</span><div className="mt-0.5">{richText(d.biz_problem)}</div></div>}
            {d.biz_solution && <div><span className="font-semibold">Proposed Solution:</span><div className="mt-0.5">{richText(d.biz_solution)}</div></div>}
          </div> : null}
        </DR>
        <DR label="Expected Benefits">
          {(d.benefits_qty || d.benefits_qlty) ? <div className="space-y-2">
            {d.benefits_qty && <div><p className="font-semibold">Quantitative Benefits</p><div className="mt-0.5">{richText(d.benefits_qty)}</div></div>}
            {d.benefits_qlty && <div><p className="font-semibold">Qualitative Benefits</p><div className="mt-0.5">{richText(d.benefits_qlty)}</div></div>}
          </div> : null}
        </DR>
        <DR label="Project Scope – Deliverables">
          {(d.deliverables || []).filter(Boolean).length ? (
            <ol className="space-y-1 list-none">
              {(d.deliverables || []).filter(Boolean).map((item, i) => (
                <li key={i} className="flex gap-2">
                  <span className="text-gray-400 flex-shrink-0">{i + 1}.</span>
                  <div className="flex-1 min-w-0">{richText(item)}</div>
                </li>
              ))}
            </ol>
          ) : null}
        </DR>
        <DR label="Strategic Alignment">{richText(d.strategic_alignment)}</DR>
        <DR label={<>Out of Scope<br/><span className="font-normal text-xs">(Potential Enhancement in future)</span></>}>
          {(d.exclusions || []).filter(Boolean).length ? (
            <ol className="space-y-0.5 list-none">
              {(d.exclusions || []).filter(Boolean).map((ex, i) => <li key={i}>{i + 1}. {ex}</li>)}
            </ol>
          ) : null}
        </DR>
        <DR label="Project Objectives">
          {(d.objectives || []).filter(Boolean).length ? (
            <ol className="space-y-1 list-none">
              {(d.objectives || []).filter(Boolean).map((item, i) => (
                <li key={i} className="flex gap-2">
                  <span className="text-gray-400 flex-shrink-0">{i + 1}.</span>
                  <div className="flex-1 min-w-0">{richText(item)}</div>
                </li>
              ))}
            </ol>
          ) : null}
        </DR>
        <DR label="Start date">{d.start_date}</DR>
        <DR label="Frequency of updates">{d.update_frequency}</DR>
        <DR label="Total Budget">{d.total_budget}</DR>
        <DR label="Budget allocation">
          {(d.budget_personnel || d.budget_hardware || d.budget_software || d.budget_services) ? <div>
            {d.budget_personnel && <p><span className="font-semibold">Personnel:</span> {d.budget_personnel}</p>}
            {d.budget_hardware && <p><span className="font-semibold">Hardware:</span> {d.budget_hardware}</p>}
            {d.budget_software && <p><span className="font-semibold">Software:</span> {d.budget_software}</p>}
            {d.budget_services && <p><span className="font-semibold">Services:</span> {d.budget_services}</p>}
          </div> : null}
        </DR>
        <DR label="Project Sponsor">{d.sponsor}</DR>
      </Tbl>

      {/* Project Team Members */}
      <Tbl title="Project Team Members">
        {[['team_business','Project Team - Business Users'],['team_it','Project Team - IT & Vendor']].map(([key, rowLabel]) => (
          <React.Fragment key={key}>
            <div className="grid grid-cols-[180px_1fr_1fr_1fr] bg-gray-100 border-b border-gray-300">
              {[rowLabel,'Name','Role','Responsibilities'].map((h, i) => (
                <div key={i} className={`px-3 py-2 font-bold text-sm ${i < 3 ? 'border-r border-gray-300' : ''}`}>{h}</div>
              ))}
            </div>
            {(d[key] || []).map((m, i) => (
              <div key={i} className="grid grid-cols-[180px_1fr_1fr_1fr] border-b border-gray-200 last:border-b-0">
                <div className="px-3 py-2 border-r border-gray-200 bg-gray-50"></div>
                <div className="px-3 py-2 text-sm border-r border-gray-200">{m.name}</div>
                <div className="px-3 py-2 text-sm border-r border-gray-200">{m.role}</div>
                <div className="px-3 py-2 text-sm">{m.responsibilities}</div>
              </div>
            ))}
          </React.Fragment>
        ))}
      </Tbl>

      {/* Steering Committee */}
      <Tbl>
        <div className="grid grid-cols-[180px_1fr_1fr_1fr] bg-gray-100 border-b border-gray-300">
          {['Steering Committee','Name','Title','Approval Date'].map((h, i) => (
            <div key={i} className={`px-3 py-2 font-bold text-sm ${i < 3 ? 'border-r border-gray-300' : ''}`}>{h}</div>
          ))}
        </div>
        {(d.steering || []).map((m, i) => (
          <div key={i} className="grid grid-cols-[180px_1fr_1fr_1fr] border-b border-gray-200 last:border-b-0">
            <div className="px-3 py-2 border-r border-gray-200 bg-gray-50"></div>
            <div className="px-3 py-2 text-sm border-r border-gray-200">{m.name}</div>
            <div className="px-3 py-2 text-sm border-r border-gray-200">{m.title}</div>
            <div className="px-3 py-2 text-sm">{m.approval_date}</div>
          </div>
        ))}
      </Tbl>

      {/* Technical Specifications */}
      <Tbl title="Technical Specifications">
        <DR label="Resources">
          <div>
            <p><span className="font-semibold">Infrastructure:</span> {d.tech_infrastructure}</p>
            {d.tech_software && <p><span className="font-semibold">Software:</span> {d.tech_software}</p>}
            {d.tech_tools && <p><span className="font-semibold">Tools:</span> {d.tech_tools}</p>}
          </div>
        </DR>
        <DR label="Dependencies">
          <div className="space-y-2">
            {(d.int_deps || []).filter(Boolean).length > 0 && (
              <div>
                <p className="font-semibold">Internal Dependencies</p>
                <ol className="space-y-0.5 list-none mt-0.5">
                  {(d.int_deps || []).filter(Boolean).map((dep, i) => <li key={i}>{i + 1}. {dep}</li>)}
                </ol>
              </div>
            )}
            {(d.ext_deps || []).filter(Boolean).length > 0 && (
              <div>
                <p className="font-semibold">External Dependencies</p>
                <ol className="space-y-0.5 list-none mt-0.5">
                  {(d.ext_deps || []).filter(Boolean).map((dep, i) => <li key={i}>{i + 1}. {dep}</li>)}
                </ol>
              </div>
            )}
          </div>
        </DR>
        <DR label="Implementation Recommendations">{richText(d.implementation_rec)}</DR>
      </Tbl>
    </div>
  );
};

const ProjectRisksTab = ({ projectId, user }) => {
  const [risks, setRisks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('table');
  const [showForm, setShowForm] = useState(false);
  const [editingRisk, setEditingRisk] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');

  const load = async () => {
    setLoading(true);
    const data = await ModulesModel.getRisks(projectId);
    setRisks(data.risks || []);
    setLoading(false);
  };

  useEffect(() => { load(); }, [projectId]);

  const filtered = risks.filter(r => {
    if (statusFilter !== 'all' && r.status !== statusFilter) return false;
    if (search && !r.title.toLowerCase().includes(search.toLowerCase()) && !(r.owner || '').toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const summary = {
    total: risks.length,
    high: risks.filter(r => r.risk_score >= 6).length,
    medium: risks.filter(r => r.risk_score >= 3 && r.risk_score < 6).length,
    open: risks.filter(r => r.status === 'open').length,
    mitigated: risks.filter(r => r.status === 'mitigated').length,
  };

  const handleSave = async (form) => {
    if (editingRisk) {
      await ModulesModel.updateRisk(editingRisk.id, form);
    } else {
      await ModulesModel.createRisk({ ...form, project_id: projectId });
    }
    setShowForm(false);
    setEditingRisk(null);
    load();
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this risk?')) return;
    await ModulesModel.deleteRisk(id);
    load();
  };

  if (loading) return <div className="py-12 text-center text-gray-400 text-sm">Loading risks…</div>;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-4">
          {[
            { label: 'Total', value: summary.total, color: 'text-gray-700' },
            { label: 'High', value: summary.high, color: 'text-red-600' },
            { label: 'Medium', value: summary.medium, color: 'text-yellow-600' },
            { label: 'Open', value: summary.open, color: 'text-blue-600' },
            { label: 'Mitigated', value: summary.mitigated, color: 'text-green-600' },
          ].map(c => (
            <div key={c.label} className="text-center">
              <div className={`text-xl font-bold ${c.color}`}>{c.value}</div>
              <div className="text-xs text-gray-400">{c.label}</div>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setView(v => v === 'table' ? 'heatmap' : 'table')}
            className="px-3 py-1.5 text-sm border rounded-lg text-gray-600 hover:bg-gray-50">
            {view === 'table' ? '🗺 Heatmap' : '☰ Table'}
          </button>
          <button onClick={() => { setEditingRisk(null); setShowForm(true); }}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">
            <Plus size={14} /> Add Risk
          </button>
        </div>
      </div>

      {view === 'heatmap' ? (
        <RiskHeatmap risks={risks} />
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search…"
                className="pl-8 pr-3 py-1.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-40" />
            </div>
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
              className="border rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="all">All Statuses</option>
              {RISK_STATUSES.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
            </select>
            <span className="text-xs text-gray-400 ml-auto">{filtered.length} risk{filtered.length !== 1 ? 's' : ''}</span>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            {filtered.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <AlertCircle size={32} className="mx-auto mb-2 opacity-30" />
                <p className="text-sm">No risks logged for this project</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b text-xs text-gray-500 uppercase tracking-wide">
                      <th className="text-left px-4 py-3">Risk</th>
                      <th className="text-left px-4 py-3">Category</th>
                      <th className="text-left px-4 py-3">Likelihood</th>
                      <th className="text-left px-4 py-3">Impact</th>
                      <th className="text-left px-4 py-3">Score</th>
                      <th className="text-left px-4 py-3">Status</th>
                      <th className="text-left px-4 py-3">Owner</th>
                      <th className="text-left px-4 py-3">Due</th>
                      <th className="px-4 py-3" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filtered.map(r => {
                      const sc = riskScoreColor(r.risk_score);
                      const today = new Date().toISOString().slice(0, 10);
                      const overdue = r.due_date && r.due_date < today && r.status === 'open';
                      return (
                        <tr key={r.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 max-w-xs">
                            <div className="font-medium text-gray-900 truncate">{r.title}</div>
                            {r.description && <div className="text-xs text-gray-400 truncate mt-0.5">{stripHtml(r.description)}</div>}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className="px-2 py-0.5 rounded-full text-xs bg-purple-100 text-purple-700">{r.category}</span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap capitalize text-gray-600">{r.likelihood}</td>
                          <td className="px-4 py-3 whitespace-nowrap capitalize text-gray-600">{r.impact}</td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium border ${sc.bg} ${sc.text} ${sc.border}`}>
                              <span className={`w-1.5 h-1.5 rounded-full ${sc.dot}`} />
                              {r.risk_score} · {sc.label}
                            </span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColor(r.status)}`}>{r.status}</span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-gray-600">{r.owner || '—'}</td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            {r.due_date ? (
                              <span className={overdue ? 'text-red-600 font-medium' : 'text-gray-600'}>
                                {overdue && '⚠ '}{r.due_date}
                              </span>
                            ) : '—'}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="flex items-center gap-1">
                              <button onClick={() => { setEditingRisk(r); setShowForm(true); }}
                                className="p-1 text-gray-400 hover:text-blue-600 rounded"><Pencil size={13} /></button>
                              <button onClick={() => requestConfirm('Delete this risk?', () => handleDelete(r.id))}
                                className="p-1 text-gray-400 hover:text-red-600 rounded"><Trash2 size={13} /></button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {showForm && (
        <RiskFormModal
          risk={editingRisk}
          onSave={handleSave}
          onClose={() => { setShowForm(false); setEditingRisk(null); }}
        />
      )}
    </div>
  );
};

// ============================================================================
// LEADERBOARD MODULE
// ============================================================================

const LeaderBoardModule = ({ data, period = 'weekly', onPeriodChange, rangeStart = '', rangeEnd = '', onRangeChange }) => {
  const teams = [
    { key: 'devops',  label: 'DevOps Team',  gradient: 'from-blue-600 to-blue-800',   bar: 'bg-blue-500',   avatarTop: 'bg-blue-500' },
    { key: 'dataops', label: 'DataOps Team', gradient: 'from-purple-600 to-purple-800', bar: 'bg-purple-500', avatarTop: 'bg-purple-500' },
  ];
  const rankIcon = ['🥇', '🥈', '🥉'];

  // YYYY-MM-DD in Pacific time — used to default the custom range when first selected.
  const pacificToday = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/Los_Angeles' });
  const pacificDateShifted = (days) => {
    const d = new Date();
    d.setUTCDate(d.getUTCDate() + days);
    return d.toLocaleDateString('en-CA', { timeZone: 'America/Los_Angeles' });
  };

  const handleTabClick = (tabId) => {
    if (tabId === 'custom' && (!rangeStart || !rangeEnd) && onRangeChange) {
      // Sensible default on first entry: last 30 days through today
      onRangeChange(pacificDateShifted(-30), pacificToday());
    }
    onPeriodChange(tabId);
  };

  const rangeInvalid = period === 'custom' && rangeStart && rangeEnd && rangeStart > rangeEnd;

  return (
    <div className="max-w-6xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <Trophy size={28} className="text-amber-500" />
          <div>
            <h1 className="text-3xl font-bold text-gray-900">LeaderBoard</h1>
            <p className="text-gray-500 text-sm mt-0.5">Rankings based on tasks completed this period, weighted by priority</p>
          </div>
        </div>
        {/* Period tabs */}
        <div className="flex gap-1 bg-gray-100 rounded-lg p-1 self-start sm:self-auto">
          {[{ id: 'weekly', label: 'This Week' }, { id: 'monthly', label: 'This Month' }, { id: 'custom', label: 'Custom' }].map(tab => (
            <button
              key={tab.id}
              onClick={() => handleTabClick(tab.id)}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                period === tab.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Custom range inputs */}
      {period === 'custom' && (
        <div className="mb-4 bg-white rounded-xl border border-gray-200 px-5 py-3 flex flex-wrap items-center gap-3">
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Custom range</span>
          <label className="text-xs text-gray-600 flex items-center gap-1.5">
            From
            <input
              type="date"
              value={rangeStart}
              max={rangeEnd || undefined}
              onChange={e => onRangeChange && onRangeChange(e.target.value, rangeEnd)}
              className="border border-gray-300 rounded px-2 py-1 text-sm text-gray-900"
            />
          </label>
          <label className="text-xs text-gray-600 flex items-center gap-1.5">
            To
            <input
              type="date"
              value={rangeEnd}
              min={rangeStart || undefined}
              max={pacificToday()}
              onChange={e => onRangeChange && onRangeChange(rangeStart, e.target.value)}
              className="border border-gray-300 rounded px-2 py-1 text-sm text-gray-900"
            />
          </label>
          {rangeInvalid && <span className="text-xs text-red-600">Start date must be on or before end date.</span>}
        </div>
      )}

      {/* Points legend */}
      <div className="mb-6 bg-white rounded-xl border border-gray-200 px-5 py-4">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Points per Priority</p>
        <div className="flex flex-wrap gap-3">
          {[
            { label: 'P1 / Urgent', pts: 10, bg: 'bg-red-50', text: 'text-red-600' },
            { label: 'P2 / High',   pts: 5,  bg: 'bg-orange-50', text: 'text-orange-600' },
            { label: 'P3 / Medium', pts: 3,  bg: 'bg-yellow-50', text: 'text-yellow-600' },
            { label: 'Low',         pts: 1,  bg: 'bg-green-50',  text: 'text-green-600' },
          ].map(({ label, pts, bg, text }) => (
            <span key={label} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold ${bg} ${text}`}>
              {label} <span className="font-black">= {pts} pts</span>
            </span>
          ))}
        </div>
      </div>

      {/* Team boards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {teams.map(team => {
          const members = (data[team.key] || []).slice().sort((a, b) => b.points - a.points);
          const maxPts = members[0]?.points || 1;
          const totalPts = members.reduce((s, m) => s + m.points, 0);
          const totalTasks = members.reduce((s, m) => s + m.taskCount, 0);

          return (
            <div key={team.key} className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
              {/* Team header */}
              <div className={`bg-gradient-to-r ${team.gradient} px-6 py-5`}>
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-bold text-white">{team.label}</h2>
                    <p className="text-white/70 text-sm mt-0.5">{members.length} members · {totalTasks} completed</p>
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-black text-white">{totalPts}</div>
                    <div className="text-white/70 text-xs">total pts</div>
                  </div>
                </div>
              </div>

              {/* Rankings */}
              {members.length === 0 ? (
                <div className="p-12 text-center text-gray-400">
                  <Trophy size={36} className="mx-auto mb-3 opacity-20" />
                  <p className="text-sm font-medium">No tasks assigned yet</p>
                  <p className="text-xs mt-1">Assign tasks to {team.label} members to see rankings</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {members.map((member, idx) => {
                    const pct = Math.round((member.points / maxPts) * 100);
                    const isTop = idx === 0;
                    return (
                      <div key={member.name} className={`px-5 py-4 ${isTop ? 'bg-amber-50' : 'hover:bg-gray-50'}`}>
                        <div className="flex items-center gap-3">
                          {/* Rank */}
                          <div className="w-8 text-center shrink-0">
                            {idx < 3
                              ? <span className="text-xl leading-none">{rankIcon[idx]}</span>
                              : <span className="text-sm font-bold text-gray-400">#{idx + 1}</span>}
                          </div>

                          {/* Avatar */}
                          <div className={`w-9 h-9 rounded-full flex items-center justify-center font-bold text-sm text-white shrink-0 ${isTop ? 'bg-amber-400' : team.avatarTop}`}>
                            {member.name.charAt(0).toUpperCase()}
                          </div>

                          {/* Info */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between mb-1">
                              <span className="font-semibold text-sm text-gray-900 truncate">{member.name}</span>
                              <span className={`font-black text-base ml-2 shrink-0 ${isTop ? 'text-amber-600' : 'text-gray-800'}`}>{member.points} pts</span>
                            </div>
                            {/* Progress bar */}
                            <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden mb-2">
                              <div
                                className={`h-full rounded-full transition-all ${isTop ? 'bg-amber-400' : team.bar}`}
                                style={{ width: `${pct}%` }}
                              />
                            </div>
                            {/* Breakdown */}
                            <div className="flex flex-wrap items-center gap-1.5 text-xs text-gray-500">
                              <span className="text-green-600 font-medium">✓ {member.taskCount} completed</span>
                              {member.p1 > 0 && <span className="px-1.5 py-0.5 bg-red-50 text-red-600 rounded font-semibold">P1 ×{member.p1}</span>}
                              {member.p2 > 0 && <span className="px-1.5 py-0.5 bg-orange-50 text-orange-600 rounded font-semibold">P2 ×{member.p2}</span>}
                              {member.p3 > 0 && <span className="px-1.5 py-0.5 bg-yellow-50 text-yellow-700 rounded font-semibold">P3 ×{member.p3}</span>}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const Navigation = ({ currentPage, onNavigate, isOpen, onClose, user }) => {
  const allNavItems = [
    { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
    { id: 'modules', label: 'Modules', icon: Camera },
    { id: 'leaderboard', label: 'LeaderBoard', icon: Trophy },
    { id: 'orgchart', label: 'Org Chart', icon: Users },
    { id: 'projectdashboard', label: 'Project Dashboard', icon: Activity },
    { id: 'fastdtr', label: 'FastDTR', icon: Clock },
    { id: 'tasks', label: 'Tasks', icon: CheckSquare },
    { id: 'reports', label: 'Reports', icon: FileText },
    { id: 'audit', label: 'Audit', icon: Shield },
    { id: 'settings', label: 'Settings', icon: Settings }
  ];
  const role = user?.role || user?.user_role;
  const isAdmin = role === 'admin';
  const isManager = role === 'manager';
  const isDataOpsManager = role === 'dataops_manager';
  const isDevOpsManager = role === 'devops_manager';
  const navItems = allNavItems.filter(item => {
    if (!ADMIN_ONLY_NAV.includes(item.id)) return true;
    if (isAdmin) return true;
    if (item.id === 'tasks' || item.id === 'reports') {
      return isManager || isDataOpsManager || isDevOpsManager;
    }
    if (item.id === 'audit') {
      return isDevOpsManager;
    }
    return false;
  });

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden" onClick={onClose} />
      )}
      
      {/* Navigation */}
      <nav className={`
        fixed lg:static inset-y-0 left-0 z-50 w-64 lg:w-auto
        bg-white border-b border-gray-200 shadow-sm
        transform transition-transform duration-300 ease-in-out
        ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="lg:hidden flex justify-between items-center p-4 border-b">
          <span className="font-semibold">Menu</span>
          <button onClick={onClose}><X size={24} /></button>
        </div>
        <div className="lg:max-w-7xl lg:mx-auto lg:px-4 lg:flex lg:gap-0">
          {navItems.map(item => {
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                onClick={() => {
                  console.log('[Nav click]', item.id);
                  onNavigate(item.id);
                  onClose();
                }}
                className={`
                  w-full lg:w-auto flex items-center gap-3 px-6 py-4 lg:border-b-3
                  transition-colors
                  ${currentPage === item.id 
                    ? 'text-blue-600 bg-gray-50 lg:border-blue-600' 
                    : 'text-gray-600 hover:bg-gray-50 hover:text-blue-600 lg:border-transparent'
                  }
                `}
              >
                <Icon size={20} />
                <span className="font-medium">{item.label}</span>
              </button>
            );
          })}
        </div>
      </nav>
    </>
  );
};

// Behind a burn chart's "Task Remaining" number — lists the actual open tasks/quick-tasks
// counted into that number, fetched from the same filters the count itself uses (see
// getModuleRemainingTasks) so the list and the number on the card can never disagree.
const BurnChartRemainingModal = ({ module, title, onClose }) => {
  const [loading, setLoading] = React.useState(true);
  const [items, setItems] = React.useState([]);
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      const d = await DashboardModel.getBurnChartRemainingTasks(module);
      if (cancelled) return;
      setItems(d?.items || []);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [module]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(15,23,42,0.45)' }}>
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-xl flex flex-col max-h-[80vh]" style={{ border: '1px solid #e2e8f0' }}>
        <div className="px-6 pt-5 pb-3 border-b border-slate-200 flex items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-bold text-slate-900">Remaining tasks</h2>
            <p className="text-xs text-slate-500 mt-1">{title} · {items.length} open</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">✕</button>
        </div>
        <div className="overflow-y-auto flex-1">
          {loading ? (
            <div className="text-center py-12 text-slate-400 text-sm">Loading…</div>
          ) : items.length === 0 ? (
            <div className="text-center py-12 text-slate-400 text-sm">Nothing outstanding — all caught up.</div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {items.map(it => (
                <li key={`${it.source}-${it.id}`} className="flex items-center gap-3 px-6 py-2.5">
                  <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 w-16 shrink-0">{it.source === 'quick_task' ? 'Quick' : 'Task'}</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-slate-900 truncate">{it.title}</div>
                    <div className="text-[11px] text-slate-500 truncate">{it.assignee || 'Unassigned'}{it.dueDate ? ` · due ${it.dueDate}` : ''}</div>
                  </div>
                  <span className="text-[11px] font-medium text-slate-600 bg-slate-100 rounded px-2 py-0.5 shrink-0">{it.status}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

const BurnChart = ({ title, subtitle, days, burndown, ideal, total, completedCount, module }) => {
  const [showRemaining, setShowRemaining] = React.useState(false);
  if (!days || days.length === 0) return (
    <div className="flex items-center justify-center h-40 text-gray-400 text-sm">No data available</div>
  );

  // Format YYYY-MM-DD → M/D/YYYY
  const fmtDay = d => {
    if (!d || !d.includes('-')) return d;
    const [y, m, day] = d.split('-');
    return `${parseInt(m)}/${parseInt(day)}/${y}`;
  };

  const remaining = burndown ? (burndown[burndown.length - 1] ?? total) : total;
  const completed = completedCount !== undefined ? completedCount : Math.max(0, total - remaining);
  const completedPct = total > 0 ? Math.round((completed / total) * 100) : 0;
  const avgBurndown = days.length > 1 ? parseFloat((completed / (days.length - 1)).toFixed(1)) : 0;

  const W = 560, H = 220, padL = 36, padR = 16, padT = 12, padB = 52;
  const cW = W - padL - padR, cH = H - padT - padB;
  const n = days.length;
  const maxVal = Math.max(total || 0, ...(burndown || []), 1);
  const barW = Math.max(3, (cW / n) * 0.65);
  const x = i => padL + (i / Math.max(n - 1, 1)) * cW;
  const y = v => padT + cH - (Math.min(v, maxVal) / maxVal) * cH;
  const barBot = padT + cH;
  const yTicks = [...new Set([0, Math.round(maxVal / 2), maxVal])];
  const todayIdx = days.length - 1;

  return (
    <div className="orbit-card p-5">
      {/* Header */}
      <div className="flex justify-between items-start mb-4">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-gray-900 tracking-tight">{title}</h3>
          {subtitle && <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>}
        </div>
        <div className="text-right ml-4 shrink-0">
          <div className="text-[10px] font-medium uppercase tracking-wide text-gray-400 leading-tight">Tasks Remaining</div>
          {module ? (
            <button
              type="button"
              onClick={() => setShowRemaining(true)}
              className="text-3xl font-bold text-blue-600 hover:text-blue-700 hover:underline leading-none mt-1 tabular-nums"
              title="See which tasks are remaining"
            >{remaining}</button>
          ) : (
            <div className="text-3xl font-bold text-gray-900 leading-none mt-1 tabular-nums">{remaining}</div>
          )}
        </div>
      </div>

      {/* Stats row */}
      <div className="flex gap-6 mb-4 text-xs border-b border-gray-100 pb-4">
        <div>
          <div className="text-gray-400">Completed</div>
          <div className="font-semibold text-gray-900 text-base mt-0.5">{completedPct}%</div>
        </div>
        <div>
          <div className="text-gray-400">Avg. burndown</div>
          <div className="font-semibold text-gray-900 text-base mt-0.5">{avgBurndown}</div>
        </div>
        <div>
          <div className="text-gray-400">Scope increase</div>
          <div className="font-semibold text-gray-900 text-base mt-0.5">0</div>
        </div>
      </div>

      {/* Chart */}
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ height: 220 }}>
        {/* Grid lines */}
        {yTicks.map(v => (
          <line key={v} x1={padL} y1={y(v).toFixed(1)} x2={W - padR} y2={y(v).toFixed(1)} stroke="#f1f5f9" strokeWidth="1" />
        ))}
        {/* Today highlight */}
        {todayIdx > 0 && (
          <rect x={(x(todayIdx) - barW / 2 - 3).toFixed(1)} y={padT} width={(barW + 6).toFixed(1)} height={cH} fill="#f8fafc" rx="2" />
        )}
        {/* Burndown bars */}
        {(burndown || []).map((v, i) => (
          <rect key={i} x={(x(i) - barW / 2).toFixed(1)} y={y(v).toFixed(1)} width={barW.toFixed(1)} height={Math.max(0, barBot - y(v)).toFixed(1)} fill="#2563eb" opacity="0.85" rx="1.5">
            <title>{`${fmtDay(days[i])}: ${v} remaining`}</title>
          </rect>
        ))}
        {/* Ideal trend line */}
        {ideal && ideal.length > 1 && (
          <polyline points={ideal.map((v, i) => `${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ')} fill="none" stroke="#cbd5e1" strokeWidth="1.5" strokeDasharray="4 3" />
        )}
        {/* Axes */}
        <line x1={padL} y1={padT} x2={padL} y2={barBot} stroke="#e5e7eb" strokeWidth="1" />
        <line x1={padL} y1={barBot} x2={W - padR} y2={barBot} stroke="#e5e7eb" strokeWidth="1" />
        {/* Y labels */}
        {yTicks.map(v => (
          <text key={v} x={padL - 4} y={(y(v) + 4).toFixed(1)} textAnchor="end" fontSize="10" fill="#9ca3af">{v}</text>
        ))}
        {/* X labels — rotated dates */}
        {days.map((d, i) => (
          <text key={i} x={x(i).toFixed(1)} y={(barBot + 8).toFixed(1)} textAnchor="end" fontSize="9" fill="#9ca3af"
            transform={`rotate(-45 ${x(i).toFixed(1)} ${(barBot + 8).toFixed(1)})`}>{fmtDay(d)}</text>
        ))}
      </svg>

      {/* Legend */}
      <div className="flex gap-5 mt-2 text-xs text-gray-400">
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: '#2563eb' }} />
          Task
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-5 border-t border-dashed border-gray-300" />
          Ideal Trend
        </span>
      </div>
      {showRemaining && <BurnChartRemainingModal module={module} title={title} onClose={() => setShowRemaining(false)} />}
    </div>
  );
};

// Small "how's the module doing" line reused in both of CycleTimeChart's render paths —
// sprint velocity (committed vs completed hours, latest sprint) and, for DataOps, its
// existing pipeline success rate. Renders nothing if neither number is available (e.g. no
// sprint has ever run for that module yet) rather than showing a misleading 0%.
const CycleTimePerformanceRow = ({ performance }) => {
  if (!performance || (performance.velocity == null && performance.pipelineSuccessRate == null)) return null;
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-gray-500 mb-4 pb-4 border-b border-gray-100">
      {performance.velocity != null && (
        <span><strong className="text-gray-900 font-semibold">{performance.velocity}%</strong> velocity{performance.sprintName ? <span className="text-gray-400"> · {performance.sprintName}</span> : null}</span>
      )}
      {performance.pipelineSuccessRate != null && (
        <span><strong className="text-gray-900 font-semibold">{performance.pipelineSuccessRate}%</strong> pipeline success</span>
      )}
    </div>
  );
};

const CycleTimeChart = ({ title, avgDays, total, points, performance }) => {
  const hasData = points && points.length > 0;
  if (!hasData) return (
    <div className="orbit-card p-5">
      <div className="flex justify-between items-start mb-2">
        <div>
          <h3 className="text-sm font-semibold text-gray-900 tracking-tight">{title}</h3>
          <p className="text-xs text-gray-400 mt-0.5">Last 30 days</p>
        </div>
        <div className="text-right ml-4 shrink-0">
          <div className="text-2xl font-bold text-gray-300 leading-none">0</div>
          <div className="text-[10px] font-medium uppercase tracking-wide text-gray-400">Tasks</div>
        </div>
      </div>
      <div className="mb-4 pb-4 border-b border-gray-100">
        <span className="text-3xl font-bold text-gray-900 tabular-nums">0</span>
        <span className="ml-2 text-sm text-gray-500">days average cycle time</span>
        <div className="text-xs text-gray-400 mt-0.5">(Started to Completed)</div>
      </div>
      <CycleTimePerformanceRow performance={performance} />
      <div className="flex items-center justify-center h-32 text-gray-400 text-sm">No completed tasks in the last 30 days</div>
    </div>
  );

  // 30-day window ending today
  const today = new Date();
  const startDate = new Date(today);
  startDate.setDate(startDate.getDate() - 29);

  const parsed = points.map(p => {
    const diff = Math.round((new Date(p.date + 'T00:00:00') - startDate) / 864e5);
    return { dayIdx: diff, days: p.days };
  }).filter(p => p.dayIdx >= 0 && p.dayIdx <= 29);

  const maxY = Math.max(10, ...parsed.map(p => p.days));
  const yMax = Math.ceil(maxY / 10) * 10;

  // Moving average + ±1 std dev band (window ±5 days)
  const trendPoints = Array.from({ length: 30 }, (_, i) => {
    const nearby = parsed.filter(p => Math.abs(p.dayIdx - i) <= 5);
    if (nearby.length === 0) return null;
    const avg = nearby.reduce((s, p) => s + p.days, 0) / nearby.length;
    const std = Math.sqrt(nearby.reduce((s, p) => s + (p.days - avg) ** 2, 0) / nearby.length);
    return { i, avg, lo: Math.max(0, avg - std), hi: avg + std };
  }).filter(Boolean);

  const W = 560, H = 180, padL = 36, padR = 16, padT = 10, padB = 30;
  const cW = W - padL - padR, cH = H - padT - padB;
  const xPos = i => padL + (i / 29) * cW;
  const yPos = v => padT + cH - (Math.min(v, yMax) / yMax) * cH;

  const bandPath = trendPoints.length > 1
    ? `M${trendPoints.map(p => `${xPos(p.i).toFixed(1)},${yPos(p.hi).toFixed(1)}`).join('L')}L${[...trendPoints].reverse().map(p => `${xPos(p.i).toFixed(1)},${yPos(p.lo).toFixed(1)}`).join('L')}Z`
    : '';
  const linePath = trendPoints.length > 1
    ? `M${trendPoints.map(p => `${xPos(p.i).toFixed(1)},${yPos(p.avg).toFixed(1)}`).join('L')}`
    : '';

  const yTicks = [0, Math.round(yMax / 2), yMax];

  // X-axis: day numbers every 5 days
  const xDayLabels = Array.from({ length: 30 }, (_, i) => {
    if (i % 5 !== 0 && i !== 29) return null;
    const d = new Date(startDate); d.setDate(d.getDate() + i);
    return { i, day: d.getDate() };
  }).filter(Boolean);

  const startMonthLabel = startDate.toLocaleString('default', { month: 'short' });

  return (
    <div className="orbit-card p-5">
      <div className="flex justify-between items-start mb-2">
        <div>
          <h3 className="text-sm font-semibold text-gray-900 tracking-tight">{title}</h3>
          <p className="text-xs text-gray-400 mt-0.5">Last 30 days</p>
        </div>
        <div className="text-right ml-4 shrink-0">
          <div className="text-2xl font-bold text-gray-900 leading-none tabular-nums">{total}</div>
          <div className="text-[10px] font-medium uppercase tracking-wide text-gray-400">Tasks</div>
        </div>
      </div>

      <div className="mb-4 pb-4 border-b border-gray-100">
        <span className="text-3xl font-bold text-gray-900 tabular-nums">{Number(avgDays).toFixed(1)}</span>
        <span className="ml-2 text-sm text-gray-500">days average cycle time</span>
        <div className="text-xs text-gray-400 mt-0.5">(Created to Completed · Sprint &amp; QuickTrack tasks)</div>
      </div>
      <CycleTimePerformanceRow performance={performance} />

      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ height: 180 }}>
        {yTicks.map(v => (
          <line key={v} x1={padL} y1={yPos(v).toFixed(1)} x2={W - padR} y2={yPos(v).toFixed(1)} stroke="#f1f5f9" strokeWidth="1" />
        ))}
        {bandPath && <path d={bandPath} fill="rgba(37,99,235,0.10)" />}
        {linePath && <path d={linePath} fill="none" stroke="#1e293b" strokeWidth="1.5" />}
        {parsed.map((p, i) => (
          <circle key={i} cx={xPos(p.dayIdx).toFixed(1)} cy={yPos(p.days).toFixed(1)} r="3.5" fill="#2563eb" opacity="0.85">
            <title>{`${p.days} day${p.days === 1 ? '' : 's'}`}</title>
          </circle>
        ))}
        <line x1={padL} y1={padT} x2={padL} y2={padT + cH} stroke="#e5e7eb" strokeWidth="1" />
        <line x1={padL} y1={padT + cH} x2={W - padR} y2={padT + cH} stroke="#e5e7eb" strokeWidth="1" />
        {yTicks.map(v => (
          <text key={v} x={padL - 4} y={(yPos(v) + 4).toFixed(1)} textAnchor="end" fontSize="10" fill="#9ca3af">{v}</text>
        ))}
        {xDayLabels.map(({ i, day }) => (
          <text key={i} x={xPos(i).toFixed(1)} y={(padT + cH + 14).toFixed(1)} textAnchor="middle" fontSize="9" fill="#9ca3af">{day}</text>
        ))}
        <text x={padL} y={H} textAnchor="start" fontSize="9" fill="#9ca3af">{startMonthLabel}</text>
      </svg>
    </div>
  );
};

// ============================================================================
// QUARTERLY STATS VIEW
// ============================================================================

const QuarterlyStatsView = ({ user }) => {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [quarter, setQuarter] = useState(Math.ceil((now.getMonth() + 1) / 3));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = React.useCallback(async () => {
    setLoading(true);
    const token = localStorage.getItem('orbit_token');
    const res = await fetch(`/api/dashboard/quarterly?year=${year}&quarter=${quarter}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    const d = await res.json();
    setData(d);
    setLoading(false);
  }, [year, quarter]);

  useEffect(() => { load(); }, [load]);

  if (loading && !data) return <div className="flex items-center justify-center h-64 text-gray-400">Loading Q{quarter} {year}...</div>;

  const s = data?.summary || {};
  const burndown = data?.burndown || [];
  const cycleTime = data?.cycleTime || [];
  const byModule = data?.byModule || [];
  const byAssignee = data?.byAssignee || [];
  const sprints = data?.sprints || [];
  const weeklyTrend = data?.weeklyTrend || [];

  const completionRate = s.totalTasks > 0 ? Math.round((s.totalCompleted / s.totalTasks) * 100) : 0;
  const qLabel = `Q${quarter} ${year}`;

  // Burndown SVG
  const bdW = 700, bdH = 200, bdPad = 40;
  const bdMax = burndown.length > 0 ? Math.max(...burndown.map(b => Math.max(b.remaining, b.created))) : 1;
  const bdXStep = burndown.length > 1 ? (bdW - bdPad * 2) / (burndown.length - 1) : 0;
  const bdY = (v) => bdPad + (bdH - bdPad * 2) * (1 - v / bdMax);

  // Cycle time SVG
  const ctMax = cycleTime.length > 0 ? Math.max(...cycleTime.map(c => c.avgDays), 1) : 10;

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Quarterly Stats</h1>
          <p className="text-gray-600 mt-1">{qLabel} — {data?.qStart} to {data?.qEnd}</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => { if (quarter === 1) { setQuarter(4); setYear(y => y - 1); } else setQuarter(q => q - 1); }}
            className="px-2 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm">←</button>
          <select value={quarter} onChange={e => setQuarter(parseInt(e.target.value))}
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm">
            <option value={1}>Q1 (Jan-Mar)</option><option value={2}>Q2 (Apr-Jun)</option>
            <option value={3}>Q3 (Jul-Sep)</option><option value={4}>Q4 (Oct-Dec)</option>
          </select>
          <select value={year} onChange={e => setYear(parseInt(e.target.value))}
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm">
            {[now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <button onClick={() => { if (quarter === 4) { setQuarter(1); setYear(y => y + 1); } else setQuarter(q => q + 1); }}
            className="px-2 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm">→</button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
        {[
          { label: 'Total Tasks', value: s.totalTasks, color: '#374151', bg: '#f9fafb' },
          { label: 'Completed', value: `${s.totalCompleted} (${completionRate}%)`, color: '#16a34a', bg: '#f0fdf4' },
          { label: 'Story Points', value: s.storyPoints, color: '#7c3aed', bg: '#faf5ff' },
          { label: 'Avg Cycle Time', value: `${s.avgCycleDays}d`, color: '#b45309', bg: '#fffbeb' },
          { label: 'Hours Logged', value: s.hoursLogged, color: '#0891b2', bg: '#ecfeff' },
          { label: 'Bugs Filed', value: `${s.bugs} (${s.bugsCritical} crit)`, color: '#dc2626', bg: '#fef2f2' },
        ].map(c => (
          <div key={c.label} className="rounded-xl border border-gray-200 shadow-sm p-4" style={{ background: c.bg }}>
            <div className="text-xs font-medium text-gray-500 mb-1">{c.label}</div>
            <div className="text-2xl font-bold" style={{ color: c.color }}>{c.value}</div>
          </div>
        ))}
      </div>

      {/* Burndown + Cycle Time Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Burndown Chart */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
          <h3 className="text-sm font-bold text-gray-700 mb-1">Cumulative Burndown</h3>
          <p className="text-xs text-gray-400 mb-3">Tasks remaining per week — created vs completed</p>
          {burndown.length === 0 ? <p className="text-sm text-gray-400 py-8 text-center">No data for {qLabel}</p> : (
            <svg viewBox={`0 0 ${bdW} ${bdH}`} className="w-full" style={{ maxHeight: 220 }}>
              {/* Grid */}
              {[0, 0.25, 0.5, 0.75, 1].map(f => (
                <g key={f}>
                  <line x1={bdPad} y1={bdY(bdMax * f)} x2={bdW - bdPad} y2={bdY(bdMax * f)} stroke="#f1f5f9" strokeWidth="1" />
                  <text x={bdPad - 5} y={bdY(bdMax * f) + 3} textAnchor="end" fontSize="9" fill="#94a3b8">{Math.round(bdMax * f)}</text>
                </g>
              ))}
              {/* Created line (gray) */}
              <polyline fill="none" stroke="#94a3b8" strokeWidth="1.5" strokeDasharray="4 2"
                points={burndown.map((b, i) => `${bdPad + i * bdXStep},${bdY(b.created)}`).join(' ')} />
              {/* Remaining area */}
              <polygon fill="#3b82f6" opacity="0.15"
                points={`${bdPad},${bdY(0)} ${burndown.map((b, i) => `${bdPad + i * bdXStep},${bdY(b.remaining)}`).join(' ')} ${bdPad + (burndown.length - 1) * bdXStep},${bdY(0)}`} />
              {/* Remaining line */}
              <polyline fill="none" stroke="#3b82f6" strokeWidth="2"
                points={burndown.map((b, i) => `${bdPad + i * bdXStep},${bdY(b.remaining)}`).join(' ')} />
              {/* Dots + labels */}
              {burndown.map((b, i) => (
                <g key={i}>
                  <circle cx={bdPad + i * bdXStep} cy={bdY(b.remaining)} r="3" fill="#3b82f6" />
                  {burndown.length <= 14 && <text x={bdPad + i * bdXStep} y={bdH - 5} textAnchor="middle" fontSize="8" fill="#94a3b8">{b.week.replace(/^\d{4}-/, '')}</text>}
                </g>
              ))}
            </svg>
          )}
          <div className="flex gap-4 mt-2 text-xs text-gray-500">
            <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-blue-500 inline-block" /> Remaining</span>
            <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-gray-400 inline-block" style={{ borderTop: '1px dashed #94a3b8' }} /> Created (cumulative)</span>
          </div>
        </div>

        {/* Cycle Time Chart */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
          <h3 className="text-sm font-bold text-gray-700 mb-1">Cycle Time Trend</h3>
          <p className="text-xs text-gray-400 mb-3">Avg days from created to completed, per week — {s.avgCycleDays}d overall</p>
          {cycleTime.length === 0 ? <p className="text-sm text-gray-400 py-8 text-center">No completed tasks for {qLabel}</p> : (
            <svg viewBox={`0 0 ${bdW} ${bdH}`} className="w-full" style={{ maxHeight: 220 }}>
              {[0, 0.5, 1].map(f => (
                <g key={f}>
                  <line x1={bdPad} y1={bdY(ctMax * f)} x2={bdW - bdPad} y2={bdY(ctMax * f)} stroke="#f1f5f9" strokeWidth="1" />
                  <text x={bdPad - 5} y={bdY(ctMax * f) + 3} textAnchor="end" fontSize="9" fill="#94a3b8">{Math.round(ctMax * f)}d</text>
                </g>
              ))}
              {/* Avg line */}
              <line x1={bdPad} y1={bdY(s.avgCycleDays)} x2={bdW - bdPad} y2={bdY(s.avgCycleDays)} stroke="#f59e0b" strokeWidth="1" strokeDasharray="4 2" />
              {/* Bars */}
              {cycleTime.map((c, i) => {
                const bw = Math.max(8, (bdW - bdPad * 2) / cycleTime.length - 4);
                const x = bdPad + i * ((bdW - bdPad * 2) / cycleTime.length) + 2;
                return (
                  <g key={i}>
                    <rect x={x} y={bdY(c.avgDays)} width={bw} height={bdY(0) - bdY(c.avgDays)} rx="2" fill="#b45309" opacity="0.7" />
                    <text x={x + bw / 2} y={bdY(c.avgDays) - 4} textAnchor="middle" fontSize="8" fill="#b45309" fontWeight="600">{c.avgDays}d</text>
                    {cycleTime.length <= 14 && <text x={x + bw / 2} y={bdH - 5} textAnchor="middle" fontSize="8" fill="#94a3b8">{c.week.replace(/^\d{4}-/, '')}</text>}
                  </g>
                );
              })}
            </svg>
          )}
          <div className="flex gap-4 mt-2 text-xs text-gray-500">
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-amber-700 opacity-70 inline-block" /> Weekly avg</span>
            <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-amber-500 inline-block" style={{ borderTop: '1px dashed #f59e0b' }} /> Overall avg ({s.avgCycleDays}d)</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Weekly Created vs Completed */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
          <h3 className="text-sm font-bold text-gray-700 mb-3">Weekly Throughput</h3>
          {weeklyTrend.length === 0 ? <p className="text-sm text-gray-400 text-center py-4">No data</p> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-gray-200"><th className="text-left py-2 text-xs font-semibold text-gray-500">Week</th><th className="text-right py-2 text-xs font-semibold text-gray-500">Created</th><th className="text-right py-2 text-xs font-semibold text-gray-500">Completed</th><th className="text-right py-2 text-xs font-semibold text-gray-500">Net</th></tr></thead>
                <tbody>
                  {weeklyTrend.map(w => (
                    <tr key={w.week} className="border-b border-gray-100">
                      <td className="py-1.5 text-gray-600 font-mono text-xs">{w.week}</td>
                      <td className="py-1.5 text-right text-gray-800">{w.created}</td>
                      <td className="py-1.5 text-right text-green-600 font-medium">{w.completed}</td>
                      <td className={`py-1.5 text-right font-medium ${w.completed - w.created >= 0 ? 'text-green-600' : 'text-red-500'}`}>{w.completed - w.created >= 0 ? '+' : ''}{w.completed - w.created}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* By Assignee — all members */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
          <h3 className="text-sm font-bold text-gray-700 mb-3">All Team Members ({byAssignee.length})</h3>
          {byAssignee.length === 0 ? <p className="text-sm text-gray-400 text-center py-4">No data</p> : (
            <div className="overflow-y-auto" style={{ maxHeight: 400 }}>
              <table className="w-full text-sm">
                <thead><tr className="border-b border-gray-200 sticky top-0 bg-white">
                  <th className="text-left py-2 text-xs font-semibold text-gray-500">Member</th>
                  <th className="text-right py-2 text-xs font-semibold text-gray-500">Total</th>
                  <th className="text-right py-2 text-xs font-semibold text-gray-500">Done</th>
                  <th className="text-right py-2 text-xs font-semibold text-gray-500">Rate</th>
                  <th className="text-right py-2 text-xs font-semibold text-gray-500">Cycle</th>
                  <th className="py-2 pl-3 text-xs font-semibold text-gray-500 w-32"></th>
                </tr></thead>
                <tbody>
                  {byAssignee.map(a => {
                    const pct = a.total > 0 ? Math.round((a.completed / a.total) * 100) : 0;
                    return (
                      <tr key={a.assignee} className="border-b border-gray-100">
                        <td className="py-1.5 text-gray-700 font-medium">{a.assignee}</td>
                        <td className="py-1.5 text-right text-gray-800">{a.total}</td>
                        <td className="py-1.5 text-right text-green-600 font-medium">{a.completed}</td>
                        <td className={`py-1.5 text-right font-semibold ${pct >= 80 ? 'text-green-600' : pct >= 50 ? 'text-amber-600' : 'text-red-500'}`}>{pct}%</td>
                        <td className="py-1.5 text-right text-amber-600">{a.avgCycleDays != null ? `${a.avgCycleDays}d` : '—'}</td>
                        <td className="py-1.5 pl-3"><div className="h-3 bg-gray-100 rounded-full overflow-hidden"><div className="h-full bg-blue-500 rounded-full" style={{ width: `${pct}%` }} /></div></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* By Module */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
          <h3 className="text-sm font-bold text-gray-700 mb-3">Tasks by Module</h3>
          {byModule.length === 0 ? <p className="text-sm text-gray-400 text-center py-4">No data</p> : (
            <div className="space-y-2">
              {byModule.map(m => (
                <div key={m.module} className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg">
                  <span className="text-sm font-medium text-gray-700 capitalize">{m.module}</span>
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-gray-900 font-bold">{m.total}</span>
                    <span className="text-xs text-green-600">{m.completed} done</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Sprint Velocity */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
          <h3 className="text-sm font-bold text-gray-700 mb-3">Sprint Velocity</h3>
          {sprints.length === 0 ? <p className="text-sm text-gray-400 text-center py-4">No sprints in {qLabel}</p> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-gray-200"><th className="text-left py-2 text-xs font-semibold text-gray-500">Sprint</th><th className="text-right py-2 text-xs font-semibold text-gray-500">Planned</th><th className="text-right py-2 text-xs font-semibold text-gray-500">Completed</th><th className="text-right py-2 text-xs font-semibold text-gray-500">Velocity</th></tr></thead>
                <tbody>
                  {sprints.map(sp => {
                    const vel = sp.pointsPlanned > 0 ? Math.round((sp.pointsCompleted / sp.pointsPlanned) * 100) : 0;
                    return (
                      <tr key={sp.number} className="border-b border-gray-100">
                        <td className="py-1.5 text-gray-700 font-medium">{sp.name}</td>
                        <td className="py-1.5 text-right text-gray-600">{sp.pointsPlanned} pts</td>
                        <td className="py-1.5 text-right text-green-600 font-medium">{sp.pointsCompleted} pts</td>
                        <td className={`py-1.5 text-right font-bold ${vel >= 80 ? 'text-green-600' : vel >= 50 ? 'text-amber-600' : 'text-red-500'}`}>{vel}%</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const DASHBOARD_PROJECT_STATUS_COLORS = {
  planning: { bg: '#eff6ff', color: '#1d4ed8' },
  active: { bg: '#d1fae5', color: '#065f46' },
  on_hold: { bg: '#fef3c7', color: '#92400e' },
  completed: { bg: '#f3f4f6', color: '#374151' },
};
const DASHBOARD_HEALTH_META = {
  overdue: { bg: '#fee2e2', color: '#991b1b', icon: '⏰' },
  critical: { bg: '#fee2e2', color: '#991b1b', icon: '🔥' },
  'at-risk': { bg: '#fef3c7', color: '#92400e', icon: '⚠' },
};
const DASHBOARD_SEVERITY_META = {
  critical: { bg: '#fee2e2', color: '#991b1b' },
  high: { bg: '#ffedd5', color: '#9a3412' },
  medium: { bg: '#fef3c7', color: '#92400e' },
  low: { bg: '#f3f4f6', color: '#374151' },
};

// Project status counts + at-risk/upcoming milestones — reuses the same executive
// milestone summary PlanTrack's own dashboard already computes, just org-wide (no
// projectId), so this always reflects real project data with no separate "empty" design
// needed beyond the lists' own natural empty state.
const ProjectHealthCard = ({ projectStatus }) => {
  const dist = projectStatus?.distribution || [];
  const summary = projectStatus?.summary || {};
  const upcoming = projectStatus?.upcomingMilestones || [];
  const delayed = projectStatus?.delayedMilestones || [];
  const fmtStatus = s => String(s || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  const hasAlerts = summary.overdue > 0 || summary.critical > 0 || summary.at_risk > 0;
  return (
    <div className="orbit-card p-5">
      <h3 className="text-sm font-semibold text-gray-900 tracking-tight mb-4">Project Health</h3>
      {dist.length > 0 && (
        <div className="flex flex-wrap gap-x-5 gap-y-1 mb-4 pb-4 border-b border-gray-100">
          {dist.map(d => (
            <div key={d.status}>
              <span className="text-xl font-bold text-gray-900 tabular-nums">{d.cnt}</span>
              <span className="ml-1.5 text-xs text-gray-500">{fmtStatus(d.status)}</span>
            </div>
          ))}
        </div>
      )}
      {hasAlerts && (
        <div className="flex flex-wrap gap-2 mb-4">
          {summary.overdue > 0 && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-semibold bg-rose-50 text-rose-700 border border-rose-100">
              <CalendarClock size={12} /> {summary.overdue} Overdue
            </span>
          )}
          {summary.critical > 0 && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-semibold bg-rose-50 text-rose-700 border border-rose-100">
              <ShieldAlert size={12} /> {summary.critical} Critical
            </span>
          )}
          {summary.at_risk > 0 && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-100">
              <AlertTriangle size={12} /> {summary.at_risk} At Risk
            </span>
          )}
        </div>
      )}
      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-2">At risk</div>
          {delayed.length === 0 ? (
            <div className="text-xs text-gray-400">Nothing at risk right now.</div>
          ) : (
            <ul className="space-y-2">
              {delayed.slice(0, 5).map(m => {
                const meta = DASHBOARD_HEALTH_META[m.health_status] || DASHBOARD_HEALTH_META['at-risk'];
                return (
                  <li key={m.milestone_id} className="text-xs flex items-start gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full mt-1 shrink-0" style={{ background: meta.color }} />
                    <div className="min-w-0 flex-1">
                      <div className="text-gray-900 truncate font-medium">{m.title}</div>
                      <div className="text-gray-400 truncate">{m.project_name}</div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-2">Upcoming</div>
          {upcoming.length === 0 ? (
            <div className="text-xs text-gray-400">No upcoming due dates.</div>
          ) : (
            <ul className="space-y-2">
              {upcoming.slice(0, 5).map(m => (
                <li key={m.milestone_id} className="text-xs">
                  <div className="text-gray-900 truncate font-medium">{m.title}</div>
                  <div className="text-gray-400 truncate">{m.project_name}{m.due_date ? ` · due ${m.due_date}` : ''}</div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

// Cross-project QA rollup — same query shapes Reports' own quality section already
// computes, just scoped down since the Dashboard doesn't need that endpoint's full payload.
// Small circular progress indicator — plain SVG, no charting dependency. Used to give
// the QA test pass rate the primary visual weight the redesign calls for.
const QaPassRateRing = ({ pct }) => {
  const r = 30, c = 2 * Math.PI * r;
  const clamped = Math.max(0, Math.min(100, pct || 0));
  const color = clamped >= 90 ? '#059669' : clamped >= 70 ? '#d97706' : '#dc2626';
  return (
    <svg width="76" height="76" viewBox="0 0 76 76" className="shrink-0">
      <circle cx="38" cy="38" r={r} fill="none" stroke="#f1f5f9" strokeWidth="8" />
      <circle cx="38" cy="38" r={r} fill="none" stroke={color} strokeWidth="8" strokeLinecap="round"
        strokeDasharray={c} strokeDashoffset={c - (clamped / 100) * c}
        transform="rotate(-90 38 38)" />
      <text x="38" y="34" textAnchor="middle" fontSize="17" fontWeight="700" fill="#111827">{clamped}%</text>
      <text x="38" y="48" textAnchor="middle" fontSize="8" fill="#9ca3af">pass rate</text>
    </svg>
  );
};

const QASummaryCard = ({ qa }) => {
  const bugs = qa?.bugsBySeverity || [];
  const tc = qa?.testCases || { total: 0, passed: 0, failed: 0, pending: 0, passRate: 0 };
  const reqs = qa?.requirements || [];
  return (
    <div className="orbit-card p-5">
      <h3 className="text-sm font-semibold text-gray-900 tracking-tight mb-4">QA Health</h3>
      <div className="flex items-center gap-5 mb-4 pb-4 border-b border-gray-100">
        <QaPassRateRing pct={tc.passRate} />
        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
          <div><span className="text-emerald-600 font-semibold">{tc.passed}</span> <span className="text-gray-500">Passed</span></div>
          <div><span className="text-gray-500 font-semibold">{tc.pending}</span> <span className="text-gray-500">Pending</span></div>
          <div><span className="text-rose-600 font-semibold">{tc.failed}</span> <span className="text-gray-500">Failed</span></div>
          <div><span className="text-gray-900 font-semibold">{tc.total}</span> <span className="text-gray-500">Total</span></div>
        </div>
      </div>
      <div className="mb-4">
        <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-2">Open bugs by severity</div>
        {bugs.length === 0 ? (
          <div className="text-xs text-gray-400">No open bugs.</div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {bugs.map(b => {
              const meta = DASHBOARD_SEVERITY_META[b.severity] || DASHBOARD_SEVERITY_META.low;
              return (
                <span key={b.severity} className="px-2.5 py-1 rounded-md text-xs font-semibold" style={{ background: meta.bg, color: meta.color }}>
                  {(b.severity || '').replace(/\b\w/g, c => c.toUpperCase())} · {b.cnt}
                </span>
              );
            })}
          </div>
        )}
      </div>
      {reqs.length > 0 && (
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-2">Requirements</div>
          <div className="flex flex-wrap gap-3 text-xs text-gray-600">
            {reqs.map(r => (
              <span key={r.status}><strong className="text-gray-900">{r.cnt}</strong> {(r.status || '').replace(/\b\w/g, c => c.toUpperCase())}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const DASHBOARD_REQUEST_STATUS_META = {
  pending: { bg: '#fef3c7', color: '#92400e', label: 'Pending' },
  approved: { bg: '#d1fae5', color: '#065f46', label: 'Approved' },
  inprogress: { bg: '#dbeafe', color: '#1e40af', label: 'In Progress' },
  completed: { bg: '#f3f4f6', color: '#374151', label: 'Completed' },
  rejected: { bg: '#fee2e2', color: '#991b1b', label: 'Rejected' },
};

// Drill-down behind RequestBoard's "Total" number — mirrors BurnChartRemainingModal's
// pattern exactly (lazy-fetch on open, same modal chrome). Reuses the existing
// GET /api/requests list the RequestBoard module itself already fetches; no new endpoint.
const RequestBoardListModal = ({ onClose }) => {
  const [loading, setLoading] = React.useState(true);
  const [items, setItems] = React.useState([]);
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      const d = await ModulesModel.getRequests();
      if (cancelled) return;
      setItems(Array.isArray(d) ? d : []);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(15,23,42,0.45)' }}>
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-xl flex flex-col max-h-[80vh]" style={{ border: '1px solid #e2e8f0' }}>
        <div className="px-6 pt-5 pb-3 border-b border-slate-200 flex items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-bold text-slate-900">All requests</h2>
            <p className="text-xs text-slate-500 mt-1">{items.length} total</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">✕</button>
        </div>
        <div className="overflow-y-auto flex-1">
          {loading ? (
            <div className="text-center py-12 text-slate-400 text-sm">Loading…</div>
          ) : items.length === 0 ? (
            <div className="text-center py-12 text-slate-400 text-sm">No requests yet.</div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {items.map(it => (
                <li key={it.id} className="flex items-center gap-3 px-6 py-2.5">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-slate-900 truncate">{it.title}</div>
                    <div className="text-[11px] text-slate-500 truncate">{it.request_type}{it.requested_by ? ` · ${it.requested_by}` : ''}</div>
                  </div>
                  <span className="text-[11px] font-medium text-slate-600 bg-slate-100 rounded px-2 py-0.5 shrink-0">{it.status}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

// RequestBoard's own stats function (getRequestStats) already computes exactly this shape
// for that module's own page — reused verbatim, no new aggregation.
const RequestBoardCard = ({ stats }) => {
  const [showAll, setShowAll] = React.useState(false);
  const s = stats || { total: 0, pending: 0, approved: 0, rejected: 0, inprogress: 0, completed: 0, todayCount: 0, byType: [] };
  const buckets = ['completed', 'inprogress', 'pending', 'approved', 'rejected'];
  const maxBucket = Math.max(1, ...buckets.map(b => s[b] || 0));
  return (
    <div className="orbit-card p-5">
      <div className="flex items-start justify-between mb-4 pb-4 border-b border-gray-100">
        <div>
          <h3 className="text-sm font-semibold text-gray-900 tracking-tight">RequestBoard</h3>
          <p className="text-xs text-gray-400 mt-0.5">{s.todayCount} submitted today</p>
        </div>
        <div className="text-right">
          <button
            type="button"
            onClick={() => setShowAll(true)}
            className="text-3xl font-bold text-blue-600 hover:text-blue-700 hover:underline leading-none tabular-nums"
            title="See all requests"
          >{s.total}</button>
          <div className="text-[10px] font-medium uppercase tracking-wide text-gray-400">Total</div>
        </div>
      </div>
      <div className="space-y-1.5 mb-4">
        {buckets.map(b => {
          const meta = DASHBOARD_REQUEST_STATUS_META[b];
          const val = s[b] || 0;
          return (
            <div key={b} className="flex items-center gap-3 text-xs">
              <span className="w-20 shrink-0 text-gray-500">{meta.label}</span>
              <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${(val / maxBucket) * 100}%`, background: meta.color }} />
              </div>
              <span className="w-5 text-right font-semibold text-gray-900 tabular-nums">{val}</span>
            </div>
          );
        })}
      </div>
      {(s.byType || []).length > 0 && (
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-2">By type</div>
          <div className="flex flex-wrap gap-3 text-xs text-gray-600">
            {s.byType.slice(0, 5).map(t => (
              <span key={t.request_type}><strong className="text-gray-900">{t.n}</strong> {t.request_type}</span>
            ))}
          </div>
        </div>
      )}
      {showAll && <RequestBoardListModal onClose={() => setShowAll(false)} />}
    </div>
  );
};

// Drill-down behind IncidentBoard's "Open" number — same lazy-fetch-on-open pattern,
// reusing the existing GET /api/incidents list (via getIncidentsData) the IncidentBoard
// module itself already fetches; filtered client-side to match the "Open" metric's own
// definition exactly (not Resolved/Closed) so the modal never disagrees with the count.
const IncidentBoardOpenModal = ({ onClose }) => {
  const [loading, setLoading] = React.useState(true);
  const [items, setItems] = React.useState([]);
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      const d = await ModulesModel.getIncidentsData();
      if (cancelled) return;
      const all = d?.incidents || [];
      setItems(all.filter(i => !['Resolved', 'Closed'].includes(i.status)));
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(15,23,42,0.45)' }}>
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-xl flex flex-col max-h-[80vh]" style={{ border: '1px solid #e2e8f0' }}>
        <div className="px-6 pt-5 pb-3 border-b border-slate-200 flex items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-bold text-slate-900">Open incidents</h2>
            <p className="text-xs text-slate-500 mt-1">{items.length} open</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">✕</button>
        </div>
        <div className="overflow-y-auto flex-1">
          {loading ? (
            <div className="text-center py-12 text-slate-400 text-sm">Loading…</div>
          ) : items.length === 0 ? (
            <div className="text-center py-12 text-slate-400 text-sm">Nothing open — all caught up.</div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {items.map(it => {
                const sevMeta = DASHBOARD_SEVERITY_META[(it.severity || '').toLowerCase()] || DASHBOARD_SEVERITY_META.low;
                return (
                  <li key={it.id} className="flex items-center gap-3 px-6 py-2.5">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-slate-900 truncate">{it.title}</div>
                      <div className="text-[11px] text-slate-500 truncate">{it.category}{it.service_affected ? ` · ${it.service_affected}` : ''}</div>
                    </div>
                    <span className="text-[11px] font-semibold rounded px-2 py-0.5 shrink-0" style={{ background: sevMeta.bg, color: sevMeta.color }}>{it.severity}</span>
                    <span className="text-[11px] font-medium text-slate-600 bg-slate-100 rounded px-2 py-0.5 shrink-0">{it.status}</span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

// IncidentBoard's own stats function (getIncidentsData) already computes exactly this shape
// for that module's own page — reused verbatim (just its `metrics` piece), no new aggregation.
const IncidentBoardCard = ({ metrics }) => {
  const [showOpen, setShowOpen] = React.useState(false);
  const m = metrics || { total: 0, open: 0, critical: 0, resolvedToday: 0, bySeverity: {}, avgResolutionHours: 0 };
  const severities = ['Critical', 'High', 'Medium', 'Low'];
  const sevMeta = { Critical: DASHBOARD_SEVERITY_META.critical, High: DASHBOARD_SEVERITY_META.high, Medium: DASHBOARD_SEVERITY_META.medium, Low: DASHBOARD_SEVERITY_META.low };
  return (
    <div className="orbit-card p-5">
      <div className="flex items-start justify-between mb-4 pb-4 border-b border-gray-100">
        <div>
          <h3 className="text-sm font-semibold text-gray-900 tracking-tight">IncidentBoard</h3>
          <p className="text-xs text-gray-400 mt-0.5">{m.resolvedToday} resolved today{m.avgResolutionHours ? ` · ${m.avgResolutionHours}h avg resolution` : ''}</p>
        </div>
        <div className="text-right">
          <button
            type="button"
            onClick={() => setShowOpen(true)}
            className="text-3xl font-bold text-blue-600 hover:text-blue-700 hover:underline leading-none tabular-nums"
            title="See open incidents"
          >{m.open}</button>
          <div className="text-[10px] font-medium uppercase tracking-wide text-gray-400">Open</div>
        </div>
      </div>
      {m.critical > 0 && (
        <div className="mb-4 flex items-center gap-1.5 text-xs font-semibold text-rose-700 bg-rose-50 border border-rose-100 rounded-md px-2.5 py-1.5 w-fit">
          <ShieldAlert size={12} /> {m.critical} critical incident{m.critical === 1 ? '' : 's'} open
        </div>
      )}
      <div>
        <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-2">By severity</div>
        <div className="flex flex-wrap gap-2">
          {severities.map(sv => {
            const cnt = (m.bySeverity || {})[sv] || 0;
            const meta = sevMeta[sv];
            return (
              <span key={sv} className="px-2.5 py-1 rounded-md text-xs font-semibold" style={{ background: meta.bg, color: meta.color }}>
                {sv} · {cnt}
              </span>
            );
          })}
        </div>
      </div>
      {showOpen && <IncidentBoardOpenModal onClose={() => setShowOpen(false)} />}
    </div>
  );
};

// ============================================================================
// DEPLOYMENT OVERVIEW (Dashboard) — read-only summary of the existing Deployment
// module. Reuses the exact payload the full DeploymentsModule already fetches
// (ModulesModel.getDeployments → { deployments[], stats }); no new endpoint, no
// new aggregation, no change to the approval workflow itself. DPL_STATUS_COLORS
// (defined further below in this file) is the single source of truth for status
// colors and is reused here rather than duplicated.
// ============================================================================
const DASHBOARD_DEPLOYMENT_TERMINAL_STATUSES = new Set([
  'Completed', 'Rejected', 'Cancelled', 'Rollback Required', 'Deployment Failed',
]);

// One canonical status per pipeline stage — mirrors the backend's
// DEPLOYMENT_STAGE_STATUS map exactly (functions/api/[[path]].js), plus the
// pre-submission "Developer" stage (Draft), so every deployment lands in exactly
// one bucket and the visualization can never disagree with the real workflow.
const DASHBOARD_DEPLOYMENT_PIPELINE_DEFS = [
  { key: 'draft',          label: 'Developer', status: 'Draft' },
  { key: 'qa_verify',      label: 'QA',         status: 'Ready for QA' },
  { key: 'devops_review',  label: 'DevOps',     status: 'Pending Technical/DevOps Manager Approval' },
  { key: 'pm_verify',      label: 'PM',         status: 'Pending PM Approval' },
  { key: 'final_approval', label: 'Executive',  status: 'Pending Final Approval' },
  { key: 'execute',        label: 'Production', status: 'Approved for Deployment' },
  { key: 'post_verify',    label: 'Validation', status: 'Deployed – Pending Validation' },
];

const dashboardRelativeTime = (ts) => {
  if (!ts) return '—';
  const d = new Date(ts.includes('T') ? ts : ts.replace(' ', 'T') + 'Z');
  if (isNaN(d.getTime())) return ts;
  const diffDay = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (diffDay <= 0) return 'Today';
  if (diffDay === 1) return 'Yesterday';
  if (diffDay < 7) return `${diffDay}d ago`;
  return d.toLocaleDateString();
};

const DeploymentOverviewSection = ({ deployments, onNavigate, onOpenDeployment }) => {
  const stats = deployments?.stats || {};
  const rows = deployments?.deployments || [];
  if (!rows.length && Object.keys(stats).length === 0) return null;

  const total = Object.values(stats).reduce((a, b) => a + (b || 0), 0);
  const terminalCount = Object.entries(stats).reduce((sum, [k, v]) => sum + (DASHBOARD_DEPLOYMENT_TERMINAL_STATUSES.has(k) ? v : 0), 0);
  const activeCount = total - terminalCount;

  const pipeline = DASHBOARD_DEPLOYMENT_PIPELINE_DEFS.map(s => ({ ...s, count: stats[s.status] || 0 }));

  const metrics = [
    { label: 'Active', value: activeCount },
    { label: 'Awaiting QA', value: stats['Ready for QA'] || 0 },
    { label: 'Awaiting DevOps', value: stats['Pending Technical/DevOps Manager Approval'] || 0 },
    { label: 'Awaiting PM', value: stats['Pending PM Approval'] || 0 },
    { label: 'Awaiting Executive', value: stats['Pending Final Approval'] || 0 },
    { label: 'Ready for Production', value: stats['Approved for Deployment'] || 0 },
  ];

  const activeRows = rows
    .filter(d => !DASHBOARD_DEPLOYMENT_TERMINAL_STATUSES.has(d.status))
    .sort((a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0))
    .slice(0, 8);

  const recentlyDeployed = rows
    .filter(d => d.status === 'Completed' || d.status === 'Deployed – Pending Validation' || d.deployedAt)
    .sort((a, b) => new Date(b.deployedAt || b.completedAt || 0) - new Date(a.deployedAt || a.completedAt || 0))
    .slice(0, 5);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-semibold text-gray-900 tracking-tight">Deployment Overview</h2>
        <button onClick={() => onNavigate('deployments')} className="text-xs font-medium text-blue-600 hover:text-blue-700 inline-flex items-center gap-1">
          View All Deployments <ArrowUpRight size={13} />
        </button>
      </div>

      <div className="orbit-card p-5">
        {/* Summary metrics */}
        <div className="flex flex-wrap gap-x-8 gap-y-3 mb-5 pb-5 border-b border-gray-100">
          {metrics.map(m => (
            <div key={m.label}>
              <div className="text-2xl font-bold text-gray-900 tabular-nums leading-none">{m.value}</div>
              <div className="text-xs text-gray-500 mt-1">{m.label}</div>
            </div>
          ))}
        </div>

        {/* Pipeline */}
        <div className="mb-5 pb-5 border-b border-gray-100">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-3">Approval Pipeline</div>
          <div className="flex items-stretch gap-1 overflow-x-auto">
            {pipeline.map((s, i) => (
              <React.Fragment key={s.key}>
                <div className={`flex-1 min-w-[84px] rounded-lg px-3 py-2.5 text-center ${s.count > 0 ? 'bg-blue-50' : 'bg-gray-50'}`}>
                  <div className={`text-xl font-bold tabular-nums ${s.count > 0 ? 'text-blue-700' : 'text-gray-300'}`}>{s.count}</div>
                  <div className="text-[10px] font-medium text-gray-500 mt-0.5 truncate">{s.label}</div>
                </div>
                {i < pipeline.length - 1 && (
                  <div className="flex items-center text-gray-300 shrink-0 px-0.5">
                    <ChevronRight size={16} />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Active deployments */}
        <div className={recentlyDeployed.length > 0 ? 'mb-5 pb-5 border-b border-gray-100' : ''}>
          <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-2">Active Deployments</div>
          {activeRows.length === 0 ? (
            <div className="text-xs text-gray-400 py-2">No deployments currently in progress.</div>
          ) : (
            <div className="overflow-x-auto -mx-1">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-gray-400">
                    <th className="text-left font-medium px-1 pb-2">Deployment</th>
                    <th className="text-left font-medium px-1 pb-2">Project</th>
                    <th className="text-left font-medium px-1 pb-2">Status</th>
                    <th className="text-right font-medium px-1 pb-2">Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {activeRows.map(d => {
                    const pill = DPL_STATUS_COLORS[d.status] || { bg: '#f3f4f6', color: '#374151' };
                    return (
                      <tr key={d.id} onClick={() => onOpenDeployment(d.id)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpenDeployment(d.id); } }} tabIndex={0} role="button" aria-label={`Open deployment ${d.deploymentCode}`} className="cursor-pointer hover:bg-gray-50 border-t border-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-inset">
                        <td className="px-1 py-2 font-medium text-gray-900 whitespace-nowrap">{d.deploymentCode}</td>
                        <td className="px-1 py-2 text-gray-600 truncate max-w-[160px]">{d.projectName || d.projectApplication || '—'}</td>
                        <td className="px-1 py-2">
                          <span className="px-2 py-0.5 rounded text-[11px] font-semibold whitespace-nowrap" style={{ background: pill.bg, color: pill.color }}>{d.status}</span>
                        </td>
                        <td className="px-1 py-2 text-right text-gray-400 whitespace-nowrap">{dashboardRelativeTime(d.updatedAt)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Recently deployed */}
        {recentlyDeployed.length > 0 && (
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-2">Recently Deployed</div>
            <ul className="space-y-1.5">
              {recentlyDeployed.map(d => (
                <li key={d.id} onClick={() => onOpenDeployment(d.id)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpenDeployment(d.id); } }} tabIndex={0} role="button" aria-label={`Open deployment ${d.deploymentCode}`} className="flex items-center justify-between text-xs cursor-pointer hover:bg-gray-50 rounded-md px-1 py-1 -mx-1 focus:outline-none focus:ring-2 focus:ring-blue-400">
                  <div className="min-w-0 flex-1">
                    <span className="font-medium text-gray-900">{d.deploymentCode}</span>
                    <span className="text-gray-400"> · {d.projectName || d.projectApplication || '—'}</span>
                  </div>
                  <span className="text-gray-400 shrink-0 ml-2">{dashboardRelativeTime(d.deployedAt || d.completedAt)}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

// "What needs my attention right now?" — composed entirely from data the Dashboard
// already fetches (stats, enhancements, deployments); no new backend call, no
// invented statuses. Rows with a zero count are simply omitted.
const DASHBOARD_ATTENTION_TONES = {
  rose:  { bg: 'bg-rose-50',  icon: 'text-rose-600' },
  amber: { bg: 'bg-amber-50', icon: 'text-amber-600' },
  blue:  { bg: 'bg-blue-50',  icon: 'text-blue-600' },
};
const NeedsAttentionSection = ({ data, enhancements, deployments, onNavigate }) => {
  const statValue = (label) => Number((data?.stats || []).find(s => s.label === label)?.value || 0);
  const openBugs = statValue('Open Bugs');
  const highRisks = statValue('High Risks');
  const atRisk = enhancements?.projectStatus?.summary?.at_risk || 0;
  const overdue = enhancements?.projectStatus?.summary?.overdue || 0;
  const qaPending = enhancements?.qa?.testCases?.pending || 0;
  const requestsWaiting = (enhancements?.requestBoard?.pending || 0) + (enhancements?.requestBoard?.inprogress || 0);

  const depStats = deployments?.stats || {};
  const depAwaiting = (depStats['Ready for QA'] || 0)
    + (depStats['Pending Technical/DevOps Manager Approval'] || 0)
    + (depStats['Pending PM Approval'] || 0)
    + (depStats['Pending Final Approval'] || 0);

  // at_risk/overdue come from getExecutiveMilestoneSummary, which counts *milestones*
  // grouped by health_status — not distinct projects, so this must read "Milestones",
  // not "Projects" (a project with several overdue milestones would otherwise inflate
  // the displayed project count).
  const milestoneIssues = atRisk + overdue;

  const items = [
    openBugs > 0 && {
      icon: Bug, tone: 'rose', title: `${openBugs} Open Bug${openBugs === 1 ? '' : 's'}`,
      subtitle: 'QA', onClick: () => onNavigate('quality'),
    },
    milestoneIssues > 0 && {
      icon: AlertTriangle, tone: 'amber',
      title: `${milestoneIssues} Milestone${milestoneIssues === 1 ? '' : 's'} ${overdue > 0 && atRisk > 0 ? 'At Risk / Overdue' : overdue > 0 ? 'Overdue' : 'At Risk'}`,
      subtitle: 'Review required', onClick: () => onNavigate('plantrack'),
    },
    highRisks > 0 && {
      icon: ShieldAlert, tone: 'rose', title: `${highRisks} High Risk${highRisks === 1 ? '' : 's'}`,
      subtitle: 'Risk register', onClick: () => onNavigate('plantrack'),
    },
    depAwaiting > 0 && {
      icon: Rocket, tone: 'blue', title: `${depAwaiting} Deployment${depAwaiting === 1 ? '' : 's'} Awaiting Approval`,
      subtitle: 'Review required', onClick: () => onNavigate('deployments'),
    },
    qaPending > 0 && {
      icon: ListChecks, tone: 'amber', title: `${qaPending} QA Test${qaPending === 1 ? '' : 's'} Pending`,
      subtitle: 'Test execution', onClick: () => onNavigate('quality'),
    },
    requestsWaiting > 0 && {
      icon: FileText, tone: 'blue', title: `${requestsWaiting} Request${requestsWaiting === 1 ? '' : 's'} Awaiting Action`,
      subtitle: 'RequestBoard', onClick: () => onNavigate('requestboard'),
    },
  ].filter(Boolean);

  return (
    <div>
      <h2 className="text-base font-semibold text-gray-900 tracking-tight mb-4">Needs Attention</h2>
      {items.length === 0 ? (
        <div className="orbit-card p-4 flex items-center gap-2 text-sm text-gray-500">
          <CheckCircle2 size={16} className="text-emerald-500 shrink-0" /> All clear — nothing needs your attention right now.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.map((it, i) => {
            const tone = DASHBOARD_ATTENTION_TONES[it.tone];
            const Icon = it.icon;
            return (
              <button key={i} onClick={it.onClick} className="orbit-card p-4 flex items-start gap-3 text-left hover:border-gray-300">
                <div className={`w-9 h-9 rounded-lg ${tone.bg} flex items-center justify-center shrink-0`}>
                  <Icon size={16} className={tone.icon} />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-gray-900">{it.title}</div>
                  <div className="text-xs text-gray-400 mt-0.5">{it.subtitle}</div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const DASHBOARD_KPI_META = {
  'Active Tasks':  { icon: ListChecks,   tone: 'blue' },
  'Completed':     { icon: CheckCircle2, tone: 'emerald' },
  'Open Bugs':     { icon: Bug,          tone: 'rose' },
  'Pending Leave': { icon: CalendarClock, tone: 'amber' },
  'High Risks':    { icon: ShieldAlert,  tone: 'rose' },
};
const DASHBOARD_KPI_TONE = {
  blue:    { bg: 'bg-blue-50',    icon: 'text-blue-600' },
  emerald: { bg: 'bg-emerald-50', icon: 'text-emerald-600' },
  rose:    { bg: 'bg-rose-50',    icon: 'text-rose-600' },
  amber:   { bg: 'bg-amber-50',   icon: 'text-amber-600' },
  gray:    { bg: 'bg-gray-50',    icon: 'text-gray-500' },
};

const DashboardHeader = ({ user, data, enhancements }) => {
  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';
  const firstName = (user?.name || user?.user_name || '').split(' ')[0];
  const today = new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });

  const activeTasks = Number((data?.stats || []).find(s => s.label === 'Active Tasks')?.value || 0);
  const openBugs = Number((data?.stats || []).find(s => s.label === 'Open Bugs')?.value || 0);
  const activeProjects = (enhancements?.projectStatus?.distribution || []).find(d => d.status === 'active')?.cnt || 0;

  const summaryParts = [];
  if (activeProjects > 0) summaryParts.push(`${activeProjects} active project${activeProjects === 1 ? '' : 's'}`);
  summaryParts.push(`${activeTasks} active task${activeTasks === 1 ? '' : 's'}`);
  summaryParts.push(`${openBugs} open bug${openBugs === 1 ? '' : 's'}`);

  return (
    <div>
      <div className="text-xs font-medium text-gray-400">{today}</div>
      <h1 className="text-2xl font-bold text-gray-900 tracking-tight mt-0.5">
        {greeting}{firstName ? `, ${firstName}` : ''} <span className="text-gray-400 font-semibold">— Operations Overview</span>
      </h1>
      <p className="text-sm text-gray-500 mt-1.5">{summaryParts.join(' · ')}</p>
    </div>
  );
};

const DashboardView = ({ data, burnCharts, enhancements, deployments, onNavigate, onOpenDeployment, user }) => {
  const allowedModules = getAllowedModules(user);
  const [activityPage, setActivityPage] = useState(0);
  const ACTIVITY_PAGE_SIZE = 5;
  const activities = data.activities || [];
  const totalActivityPages = Math.ceil(activities.length / ACTIVITY_PAGE_SIZE);
  // Clamp if a data refresh shrinks the list out from under the current page —
  // otherwise the Prev/Next controls disappear (totalActivityPages <= activityPage)
  // while the table renders empty, with no way back to page 0.
  useEffect(() => {
    if (activityPage > 0 && activityPage >= totalActivityPages) setActivityPage(Math.max(0, totalActivityPages - 1));
  }, [totalActivityPages]);
  const pagedActivities = activities.slice(activityPage * ACTIVITY_PAGE_SIZE, (activityPage + 1) * ACTIVITY_PAGE_SIZE);

  // Quick Access, enriched with a few more already-loaded modules (Quality, RequestBoard,
  // IncidentBoard, Deployments) alongside the original four — all counts come from data
  // this page already fetched, nothing new is called for this.
  const extraQuickModules = [];
  if (enhancements?.qa) {
    const openBugsCount = Number((data.stats || []).find(s => s.label === 'Open Bugs')?.value || 0);
    extraQuickModules.push({ id: 'quality', name: 'Quality (QMS)', icon: '🧪', count: `${openBugsCount} open bug${openBugsCount === 1 ? '' : 's'}` });
  }
  if (enhancements?.requestBoard) {
    const active = (enhancements.requestBoard.pending || 0) + (enhancements.requestBoard.inprogress || 0);
    extraQuickModules.push({ id: 'requestboard', name: 'RequestBoard', icon: '📮', count: `${active} active` });
  }
  if (enhancements?.incidentBoard) {
    extraQuickModules.push({ id: 'incidentboard', name: 'IncidentBoard', icon: '🚨', count: `${enhancements.incidentBoard.open || 0} open` });
  }
  if (deployments?.stats) {
    const activeDep = Object.entries(deployments.stats).reduce((s, [k, v]) => s + (DASHBOARD_DEPLOYMENT_TERMINAL_STATUSES.has(k) ? 0 : v), 0);
    extraQuickModules.push({ id: 'deployments', name: 'Deployments', icon: '🚀', count: `${activeDep} active` });
  }
  const allQuickModules = [...data.quickModules, ...extraQuickModules];
  const visibleQuickModules = allowedModules
    ? allQuickModules.filter(m => allowedModules.includes(m.id))
    : allQuickModules;

  return (
  <div className="max-w-7xl mx-auto px-4 py-7 space-y-8">
    <DashboardHeader user={user} data={data} enhancements={enhancements} />

    {/* KPI Summary */}
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
      {data.stats.map((stat, i) => {
        const meta = DASHBOARD_KPI_META[stat.label] || { icon: Activity, tone: 'gray' };
        const tone = DASHBOARD_KPI_TONE[meta.tone];
        const Icon = meta.icon;
        return (
          <div key={i} className="orbit-card p-5">
            <div className="flex justify-between items-start">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">{stat.label}</div>
              <div className={`w-8 h-8 rounded-lg ${tone.bg} flex items-center justify-center shrink-0`}>
                <Icon size={15} className={tone.icon} />
              </div>
            </div>
            <div className="text-3xl font-bold text-gray-900 mt-2 tabular-nums leading-none">{stat.value}</div>
            <div className="text-xs text-gray-400 mt-2">{stat.change ? `${stat.change} ` : ''}from last week</div>
          </div>
        );
      })}
    </div>

    <NeedsAttentionSection data={data} enhancements={enhancements} deployments={deployments} onNavigate={onNavigate} />

    <DeploymentOverviewSection deployments={deployments} onNavigate={onNavigate} onOpenDeployment={onOpenDeployment} />

    {/* Delivery Performance */}
    {burnCharts && (burnCharts.devops || burnCharts.dataops) && (
      <div>
        <h2 className="text-base font-semibold text-gray-900 tracking-tight mb-4">Delivery Performance</h2>
        <div className="grid lg:grid-cols-2 gap-6">
          {burnCharts.devops ? (
            <BurnChart
              title={burnCharts.devops.sprintName ? `DevOps — ${burnCharts.devops.sprintName}` : 'DevOps — Last 14 Days'}
              subtitle={burnCharts.devops.sprintName ? `Sprint: ${burnCharts.devops.startDate} → ${burnCharts.devops.endDate} · Sprint tasks + ad hoc tasks` : 'All DevOps tasks (sprint tasks + ad hoc) completed'}
              days={burnCharts.devops.days}
              burndown={burnCharts.devops.burndown}
              burnup={burnCharts.devops.burnup}
              ideal={burnCharts.devops.ideal}
              total={burnCharts.devops.total}
              completedCount={burnCharts.devops.completedCount}
              unit={burnCharts.devops.unit || ' tasks'}
              module="devops"
            />
          ) : (
            <div className="orbit-card p-6 flex items-center justify-center text-gray-400 text-sm">
              No active sprint — DevOps burn chart unavailable
            </div>
          )}
          {burnCharts.dataops ? (
            <BurnChart
              title={burnCharts.dataops.sprintName ? `DataOps — ${burnCharts.dataops.sprintName}` : 'DataOps — Last 14 Days'}
              subtitle={burnCharts.dataops.sprintName ? `Sprint: ${burnCharts.dataops.startDate} → ${burnCharts.dataops.endDate} · Sprint tasks + ad hoc tasks` : 'All DataOps tasks (sprint tasks + ad hoc) completed'}
              days={burnCharts.dataops.days}
              burndown={burnCharts.dataops.burndown}
              burnup={burnCharts.dataops.burnup}
              ideal={burnCharts.dataops.ideal}
              total={burnCharts.dataops.total}
              completedCount={burnCharts.dataops.completedCount}
              unit={burnCharts.dataops.unit || ' tasks'}
              module="dataops"
            />
          ) : (
            <div className="orbit-card p-6 flex items-center justify-center text-gray-400 text-sm">
              No pipeline data — DataOps burn chart unavailable
            </div>
          )}
        </div>
      </div>
    )}

    {/* Team & Quality */}
    {(burnCharts?.devops_cycle || burnCharts?.dataops_cycle || enhancements) && (
      <div>
        <h2 className="text-base font-semibold text-gray-900 tracking-tight mb-4">Team &amp; Quality</h2>
        <div className="grid lg:grid-cols-2 gap-6">
          {(burnCharts?.devops_cycle || burnCharts?.dataops_cycle) && (
            <>
              <CycleTimeChart
                title="DevOps Cycle Time"
                avgDays={burnCharts.devops_cycle?.avgDays ?? 0}
                total={burnCharts.devops_cycle?.total ?? 0}
                points={burnCharts.devops_cycle?.points ?? []}
                performance={enhancements?.performance?.devops}
              />
              <CycleTimeChart
                title="DataOps Cycle Time"
                avgDays={burnCharts.dataops_cycle?.avgDays ?? 0}
                total={burnCharts.dataops_cycle?.total ?? 0}
                points={burnCharts.dataops_cycle?.points ?? []}
                performance={enhancements?.performance?.dataops}
              />
            </>
          )}
          {enhancements && (
            <>
              <ProjectHealthCard projectStatus={enhancements.projectStatus} />
              <QASummaryCard qa={enhancements.qa} />
            </>
          )}
        </div>
      </div>
    )}

    {/* Operations */}
    {enhancements && (enhancements.requestBoard || enhancements.incidentBoard) && (
      <div>
        <h2 className="text-base font-semibold text-gray-900 tracking-tight mb-4">Operations</h2>
        <div className="grid lg:grid-cols-2 gap-6">
          <RequestBoardCard stats={enhancements.requestBoard} />
          <IncidentBoardCard metrics={enhancements.incidentBoard} />
        </div>
      </div>
    )}

    {/* Activity */}
    <div className="grid lg:grid-cols-3 gap-6">
      {/* Recent Activity */}
      <div className="lg:col-span-2 orbit-card">
        <div className="px-5 py-4 border-b border-gray-100">
          <h2 className="text-sm font-semibold text-gray-900 tracking-tight">Recent Activity</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="px-5 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-400">Action</th>
                <th className="px-5 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-400">Item</th>
                <th className="px-5 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-400">User</th>
                <th className="px-5 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-400">Time</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {pagedActivities.map((activity, i) => (
                <tr key={i} className="hover:bg-gray-50/80 transition-colors">
                  <td className="px-5 py-3">
                    <span className="px-2.5 py-1 bg-blue-50 text-blue-700 rounded-md text-xs font-medium">
                      {activity.action}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-sm text-gray-900">{activity.item}</td>
                  <td className="px-5 py-3 text-sm text-gray-500">{activity.user}</td>
                  <td className="px-5 py-3 text-xs text-gray-400">{activity.time}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {totalActivityPages > 1 && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100">
            <span className="text-xs text-gray-400">
              {activityPage * ACTIVITY_PAGE_SIZE + 1}–{Math.min((activityPage + 1) * ACTIVITY_PAGE_SIZE, activities.length)} of {activities.length}
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => setActivityPage(p => p - 1)}
                disabled={activityPage === 0}
                className="px-3 py-1 text-xs font-medium border border-gray-200 rounded-md hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >Prev</button>
              <button
                onClick={() => setActivityPage(p => p + 1)}
                disabled={activityPage >= totalActivityPages - 1}
                className="px-3 py-1 text-xs font-medium border border-gray-200 rounded-md hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >Next</button>
            </div>
          </div>
        )}
      </div>

      {/* Quick Access */}
      <div className="orbit-card">
        <div className="px-5 py-4 border-b border-gray-100">
          <h2 className="text-sm font-semibold text-gray-900 tracking-tight">Quick Access</h2>
        </div>
        <div className="p-3 space-y-1">
          {visibleQuickModules.map((module, i) => (
            <div key={i} onClick={() => onNavigate(module.id)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onNavigate(module.id); } }} tabIndex={0} role="button" aria-label={`Open ${module.name}`} className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-400">
              <div className="text-2xl">{module.icon}</div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-gray-900">{module.name}</div>
                <div className="text-xs text-gray-400">{module.count}</div>
              </div>
              <ChevronRight className="text-gray-300" size={16} />
            </div>
          ))}
        </div>
      </div>
    </div>

  </div>
  );
};

const ModulesView = ({ modules, onNavigate, user }) => {
  const allowedModules = getAllowedModules(user);
  const visibleModules = allowedModules
    ? modules.filter(m => allowedModules.includes(m.id) || ALWAYS_ALLOWED_PAGES.includes(m.id))
    : modules;
  return (
  <div className="max-w-7xl mx-auto px-4 py-6">
    <div className="mb-6">
      <h1 className="text-3xl font-bold text-gray-900">ORBIT Modules</h1>
      <p className="text-gray-600 mt-1">Core API modules and shared services for the ORBIT tracking system.</p>
    </div>

    <div className="mb-4">
      <h2 className="text-xl font-semibold text-gray-900 pb-3 border-b-2 border-gray-200">
        Core API Modules
      </h2>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {visibleModules.map(module => (
        <div key={module.id} onClick={() => onNavigate(module.id)} className="bg-white rounded-lg shadow border border-gray-200 p-6 hover:shadow-xl hover:border-blue-500 transition-all cursor-pointer">
          <div className="flex items-center justify-center w-14 h-14 rounded-xl text-2xl mb-4" 
               style={{ background: `${module.color}20`, color: module.color }}>
            {module.icon}
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">{module.name}</h3>
          <p className="text-gray-600 text-sm mb-4 leading-relaxed">{module.description}</p>
          <div className="mt-4 pt-4 border-t border-gray-100 flex justify-between items-center">
            <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
              {module.status}
            </span>
            <span className="text-gray-600 text-sm">{(module.records ?? 0).toLocaleString()} records</span>
          </div>
        </div>
      ))}
    </div>
  </div>
  );
};

// ---- DevOps Badge Helpers ----
const getTypeBadge = (type) => {
  const normalized = (type || '').toUpperCase();
  const badges = {
    TASK:    { color: '#0366d6', bg: '#d4e4ff',  label: 'Task' },
    PROJECT: { color: '#6f42c1', bg: '#f3e8ff',  label: 'Project' },
    BUG:     { color: '#dc3545', bg: '#ffe5e9',  label: 'Bug' },
    ADHOC:   { color: '#e36209', bg: '#fff5cc',  label: 'Ad-hoc' },
    STORY:   { color: '#17a2b8', bg: '#d4f4ff',  label: 'Story' },
    FEATURE: { color: '#28a745', bg: '#dcffe4',  label: 'Feature' },
  };
  return badges[normalized] || { color: '#586069', bg: '#f6f8fa', label: type || 'Task' };
};

const getDevOpsPriorityBadge = (priority) => {
  const badges = {
    P1: { color: '#dc3545', bg: '#ffe5e9' },
    P2: { color: '#ffc107', bg: '#fff5cc' },
    P3: { color: '#28a745', bg: '#dcffe4' }
  };
  return badges[priority] || { color: '#586069', bg: '#f6f8fa' };
};

const getOpsStatusStyle = (status) => {
  const styles = {
    running: { color: '#0366d6', bg: '#d4e4ff', label: 'Running' },
    completed: { color: '#28a745', bg: '#dcffe4', label: 'Completed' },
    failed: { color: '#dc3545', bg: '#ffe5e9', label: 'Failed' },
    idle: { color: '#586069', bg: '#f6f8fa', label: 'Idle' },
    queued: { color: '#6f42c1', bg: '#f3e8ff', label: 'Queued' }
  };
  return styles[status] || { color: '#586069', bg: '#f6f8fa', label: status };
};

const stripHtml = (html) => (html || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

const formatDuration = (seconds) => {
  if (!seconds) return '-';
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
};

const formatRecords = (count) => {
  if (!count) return '0';
  if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`;
  if (count >= 1000) return `${(count / 1000).toFixed(0)}K`;
  return count.toString();
};

// ---- DevOps Task Card ----
const DevOpsTaskCard = ({ item, currentStatus, onClick, onStatusChange, onDelete, canDelete }) => {
  const [showActualTime, setShowActualTime] = useState(false);
  const typeBadge = getTypeBadge(item.type);
  const priorityBadge = getDevOpsPriorityBadge(item.priority);
  const isQuickTask = !!item.isQuickTask || String(item.id).startsWith('QT-');
  const isBug = !!item.isBug || String(item.id).startsWith('BUG-');

  const allStatuses = isQuickTask
    ? ['backlog', 'in_progress', 'done']
    : isBug
      ? ['in_progress', 'review', 'needs_rework', 'done']
      : ['backlog', 'ready', 'in_progress', 'review', 'done'];

  const moveOptions = allStatuses.filter(s => s !== currentStatus);
  const statusLabels = { backlog: 'Backlog', ready: 'Ready', in_progress: 'In Progress', review: 'Review', needs_rework: 'Needs Rework', done: 'Done' };
  const cardBg = isBug ? 'bg-red-50 border-red-200' : isQuickTask ? 'bg-amber-50 border-amber-200' : 'bg-white border-gray-200';
  const barColor = isBug ? 'bg-red-500' : isQuickTask ? 'bg-amber-500' : 'bg-blue-600';

  const estMins = item.estimatedMinutes || item.estimated_minutes ||
    (item.estimateHours ? Math.round(item.estimateHours * 60) : 0) ||
    (item.estimate ? item.estimate * 60 : 0);
  const actMins = item.actualMinutes || item.actual_minutes ||
    (item.actualHours ? Math.round(item.actualHours * 60) : 0);

  const handleMoveSelect = (e) => {
    e.stopPropagation();
    const val = e.target.value;
    if (!val) return;
    if (val === 'done') { setShowActualTime(true); return; }
    onStatusChange(item.id, val);
    e.target.value = '';
  };

  return (
    <div className={`border rounded-lg p-3 hover:shadow-md transition-shadow cursor-pointer ${cardBg}`} onClick={() => onClick(item.id)}>
      {showActualTime && (
        <ActualTimeModal
          taskTitle={item.title}
          onConfirm={mins => { setShowActualTime(false); onStatusChange(item.id, 'done', { actualMinutes: mins }); }}
          onCancel={() => setShowActualTime(false)}
        />
      )}
      <div className="flex items-start justify-between gap-1 mb-2">
        <div className="flex flex-wrap gap-1.5 flex-1 min-w-0">
          <span className="text-xs font-mono text-gray-400">{item.bugCode || item.id}</span>
          <span className="px-2 py-0.5 rounded text-xs font-semibold" style={{ background: typeBadge.bg, color: typeBadge.color }}>{typeBadge.label}</span>
          {isBug && item.severity && <span className="px-2 py-0.5 rounded text-xs font-semibold bg-red-100 text-red-700">{item.severity}</span>}
          {isQuickTask && item.category && <span className="px-2 py-0.5 rounded text-xs font-semibold bg-amber-100 text-amber-700">{item.category}</span>}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <span className="px-2 py-0.5 rounded text-xs font-semibold" style={{ background: priorityBadge.bg, color: priorityBadge.color }}>{item.priority}</span>
          {canDelete && isQuickTask && (
            <button onClick={e => { e.stopPropagation(); requestConfirm(`Delete "${item.title}"?`, () => onDelete(item.id)); }}
              className="text-gray-300 hover:text-red-500 text-base leading-none" title="Delete task">×</button>
          )}
        </div>
      </div>
      <div className="text-sm font-medium text-gray-900 mb-1 leading-snug break-words">{item.title}</div>
      {/* Description — always shown */}
      {item.description
        ? <div className="text-xs text-gray-500 mb-2 leading-snug line-clamp-2 break-words">{stripHtml(item.description)}</div>
        : <div className="text-xs text-gray-300 italic mb-2">No description</div>
      }
      {item.projectName && <div className="text-xs text-blue-600 font-medium mb-1 truncate" title={item.projectName}>{item.projectName}</div>}
      {item.assignee && <div className="text-xs text-gray-500 mb-1 break-words">👤 {item.assignee}</div>}
      {/* Time — always shown */}
      <div className="flex gap-3 text-xs mb-1">
        <span className={estMins > 0 ? 'text-gray-500' : 'text-red-400 font-medium'}>
          ⏱ Est: {estMins > 0 ? `${estMins}m` : 'not set'}
        </span>
        {actMins > 0 && (
          <span className={actMins > estMins && estMins > 0 ? 'text-red-500 font-medium' : 'text-gray-500'}>
            ✓ Act: {actMins}m
          </span>
        )}
        {item.storyPoints > 0 && <span className="text-gray-500">◆ {item.storyPoints} pts</span>}
      </div>
      {item.progress > 0 && (
        <div className="mt-1.5 h-1 bg-gray-200 rounded-full overflow-hidden">
          <div className={`h-full rounded-full ${barColor}`} style={{ width: `${item.progress}%` }} />
        </div>
      )}
      {onStatusChange && moveOptions.length > 0 && (
        <div className="mt-2 pt-2 border-t border-gray-100" onClick={e => e.stopPropagation()}>
          <select
            value=""
            onChange={handleMoveSelect}
            className="w-full text-xs border border-gray-200 rounded px-2 py-1 text-gray-500 bg-white"
          >
            <option value="">Move to…</option>
            {moveOptions.map(s => <option key={s} value={s}>{statusLabels[s] || s}</option>)}
          </select>
        </div>
      )}
    </div>
  );
};

// ---- DevOps Capacity Cards ----
const DevOpsCapacityCards = ({ capacity }) => (
  <div className="mb-6">
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <div className="text-sm text-gray-600 mb-2">Total Capacity</div>
        <div className="text-4xl font-bold text-blue-700">{capacity.total}h</div>
      </div>
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
        <div className="text-sm text-gray-600 mb-2">Committed</div>
        <div className="text-4xl font-bold text-yellow-700">{capacity.committed}h</div>
        <div className="text-sm text-gray-600 mt-1">{capacity.utilization}% utilized</div>
      </div>
      <div className="bg-green-50 border border-green-200 rounded-lg p-6">
        <div className="text-sm text-gray-600 mb-2">Completed</div>
        <div className="text-4xl font-bold text-green-700">{capacity.completed}h</div>
        <div className="text-sm text-gray-600 mt-1">actual hours</div>
      </div>
    </div>
  </div>
);

// ---- DevOps Team Workload ----
const DevOpsTeamWorkload = ({ workload: initialWorkload, workloadRange: initialRange, sprints = [] }) => {
  const [preset, setPreset] = useState(initialRange?.source || 'auto');
  const [customStart, setCustomStart] = useState(initialRange?.start || '');
  const [customEnd, setCustomEnd] = useState(initialRange?.end || '');
  const [workload, setWorkload] = useState(initialWorkload || []);
  const [activeRange, setActiveRange] = useState(initialRange || null);
  const [loading, setLoading] = useState(false);

  // Sync from parent when first load completes
  useEffect(() => { setWorkload(initialWorkload || []); }, [initialWorkload]);
  useEffect(() => {
    if (initialRange) {
      setActiveRange(initialRange);
      setCustomStart(initialRange.start || '');
      setCustomEnd(initialRange.end || '');
    }
  }, [initialRange?.start, initialRange?.end, initialRange?.source]);

  const fetchWith = async (start, end, sourceLabel) => {
    setLoading(true);
    const data = await ModulesModel.getDevOpsPipeline({ start, end });
    setLoading(false);
    setWorkload(data?.workload || []);
    setActiveRange(data?.workloadRange || { start, end, source: sourceLabel });
  };

  const applyPreset = (key) => {
    setPreset(key);
    if (key === 'auto') {
      // Re-fetch with no params → server picks active sprint or rolling 14d
      ModulesModel.getDevOpsPipeline().then(d => {
        setWorkload(d?.workload || []);
        setActiveRange(d?.workloadRange || null);
      });
      return;
    }
    const today = new Date(); const todayStr = today.toISOString().slice(0, 10);
    if (key === '14d') {
      const d = new Date(today); d.setDate(d.getDate() - 13);
      fetchWith(d.toISOString().slice(0, 10), todayStr, '14d');
    } else if (key === '30d') {
      const d = new Date(today); d.setDate(d.getDate() - 29);
      fetchWith(d.toISOString().slice(0, 10), todayStr, '30d');
    } else if (key === 'quarter') {
      const m = today.getMonth(); const qStart = new Date(today.getFullYear(), Math.floor(m / 3) * 3, 1);
      const qEnd = new Date(today.getFullYear(), Math.floor(m / 3) * 3 + 3, 0);
      fetchWith(qStart.toISOString().slice(0, 10), qEnd.toISOString().slice(0, 10), 'quarter');
    } else if (key === 'mtd') {
      const start = new Date(today.getFullYear(), today.getMonth(), 1);
      fetchWith(start.toISOString().slice(0, 10), todayStr, 'mtd');
    } else if (key.startsWith('sprint:')) {
      const id = parseInt(key.slice(7));
      const s = sprints.find(x => x.id === id);
      if (s) fetchWith(s.startDate, s.endDate, 'sprint');
    } else if (key === 'custom') {
      // wait for the user to type both dates and click Apply
    }
  };

  const applyCustom = () => {
    if (!customStart || !customEnd) return;
    fetchWith(customStart, customEnd, 'custom');
  };

  const sourceLabelText = (() => {
    const r = activeRange;
    if (!r) return '';
    if (r.source === 'sprint') return 'Active sprint';
    if (r.source === 'rolling14') return 'Last 14 days';
    if (r.source === '14d') return 'Last 14 days';
    if (r.source === '30d') return 'Last 30 days';
    if (r.source === 'mtd') return 'Month to date';
    if (r.source === 'quarter') return 'Current quarter';
    if (r.source === 'custom') return 'Custom range';
    return r.source;
  })();

  return (
  <div className="bg-white rounded-lg shadow p-6 mb-6">
    <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
      <h3 className="text-lg font-semibold">Team Workload</h3>
      <span className="text-xs text-gray-400">Estimated hours from sprint + QuickTrack tasks vs capacity. Actual hours shown alongside.</span>
    </div>

    {/* Date-range controls */}
    <div className="flex items-center gap-2 flex-wrap mb-4 pb-3 border-b border-gray-100">
      <span className="text-xs font-medium text-gray-600 uppercase tracking-wide">Range:</span>
      {[
        { key: 'auto', label: 'Auto' },
        { key: '14d', label: 'Last 14 days' },
        { key: '30d', label: 'Last 30 days' },
        { key: 'mtd', label: 'Month to date' },
        { key: 'quarter', label: 'This quarter' },
      ].map(p => (
        <button key={p.key} onClick={() => applyPreset(p.key)}
          className={`px-2.5 py-1 text-xs rounded border transition-colors ${preset === p.key ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}>
          {p.label}
        </button>
      ))}
      {sprints.length > 0 && (
        <select value={preset.startsWith('sprint:') ? preset : ''} onChange={e => e.target.value && applyPreset(e.target.value)}
          className="text-xs border border-gray-300 rounded px-2 py-1 bg-white">
          <option value="">— Pick sprint —</option>
          {sprints.map(s => (
            <option key={s.id} value={`sprint:${s.id}`}>{s.name} · {s.startDate} → {s.endDate}{s.status === 'active' ? ' (active)' : ''}</option>
          ))}
        </select>
      )}
      <span className="text-xs text-gray-400 mx-1">|</span>
      <input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)}
        className="text-xs border border-gray-300 rounded px-2 py-1" />
      <span className="text-xs text-gray-400">→</span>
      <input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)}
        className="text-xs border border-gray-300 rounded px-2 py-1" />
      <button onClick={() => { setPreset('custom'); applyCustom(); }} disabled={!customStart || !customEnd}
        className="px-2.5 py-1 text-xs rounded bg-gray-800 text-white hover:bg-black disabled:opacity-40">Apply</button>

      {activeRange && (
        <span className="ml-auto text-xs text-gray-500">
          {loading ? 'Loading…' : <>{sourceLabelText} · <span className="font-medium text-gray-700">{activeRange.start} → {activeRange.end}</span></>}
        </span>
      )}
    </div>

    <div className="space-y-4">
      {workload.length === 0 && (
        <div className="text-sm text-gray-400 italic text-center py-6">No completed tasks in this range.</div>
      )}
      {workload.map((member, i) => {
        const sprintEst = member.sprintEstimated ?? member.timesheetHours ?? 0;
        const sprintAct = member.sprintActual ?? 0;
        const qtEst = member.qtEstimated ?? member.qtHours ?? 0;
        const qtAct = member.qtActual ?? 0;
        const sprintWidth = member.capacity > 0 ? Math.min(sprintEst * 100 / member.capacity, 100) : 0;
        const qtWidth = member.capacity > 0 ? Math.min(qtEst * 100 / member.capacity, 100 - sprintWidth) : 0;
        const overCapacity = member.percentage > 100;
        return (
          <div key={i}>
            <div className="flex items-center gap-4 mb-1">
              <div className="w-36 text-sm text-gray-700 truncate">{member.name}</div>
              <div className="flex-1 h-7 bg-gray-200 rounded-lg overflow-hidden flex">
                {sprintWidth > 0 && (
                  <div
                    className={`h-full flex items-center justify-center text-white text-xs font-semibold ${overCapacity ? 'bg-red-500' : 'bg-blue-500'}`}
                    style={{ width: `${sprintWidth}%` }}
                    title={`Sprint estimated: ${sprintEst}h${sprintAct ? ` · actual: ${sprintAct}h` : ''}`}
                  >
                    {sprintWidth >= 12 ? `${sprintEst}h` : ''}
                  </div>
                )}
                {qtWidth > 0 && (
                  <div
                    className="h-full flex items-center justify-center text-white text-xs font-semibold bg-indigo-400"
                    style={{ width: `${qtWidth}%` }}
                    title={`QuickTrack estimated: ${qtEst}h${qtAct ? ` · actual: ${qtAct}h` : ''}`}
                  >
                    {qtWidth >= 12 ? `${qtEst}h` : ''}
                  </div>
                )}
              </div>
              <div className="w-32 text-sm text-gray-600 text-right whitespace-nowrap">
                {member.assigned}h / {member.capacity}h
                <span className={`ml-1 text-xs font-semibold ${overCapacity ? 'text-red-500' : 'text-gray-400'}`}>
                  ({member.percentage}%)
                </span>
              </div>
            </div>
            {(sprintEst > 0 || qtEst > 0 || sprintAct > 0 || qtAct > 0) && (
              <div className="flex items-center gap-3 pl-36 text-xs text-gray-400 mb-1 flex-wrap">
                {(sprintEst > 0 || sprintAct > 0) && (
                  <span className="flex items-center gap-1">
                    <span className="inline-block w-2 h-2 rounded-sm bg-blue-500" />
                    Sprint {sprintEst}h est<span className="text-gray-300"> · </span>{sprintAct}h actual
                    {member.sprintTasks ? <span className="text-gray-300"> · {member.sprintTasks} task{member.sprintTasks === 1 ? '' : 's'}</span> : null}
                  </span>
                )}
                {(qtEst > 0 || qtAct > 0) && (
                  <span className="flex items-center gap-1">
                    <span className="inline-block w-2 h-2 rounded-sm bg-indigo-400" />
                    QT {qtEst}h est<span className="text-gray-300"> · </span>{qtAct}h actual
                    {member.quickTasks ? <span className="text-gray-300"> · {member.quickTasks} task{member.quickTasks === 1 ? '' : 's'}</span> : null}
                  </span>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
    <div className="flex items-center gap-4 mt-4 pt-4 border-t border-gray-100 text-xs text-gray-500">
      <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-sm bg-blue-500" />Sprint task hours</span>
      <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-sm bg-indigo-400" />QuickTrack task hours</span>
    </div>
  </div>
  );
};

// ---- DevOps Pipeline Board (shared for Sprint & Kanban) ----
const DevOpsPipelineBoard = ({ pipeline, title, onTaskClick, onStatusChange, onDelete, canDelete }) => {
  const columnLabels = { backlog: 'Backlog', ready: 'Ready', in_progress: 'In Progress', review: 'Review', needs_rework: 'Needs Rework', done: 'Done' };

  // Normalize all keys to snake_case and merge done/completed
  const normalizedPipeline = {};
  Object.entries(pipeline).forEach(([key, items]) => {
    let normalized = key;
    if (key === 'inProgress') normalized = 'in_progress';
    if (key === 'completed')  normalized = 'done';
    if (!normalizedPipeline[normalized]) normalizedPipeline[normalized] = [];
    normalizedPipeline[normalized] = [...normalizedPipeline[normalized], ...items];
  });

  const columnOrder = ['backlog', 'ready', 'in_progress', 'review', 'needs_rework', 'done'];
  const uniqueColumns = columnOrder.filter(k => normalizedPipeline[k] !== undefined);

  return (
    <div className="mb-6">
      <h3 className="text-lg font-semibold mb-4">{title}</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        {uniqueColumns.map(status => {
          const items = normalizedPipeline[status] || [];
          const totalPoints = items.reduce((sum, item) => sum + (item.storyPoints || 0), 0);
          return (
            <div key={status} className="bg-white rounded-lg shadow border border-gray-200 min-h-96">
              <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                <div className="font-semibold text-gray-900">{columnLabels[status] || status}</div>
                <div className="text-sm text-gray-600">{items.length} items · {totalPoints} pts</div>
              </div>
              <div className="p-3 space-y-3">
                {items.length === 0 ? (
                  <div className="text-center py-12 text-gray-400">
                    <div className="text-3xl mb-2">🔭</div>
                    <div className="text-sm">No items</div>
                  </div>
                ) : (
                  items.map((item, i) => (
                    <DevOpsTaskCard key={item.id || i} item={item} currentStatus={status} onClick={onTaskClick} onStatusChange={onStatusChange} onDelete={onDelete} canDelete={canDelete} />
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ---- DevOps Kanban Board (DataOps-style design) ----
const DevOpsKanbanCard = ({ item, currentStatus, onClick, onStatusChange, onDelete, canDelete }) => {
  const [showActualTime, setShowActualTime] = useState(false);
  const priorityColors = {
    critical: { bg: '#fef2f2', color: '#dc2626', label: 'Critical' },
    high: { bg: '#fff7ed', color: '#ea580c', label: 'High' },
    medium: { bg: '#fefce8', color: '#ca8a04', label: 'Medium' },
    low: { bg: '#f0fdf4', color: '#16a34a', label: 'Low' },
    P1: { bg: '#fef2f2', color: '#dc2626', label: 'Critical' },
    P2: { bg: '#fff7ed', color: '#ea580c', label: 'High' },
    P3: { bg: '#fefce8', color: '#ca8a04', label: 'Medium' },
  };
  const p = priorityColors[item.priority] || priorityColors.medium;
  const isQuickTask = !!item.isQuickTask || String(item.id).startsWith('QT-');
  const isBug = !!item.isBug || String(item.id).startsWith('BUG-');
  const typeLabel = isBug ? 'Bug' : isQuickTask ? 'Ad Hoc' : 'Task';
  const isOverdue = currentStatus !== 'done' && item.dueDate && item.dueDate < new Date().toISOString().split('T')[0];

  const nextStatusMap = { open: 'in_progress', in_progress: 'done', done: 'open' };
  const nextLabelMap = { open: '▶ Start', in_progress: '✓ Done', done: '↺ Reopen' };
  const nextStatus = nextStatusMap[currentStatus];
  const nextLabel = nextLabelMap[currentStatus];

  const handleStatusClick = (e) => {
    e.stopPropagation();
    if (nextStatus === 'done') { setShowActualTime(true); return; }
    onStatusChange(item.id, nextStatus);
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-3 shadow-sm hover:shadow-md transition-shadow cursor-pointer" onClick={() => onClick(item.id)}>
      {showActualTime && (
        <ActualTimeModal
          taskTitle={item.title}
          onConfirm={mins => { setShowActualTime(false); onStatusChange(item.id, nextStatus, { actualMinutes: mins }); }}
          onCancel={() => setShowActualTime(false)}
        />
      )}
      <div className="flex items-start justify-between gap-2 mb-2">
        <span className="text-xs font-semibold px-2 py-0.5 rounded" style={{ background: p.bg, color: p.color }}>{p.label}</span>
        <div className="flex items-center gap-1">
          <span className="text-xs text-gray-400">{typeLabel}{isBug && item.severity ? ` · ${item.severity}` : ''}</span>
          {canDelete && (isQuickTask || isBug) && (
            <button onClick={e => { e.stopPropagation(); requestConfirm(`Delete "${item.title}"?`, () => onDelete(item.id)); }}
              className="text-gray-300 hover:text-red-500 text-base leading-none ml-1" title="Delete">×</button>
          )}
        </div>
      </div>
      <p className="text-sm font-medium text-gray-900 mb-1 leading-snug break-words">{item.title}</p>
      {item.description
        ? <p className="text-xs text-gray-500 mb-2 truncate">{stripHtml(item.description)}</p>
        : <p className="text-xs text-gray-400 font-mono mb-2">{item.bugCode || item.id}</p>
      }
      {item.projectName && <p className="text-xs text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded inline-block mb-2 truncate max-w-full" title={item.projectName}>{item.projectName}</p>}
      {(() => {
        // Surface est / actual minutes so what's stored is what's shown — matches the sprint board.
        const estMins = item.estimatedMinutes || item.estimated_minutes ||
          (item.estimateHours ? Math.round(item.estimateHours * 60) : 0);
        const actMins = item.actualMinutes || item.actual_minutes ||
          (item.actualHours ? Math.round(item.actualHours * 60) : 0);
        if (estMins === 0 && actMins === 0) return null;
        return (
          <div className="flex flex-wrap gap-3 text-xs mb-1">
            {estMins > 0 && <span className="text-gray-500">⏱ Est: {estMins}m</span>}
            {actMins > 0 && (
              <span className={actMins > estMins && estMins > 0 ? 'text-red-500 font-medium' : 'text-gray-500'}>
                ✓ Act: {actMins}m
              </span>
            )}
          </div>
        );
      })()}
      <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100">
        <div className="text-xs text-gray-500">
          {item.assignee && <span className="font-medium text-gray-700">{item.assignee}</span>}
          {item.dueDate && (
            <span className={`ml-2 ${isOverdue ? 'text-red-600 font-semibold' : 'text-gray-400'}`}>
              {isOverdue ? '⚠ ' : ''}{new Date(item.dueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            </span>
          )}
        </div>
        {nextStatus && (
          <button onClick={handleStatusClick} className="text-xs text-purple-600 hover:text-purple-800 font-medium whitespace-nowrap">
            {nextLabel}
          </button>
        )}
      </div>
    </div>
  );
};

const DevOpsKanbanBoard = ({ pipeline, onTaskClick, onStatusChange, onDelete, canDelete }) => {
  const normalizedPipeline = { open: [], in_progress: [], done: [] };
  // 3-column kanban (Open / In Progress / Done). Map every upstream bucket to the
  // closest semantic column. Active work — review, needs_rework, awaiting_vendor —
  // belongs under In Progress, NOT Open (which is for backlog / ready work).
  Object.entries(pipeline || {}).forEach(([key, items]) => {
    let normalized;
    if (key === 'inProgress' || key === 'in_progress' || key === 'review' || key === 'needs_rework' || key === 'awaiting_vendor') {
      normalized = 'in_progress';
    } else if (key === 'completed' || key === 'done') {
      normalized = 'done';
    } else {
      normalized = 'open'; // backlog, ready, and anything unmapped
    }
    normalizedPipeline[normalized] = [...normalizedPipeline[normalized], ...(items || [])];
  });
  const todayStr = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Los_Angeles' });
  normalizedPipeline.done = normalizedPipeline.done.filter(item =>
    !item.completedAt || new Date(item.completedAt).toLocaleDateString('en-CA', { timeZone: 'America/Los_Angeles' }) === todayStr
  );
  const columns = [
    { key: 'open', label: 'Open', color: 'bg-blue-500' },
    { key: 'in_progress', label: 'In Progress', color: 'bg-amber-500' },
    { key: 'done', label: 'Done', color: 'bg-green-500' },
  ];
  const totalTasks = columns.reduce((sum, col) => sum + (normalizedPipeline[col.key]?.length || 0), 0);
  return (
    <div className="mb-6">
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-gray-900">Team Kanban Board</h2>
        <p className="text-xs text-gray-500 mt-0.5">Sprint tasks, bugs, and ad-hoc tasks for the DevOps team</p>
      </div>
      {totalTasks === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center text-gray-400">
          <div className="text-4xl mb-3">📋</div>
          <p className="text-sm font-medium">No tasks for the DevOps team yet.</p>
          <p className="text-xs mt-1">Add a QuickTrack task and assign it to a DevOps team member.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {columns.map(col => {
            const tasks = normalizedPipeline[col.key] || [];
            return (
              <div key={col.key} className="bg-gray-50 rounded-lg border border-gray-200">
                <div className="px-4 py-3 border-b border-gray-200 flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${col.color}`} />
                  <span className="font-semibold text-sm text-gray-800">{col.label}</span>
                  <span className="ml-auto text-xs bg-white border border-gray-200 text-gray-600 rounded-full px-2 py-0.5">{tasks.length}</span>
                </div>
                <div className="p-3 space-y-2 min-h-32">
                  {tasks.length === 0 ? (
                    <div className="text-center py-8 text-gray-400 text-xs">No tasks</div>
                  ) : (
                    tasks.map((item, i) => (
                      <DevOpsKanbanCard key={item.id || i} item={item} currentStatus={col.key} onClick={onTaskClick} onStatusChange={onStatusChange} onDelete={onDelete} canDelete={canDelete} />
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ---- DevOps Ops Group (CI/CD Pipelines) ----
const DevOpsOpsGroup = ({ pipelines }) => (
  <div className="mb-6">
    <h3 className="text-lg font-semibold mb-4">Ops Pipeline</h3>
    <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
      <table className="w-full">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Pipeline</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Type</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Status</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Progress</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Records</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Duration</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {pipelines.map(p => {
            const statusStyle = getOpsStatusStyle(p.status);
            return (
              <tr key={p.id} className="hover:bg-gray-50">
                <td className="px-4 py-3">
                  <div className="text-sm font-medium text-gray-900">{p.name}</div>
                  <div className="text-xs text-gray-500">{p.id}</div>
                </td>
                <td className="px-4 py-3">
                  <span className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs font-semibold">{p.type}</span>
                </td>
                <td className="px-4 py-3">
                  <span className="px-2 py-0.5 rounded text-xs font-semibold" style={{ background: statusStyle.bg, color: statusStyle.color }}>{statusStyle.label}</span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className="w-20 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full ${p.status === 'failed' ? 'bg-red-500' : 'bg-blue-600'}`} style={{ width: `${p.progress}%` }} />
                    </div>
                    <span className="text-xs text-gray-500">{p.progress}%</span>
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-gray-600">{formatRecords(p.recordsProcessed)}</td>
                <td className="px-4 py-3 text-sm text-gray-600">{formatDuration(p.durationSeconds)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {pipelines.length === 0 && (
        <div className="p-8 text-center text-gray-400">No pipelines found.</div>
      )}
    </div>
  </div>
);

// ---- Add DevOps Task Modal ----
const AddDevOpsTaskModal = ({ onClose, onSubmit, teamMembers = [], user, allUsers = [], projects = [] }) => {
  const isAdmin = user?.role === 'admin';
  const isManager = !isAdmin && (user?.role?.endsWith('_manager') || user?.role === 'manager');
  const selfAssign = !isAdmin && !isManager;
  const memberPool = teamMembers.length > 0 ? teamMembers : allUsers.map(u => ({ user_id: u.user_id, user_name: u.user_name }));
  const assignableMembers = isAdmin || isManager ? memberPool : [];
  const defaultAssignee = user?.name || '';

  const [form, setForm] = useState({ title: '', type: 'task', priority: 'P2', assignee: defaultAssignee, estimatedMinutes: '', actualMinutes: '', storyPoints: '', status: 'backlog', description: '', projectId: '' });
  const [errors, setErrors] = useState({});
  const isDone = ['done', 'completed'].includes(form.status);

  const handleSubmit = (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.estimatedMinutes || parseInt(form.estimatedMinutes) <= 0) errs.estimatedMinutes = 'Estimated Time is required';
    if (isDone && (!form.actualMinutes || parseInt(form.actualMinutes) <= 0)) errs.actualMinutes = 'Actual Time is required when status is Done';
    if (Object.keys(errs).length) { setErrors(errs); return; }
    onSubmit(form);
  };

  const priorityPill = { P1: 'bg-red-100 text-red-700', P2: 'bg-orange-100 text-orange-700', P3: 'bg-blue-100 text-blue-700' };
  const statusPill = { backlog: 'bg-gray-100 text-gray-700', ready: 'bg-blue-100 text-blue-700', in_progress: 'bg-yellow-100 text-yellow-800', review: 'bg-purple-100 text-purple-700', needs_rework: 'bg-orange-100 text-orange-700', done: 'bg-green-100 text-green-700', blocked: 'bg-red-100 text-red-700' };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-gray-50 rounded-xl shadow-2xl w-full max-w-4xl max-h-[92vh] flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 bg-white rounded-t-xl flex-shrink-0">
          <h2 className="text-base font-bold text-gray-900">New Task</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl font-bold leading-none">&times;</button>
        </div>

        {/* Scrollable Body */}
        <div className="overflow-y-auto flex-1">
          <form onSubmit={handleSubmit}>
            <div className="p-6 grid grid-cols-3 gap-5">

              {/* ── Main Column (2/3) ── */}
              <div className="col-span-2 space-y-4">
                {/* Priority + Status + Type pills + Title */}
                <div className="bg-white rounded-xl border border-gray-200 p-5">
                  <div className="flex items-center gap-2 mb-4 flex-wrap">
                    <select value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })}
                      className={`text-xs font-semibold px-3 py-1 rounded-full border-0 cursor-pointer focus:ring-2 focus:ring-blue-400 ${priorityPill[form.priority] || 'bg-gray-100 text-gray-700'}`}>
                      <option value="P1">P1 – Critical</option>
                      <option value="P2">P2 – High</option>
                      <option value="P3">P3 – Normal</option>
                    </select>
                    <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}
                      className={`text-xs font-medium px-3 py-1 rounded-full border-0 cursor-pointer focus:ring-2 focus:ring-blue-400 ${statusPill[form.status] || 'bg-gray-100 text-gray-700'}`}>
                      <option value="backlog">Backlog</option>
                      <option value="ready">Ready</option>
                      <option value="in_progress">In Progress</option>
                      <option value="done">Done</option>
                      <option value="blocked">Blocked</option>
                    </select>
                    <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}
                      className="text-xs font-medium px-3 py-1 rounded-full border border-gray-200 cursor-pointer focus:ring-2 focus:ring-blue-400 bg-white text-gray-600">
                      <option value="task">Task</option>
                      <option value="BUG">Bug</option>
                      <option value="ADHOC">Ad-hoc</option>
                      <option value="PROJECT">Project</option>
                    </select>
                  </div>
                  <input required value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
                    placeholder="Task title…"
                    className="w-full text-xl font-bold text-gray-900 border border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-400 focus:border-blue-400" />
                </div>

                {/* Description */}
                <div className="bg-white rounded-xl border border-gray-200 p-5">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Description</label>
                  <MiniWysiwyg value={form.description} onChange={v => setForm({ ...form, description: v })} placeholder="Add a description…" minHeight="120px" />
                </div>
              </div>

              {/* ── Sidebar (1/3) ── */}
              <div className="space-y-4">
                <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-4">
                  <h3 className="font-semibold text-gray-700 text-sm border-b border-gray-100 pb-2">Task Details</h3>

                  {/* Assignee */}
                  <div>
                    <p className="text-xs text-gray-400 mb-0.5">Assignee</p>
                    {selfAssign ? (
                      <p className="text-sm font-medium text-gray-800">{form.assignee || '—'}</p>
                    ) : (
                      <select value={form.assignee} onChange={e => setForm({ ...form, assignee: e.target.value })}
                        className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:ring-2 focus:ring-blue-400">
                        <option value="">— Unassigned —</option>
                        {assignableMembers.map(m => <option key={m.user_id} value={m.user_name}>{m.user_name}</option>)}
                      </select>
                    )}
                  </div>

                  {/* Project */}
                  {projects.length > 0 && (
                    <div>
                      <p className="text-xs text-gray-400 mb-0.5">Project</p>
                      <select value={form.projectId} onChange={e => setForm({ ...form, projectId: e.target.value })}
                        className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:ring-2 focus:ring-blue-400">
                        <option value="">— No Project —</option>
                        {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                      </select>
                    </div>
                  )}

                  {/* Est. Time */}
                  <div>
                    <p className="text-xs text-gray-400 mb-0.5">Est. Time (min) <span className="text-red-500">*</span></p>
                    <input type="number" min="1" placeholder="e.g. 60" value={form.estimatedMinutes}
                      onChange={e => { setForm({ ...form, estimatedMinutes: e.target.value }); setErrors(ev => ({ ...ev, estimatedMinutes: '' })); }}
                      className={`w-full border rounded-lg px-2 py-1.5 text-sm focus:ring-2 focus:ring-blue-400 ${errors.estimatedMinutes ? 'border-red-400' : 'border-gray-200'}`} />
                    {errors.estimatedMinutes && <p className="text-xs text-red-500 mt-1">{errors.estimatedMinutes}</p>}
                  </div>

                  {/* Actual Time */}
                  <div>
                    <p className="text-xs text-gray-400 mb-0.5">Actual Time (min){isDone && <span className="text-red-500"> *</span>}</p>
                    <input type="number" min="0" placeholder="0" value={form.actualMinutes}
                      onChange={e => { setForm({ ...form, actualMinutes: e.target.value }); setErrors(ev => ({ ...ev, actualMinutes: '' })); }}
                      className={`w-full border rounded-lg px-2 py-1.5 text-sm focus:ring-2 focus:ring-blue-400 ${errors.actualMinutes ? 'border-red-400' : 'border-gray-200'}`} />
                    {errors.actualMinutes && <p className="text-xs text-red-500 mt-1">{errors.actualMinutes}</p>}
                  </div>

                  {/* Story Points */}
                  <div>
                    <p className="text-xs text-gray-400 mb-0.5">Story Points</p>
                    <input type="number" min="0" placeholder="0" value={form.storyPoints}
                      onChange={e => setForm({ ...form, storyPoints: e.target.value })}
                      className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:ring-2 focus:ring-blue-400" />
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end items-center gap-3 px-6 pb-6">
              <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</button>
              <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700">Create Task</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// ---- Shared comment item with inline edit ----
const CommentItem = ({ comment, currentUser, onEdit, onDelete, fmtTime, avatarColor = 'bg-teal-100 text-teal-700' }) => {
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState(comment.content);
  const [editKey, setEditKey] = useState(0);
  const isOwner = (comment.user_name || comment.userName) === currentUser;
  const name = comment.user_name || comment.userName || '?';
  const content = comment.content;
  const createdAt = comment.created_at || comment.createdAt;

  const handleSave = async () => {
    if (!editText.trim() || editText.trim() === content) { setEditing(false); return; }
    await onEdit(comment.id, editText.trim());
    setEditing(false);
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 group">
      <div className="flex items-center gap-2 mb-2">
        <div className={`w-8 h-8 rounded-full ${avatarColor} flex items-center justify-center text-sm font-bold flex-shrink-0`}>
          {name.charAt(0).toUpperCase()}
        </div>
        <span className="font-medium text-sm text-gray-900">{name}</span>
        <span className="text-xs text-gray-400 ml-auto">{fmtTime(createdAt)}</span>
        {isOwner && !editing && (
          <button onClick={() => { setEditing(true); setEditText(content); setEditKey(k => k + 1); }}
            className="opacity-0 group-hover:opacity-100 text-gray-300 hover:text-blue-400 transition-opacity ml-1 text-xs px-1.5 py-0.5 rounded" title="Edit">
            Edit
          </button>
        )}
        {!editing && (
          <button onClick={() => onDelete(comment.id)}
            className="opacity-0 group-hover:opacity-100 text-gray-300 hover:text-red-400 transition-opacity ml-1" title="Delete">
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      {editing ? (
        <div className="space-y-2">
          <MiniWysiwyg key={editKey} value={editText} onChange={setEditText} placeholder="Edit comment…" minHeight="72px" />
          <div className="flex gap-2 justify-end">
            <button onClick={() => setEditing(false)} className="text-xs px-3 py-1.5 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg">Cancel</button>
            <button onClick={handleSave} className="text-xs px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700">Save</button>
          </div>
        </div>
      ) : (
        <div className="text-sm text-gray-700 leading-relaxed prose prose-sm max-w-none [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_img]:max-w-full [&_img]:rounded [&_img]:my-1"
          dangerouslySetInnerHTML={{ __html: content }} />
      )}
    </div>
  );
};

// ---- Task Detail Panel ----
// ---- Shared task comments component (used by both DevOps and QuickTrack) ----
const TaskComments = ({ taskRef, taskSource, userName }) => {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState('');
  const [commentHtml, setCommentHtml] = useState('');
  const [editorKey, setEditorKey] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const apiBase = taskSource === 'devops'
    ? `/api/devops/tasks/${taskRef}`
    : taskSource === 'bug'
      ? `/api/quality/bugs/${taskRef}`
      : `/api/quicktrack/tasks/${taskRef}`;

  const load = async () => {
    try {
      const token = localStorage.getItem('orbit_token');
      const res = await fetch(`${apiBase}/comments`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const data = await res.json();
      if (Array.isArray(data)) setComments(data);
    } catch { /* leave existing comments on error */ }
    setLoading(false);
  };

  useEffect(() => { load(); }, [taskRef]);

  const handleSubmit = async () => {
    const content = (commentHtml || text).trim();
    if (!content) return;
    setText('');
    setCommentHtml('');
    setEditorKey(k => k + 1);
    // Add instantly to the list
    const optimistic = { id: `tmp_${Date.now()}`, user_name: userName || 'User', content, created_at: new Date().toISOString() };
    setComments(c => [...c, optimistic]);
    setSubmitting(true);
    try {
      const token = localStorage.getItem('orbit_token');
      const res = await fetch(`${apiBase}/comments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ content, userName: userName || 'User' })
      });
      if (res.ok) {
        // Replace optimistic entry with real server data
        const fresh = await fetch(`${apiBase}/comments`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const data = await fresh.json();
        if (Array.isArray(data)) setComments(data);
      }
    } catch { /* keep optimistic comment on error */ }
    setSubmitting(false);
  };

  const commentActionUrl = (commentId) => taskSource === 'devops'
    ? `/api/devops/tasks/comments/${commentId}`
    : taskSource === 'bug'
      ? `/api/quality/bugs/comments/${commentId}`
      : `/api/quicktrack/tasks/comments/${commentId}`;

  const handleDelete = async (commentId) => {
    const token = localStorage.getItem('orbit_token');
    try {
      await fetch(commentActionUrl(commentId), { method: 'DELETE', headers: token ? { Authorization: `Bearer ${token}` } : {} });
      setComments(c => c.filter(x => x.id !== commentId));
    } catch { /* ignore */ }
  };

  const handleEdit = async (commentId, newContent) => {
    if (!newContent.trim()) return;
    const token = localStorage.getItem('orbit_token');
    try {
      await fetch(commentActionUrl(commentId), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ content: newContent.trim() })
      });
      setComments(c => c.map(x => x.id === commentId ? { ...x, content: newContent.trim() } : x));
    } catch { /* ignore */ }
  };

  const fmtTime = (ts) => {
    try {
      const d = new Date(ts.endsWith('Z') ? ts : ts + 'Z');
      return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
    } catch { return ''; }
  };

  return (
    <div className="mt-4 border-t border-gray-200 pt-4">
      <h3 className="text-base font-semibold text-gray-900 mb-4">Comments</h3>
      <div className="mb-5 space-y-2">
        <BugCommentEditor key={editorKey} onChange={setCommentHtml} placeholder="Write a comment… (supports bold, italic, lists)" />
        <div className="flex items-center justify-between">
          <p className="text-xs text-gray-400">Ctrl+B Bold · Ctrl+I Italic · Ctrl+U Underline</p>
          <button onClick={handleSubmit} disabled={submitting || !commentHtml.trim()}
            className="px-4 py-2 bg-teal-600 text-white text-sm rounded-lg hover:bg-teal-700 disabled:opacity-50 font-medium">
            {submitting ? 'Posting…' : 'Comment'}
          </button>
        </div>
      </div>
      {loading ? (
        <div className="text-sm text-gray-400 py-2">Loading comments…</div>
      ) : comments.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-6 text-center text-gray-400 text-sm">
          No comments yet. Be the first to comment!
        </div>
      ) : (
        <div className="space-y-3">
          {comments.map(c => (
            <CommentItem
              key={c.id}
              comment={c}
              currentUser={userName}
              onEdit={handleEdit}
              onDelete={(id) => requestConfirm('Delete this comment?', () => handleDelete(id))}
              fmtTime={fmtTime}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ---- Mini WYSIWYG Editor ----
const MiniWysiwyg = ({ value, onChange, placeholder = 'Add a description…', minHeight = '80px' }) => {
  const editorRef = React.useRef(null);
  const mountedRef = React.useRef(false);
  const [isEmpty, setIsEmpty] = React.useState(!value);

  React.useEffect(() => {
    if (editorRef.current && !mountedRef.current) {
      mountedRef.current = true;
      if (value) { editorRef.current.innerHTML = value; setIsEmpty(false); }
    }
  }, []);

  const syncValue = () => {
    const html = editorRef.current?.innerHTML || '';
    const empty = !html || html === '<br>' || html === '<div><br></div>' || html === '<p><br></p>';
    setIsEmpty(empty);
    onChange(empty ? '' : html);
  };

  const exec = (cmd, val = null) => {
    document.execCommand(cmd, false, val);
    editorRef.current?.focus();
    syncValue();
  };

  const handlePaste = (e) => {
    e.preventDefault();
    document.execCommand('insertText', false, e.clipboardData.getData('text/plain'));
  };

  const tbtn = (cmd, content, title, val = null) => (
    <button type="button" title={title} key={cmd} onMouseDown={e => { e.preventDefault(); exec(cmd, val); }}
      className="px-2 py-1 text-xs rounded hover:bg-gray-200 transition-colors text-gray-700 select-none">{content}</button>
  );

  return (
    <div className="border border-gray-300 rounded-xl overflow-hidden focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500 bg-white">
      <div className="flex items-center gap-0.5 px-2 py-1.5 bg-gray-50 border-b border-gray-200 flex-wrap">
        {tbtn('bold', <strong className="font-bold">B</strong>, 'Bold')}
        {tbtn('italic', <em>I</em>, 'Italic')}
        {tbtn('underline', <u>U</u>, 'Underline')}
        {tbtn('strikeThrough', <s>S</s>, 'Strikethrough')}
        <div className="w-px h-4 bg-gray-300 mx-1" />
        {tbtn('insertUnorderedList', '• List', 'Bullet list')}
        {tbtn('insertOrderedList', '1. List', 'Numbered list')}
        <div className="w-px h-4 bg-gray-300 mx-1" />
        {tbtn('removeFormat', 'Aa✕', 'Clear formatting')}
      </div>
      <div className="relative">
        {isEmpty && <div className="absolute top-2.5 left-3 text-sm text-gray-400 pointer-events-none select-none">{placeholder}</div>}
        <div ref={editorRef} contentEditable suppressContentEditableWarning
          onInput={syncValue} onPaste={handlePaste}
          style={{ minHeight }}
          className="max-h-48 overflow-y-auto px-3 py-2.5 text-sm text-gray-800 focus:outline-none prose prose-sm max-w-none [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5" />
      </div>
    </div>
  );
};

const TaskDetailPanel = ({ task, onClose, onUpdate, onDelete, user, teamMembers = [], allUsers = [], projects = [] }) => {
  const isAdmin = user?.role === 'admin';
  const isManager = !isAdmin && (user?.role?.endsWith('_manager') || user?.role === 'manager');
  const selfAssign = !isAdmin && !isManager;
  const memberPool = teamMembers.length > 0 ? teamMembers : allUsers.map(u => ({ user_id: u.user_id, user_name: u.user_name }));
  const assignableMembers = isAdmin || isManager ? memberPool : [];

  const [editStatus, setEditStatus] = useState(task.status);
  const [editReviewStatus, setEditReviewStatus] = useState(task.reviewStatus || task.review_status || 'none');
  const [editAssignee, setEditAssignee] = useState(task.assignee || '');
  const [editPriority, setEditPriority] = useState(task.priority);
  const [editProjectId, setEditProjectId] = useState(task.projectId || '');
  const [editActualHours, setEditActualHours] = useState(task.actualHours || task.estimateHours || task.estimate || 0);
  const [editEstimatedMinutes, setEditEstimatedMinutes] = useState(task.estimatedMinutes || task.estimated_minutes || 0);
  const [editActualMinutes, setEditActualMinutes] = useState(task.actualMinutes || task.actual_minutes || 0);
  const [editDescription, setEditDescription] = useState(task.description || '');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [activeTab, setActiveTab] = useState('details');

  const isCompleting = (editStatus === 'done' || editStatus === 'completed') && task.status !== 'done' && task.status !== 'completed';
  const isDone = editStatus === 'done' || editStatus === 'completed';

  const handleSave = async () => {
    setSaveError('');
    if (!(parseInt(editEstimatedMinutes) > 0)) { setSaveError('Estimated Time is required.'); return; }
    if (isDone && !(parseInt(editActualMinutes) > 0)) { setSaveError('Actual Time is required when status is Done.'); return; }
    setSaving(true);
    const changes = {};
    if (editStatus !== task.status) changes.status = editStatus;
    if (editAssignee !== (task.assignee || '')) changes.assignee = editAssignee;
    if (editPriority !== task.priority) changes.priority = editPriority;
    if (editProjectId !== (task.projectId || '')) changes.projectId = editProjectId;
    if (isCompleting && editActualHours > 0) changes.actualHours = parseFloat(editActualHours);
    if (editEstimatedMinutes !== (task.estimatedMinutes || task.estimated_minutes || 0)) changes.estimatedMinutes = parseInt(editEstimatedMinutes) || 0;
    if (editActualMinutes !== (task.actualMinutes || task.actual_minutes || 0)) changes.actualMinutes = parseInt(editActualMinutes) || 0;
    if (editDescription !== (task.description || '')) changes.description = editDescription;
    if (editReviewStatus !== (task.reviewStatus || task.review_status || 'none')) changes.reviewStatus = editReviewStatus;
    if (Object.keys(changes).length > 0) await onUpdate(task.id, changes);
    setSaving(false);
    onClose();
  };

  const typeBadge = getTypeBadge(task.type);
  const priorityPill = { P1: 'bg-red-100 text-red-700', P2: 'bg-orange-100 text-orange-700', P3: 'bg-blue-100 text-blue-700' };
  const statusPill = { backlog: 'bg-gray-100 text-gray-700', ready: 'bg-blue-100 text-blue-700', in_progress: 'bg-yellow-100 text-yellow-800', review: 'bg-purple-100 text-purple-700', needs_rework: 'bg-orange-100 text-orange-700', done: 'bg-green-100 text-green-700', blocked: 'bg-red-100 text-red-700', completed: 'bg-green-100 text-green-700' };
  const statusDot = { backlog: 'bg-gray-400', ready: 'bg-blue-500', in_progress: 'bg-yellow-500', review: 'bg-purple-500', needs_rework: 'bg-orange-500', done: 'bg-green-500' };
  const statusLabel = { backlog: 'Backlog', ready: 'Ready', in_progress: 'In Progress', review: 'Review', needs_rework: 'Needs Rework', done: 'Done' };
  const TASK_STATUSES = ['backlog', 'ready', 'in_progress', 'review', 'needs_rework', 'done'];
  const variance = parseInt(editActualMinutes) - parseInt(editEstimatedMinutes);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-gray-50 rounded-xl shadow-2xl w-full max-w-5xl max-h-[92vh] flex flex-col">

        {/* Header */}
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 bg-white rounded-t-xl flex-shrink-0">
          <div className="flex items-center gap-3">
            <span className="font-mono text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded">{task.id}</span>
            <span className="text-xs font-semibold px-2 py-0.5 rounded" style={{ background: typeBadge.bg, color: typeBadge.color }}>{task.type}</span>
            <h2 className="text-base font-bold text-gray-900">Edit Task</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl font-bold leading-none">&times;</button>
        </div>

        {/* Scrollable Body */}
        <div className="overflow-y-auto flex-1">
          <div className="p-6 grid grid-cols-3 gap-5">

            {/* ── Main Column (2/3) ── */}
            <div className="col-span-2 space-y-4">

              {/* Priority + Status pills + Title */}
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <div className="flex items-center gap-2 mb-4 flex-wrap">
                  <select value={editPriority} onChange={e => setEditPriority(e.target.value)}
                    className={`text-xs font-semibold px-3 py-1 rounded-full border-0 cursor-pointer focus:ring-2 focus:ring-blue-400 ${priorityPill[editPriority] || 'bg-gray-100 text-gray-700'}`}>
                    <option value="P1">P1 – Critical</option>
                    <option value="P2">P2 – High</option>
                    <option value="P3">P3 – Normal</option>
                  </select>
                  <select value={editStatus} onChange={e => setEditStatus(e.target.value)}
                    className={`text-xs font-medium px-3 py-1 rounded-full border-0 cursor-pointer focus:ring-2 focus:ring-blue-400 ${statusPill[editStatus] || 'bg-gray-100 text-gray-700'}`}>
                    {TASK_STATUSES.map(s => <option key={s} value={s}>{statusLabel[s]}</option>)}
                  </select>
                </div>
                <p className="text-xl font-bold text-gray-900">{task.title}</p>
              </div>

              {/* Completing banner */}
              {isCompleting && (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                  <label className="block text-xs font-semibold text-blue-700 mb-1">Actual Hours Spent</label>
                  <p className="text-[10px] text-blue-500 mb-2">These hours will be automatically logged to your TimeBoard.</p>
                  <input type="number" min="0" step="0.5" value={editActualHours}
                    onChange={e => setEditActualHours(e.target.value)}
                    className="w-full border border-blue-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-400" />
                </div>
              )}

              {/* Tabbed card */}
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="flex border-b border-gray-200 px-4 pt-3 gap-1">
                  {[{ key: 'details', label: '📋 Details' }, { key: 'comments', label: '💬 Comments' }].map(t => (
                    <button key={t.key} type="button" onClick={() => setActiveTab(t.key)}
                      className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${activeTab === t.key ? 'bg-blue-50 text-blue-700 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`}>
                      {t.label}
                    </button>
                  ))}
                </div>
                <div className="p-5">
                  {activeTab === 'details' && (
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Description</label>
                      <MiniWysiwyg value={editDescription} onChange={setEditDescription} placeholder="Add a description…" minHeight={120} />
                    </div>
                  )}
                  {activeTab === 'comments' && (
                    <TaskComments taskRef={task.id} taskSource="devops" userName={user?.user_name || user?.name} />
                  )}
                </div>
              </div>

              {/* Metrics */}
              <div className="grid grid-cols-3 gap-3">
                {[
                  { label: 'Story Points', value: task.storyPoints || 0, color: 'text-gray-900' },
                  { label: 'Progress', value: `${task.progress || 0}%`, color: 'text-gray-900' },
                  { label: 'Variance', value: parseInt(editEstimatedMinutes) > 0 ? `${variance > 0 ? '+' : ''}${variance}m` : '—', color: parseInt(editEstimatedMinutes) > 0 ? (variance > 0 ? 'text-red-600' : 'text-green-600') : 'text-gray-400' },
                ].map(m => (
                  <div key={m.label} className="bg-white rounded-xl border border-gray-200 p-3 text-center">
                    <div className="text-xs text-gray-500 mb-1">{m.label}</div>
                    <div className={`text-lg font-semibold ${m.color}`}>{m.value}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* ── Sidebar (1/3) ── */}
            <div className="space-y-4">
              <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-4">
                <h3 className="font-semibold text-gray-700 text-sm border-b border-gray-100 pb-2">Task Details</h3>

                {/* Assignee */}
                <div>
                  <p className="text-xs text-gray-400 mb-0.5">Assignee</p>
                  {selfAssign ? (
                    <p className="text-sm font-medium text-gray-800">{editAssignee || '—'}</p>
                  ) : (
                    <select value={editAssignee} onChange={e => setEditAssignee(e.target.value)}
                      className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:ring-2 focus:ring-blue-400">
                      <option value="">— Unassigned —</option>
                      {editAssignee && !assignableMembers.find(m => m.user_name === editAssignee) && (
                        <option value={editAssignee}>{editAssignee}</option>
                      )}
                      {assignableMembers.map(m => <option key={m.user_id} value={m.user_name}>{m.user_name}</option>)}
                    </select>
                  )}
                </div>

                {/* QA Review */}
                <div>
                  <p className="text-xs text-gray-400 mb-0.5">QA Review</p>
                  <select value={editReviewStatus} onChange={e => setEditReviewStatus(e.target.value)}
                    className={`w-full border rounded-lg px-2 py-1.5 text-sm focus:ring-2 focus:ring-blue-400 ${editReviewStatus === 'ready_for_qa' ? 'border-teal-400 bg-teal-50 text-teal-700' : 'border-gray-200'}`}>
                    <option value="none">None</option>
                    <option value="ready_for_qa">Ready for QA</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </div>

                {/* Project */}
                {projects.length > 0 && (
                  <div>
                    <p className="text-xs text-gray-400 mb-0.5">Project</p>
                    <select value={editProjectId} onChange={e => setEditProjectId(e.target.value)}
                      className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:ring-2 focus:ring-blue-400">
                      <option value="">— No Project —</option>
                      {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  </div>
                )}

                {/* Est. Time */}
                <div>
                  <p className="text-xs text-gray-400 mb-0.5">Est. Time (min) <span className="text-red-500">*</span></p>
                  <input type="number" min="0" placeholder="0" value={editEstimatedMinutes}
                    onChange={e => { setEditEstimatedMinutes(e.target.value); setSaveError(''); }}
                    className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:ring-2 focus:ring-blue-400" />
                </div>

                {/* Actual Time */}
                <div>
                  <p className={`text-xs mb-0.5 ${isDone ? 'text-red-500 font-medium' : 'text-gray-400'}`}>
                    Actual Time (min){isDone && <span className="ml-0.5">*</span>}
                  </p>
                  <input type="number" min="0" placeholder="0" value={editActualMinutes}
                    onChange={e => { setEditActualMinutes(e.target.value); setSaveError(''); }}
                    className={`w-full border rounded-lg px-2 py-1.5 text-sm focus:ring-2 focus:ring-blue-400 ${isDone && !(parseInt(editActualMinutes) > 0) ? 'border-red-300 bg-red-50' : 'border-gray-200'}`} />
                </div>
              </div>

              {/* Quick Status */}
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <h3 className="font-semibold text-gray-700 text-sm mb-2">Quick Status</h3>
                <div className="space-y-1">
                  {TASK_STATUSES.map(s => (
                    <button key={s} type="button" onClick={() => setEditStatus(s)}
                      className={`w-full text-left px-3 py-1.5 text-xs rounded-lg flex items-center gap-2 ${editStatus === s ? (statusPill[s] + ' font-semibold') : 'text-gray-600 hover:bg-gray-50'}`}>
                      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${statusDot[s]}`}></span>{statusLabel[s]}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {saveError && (
            <div className="mx-6 mb-4 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-xs text-red-600">{saveError}</div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center px-6 py-4 border-t border-gray-200 bg-white rounded-b-xl flex-shrink-0">
          {onDelete && (isAdmin || isManager)
            ? <button type="button" onClick={() => requestConfirm(`Delete task "${task.title}"?`, () => onDelete(task.id))}
                className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg text-sm font-medium">Delete Task</button>
            : <div />}
          <div className="flex gap-3">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</button>
            <button type="button" onClick={handleSave} disabled={saving}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ---- File attachment helpers (shared by bug & project attachment managers) ----
const readFileAsBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve({ fileName: file.name, fileType: file.type, fileSize: file.size, fileData: reader.result });
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

const MAX_ATTACHMENT_SIZE = 2 * 1024 * 1024; // 2 MB
const MAX_ATTACHMENTS = 5;
const ALLOWED_TYPES = [
  'image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/csv', 'application/csv', 'text/plain',
  'application/vnd.ms-excel', 'application/msword', 'application/octet-stream',
];
const ALLOWED_EXTENSIONS = new Set(['.jpg', '.jpeg', '.png', '.gif', '.webp', '.pdf', '.docx', '.xlsx', '.csv']);
const ACCEPT_ATTR = '.jpg,.jpeg,.png,.gif,.webp,.pdf,.docx,.xlsx,.csv';
const isAllowedFile = (f) => {
  const ext = '.' + f.name.split('.').pop().toLowerCase();
  return ALLOWED_TYPES.includes(f.type) || ALLOWED_EXTENSIONS.has(ext);
};
const getFileDisplay = (fileName, fileType) => {
  const ext = (fileName || '').split('.').pop().toLowerCase();
  if (ext === 'pdf' || fileType === 'application/pdf')
    return { label: 'PDF', emoji: '📄', bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-600', hover: 'hover:bg-red-100' };
  if (ext === 'docx' || (fileType || '').includes('wordprocessingml'))
    return { label: 'DOCX', emoji: '📝', bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-600', hover: 'hover:bg-blue-100' };
  if (ext === 'xlsx' || (fileType || '').includes('spreadsheetml') || fileType === 'application/vnd.ms-excel')
    return { label: 'XLSX', emoji: '📊', bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-600', hover: 'hover:bg-green-100' };
  if (ext === 'csv' || fileType === 'text/csv' || fileType === 'application/csv')
    return { label: 'CSV', emoji: '📋', bg: 'bg-teal-50', border: 'border-teal-200', text: 'text-teal-600', hover: 'hover:bg-teal-100' };
  return { label: 'FILE', emoji: '📎', bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-600', hover: 'hover:bg-gray-100' };
};

// API-backed attachment manager (used in BugDetailPanel and EditBugModal)
const ApiAttachmentManager = ({ bugId, uploadedBy }) => {
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const inputRef = React.useRef();

  const load = async () => { setLoading(true); setAttachments(await ModulesModel.getBugAttachments(bugId)); setLoading(false); };
  useEffect(() => { load(); }, [bugId]);

  const handleFiles = async (selected) => {
    const incoming = Array.from(selected);
    const errors = [];
    setUploading(true);
    for (const f of incoming) {
      if (!isAllowedFile(f)) { errors.push(`${f.name}: unsupported type`); continue; }
      if (f.size > MAX_ATTACHMENT_SIZE) { errors.push(`${f.name}: exceeds 2 MB limit`); continue; }
      if (attachments.length >= MAX_ATTACHMENTS) { errors.push('Max 5 attachments reached'); break; }
      const att = await readFileAsBase64(f);
      await ModulesModel.addBugAttachment(bugId, att);
    }
    if (errors.length) alert(errors.join('\n'));
    setUploading(false);
    await load();
  };

  const handleDelete = async (id) => {
    requestConfirm('Remove this attachment?', async () => {
      await ModulesModel.deleteBugAttachment(id);
      await load();
    });
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="block text-sm font-medium text-gray-700">Attachments</label>
        {attachments.length < MAX_ATTACHMENTS && (
          <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading}
            className="px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-50 text-gray-600 disabled:opacity-50">
            {uploading ? 'Uploading…' : '+ Add'}
          </button>
        )}
        <input ref={inputRef} type="file" multiple accept={ACCEPT_ATTR} className="hidden"
          onChange={e => { handleFiles(e.target.files); e.target.value = ''; }} />
      </div>
      {loading ? <p className="text-xs text-gray-400">Loading…</p> : attachments.length === 0 ? (
        <p className="text-xs text-gray-400">No attachments yet.</p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {attachments.map(a => (
            <div key={a.id} className="relative group">
              {a.fileType.startsWith('image/') ? (
                <a href={a.fileData} target="_blank" rel="noreferrer">
                  <img src={a.fileData} alt={a.fileName} className="w-16 h-16 object-cover rounded border border-gray-200 cursor-pointer hover:opacity-80" />
                </a>
              ) : (() => { const d = getFileDisplay(a.fileName, a.fileType); return (
                <a href={a.fileData} download={a.fileName} className={`w-16 h-16 flex flex-col items-center justify-center ${d.bg} border ${d.border} rounded ${d.text} text-xs font-medium ${d.hover}`}>
                  {d.label}<span className="text-gray-400 text-[10px] truncate max-w-[56px] mt-0.5">{a.fileName}</span>
                </a>
              ); })()}
              {uploadedBy === a.uploadedBy && (
                <button type="button" onClick={() => handleDelete(a.id)}
                  className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white rounded-full text-[10px] hidden group-hover:flex items-center justify-center leading-none">×</button>
              )}
              <div className="text-[10px] text-gray-400 text-center mt-0.5 w-16 truncate">{a.uploadedBy || ''}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const sanitizeBugHtml = sanitizeHtml;

// Convert backend-inserted "[Screenshot saved to Attachments: filename]" placeholders
// back into inline <img> tags of the actual attachment. The backend (extractInlineImagesFromHtml)
// strips base64 images out of steps/expected/actual fields to fit under D1's ~1 MB text-value
// cap and stores them as separate bug_attachments rows. Without this hydration, users see
// only the placeholder text and have to eyeball the sidebar attachments in order — awful UX.
const hydrateScreenshotPlaceholders = (html, attachments = []) => {
  if (!html) return '';
  const byName = new Map();
  for (const a of attachments || []) {
    if (a?.fileName) byName.set(String(a.fileName).toLowerCase(), a);
  }
  if (byName.size === 0) return html;
  // Matches the exact wrapper the backend inserts:
  //   <p><em style="color:#6b7280">[Screenshot saved to Attachments: filename.png]</em></p>
  // Also tolerates users' hand-edits that lose the wrapper — falls back to inline replacement.
  const escapeAttr = (s) => String(s).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const renderImg = (fileName) => {
    const a = byName.get(String(fileName).trim().toLowerCase());
    if (!a?.fileData) return null;
    // fileData may come with a data:URL prefix (from the extract path) or without one
    // (from R2 fetch). Normalize both.
    const src = String(a.fileData).startsWith('data:')
      ? a.fileData
      : `data:${a.fileType || 'image/png'};base64,${a.fileData}`;
    return `<figure style="margin:8px 0"><img src="${escapeAttr(src)}" alt="${escapeAttr(fileName)}" style="max-width:100%;border-radius:6px;border:1px solid #e5e7eb;display:block" /><figcaption style="font-size:11px;color:#9ca3af;margin-top:2px">${escapeAttr(fileName)}</figcaption></figure>`;
  };
  // Full wrapped form
  let out = html.replace(
    /<p>\s*<em[^>]*>\s*\[Screenshot saved to Attachments:\s*([^\]]+?)\s*\]\s*<\/em>\s*<\/p>/gi,
    (m, fileName) => renderImg(fileName) || m
  );
  // Bare form (after hand-edits or in plain text)
  out = out.replace(
    /\[Screenshot saved to Attachments:\s*([^\]]+?)\s*\]/gi,
    (m, fileName) => renderImg(fileName) || m
  );
  return out;
};

// ---- Bug Detail Panel ----
const BugDetailPanelField = ({ label, children }) => (
  <div><label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>{children}</div>
);
const BugDetailPanel = ({ bug, onClose, onUpdate, onDelete, readOnly = false, user, teamMembers = [] }) => {
  const [form, setForm] = useState({
    title: bug.title || '',
    status: bug.status || 'open',
    severity: bug.severity || 'medium',
    environment: bug.environment || 'DEV',
    module: bug.module || '',
    assignedTo: bug.assignedTo || '',
    stepsToReproduce: bug.stepsToReproduce || '',
    expectedResult: bug.expectedResult || '',
    actualResult: bug.actualResult || '',
    estimatedMinutes: bug.estimatedMinutes != null ? String(bug.estimatedMinutes) : '',
    actualMinutes: bug.actualMinutes != null ? String(bug.actualMinutes) : '',
  });
  const [activeTab, setActiveTab] = useState('details');
  const [saving, setSaving] = useState(false);
  const userRole = user?.role || user?.user_role;
  const isManager = userRole?.endsWith('_manager') || userRole === 'manager';
  const canEditTime = ['admin', 'qa_lead', 'qa', 'devops', 'dataops', 'developer'].includes(userRole);
  const isDeveloper = userRole === 'developer' || userRole === 'dataops';
  const managerModule = (isManager && userRole !== 'manager') ? userRole.replace('_manager', '') : null;
  const myTeamMembers = isManager ? (managerModule ? teamMembers.filter(m => m.team_module === managerModule) : teamMembers) : [];
  const fmtDate = (d) => d ? new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—';

  const handleSave = async () => {
    setSaving(true);
    const changes = {};
    if (form.title !== (bug.title || '')) changes.title = form.title;
    if (form.status !== (bug.status || 'open')) changes.status = form.status;
    if (form.severity !== (bug.severity || 'medium')) changes.severity = form.severity;
    if (form.environment !== (bug.environment || '')) changes.environment = form.environment;
    if (form.module !== (bug.module || '')) changes.module = form.module;
    if (form.assignedTo !== (bug.assignedTo || '')) changes.assignedTo = form.assignedTo;
    if (form.stepsToReproduce !== (bug.stepsToReproduce || '')) changes.stepsToReproduce = form.stepsToReproduce;
    if (form.expectedResult !== (bug.expectedResult || '')) changes.expectedResult = form.expectedResult;
    if (form.actualResult !== (bug.actualResult || '')) changes.actualResult = form.actualResult;
    const estVal = form.estimatedMinutes !== '' ? parseInt(form.estimatedMinutes) : null;
    const actVal = form.actualMinutes !== '' ? parseInt(form.actualMinutes) : null;
    if (estVal !== (bug.estimatedMinutes ?? null)) changes.estimatedMinutes = estVal;
    if (actVal !== (bug.actualMinutes ?? null)) changes.actualMinutes = actVal;
    if (Object.keys(changes).length > 0 && onUpdate) await onUpdate(bug.id, changes);
    setSaving(false);
    onClose();
  };

  const handleMarkForQA = async () => {
    if (onUpdate) await onUpdate(bug.id, { status: 'for_qa' });
    onClose();
  };

  const iCls = 'w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:ring-2 focus:ring-teal-400 focus:border-teal-400';
  const rCls = 'text-sm text-gray-800 font-medium';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-gray-50 rounded-xl shadow-2xl w-full max-w-5xl max-h-[92vh] flex flex-col">

        {/* Header */}
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 bg-white rounded-t-xl flex-shrink-0">
          <div className="flex items-center gap-3">
            <span className="font-mono text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded">{bug.bugCode}</span>
            {bug.projectName && <span className="text-xs text-gray-400">{bug.projectName}</span>}
            {readOnly && <span className="text-xs text-gray-400 italic">· view only</span>}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl font-bold leading-none">&times;</button>
        </div>

        {/* Scrollable body */}
        <div className="overflow-y-auto flex-1">
          <div className="p-6 grid grid-cols-3 gap-5">

            {/* ── Main Column (2/3) ── */}
            <div className="col-span-2 space-y-4">

              {/* Title + badge card */}
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <div className="flex items-center gap-3 mb-4 flex-wrap">
                  {/* Severity */}
                  {readOnly
                    ? <span className={`text-xs font-semibold px-3 py-1 rounded-full capitalize ${bugSevStyle(form.severity)}`}>{form.severity}</span>
                    : <select value={form.severity} onChange={e => setForm({ ...form, severity: e.target.value })}
                        className={`text-xs font-semibold px-3 py-1 rounded-full border-0 cursor-pointer focus:ring-2 focus:ring-teal-400 capitalize ${bugSevStyle(form.severity)}`}>
                        <option value="critical">Critical</option>
                        <option value="high">High</option>
                        <option value="medium">Medium</option>
                        <option value="low">Low</option>
                      </select>}
                  {/* Status */}
                  {readOnly
                    ? <span className={`text-xs font-medium px-3 py-1 rounded-full capitalize flex items-center gap-1 ${bugStatStyle(form.status)}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${bugStatDot(form.status)}`}></span>{bugStatLabel(form.status)}
                      </span>
                    : <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}
                        className={`text-xs font-medium px-3 py-1 rounded-full border-0 cursor-pointer focus:ring-2 focus:ring-teal-400 ${bugStatStyle(form.status)}`}>
                        {BUG_STATUSES.map(s => <option key={s} value={s}>{bugStatLabel(s)}</option>)}
                      </select>}
                  {/* Mark as For QA button (developer view-only) */}
                  {readOnly && isDeveloper && form.status !== 'for_qa' && onUpdate && (
                    <button onClick={handleMarkForQA}
                      className="text-xs px-3 py-1 rounded-full bg-pink-50 text-pink-700 border border-pink-200 hover:bg-pink-100 font-medium">
                      Mark as For QA
                    </button>
                  )}
                </div>
                {/* Title */}
                {readOnly
                  ? <h1 className="text-xl font-bold text-gray-900">{form.title}</h1>
                  : <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
                      className="w-full text-xl font-bold text-gray-900 border border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-teal-400 focus:border-teal-400" />}
              </div>

              {/* Tabbed card */}
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="flex border-b border-gray-200 px-4 pt-3 gap-1">
                  {[
                    { key: 'details', label: '📋 Details' },
                    { key: 'comments', label: '💬 Comments' },
                    { key: 'attachments', label: '📎 Attachments' },
                  ].map(t => (
                    <button key={t.key} onClick={() => setActiveTab(t.key)}
                      className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${activeTab === t.key ? 'bg-teal-50 text-teal-700 border-b-2 border-teal-600' : 'text-gray-500 hover:text-gray-700'}`}>
                      {t.label}
                    </button>
                  ))}
                </div>

                <div className="p-5">
                  {/* Details Tab */}
                  {activeTab === 'details' && (
                    <div className="space-y-5">
                      <div>
                        <h3 className="text-sm font-semibold text-gray-700 mb-2">Steps to Reproduce</h3>
                        {readOnly
                          ? <div className="text-sm text-gray-700 bg-gray-50 rounded-lg p-3 min-h-[48px] prose prose-sm max-w-none [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_img]:max-w-full [&_img]:rounded"
                              dangerouslySetInnerHTML={{ __html: sanitizeBugHtml(hydrateScreenshotPlaceholders(form.stepsToReproduce, bug.attachments)) || '<span class="text-gray-400 italic">Not provided</span>' }} />
                          : <BugFieldEditor key={`str-dp-${bug.id}`} initialValue={form.stepsToReproduce} onChange={v => setForm({ ...form, stepsToReproduce: v })} minHeight="80px" />}
                      </div>
                      <div className="border-t border-gray-100" />
                      <div>
                        <h3 className="text-sm font-semibold text-gray-700 mb-2">Expected Result</h3>
                        {readOnly
                          ? <div className="text-sm text-gray-700 bg-green-50 rounded-lg p-3 min-h-[40px] prose prose-sm max-w-none [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_img]:max-w-full [&_img]:rounded"
                              dangerouslySetInnerHTML={{ __html: sanitizeBugHtml(hydrateScreenshotPlaceholders(form.expectedResult, bug.attachments)) || '<span class="text-gray-400 italic">Not provided</span>' }} />
                          : <BugFieldEditor key={`exp-dp-${bug.id}`} initialValue={form.expectedResult} onChange={v => setForm({ ...form, expectedResult: v })} minHeight="60px" />}
                      </div>
                      <div className="border-t border-gray-100" />
                      <div>
                        <h3 className="text-sm font-semibold text-gray-700 mb-2">Actual Result</h3>
                        {readOnly
                          ? <div className="text-sm text-gray-700 bg-red-50 rounded-lg p-3 min-h-[40px] prose prose-sm max-w-none [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_img]:max-w-full [&_img]:rounded"
                              dangerouslySetInnerHTML={{ __html: sanitizeBugHtml(hydrateScreenshotPlaceholders(form.actualResult, bug.attachments)) || '<span class="text-gray-400 italic">Not provided</span>' }} />
                          : <BugFieldEditor key={`act-dp-${bug.id}`} initialValue={form.actualResult} onChange={v => setForm({ ...form, actualResult: v })} minHeight="60px" />}
                      </div>
                    </div>
                  )}

                  {/* Comments Tab */}
                  {activeTab === 'comments' && (
                    <TaskComments taskRef={bug.id} taskSource="bug" userName={user?.user_name || user?.name} />
                  )}

                  {/* Attachments Tab */}
                  {activeTab === 'attachments' && (
                    <ApiAttachmentManager bugId={bug.id} uploadedBy={user?.user_name || user?.name} />
                  )}
                </div>
              </div>
            </div>

            {/* ── Sidebar (1/3) ── */}
            <div className="space-y-4">
              <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-4">
                <h3 className="font-semibold text-gray-700 text-sm border-b border-gray-100 pb-2">Bug Details</h3>

                {/* Reported By */}
                {bug.reportedBy && (
                  <div>
                    <p className="text-xs text-gray-400 mb-0.5">Reported By</p>
                    <p className={rCls}>{bug.reportedBy}</p>
                  </div>
                )}

                {/* Assigned To */}
                <div>
                  <p className="text-xs text-gray-400 mb-0.5">Assigned To</p>
                  {readOnly && isManager
                    ? <select value={form.assignedTo} onChange={e => setForm({ ...form, assignedTo: e.target.value })} className={iCls}>
                        <option value="">— Unassigned —</option>
                        {form.assignedTo && !myTeamMembers.find(m => m.user_name === form.assignedTo) && (
                          <option value={form.assignedTo}>{form.assignedTo}</option>
                        )}
                        {myTeamMembers.map(m => <option key={m.user_id} value={m.user_name}>{m.user_name}</option>)}
                      </select>
                    : readOnly
                      ? <p className={rCls}>{form.assignedTo || '—'}</p>
                      : <input value={form.assignedTo} onChange={e => setForm({ ...form, assignedTo: e.target.value })} placeholder="Developer name" className={iCls} />}
                </div>

                {/* Environment */}
                <div>
                  <p className="text-xs text-gray-400 mb-0.5">Environment</p>
                  {readOnly
                    ? <p className={rCls}>{form.environment || '—'}</p>
                    : <select value={form.environment} onChange={e => setForm({ ...form, environment: e.target.value })} className={iCls}>
                        <option>DEV</option><option>UAT</option><option>Staging</option><option>Production</option>
                      </select>}
                </div>

                {/* Module */}
                <div>
                  <p className="text-xs text-gray-400 mb-0.5">Module</p>
                  {readOnly
                    ? <p className={rCls}>{form.module || '—'}</p>
                    : <select value={form.module} onChange={e => setForm({ ...form, module: e.target.value })} className={iCls}>
                        <option value="">None</option>
                        <option>Authentication</option><option>Settlement Calculator</option>
                        <option>Payment Processing</option><option>Reporting</option>
                        <option>User Management</option><option>UI</option>
                      </select>}
                </div>

                {/* Est. Time */}
                <div>
                  <p className="text-xs text-gray-400 mb-0.5">Est. Time (min)</p>
                  {canEditTime
                    ? <input type="number" min="0" placeholder="e.g. 60" value={form.estimatedMinutes}
                        onChange={e => setForm({ ...form, estimatedMinutes: e.target.value })} className={iCls} />
                    : <p className={rCls}>{bug.estimatedMinutes != null ? `${bug.estimatedMinutes}m` : '—'}</p>}
                </div>

                {/* Actual Time */}
                <div>
                  <p className="text-xs text-gray-400 mb-0.5">Actual Time (min)</p>
                  {canEditTime
                    ? <input type="number" min="0" placeholder="0" value={form.actualMinutes}
                        onChange={e => setForm({ ...form, actualMinutes: e.target.value })} className={iCls} />
                    : <p className={rCls}>{bug.actualMinutes != null ? `${bug.actualMinutes}m` : '—'}</p>}
                </div>

                {/* Linked TC */}
                {bug.testCaseCode && (
                  <div>
                    <p className="text-xs text-gray-400 mb-0.5">Linked TC</p>
                    <p className={rCls}>{bug.testCaseCode}{bug.testCaseTitle ? ` — ${bug.testCaseTitle}` : ''}</p>
                  </div>
                )}

                {/* Created */}
                <div>
                  <p className="text-xs text-gray-400 mb-0.5">Created</p>
                  <p className="text-sm text-gray-600">{fmtDate(bug.createdAt)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 mb-0.5">Last Updated</p>
                  <p className="text-sm text-gray-600">{fmtDate(bug.updatedAt)}</p>
                </div>
              </div>

              {/* Quick Status (edit mode only) */}
              {!readOnly && (
                <div className="bg-white rounded-xl border border-gray-200 p-4">
                  <h3 className="font-semibold text-gray-700 text-sm mb-2">Quick Status</h3>
                  <div className="space-y-1">
                    {BUG_STATUSES.map(s => (
                      <button key={s} type="button" onClick={() => setForm(f => ({ ...f, status: s }))}
                        className={`w-full text-left px-3 py-1.5 text-xs rounded-lg flex items-center gap-2 capitalize ${form.status === s ? bugStatStyle(s) + ' font-semibold' : 'text-gray-600 hover:bg-gray-50'}`}>
                        <span className={`w-2 h-2 rounded-full ${bugStatDot(s)}`}></span>{bugStatLabel(s)}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-between items-center px-6 pb-6">
            {!readOnly && onDelete
              ? <button onClick={() => requestConfirm('Delete this bug?', () => onDelete(bug.id))}
                  className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg text-sm font-medium">Delete Bug</button>
              : <div />}
            <div className="flex gap-3">
              <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50">Close</button>
              {readOnly && isManager && onUpdate && (
                <button onClick={async () => { setSaving(true); await onUpdate(bug.id, { assignedTo: form.assignedTo }); setSaving(false); onClose(); }}
                  disabled={saving || form.assignedTo === (bug.assignedTo || '')}
                  className="px-4 py-2 bg-teal-600 text-white text-sm rounded-lg hover:bg-teal-700 disabled:opacity-50">
                  {saving ? 'Saving…' : 'Reassign'}
                </button>
              )}
              {readOnly && canEditTime && onUpdate && (
                <button onClick={async () => {
                  setSaving(true);
                  const changes = {};
                  const estVal = form.estimatedMinutes !== '' ? parseInt(form.estimatedMinutes) : null;
                  const actVal = form.actualMinutes !== '' ? parseInt(form.actualMinutes) : null;
                  if (estVal !== (bug.estimatedMinutes ?? null)) changes.estimatedMinutes = estVal;
                  if (actVal !== (bug.actualMinutes ?? null)) changes.actualMinutes = actVal;
                  if (Object.keys(changes).length > 0) await onUpdate(bug.id, changes);
                  setSaving(false);
                  onClose();
                }} disabled={saving} className="px-4 py-2 bg-teal-600 text-white text-sm rounded-lg hover:bg-teal-700 disabled:opacity-50">
                  {saving ? 'Saving…' : 'Save Time'}
                </button>
              )}
              {!readOnly && (
                <button onClick={handleSave} disabled={saving}
                  className="px-4 py-2 bg-teal-600 text-white text-sm rounded-lg hover:bg-teal-700 disabled:opacity-50">
                  {saving ? 'Saving…' : 'Save Changes'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ---- DevOps Module (Main Container) ----
const EodSettingsModal = ({ onClose }) => {
  const [settings, setSettings] = React.useState({
    eod_enabled: 'false', eod_slack_channel: '#daily-summary',
    pipedream_outbound_url: '', pipedream_project_url: '', pipedream_api_key: ''
  });
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [saved, setSaved] = React.useState(false);
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    ModulesModel.getAppSettings().then(s => { setSettings(s); setLoading(false); });
  }, []);

  const handleSave = async () => {
    setSaving(true); setError(''); setSaved(false);
    const res = await ModulesModel.saveAppSettings(settings);
    setSaving(false);
    if (res?.success) setSaved(true);
    else setError(res?.error || 'Failed to save settings');
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-lg">
        <div className="flex justify-between items-center mb-5">
          <h2 className="text-lg font-semibold text-gray-900">⚙️ EOD Settings</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
        </div>
        {loading ? (
          <div className="text-center py-8 text-gray-500">Loading settings…</div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <div className="text-sm font-medium text-gray-800">EOD Summary Enabled</div>
                <div className="text-xs text-gray-500">Allow sending EOD reports to Slack</div>
              </div>
              <button
                onClick={() => setSettings(s => ({ ...s, eod_enabled: s.eod_enabled === 'true' ? 'false' : 'true' }))}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${settings.eod_enabled === 'true' ? 'bg-blue-600' : 'bg-gray-300'}`}>
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${settings.eod_enabled === 'true' ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Slack Channel</label>
              <input type="text" value={settings.eod_slack_channel || ''} onChange={e => setSettings(s => ({ ...s, eod_slack_channel: e.target.value }))}
                placeholder="#daily-summary"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">EOD Pipedream URL</label>
              <input type="text" value={settings.pipedream_outbound_url || ''} onChange={e => setSettings(s => ({ ...s, pipedream_outbound_url: e.target.value }))}
                placeholder="https://eod.m.pipedream.net"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Project Update Pipedream URL</label>
              <input type="text" value={settings.pipedream_project_url || ''} onChange={e => setSettings(s => ({ ...s, pipedream_project_url: e.target.value }))}
                placeholder="https://project-update.m.pipedream.net"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Pipedream API Key <span className="text-gray-400 font-normal">(leave blank to keep existing)</span></label>
              <input type="password" value={settings.pipedream_api_key || ''} onChange={e => setSettings(s => ({ ...s, pipedream_api_key: e.target.value }))}
                placeholder="••••••••"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            {error && <p className="text-red-500 text-xs">{error}</p>}
            {saved && <p className="text-green-600 text-xs font-medium">✓ Settings saved</p>}
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900">Cancel</button>
              <button onClick={handleSave} disabled={saving} className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50">
                {saving ? 'Saving…' : 'Save Settings'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const SendEodModal = ({ module, onClose }) => {
  const [sending, setSending] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const [error, setError] = React.useState('');

  const handleSend = async () => {
    setSending(true); setError('');
    const res = await ModulesModel.sendEodSummary(module);
    setSending(false);
    if (res?.success) setResult(res);
    else setError(res?.error || 'Failed to send EOD summary');
  };

  const moduleLabel = module === 'devops' ? 'DevOps' : 'DataOps';
  const moduleColor = module === 'devops' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-purple-600 hover:bg-purple-700';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-900">📤 {moduleLabel} EOD Summary</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
        </div>
        {result ? (
          <div className="text-center py-4">
            <div className="text-4xl mb-3">✅</div>
            <p className="font-medium text-gray-800 mb-3">EOD summary sent to Slack!</p>
            <div className="bg-gray-50 rounded-lg p-4 text-sm text-left space-y-1">
              <div className="flex justify-between"><span className="text-gray-500">Members included</span><span className="font-medium">{result.usersIncluded}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Completed today</span><span className="font-medium text-green-600">{result.totals?.total_completed}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">In progress</span><span className="font-medium text-blue-600">{result.totals?.total_in_progress}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Pending</span><span className="font-medium text-gray-600">{result.totals?.total_pending}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Slack channel</span><span className="font-medium">{result.slackChannel}</span></div>
            </div>
            <button onClick={onClose} className="mt-4 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200">Close</button>
          </div>
        ) : (
          <>
            <p className="text-sm text-gray-600 mb-5">
              This will send an End of Day summary for all <strong>{moduleLabel}</strong> team members to Slack via Pipedream,
              showing tasks completed today, in progress, and pending.
            </p>
            {error && <p className="text-red-500 text-sm mb-3 bg-red-50 p-3 rounded-lg">{error}</p>}
            <div className="flex justify-end gap-2">
              <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900">Cancel</button>
              <button onClick={handleSend} disabled={sending} className={`px-4 py-2 text-white text-sm rounded-lg disabled:opacity-50 ${moduleColor}`}>
                {sending ? 'Sending…' : 'Send EOD Report'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const DevOpsModule = ({ data, viewMode, group, onViewModeChange, onGroupChange, onAddTask, onTaskClick, onStatusChange, onSendEod, onEodSettings, userRole, onDelete }) => {
  const canManageEod = ['admin', 'manager', 'dataops_manager', 'devops_manager'].includes(userRole);
  const canEodSettings = ['admin', 'devops_manager'].includes(userRole);
  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">DevOps Pipeline</h1>
          <p className="text-gray-600 mt-1">Developer pipeline & operations tracking</p>
        </div>
        <div className="flex gap-2">
          {canEodSettings && <button onClick={onEodSettings} className="px-3 py-2 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200 font-medium border border-gray-300">⚙️ EOD Settings</button>}
          {canManageEod && <button onClick={onSendEod} className="px-3 py-2 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200 font-medium border border-gray-300">📤 Send EOD</button>}
          <button onClick={onAddTask} className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 font-medium">+ New Task</button>
        </div>
      </div>

      {/* View Mode Toggle */}
      <div className="flex gap-2 mb-4">
        {['sprint', 'kanban'].map(mode => (
          <button key={mode} onClick={() => onViewModeChange(mode)}
            className={`px-4 py-2 text-sm rounded-lg font-medium ${viewMode === mode ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
            {mode === 'sprint' ? 'Sprint Board' : 'Kanban Board'}
          </button>
        ))}
      </div>

      {/* Group Toggle */}
      <div className="flex gap-2 mb-6">
        {[{ key: 'all', label: 'All' }, { key: 'dev', label: 'Dev Pipeline' }, { key: 'ops', label: 'Ops Pipeline' }].map(g => (
          <button key={g.key} onClick={() => onGroupChange(g.key)}
            className={`px-3 py-1.5 text-xs rounded-full font-medium ${group === g.key ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
            {g.label}
          </button>
        ))}
      </div>

      {/* Sprint Header (only in sprint view) */}
      {viewMode === 'sprint' && data.sprint && (group === 'all' || group === 'dev') && (
        <div className="bg-white rounded-lg shadow border border-gray-200 p-5 mb-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h2 className="text-xl font-bold text-gray-900">{data.sprint.name}</h2>
              <p className="text-sm text-gray-600">{data.sprint.goal}</p>
              <div className="text-xs text-gray-500 mt-1">📅 {data.sprint.startDate} → {data.sprint.endDate}</div>
            </div>
          </div>
        </div>
      )}

      {/* Dev Pipeline content */}
      {(group === 'all' || group === 'dev') && (
        <>
          {viewMode === 'sprint' && (
            <>
              <DevOpsCapacityCards capacity={data.capacity} />
              <DevOpsTeamWorkload workload={data.workload} workloadRange={data.workloadRange} sprints={data.sprints || []} />
              <DevOpsPipelineBoard
                pipeline={data.pipeline}
                title="Sprint Pipeline"
                onTaskClick={onTaskClick}
                onStatusChange={onStatusChange}
              />
            </>
          )}
          {viewMode === 'kanban' && (
            <DevOpsKanbanBoard
              pipeline={data.kanbanPipeline || data.pipeline}
              onTaskClick={onTaskClick}
              onStatusChange={onStatusChange}
              onDelete={onDelete}
              canDelete={userRole === 'admin' || userRole === 'devops_manager' || userRole === 'manager'}
            />
          )}
        </>
      )}

      {/* Ops Pipeline content */}
      {(group === 'all' || group === 'ops') && data.ops && (
        <DevOpsOpsGroup pipelines={data.ops.pipelines} />
      )}
    </div>
  );
};

// Shared style helpers for PlanTrack
const getStatusStyle = (status) => {
  const styles = {
    active: { color: '#0366d6', bg: '#d4e4ff', label: 'Active' },
    planning: { color: '#6f42c1', bg: '#f3e8ff', label: 'Planning' },
    completed: { color: '#28a745', bg: '#dcffe4', label: 'Completed' },
    done: { color: '#28a745', bg: '#dcffe4', label: 'Done' },
    on_hold: { color: '#e36209', bg: '#fff5cc', label: 'On Hold' },
    backlog: { color: '#586069', bg: '#f6f8fa', label: 'Backlog' },
    in_progress: { color: '#0366d6', bg: '#d4e4ff', label: 'In Progress' },
    ready: { color: '#17a2b8', bg: '#d4f4ff', label: 'Ready' },
    review: { color: '#6f42c1', bg: '#f3e8ff', label: 'Review' },
    blocked: { color: '#dc3545', bg: '#ffe5e9', label: 'Blocked' }
  };
  return styles[status] || { color: '#586069', bg: '#f6f8fa', label: status };
};

const getPriorityStyle = (priority) => {
  const styles = {
    P1: { color: '#dc3545', bg: '#ffe5e9' },
    P2: { color: '#e36209', bg: '#fff5cc' },
    P3: { color: '#28a745', bg: '#dcffe4' }
  };
  return styles[priority] || { color: '#586069', bg: '#f6f8fa' };
};

const getMilestoneIcon = (status) => {
  if (status === 'completed') return { symbol: '✓', color: '#28a745', bg: '#dcffe4' };
  if (status === 'in_progress') return { symbol: '●', color: '#0366d6', bg: '#d4e4ff' };
  if (status === 'overdue') return { symbol: '!', color: '#dc3545', bg: '#ffe5e9' };
  return { symbol: '○', color: '#959da5', bg: '#f6f8fa' };
};

// ---- Milestone Engine Utilities ----

const getHealthBadgeStyle = (hs) => {
  const s = {
    'completed': { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Completed' },
    'on-track': { bg: 'bg-green-100', text: 'text-green-700', label: 'On Track' },
    'at-risk': { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'At Risk' },
    'critical': { bg: 'bg-orange-100', text: 'text-orange-700', label: 'Critical' },
    'overdue': { bg: 'bg-red-100', text: 'text-red-700', label: 'Overdue' }
  };
  return s[hs] || s['on-track'];
};

// How many milestones the PlanTrack sidebar preview shows before collapsing the rest
// behind "+N more · See all" (which links to the full Manage Milestones page).
const PLANTRACK_MILESTONE_SIDEBAR_LIMIT = 6;

// Solid dot colors mirroring getHealthBadgeStyle's palette — used by the Milestone
// Engine's compact list rows, where a small status dot reads faster than a full pill.
const MILESTONE_HEALTH_DOT = {
  completed: '#9ca3af',
  'on-track': '#22c55e',
  'at-risk': '#eab308',
  critical: '#f97316',
  overdue: '#ef4444',
};

const formatRiskScore = (score) => {
  if (score >= 80) return { level: 'Critical', color: 'text-red-600' };
  if (score >= 60) return { level: 'High', color: 'text-orange-600' };
  if (score >= 40) return { level: 'Medium', color: 'text-yellow-600' };
  if (score >= 20) return { level: 'Low', color: 'text-lime-600' };
  return { level: 'Minimal', color: 'text-green-600' };
};

// ---- Milestone Engine Modals ----

const AddMilestoneModal = ({ onClose, onSubmit, projectId, projects, user }) => {
  const createdBy = user?.user_name || user?.name || '';
  const [form, setForm] = useState({ projectId: projectId || '', title: '', description: '', dueDate: '', ownerId: '', isCriticalPath: false, createdBy });
  const handleSubmit = (e) => { e.preventDefault(); if (!form.title || !form.projectId || !form.dueDate) return; onSubmit(form); };
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
          <h2 className="text-xl font-bold text-gray-900">Create Milestone</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Project *</label>
            <select value={form.projectId} onChange={e => setForm({...form, projectId: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" required disabled={!!projectId}>
              <option value="">Select project...</option>
              {(projects || []).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Title *</label>
            <input value={form.title} onChange={e => setForm({...form, title: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" placeholder="e.g., Requirements Complete" required />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Description</label>
            <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" rows={2} />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Due Date *</label>
            <input type="date" value={form.dueDate} onChange={e => setForm({...form, dueDate: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" required />
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={form.isCriticalPath} onChange={e => setForm({...form, isCriticalPath: e.target.checked})}
              className="w-4 h-4 text-blue-600 rounded" /> Mark as Critical Path
          </label>
          {form.createdBy && (
            <div className="bg-gray-50 rounded-lg px-3 py-2 text-sm text-gray-600 flex items-center gap-2">
              <span className="font-medium text-gray-500">Created by:</span> {form.createdBy}
            </div>
          )}
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
            <button type="submit" className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 font-medium">Create Milestone</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const EditMilestoneModal = ({ milestone, allMilestones, onClose, onSubmit, onDelete }) => {
  const [form, setForm] = useState({
    title: milestone.title || '', description: milestone.description || '', dueDate: milestone.due_date || '',
    status: milestone.status || 'pending', completionPercentage: milestone.completion_percentage || 0,
    ownerId: milestone.owner_id || '', isCriticalPath: !!milestone.is_critical_path
  });
  const [deps, setDeps] = useState(milestone.dependencies || []);
  const [showAddDep, setShowAddDep] = useState(false);
  const [newDep, setNewDep] = useState({ predecessorId: '', type: 'finish-to-start' });

  const handleSubmit = (e) => { e.preventDefault(); onSubmit(form); };
  const handleAddDep = async () => {
    if (!newDep.predecessorId) return;
    await ModulesModel.createMilestoneDependency({ predecessorId: newDep.predecessorId, successorId: milestone.milestone_id, dependencyType: newDep.type });
    const updated = await ModulesModel.getMilestoneDetail(milestone.milestone_id);
    if (updated) setDeps(updated.dependencies || []);
    setShowAddDep(false); setNewDep({ predecessorId: '', type: 'finish-to-start' });
  };
  const handleRemoveDep = async (predId) => {
    await ModulesModel.deleteMilestoneDependency(predId, milestone.milestone_id);
    setDeps(deps.filter(d => d.predecessor_milestone_id !== predId));
  };

  // Filter milestones: same project, not self
  const depOptions = (allMilestones || []).filter(m => m.project_id === milestone.project_id && m.milestone_id !== milestone.milestone_id);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center z-10">
          <h2 className="text-xl font-bold text-gray-900">Edit Milestone</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Title</label>
            <input value={form.title} onChange={e => setForm({...form, title: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" required />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Description</label>
            <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" rows={2} />
          </div>
          {(milestone.created_at || milestone.created_by) && (
            <div className="grid grid-cols-2 gap-4 bg-gray-50 rounded-lg px-4 py-3">
              {milestone.created_at && (
                <div>
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Creation Date</div>
                  <div className="text-sm text-gray-700">{new Date(milestone.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</div>
                </div>
              )}
              {milestone.created_by && (
                <div>
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Created By</div>
                  <div className="text-sm text-gray-700">{milestone.created_by}</div>
                </div>
              )}
            </div>
          )}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Due Date</label>
              <input type="date" value={form.dueDate} onChange={e => setForm({...form, dueDate: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" required />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Status</label>
              <select value={form.status} onChange={e => setForm({...form, status: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500">
                <option value="pending">Pending</option>
                <option value="in_progress">In Progress</option>
                <option value="completed">Completed</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Completion: {form.completionPercentage}%</label>
              <input type="range" min="0" max="100" value={form.completionPercentage}
                onChange={e => setForm({...form, completionPercentage: parseInt(e.target.value)})} className="w-full mt-2" />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={form.isCriticalPath} onChange={e => setForm({...form, isCriticalPath: e.target.checked})}
              className="w-4 h-4 text-blue-600 rounded" /> Critical Path Milestone
          </label>

          {/* Dependencies section */}
          <div className="border-t pt-4">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-sm font-bold text-gray-900">Dependencies (predecessors)</h3>
              <button type="button" onClick={() => setShowAddDep(!showAddDep)}
                className="text-xs px-3 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200">+ Add</button>
            </div>
            {showAddDep && (
              <div className="bg-gray-50 p-3 rounded-lg mb-3 flex gap-2 items-end">
                <div className="flex-1">
                  <select value={newDep.predecessorId} onChange={e => setNewDep({...newDep, predecessorId: e.target.value})}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500">
                    <option value="">Select milestone...</option>
                    {depOptions.map(m => <option key={m.milestone_id} value={m.milestone_id}>{m.title}</option>)}
                  </select>
                </div>
                <select value={newDep.type} onChange={e => setNewDep({...newDep, type: e.target.value})}
                  className="w-40 px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500">
                  <option value="finish-to-start">Finish-to-Start</option>
                  <option value="start-to-start">Start-to-Start</option>
                  <option value="finish-to-finish">Finish-to-Finish</option>
                </select>
                <button type="button" onClick={handleAddDep} className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700">Add</button>
              </div>
            )}
            {deps.length === 0 ? <p className="text-sm text-gray-400 text-center py-2">No dependencies</p> : (
              <div className="space-y-1">
                {deps.filter(d => d.successor_milestone_id === milestone.milestone_id).map(d => (
                  <div key={d.id} className="flex justify-between items-center bg-gray-50 px-3 py-2 rounded text-sm">
                    <span><span className="font-medium">{d.predecessor_title}</span> <span className="text-gray-400 text-xs mx-1">{d.dependency_type}</span></span>
                    <button type="button" onClick={() => handleRemoveDep(d.predecessor_milestone_id)} className="text-red-500 hover:text-red-700"><Trash2 size={14} /></button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Linked tasks */}
          {milestone.linkedTasks && milestone.linkedTasks.length > 0 && (
            <div className="border-t pt-4">
              <h3 className="text-sm font-bold text-gray-900 mb-2">Linked Tasks ({milestone.linkedTasks.length})</h3>
              <div className="space-y-1">
                {milestone.linkedTasks.map(t => (
                  <div key={t.task_id} className="flex justify-between items-center bg-gray-50 px-3 py-2 rounded text-sm">
                    <span>{t.title}</span>
                    <span className={`text-xs font-medium px-2 py-0.5 rounded ${t.status === 'completed' || t.status === 'done' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>{t.status}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex justify-between items-center pt-4 border-t">
            <button type="button" onClick={() => requestConfirm('Delete this milestone?', () => onDelete(milestone.milestone_id))}
              className="px-4 py-2 text-sm text-red-600 bg-red-50 rounded-lg hover:bg-red-100 font-medium">Delete</button>
            <div className="flex gap-3">
              <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
              <button type="submit" className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 font-medium">Save Changes</button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

const ManageTemplatesModal = ({ onClose, onChanged }) => {
  const [templates, setTemplates] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewTemplate, setShowNewTemplate] = useState(false);
  const [newTemplate, setNewTemplate] = useState({ name: '', projectType: 'general', description: '' });
  const [newItem, setNewItem] = useState({ title: '', offsetDays: 0, durationDays: 7, isCritical: false });
  const [showNewItem, setShowNewItem] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadTemplates = async () => {
    setLoading(true);
    const data = await ModulesModel.getMilestoneTemplates();
    setTemplates(data || []);
    setLoading(false);
  };

  useEffect(() => { loadTemplates(); }, []);

  useEffect(() => {
    if (selectedId) {
      ModulesModel.getTemplateItems(selectedId).then(data => setItems(data || []));
    } else {
      setItems([]);
    }
  }, [selectedId]);

  const selected = templates.find(t => t.id === selectedId);

  const handleCreateTemplate = async (e) => {
    e.preventDefault();
    if (!newTemplate.name.trim()) return;
    setSaving(true);
    await ModulesModel.createMilestoneTemplate(newTemplate);
    setNewTemplate({ name: '', projectType: 'general', description: '' });
    setShowNewTemplate(false);
    setSaving(false);
    await loadTemplates();
    onChanged();
  };

  const handleDeleteTemplate = async (id) => {
    if (!confirm('Delete this template and all its milestones?')) return;
    await ModulesModel.deleteMilestoneTemplate(id);
    if (selectedId === id) setSelectedId(null);
    await loadTemplates();
    onChanged();
  };

  const handleAddItem = async (e) => {
    e.preventDefault();
    if (!newItem.title.trim() || !selectedId) return;
    setSaving(true);
    await ModulesModel.createTemplateItem(selectedId, { ...newItem, sortOrder: items.length });
    setNewItem({ title: '', offsetDays: 0, durationDays: 7, isCritical: false });
    setShowNewItem(false);
    setSaving(false);
    const updated = await ModulesModel.getTemplateItems(selectedId);
    setItems(updated || []);
  };

  const handleDeleteItem = async (itemId) => {
    await ModulesModel.deleteTemplateItem(selectedId, itemId);
    const updated = await ModulesModel.getTemplateItems(selectedId);
    setItems(updated || []);
  };

  const projectTypeOptions = ['general', 'software', 'marketing', 'operations', 'research', 'infrastructure'];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        <div className="flex justify-between items-center px-6 py-4 border-b">
          <h2 className="text-xl font-bold text-gray-900">Manage Milestone Templates</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* Template list */}
          <div className="w-64 border-r flex flex-col bg-gray-50">
            <div className="p-3 border-b">
              <button onClick={() => setShowNewTemplate(true)} className="w-full px-3 py-2 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 font-medium">+ New Template</button>
            </div>
            {showNewTemplate && (
              <form onSubmit={handleCreateTemplate} className="p-3 border-b bg-white space-y-2">
                <input required placeholder="Template name" value={newTemplate.name} onChange={e => setNewTemplate({...newTemplate, name: e.target.value})}
                  className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm" />
                <select value={newTemplate.projectType} onChange={e => setNewTemplate({...newTemplate, projectType: e.target.value})}
                  className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm">
                  {projectTypeOptions.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
                </select>
                <input placeholder="Description (optional)" value={newTemplate.description} onChange={e => setNewTemplate({...newTemplate, description: e.target.value})}
                  className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm" />
                <div className="flex gap-2">
                  <button type="submit" disabled={saving} className="flex-1 px-2 py-1.5 bg-purple-600 text-white text-xs rounded hover:bg-purple-700">Save</button>
                  <button type="button" onClick={() => setShowNewTemplate(false)} className="flex-1 px-2 py-1.5 text-xs text-gray-600 hover:text-gray-800">Cancel</button>
                </div>
              </form>
            )}
            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <p className="text-xs text-gray-400 p-3">Loading...</p>
              ) : templates.length === 0 ? (
                <p className="text-xs text-gray-400 p-3">No templates yet.</p>
              ) : templates.map(t => (
                <div key={t.id} onClick={() => setSelectedId(t.id)}
                  className={`px-3 py-2.5 cursor-pointer border-b border-gray-100 hover:bg-purple-50 flex justify-between items-start ${selectedId === t.id ? 'bg-purple-100 border-l-4 border-l-purple-500' : ''}`}>
                  <div>
                    <p className="text-sm font-medium text-gray-900">{t.template_name}</p>
                    <p className="text-xs text-gray-500">{t.project_type} · {t.item_count} milestones</p>
                  </div>
                  <button onClick={e => { e.stopPropagation(); requestConfirm(`Delete template "${t.template_name}"?`, () => handleDeleteTemplate(t.id)); }} className="text-red-400 hover:text-red-600 text-xs ml-1 mt-0.5">✕</button>
                </div>
              ))}
            </div>
          </div>

          {/* Template items */}
          <div className="flex-1 flex flex-col overflow-hidden">
            {!selected ? (
              <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">Select a template to view and edit its milestones</div>
            ) : (
              <>
                <div className="px-5 py-3 border-b flex justify-between items-center">
                  <div>
                    <h3 className="font-semibold text-gray-900">{selected.template_name}</h3>
                    {selected.description && <p className="text-xs text-gray-500 mt-0.5">{selected.description}</p>}
                  </div>
                  <button onClick={() => setShowNewItem(true)} className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">+ Add Milestone</button>
                </div>

                {showNewItem && (
                  <form onSubmit={handleAddItem} className="px-5 py-3 bg-blue-50 border-b grid grid-cols-2 gap-3">
                    <div className="col-span-2">
                      <input required placeholder="Milestone title" value={newItem.title} onChange={e => setNewItem({...newItem, title: e.target.value})}
                        className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Start offset (days from project start)</label>
                      <input type="number" min="0" value={newItem.offsetDays} onChange={e => setNewItem({...newItem, offsetDays: parseInt(e.target.value) || 0})}
                        className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Duration (days)</label>
                      <input type="number" min="1" value={newItem.durationDays} onChange={e => setNewItem({...newItem, durationDays: parseInt(e.target.value) || 1})}
                        className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm" />
                    </div>
                    <div className="col-span-2 flex items-center justify-between">
                      <label className="flex items-center gap-2 text-sm text-gray-700">
                        <input type="checkbox" checked={newItem.isCritical} onChange={e => setNewItem({...newItem, isCritical: e.target.checked})} />
                        Mark as critical path
                      </label>
                      <div className="flex gap-2">
                        <button type="submit" disabled={saving} className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700">Add</button>
                        <button type="button" onClick={() => setShowNewItem(false)} className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
                      </div>
                    </div>
                  </form>
                )}

                <div className="flex-1 overflow-y-auto">
                  {items.length === 0 ? (
                    <div className="p-6 text-center text-gray-400 text-sm">No milestones in this template yet. Add one above.</div>
                  ) : (
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">#</th>
                          <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Milestone</th>
                          <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Offset</th>
                          <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Duration</th>
                          <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Critical</th>
                          <th className="px-4 py-2"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {items.map((item, i) => (
                          <tr key={item.id} className="hover:bg-gray-50">
                            <td className="px-4 py-2.5 text-gray-400 text-xs">{i + 1}</td>
                            <td className="px-4 py-2.5 font-medium text-gray-900">{item.title}</td>
                            <td className="px-4 py-2.5 text-gray-600">Day {item.offset_from_start}</td>
                            <td className="px-4 py-2.5 text-gray-600">{item.default_duration_days}d</td>
                            <td className="px-4 py-2.5">
                              {item.is_critical ? <span className="px-1.5 py-0.5 bg-red-100 text-red-700 rounded text-xs font-semibold">Critical</span> : <span className="text-gray-300">—</span>}
                            </td>
                            <td className="px-4 py-2.5 text-right">
                              <button onClick={() => requestConfirm('Remove this checklist item?', () => handleDeleteItem(item.id))} className="text-red-400 hover:text-red-600 text-xs">Remove</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const GenerateFromTemplateModal = ({ projects, templates, onClose, onGenerate }) => {
  const [selectedProject, setSelectedProject] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [startDate, setStartDate] = useState('');
  const [templateItems, setTemplateItems] = useState([]);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    if (selectedTemplate) {
      ModulesModel.getTemplateItems(parseInt(selectedTemplate)).then(items => setTemplateItems(items || []));
    } else { setTemplateItems([]); }
  }, [selectedTemplate]);

  const handleGenerate = async () => {
    if (!selectedProject || !selectedTemplate || !startDate) return;
    setGenerating(true);
    await onGenerate({ projectId: selectedProject, templateId: parseInt(selectedTemplate), startDate });
    setGenerating(false);
  };

  const tmpl = templates.find(t => t.id === parseInt(selectedTemplate));

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
          <h2 className="text-xl font-bold text-gray-900">Generate from Template</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <div className="p-6 space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Project *</label>
              <select value={selectedProject} onChange={e => setSelectedProject(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500">
                <option value="">Select project...</option>
                {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Template *</label>
              <select value={selectedTemplate} onChange={e => setSelectedTemplate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500">
                <option value="">Select template...</option>
                {templates.map(t => <option key={t.id} value={t.id}>{t.template_name} ({t.item_count} milestones)</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Project Start Date *</label>
            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" />
            <p className="text-xs text-gray-500 mt-1">Milestone due dates are calculated from this date</p>
          </div>
          {tmpl && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="font-semibold text-blue-900 mb-1">{tmpl.template_name}</h3>
              <p className="text-sm text-blue-800">{tmpl.description}</p>
              <p className="text-xs text-blue-600 mt-1">Type: {tmpl.project_type}</p>
            </div>
          )}
          {templateItems.length > 0 && (
            <div className="border rounded-lg">
              <div className="bg-gray-50 px-4 py-2 border-b"><h3 className="font-semibold text-gray-900 text-sm">Preview: {templateItems.length} Milestones</h3></div>
              <div className="p-4 max-h-52 overflow-y-auto space-y-2">
                {templateItems.map((item, i) => (
                  <div key={item.id} className="flex items-start gap-3 text-sm">
                    <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-bold text-xs shrink-0">{i+1}</div>
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">{item.title}{item.is_critical ? <span className="ml-2 text-red-600 font-semibold text-xs">CRITICAL</span> : ''}</div>
                      <div className="text-xs text-gray-500">Day {item.offset_from_start} + {item.default_duration_days}d</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
            <button onClick={handleGenerate} disabled={generating || !selectedProject || !selectedTemplate || !startDate}
              className="px-4 py-2 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 font-medium disabled:opacity-50">
              {generating ? 'Generating...' : `Generate ${templateItems.length} Milestones`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ---- Milestone Engine View ----

const MilestoneEngineView = ({ onBack, projects, user }) => {
  const [milestones, setMilestones] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [execSummary, setExecSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showGenerate, setShowGenerate] = useState(false);
  const [showManageTemplates, setShowManageTemplates] = useState(false);
  const [editingMs, setEditingMs] = useState(null);
  const [filters, setFilters] = useState({ project: '', status: '', health: '' });

  const loadData = async () => {
    setLoading(true);
    const [ms, tmpl, summary] = await Promise.all([
      ModulesModel.getMilestones(), ModulesModel.getMilestoneTemplates(), ModulesModel.getExecutiveSummary()
    ]);
    setMilestones(ms || []); setTemplates(tmpl || []); setExecSummary(summary);
    setLoading(false);
  };
  useEffect(() => { loadData(); }, []);

  const handleCreate = async (data) => { await ModulesModel.createMilestone(data); setShowCreate(false); loadData(); };
  const handleUpdate = async (msId, data) => { await ModulesModel.updateMilestone(msId, data); setEditingMs(null); loadData(); };
  const handleDelete = async (msId) => { await ModulesModel.deleteMilestone(msId); setEditingMs(null); loadData(); };
  const handleComplete = async (msId) => {
    const r = await ModulesModel.completeMilestone(msId);
    if (!r.success) alert(r.error || 'Failed'); else loadData();
  };
  const handleGenerate = async (data) => {
    const r = await ModulesModel.generateMilestonesFromTemplate(data);
    if (r.success) { alert(`Generated ${r.createdCount} milestones!`); setShowGenerate(false); loadData(); }
    else alert(r.error || 'Failed');
  };

  const filtered = milestones.filter(m => {
    if (filters.project && m.project_id !== filters.project) return false;
    if (filters.status && m.status !== filters.status) return false;
    if (filters.health && m.health_status !== filters.health) return false;
    return true;
  });

  const s = execSummary?.summary || {};

  if (loading) return <div className="flex items-center justify-center h-64"><div className="text-gray-500">Loading Milestone Engine...</div></div>;

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <button onClick={onBack} className="flex items-center gap-2 text-blue-600 hover:text-blue-800 mb-4 text-sm font-medium">
        <ChevronRight size={16} className="rotate-180" /> Back to PlanTrack
      </button>

      <div className="flex justify-between items-start mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Milestone Engine</h1>
          <p className="text-gray-600 mt-1">Template-driven milestones, risk scoring, and critical path analysis</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowManageTemplates(true)} className="px-4 py-2 bg-gray-600 text-white text-sm rounded-lg hover:bg-gray-700 font-medium">Manage Templates</button>
          <button onClick={() => setShowGenerate(true)} className="px-4 py-2 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 font-medium">Generate from Template</button>
          <button onClick={() => setShowCreate(true)} className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 font-medium">+ New Milestone</button>
        </div>
      </div>

      {/* Executive Summary */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
        {[
          { label: 'Total', value: s.total_milestones || 0, sub: `${s.completed || 0} completed`, color: 'text-gray-900' },
          { label: 'Overdue', value: s.overdue || 0, sub: 'Requires attention', color: 'text-red-600' },
          { label: 'Critical', value: s.critical || 0, sub: 'High priority', color: 'text-orange-600' },
          { label: 'At Risk', value: s.at_risk || 0, sub: 'Monitor closely', color: 'text-yellow-600' },
          { label: 'Critical Path', value: s.on_critical_path || 0, sub: 'Essential', color: 'text-purple-600' }
        ].map(c => (
          <div key={c.label} className="orbit-card p-3.5">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-1">{c.label}</div>
            <div className={`text-xl font-bold tabular-nums ${c.color}`}>{c.value}</div>
            <div className="text-[11px] text-gray-400 mt-0.5">{c.sub}</div>
          </div>
        ))}
      </div>

      {/* Delayed + Upcoming */}
      {(execSummary?.delayedMilestones?.length > 0 || execSummary?.upcomingMilestones?.length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
          {execSummary?.delayedMilestones?.length > 0 && (
            <div className="orbit-card p-4">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-red-600 mb-2.5 flex items-center gap-1.5"><AlertTriangle size={13} /> Delayed Milestones</h3>
              <div className="space-y-1.5">
                {execSummary.delayedMilestones.map(m => {
                  const hs = getHealthBadgeStyle(m.health_status);
                  return (
                    <div key={m.milestone_id} className="flex justify-between items-center gap-2 text-sm">
                      <div className="min-w-0 truncate"><span className="font-medium text-gray-900">{m.title}</span><span className="text-gray-400 ml-2 text-xs">{m.project_name}</span></div>
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold shrink-0 ${hs.bg} ${hs.text}`}>{hs.label}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {execSummary?.upcomingMilestones?.length > 0 && (
            <div className="orbit-card p-4">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-blue-600 mb-2.5">Upcoming Milestones</h3>
              <div className="space-y-1.5">
                {execSummary.upcomingMilestones.map(m => (
                  <div key={m.milestone_id} className="flex justify-between items-center gap-2 text-sm">
                    <div className="min-w-0 truncate"><span className="font-medium text-gray-900">{m.title}</span><span className="text-gray-400 ml-2 text-xs">{m.project_name}</span></div>
                    <span className="text-xs text-gray-400 shrink-0">{m.due_date}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="orbit-card p-3.5 mb-5">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <select value={filters.project} onChange={e => setFilters({...filters, project: e.target.value})}
            className="w-full px-2.5 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none text-gray-700">
            <option value="">All Projects</option>
            {(projects || []).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <select value={filters.status} onChange={e => setFilters({...filters, status: e.target.value})}
            className="w-full px-2.5 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none text-gray-700">
            <option value="">All Statuses</option><option value="pending">Pending</option><option value="in_progress">In Progress</option><option value="completed">Completed</option>
          </select>
          <select value={filters.health} onChange={e => setFilters({...filters, health: e.target.value})}
            className="w-full px-2.5 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none text-gray-700">
            <option value="">All Health</option><option value="on-track">On Track</option><option value="at-risk">At Risk</option><option value="critical">Critical</option><option value="overdue">Overdue</option><option value="completed">Completed</option>
          </select>
        </div>
      </div>

      {/* Milestone list — one compact row per milestone instead of a tall card, so the
          page stays scannable even with dozens of milestones across every project. */}
      {filtered.length === 0 ? (
        <div className="orbit-card p-12 text-center">
          <p className="text-gray-500 mb-4">No milestones match your filters.</p>
          <button onClick={() => setFilters({ project: '', status: '', health: '' })} className="text-blue-600 hover:text-blue-800 text-sm font-medium">Clear Filters</button>
        </div>
      ) : (
        <div className="orbit-card overflow-hidden">
          <div className="hidden sm:flex items-center gap-3 px-4 py-2 border-b border-gray-100 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
            <span className="w-2 shrink-0" />
            <span className="flex-1">Milestone</span>
            <span className="w-32 shrink-0">Progress</span>
            <span className="w-10 shrink-0 text-right">Risk</span>
            <span className="w-[68px] shrink-0" />
          </div>
          <div className="divide-y divide-gray-100">
            {filtered.map(ms => {
              const hs = getHealthBadgeStyle(ms.health_status);
              const rf = formatRiskScore(ms.risk_score || 0);
              const isDone = ms.status === 'completed';
              return (
                <div key={ms.id || ms.milestone_id} className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 transition-colors">
                  <span className="w-2 h-2 rounded-full shrink-0" style={{ background: MILESTONE_HEALTH_DOT[ms.health_status] || MILESTONE_HEALTH_DOT['on-track'] }} title={hs.label} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-sm font-semibold text-gray-900 truncate">{ms.title}</span>
                      {ms.is_critical_path === 1 && <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-purple-100 text-purple-700 shrink-0">CRITICAL</span>}
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold shrink-0 ${hs.bg} ${hs.text}`}>{hs.label}</span>
                    </div>
                    <div className="text-xs text-gray-400 truncate mt-0.5">
                      <span className="text-gray-500 font-medium">{ms.project_name}</span>
                      {ms.owner_name && <> · {ms.owner_name}</>}
                      {' · '}{isDone ? (ms.completed_at ? `Completed ${ms.completed_at}` : 'Completed') : `Due ${ms.due_date}`}
                    </div>
                  </div>
                  <div className="hidden sm:block w-32 shrink-0">
                    {!isDone && (
                      <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden" title={`${ms.completion_percentage || 0}% complete`}>
                        <div className="h-full bg-blue-500 rounded-full" style={{ width: `${ms.completion_percentage || 0}%` }} />
                      </div>
                    )}
                  </div>
                  <div className={`hidden sm:block w-10 shrink-0 text-right text-xs font-semibold ${isDone ? 'text-gray-300' : rf.color}`}>
                    {isDone ? '—' : (ms.risk_score || 0)}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {!isDone && (
                      <button onClick={() => handleComplete(ms.milestone_id)} title="Mark complete" className="p-1.5 text-green-600 hover:bg-green-50 rounded-md transition-colors">
                        <CheckSquare size={15} />
                      </button>
                    )}
                    <button onClick={async () => { const d = await ModulesModel.getMilestoneDetail(ms.milestone_id); if (d) setEditingMs(d); }}
                      title="Edit" className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-md transition-colors">
                      <Pencil size={14} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {showCreate && <AddMilestoneModal onClose={() => setShowCreate(false)} onSubmit={handleCreate} projects={projects || []} user={user} />}
      {showGenerate && <GenerateFromTemplateModal projects={projects || []} templates={templates} onClose={() => setShowGenerate(false)} onGenerate={handleGenerate} />}
      {showManageTemplates && <ManageTemplatesModal onClose={() => setShowManageTemplates(false)} onChanged={() => { loadData(); }} />}
      {editingMs && <EditMilestoneModal milestone={editingMs} allMilestones={milestones} onClose={() => setEditingMs(null)} onSubmit={(data) => handleUpdate(editingMs.milestone_id, data)} onDelete={handleDelete} />}
    </div>
  );
};

// ---- Modal Components ----

const AddProjectModal = ({ onClose, onSubmit }) => {
  const [form, setForm] = useState({ name: '', description: '', status: 'planning', priority: 'P2', owner: '', startDate: '', targetEndDate: '', budgetHours: '' });
  const handleSubmit = (e) => { e.preventDefault(); onSubmit(form); };
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-xl font-semibold">New Project</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Project Name *</label>
            <input required value={form.name} onChange={e => setForm({...form, name: e.target.value})}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <MiniWysiwyg value={form.description} onChange={v => setForm({...form, description: v})} placeholder="Add a description…" minHeight="80px" />
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select value={form.status} onChange={e => setForm({...form, status: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                <option value="planning">Planning</option>
                <option value="active">Active</option>
                <option value="on_hold">On Hold</option>
                <option value="completed">Completed</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
              <select value={form.priority} onChange={e => setForm({...form, priority: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                <option value="P1">P1 - Critical</option>
                <option value="P2">P2 - High</option>
                <option value="P3">P3 - Normal</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Owner</label>
              <input value={form.owner} onChange={e => setForm({...form, owner: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
              <input type="date" value={form.startDate} onChange={e => setForm({...form, startDate: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Target End Date</label>
              <input type="date" value={form.targetEndDate} onChange={e => setForm({...form, targetEndDate: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Budget Hours</label>
            <input type="number" value={form.budgetHours} onChange={e => setForm({...form, budgetHours: e.target.value})}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
            <button type="submit" className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">Create Project</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const EditProjectModal = ({ project, sprints, onClose, onSubmit }) => {
  const [form, setForm] = useState({
    name: project.name || '',
    description: project.description || '',
    status: project.status || 'planning',
    priority: project.priority || 'P2',
    owner: project.owner || '',
    startDate: project.startDate || '',
    targetEndDate: project.targetEndDate || '',
    budgetHours: project.budgetHours || '',
    progress: project.progress || 0,
    sprintId: project.sprintId || ''
  });
  const handleSubmit = (e) => { e.preventDefault(); onSubmit(form); };
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-xl font-semibold">Edit Project</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Project Name *</label>
            <input required value={form.name} onChange={e => setForm({...form, name: e.target.value})}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <MiniWysiwyg value={form.description} onChange={v => setForm({...form, description: v})} placeholder="Add a description…" minHeight="80px" />
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select value={form.status} onChange={e => setForm({...form, status: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                <option value="planning">Planning</option>
                <option value="active">Active</option>
                <option value="on_hold">On Hold</option>
                <option value="completed">Completed</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
              <select value={form.priority} onChange={e => setForm({...form, priority: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                <option value="P1">P1 - Critical</option>
                <option value="P2">P2 - High</option>
                <option value="P3">P3 - Normal</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Progress %</label>
              <input type="number" min="0" max="100" value={form.progress} onChange={e => setForm({...form, progress: parseInt(e.target.value) || 0})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Owner</label>
            <input value={form.owner} onChange={e => setForm({...form, owner: e.target.value})}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
              <input type="date" value={form.startDate} onChange={e => setForm({...form, startDate: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Target End Date</label>
              <input type="date" value={form.targetEndDate} onChange={e => setForm({...form, targetEndDate: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Budget Hours</label>
              <input type="number" value={form.budgetHours} onChange={e => setForm({...form, budgetHours: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sprint</label>
              <select value={form.sprintId} onChange={e => setForm({...form, sprintId: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                <option value="">No Sprint</option>
                {(sprints || []).map(s => (
                  <option key={s.id} value={s.id}>
                    {s.name}{s.status === 'active' ? ' (Active)' : ''}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
            <button type="submit" className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">Save Changes</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const AddSprintModal = ({ onClose, onSubmit }) => {
  const [form, setForm] = useState({ name: '', number: '', startDate: '', endDate: '', status: 'active', goal: '', capacityHours: '160' });
  const handleSubmit = (e) => { e.preventDefault(); onSubmit(form); };
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-xl font-semibold">New Sprint</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sprint Name *</label>
              <input required value={form.name} onChange={e => setForm({...form, name: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sprint Number</label>
              <input type="number" value={form.number} onChange={e => setForm({...form, number: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select value={form.status} onChange={e => setForm({...form, status: e.target.value})}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
              <option value="active">Active</option>
              <option value="planned">Planned</option>
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date *</label>
              <input required type="date" value={form.startDate} onChange={e => setForm({...form, startDate: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Date *</label>
              <input required type="date" value={form.endDate} onChange={e => setForm({...form, endDate: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Sprint Goal</label>
            <input value={form.goal} onChange={e => setForm({...form, goal: e.target.value})}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Capacity Hours</label>
            <input type="number" value={form.capacityHours} onChange={e => setForm({...form, capacityHours: e.target.value})}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
            <button type="submit" className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">Create Sprint</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const AddTaskInline = ({ onSubmit, onCancel, user, teamMembers = [] }) => {
  const isAdmin = user?.role === 'admin';
  const isManager = !isAdmin && (user?.role?.endsWith('_manager') || user?.role === 'manager');
  const selfAssign = !isAdmin && !isManager;
  const assignableMembers = isAdmin || isManager ? teamMembers : [];
  const defaultAssignee = user?.name || '';

  const [form, setForm] = useState({ title: '', description: '', priority: 'P2', assignee: defaultAssignee, type: 'task', estimatedMinutes: '', storyPoints: '' });
  const [errors, setErrors] = useState({});
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.estimatedMinutes || parseInt(form.estimatedMinutes) <= 0) { setErrors({ estimatedMinutes: 'Estimated Time is required' }); return; }
    onSubmit(form);
  };
  return (
    <form onSubmit={handleSubmit} className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
      <input required placeholder="Task title" value={form.title} onChange={e => setForm({...form, title: e.target.value})}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
      <MiniWysiwyg value={form.description} onChange={desc => setForm({...form, description: desc})} placeholder="Description (optional)" minHeight={80} />
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <select value={form.priority} onChange={e => setForm({...form, priority: e.target.value})}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm">
          <option value="P1">P1</option><option value="P2">P2</option><option value="P3">P3</option>
        </select>
        <select value={form.type} onChange={e => setForm({...form, type: e.target.value})}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm">
          <option value="task">Project Task</option><option value="ADHOC">Ad hoc</option>
        </select>
        {selfAssign ? (
          <input type="text" value={form.assignee} readOnly
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm bg-gray-50 text-gray-500 cursor-not-allowed" />
        ) : (
          <select value={form.assignee} onChange={e => setForm({...form, assignee: e.target.value})}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm">
            <option value="">Unassigned</option>
            {assignableMembers.map(m => <option key={m.user_id} value={m.user_name}>{m.user_name}</option>)}
          </select>
        )}
        <div>
          <input type="number" min="1" placeholder="Est. Time (min) *" value={form.estimatedMinutes}
            onChange={e => { setForm({...form, estimatedMinutes: e.target.value}); setErrors({}); }}
            className={`w-full border rounded-lg px-3 py-2 text-sm ${errors.estimatedMinutes ? 'border-red-400' : 'border-gray-300'}`} />
          {errors.estimatedMinutes && <p className="text-xs text-red-500 mt-1">{errors.estimatedMinutes}</p>}
        </div>
      </div>
      <div className="flex gap-2">
        <button type="submit" className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">Add Task</button>
        <button type="button" onClick={onCancel} className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
      </div>
    </form>
  );
};

// ---- Project Detail View ----

const ProjectUpdateItem = ({ upd, projectId, currentUser, userRole, onRefresh }) => {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(upd.content);
  const isAdmin = userRole === 'admin';
  const isOwner = isAdmin || (!!currentUser && currentUser === (upd.userName || upd.user_name));
  const saveEdit = async () => {
    if (!text.trim()) return;
    await ModulesModel.editProjectUpdate(projectId, upd.id, text.trim());
    setEditing(false);
    onRefresh();
  };
  const deleteUpd = async () => {
    if (!confirm('Delete this update?')) return;
    await ModulesModel.deleteProjectUpdate(projectId, upd.id);
    onRefresh();
  };
  return (
    <div className="bg-white rounded-lg shadow border border-gray-200 p-4">
      <div className="flex items-center gap-2 mb-2">
        <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-sm font-bold">{(upd.userName||'?').charAt(0).toUpperCase()}</div>
        <span className="font-medium text-sm text-gray-900">{upd.userName}</span>
        {upd.updateType === 'status_change' && <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs font-semibold">Status Change</span>}
        {upd.updateType === 'progress' && <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs font-semibold">Progress</span>}
        <span className="text-xs text-gray-400 ml-auto">{upd.createdAt ? (() => { try { const d = new Date(upd.createdAt.endsWith('Z') ? upd.createdAt : upd.createdAt + 'Z'); return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); } catch { return upd.createdAt; } })() : ''}</span>
        {isOwner && !editing && (
          <div className="flex gap-1 ml-2">
            <button onClick={() => { setText(upd.content); setEditing(true); }} className="text-xs text-indigo-500 hover:text-indigo-700 px-1.5 py-0.5 rounded hover:bg-indigo-50">Edit</button>
            <button onClick={deleteUpd} className="text-xs text-red-400 hover:text-red-600 px-1.5 py-0.5 rounded hover:bg-red-50">Delete</button>
          </div>
        )}
      </div>
      {editing ? (
        <div className="space-y-2">
          <textarea value={text} onChange={e => setText(e.target.value)} rows={3}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 resize-none" />
          <div className="flex gap-2 justify-end">
            <button onClick={() => setEditing(false)} className="px-3 py-1.5 text-xs border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
            <button onClick={saveEdit} className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700">Save</button>
          </div>
        </div>
      ) : (
        <p className="text-sm text-gray-700">{upd.content}</p>
      )}
      {upd.oldValue && upd.newValue && !editing && (
        <div className="mt-2 text-xs text-gray-500">
          <span className="px-2 py-0.5 bg-gray-100 rounded">{upd.oldValue}</span> → <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded">{upd.newValue}</span>
        </div>
      )}
    </div>
  );
};

const MilestoneGanttChart = ({ milestones, project }) => {
  if (!milestones || milestones.length === 0) return null;

  const parseDate = (s) => {
    if (!s) return null;
    const d = new Date(s);
    d.setHours(0, 0, 0, 0);
    return isNaN(d.getTime()) ? null : d;
  };

  const sorted = [...milestones]
    .filter(ms => parseDate(ms.due_date || ms.dueDate))
    .sort((a, b) => parseDate(a.due_date || a.dueDate) - parseDate(b.due_date || b.dueDate));

  if (sorted.length === 0) return null;

  const today = new Date(); today.setHours(0, 0, 0, 0);
  const dueDates = sorted.map(ms => parseDate(ms.due_date || ms.dueDate));

  let rangeStart = parseDate(project?.startDate);
  let rangeEnd = parseDate(project?.targetEndDate);
  if (!rangeStart) { rangeStart = new Date(dueDates[0]); rangeStart.setDate(rangeStart.getDate() - 14); }
  if (!rangeEnd) { rangeEnd = new Date(dueDates[dueDates.length - 1]); rangeEnd.setDate(rangeEnd.getDate() + 7); }
  if (today < rangeStart) { rangeStart = new Date(today.getTime() - 7 * 86400000); }
  if (today > rangeEnd) { rangeEnd = new Date(today.getTime() + 7 * 86400000); }

  const totalMs = rangeEnd - rangeStart;
  const CHART_W = 800;
  const ROW_H = 44;
  const HEADER_H = 42;
  const BAR_H = 20;
  const BAR_Y_OFF = (ROW_H - BAR_H) / 2;
  const svgH = HEADER_H + sorted.length * ROW_H + 4;

  const toX = (d) => Math.max(0, Math.min(CHART_W, ((d - rangeStart) / totalMs) * CHART_W));
  const todayX = toX(today);

  const months = [];
  const mc = new Date(rangeStart); mc.setDate(1);
  if (mc < rangeStart) mc.setMonth(mc.getMonth() + 1);
  while (mc <= rangeEnd) { months.push({ d: new Date(mc), x: toX(mc) }); mc.setMonth(mc.getMonth() + 1); }

  const phaseStarts = sorted.map((ms, i) =>
    i === 0 ? rangeStart : (parseDate(sorted[i - 1].due_date || sorted[i - 1].dueDate) || rangeStart)
  );

  const barColor = (ms) => {
    if (ms.status === 'completed') return { fill: '#22c55e', stroke: '#16a34a' };
    if (ms.status === 'in_progress') return { fill: '#3b82f6', stroke: '#2563eb' };
    const h = ms.health_status || ms.healthStatus;
    if (h === 'overdue') return { fill: '#ef4444', stroke: '#dc2626' };
    if (h === 'at-risk' || h === 'critical') return { fill: '#f59e0b', stroke: '#d97706' };
    return { fill: '#94a3b8', stroke: '#64748b' };
  };

  const LABEL_W = 170;

  return (
    <div className="bg-white rounded-lg shadow border border-gray-200 p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-700">Milestone Timeline</h3>
        <div className="flex items-center gap-3 flex-wrap">
          {[
            { color: '#22c55e', label: 'Completed' },
            { color: '#3b82f6', label: 'In Progress' },
            { color: '#94a3b8', label: 'Pending' },
            { color: '#f59e0b', label: 'At Risk' },
            { color: '#ef4444', label: 'Overdue' },
          ].map(({ color, label }) => (
            <div key={label} className="flex items-center gap-1">
              <div className="w-2.5 h-2.5 rounded-sm" style={{ background: color }} />
              <span className="text-[11px] text-gray-500">{label}</span>
            </div>
          ))}
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full bg-purple-500" />
            <span className="text-[11px] text-gray-500">Critical Path</span>
          </div>
        </div>
      </div>
      <div className="overflow-x-auto">
        <div style={{ display: 'flex', minWidth: LABEL_W + 400 }}>
          {/* Label column */}
          <div style={{ width: LABEL_W, flexShrink: 0 }}>
            <div style={{ height: HEADER_H }} className="border-b border-gray-200 flex items-end pb-1">
              <span className="text-[10px] text-gray-400 font-medium uppercase tracking-wide">Milestone</span>
            </div>
            {sorted.map((ms, i) => (
              <div key={ms.id || i} style={{ height: ROW_H }} className={`flex items-center pr-3 border-b border-gray-100 ${i % 2 === 0 ? 'bg-slate-50' : 'bg-white'}`}>
                <div className="flex items-center gap-1.5 min-w-0 w-full">
                  {(ms.is_critical_path || ms.isCriticalPath) == 1 && <span className="w-1.5 h-1.5 rounded-full bg-purple-500 shrink-0" />}
                  <span className="text-xs text-gray-800 truncate font-medium">{ms.title}</span>
                </div>
              </div>
            ))}
          </div>
          {/* SVG area */}
          <div className="flex-1 min-w-0">
            <svg width="100%" viewBox={`0 0 ${CHART_W} ${svgH}`} style={{ display: 'block', minWidth: 400 }} preserveAspectRatio="none">
              {/* Row backgrounds */}
              {sorted.map((_, i) => (
                <rect key={`bg-${i}`} x={0} y={HEADER_H + i * ROW_H} width={CHART_W} height={ROW_H} fill={i % 2 === 0 ? '#f8fafc' : '#ffffff'} />
              ))}
              {/* Month grid lines & labels */}
              {months.map((m, i) => (
                <g key={`m-${i}`}>
                  <line x1={m.x} y1={0} x2={m.x} y2={svgH} stroke="#e2e8f0" strokeWidth="1" />
                  <text x={m.x + 4} y={HEADER_H - 6} fontSize="11" fill="#94a3b8" fontFamily="system-ui,sans-serif">
                    {m.d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' })}
                  </text>
                </g>
              ))}
              {/* Header bottom line */}
              <line x1={0} y1={HEADER_H} x2={CHART_W} y2={HEADER_H} stroke="#cbd5e1" strokeWidth="1" />
              {/* Bars */}
              {sorted.map((ms, i) => {
                const due = parseDate(ms.due_date || ms.dueDate);
                if (!due) return null;
                const ps = phaseStarts[i];
                const x1 = toX(ps);
                const x2 = toX(due);
                const bw = Math.max(6, x2 - x1);
                const y = HEADER_H + i * ROW_H + BAR_Y_OFF;
                const { fill, stroke } = barColor(ms);
                const critical = (ms.is_critical_path || ms.isCriticalPath) == 1;
                const pct = ms.completion_percentage ?? ms.completionPercentage ?? 0;
                return (
                  <g key={`bar-${i}`}>
                    <rect x={x1} y={y} width={bw} height={BAR_H} rx={3} fill={fill} stroke={critical ? '#a855f7' : stroke} strokeWidth={critical ? 2 : 1} opacity={0.85} />
                    {ms.status === 'in_progress' && pct > 0 && (
                      <rect x={x1} y={y} width={bw * (pct / 100)} height={BAR_H} rx={3} fill="#1d4ed8" opacity={0.4} />
                    )}
                    {/* Diamond marker at due date */}
                    <polygon
                      points={`${x2},${y - 2} ${x2 + 6},${y + BAR_H / 2} ${x2},${y + BAR_H + 2} ${x2 - 6},${y + BAR_H / 2}`}
                      fill={fill} stroke={critical ? '#a855f7' : stroke} strokeWidth="1"
                    />
                    {/* Date label inside bar */}
                    {bw > 40 && (
                      <text x={x1 + 5} y={y + BAR_H / 2 + 4} fontSize="9" fill="white" fontFamily="system-ui,sans-serif" fontWeight="700">
                        {due.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </text>
                    )}
                  </g>
                );
              })}
              {/* Today line */}
              {todayX >= 0 && todayX <= CHART_W && (
                <g>
                  <line x1={todayX} y1={HEADER_H} x2={todayX} y2={svgH} stroke="#ef4444" strokeWidth="1.5" strokeDasharray="4 2" />
                  <text x={todayX + 3} y={HEADER_H - 5} fontSize="10" fill="#ef4444" fontFamily="system-ui,sans-serif" fontWeight="700">Today</text>
                </g>
              )}
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
};

const ProjectDetailView = ({ detail, onBack, onRefresh, onEdit, onCreateMilestone, onEditMilestone, onCompleteMilestone, onToggleArchive, onDelete, user, teamMembers = [] }) => {
  const [activeTab, setActiveTab] = useState('tasks');
  const [commentText, setCommentText] = useState('');
  const [commentHtml, setCommentHtml] = useState('');
  const [commentEditorKey, setCommentEditorKey] = useState(0);
  const [updateText, setUpdateText] = useState('');
  const [showAddTask, setShowAddTask] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [overview, setOverview] = useState(null);
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [editTaskForm, setEditTaskForm] = useState({});
  const [selectedProjectTask, setSelectedProjectTask] = useState(null);
  const [selectedProjectBug, setSelectedProjectBug] = useState(null);
  const [bugSort, setBugSort] = useState({ field: 'severity', dir: 'asc' });
  const [projectRequirements, setProjectRequirements] = useState([]);
  const [showReqPicker, setShowReqPicker] = useState(false);
  const [genTasksReq, setGenTasksReq] = useState(null);

  const { project, tasks, comments, updates, milestones, bugs = [], attachments = [] } = detail;

  useEffect(() => {
    if (project?.id) {
      ModulesModel.getProjectOverview(project.id).then(data => { if (data) setOverview(data); });
      ModulesModel.getQualityData(project.id).then(d => {
        if (d?.requirements?.length) setProjectRequirements(d.requirements);
      });
    }
  }, [project?.id]);

  const handleOpenGenerateTasks = () => {
    if (projectRequirements.length === 1) { setGenTasksReq(projectRequirements[0]); }
    else { setShowReqPicker(true); }
  };
  const statusStyle = getStatusStyle(project.status);
  const priorityStyle = getPriorityStyle(project.priority);
  const budgetPercent = project.budgetHours > 0 ? Math.round((project.spentHours / project.budgetHours) * 100) : 0;

  const handleAddComment = async () => {
    const content = commentHtml || commentText;
    if (!content.trim()) return;
    setSubmitting(true);
    await ModulesModel.addComment(project.id, content.trim());
    setCommentText('');
    setCommentHtml('');
    setCommentEditorKey(k => k + 1);
    setSubmitting(false);
    onRefresh();
  };

  const handleEditProjectComment = async (commentId, newContent) => {
    const token = localStorage.getItem('orbit_token');
    await fetch(`/api/plantrack/projects/${project.id}/comments/${commentId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify({ content: newContent })
    });
    onRefresh();
  };

  const handleDeleteProjectComment = async (commentId) => {
    const token = localStorage.getItem('orbit_token');
    await fetch(`/api/plantrack/projects/${project.id}/comments/${commentId}`, {
      method: 'DELETE',
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    });
    onRefresh();
  };

  const handleAddUpdate = async (e) => {
    e.preventDefault();
    if (!updateText.trim()) return;
    setSubmitting(true);
    try {
      const res = await ModulesModel.addUpdate(project.id, updateText.trim(), user?.name || user?.user_name);
      if (!res?.success) throw new Error(res?.error || 'Failed to save update');
      setUpdateText('');
      onRefresh();
    } catch (err) {
      alert('Could not save update: ' + (err.message || 'Unknown error'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!confirm('Delete this task? This cannot be undone.')) return;
    await ModulesModel.deleteProjectTask(project.id, taskId);
    onRefresh();
  };

  const startEditTask = (task) => {
    setEditingTaskId(task.id);
    setEditTaskForm({
      title: task.title, status: task.status, priority: task.priority,
      assignee: task.assignee || '', progress: task.progress || 0,
      // Minutes is the source of truth; fall back to legacy hours columns.
      estimatedMinutes: task.estimatedMinutes || (task.estimateHours ? Math.round(task.estimateHours * 60) : 0),
      actualMinutes: task.actualMinutes || (task.actualHours ? Math.round(task.actualHours * 60) : 0),
    });
  };

  const handleSaveTask = async () => {
    const res = await ModulesModel.updateDevOpsTask(editingTaskId, {
      ...editTaskForm,
      estimatedMinutes: parseInt(editTaskForm.estimatedMinutes) || 0,
      actualMinutes: parseInt(editTaskForm.actualMinutes) || 0,
    });
    // Backend rejects Done without actual time — keep the row open so the edit isn't lost.
    if (res && res.success === false) { alert(res.error || 'Could not save task.'); return; }
    setEditingTaskId(null);
    onRefresh();
  };

  const handleProjectTaskUpdate = async (taskId, changes) => {
    await ModulesModel.updateDevOpsTask(taskId, changes);
    onRefresh();
  };

  const handleAddTask = async (taskData) => {
    setSubmitting(true);
    if (taskData.type === 'ADHOC') {
      const priorityMap = { P1: 'urgent', P2: 'high', P3: 'medium' };
      await ModulesModel.createQuickTask({
        title: taskData.title,
        priority: priorityMap[taskData.priority] || 'medium',
        assignedTo: taskData.assignee || null,
        projectId: project.id,
        category: 'general',
        status: 'open',
        estimatedMinutes: parseInt(taskData.estimatedMinutes) || 0
      });
    } else {
      await ModulesModel.createProjectTask(project.id, taskData);
    }
    setShowAddTask(false);
    setSubmitting(false);
    onRefresh();
  };

  const tabs = [
    { id: 'charter', label: 'Charter', count: null },
    { id: 'stakeholders', label: 'Stakeholders', count: null },
    { id: 'tasks', label: 'Tasks', count: tasks.length },
    { id: 'bugs', label: 'Bugs', count: bugs.length },
    { id: 'risks', label: 'Risks', count: null },
    { id: 'updates', label: 'Updates', count: updates.length },
    { id: 'comments', label: 'Comments', count: comments.length },
    { id: 'milestones', label: 'Milestones', count: milestones.length },
    { id: 'gantt', label: 'Gantt', count: null },
    { id: 'attachments', label: 'Artifacts', count: attachments.length }
  ];

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      {selectedProjectTask && (
        <TaskDetailPanel
          key={selectedProjectTask.id}
          task={selectedProjectTask}
          onClose={() => setSelectedProjectTask(null)}
          onUpdate={handleProjectTaskUpdate}
          user={user}
        />
      )}
      {selectedProjectBug && (
        <BugDetailPanel
          bug={selectedProjectBug}
          onClose={() => setSelectedProjectBug(null)}
          user={user}
          teamMembers={teamMembers}
          onUpdate={async (bugId, data) => { await ModulesModel.updateBug(bugId, data); onRefresh(); }}
        />
      )}
      {/* Back button */}
      <button onClick={onBack} className="flex items-center gap-2 text-blue-600 hover:text-blue-800 mb-4 text-sm font-medium">
        <ChevronRight size={16} className="rotate-180" /> Back to Projects
      </button>

      {/* Project Header */}
      <div className="bg-white rounded-lg shadow border border-gray-200 p-6 mb-6">
        <div className="flex justify-between items-start">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <span className="text-xs font-semibold text-gray-500">{project.id}</span>
            <span className="px-2 py-0.5 rounded text-xs font-semibold" style={{ background: priorityStyle.bg, color: priorityStyle.color }}>{project.priority}</span>
            <span className="px-2 py-0.5 rounded text-xs font-semibold" style={{ background: statusStyle.bg, color: statusStyle.color }}>{statusStyle.label}</span>
            {project.sprintName && <span className="px-2 py-0.5 rounded text-xs font-semibold bg-blue-100 text-blue-700">{project.sprintName}</span>}
            {project.archived ? <span className="px-2 py-0.5 rounded text-xs font-semibold bg-gray-200 text-gray-600">Archived</span> : null}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {onToggleArchive && (
              <button
                onClick={async () => {
                  const archiving = !project.archived;
                  if (!confirm(archiving ? `Move "${project.name}" to Archive?` : `Restore "${project.name}" to active?`)) return;
                  await onToggleArchive(project.id, archiving);
                }}
                className="px-3 py-1.5 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium">
                {project.archived ? 'Unarchive' : 'Archive'}
              </button>
            )}
            <button onClick={onEdit} className="px-3 py-1.5 text-sm text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50 font-medium">Edit Project</button>
            {onDelete && ['admin', 'project'].includes(user?.role || user?.user_role) && (
              <button onClick={() => onDelete(project.id, project.name)}
                className="px-3 py-1.5 text-sm text-red-600 border border-red-300 rounded-lg hover:bg-red-50 font-medium">
                Delete
              </button>
            )}
          </div>
        </div>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">{project.name}</h1>
        {project.description && <div className="text-gray-600 mb-4 prose prose-sm max-w-none [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5" dangerouslySetInnerHTML={{ __html: sanitizeBugHtml(project.description) }} />}
        <div className="flex flex-wrap items-center gap-6 text-sm text-gray-500 mb-4">
          <span>👤 {project.owner}</span>
          <span>📅 {project.startDate} → {project.targetEndDate}</span>
          <span>💰 {project.spentHours}h / {project.budgetHours}h budget ({budgetPercent}%)</span>
        </div>
        <div>
          <div className="flex justify-between text-xs text-gray-600 mb-1">
            <span>Progress</span>
            <span>{project.progress}%</span>
          </div>
          <div className="h-2.5 bg-gray-200 rounded-full overflow-hidden">
            <div className="h-full rounded-full transition-all" style={{ width: `${project.progress}%`, background: project.progress === 100 ? '#28a745' : '#0366d6' }} />
          </div>
        </div>
      </div>

      {/* Cross-Module Summary */}
      {overview && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
          {[
            { label: 'DevOps Tasks', value: overview.tasks, icon: '🔧', color: '#0366d6' },
            { label: 'Quick Tasks', value: overview.quickTasks, icon: '⚡', color: '#667eea' },
            { label: 'Pipelines', value: overview.pipelines, icon: '📊', color: '#6f42c1' },
            { label: 'Time Logged', value: `${overview.timeLogged}h`, icon: '⏱️', color: '#17a2b8' },
            ...(overview.qa ? [
              { label: 'QA Test Cases', value: overview.qa.testCases, icon: '✓', color: '#20c997' },
              { label: 'Open Bugs', value: overview.qa.openBugs, icon: '🐛', color: overview.qa.openBugs > 0 ? '#dc3545' : '#28a745' }
            ] : [
              { label: 'QA Status', value: 'Not Enabled', icon: '✓', color: '#6b7280' },
              { label: '', value: '', icon: '', color: '' }
            ])
          ].filter(s => s.label).map(s => (
            <div key={s.label} className="bg-white rounded-lg shadow border border-gray-200 p-4">
              <div className="flex items-center gap-2 mb-1">
                <span>{s.icon}</span>
                <span className="text-xs font-medium text-gray-500">{s.label}</span>
              </div>
              <div className="text-xl font-bold" style={{ color: s.color }}>{s.value}</div>
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div className="overflow-x-auto mb-6">
        <div className="flex border-b border-gray-200 min-w-max">
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeTab === tab.id ? 'text-blue-600 border-blue-600' : 'text-gray-500 border-transparent hover:text-gray-700'}`}>
              {tab.label} {tab.count !== null && <span className="ml-1 px-1.5 py-0.5 bg-gray-100 rounded-full text-xs">{tab.count}</span>}
            </button>
          ))}
        </div>
      </div>

      {/* Tasks Tab */}
      {activeTab === 'tasks' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Linked Tasks</h2>
            <div className="flex items-center gap-2">
              {project.archived && (
                <span className="text-xs text-gray-500 italic">Archived projects can't receive new tasks.</span>
              )}
              {!project.archived && projectRequirements.length > 0 && !showAddTask && (
                <button onClick={handleOpenGenerateTasks}
                  className="px-3 py-1.5 bg-teal-600 text-white text-sm rounded-lg hover:bg-teal-700 flex items-center gap-1">
                  ⚡ Generate Sprint Tasks
                </button>
              )}
              {!project.archived && !showAddTask && (
                <button onClick={() => setShowAddTask(true)} className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">+ Add Task</button>
              )}
            </div>
          </div>
          {!project.archived && showAddTask && <div className="mb-4"><AddTaskInline onSubmit={handleAddTask} onCancel={() => setShowAddTask(false)} user={user} teamMembers={teamMembers} /></div>}
          {tasks.length === 0 ? (
            <div className="bg-white rounded-lg shadow border border-gray-200 p-8 text-center text-gray-400">No tasks linked to this project yet.</div>
          ) : (
            <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">ID</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Title</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Priority</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Assignee</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Est. / Act. (min)</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Progress</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {tasks.map(task => {
                    const isEditing = editingTaskId === task.id;
                    const ts = getStatusStyle(task.status);
                    const tp = getPriorityStyle(task.priority);
                    if (isEditing) {
                      return (
                        <tr key={task.id} className="bg-blue-50">
                          <td className="px-4 py-2 text-xs font-mono text-gray-500">{task.id}</td>
                          <td className="px-4 py-2">
                            <input value={editTaskForm.title} onChange={e => setEditTaskForm({...editTaskForm, title: e.target.value})}
                              className="w-full border border-blue-300 rounded px-2 py-1 text-sm focus:ring-1 focus:ring-blue-500" />
                          </td>
                          <td className="px-4 py-2">
                            <select value={editTaskForm.status} onChange={e => setEditTaskForm({...editTaskForm, status: e.target.value})}
                              className="border border-blue-300 rounded px-2 py-1 text-xs">
                              {['backlog','ready','in_progress','review','needs_rework','done'].map(s => <option key={s} value={s}>{s === 'needs_rework' ? 'Needs Rework' : s.replace(/_/g,' ')}</option>)}
                            </select>
                          </td>
                          <td className="px-4 py-2">
                            <select value={editTaskForm.priority} onChange={e => setEditTaskForm({...editTaskForm, priority: e.target.value})}
                              className="border border-blue-300 rounded px-2 py-1 text-xs">
                              {['P1','P2','P3'].map(p => <option key={p} value={p}>{p}</option>)}
                            </select>
                          </td>
                          <td className="px-4 py-2">
                            <input value={editTaskForm.assignee} onChange={e => setEditTaskForm({...editTaskForm, assignee: e.target.value})}
                              className="w-full border border-blue-300 rounded px-2 py-1 text-sm" placeholder="Assignee" />
                          </td>
                          <td className="px-4 py-2">
                            <div className="flex items-center gap-1">
                              <input type="number" min="0" value={editTaskForm.estimatedMinutes} placeholder="Est"
                                onChange={e => setEditTaskForm({...editTaskForm, estimatedMinutes: e.target.value})}
                                className="w-14 border border-blue-300 rounded px-1.5 py-1 text-xs" />
                              <span className="text-xs text-gray-400">/</span>
                              <input type="number" min="0" value={editTaskForm.actualMinutes} placeholder="Act"
                                onChange={e => setEditTaskForm({...editTaskForm, actualMinutes: e.target.value})}
                                className="w-14 border border-blue-300 rounded px-1.5 py-1 text-xs" />
                            </div>
                          </td>
                          <td className="px-4 py-2">
                            <div className="flex items-center gap-2">
                              <input type="number" min="0" max="100" value={editTaskForm.progress} onChange={e => setEditTaskForm({...editTaskForm, progress: parseInt(e.target.value) || 0})}
                                className="w-16 border border-blue-300 rounded px-2 py-1 text-xs" />
                              <span className="text-xs text-gray-500">%</span>
                            </div>
                          </td>
                          <td className="px-4 py-2 text-right whitespace-nowrap">
                            <button onClick={handleSaveTask} className="text-blue-600 hover:text-blue-800 text-xs font-medium mr-2">Save</button>
                            <button onClick={() => setEditingTaskId(null)} className="text-gray-400 hover:text-gray-600 text-xs">Cancel</button>
                          </td>
                        </tr>
                      );
                    }
                    return (
                      <tr key={task.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-xs font-mono text-gray-500">{task.id}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{task.title}</td>
                        <td className="px-4 py-3"><span className="px-2 py-0.5 rounded text-xs font-semibold" style={{ background: ts.bg, color: ts.color }}>{ts.label}</span></td>
                        <td className="px-4 py-3"><span className="px-2 py-0.5 rounded text-xs font-semibold" style={{ background: tp.bg, color: tp.color }}>{task.priority}</span></td>
                        <td className="px-4 py-3 text-sm text-gray-600">{task.assignee}</td>
                        <td className="px-4 py-3 text-xs whitespace-nowrap">
                          {(() => {
                            // Minutes is the source of truth; estimateHours is a legacy fallback.
                            const est = task.estimatedMinutes || (task.estimateHours ? Math.round(task.estimateHours * 60) : 0);
                            const act = task.actualMinutes || (task.actualHours ? Math.round(task.actualHours * 60) : 0);
                            if (!est && !act) return <span className="text-gray-400">—</span>;
                            return (
                              <span className="text-gray-600">
                                {est || 0}m
                                <span className="text-gray-300 mx-1">/</span>
                                <span className={act > est && est > 0 ? 'text-red-600 font-medium' : ''}>{act || 0}m</span>
                              </span>
                            );
                          })()}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-16 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                              <div className="h-full bg-blue-600 rounded-full" style={{ width: `${task.progress || 0}%` }} />
                            </div>
                            <span className="text-xs text-gray-500">{task.progress || 0}%</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          <button onClick={() => setSelectedProjectTask(task)} className="text-blue-500 hover:text-blue-700 text-xs font-medium mr-2">View</button>
                          <button onClick={() => startEditTask(task)} className="text-indigo-500 hover:text-indigo-700 text-xs font-medium mr-2">Edit</button>
                          <button onClick={() => requestConfirm(`Delete task "${task.title}"?`, () => handleDeleteTask(task.id))} className="text-red-400 hover:text-red-600 text-xs font-medium">Delete</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Requirement picker modal for Generate Sprint Tasks */}
      {showReqPicker && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h2 className="text-base font-bold text-gray-900">Select a Requirement</h2>
              <button onClick={() => setShowReqPicker(false)} className="text-gray-400 hover:text-gray-600 text-xl font-bold leading-none">×</button>
            </div>
            <div className="px-6 py-4 max-h-80 overflow-y-auto space-y-2">
              {(projectRequirements || []).map(req => (
                <button key={req.id} onClick={() => { setGenTasksReq(req); setShowReqPicker(false); }}
                  className="w-full text-left px-4 py-3 rounded-lg border border-gray-200 hover:border-teal-400 hover:bg-teal-50 transition-colors">
                  <div className="text-xs font-mono text-teal-600 mb-0.5">{req.req_code || req.reqCode}</div>
                  <div className="text-sm font-medium text-gray-900">{req.title}</div>
                  {req.req_type && <div className="text-xs text-gray-400 mt-0.5">{req.req_type}</div>}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Generate Sprint Tasks modal */}
      {genTasksReq && (
        <GenerateSprintTasksModal
          requirement={genTasksReq}
          teamMembers={teamMembers}
          onClose={() => setGenTasksReq(null)}
          onGenerated={() => { setGenTasksReq(null); onRefresh(); }}
        />
      )}

      {/* Bugs Tab */}
      {activeTab === 'bugs' && (() => {
        const SEVERITY_RANK = { critical: 0, high: 1, medium: 2, low: 3 };
        const STATUS_RANK = { open: 0, in_progress: 1, for_qa: 2, awaiting_vendor: 3, needs_rework: 4, resolved: 5, closed: 6 };
        const sortKey = (b) => {
          switch (bugSort.field) {
            case 'bugCode':    return b.bugCode || '';
            case 'title':      return (b.title || '').toLowerCase();
            case 'severity':   return SEVERITY_RANK[b.severity] ?? 99;
            case 'status':     return STATUS_RANK[b.status] ?? 99;
            case 'module':     return (b.module || '￿').toLowerCase();
            case 'assignedTo': return (b.assignedTo || '￿').toLowerCase();
            case 'created':    return b.createdAt ? new Date(b.createdAt).getTime() : 0;
            default:           return 0;
          }
        };
        const sortedBugs = [...bugs].sort((a, b) => {
          const ka = sortKey(a), kb = sortKey(b);
          const cmp = typeof ka === 'string' ? ka.localeCompare(kb) : ka - kb;
          return bugSort.dir === 'asc' ? cmp : -cmp;
        });
        const onHeaderClick = (field) => setBugSort(s => s.field === field
          ? { field, dir: s.dir === 'asc' ? 'desc' : 'asc' }
          : { field, dir: field === 'created' ? 'desc' : 'asc' });
        const SortHeader = ({ field, label, align = 'left' }) => {
          const active = bugSort.field === field;
          const arrow = active ? (bugSort.dir === 'asc' ? '↑' : '↓') : '';
          return (
            <th onClick={() => onHeaderClick(field)}
              className={`px-4 py-3 text-${align} text-xs font-semibold cursor-pointer select-none transition-colors ${active ? 'text-indigo-700 bg-indigo-50/40' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100/60'}`}>
              <span className="inline-flex items-center gap-1">
                {label}
                <span className={`text-[10px] ${active ? 'opacity-80' : 'opacity-30'}`}>{arrow || '↕'}</span>
              </span>
            </th>
          );
        };
        return (
        <div>
          <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
            <h2 className="text-lg font-semibold text-gray-900">QA Bugs <span className="ml-1 text-sm font-normal text-gray-400">({bugs.length})</span></h2>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500 hidden sm:inline">Sort:</span>
              <select value={`${bugSort.field}:${bugSort.dir}`}
                onChange={(e) => { const [field, dir] = e.target.value.split(':'); setBugSort({ field, dir }); }}
                className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 bg-white focus:ring-2 focus:ring-indigo-500">
                <option value="severity:asc">Severity (critical → low)</option>
                <option value="severity:desc">Severity (low → critical)</option>
                <option value="status:asc">Status (open → closed)</option>
                <option value="status:desc">Status (closed → open)</option>
                <option value="created:desc">Newest first</option>
                <option value="created:asc">Oldest first</option>
                <option value="title:asc">Title A → Z</option>
                <option value="title:desc">Title Z → A</option>
                <option value="assignedTo:asc">Assignee A → Z</option>
                <option value="bugCode:asc">Bug ID A → Z</option>
              </select>
              <span className="text-xs text-gray-400 hidden md:inline">· Bugs are created in Quality</span>
            </div>
          </div>
          {bugs.length === 0 ? (
            <div className="bg-white rounded-lg shadow border border-gray-200 p-8 text-center text-gray-400">No bugs reported for this project yet.</div>
          ) : (
            <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <SortHeader field="bugCode" label="Bug ID" />
                    <SortHeader field="title" label="Title" />
                    <SortHeader field="severity" label="Severity" />
                    <SortHeader field="status" label="Status" />
                    <SortHeader field="module" label="Module" />
                    <SortHeader field="assignedTo" label="Assigned To" />
                    <SortHeader field="created" label="Reported" />
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {sortedBugs.map(bug => {
                    const severityColors = { critical: { bg: '#fee2e2', color: '#dc2626' }, high: { bg: '#fed7aa', color: '#ea580c' }, medium: { bg: '#fef9c3', color: '#ca8a04' }, low: { bg: '#dcfce7', color: '#16a34a' } };
                    const sc = severityColors[bug.severity] || severityColors.medium;
                    const bugStatusColors = { open: { bg: '#fee2e2', color: '#dc2626' }, 'in-progress': { bg: '#dbeafe', color: '#2563eb' }, for_qa: { bg: '#fdf2f8', color: '#be185d' }, awaiting_vendor: { bg: '#fef3c7', color: '#b45309' }, needs_rework: { bg: '#ffedd5', color: '#c2410c' }, resolved: { bg: '#dcfce7', color: '#16a34a' }, closed: { bg: '#f3f4f6', color: '#6b7280' } };
                    const bc = bugStatusColors[bug.status] || bugStatusColors.open;
                    return (
                      <tr key={bug.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-xs font-mono text-gray-500">{bug.bugCode}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{bug.title}</td>
                        <td className="px-4 py-3"><span className="px-2 py-0.5 rounded text-xs font-semibold capitalize" style={{ background: sc.bg, color: sc.color }}>{bug.severity}</span></td>
                        <td className="px-4 py-3"><span className="px-2 py-0.5 rounded text-xs font-semibold capitalize" style={{ background: bc.bg, color: bc.color }}>{bug.status || 'open'}</span></td>
                        <td className="px-4 py-3 text-sm text-gray-600">{bug.module || '—'}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{bug.assignedTo || '—'}</td>
                        <td className="px-4 py-3 text-xs text-gray-400">{bug.createdAt ? new Date(bug.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}</td>
                        <td className="px-4 py-3 text-right">
                          <button onClick={() => setSelectedProjectBug(bug)} className="text-blue-500 hover:text-blue-700 text-xs font-medium">View</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
        );
      })()}

      {/* Updates Tab */}
      {activeTab === 'updates' && (
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Project Updates</h2>
          <form onSubmit={handleAddUpdate} className="mb-6 flex gap-3">
            <input value={updateText} onChange={e => setUpdateText(e.target.value)} placeholder="Post a project update..."
              className="flex-1 border border-gray-300 rounded-lg px-4 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
            <button type="submit" disabled={submitting || !updateText.trim()} className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50">Post</button>
          </form>
          {updates.length === 0 ? (
            <div className="bg-white rounded-lg shadow border border-gray-200 p-8 text-center text-gray-400">No updates yet.</div>
          ) : (
            <div className="space-y-3">
              {updates.map(upd => (
                <ProjectUpdateItem key={upd.id} upd={upd} projectId={project.id} currentUser={user?.user_name || user?.name} userRole={user?.role || user?.user_role} onRefresh={onRefresh} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Comments Tab */}
      {activeTab === 'comments' && (
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Comments</h2>
          <div className="mb-6 space-y-2">
            <BugCommentEditor key={commentEditorKey} onChange={setCommentHtml} placeholder="Write a comment… (supports bold, italic, lists)" />
            <div className="flex items-center justify-between">
              <p className="text-xs text-gray-400">Ctrl+B Bold · Ctrl+I Italic · Ctrl+U Underline</p>
              <button onClick={handleAddComment} disabled={submitting || !commentHtml.trim()}
                className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium">
                {submitting ? 'Posting…' : 'Comment'}
              </button>
            </div>
          </div>
          {comments.length === 0 ? (
            <div className="bg-white rounded-lg shadow border border-gray-200 p-8 text-center text-gray-400">No comments yet. Be the first to comment!</div>
          ) : (
            <div className="space-y-3">
              {comments.map(comment => (
                <CommentItem
                  key={comment.id}
                  comment={comment}
                  currentUser={user?.user_name || user?.name}
                  onEdit={(id, content) => handleEditProjectComment(id, content)}
                  onDelete={(id) => requestConfirm('Delete this comment?', () => handleDeleteProjectComment(id))}
                  fmtTime={(ts) => { try { return new Date(ts.endsWith('Z') ? ts : ts + 'Z').toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); } catch { return ''; } }}
                  avatarColor="bg-green-100 text-green-700"
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Milestones Tab — Enhanced with Milestone Engine */}
      {activeTab === 'milestones' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Project Milestones</h2>
            <div className="flex gap-2">
              <button onClick={async () => { const r = await ModulesModel.calculateCriticalPath(project.id); if (r.success) onRefresh(); }}
                className="px-3 py-1.5 text-xs bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 font-medium">Calculate Critical Path</button>
              {onCreateMilestone && <button onClick={onCreateMilestone} className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium">+ Add Milestone</button>}
            </div>
          </div>
          {milestones.length === 0 ? (
            <div className="bg-white rounded-lg shadow border border-gray-200 p-8 text-center">
              <p className="text-gray-400 mb-3">No milestones defined for this project.</p>
              {onCreateMilestone && <button onClick={onCreateMilestone} className="text-blue-600 hover:text-blue-800 text-sm font-medium">Create first milestone</button>}
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow border border-gray-200 p-5">
              <div className="space-y-3">
                {milestones.map((ms, i) => {
                  const icon = getMilestoneIcon(ms.status);
                  const healthVal = ms.health_status || ms.healthStatus || (ms.status === 'completed' ? 'completed' : 'on-track');
                  const hs = getHealthBadgeStyle(healthVal);
                  const riskVal = ms.risk_score ?? ms.riskScore ?? 0;
                  const rf = formatRiskScore(riskVal);
                  const isCritical = ms.is_critical_path || ms.isCriticalPath;
                  const pct = ms.completion_percentage ?? ms.completionPercentage ?? 0;
                  const createdAt = ms.created_at || ms.createdAt;
                  const createdBy = ms.created_by || ms.createdBy;
                  return (
                    <div key={ms.id} className="flex gap-4 pb-3 border-b last:border-b-0 last:pb-0">
                      <div className="flex flex-col items-center">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold shrink-0 ${isCritical ? 'ring-2 ring-purple-400' : ''}`} style={{ background: icon.bg, color: icon.color }}>{icon.symbol}</div>
                        {i < milestones.length - 1 && <div className="w-0.5 flex-1 bg-gray-200 my-1" />}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                              <span className="font-semibold text-gray-900">{ms.title}</span>
                              {isCritical == 1 && <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-purple-100 text-purple-700">CRITICAL</span>}
                              <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${hs.bg} ${hs.text}`}>{hs.label}</span>
                            </div>
                            {ms.description && <p className="text-sm text-gray-600 mb-1">{ms.description}</p>}
                            <div className="flex items-center gap-4 text-xs text-gray-500">
                              <span>{ms.status === 'completed' ? `Completed ${formatDate(ms.completedAt || ms.completed_at)}` : `Due ${formatDate(ms.dueDate || ms.due_date)}`}</span>
                              {ms.status !== 'completed' && riskVal > 0 && <span className={`font-semibold ${rf.color}`}>Risk: {riskVal}</span>}
                              {pct > 0 && ms.status !== 'completed' && <span>{pct}% done</span>}
                              {createdAt && <span>Created {formatDate(createdAt)}{createdBy ? ` by ${createdBy}` : ''}</span>}
                            </div>
                          </div>
                          <div className="flex items-center gap-1 ml-2 shrink-0">
                            {ms.status !== 'completed' && onCompleteMilestone && (
                              <button onClick={() => onCompleteMilestone(ms.milestoneId || ms.milestone_id || ms.id)} className="text-green-600 hover:text-green-800 text-xs font-medium px-2 py-1">Complete</button>
                            )}
                            {onEditMilestone && <button onClick={() => onEditMilestone(ms.milestoneId || ms.milestone_id || ms.id)} className="text-blue-600 hover:text-blue-800 text-xs font-medium px-2 py-1">Edit</button>}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Charter Tab */}
      {activeTab === 'charter' && (
        <ProjectCharterTab projectId={project.id} projectName={project.name} user={user} />
      )}

      {/* Stakeholders Tab */}
      {activeTab === 'stakeholders' && (
        <StakeholderRegisterTab projectId={project.id} user={user} />
      )}

      {/* Risks Tab */}
      {activeTab === 'risks' && (
        <div>
          <div className="flex items-center gap-2 mb-4">
            <AlertCircle size={18} className="text-orange-500" />
            <h2 className="text-lg font-semibold text-gray-900">Risk Register</h2>
          </div>
          <ProjectRisksTab projectId={project.id} user={user} />
        </div>
      )}

      {/* Gantt Tab */}
      {activeTab === 'gantt' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Gantt Chart</h2>
            {onCreateMilestone && (
              <button onClick={onCreateMilestone} className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium">+ Add Milestone</button>
            )}
          </div>
          {milestones.length === 0 ? (
            <div className="bg-white rounded-lg shadow border border-gray-200 p-8 text-center">
              <p className="text-gray-400 mb-3">No milestones to display. Add milestones to see the Gantt chart.</p>
              {onCreateMilestone && <button onClick={onCreateMilestone} className="text-blue-600 hover:text-blue-800 text-sm font-medium">Create first milestone</button>}
            </div>
          ) : (
            <MilestoneGanttChart milestones={milestones} project={project} />
          )}
        </div>
      )}

      {/* Attachments Tab */}
      {activeTab === 'attachments' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Project Artifacts</h2>
          </div>
          <ProjectAttachmentManager projectId={project.id} user={user} />
        </div>
      )}
    </div>
  );
};

// ---- PlanTrack List View ----

const PostUpdateReportModal = ({ onClose }) => {
  const [preparedBy, setPreparedBy] = React.useState('');
  const [role, setRole] = React.useState('');
  const [sending, setSending] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const [error, setError] = React.useState('');

  const handleSend = async () => {
    if (!preparedBy.trim()) { setError('Enter your name'); return; }
    setSending(true); setError('');
    const res = await ModulesModel.postProjectUpdateReport(preparedBy.trim(), role.trim());
    setSending(false);
    if (res?.success) setResult(res);
    else setError(res?.error || 'Failed to send report');
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-900">📋 Post Project Update Report</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
        </div>
        {result ? (
          <div className="text-center py-6">
            <div className="text-4xl mb-2">✅</div>
            <p className="font-medium text-gray-800">Report sent to Pipedream!</p>
            <p className="text-sm text-gray-500 mt-1">{result.projectCount} active project(s) included</p>
            <button onClick={onClose} className="mt-4 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200">Close</button>
          </div>
        ) : (
          <>
            <p className="text-sm text-gray-600 mb-4">Sends a consolidated status report covering all active projects with their latest updates.</p>
            <label className="block text-sm font-medium text-gray-700 mb-1">Prepared By</label>
            <input type="text" value={preparedBy} onChange={e => setPreparedBy(e.target.value)}
              placeholder="e.g. Gian Halili"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-blue-500" />
            <label className="block text-sm font-medium text-gray-700 mb-1">Role <span className="text-gray-400 font-normal">(optional)</span></label>
            <input type="text" value={role} onChange={e => setRole(e.target.value)}
              placeholder="e.g. IT Project Manager"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            {error && <p className="text-red-500 text-sm mt-2 bg-red-50 p-2 rounded-lg">{error}</p>}
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900">Cancel</button>
              <button onClick={handleSend} disabled={sending}
                className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50">
                {sending ? 'Sending…' : 'Send Report'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const PlanTrackView = ({ data, onSelectProject, onShowAddProject, onShowSprints, onShowMilestones, onShowPostUpdateReport, onToggleArchive }) => {
  const [projectTab, setProjectTab] = useState('active');
  const allProjects = data.projects || [];
  const activeProjects = allProjects.filter(p => !p.archived);
  const archivedProjects = allProjects.filter(p => p.archived);
  const visibleProjects = projectTab === 'archive' ? archivedProjects : activeProjects;

  const handleArchiveClick = async (e, project) => {
    e.stopPropagation();
    const archiving = !project.archived;
    if (!confirm(archiving ? `Move "${project.name}" to Archive?` : `Restore "${project.name}" to active?`)) return;
    if (onToggleArchive) await onToggleArchive(project.id, archiving);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">📋 PlanTrack</h1>
          <p className="text-gray-600 mt-1">Project planning, sprint management, and milestone tracking.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={onShowPostUpdateReport} className="px-4 py-2 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200 font-medium border border-gray-300">📋 Post Update</button>
          <button onClick={onShowAddProject} className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 font-medium">+ New Project</button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Total Projects', value: data.stats.total, color: '#667eea' },
          { label: 'Active', value: data.stats.active, color: '#0366d6' },
          { label: 'Completed', value: data.stats.completed, color: '#28a745' },
          { label: 'On Hold', value: data.stats.onHold, color: '#e36209' }
        ].map((stat, i) => (
          <div key={i} className="bg-white rounded-lg shadow p-5">
            <div className="text-sm text-gray-600 mb-1">{stat.label}</div>
            <div className="text-3xl font-bold" style={{ color: stat.color }}>{stat.value}</div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mb-6">
        {/* Projects */}
        <div className="lg:col-span-2">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <h2 className="text-xl font-semibold text-gray-900">Projects</h2>
            <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden text-sm">
              <button onClick={() => setProjectTab('active')}
                className={`px-3 py-1.5 font-medium ${projectTab === 'active' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}>
                Active <span className={`ml-1 text-xs ${projectTab === 'active' ? 'text-blue-100' : 'text-gray-400'}`}>({activeProjects.length})</span>
              </button>
              <button onClick={() => setProjectTab('archive')}
                className={`px-3 py-1.5 font-medium border-l border-gray-300 ${projectTab === 'archive' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}>
                Archive <span className={`ml-1 text-xs ${projectTab === 'archive' ? 'text-blue-100' : 'text-gray-400'}`}>({archivedProjects.length})</span>
              </button>
            </div>
          </div>
          <div className="space-y-4">
            {visibleProjects.length === 0 && (
              <div className="bg-white rounded-lg border border-dashed border-gray-300 p-8 text-center text-sm text-gray-500">
                {projectTab === 'archive' ? 'No archived projects.' : 'No active projects.'}
              </div>
            )}
            {visibleProjects.map(project => {
              const ss = getStatusStyle(project.status);
              const ps = getPriorityStyle(project.priority);
              const bp = project.budgetHours > 0 ? Math.round((project.spentHours / project.budgetHours) * 100) : 0;
              return (
                <div key={project.id} onClick={() => onSelectProject(project.id)} className={`bg-white rounded-lg shadow border p-5 hover:shadow-lg hover:border-blue-400 transition-all cursor-pointer ${project.archived ? 'border-gray-200 opacity-80' : 'border-gray-200'}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="text-xs font-semibold text-gray-500">{project.id}</span>
                      <span className="px-2 py-0.5 rounded text-xs font-semibold" style={{ background: ps.bg, color: ps.color }}>{project.priority}</span>
                      <span className="px-2 py-0.5 rounded text-xs font-semibold" style={{ background: ss.bg, color: ss.color }}>{ss.label}</span>
                      {project.sprintName && <span className="px-2 py-0.5 rounded text-xs font-semibold bg-blue-100 text-blue-700">{project.sprintName}</span>}
                      {project.archived ? <span className="px-2 py-0.5 rounded text-xs font-semibold bg-gray-200 text-gray-600">Archived</span> : null}
                    </div>
                    {onToggleArchive && (
                      <button onClick={(e) => handleArchiveClick(e, project)}
                        className="text-xs text-gray-500 hover:text-gray-800 px-2 py-1 rounded border border-gray-200 hover:bg-gray-50 shrink-0"
                        title={project.archived ? 'Restore from archive' : 'Move to archive'}>
                        {project.archived ? 'Unarchive' : 'Archive'}
                      </button>
                    )}
                  </div>
                  <h3 className="font-semibold text-gray-900 text-lg">{project.name}</h3>
                  {project.description && <div className="text-sm text-gray-600 mt-1 line-clamp-3 prose prose-sm max-w-none [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:m-0" dangerouslySetInnerHTML={{ __html: sanitizeBugHtml(project.description) }} />}
                  <div className="flex items-center gap-4 text-xs text-gray-500 mt-3 mb-3">
                    <span>👤 {project.owner}</span>
                    <span>📅 {project.startDate} → {project.targetEndDate}</span>
                  </div>
                  <div className="mb-2">
                    <div className="flex justify-between text-xs text-gray-600 mb-1"><span>Progress</span><span>{project.progress}%</span></div>
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div className="h-full rounded-full transition-all" style={{ width: `${project.progress}%`, background: project.progress === 100 ? '#28a745' : '#0366d6' }} />
                    </div>
                  </div>
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>Budget: {project.spentHours}h / {project.budgetHours}h ({bp}%)</span>
                    <span className={bp > 90 ? 'text-red-600 font-semibold' : ''}>{project.budgetHours - project.spentHours}h remaining</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Right sidebar */}
        <div className="space-y-6">
          {/* Active Sprint */}
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-900">Active Sprint</h2>
              <button onClick={onShowSprints} className="text-sm text-blue-600 hover:text-blue-800 font-medium">Manage Sprints</button>
            </div>
            {data.sprint ? (() => {
              const progress = data.sprint.capacityHours > 0 ? Math.round((data.sprint.completedHours / data.sprint.capacityHours) * 100) : 0;
              return (
                <div className="bg-white rounded-lg shadow border border-blue-400 ring-1 ring-blue-100 p-5">
                  <div className="font-semibold text-gray-900 text-lg mb-1">{data.sprint.name}</div>
                  {data.sprint.goal && <p className="text-sm text-gray-600 mb-3">{data.sprint.goal}</p>}
                  <div className="text-xs text-gray-500 mb-3">{data.sprint.startDate} → {data.sprint.endDate}</div>
                  <div className="space-y-3">
                    <div>
                      <div className="flex justify-between text-xs text-gray-600 mb-1"><span>Completed</span><span>{data.sprint.completedHours}h / {data.sprint.capacityHours}h ({progress}%)</span></div>
                      <div className="h-2 bg-gray-200 rounded-full overflow-hidden"><div className="h-full bg-green-500 rounded-full" style={{ width: `${progress}%` }} /></div>
                    </div>
                    <div>
                      <div className="flex justify-between text-xs text-gray-600 mb-1"><span>Committed</span><span>{data.sprint.committedHours}h / {data.sprint.capacityHours}h</span></div>
                      <div className="h-2 bg-gray-200 rounded-full overflow-hidden"><div className="h-full bg-yellow-500 rounded-full" style={{ width: `${Math.min(Math.round((data.sprint.committedHours / data.sprint.capacityHours) * 100), 100)}%` }} /></div>
                    </div>
                  </div>
                </div>
              );
            })() : (
              <div className="bg-white rounded-lg shadow border border-gray-200 p-6 text-center">
                <p className="text-gray-500 text-sm mb-3">No active sprint</p>
                <button onClick={onShowSprints} className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">Manage Sprints</button>
              </div>
            )}
          </div>

          {/* Milestones — a short, prioritized preview (soonest-due first, a couple of
              recently-completed ones for context), not every milestone on record. The
              full sortable/filterable list lives on the Manage Milestones page. */}
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-900">Milestones</h2>
              <button onClick={onShowMilestones} className="text-sm text-blue-600 hover:text-blue-800 font-medium">Manage Milestones</button>
            </div>
            <div className="orbit-card overflow-hidden">
              {(() => {
                const all = data.milestones || [];
                const active = all.filter(m => m.status !== 'completed').sort((a, b) => new Date(a.dueDate || 0) - new Date(b.dueDate || 0));
                const completed = all.filter(m => m.status === 'completed').sort((a, b) => new Date(b.completedAt || 0) - new Date(a.completedAt || 0));
                const ordered = [...active, ...completed];
                const visible = ordered.slice(0, PLANTRACK_MILESTONE_SIDEBAR_LIMIT);
                const hiddenCount = ordered.length - visible.length;
                if (visible.length === 0) {
                  return <div className="p-5 text-center text-sm text-gray-400">No milestones yet.</div>;
                }
                return (
                  <>
                    <div className="divide-y divide-gray-100">
                      {visible.map(ms => {
                        const isDone = ms.status === 'completed';
                        const hs = getHealthBadgeStyle(ms.health_status || (isDone ? 'completed' : 'on-track'));
                        return (
                          <div key={ms.id} className="flex items-start gap-2.5 px-4 py-2.5">
                            <span className="w-2 h-2 rounded-full shrink-0 mt-1.5" style={{ background: MILESTONE_HEALTH_DOT[ms.health_status] || (isDone ? MILESTONE_HEALTH_DOT.completed : MILESTONE_HEALTH_DOT['on-track']) }} title={hs.label} />
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <span className="text-sm font-semibold text-gray-900 truncate">{ms.title}</span>
                                {ms.is_critical_path === 1 && <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-purple-100 text-purple-700 shrink-0">CP</span>}
                              </div>
                              <div className="text-xs text-gray-400 truncate mt-0.5">
                                <span className="text-gray-500 font-medium">{ms.projectName}</span>
                                {' · '}{isDone ? (ms.completedAt ? `Completed ${ms.completedAt}` : 'Completed') : `Due ${ms.dueDate}`}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    {hiddenCount > 0 && (
                      <button onClick={onShowMilestones} className="w-full text-center text-xs font-medium text-blue-600 hover:text-blue-800 hover:bg-gray-50 px-4 py-2.5 border-t border-gray-100 transition-colors">
                        +{hiddenCount} more milestone{hiddenCount === 1 ? '' : 's'} · See all
                      </button>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ---- Sprints Management View ----

const SprintsManagementView = ({ sprints, onBack, onCreateSprint, onUpdateSprint, onDeleteSprint, user }) => {
  const [showCreate, setShowCreate] = useState(false);
  const [editingSprint, setEditingSprint] = useState(null); // sprint object being edited
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const role = user?.role || user?.user_role;
  const canManage = role === 'admin' || role === 'project';

  const handleDelete = async (sprint) => {
    if (!onDeleteSprint) return;
    const msg = `Delete sprint "${sprint.name}"?\n\nAny tasks linked to this sprint will be unassigned (not deleted). This cannot be undone.`;
    if (!confirm(msg)) return;
    setDeletingId(sprint.id);
    const res = await onDeleteSprint(sprint.id);
    setDeletingId(null);
    if (res?.success && res.unlinkedTasks > 0) {
      alert(`Sprint deleted. ${res.unlinkedTasks} task${res.unlinkedTasks === 1 ? ' was' : 's were'} unassigned.`);
    }
  };

  const handleCreate = async (formData) => {
    await onCreateSprint(formData);
    setShowCreate(false);
  };

  const handleStatusChange = async (sprintId, newStatus) => {
    await onUpdateSprint(sprintId, { status: newStatus });
  };

  const openEdit = (sprint) => {
    setEditForm({
      name: sprint.name || '',
      goal: sprint.goal || '',
      startDate: sprint.startDate || '',
      endDate: sprint.endDate || '',
      status: sprint.status || 'planned',
      capacityHours: sprint.capacityHours || 0,
    });
    setEditingSprint(sprint);
  };

  const handleSaveEdit = async () => {
    if (!editingSprint) return;
    setSaving(true);
    await onUpdateSprint(editingSprint.id, editForm);
    setSaving(false);
    setEditingSprint(null);
  };

  const inp = 'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500';

  return (
    <div className="max-w-5xl mx-auto px-4 py-6">
      <button onClick={onBack} className="flex items-center gap-2 text-blue-600 hover:text-blue-800 mb-4 text-sm font-medium">
        <ChevronRight size={16} className="rotate-180" /> Back to PlanTrack
      </button>

      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Sprint Management</h1>
          <p className="text-gray-600 mt-1">Create, manage, and track all sprints.</p>
        </div>
        {canManage && (
          <button onClick={() => setShowCreate(true)} className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 font-medium">+ New Sprint</button>
        )}
      </div>

      {!canManage && (
        <div className="mb-4 px-4 py-2 bg-yellow-50 border border-yellow-200 rounded-lg text-xs text-yellow-700">
          View only — only Admin and Project roles can create or edit sprints.
        </div>
      )}

      {(sprints || []).length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <p className="text-gray-500 mb-4">No sprints created yet.</p>
          {canManage && <button onClick={() => setShowCreate(true)} className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">+ Create First Sprint</button>}
        </div>
      ) : (
        <div className="space-y-4">
          {(sprints || []).map(sprint => {
            const progress = sprint.capacityHours > 0 ? Math.round((sprint.completedHours / sprint.capacityHours) * 100) : 0;
            const isActive = sprint.status === 'active';
            return (
              <div key={sprint.id} className={`bg-white rounded-lg shadow border p-5 ${isActive ? 'border-blue-400 ring-1 ring-blue-100' : 'border-gray-200'}`}>
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">{sprint.name}</h3>
                      <span className="text-xs font-semibold text-gray-500">#{sprint.number}</span>
                      <span className={`px-2 py-0.5 rounded text-xs font-semibold ${isActive ? 'bg-green-100 text-green-700' : sprint.status === 'completed' ? 'bg-gray-100 text-gray-600' : 'bg-yellow-100 text-yellow-700'}`}>
                        {sprint.status}
                      </span>
                    </div>
                    {sprint.goal && <p className="text-sm text-gray-600 mb-2">{sprint.goal}</p>}
                    <div className="text-sm text-gray-500 mb-3">{sprint.startDate} → {sprint.endDate}</div>
                    <div className="max-w-md">
                      <div className="flex justify-between text-xs text-gray-500 mb-1"><span>Progress</span><span>{sprint.completedHours}h / {sprint.capacityHours}h ({progress}%)</span></div>
                      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${progress}%`, background: isActive ? '#22c55e' : sprint.status === 'completed' ? '#9ca3af' : '#eab308' }} />
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    {canManage ? (
                      <>
                        <select value={sprint.status} onChange={(e) => handleStatusChange(sprint.id, e.target.value)}
                          className="text-sm border border-gray-300 rounded-lg px-2 py-1.5 focus:ring-2 focus:ring-blue-500">
                          <option value="planned">Planned</option>
                          <option value="active">Active</option>
                          <option value="completed">Completed</option>
                        </select>
                        <button onClick={() => openEdit(sprint)}
                          className="px-3 py-1.5 text-xs border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-600">
                          ✏️ Edit
                        </button>
                        {onDeleteSprint && (
                          <button onClick={() => handleDelete(sprint)}
                            disabled={deletingId === sprint.id}
                            className="px-3 py-1.5 text-xs border border-red-300 text-red-600 rounded-lg hover:bg-red-50 disabled:opacity-50">
                            {deletingId === sprint.id ? 'Deleting…' : '🗑 Delete'}
                          </button>
                        )}
                      </>
                    ) : (
                      <span className={`px-2 py-0.5 rounded text-xs font-semibold ${isActive ? 'bg-green-100 text-green-700' : sprint.status === 'completed' ? 'bg-gray-100 text-gray-600' : 'bg-yellow-100 text-yellow-700'}`}>
                        {sprint.status}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showCreate && <AddSprintModal onClose={() => setShowCreate(false)} onSubmit={handleCreate} />}

      {editingSprint && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h2 className="text-lg font-bold text-gray-900">Edit Sprint</h2>
              <button onClick={() => setEditingSprint(null)} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">×</button>
            </div>
            <div className="px-6 py-4 space-y-3">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Sprint Name</label>
                <input value={editForm.name} onChange={e => setEditForm(f => ({...f, name: e.target.value}))} className={inp} />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Goal</label>
                <textarea value={editForm.goal} onChange={e => setEditForm(f => ({...f, goal: e.target.value}))} rows={2} className={inp + ' resize-none'} placeholder="Sprint goal…" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Start Date</label>
                  <input type="date" value={editForm.startDate} onChange={e => setEditForm(f => ({...f, startDate: e.target.value}))} className={inp} />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">End Date</label>
                  <input type="date" value={editForm.endDate} onChange={e => setEditForm(f => ({...f, endDate: e.target.value}))} className={inp} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Status</label>
                  <select value={editForm.status} onChange={e => setEditForm(f => ({...f, status: e.target.value}))} className={inp}>
                    <option value="planned">Planned</option>
                    <option value="active">Active</option>
                    <option value="completed">Completed</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Capacity (hours)</label>
                  <input type="number" value={editForm.capacityHours} onChange={e => setEditForm(f => ({...f, capacityHours: parseInt(e.target.value)||0}))} className={inp} min={0} />
                </div>
              </div>
            </div>
            <div className="flex gap-3 px-6 py-4 border-t">
              <button onClick={() => setEditingSprint(null)} className="flex-1 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
              <button onClick={handleSaveEdit} disabled={saving} className="flex-1 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-60">{saving ? 'Saving…' : 'Save Changes'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// TASK MANAGEMENT VIEW (Manager/Admin cross-module view)
// ============================================================================

const SOURCE_META = {
  sprint:     { label: 'Sprint Board', bg: '#dbeafe', color: '#1e40af' },
  quicktrack: { label: 'QuickTrack',   bg: '#ede9fe', color: '#5b21b6' },
};

const SPRINT_STATUSES  = ['backlog', 'ready', 'in_progress', 'review', 'needs_rework', 'completed', 'blocked', 'cancelled'];
const QUICK_STATUSES   = ['open', 'in_progress', 'done', 'blocked'];
const ALL_STATUSES     = [...new Set([...SPRINT_STATUSES, ...QUICK_STATUSES])];
const PRIORITIES       = ['P1', 'P2', 'P3', 'P4', 'high', 'medium', 'low'];

const MultiSelect = ({ options, values = [], onChange, placeholder, labelKey = 'label', valueKey = 'value' }) => {
  const [open, setOpen] = useState(false);
  const ref = React.useRef();

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggle = (val) => {
    const next = values.includes(val) ? values.filter(v => v !== val) : [...values, val];
    onChange(next);
  };

  const label = values.length === 0
    ? placeholder
    : values.length === 1
      ? (options.find(o => (o[valueKey] ?? o) === values[0])?.[labelKey] ?? values[0])
      : `${values.length} selected`;

  return (
    <div className="relative" ref={ref}>
      <button type="button" onClick={() => setOpen(o => !o)}
        className={`flex items-center gap-1.5 border rounded-lg px-3 py-1.5 text-sm bg-white focus:outline-none min-w-[130px] justify-between ${values.length > 0 ? 'border-blue-400 text-blue-700 font-medium' : 'border-gray-300 text-gray-600'}`}>
        <span className="truncate">{label}</span>
        <ChevronDown size={14} className={`flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute z-30 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg min-w-full max-h-56 overflow-y-auto">
          {options.map(opt => {
            const val = opt[valueKey] ?? opt;
            const lbl = opt[labelKey] ?? opt;
            const checked = values.includes(val);
            return (
              <label key={val} className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer text-sm">
                <input type="checkbox" checked={checked} onChange={() => toggle(val)} className="rounded accent-blue-600" />
                <span className={`capitalize ${checked ? 'text-gray-900 font-medium' : 'text-gray-700'}`}>{String(lbl).replace(/_/g, ' ')}</span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
};

const TaskManagementView = ({ data, filters, onFilterChange, onBulkUpdate, onRefresh }) => {
  const [selected, setSelected] = useState(new Set());
  const [bulkAssignee, setBulkAssignee] = useState('');
  const [bulkStatus, setBulkStatus]   = useState('');
  const [applying, setApplying]       = useState(false);

  const tasks     = data?.tasks     || [];
  const summary   = data?.summary   || {};
  const assignees = data?.assignees || [];
  const projects  = data?.projects  || [];

  const taskKey = (t) => `${t.source}:${t.id}`;

  const toggleOne = (t) => setSelected(prev => {
    const s = new Set(prev);
    s.has(taskKey(t)) ? s.delete(taskKey(t)) : s.add(taskKey(t));
    return s;
  });
  const toggleAll = () => setSelected(
    selected.size === tasks.length ? new Set() : new Set(tasks.map(taskKey))
  );

  const handleApplyBulk = async () => {
    if (!bulkAssignee && !bulkStatus) return;
    const selectedTasks = [...selected].map(k => {
      const idx = k.indexOf(':');
      return { source: k.slice(0, idx), id: k.slice(idx + 1) };
    });
    const updates = {};
    if (bulkAssignee) updates.assignee = bulkAssignee === '__unassigned__' ? '' : bulkAssignee;
    if (bulkStatus)   updates.status   = bulkStatus;
    setApplying(true);
    await onBulkUpdate(selectedTasks, updates);
    setSelected(new Set()); setBulkAssignee(''); setBulkStatus('');
    setApplying(false);
  };

  const isOverdue = (t) => t.due_date && t.due_date < new Date().toISOString().slice(0, 10)
    && !['completed', 'done', 'cancelled'].includes(t.status);

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Task Management</h1>
        <p className="text-gray-600 mt-1">Unified view across Sprint Board and QuickTrack — for managers and admins.</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4 mb-6">
        {[
          { label: 'Total Tasks',   value: summary.total       || 0, color: 'blue'   },
          { label: 'Active',        value: summary.active      || 0, color: 'green',  sub: 'In progress' },
          { label: 'Overdue',       value: summary.overdue     || 0, color: 'red',    sub: 'Past due date' },
          { label: 'Unassigned',    value: summary.unassigned  || 0, color: 'orange', sub: 'No assignee' },
          { label: 'Sprint Board',  value: summary.sprintTasks || 0, color: 'blue',   sub: 'DevOps / PlanTrack' },
          { label: 'QuickTrack',    value: summary.quickTasks  || 0, color: 'purple', sub: 'Ad-hoc tasks' },
        ].map(c => <ReportCard key={c.label} {...c} />)}
      </div>

      {/* Filter Bar */}
      <div className="bg-white rounded-lg shadow border border-gray-200 p-4 mb-4">
        <div className="flex flex-wrap gap-3 items-end">
          {/* Source toggle */}
          <div>
            <div className="text-xs font-medium text-gray-500 mb-1">Source</div>
            <div className="flex rounded-lg border border-gray-300 overflow-hidden text-sm flex-wrap">
              {[['', 'All'], ['sprint', 'Sprint'], ['quicktrack', 'QuickTrack']].map(([v, l]) => (
                <button key={v} onClick={() => onFilterChange({ source: v })}
                  className={`px-3 py-1.5 font-medium ${filters.source === v ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
                  {l}
                </button>
              ))}
            </div>
          </div>
          {/* Search */}
          <div className="flex-1 min-w-[160px]">
            <div className="text-xs font-medium text-gray-500 mb-1">Search</div>
            <input value={filters.search || ''} onChange={e => onFilterChange({ search: e.target.value })}
              placeholder="Search tasks…"
              className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none" />
          </div>
          {/* Status */}
          <div>
            <div className="text-xs font-medium text-gray-500 mb-1">Status</div>
            <MultiSelect
              options={ALL_STATUSES}
              values={filters.status || []}
              onChange={v => onFilterChange({ status: v })}
              placeholder="All Statuses"
            />
          </div>
          {/* Priority */}
          <div>
            <div className="text-xs font-medium text-gray-500 mb-1">Priority</div>
            <MultiSelect
              options={PRIORITIES}
              values={filters.priority || []}
              onChange={v => onFilterChange({ priority: v })}
              placeholder="All Priorities"
            />
          </div>
          {/* Assignee */}
          <div>
            <div className="text-xs font-medium text-gray-500 mb-1">Assignee</div>
            <MultiSelect
              options={assignees}
              values={filters.assignee || []}
              onChange={v => onFilterChange({ assignee: v })}
              placeholder="All Assignees"
            />
          </div>
          {/* Project */}
          <div>
            <div className="text-xs font-medium text-gray-500 mb-1">Project</div>
            <MultiSelect
              options={projects.map(p => ({ label: p.name, value: p.project_id }))}
              values={filters.project || []}
              onChange={v => onFilterChange({ project: v })}
              placeholder="All Projects"
              labelKey="label"
              valueKey="value"
            />
          </div>
          {/* Clear */}
          <button onClick={() => onFilterChange({ source: '', status: [], priority: [], assignee: [], project: [], search: '' })}
            className="px-3 py-1.5 text-sm text-gray-500 hover:text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50">
            Clear
          </button>
          <button onClick={onRefresh} className="px-3 py-1.5 text-sm text-gray-500 hover:text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50">
            ↻ Refresh
          </button>
        </div>
      </div>

      {/* Bulk Action Bar */}
      {selected.size > 0 && (
        <div className="flex items-center gap-3 bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 mb-4">
          <span className="text-sm font-semibold text-blue-800">{selected.size} selected</span>
          <div className="flex-1" />
          <div className="flex items-center gap-2 flex-wrap">
            <select value={bulkAssignee} onChange={e => setBulkAssignee(e.target.value)}
              className="border border-blue-300 rounded px-2 py-1 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="">Reassign to…</option>
              <option value="__unassigned__">— Unassigned —</option>
              {assignees.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
            <select value={bulkStatus} onChange={e => setBulkStatus(e.target.value)}
              className="border border-blue-300 rounded px-2 py-1 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="">Change status…</option>
              {ALL_STATUSES.map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
            </select>
            <button onClick={handleApplyBulk} disabled={applying || (!bulkAssignee && !bulkStatus)}
              className="px-4 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 font-medium disabled:opacity-50">
              {applying ? 'Applying…' : 'Apply'}
            </button>
            <button onClick={() => setSelected(new Set())}
              className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg bg-white">
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Task Table */}
      <div className="bg-white rounded-lg shadow border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-3 w-10">
                <input type="checkbox" checked={tasks.length > 0 && selected.size === tasks.length}
                  onChange={toggleAll} className="rounded" />
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Source</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">ID</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 min-w-[240px]">Title</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Type</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Status</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Priority</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Assignee</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Project</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Due Date</th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Est. Hours</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {tasks.map(t => {
              const key  = taskKey(t);
              const src  = SOURCE_META[t.source] || SOURCE_META.sprint;
              const over = isOverdue(t);
              return (
                <tr key={key} className={`hover:bg-gray-50 ${selected.has(key) ? 'bg-blue-50' : ''}`}>
                  <td className="px-4 py-3">
                    <input type="checkbox" checked={selected.has(key)} onChange={() => toggleOne(t)} className="rounded" />
                  </td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-0.5 rounded text-xs font-semibold whitespace-nowrap"
                      style={{ background: src.bg, color: src.color }}>{src.label}</span>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-gray-500 whitespace-nowrap">{t.id}</td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900 max-w-xs truncate">{t.title}</div>
                    {t.sprint_name && <div className="text-xs text-gray-400">{t.sprint_name}</div>}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-600 capitalize">{t.type?.replace('_', ' ')}</td>
                  <td className="px-4 py-3">
                    <StatusBadge value={t.status} map={statusColors} />
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge value={t.priority} map={statusColors} />
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700">
                    {t.assignee_name
                      ? <div className="flex items-center gap-1.5">
                          <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-bold flex-shrink-0">{t.assignee_name.charAt(0)}</div>
                          <span className="truncate max-w-[100px]">{t.assignee_name}</span>
                        </div>
                      : <span className="text-orange-500 text-xs font-medium">Unassigned</span>}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-600 truncate max-w-[120px]">
                    {t.project_name || t.project_id || '—'}
                  </td>
                  <td className="px-4 py-3 text-xs whitespace-nowrap">
                    {t.due_date
                      ? <span className={over ? 'text-red-600 font-semibold' : 'text-gray-600'}>
                          {over ? '⚠ ' : ''}{t.due_date}
                        </span>
                      : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-4 py-3 text-right text-sm text-gray-600">
                    {t.estimate_hours > 0 ? `${t.estimate_hours}h` : '—'}
                  </td>
                </tr>
              );
            })}
            {tasks.length === 0 && (
              <tr><td colSpan={11} className="px-4 py-12 text-center text-gray-400">
                No tasks match the current filters.
              </td></tr>
            )}
          </tbody>
        </table>
        {tasks.length > 0 && (
          <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 text-xs text-gray-500">
            Showing {tasks.length} task{tasks.length !== 1 ? 's' : ''}
            {selected.size > 0 && ` · ${selected.size} selected`}
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// REPORTS VIEW
// ============================================================================

const downloadCSV = (filename, headers, rows) => {
  const escape = v => {
    const s = v == null ? '' : String(v);
    return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = [headers.map(escape).join(','), ...rows.map(r => r.map(escape).join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
};

const ReportCard = ({ label, value, sub, color = 'blue' }) => {
  const colors = {
    blue: 'bg-blue-50 border-blue-200 text-blue-700',
    green: 'bg-green-50 border-green-200 text-green-700',
    purple: 'bg-purple-50 border-purple-200 text-purple-700',
    orange: 'bg-orange-50 border-orange-200 text-orange-700',
    red: 'bg-red-50 border-red-200 text-red-700',
    pink: 'bg-pink-50 border-pink-200 text-pink-700',
    gray: 'bg-gray-50 border-gray-200 text-gray-700',
  };
  const cls = colors[color] || colors.blue;
  return (
    <div className={`rounded-lg border p-5 ${cls.split(' ').slice(0,2).join(' ')}`}>
      <div className="text-sm text-gray-600 mb-1">{label}</div>
      <div className={`text-3xl font-bold ${cls.split(' ')[2]}`}>{value}</div>
      {sub && <div className="text-xs text-gray-500 mt-1">{sub}</div>}
    </div>
  );
};

const StatusBadge = ({ value, map }) => {
  const cfg = map[value] || { bg: '#e5e7eb', color: '#374151' };
  return <span className="px-2 py-0.5 rounded text-xs font-semibold capitalize" style={{ background: cfg.bg, color: cfg.color }}>{value}</span>;
};

const statusColors = {
  active: { bg: '#dcffe4', color: '#166534' }, completed: { bg: '#dbeafe', color: '#1e40af' },
  'on-hold': { bg: '#fef9c3', color: '#854d0e' }, cancelled: { bg: '#fee2e2', color: '#991b1b' },
  planning: { bg: '#ede9fe', color: '#5b21b6' }, backlog: { bg: '#f3f4f6', color: '#374151' },
  in_progress: { bg: '#dbeafe', color: '#1e40af' }, review: { bg: '#fef9c3', color: '#854d0e' },
  blocked: { bg: '#fee2e2', color: '#991b1b' }, open: { bg: '#fee2e2', color: '#991b1b' },
  approved: { bg: '#dcffe4', color: '#166534' }, pending: { bg: '#fef9c3', color: '#854d0e' },
  rejected: { bg: '#fee2e2', color: '#991b1b' }, draft: { bg: '#f3f4f6', color: '#374151' },
  submitted: { bg: '#ede9fe', color: '#5b21b6' }, 'in-progress': { bg: '#dbeafe', color: '#1e40af' },
  pass: { bg: '#dcffe4', color: '#166534' }, fail: { bg: '#fee2e2', color: '#991b1b' },
  resolved: { bg: '#dcffe4', color: '#166534' }, closed: { bg: '#f3f4f6', color: '#374151' },
  critical: { bg: '#fee2e2', color: '#991b1b' }, high: { bg: '#fed7aa', color: '#9a3412' },
  medium: { bg: '#fef9c3', color: '#854d0e' }, low: { bg: '#dcffe4', color: '#166534' },
  P1: { bg: '#fee2e2', color: '#991b1b' }, P2: { bg: '#fed7aa', color: '#9a3412' },
  P3: { bg: '#fef9c3', color: '#854d0e' }, P4: { bg: '#dcffe4', color: '#166534' },
};

const ExportBtn = ({ onClick }) => (
  <button onClick={onClick} className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50 font-medium">
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
    Export CSV
  </button>
);

const ReportsView = ({ data }) => {
  const [tab, setTab] = useState('overview');
  const d = data || {};
  const s = d.summary || {};

  const tabs = [
    { id: 'overview',   label: 'Overview' },
    { id: 'projects',   label: 'Projects' },
    { id: 'sprints',    label: 'Sprints & Velocity' },
    { id: 'tasks',      label: 'Tasks' },
    { id: 'time',       label: 'Time & Leave' },
    { id: 'quality',    label: 'Quality' },
  ];

  // ---- CSV exports ----
  const exportProjects = () => {
    downloadCSV('projects_report.csv',
      ['Project ID','Name','Status','Priority','Health','Progress %','Budget Hours','Spent Hours','Tasks','Completed Tasks','Estimated Hours','Milestones','Start Date','End Date'],
      (d.projects || []).map(p => [p.project_id, p.name, p.status, p.priority, p.health_status, p.progress, p.budget_hours, p.spent_hours, p.task_count, p.completed_tasks, p.estimated_hours, p.milestone_count, p.start_date, p.end_date])
    );
  };
  const exportSprints = () => {
    downloadCSV('sprints_report.csv',
      ['Sprint','Name','Status','Start Date','End Date','Points Planned','Points Completed','Velocity','Capacity Hours','Committed Hours','Completed Hours'],
      (d.sprints || []).map(sp => [sp.sprint_number, sp.name, sp.status, sp.start_date, sp.end_date, sp.story_points_planned, sp.story_points_completed, sp.velocity, sp.capacity_hours, sp.committed_hours, sp.completed_hours])
    );
  };
  const exportTasks = () => {
    downloadCSV('tasks_by_assignee.csv',
      ['Assignee','Total Tasks','Completed','Estimated Hours','Actual Hours'],
      (d.tasks?.byAssignee || []).map(t => [t.assignee, t.total, t.completed, t.estimated_hours, t.actual_hours])
    );
  };
  const exportTime = () => {
    downloadCSV('timesheet_report.csv',
      ['User','Total Hours','Billable Hours','Leave Hours','Weeks Logged'],
      (d.timesheets?.byUser || []).map(t => [t.user_name, t.total_hours, t.billable_hours, t.leave_hours, t.weeks_logged])
    );
  };
  const exportLeave = () => {
    downloadCSV('leave_report.csv',
      ['User','Approved Days','Pending Days','Rejected Days'],
      (d.leaves?.byUser || []).map(l => [l.user_name, l.approved_days, l.pending_days, l.rejected_days])
    );
  };
  const exportQuality = () => {
    downloadCSV('bugs_report.csv',
      ['Bug ID','Title','Severity','Status','Environment','Assignee','Created'],
      (d.quality?.recentBugs || []).map(b => [b.bug_id, b.title, b.severity, b.status, b.environment, b.assignee, b.created_at])
    );
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Reports & Analytics</h1>
        <p className="text-gray-600 mt-1">Generate, view, and export comprehensive system reports.</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-lg mb-6 overflow-x-auto">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-all ${tab === t.id ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-600 hover:text-gray-800'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── OVERVIEW ── */}
      {tab === 'overview' && (
        <div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <ReportCard label="Total Projects"    value={s.totalProjects}    color="blue"   sub="All projects" />
            <ReportCard label="Active Sprints"    value={s.activeSprints}    color="green"  sub="Currently running" />
            <ReportCard label="Open Bugs"         value={s.openBugs}         color="red"    sub="Requiring attention" />
            <ReportCard label="Pending Leave"     value={s.pendingLeave}     color="orange" sub="Awaiting approval" />
            <ReportCard label="Hours This Month"  value={`${s.hoursThisMonth}h`} color="purple" sub="Last 30 days" />
            <ReportCard label="Active Users"      value={s.activeUsers}      color="gray"   sub="In the system" />
            <ReportCard label="Open Quick Tasks"  value={s.openQuickTasks}   color="pink"   sub="Unresolved" />
          </div>

          {/* Task status + Quick tasks side by side */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="bg-white rounded-lg shadow border border-gray-200 p-5">
              <h3 className="text-base font-semibold text-gray-900 mb-4">Tasks by Status</h3>
              <div className="space-y-2">
                {(d.tasks?.byStatus || []).map(row => {
                  const total = (d.tasks?.byStatus || []).reduce((s, r) => s + r.count, 0) || 1;
                  const pct = Math.round((row.count / total) * 100);
                  return (
                    <div key={row.status} className="flex items-center gap-3">
                      <div className="w-24 text-xs text-right capitalize text-gray-600">{row.status?.replace('_', ' ')}</div>
                      <div className="flex-1 h-5 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: statusColors[row.status]?.color || '#6b7280' }} />
                      </div>
                      <div className="w-12 text-xs text-gray-700 font-semibold">{row.count} <span className="text-gray-400">({pct}%)</span></div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="bg-white rounded-lg shadow border border-gray-200 p-5">
              <h3 className="text-base font-semibold text-gray-900 mb-4">Bugs by Severity</h3>
              <div className="space-y-2">
                {(d.quality?.bugsBySeverity || []).map(row => {
                  const total = (d.quality?.bugsBySeverity || []).reduce((s, r) => s + r.count, 0) || 1;
                  const pct = Math.round((row.count / total) * 100);
                  return (
                    <div key={row.severity} className="flex items-center gap-3">
                      <div className="w-16 text-xs text-right capitalize text-gray-600">{row.severity}</div>
                      <div className="flex-1 h-5 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: statusColors[row.severity]?.color || '#6b7280' }} />
                      </div>
                      <div className="w-12 text-xs text-gray-700 font-semibold">{row.count} <span className="text-gray-400">({pct}%)</span></div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Weekly hours trend */}
          {(d.timesheets?.weekly || []).length > 0 && (
            <div className="bg-white rounded-lg shadow border border-gray-200 p-5">
              <h3 className="text-base font-semibold text-gray-900 mb-4">Weekly Hours (Last 4 Weeks)</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="border-b border-gray-200">
                    <th className="pb-2 text-left text-xs font-semibold text-gray-600">Week Starting</th>
                    <th className="pb-2 text-right text-xs font-semibold text-gray-600">Total Hours</th>
                    <th className="pb-2 text-right text-xs font-semibold text-gray-600">Billable Hours</th>
                    <th className="pb-2 text-right text-xs font-semibold text-gray-600">Billable %</th>
                  </tr></thead>
                  <tbody className="divide-y divide-gray-100">
                    {d.timesheets.weekly.map(w => {
                      const pct = w.total_hours > 0 ? Math.round((w.billable_hours / w.total_hours) * 100) : 0;
                      return (
                        <tr key={w.week_start_date} className="hover:bg-gray-50">
                          <td className="py-2 text-gray-800">{w.week_start_date}</td>
                          <td className="py-2 text-right font-semibold text-blue-700">{w.total_hours}h</td>
                          <td className="py-2 text-right text-green-700">{w.billable_hours}h</td>
                          <td className="py-2 text-right text-gray-600">{pct}%</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── PROJECTS ── */}
      {tab === 'projects' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Project Status Report</h2>
            <ExportBtn onClick={exportProjects} />
          </div>
          <div className="bg-white rounded-lg shadow border border-gray-200 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {['Project','Status','Priority','Health','Progress','Budget','Spent','Tasks','Milestones'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-600">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {(d.projects || []).map(p => (
                  <tr key={p.project_id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{p.name}</div>
                      <div className="text-xs text-gray-400">{p.project_id}</div>
                    </td>
                    <td className="px-4 py-3"><StatusBadge value={p.status} map={statusColors} /></td>
                    <td className="px-4 py-3"><StatusBadge value={p.priority} map={statusColors} /></td>
                    <td className="px-4 py-3"><StatusBadge value={p.health_status || 'on-track'} map={statusColors} /></td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div className="h-full rounded-full bg-blue-500" style={{ width: `${p.progress || 0}%` }} />
                        </div>
                        <span className="text-xs text-gray-600">{p.progress || 0}%</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-700">{p.budget_hours || 0}h</td>
                    <td className="px-4 py-3">
                      <span className={p.spent_hours > p.budget_hours && p.budget_hours > 0 ? 'text-red-600 font-semibold' : 'text-gray-700'}>{p.spent_hours || 0}h</span>
                    </td>
                    <td className="px-4 py-3 text-gray-700">{p.completed_tasks || 0}/{p.task_count || 0}</td>
                    <td className="px-4 py-3 text-gray-700">{p.milestone_count || 0}</td>
                  </tr>
                ))}
                {(!d.projects || d.projects.length === 0) && (
                  <tr><td colSpan={9} className="px-4 py-8 text-center text-gray-400">No project data available.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── SPRINTS ── */}
      {tab === 'sprints' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Sprint Velocity Report</h2>
            <ExportBtn onClick={exportSprints} />
          </div>
          <div className="bg-white rounded-lg shadow border border-gray-200 overflow-x-auto mb-6">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {['Sprint','Status','Dates','Story Points','Velocity','Capacity','Committed','Completed'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-600">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {(d.sprints || []).map(sp => {
                  const completion = sp.story_points_planned > 0 ? Math.round((sp.story_points_completed / sp.story_points_planned) * 100) : 0;
                  return (
                    <tr key={sp.sprint_number} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">Sprint {sp.sprint_number}</div>
                        <div className="text-xs text-gray-400">{sp.name}</div>
                      </td>
                      <td className="px-4 py-3"><StatusBadge value={sp.status} map={statusColors} /></td>
                      <td className="px-4 py-3 text-xs text-gray-600">{sp.start_date} → {sp.end_date}</td>
                      <td className="px-4 py-3">
                        <div className="text-sm font-semibold text-gray-900">{sp.story_points_completed}/{sp.story_points_planned}</div>
                        <div className="text-xs text-gray-500">{completion}% done</div>
                      </td>
                      <td className="px-4 py-3 text-blue-700 font-bold">{sp.velocity || 0}</td>
                      <td className="px-4 py-3 text-gray-700">{sp.capacity_hours}h</td>
                      <td className="px-4 py-3 text-gray-700">{sp.committed_hours}h</td>
                      <td className="px-4 py-3 text-green-700 font-semibold">{sp.completed_hours}h</td>
                    </tr>
                  );
                })}
                {(!d.sprints || d.sprints.length === 0) && (
                  <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-400">No sprint data available.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── TASKS ── */}
      {tab === 'tasks' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Task Analysis Report</h2>
            <ExportBtn onClick={exportTasks} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {/* By Status */}
            <div className="bg-white rounded-lg shadow border border-gray-200 p-5">
              <h3 className="text-base font-semibold text-gray-900 mb-4">Tasks by Status</h3>
              <table className="w-full text-sm">
                <thead><tr className="border-b border-gray-200">
                  <th className="pb-2 text-left text-xs font-semibold text-gray-600">Status</th>
                  <th className="pb-2 text-right text-xs font-semibold text-gray-600">Count</th>
                  <th className="pb-2 text-right text-xs font-semibold text-gray-600">%</th>
                </tr></thead>
                <tbody className="divide-y divide-gray-100">
                  {(() => {
                    const total = (d.tasks?.byStatus || []).reduce((s, r) => s + r.count, 0) || 1;
                    return (d.tasks?.byStatus || []).map(row => (
                      <tr key={row.status} className="hover:bg-gray-50">
                        <td className="py-2"><StatusBadge value={row.status} map={statusColors} /></td>
                        <td className="py-2 text-right font-semibold text-gray-900">{row.count}</td>
                        <td className="py-2 text-right text-gray-500">{Math.round((row.count/total)*100)}%</td>
                      </tr>
                    ));
                  })()}
                </tbody>
              </table>
            </div>
            {/* By Priority */}
            <div className="bg-white rounded-lg shadow border border-gray-200 p-5">
              <h3 className="text-base font-semibold text-gray-900 mb-4">Tasks by Priority</h3>
              <table className="w-full text-sm">
                <thead><tr className="border-b border-gray-200">
                  <th className="pb-2 text-left text-xs font-semibold text-gray-600">Priority</th>
                  <th className="pb-2 text-right text-xs font-semibold text-gray-600">Count</th>
                  <th className="pb-2 text-right text-xs font-semibold text-gray-600">%</th>
                </tr></thead>
                <tbody className="divide-y divide-gray-100">
                  {(() => {
                    const total = (d.tasks?.byPriority || []).reduce((s, r) => s + r.count, 0) || 1;
                    return (d.tasks?.byPriority || []).map(row => (
                      <tr key={row.priority} className="hover:bg-gray-50">
                        <td className="py-2"><StatusBadge value={row.priority} map={statusColors} /></td>
                        <td className="py-2 text-right font-semibold text-gray-900">{row.count}</td>
                        <td className="py-2 text-right text-gray-500">{Math.round((row.count/total)*100)}%</td>
                      </tr>
                    ));
                  })()}
                </tbody>
              </table>
            </div>
          </div>
          {/* By Assignee */}
          <div className="bg-white rounded-lg shadow border border-gray-200 overflow-x-auto">
            <div className="px-5 py-4 border-b border-gray-200">
              <h3 className="text-base font-semibold text-gray-900">Tasks by Assignee</h3>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {['Assignee','Total','Completed','Completion %','Estimated Hours','Actual Hours'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-600">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {(d.tasks?.byAssignee || []).map(row => {
                  const pct = row.total > 0 ? Math.round((row.completed / row.total) * 100) : 0;
                  return (
                    <tr key={row.assignee} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-bold">{(row.assignee || '?').charAt(0)}</div>
                          <span className="font-medium text-gray-900">{row.assignee}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 font-semibold text-gray-900">{row.total}</td>
                      <td className="px-4 py-3 text-green-700">{row.completed}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div className="h-full rounded-full" style={{ width: `${pct}%`, background: pct >= 75 ? '#10b981' : pct >= 50 ? '#f59e0b' : '#ef4444' }} />
                          </div>
                          <span className="text-xs text-gray-600">{pct}%</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-purple-700">{row.estimated_hours || 0}h</td>
                      <td className="px-4 py-3 text-blue-700">{row.actual_hours || 0}h</td>
                    </tr>
                  );
                })}
                {(!d.tasks?.byAssignee || d.tasks.byAssignee.length === 0) && (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400">No task data available.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── TIME & LEAVE ── */}
      {tab === 'time' && (
        <div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Timesheet by User */}
            <div>
              <div className="flex justify-between items-center mb-3">
                <h2 className="text-lg font-semibold text-gray-900">Hours by Team Member (Last 4 Weeks)</h2>
                <ExportBtn onClick={exportTime} />
              </div>
              <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      {['Member','Total','Billable','Leave','Weeks'].map(h => (
                        <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-600">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {(d.timesheets?.byUser || []).map(row => (
                      <tr key={row.user_name} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium text-gray-900">{row.user_name}</td>
                        <td className="px-4 py-3 font-semibold text-blue-700">{row.total_hours}h</td>
                        <td className="px-4 py-3 text-green-700">{row.billable_hours}h</td>
                        <td className="px-4 py-3 text-pink-600">{row.leave_hours > 0 ? `${row.leave_hours}h` : '—'}</td>
                        <td className="px-4 py-3 text-gray-600">{row.weeks_logged}w</td>
                      </tr>
                    ))}
                    {(!d.timesheets?.byUser || d.timesheets.byUser.length === 0) && (
                      <tr><td colSpan={5} className="px-4 py-6 text-center text-gray-400">No timesheet data.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            {/* Hours by Project */}
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-3">Hours by Project</h2>
              <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      {['Project','Hours','Billable','Billable %'].map(h => (
                        <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-600">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {(d.timesheets?.byProject || []).map(row => {
                      const pct = row.hours > 0 ? Math.round((row.billable_hours / row.hours) * 100) : 0;
                      return (
                        <tr key={row.project_id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 font-medium text-gray-900">{row.project_id}</td>
                          <td className="px-4 py-3 font-semibold text-blue-700">{row.hours}h</td>
                          <td className="px-4 py-3 text-green-700">{row.billable_hours}h</td>
                          <td className="px-4 py-3 text-gray-600">{pct}%</td>
                        </tr>
                      );
                    })}
                    {(!d.timesheets?.byProject || d.timesheets.byProject.length === 0) && (
                      <tr><td colSpan={4} className="px-4 py-6 text-center text-gray-400">No project hours data.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Leave Report */}
          <div className="flex justify-between items-center mb-3">
            <h2 className="text-lg font-semibold text-gray-900">Leave Report</h2>
            <ExportBtn onClick={exportLeave} />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Leave by User */}
            <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
              <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 text-xs font-semibold text-gray-600">By Team Member</div>
              <table className="w-full text-sm">
                <thead><tr>
                  {['Member','Approved Days','Pending','Rejected'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-600">{h}</th>
                  ))}
                </tr></thead>
                <tbody className="divide-y divide-gray-100">
                  {(d.leaves?.byUser || []).map(row => (
                    <tr key={row.user_name} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium text-gray-900">{row.user_name}</td>
                      <td className="px-4 py-3 text-green-700 font-semibold">{row.approved_days}d</td>
                      <td className="px-4 py-3 text-yellow-700">{row.pending_days > 0 ? `${row.pending_days}d` : '—'}</td>
                      <td className="px-4 py-3 text-red-600">{row.rejected_days > 0 ? `${row.rejected_days}d` : '—'}</td>
                    </tr>
                  ))}
                  {(!d.leaves?.byUser || d.leaves.byUser.length === 0) && (
                    <tr><td colSpan={4} className="px-4 py-6 text-center text-gray-400">No leave data.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            {/* Leave Balances */}
            <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
              <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 text-xs font-semibold text-gray-600">Leave Balances</div>
              <table className="w-full text-sm">
                <thead><tr>
                  {['Member','Vacation','Sick','Personal'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-600">{h}</th>
                  ))}
                </tr></thead>
                <tbody className="divide-y divide-gray-100">
                  {(d.leaves?.balances || []).map(row => (
                    <tr key={row.user_name} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium text-gray-900">{row.user_name}</td>
                      <td className="px-4 py-3 text-sm">
                        <span className="font-semibold text-blue-700">{row.vacation_remaining}</span>
                        <span className="text-gray-400 text-xs"> /{row.vacation_total}</span>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span className="font-semibold text-red-600">{row.sick_remaining}</span>
                        <span className="text-gray-400 text-xs"> /{row.sick_total}</span>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span className="font-semibold text-purple-700">{row.personal_remaining}</span>
                        <span className="text-gray-400 text-xs"> /{row.personal_total}</span>
                      </td>
                    </tr>
                  ))}
                  {(!d.leaves?.balances || d.leaves.balances.length === 0) && (
                    <tr><td colSpan={4} className="px-4 py-6 text-center text-gray-400">No balance data.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ── QUALITY ── */}
      {tab === 'quality' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Quality & Bug Report</h2>
            <ExportBtn onClick={exportQuality} />
          </div>
          {/* KPI cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {(() => {
              const bugs = d.quality?.bugsBySeverity || [];
              const totalBugs = bugs.reduce((s, r) => s + r.count, 0);
              const critical = bugs.find(r => r.severity === 'critical')?.count || 0;
              const high = bugs.find(r => r.severity === 'high')?.count || 0;
              const tc = d.quality?.testCases || [];
              const totalTC = tc.reduce((s, r) => s + r.count, 0);
              const passed = tc.find(r => r.status === 'pass' || r.status === 'passed')?.count || 0;
              const coverage = totalTC > 0 ? Math.round((passed / totalTC) * 100) : 0;
              return [
                <ReportCard key="total" label="Total Bugs"    value={totalBugs} color="red"    sub="All bugs" />,
                <ReportCard key="crit"  label="Critical Bugs" value={critical}  color="red"    sub="Needs immediate action" />,
                <ReportCard key="high"  label="High Severity" value={high}      color="orange" sub="High priority" />,
                <ReportCard key="cov"   label="Test Pass Rate" value={`${coverage}%`} color="green" sub={`${passed}/${totalTC} test cases`} />,
              ];
            })()}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {/* Bugs by severity */}
            <div className="bg-white rounded-lg shadow border border-gray-200 p-5">
              <h3 className="text-base font-semibold text-gray-900 mb-4">Bugs by Severity</h3>
              <table className="w-full text-sm">
                <thead><tr className="border-b border-gray-200">
                  <th className="pb-2 text-left text-xs font-semibold text-gray-600">Severity</th>
                  <th className="pb-2 text-right text-xs font-semibold text-gray-600">Count</th>
                  <th className="pb-2 text-right text-xs font-semibold text-gray-600">%</th>
                </tr></thead>
                <tbody className="divide-y divide-gray-100">
                  {(() => {
                    const total = (d.quality?.bugsBySeverity || []).reduce((s, r) => s + r.count, 0) || 1;
                    return (d.quality?.bugsBySeverity || []).map(row => (
                      <tr key={row.severity} className="hover:bg-gray-50">
                        <td className="py-2"><StatusBadge value={row.severity} map={statusColors} /></td>
                        <td className="py-2 text-right font-semibold text-gray-900">{row.count}</td>
                        <td className="py-2 text-right text-gray-500">{Math.round((row.count/total)*100)}%</td>
                      </tr>
                    ));
                  })()}
                </tbody>
              </table>
            </div>
            {/* Test Cases */}
            <div className="bg-white rounded-lg shadow border border-gray-200 p-5">
              <h3 className="text-base font-semibold text-gray-900 mb-4">Test Case Results</h3>
              <table className="w-full text-sm">
                <thead><tr className="border-b border-gray-200">
                  <th className="pb-2 text-left text-xs font-semibold text-gray-600">Status</th>
                  <th className="pb-2 text-right text-xs font-semibold text-gray-600">Count</th>
                  <th className="pb-2 text-right text-xs font-semibold text-gray-600">%</th>
                </tr></thead>
                <tbody className="divide-y divide-gray-100">
                  {(() => {
                    const total = (d.quality?.testCases || []).reduce((s, r) => s + r.count, 0) || 1;
                    return (d.quality?.testCases || []).map(row => (
                      <tr key={row.status} className="hover:bg-gray-50">
                        <td className="py-2"><StatusBadge value={row.status} map={statusColors} /></td>
                        <td className="py-2 text-right font-semibold text-gray-900">{row.count}</td>
                        <td className="py-2 text-right text-gray-500">{Math.round((row.count/total)*100)}%</td>
                      </tr>
                    ));
                  })()}
                </tbody>
              </table>
            </div>
          </div>

          {/* Recent Bugs table */}
          <div className="bg-white rounded-lg shadow border border-gray-200 overflow-x-auto">
            <div className="px-5 py-4 border-b border-gray-200">
              <h3 className="text-base font-semibold text-gray-900">Bug List (by Severity)</h3>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {['Bug ID','Title','Severity','Status','Environment','Assignee','Reported'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-600">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {(d.quality?.recentBugs || []).map(b => (
                  <tr key={b.bug_id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-xs font-mono text-gray-500">{b.bug_id}</td>
                    <td className="px-4 py-3 font-medium text-gray-900 max-w-xs truncate">{b.title}</td>
                    <td className="px-4 py-3"><StatusBadge value={b.severity} map={statusColors} /></td>
                    <td className="px-4 py-3"><StatusBadge value={b.status} map={statusColors} /></td>
                    <td className="px-4 py-3 text-gray-600">{b.environment || '—'}</td>
                    <td className="px-4 py-3 text-gray-600">{b.assignee || '—'}</td>
                    <td className="px-4 py-3 text-gray-500 text-xs">{b.created_at?.slice(0, 10)}</td>
                  </tr>
                ))}
                {(!d.quality?.recentBugs || d.quality.recentBugs.length === 0) && (
                  <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-400">No bug data available.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// ---- TimeBoard Components ----

const getWeekDates = (weekStart) => {
  const dates = [];
  const start = new Date(weekStart + 'T00:00:00');
  for (let i = 0; i < 7; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    dates.push(d.toISOString().split('T')[0]);
  }
  return dates;
};

// ============================================================
// KNOWLEDGEBOARD COMPONENTS
// ============================================================

// Simple markdown renderer (converts common markdown to HTML)
// Shared KnowledgeBoard helpers (used by markdown renderer + article view)
function kbSlugify(s) {
  return String(s || '')
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .slice(0, 80);
}

function kbExtractTOC(content) {
  if (!content) return [];
  const toc = [];
  const lines = content.split('\n');
  let inCode = false;
  for (const raw of lines) {
    if (/^```/.test(raw)) { inCode = !inCode; continue; }
    if (inCode) continue;
    const m = raw.match(/^(#{1,3})\s+(.+)$/);
    if (!m) continue;
    const level = m[1].length;
    const text = m[2].trim();
    toc.push({ level, text, id: kbSlugify(text) });
  }
  return toc;
}

function kbReadingTime(content) {
  if (!content) return '1 min read';
  const words = content.trim().split(/\s+/).filter(Boolean).length;
  const mins = Math.max(1, Math.round(words / 200));
  return `${mins} min read`;
}

function kbWordCount(content) {
  if (!content) return 0;
  return content.trim().split(/\s+/).filter(Boolean).length;
}

// Split markdown by H1 headings into tab sections.
// Returns a single { title: null, content } when the article has fewer than 2 H1s
// (so the caller can fall back to continuous rendering).
function kbSplitByH1(content) {
  if (!content) return [{ title: null, slug: null, content: '' }];
  const lines = content.split('\n');
  const sections = [];
  let preamble = [];
  let current = null; // { title, slug, lines: [] }
  let inCode = false;
  for (const line of lines) {
    if (/^```/.test(line)) inCode = !inCode;
    const h1 = !inCode && line.match(/^#\s+(.+?)\s*$/);
    if (h1) {
      if (current) sections.push(current);
      const title = h1[1];
      current = { title, slug: kbSlugify(title), lines: [] };
    } else if (current) {
      current.lines.push(line);
    } else {
      preamble.push(line);
    }
  }
  if (current) sections.push(current);
  if (sections.length < 2) {
    // Not enough H1s to tab — return the original article as one section
    return [{ title: null, slug: null, content }];
  }
  // Prepend any preamble text (content before the first H1) to the first tab so it isn't lost
  const preambleText = preamble.join('\n').trim();
  return sections.map((s, i) => ({
    title: s.title,
    slug: s.slug,
    content: (i === 0 && preambleText ? preambleText + '\n\n' : '') + s.lines.join('\n').trim()
  }));
}

function renderMarkdown(text) {
  if (!text) return '';

  const esc = s => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

  const inline = s => {
    if (!s) return '';
    return esc(s)
      .replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>')
      .replace(/\*\*(.+?)\*\*/g, '<strong class="font-semibold text-gray-900">$1</strong>')
      .replace(/\*(.+?)\*/g, '<em>$1</em>')
      .replace(/`([^`]+)`/g, '<code class="bg-indigo-50 text-indigo-700 px-1.5 py-0.5 rounded-md text-[0.85em] font-mono border border-indigo-100">$1</code>')
      // image: ![alt](url) — must come BEFORE link
      .replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '<img src="$2" alt="$1" class="block max-w-full my-5 rounded-lg border border-gray-200 shadow-sm" loading="lazy" />')
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener" class="text-indigo-600 hover:text-indigo-800 underline decoration-indigo-200 hover:decoration-indigo-500 underline-offset-2 decoration-2 font-medium">$1</a>');
  };

  const CALLOUT_STYLES = {
    info:      { bg: '#eff6ff', border: '#3b82f6', color: '#1e40af', icon: 'ℹ️' },
    tip:       { bg: '#f0fdf4', border: '#22c55e', color: '#166534', icon: '💡' },
    warn:      { bg: '#fffbeb', border: '#f59e0b', color: '#92400e', icon: '⚠️' },
    warning:   { bg: '#fffbeb', border: '#f59e0b', color: '#92400e', icon: '⚠️' },
    note:      { bg: '#f0f9ff', border: '#0ea5e9', color: '#0c4a6e', icon: '📝' },
    danger:    { bg: '#fef2f2', border: '#ef4444', color: '#991b1b', icon: '🚨' },
    important: { bg: '#fdf4ff', border: '#a855f7', color: '#6b21a8', icon: '❗' },
  };

  const lines = text.split('\n');
  const out = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    // ── Fenced code block ──────────────────────────────────────
    const fenceMatch = line.match(/^```(\w*)/);
    if (fenceMatch) {
      const lang = fenceMatch[1] || 'code';
      const codeLines = [];
      i++;
      while (i < lines.length && !lines[i].startsWith('```')) { codeLines.push(lines[i]); i++; }
      out.push(
        `<div class="my-5 rounded-xl overflow-hidden border border-gray-700 shadow-sm">` +
        `<div class="flex items-center gap-2 px-4 py-2 bg-gray-800">` +
        `<span class="flex gap-1.5"><span class="w-2.5 h-2.5 rounded-full bg-red-500/80"></span><span class="w-2.5 h-2.5 rounded-full bg-yellow-500/80"></span><span class="w-2.5 h-2.5 rounded-full bg-green-500/80"></span></span>` +
        `<span class="ml-1 text-xs text-gray-400 font-mono">${esc(lang)}</span></div>` +
        `<pre class="bg-gray-900 text-green-300 px-5 py-4 overflow-x-auto text-[0.8rem] leading-relaxed m-0"><code>${esc(codeLines.join('\n'))}</code></pre></div>`
      );
      i++; continue;
    }

    // ── Markdown table ─────────────────────────────────────────
    if (line.trim().startsWith('|') && line.includes('|')) {
      const tLines = [];
      while (i < lines.length && lines[i].trim().startsWith('|') && lines[i].includes('|')) { tLines.push(lines[i]); i++; }
      let tHtml = `<div class="my-5 overflow-x-auto rounded-xl border border-gray-200 shadow-sm"><table class="w-full border-collapse bg-white text-sm">`;
      let headDone = false;
      let rowIdx = 0;
      tLines.forEach(tl => {
        if (/^[|\s\-:]+$/.test(tl)) return; // separator row
        const cells = tl.split('|').slice(1, -1);
        if (!headDone) {
          tHtml += `<thead class="bg-gradient-to-b from-gray-50 to-gray-100/70"><tr>` + cells.map(c => `<th class="px-4 py-3 text-left text-[0.7rem] font-semibold uppercase tracking-wider text-gray-500 border-b-2 border-gray-200 whitespace-nowrap">${inline(c.trim())}</th>`).join('') + `</tr></thead><tbody>`;
          headDone = true;
        } else {
          const zebra = (rowIdx++ % 2 === 1) ? 'bg-gray-50/50' : 'bg-white';
          tHtml += `<tr class="${zebra} hover:bg-indigo-50/60 transition-colors">` + cells.map(c => `<td class="px-4 py-3 text-gray-700 align-top border-b border-gray-100 last:border-b-0">${inline(c.trim())}</td>`).join('') + `</tr>`;
        }
      });
      tHtml += `</tbody></table></div>`;
      out.push(tHtml); continue;
    }

    // ── Callout  > [!type] ─────────────────────────────────────
    const calloutMatch = line.match(/^> \[!(info|tip|warn(?:ing)?|note|danger|important)\]\s*(.*)/i);
    if (calloutMatch) {
      const st = CALLOUT_STYLES[calloutMatch[1].toLowerCase()] || CALLOUT_STYLES.note;
      let content = calloutMatch[2];
      i++;
      while (i < lines.length && lines[i].startsWith('> ')) { content += ' ' + lines[i].substring(2); i++; }
      out.push(
        `<div style="background:${st.bg};border-left:4px solid ${st.border}" class="flex gap-3 px-4 py-3 my-4 rounded-r-xl text-sm">` +
        `<span class="flex-shrink-0 text-base leading-snug">${st.icon}</span>` +
        `<div style="color:${st.color}" class="leading-relaxed">${inline(content)}</div></div>`
      );
      continue;
    }

    // ── Regular blockquote ─────────────────────────────────────
    if (line.startsWith('> ')) {
      out.push(`<blockquote class="border-l-4 border-indigo-300 pl-4 py-1 my-3 text-gray-600 italic bg-indigo-50/30 rounded-r-lg">${inline(line.substring(2))}</blockquote>`);
      i++; continue;
    }

    // ── Headings ───────────────────────────────────────────────
    if (line.startsWith('# ')) {
      const t = line.substring(2);
      const id = kbSlugify(t);
      out.push(`<h1 id="${id}" class="group scroll-mt-24 flex items-baseline gap-2 text-[1.875rem] font-bold text-gray-900 mt-10 mb-4 pb-3 border-b-2 border-gray-200 tracking-tight"><span class="flex-1">${inline(t)}</span><a href="#${id}" class="opacity-0 group-hover:opacity-60 hover:!opacity-100 text-indigo-500 text-base font-normal no-underline transition-opacity" aria-label="Copy link to section">#</a></h1>`);
      i++; continue;
    }
    if (line.startsWith('## ')) {
      const t = line.substring(3);
      const id = kbSlugify(t);
      // Leading icon tile — small rounded violet square with a doc glyph.
      // If the heading text starts with an emoji, use it instead so authors can pick a per-section icon by writing "## 🏠 Home".
      const leadingEmoji = t.match(/^([\p{Emoji_Presentation}\p{Extended_Pictographic}])\s*/u);
      const iconHtml = leadingEmoji
        ? `<span class="kb-h-icon inline-flex items-center justify-center w-10 h-10 rounded-xl bg-violet-100 text-xl flex-shrink-0">${leadingEmoji[1]}</span>`
        : `<span class="kb-h-icon inline-flex items-center justify-center w-10 h-10 rounded-xl bg-violet-100 text-violet-600 flex-shrink-0"><svg viewBox="0 0 24 24" class="w-5 h-5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="8" y1="13" x2="16" y2="13"/><line x1="8" y1="17" x2="14" y2="17"/></svg></span>`;
      const headingText = leadingEmoji ? t.slice(leadingEmoji[0].length) : t;
      out.push(`<h2 id="${id}" class="group scroll-mt-24 flex items-center gap-3 mt-10 mb-4 pb-2 border-b border-gray-200">${iconHtml}<span class="flex-1 text-2xl font-bold text-gray-900 tracking-tight">${inline(headingText)}</span><a href="#${id}" class="opacity-0 group-hover:opacity-60 hover:!opacity-100 text-indigo-500 text-base font-normal no-underline transition-opacity" aria-label="Copy link to section">#</a></h2>`);
      i++; continue;
    }
    if (line.startsWith('### ')) {
      const t = line.substring(4);
      const id = kbSlugify(t);
      out.push(`<h3 id="${id}" class="group scroll-mt-24 flex items-baseline gap-2 text-lg font-bold text-gray-900 mt-7 mb-3 pl-3 border-l-4 border-indigo-400"><span class="flex-1">${inline(t)}</span><a href="#${id}" class="opacity-0 group-hover:opacity-60 hover:!opacity-100 text-indigo-500 text-sm font-normal no-underline transition-opacity" aria-label="Copy link to section">#</a></h3>`);
      i++; continue;
    }
    if (line.startsWith('#### ')) {
      out.push(`<h4 class="text-xs font-bold text-gray-500 mt-5 mb-2 uppercase tracking-widest">${inline(line.substring(5))}</h4>`);
      i++; continue;
    }

    // ── Horizontal rule ────────────────────────────────────────
    if (line.match(/^---+\s*$/)) {
      out.push('<hr class="border-0 my-8 flex items-center"><span class="mx-auto text-gray-300 tracking-widest text-xs">• • •</span></hr>');
      i++; continue;
    }

    // ── Task list (must come before generic unordered list) ────
    if (line.match(/^[*\-]\s+\[[ xX]\]\s+/)) {
      const items = [];
      while (i < lines.length && lines[i].match(/^[*\-]\s+\[[ xX]\]\s+/)) {
        const m = lines[i].match(/^[*\-]\s+\[([ xX])\]\s+(.*)$/);
        const done = m[1].toLowerCase() === 'x';
        items.push(
          `<li class="flex items-start gap-2.5 leading-relaxed">` +
          `<span class="mt-1 inline-flex items-center justify-center w-4 h-4 rounded ${done ? 'bg-indigo-600 text-white' : 'bg-white border-2 border-gray-300'} flex-shrink-0">${done ? '<svg viewBox=\"0 0 12 12\" class=\"w-3 h-3\" fill=\"none\" stroke=\"currentColor\" stroke-width=\"2.5\" stroke-linecap=\"round\" stroke-linejoin=\"round\"><polyline points=\"2.5,6 5,8.5 9.5,3.5\"/></svg>' : ''}</span>` +
          `<span class="${done ? 'line-through text-gray-400' : 'text-gray-700'}">${inline(m[2])}</span>` +
          `</li>`
        );
        i++;
      }
      out.push(`<ul class="my-4 space-y-2 list-none pl-1">${items.join('')}</ul>`);
      continue;
    }

    // ── Ordered list ───────────────────────────────────────────
    if (line.match(/^\d+\. /)) {
      const items = [];
      while (i < lines.length && lines[i].match(/^\d+\. /)) {
        items.push(`<li class="text-gray-700 leading-relaxed pl-1">${inline(lines[i].replace(/^\d+\. /, ''))}</li>`);
        i++;
      }
      out.push(`<ol class="list-decimal pl-6 my-4 space-y-2 marker:text-indigo-500 marker:font-semibold">${items.join('')}</ol>`);
      continue;
    }

    // ── Unordered list ─────────────────────────────────────────
    if (line.match(/^[*\-] /)) {
      const items = [];
      while (i < lines.length && lines[i].match(/^[*\-] /)) {
        items.push(`<li class="text-gray-700 leading-relaxed pl-1">${inline(lines[i].substring(2))}</li>`);
        i++;
      }
      out.push(`<ul class="list-disc pl-6 my-4 space-y-2 marker:text-indigo-400">${items.join('')}</ul>`);
      continue;
    }

    // ── Empty line ─────────────────────────────────────────────
    if (line.trim() === '') { i++; continue; }

    // ── Paragraph (collect consecutive non-special lines) ──────
    const pLines = [];
    while (
      i < lines.length &&
      lines[i].trim() !== '' &&
      !lines[i].startsWith('#') &&
      !lines[i].startsWith('> ') &&
      !lines[i].match(/^[*\-] /) &&
      !lines[i].match(/^\d+\. /) &&
      !lines[i].match(/^---+\s*$/) &&
      !lines[i].match(/^```/) &&
      !lines[i].trim().startsWith('|')
    ) { pLines.push(lines[i]); i++; }
    if (pLines.length) out.push(`<p class="mb-4 text-gray-700 leading-7">${inline(pLines.join(' '))}</p>`);
    else i++;
  }

  return out.join('\n');
}

// ============================================================
// INCIDENTBOARD COMPONENTS
// ============================================================

const INCIDENT_SEVERITIES = ['Critical', 'High', 'Medium', 'Low'];
const INCIDENT_STATUSES = ['Open', 'Investigating', 'Mitigating', 'Monitoring', 'Resolved', 'Closed'];
const INCIDENT_TEAMS = ['DevOps', 'DataOps', 'Engineering', 'QA', 'Support', 'Security'];
const INCIDENT_CATEGORIES = ['Backend', 'Infrastructure', 'API', 'Database', 'UI', 'Security', 'Network', 'Integration'];
const INCIDENT_ENVIRONMENTS = ['Production', 'Staging', 'QA', 'Development'];
const INCIDENT_IMPACT_LEVELS = ['Internal Only', 'Customer Facing', 'All Users'];
const INCIDENT_DETECTED_BY = ['User Report', 'QA Testing', 'Monitoring Alert', 'Automated Detection', 'Support Ticket'];

const incSeverityColor = (s) => ({ Critical: 'bg-red-100 text-red-700', High: 'bg-orange-100 text-orange-700', Medium: 'bg-yellow-100 text-yellow-700', Low: 'bg-gray-100 text-gray-600' }[s] || 'bg-gray-100 text-gray-600');
const incSeverityBorder = (s) => ({ Critical: 'border-red-400', High: 'border-orange-400', Medium: 'border-yellow-400', Low: 'border-gray-300' }[s] || 'border-gray-300');
const incStatusColor = (s) => ({ Open: 'bg-red-100 text-red-700', Investigating: 'bg-orange-100 text-orange-700', Mitigating: 'bg-yellow-100 text-yellow-700', Monitoring: 'bg-blue-100 text-blue-700', Resolved: 'bg-green-100 text-green-700', Closed: 'bg-gray-100 text-gray-500' }[s] || 'bg-gray-100 text-gray-600');
const incStatusDot = (s) => ({ Open: 'bg-red-500', Investigating: 'bg-orange-500', Mitigating: 'bg-yellow-500', Monitoring: 'bg-blue-500', Resolved: 'bg-green-500', Closed: 'bg-gray-400' }[s] || 'bg-gray-400');
const incImpactColor = (s) => ({ 'All Users': 'bg-red-100 text-red-700', 'Customer Facing': 'bg-orange-100 text-orange-700', 'Internal Only': 'bg-blue-100 text-blue-600' }[s] || 'bg-gray-100 text-gray-600');
const incCategoryColor = (s) => ({ Backend: 'bg-purple-100 text-purple-700', Infrastructure: 'bg-gray-200 text-gray-700', API: 'bg-blue-100 text-blue-700', Database: 'bg-cyan-100 text-cyan-700', UI: 'bg-pink-100 text-pink-700', Security: 'bg-red-100 text-red-800', Network: 'bg-teal-100 text-teal-700', Integration: 'bg-indigo-100 text-indigo-700' }[s] || 'bg-gray-100 text-gray-600');
const incTimelineIcon = (t) => ({ incident_created: '🚨', status_changed: '🔄', severity_changed: '⚡', assigned_to_changed: '👤', incident_commander_changed: '🎖️', ticket_owner_changed: '🎫', comment: '💬', assigned: '👤', mitigation_started: '🛠️', resolved: '✅', closed: '🔒' }[t] || '📝');

const fmtDuration = (ms) => {
  if (!ms || ms <= 0) return null;
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  if (h > 24) return `${Math.floor(h/24)}d ${h%24}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
};

const calcIncidentMetrics = (incident) => {
  if (!incident?.created_at) return {};
  const created = new Date(incident.created_at);
  const mitigated = incident.mitigation_at ? new Date(incident.mitigation_at) : null;
  const resolved = incident.resolved_at ? new Date(incident.resolved_at) : null;
  const closed = incident.closed_at ? new Date(incident.closed_at) : null;
  const now = new Date();
  return {
    timeToMitigate: mitigated ? fmtDuration(mitigated - created) : null,
    timeToResolve: resolved ? fmtDuration(resolved - created) : null,
    timeToClose: closed ? fmtDuration(closed - created) : null,
    timeOpen: !resolved ? fmtDuration(now - created) : null,
  };
};

const aiRootCauseHints = (incident) => {
  if (!incident) return [];
  const text = `${incident.title || ''} ${incident.description || ''} ${incident.service_affected || ''}`.toLowerCase();
  const suggestions = [];
  if (text.includes('timeout') || text.includes('slow')) suggestions.push('⚡ Possible database query timeout or slow network response. Check DB slow query log and connection pool metrics.');
  if (text.includes('memory') || text.includes('oom') || text.includes('heap')) suggestions.push('💾 Memory pressure detected. Check for memory leaks, large object allocations, or insufficient heap size.');
  if (text.includes('deploy') || text.includes('release') || text.includes('version')) suggestions.push('🚀 Recent deployment may have introduced regression. Consider rolling back the latest release.');
  if (text.includes('auth') || text.includes('login') || text.includes('token') || text.includes('jwt')) suggestions.push('🔐 Authentication service anomaly. Check token expiration settings and auth service health.');
  if (text.includes('api') || text.includes('gateway') || text.includes('endpoint')) suggestions.push('🔌 API gateway or upstream service failure. Verify rate limits, circuit breaker status, and upstream health.');
  if (text.includes('database') || text.includes('db') || text.includes('sql') || text.includes('query')) suggestions.push('🗄️ Database issue detected. Check connection pool exhaustion, lock contention, or disk I/O saturation.');
  if (text.includes('network') || text.includes('vpn') || text.includes('dns') || text.includes('connection')) suggestions.push('🌐 Network-layer issue. Check VPN gateway, DNS resolution, firewall rules, and routing tables.');
  if (text.includes('disk') || text.includes('storage') || text.includes('space')) suggestions.push('💿 Storage saturation. Check disk space usage, log rotation, and data retention policies.');
  if (text.includes('cpu') || text.includes('load') || text.includes('performance')) suggestions.push('⚙️ High CPU load detected. Profile running processes, check for infinite loops or runaway jobs.');
  if (text.includes('certificate') || text.includes('ssl') || text.includes('tls') || text.includes('https')) suggestions.push('🔒 SSL/TLS certificate issue. Verify certificate expiry date and chain of trust.');
  if (text.includes('permission') || text.includes('access') || text.includes('403') || text.includes('unauthorized')) suggestions.push('🚫 Authorization failure. Check IAM roles, RBAC policies, and recent permission changes.');
  if (suggestions.length === 0) suggestions.push('🔍 Gather more details: check application logs, infrastructure metrics (CPU, memory, disk, network), and recent deployment history for this service.');
  return suggestions.slice(0, 3);
};

const IncidentCreateModal = ({ onSave, onClose, allUsers = [], user }) => {
  const [tab, setTab] = React.useState('basics');
  const [form, setForm] = React.useState({
    title: '', description: '', severity: 'High', status: 'Open',
    category: 'Backend', environment: 'Production', impact_level: 'Internal Only',
    service_affected: '', affected_components: '', team: 'Engineering',
    detected_by: 'User Report', users_affected: 0, region_affected: '',
    encountered_by: '', encountered_by_id: '', location: '',
    incident_commander: '', incident_commander_id: '',
    ticket_owner: '', ticket_owner_id: '',
    assigned_to: '', assigned_to_id: '',
    mitigation_steps: '', customer_impact: '',
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setUser = (idKey, nameKey, id, users) => {
    const u = users.find(x => String(x.id) === id);
    setForm(f => ({ ...f, [idKey]: id, [nameKey]: u?.user_name || u?.name || '' }));
  };
  const handleSubmit = (e) => { e.preventDefault(); if (!form.title.trim()) return; onSave(form); };
  const tabs = [{ id: 'basics', label: '📋 Basics' }, { id: 'impact', label: '⚡ Impact' }, { id: 'ownership', label: '👥 Ownership' }, { id: 'service', label: '⚙️ Service' }];
  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
          <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">🚨 Report Incident</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl font-bold">&times;</button>
        </div>
        {/* Tabs */}
        <div className="flex border-b border-gray-200 px-6 flex-shrink-0">
          {tabs.map(t => (
            <button key={t.id} type="button" onClick={() => setTab(t.id)}
              className={`px-3 py-2.5 text-xs font-medium border-b-2 transition-colors -mb-px ${tab === t.id ? 'border-red-500 text-red-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
              {t.label}
            </button>
          ))}
        </div>
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-4">
            {tab === 'basics' && (<>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Incident Title *</label>
                <input value={form.title} onChange={e => set('title', e.target.value)} required placeholder="Brief description of the incident"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Description</label>
                <MiniWysiwyg value={form.description} onChange={v => set('description', v)} placeholder="What happened? What is the current impact?" minHeight="100px" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Severity</label>
                  <select value={form.severity} onChange={e => set('severity', e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-500">
                    {INCIDENT_SEVERITIES.map(s => <option key={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Status</label>
                  <select value={form.status} onChange={e => set('status', e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-500">
                    {INCIDENT_STATUSES.map(s => <option key={s}>{s}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Category</label>
                  <select value={form.category} onChange={e => set('category', e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-500">
                    {INCIDENT_CATEGORIES.map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Environment</label>
                  <select value={form.environment} onChange={e => set('environment', e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-500">
                    {INCIDENT_ENVIRONMENTS.map(e => <option key={e}>{e}</option>)}
                  </select>
                </div>
              </div>
            </>)}
            {tab === 'impact' && (<>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Impact Level</label>
                  <select value={form.impact_level} onChange={e => set('impact_level', e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-500">
                    {INCIDENT_IMPACT_LEVELS.map(i => <option key={i}>{i}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Users Affected</label>
                  <input type="number" min="0" value={form.users_affected} onChange={e => set('users_affected', parseInt(e.target.value)||0)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-500" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Region / Location Affected</label>
                <input value={form.region_affected} onChange={e => set('region_affected', e.target.value)} placeholder="e.g. Manila, All Regions, Southeast Asia"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-500" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Customer Impact Notes</label>
                <MiniWysiwyg value={form.customer_impact} onChange={v => set('customer_impact', v)} placeholder="Describe impact on customers..." minHeight="80px" />
              </div>
            </>)}
            {tab === 'ownership' && (<>
              <div className="p-3 bg-gray-50 rounded-lg text-sm text-gray-700 border border-gray-200">
                <span className="font-medium">Reported by:</span> {user?.user_name || user?.name || '—'} <span className="text-gray-400 text-xs">(auto-filled)</span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {(<>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Encountered By</label>
                    <input value={form.encountered_by} onChange={e => set('encountered_by', e.target.value)} placeholder="Enter name…"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Location</label>
                    <input value={form.location} onChange={e => set('location', e.target.value)} placeholder="e.g. Manila Office, WFH"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Incident Commander</label>
                    <select value={form.incident_commander_id} onChange={e => setUser('incident_commander_id','incident_commander',e.target.value,allUsers)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-500">
                      <option value="">— Unassigned —</option>
                      {allUsers.map(u => <option key={u.id} value={u.id}>{u.user_name||u.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Ticket Owner</label>
                    <select value={form.ticket_owner_id} onChange={e => setUser('ticket_owner_id','ticket_owner',e.target.value,allUsers)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-500">
                      <option value="">— Unassigned —</option>
                      {allUsers.map(u => <option key={u.id} value={u.id}>{u.user_name||u.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Assigned To (Resolver)</label>
                    <select value={form.assigned_to_id} onChange={e => setUser('assigned_to_id','assigned_to',e.target.value,allUsers)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-500">
                      <option value="">— Unassigned —</option>
                      {allUsers.map(u => <option key={u.id} value={u.id}>{u.user_name||u.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Team</label>
                    <select value={form.team} onChange={e => set('team', e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-500">
                      {INCIDENT_TEAMS.map(t => <option key={t}>{t}</option>)}
                    </select>
                  </div>
                </>)}
              </div>
            </>)}
            {tab === 'service' && (<>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Affected Service / System</label>
                <input value={form.service_affected} onChange={e => set('service_affected', e.target.value)} placeholder="e.g. API Gateway, Payment Service, Scrub Upload API"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-500" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Affected Components</label>
                <input value={form.affected_components} onChange={e => set('affected_components', e.target.value)} placeholder="e.g. Auth module, Rate limiter, DB replica"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-500" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Detected By</label>
                <select value={form.detected_by} onChange={e => set('detected_by', e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-500">
                  {INCIDENT_DETECTED_BY.map(d => <option key={d}>{d}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Initial Mitigation Steps</label>
                <MiniWysiwyg value={form.mitigation_steps} onChange={v => set('mitigation_steps', v)} placeholder="What steps are being taken to mitigate?" minHeight="80px" />
              </div>
            </>)}
          </div>
          <div className="px-6 pb-6 flex justify-between items-center border-t border-gray-100 pt-4">
            <div className="flex gap-1">
              {tabs.map((t,i) => (
                <button key={t.id} type="button" onClick={() => setTab(t.id)}
                  className={`w-2 h-2 rounded-full transition-colors ${tab === t.id ? 'bg-red-500' : 'bg-gray-300 hover:bg-gray-400'}`} />
              ))}
            </div>
            <div className="flex gap-3">
              <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 font-medium">Cancel</button>
              {tab !== 'service' && (
                <button type="button" onClick={() => { const idx = tabs.findIndex(t2=>t2.id===tab); if(idx<tabs.length-1) setTab(tabs[idx+1].id); }}
                  className="px-4 py-2 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200 font-medium">Next →</button>
              )}
              <button type="submit" className="px-5 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 font-medium">🚨 Report Incident</button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

const IncidentAttachmentManager = ({ incidentId, uploadedBy }) => {
  const [attachments, setAttachments] = React.useState([]);
  const [uploading, setUploading] = React.useState(false);
  React.useEffect(() => {
    if (!incidentId) return;
    fetch(`/api/incidents/${incidentId}/attachments`, { headers: { Authorization: `Bearer ${localStorage.getItem('orbit_token')}` } })
      .then(r => r.json()).then(d => setAttachments(d.attachments || [])).catch(() => {});
  }, [incidentId]);
  const handleFile = async (e) => {
    const files = Array.from(e.target.files || []);
    for (const file of files) {
      if (file.size > 10*1024*1024) { alert(`${file.name} exceeds 10MB limit`); continue; }
      setUploading(true);
      const reader = new FileReader();
      reader.onload = async (ev) => {
        const base64 = ev.target.result;
        await fetch(`/api/incidents/${incidentId}/attachments`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('orbit_token')}` }, body: JSON.stringify({ file_name: file.name, file_type: file.type, file_size: file.size, file_data: base64 }) });
        const r2 = await fetch(`/api/incidents/${incidentId}/attachments`, { headers: { Authorization: `Bearer ${localStorage.getItem('orbit_token')}` } });
        const d2 = await r2.json();
        setAttachments(d2.attachments || []);
        setUploading(false);
      };
      reader.readAsDataURL(file);
    }
  };
  const handleDelete = async (id) => {
    await fetch(`/api/incidents/${incidentId}/attachments/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${localStorage.getItem('orbit_token')}` } });
    setAttachments(a => a.filter(x => x.id !== id));
  };
  const handleDownload = async (att) => {
    const r = await fetch(`/api/incidents/${incidentId}/attachments/${att.id}/data`, { headers: { Authorization: `Bearer ${localStorage.getItem('orbit_token')}` } });
    const d = await r.json();
    if (!d.fileData) return;
    const a = document.createElement('a'); a.href = d.fileData; a.download = d.fileName || att.file_name; a.click();
  };
  const fmtSize = (b) => b > 1048576 ? `${(b/1048576).toFixed(1)}MB` : `${Math.round(b/1024)}KB`;
  const fileIcon = (t='') => t.includes('image') ? '🖼️' : t.includes('pdf') ? '📄' : t.includes('csv') ? '📊' : t.includes('text') ? '📝' : '📎';
  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <label className={`flex items-center gap-2 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-xs font-medium cursor-pointer transition-colors ${uploading ? 'opacity-50 pointer-events-none' : ''}`}>
          <input type="file" multiple className="hidden" onChange={handleFile} accept="image/*,.pdf,.csv,.txt,.log,.har,.json,.xml,.zip" />
          {uploading ? '⏳ Uploading...' : '📎 Attach File'}
        </label>
        <span className="text-xs text-gray-400">Max 10MB per file</span>
      </div>
      {attachments.length === 0 ? (
        <p className="text-xs text-gray-400 italic">No attachments yet — upload logs, screenshots, error traces, or HAR files.</p>
      ) : (
        <div className="space-y-2">
          {attachments.map(a => (
            <div key={a.id} className="flex items-center gap-3 p-2.5 bg-gray-50 rounded-lg border border-gray-200">
              <span className="text-xl">{fileIcon(a.file_type)}</span>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-gray-800 truncate">{a.file_name}</p>
                <p className="text-[10px] text-gray-400">{a.file_size ? fmtSize(a.file_size) : ''} · {a.uploaded_by || 'Unknown'}</p>
              </div>
              <button onClick={() => handleDownload(a)} className="text-xs text-blue-600 hover:underline">Download</button>
              <button onClick={() => handleDelete(a.id)} className="text-xs text-red-400 hover:text-red-600">✕</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const IncidentSidebarField = ({ label, value, className = '' }) => value ? (
  <div className={className}>
    <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">{label}</p>
    <p className="text-sm text-gray-800 font-medium mt-0.5">{value}</p>
  </div>
) : null;

const IncidentSection = ({ title, children, icon }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-5">
    <h3 className="font-semibold text-gray-800 text-sm mb-3 flex items-center gap-2">{icon}{title}</h3>
    {children}
  </div>
);

const IncidentFieldEditor = ({ initialValue = '', onChange, placeholder = '', minHeight = '80px' }) => {
  const editorRef = React.useRef(null);
  const [isEmpty, setIsEmpty] = React.useState(!initialValue);
  const mountedRef = React.useRef(false);

  React.useEffect(() => {
    if (editorRef.current && !mountedRef.current) {
      mountedRef.current = true;
      if (initialValue) { editorRef.current.innerHTML = initialValue; setIsEmpty(false); }
    }
  }, []);

  const syncValue = () => {
    const html = editorRef.current?.innerHTML || '';
    const empty = !html || html === '<br>' || html === '<div><br></div>' || html === '<p><br></p>';
    setIsEmpty(empty);
    onChange(empty ? '' : html);
  };

  const exec = (cmd, val = null) => { document.execCommand(cmd, false, val); editorRef.current?.focus(); syncValue(); };

  const handlePaste = (e) => {
    e.preventDefault();
    document.execCommand('insertText', false, e.clipboardData.getData('text/plain'));
  };

  const tbtn = (cmd, content, title) => (
    <button type="button" title={title} key={cmd} onMouseDown={e => { e.preventDefault(); exec(cmd); }}
      className="px-2 py-1 text-xs rounded hover:bg-gray-200 transition-colors text-gray-700 select-none">{content}</button>
  );

  return (
    <div className="border border-gray-300 rounded-xl overflow-hidden focus-within:ring-2 focus-within:ring-indigo-400 focus-within:border-indigo-400 bg-white">
      <div className="flex items-center gap-0.5 px-2 py-1.5 bg-gray-50 border-b border-gray-200 flex-wrap">
        {tbtn('bold', <strong className="font-bold">B</strong>, 'Bold')}
        {tbtn('italic', <em>I</em>, 'Italic')}
        {tbtn('underline', <u>U</u>, 'Underline')}
        {tbtn('strikeThrough', <s>S</s>, 'Strikethrough')}
        <div className="w-px h-4 bg-gray-300 mx-1" />
        {tbtn('insertUnorderedList', '• List', 'Bullet list')}
        {tbtn('insertOrderedList', '1. List', 'Numbered list')}
        <div className="w-px h-4 bg-gray-300 mx-1" />
        {tbtn('removeFormat', 'Aa✕', 'Clear formatting')}
      </div>
      <div className="relative">
        {isEmpty && <div className="absolute top-2.5 left-3 text-sm text-gray-400 pointer-events-none select-none">{placeholder}</div>}
        <div ref={editorRef} contentEditable onInput={syncValue} onPaste={handlePaste}
          style={{ minHeight }}
          className="max-h-48 overflow-y-auto px-3 py-2.5 text-sm text-gray-800 focus:outline-none prose prose-sm max-w-none [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5"
          suppressContentEditableWarning />
      </div>
    </div>
  );
};

const IncidentDetailView = ({ incident, user, onBack, onUpdateIncident, onAddUpdate, onDeleteIncident }) => {
  const [form, setForm] = React.useState({ ...incident });
  const [editMode, setEditMode] = React.useState(false);
  const [savingEdit, setSavingEdit] = React.useState(false);
  const [updateMsg, setUpdateMsg] = React.useState('');
  const [submittingUpdate, setSubmittingUpdate] = React.useState(false);
  const [activeSection, setActiveSection] = React.useState('details');
  const [allUsers, setAllUsers] = React.useState([]);
  const isAdmin = (user?.role || user?.user_role) === 'admin';
  const _role = user?.role || user?.user_role || '';
  const canEdit = isAdmin || _role === 'devops_manager' || _role === 'manager' || _role === 'dataops_manager' || _role === 'service_desk_manager' || incident.ticket_owner === (user?.user_name) || incident.reported_by === (user?.user_name);
  const updates = incident.updates || [];
  const related = incident.relatedIncidents || [];
  const metrics = calcIncidentMetrics(incident);
  const aiHints = aiRootCauseHints(incident);
  const isInvestigating = ['Open','Investigating','Mitigating'].includes(incident.status);

  React.useEffect(() => {
    fetch('/api/users/directory', { headers: { Authorization: `Bearer ${localStorage.getItem('orbit_token')}` } })
      .then(r => r.json()).then(d => setAllUsers(Array.isArray(d) ? d : d.users || [])).catch(() => {});
  }, []);

  React.useEffect(() => { setForm({ ...incident }); setEditMode(false); }, [incident.id, incident.status, incident.severity]);

  const handleFieldSave = async (fields) => { await onUpdateIncident(incident.id, { ...incident, ...fields }); };
  const handleSaveEdit = async () => {
    setSavingEdit(true);
    await handleFieldSave({
      root_cause: form.root_cause, mitigation_steps: form.mitigation_steps,
      resolution_summary: form.resolution_summary, customer_impact: form.customer_impact,
      preventive_actions: form.preventive_actions,
      severity: form.severity, impact_level: form.impact_level,
      category: form.category, environment: form.environment,
      detected_by: form.detected_by, service_affected: form.service_affected,
      affected_components: form.affected_components, users_affected: form.users_affected,
      region_affected: form.region_affected,
      incident_commander: form.incident_commander, incident_commander_id: form.incident_commander_id,
      ticket_owner: form.ticket_owner, ticket_owner_id: form.ticket_owner_id,
      assigned_to: form.assigned_to, assigned_to_id: form.assigned_to_id,
      team: form.team,
      encountered_by: form.encountered_by, encountered_by_id: form.encountered_by_id,
      location: form.location,
    });
    setSavingEdit(false);
    setEditMode(false);
  };
  const handleCancelEdit = () => { setForm({ ...incident }); setEditMode(false); };
  const handleAddUpdate = async () => {
    if (!updateMsg.trim()) return;
    setSubmittingUpdate(true);
    await onAddUpdate(incident.id, updateMsg.trim());
    setUpdateMsg('');
    setSubmittingUpdate(false);
  };
  const setUser = (idKey, nameKey, id) => {
    const u = allUsers.find(x => String(x.id || x.user_id) === id);
    setForm(f => ({ ...f, [idKey]: id, [nameKey]: u?.user_name || u?.name || '' }));
  };

  return (
    <div className="max-w-[1400px] mx-auto px-4 py-6">
      {/* Top bar */}
      <div className="flex items-center gap-3 mb-5 flex-wrap">
        <button onClick={onBack} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800 font-medium">
          <ArrowLeft size={16} /> Back
        </button>
        <span className="text-gray-300">|</span>
        <span className="text-xs font-mono text-gray-400 font-semibold">INC-{String(incident.id).padStart(4,'0')}</span>
        {incident.incident_channel && <span className="text-xs font-mono bg-gray-900 text-green-400 px-2 py-0.5 rounded">{incident.incident_channel}</span>}
        <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${incSeverityColor(incident.severity)}`}>{incident.severity}</span>
        <span className={`text-xs font-semibold px-2.5 py-1 rounded-full flex items-center gap-1 ${incStatusColor(incident.status)}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${incStatusDot(incident.status)}`}></span>{incident.status}
        </span>
        {incident.impact_level && <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${incImpactColor(incident.impact_level)}`}>{incident.impact_level}</span>}
        {incident.category && <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${incCategoryColor(incident.category)}`}>{incident.category}</span>}
        <div className="ml-auto flex items-center gap-2">
          {canEdit && !editMode && (
            <button onClick={() => setEditMode(true)}
              className="px-3 py-1.5 text-xs text-indigo-600 border border-indigo-200 hover:bg-indigo-50 rounded-lg font-medium flex items-center gap-1">
              ✏️ Edit
            </button>
          )}
          {canEdit && editMode && (<>
            <button onClick={handleCancelEdit}
              className="px-3 py-1.5 text-xs text-gray-600 border border-gray-200 hover:bg-gray-50 rounded-lg font-medium">Cancel</button>
            <button onClick={handleSaveEdit} disabled={savingEdit}
              className="px-3 py-1.5 text-xs text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 rounded-lg font-medium">
              {savingEdit ? 'Saving...' : 'Save Changes'}
            </button>
          </>)}
          {isAdmin && (
            <button onClick={() => { if(window.confirm('Delete this incident?')) onDeleteIncident(incident.id); }}
              className="px-3 py-1.5 text-xs text-red-500 border border-red-200 hover:bg-red-50 rounded-lg font-medium">Delete</button>
          )}
        </div>
      </div>

      {/* 3-column layout */}
      <div className="grid grid-cols-12 gap-5">

        {/* LEFT: Main content (5 cols) */}
        <div className="col-span-12 lg:col-span-5 space-y-4">
          {/* Title + Description */}
          <IncidentSection title={incident.title} icon="🚨">
            {incident.environment && <span className="inline-block text-[10px] font-semibold px-2 py-0.5 rounded bg-gray-100 text-gray-600 mb-2">{incident.environment}</span>}
            {incident.description ? (
              <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-wrap">{incident.description}</p>
            ) : (
              <p className="text-sm text-gray-400 italic">No description provided.</p>
            )}
          </IncidentSection>

          {/* AI Root Cause Assistant */}
          {isInvestigating && (
            <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl border border-indigo-200 p-4">
              <h3 className="font-semibold text-indigo-800 text-sm mb-2 flex items-center gap-2">🤖 AI Root Cause Assistant</h3>
              <p className="text-xs text-indigo-600 mb-3">Based on incident details, here are probable root causes:</p>
              <div className="space-y-2">
                {aiHints.map((h,i) => (
                  <div key={i} className="bg-white rounded-lg p-3 border border-indigo-100 text-xs text-gray-700 leading-relaxed">{h}</div>
                ))}
              </div>
            </div>
          )}

          {/* RCA Section */}
          <IncidentSection title="Root Cause Analysis" icon="🔍">
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">Root Cause</label>
                {canEdit && editMode ? (
                  <IncidentFieldEditor key={`rc-${incident.id}`} initialValue={form.root_cause || ''} onChange={v => setForm(f => ({...f, root_cause: v}))} placeholder="What caused this incident?" minHeight="80px" />
                ) : (
                  <div className="text-sm text-gray-700 bg-gray-50 rounded-lg p-3 min-h-[60px] prose prose-sm max-w-none [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5" dangerouslySetInnerHTML={{ __html: sanitizeBugHtml(form.root_cause) || '<span class="italic text-gray-400">Not yet documented.</span>' }} />
                )}
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">Mitigation Steps</label>
                {canEdit && editMode ? (
                  <IncidentFieldEditor key={`ms-${incident.id}`} initialValue={form.mitigation_steps || ''} onChange={v => setForm(f => ({...f, mitigation_steps: v}))} placeholder="Steps taken to mitigate..." minHeight="80px" />
                ) : (
                  <div className="text-sm text-gray-700 bg-gray-50 rounded-lg p-3 min-h-[60px] prose prose-sm max-w-none [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5" dangerouslySetInnerHTML={{ __html: sanitizeBugHtml(form.mitigation_steps) || '<span class="italic text-gray-400">Not yet documented.</span>' }} />
                )}
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">Resolution Summary</label>
                {canEdit && editMode ? (
                  <IncidentFieldEditor key={`rs-${incident.id}`} initialValue={form.resolution_summary || ''} onChange={v => setForm(f => ({...f, resolution_summary: v}))} placeholder="How was this resolved?" minHeight="80px" />
                ) : (
                  <div className="text-sm text-gray-700 bg-gray-50 rounded-lg p-3 min-h-[60px] prose prose-sm max-w-none [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5" dangerouslySetInnerHTML={{ __html: sanitizeBugHtml(form.resolution_summary) || '<span class="italic text-gray-400">Not yet documented.</span>' }} />
                )}
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">Customer Impact Notes</label>
                {canEdit && editMode ? (
                  <IncidentFieldEditor key={`ci-${incident.id}`} initialValue={form.customer_impact || ''} onChange={v => setForm(f => ({...f, customer_impact: v}))} placeholder="Impact on customers / end users..." minHeight="60px" />
                ) : (
                  <div className="text-sm text-gray-700 bg-gray-50 rounded-lg p-3 min-h-[40px] prose prose-sm max-w-none [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5" dangerouslySetInnerHTML={{ __html: sanitizeBugHtml(form.customer_impact) || '<span class="italic text-gray-400">N/A</span>' }} />
                )}
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">Preventive Actions / Lessons Learned</label>
                {canEdit && editMode ? (
                  <IncidentFieldEditor key={`pa-${incident.id}`} initialValue={form.preventive_actions || ''} onChange={v => setForm(f => ({...f, preventive_actions: v}))} placeholder="What will prevent recurrence?" minHeight="60px" />
                ) : (
                  <div className="text-sm text-gray-700 bg-gray-50 rounded-lg p-3 min-h-[40px] prose prose-sm max-w-none [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5" dangerouslySetInnerHTML={{ __html: sanitizeBugHtml(form.preventive_actions) || '<span class="italic text-gray-400">N/A</span>' }} />
                )}
              </div>
            </div>
          </IncidentSection>

          {/* Attachments */}
          <IncidentSection title="Attachments" icon="📎">
            <IncidentAttachmentManager incidentId={incident.id} uploadedBy={user?.user_name} />
          </IncidentSection>

          {/* Related Incidents */}
          {related.length > 0 && (
            <div className="bg-amber-50 rounded-xl border border-amber-200 p-4">
              <h3 className="font-semibold text-amber-800 text-sm mb-2 flex items-center gap-2">⚠️ Related Incidents ({related.length})</h3>
              <p className="text-xs text-amber-600 mb-3">Similar incidents found in the last 90 days for the same service or category.</p>
              <div className="space-y-2">
                {related.map(r => (
                  <div key={r.id} className="bg-white rounded-lg p-2.5 border border-amber-100 flex items-center gap-2">
                    <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${incStatusDot(r.status)}`}></span>
                    <span className="text-[10px] font-mono text-gray-400">INC-{String(r.id).padStart(4,'0')}</span>
                    <span className="text-xs text-gray-700 flex-1 truncate">{r.title}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${incSeverityColor(r.severity)}`}>{r.severity}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* CENTER: Timeline (4 cols) */}
        <div className="col-span-12 lg:col-span-4">
          <div className="bg-white rounded-xl border border-gray-200 p-5 h-full flex flex-col">
            <h3 className="font-semibold text-gray-800 text-sm mb-4 flex items-center gap-2">📋 Incident Timeline</h3>
            <div className="flex-1 overflow-y-auto space-y-0 max-h-[600px]">
              {updates.length === 0 && <p className="text-sm text-gray-400 italic">No timeline events yet.</p>}
              {updates.map((u, i) => (
                <div key={u.id || i} className="flex gap-3 pb-4">
                  <div className="flex flex-col items-center flex-shrink-0">
                    <div className="w-7 h-7 rounded-full bg-gray-100 border-2 border-white shadow flex items-center justify-center text-sm flex-shrink-0">
                      {incTimelineIcon(u.event_type)}
                    </div>
                    {i < updates.length - 1 && <div className="w-px flex-1 bg-gray-200 mt-1 min-h-[12px]"></div>}
                  </div>
                  <div className="pb-1 flex-1 min-w-0">
                    <p className="text-sm text-gray-800 leading-relaxed">{u.update_message}</p>
                    <p className="text-[10px] text-gray-400 mt-0.5 flex items-center gap-1">
                      {u.created_by && <><span className="font-medium text-gray-500">{u.created_by}</span><span>·</span></>}
                      {u.created_at ? new Date(u.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : ''}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 pt-4 border-t border-gray-100">
              <p className="text-xs font-semibold text-gray-500 mb-2">Add Timeline Update</p>
              <textarea value={updateMsg} onChange={e => setUpdateMsg(e.target.value)} rows={2}
                placeholder="Describe what happened, action taken, or update..."
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-400 resize-none mb-2" />
              <button onClick={handleAddUpdate} disabled={!updateMsg.trim() || submittingUpdate}
                className="w-full py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 disabled:opacity-40 font-medium transition-colors">
                {submittingUpdate ? 'Posting...' : '+ Add Update'}
              </button>
            </div>
          </div>
        </div>

        {/* RIGHT: Sidebar (3 cols) */}
        <div className="col-span-12 lg:col-span-3 space-y-4">
          {/* Quick Status Change */}
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <h3 className="font-semibold text-gray-700 text-xs uppercase tracking-wide mb-3">Update Status</h3>
            <div className="space-y-1">
              {INCIDENT_STATUSES.map(s => (
                <button key={s} onClick={() => onUpdateIncident(incident.id, { ...incident, status: s })}
                  className={`w-full text-left px-3 py-1.5 text-xs rounded-lg flex items-center gap-2 transition-colors font-medium ${incident.status === s ? incStatusColor(s) + ' ring-1 ring-current' : 'text-gray-600 hover:bg-gray-50'}`}>
                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${incStatusDot(s)}`}></span>{s}
                </button>
              ))}
            </div>
          </div>

          {/* Metadata */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
            <h3 className="font-semibold text-gray-700 text-xs uppercase tracking-wide">Details</h3>
            <IncidentSidebarField label="Incident ID" value={`INC-${String(incident.id).padStart(4,'0')}`} />
            {(() => {
              const ef = (label, key, type = 'text', opts = null) => (
                <div key={key}>
                  <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1">{label}</p>
                  {canEdit && editMode ? (
                    opts ? (
                      <select value={form[key] || ''} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                        className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-xs focus:ring-1 focus:ring-indigo-400">
                        <option value="">— Select —</option>
                        {opts.map(o => <option key={o}>{o}</option>)}
                      </select>
                    ) : (
                      <input type={type} value={form[key] || ''} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                        placeholder={`Enter ${label.toLowerCase()}…`}
                        className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-xs focus:ring-1 focus:ring-indigo-400" />
                    )
                  ) : (
                    <p className="text-sm text-gray-800 font-medium mt-0.5">{form[key] || '—'}</p>
                  )}
                </div>
              );
              return (<>
                {ef('Category', 'category', 'text', INCIDENT_CATEGORIES)}
                {ef('Environment', 'environment', 'text', INCIDENT_ENVIRONMENTS)}
                {ef('Impact Level', 'impact_level', 'text', INCIDENT_IMPACT_LEVELS)}
                {ef('Detected By', 'detected_by')}
                {ef('Affected Service', 'service_affected')}
                {ef('Affected Components', 'affected_components')}
                {ef('Users Affected', 'users_affected', 'number')}
                {ef('Region Affected', 'region_affected')}
              </>);
            })()}
            <div className="border-t border-gray-100 pt-3 space-y-3">
              <h4 className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">People</h4>
              <IncidentSidebarField label="Reported By" value={incident.reported_by} />
              {(() => {
                const pf = (label, key) => (
                  <div key={key}>
                    <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1">{label}</p>
                    {canEdit && editMode ? (
                      <input value={form[key] || ''} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                        placeholder={`Enter ${label.toLowerCase()}…`}
                        className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-xs focus:ring-1 focus:ring-indigo-400" />
                    ) : (
                      <p className="text-sm text-gray-800 font-medium mt-0.5">{form[key] || '—'}</p>
                    )}
                  </div>
                );
                return (<>
                  {pf('Encountered By', 'encountered_by')}
                  {pf('Location', 'location')}
                  {canEdit && editMode ? (
                    <div>
                      <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1">Incident Commander</p>
                      <select value={form.incident_commander_id || ''} onChange={e => setUser('incident_commander_id','incident_commander',e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-xs focus:ring-1 focus:ring-indigo-400">
                        <option value="">— Unassigned —</option>
                        {allUsers.map(u => <option key={u.id} value={u.id}>{u.user_name||u.name}</option>)}
                      </select>
                    </div>
                  ) : <IncidentSidebarField label="Incident Commander" value={incident.incident_commander} />}
                  {canEdit && editMode ? (
                    <div>
                      <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1">Ticket Owner</p>
                      <select value={form.ticket_owner_id || ''} onChange={e => setUser('ticket_owner_id','ticket_owner',e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-xs focus:ring-1 focus:ring-indigo-400">
                        <option value="">— Unassigned —</option>
                        {allUsers.map(u => <option key={u.id} value={u.id}>{u.user_name||u.name}</option>)}
                      </select>
                    </div>
                  ) : <IncidentSidebarField label="Ticket Owner" value={incident.ticket_owner} />}
                  {canEdit && editMode ? (
                    <div>
                      <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1">Team</p>
                      <select value={form.team || ''} onChange={e => setForm(f => ({ ...f, team: e.target.value }))}
                        className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-xs focus:ring-1 focus:ring-indigo-400">
                        <option value="">— Select —</option>
                        {INCIDENT_TEAMS.map(t => <option key={t}>{t}</option>)}
                      </select>
                    </div>
                  ) : <IncidentSidebarField label="Team" value={incident.team} />}
                  {canEdit && editMode ? (
                    <div>
                      <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1">Assigned To</p>
                      <select value={form.assigned_to_id || ''} onChange={e => setUser('assigned_to_id','assigned_to',e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-xs focus:ring-1 focus:ring-indigo-400">
                        <option value="">— Unassigned —</option>
                        {allUsers.map(u => <option key={u.id} value={u.id}>{u.user_name||u.name}</option>)}
                      </select>
                    </div>
                  ) : <IncidentSidebarField label="Assigned To" value={incident.assigned_to} />}
                </>);
              })()}
            </div>
            <div className="border-t border-gray-100 pt-3 space-y-2">
              <h4 className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">Timestamps</h4>
              {incident.created_at && <div><p className="text-[10px] text-gray-400">Created</p><p className="text-xs text-gray-700">{new Date(incident.created_at).toLocaleString('en-US',{month:'short',day:'numeric',year:'numeric',hour:'numeric',minute:'2-digit'})}</p></div>}
              {incident.mitigation_at && <div><p className="text-[10px] text-gray-400">Mitigated</p><p className="text-xs text-yellow-700 font-medium">{new Date(incident.mitigation_at).toLocaleString('en-US',{month:'short',day:'numeric',hour:'numeric',minute:'2-digit'})}</p></div>}
              {incident.resolved_at && <div><p className="text-[10px] text-gray-400">Resolved</p><p className="text-xs text-green-700 font-medium">{new Date(incident.resolved_at).toLocaleString('en-US',{month:'short',day:'numeric',hour:'numeric',minute:'2-digit'})}</p></div>}
              {incident.closed_at && <div><p className="text-[10px] text-gray-400">Closed</p><p className="text-xs text-gray-600 font-medium">{new Date(incident.closed_at).toLocaleString('en-US',{month:'short',day:'numeric',hour:'numeric',minute:'2-digit'})}</p></div>}
            </div>
          </div>

          {/* Operational Metrics */}
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <h3 className="font-semibold text-gray-700 text-xs uppercase tracking-wide mb-3">Operational Metrics</h3>
            <div className="space-y-2">
              {metrics.timeOpen && (
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">Time Open</span>
                  <span className="text-xs font-semibold text-red-600 bg-red-50 px-2 py-0.5 rounded">{metrics.timeOpen}</span>
                </div>
              )}
              {metrics.timeToMitigate && (
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">Time to Mitigate</span>
                  <span className="text-xs font-semibold text-yellow-700 bg-yellow-50 px-2 py-0.5 rounded">{metrics.timeToMitigate}</span>
                </div>
              )}
              {metrics.timeToResolve && (
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">Time to Resolve</span>
                  <span className="text-xs font-semibold text-green-700 bg-green-50 px-2 py-0.5 rounded">{metrics.timeToResolve}</span>
                </div>
              )}
              {metrics.timeToClose && (
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">Time to Close</span>
                  <span className="text-xs font-semibold text-gray-600 bg-gray-100 px-2 py-0.5 rounded">{metrics.timeToClose}</span>
                </div>
              )}
              {!metrics.timeOpen && !metrics.timeToMitigate && !metrics.timeToResolve && !metrics.timeToClose && (
                <p className="text-xs text-gray-400 italic">Metrics populate as incident progresses.</p>
              )}
            </div>
          </div>

          {/* Edit Assignment Panel */}
          {canEdit && editMode && (
            <div className="bg-indigo-50 rounded-xl border border-indigo-200 p-4 space-y-3">
              <h3 className="font-semibold text-indigo-700 text-xs uppercase tracking-wide">Edit Assignment</h3>
              <div>
                <label className="block text-[10px] text-gray-500 mb-1">Severity</label>
                <select value={form.severity || ''} onChange={e => setForm(f=>({...f,severity:e.target.value}))}
                  className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:ring-2 focus:ring-indigo-400 bg-white">
                  {INCIDENT_SEVERITIES.map(s => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[10px] text-gray-500 mb-1">Impact Level</label>
                <select value={form.impact_level || ''} onChange={e => setForm(f=>({...f,impact_level:e.target.value}))}
                  className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:ring-2 focus:ring-indigo-400 bg-white">
                  {INCIDENT_IMPACT_LEVELS.map(i => <option key={i}>{i}</option>)}
                </select>
              </div>
              {allUsers.length > 0 && (<>
                <div>
                  <label className="block text-[10px] text-gray-500 mb-1">Incident Commander</label>
                  <select value={form.incident_commander_id || ''} onChange={e => setUser('incident_commander_id','incident_commander',e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:ring-2 focus:ring-indigo-400 bg-white">
                    <option value="">— Unassigned —</option>
                    {allUsers.map(u => <option key={u.id||u.user_id} value={u.id||u.user_id}>{u.user_name||u.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] text-gray-500 mb-1">Assigned To</label>
                  <select value={form.assigned_to_id || ''} onChange={e => setUser('assigned_to_id','assigned_to',e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:ring-2 focus:ring-indigo-400 bg-white">
                    <option value="">— Unassigned —</option>
                    {allUsers.map(u => <option key={u.id||u.user_id} value={u.id||u.user_id}>{u.user_name||u.name}</option>)}
                  </select>
                </div>
              </>)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// REQUESTBOARD MODULE
// ============================================================================

const REQ_TYPES = ['Feature Request','Change Request','IT Support','Access Request','Process Improvement','Data Request','General Request'];
const REQ_STATUSES = ['Submitted','Under Review','Approved','Rejected','In Progress','Completed'];
const REQ_PRIORITIES = ['Low','Medium','High','Critical'];
const CONVERT_TARGETS = [
  { id: 'quicktrack',    label: 'QuickTrack Task' },
  { id: 'quality',       label: 'Quality Bug' },
  { id: 'incidentboard', label: 'Incident' },
];

const reqStatusStyle = {
  'Submitted':    'bg-gray-100 text-gray-600',
  'Under Review': 'bg-blue-100 text-blue-700',
  'Approved':     'bg-green-100 text-green-700',
  'Rejected':     'bg-red-100 text-red-700',
  'In Progress':  'bg-purple-100 text-purple-700',
  'Completed':    'bg-teal-100 text-teal-700',
};
const reqPriorityStyle = {
  'Low':      'bg-gray-100 text-gray-500',
  'Medium':   'bg-blue-50 text-blue-600',
  'High':     'bg-orange-100 text-orange-700',
  'Critical': 'bg-red-100 text-red-700',
};

function RequestFormModal({ req, categories, user, onSave, onClose }) {
  const isEdit = !!req?.id;
  const [form, setForm] = React.useState({
    title: req?.title || '',
    description: req?.description || '',
    request_type: req?.request_type || 'General Request',
    category_id: req?.category_id != null ? String(req.category_id) : '',
    priority: req?.priority || 'Medium',
    requested_by: req?.requested_by || user?.user_name || user?.name || '',
    department: req?.department || '',
    assigned_to: req?.assigned_to || '',
  });
  const [saving, setSaving] = React.useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const inp = 'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400';

  // Full team data (with members) — used to filter the resolver dropdown to the selected team
  const [teamsFull, setTeamsFull] = React.useState([]);

  React.useEffect(() => {
    const token = localStorage.getItem('orbit_token');
    const hdrs = token ? { Authorization: `Bearer ${token}` } : {};
    fetch('/api/teams', { headers: hdrs }).then(r => r.json()).then(teams => {
      if (Array.isArray(teams)) setTeamsFull(teams);
    }).catch(() => {});
    if (!isEdit && !form.department) {
      fetch('/api/requests/user-department', { headers: hdrs }).then(r => r.json()).then(d => {
        if (d?.department) setForm(f => f.department ? f : { ...f, department: d.department });
      }).catch(() => {});
    }
  }, []);

  // Members of the team picked in "Assigned To" — lead first, then everyone else by name
  const selectedTeam = teamsFull.find(t => String(t.id) === String(form.category_id));
  const teamMembers = React.useMemo(() => {
    if (!selectedTeam) return [];
    const members = (selectedTeam.members || []).map(m => ({
      name: m.user_name,
      role: m.member_role,
      userRole: m.user_role,
    }));
    // If the lead isn't in member_roles for some reason, prepend them
    if (selectedTeam.lead_name && !members.some(m => m.name === selectedTeam.lead_name)) {
      members.unshift({ name: selectedTeam.lead_name, role: 'lead', userRole: null });
    }
    return members;
  }, [selectedTeam?.id]);

  const handleSave = async () => {
    if (!form.title.trim()) return;
    setSaving(true);
    await onSave({ ...form, category_id: form.category_id ? parseInt(form.category_id) : null });
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white">
          <h2 className="text-lg font-semibold">{isEdit ? 'Edit Request' : 'Submit New Request'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Title *</label>
            <input value={form.title} onChange={e => set('title', e.target.value)} className={inp} placeholder="Brief summary of your request" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
            <MiniWysiwyg value={form.description} onChange={v => set('description', v)} placeholder="Detailed description, business justification, expected outcome..." minHeight="100px" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Request Type</label>
              <select value={form.request_type} onChange={e => set('request_type', e.target.value)} className={inp}>
                {REQ_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Priority</label>
              <select value={form.priority} onChange={e => set('priority', e.target.value)} className={inp}>
                {REQ_PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Assigned To <span className="text-gray-400 font-normal">(team)</span></label>
              <select value={form.category_id} onChange={e => {
                const teamId = e.target.value;
                const team = teamsFull.find(t => String(t.id) === String(teamId));
                // When a team is picked, auto-populate the resolver to that team's lead.
                // If no lead is set, fall back to the first active member; else leave blank.
                let defaultResolver = '';
                if (team) {
                  if (team.lead_name) defaultResolver = team.lead_name;
                  else if (Array.isArray(team.members) && team.members.length) defaultResolver = team.members[0].user_name;
                }
                setForm(f => ({ ...f, category_id: teamId, assigned_to: defaultResolver }));
              }} className={inp}>
                <option value="">Select team...</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Requesting Department</label>
              <input value={form.department} onChange={e => set('department', e.target.value)} className={inp} placeholder="e.g. Engineering" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Requested By</label>
              <input value={form.requested_by} onChange={e => set('requested_by', e.target.value)} className={inp} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Assign To <span className="text-gray-400 font-normal">(resolver)</span></label>
              <select value={form.assigned_to} onChange={e => set('assigned_to', e.target.value)}
                disabled={!form.category_id}
                className={inp + (!form.category_id ? ' bg-gray-50 cursor-not-allowed' : '')}>
                <option value="">{form.category_id ? '— unassigned —' : 'Pick a team first'}</option>
                {teamMembers.map(m => (
                  <option key={m.name} value={m.name}>
                    {m.name}{m.role === 'lead' ? ' (Lead)' : ''}
                  </option>
                ))}
                {/* Keep the existing value as an option if it doesn't match the current team's roster (e.g. editing an older request) */}
                {form.assigned_to && !teamMembers.some(m => m.name === form.assigned_to) && (
                  <option value={form.assigned_to}>{form.assigned_to} (not on selected team)</option>
                )}
              </select>
              {!form.category_id && (
                <div className="mt-1 text-[11px] text-gray-400">Select a team in <span className="font-medium">Assigned To</span> first — the resolver list will populate with that team's members.</div>
              )}
            </div>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3 sticky bottom-0 bg-white">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600">Cancel</button>
          <button onClick={handleSave} disabled={saving || !form.title.trim()} className="px-5 py-2 bg-emerald-600 text-white text-sm rounded-lg hover:bg-emerald-700 disabled:opacity-50">
            {saving ? 'Submitting…' : isEdit ? 'Save Changes' : 'Submit Request'}
          </button>
        </div>
      </div>
    </div>
  );
}

function RequestDetailView({ requestId, categories, user, onBack, onRefresh }) {
  const [req, setReq] = React.useState(null);
  const [comments, setComments] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [comment, setComment] = React.useState('');
  const [commentHtml, setCommentHtml] = React.useState('');
  const [commentEditorKey, setCommentEditorKey] = React.useState(0);
  const [posting, setPosting] = React.useState(false);
  const [showApprove, setShowApprove] = React.useState(false);
  const [showReject, setShowReject]  = React.useState(false);
  const [showConvert, setShowConvert] = React.useState(false);
  const [actionNotes, setActionNotes] = React.useState('');
  const [convertTarget, setConvertTarget] = React.useState('quicktrack');
  const [acting, setActing] = React.useState(false);
  const [showEdit, setShowEdit] = React.useState(false);

  const role = user?.role || user?.user_role;
  const canManage = ['admin', 'manager', 'devops_manager', 'service_desk_manager'].includes(role);
  const canReview = canManage;

  const load = React.useCallback(async () => {
    setLoading(true);
    const [r, c] = await Promise.all([ModulesModel.getRequest(requestId), ModulesModel.getRequestComments(requestId)]);
    setReq(r); setComments(c);
    setLoading(false);
  }, [requestId]);

  React.useEffect(() => { load(); }, [load]);

  const handleComment = async () => {
    const content = commentHtml || comment;
    if (!content.trim()) return;
    setPosting(true);
    await ModulesModel.addRequestComment(requestId, { comment_text: content, created_by: user?.user_name || user?.name || 'User' });
    setComment(''); setCommentHtml(''); setCommentEditorKey(k => k + 1); load();
    setPosting(false);
  };

  const handleApprove = async () => {
    setActing(true);
    await ModulesModel.approveRequest(requestId, actionNotes);
    setShowApprove(false); setActionNotes(''); load(); onRefresh();
    setActing(false);
  };

  const handleReject = async () => {
    setActing(true);
    await ModulesModel.rejectRequest(requestId, actionNotes);
    setShowReject(false); setActionNotes(''); load(); onRefresh();
    setActing(false);
  };

  const handleConvert = async () => {
    setActing(true);
    const result = await ModulesModel.convertRequest(requestId, { target_module: convertTarget });
    if (result.success) { setShowConvert(false); load(); onRefresh(); }
    setActing(false);
  };

  const handleMarkComplete = async () => {
    if (!confirm('Mark this request as Completed?')) return;
    setActing(true);
    await ModulesModel.updateRequest(requestId, { status: 'Completed' });
    load(); onRefresh();
    setActing(false);
  };

  const handleReopen = async () => {
    if (!confirm('Reopen this request? Status will change back to In Progress.')) return;
    setActing(true);
    await ModulesModel.updateRequest(requestId, { status: 'In Progress' });
    load(); onRefresh();
    setActing(false);
  };

  const handleEditSave = async (data) => {
    const result = await ModulesModel.updateRequest(requestId, data);
    if (result?.success === true) {
      setShowEdit(false);
      const [r, c] = await Promise.all([ModulesModel.getRequest(requestId), ModulesModel.getRequestComments(requestId)]);
      if (r) { setReq(r); setComments(c); }
      onRefresh();
    } else {
      alert(`Failed to save request: ${result?.error || 'Unknown error'}`);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-gray-400">Loading…</div>;
  if (!req) return <div className="p-6 text-gray-500">Request not found.</div>;

  const teamName = categories.find(c => String(c.id) === String(req.category_id))?.name || req.category_name || '—';

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <button onClick={onBack} className="text-sm text-gray-500 hover:text-gray-800 mb-4 flex items-center gap-1">← Back to Requests</button>

      <div className="bg-white rounded-2xl border border-gray-200 shadow p-6 mb-5">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span className="text-xs text-gray-400 font-mono">#{req.id}</span>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${reqStatusStyle[req.status] || 'bg-gray-100 text-gray-600'}`}>{req.status}</span>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${reqPriorityStyle[req.priority] || 'bg-gray-100 text-gray-600'}`}>{req.priority}</span>
              <span className="px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-600">{req.request_type}</span>
            </div>
            <h1 className="text-xl font-bold text-gray-900">{req.title}</h1>
          </div>
          <div className="flex gap-2 shrink-0 flex-wrap justify-end">
            {canManage && <button onClick={() => setShowEdit(true)} className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg">Edit</button>}
            {canReview && req.status === 'Submitted' && <button onClick={() => setShowApprove(true)} className="px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700">Approve</button>}
            {canReview && ['Submitted','Under Review'].includes(req.status) && <button onClick={() => setShowReject(true)} className="px-3 py-1.5 text-sm bg-red-500 text-white rounded-lg hover:bg-red-600">Reject</button>}
            {canManage && !['Rejected','Completed'].includes(req.status) && <button onClick={() => setShowConvert(true)} className="px-3 py-1.5 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700">{req.converted_module ? 'Re-Convert →' : 'Convert →'}</button>}
            {canManage && !['Rejected','Completed'].includes(req.status) && <button onClick={handleMarkComplete} disabled={acting} className="px-3 py-1.5 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50">✓ Mark Complete</button>}
            {canManage && req.status === 'Completed' && <button onClick={handleReopen} disabled={acting} className="px-3 py-1.5 text-sm border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 disabled:opacity-50">↻ Reopen</button>}
          </div>
        </div>

        {req.description && <div className="text-sm text-gray-700 mb-4 prose prose-sm max-w-none [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5" dangerouslySetInnerHTML={{ __html: sanitizeBugHtml(req.description) }} />}

        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
          {[
            ['Requested By', req.requested_by || '—'],
            ['Created By', req.created_by || req.requested_by || '—'],
            ['Department', req.department || '—'],
            ['Team', teamName],
            ['Assigned To', req.assigned_to || '—'],
            ['Submitted', req.created_at ? new Date(req.created_at).toLocaleDateString() : '—'],
            ['Last Updated', req.updated_at ? new Date(req.updated_at).toLocaleDateString() : '—'],
          ].map(([label, val]) => (
            <div key={label} className="bg-gray-50 rounded-lg p-3">
              <div className="text-xs text-gray-400 mb-1">{label}</div>
              <div className="font-medium text-gray-800">{val}</div>
            </div>
          ))}
        </div>

        {(req.reviewed_by || req.approval_notes) && (
          <div className={`mt-4 p-3 rounded-lg border text-sm ${req.status === 'Approved' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
            <div className="font-medium mb-1">{req.status === 'Approved' ? '✓ Approved' : '✗ Rejected'} by {req.reviewed_by} {req.reviewed_at ? `on ${new Date(req.reviewed_at).toLocaleDateString()}` : ''}</div>
            {req.approval_notes && <div className="text-gray-600">{req.approval_notes}</div>}
          </div>
        )}

        {req.converted_module && (
          <div className="mt-4 p-3 bg-purple-50 border border-purple-200 rounded-lg text-sm">
            <span className="font-medium text-purple-700">↗ Converted to {req.converted_module}</span>
            {req.converted_item_id && <span className="text-purple-500 ml-2">ID: {req.converted_item_id}</span>}
          </div>
        )}
      </div>

      {/* Comments */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow p-6">
        <h2 className="font-semibold text-gray-800 mb-4">Comments ({comments.length})</h2>
        <div className="space-y-3 mb-4">
          {comments.length === 0 && <p className="text-sm text-gray-400">No comments yet.</p>}
          {comments.map(c => (
            <div key={c.id} className="flex gap-3">
              <div className="w-8 h-8 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-xs font-bold shrink-0">{(c.created_by || '?')[0].toUpperCase()}</div>
              <div className="flex-1 bg-gray-50 rounded-xl p-3">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-medium text-gray-800">{c.created_by}</span>
                  <span className="text-xs text-gray-400">{c.created_at ? new Date(c.created_at).toLocaleString() : ''}</span>
                </div>
                <div className="text-sm text-gray-700 prose prose-sm max-w-none [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_img]:max-w-full [&_img]:rounded [&_img]:my-1"
                  dangerouslySetInnerHTML={{ __html: c.comment_text }} />
              </div>
            </div>
          ))}
        </div>
        <div className="flex gap-3">
          <div className="w-8 h-8 rounded-full bg-emerald-600 text-white flex items-center justify-center text-xs font-bold shrink-0">{(user?.user_name || user?.name || 'U')[0].toUpperCase()}</div>
          <div className="flex-1 space-y-2">
            <BugCommentEditor key={commentEditorKey} onChange={setCommentHtml} placeholder="Add a comment… (supports bold, italic, lists)" />
            <div className="flex items-center justify-between">
              <p className="text-xs text-gray-400">Ctrl+B Bold · Ctrl+I Italic · Ctrl+U Underline</p>
              <button onClick={handleComment} disabled={posting || !commentHtml.trim()} className="px-4 py-2 bg-emerald-600 text-white text-sm rounded-xl hover:bg-emerald-700 disabled:opacity-50 font-medium">Post</button>
            </div>
          </div>
        </div>
      </div>

      {/* Approve Modal */}
      {showApprove && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowApprove(false)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6" onClick={e => e.stopPropagation()}>
            <h3 className="font-semibold text-gray-900 mb-3">Approve Request</h3>
            <textarea value={actionNotes} onChange={e => setActionNotes(e.target.value)} placeholder="Approval notes (optional)" rows={3} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mb-4 focus:outline-none" />
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowApprove(false)} className="px-3 py-1.5 text-sm text-gray-600">Cancel</button>
              <button onClick={handleApprove} disabled={acting} className="px-4 py-1.5 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 disabled:opacity-50">{acting ? 'Approving…' : 'Approve'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showReject && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowReject(false)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6" onClick={e => e.stopPropagation()}>
            <h3 className="font-semibold text-gray-900 mb-3">Reject Request</h3>
            <textarea value={actionNotes} onChange={e => setActionNotes(e.target.value)} placeholder="Reason for rejection *" rows={3} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mb-4 focus:outline-none" />
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowReject(false)} className="px-3 py-1.5 text-sm text-gray-600">Cancel</button>
              <button onClick={handleReject} disabled={acting} className="px-4 py-1.5 bg-red-500 text-white text-sm rounded-lg hover:bg-red-600 disabled:opacity-50">{acting ? 'Rejecting…' : 'Reject'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Convert Modal */}
      {showConvert && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowConvert(false)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6" onClick={e => e.stopPropagation()}>
            <h3 className="font-semibold text-gray-900 mb-1">Convert to Work Item</h3>
            <p className="text-sm text-gray-500 mb-4">Creates a new item in the target module.</p>
            <div className="space-y-2 mb-4">
              {CONVERT_TARGETS.map(t => (
                <label key={t.id} className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer ${convertTarget === t.id ? 'border-purple-400 bg-purple-50' : 'border-gray-200 hover:bg-gray-50'}`}>
                  <input type="radio" name="target" value={t.id} checked={convertTarget === t.id} onChange={() => setConvertTarget(t.id)} className="accent-purple-600" />
                  <span className="text-sm font-medium">{t.label}</span>
                </label>
              ))}
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowConvert(false)} className="px-3 py-1.5 text-sm text-gray-600">Cancel</button>
              <button onClick={handleConvert} disabled={acting} className="px-4 py-1.5 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 disabled:opacity-50">{acting ? 'Converting…' : 'Convert'}</button>
            </div>
          </div>
        </div>
      )}

      {showEdit && <RequestFormModal req={req} categories={categories} user={user} onSave={handleEditSave} onClose={() => setShowEdit(false)} />}
    </div>
  );
}

// ============================================================================
// TCMS — Test Case Management System
// ============================================================================

const TCMSModule = ({ user }) => {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [projectFilter, setProjectFilter] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState('');
  const [priorityFilter, setPriorityFilter] = React.useState('');
  const [view, setView] = React.useState('dashboard');
  const [selectedTC, setSelectedTC] = React.useState(null);
  const [executionHistory, setExecutionHistory] = React.useState([]);
  const [executing, setExecuting] = React.useState(false);
  const [actualResult, setActualResult] = React.useState('');
  const [execNotes, setExecNotes] = React.useState('');
  const [expandedSteps, setExpandedSteps] = React.useState({});
  const [stepResults, setStepResults] = React.useState({});
  const [stepAttachments, setStepAttachments] = React.useState({});
  const [stepForms, setStepForms] = React.useState({});
  const [savingStep, setSavingStep] = React.useState(null);
  const [previewAttach, setPreviewAttach] = React.useState(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (projectFilter) params.set('projectId', projectFilter);
    if (statusFilter) params.set('status', statusFilter);
    if (priorityFilter) params.set('priority', priorityFilter);
    const token = localStorage.getItem('orbit_token');
    const res = await fetch(`/api/tcms?${params}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    const d = await res.json();
    setData(d);
    setLoading(false);
  }, [projectFilter, statusFilter, priorityFilter]);

  React.useEffect(() => { load(); }, [load]);

  const handleExecute = async (tcId, status) => {
    setExecuting(true);
    const token = localStorage.getItem('orbit_token');
    await fetch('/api/tcms/execute', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify({ testCaseId: tcId, status, actualResult: actualResult || null, notes: execNotes || null })
    });
    setActualResult(''); setExecNotes(''); setExecuting(false);
    load();
    if (selectedTC?.id === tcId) {
      const updated = { ...selectedTC, status, executedBy: user?.user_name || 'UAT Tester', executedAt: new Date().toISOString() };
      setSelectedTC(updated);
      loadHistory(tcId);
    }
  };

  const loadHistory = async (tcId) => {
    const token = localStorage.getItem('orbit_token');
    const res = await fetch(`/api/tcms/history?testCaseId=${tcId}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    const h = await res.json();
    setExecutionHistory(Array.isArray(h) ? h : []);
  };

  const loadStepResults = async (tcId) => {
    const token = localStorage.getItem('orbit_token');
    const res = await fetch(`/api/tcms/step-results?testCaseId=${tcId}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    const d = await res.json();
    const srMap = {}; const saMap = {};
    (d.stepResults || []).forEach(r => { srMap[r.stepIndex] = r; });
    (d.attachments || []).forEach(a => { if (!saMap[a.stepIndex]) saMap[a.stepIndex] = []; saMap[a.stepIndex].push(a); });
    setStepResults(srMap); setStepAttachments(saMap);
  };

  const handleStepExecute = async (tcId, stepIdx, status) => {
    setSavingStep(stepIdx);
    const form = stepForms[stepIdx] || {};
    const token = localStorage.getItem('orbit_token');
    const res = await fetch('/api/tcms/step-results', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify({ testCaseId: tcId, stepIndex: stepIdx, status, actualResult: form.actualResult || '', comment: form.comment || '' })
    });
    const d = await res.json();
    setSavingStep(null);
    setStepForms(f => { const n = { ...f }; delete n[stepIdx]; return n; });
    await loadStepResults(tcId);
    if (d.overallStatus) {
      load(); // refresh main data
      if (selectedTC) setSelectedTC(prev => ({ ...prev, status: d.overallStatus }));
    }
  };

  const handleStepAttachUpload = async (tcId, stepIdx, file) => {
    if (file.size > 2 * 1024 * 1024) { alert('File too large (max 2MB)'); return; }
    const allowed = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'];
    if (!allowed.includes(file.type)) { alert('Only jpg/png/gif/webp/pdf allowed'); return; }
    const reader = new FileReader();
    reader.onload = async () => {
      const base64 = reader.result.split(',')[1];
      const token = localStorage.getItem('orbit_token');
      await fetch('/api/tcms/step-attachments', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ testCaseId: tcId, stepIndex: stepIdx, fileName: file.name, fileType: file.type, fileSize: file.size, fileData: base64 })
      });
      await loadStepResults(tcId);
    };
    reader.readAsDataURL(file);
  };

  const handleDeleteStepAttach = async (attachId, tcId) => {
    const token = localStorage.getItem('orbit_token');
    await fetch(`/api/tcms/step-attachments?id=${attachId}`, { method: 'DELETE', headers: token ? { Authorization: `Bearer ${token}` } : {} });
    await loadStepResults(tcId);
  };

  const openDetail = (tc) => {
    setSelectedTC(tc);
    setActualResult(''); setExecNotes(''); setStepForms({});
    setView('detail');
    loadHistory(tc.id);
    loadStepResults(tc.id);
  };

  const metrics = data?.metrics || {};
  const testCases = data?.testCases || [];
  const projects = data?.projects || [];

  const fmtDate = (d) => { if (!d) return '—'; return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); };
  const fmtDateTime = (d) => { if (!d) return '—'; return new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); };

  const statusStyles = { pending: 'bg-gray-100 text-gray-700', pass: 'bg-green-100 text-green-700', fail: 'bg-red-100 text-red-700', blocked: 'bg-orange-100 text-orange-700' };
  const priorityStyles = { critical: 'bg-red-100 text-red-700', high: 'bg-orange-100 text-orange-700', medium: 'bg-yellow-100 text-yellow-700', low: 'bg-green-100 text-green-700' };
  const statusLabels = { pending: 'Pending', pass: 'Passed', fail: 'Failed', blocked: 'Blocked' };

  if (loading && !data) return <div className="flex items-center justify-center h-64 text-gray-400">Loading TCMS...</div>;

  // ---- Detail View ----
  if (view === 'detail' && selectedTC) {
    const tc = testCases.find(t => t.id === selectedTC.id) || selectedTC;
    const steps = tc.steps || [];
    return (
      <div className="max-w-4xl mx-auto px-4 py-6">
        <button onClick={() => { setView('cases'); setSelectedTC(null); }} className="text-sm text-gray-500 hover:text-gray-800 mb-4 flex items-center gap-1">← Back to Test Cases</button>
        <div className="bg-white rounded-2xl border border-gray-200 shadow p-6 mb-5">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div>
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <span className="text-xs font-mono text-purple-600 font-bold">{tc.tcCode}</span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${statusStyles[tc.status]}`}>{statusLabels[tc.status] || tc.status}</span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${priorityStyles[tc.priority]}`}>{tc.priority}</span>
                {tc.environment && <span className="px-2 py-0.5 rounded-full text-xs bg-blue-50 text-blue-600 font-medium">{tc.environment}</span>}
              </div>
              <h1 className="text-xl font-bold text-gray-900">{tc.title}</h1>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm mb-5">
            {[['Project', tc.projectName || '—'], ['Module', tc.module || '—'],
              ['Executed By', tc.executedBy || '—'], ['Executed At', fmtDateTime(tc.executedAt)]].map(([l, v]) => (
              <div key={l}><span className="text-xs text-gray-400">{l}</span><div className="font-medium text-gray-800">{v}</div></div>
            ))}
          </div>
          {tc.requirementCode && (
            <div className="mb-4 px-3 py-2 bg-purple-50 border border-purple-200 rounded-lg text-sm">
              Linked Requirement: <span className="font-semibold text-purple-700">{tc.requirementCode}</span> — {tc.requirementTitle}
            </div>
          )}
          {tc.prerequisites && <div className="mb-4"><span className="text-xs font-semibold text-gray-500 uppercase">Prerequisites</span><p className="text-sm text-gray-700 mt-1 whitespace-pre-wrap">{tc.prerequisites}</p></div>}

          {/* Test Steps — Per-Step Execution */}
          {steps.length > 0 && (
            <div className="mb-5">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-bold text-gray-700">Test Steps ({steps.length})</h3>
                <div className="flex items-center gap-2 text-xs">
                  {(() => { const sr = Object.values(stepResults); const p = sr.filter(r => r.status === 'pass').length; const f = sr.filter(r => r.status === 'fail').length; const b = sr.filter(r => r.status === 'blocked').length; return (<><span className="text-green-600 font-semibold">{p} passed</span><span className="text-red-600 font-semibold">{f} failed</span><span className="text-orange-600 font-semibold">{b} blocked</span><span className="text-gray-400">{steps.length - p - f - b} pending</span></>); })()}
                </div>
              </div>
              <div className="space-y-3">
                {steps.map((s, i) => {
                  const sr = stepResults[i];
                  const sa = stepAttachments[i] || [];
                  const form = stepForms[i] || {};
                  const isExpanded = expandedSteps[i];
                  const stepStatus = sr?.status || 'pending';
                  const stepBorder = stepStatus === 'pass' ? 'border-green-300 bg-green-50/30' : stepStatus === 'fail' ? 'border-red-300 bg-red-50/30' : stepStatus === 'blocked' ? 'border-orange-300 bg-orange-50/30' : 'border-gray-200';
                  return (
                    <div key={i} className={`border rounded-lg ${stepBorder} overflow-hidden`}>
                      {/* Step Header */}
                      <div className="flex items-center gap-3 px-4 py-3 cursor-pointer" onClick={() => setExpandedSteps(p => ({ ...p, [i]: !p[i] }))}>
                        <span className="text-xs font-mono font-bold text-gray-400 w-6 shrink-0">{i + 1}</span>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold shrink-0 ${statusStyles[stepStatus]}`}>{statusLabels[stepStatus]}</span>
                        <div className="flex-1 min-w-0">
                          <span className="text-sm font-medium text-gray-800">{s.action}</span>
                          {s.expected && <span className="text-xs text-gray-400 ml-2">Expected: {s.expected}</span>}
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          {sa.length > 0 && <span className="text-xs text-gray-400">{sa.length} file{sa.length > 1 ? 's' : ''}</span>}
                          <span className="text-gray-400 text-xs">{isExpanded ? '▲' : '▼'}</span>
                        </div>
                      </div>

                      {/* Expanded Content */}
                      {isExpanded && (
                        <div className="border-t border-gray-200 px-4 py-3 space-y-3 bg-white">
                          <div className="grid grid-cols-2 gap-4">
                            <div><span className="text-xs font-semibold text-gray-500 uppercase block mb-0.5">Action</span><p className="text-sm text-gray-700 whitespace-pre-wrap">{s.action}</p></div>
                            <div><span className="text-xs font-semibold text-gray-500 uppercase block mb-0.5">Expected Result</span><p className="text-sm text-gray-600 whitespace-pre-wrap">{s.expected || '—'}</p></div>
                          </div>

                          {/* Previous result display */}
                          {sr && (sr.actualResult || sr.comment) && (
                            <div className="bg-gray-50 rounded-lg p-3 space-y-1">
                              {sr.actualResult && <div><span className="text-xs font-semibold text-gray-500">Actual Result:</span><p className="text-sm text-gray-700">{sr.actualResult}</p></div>}
                              {sr.comment && <div><span className="text-xs font-semibold text-gray-500">Comment:</span><p className="text-sm text-gray-700">{sr.comment}</p></div>}
                              <div className="text-xs text-gray-400">{sr.executedBy} · {fmtDateTime(sr.executedAt)}</div>
                            </div>
                          )}

                          {/* Attachments */}
                          {sa.length > 0 && (
                            <div>
                              <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Attachments</span>
                              <div className="flex flex-wrap gap-2">
                                {sa.map(a => {
                                  const isImage = (a.fileType || '').startsWith('image/');
                                  const dataUrl = a.fileData
                                    ? (a.fileData.startsWith('data:') ? a.fileData : `data:${a.fileType || 'application/octet-stream'};base64,${a.fileData}`)
                                    : `/api/tcms/step-attachments/${a.id}/download`;
                                  const open = (e) => { e.stopPropagation(); setPreviewAttach({ ...a, dataUrl, isImage }); };
                                  return (
                                    <div key={a.id} className="relative group">
                                      {isImage ? (
                                        <button type="button" onClick={open} title={a.fileName} className="block">
                                          <img src={dataUrl} alt={a.fileName} className="w-16 h-16 object-cover rounded border border-gray-200 cursor-pointer hover:opacity-80" />
                                        </button>
                                      ) : (() => { const d = getFileDisplay(a.fileName, a.fileType); return (
                                        <button type="button" onClick={open} title={a.fileName}
                                          className={`w-16 h-16 flex flex-col items-center justify-center ${d.bg} border ${d.border} rounded ${d.text} text-xs font-medium ${d.hover} cursor-pointer`}>
                                          {d.label}<span className="text-gray-400 text-[10px] truncate max-w-[56px] mt-0.5">{a.fileName}</span>
                                        </button>
                                      ); })()}
                                      <button onClick={(e) => { e.stopPropagation(); handleDeleteStepAttach(a.id, tc.id); }}
                                        className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white rounded-full text-[10px] hidden group-hover:flex items-center justify-center leading-none" title="Remove">×</button>
                                      <div className="text-[10px] text-gray-400 text-center mt-0.5 w-16 truncate">{Math.round((a.fileSize || 0) / 1024)}KB</div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                          {/* Execution Form */}
                          <div className="space-y-2">
                            <div>
                              <label className="text-xs text-gray-500 font-medium">Actual Result</label>
                              <textarea value={form.actualResult || ''} onChange={e => setStepForms(f => ({ ...f, [i]: { ...f[i], actualResult: e.target.value } }))} rows={2}
                                placeholder="What actually happened..." className="w-full mt-0.5 px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-400 focus:outline-none" />
                            </div>
                            <div>
                              <label className="text-xs text-gray-500 font-medium">Comment</label>
                              <input value={form.comment || ''} onChange={e => setStepForms(f => ({ ...f, [i]: { ...f[i], comment: e.target.value } }))}
                                placeholder="Optional comment..." className="w-full mt-0.5 px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-400 focus:outline-none" />
                            </div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <button onClick={() => handleStepExecute(tc.id, i, 'pass')} disabled={savingStep === i}
                                className="px-3 py-1.5 bg-green-600 text-white text-xs rounded-lg hover:bg-green-700 disabled:opacity-50 font-medium">Pass</button>
                              <button onClick={() => handleStepExecute(tc.id, i, 'fail')} disabled={savingStep === i}
                                className="px-3 py-1.5 bg-red-600 text-white text-xs rounded-lg hover:bg-red-700 disabled:opacity-50 font-medium">Fail</button>
                              <button onClick={() => handleStepExecute(tc.id, i, 'blocked')} disabled={savingStep === i}
                                className="px-3 py-1.5 bg-orange-500 text-white text-xs rounded-lg hover:bg-orange-600 disabled:opacity-50 font-medium">Blocked</button>
                              {stepStatus !== 'pending' && (
                                <button onClick={() => handleStepExecute(tc.id, i, 'pending')} disabled={savingStep === i}
                                  className="px-3 py-1.5 border border-gray-300 text-gray-500 text-xs rounded-lg hover:bg-gray-50 disabled:opacity-50">Reset</button>
                              )}
                              <label className="px-3 py-1.5 border border-gray-300 text-gray-600 text-xs rounded-lg hover:bg-gray-50 cursor-pointer font-medium">
                                📎 Attach
                                <input type="file" className="hidden" accept=".jpg,.jpeg,.png,.gif,.webp,.pdf"
                                  onChange={e => { if (e.target.files[0]) handleStepAttachUpload(tc.id, i, e.target.files[0]); e.target.value = ''; }} />
                              </label>
                              {savingStep === i && <span className="text-xs text-gray-400">Saving...</span>}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Overall Status Summary */}
          <div className="border-t border-gray-200 pt-4 mt-4 flex items-center gap-3 flex-wrap">
            <span className="text-sm font-semibold text-gray-700">Overall:</span>
            <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${statusStyles[tc.status]}`}>{statusLabels[tc.status] || tc.status}</span>
            {steps.length === 0 && (
              <div className="flex gap-2">
                <button onClick={() => handleExecute(tc.id, 'pass')} disabled={executing} className="px-3 py-1.5 bg-green-600 text-white text-xs rounded-lg hover:bg-green-700 disabled:opacity-50 font-medium">Pass</button>
                <button onClick={() => handleExecute(tc.id, 'fail')} disabled={executing} className="px-3 py-1.5 bg-red-600 text-white text-xs rounded-lg hover:bg-red-700 disabled:opacity-50 font-medium">Fail</button>
                <button onClick={() => handleExecute(tc.id, 'blocked')} disabled={executing} className="px-3 py-1.5 bg-orange-500 text-white text-xs rounded-lg hover:bg-orange-600 disabled:opacity-50 font-medium">Blocked</button>
              </div>
            )}
            {tc.status !== 'pending' && (
              <button onClick={() => handleExecute(tc.id, 'pending')} disabled={executing}
                className="px-3 py-1.5 border border-gray-300 text-gray-500 text-xs rounded-lg hover:bg-gray-50">Reset All</button>
            )}
            <span className="text-xs text-gray-400 ml-auto">{steps.length > 0 ? 'Overall status auto-derived from step results' : 'No steps — set status manually'}</span>
          </div>
        </div>

        {/* Execution History */}
        {executionHistory.length > 0 && (
          <div className="bg-white rounded-2xl border border-gray-200 shadow p-6">
            <h3 className="text-sm font-bold text-gray-700 mb-3">Execution History</h3>
            <div className="space-y-2">
              {executionHistory.map(h => (
                <div key={h.id} className="flex items-center gap-3 text-sm border-b border-gray-100 pb-2 last:border-0">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${statusStyles[h.status]}`}>{statusLabels[h.status]}</span>
                  <span className="text-gray-600">{h.executedBy}</span>
                  <span className="text-gray-400 text-xs">{fmtDateTime(h.executedAt)}</span>
                  {h.actualResult && <span className="text-gray-500 text-xs truncate max-w-[200px]" title={h.actualResult}>{h.actualResult}</span>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Attachment Preview Modal */}
        {previewAttach && (
          <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setPreviewAttach(null)}>
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[92vh] flex flex-col" onClick={e => e.stopPropagation()}>
              <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-gray-900 truncate">{previewAttach.fileName}</div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    {previewAttach.fileType || 'Unknown type'}
                    {previewAttach.fileSize ? ` · ${Math.round(previewAttach.fileSize / 1024)} KB` : ''}
                    {previewAttach.uploadedBy ? ` · uploaded by ${previewAttach.uploadedBy}` : ''}
                    {previewAttach.createdAt ? ` · ${fmtDateTime(previewAttach.createdAt)}` : ''}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <a href={previewAttach.dataUrl} download={previewAttach.fileName}
                    className="px-3 py-1.5 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 font-medium">↓ Download</a>
                  <button onClick={() => setPreviewAttach(null)} className="text-gray-400 hover:text-gray-600 text-2xl leading-none px-1">×</button>
                </div>
              </div>
              <div className="flex-1 overflow-auto bg-gray-50 flex items-center justify-center p-4">
                {previewAttach.isImage ? (
                  <img src={previewAttach.dataUrl} alt={previewAttach.fileName} className="max-w-full max-h-[75vh] object-contain rounded shadow" />
                ) : (previewAttach.fileType === 'application/pdf' || /\.pdf$/i.test(previewAttach.fileName || '')) ? (
                  <iframe src={previewAttach.dataUrl} title={previewAttach.fileName} className="w-full h-[75vh] bg-white rounded shadow border border-gray-200" />
                ) : (() => { const d = getFileDisplay(previewAttach.fileName, previewAttach.fileType); return (
                  <div className={`flex flex-col items-center justify-center ${d.bg} border ${d.border} rounded-lg p-12 ${d.text}`}>
                    <div className="text-5xl mb-3">{d.emoji}</div>
                    <div className="text-base font-semibold mb-1">{d.label}</div>
                    <div className="text-sm text-gray-600 break-all max-w-md text-center">{previewAttach.fileName}</div>
                    <p className="text-xs text-gray-500 mt-3">No inline preview available for this file type.</p>
                  </div>
                ); })()}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ---- Test Cases List View ----
  if (view === 'cases') {
    // Group by project
    const grouped = {};
    testCases.forEach(tc => {
      const pName = tc.projectName || 'Unassigned';
      if (!grouped[pName]) grouped[pName] = [];
      grouped[pName].push(tc);
    });

    return (
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">🧪 Test Cases</h1>
            <p className="text-sm text-gray-500 mt-1">{testCases.length} test cases across {Object.keys(grouped).length} projects</p>
          </div>
          <button onClick={() => setView('dashboard')} className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">← Dashboard</button>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-5">
          <select value={projectFilter} onChange={e => setProjectFilter(e.target.value)} className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-400">
            <option value="">All Projects</option>
            {projects.map(p => <option key={p.projectId} value={p.projectId}>{p.projectName}</option>)}
          </select>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-400">
            <option value="">All Statuses</option>
            <option value="pending">Pending</option><option value="pass">Passed</option><option value="fail">Failed</option><option value="blocked">Blocked</option>
          </select>
          <select value={priorityFilter} onChange={e => setPriorityFilter(e.target.value)} className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-400">
            <option value="">All Priorities</option>
            <option value="critical">Critical</option><option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option>
          </select>
        </div>

        {testCases.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 shadow p-12 text-center">
            <div className="text-4xl mb-3">🧪</div>
            <p className="text-gray-500">No test cases found. Test cases created in the Quality module will appear here.</p>
          </div>
        ) : (
          Object.entries(grouped).map(([project, tcs]) => (
            <div key={project} className="mb-6">
              <h2 className="text-sm font-bold text-gray-600 uppercase tracking-wide mb-2">{project} ({tcs.length})</h2>
              <div className="bg-white rounded-xl border border-gray-200 shadow overflow-hidden">
                <table className="w-full text-sm">
                  <thead><tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500">Code</th>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500">Title</th>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500">Module</th>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500">Priority</th>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500">Status</th>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500">Executed</th>
                    <th className="px-4 py-2.5 text-right text-xs font-semibold text-gray-500">Actions</th>
                  </tr></thead>
                  <tbody>
                    {tcs.map(tc => (
                      <tr key={tc.id} className="border-t border-gray-100 hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-2.5 font-mono text-xs text-purple-600 font-bold">{tc.tcCode}</td>
                        <td className="px-4 py-2.5 text-gray-800 font-medium">
                          <button onClick={() => openDetail(tc)} className="hover:text-purple-600 text-left">{tc.title}</button>
                          {tc.requirementCode && <span className="ml-1 text-xs text-gray-400">({tc.requirementCode})</span>}
                        </td>
                        <td className="px-4 py-2.5 text-gray-500 text-xs">{tc.module || '—'}</td>
                        <td className="px-4 py-2.5"><span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${priorityStyles[tc.priority]}`}>{tc.priority}</span></td>
                        <td className="px-4 py-2.5"><span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${statusStyles[tc.status]}`}>{statusLabels[tc.status]}</span></td>
                        <td className="px-4 py-2.5 text-xs text-gray-400">{tc.executedBy ? `${tc.executedBy}, ${fmtDate(tc.executedAt)}` : '—'}</td>
                        <td className="px-4 py-2.5 text-right">
                          <div className="flex items-center gap-1 justify-end">
                            {tc.status !== 'pass' && (
                              <>
                                <button onClick={() => handleExecute(tc.id, 'pass')} title="Pass" className="px-2 py-1 text-xs bg-green-50 text-green-700 rounded hover:bg-green-100 font-medium">Pass</button>
                                <button onClick={() => handleExecute(tc.id, 'fail')} title="Fail" className="px-2 py-1 text-xs bg-red-50 text-red-700 rounded hover:bg-red-100 font-medium">Fail</button>
                              </>
                            )}
                            {tc.status === 'pass' && <span className="text-green-500 text-xs font-bold">Passed</span>}
                            <button onClick={() => openDetail(tc)} className="px-2 py-1 text-xs bg-purple-50 text-purple-700 rounded hover:bg-purple-100 font-medium">View</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))
        )}
      </div>
    );
  }

  // ---- Dashboard View (default) ----
  const passBarW = metrics.total > 0 ? (metrics.passed / metrics.total) * 100 : 0;
  const failBarW = metrics.total > 0 ? (metrics.failed / metrics.total) * 100 : 0;
  const blockedBarW = metrics.total > 0 ? (metrics.blocked / metrics.total) * 100 : 0;

  // Recent executions (test cases with executedAt, sorted desc)
  const recentlyExecuted = [...testCases].filter(tc => tc.executedAt).sort((a, b) => new Date(b.executedAt) - new Date(a.executedAt)).slice(0, 8);
  const pendingCases = testCases.filter(tc => tc.status === 'pending');
  const failedCases = testCases.filter(tc => tc.status === 'fail');

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">🧪 Test Case Management System</h1>
          <p className="text-gray-500 text-sm mt-1">Execute test cases, track results — synced with Quality module</p>
        </div>
        <button onClick={() => setView('cases')} className="px-4 py-2 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 font-medium">View All Test Cases</button>
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
        {[
          { label: 'Total', value: metrics.total || 0, color: '#6b7280', bg: '#f9fafb' },
          { label: 'Passed', value: metrics.passed || 0, color: '#16a34a', bg: '#f0fdf4' },
          { label: 'Failed', value: metrics.failed || 0, color: '#dc2626', bg: '#fef2f2' },
          { label: 'Blocked', value: metrics.blocked || 0, color: '#ea580c', bg: '#fff7ed' },
          { label: 'Pending', value: metrics.pending || 0, color: '#6b7280', bg: '#f9fafb' },
          { label: 'Pass Rate', value: `${metrics.passRate || 0}%`, color: '#7c3aed', bg: '#faf5ff' },
        ].map(m => (
          <div key={m.label} className="rounded-xl border border-gray-200 shadow-sm p-4" style={{ background: m.bg }}>
            <div className="text-xs font-medium text-gray-500 mb-1">{m.label}</div>
            <div className="text-2xl font-bold" style={{ color: m.color }}>{m.value}</div>
          </div>
        ))}
      </div>

      {/* Progress Bar */}
      {metrics.total > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold text-gray-700">Overall Execution Progress</span>
            <span className="text-sm text-gray-500">{metrics.passed + metrics.failed + metrics.blocked} / {metrics.total} executed</span>
          </div>
          <div className="h-4 bg-gray-100 rounded-full overflow-hidden flex">
            {passBarW > 0 && <div className="h-full bg-green-500 transition-all" style={{ width: `${passBarW}%` }} />}
            {failBarW > 0 && <div className="h-full bg-red-500 transition-all" style={{ width: `${failBarW}%` }} />}
            {blockedBarW > 0 && <div className="h-full bg-orange-400 transition-all" style={{ width: `${blockedBarW}%` }} />}
          </div>
          <div className="flex gap-4 mt-2 text-xs">
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-green-500" /> Passed ({metrics.passed})</span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-red-500" /> Failed ({metrics.failed})</span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-orange-400" /> Blocked ({metrics.blocked})</span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-gray-200" /> Pending ({metrics.pending})</span>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Failed Cases - Needs Attention */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
          <h3 className="text-sm font-bold text-red-700 mb-3 flex items-center gap-1">Failed Test Cases ({failedCases.length})</h3>
          {failedCases.length === 0 ? <p className="text-xs text-gray-400">No failed test cases</p> : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {failedCases.slice(0, 10).map(tc => (
                <button key={tc.id} onClick={() => openDetail(tc)} className="w-full text-left flex items-center justify-between px-3 py-2 rounded-lg hover:bg-red-50 border border-gray-100 transition-colors">
                  <div className="min-w-0">
                    <span className="text-xs font-mono text-gray-400 mr-1">{tc.tcCode}</span>
                    <span className="text-sm font-medium text-gray-800 truncate">{tc.title}</span>
                  </div>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold shrink-0 ${priorityStyles[tc.priority]}`}>{tc.priority}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Pending Cases - To Execute */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
          <h3 className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-1">Pending Execution ({pendingCases.length})</h3>
          {pendingCases.length === 0 ? <p className="text-xs text-gray-400">All test cases have been executed</p> : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {pendingCases.slice(0, 10).map(tc => (
                <div key={tc.id} className="flex items-center justify-between px-3 py-2 rounded-lg border border-gray-100">
                  <button onClick={() => openDetail(tc)} className="min-w-0 text-left hover:text-purple-600">
                    <span className="text-xs font-mono text-gray-400 mr-1">{tc.tcCode}</span>
                    <span className="text-sm font-medium text-gray-800">{tc.title}</span>
                  </button>
                  <div className="flex gap-1 shrink-0 ml-2">
                    <button onClick={() => handleExecute(tc.id, 'pass')} className="px-2 py-1 text-xs bg-green-50 text-green-700 rounded hover:bg-green-100 font-medium">Pass</button>
                    <button onClick={() => handleExecute(tc.id, 'fail')} className="px-2 py-1 text-xs bg-red-50 text-red-700 rounded hover:bg-red-100 font-medium">Fail</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recently Executed */}
        {recentlyExecuted.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 lg:col-span-2">
            <h3 className="text-sm font-bold text-gray-700 mb-3">Recent Executions</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {recentlyExecuted.map(tc => (
                <button key={tc.id} onClick={() => openDetail(tc)} className="text-left flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-50 border border-gray-100 transition-colors">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold shrink-0 ${statusStyles[tc.status]}`}>{statusLabels[tc.status]}</span>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-gray-800 truncate">{tc.tcCode} — {tc.title}</div>
                    <div className="text-xs text-gray-400">{tc.executedBy} · {fmtDateTime(tc.executedAt)}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const RequestBoardModule = ({ user }) => {
  const [requests, setRequests] = React.useState([]);
  const [stats, setStats] = React.useState({ total: 0, pending: 0, approved: 0, rejected: 0, inprogress: 0, completed: 0, todayCount: 0, byType: [], byDept: [] });
  const [categories, setCategories] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [view, setView] = React.useState('dashboard');
  const [viewingRequest, setViewingRequest] = React.useState(null);
  const [showForm, setShowForm] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const [filterStatus, setFilterStatus]   = React.useState('');
  const [filterType, setFilterType]       = React.useState('');
  const [filterPriority, setFilterPriority] = React.useState('');

  const [bulkSelected, setBulkSelected] = React.useState(new Set());
  const [bulkConverting, setBulkConverting] = React.useState(false);

  const role = user?.role || user?.user_role;
  const canManage = ['admin', 'manager', 'devops_manager', 'service_desk_manager'].includes(role);

  const load = React.useCallback(async () => {
    setLoading(true);
    const [r, s, c] = await Promise.all([
      ModulesModel.getRequests({ search, status: filterStatus, type: filterType, priority: filterPriority }),
      ModulesModel.getRequestStats(),
      fetch('/api/teams', { headers: { Authorization: `Bearer ${localStorage.getItem('orbit_token')}` } }).then(r => r.json()).catch(() => []),
    ]);
    const teams = (Array.isArray(c) ? c : []).map(t => ({ id: t.id, name: t.team_name || t.name }));
    setRequests(r || []); setStats(s); setCategories(teams);
    setLoading(false); setBulkSelected(new Set());
  }, [search, filterStatus, filterType, filterPriority]);

  const handleBulkConvert = async (targetModule) => {
    if (bulkSelected.size === 0) return;
    if (!confirm(`Convert ${bulkSelected.size} request(s) to QuickTrack tasks?`)) return;
    setBulkConverting(true);
    const token = localStorage.getItem('orbit_token');
    const res = await fetch('/api/requests/bulk-convert', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify({ requestIds: [...bulkSelected], target_module: targetModule })
    });
    const d = await res.json();
    setBulkConverting(false);
    if (d.success) { alert(`Converted ${d.converted} request(s) to QuickTrack. ${d.failed > 0 ? `${d.failed} failed.` : ''}`); load(); }
  };

  React.useEffect(() => { load(); }, [load]);

  const handleSubmit = async (data) => {
    await ModulesModel.createRequest(data);
    setShowForm(false); load();
  };

  if (viewingRequest) {
    return <RequestDetailView requestId={viewingRequest} categories={categories} user={user} onBack={() => setViewingRequest(null)} onRefresh={load} />;
  }

  const statCards = [
    { label: 'Total',        value: stats.total,      color: 'text-gray-800',   bg: 'bg-gray-50',    border: 'border-gray-200' },
    { label: 'Pending',      value: stats.pending,    color: 'text-blue-700',   bg: 'bg-blue-50',    border: 'border-blue-200' },
    { label: 'Approved',     value: stats.approved,   color: 'text-green-700',  bg: 'bg-green-50',   border: 'border-green-200' },
    { label: 'In Progress',  value: stats.inprogress, color: 'text-purple-700', bg: 'bg-purple-50',  border: 'border-purple-200' },
    { label: 'Rejected',     value: stats.rejected,   color: 'text-red-600',    bg: 'bg-red-50',     border: 'border-red-200' },
    { label: 'Today',        value: stats.todayCount, color: 'text-emerald-700',bg: 'bg-emerald-50', border: 'border-emerald-200' },
  ];

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">📥 RequestBoard</h1>
          <p className="text-sm text-gray-500 mt-1">Central intake for all internal requests</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex bg-gray-100 rounded-lg p-1">
            {[{id:'dashboard',label:'Dashboard'},{id:'requests',label:'All Requests'}].map(v => (
              <button key={v.id} onClick={() => setView(v.id)} className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${view === v.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>{v.label}</button>
            ))}
          </div>
          <button onClick={() => setShowForm(true)} className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700">+ New Request</button>
        </div>
      </div>

      {view === 'dashboard' ? (
        <>
          {/* Stat Cards */}
          <div className="grid grid-cols-3 md:grid-cols-6 gap-4 mb-6">
            {statCards.map(s => (
              <div key={s.label} className={`${s.bg} border ${s.border} rounded-xl p-4`}>
                <div className={`text-2xl font-bold ${s.color}`}>{s.value}</div>
                <div className="text-xs text-gray-500 mt-1">{s.label}</div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
            {/* By Type */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow p-5">
              <h3 className="font-semibold text-gray-800 mb-3 text-sm">Requests by Type</h3>
              <div className="space-y-2">
                {stats.byType.length === 0 ? <p className="text-sm text-gray-400">No data yet.</p> : stats.byType.map(t => (
                  <div key={t.request_type} className="flex items-center gap-2">
                    <div className="flex-1 text-xs text-gray-600 truncate">{t.request_type}</div>
                    <div className="text-xs font-semibold text-gray-800 w-6 text-right">{t.n}</div>
                    <div className="w-20 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${Math.min(100, (t.n / (stats.total || 1)) * 100)}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* By Department */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow p-5">
              <h3 className="font-semibold text-gray-800 mb-3 text-sm">Requests by Department</h3>
              <div className="space-y-2">
                {stats.byDept.length === 0 ? <p className="text-sm text-gray-400">No data yet.</p> : stats.byDept.map(d => (
                  <div key={d.department} className="flex items-center gap-2">
                    <div className="flex-1 text-xs text-gray-600 truncate">{d.department}</div>
                    <div className="text-xs font-semibold text-gray-800 w-6 text-right">{d.n}</div>
                    <div className="w-20 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full bg-blue-400 rounded-full" style={{ width: `${Math.min(100, (d.n / (stats.total || 1)) * 100)}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Pending Approval */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow p-5">
              <h3 className="font-semibold text-gray-800 mb-3 text-sm">Awaiting Review</h3>
              <div className="space-y-2">
                {requests.filter(r => r.status === 'Submitted').length === 0
                  ? <p className="text-sm text-gray-400">No pending requests.</p>
                  : requests.filter(r => r.status === 'Submitted').slice(0, 5).map(r => (
                    <button key={r.id} onClick={() => setViewingRequest(r.id)} className="w-full text-left p-2 rounded-lg hover:bg-gray-50 border border-gray-100">
                      <div className="text-xs font-medium text-gray-800 truncate">{r.title}</div>
                      <div className="text-xs text-gray-400 mt-0.5">{r.requested_by} · <span className={`font-medium ${reqPriorityStyle[r.priority]?.split(' ')[1]}`}>{r.priority}</span></div>
                    </button>
                  ))
                }
              </div>
            </div>
          </div>

          {/* Recent requests */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
              <h2 className="font-semibold text-gray-800">Recent Requests</h2>
              <button onClick={() => setView('requests')} className="text-sm text-emerald-600 hover:underline">View all →</button>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>{['#','Title','Type','Priority','Requester','Dept','Status','Date'].map(h => <th key={h} className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase">{h}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading ? <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-400">Loading…</td></tr>
                : requests.slice(0, 10).map(r => (
                  <tr key={r.id} onClick={() => setViewingRequest(r.id)} className="hover:bg-gray-50 cursor-pointer">
                    <td className="px-4 py-3 text-gray-400 text-xs font-mono">#{r.id}</td>
                    <td className="px-4 py-3 font-medium text-gray-900 max-w-[200px] truncate">{r.title}</td>
                    <td className="px-4 py-3 text-gray-500 text-xs">{r.request_type}</td>
                    <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${reqPriorityStyle[r.priority] || 'bg-gray-100 text-gray-600'}`}>{r.priority}</span></td>
                    <td className="px-4 py-3 text-gray-600">{r.requested_by}</td>
                    <td className="px-4 py-3 text-gray-400 text-xs">{r.department || '—'}</td>
                    <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${reqStatusStyle[r.status] || 'bg-gray-100 text-gray-600'}`}>{r.status}</span></td>
                    <td className="px-4 py-3 text-gray-400 text-xs whitespace-nowrap">{r.created_at ? new Date(r.created_at).toLocaleDateString() : '—'}</td>
                  </tr>
                ))}
                {!loading && requests.length === 0 && <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-400">No requests yet. Click "+ New Request" to submit one.</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        /* All Requests View */
        <div>
          <div className="flex flex-wrap gap-3 mb-4">
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search title or description…" className="flex-1 min-w-48 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400" />
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none">
              <option value="">All Statuses</option>
              {REQ_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <select value={filterType} onChange={e => setFilterType(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none">
              <option value="">All Types</option>
              {REQ_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <select value={filterPriority} onChange={e => setFilterPriority(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none">
              <option value="">All Priorities</option>
              {REQ_PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
            {(search || filterStatus || filterType || filterPriority) && (
              <button onClick={() => { setSearch(''); setFilterStatus(''); setFilterType(''); setFilterPriority(''); }} className="px-3 py-2 text-sm text-gray-500 border border-gray-300 rounded-lg hover:bg-gray-50">Clear</button>
            )}
          </div>

          {/* Bulk action bar */}
          {canManage && bulkSelected.size > 0 && (
            <div className="flex items-center gap-3 mb-3 px-4 py-2.5 bg-purple-50 border border-purple-200 rounded-xl">
              <span className="text-sm font-semibold text-purple-800">{bulkSelected.size} selected</span>
              <button onClick={() => handleBulkConvert('quicktrack')} disabled={bulkConverting}
                className="px-3 py-1.5 text-xs bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 font-medium">
                {bulkConverting ? 'Converting…' : 'Convert to QuickTrack'}
              </button>
              <button onClick={() => setBulkSelected(new Set())} className="px-3 py-1.5 text-xs text-gray-500 border border-gray-300 rounded-lg hover:bg-white">Clear</button>
            </div>
          )}

          <div className="bg-white rounded-2xl border border-gray-200 shadow overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {canManage && <th className="px-3 py-3 w-8"><input type="checkbox"
                    checked={requests.length > 0 && requests.filter(r => !['Rejected','Completed'].includes(r.status)).every(r => bulkSelected.has(r.id))}
                    onChange={e => { const convertible = requests.filter(r => !['Rejected','Completed'].includes(r.status)); setBulkSelected(e.target.checked ? new Set(convertible.map(r => r.id)) : new Set()); }}
                    className="w-3.5 h-3.5 rounded accent-purple-600" /></th>}
                  {['#','Title','Type','Priority','Requester','Dept','Assigned','Status','Date'].map(h => <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">{h}</th>)}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading ? <tr><td colSpan={canManage ? 10 : 9} className="px-4 py-8 text-center text-gray-400">Loading…</td></tr>
                : requests.length === 0 ? <tr><td colSpan={canManage ? 10 : 9} className="px-4 py-8 text-center text-gray-400">No requests found.</td></tr>
                : requests.map(r => {
                  const convertible = !['Rejected','Completed'].includes(r.status);
                  return (
                  <tr key={r.id} className="hover:bg-gray-50 cursor-pointer">
                    {canManage && <td className="px-3 py-3" onClick={e => e.stopPropagation()}>
                      {convertible && <input type="checkbox" checked={bulkSelected.has(r.id)}
                        onChange={e => { const s = new Set(bulkSelected); if (e.target.checked) s.add(r.id); else s.delete(r.id); setBulkSelected(s); }}
                        className="w-3.5 h-3.5 rounded accent-purple-600" />}
                      {r.converted_module && <span className="text-[9px] font-bold text-purple-500" title={`Converted to ${r.converted_module}`}>QT</span>}
                    </td>}
                    <td className="px-4 py-3 text-gray-400 text-xs font-mono" onClick={() => setViewingRequest(r.id)}>#{r.id}</td>
                    <td className="px-4 py-3 font-medium text-gray-900 max-w-[180px] truncate" onClick={() => setViewingRequest(r.id)}>{r.title}</td>
                    <td className="px-4 py-3 text-xs text-gray-500" onClick={() => setViewingRequest(r.id)}>{r.request_type}</td>
                    <td className="px-4 py-3" onClick={() => setViewingRequest(r.id)}><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${reqPriorityStyle[r.priority] || 'bg-gray-100 text-gray-600'}`}>{r.priority}</span></td>
                    <td className="px-4 py-3 text-gray-600" onClick={() => setViewingRequest(r.id)}>{r.requested_by}</td>
                    <td className="px-4 py-3 text-gray-400 text-xs" onClick={() => setViewingRequest(r.id)}>{r.department || '—'}</td>
                    <td className="px-4 py-3 text-gray-400 text-xs" onClick={() => setViewingRequest(r.id)}>{r.assigned_to || '—'}</td>
                    <td className="px-4 py-3" onClick={() => setViewingRequest(r.id)}><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${reqStatusStyle[r.status] || 'bg-gray-100 text-gray-600'}`}>{r.status}</span></td>
                    <td className="px-4 py-3 text-gray-400 text-xs whitespace-nowrap" onClick={() => setViewingRequest(r.id)}>{r.created_at ? new Date(r.created_at).toLocaleDateString() : '—'}</td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="px-4 py-2 bg-gray-50 border-t text-xs text-gray-400">{requests.length} request{requests.length !== 1 ? 's' : ''}</div>
          </div>
        </div>
      )}

      {showForm && <RequestFormModal categories={categories} user={user} onSave={handleSubmit} onClose={() => setShowForm(false)} />}
    </div>
  );
};

// ============================================================================
// ASSETBOARD MODULE
// ============================================================================

const ASSET_STATUSES = ['Available', 'Assigned', 'In Maintenance', 'Retired'];
const assetStatusStyle = {
  'Available':      'bg-green-100 text-green-700',
  'Assigned':       'bg-blue-100 text-blue-700',
  'In Maintenance': 'bg-yellow-100 text-yellow-700',
  'Retired':        'bg-gray-100 text-gray-500',
};

function AssetFormModal({ asset, categories, onSave, onClose }) {
  const isEdit = !!asset?.id;
  const [form, setForm] = React.useState({
    asset_name: asset?.asset_name || '',
    category_id: asset?.category_id || '',
    asset_tag: asset?.asset_tag || '',
    serial_number: asset?.serial_number || '',
    purchase_date: asset?.purchase_date || '',
    warranty_expiration: asset?.warranty_expiration || '',
    status: asset?.status || 'Available',
    current_owner: asset?.current_owner || '',
    location: asset?.location || '',
    notes: asset?.notes || '',
  });
  const [saving, setSaving] = React.useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.asset_name.trim()) return;
    setSaving(true);
    await onSave({ ...form, category_id: form.category_id || null });
    setSaving(false);
  };

  const inp = 'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400';
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white">
          <h2 className="text-lg font-semibold">{isEdit ? 'Edit Asset' : 'New Asset'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
        </div>
        <div className="p-6 grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <label className="block text-xs font-medium text-gray-600 mb-1">Asset Name *</label>
            <input value={form.asset_name} onChange={e => set('asset_name', e.target.value)} className={inp} placeholder="e.g. MacBook Pro 16" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Category</label>
            <select value={form.category_id} onChange={e => set('category_id', e.target.value)} className={inp}>
              <option value="">Select category...</option>
              {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
            <select value={form.status} onChange={e => set('status', e.target.value)} className={inp}>
              {ASSET_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Asset Tag</label>
            <input value={form.asset_tag} onChange={e => set('asset_tag', e.target.value)} className={inp} placeholder="Auto-generated if blank" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Serial Number</label>
            <input value={form.serial_number} onChange={e => set('serial_number', e.target.value)} className={inp} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Purchase Date</label>
            <input type="date" value={form.purchase_date} onChange={e => set('purchase_date', e.target.value)} className={inp} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Warranty Expiration</label>
            <input type="date" value={form.warranty_expiration} onChange={e => set('warranty_expiration', e.target.value)} className={inp} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Current Owner</label>
            <input value={form.current_owner} onChange={e => set('current_owner', e.target.value)} className={inp} placeholder="Username" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Location</label>
            <input value={form.location} onChange={e => set('location', e.target.value)} className={inp} placeholder="e.g. Office A, Remote" />
          </div>
          <div className="col-span-2">
            <label className="block text-xs font-medium text-gray-600 mb-1">Notes</label>
            <textarea value={form.notes} onChange={e => set('notes', e.target.value)} rows={2} className={inp} />
          </div>
        </div>
        <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3 sticky bottom-0 bg-white">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
          <button onClick={handleSave} disabled={saving || !form.asset_name.trim()} className="px-5 py-2 bg-cyan-600 text-white text-sm rounded-lg hover:bg-cyan-700 disabled:opacity-50">
            {saving ? 'Saving…' : isEdit ? 'Update' : 'Create Asset'}
          </button>
        </div>
      </div>
    </div>
  );
}

function AssignModal({ asset, onSave, onClose }) {
  const [assignTo, setAssignTo] = React.useState(asset?.current_owner || '');
  const [notes, setNotes] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const handleSave = async () => {
    if (!assignTo.trim()) return;
    setSaving(true);
    await onSave({ asset_id: asset.id, assigned_to: assignTo, notes });
    setSaving(false);
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="font-semibold text-gray-900">Assign Asset</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">✕</button>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-sm text-gray-600">Assigning: <strong>{asset?.asset_name}</strong></p>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Assign To (username) *</label>
            <input value={assignTo} onChange={e => setAssignTo(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400" placeholder="Enter username" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Notes</label>
            <input value={notes} onChange={e => setNotes(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400" />
          </div>
        </div>
        <div className="px-5 py-3 border-t border-gray-100 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-sm text-gray-600">Cancel</button>
          <button onClick={handleSave} disabled={saving || !assignTo.trim()} className="px-4 py-1.5 bg-cyan-600 text-white text-sm rounded-lg hover:bg-cyan-700 disabled:opacity-50">
            {saving ? 'Assigning…' : 'Assign'}
          </button>
        </div>
      </div>
    </div>
  );
}

function AssetDetailView({ assetId, categories, user, onBack, onEdit, onRefresh }) {
  const [asset, setAsset] = React.useState(null);
  const [history, setHistory] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [showAssign, setShowAssign] = React.useState(false);
  const canManage = ['admin', 'manager'].includes(user?.role || user?.user_role);

  const load = React.useCallback(async () => {
    setLoading(true);
    const [a, h] = await Promise.all([ModulesModel.getAsset(assetId), ModulesModel.getAssetHistory(assetId)]);
    setAsset(a); setHistory(h);
    setLoading(false);
  }, [assetId]);

  React.useEffect(() => { load(); }, [load]);

  const handleAssign = async (data) => {
    await ModulesModel.assignAsset(data);
    setShowAssign(false); load(); onRefresh();
  };
  const handleReturn = async () => {
    if (!confirm('Mark this asset as returned?')) return;
    await ModulesModel.returnAsset({ asset_id: assetId });
    load(); onRefresh();
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-gray-400">Loading…</div>;
  if (!asset) return <div className="p-6 text-gray-500">Asset not found.</div>;

  const today = new Date().toISOString().split('T')[0];
  const warrantyDays = asset.warranty_expiration ? Math.ceil((new Date(asset.warranty_expiration) - new Date(today)) / 86400000) : null;
  const catName = categories.find(c => c.id === asset.category_id)?.name || asset.category_name || '—';

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <button onClick={onBack} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800 mb-4">← Back to Assets</button>

      <div className="bg-white rounded-2xl shadow border border-gray-200 p-6 mb-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{asset.asset_name}</h1>
            <p className="text-sm text-gray-500 mt-1">{asset.asset_tag} · {catName}</p>
          </div>
          <div className="flex items-center gap-2">
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${assetStatusStyle[asset.status] || 'bg-gray-100 text-gray-600'}`}>{asset.status}</span>
            {canManage && (
              <>
                <button onClick={() => onEdit(asset)} className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-sm rounded-lg">Edit</button>
                {asset.status !== 'Assigned' && <button onClick={() => setShowAssign(true)} className="px-3 py-1.5 bg-cyan-600 text-white text-sm rounded-lg hover:bg-cyan-700">Assign</button>}
                {asset.status === 'Assigned' && <button onClick={handleReturn} className="px-3 py-1.5 bg-orange-500 text-white text-sm rounded-lg hover:bg-orange-600">Return</button>}
              </>
            )}
          </div>
        </div>

        {warrantyDays !== null && warrantyDays <= 30 && (
          <div className={`mb-4 px-4 py-2.5 rounded-lg text-sm font-medium flex items-center gap-2 ${warrantyDays < 0 ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-amber-50 text-amber-700 border border-amber-200'}`}>
            ⚠️ {warrantyDays < 0 ? `Warranty expired ${Math.abs(warrantyDays)} days ago` : `Warranty expiring in ${warrantyDays} day${warrantyDays !== 1 ? 's' : ''}`}
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
          {[
            ['Serial Number', asset.serial_number || '—'],
            ['Current Owner', asset.current_owner || '—'],
            ['Location', asset.location || '—'],
            ['Purchase Date', asset.purchase_date || '—'],
            ['Warranty Expires', asset.warranty_expiration || '—'],
            ['Added', asset.created_at ? new Date(asset.created_at).toLocaleDateString() : '—'],
          ].map(([label, val]) => (
            <div key={label} className="bg-gray-50 rounded-lg p-3">
              <div className="text-xs text-gray-500 mb-1">{label}</div>
              <div className="font-medium text-gray-800">{val}</div>
            </div>
          ))}
        </div>
        {asset.notes && <div className="mt-4 p-3 bg-gray-50 rounded-lg text-sm text-gray-600"><span className="font-medium">Notes:</span> {asset.notes}</div>}
      </div>

      <div className="bg-white rounded-2xl shadow border border-gray-200 p-6">
        <h2 className="font-semibold text-gray-800 mb-4">Assignment History</h2>
        {history.length === 0 ? (
          <p className="text-sm text-gray-400">No assignment history.</p>
        ) : (
          <div className="space-y-3">
            {history.map(h => (
              <div key={h.id} className="flex items-start gap-3 pb-3 border-b border-gray-100 last:border-0">
                <div className={`mt-0.5 w-2.5 h-2.5 rounded-full shrink-0 ${h.returned_at ? 'bg-gray-300' : 'bg-green-500'}`} />
                <div className="flex-1 text-sm">
                  <span className="font-medium text-gray-900">{h.assigned_to}</span>
                  <span className="text-gray-500"> · Assigned by {h.assigned_by || '—'} on {new Date(h.assigned_at).toLocaleDateString()}</span>
                  {h.returned_at && <span className="text-gray-400"> · Returned {new Date(h.returned_at).toLocaleDateString()}</span>}
                  {h.notes && <div className="text-gray-400 text-xs mt-0.5">{h.notes}</div>}
                </div>
                {!h.returned_at && <span className="text-xs text-green-600 font-medium bg-green-50 px-2 py-0.5 rounded-full">Active</span>}
              </div>
            ))}
          </div>
        )}
      </div>

      {showAssign && <AssignModal asset={asset} onSave={handleAssign} onClose={() => setShowAssign(false)} />}
    </div>
  );
}

// ============================================================================
// SUPPORTBOARD MODULE
// ============================================================================

const SUPPORT_TICKET_TYPES = ['IT Support','Access Request','Hardware Issue','Software Issue','Network Issue','Account Issue'];
const SUPPORT_PRIORITIES = ['Urgent','High','Medium','Low'];
const SUPPORT_STATUSES = ['Open','Assigned','In Progress','Waiting for User','Resolved','Closed'];
const SUPPORT_QUEUES = ['IT Helpdesk','Infrastructure','Security','Access Management'];

const SUPPORT_PRIORITY_STYLES = {
  Urgent:  { bg: '#fef2f2', color: '#dc2626', border: '#fecaca' },
  High:    { bg: '#fff7ed', color: '#c2410c', border: '#fed7aa' },
  Medium:  { bg: '#fffbeb', color: '#b45309', border: '#fde68a' },
  Low:     { bg: '#f0fdf4', color: '#15803d', border: '#bbf7d0' },
};

const SUPPORT_STATUS_STYLES = {
  'Open':              { bg: '#eff6ff', color: '#1d4ed8' },
  'Assigned':          { bg: '#f5f3ff', color: '#6d28d9' },
  'In Progress':       { bg: '#fef3c7', color: '#92400e' },
  'Waiting for User':  { bg: '#fff7ed', color: '#c2410c' },
  'Resolved':          { bg: '#f0fdf4', color: '#15803d' },
  'Closed':            { bg: '#f9fafb', color: '#6b7280' },
};

const SlaChip = ({ status, dueAt, label = '', compact = false }) => {
  const [now, setNow] = React.useState(new Date());
  React.useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(t);
  }, []);
  if (!dueAt && !status) return null;
  const due = dueAt ? new Date(dueAt) : null;
  const isBreached = status === 'breached' || (due && now > due);
  const minsLeft = due ? Math.round((due - now) / 60000) : null;
  const hoursLeft = minsLeft !== null ? Math.floor(Math.abs(minsLeft) / 60) : null;
  const minsRem = minsLeft !== null ? Math.abs(minsLeft) % 60 : null;
  const timeStr = hoursLeft !== null ? (hoursLeft > 0 ? `${hoursLeft}h ${minsRem}m` : `${minsRem}m`) : null;
  const isWarning = !isBreached && minsLeft !== null && minsLeft < 60;

  if (compact) {
    return (
      <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-semibold ${isBreached ? 'bg-red-100 text-red-700' : isWarning ? 'bg-orange-100 text-orange-700' : 'bg-green-100 text-green-700'}`}>
        {isBreached ? '🔴' : isWarning ? '⚠️' : '🟢'} {timeStr ? (isBreached ? `-${timeStr}` : timeStr) : (isBreached ? 'Breached' : 'On-time')}
      </span>
    );
  }
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${isBreached ? 'bg-red-100 text-red-700 border border-red-200' : isWarning ? 'bg-orange-100 text-orange-700 border border-orange-200' : 'bg-green-100 text-green-700 border border-green-200'}`}>
      <span>{isBreached ? '🔴' : isWarning ? '⚠️' : '🟢'}</span>
      <span>{label || (isBreached ? 'SLA Breached' : isWarning ? 'SLA Warning' : 'On-time')}</span>
      {timeStr && <span className="opacity-75">{isBreached ? `(${timeStr} ago)` : `(${timeStr} left)`}</span>}
    </span>
  );
};

const EscalationBadge = ({ level }) => {
  if (!level || level === 0) return null;
  const cfg = [
    null,
    { label: 'L1 Escalated', color: 'bg-yellow-100 text-yellow-800 border-yellow-300' },
    { label: 'L2 Escalated', color: 'bg-orange-100 text-orange-800 border-orange-300' },
    { label: '🚨 L3 Critical', color: 'bg-red-100 text-red-800 border-red-300' },
  ];
  const c = cfg[Math.min(level, 3)];
  if (!c) return null;
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold border ${c.color}`}>{c.label}</span>;
};

const RoutingBadge = ({ isAutoRouted }) => {
  if (!isAutoRouted) return null;
  return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-teal-50 text-teal-700 border border-teal-200">🤖 Auto-routed</span>;
};

const SlaCountdownBar = ({ responseDueAt, resolutionDueAt, firstResponseAt, resolvedAt }) => {
  const [now, setNow] = React.useState(new Date());
  React.useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(t);
  }, []);

  const renderBar = (label, dueAt, completedAt) => {
    if (!dueAt) return null;
    const due = new Date(dueAt);
    const isBreached = !completedAt && now > due;
    const isDone = !!completedAt;
    const pct = isDone ? 100 : isBreached ? 100 : Math.min(100, Math.round(((now - (due - (due - now + (due - now)))) / (due.getTime() - (due.getTime() - 86400000))) * 100));
    const minsLeft = isDone ? null : Math.round((due - now) / 60000);
    const timeLabel = isDone ? `Completed ${completedAt?.slice(0,16)}` : isBreached ? `Breached ${Math.abs(Math.round((due - now)/60000))}m ago` : minsLeft > 60 ? `${Math.floor(minsLeft/60)}h ${minsLeft%60}m left` : `${minsLeft}m left`;
    return (
      <div className="mb-2">
        <div className="flex justify-between items-center mb-1">
          <span className="text-xs text-gray-500">{label}</span>
          <span className={`text-xs font-medium ${isDone ? 'text-green-600' : isBreached ? 'text-red-600' : 'text-gray-700'}`}>{timeLabel}</span>
        </div>
        <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
          <div className={`h-full rounded-full transition-all ${isDone ? 'bg-green-500' : isBreached ? 'bg-red-500' : pct > 80 ? 'bg-orange-500' : 'bg-teal-500'}`} style={{ width: `${isDone || isBreached ? 100 : pct}%` }} />
        </div>
      </div>
    );
  };

  return (
    <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
      <div className="text-xs font-semibold text-gray-600 mb-2">SLA Progress</div>
      {renderBar('First Response', responseDueAt, firstResponseAt)}
      {renderBar('Resolution', resolutionDueAt, resolvedAt)}
    </div>
  );
};

const RoutingRulesPanel = ({ onClose }) => {
  const [rules, setRules] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [form, setForm] = React.useState({ keyword: '', target_team: '', target_queue: '', create_incident: false, link_asset: false, priority_override: '' });
  const [saving, setSaving] = React.useState(false);
  const QUEUES = ['IT Helpdesk','Infrastructure','Security','Access Management'];
  const TYPES = ['IT Support','Access Request','Hardware Issue','Software Issue','Network Issue','Account Issue'];
  const load = async () => { setLoading(true); setRules(await ModulesModel.getRoutingRules()); setLoading(false); };
  React.useEffect(() => { load(); }, []);
  const handleAdd = async (e) => {
    e.preventDefault();
    if (!form.keyword.trim()) return;
    setSaving(true);
    await ModulesModel.createRoutingRule(form);
    setForm({ keyword: '', target_team: '', target_queue: '', create_incident: false, link_asset: false, priority_override: '' });
    setSaving(false); load();
  };
  const handleDelete = async (id) => { await ModulesModel.deleteRoutingRule(id); load(); };
  const inp = 'border border-gray-300 rounded px-2 py-1.5 text-sm focus:ring-1 focus:ring-teal-400';
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-lg font-bold text-gray-900">🤖 Routing Rules</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl">×</button>
        </div>
        <div className="overflow-y-auto flex-1 px-6 py-4 space-y-4">
          <p className="text-xs text-gray-500">When a ticket title or description contains a keyword, it is automatically routed to the matching team and queue.</p>
          <form onSubmit={handleAdd} className="grid grid-cols-6 gap-2 items-end bg-gray-50 rounded-lg p-3 border">
            <div className="col-span-1">
              <label className="text-xs font-semibold text-gray-600 block mb-1">Keyword *</label>
              <input value={form.keyword} onChange={e => setForm(f=>({...f,keyword:e.target.value}))} className={`${inp} w-full`} placeholder="e.g. vpn" />
            </div>
            <div className="col-span-1">
              <label className="text-xs font-semibold text-gray-600 block mb-1">Team</label>
              <select value={form.target_team} onChange={e => setForm(f=>({...f,target_team:e.target.value}))} className={`${inp} w-full`}>
                <option value="">—</option>
                {QUEUES.map(q=><option key={q}>{q}</option>)}
              </select>
            </div>
            <div className="col-span-1">
              <label className="text-xs font-semibold text-gray-600 block mb-1">Type</label>
              <select value={form.target_queue} onChange={e => setForm(f=>({...f,target_queue:e.target.value}))} className={`${inp} w-full`}>
                <option value="">—</option>
                {TYPES.map(t=><option key={t}>{t}</option>)}
              </select>
            </div>
            <div className="col-span-1">
              <label className="text-xs font-semibold text-gray-600 block mb-1">Priority</label>
              <select value={form.priority_override} onChange={e => setForm(f=>({...f,priority_override:e.target.value}))} className={`${inp} w-full`}>
                <option value="">—</option>
                {['Urgent','High','Medium','Low'].map(p=><option key={p}>{p}</option>)}
              </select>
            </div>
            <div className="col-span-1 flex flex-col gap-1">
              <label className="flex items-center gap-1 text-xs cursor-pointer"><input type="checkbox" checked={form.create_incident} onChange={e=>setForm(f=>({...f,create_incident:e.target.checked}))} /> Create Incident</label>
              <label className="flex items-center gap-1 text-xs cursor-pointer"><input type="checkbox" checked={form.link_asset} onChange={e=>setForm(f=>({...f,link_asset:e.target.checked}))} /> Link Asset</label>
            </div>
            <div className="col-span-1">
              <button type="submit" disabled={saving} className="w-full py-1.5 text-sm font-semibold bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-60">+ Add</button>
            </div>
          </form>
          {loading ? <div className="text-center py-6 text-gray-400 text-sm">Loading…</div> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b bg-gray-50">
                  {['Keyword','Team','Type','Priority','Incident','Asset',''].map(h=><th key={h} className="px-3 py-2 text-left text-xs font-semibold text-gray-600">{h}</th>)}
                </tr></thead>
                <tbody className="divide-y divide-gray-100">
                  {rules.map(r=>(
                    <tr key={r.id} className={`hover:bg-gray-50 ${!r.is_active ? 'opacity-40' : ''}`}>
                      <td className="px-3 py-2 font-mono text-teal-700 font-semibold">{r.keyword}</td>
                      <td className="px-3 py-2 text-gray-700">{r.target_team||'—'}</td>
                      <td className="px-3 py-2 text-gray-700">{r.target_queue||'—'}</td>
                      <td className="px-3 py-2">{r.priority_override ? <span className="px-2 py-0.5 rounded text-xs font-semibold bg-orange-100 text-orange-700">{r.priority_override}</span> : '—'}</td>
                      <td className="px-3 py-2 text-center">{r.create_incident ? '✓' : ''}</td>
                      <td className="px-3 py-2 text-center">{r.link_asset ? '✓' : ''}</td>
                      <td className="px-3 py-2 text-right"><button onClick={()=>handleDelete(r.id)} className="text-xs text-red-400 hover:text-red-600">Delete</button></td>
                    </tr>
                  ))}
                  {rules.length === 0 && <tr><td colSpan={7} className="px-3 py-6 text-center text-gray-400 text-sm">No routing rules yet.</td></tr>}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const SlaPoliciesPanel = ({ onClose }) => {
  const [policies, setPolicies] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [editing, setEditing] = React.useState(null); // { priority, first_response_minutes, ... }
  const [saving, setSaving] = React.useState(false);
  const load = async () => { setLoading(true); setPolicies(await ModulesModel.getSlaPolicies()); setLoading(false); };
  React.useEffect(() => { load(); }, []);
  const handleSave = async () => {
    if (!editing) return;
    setSaving(true);
    await ModulesModel.upsertSlaPolicy(editing);
    setSaving(false); setEditing(null); load();
  };
  const fmtMin = (m) => m >= 60 ? `${Math.floor(m/60)}h${m%60>0?` ${m%60}m`:''}` : `${m}m`;
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-lg font-bold text-gray-900">⏱️ SLA Policies</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl">×</button>
        </div>
        <div className="overflow-y-auto flex-1 px-6 py-4 space-y-4">
          {loading ? <div className="text-center py-6 text-gray-400 text-sm">Loading…</div> : (
            <div className="space-y-3">
              {policies.map(p => {
                const isEditing = editing?.priority === p.priority;
                const ps = SUPPORT_PRIORITY_STYLES[p.priority] || {};
                return (
                  <div key={p.priority} className={`rounded-xl border p-4 ${isEditing ? 'border-teal-400 bg-teal-50' : 'border-gray-200 bg-white'}`}>
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-bold px-3 py-1 rounded-full border" style={{background:ps.bg,color:ps.color,borderColor:ps.border}}>{p.priority}</span>
                      {!isEditing
                        ? <button onClick={()=>setEditing({...p})} className="text-xs text-teal-600 hover:underline">Edit</button>
                        : <div className="flex gap-2"><button onClick={handleSave} disabled={saving} className="text-xs px-3 py-1 bg-teal-600 text-white rounded-lg">{saving?'Saving…':'Save'}</button><button onClick={()=>setEditing(null)} className="text-xs px-3 py-1 border rounded-lg">Cancel</button></div>
                      }
                    </div>
                    {isEditing ? (
                      <div className="grid grid-cols-2 gap-3">
                        {[['first_response_minutes','First Response (min)'],['resolution_minutes','Resolution (min)'],['escalation_level_1_minutes','Escalation L1 (min)'],['escalation_level_2_minutes','Escalation L2 (min)']].map(([k,label])=>(
                          <div key={k}>
                            <label className="text-xs text-gray-600 block mb-1">{label}</label>
                            <input type="number" value={editing[k]} onChange={e=>setEditing(ed=>({...ed,[k]:parseInt(e.target.value)||0}))}
                              className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm" />
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="grid grid-cols-4 gap-3 text-center">
                        {[['First Response', fmtMin(p.first_response_minutes)],['Resolution', fmtMin(p.resolution_minutes)],['Escalation L1', fmtMin(p.escalation_level_1_minutes)],['Escalation L2', fmtMin(p.escalation_level_2_minutes)]].map(([l,v])=>(
                          <div key={l}><div className="text-xs text-gray-400">{l}</div><div className="text-sm font-bold text-gray-900">{v}</div></div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const CreateTicketModal = ({ onClose, onCreated, user, teamMembers = [] }) => {
  const [form, setForm] = React.useState({ title: '', description: '', ticket_type: 'IT Support', priority: 'Medium', requester: user?.user_name || '', assigned_to: '', team: '', related_asset_id: '', tags: '', pushToZendesk: false });
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const inp = 'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400 focus:border-transparent';
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { setError('Title is required'); return; }
    setSaving(true); setError('');
    const result = await ModulesModel.createSupportTicket({ ...form, related_asset_id: form.related_asset_id ? parseInt(form.related_asset_id) : null });
    setSaving(false);
    if (result.success) onCreated(result);
    else setError(result.error || 'Failed to create ticket');
  };
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b sticky top-0 bg-white">
          <h2 className="text-lg font-bold text-gray-900">Create Support Ticket</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">×</button>
        </div>
        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
          {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-3 py-2 text-sm">{error}</div>}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Title *</label>
            <input value={form.title} onChange={e => set('title', e.target.value)} className={inp} placeholder="Brief description of the issue" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Type</label>
              <select value={form.ticket_type} onChange={e => set('ticket_type', e.target.value)} className={inp}>
                {SUPPORT_TICKET_TYPES.map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Priority</label>
              <select value={form.priority} onChange={e => set('priority', e.target.value)} className={inp}>
                {SUPPORT_PRIORITIES.map(p => <option key={p}>{p}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Requester</label>
              <input value={form.requester} onChange={e => set('requester', e.target.value)} className={inp} placeholder="Your name" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Queue / Team</label>
              <select value={form.team} onChange={e => set('team', e.target.value)} className={inp}>
                <option value="">— unassigned —</option>
                {SUPPORT_QUEUES.map(q => <option key={q}>{q}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Assign To</label>
            <select value={form.assigned_to} onChange={e => set('assigned_to', e.target.value)} className={inp}>
              <option value="">— unassigned —</option>
              {teamMembers.map(m => <option key={m.id} value={m.user_name}>{m.user_name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Description</label>
            <MiniWysiwyg value={form.description} onChange={v => set('description', v)} placeholder="Detailed description of the issue…" minHeight="100px" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Tags</label>
              <input value={form.tags} onChange={e => set('tags', e.target.value)} className={inp} placeholder="e.g. billing, vpn, hardware" />
              <p className="text-[10px] text-gray-400 mt-0.5">Comma-separated</p>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Related Asset ID</label>
              <input type="number" value={form.related_asset_id} onChange={e => set('related_asset_id', e.target.value)} className={inp} placeholder="From AssetBoard" />
            </div>
          </div>
          <label className="flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-lg cursor-pointer">
            <input type="checkbox" checked={form.pushToZendesk} onChange={e => set('pushToZendesk', e.target.checked)} className="w-4 h-4 text-emerald-600 rounded" />
            <div>
              <span className="text-sm font-medium text-emerald-800">Also create in Zendesk</span>
              <span className="block text-[10px] text-emerald-600">Pushes this ticket to your connected Zendesk instance</span>
            </div>
          </label>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={saving} className="px-4 py-2 text-sm font-semibold text-white bg-teal-600 rounded-lg hover:bg-teal-700 disabled:opacity-60">
              {saving ? 'Creating…' : 'Create Ticket'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const SupportTicketDetail = ({ ticket: initialTicket, onBack, onUpdated, user, teamMembers = [] }) => {
  const [ticket, setTicket] = React.useState(initialTicket);
  const [activeTab, setActiveTab] = React.useState('conversation');
  const [commentText, setCommentText] = React.useState('');
  const [commentHtml, setCommentHtml] = React.useState('');
  const [commentEditorKey, setCommentEditorKey] = React.useState(0);
  const [isInternal, setIsInternal] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [editingStatus, setEditingStatus] = React.useState(false);
  const [editingAssign, setEditingAssign] = React.useState(false);
  const [newStatus, setNewStatus] = React.useState(ticket.status);
  const [newAssignee, setNewAssignee] = React.useState(ticket.assigned_to || '');
  const [resolveText, setResolveText] = React.useState('');
  const [showResolve, setShowResolve] = React.useState(false);
  const [escalating, setEscalating] = React.useState(false);

  const refresh = async () => {
    const updated = await ModulesModel.getSupportTicket(ticket.id);
    if (updated) { setTicket(updated); if (onUpdated) onUpdated(updated); }
  };

  const handleAddComment = async () => {
    const content = commentHtml || commentText;
    if (!content.trim()) return;
    setSubmitting(true);
    await ModulesModel.addSupportTicketComment(ticket.id, { comment_text: content.trim(), is_internal: isInternal });
    setCommentText(''); setCommentHtml(''); setCommentEditorKey(k => k + 1); setSubmitting(false);
    refresh();
  };

  const handleStatusChange = async () => {
    await ModulesModel.updateSupportTicket(ticket.id, { status: newStatus });
    setEditingStatus(false); refresh();
  };

  const handleAssign = async () => {
    const member = teamMembers.find(m => m.user_name === newAssignee);
    await ModulesModel.assignSupportTicket(ticket.id, { assigned_to: newAssignee, assigned_to_id: member?.id || null });
    setEditingAssign(false); refresh();
  };

  const handleResolve = async () => {
    await ModulesModel.resolveSupportTicket(ticket.id, resolveText);
    setShowResolve(false); refresh();
  };

  const handleEscalate = async (level) => {
    setEscalating(true);
    await ModulesModel.escalateTicket(ticket.id, level);
    setEscalating(false); refresh();
  };

  const ps = SUPPORT_PRIORITY_STYLES[ticket.priority] || {};
  const ss = SUPPORT_STATUS_STYLES[ticket.status] || {};
  const isOpen = !['Resolved','Closed'].includes(ticket.status);
  const escalationLevel = ticket.escalation_level || 0;

  return (
    <div className="max-w-5xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button onClick={onBack} className="p-2 rounded-lg hover:bg-gray-100 text-gray-500">←</button>
        <div className="flex-1">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <span className="text-xs font-mono text-teal-600 font-semibold">{ticket.ticket_number}</span>
            <span className="text-xs px-2 py-0.5 rounded-full font-semibold" style={{ background: ss.bg, color: ss.color }}>{ticket.status}</span>
            <span className="text-xs px-2 py-0.5 rounded-full font-semibold border" style={{ background: ps.bg, color: ps.color, borderColor: ps.border }}>{ticket.priority}</span>
            <SlaChip status={ticket.sla_status} dueAt={ticket.resolution_due_at || ticket.due_at} />
            <EscalationBadge level={escalationLevel} />
            <RoutingBadge isAutoRouted={ticket.is_auto_routed} />
            {ticket.source === 'zendesk' && (
              <a href={ticket.zendesk_url || '#'} target="_blank" rel="noreferrer"
                className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-700 hover:bg-emerald-200 transition-colors"
                title={`Open in Zendesk: #${ticket.zendesk_ticket_id}`}>ZD #{ticket.zendesk_ticket_id}</a>
            )}
          </div>
          <h1 className="text-xl font-bold text-gray-900">{ticket.title}</h1>
        </div>
        <div className="flex gap-2">
          {isOpen && escalationLevel < 3 && (
            <button onClick={() => handleEscalate(escalationLevel + 1)} disabled={escalating}
              className="px-3 py-1.5 text-sm border border-orange-300 text-orange-700 rounded-lg hover:bg-orange-50 disabled:opacity-60">
              {escalating ? '…' : `↑ Escalate L${escalationLevel + 1}`}
            </button>
          )}
          {isOpen && (
            <>
              <button onClick={() => setShowResolve(true)} className="px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700">✓ Resolve</button>
              <button onClick={() => setEditingAssign(true)} className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">Assign</button>
            </>
          )}
        </div>
      </div>

      {/* SLA countdown bars */}
      {(ticket.response_due_at || ticket.resolution_due_at) && (
        <div className="mb-4">
          <SlaCountdownBar
            responseDueAt={ticket.response_due_at}
            resolutionDueAt={ticket.resolution_due_at || ticket.due_at}
            firstResponseAt={ticket.first_response_time}
            resolvedAt={ticket.resolved_at}
          />
        </div>
      )}

      <div className="grid grid-cols-3 gap-6">
        {/* Main content */}
        <div className="col-span-2 space-y-4">
          <div className="bg-white rounded-xl shadow border border-gray-200 p-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Description</h3>
            {ticket.description ? (
              <div className="text-sm text-gray-600 prose prose-sm max-w-none [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5" dangerouslySetInnerHTML={{ __html: sanitizeBugHtml(ticket.description) }} />
            ) : <p className="text-sm text-gray-600">—</p>}
          </div>

          <div className="bg-white rounded-xl shadow border border-gray-200">
            <div className="flex border-b border-gray-200">
              {[['conversation','💬 Conversation'],['activity','📋 Activity'],['sla','⏱️ SLA Timeline']].map(([id,label]) => (
                <button key={id} onClick={() => setActiveTab(id)}
                  className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === id ? 'text-teal-600 border-teal-600' : 'text-gray-500 border-transparent hover:text-gray-700'}`}>
                  {label}
                </button>
              ))}
            </div>
            <div className="p-4">
              {activeTab === 'conversation' && (
                <div className="space-y-4">
                  {(ticket.comments || []).length === 0 && <p className="text-sm text-gray-400 text-center py-4">No comments yet.</p>}
                  {(ticket.comments || []).map(c => (
                    <div key={c.id} className={`flex gap-3 ${c.is_internal ? 'opacity-80' : ''}`}>
                      <div className="w-8 h-8 rounded-full bg-teal-100 text-teal-700 flex items-center justify-center text-xs font-bold flex-shrink-0">
                        {(c.created_by || '?')[0].toUpperCase()}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-semibold text-gray-900">{c.created_by}</span>
                          {c.is_internal ? <span className="text-xs bg-yellow-100 text-yellow-700 px-1.5 py-0.5 rounded font-medium">Internal</span> : null}
                          <span className="text-xs text-gray-400">{c.created_at?.slice(0,16)}</span>
                        </div>
                        <div className={`text-sm text-gray-700 rounded-lg p-3 prose prose-sm max-w-none [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_img]:max-w-full [&_img]:rounded [&_img]:my-1 ${c.is_internal ? 'bg-yellow-50 border border-yellow-200' : 'bg-gray-50'}`}
                          dangerouslySetInnerHTML={{ __html: sanitizeBugHtml(c.comment_text) }} />
                      </div>
                    </div>
                  ))}
                  <div className="border-t pt-4 space-y-2">
                    <BugCommentEditor key={commentEditorKey} onChange={setCommentHtml} placeholder="Write a reply… (supports bold, italic, lists)" />
                    <div className="flex items-center justify-between">
                      <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
                        <input type="checkbox" checked={isInternal} onChange={e => setIsInternal(e.target.checked)} className="rounded" />
                        Internal note (IT team only)
                      </label>
                      <div className="flex items-center gap-3">
                        <p className="text-xs text-gray-400">Ctrl+B Bold · Ctrl+I Italic</p>
                        <button onClick={handleAddComment} disabled={submitting || !commentHtml.trim()} className="px-4 py-1.5 text-sm font-semibold text-white bg-teal-600 rounded-lg hover:bg-teal-700 disabled:opacity-60">
                          {submitting ? 'Sending…' : 'Send'}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {activeTab === 'activity' && (
                <div className="space-y-3 py-2">
                  {[
                    { time: ticket.created_at, text: `Ticket created by ${ticket.requester}`, icon: '🎫' },
                    ticket.is_auto_routed ? { time: ticket.created_at, text: `Auto-routed to ${ticket.team || 'IT Helpdesk'} queue`, icon: '🤖' } : null,
                    ticket.first_response_time ? { time: ticket.first_response_time, text: 'First response recorded', icon: '💬' } : null,
                    ticket.escalated_at ? { time: ticket.escalated_at, text: `Escalated to Level ${ticket.escalation_level}`, icon: '⚠️' } : null,
                    ticket.resolved_at ? { time: ticket.resolved_at, text: 'Ticket resolved', icon: '✅' } : null,
                  ].filter(Boolean).map((e, i) => (
                    <div key={i} className="flex gap-3 items-start">
                      <span className="text-base flex-shrink-0">{e.icon}</span>
                      <div>
                        <div className="text-sm text-gray-700">{e.text}</div>
                        <div className="text-xs text-gray-400">{e.time?.slice(0,16)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {activeTab === 'sla' && (
                <div className="space-y-4 py-2">
                  <SlaCountdownBar
                    responseDueAt={ticket.response_due_at}
                    resolutionDueAt={ticket.resolution_due_at || ticket.due_at}
                    firstResponseAt={ticket.first_response_time}
                    resolvedAt={ticket.resolved_at}
                  />
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    {[
                      ['Response Due', ticket.response_due_at?.slice(0,16)],
                      ['Resolution Due', (ticket.resolution_due_at || ticket.due_at)?.slice(0,16)],
                      ['First Response', ticket.first_response_time?.slice(0,16) || '—'],
                      ['Resolved At', ticket.resolved_at?.slice(0,16) || '—'],
                      ['Escalation Level', ticket.escalation_level || 0],
                      ['SLA Status', ticket.sla_status || 'on-time'],
                    ].map(([l,v]) => (
                      <div key={l} className="bg-gray-50 rounded-lg p-3">
                        <div className="text-xs text-gray-400">{l}</div>
                        <div className="font-semibold text-gray-900">{v || '—'}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          <div className="bg-white rounded-xl shadow border border-gray-200 p-4 space-y-3">
            <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider">Details</h3>
            {[
              ['Type', ticket.ticket_type],
              ['Requester', ticket.requester],
              ['Assigned To', ticket.assigned_to || '—'],
              ['Queue', ticket.team || '—'],
              ['Channel', ticket.channel || '—'],
              ['Created', ticket.created_at?.slice(0,10)],
              ['Due', (ticket.resolution_due_at || ticket.due_at)?.slice(0,16) || '—'],
              ['Asset ID', ticket.related_asset_id || '—'],
              ...(ticket.zendesk_tags ? [['Tags', ticket.zendesk_tags]] : []),
            ].map(([l, v]) => (
              <div key={l}>
                <div className="text-xs text-gray-400">{l}</div>
                <div className="text-sm text-gray-900 font-medium">{v}</div>
              </div>
            ))}
          </div>

          {isOpen && (
            <div className="bg-white rounded-xl shadow border border-gray-200 p-4">
              <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Change Status</h3>
              {editingStatus ? (
                <div className="space-y-2">
                  <select value={newStatus} onChange={e => setNewStatus(e.target.value)} className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm">
                    {SUPPORT_STATUSES.filter(s => s !== 'Resolved').map(s => <option key={s}>{s}</option>)}
                  </select>
                  <div className="flex gap-2">
                    <button onClick={handleStatusChange} className="flex-1 py-1.5 text-xs font-semibold bg-teal-600 text-white rounded-lg">Save</button>
                    <button onClick={() => setEditingStatus(false)} className="flex-1 py-1.5 text-xs border border-gray-300 rounded-lg">Cancel</button>
                  </div>
                </div>
              ) : (
                <button onClick={() => setEditingStatus(true)} className="w-full py-1.5 text-xs border border-gray-300 rounded-lg hover:bg-gray-50">Update Status</button>
              )}
            </div>
          )}

          {ticket.resolution && (
            <div className="bg-green-50 rounded-xl border border-green-200 p-4">
              <h3 className="text-xs font-bold text-green-700 mb-1">Resolution</h3>
              <p className="text-sm text-green-800">{ticket.resolution}</p>
            </div>
          )}

          {escalationLevel > 0 && (
            <div className="bg-orange-50 rounded-xl border border-orange-200 p-4">
              <h3 className="text-xs font-bold text-orange-700 mb-1">Escalation Status</h3>
              <EscalationBadge level={escalationLevel} />
              {ticket.escalated_at && <p className="text-xs text-orange-600 mt-1">Since {ticket.escalated_at?.slice(0,16)}</p>}
            </div>
          )}
        </div>
      </div>

      {editingAssign && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm p-6">
            <h3 className="font-bold text-gray-900 mb-4">Assign Ticket</h3>
            <select value={newAssignee} onChange={e => setNewAssignee(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mb-4">
              <option value="">— unassigned —</option>
              {teamMembers.map(m => <option key={m.id} value={m.user_name}>{m.user_name}</option>)}
            </select>
            <div className="flex gap-3">
              <button onClick={handleAssign} className="flex-1 py-2 text-sm font-semibold bg-teal-600 text-white rounded-lg">Assign</button>
              <button onClick={() => setEditingAssign(false)} className="flex-1 py-2 text-sm border border-gray-300 rounded-lg">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {showResolve && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm p-6">
            <h3 className="font-bold text-gray-900 mb-4">Resolve Ticket</h3>
            <div className="mb-4"><MiniWysiwyg value={resolveText} onChange={setResolveText} placeholder="Describe the resolution…" minHeight="70px" /></div>
            <div className="flex gap-3">
              <button onClick={handleResolve} className="flex-1 py-2 text-sm font-semibold bg-green-600 text-white rounded-lg">Mark Resolved</button>
              <button onClick={() => setShowResolve(false)} className="flex-1 py-2 text-sm border border-gray-300 rounded-lg">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const SupportBoardModule = ({ user }) => {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [view, setView] = React.useState('dashboard');
  const [selectedTicket, setSelectedTicket] = React.useState(null);
  const [showCreate, setShowCreate] = React.useState(false);
  const [showRoutingRules, setShowRoutingRules] = React.useState(false);
  const [showSlaPolicies, setShowSlaPolicies] = React.useState(false);
  const [showZendeskSettings, setShowZendeskSettings] = React.useState(false);
  const [syncing, setSyncing] = React.useState(false);
  const [syncResult, setSyncResult] = React.useState(null);
  const [filters, setFilters] = React.useState({ status: '', priority: '', team: '', ticket_type: '', search: '' });
  const [teamMembers, setTeamMembers] = React.useState([]);

  const load = React.useCallback(async (f = filters) => {
    setLoading(true);
    const activeFilters = Object.fromEntries(Object.entries(f).filter(([,v]) => v));
    const d = await ModulesModel.getSupportTickets(activeFilters);
    setData(d);
    setLoading(false);
  }, []);

  React.useEffect(() => {
    load(filters);
    fetch('/api/users', { headers: { Authorization: `Bearer ${localStorage.getItem('orbit_token')}` } })
      .then(r => r.json()).then(d => { if (Array.isArray(d)) setTeamMembers(d); }).catch(() => {});
  }, []);

  const handleFilterChange = (key, val) => {
    const newFilters = { ...filters, [key]: val };
    setFilters(newFilters);
    load(newFilters);
  };

  const handleViewTicket = async (ticket) => {
    const detail = await ModulesModel.getSupportTicket(ticket.id);
    setSelectedTicket(detail || ticket);
    setView('detail');
  };

  const handleZendeskSync = async ({ full = false } = {}) => {
    if (full && !window.confirm('Full re-sync ignores the watermark and pulls the last 30 days of Zendesk activity. Continue?')) return;
    setSyncing(true); setSyncResult(null);
    const token = localStorage.getItem('orbit_token');
    const url = full ? '/api/support/zendesk/sync?full=1' : '/api/support/zendesk/sync';
    let res, d;
    try {
      res = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) } });
      d = await res.json();
    } catch (e) {
      setSyncing(false);
      setSyncResult(`Network error: ${e.message || e}`);
      setTimeout(() => setSyncResult(null), 6000);
      return;
    }
    setSyncing(false);
    if (d.success) {
      const parts = [`${d.created} new`, `${d.updated} updated`];
      if (d.commentsAdded) parts.push(`${d.commentsAdded} comments`);
      let msg = `✓ ${parts.join(', ')}`;
      if (d.filterActive && d.filtered > 0) msg += ` · ${d.filtered} filtered out`;
      if (d.moreAvailable) msg += ' — more pages remain, click Sync again';
      if ((d.errors || []).length) msg += ` (${d.errors[0]})`;
      setSyncResult(msg);
      load(filters);
    } else {
      setSyncResult(d.error || 'Sync failed');
    }
    setTimeout(() => setSyncResult(null), d.moreAvailable ? 8000 : 5000);
  };

  const stats = data?.stats || {};
  const tickets = data?.tickets || [];
  const isAdmin = ['admin','manager','devops_manager','service_desk_manager'].includes(user?.role || user?.user_role);

  if (view === 'detail' && selectedTicket) {
    return (
      <SupportTicketDetail
        ticket={selectedTicket}
        onBack={() => { setView('tickets'); setSelectedTicket(null); load(filters); }}
        onUpdated={(t) => setSelectedTicket(t)}
        user={user}
        teamMembers={teamMembers}
      />
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">SupportBoard</h1>
          <p className="text-gray-500 text-sm mt-0.5">IT Operations — Internal Helpdesk</p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <>
              <div className="flex items-stretch border border-gray-300 rounded-lg overflow-hidden">
                <button onClick={() => handleZendeskSync()} disabled={syncing} className="px-3 py-2 text-sm hover:bg-gray-50 flex items-center gap-1 disabled:opacity-50 border-r border-gray-300">
                  {syncing ? '⏳ Syncing…' : '🔄 Zendesk Sync'}
                </button>
                <button onClick={() => handleZendeskSync({ full: true })} disabled={syncing} title="Ignore watermark and re-pull last 30 days" className="px-2 py-2 text-xs text-gray-500 hover:bg-gray-50 disabled:opacity-50">
                  ↻ Full
                </button>
              </div>
              <button onClick={() => setShowZendeskSettings(true)} className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-1">⚙️ Zendesk</button>
              <button onClick={() => setShowRoutingRules(true)} className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-1">🤖 Routing Rules</button>
              <button onClick={() => setShowSlaPolicies(true)} className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-1">⏱️ SLA Policies</button>
            </>
          )}
          <button onClick={() => setShowCreate(true)} className="px-4 py-2 bg-teal-600 text-white rounded-lg font-semibold text-sm hover:bg-teal-700">+ New Ticket</button>
        </div>
      </div>

      <div className="flex gap-2 mb-6 border-b border-gray-200">
        {[['dashboard','📊 Dashboard'],['tickets','🎫 All Tickets']].map(([id,label]) => (
          <button key={id} onClick={() => { setView(id); if (id === 'tickets') load(filters); }}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${view === id ? 'text-teal-600 border-teal-600' : 'text-gray-500 border-transparent hover:text-gray-700'}`}>
            {label}
          </button>
        ))}
      </div>

      {view === 'dashboard' && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[
              { label: 'Open Tickets', value: stats.open || 0, icon: '🎫', color: '#1d4ed8', bg: '#eff6ff' },
              { label: 'Urgent', value: stats.urgent || 0, icon: '🔴', color: '#dc2626', bg: '#fef2f2' },
              { label: 'SLA Breaches', value: stats.breached || 0, icon: '⚠️', color: '#c2410c', bg: '#fff7ed' },
              { label: 'Resolved Today', value: stats.resolvedToday || 0, icon: '✓', color: '#15803d', bg: '#f0fdf4' },
            ].map(s => (
              <div key={s.label} className="bg-white rounded-xl shadow border border-gray-200 p-5">
                <div className="flex items-center gap-2 mb-2"><span>{s.icon}</span><span className="text-xs font-medium text-gray-500">{s.label}</span></div>
                <div className="text-3xl font-bold" style={{ color: s.color }}>{s.value}</div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white rounded-xl shadow border border-gray-200 p-5">
              <h3 className="text-sm font-bold text-gray-700 mb-3">By Status</h3>
              <div className="space-y-2">
                {(stats.byStatus || []).filter(s => s.count > 0).map(s => {
                  const ss2 = SUPPORT_STATUS_STYLES[s.status] || {};
                  return (<div key={s.status} className="flex items-center justify-between">
                    <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ background: ss2.bg, color: ss2.color }}>{s.status}</span>
                    <span className="text-sm font-bold text-gray-900">{s.count}</span>
                  </div>);
                })}
                {(stats.byStatus || []).every(s => s.count === 0) && <p className="text-xs text-gray-400">No tickets yet</p>}
              </div>
            </div>
            <div className="bg-white rounded-xl shadow border border-gray-200 p-5">
              <h3 className="text-sm font-bold text-gray-700 mb-3">By Priority</h3>
              <div className="space-y-2">
                {(stats.byPriority || []).filter(p => p.count > 0).map(p => {
                  const ps2 = SUPPORT_PRIORITY_STYLES[p.priority] || {};
                  return (<div key={p.priority} className="flex items-center justify-between">
                    <span className="text-xs px-2 py-0.5 rounded-full font-semibold border" style={{ background: ps2.bg, color: ps2.color, borderColor: ps2.border }}>{p.priority}</span>
                    <span className="text-sm font-bold text-gray-900">{p.count}</span>
                  </div>);
                })}
                {(stats.byPriority || []).every(p => p.count === 0) && <p className="text-xs text-gray-400">No tickets yet</p>}
              </div>
            </div>
            <div className="bg-white rounded-xl shadow border border-gray-200 p-5">
              <h3 className="text-sm font-bold text-gray-700 mb-3">By Type</h3>
              <div className="space-y-1.5">
                {(stats.byType || []).filter(t => t.count > 0).map(t => (
                  <div key={t.type} className="flex items-center justify-between">
                    <span className="text-xs text-gray-600 truncate">{t.type}</span>
                    <span className="text-sm font-bold text-gray-900 ml-2">{t.count}</span>
                  </div>
                ))}
                {(stats.byType || []).every(t => t.count === 0) && <p className="text-xs text-gray-400">No tickets yet</p>}
              </div>
            </div>
          </div>

          {tickets.filter(t => ['Open','Assigned','In Progress'].includes(t.status)).length > 0 && (
            <div className="bg-white rounded-xl shadow border border-gray-200">
              <div className="px-5 py-4 border-b flex items-center justify-between">
                <h3 className="text-sm font-bold text-gray-900">Active Tickets</h3>
                <button onClick={() => setView('tickets')} className="text-xs text-teal-600 hover:underline">View all →</button>
              </div>
              <div className="divide-y divide-gray-100">
                {tickets.filter(t => ['Open','Assigned','In Progress'].includes(t.status)).slice(0,8).map(ticket => {
                  const ps2 = SUPPORT_PRIORITY_STYLES[ticket.priority] || {};
                  const ss2 = SUPPORT_STATUS_STYLES[ticket.status] || {};
                  return (
                    <div key={ticket.id} className="px-5 py-3 flex items-center gap-3 hover:bg-gray-50 cursor-pointer" onClick={() => handleViewTicket(ticket)}>
                      <span className="text-xs font-mono text-teal-600 w-24 flex-shrink-0">{ticket.ticket_number}</span>
                      <span className="flex-1 text-sm text-gray-900 truncate">{ticket.title}</span>
                      {ticket.is_auto_routed ? <span className="text-xs text-teal-600">🤖</span> : null}
                      {ticket.source === 'zendesk' && <span className="px-1 py-0.5 rounded text-[9px] font-bold bg-emerald-100 text-emerald-700">ZD</span>}
                      <EscalationBadge level={ticket.escalation_level} />
                      <span className="text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0" style={{ background: ss2.bg, color: ss2.color }}>{ticket.status}</span>
                      <span className="text-xs px-2 py-0.5 rounded-full font-semibold border flex-shrink-0" style={{ background: ps2.bg, color: ps2.color, borderColor: ps2.border }}>{ticket.priority}</span>
                      <SlaChip status={ticket.sla_status} dueAt={ticket.resolution_due_at || ticket.due_at} compact />
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {view === 'tickets' && (
        <div className="space-y-4">
          <div className="bg-white rounded-xl shadow border border-gray-200 p-4">
            <div className="flex flex-wrap gap-3">
              <input value={filters.search} onChange={e => handleFilterChange('search', e.target.value)}
                className="flex-1 min-w-40 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400" placeholder="Search tickets…" />
              <select value={filters.status} onChange={e => handleFilterChange('status', e.target.value)} className="border border-gray-300 rounded-lg px-3 py-2 text-sm">
                <option value="">All Statuses</option>
                {SUPPORT_STATUSES.map(s => <option key={s}>{s}</option>)}
              </select>
              <select value={filters.priority} onChange={e => handleFilterChange('priority', e.target.value)} className="border border-gray-300 rounded-lg px-3 py-2 text-sm">
                <option value="">All Priorities</option>
                {SUPPORT_PRIORITIES.map(p => <option key={p}>{p}</option>)}
              </select>
              <select value={filters.team} onChange={e => handleFilterChange('team', e.target.value)} className="border border-gray-300 rounded-lg px-3 py-2 text-sm">
                <option value="">All Queues</option>
                {SUPPORT_QUEUES.map(q => <option key={q}>{q}</option>)}
              </select>
              <select value={filters.ticket_type} onChange={e => handleFilterChange('ticket_type', e.target.value)} className="border border-gray-300 rounded-lg px-3 py-2 text-sm">
                <option value="">All Types</option>
                {SUPPORT_TICKET_TYPES.map(t => <option key={t}>{t}</option>)}
              </select>
              {Object.values(filters).some(v => v) && (
                <button onClick={() => { const f = { status:'',priority:'',team:'',ticket_type:'',search:'' }; setFilters(f); load(f); }} className="text-xs text-gray-500 hover:text-red-500 px-2">✕ Clear</button>
              )}
            </div>
          </div>

          <div className="bg-white rounded-xl shadow border border-gray-200 overflow-hidden">
            {loading ? (
              <div className="py-12 text-center text-gray-400 text-sm">Loading…</div>
            ) : tickets.length === 0 ? (
              <div className="py-12 text-center text-gray-400 text-sm">No tickets found.</div>
            ) : (
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Ticket</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Title</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Priority</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Assigned</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">SLA</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Tags</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Created</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {tickets.map(ticket => {
                    const ps2 = SUPPORT_PRIORITY_STYLES[ticket.priority] || {};
                    const ss2 = SUPPORT_STATUS_STYLES[ticket.status] || {};
                    return (
                      <tr key={ticket.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => handleViewTicket(ticket)}>
                        <td className="px-4 py-3 text-xs font-mono text-teal-600 font-semibold whitespace-nowrap">{ticket.ticket_number}</td>
                        <td className="px-4 py-3 text-sm text-gray-900 max-w-xs truncate">{ticket.title}</td>
                        <td className="px-4 py-3">
                          <span className="text-xs px-2 py-0.5 rounded-full font-semibold border whitespace-nowrap" style={{ background: ps2.bg, color: ps2.color, borderColor: ps2.border }}>{ticket.priority}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap" style={{ background: ss2.bg, color: ss2.color }}>{ticket.status}</span>
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-600">{ticket.assigned_to || <span className="text-gray-300">—</span>}</td>
                        <td className="px-4 py-3"><SlaChip status={ticket.sla_status} dueAt={ticket.resolution_due_at || ticket.due_at} compact /></td>
                        <td className="px-4 py-3">
                          <div className="flex gap-1 flex-wrap">
                            {ticket.is_auto_routed ? <RoutingBadge isAutoRouted /> : null}
                            <EscalationBadge level={ticket.escalation_level} />
                            {ticket.source === 'zendesk' && (
                              <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-700" title={ticket.zendesk_url ? `Zendesk #${ticket.zendesk_ticket_id}` : ''}>ZD</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-400 whitespace-nowrap">{ticket.created_at?.slice(0,10)}</td>
                        <td className="px-4 py-3 text-right">
                          <button onClick={e => { e.stopPropagation(); handleViewTicket(ticket); }} className="text-xs text-teal-600 hover:underline">View →</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {showCreate && (
        <CreateTicketModal
          onClose={() => setShowCreate(false)}
          onCreated={(result) => {
            setShowCreate(false);
            load(filters);
            setView('tickets');
            if (result && result.routing) {
              // Brief routing notification could go here
            }
          }}
          user={user}
          teamMembers={teamMembers}
        />
      )}
      {showRoutingRules && <RoutingRulesPanel onClose={() => setShowRoutingRules(false)} />}
      {showSlaPolicies && <SlaPoliciesPanel onClose={() => setShowSlaPolicies(false)} />}
      {showZendeskSettings && <ZendeskSettingsModal onClose={() => setShowZendeskSettings(false)} />}
      {syncResult && (
        <div className="fixed bottom-6 right-6 z-50 px-4 py-3 bg-white border border-gray-200 rounded-xl shadow-lg text-sm flex items-center gap-2">
          <span>{syncResult.includes('failed') || syncResult.includes('error') ? '❌' : '✅'}</span>
          <span className="text-gray-700">{syncResult}</span>
        </div>
      )}
    </div>
  );
};

const ZendeskSettingsModal = ({ onClose }) => {
  const [settings, setSettings] = React.useState({
    zendesk_subdomain: '', zendesk_email: '', zendesk_api_token: '', zendesk_webhook_secret: '', zendesk_enabled: 'false',
    zendesk_filter_tags: '', zendesk_filter_groups: '', zendesk_filter_types: '',
  });
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [saved, setSaved] = React.useState(false);
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    const token = localStorage.getItem('orbit_token');
    fetch('/api/support/zendesk/settings', { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then(r => r.json()).then(d => { setSettings(s => ({ ...s, ...d })); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true); setError(''); setSaved(false);
    const token = localStorage.getItem('orbit_token');
    const res = await fetch('/api/support/zendesk/settings', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify(settings)
    });
    const d = await res.json();
    setSaving(false);
    if (d.success) setSaved(true); else setError(d.error || 'Failed to save');
  };

  const webhookUrl = typeof window !== 'undefined' ? `${window.location.origin}/api/support/zendesk/webhook` : '/api/support/zendesk/webhook';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-lg">
        <div className="flex justify-between items-center mb-5">
          <h2 className="text-lg font-semibold text-gray-900">Zendesk Integration</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
        </div>
        {loading ? <div className="text-center py-8 text-gray-500">Loading...</div> : (
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <div className="text-sm font-medium text-gray-800">Zendesk Integration Enabled</div>
                <div className="text-xs text-gray-500">Allow syncing tickets from Zendesk</div>
              </div>
              <button onClick={() => setSettings(s => ({ ...s, zendesk_enabled: s.zendesk_enabled === 'true' ? 'false' : 'true' }))}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${settings.zendesk_enabled === 'true' ? 'bg-teal-600' : 'bg-gray-300'}`}>
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${settings.zendesk_enabled === 'true' ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Subdomain</label>
              <div className="flex items-center gap-1">
                <input type="text" value={settings.zendesk_subdomain} onChange={e => setSettings(s => ({ ...s, zendesk_subdomain: e.target.value }))}
                  placeholder="yourcompany" className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500" />
                <span className="text-sm text-gray-400">.zendesk.com</span>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Agent Email</label>
              <input type="email" value={settings.zendesk_email} onChange={e => setSettings(s => ({ ...s, zendesk_email: e.target.value }))}
                placeholder="agent@company.com" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">API Token <span className="text-gray-400 font-normal">(leave blank to keep existing)</span></label>
              <input type="password" value={settings.zendesk_api_token} onChange={e => setSettings(s => ({ ...s, zendesk_api_token: e.target.value }))}
                placeholder="••••••••" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Webhook Secret <span className="text-gray-400 font-normal">(for inbound webhooks)</span></label>
              <input type="password" value={settings.zendesk_webhook_secret} onChange={e => setSettings(s => ({ ...s, zendesk_webhook_secret: e.target.value }))}
                placeholder="••••••••" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500" />
            </div>
            <div className="pt-3 border-t border-gray-100">
              <p className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">Ingestion filters <span className="text-gray-400 font-normal normal-case tracking-normal">(leave blank to pull everything)</span></p>
              <p className="text-xs text-gray-500 mb-3">Only tickets matching <strong>all</strong> set filters will be synced. Within a single field, separate values with commas. Filters are case-insensitive.</p>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tags <span className="text-gray-400 font-normal text-xs">— e.g. <code>dataops, devops, infrastructure</code></span></label>
                  <input type="text" value={settings.zendesk_filter_tags} onChange={e => setSettings(s => ({ ...s, zendesk_filter_tags: e.target.value }))}
                    placeholder="dataops, devops" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Group names <span className="text-gray-400 font-normal text-xs">— e.g. <code>DataOps Team, DevOps Team</code></span></label>
                  <input type="text" value={settings.zendesk_filter_groups} onChange={e => setSettings(s => ({ ...s, zendesk_filter_groups: e.target.value }))}
                    placeholder="DataOps Team, DevOps Team" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ticket types</label>
                  <div className="flex flex-wrap gap-2">
                    {['problem','incident','task','question'].map(t => {
                      const set = new Set((settings.zendesk_filter_types || '').split(',').map(x => x.trim()).filter(Boolean));
                      const on = set.has(t);
                      return (
                        <button
                          type="button"
                          key={t}
                          onClick={() => {
                            const next = new Set(set);
                            if (on) next.delete(t); else next.add(t);
                            setSettings(s => ({ ...s, zendesk_filter_types: Array.from(next).join(',') }));
                          }}
                          className={`px-3 py-1 text-xs rounded-full border ${on ? 'bg-teal-50 border-teal-300 text-teal-700' : 'bg-white border-gray-300 text-gray-600 hover:border-teal-300'}`}
                        >{t}</button>
                      );
                    })}
                  </div>
                  <p className="text-xs text-gray-400 mt-1">Leave all off to allow every type.</p>
                </div>
              </div>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-700">
              <p className="font-semibold mb-1">Webhook URL (configure in Zendesk):</p>
              <code className="block bg-white px-2 py-1 rounded border border-blue-200 text-xs break-all select-all">{webhookUrl}</code>
              <p className="mt-2 text-blue-600">In Zendesk Admin → Webhooks, create a webhook pointing to this URL. Set the secret in the <code>X-Zendesk-Webhook-Secret</code> header.</p>
            </div>
            {error && <p className="text-red-500 text-xs">{error}</p>}
            {saved && <p className="text-green-600 text-xs font-medium">Settings saved</p>}
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900">Cancel</button>
              <button onClick={handleSave} disabled={saving} className="px-4 py-2 bg-teal-600 text-white text-sm rounded-lg hover:bg-teal-700 disabled:opacity-50">
                {saving ? 'Saving…' : 'Save Settings'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const AssetBoardModule = ({ user }) => {
  const [assets, setAssets] = React.useState([]);
  const [stats, setStats] = React.useState({ total: 0, assigned: 0, available: 0, maintenance: 0, retired: 0, expiringCount: 0, expiringAssets: [] });
  const [categories, setCategories] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [view, setView] = React.useState('dashboard'); // dashboard | assets
  const [viewingAsset, setViewingAsset] = React.useState(null);
  const [showForm, setShowForm] = React.useState(false);
  const [editingAsset, setEditingAsset] = React.useState(null);
  const [showAssign, setShowAssign] = React.useState(null);
  const [search, setSearch] = React.useState('');
  const [filterCategory, setFilterCategory] = React.useState('');
  const [filterStatus, setFilterStatus] = React.useState('');

  const canManage = ['admin', 'manager'].includes(user?.role || user?.user_role);

  const load = React.useCallback(async () => {
    setLoading(true);
    const [a, s, c] = await Promise.all([
      ModulesModel.getAssets({ search, category_id: filterCategory, status: filterStatus }),
      ModulesModel.getAssetStats(),
      ModulesModel.getAssetCategories(),
    ]);
    setAssets(a || []); setStats(s); setCategories(c || []);
    setLoading(false);
  }, [search, filterCategory, filterStatus]);

  React.useEffect(() => { load(); }, [load]);

  const handleSaveAsset = async (data) => {
    if (editingAsset?.id) await ModulesModel.updateAsset(editingAsset.id, data);
    else await ModulesModel.createAsset(data);
    setShowForm(false); setEditingAsset(null); load();
  };
  const handleDelete = async (id) => {
    if (!confirm('Delete this asset?')) return;
    await ModulesModel.deleteAsset(id); load();
  };
  const handleAssign = async (data) => {
    await ModulesModel.assignAsset(data);
    setShowAssign(null); load();
  };
  const handleReturn = async (assetId) => {
    if (!confirm('Mark as returned?')) return;
    await ModulesModel.returnAsset({ asset_id: assetId }); load();
  };
  const openEdit = (asset) => { setEditingAsset(asset); setShowForm(true); };

  if (viewingAsset) {
    return <AssetDetailView assetId={viewingAsset} categories={categories} user={user} onBack={() => setViewingAsset(null)} onEdit={openEdit} onRefresh={load} />;
  }

  const today = new Date().toISOString().split('T')[0];

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">🖥️ AssetBoard</h1>
          <p className="text-sm text-gray-500 mt-1">Track company assets, hardware, and software licenses</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex bg-gray-100 rounded-lg p-1">
            {[{ id: 'dashboard', label: 'Dashboard' }, { id: 'assets', label: 'All Assets' }].map(v => (
              <button key={v.id} onClick={() => setView(v.id)} className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${view === v.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>{v.label}</button>
            ))}
          </div>
          {canManage && (
            <button onClick={() => { setEditingAsset(null); setShowForm(true); }} className="px-4 py-2 bg-cyan-600 text-white rounded-lg text-sm font-medium hover:bg-cyan-700">+ New Asset</button>
          )}
        </div>
      </div>

      {/* Warranty Alerts Banner */}
      {stats.expiringAssets.length > 0 && (
        <div className="mb-6 bg-amber-50 border border-amber-200 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-amber-600 font-semibold text-sm">⚠️ Warranty Alerts ({stats.expiringAssets.length})</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {stats.expiringAssets.map(a => {
              const days = Math.ceil((new Date(a.warranty_expiration) - new Date(today)) / 86400000);
              return (
                <button key={a.id} onClick={() => setViewingAsset(a.id)} className="text-xs bg-white border border-amber-200 rounded-lg px-3 py-1.5 text-amber-800 hover:bg-amber-50">
                  {a.asset_name} · {days <= 0 ? 'EXPIRED' : `${days}d left`}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {view === 'dashboard' ? (
        <>
          {/* Stat Cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            {[
              { label: 'Total Assets',    value: stats.total,       color: 'text-gray-800',   bg: 'bg-gray-50',    border: 'border-gray-200' },
              { label: 'Available',       value: stats.available,   color: 'text-green-700',  bg: 'bg-green-50',   border: 'border-green-200' },
              { label: 'Assigned',        value: stats.assigned,    color: 'text-blue-700',   bg: 'bg-blue-50',    border: 'border-blue-200' },
              { label: 'In Maintenance',  value: stats.maintenance, color: 'text-yellow-700', bg: 'bg-yellow-50',  border: 'border-yellow-200' },
              { label: 'Expiring Soon',   value: stats.expiringCount, color: 'text-amber-700', bg: 'bg-amber-50', border: 'border-amber-200' },
            ].map(s => (
              <div key={s.label} className={`${s.bg} border ${s.border} rounded-xl p-4`}>
                <div className={`text-3xl font-bold ${s.color}`}>{s.value}</div>
                <div className="text-sm text-gray-600 mt-1">{s.label}</div>
              </div>
            ))}
          </div>

          {/* Recent Assets Table */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
              <h2 className="font-semibold text-gray-800">Recent Assets</h2>
              <button onClick={() => setView('assets')} className="text-sm text-cyan-600 hover:underline">View all →</button>
            </div>
            {loading ? <div className="p-8 text-center text-gray-400">Loading…</div> : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>{['Asset', 'Category', 'Tag', 'Owner', 'Status', 'Warranty'].map(h => <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">{h}</th>)}</tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {assets.slice(0, 8).map(a => (
                    <tr key={a.id} onClick={() => setViewingAsset(a.id)} className="hover:bg-gray-50 cursor-pointer">
                      <td className="px-4 py-3 font-medium text-gray-900">{a.asset_name}</td>
                      <td className="px-4 py-3 text-gray-500">{a.category_name || '—'}</td>
                      <td className="px-4 py-3 text-gray-400 font-mono text-xs">{a.asset_tag}</td>
                      <td className="px-4 py-3 text-gray-600">{a.current_owner || '—'}</td>
                      <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${assetStatusStyle[a.status] || 'bg-gray-100 text-gray-600'}`}>{a.status}</span></td>
                      <td className="px-4 py-3 text-gray-400 text-xs">{a.warranty_expiration || '—'}</td>
                    </tr>
                  ))}
                  {assets.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400">No assets yet. {canManage ? 'Click "+ New Asset" to add one.' : ''}</td></tr>}
                </tbody>
              </table>
            )}
          </div>
        </>
      ) : (
        /* All Assets View */
        <div>
          {/* Search & Filters */}
          <div className="flex flex-wrap gap-3 mb-4">
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name, tag, serial, owner…" className="flex-1 min-w-48 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400" />
            <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none">
              <option value="">All Categories</option>
              {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none">
              <option value="">All Statuses</option>
              {ASSET_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            {(search || filterCategory || filterStatus) && (
              <button onClick={() => { setSearch(''); setFilterCategory(''); setFilterStatus(''); }} className="px-3 py-2 text-sm text-gray-500 hover:text-gray-800 border border-gray-300 rounded-lg">Clear</button>
            )}
          </div>

          <div className="bg-white rounded-2xl border border-gray-200 shadow overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {['Asset Name', 'Category', 'Tag', 'Serial', 'Owner', 'Location', 'Status', 'Warranty', ''].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  <tr><td colSpan={9} className="px-4 py-8 text-center text-gray-400">Loading…</td></tr>
                ) : assets.length === 0 ? (
                  <tr><td colSpan={9} className="px-4 py-8 text-center text-gray-400">No assets found.</td></tr>
                ) : assets.map(a => {
                  const days = a.warranty_expiration ? Math.ceil((new Date(a.warranty_expiration) - new Date(today)) / 86400000) : null;
                  const warnColor = days !== null && days <= 30 ? (days <= 0 ? 'text-red-600 font-semibold' : 'text-amber-600 font-medium') : 'text-gray-400';
                  return (
                    <tr key={a.id} className="hover:bg-gray-50 group">
                      <td className="px-4 py-3">
                        <button onClick={() => setViewingAsset(a.id)} className="font-medium text-gray-900 hover:text-cyan-700 text-left">{a.asset_name}</button>
                      </td>
                      <td className="px-4 py-3 text-gray-500">{a.category_name || '—'}</td>
                      <td className="px-4 py-3 font-mono text-xs text-gray-400">{a.asset_tag}</td>
                      <td className="px-4 py-3 text-gray-400 text-xs">{a.serial_number || '—'}</td>
                      <td className="px-4 py-3 text-gray-600">{a.current_owner || '—'}</td>
                      <td className="px-4 py-3 text-gray-400">{a.location || '—'}</td>
                      <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${assetStatusStyle[a.status] || 'bg-gray-100 text-gray-600'}`}>{a.status}</span></td>
                      <td className={`px-4 py-3 text-xs ${warnColor}`}>
                        {a.warranty_expiration ? (days <= 0 ? `Expired` : days <= 30 ? `${days}d left` : a.warranty_expiration) : '—'}
                      </td>
                      <td className="px-4 py-3">
                        {canManage && (
                          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            {a.status !== 'Assigned' && (
                              <button onClick={() => setShowAssign(a)} className="px-2 py-1 text-xs bg-cyan-50 text-cyan-700 rounded hover:bg-cyan-100">Assign</button>
                            )}
                            {a.status === 'Assigned' && (
                              <button onClick={() => handleReturn(a.id)} className="px-2 py-1 text-xs bg-orange-50 text-orange-700 rounded hover:bg-orange-100">Return</button>
                            )}
                            <button onClick={() => openEdit(a)} className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded hover:bg-gray-200">Edit</button>
                            <button onClick={() => handleDelete(a.id)} className="px-2 py-1 text-xs bg-red-50 text-red-600 rounded hover:bg-red-100">Del</button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="px-4 py-2 bg-gray-50 border-t border-gray-100 text-xs text-gray-400">{assets.length} asset{assets.length !== 1 ? 's' : ''}</div>
          </div>
        </div>
      )}

      {showForm && (
        <AssetFormModal asset={editingAsset} categories={categories} onSave={handleSaveAsset} onClose={() => { setShowForm(false); setEditingAsset(null); }} />
      )}
      {showAssign && (
        <AssignModal asset={showAssign} onSave={handleAssign} onClose={() => setShowAssign(null)} />
      )}
    </div>
  );
};

// ============================================================================
// PRODUCT (TEAM WORK LOG) MODULE
// ============================================================================

const PRODUCT_CAT_LABEL = { operational: 'Operational', adhoc: 'Ad hoc', incident: 'Incident' };
const PRODUCT_CAT_STYLE = {
  operational: 'bg-blue-100 text-blue-700',
  adhoc:       'bg-purple-100 text-purple-700',
  incident:    'bg-rose-100 text-rose-700',
};
const PRODUCT_EFFORT_LABEL = { S: 'Small', M: 'Medium', L: 'Large' };
const PRODUCT_EFFORT_STYLE = {
  S: 'text-green-700',
  M: 'text-amber-700',
  L: 'text-red-700',
};

const productWeekLabel = (dateStr) => {
  const d = new Date(dateStr);
  const start = new Date(d);
  start.setDate(d.getDate() - d.getDay());
  return 'Week of ' + start.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const ProductModule = ({ user }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('log');
  const [filter, setFilter] = useState('all');
  const [form, setForm] = useState({ what: '', category: 'operational', effort: 'M', requester: '', member: '' });
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState('');
  const [copyPreview, setCopyPreview] = useState('');

  const refresh = async () => {
    setLoading(true);
    const data = await ModulesModel.getProduct();
    setEntries(data.entries || []);
    setLoading(false);
  };
  useEffect(() => { refresh(); }, []);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(''), 2200);
    return () => clearTimeout(t);
  }, [toast]);

  const addEntry = async () => {
    if (!form.what.trim()) return;
    setSaving(true);
    const r = await ModulesModel.createProductEntry({ ...form, what: form.what.trim() });
    setSaving(false);
    if (r?.success === false) { alert(r?.error || 'Failed to add entry'); return; }
    setForm({ what: '', category: form.category, effort: form.effort, requester: '', member: form.member });
    setToast('Entry added');
    await refresh();
  };

  const removeEntry = async (id) => {
    if (!confirm('Remove this entry?')) return;
    await ModulesModel.deleteProductEntry(id);
    await refresh();
  };

  const filtered = useMemo(() =>
    filter === 'all' ? entries : entries.filter(e => e.category === filter),
    [entries, filter]
  );

  const stats = useMemo(() => {
    const byCategory = { operational: 0, adhoc: 0, incident: 0 };
    const byEffort = { S: 0, M: 0, L: 0 };
    const requesters = {};
    entries.forEach(e => {
      byCategory[e.category] = (byCategory[e.category] || 0) + 1;
      byEffort[e.effort] = (byEffort[e.effort] || 0) + 1;
      const r = (e.requester || '').trim();
      if (r) requesters[r] = (requesters[r] || 0) + 1;
    });
    return {
      total: entries.length, byCategory, byEffort,
      requesters: Object.entries(requesters).sort((a, b) => b[1] - a[1]).slice(0, 6),
    };
  }, [entries]);

  const largestEfforts = useMemo(() => entries.filter(e => e.effort === 'L').slice(0, 5), [entries]);

  // ---- Export ----
  const buildCSV = () => {
    const escape = (v) => `"${String(v ?? '').replace(/"/g, '""').replace(/\n/g, ' ')}"`;
    const headers = ['Date', 'What', 'Category', 'Effort', 'Requested by', 'Team member'];
    const rows = entries.map(e => [
      new Date(e.createdAt).toLocaleDateString(),
      e.what,
      PRODUCT_CAT_LABEL[e.category] || e.category,
      PRODUCT_EFFORT_LABEL[e.effort] || e.effort,
      e.requester || '—',
      e.member || '—',
    ].map(escape).join(','));
    return [headers.join(','), ...rows].join('\n');
  };
  const downloadCSV = () => {
    if (!entries.length) { setToast('No entries to export'); return; }
    const blob = new Blob([buildCSV()], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `product_work_log_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
    setToast('CSV downloaded');
  };
  const buildSummaryText = () => {
    const large = entries.filter(e => e.effort === 'L').map(e => `  - ${e.what} (from ${e.requester || '—'})`).join('\n');
    return [
      'PRODUCT WORK LOG SUMMARY',
      '='.repeat(40),
      `Total entries: ${stats.total}`,
      '',
      'By category:',
      `  Recurring operational: ${stats.byCategory.operational}`,
      `  Ad hoc requests: ${stats.byCategory.adhoc}`,
      `  Incidents / firefighting: ${stats.byCategory.incident}`,
      '',
      'By effort:',
      `  Small (<2 hrs): ${stats.byEffort.S}`,
      `  Medium (2-8 hrs): ${stats.byEffort.M}`,
      `  Large (>8 hrs): ${stats.byEffort.L}`,
      '',
      'Largest efforts:',
      large || '  None logged yet',
      '',
      `Generated: ${new Date().toLocaleDateString()}`,
    ].join('\n');
  };
  const copySummary = async () => {
    if (!entries.length) { setToast('No entries to copy'); return; }
    const text = buildSummaryText();
    setCopyPreview(text);
    try { await navigator.clipboard.writeText(text); setToast('Copied to clipboard'); } catch {}
  };

  const inputCls = 'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400';

  // Group history by week
  const historyGroups = useMemo(() => {
    const groups = [];
    let lastWeek = null;
    filtered.forEach(e => {
      const w = productWeekLabel(e.createdAt);
      if (w !== lastWeek) { groups.push({ week: w, items: [] }); lastWeek = w; }
      groups[groups.length - 1].items.push(e);
    });
    return groups;
  }, [filtered]);

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">📦 Product</h1>
          <p className="text-gray-600 mt-1">Track team work as it happens. Review every Friday. Report with confidence.</p>
        </div>
        <div className="flex items-center gap-4 text-sm">
          <span className="text-gray-600"><span className="font-bold text-gray-900">{stats.total}</span> entries</span>
          <span className="text-gray-600"><span className="font-bold text-rose-700">{stats.byCategory.incident}</span> incidents</span>
          <span className="text-gray-600"><span className="font-bold text-red-700">{stats.byEffort.L}</span> large</span>
        </div>
      </div>

      <div className="flex gap-1 mb-5 bg-gray-100 rounded-xl p-1 flex-wrap">
        {[
          { id: 'log',     label: '📝 Log work' },
          { id: 'history', label: '📚 History', count: stats.total },
          { id: 'summary', label: '📊 Summary' },
          { id: 'export',  label: '⬇️ Export' },
        ].map(t => (
          <button key={t.id} onClick={() => setView(t.id)}
            className={`px-4 py-2 text-sm rounded-lg font-medium flex items-center gap-2 transition-colors ${view === t.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
            {t.label}
            {t.count != null && (
              <span className={`text-xs px-1.5 py-0.5 rounded-full ${view === t.id ? 'bg-slate-100 text-slate-700' : 'bg-gray-200 text-gray-500'}`}>{t.count}</span>
            )}
          </button>
        ))}
      </div>

      {view === 'log' && (
        <div className="bg-white rounded-lg shadow border border-gray-200 p-5">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">What was done *</label>
              <textarea className={inputCls} rows={3}
                placeholder="e.g. Resolved SSO outage affecting 3 enterprise accounts"
                value={form.what} onChange={e => setForm({ ...form, what: e.target.value })} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <select className={inputCls} value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}>
                  <option value="operational">Recurring operational</option>
                  <option value="adhoc">Ad hoc request</option>
                  <option value="incident">Incident / firefighting</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Effort</label>
                <select className={inputCls} value={form.effort} onChange={e => setForm({ ...form, effort: e.target.value })}>
                  <option value="S">Small (&lt; 2 hrs)</option>
                  <option value="M">Medium (2–8 hrs)</option>
                  <option value="L">Large (&gt; 8 hrs)</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Requested by</label>
                <input className={inputCls} placeholder="e.g. Sales, Engineering"
                  value={form.requester} onChange={e => setForm({ ...form, requester: e.target.value })} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Team member</label>
                <input className={inputCls} placeholder="e.g. Alex"
                  value={form.member} onChange={e => setForm({ ...form, member: e.target.value })} />
              </div>
            </div>
            <div className="flex justify-end pt-2">
              <button onClick={addEntry} disabled={!form.what.trim() || saving}
                className="px-5 py-2 bg-slate-700 text-white text-sm rounded-lg hover:bg-slate-800 font-medium disabled:opacity-50 disabled:cursor-not-allowed">
                {saving ? 'Adding…' : '+ Add entry'}
              </button>
            </div>
          </div>
        </div>
      )}

      {view === 'history' && (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {[
              { id: 'all',         label: 'All' },
              { id: 'operational', label: 'Operational' },
              { id: 'adhoc',       label: 'Ad hoc' },
              { id: 'incident',    label: 'Incidents' },
            ].map(f => (
              <button key={f.id} onClick={() => setFilter(f.id)}
                className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${filter === f.id ? 'bg-slate-700 text-white border-slate-700' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}>
                {f.label}
              </button>
            ))}
          </div>

          <div className="bg-white rounded-lg shadow border border-gray-200">
            {loading ? (
              <div className="p-12 text-center text-gray-400">Loading…</div>
            ) : filtered.length === 0 ? (
              <div className="p-12 text-center">
                <p className="text-gray-400 mb-3">No entries yet.</p>
                {entries.length === 0 && (
                  <button onClick={() => setView('log')} className="px-4 py-2 bg-slate-700 text-white text-sm rounded-lg hover:bg-slate-800 font-medium">+ Log first entry</button>
                )}
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {historyGroups.map(grp => (
                  <div key={grp.week}>
                    <div className="px-5 py-2 bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wide">{grp.week}</div>
                    <ul className="divide-y divide-gray-100">
                      {grp.items.map(e => (
                        <li key={e.id} className="px-5 py-3 group hover:bg-gray-50/50">
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="text-sm text-gray-900 mb-1.5">{e.what}</div>
                              <div className="flex items-center gap-2 flex-wrap text-xs">
                                <span className={`px-2 py-0.5 rounded-full font-medium ${PRODUCT_CAT_STYLE[e.category]}`}>{PRODUCT_CAT_LABEL[e.category]}</span>
                                <span className={`font-medium ${PRODUCT_EFFORT_STYLE[e.effort]}`}>{PRODUCT_EFFORT_LABEL[e.effort]}</span>
                                <span className="text-gray-500">from {e.requester || '—'}</span>
                                <span className="text-gray-400">·</span>
                                <span className="text-gray-500">{e.member || '—'}</span>
                                <span className="text-gray-400">·</span>
                                <span className="text-gray-400">{new Date(e.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                              </div>
                            </div>
                            <button onClick={() => removeEntry(e.id)}
                              className="opacity-0 group-hover:opacity-100 text-xs text-red-600 hover:bg-red-50 px-2 py-1 rounded transition-opacity">Remove</button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {view === 'summary' && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-white rounded-lg shadow border border-gray-200 p-4">
              <div className="text-xs uppercase tracking-wide text-gray-500 font-medium">Total entries</div>
              <div className="text-3xl font-bold text-gray-900 mt-1">{stats.total}</div>
            </div>
            <div className="bg-white rounded-lg shadow border border-gray-200 p-4">
              <div className="text-xs uppercase tracking-wide text-gray-500 font-medium">Incidents</div>
              <div className="text-3xl font-bold text-rose-700 mt-1">{stats.byCategory.incident}</div>
            </div>
            <div className="bg-white rounded-lg shadow border border-gray-200 p-4">
              <div className="text-xs uppercase tracking-wide text-gray-500 font-medium">Ad hoc</div>
              <div className="text-3xl font-bold text-purple-700 mt-1">{stats.byCategory.adhoc}</div>
            </div>
            <div className="bg-white rounded-lg shadow border border-gray-200 p-4">
              <div className="text-xs uppercase tracking-wide text-gray-500 font-medium">Large efforts</div>
              <div className="text-3xl font-bold text-red-700 mt-1">{stats.byEffort.L}</div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow border border-gray-200 p-5">
            <h3 className="text-sm font-semibold text-gray-800 mb-3">Top requesters</h3>
            {stats.requesters.length === 0 ? (
              <p className="text-sm text-gray-400">No requester data yet.</p>
            ) : (
              <div className="space-y-2">
                {stats.requesters.map(([name, count]) => {
                  const pct = stats.total ? Math.round(count / stats.total * 100) : 0;
                  return (
                    <div key={name}>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="text-gray-800">{name}</span>
                        <span className="text-gray-500 text-xs">{count} entr{count === 1 ? 'y' : 'ies'} · {pct}%</span>
                      </div>
                      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full bg-slate-700 rounded-full transition-all" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {largestEfforts.length > 0 && (
            <div className="bg-white rounded-lg shadow border border-gray-200">
              <div className="px-5 py-3 border-b border-gray-100">
                <h3 className="text-sm font-semibold text-gray-800">Largest efforts</h3>
              </div>
              <ul className="divide-y divide-gray-100">
                {largestEfforts.map(e => (
                  <li key={e.id} className="px-5 py-3">
                    <div className="text-sm text-gray-900 mb-1">{e.what}</div>
                    <div className="flex items-center gap-2 text-xs flex-wrap">
                      <span className={`px-2 py-0.5 rounded-full font-medium ${PRODUCT_CAT_STYLE[e.category]}`}>{PRODUCT_CAT_LABEL[e.category]}</span>
                      <span className="text-gray-500">from {e.requester || '—'}</span>
                      <span className="text-gray-400">·</span>
                      <span className="text-gray-500">{e.member || '—'}</span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {!stats.total && (
            <div className="bg-white rounded-lg border border-dashed border-gray-300 p-12 text-center text-sm text-gray-400">
              Add entries to see your summary.
            </div>
          )}
        </div>
      )}

      {view === 'export' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="bg-white rounded-lg shadow border border-gray-200 p-5">
            <h3 className="text-sm font-semibold text-gray-800 mb-1">Export to CSV</h3>
            <p className="text-sm text-gray-500 mb-3">Download all entries as a spreadsheet you can paste into Excel or Google Sheets.</p>
            <button onClick={downloadCSV} className="px-4 py-2 bg-slate-700 text-white text-sm rounded-lg hover:bg-slate-800 font-medium">↓ Download CSV</button>
          </div>
          <div className="bg-white rounded-lg shadow border border-gray-200 p-5">
            <h3 className="text-sm font-semibold text-gray-800 mb-1">Copy as text</h3>
            <p className="text-sm text-gray-500 mb-3">Paste into an email or document for a quick leadership update.</p>
            <button onClick={copySummary} className="px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50 font-medium">📋 Copy summary text</button>
            {copyPreview && (
              <pre className="mt-3 bg-gray-50 border border-gray-200 rounded-lg p-3 text-xs text-gray-700 font-mono whitespace-pre-wrap max-h-64 overflow-y-auto">{copyPreview}</pre>
            )}
          </div>
        </div>
      )}

      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-slate-800 text-white text-sm px-4 py-2 rounded-lg shadow-lg z-50 font-medium">
          {toast}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// TECH DEBT REVIEW MODULE
// ============================================================================

const TD_CATEGORIES = [
  { id: 'code',     label: 'Code Quality',    short: 'CODE',  color: '#8B7355' },
  { id: 'infra',    label: 'Infrastructure',  short: 'INFRA', color: '#3D5A6C' },
  { id: 'arch',     label: 'Architecture',    short: 'ARCH',  color: '#6B4A6B' },
  { id: 'tests',    label: 'Testing',         short: 'TEST',  color: '#4A6B3F' },
  { id: 'docs',     label: 'Documentation',   short: 'DOCS',  color: '#8B6B3F' },
  { id: 'security', label: 'Security',        short: 'SEC',   color: '#A02D10' },
  { id: 'perf',     label: 'Performance',     short: 'PERF',  color: '#B8860B' },
  { id: 'deps',     label: 'Dependencies',    short: 'DEPS',  color: '#5B6B8B' },
];
const TD_STATUSES = [
  { id: 'new',         label: 'New' },
  { id: 'accepted',    label: 'Accepted' },
  { id: 'scheduled',   label: 'Scheduled' },
  { id: 'in_progress', label: 'In Progress' },
  { id: 'deferred',    label: 'Deferred' },
  { id: 'rejected',    label: 'Rejected' },
];
const TD_DECISIONS = [
  { id: 'accept',      label: 'Accept & Prioritize' },
  { id: 'schedule',    label: 'Schedule for Sprint' },
  { id: 'defer',       label: 'Defer to Later' },
  { id: 'reject',      label: 'Reject / Won\'t Fix' },
  { id: 'investigate', label: 'Needs Investigation' },
];
const tdPriority = (impact, effort) => (impact || 0) * 2 - (effort || 0);
const tdTier = (s) => {
  if (s >= 6) return { tier: 'critical', label: 'CRITICAL', color: '#A02D10' };
  if (s >= 3) return { tier: 'high',     label: 'HIGH',     color: '#B8860B' };
  if (s >= 1) return { tier: 'medium',   label: 'MEDIUM',   color: '#4A6B3F' };
  return        { tier: 'low',      label: 'LOW',      color: '#6B635A' };
};
const tdFmtTime = (s) => {
  const m = Math.floor(Math.abs(s) / 60);
  const sec = Math.abs(s) % 60;
  return `${s < 0 ? '-' : ''}${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
};
const tdActionId = () => 'a-' + Math.random().toString(36).slice(2, 9);

const tdTierPill = (tier) => {
  if (tier === 'critical') return 'bg-red-100 text-red-700';
  if (tier === 'high')     return 'bg-amber-100 text-amber-700';
  if (tier === 'medium')   return 'bg-green-100 text-green-700';
  return 'bg-gray-100 text-gray-600';
};

const TechDebtModule = ({ user }) => {
  const [items, setItems] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [currentMeetingId, setCurrentMeetingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('backlog');
  const [filter, setFilter] = useState({ category: 'all', status: 'all', search: '' });
  const [sortBy, setSortBy] = useState('priority');
  const [editingItem, setEditingItem] = useState(null);
  const [attendeeInput, setAttendeeInput] = useState('');
  const [copied, setCopied] = useState(null);
  const [timer, setTimer] = useState({ isRunning: false, timeRemaining: 300 });

  const refresh = async () => {
    const data = await ModulesModel.getTechDebt();
    setItems(data.items || []);
    setMeetings(data.meetings || []);
    if (!currentMeetingId || !(data.meetings || []).some(m => m.id === currentMeetingId)) {
      const draft = (data.meetings || []).find(m => m.status === 'draft');
      setCurrentMeetingId(draft?.id || (data.meetings && data.meetings[0]?.id) || null);
    }
    setLoading(false);
  };

  useEffect(() => { refresh(); }, []);

  // If no draft meeting exists after load, auto-create one.
  useEffect(() => {
    if (loading) return;
    if (meetings.length === 0) {
      (async () => {
        const r = await ModulesModel.tdCreateMeeting({
          title: 'Technical Debt Review',
          date: new Date().toISOString().slice(0, 10),
        });
        if (r?.id) await refresh();
      })();
    }
  }, [loading, meetings.length]);

  const meeting = useMemo(
    () => meetings.find(m => m.id === currentMeetingId) || null,
    [meetings, currentMeetingId]
  );

  // Timer tick
  useEffect(() => {
    if (!timer.isRunning) return;
    const t = setInterval(() => setTimer(tt => ({ ...tt, timeRemaining: tt.timeRemaining - 1 })), 1000);
    return () => clearInterval(t);
  }, [timer.isRunning]);

  // Reset timer when switching meetings
  useEffect(() => {
    if (meeting) setTimer({ isRunning: false, timeRemaining: meeting.perItemSeconds });
  }, [currentMeetingId]); // eslint-disable-line

  const updateMeetingLocal = (id, patch) => {
    setMeetings(ms => ms.map(m => m.id === id ? { ...m, ...patch } : m));
  };
  // Patch + persist (debounced for high-frequency fields if needed; here we do straight calls)
  const persistMeeting = async (id, patch) => {
    updateMeetingLocal(id, patch);
    await ModulesModel.tdUpdateMeeting(id, patch);
  };

  const filteredItems = useMemo(() => {
    let list = items.filter(item => {
      if (filter.category !== 'all' && item.category !== filter.category) return false;
      if (filter.status !== 'all' && item.status !== filter.status) return false;
      if (filter.search && !((item.title || '') + ' ' + (item.description || '')).toLowerCase().includes(filter.search.toLowerCase())) return false;
      return true;
    });
    if (sortBy === 'priority') list.sort((a, b) => tdPriority(b.impact, b.effort) - tdPriority(a.impact, a.effort));
    if (sortBy === 'impact') list.sort((a, b) => b.impact - a.impact);
    if (sortBy === 'effort') list.sort((a, b) => a.effort - b.effort);
    if (sortBy === 'title') list.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
    return list;
  }, [items, filter, sortBy]);

  const stats = useMemo(() => {
    const critical = items.filter(i => tdPriority(i.impact, i.effort) >= 6).length;
    return { total: items.length, critical, inQueue: meeting?.queue?.length || 0 };
  }, [items, meeting]);

  const pastMeetings = useMemo(
    () => meetings.filter(m => m.status === 'ended' && m.id !== currentMeetingId),
    [meetings, currentMeetingId]
  );

  const currentItem = useMemo(() => {
    if (!meeting?.queue?.length) return null;
    const id = meeting.queue[meeting.currentIndex];
    return items.find(i => i.id === id) || null;
  }, [items, meeting]);

  // ---- item handlers ----
  const saveItem = async (data) => {
    if (data.id) {
      await ModulesModel.tdUpdateItem(data.id, data);
    } else {
      await ModulesModel.tdCreateItem(data);
    }
    setEditingItem(null);
    await refresh();
  };
  const deleteItem = async (id) => {
    if (!confirm('Delete this item?')) return;
    await ModulesModel.tdDeleteItem(id);
    await refresh();
  };
  const updateCurrentItem = async (patch) => {
    if (!currentItem) return;
    setItems(items.map(i => i.id === currentItem.id ? { ...i, ...patch } : i));
    await ModulesModel.tdUpdateItem(currentItem.id, patch);
  };

  // ---- meeting handlers ----
  const toggleQueue = (itemId) => {
    if (!meeting) return;
    const next = meeting.queue.includes(itemId)
      ? meeting.queue.filter(q => q !== itemId)
      : [...meeting.queue, itemId];
    persistMeeting(meeting.id, { queue: next });
  };
  const addAllCriticalToQueue = () => {
    if (!meeting) return;
    const crits = items.filter(i => tdPriority(i.impact, i.effort) >= 6 && !meeting.queue.includes(i.id)).map(i => i.id);
    if (crits.length) persistMeeting(meeting.id, { queue: [...meeting.queue, ...crits] });
  };
  const startMeeting = async () => {
    if (!meeting?.queue?.length) return;
    const nowIso = new Date().toISOString();
    await persistMeeting(meeting.id, { started: true, startedAt: nowIso });
    setTimer({ isRunning: true, timeRemaining: meeting.perItemSeconds });
    setView('meeting');
    // Bump status of new items to accepted (they're now "on the docket")
    const toUpdate = items.filter(i => meeting.queue.includes(i.id) && i.status === 'new');
    for (const it of toUpdate) {
      await ModulesModel.tdUpdateItem(it.id, { status: 'accepted' });
    }
    if (toUpdate.length) await refresh();
  };
  const nextItem = () => {
    if (!meeting) return;
    const next = Math.min(meeting.currentIndex + 1, meeting.queue.length - 1);
    persistMeeting(meeting.id, { currentIndex: next });
    setTimer({ isRunning: true, timeRemaining: meeting.perItemSeconds });
  };
  const prevItem = () => {
    if (!meeting) return;
    const next = Math.max(meeting.currentIndex - 1, 0);
    persistMeeting(meeting.id, { currentIndex: next });
    setTimer({ isRunning: true, timeRemaining: meeting.perItemSeconds });
  };
  const endMeeting = async () => {
    if (!meeting) return;
    setTimer(t => ({ ...t, isRunning: false }));
    await persistMeeting(meeting.id, { ended: true, endedAt: new Date().toISOString(), status: 'ended' });
    setView('summary');
  };
  const newMeeting = async () => {
    const r = await ModulesModel.tdCreateMeeting({
      title: 'Technical Debt Review',
      date: new Date().toISOString().slice(0, 10),
    });
    if (r?.id) {
      await refresh();
      setCurrentMeetingId(r.id);
      setView('backlog');
    }
  };
  const switchMeeting = (id) => {
    setCurrentMeetingId(id);
    const m = meetings.find(x => x.id === id);
    if (m) setView(m.status === 'ended' ? 'summary' : 'backlog');
  };
  const deleteMeeting = async (id) => {
    if (!confirm('Delete this meeting permanently?')) return;
    await ModulesModel.tdDeleteMeeting(id);
    await refresh();
  };

  const addActionItem = async (text, owner) => {
    if (!currentItem || !text.trim()) return;
    const next = [...(currentItem.actions || []), { id: tdActionId(), text: text.trim(), owner: owner.trim() || 'Unassigned', done: false }];
    await updateCurrentItem({ actions: next });
  };
  const removeActionItem = async (actionId) => {
    if (!currentItem) return;
    const next = (currentItem.actions || []).filter(a => a.id !== actionId);
    await updateCurrentItem({ actions: next });
  };
  const toggleActionDone = async (actionId) => {
    if (!currentItem) return;
    const next = (currentItem.actions || []).map(a => a.id === actionId ? { ...a, done: !a.done } : a);
    await updateCurrentItem({ actions: next });
  };

  // ---- export helpers ----
  const buildCSV = () => {
    const escape = (v) => `"${String(v ?? '').replace(/"/g, '""').replace(/\n/g, ' ')}"`;
    const headers = ['ID', 'Title', 'Category', 'Impact', 'Effort', 'Priority', 'Status', 'Owner', 'Decision', 'Notes', 'Actions', 'Description'];
    const rows = items.map(i => [
      i.id, i.title,
      TD_CATEGORIES.find(c => c.id === i.category)?.label || i.category,
      i.impact, i.effort, tdPriority(i.impact, i.effort),
      TD_STATUSES.find(s => s.id === i.status)?.label || i.status,
      i.owner, TD_DECISIONS.find(d => d.id === i.decision)?.label || '',
      i.notes || '',
      (i.actions || []).map(a => `[${a.owner}] ${a.text}`).join(' | '),
      i.description,
    ].map(escape).join(','));
    return [headers.join(','), ...rows].join('\n');
  };
  const buildJSON = () => JSON.stringify({ meeting, items }, null, 2);
  const buildMarkdown = () => {
    if (!meeting) return '';
    const discussed = items.filter(i => meeting.queue.includes(i.id));
    const allActions = discussed.flatMap(i => (i.actions || []).map(a => ({ ...a, item: i.title })));
    let md = `# ${meeting.title}\n\n`;
    md += `**Date:** ${meeting.date}  \n`;
    if (meeting.facilitator) md += `**Facilitator:** ${meeting.facilitator}  \n`;
    if (meeting.attendees?.length) md += `**Attendees:** ${meeting.attendees.join(', ')}  \n`;
    md += `\n---\n\n## Items Discussed (${discussed.length})\n\n`;
    discussed.forEach((i, idx) => {
      const cat = TD_CATEGORIES.find(c => c.id === i.category)?.label || i.category;
      const dec = TD_DECISIONS.find(d => d.id === i.decision)?.label || '_No decision recorded_';
      md += `### ${idx + 1}. ${i.title}\n\n`;
      md += `- **Category:** ${cat}\n`;
      md += `- **Impact/Effort:** ${i.impact} / ${i.effort} (Priority ${tdPriority(i.impact, i.effort)})\n`;
      md += `- **Decision:** ${dec}\n`;
      if (i.owner && i.owner !== 'Unassigned') md += `- **Owner:** ${i.owner}\n`;
      if (i.notes) md += `- **Notes:** ${i.notes}\n`;
      if (i.actions?.length) {
        md += `- **Action Items:**\n`;
        i.actions.forEach(a => { md += `  - [${a.done ? 'x' : ' '}] ${a.text} — _${a.owner}_\n`; });
      }
      md += '\n';
    });
    if (allActions.length) {
      md += `## All Action Items\n\n`;
      allActions.forEach(a => { md += `- [${a.done ? 'x' : ' '}] ${a.text} — _${a.owner}_ (${a.item})\n`; });
    }
    return md;
  };
  const downloadFile = (content, filename, type) => {
    const blob = new Blob([content], { type });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };
  const copyToClipboard = async (content, key) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopied(key);
      setTimeout(() => setCopied(null), 1500);
    } catch {}
  };

  if (loading || !meeting) {
    return <div className="p-10 text-center text-gray-400">Loading…</div>;
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">📜 Tech Debt Review</h1>
          <p className="text-gray-600 mt-1">Score, prioritize, and discuss technical debt with your team.</p>
        </div>
        <div className="flex items-center gap-4 text-sm">
          <span className="text-gray-600"><span className="font-bold text-gray-900">{stats.total}</span> items</span>
          <span className="text-gray-600"><span className="font-bold text-red-600">{stats.critical}</span> critical</span>
          <span className="text-gray-600"><span className="font-bold text-gray-900">{stats.inQueue}</span> queued</span>
        </div>
      </div>

      <div className="flex gap-1 mb-5 bg-gray-100 rounded-xl p-1 flex-wrap">
        {[
          { id: 'backlog', label: '📋 Backlog', count: stats.total },
          { id: 'meeting', label: '⏱ Meeting', disabled: !meeting.started && meeting.queue.length === 0 },
          { id: 'summary', label: '✓ Summary' },
        ].map(t => (
          <button key={t.id} onClick={() => setView(t.id)} disabled={t.disabled}
            className={`px-4 py-2 text-sm rounded-lg font-medium flex items-center gap-2 transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${view === t.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
            {t.label}
            {t.count != null && (
              <span className={`text-xs px-1.5 py-0.5 rounded-full ${view === t.id ? 'bg-red-100 text-red-700' : 'bg-gray-200 text-gray-500'}`}>{t.count}</span>
            )}
          </button>
        ))}
      </div>

      {view === 'backlog' && (
        <TdBacklogView
          items={filteredItems} allItems={items} meeting={meeting}
          persistMeeting={(p) => persistMeeting(meeting.id, p)}
          pastMeetings={pastMeetings}
          onSwitchMeeting={switchMeeting}
          onNewMeeting={newMeeting}
          onDeleteMeeting={deleteMeeting}
          filter={filter} setFilter={setFilter}
          sortBy={sortBy} setSortBy={setSortBy}
          onEdit={setEditingItem}
          onNew={() => setEditingItem({})}
          onDelete={deleteItem}
          onToggleQueue={toggleQueue}
          onAddCritical={addAllCriticalToQueue}
          onStart={startMeeting}
          attendeeInput={attendeeInput} setAttendeeInput={setAttendeeInput}
        />
      )}

      {view === 'meeting' && (
        <TdMeetingView
          meeting={meeting}
          persistMeeting={(p) => persistMeeting(meeting.id, p)}
          timer={timer} setTimer={setTimer}
          currentItem={currentItem}
          allItems={items}
          onNext={nextItem} onPrev={prevItem} onEnd={endMeeting}
          onUpdate={updateCurrentItem}
          onAddAction={addActionItem}
          onRemoveAction={removeActionItem}
          onToggleAction={toggleActionDone}
        />
      )}

      {view === 'summary' && (
        <TdSummaryView
          meeting={meeting} items={items}
          onNewMeeting={newMeeting}
          onExport={(fmt) => {
            if (fmt === 'csv') downloadFile(buildCSV(), `tech-debt-${meeting.date}.csv`, 'text/csv');
            if (fmt === 'json') downloadFile(buildJSON(), `tech-debt-${meeting.date}.json`, 'application/json');
            if (fmt === 'md') downloadFile(buildMarkdown(), `tech-debt-minutes-${meeting.date}.md`, 'text/markdown');
          }}
          onCopy={(fmt) => {
            if (fmt === 'csv') copyToClipboard(buildCSV(), 'csv');
            if (fmt === 'json') copyToClipboard(buildJSON(), 'json');
            if (fmt === 'md') copyToClipboard(buildMarkdown(), 'md');
          }}
          copied={copied}
        />
      )}

      {editingItem && (
        <TdItemEditor item={editingItem} onSave={saveItem} onClose={() => setEditingItem(null)} />
      )}
    </div>
  );
};

// ---- Tech Debt: Backlog View ----
const TdBacklogView = ({
  items, allItems, meeting, persistMeeting,
  pastMeetings, onSwitchMeeting, onNewMeeting, onDeleteMeeting,
  filter, setFilter, sortBy, setSortBy,
  onEdit, onNew, onDelete, onToggleQueue, onAddCritical, onStart,
  attendeeInput, setAttendeeInput,
}) => {
  const isEnded = meeting.status === 'ended';
  const inputCls = 'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-400';
  return (
    <div className="space-y-5">
      {pastMeetings.length > 0 && (
        <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
            <h2 className="font-semibold text-gray-800">Past Meetings</h2>
            <span className="text-xs text-gray-500">{pastMeetings.length} archived</span>
          </div>
          <ul className="divide-y divide-gray-100">
            {pastMeetings.map(m => (
              <li key={m.id} className="px-5 py-3 flex items-center justify-between gap-4 group hover:bg-gray-50">
                <button onClick={() => onSwitchMeeting(m.id)} className="text-left flex-1 min-w-0">
                  <div className="font-medium text-gray-900 truncate">{m.title}</div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    {m.date} · {(m.queue || []).length} item{(m.queue || []).length === 1 ? '' : 's'} discussed
                    {m.endedAt && ` · ended ${new Date(m.endedAt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`}
                  </div>
                </button>
                <button onClick={() => onDeleteMeeting(m.id)} className="opacity-0 group-hover:opacity-100 text-xs text-red-600 hover:text-red-700">Remove</button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Meeting setup */}
      <div className="bg-white rounded-lg shadow border border-gray-200">
        <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between gap-3">
          <h2 className="font-semibold text-gray-800">Meeting Setup</h2>
          <div className="flex items-center gap-3">
            <span className="text-xs text-gray-500">{isEnded ? 'Viewing archived · edits still save' : 'Current draft'}</span>
            <button onClick={onNewMeeting} className="px-3 py-1.5 border border-gray-300 text-gray-600 text-sm rounded-lg hover:bg-gray-50">+ New Meeting</button>
          </div>
        </div>
        <div className="p-5 grid md:grid-cols-2 gap-5">
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Meeting Title</label>
              <input className={inputCls} value={meeting.title} onChange={e => persistMeeting({ title: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Date</label>
                <input type="date" className={inputCls} value={meeting.date || ''} onChange={e => persistMeeting({ date: e.target.value })} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Facilitator</label>
                <input className={inputCls} value={meeting.facilitator} onChange={e => persistMeeting({ facilitator: e.target.value })} placeholder="Name" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Time per item</label>
              <div className="flex items-center gap-2">
                <input type="number" min="1" max="30"
                  className="w-24 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-400"
                  value={Math.round(meeting.perItemSeconds / 60)}
                  onChange={e => {
                    const secs = Math.max(60, parseInt(e.target.value || '0') * 60);
                    persistMeeting({ perItemSeconds: secs });
                  }} />
                <span className="text-sm text-gray-500">minutes</span>
              </div>
            </div>
          </div>
          <div className="space-y-2">
            <label className="block text-xs font-medium text-gray-600 mb-1">Attendees</label>
            <div className="flex gap-2">
              <input className={inputCls}
                placeholder="Add a name and press enter"
                value={attendeeInput}
                onChange={e => setAttendeeInput(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && attendeeInput.trim()) {
                    persistMeeting({ attendees: [...(meeting.attendees || []), attendeeInput.trim()] });
                    setAttendeeInput('');
                  }
                }} />
              <button className="px-3 py-1.5 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200 font-medium"
                onClick={() => {
                  if (!attendeeInput.trim()) return;
                  persistMeeting({ attendees: [...(meeting.attendees || []), attendeeInput.trim()] });
                  setAttendeeInput('');
                }}>Add</button>
            </div>
            <div className="flex flex-wrap gap-1.5 min-h-[2rem] mt-2">
              {(meeting.attendees || []).map((a, i) => (
                <span key={i} className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                  {a}
                  <button onClick={() => persistMeeting({ attendees: meeting.attendees.filter((_, idx) => idx !== i) })} className="text-gray-500 hover:text-gray-800">×</button>
                </span>
              ))}
              {(meeting.attendees || []).length === 0 && <span className="text-xs text-gray-400 italic">No attendees added yet</span>}
            </div>
          </div>
        </div>
      </div>

      {/* Backlog */}
      <div className="bg-white rounded-lg shadow border border-gray-200">
        <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between flex-wrap gap-2">
          <h2 className="font-semibold text-gray-800">The Backlog</h2>
          <div className="flex items-center gap-2">
            <button onClick={onAddCritical} className="px-3 py-1.5 border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50 font-medium">🔥 Queue all critical</button>
            <button onClick={onNew} className="px-3 py-1.5 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 font-medium">+ New item</button>
          </div>
        </div>
        <div className="px-5 pt-4 pb-2 flex flex-wrap items-center gap-2">
          <input className="flex-1 min-w-[200px] border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-400"
            placeholder="Search items..." value={filter.search}
            onChange={e => setFilter({ ...filter, search: e.target.value })} />
          <select className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-400"
            value={filter.category} onChange={e => setFilter({ ...filter, category: e.target.value })}>
            <option value="all">All categories</option>
            {TD_CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
          <select className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-400"
            value={filter.status} onChange={e => setFilter({ ...filter, status: e.target.value })}>
            <option value="all">All statuses</option>
            {TD_STATUSES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
          <select className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-400"
            value={sortBy} onChange={e => setSortBy(e.target.value)}>
            <option value="priority">Sort: Priority</option>
            <option value="impact">Sort: Impact</option>
            <option value="effort">Sort: Effort</option>
            <option value="title">Sort: Title</option>
          </select>
        </div>
        <div className="p-5 pt-2 space-y-2">
          {items.length === 0 ? (
            <div className="text-center py-12 text-gray-400 text-sm">
              {allItems.length === 0 ? 'No items yet — click "+ New item" to begin.' : 'No items match the current filters.'}
            </div>
          ) : items.map(item => (
            <TdItemRow key={item.id} item={item}
              inQueue={meeting.queue.includes(item.id)}
              onEdit={() => onEdit(item)}
              onDelete={() => onDelete(item.id)}
              onToggleQueue={() => onToggleQueue(item.id)} />
          ))}
        </div>
      </div>

      {/* Begin meeting footer */}
      <div className="flex items-center justify-between bg-white rounded-lg shadow border border-gray-200 px-5 py-3">
        <div className="text-sm text-gray-600">
          <span className="font-bold text-gray-900">{meeting.queue.length}</span> item{meeting.queue.length === 1 ? '' : 's'} queued
          {meeting.queue.length > 0 && <> · est. <span className="font-bold text-gray-900">{Math.round(meeting.queue.length * meeting.perItemSeconds / 60)}</span> min</>}
        </div>
        <button onClick={onStart} disabled={meeting.queue.length === 0 || isEnded}
          className="px-5 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 font-medium disabled:opacity-40 disabled:cursor-not-allowed"
          title={isEnded ? 'This meeting is archived — start a new one' : ''}>
          ▶ {meeting.started ? 'Resume Meeting' : 'Begin Meeting'}
        </button>
      </div>
    </div>
  );
};

const TdItemRow = ({ item, inQueue, onEdit, onDelete, onToggleQueue }) => {
  const cat = TD_CATEGORIES.find(c => c.id === item.category);
  const score = tdPriority(item.impact, item.effort);
  const tier = tdTier(score);
  const status = TD_STATUSES.find(s => s.id === item.status);
  return (
    <article className={`border rounded-lg group transition-colors ${inQueue ? 'border-red-300 bg-red-50/30' : 'border-gray-200 hover:border-red-200 bg-white'}`}>
      <div className="grid grid-cols-[auto_auto_1fr_auto] gap-0">
        <button onClick={onToggleQueue}
          className={`px-3 flex items-center justify-center text-lg transition-colors border-r ${inQueue ? 'bg-red-600 text-white border-red-600' : 'border-gray-200 text-gray-400 hover:bg-gray-50 hover:text-gray-700'}`}
          title={inQueue ? 'Remove from queue' : 'Add to queue'}>
          {inQueue ? '✓' : '+'}
        </button>
        <div className={`px-4 py-3 text-center min-w-[90px] flex flex-col justify-center border-r ${inQueue ? 'border-red-200' : 'border-gray-200'}`}>
          <div className="text-2xl font-bold leading-none" style={{ color: tier.color }}>{score}</div>
          <div className="text-[10px] uppercase tracking-wide text-gray-500 mt-1 font-medium">Priority</div>
        </div>
        <div className="px-4 py-3 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded text-white" style={{ backgroundColor: cat?.color }}>{cat?.short}</span>
            <span className={`text-[10px] font-semibold uppercase px-2 py-0.5 rounded ${tdTierPill(tier.tier)}`}>{tier.label}</span>
            <span className="text-xs text-gray-500">· {status?.label}</span>
            {item.owner && item.owner !== 'Unassigned' && (
              <span className="text-xs text-gray-500">· {item.owner}</span>
            )}
          </div>
          <h3 className="text-base font-semibold text-gray-900 mb-0.5">{item.title}</h3>
          {item.description && <p className="text-sm text-gray-600 line-clamp-2">{stripHtml(item.description)}</p>}
          <div className="flex items-center gap-3 mt-2 text-xs text-gray-500 flex-wrap">
            <span>Impact <span className="font-semibold text-gray-800 ml-0.5">{item.impact}</span>/5</span>
            <span>Effort <span className="font-semibold text-gray-800 ml-0.5">{item.effort}</span>/5</span>
            {item.decision && <span className="text-red-700">· Decided: {TD_DECISIONS.find(d => d.id === item.decision)?.label}</span>}
          </div>
        </div>
        <div className="flex flex-col opacity-0 group-hover:opacity-100 transition-opacity">
          <button onClick={onEdit} className="px-3 py-2 text-xs text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-tr-lg">Edit</button>
          <button onClick={onDelete} className="px-3 py-2 text-xs text-red-700 bg-red-50 hover:bg-red-100 rounded-br-lg">Remove</button>
        </div>
      </div>
    </article>
  );
};

// ---- Tech Debt: Item Editor ----
const TdItemEditor = ({ item, onSave, onClose }) => {
  const [form, setForm] = useState({
    id: item.id || null,
    title: item.title || '',
    description: item.description || '',
    category: item.category || 'code',
    impact: item.impact ?? 3,
    effort: item.effort ?? 3,
    owner: item.owner || 'Unassigned',
    status: item.status || 'new',
  });
  const score = tdPriority(form.impact, form.effort);
  const tier = tdTier(score);
  const inputCls = 'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-400';

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white">
          <h2 className="text-lg font-semibold text-gray-900">{form.id ? 'Edit Item' : 'New Item'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">×</button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
            <input className={inputCls} value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
              placeholder="What is the debt?" autoFocus />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <MiniWysiwyg value={form.description} onChange={v => setForm({ ...form, description: v })}
              placeholder="Why does this matter? What's the current pain?" minHeight="80px" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <select className={inputCls} value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}>
                {TD_CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Owner</label>
              <input className={inputCls} value={form.owner} onChange={e => setForm({ ...form, owner: e.target.value })} />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4 items-center border border-gray-200 rounded-lg p-4 bg-gray-50">
            <TdRatingControl label="Impact" value={form.impact} onChange={v => setForm({ ...form, impact: v })} />
            <TdRatingControl label="Effort" value={form.effort} onChange={v => setForm({ ...form, effort: v })} />
            <div className="text-center border-l border-gray-200 pl-4">
              <div className="text-xs uppercase tracking-wide text-gray-500 mb-1 font-medium">Priority</div>
              <div className="text-4xl font-bold" style={{ color: tier.color }}>{score}</div>
              <div className={`inline-block mt-1 text-[10px] font-semibold uppercase px-2 py-0.5 rounded ${tdTierPill(tier.tier)}`}>{tier.label}</div>
            </div>
          </div>
          {form.id && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select className={inputCls} value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                {TD_STATUSES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
            </div>
          )}
        </div>
        <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3 sticky bottom-0 bg-white">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
          <button onClick={() => form.title.trim() && onSave(form)} disabled={!form.title.trim()}
            className="px-5 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 disabled:opacity-50 font-medium">
            {form.id ? 'Save Changes' : 'Add Item'}
          </button>
        </div>
      </div>
    </div>
  );
};

const TdRatingControl = ({ label, value, onChange }) => (
  <div>
    <div className="text-xs uppercase tracking-wide text-gray-500 mb-2 font-medium">{label}</div>
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map(n => (
        <button key={n} onClick={() => onChange(n)}
          className={`w-8 h-8 rounded-lg text-sm font-semibold transition-colors ${n <= value ? 'bg-red-600 text-white' : 'bg-white text-gray-500 border border-gray-300 hover:bg-gray-100'}`}>{n}</button>
      ))}
    </div>
  </div>
);

// ---- Tech Debt: Meeting View ----
const TdMeetingView = ({ meeting, persistMeeting, timer, setTimer, currentItem, allItems, onNext, onPrev, onEnd, onUpdate, onAddAction, onRemoveAction, onToggleAction }) => {
  const [actionText, setActionText] = useState('');
  const [actionOwner, setActionOwner] = useState('');

  if (!meeting.started) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
        <p className="text-gray-400 mb-3">Meeting hasn't started yet.</p>
        <p className="text-sm text-gray-500">Queue items from the Backlog tab and press "Begin Meeting".</p>
      </div>
    );
  }

  if (!currentItem) return <div className="p-12 text-center text-gray-400">No current item.</div>;

  const cat = TD_CATEGORIES.find(c => c.id === currentItem.category);
  const score = tdPriority(currentItem.impact, currentItem.effort);
  const tier = tdTier(score);
  const isOvertime = timer.timeRemaining < 0;
  const progress = ((meeting.currentIndex + 1) / meeting.queue.length) * 100;

  return (
    <div className="space-y-5">
      {/* Progress + Timer strip */}
      <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
        <div className="px-5 py-3 flex items-center justify-between gap-3 flex-wrap">
          <span className="text-sm text-gray-600">Item <span className="font-bold text-gray-900">{meeting.currentIndex + 1}</span> of <span className="font-bold text-gray-900">{meeting.queue.length}</span></span>
          <div className="flex items-center gap-2">
            <span className={`text-2xl font-bold tabular-nums ${isOvertime ? 'text-red-600' : 'text-gray-900'}`}>{tdFmtTime(timer.timeRemaining)}</span>
            <button onClick={() => setTimer(t => ({ ...t, isRunning: !t.isRunning }))}
              className="px-3 py-1.5 border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50">{timer.isRunning ? '❚❚ Pause' : '▶ Resume'}</button>
            <button onClick={() => setTimer({ isRunning: false, timeRemaining: meeting.perItemSeconds })}
              className="px-3 py-1.5 border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50">Reset</button>
          </div>
          <span className="text-xs text-gray-500">{meeting.queue.length - meeting.currentIndex - 1} remaining</span>
        </div>
        <div className="h-1.5 bg-gray-100">
          <div className="h-full bg-red-500 transition-all duration-300" style={{ width: `${progress}%` }} />
        </div>
      </div>

      <div className="grid lg:grid-cols-[1fr_300px] gap-5">
        <section className="bg-white rounded-lg shadow border border-gray-200" key={currentItem.id}>
          <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between flex-wrap gap-2">
            <span className="text-xs text-gray-500 uppercase tracking-wide font-medium">Now Discussing</span>
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded text-white" style={{ backgroundColor: cat?.color }}>{cat?.short}</span>
              <span className={`text-[10px] font-semibold uppercase px-2 py-0.5 rounded ${tdTierPill(tier.tier)}`}>{tier.label} · {score}</span>
            </div>
          </div>
          <div className="p-6 space-y-5">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">{currentItem.title}</h2>
              {currentItem.description && <p className="text-gray-600">{currentItem.description}</p>}
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div className="bg-gray-50 rounded-lg px-3 py-2 text-center">
                <div className="text-xs uppercase tracking-wide text-gray-500 font-medium">Impact</div>
                <div className="text-2xl font-bold text-gray-900 mt-1">{currentItem.impact}<span className="text-gray-400 text-sm">/5</span></div>
              </div>
              <div className="bg-gray-50 rounded-lg px-3 py-2 text-center">
                <div className="text-xs uppercase tracking-wide text-gray-500 font-medium">Effort</div>
                <div className="text-2xl font-bold text-gray-900 mt-1">{currentItem.effort}<span className="text-gray-400 text-sm">/5</span></div>
              </div>
              <div className="bg-gray-50 rounded-lg px-3 py-2 text-center">
                <div className="text-xs uppercase tracking-wide text-gray-500 font-medium">Owner</div>
                <div className="text-sm font-medium text-gray-900 mt-1 truncate">{currentItem.owner || '—'}</div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Decision</label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {TD_DECISIONS.map(d => (
                  <button key={d.id}
                    onClick={() => onUpdate({
                      decision: d.id,
                      status: d.id === 'defer' ? 'deferred' : d.id === 'reject' ? 'rejected' : d.id === 'schedule' ? 'scheduled' : 'accepted',
                    })}
                    className={`border rounded-lg px-3 py-2 text-sm text-left font-medium transition-colors ${currentItem.decision === d.id ? 'bg-red-600 text-white border-red-600' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`}>
                    {currentItem.decision === d.id && '✓ '}{d.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Discussion Notes</label>
              <MiniWysiwyg key={currentItem.id} value={currentItem.notes || ''} onChange={v => onUpdate({ notes: v })}
                placeholder="Key points raised, context, concerns..." minHeight="80px" />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Action Items</label>
              <div className="space-y-1.5 mb-2">
                {(currentItem.actions || []).map(a => (
                  <div key={a.id} className="flex items-center gap-2 border border-gray-200 rounded-lg px-3 py-2 bg-gray-50">
                    <input type="checkbox" checked={!!a.done} onChange={() => onToggleAction(a.id)} className="rounded" />
                    <span className={`flex-1 text-sm ${a.done ? 'line-through text-gray-400' : 'text-gray-800'}`}>{a.text}</span>
                    <span className="text-xs text-gray-500">{a.owner}</span>
                    <button onClick={() => onRemoveAction(a.id)} className="text-gray-400 hover:text-red-600 text-sm">×</button>
                  </div>
                ))}
                {(currentItem.actions || []).length === 0 && (
                  <div className="text-xs text-gray-400 italic">No action items yet.</div>
                )}
              </div>
              <div className="grid grid-cols-[1fr_auto_auto] gap-2">
                <input className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-400"
                  placeholder="What needs doing?"
                  value={actionText} onChange={e => setActionText(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { onAddAction(actionText, actionOwner); setActionText(''); } }} />
                <input className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-32 focus:outline-none focus:ring-2 focus:ring-red-400"
                  placeholder="Owner"
                  value={actionOwner} onChange={e => setActionOwner(e.target.value)} />
                <button onClick={() => { if (actionText.trim()) { onAddAction(actionText, actionOwner); setActionText(''); } }}
                  className="px-4 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 font-medium">Add</button>
              </div>
            </div>
          </div>
        </section>

        {/* Side rail */}
        <aside className="bg-white rounded-lg shadow border border-gray-200 h-max">
          <div className="px-4 py-3 border-b border-gray-100 text-sm font-semibold text-gray-800">Queue ({meeting.queue.length})</div>
          <ul className="divide-y divide-gray-100">
            {meeting.queue.map((id, i) => {
              const it = allItems.find(x => x.id === id);
              if (!it) return null;
              const isCurrent = i === meeting.currentIndex;
              return (
                <li key={id} className={`px-4 py-2 ${isCurrent ? 'bg-red-50' : ''}`}>
                  <div className="flex items-baseline gap-2">
                    <span className={`text-xs font-bold ${isCurrent ? 'text-red-600' : 'text-gray-400'}`}>{i + 1}.</span>
                    <span className={`text-sm flex-1 truncate ${isCurrent ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>{it.title}</span>
                  </div>
                  {it.decision && (
                    <div className="text-[10px] text-gray-500 ml-5 mt-0.5 truncate">→ {TD_DECISIONS.find(d => d.id === it.decision)?.label}</div>
                  )}
                </li>
              );
            })}
          </ul>
        </aside>
      </div>

      {/* Footer controls */}
      <div className="flex items-center justify-between bg-white rounded-lg shadow border border-gray-200 px-5 py-3">
        <div className="flex items-center gap-2">
          <button onClick={onPrev} disabled={meeting.currentIndex === 0}
            className="px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">◀ Prev</button>
          <button onClick={onNext} disabled={meeting.currentIndex >= meeting.queue.length - 1}
            className="px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">Next ▶</button>
        </div>
        <button onClick={onEnd} className="px-5 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 font-medium">■ End Meeting</button>
      </div>
    </div>
  );
};

// ---- Tech Debt: Summary View ----
const TdSummaryView = ({ meeting, items, onNewMeeting, onExport, onCopy, copied }) => {
  const discussed = items.filter(i => meeting.queue.includes(i.id));
  const allActions = discussed.flatMap(i => (i.actions || []).map(a => ({ ...a, itemTitle: i.title })));
  const decisionCounts = {};
  discussed.forEach(i => { if (i.decision) decisionCounts[i.decision] = (decisionCounts[i.decision] || 0) + 1; });
  const byDecision = {};
  discussed.forEach(i => {
    const k = i.decision || 'none';
    if (!byDecision[k]) byDecision[k] = [];
    byDecision[k].push(i);
  });

  return (
    <div className="space-y-5">
      <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
        <div className="flex items-baseline justify-between mb-4 gap-3 flex-wrap">
          <div>
            <div className="text-xs uppercase tracking-wide text-gray-500 mb-1 font-medium">Meeting Summary</div>
            <h2 className="text-2xl font-bold text-gray-900">{meeting.title}</h2>
            <div className="text-sm text-gray-500 mt-1">
              {meeting.date}
              {meeting.facilitator && ` · facilitated by ${meeting.facilitator}`}
              {meeting.endedAt && ` · ended ${new Date(meeting.endedAt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`}
            </div>
            {meeting.attendees?.length > 0 && (
              <div className="text-xs text-gray-500 mt-2">{meeting.attendees.length} attendee{meeting.attendees.length === 1 ? '' : 's'}: {meeting.attendees.join(', ')}</div>
            )}
          </div>
          <button onClick={onNewMeeting} className="px-3 py-1.5 border border-gray-300 text-gray-600 text-sm rounded-lg hover:bg-gray-50">+ New Meeting</button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
          <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
            <div className="text-xs text-gray-500 uppercase tracking-wide font-medium">Items discussed</div>
            <div className="text-2xl font-bold text-gray-900 mt-1">{discussed.length}</div>
          </div>
          <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
            <div className="text-xs text-gray-500 uppercase tracking-wide font-medium">Action items</div>
            <div className="text-2xl font-bold text-gray-900 mt-1">{allActions.length}</div>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3">
            <div className="text-xs text-green-700 uppercase tracking-wide font-medium">Accepted</div>
            <div className="text-2xl font-bold text-green-700 mt-1">{(decisionCounts.accept || 0) + (decisionCounts.schedule || 0)}</div>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
            <div className="text-xs text-amber-700 uppercase tracking-wide font-medium">Deferred / Rejected</div>
            <div className="text-2xl font-bold text-amber-700 mt-1">{(decisionCounts.defer || 0) + (decisionCounts.reject || 0)}</div>
          </div>
        </div>

        <div className="space-y-3">
          {discussed.map((i, idx) => {
            const cat = TD_CATEGORIES.find(c => c.id === i.category);
            const dec = TD_DECISIONS.find(d => d.id === i.decision);
            const tier = tdTier(tdPriority(i.impact, i.effort));
            return (
              <div key={i.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-baseline gap-2 mb-1 flex-wrap">
                  <span className="text-sm text-gray-400">{idx + 1}.</span>
                  <h3 className="text-base font-semibold text-gray-900 flex-1">{i.title}</h3>
                  <span className="text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded text-white" style={{ backgroundColor: cat?.color }}>{cat?.short}</span>
                  <span className={`text-[10px] font-semibold uppercase px-2 py-0.5 rounded ${tdTierPill(tier.tier)}`}>{tier.label}</span>
                </div>
                <div className="text-xs text-gray-500 mb-1.5">
                  Impact {i.impact}/5 · Effort {i.effort}/5 · Priority {tdPriority(i.impact, i.effort)}
                </div>
                <div className={`text-sm mb-1 ${i.decision ? 'text-red-700 font-medium' : 'text-gray-400 italic'}`}>
                  Decision: {dec?.label || 'none recorded'}
                </div>
                {i.notes && <div className="text-sm text-gray-600 mb-2 mt-2"><span className="text-xs text-gray-500 uppercase tracking-wide font-medium">Notes:</span> {i.notes}</div>}
                {i.actions?.length > 0 && (
                  <ul className="text-sm space-y-0.5 mt-2">
                    {i.actions.map(a => (
                      <li key={a.id} className={a.done ? 'line-through text-gray-400' : 'text-gray-700'}>
                        ▸ {a.text} — <span className="text-gray-500 text-xs">{a.owner}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
          {discussed.length === 0 && <div className="text-sm text-gray-400 italic text-center py-8">Nothing was queued for this meeting.</div>}
        </div>
      </div>

      {discussed.length > 0 && (
        <div className="bg-white rounded-lg shadow border border-gray-200">
          <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
            <h3 className="font-semibold text-gray-800">Decisions</h3>
            <span className="text-xs text-gray-500">{discussed.filter(i => i.decision).length} of {discussed.length} recorded</span>
          </div>
          <div className="p-5 space-y-3">
            {TD_DECISIONS.map(d => {
              const list = byDecision[d.id];
              if (!list || list.length === 0) return null;
              return (
                <div key={d.id} className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="px-4 py-2 bg-gray-50 flex items-center justify-between">
                    <span className="text-sm font-semibold text-gray-800">{d.label}</span>
                    <span className="text-xs font-bold text-gray-700 bg-white border border-gray-200 px-2 py-0.5 rounded-full">{list.length}</span>
                  </div>
                  <ul className="divide-y divide-gray-100">
                    {list.map(i => {
                      const cat = TD_CATEGORIES.find(c => c.id === i.category);
                      return (
                        <li key={i.id} className="px-4 py-3 flex items-start gap-3">
                          <span className="text-[10px] font-semibold uppercase px-1.5 py-0.5 mt-0.5 rounded text-white shrink-0" style={{ backgroundColor: cat?.color }}>
                            {cat?.short}
                          </span>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-gray-900">{i.title}</div>
                            {i.notes && <div className="text-xs text-gray-500 mt-0.5 italic">"{i.notes}"</div>}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              );
            })}
            {byDecision['none'] && byDecision['none'].length > 0 && (
              <div className="border border-dashed border-amber-300 bg-amber-50 rounded-lg p-3 text-center text-sm text-amber-800">
                {byDecision['none'].length} item{byDecision['none'].length === 1 ? '' : 's'} had no decision recorded.
              </div>
            )}
          </div>
        </div>
      )}

      {allActions.length > 0 && (
        <div className="bg-white rounded-lg shadow border border-gray-200">
          <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
            <h3 className="font-semibold text-gray-800">Action Items</h3>
            <span className="text-xs font-bold text-gray-700 bg-gray-100 px-2 py-0.5 rounded-full">{allActions.length}</span>
          </div>
          <ul className="divide-y divide-gray-100">
            {allActions.map((a, idx) => (
              <li key={`${a.id}-${idx}`} className="px-4 py-3 grid grid-cols-[auto_1fr_auto] gap-3 items-start">
                <span className="text-xs font-bold text-gray-400 mt-0.5 tabular-nums">{String(idx + 1).padStart(2, '0')}</span>
                <div className="min-w-0">
                  <div className={`text-sm ${a.done ? 'line-through text-gray-400' : 'text-gray-900'}`}>{a.text}</div>
                  <div className="text-xs text-gray-500 italic mt-0.5">re: {a.itemTitle}</div>
                </div>
                <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 font-medium whitespace-nowrap">👤 {a.owner}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {discussed.length === 0 && (
        <div className="bg-white rounded-lg border border-dashed border-gray-300 p-10 text-center text-sm text-gray-400 italic">
          No items were discussed in this session.
        </div>
      )}

      <div className="bg-white rounded-lg shadow border border-gray-200 p-5">
        <h3 className="text-sm font-semibold text-gray-800 mb-3">Export</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {[
            { fmt: 'md',   label: 'Markdown Minutes', hint: 'Meeting notes for sharing' },
            { fmt: 'csv',  label: 'Backlog CSV',      hint: 'Full backlog with priorities' },
            { fmt: 'json', label: 'JSON Snapshot',    hint: 'Raw data for archives' },
          ].map(opt => (
            <div key={opt.fmt} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
              <div className="text-xs text-gray-500 uppercase tracking-wide font-medium">{opt.hint}</div>
              <div className="text-base font-semibold text-gray-900 mb-3">{opt.label}</div>
              <div className="flex gap-2">
                <button onClick={() => onExport(opt.fmt)} className="px-3 py-1.5 bg-red-600 text-white text-xs rounded-lg hover:bg-red-700 font-medium">↓ Download</button>
                <button onClick={() => onCopy(opt.fmt)} className="px-3 py-1.5 border border-gray-300 text-gray-700 text-xs rounded-lg hover:bg-white font-medium">
                  {copied === opt.fmt ? '✓ Copied' : '📋 Copy'}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// MANAGER CONSOLE MODULE
// ============================================================================

const MC_MONTH_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const mcParseQuarter = (q) => {
  const m = /^(\d{4})-Q([1-4])$/.exec(q || '');
  if (!m) {
    const d = new Date();
    return { year: d.getFullYear(), quarter: Math.floor(d.getMonth() / 3) + 1 };
  }
  return { year: parseInt(m[1]), quarter: parseInt(m[2]) };
};
const mcCurrentQuarter = () => {
  const d = new Date();
  return `${d.getFullYear()}-Q${Math.floor(d.getMonth() / 3) + 1}`;
};
const mcQuarterMonths = (q) => {
  const { year, quarter } = mcParseQuarter(q);
  const start = (quarter - 1) * 3;
  return [0, 1, 2].map(i => `${year}-${String(start + i + 1).padStart(2, '0')}`);
};
const mcMonthLabel = (ym) => {
  const [y, m] = ym.split('-');
  return `${MC_MONTH_SHORT[parseInt(m) - 1]} ${y}`;
};
const mcQuarterLabel = (q) => {
  const { year, quarter } = mcParseQuarter(q);
  const months = mcQuarterMonths(q);
  return `Q${quarter} · ${year} · ${MC_MONTH_SHORT[parseInt(months[0].split('-')[1]) - 1]} — ${MC_MONTH_SHORT[parseInt(months[2].split('-')[1]) - 1]} ${year}`;
};
const mcShiftQuarter = (q, delta) => {
  const { year, quarter } = mcParseQuarter(q);
  let n = quarter + delta, y = year;
  while (n < 1) { n += 4; y -= 1; }
  while (n > 4) { n -= 4; y += 1; }
  return `${y}-Q${n}`;
};
const mcResolveLabel = (template, q) => {
  if (!template) return '';
  const months = mcQuarterMonths(q);
  const startShort = mcMonthLabel(months[0]);
  const endShort = mcMonthLabel(months[2]);
  return String(template).replace(/\{QSTART_SHORT\}/g, startShort).replace(/\{QEND_SHORT\}/g, endShort);
};
const mcTargetLabel = (kpi, q) => {
  if (kpi.displayLabel) return mcResolveLabel(kpi.displayLabel, q);
  const arrow = kpi.direction === 'lower' ? '≤' : '≥';
  const unitSuffix = kpi.unit === 'percent' ? '%' : kpi.unit === 'hours' ? 'h' : kpi.unit === 'minutes' ? 'm' : '';
  return `${arrow} ${kpi.target}${unitSuffix}${kpi.cumulative ? ' (cumulative)' : ''}`;
};
const mcScoreKpi = (kpi, actual, monthIdx) => {
  const a = Number(actual);
  if (actual === undefined || actual === null || actual === '' || Number.isNaN(a)) return null;
  const target = Number(kpi.target) || 0;
  if (kpi.unit === 'boolean') return a ? 1 : 0;
  if (target === 0 && kpi.direction !== 'lower') return null;
  const eff = kpi.cumulative ? target * (monthIdx + 1) / 3 : target;
  if (kpi.direction === 'lower') {
    if (a === 0) return 1.2;
    return Math.min(1.2, eff / a);
  }
  return Math.min(1.2, a / (eff || 1));
};
const mcRag = (score) => {
  if (score == null) return { label: '—', color: '#94a3b8', bg: '#f1f5f9' };
  if (score >= 0.9) return { label: score.toFixed(2), color: '#166534', bg: '#dcfce7' };
  if (score >= 0.7) return { label: score.toFixed(2), color: '#92400e', bg: '#fef3c7' };
  return { label: score.toFixed(2), color: '#991b1b', bg: '#fee2e2' };
};
const mcFlattenKpis = (okrTemplate) => {
  const out = [];
  (okrTemplate || []).forEach(obj => (obj.keyResults || []).forEach(kr => (kr.kpis || []).forEach(kpi =>
    out.push({ kpi, objective: obj, keyResult: kr })
  )));
  return out;
};
const mcScoreObjective = (obj, evalForMonth, monthIdx) => {
  const scores = [];
  (obj.keyResults || []).forEach(kr => (kr.kpis || []).forEach(kpi => {
    const entry = evalForMonth?.[kpi.id];
    const s = mcScoreKpi(kpi, entry?.actual, monthIdx);
    if (s != null) scores.push(s);
  }));
  if (scores.length === 0) return null;
  return scores.reduce((a, b) => a + b, 0) / scores.length;
};
const mcScoreOverall = (okrTemplate, evalForMonth, monthIdx) => {
  const scores = (okrTemplate || []).map(obj => mcScoreObjective(obj, evalForMonth, monthIdx)).filter(s => s != null);
  if (scores.length === 0) return null;
  return scores.reduce((a, b) => a + b, 0) / scores.length;
};
const mcNewId = (prefix) => `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;

// ---- Sidebar ----
const ManagerConsoleSidebar = ({ view, onView, teamLabel, onOpenSettings, showOncall }) => {
  const groups = [
    {
      key: 'overview', label: 'Overview', enabled: true,
      items: [
        { id: 'executive', label: 'Executive View', icon: '📈' },
      ],
    },
    {
      key: 'team-reviews', label: 'Team Reviews', enabled: true,
      items: [
        { id: 'dashboard', label: 'Dashboard', icon: '📊' },
        { id: 'monthly',   label: 'Monthly Review', icon: '📝' },
        { id: 'team',      label: 'Team', icon: '👥' },
        { id: 'okr',       label: 'OKR Framework', icon: '🎯' },
        { id: 'edit-okr',  label: 'Edit OKRs', icon: '✏️' },
        { id: 'skills',    label: 'Skills Matrix', icon: '🧠' },
      ],
    },
    {
      key: 'hiring', label: 'Hiring', enabled: true,
      items: [
        { id: 'pipeline',   label: 'Candidate Pipeline', icon: '📇' },
        { id: 'onboarding', label: 'Onboarding',         icon: '🧭' },
      ],
    },
    {
      key: 'development', label: 'Development', enabled: true,
      items: [
        { id: 'coaching',   label: 'Coaching Logs',      icon: '📓' },
        { id: 'one-on-one', label: '1:1 Planner',         icon: '🧩' },
        { id: 'idp',        label: 'Development Plans',  icon: '🌱' },
      ],
    },
    {
      key: 'operations', label: 'Operations', enabled: true,
      items: [
        ...(showOncall ? [{ id: 'oncall', label: 'On-call Rotation', icon: '🚨' }] : []),
        { id: 'pto',   label: 'Time Off',          icon: '🏖️' },
        { id: 'pulse', label: 'Team Health Pulse', icon: '❤️' },
      ],
    },
    {
      key: 'people-ops', label: 'People Ops', enabled: true,
      items: [
        { id: 'comp',       label: 'Compensation',    icon: '💰' },
        { id: 'retention',  label: 'Retention Risk',  icon: '🚨' },
        { id: 'succession', label: 'Succession Map',  icon: '🔗' },
      ],
    },
  ];
  return (
    <aside className="w-60 bg-white border-r border-gray-200 min-h-[calc(100vh-150px)] flex-shrink-0">
      <div className="px-4 py-4 border-b border-gray-100">
        <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Workspace</div>
        <button onClick={onOpenSettings} className="text-sm font-semibold text-gray-900 hover:text-pink-700 truncate max-w-full text-left">
          {teamLabel || 'My Team'} <span className="text-gray-300">⚙</span>
        </button>
      </div>
      {groups.map(g => (
        <div key={g.key} className={`px-3 py-3 ${g.enabled ? '' : 'opacity-50'}`}>
          <div className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider px-2 mb-1 flex items-center justify-between">
            <span>{g.label}</span>
            {!g.enabled && <span className="text-[9px] text-gray-300 normal-case font-medium">soon</span>}
          </div>
          {g.enabled ? g.items.map(it => (
            <button key={it.id} onClick={() => onView(it.id)}
              className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-sm text-left transition-colors ${view === it.id ? 'bg-pink-50 text-pink-800 font-medium' : 'text-gray-600 hover:bg-gray-50'}`}>
              <span>{it.icon}</span>
              <span>{it.label}</span>
            </button>
          )) : null}
        </div>
      ))}
    </aside>
  );
};

// ---- Quarter Switcher ----
const ManagerQuarterSwitcher = ({ quarter, onChange }) => (
  <div className="inline-flex items-center gap-2 bg-gray-100 rounded-lg p-1">
    <button onClick={() => onChange(mcShiftQuarter(quarter, -1))} className="px-2 py-1 text-sm text-gray-600 hover:text-pink-700">◂</button>
    <span className="text-sm font-medium text-gray-800 px-2">{mcQuarterLabel(quarter)}</span>
    <button onClick={() => onChange(mcShiftQuarter(quarter, 1))} className="px-2 py-1 text-sm text-gray-600 hover:text-pink-700">▸</button>
    <button onClick={() => onChange(mcCurrentQuarter())} className="px-2 py-1 text-xs text-gray-500 hover:text-pink-700 border-l border-gray-300">Now</button>
  </div>
);

// ---- Team Roster ----
const ManagerTeamView = ({ ws, onAdd, onUpdate, onRemove, onSyncFromTeams }) => {
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: '', role: '', group: '', notes: '' });
  const [showAdd, setShowAdd] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const startEdit = (m) => {
    setEditingId(m.id);
    setForm({ name: m.name, role: m.role || '', group: m.groupName || '', notes: m.notes || '' });
  };
  const saveEdit = async () => {
    await onUpdate(editingId, form);
    setEditingId(null);
  };
  const submitAdd = async () => {
    if (!form.name.trim()) return;
    await onAdd(form);
    setForm({ name: '', role: '', group: '', notes: '' });
    setShowAdd(false);
  };
  const handleSync = async () => {
    setSyncing(true);
    const r = await onSyncFromTeams();
    setSyncing(false);
    if (r?.success === false) { alert(r.error || 'Sync failed'); return; }
    if (r?.added === 0 && r?.totalFromTeams === 0) alert('No teams found where you are listed as the team lead.');
    else if (r?.added === 0) alert(`No new members to add — all ${r.totalFromTeams} member${r.totalFromTeams === 1 ? '' : 's'} from your team(s) are already in the workspace.`);
    else alert(`Imported ${r.added} new member${r.added === 1 ? '' : 's'} from your team(s).`);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Team</h2>
          <p className="text-sm text-gray-500">{ws.members.length} member{ws.members.length === 1 ? '' : 's'}</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={handleSync} disabled={syncing}
            className="px-3 py-1.5 border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50 font-medium disabled:opacity-50"
            title="Pull members from teams where you're the team lead">
            {syncing ? 'Syncing…' : '↻ Sync from team'}
          </button>
          <button onClick={() => { setShowAdd(true); setForm({ name: '', role: '', group: '', notes: '' }); }}
            className="px-3 py-1.5 bg-pink-600 text-white text-sm rounded-lg hover:bg-pink-700 font-medium">+ Add Member</button>
        </div>
      </div>

      {showAdd && (
        <div className="bg-pink-50 border border-pink-200 rounded-lg p-4 mb-4 space-y-2">
          <input autoFocus value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Name"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
          <div className="grid grid-cols-2 gap-2">
            <input value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))} placeholder="Role (optional)"
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            <input value={form.group} onChange={e => setForm(f => ({ ...f, group: e.target.value }))} placeholder="Group / sub-team (optional)"
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm" />
          </div>
          <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Notes (optional)" rows={2}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
          <div className="flex gap-2">
            <button onClick={submitAdd} className="px-3 py-1.5 bg-pink-600 text-white text-sm rounded hover:bg-pink-700">Add</button>
            <button onClick={() => setShowAdd(false)} className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
          </div>
        </div>
      )}

      {ws.members.length === 0 && !showAdd ? (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <p className="text-gray-400 mb-3">No team members yet.</p>
          <button onClick={() => setShowAdd(true)} className="px-4 py-2 bg-pink-600 text-white text-sm rounded-lg hover:bg-pink-700">+ Add Your First Member</button>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 divide-y divide-gray-100">
          {ws.members.map(m => editingId === m.id ? (
            <div key={m.id} className="p-4 bg-gray-50 space-y-2">
              <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              <div className="grid grid-cols-2 gap-2">
                <input value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))} placeholder="Role" className="border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                <input value={form.group} onChange={e => setForm(f => ({ ...f, group: e.target.value }))} placeholder="Group" className="border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              </div>
              <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              <div className="flex gap-2">
                <button onClick={saveEdit} className="px-3 py-1.5 bg-pink-600 text-white text-sm rounded hover:bg-pink-700">Save</button>
                <button onClick={() => setEditingId(null)} className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
              </div>
            </div>
          ) : (
            <div key={m.id} className="p-4 flex items-start justify-between group">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-gray-900">{m.name}</span>
                  {m.role && <span className="text-sm text-gray-500">· {m.role}</span>}
                  {m.groupName && <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">{m.groupName}</span>}
                </div>
                {m.notes && <p className="text-sm text-gray-500 mt-1">{m.notes}</p>}
              </div>
              <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                <button onClick={() => startEdit(m)} className="px-2 py-1 text-xs text-blue-700 bg-blue-50 rounded hover:bg-blue-100">Edit</button>
                <button onClick={() => requestConfirm(`Remove ${m.name}? Their evaluations will also be deleted.`, () => onRemove(m.id))}
                  className="px-2 py-1 text-xs text-red-700 bg-red-50 rounded hover:bg-red-100">Remove</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ---- OKR Framework (read-only) ----
const ManagerOkrView = ({ ws, activeQuarter, onEdit }) => {
  const tpl = ws.okrTemplate || [];
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-bold text-gray-900">OKR Framework</h2>
          <p className="text-sm text-gray-500">Reference for {mcQuarterLabel(activeQuarter)}</p>
        </div>
        <button onClick={onEdit} className="px-3 py-1.5 bg-pink-600 text-white text-sm rounded-lg hover:bg-pink-700 font-medium">Edit OKRs →</button>
      </div>
      {tpl.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <p className="text-gray-400 mb-3">No OKRs defined yet.</p>
          <button onClick={onEdit} className="px-4 py-2 bg-pink-600 text-white text-sm rounded-lg hover:bg-pink-700">→ Edit OKRs</button>
        </div>
      ) : tpl.map(obj => (
        <div key={obj.id} className="bg-white rounded-lg border border-gray-200 p-4 mb-3">
          <h3 className="font-semibold text-gray-900 text-base mb-2">🎯 {obj.name}</h3>
          {(obj.keyResults || []).map(kr => (
            <div key={kr.id} className="ml-4 mb-3 border-l-2 border-pink-200 pl-3">
              <div className="text-sm font-medium text-pink-800 mb-1">↳ {kr.name}</div>
              {(kr.kpis || []).map(kpi => (
                <div key={kpi.id} className="flex items-center gap-3 py-1.5 text-sm">
                  <span className="text-gray-800 min-w-0 flex-1 truncate">• {kpi.name}</span>
                  <span className="text-xs text-gray-500">{mcTargetLabel(kpi, activeQuarter)}</span>
                  <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${kpi.direction === 'lower' ? 'bg-blue-50 text-blue-700' : 'bg-emerald-50 text-emerald-700'}`}>
                    {kpi.direction === 'lower' ? '↓ lower' : '↑ higher'}
                  </span>
                  {kpi.cumulative && <span className="px-2 py-0.5 text-xs rounded-full bg-amber-50 text-amber-700 font-medium">cumulative</span>}
                </div>
              ))}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

// Parse OKR markdown into the workspace tree: [{ id, name, keyResults: [{ id, name, kpis: [{ ... }] }] }]
// Recognized format:
//   ## Objective Name                          (## but not ###)
//   ### KR Title                               (key result)
//   | KPI | Unit | Target | Direction |        (table header)
//   |---|---|---|---|                          (separator)
//   | KPI name | percent | ≥ 90% | higher is better |  (data row)
const parseOkrMarkdown = (md) => {
  const lines = (md || '').split(/\r?\n/);
  const objectives = [];
  let obj = null;
  let kr = null;
  let header = null;
  const UNITS = ['percent', 'count', 'hours', 'minutes', 'boolean'];
  const cleanName = (s) => s.replace(/^[#\s]+/, '').replace(/\s*[—\-–·]\s*/, ' — ').trim();
  const isMetaHeading = (s) => /^(scoring|source|placeholders|notes?)\b/i.test(s.replace(/^[#\s]+/, ''));

  for (const raw of lines) {
    const line = raw.trim();
    if (!line) continue;
    if (/^###\s+/.test(line)) {
      // KR — only inside an objective
      if (!obj) continue;
      kr = { id: mcNewId('kr'), name: cleanName(line.slice(3)), kpis: [] };
      obj.keyResults.push(kr);
      header = null;
      continue;
    }
    if (/^##\s+/.test(line) && !/^###/.test(line)) {
      if (isMetaHeading(line)) { obj = null; kr = null; header = null; continue; }
      obj = { id: mcNewId('obj'), name: cleanName(line.slice(2)), keyResults: [] };
      objectives.push(obj);
      kr = null;
      header = null;
      continue;
    }
    if (!kr) continue;
    if (!line.startsWith('|')) continue;

    const cells = line.split('|').slice(1, -1).map(c => c.trim());
    if (cells.length === 0) continue;
    if (cells.every(c => /^:?-+:?$/.test(c))) continue; // separator row

    if (!header) { header = cells.map(c => c.toLowerCase()); continue; }

    const pickCell = (...keys) => {
      for (const k of keys) {
        const idx = header.findIndex(h => h.includes(k));
        if (idx >= 0) return cells[idx] || '';
      }
      return '';
    };
    const name = pickCell('kpi', 'name', 'metric') || cells[0] || '';
    if (!name) continue;
    const unitRaw = pickCell('unit').toLowerCase().replace(/[^a-z]/g, '');
    const unit = UNITS.includes(unitRaw) ? unitRaw : 'count';
    const targetRaw = pickCell('target');
    const directionRaw = pickCell('direction').toLowerCase();
    const numMatch = targetRaw.match(/-?\d+(?:\.\d+)?/);
    const target = numMatch ? parseFloat(numMatch[0]) : (unit === 'boolean' ? 1 : 0);
    const cumulative = /cumulative/i.test(targetRaw);
    const direction = /lower/.test(directionRaw) ? 'lower' : 'higher';

    kr.kpis.push({
      id: mcNewId('kpi'),
      name,
      unit,
      target,
      direction,
      cumulative,
      displayLabel: targetRaw.replace(/\*+/g, '').trim(), // strip markdown emphasis like "*(cumulative)*"
    });
  }
  return objectives;
};

// ---- Edit OKRs ----
const ManagerEditOkrView = ({ ws, onSave }) => {
  const [draft, setDraft] = useState(() => JSON.parse(JSON.stringify(ws.okrTemplate || [])));
  const [saving, setSaving] = useState(false);
  const original = JSON.stringify(ws.okrTemplate || []);
  const dirty = JSON.stringify(draft) !== original;

  const move = (arr, idx, delta) => {
    const j = idx + delta;
    if (j < 0 || j >= arr.length) return arr;
    const next = [...arr];
    [next[idx], next[j]] = [next[j], next[idx]];
    return next;
  };

  const addObjective = () => setDraft(d => [...d, { id: mcNewId('obj'), name: '', keyResults: [] }]);
  const removeObjective = (oIdx) => setDraft(d => d.filter((_, i) => i !== oIdx));
  const updateObjective = (oIdx, patch) => setDraft(d => d.map((o, i) => i === oIdx ? { ...o, ...patch } : o));
  const moveObjective = (oIdx, delta) => setDraft(d => move(d, oIdx, delta));

  const addKR = (oIdx) => setDraft(d => d.map((o, i) => i === oIdx ? { ...o, keyResults: [...(o.keyResults || []), { id: mcNewId('kr'), name: '', kpis: [] }] } : o));
  const removeKR = (oIdx, kIdx) => setDraft(d => d.map((o, i) => i === oIdx ? { ...o, keyResults: o.keyResults.filter((_, j) => j !== kIdx) } : o));
  const updateKR = (oIdx, kIdx, patch) => setDraft(d => d.map((o, i) => i === oIdx ? { ...o, keyResults: o.keyResults.map((kr, j) => j === kIdx ? { ...kr, ...patch } : kr) } : o));
  const moveKR = (oIdx, kIdx, delta) => setDraft(d => d.map((o, i) => i === oIdx ? { ...o, keyResults: move(o.keyResults, kIdx, delta) } : o));

  const addKpi = (oIdx, kIdx) => setDraft(d => d.map((o, i) => i === oIdx ? {
    ...o, keyResults: o.keyResults.map((kr, j) => j === kIdx ? {
      ...kr, kpis: [...(kr.kpis || []), { id: mcNewId('kpi'), name: '', unit: 'count', target: 0, direction: 'higher', cumulative: false, displayLabel: '' }]
    } : kr)
  } : o));
  const removeKpi = (oIdx, kIdx, pIdx) => setDraft(d => d.map((o, i) => i === oIdx ? {
    ...o, keyResults: o.keyResults.map((kr, j) => j === kIdx ? { ...kr, kpis: kr.kpis.filter((_, k) => k !== pIdx) } : kr)
  } : o));
  const updateKpi = (oIdx, kIdx, pIdx, patch) => setDraft(d => d.map((o, i) => i === oIdx ? {
    ...o, keyResults: o.keyResults.map((kr, j) => j === kIdx ? { ...kr, kpis: kr.kpis.map((kpi, k) => k === pIdx ? { ...kpi, ...patch } : kpi) } : kr)
  } : o));
  const moveKpi = (oIdx, kIdx, pIdx, delta) => setDraft(d => d.map((o, i) => i === oIdx ? {
    ...o, keyResults: o.keyResults.map((kr, j) => j === kIdx ? { ...kr, kpis: move(kr.kpis, pIdx, delta) } : kr)
  } : o));

  const handleSave = async () => { setSaving(true); await onSave(draft); setSaving(false); };
  const handleDiscard = () => setDraft(JSON.parse(original));
  const handleReset = () => requestConfirm('Reset all OKRs? This will clear the template.', () => setDraft([]));

  const handleUploadMd = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const parsed = parseOkrMarkdown(text);
      if (parsed.length === 0) {
        alert('No OKRs found.\n\nExpected format:\n  ## Objective name\n  ### Key result\n  | KPI | Unit | Target | Direction |\n  | ... |');
      } else if (draft.length === 0) {
        setDraft(parsed);
      } else {
        const replace = confirm(
          `Found ${parsed.length} objective${parsed.length === 1 ? '' : 's'} in "${file.name}".\n\n` +
          `OK = Replace your current ${draft.length} objective${draft.length === 1 ? '' : 's'}\n` +
          `Cancel = Append to the end`
        );
        setDraft(replace ? parsed : [...draft, ...parsed]);
      }
    } catch (err) {
      alert('Could not read the file: ' + (err.message || err));
    } finally {
      e.target.value = ''; // allow re-uploading the same file
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Edit OKRs</h2>
          <p className="text-sm text-gray-500">Build your OKR tree. Use <code className="text-xs bg-gray-100 px-1 rounded">{'{QEND_SHORT}'}</code> or <code className="text-xs bg-gray-100 px-1 rounded">{'{QSTART_SHORT}'}</code> in display labels.</p>
        </div>
        <div className="flex items-center gap-2">
          {dirty && <span className="text-xs text-amber-700 bg-amber-50 px-2 py-1 rounded">● Unsaved</span>}
          <label className="px-3 py-1.5 border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50 cursor-pointer">
            ⬆ Upload .md
            <input type="file" accept=".md,.markdown,text/markdown,text/plain" onChange={handleUploadMd} className="hidden" />
          </label>
          <button onClick={handleDiscard} disabled={!dirty} className="px-3 py-1.5 border border-gray-300 text-gray-600 text-sm rounded-lg hover:bg-gray-50 disabled:opacity-40">Discard</button>
          <button onClick={handleReset} className="px-3 py-1.5 border border-red-200 text-red-600 text-sm rounded-lg hover:bg-red-50">Reset</button>
          <button onClick={handleSave} disabled={!dirty || saving} className="px-3 py-1.5 bg-pink-600 text-white text-sm rounded-lg hover:bg-pink-700 disabled:opacity-50">{saving ? 'Saving…' : 'Save'}</button>
        </div>
      </div>

      {draft.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <p className="text-gray-400 mb-3">Start by adding an objective.</p>
          <button onClick={addObjective} className="px-4 py-2 bg-pink-600 text-white text-sm rounded-lg hover:bg-pink-700">+ Add Objective</button>
        </div>
      ) : draft.map((obj, oIdx) => (
        <div key={obj.id} className="bg-white rounded-lg border border-gray-200 p-4 mb-3">
          <div className="flex items-start gap-2 mb-3">
            <span className="text-gray-400 pt-2">🎯</span>
            <input value={obj.name} onChange={e => updateObjective(oIdx, { name: e.target.value })} placeholder="Objective name"
              className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm font-semibold" />
            <button onClick={() => moveObjective(oIdx, -1)} disabled={oIdx === 0} className="px-2 py-1 text-xs text-gray-500 hover:text-gray-800 disabled:opacity-30">▲</button>
            <button onClick={() => moveObjective(oIdx, 1)} disabled={oIdx === draft.length - 1} className="px-2 py-1 text-xs text-gray-500 hover:text-gray-800 disabled:opacity-30">▼</button>
            <button onClick={() => removeObjective(oIdx)} className="px-2 py-1 text-xs text-red-600 hover:bg-red-50 rounded">✕</button>
          </div>

          {(obj.keyResults || []).map((kr, kIdx) => (
            <div key={kr.id} className="ml-4 mb-3 border-l-2 border-pink-200 pl-3">
              <div className="flex items-start gap-2 mb-2">
                <span className="text-pink-400 pt-2">↳</span>
                <input value={kr.name} onChange={e => updateKR(oIdx, kIdx, { name: e.target.value })} placeholder="Key result"
                  className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                <button onClick={() => moveKR(oIdx, kIdx, -1)} disabled={kIdx === 0} className="px-2 py-1 text-xs text-gray-500 hover:text-gray-800 disabled:opacity-30">▲</button>
                <button onClick={() => moveKR(oIdx, kIdx, 1)} disabled={kIdx === (obj.keyResults.length - 1)} className="px-2 py-1 text-xs text-gray-500 hover:text-gray-800 disabled:opacity-30">▼</button>
                <button onClick={() => removeKR(oIdx, kIdx)} className="px-2 py-1 text-xs text-red-600 hover:bg-red-50 rounded">✕</button>
              </div>

              {(kr.kpis || []).map((kpi, pIdx) => (
                <div key={kpi.id} className="ml-4 mb-2 bg-gray-50 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <input value={kpi.name} onChange={e => updateKpi(oIdx, kIdx, pIdx, { name: e.target.value })} placeholder="KPI name"
                      className="flex-1 border border-gray-300 rounded-lg px-2 py-1 text-sm" />
                    <button onClick={() => moveKpi(oIdx, kIdx, pIdx, -1)} disabled={pIdx === 0} className="px-1 py-0.5 text-xs text-gray-500 hover:text-gray-800 disabled:opacity-30">▲</button>
                    <button onClick={() => moveKpi(oIdx, kIdx, pIdx, 1)} disabled={pIdx === (kr.kpis.length - 1)} className="px-1 py-0.5 text-xs text-gray-500 hover:text-gray-800 disabled:opacity-30">▼</button>
                    <button onClick={() => removeKpi(oIdx, kIdx, pIdx)} className="px-1 py-0.5 text-xs text-red-600 hover:bg-red-50 rounded">✕</button>
                  </div>
                  <div className="grid grid-cols-4 gap-2 text-xs">
                    <label className="flex flex-col gap-1"><span className="text-gray-500">Unit</span>
                      <select value={kpi.unit} onChange={e => updateKpi(oIdx, kIdx, pIdx, { unit: e.target.value })} className="border border-gray-300 rounded px-2 py-1">
                        {['percent', 'count', 'hours', 'minutes', 'boolean'].map(u => <option key={u}>{u}</option>)}
                      </select>
                    </label>
                    <label className="flex flex-col gap-1"><span className="text-gray-500">Target</span>
                      <input type="number" value={kpi.target} onChange={e => updateKpi(oIdx, kIdx, pIdx, { target: parseFloat(e.target.value) || 0 })} className="border border-gray-300 rounded px-2 py-1" />
                    </label>
                    <label className="flex flex-col gap-1"><span className="text-gray-500">Direction</span>
                      <select value={kpi.direction} onChange={e => updateKpi(oIdx, kIdx, pIdx, { direction: e.target.value })} className="border border-gray-300 rounded px-2 py-1">
                        <option value="higher">higher = better</option>
                        <option value="lower">lower = better</option>
                      </select>
                    </label>
                    <label className="flex items-center gap-1 pt-4">
                      <input type="checkbox" checked={!!kpi.cumulative} onChange={e => updateKpi(oIdx, kIdx, pIdx, { cumulative: e.target.checked })} />
                      <span className="text-gray-700">Cumulative</span>
                    </label>
                  </div>
                  <input value={kpi.displayLabel || ''} onChange={e => updateKpi(oIdx, kIdx, pIdx, { displayLabel: e.target.value })}
                    placeholder="Display label (optional) — e.g. ≥ 95% by {QEND_SHORT}"
                    className="w-full mt-2 border border-gray-300 rounded px-2 py-1 text-xs" />
                </div>
              ))}
              <button onClick={() => addKpi(oIdx, kIdx)} className="ml-4 text-xs text-pink-700 hover:text-pink-900">+ Add KPI</button>
            </div>
          ))}
          <button onClick={() => addKR(oIdx)} className="ml-4 text-sm text-pink-700 hover:text-pink-900">+ Add Key Result</button>
        </div>
      ))}

      {draft.length > 0 && (
        <button onClick={addObjective} className="mt-2 px-4 py-2 bg-white border border-dashed border-pink-300 text-pink-700 text-sm rounded-lg hover:bg-pink-50">+ Add Objective</button>
      )}
    </div>
  );
};

// ---- Monthly Review ----
const ManagerMonthlyReviewView = ({ ws, activeQuarter, onQuarterChange, onSaveEvaluation }) => {
  const months = mcQuarterMonths(activeQuarter);
  const [memberId, setMemberId] = useState(ws.members[0]?.id || null);
  const [month, setMonth] = useState(months[0]);
  const [draft, setDraft] = useState({});
  const [notes, setNotes] = useState('');
  const saveTimer = useRef(null);

  useEffect(() => {
    if (!ws.members.find(m => m.id === memberId)) setMemberId(ws.members[0]?.id || null);
  }, [ws.members, memberId]);

  useEffect(() => {
    if (!months.includes(month)) setMonth(months[0]);
  }, [activeQuarter, month, months]);

  useEffect(() => {
    if (!memberId) { setDraft({}); setNotes(''); return; }
    const existing = ws.evaluations[memberId]?.[month] || {};
    const { _notes = '', ...kpiData } = existing;
    setDraft(kpiData);
    setNotes(_notes);
  }, [memberId, month, ws.evaluations]);

  const monthIdx = months.indexOf(month);

  const scheduleSave = (nextDraft, nextNotes) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      onSaveEvaluation(memberId, month, { ...nextDraft, _notes: nextNotes });
    }, 600);
  };

  const updateKpi = (kpiId, patch) => {
    const next = { ...draft, [kpiId]: { ...(draft[kpiId] || {}), ...patch } };
    setDraft(next);
    scheduleSave(next, notes);
  };
  const updateNotes = (val) => {
    setNotes(val);
    scheduleSave(draft, val);
  };

  if (ws.members.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
        <p className="text-gray-400 mb-3">Add at least one team member to start monthly reviews.</p>
      </div>
    );
  }
  if (!ws.okrTemplate || ws.okrTemplate.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
        <p className="text-gray-400 mb-3">Define your OKRs first.</p>
      </div>
    );
  }

  const overall = mcScoreOverall(ws.okrTemplate, { ...draft }, monthIdx);
  const overallRag = mcRag(overall);

  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Monthly Review</h2>
          <p className="text-sm text-gray-500">Auto-saves as you type</p>
        </div>
        <ManagerQuarterSwitcher quarter={activeQuarter} onChange={onQuarterChange} />
      </div>

      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <select value={memberId || ''} onChange={e => setMemberId(parseInt(e.target.value))} className="border border-gray-300 rounded-lg px-3 py-2 text-sm">
          {ws.members.map(m => <option key={m.id} value={m.id}>{m.name}{m.role ? ` — ${m.role}` : ''}</option>)}
        </select>
        <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
          {months.map(m => (
            <button key={m} onClick={() => setMonth(m)}
              className={`px-3 py-1.5 text-sm rounded font-medium ${month === m ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
              {mcMonthLabel(m)}
            </button>
          ))}
        </div>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-sm text-gray-500">Overall:</span>
          <span className="px-3 py-1 rounded-full text-sm font-semibold" style={{ background: overallRag.bg, color: overallRag.color }}>
            {overallRag.label}
          </span>
        </div>
      </div>

      {ws.okrTemplate.map(obj => {
        const objScore = mcScoreObjective(obj, draft, monthIdx);
        const rag = mcRag(objScore);
        return (
          <div key={obj.id} className="bg-white rounded-lg border border-gray-200 p-4 mb-3">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-900">🎯 {obj.name}</h3>
              <span className="px-3 py-1 rounded-full text-xs font-semibold" style={{ background: rag.bg, color: rag.color }}>{rag.label}</span>
            </div>
            {(obj.keyResults || []).map(kr => (
              <div key={kr.id} className="ml-4 mb-3 border-l-2 border-pink-200 pl-3">
                <div className="text-sm font-medium text-pink-800 mb-2">↳ {kr.name}</div>
                {(kr.kpis || []).map(kpi => {
                  const entry = draft[kpi.id] || {};
                  const score = mcScoreKpi(kpi, entry.actual, monthIdx);
                  const kpiRag = mcRag(score);
                  return (
                    <div key={kpi.id} className="grid grid-cols-12 gap-2 items-center py-1.5 text-sm">
                      <div className="col-span-4 truncate">• {kpi.name}</div>
                      <div className="col-span-3 text-xs text-gray-500 truncate">{mcTargetLabel(kpi, activeQuarter)}</div>
                      <input type="number" step="any" value={entry.actual ?? ''} onChange={e => updateKpi(kpi.id, { actual: e.target.value })}
                        placeholder="Actual" className="col-span-2 border border-gray-300 rounded px-2 py-1 text-sm" />
                      <input type="number" min="0" max="100" value={entry.confidence ?? ''} onChange={e => updateKpi(kpi.id, { confidence: e.target.value })}
                        placeholder="Conf %" className="col-span-2 border border-gray-300 rounded px-2 py-1 text-sm" />
                      <span className="col-span-1 px-2 py-0.5 rounded-full text-xs font-semibold text-center" style={{ background: kpiRag.bg, color: kpiRag.color }}>{kpiRag.label}</span>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        );
      })}

      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <label className="text-sm font-semibold text-gray-700 block mb-2">Manager Notes — {mcMonthLabel(month)}</label>
        <textarea value={notes} onChange={e => updateNotes(e.target.value)} rows={4} placeholder="Context, wins, risks, coaching moments…"
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
      </div>
    </div>
  );
};

// ---- Dashboard ----
const ManagerDashboardView = ({ ws, activeQuarter, onQuarterChange }) => {
  const months = mcQuarterMonths(activeQuarter);
  const tpl = ws.okrTemplate || [];

  if (ws.members.length === 0 || tpl.length === 0) {
    return (
      <div>
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <h2 className="text-xl font-bold text-gray-900">Dashboard</h2>
          <ManagerQuarterSwitcher quarter={activeQuarter} onChange={onQuarterChange} />
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <p className="text-gray-400">
            {ws.members.length === 0 ? 'Add team members to see the dashboard.' : 'Define OKRs to see the dashboard.'}
          </p>
        </div>
      </div>
    );
  }

  const memberMonthlyScores = ws.members.map(m => {
    const scores = months.map((mo, idx) => {
      const e = ws.evaluations[m.id]?.[mo] || {};
      return mcScoreOverall(tpl, e, idx);
    });
    const latest = [...scores].reverse().find(s => s != null) ?? null;
    let trend = 'steady';
    const defined = scores.filter(s => s != null);
    if (defined.length >= 2) {
      const diff = defined[defined.length - 1] - defined[defined.length - 2];
      if (diff > 0.05) trend = 'improving';
      else if (diff < -0.05) trend = 'declining';
    }
    return { member: m, scores, latest, trend };
  });

  const latestScores = memberMonthlyScores.map(r => r.latest).filter(s => s != null);
  const avg = latestScores.length ? latestScores.reduce((a, b) => a + b, 0) / latestScores.length : null;
  const onTrack = latestScores.filter(s => s >= 0.9).length;
  const atRisk = latestScores.filter(s => s >= 0.7 && s < 0.9).length;
  const offTrack = latestScores.filter(s => s < 0.7).length;
  const avgRag = mcRag(avg);

  const needsAttention = memberMonthlyScores
    .filter(r => r.latest != null && r.latest < 0.7)
    .sort((a, b) => (a.latest || 0) - (b.latest || 0));

  const objectiveSeries = tpl.map(obj => ({
    objective: obj,
    data: months.map((mo, idx) => {
      const scores = ws.members.map(m => {
        const e = ws.evaluations[m.id]?.[mo] || {};
        return mcScoreObjective(obj, e, idx);
      }).filter(s => s != null);
      return scores.length ? scores.reduce((a, b) => a + b, 0) / scores.length : null;
    }),
  }));

  const barMax = 1.2;

  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <h2 className="text-xl font-bold text-gray-900">Dashboard</h2>
        <ManagerQuarterSwitcher quarter={activeQuarter} onChange={onQuarterChange} />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="text-xs text-gray-500 uppercase tracking-wide">Team Avg</div>
          <div className="text-2xl font-bold mt-1" style={{ color: avgRag.color }}>{avgRag.label}</div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="text-xs text-gray-500 uppercase tracking-wide">On Track</div>
          <div className="text-2xl font-bold text-green-600 mt-1">{onTrack}</div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="text-xs text-gray-500 uppercase tracking-wide">At Risk</div>
          <div className="text-2xl font-bold text-amber-600 mt-1">{atRisk}</div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="text-xs text-gray-500 uppercase tracking-wide">Off Track</div>
          <div className="text-2xl font-bold text-red-600 mt-1">{offTrack}</div>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 mb-5 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 font-semibold text-gray-800 text-sm">Team Members</div>
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Member</th>
              {months.map(m => <th key={m} className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">{mcMonthLabel(m).split(' ')[0]}</th>)}
              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Latest</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Trend</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {memberMonthlyScores.map(r => (
              <tr key={r.member.id}>
                <td className="px-3 py-2 font-medium text-gray-900">{r.member.name}</td>
                {r.scores.map((s, i) => {
                  const rag = mcRag(s);
                  return <td key={i} className="px-3 py-2">
                    <span className="px-2 py-0.5 rounded-full text-xs font-semibold" style={{ background: rag.bg, color: rag.color }}>{rag.label}</span>
                  </td>;
                })}
                <td className="px-3 py-2">
                  <span className="px-2 py-0.5 rounded-full text-xs font-semibold" style={{ background: mcRag(r.latest).bg, color: mcRag(r.latest).color }}>{mcRag(r.latest).label}</span>
                </td>
                <td className="px-3 py-2 text-xs">
                  {r.trend === 'improving' && <span className="text-green-700">↑ improving</span>}
                  {r.trend === 'declining' && <span className="text-red-700">↓ declining</span>}
                  {r.trend === 'steady' && <span className="text-gray-500">→ steady</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-5">
        <div className="font-semibold text-gray-800 text-sm mb-3">Per-Objective Month-over-Month</div>
        <div className="space-y-3">
          {objectiveSeries.map(({ objective, data }) => (
            <div key={objective.id}>
              <div className="text-sm font-medium text-gray-700 mb-1 truncate">🎯 {objective.name}</div>
              <div className="flex items-end gap-2 h-20">
                {months.map((m, i) => {
                  const s = data[i];
                  const h = s == null ? 4 : Math.max(6, (s / barMax) * 70);
                  const rag = mcRag(s);
                  return (
                    <div key={m} className="flex-1 flex flex-col items-center gap-1">
                      <div className="w-full rounded-t" style={{ height: `${h}px`, background: s == null ? '#e5e7eb' : rag.color }} title={s == null ? '—' : s.toFixed(2)} />
                      <span className="text-[10px] text-gray-500">{mcMonthLabel(m).split(' ')[0]}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      {needsAttention.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="font-semibold text-red-800 text-sm mb-2">⚠ Needs Attention</div>
          <ul className="space-y-1">
            {needsAttention.map(r => (
              <li key={r.member.id} className="text-sm text-red-700">
                <span className="font-medium">{r.member.name}</span>
                <span className="text-red-500 ml-2">latest score {r.latest.toFixed(2)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

// ---- Workspace Settings ----
const ManagerWorkspaceSettings = ({ ws, onSave, onClose }) => {
  const [teamLabel, setTeamLabel] = useState(ws.settings.teamLabel || '');
  const [delegates, setDelegates] = useState(ws.settings.delegates || []);
  const [showOncall, setShowOncall] = useState(!!ws.settings.showOncall);
  const [delegateEmail, setDelegateEmail] = useState('');
  const [delegateAccess, setDelegateAccess] = useState('read');
  const [saving, setSaving] = useState(false);

  const addDelegate = () => {
    if (!delegateEmail.trim()) return;
    setDelegates([...delegates, { email: delegateEmail.trim(), access: delegateAccess }]);
    setDelegateEmail('');
  };
  const removeDelegate = (idx) => setDelegates(delegates.filter((_, i) => i !== idx));

  const handleSave = async () => {
    setSaving(true);
    await onSave({ teamLabel, delegates, showOncall });
    setSaving(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-base font-semibold">Workspace Settings</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">×</button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Team Label</label>
            <input value={teamLabel} onChange={e => setTeamLabel(e.target.value)} placeholder="e.g. Platform Eng, Growth Sales"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input type="checkbox" checked={showOncall} onChange={e => setShowOncall(e.target.checked)} />
            Show On-call Rotation <span className="text-xs text-gray-400">(in Operations)</span>
          </label>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Delegates</label>
            <p className="text-xs text-gray-500 mb-2">Grant a skip-level, co-manager, or HR partner access to this workspace.</p>
            <div className="space-y-1 mb-2">
              {delegates.length === 0 && <div className="text-xs text-gray-400 italic">No delegates.</div>}
              {delegates.map((d, i) => (
                <div key={i} className="flex items-center gap-2 bg-gray-50 rounded px-2 py-1.5 text-sm">
                  <span className="flex-1 truncate">{d.email}</span>
                  <span className="text-xs text-gray-500">{d.access}</span>
                  <button onClick={() => removeDelegate(i)} className="text-red-500 hover:text-red-700 text-xs">✕</button>
                </div>
              ))}
            </div>
            <div className="flex gap-1">
              <input value={delegateEmail} onChange={e => setDelegateEmail(e.target.value)} placeholder="email@company.com"
                className="flex-1 border border-gray-300 rounded px-2 py-1.5 text-sm" />
              <select value={delegateAccess} onChange={e => setDelegateAccess(e.target.value)} className="border border-gray-300 rounded px-2 py-1.5 text-sm">
                <option value="read">read</option>
                <option value="comment">read + comment</option>
                <option value="edit">read + edit</option>
              </select>
              <button onClick={addDelegate} className="px-3 py-1.5 bg-pink-600 text-white text-sm rounded hover:bg-pink-700">+</button>
            </div>
          </div>
        </div>
        <div className="px-5 py-3 border-t border-gray-100 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-sm text-gray-600">Cancel</button>
          <button onClick={handleSave} disabled={saving} className="px-4 py-1.5 bg-pink-600 text-white text-sm rounded-lg hover:bg-pink-700 disabled:opacity-50">{saving ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
};

// ---- Candidate Pipeline ----
const MC_CANDIDATE_SOURCES = ['Referral', 'Inbound', 'Recruiter', 'LinkedIn', 'Event', 'Agency', 'Other'];

const ManagerCandidateModal = ({ candidate, members, stages, onClose, onSave, onDelete }) => {
  const editing = !!candidate?.id;
  const [form, setForm] = useState({
    name: candidate?.name || '',
    roleAppliedFor: candidate?.roleAppliedFor || '',
    source: candidate?.source || '',
    stage: candidate?.stage || stages[0] || 'Sourced',
    interviewerIds: candidate?.interviewerIds || [],
    plannedStartDate: candidate?.plannedStartDate || '',
    notes: candidate?.notes || '',
  });
  const toggleInterviewer = (id) => {
    setForm(f => f.interviewerIds.includes(id)
      ? { ...f, interviewerIds: f.interviewerIds.filter(x => x !== id) }
      : { ...f, interviewerIds: [...f.interviewerIds, id] });
  };
  const handleSave = async () => {
    if (!form.name.trim()) return;
    await onSave(form);
    onClose();
  };
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-base font-semibold">{editing ? 'Edit Candidate' : 'New Candidate'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">×</button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="text-xs text-gray-500 uppercase tracking-wide font-medium">Name</label>
            <input autoFocus value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mt-1" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-500 uppercase tracking-wide font-medium">Role</label>
              <input value={form.roleAppliedFor} onChange={e => setForm({ ...form, roleAppliedFor: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mt-1" placeholder="e.g. Senior Engineer" />
            </div>
            <div>
              <label className="text-xs text-gray-500 uppercase tracking-wide font-medium">Source</label>
              <select value={form.source} onChange={e => setForm({ ...form, source: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mt-1">
                <option value="">—</option>
                {MC_CANDIDATE_SOURCES.map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-500 uppercase tracking-wide font-medium">Stage</label>
              <select value={form.stage} onChange={e => setForm({ ...form, stage: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mt-1">
                {stages.map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-500 uppercase tracking-wide font-medium">Planned Start</label>
              <input type="date" value={form.plannedStartDate || ''} onChange={e => setForm({ ...form, plannedStartDate: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mt-1" />
            </div>
          </div>
          <div>
            <label className="text-xs text-gray-500 uppercase tracking-wide font-medium">Interviewers</label>
            {members.length === 0 ? (
              <p className="text-xs text-gray-400 mt-1">No team members yet.</p>
            ) : (
              <div className="flex flex-wrap gap-1 mt-1">
                {members.map(m => (
                  <button key={m.id} onClick={() => toggleInterviewer(m.id)}
                    className={`px-2 py-1 text-xs rounded border ${form.interviewerIds.includes(m.id) ? 'bg-pink-100 border-pink-400 text-pink-800' : 'bg-white border-gray-300 text-gray-600 hover:border-pink-300'}`}>
                    {m.name}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div>
            <label className="text-xs text-gray-500 uppercase tracking-wide font-medium">Notes</label>
            <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={3}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mt-1" />
          </div>
          {editing && candidate.history?.length > 0 && (
            <div>
              <div className="text-xs text-gray-500 uppercase tracking-wide font-medium mb-1">Stage history</div>
              <div className="bg-gray-50 border border-gray-200 rounded p-2 space-y-1 max-h-32 overflow-y-auto text-xs">
                {[...candidate.history].reverse().map((h, i) => (
                  <div key={i} className="text-gray-600">
                    <span className="text-gray-400">{new Date(h.at).toLocaleString()}</span>
                    {' — '}
                    <span className="font-medium">{h.fromStage || '•'} → {h.toStage}</span>
                    {h.byEmail && <span className="text-gray-400"> · {h.byEmail}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t border-gray-100 flex justify-between items-center">
          <div>
            {editing && <button onClick={() => requestConfirm(`Delete ${form.name}?`, async () => { await onDelete(candidate.id); onClose(); })}
              className="text-xs text-red-600 hover:underline">Delete</button>}
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-3 py-1.5 text-sm text-gray-600">Cancel</button>
            <button onClick={handleSave} disabled={!form.name.trim()} className="px-4 py-1.5 bg-pink-600 text-white text-sm rounded-lg hover:bg-pink-700 disabled:opacity-50">{editing ? 'Save' : 'Add'}</button>
          </div>
        </div>
      </div>
    </div>
  );
};

const ManagerStageEditor = ({ stages, onSave, onClose }) => {
  const [list, setList] = useState([...stages]);
  const [input, setInput] = useState('');
  const [saving, setSaving] = useState(false);
  const move = (i, d) => {
    const j = i + d;
    if (j < 0 || j >= list.length) return;
    const next = [...list]; [next[i], next[j]] = [next[j], next[i]]; setList(next);
  };
  const removeAt = (i) => setList(list.filter((_, idx) => idx !== i));
  const add = () => { if (!input.trim()) return; setList([...list, input.trim()]); setInput(''); };
  const rename = (i, v) => setList(list.map((s, idx) => idx === i ? v : s));
  const handleSave = async () => { setSaving(true); const r = await onSave(list); setSaving(false); if (r?.success !== false) onClose(); else alert(r?.error || 'Save failed'); };
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-base font-semibold">Pipeline Stages</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">×</button>
        </div>
        <div className="p-5 space-y-2">
          {list.map((s, i) => (
            <div key={i} className="flex items-center gap-2">
              <input value={s} onChange={e => rename(i, e.target.value)} className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm" />
              <button onClick={() => move(i, -1)} disabled={i === 0} className="px-1.5 py-0.5 text-xs text-gray-500 hover:text-gray-800 disabled:opacity-30">▲</button>
              <button onClick={() => move(i, 1)} disabled={i === list.length - 1} className="px-1.5 py-0.5 text-xs text-gray-500 hover:text-gray-800 disabled:opacity-30">▼</button>
              <button onClick={() => removeAt(i)} disabled={list.length <= 1} className="px-1.5 py-0.5 text-xs text-red-600 hover:bg-red-50 rounded disabled:opacity-30">✕</button>
            </div>
          ))}
          <div className="flex gap-1 pt-2">
            <input value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') add(); }}
              placeholder="Add stage…" className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm" />
            <button onClick={add} className="px-3 py-1 bg-pink-600 text-white text-sm rounded hover:bg-pink-700">Add</button>
          </div>
        </div>
        <div className="px-5 py-3 border-t border-gray-100 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-sm text-gray-600">Cancel</button>
          <button onClick={handleSave} disabled={saving} className="px-4 py-1.5 bg-pink-600 text-white text-sm rounded-lg hover:bg-pink-700 disabled:opacity-50">{saving ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
};

const mcTimeInStage = (c) => {
  const hist = c.history || [];
  let lastChange = c.createdAt;
  for (let i = hist.length - 1; i >= 0; i--) { if (hist[i].toStage === c.stage) { lastChange = hist[i].at; break; } }
  if (!lastChange) return 0;
  return Math.max(0, Math.floor((Date.now() - new Date(lastChange).getTime()) / 86400000));
};

const ManagerCandidatePipelineView = ({ ws, onAdd, onUpdate, onDelete, onSaveStages }) => {
  const stages = ws.pipelineStages || [];
  const [view, setView] = useState('board');
  const [filterStage, setFilterStage] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [filterSource, setFilterSource] = useState('');
  const [editing, setEditing] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [showStages, setShowStages] = useState(false);

  const candidates = ws.candidates || [];
  const filtered = candidates.filter(c =>
    (!filterStage || c.stage === filterStage) &&
    (!filterRole || (c.roleAppliedFor || '').toLowerCase().includes(filterRole.toLowerCase())) &&
    (!filterSource || c.source === filterSource));

  const byStage = {};
  stages.forEach(s => byStage[s] = []);
  filtered.forEach(c => { if (byStage[c.stage]) byStage[c.stage].push(c); else byStage[c.stage] = [c]; });

  const avgDays = (list) => {
    if (list.length === 0) return 0;
    const total = list.reduce((a, c) => a + mcTimeInStage(c), 0);
    return Math.round(total / list.length);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Candidate Pipeline</h2>
          <p className="text-sm text-gray-500">{candidates.length} candidate{candidates.length === 1 ? '' : 's'} total</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex bg-gray-100 rounded-lg p-1">
            <button onClick={() => setView('board')} className={`px-3 py-1 text-sm rounded ${view === 'board' ? 'bg-white text-gray-900 shadow-sm font-medium' : 'text-gray-500'}`}>Board</button>
            <button onClick={() => setView('list')} className={`px-3 py-1 text-sm rounded ${view === 'list' ? 'bg-white text-gray-900 shadow-sm font-medium' : 'text-gray-500'}`}>List</button>
          </div>
          <button onClick={() => setShowStages(true)} className="px-3 py-1.5 border border-gray-300 text-gray-600 text-sm rounded-lg hover:bg-gray-50">Stages</button>
          <button onClick={() => setShowAdd(true)} className="px-3 py-1.5 bg-pink-600 text-white text-sm rounded-lg hover:bg-pink-700 font-medium">+ Candidate</button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
        <div className="bg-white rounded-lg border border-gray-200 px-3 py-2">
          <div className="text-[10px] text-gray-500 uppercase tracking-wide">In pipeline</div>
          <div className="text-lg font-bold text-gray-900">{candidates.filter(c => c.stage !== 'Hired' && c.stage !== 'Declined').length}</div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 px-3 py-2">
          <div className="text-[10px] text-gray-500 uppercase tracking-wide">Hired</div>
          <div className="text-lg font-bold text-green-700">{candidates.filter(c => c.stage === 'Hired').length}</div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 px-3 py-2">
          <div className="text-[10px] text-gray-500 uppercase tracking-wide">Declined</div>
          <div className="text-lg font-bold text-gray-500">{candidates.filter(c => c.stage === 'Declined').length}</div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 px-3 py-2">
          <div className="text-[10px] text-gray-500 uppercase tracking-wide">Avg days in current stage</div>
          <div className="text-lg font-bold text-gray-900">{avgDays(candidates.filter(c => c.stage !== 'Hired' && c.stage !== 'Declined'))}d</div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        <select value={filterStage} onChange={e => setFilterStage(e.target.value)} className="border border-gray-300 rounded px-2 py-1 text-sm">
          <option value="">All stages</option>
          {stages.map(s => <option key={s}>{s}</option>)}
        </select>
        <input value={filterRole} onChange={e => setFilterRole(e.target.value)} placeholder="Filter by role…" className="border border-gray-300 rounded px-2 py-1 text-sm" />
        <select value={filterSource} onChange={e => setFilterSource(e.target.value)} className="border border-gray-300 rounded px-2 py-1 text-sm">
          <option value="">All sources</option>
          {MC_CANDIDATE_SOURCES.map(s => <option key={s}>{s}</option>)}
        </select>
        {(filterStage || filterRole || filterSource) && (
          <button onClick={() => { setFilterStage(''); setFilterRole(''); setFilterSource(''); }} className="px-2 py-1 text-sm text-gray-500 hover:text-gray-800">Clear</button>
        )}
      </div>

      {filtered.length === 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center text-gray-400">
          {candidates.length === 0 ? 'No candidates yet. Add your first one to get started.' : 'No candidates match your filters.'}
        </div>
      )}

      {view === 'board' && filtered.length > 0 && (
        <div className="flex gap-3 overflow-x-auto pb-4">
          {stages.map(stage => {
            const list = byStage[stage] || [];
            return (
              <div key={stage} className="bg-gray-50 border border-gray-200 rounded-lg p-3 min-w-[240px] flex-shrink-0">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">{stage}</span>
                  <span className="text-xs text-gray-500">{list.length} · avg {avgDays(list)}d</span>
                </div>
                <div className="space-y-2">
                  {list.map(c => (
                    <div key={c.id} onClick={() => setEditing(c)}
                      className="bg-white border border-gray-200 rounded-md p-2 cursor-pointer hover:border-pink-300 hover:shadow-sm">
                      <div className="font-medium text-sm text-gray-900 truncate">{c.name}</div>
                      {c.roleAppliedFor && <div className="text-xs text-gray-500 truncate">{c.roleAppliedFor}</div>}
                      <div className="flex items-center gap-1 mt-1 flex-wrap">
                        {c.source && <span className="text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded">{c.source}</span>}
                        <span className="text-[10px] text-gray-400 ml-auto">{mcTimeInStage(c)}d</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {view === 'list' && filtered.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {['Name','Role','Source','Stage','Time in stage','Interviewers','Start'].map(h =>
                  <th key={h} className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">{h}</th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map(c => (
                <tr key={c.id} onClick={() => setEditing(c)} className="hover:bg-gray-50 cursor-pointer">
                  <td className="px-3 py-2 font-medium text-gray-900">{c.name}</td>
                  <td className="px-3 py-2 text-gray-600">{c.roleAppliedFor || '—'}</td>
                  <td className="px-3 py-2 text-gray-500 text-xs">{c.source || '—'}</td>
                  <td className="px-3 py-2">
                    <select value={c.stage} onChange={e => { e.stopPropagation(); onUpdate(c.id, { stage: e.target.value }); }} onClick={e => e.stopPropagation()}
                      className="text-xs border border-gray-200 rounded px-1 py-0.5 bg-white">
                      {stages.map(s => <option key={s}>{s}</option>)}
                    </select>
                  </td>
                  <td className="px-3 py-2 text-gray-500 text-xs">{mcTimeInStage(c)}d</td>
                  <td className="px-3 py-2 text-xs">
                    {c.interviewerIds.map(id => ws.members.find(m => m.id === id)?.name).filter(Boolean).join(', ') || '—'}
                  </td>
                  <td className="px-3 py-2 text-gray-500 text-xs">{c.plannedStartDate || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showAdd && <ManagerCandidateModal candidate={null} members={ws.members} stages={stages}
        onClose={() => setShowAdd(false)} onSave={onAdd} onDelete={onDelete} />}
      {editing && <ManagerCandidateModal candidate={editing} members={ws.members} stages={stages}
        onClose={() => setEditing(null)} onSave={(data) => onUpdate(editing.id, data)} onDelete={onDelete} />}
      {showStages && <ManagerStageEditor stages={stages} onSave={onSaveStages} onClose={() => setShowStages(false)} />}
    </div>
  );
};

// ---- Onboarding: template editor ----
const ManagerOnboardingTemplateEditor = ({ template, onSave, onClose }) => {
  const [draft, setDraft] = useState(() => JSON.parse(JSON.stringify(template)));
  const [saving, setSaving] = useState(false);
  const original = JSON.stringify(template);
  const dirty = JSON.stringify(draft) !== original;

  const updatePhase = (idx, patch) => setDraft(d => ({ ...d, phases: d.phases.map((p, i) => i === idx ? { ...p, ...patch } : p) }));
  const removeChecklist = (pIdx, cIdx) => updatePhase(pIdx, { checklist: draft.phases[pIdx].checklist.filter((_, i) => i !== cIdx) });
  const addChecklist = (pIdx) => updatePhase(pIdx, { checklist: [...draft.phases[pIdx].checklist, 'New item'] });
  const renameChecklist = (pIdx, cIdx, v) => updatePhase(pIdx, { checklist: draft.phases[pIdx].checklist.map((x, i) => i === cIdx ? v : x) });
  const updateCriterion = (pIdx, cIdx, patch) => updatePhase(pIdx, { criteria: draft.phases[pIdx].criteria.map((c, i) => i === cIdx ? { ...c, ...patch } : c) });
  const removeCriterion = (pIdx, cIdx) => updatePhase(pIdx, { criteria: draft.phases[pIdx].criteria.filter((_, i) => i !== cIdx) });
  const addCriterion = (pIdx) => updatePhase(pIdx, { criteria: [...draft.phases[pIdx].criteria, { name: 'New criterion', hint: '' }] });
  const update90Crit = (cIdx, patch) => setDraft(d => ({ ...d, review90: { ...d.review90, criteria: d.review90.criteria.map((c, i) => i === cIdx ? { ...c, ...patch } : c) } }));
  const add90Crit = () => setDraft(d => ({ ...d, review90: { ...d.review90, criteria: [...d.review90.criteria, { name: 'New criterion', hint: '' }] } }));
  const remove90Crit = (cIdx) => setDraft(d => ({ ...d, review90: { ...d.review90, criteria: d.review90.criteria.filter((_, i) => i !== cIdx) } }));

  const handleSave = async () => { setSaving(true); await onSave(draft); setSaving(false); onClose(); };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white z-10">
          <div>
            <h2 className="text-base font-semibold">Onboarding Template</h2>
            <p className="text-xs text-gray-500">Customize phases, checklists, deliverables, and rubric for your team.</p>
          </div>
          <div className="flex items-center gap-2">
            {dirty && <span className="text-xs text-amber-700 bg-amber-50 px-2 py-1 rounded">● Unsaved</span>}
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">×</button>
          </div>
        </div>
        <div className="p-5 space-y-4">
          {draft.phases.map((p, pIdx) => (
            <div key={p.key} className="border border-gray-200 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-2">
                <input value={p.label} onChange={e => updatePhase(pIdx, { label: e.target.value })}
                  className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm font-semibold" />
              </div>
              <input value={p.summary} onChange={e => updatePhase(pIdx, { summary: e.target.value })}
                className="w-full border border-gray-300 rounded px-2 py-1 text-xs text-gray-700 mb-2" placeholder="Summary" />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Checklist</div>
                  {p.checklist.map((item, cIdx) => (
                    <div key={cIdx} className="flex gap-1 mb-1">
                      <input value={item} onChange={e => renameChecklist(pIdx, cIdx, e.target.value)} className="flex-1 border border-gray-300 rounded px-2 py-1 text-xs" />
                      <button onClick={() => removeChecklist(pIdx, cIdx)} className="text-red-500 hover:bg-red-50 px-1 rounded text-xs">✕</button>
                    </div>
                  ))}
                  <button onClick={() => addChecklist(pIdx)} className="text-xs text-pink-700 hover:text-pink-900">+ Add item</button>
                </div>
                <div>
                  <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Criteria (1–5)</div>
                  {p.criteria.map((crit, cIdx) => (
                    <div key={cIdx} className="mb-1">
                      <div className="flex gap-1">
                        <input value={crit.name} onChange={e => updateCriterion(pIdx, cIdx, { name: e.target.value })} className="flex-1 border border-gray-300 rounded px-2 py-1 text-xs" />
                        <button onClick={() => removeCriterion(pIdx, cIdx)} className="text-red-500 hover:bg-red-50 px-1 rounded text-xs">✕</button>
                      </div>
                      <input value={crit.hint || ''} onChange={e => updateCriterion(pIdx, cIdx, { hint: e.target.value })}
                        placeholder="Hint (optional)" className="w-full border border-gray-200 rounded px-2 py-0.5 text-[11px] text-gray-600 mt-0.5" />
                    </div>
                  ))}
                  <button onClick={() => addCriterion(pIdx)} className="text-xs text-pink-700 hover:text-pink-900">+ Add criterion</button>
                </div>
              </div>
              <div className="mt-2">
                <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Deliverable</div>
                <input value={p.deliverable} onChange={e => updatePhase(pIdx, { deliverable: e.target.value })} className="w-full border border-gray-300 rounded px-2 py-1 text-xs" />
              </div>
            </div>
          ))}
          <div className="border border-gray-200 rounded-lg p-3 bg-pink-50/40">
            <div className="text-sm font-semibold text-gray-800 mb-2">90-day Review Rubric</div>
            {draft.review90.criteria.map((crit, cIdx) => (
              <div key={cIdx} className="mb-2">
                <div className="flex gap-1">
                  <input value={crit.name} onChange={e => update90Crit(cIdx, { name: e.target.value })} className="flex-1 border border-gray-300 rounded px-2 py-1 text-xs" />
                  <button onClick={() => remove90Crit(cIdx)} className="text-red-500 hover:bg-red-50 px-1 rounded text-xs">✕</button>
                </div>
                <input value={crit.hint || ''} onChange={e => update90Crit(cIdx, { hint: e.target.value })}
                  placeholder="Hint (optional)" className="w-full border border-gray-200 rounded px-2 py-0.5 text-[11px] text-gray-600 mt-0.5" />
              </div>
            ))}
            <button onClick={add90Crit} className="text-xs text-pink-700 hover:text-pink-900">+ Add criterion</button>
          </div>
        </div>
        <div className="px-5 py-3 border-t border-gray-100 flex justify-end gap-2 sticky bottom-0 bg-white">
          <button onClick={onClose} className="px-3 py-1.5 text-sm text-gray-600">Cancel</button>
          <button onClick={handleSave} disabled={!dirty || saving} className="px-4 py-1.5 bg-pink-600 text-white text-sm rounded-lg hover:bg-pink-700 disabled:opacity-50">{saving ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
};

// ---- Per-hire plan view ----
const mcPhaseScore = (phaseTpl, phaseState) => {
  const criteria = phaseTpl.criteria || [];
  const cData = phaseState?.criteria || {};
  const ratings = criteria.map(c => Number(cData[c.name]?.rating)).filter(r => r >= 1 && r <= 5);
  const avg = ratings.length ? ratings.reduce((a, b) => a + b, 0) / ratings.length : null;
  const checklist = phaseTpl.checklist || [];
  const cState = phaseState?.checks || {};
  const done = checklist.filter(item => cState[item]).length;
  const pct = checklist.length ? Math.round(done / checklist.length * 100) : 0;
  return { avg, pct, done, total: checklist.length };
};

const mcPhaseRag = (score) => {
  if (score == null) return { label: '—', bg: '#f1f5f9', color: '#94a3b8' };
  if (score >= 4) return { label: score.toFixed(1), bg: '#dcfce7', color: '#166534' };
  if (score >= 3) return { label: score.toFixed(1), bg: '#fef3c7', color: '#92400e' };
  return { label: score.toFixed(1), bg: '#fee2e2', color: '#991b1b' };
};

const ManagerHirePlanView = ({ hire, template, progress, onBack, onSaveProgress, onConvert, onDelete, user }) => {
  const [activeKey, setActiveKey] = useState(template.phases[0]?.key || 'review90');
  const [draft, setDraft] = useState(() => JSON.parse(JSON.stringify(progress || {})));
  const saveTimer = useRef(null);

  const dirty = JSON.stringify(draft) !== JSON.stringify(progress || {});
  const scheduleSave = (next) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => onSaveProgress(next), 600);
  };

  const patchPhase = (key, patch) => {
    const prev = draft[key] || {};
    const next = { ...draft, [key]: { ...prev, ...patch } };
    setDraft(next);
    scheduleSave(next);
  };

  const toggleCheck = (key, item) => {
    const phase = draft[key] || {};
    const checks = { ...(phase.checks || {}), [item]: !(phase.checks || {})[item] };
    patchPhase(key, { checks });
  };

  const setCriterion = (key, name, patch) => {
    const phase = draft[key] || {};
    const criteria = { ...(phase.criteria || {}), [name]: { ...(phase.criteria || {})[name], ...patch } };
    patchPhase(key, { criteria });
  };

  const signOffPhase = (key) => {
    const phase = draft[key] || {};
    patchPhase(key, { signedOffBy: user?.user_name || 'Manager', signedOffAt: new Date().toISOString() });
  };
  const clearSignOff = (key) => patchPhase(key, { signedOffBy: null, signedOffAt: null });

  const patchReview90 = (patch) => {
    const prev = draft.review90 || {};
    const next = { ...draft, review90: { ...prev, ...patch } };
    setDraft(next);
    scheduleSave(next);
  };

  const activePhase = template.phases.find(p => p.key === activeKey);
  const activeProgress = draft[activeKey] || {};

  return (
    <div>
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <button onClick={onBack} className="text-sm text-gray-500 hover:text-gray-800">← Back</button>
        <div className="flex-1 min-w-0">
          <h2 className="text-xl font-bold text-gray-900 truncate">{hire.name}</h2>
          <p className="text-sm text-gray-500">{hire.role || 'Role not set'}{hire.startDate ? ` · start ${hire.startDate}` : ''}</p>
        </div>
        {dirty && <span className="text-xs text-amber-700 bg-amber-50 px-2 py-1 rounded">● Saving…</span>}
        <button onClick={() => requestConfirm(`Convert ${hire.name} to a team member? Their hire record will be archived.`, onConvert)}
          className="px-3 py-1.5 bg-emerald-600 text-white text-sm rounded-lg hover:bg-emerald-700 font-medium">Convert to team member →</button>
        {onDelete && (
          <button onClick={() => requestConfirm(`Delete ${hire.name}'s hire record? Their onboarding progress will be removed permanently.`, onDelete)}
            className="px-3 py-1.5 border border-red-300 text-red-700 text-sm rounded-lg hover:bg-red-50 font-medium">Delete hire</button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-4 bg-gray-100 rounded-lg p-1 overflow-x-auto">
        {template.phases.map(p => {
          const { avg, pct } = mcPhaseScore(p, draft[p.key]);
          const rag = mcPhaseRag(avg);
          return (
            <button key={p.key} onClick={() => setActiveKey(p.key)}
              className={`px-3 py-1.5 text-sm rounded whitespace-nowrap ${activeKey === p.key ? 'bg-white text-gray-900 shadow-sm font-medium' : 'text-gray-500 hover:text-gray-700'}`}>
              {p.label}
              <span className="ml-2 text-[10px] text-gray-500">{pct}%</span>
              <span className="ml-1 px-1 py-0 rounded text-[10px] font-semibold" style={{ background: rag.bg, color: rag.color }}>{rag.label}</span>
            </button>
          );
        })}
        <button onClick={() => setActiveKey('review90')}
          className={`px-3 py-1.5 text-sm rounded whitespace-nowrap ${activeKey === 'review90' ? 'bg-white text-gray-900 shadow-sm font-medium' : 'text-gray-500 hover:text-gray-700'}`}>90-day Review</button>
      </div>

      {activeKey === 'review90' ? (() => {
        const r = draft.review90 || {};
        const crits = template.review90?.criteria || [];
        const ratings = crits.map(c => Number(r.criteria?.[c.name]?.rating)).filter(n => n >= 1 && n <= 5);
        const avg = ratings.length ? ratings.reduce((a, b) => a + b, 0) / ratings.length : null;
        const rag = mcPhaseRag(avg);
        const set90Crit = (name, patch) => patchReview90({ criteria: { ...(r.criteria || {}), [name]: { ...(r.criteria || {})[name], ...patch } } });
        return (
          <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-gray-900">90-day Review</h3>
              <span className="px-3 py-1 rounded-full text-sm font-semibold" style={{ background: rag.bg, color: rag.color }}>Overall {rag.label}</span>
            </div>

            <div>
              <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">Role-readiness rubric</div>
              {crits.map(c => {
                const v = r.criteria?.[c.name] || {};
                return (
                  <div key={c.name} className="mb-2 border-b border-gray-100 pb-2">
                    <div className="flex items-center justify-between mb-1">
                      <div>
                        <div className="text-sm font-medium text-gray-800">{c.name}</div>
                        {c.hint && <div className="text-xs text-gray-500">{c.hint}</div>}
                      </div>
                      <div className="flex gap-1">
                        {[1, 2, 3, 4, 5].map(n => (
                          <button key={n} onClick={() => set90Crit(c.name, { rating: n })}
                            className={`w-7 h-7 text-xs rounded border ${v.rating === n ? 'bg-pink-600 text-white border-pink-600' : 'bg-white text-gray-600 border-gray-200 hover:border-pink-300'}`}>{n}</button>
                        ))}
                      </div>
                    </div>
                    <input value={v.evidence || ''} onChange={e => set90Crit(c.name, { evidence: e.target.value })}
                      placeholder="Evidence…" className="w-full border border-gray-200 rounded px-2 py-1 text-xs" />
                  </div>
                );
              })}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Summary
                <textarea value={r.summary || ''} onChange={e => patchReview90({ summary: e.target.value })} rows={3}
                  className="w-full mt-1 border border-gray-300 rounded px-2 py-1 text-xs normal-case font-normal text-gray-800" />
              </label>
              <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Strengths
                <textarea value={r.strengths || ''} onChange={e => patchReview90({ strengths: e.target.value })} rows={3}
                  className="w-full mt-1 border border-gray-300 rounded px-2 py-1 text-xs normal-case font-normal text-gray-800" />
              </label>
              <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Growth areas
                <textarea value={r.growth || ''} onChange={e => patchReview90({ growth: e.target.value })} rows={3}
                  className="w-full mt-1 border border-gray-300 rounded px-2 py-1 text-xs normal-case font-normal text-gray-800" />
              </label>
              <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Peer feedback (360°)
                <textarea value={r.peerFeedback || ''} onChange={e => patchReview90({ peerFeedback: e.target.value })} rows={3}
                  className="w-full mt-1 border border-gray-300 rounded px-2 py-1 text-xs normal-case font-normal text-gray-800" />
              </label>
              <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Next-period goals
                <textarea value={r.nextGoals || ''} onChange={e => patchReview90({ nextGoals: e.target.value })} rows={3}
                  className="w-full mt-1 border border-gray-300 rounded px-2 py-1 text-xs normal-case font-normal text-gray-800" />
              </label>
              <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Career interests
                <textarea value={r.careerInterests || ''} onChange={e => patchReview90({ careerInterests: e.target.value })} rows={3}
                  className="w-full mt-1 border border-gray-300 rounded px-2 py-1 text-xs normal-case font-normal text-gray-800" />
              </label>
            </div>

            <div>
              <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">Decision</div>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { key: 'confirmed', label: 'Confirmed', bg: 'bg-green-50 border-green-300 text-green-800', active: 'bg-green-600 border-green-600 text-white' },
                  { key: 'extension', label: 'Extension', bg: 'bg-amber-50 border-amber-300 text-amber-800', active: 'bg-amber-600 border-amber-600 text-white' },
                  { key: 'concerns',  label: 'Concerns',  bg: 'bg-red-50 border-red-300 text-red-800',      active: 'bg-red-600 border-red-600 text-white' },
                ].map(o => (
                  <button key={o.key} onClick={() => patchReview90({ decision: o.key })}
                    className={`border rounded-lg py-2 text-sm font-medium transition-colors ${r.decision === o.key ? o.active : o.bg}`}>{o.label}</button>
                ))}
              </div>
            </div>
          </div>
        );
      })() : activePhase ? (() => {
        const { avg, pct, done, total } = mcPhaseScore(activePhase, activeProgress);
        const rag = mcPhaseRag(avg);
        return (
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
              <h3 className="font-semibold text-gray-900">{activePhase.label}</h3>
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <span>Checklist {done}/{total} ({pct}%)</span>
                <span className="px-2 py-0.5 rounded-full font-semibold" style={{ background: rag.bg, color: rag.color }}>Rubric {rag.label}</span>
              </div>
            </div>
            <p className="text-sm text-gray-500 mb-3">{activePhase.summary}</p>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">Checklist</div>
                {activePhase.checklist.map(item => (
                  <label key={item} className="flex items-start gap-2 py-1 text-sm cursor-pointer">
                    <input type="checkbox" checked={!!activeProgress.checks?.[item]} onChange={() => toggleCheck(activeKey, item)}
                      className="mt-0.5" />
                    <span className={activeProgress.checks?.[item] ? 'text-gray-500 line-through' : 'text-gray-800'}>{item}</span>
                  </label>
                ))}
              </div>
              <div>
                <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">Rubric (1–5)</div>
                {activePhase.criteria.map(c => {
                  const v = activeProgress.criteria?.[c.name] || {};
                  return (
                    <div key={c.name} className="mb-2 pb-2 border-b border-gray-100 last:border-0">
                      <div className="flex items-center justify-between">
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-gray-800">{c.name}</div>
                          {c.hint && <div className="text-xs text-gray-500">{c.hint}</div>}
                        </div>
                        <div className="flex gap-1 shrink-0">
                          {[1, 2, 3, 4, 5].map(n => (
                            <button key={n} onClick={() => setCriterion(activeKey, c.name, { rating: n })}
                              className={`w-6 h-6 text-xs rounded border ${v.rating === n ? 'bg-pink-600 text-white border-pink-600' : 'bg-white text-gray-600 border-gray-200 hover:border-pink-300'}`}>{n}</button>
                          ))}
                        </div>
                      </div>
                      <input value={v.evidence || ''} onChange={e => setCriterion(activeKey, c.name, { evidence: e.target.value })}
                        placeholder="Evidence…" className="w-full mt-1 border border-gray-200 rounded px-2 py-1 text-xs" />
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="mt-3 bg-gray-50 rounded-lg p-3 border border-gray-200">
              <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Deliverable</div>
              <p className="text-sm text-gray-700">{activePhase.deliverable}</p>
              <textarea value={activeProgress.notes || ''} onChange={e => patchPhase(activeKey, { notes: e.target.value })} rows={2}
                placeholder="Notes on the deliverable…" className="w-full mt-2 border border-gray-300 rounded px-2 py-1 text-xs" />
            </div>

            <div className="mt-3 flex items-center justify-between bg-pink-50 border border-pink-200 rounded-lg p-3">
              <div className="text-sm">
                {activeProgress.signedOffBy ? (
                  <span className="text-pink-800">✓ Signed off by <span className="font-semibold">{activeProgress.signedOffBy}</span> on {new Date(activeProgress.signedOffAt).toLocaleDateString()}</span>
                ) : (
                  <span className="text-pink-700">Phase not yet signed off.</span>
                )}
              </div>
              {activeProgress.signedOffBy ? (
                <button onClick={() => clearSignOff(activeKey)} className="px-3 py-1 text-xs text-pink-700 hover:bg-pink-100 rounded">Clear</button>
              ) : (
                <button onClick={() => signOffPhase(activeKey)} className="px-3 py-1 bg-pink-600 text-white text-sm rounded hover:bg-pink-700">Sign off phase</button>
              )}
            </div>
          </div>
        );
      })() : null}
    </div>
  );
};

// ---- Onboarding list view ----
const ManagerOnboardingView = ({ ws, onAddHire, onUpdateHire, onDeleteHire, onSaveProgress, onConvert, onSaveTemplate, user }) => {
  const [editingHireId, setEditingHireId] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [showTemplate, setShowTemplate] = useState(false);
  const [addForm, setAddForm] = useState({ name: '', role: '', startDate: '', buddy: '', manager: '' });
  const [showArchived, setShowArchived] = useState(false);

  const hires = (ws.hires || []).filter(h => showArchived || h.status === 'active');
  const active = editingHireId ? ws.hires.find(h => h.id === editingHireId) : null;

  if (active) {
    return <ManagerHirePlanView
      hire={active}
      template={ws.onboardingTemplate}
      progress={ws.hireProgress[active.id] || {}}
      user={user}
      onBack={() => setEditingHireId(null)}
      onSaveProgress={(p) => onSaveProgress(active.id, p)}
      onConvert={async () => { await onConvert(active.id); setEditingHireId(null); }}
      onDelete={async () => { await onDeleteHire(active.id); setEditingHireId(null); }}
    />;
  }

  const submitAdd = async () => {
    if (!addForm.name.trim()) return;
    await onAddHire(addForm);
    setAddForm({ name: '', role: '', startDate: '', buddy: '', manager: '' });
    setShowAdd(false);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Onboarding</h2>
          <p className="text-sm text-gray-500">{hires.length} hire{hires.length === 1 ? '' : 's'}</p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-gray-500 flex items-center gap-1 cursor-pointer">
            <input type="checkbox" checked={showArchived} onChange={e => setShowArchived(e.target.checked)} /> Show archived
          </label>
          <button onClick={() => setShowTemplate(true)} className="px-3 py-1.5 border border-gray-300 text-gray-600 text-sm rounded-lg hover:bg-gray-50">Template</button>
          <button onClick={() => setShowAdd(true)} className="px-3 py-1.5 bg-pink-600 text-white text-sm rounded-lg hover:bg-pink-700 font-medium">+ Hire</button>
        </div>
      </div>

      {showAdd && (
        <div className="bg-pink-50 border border-pink-200 rounded-lg p-4 mb-4 space-y-2">
          <input autoFocus value={addForm.name} onChange={e => setAddForm(f => ({ ...f, name: e.target.value }))} placeholder="Name"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
          <div className="grid grid-cols-2 gap-2">
            <input value={addForm.role} onChange={e => setAddForm(f => ({ ...f, role: e.target.value }))} placeholder="Role" className="border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            <input type="date" value={addForm.startDate} onChange={e => setAddForm(f => ({ ...f, startDate: e.target.value }))} className="border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            <input value={addForm.buddy} onChange={e => setAddForm(f => ({ ...f, buddy: e.target.value }))} placeholder="Buddy (optional)" className="border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            <input value={addForm.manager} onChange={e => setAddForm(f => ({ ...f, manager: e.target.value }))} placeholder="Manager (optional)" className="border border-gray-300 rounded-lg px-3 py-2 text-sm" />
          </div>
          <div className="flex gap-2">
            <button onClick={submitAdd} className="px-3 py-1.5 bg-pink-600 text-white text-sm rounded hover:bg-pink-700">Add</button>
            <button onClick={() => setShowAdd(false)} className="px-3 py-1.5 text-sm text-gray-600">Cancel</button>
          </div>
        </div>
      )}

      {hires.length === 0 && !showAdd ? (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <p className="text-gray-400 mb-3">No hires yet.</p>
          <button onClick={() => setShowAdd(true)} className="px-4 py-2 bg-pink-600 text-white text-sm rounded-lg hover:bg-pink-700">+ Add Your First Hire</button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {hires.map(h => {
            const progress = ws.hireProgress[h.id] || {};
            const phaseScores = ws.onboardingTemplate.phases.map(p => mcPhaseScore(p, progress[p.key]));
            const totalChecks = phaseScores.reduce((a, x) => a + x.total, 0);
            const doneChecks  = phaseScores.reduce((a, x) => a + x.done, 0);
            const overallPct = totalChecks ? Math.round(doneChecks / totalChecks * 100) : 0;
            const avgs = phaseScores.map(x => x.avg).filter(x => x != null);
            const rubricAvg = avgs.length ? avgs.reduce((a, b) => a + b, 0) / avgs.length : null;
            const rag = mcPhaseRag(rubricAvg);
            return (
              <div key={h.id} onClick={() => setEditingHireId(h.id)}
                className="bg-white border border-gray-200 rounded-lg p-4 cursor-pointer hover:border-pink-300 hover:shadow-sm transition-colors group relative">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    requestConfirm(`Delete ${h.name}'s hire record? Their onboarding progress will be removed permanently.`, () => onDeleteHire(h.id));
                  }}
                  title="Delete hire"
                  className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity text-xs text-red-600 hover:bg-red-50 px-1.5 py-0.5 rounded">×</button>
                <div className="flex items-start justify-between mb-1 pr-6">
                  <div className="min-w-0">
                    <div className="font-semibold text-gray-900 truncate">{h.name}</div>
                    {h.role && <div className="text-xs text-gray-500 truncate">{h.role}</div>}
                  </div>
                  {h.status === 'archived' && <span className="text-[10px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">archived</span>}
                </div>
                {h.startDate && <div className="text-xs text-gray-400 mb-2">Start: {h.startDate}</div>}
                <div className="flex items-center gap-2 mb-2">
                  <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full rounded-full bg-pink-500" style={{ width: `${overallPct}%` }} />
                  </div>
                  <span className="text-xs text-gray-500">{overallPct}%</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">Rubric</span>
                  <span className="px-2 py-0.5 rounded-full text-xs font-semibold" style={{ background: rag.bg, color: rag.color }}>{rag.label}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showTemplate && <ManagerOnboardingTemplateEditor template={ws.onboardingTemplate} onSave={onSaveTemplate} onClose={() => setShowTemplate(false)} />}
    </div>
  );
};

// ---- Coaching Logs ----
const MC_COACHING_TYPES = [
  { key: 'one_on_one', label: '1:1', fields: [
    { key: 'topicsRaised',   label: 'Topics they raised' },
    { key: 'wins',           label: 'Wins & recognition' },
    { key: 'challenges',     label: 'Challenges / blockers' },
    { key: 'development',    label: 'Development / growth' },
    { key: 'actionItems',    label: 'Action items' },
    { key: 'nextSession',    label: 'For next 1:1' },
  ] },
  { key: 'performance_checkin', label: 'Performance check-in', fields: [
    { key: 'performanceVsExpectations', label: 'Performance vs expectations' },
    { key: 'strengths',      label: 'Strengths this period' },
    { key: 'growthAreas',    label: 'Growth areas / feedback' },
    { key: 'goalProgress',   label: 'Goal progress' },
    { key: 'commitments',    label: 'Commitments agreed' },
    { key: 'nextFocus',      label: 'Next review focus' },
  ] },
  { key: 'skip_level', label: 'Skip-level', fields: [
    { key: 'teamDynamics',         label: 'Team dynamics & culture' },
    { key: 'managerRelationship',  label: 'Manager relationship' },
    { key: 'orgFeedback',          label: 'Org-level feedback' },
    { key: 'careerConversation',   label: 'Career conversation' },
    { key: 'followUps',            label: 'Follow-ups for you' },
  ] },
  { key: 'feedback_session', label: 'Feedback session', fields: [
    { key: 'feedbackDelivered', label: 'Feedback delivered (SBI)' },
    { key: 'evidence',          label: 'Evidence' },
    { key: 'reaction',          label: 'Reaction' },
    { key: 'nextSteps',         label: 'Agreed next steps' },
    { key: 'followUpPlan',      label: 'Follow-up plan' },
  ] },
  { key: 'ad_hoc', label: 'Ad-hoc', fields: [
    { key: 'whatCameUp',   label: 'What came up' },
    { key: 'actionItems',  label: 'Action items' },
    { key: 'notesForLater', label: 'Notes for later' },
  ] },
];
const mcTypeByKey = (k) => MC_COACHING_TYPES.find(t => t.key === k) || MC_COACHING_TYPES[0];

const ManagerCoachingLogEditor = ({ log, memberId, members, onSave, onDelete, onClose }) => {
  const editing = !!log?.id;
  const [type, setType] = useState(log?.type || 'one_on_one');
  const [date, setDate] = useState(log?.date || new Date().toISOString().slice(0, 10));
  const [memberSel, setMemberSel] = useState(log?.memberId || memberId || (members[0]?.id ?? null));
  // Draft keeps ALL fields from ALL types, so switching preserves input.
  const [draft, setDraft] = useState(() => {
    const d = {};
    if (log) MC_COACHING_TYPES.forEach(t => t.fields.forEach(f => { if (log[f.key] !== undefined) d[f.key] = log[f.key]; }));
    return d;
  });
  const setField = (k, v) => setDraft(d => ({ ...d, [k]: v }));
  const typeDef = mcTypeByKey(type);

  const handleSave = async () => {
    if (!memberSel) return;
    const payload = { memberId: memberSel, type, date };
    typeDef.fields.forEach(f => { payload[f.key] = draft[f.key] || ''; });
    await onSave(payload);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white z-10">
          <h2 className="text-base font-semibold">{editing ? 'Edit Log Entry' : 'New Log Entry'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">×</button>
        </div>
        <div className="p-5 space-y-3">
          <div className="grid grid-cols-3 gap-2">
            <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Member
              <select value={memberSel || ''} onChange={e => setMemberSel(parseInt(e.target.value))}
                className="w-full mt-1 border border-gray-300 rounded-lg px-3 py-2 text-sm font-normal normal-case text-gray-800">
                {members.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            </label>
            <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Type
              <select value={type} onChange={e => setType(e.target.value)}
                className="w-full mt-1 border border-gray-300 rounded-lg px-3 py-2 text-sm font-normal normal-case text-gray-800">
                {MC_COACHING_TYPES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
              </select>
            </label>
            <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Date
              <input type="date" value={date} onChange={e => setDate(e.target.value)}
                className="w-full mt-1 border border-gray-300 rounded-lg px-3 py-2 text-sm font-normal normal-case text-gray-800" />
            </label>
          </div>
          {typeDef.fields.map(f => (
            <label key={f.key} className="block text-xs font-semibold text-gray-600 uppercase tracking-wide">{f.label}
              <textarea value={draft[f.key] || ''} onChange={e => setField(f.key, e.target.value)} rows={2}
                className="w-full mt-1 border border-gray-300 rounded-lg px-3 py-2 text-sm font-normal normal-case text-gray-800" />
            </label>
          ))}
        </div>
        <div className="px-5 py-3 border-t border-gray-100 flex justify-between items-center sticky bottom-0 bg-white">
          <div>
            {editing && <button onClick={() => requestConfirm('Delete this log entry?', async () => { await onDelete(log.id); onClose(); })}
              className="text-xs text-red-600 hover:underline">Delete</button>}
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-3 py-1.5 text-sm text-gray-600">Cancel</button>
            <button onClick={handleSave} disabled={!memberSel} className="px-4 py-1.5 bg-pink-600 text-white text-sm rounded-lg hover:bg-pink-700 disabled:opacity-50">{editing ? 'Save' : 'Add'}</button>
          </div>
        </div>
      </div>
    </div>
  );
};

const ManagerCoachingLogsView = ({ ws, onAdd, onUpdate, onDelete }) => {
  const [filterMember, setFilterMember] = useState('');
  const [filterType, setFilterType] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const [addForMember, setAddForMember] = useState(null);

  const allLogs = [];
  Object.entries(ws.coachingLogs || {}).forEach(([mid, arr]) => {
    arr.forEach(l => allLogs.push({ ...l, memberId: parseInt(mid) }));
  });
  allLogs.sort((a, b) => (b.date || '').localeCompare(a.date || '') || (b.id - a.id));

  const filtered = allLogs.filter(l =>
    (!filterMember || l.memberId === parseInt(filterMember)) &&
    (!filterType || l.type === filterType));

  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Coaching Logs</h2>
          <p className="text-sm text-gray-500">{allLogs.length} entr{allLogs.length === 1 ? 'y' : 'ies'}</p>
        </div>
        <button onClick={() => { setAddForMember(null); setShowAdd(true); }}
          disabled={ws.members.length === 0}
          className="px-3 py-1.5 bg-pink-600 text-white text-sm rounded-lg hover:bg-pink-700 font-medium disabled:opacity-50">+ Log Entry</button>
      </div>

      <div className="flex gap-2 mb-4 flex-wrap">
        <select value={filterMember} onChange={e => setFilterMember(e.target.value)} className="border border-gray-300 rounded px-2 py-1 text-sm">
          <option value="">All members</option>
          {ws.members.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
        </select>
        <select value={filterType} onChange={e => setFilterType(e.target.value)} className="border border-gray-300 rounded px-2 py-1 text-sm">
          <option value="">All types</option>
          {MC_COACHING_TYPES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
        </select>
        {(filterMember || filterType) && (
          <button onClick={() => { setFilterMember(''); setFilterType(''); }} className="px-2 py-1 text-sm text-gray-500 hover:text-gray-800">Clear</button>
        )}
      </div>

      {ws.members.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center text-gray-400">Add team members before logging conversations.</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <p className="text-gray-400 mb-3">{allLogs.length === 0 ? 'No log entries yet.' : 'No entries match your filter.'}</p>
          {allLogs.length === 0 && (
            <button onClick={() => setShowAdd(true)} className="px-4 py-2 bg-pink-600 text-white text-sm rounded-lg hover:bg-pink-700">+ First Entry</button>
          )}
        </div>
      ) : (
        <div className="orbit-card divide-y divide-gray-100 overflow-hidden">
          {filtered.map(l => {
            const t = mcTypeByKey(l.type);
            const member = ws.members.find(m => m.id === l.memberId);
            // Full field grid lives in the click-through editor — the list row is just
            // enough to identify and skim an entry (member/type/date + one snippet line).
            const snippet = t.fields.map(f => l[f.key]).filter(Boolean).join(' · ');
            return (
              <div key={l.id} onClick={() => setEditing(l)} className="px-4 py-2.5 cursor-pointer hover:bg-gray-50 transition-colors">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="font-semibold text-gray-900 text-sm truncate">{member?.name || '—'}</span>
                    <span className="px-1.5 py-0.5 rounded bg-pink-100 text-pink-800 text-[10px] font-medium shrink-0">{t.label}</span>
                  </div>
                  <span className="text-xs text-gray-400 shrink-0">{l.date}</span>
                </div>
                {snippet && <div className="text-xs text-gray-500 truncate mt-0.5">{snippet}</div>}
              </div>
            );
          })}
        </div>
      )}

      {showAdd && <ManagerCoachingLogEditor log={null} memberId={addForMember} members={ws.members}
        onSave={onAdd} onDelete={onDelete} onClose={() => setShowAdd(false)} />}
      {editing && <ManagerCoachingLogEditor log={editing} memberId={editing.memberId} members={ws.members}
        onSave={(data) => onUpdate(editing.id, data)} onDelete={onDelete} onClose={() => setEditing(null)} />}
    </div>
  );
};

// ---- 1:1 Planner ----
const ManagerOneOnOneView = ({ ws, onSaveOneOnOne }) => {
  const [selectedId, setSelectedId] = useState(ws.members[0]?.id || null);
  useEffect(() => {
    if (!ws.members.find(m => m.id === selectedId)) setSelectedId(ws.members[0]?.id || null);
  }, [ws.members, selectedId]);

  const data = (selectedId && ws.oneOnOnes?.[selectedId]) || { nextMeetingDate: '', topics: [], prepNotes: '' };
  const [draft, setDraft] = useState(data);
  const [topicInput, setTopicInput] = useState('');
  const saveTimer = useRef(null);

  useEffect(() => {
    const d = (selectedId && ws.oneOnOnes?.[selectedId]) || { nextMeetingDate: '', topics: [], prepNotes: '' };
    setDraft(d);
  }, [selectedId, ws.oneOnOnes]);

  const schedule = (next) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => onSaveOneOnOne(selectedId, next), 500);
  };

  const setDate = (v) => { const next = { ...draft, nextMeetingDate: v }; setDraft(next); schedule(next); };
  const setPrep = (v) => { const next = { ...draft, prepNotes: v }; setDraft(next); schedule(next); };
  const addTopic = () => {
    if (!topicInput.trim()) return;
    const topics = [...(draft.topics || []), { id: `t_${Date.now()}`, text: topicInput.trim(), done: false }];
    const next = { ...draft, topics };
    setDraft(next); schedule(next); setTopicInput('');
  };
  const toggleTopic = (id) => {
    const topics = (draft.topics || []).map(t => t.id === id ? { ...t, done: !t.done } : t);
    const next = { ...draft, topics };
    setDraft(next); schedule(next);
  };
  const editTopic = (id, text) => {
    const topics = (draft.topics || []).map(t => t.id === id ? { ...t, text } : t);
    const next = { ...draft, topics };
    setDraft(next); schedule(next);
  };
  const removeTopic = (id) => {
    const topics = (draft.topics || []).filter(t => t.id !== id);
    const next = { ...draft, topics };
    setDraft(next); schedule(next);
  };

  if (ws.members.length === 0) {
    return <div className="bg-white rounded-lg border border-gray-200 p-12 text-center text-gray-400">Add team members to plan 1:1s.</div>;
  }

  const logs = (selectedId && ws.coachingLogs?.[selectedId]) || [];
  const latest = logs.length ? logs[0] : null;
  const carryover = [];
  if (latest) {
    if (latest.actionItems) carryover.push({ tag: 'Action items', text: latest.actionItems });
    if (latest.nextSession) carryover.push({ tag: 'For next 1:1', text: latest.nextSession });
  }

  const openCount = (m) => ((ws.oneOnOnes?.[m.id]?.topics) || []).filter(t => !t.done).length;

  return (
    <div className="flex gap-4">
      <aside className="w-64 bg-white rounded-lg border border-gray-200 p-2 flex-shrink-0 h-max">
        {ws.members.map(m => {
          const oo = ws.oneOnOnes?.[m.id];
          return (
            <button key={m.id} onClick={() => setSelectedId(m.id)}
              className={`w-full flex flex-col items-start text-left px-3 py-2 rounded text-sm ${selectedId === m.id ? 'bg-pink-50 text-pink-800' : 'text-gray-700 hover:bg-gray-50'}`}>
              <span className="font-medium">{m.name}</span>
              <span className="text-xs text-gray-500">
                {oo?.nextMeetingDate ? `Next: ${oo.nextMeetingDate}` : 'No meeting set'}
                {openCount(m) > 0 && <span className="ml-2 text-pink-600 font-semibold">{openCount(m)} open</span>}
              </span>
            </button>
          );
        })}
      </aside>

      <div className="flex-1 min-w-0">
        <div className="mb-4">
          <h2 className="text-xl font-bold text-gray-900">{ws.members.find(m => m.id === selectedId)?.name || 'Select a member'}</h2>
          <p className="text-sm text-gray-500">1:1 planner — auto-saves</p>
        </div>

        {carryover.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3">
            <div className="text-xs font-semibold text-amber-800 uppercase tracking-wide mb-2">Carryover from last log ({latest.date})</div>
            <div className="space-y-1.5">
              {carryover.map((c, i) => (
                <div key={i}>
                  <div className="text-[10px] font-semibold text-amber-700 uppercase tracking-wide">{c.tag}</div>
                  <div className="text-sm text-amber-900 whitespace-pre-wrap">{c.text}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="bg-white border border-gray-200 rounded-lg p-4 mb-3">
          <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Next meeting</label>
          <input type="date" value={draft.nextMeetingDate || ''} onChange={e => setDate(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm" />
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-4 mb-3">
          <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">Agenda topics</label>
          <div className="space-y-1 mb-2">
            {(draft.topics || []).map(t => (
              <div key={t.id} className="flex items-center gap-2">
                <input type="checkbox" checked={!!t.done} onChange={() => toggleTopic(t.id)} />
                <input value={t.text} onChange={e => editTopic(t.id, e.target.value)}
                  className={`flex-1 border border-gray-200 rounded px-2 py-1 text-sm ${t.done ? 'line-through text-gray-400' : ''}`} />
                <button onClick={() => removeTopic(t.id)} className="text-red-500 hover:bg-red-50 px-1.5 py-0.5 rounded text-xs">✕</button>
              </div>
            ))}
            {(draft.topics || []).length === 0 && <div className="text-xs text-gray-400">No topics yet.</div>}
          </div>
          <div className="flex gap-1">
            <input value={topicInput} onChange={e => setTopicInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') addTopic(); }}
              placeholder="Add topic…" className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm" />
            <button onClick={addTopic} className="px-3 py-1 bg-pink-600 text-white text-sm rounded hover:bg-pink-700">Add</button>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Prep notes</label>
          <textarea value={draft.prepNotes || ''} onChange={e => setPrep(e.target.value)} rows={5}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
        </div>
      </div>
    </div>
  );
};

// ---- IDP (Development Plans) ----
const MC_IDP_STATUS_OPTS = [
  { key: 'draft',        label: 'Draft',        bg: 'bg-gray-100 text-gray-700' },
  { key: 'active',       label: 'Active',       bg: 'bg-blue-100 text-blue-700' },
  { key: 'under_review', label: 'Under review', bg: 'bg-amber-100 text-amber-700' },
  { key: 'complete',     label: 'Complete',     bg: 'bg-green-100 text-green-700' },
];
const MC_LEARNING_STATUSES = ['planned', 'in_progress', 'complete', 'paused'];
const MC_LEARNING_STATUS_STYLE = {
  planned:     { bg: '#f1f5f9', color: '#475569' },
  in_progress: { bg: '#dbeafe', color: '#1d4ed8' },
  complete:    { bg: '#dcfce7', color: '#166534' },
  paused:      { bg: '#fef3c7', color: '#92400e' },
};

const mcIdpCompletion = (idp) => {
  const sections = [
    !!(idp?.careerGoals || '').trim(),
    !!(idp?.strengths || '').trim(),
    !!(idp?.growthAreas || '').trim(),
    (idp?.learningPlan || []).length > 0,
    (idp?.milestones || []).length > 0,
    !!(idp?.supportNeeded || '').trim(),
    !!idp?.reviewDate,
  ];
  const done = sections.filter(Boolean).length;
  return Math.round(done / sections.length * 100);
};

const ManagerIdpView = ({ ws, onSaveIdp }) => {
  const [selectedId, setSelectedId] = useState(ws.members[0]?.id || null);
  useEffect(() => {
    if (!ws.members.find(m => m.id === selectedId)) setSelectedId(ws.members[0]?.id || null);
  }, [ws.members, selectedId]);

  const empty = { careerGoals: '', strengths: '', growthAreas: '', learningPlan: [], milestones: [], supportNeeded: '', reviewDate: '', status: 'draft' };
  const [draft, setDraft] = useState(empty);
  const saveTimer = useRef(null);

  useEffect(() => {
    if (!selectedId) { setDraft(empty); return; }
    const d = ws.idps?.[selectedId];
    setDraft(d ? { ...empty, ...d } : empty);
  }, [selectedId, ws.idps]);

  const schedule = (next) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => onSaveIdp(selectedId, next), 500);
  };
  const set = (patch) => { const next = { ...draft, ...patch }; setDraft(next); schedule(next); };

  const addLearning = () => set({ learningPlan: [...(draft.learningPlan || []), { id: `lp_${Date.now()}`, title: '', status: 'planned', notes: '' }] });
  const updateLearning = (id, patch) => set({ learningPlan: draft.learningPlan.map(x => x.id === id ? { ...x, ...patch } : x) });
  const removeLearning = (id) => set({ learningPlan: draft.learningPlan.filter(x => x.id !== id) });

  const addMilestone = () => set({ milestones: [...(draft.milestones || []), { id: `ms_${Date.now()}`, description: '', dueDate: '', done: false }] });
  const updateMilestone = (id, patch) => set({ milestones: draft.milestones.map(x => x.id === id ? { ...x, ...patch } : x) });
  const removeMilestone = (id) => set({ milestones: draft.milestones.filter(x => x.id !== id) });

  if (ws.members.length === 0) {
    return <div className="bg-white rounded-lg border border-gray-200 p-12 text-center text-gray-400">Add team members to build IDPs.</div>;
  }

  const statusDef = MC_IDP_STATUS_OPTS.find(s => s.key === draft.status) || MC_IDP_STATUS_OPTS[0];
  const pct = mcIdpCompletion(draft);

  return (
    <div className="flex gap-4">
      <aside className="w-64 bg-white rounded-lg border border-gray-200 p-2 flex-shrink-0 h-max">
        {ws.members.map(m => {
          const d = ws.idps?.[m.id];
          const s = MC_IDP_STATUS_OPTS.find(o => o.key === (d?.status || 'draft')) || MC_IDP_STATUS_OPTS[0];
          return (
            <button key={m.id} onClick={() => setSelectedId(m.id)}
              className={`w-full flex items-center justify-between text-left px-3 py-2 rounded text-sm ${selectedId === m.id ? 'bg-pink-50 text-pink-800' : 'text-gray-700 hover:bg-gray-50'}`}>
              <span className="font-medium truncate">{m.name}</span>
              <span className={`text-[10px] px-1.5 py-0.5 rounded ${s.bg}`}>{s.label}</span>
            </button>
          );
        })}
      </aside>

      <div className="flex-1 min-w-0 space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
          <div>
            <h2 className="text-xl font-bold text-gray-900">{ws.members.find(m => m.id === selectedId)?.name || '—'}</h2>
            <p className="text-sm text-gray-500">Individual Development Plan — auto-saves</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="text-xs text-gray-500">Completion</div>
            <div className="w-24 h-1.5 bg-gray-100 rounded-full overflow-hidden">
              <div className="h-full rounded-full bg-pink-500" style={{ width: `${pct}%` }} />
            </div>
            <div className="text-xs text-gray-600 font-medium">{pct}%</div>
            <select value={draft.status} onChange={e => set({ status: e.target.value })}
              className={`text-xs rounded px-2 py-1 border-0 font-semibold ${statusDef.bg}`}>
              {MC_IDP_STATUS_OPTS.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
            </select>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-4 grid grid-cols-1 md:grid-cols-3 gap-3">
          <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Career goals
            <textarea value={draft.careerGoals || ''} onChange={e => set({ careerGoals: e.target.value })} rows={3}
              className="w-full mt-1 border border-gray-300 rounded-lg px-3 py-2 text-sm font-normal normal-case text-gray-800" />
          </label>
          <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Strengths
            <textarea value={draft.strengths || ''} onChange={e => set({ strengths: e.target.value })} rows={3}
              className="w-full mt-1 border border-gray-300 rounded-lg px-3 py-2 text-sm font-normal normal-case text-gray-800" />
          </label>
          <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Growth areas
            <textarea value={draft.growthAreas || ''} onChange={e => set({ growthAreas: e.target.value })} rows={3}
              className="w-full mt-1 border border-gray-300 rounded-lg px-3 py-2 text-sm font-normal normal-case text-gray-800" />
          </label>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-gray-800">Learning plan</h3>
            <button onClick={addLearning} className="text-xs text-pink-700 hover:text-pink-900">+ Add item</button>
          </div>
          {(draft.learningPlan || []).length === 0 && <div className="text-xs text-gray-400">No learning items yet.</div>}
          {(draft.learningPlan || []).map(item => {
            const s = MC_LEARNING_STATUS_STYLE[item.status] || MC_LEARNING_STATUS_STYLE.planned;
            return (
              <div key={item.id} className="mb-2 bg-gray-50 rounded-lg p-2">
                <div className="flex items-center gap-2">
                  <input value={item.title} onChange={e => updateLearning(item.id, { title: e.target.value })}
                    placeholder="e.g. Complete SQL fundamentals" className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm" />
                  <select value={item.status} onChange={e => updateLearning(item.id, { status: e.target.value })}
                    className="text-xs px-2 py-1 rounded border-0 font-semibold" style={{ background: s.bg, color: s.color }}>
                    {MC_LEARNING_STATUSES.map(st => <option key={st} value={st}>{st.replace('_', ' ')}</option>)}
                  </select>
                  <button onClick={() => removeLearning(item.id)} className="text-red-500 hover:bg-red-50 px-1.5 py-0.5 rounded text-xs">✕</button>
                </div>
                <input value={item.notes || ''} onChange={e => updateLearning(item.id, { notes: e.target.value })}
                  placeholder="Notes (optional)" className="w-full mt-1 border border-gray-200 rounded px-2 py-0.5 text-xs" />
              </div>
            );
          })}
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-gray-800">Milestones</h3>
            <button onClick={addMilestone} className="text-xs text-pink-700 hover:text-pink-900">+ Add milestone</button>
          </div>
          {(draft.milestones || []).length === 0 && <div className="text-xs text-gray-400">No milestones yet.</div>}
          {(draft.milestones || []).map(ms => (
            <div key={ms.id} className="flex items-center gap-2 mb-1">
              <input type="checkbox" checked={!!ms.done} onChange={e => updateMilestone(ms.id, { done: e.target.checked })} />
              <input value={ms.description} onChange={e => updateMilestone(ms.id, { description: e.target.value })}
                placeholder="Milestone description" className={`flex-1 border border-gray-300 rounded px-2 py-1 text-sm ${ms.done ? 'line-through text-gray-400' : ''}`} />
              <input type="date" value={ms.dueDate || ''} onChange={e => updateMilestone(ms.id, { dueDate: e.target.value })}
                className="border border-gray-300 rounded px-2 py-1 text-xs" />
              <button onClick={() => removeMilestone(ms.id)} className="text-red-500 hover:bg-red-50 px-1.5 py-0.5 rounded text-xs">✕</button>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <label className="bg-white border border-gray-200 rounded-lg p-4 text-xs font-semibold text-gray-600 uppercase tracking-wide block">Support needed
            <textarea value={draft.supportNeeded || ''} onChange={e => set({ supportNeeded: e.target.value })} rows={3}
              className="w-full mt-1 border border-gray-300 rounded-lg px-3 py-2 text-sm font-normal normal-case text-gray-800" />
          </label>
          <label className="bg-white border border-gray-200 rounded-lg p-4 text-xs font-semibold text-gray-600 uppercase tracking-wide block">Next review date
            <input type="date" value={draft.reviewDate || ''} onChange={e => set({ reviewDate: e.target.value })}
              className="w-full mt-1 border border-gray-300 rounded-lg px-3 py-2 text-sm font-normal normal-case text-gray-800" />
          </label>
        </div>
      </div>
    </div>
  );
};

// ---- Operations helpers ----
const mcDateObj = (s) => new Date(s + 'T12:00:00Z');
const mcTodayIso = () => new Date().toISOString().slice(0, 10);
const mcSnapMonday = (iso) => {
  if (!iso) return mcSnapMonday(mcTodayIso());
  const d = mcDateObj(iso);
  const day = d.getUTCDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setUTCDate(d.getUTCDate() + diff);
  return d.toISOString().slice(0, 10);
};
const mcAddDays = (iso, n) => {
  const d = mcDateObj(iso);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};
const mcFmtShort = (iso) => {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${months[parseInt(m) - 1]} ${parseInt(d)}, ${y}`;
};
const mcWeekRangeLabel = (monday) => {
  if (!monday) return '';
  const sun = mcAddDays(monday, 6);
  const [y1, m1, d1] = monday.split('-');
  const [y2, m2, d2] = sun.split('-');
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  if (y1 === y2 && m1 === m2) return `${months[parseInt(m1) - 1]} ${parseInt(d1)} – ${parseInt(d2)}, ${y1}`;
  if (y1 === y2) return `${months[parseInt(m1) - 1]} ${parseInt(d1)} – ${months[parseInt(m2) - 1]} ${parseInt(d2)}, ${y1}`;
  return `${mcFmtShort(monday)} – ${mcFmtShort(sun)}`;
};
const mcCurrentMonday = () => mcSnapMonday(mcTodayIso());
const mcDaysInclusive = (a, b) => {
  const da = mcDateObj(a), db = mcDateObj(b);
  return Math.max(1, Math.round((db - da) / 86400000) + 1);
};
const mcPtoStyle = {
  vacation: { bg: 'bg-emerald-100', color: 'text-emerald-700' },
  sick:     { bg: 'bg-rose-100',    color: 'text-rose-700' },
  personal: { bg: 'bg-blue-100',    color: 'text-blue-700' },
  comp:     { bg: 'bg-purple-100',  color: 'text-purple-700' },
  other:    { bg: 'bg-gray-100',    color: 'text-gray-700' },
};

// ---- On-call Rotation ----
const ManagerOncallView = ({ ws, onUpsert, onDelete, onAutoFill }) => {
  const roster = ws.members;
  const rows = [...(ws.oncall || [])].sort((a, b) => (a.weekStartDate || '').localeCompare(b.weekStartDate || ''));
  const today = mcCurrentMonday();
  const thisWeek = rows.find(r => r.weekStartDate === today) || null;

  const [form, setForm] = useState({ weekStartDate: today, primaryId: '', secondaryId: '', notes: '' });
  const [editingId, setEditingId] = useState(null);
  const [autoFillWeeks, setAutoFillWeeks] = useState(8);
  const [autoFillStart, setAutoFillStart] = useState(today);

  const startEdit = (r) => {
    setEditingId(r.id);
    setForm({ weekStartDate: r.weekStartDate, primaryId: r.primaryId || '', secondaryId: r.secondaryId || '', notes: r.notes || '' });
  };
  const resetForm = () => { setEditingId(null); setForm({ weekStartDate: today, primaryId: '', secondaryId: '', notes: '' }); };
  const submitForm = async () => {
    await onUpsert({
      weekStartDate: mcSnapMonday(form.weekStartDate),
      primaryId: form.primaryId ? parseInt(form.primaryId) : null,
      secondaryId: form.secondaryId ? parseInt(form.secondaryId) : null,
      notes: form.notes,
    });
    resetForm();
  };

  const handleAutoFill = async () => {
    const r = await onAutoFill({ weeks: autoFillWeeks, startDate: mcSnapMonday(autoFillStart) });
    if (r?.success === false) alert(r?.error || 'Auto-fill failed');
  };

  // Fairness tallies (counts only weeks that are assigned)
  const tally = {};
  roster.forEach(m => { tally[m.id] = { primary: 0, secondary: 0 }; });
  rows.forEach(r => {
    if (r.primaryId && tally[r.primaryId]) tally[r.primaryId].primary += 1;
    if (r.secondaryId && tally[r.secondaryId]) tally[r.secondaryId].secondary += 1;
  });

  const nameOf = (id) => roster.find(m => m.id === id)?.name || (id ? '— unassigned —' : '—');
  const statusPill = (wk) => {
    if (wk === today) return { label: 'This week', bg: 'bg-green-100', color: 'text-green-700' };
    if (wk < today) return { label: 'Past',        bg: 'bg-gray-100',  color: 'text-gray-500' };
    return { label: 'Upcoming',  bg: 'bg-blue-100',  color: 'text-blue-700' };
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-gray-900">On-call Rotation</h2>
          <p className="text-sm text-gray-500">{rows.length} week{rows.length === 1 ? '' : 's'} scheduled</p>
        </div>
      </div>

      {thisWeek ? (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4 flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="text-xs font-semibold text-green-800 uppercase tracking-wide">This week</div>
            <div className="text-sm text-green-900 font-medium">{mcWeekRangeLabel(thisWeek.weekStartDate)}</div>
            <div className="text-xs text-green-700">
              Primary: <span className="font-semibold">{nameOf(thisWeek.primaryId) || '—'}</span>
              {thisWeek.secondaryId && <> · Secondary: <span className="font-semibold">{nameOf(thisWeek.secondaryId)}</span></>}
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-4 text-sm text-gray-500">No on-call assigned for this week. Add or auto-fill below.</div>
      )}

      <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4">
        <h3 className="text-sm font-semibold text-gray-800 mb-2">{editingId ? 'Edit week' : 'Add week'}</h3>
        <div className="grid grid-cols-4 gap-2 mb-2">
          <label className="text-xs text-gray-500 uppercase tracking-wide font-medium">Week (Monday)
            <input type="date" value={form.weekStartDate} onChange={e => setForm({ ...form, weekStartDate: e.target.value })}
              className="w-full mt-1 border border-gray-300 rounded-lg px-2 py-1.5 text-sm font-normal normal-case text-gray-800" />
          </label>
          <label className="text-xs text-gray-500 uppercase tracking-wide font-medium">Primary
            <select value={form.primaryId} onChange={e => setForm({ ...form, primaryId: e.target.value })}
              className="w-full mt-1 border border-gray-300 rounded-lg px-2 py-1.5 text-sm font-normal normal-case text-gray-800">
              <option value="">—</option>
              {roster.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
          </label>
          <label className="text-xs text-gray-500 uppercase tracking-wide font-medium">Secondary
            <select value={form.secondaryId} onChange={e => setForm({ ...form, secondaryId: e.target.value })}
              className="w-full mt-1 border border-gray-300 rounded-lg px-2 py-1.5 text-sm font-normal normal-case text-gray-800">
              <option value="">—</option>
              {roster.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
          </label>
          <label className="text-xs text-gray-500 uppercase tracking-wide font-medium">Notes
            <input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
              className="w-full mt-1 border border-gray-300 rounded-lg px-2 py-1.5 text-sm font-normal normal-case text-gray-800" />
          </label>
        </div>
        <div className="flex gap-2">
          <button onClick={submitForm} className="px-3 py-1.5 bg-pink-600 text-white text-sm rounded hover:bg-pink-700">{editingId ? 'Save' : 'Add'}</button>
          {editingId && <button onClick={resetForm} className="px-3 py-1.5 text-sm text-gray-500 hover:text-gray-800">Cancel</button>}
          <div className="ml-auto flex items-center gap-2">
            <span className="text-xs text-gray-500">Auto-fill</span>
            <input type="number" min="1" max="52" value={autoFillWeeks} onChange={e => setAutoFillWeeks(parseInt(e.target.value) || 1)}
              className="w-16 border border-gray-300 rounded px-2 py-1 text-sm" />
            <span className="text-xs text-gray-500">weeks from</span>
            <input type="date" value={autoFillStart} onChange={e => setAutoFillStart(e.target.value)}
              className="border border-gray-300 rounded px-2 py-1 text-sm" />
            <button onClick={handleAutoFill} disabled={roster.length === 0}
              className="px-3 py-1 text-sm bg-amber-500 text-white rounded hover:bg-amber-600 disabled:opacity-50">Auto-fill</button>
          </div>
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center text-gray-400">No weeks scheduled yet.</div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden mb-4">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {['', 'Week', 'Primary', 'Secondary', 'Notes', ''].map((h, i) => (
                  <th key={i} className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map(r => {
                const s = statusPill(r.weekStartDate);
                const flag = !r.primaryId;
                return (
                  <tr key={r.id} className={`hover:bg-gray-50 ${flag ? 'bg-amber-50/40' : ''}`}>
                    <td className="px-3 py-2"><span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${s.bg} ${s.color}`}>{s.label}</span></td>
                    <td className="px-3 py-2 text-gray-800">{mcWeekRangeLabel(r.weekStartDate)}</td>
                    <td className="px-3 py-2">
                      <span className={r.primaryId ? 'text-gray-800' : 'text-amber-700 italic'}>{nameOf(r.primaryId)}</span>
                    </td>
                    <td className="px-3 py-2 text-gray-600">{r.secondaryId ? nameOf(r.secondaryId) : <span className="text-gray-400">—</span>}</td>
                    <td className="px-3 py-2 text-gray-500 text-xs">{r.notes || ''}</td>
                    <td className="px-3 py-2 text-right">
                      <button onClick={() => startEdit(r)} className="text-xs text-blue-700 hover:bg-blue-50 px-2 py-0.5 rounded mr-1">Edit</button>
                      <button onClick={() => requestConfirm(`Delete week ${mcWeekRangeLabel(r.weekStartDate)}?`, () => onDelete(r.id))} className="text-xs text-red-700 hover:bg-red-50 px-2 py-0.5 rounded">Remove</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-gray-800 mb-2">Fairness</h3>
        {roster.length === 0 ? (
          <div className="text-xs text-gray-400">No team members yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-1.5 text-left text-xs font-semibold text-gray-500 uppercase">Member</th>
                <th className="px-3 py-1.5 text-left text-xs font-semibold text-gray-500 uppercase">Primary weeks</th>
                <th className="px-3 py-1.5 text-left text-xs font-semibold text-gray-500 uppercase">Secondary weeks</th>
                <th className="px-3 py-1.5 text-left text-xs font-semibold text-gray-500 uppercase">Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {roster.map(m => {
                const t = tally[m.id];
                return (
                  <tr key={m.id}>
                    <td className="px-3 py-1.5 text-gray-800">{m.name}</td>
                    <td className="px-3 py-1.5 text-gray-700">{t.primary}</td>
                    <td className="px-3 py-1.5 text-gray-700">{t.secondary}</td>
                    <td className="px-3 py-1.5 text-gray-700 font-medium">{t.primary + t.secondary}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

// ---- Time Off ----
const ManagerPtoView = ({ ws, onAdd, onDelete }) => {
  const today = mcTodayIso();
  const allEntries = [];
  Object.entries(ws.pto || {}).forEach(([mid, arr]) => {
    arr.forEach(e => allEntries.push({ ...e, memberId: parseInt(mid) }));
  });
  const upcoming = allEntries.filter(e => e.endDate >= today).sort((a, b) => (a.startDate || '').localeCompare(b.startDate || ''));
  const outToday = allEntries.filter(e => e.startDate <= today && e.endDate >= today);

  const [form, setForm] = useState({ memberId: '', type: 'vacation', startDate: today, endDate: today, notes: '' });
  const submit = async () => {
    if (!form.memberId) return;
    if (form.endDate < form.startDate) { alert('End date must be on or after start date'); return; }
    await onAdd({ ...form, memberId: parseInt(form.memberId) });
    setForm({ memberId: '', type: 'vacation', startDate: today, endDate: today, notes: '' });
  };

  // Totals per member per type
  const totals = {};
  ws.members.forEach(m => { totals[m.id] = { vacation: 0, sick: 0, personal: 0, comp: 0, other: 0 }; });
  allEntries.forEach(e => {
    if (!totals[e.memberId]) return;
    totals[e.memberId][e.type] = (totals[e.memberId][e.type] || 0) + mcDaysInclusive(e.startDate, e.endDate);
  });

  const nameOf = (id) => ws.members.find(m => m.id === id)?.name || '—';

  return (
    <div>
      <div className="mb-4">
        <h2 className="text-xl font-bold text-gray-900">Time Off / Availability</h2>
        <p className="text-sm text-gray-500">{allEntries.length} entr{allEntries.length === 1 ? 'y' : 'ies'} total</p>
      </div>

      {outToday.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
          <div className="text-xs font-semibold text-amber-800 uppercase tracking-wide mb-1">Out today</div>
          <div className="flex flex-wrap gap-2">
            {outToday.map(e => {
              const st = mcPtoStyle[e.type] || mcPtoStyle.other;
              return (
                <span key={e.id} className="text-sm text-amber-900">
                  <span className="font-medium">{nameOf(e.memberId)}</span>
                  <span className={`ml-1 px-1.5 py-0.5 rounded text-[10px] font-semibold ${st.bg} ${st.color}`}>{e.type}</span>
                </span>
              );
            })}
          </div>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4">
        <h3 className="text-sm font-semibold text-gray-800 mb-2">Add entry</h3>
        <div className="grid grid-cols-5 gap-2 mb-2">
          <label className="text-xs text-gray-500 uppercase tracking-wide font-medium">Member
            <select value={form.memberId} onChange={e => setForm({ ...form, memberId: e.target.value })}
              className="w-full mt-1 border border-gray-300 rounded-lg px-2 py-1.5 text-sm font-normal normal-case text-gray-800">
              <option value="">—</option>
              {ws.members.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
          </label>
          <label className="text-xs text-gray-500 uppercase tracking-wide font-medium">Type
            <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}
              className="w-full mt-1 border border-gray-300 rounded-lg px-2 py-1.5 text-sm font-normal normal-case text-gray-800">
              <option value="vacation">Vacation</option>
              <option value="sick">Sick</option>
              <option value="personal">Personal</option>
              <option value="comp">Comp</option>
              <option value="other">Other</option>
            </select>
          </label>
          <label className="text-xs text-gray-500 uppercase tracking-wide font-medium">Start
            <input type="date" value={form.startDate} onChange={e => setForm({ ...form, startDate: e.target.value })}
              className="w-full mt-1 border border-gray-300 rounded-lg px-2 py-1.5 text-sm font-normal normal-case text-gray-800" />
          </label>
          <label className="text-xs text-gray-500 uppercase tracking-wide font-medium">End
            <input type="date" value={form.endDate} onChange={e => setForm({ ...form, endDate: e.target.value })}
              className="w-full mt-1 border border-gray-300 rounded-lg px-2 py-1.5 text-sm font-normal normal-case text-gray-800" />
          </label>
          <label className="text-xs text-gray-500 uppercase tracking-wide font-medium">Notes
            <input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
              className="w-full mt-1 border border-gray-300 rounded-lg px-2 py-1.5 text-sm font-normal normal-case text-gray-800" />
          </label>
        </div>
        <button onClick={submit} disabled={!form.memberId} className="px-3 py-1.5 bg-pink-600 text-white text-sm rounded hover:bg-pink-700 disabled:opacity-50">+ Add</button>
      </div>

      {upcoming.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-8 text-center text-gray-400 mb-4">No upcoming time off.</div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden mb-4">
          <div className="px-3 py-2 border-b border-gray-100 text-sm font-semibold text-gray-800">Upcoming</div>
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                {['Member','Type','Range','Days','Notes',''].map((h, i) => (
                  <th key={i} className="px-3 py-1.5 text-left text-xs font-semibold text-gray-500 uppercase">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {upcoming.map(e => {
                const st = mcPtoStyle[e.type] || mcPtoStyle.other;
                const days = mcDaysInclusive(e.startDate, e.endDate);
                return (
                  <tr key={e.id} className="hover:bg-gray-50">
                    <td className="px-3 py-1.5 text-gray-800">{nameOf(e.memberId)}</td>
                    <td className="px-3 py-1.5"><span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${st.bg} ${st.color}`}>{e.type}</span></td>
                    <td className="px-3 py-1.5 text-gray-700">{mcFmtShort(e.startDate)}{e.startDate !== e.endDate && ` – ${mcFmtShort(e.endDate)}`}</td>
                    <td className="px-3 py-1.5 text-gray-700">{days}</td>
                    <td className="px-3 py-1.5 text-gray-500 text-xs">{e.notes || ''}</td>
                    <td className="px-3 py-1.5 text-right">
                      <button onClick={() => requestConfirm(`Delete ${nameOf(e.memberId)}'s ${e.type}?`, () => onDelete(e.id))}
                        className="text-xs text-red-700 hover:bg-red-50 px-2 py-0.5 rounded">Remove</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-gray-800 mb-2">Totals (days, all-time)</h3>
        {ws.members.length === 0 ? (
          <div className="text-xs text-gray-400">No team members.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-1.5 text-left text-xs font-semibold text-gray-500 uppercase">Member</th>
                <th className="px-3 py-1.5 text-left text-xs font-semibold text-gray-500 uppercase">Vacation</th>
                <th className="px-3 py-1.5 text-left text-xs font-semibold text-gray-500 uppercase">Sick</th>
                <th className="px-3 py-1.5 text-left text-xs font-semibold text-gray-500 uppercase">Personal</th>
                <th className="px-3 py-1.5 text-left text-xs font-semibold text-gray-500 uppercase">Comp + Other</th>
                <th className="px-3 py-1.5 text-left text-xs font-semibold text-gray-500 uppercase">Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {ws.members.map(m => {
                const t = totals[m.id];
                const compOther = (t.comp || 0) + (t.other || 0);
                const total = (t.vacation || 0) + (t.sick || 0) + (t.personal || 0) + compOther;
                return (
                  <tr key={m.id}>
                    <td className="px-3 py-1.5 text-gray-800">{m.name}</td>
                    <td className="px-3 py-1.5 text-gray-700">{t.vacation}</td>
                    <td className="px-3 py-1.5 text-gray-700">{t.sick}</td>
                    <td className="px-3 py-1.5 text-gray-700">{t.personal}</td>
                    <td className="px-3 py-1.5 text-gray-700">{compOther}</td>
                    <td className="px-3 py-1.5 text-gray-700 font-medium">{total}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

// ---- Team Health Pulse ----
const MC_PULSE_DIM_LABELS = {
  workload: 'Workload',
  backlog: 'Backlog load',
  collaboration: 'Collaboration',
  tools: 'Tools & systems',
  growth: 'Growth opportunities',
  morale: 'Morale',
};
const MC_PULSE_DIM_KEYS = Object.keys(MC_PULSE_DIM_LABELS);
const mcPulseAvg = (scores) => {
  const vals = MC_PULSE_DIM_KEYS.map(k => Number(scores?.[k])).filter(n => !Number.isNaN(n));
  if (vals.length === 0) return null;
  return vals.reduce((a, b) => a + b, 0) / vals.length;
};
const mcPulseScoreStyle = (v) => {
  if (v == null) return { color: '#94a3b8' };
  if (v >= 7) return { color: '#16a34a' };
  if (v >= 4) return { color: '#d97706' };
  return { color: '#dc2626' };
};

const ManagerPulseView = ({ ws, onAdd, onUpdate, onDelete }) => {
  const all = ws.pulse || [];
  const team = all.filter(p => p.scope === 'team');

  const latest = all[0];
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState({ date: mcTodayIso(), scope: 'team', memberId: '', scores: {}, notes: '' });
  const [editingId, setEditingId] = useState(null);

  const openNew = () => { setEditingId(null); setForm({ date: mcTodayIso(), scope: 'team', memberId: '', scores: {}, notes: '' }); setFormOpen(true); };
  const openEdit = (p) => {
    setEditingId(p.id);
    setForm({ date: p.date, scope: p.scope, memberId: p.memberId || '', scores: { ...p.scores }, notes: p.notes || '' });
    setFormOpen(true);
  };
  const closeForm = () => { setFormOpen(false); setEditingId(null); };

  const submit = async () => {
    const payload = {
      date: form.date,
      scope: form.scope,
      memberId: form.scope === 'individual' ? (form.memberId ? parseInt(form.memberId) : null) : null,
      scores: form.scores,
      notes: form.notes,
    };
    if (form.scope === 'individual' && !payload.memberId) { alert('Select a member for individual pulse'); return; }
    if (editingId) await onUpdate(editingId, payload); else await onAdd(payload);
    closeForm();
  };

  // Trend: last 12 team snapshots
  const trend = [...team].sort((a, b) => (a.date || '').localeCompare(b.date || '')).slice(-12);
  const trendAvgs = trend.map(p => ({ date: p.date, avg: mcPulseAvg(p.scores) })).filter(x => x.avg != null);

  const nameOf = (id) => ws.members.find(m => m.id === id)?.name || '—';

  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Team Health Pulse</h2>
          <p className="text-sm text-gray-500">Your own read of team sentiment. Not a survey.</p>
        </div>
        <button onClick={openNew} className="px-3 py-1.5 bg-pink-600 text-white text-sm rounded-lg hover:bg-pink-700 font-medium">+ Snapshot</button>
      </div>

      {latest ? (
        <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <div className="text-xs text-gray-500 uppercase tracking-wide">Latest pulse</div>
              <div className="text-lg font-bold text-gray-900">{mcFmtShort(latest.date)}
                <span className="ml-2 text-xs font-normal text-gray-500">{latest.scope === 'individual' ? `· ${nameOf(latest.memberId)}` : '· team-wide'}</span>
              </div>
            </div>
            <div className="text-right">
              <div className="text-xs text-gray-500 uppercase tracking-wide">Avg</div>
              {(() => { const avg = mcPulseAvg(latest.scores); const st = mcPulseScoreStyle(avg); return (
                <div className="text-2xl font-bold" style={{ color: st.color }}>{avg == null ? '—' : avg.toFixed(1)}</div>
              ); })()}
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-3">
            {MC_PULSE_DIM_KEYS.map(k => {
              const v = latest.scores?.[k];
              const st = mcPulseScoreStyle(v);
              return (
                <div key={k} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2">
                  <span className="text-xs text-gray-600">{MC_PULSE_DIM_LABELS[k]}</span>
                  <span className="text-base font-bold" style={{ color: st.color }}>{v != null ? v : '—'}<span className="text-xs text-gray-400">/10</span></span>
                </div>
              );
            })}
          </div>
          {latest.notes && <div className="text-sm text-gray-600 mt-3 whitespace-pre-wrap border-t border-gray-100 pt-2">{latest.notes}</div>}
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center text-gray-400 mb-4">No pulse recorded yet.</div>
      )}

      {trendAvgs.length >= 2 && (
        <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4">
          <div className="text-sm font-semibold text-gray-800 mb-2">Team-wide trend</div>
          {(() => {
            const maxVal = 10, minVal = 0;
            const w = 100 / Math.max(1, trendAvgs.length - 1);
            const pts = trendAvgs.map((p, i) => `${(i * w).toFixed(2)},${(100 - (p.avg - minVal) / (maxVal - minVal) * 100).toFixed(2)}`).join(' ');
            return (
              <div>
                <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="w-full h-28">
                  <polyline points={pts} fill="none" stroke="#db2777" strokeWidth="1.2" vectorEffect="non-scaling-stroke" />
                  {trendAvgs.map((p, i) => (
                    <circle key={i} cx={i * w} cy={100 - (p.avg - minVal) / (maxVal - minVal) * 100} r="1.4" fill="#db2777" />
                  ))}
                </svg>
                <div className="flex justify-between text-[10px] text-gray-400 mt-1">
                  <span>{trendAvgs[0].date}</span>
                  <span>{trendAvgs[trendAvgs.length - 1].date}</span>
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {all.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="px-3 py-2 border-b border-gray-100 text-sm font-semibold text-gray-800">History</div>
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                {['Date','Scope','Avg','Notes',''].map((h, i) =>
                  <th key={i} className="px-3 py-1.5 text-left text-xs font-semibold text-gray-500 uppercase">{h}</th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {all.map(p => {
                const avg = mcPulseAvg(p.scores);
                const st = mcPulseScoreStyle(avg);
                return (
                  <tr key={p.id} className="hover:bg-gray-50">
                    <td className="px-3 py-1.5 text-gray-800">{mcFmtShort(p.date)}</td>
                    <td className="px-3 py-1.5 text-xs text-gray-600">{p.scope === 'individual' ? `Individual · ${nameOf(p.memberId)}` : 'Team-wide'}</td>
                    <td className="px-3 py-1.5 font-semibold" style={{ color: st.color }}>{avg == null ? '—' : avg.toFixed(1)}</td>
                    <td className="px-3 py-1.5 text-gray-500 text-xs truncate max-w-xs">{p.notes || ''}</td>
                    <td className="px-3 py-1.5 text-right">
                      <button onClick={() => openEdit(p)} className="text-xs text-blue-700 hover:bg-blue-50 px-2 py-0.5 rounded mr-1">Edit</button>
                      <button onClick={() => requestConfirm(`Delete snapshot from ${mcFmtShort(p.date)}?`, () => onDelete(p.id))}
                        className="text-xs text-red-700 hover:bg-red-50 px-2 py-0.5 rounded">Remove</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {formOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={closeForm}>
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="px-5 py-3 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-base font-semibold">{editingId ? 'Edit snapshot' : 'New snapshot'}</h2>
              <button onClick={closeForm} className="text-gray-400 hover:text-gray-600 text-xl">×</button>
            </div>
            <div className="p-5 space-y-3">
              <div className="grid grid-cols-3 gap-2">
                <label className="text-xs text-gray-500 uppercase tracking-wide font-medium">Date
                  <input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })}
                    className="w-full mt-1 border border-gray-300 rounded px-2 py-1.5 text-sm font-normal normal-case text-gray-800" />
                </label>
                <label className="text-xs text-gray-500 uppercase tracking-wide font-medium">Scope
                  <select value={form.scope} onChange={e => setForm({ ...form, scope: e.target.value })}
                    className="w-full mt-1 border border-gray-300 rounded px-2 py-1.5 text-sm font-normal normal-case text-gray-800">
                    <option value="team">Team-wide</option>
                    <option value="individual">Individual</option>
                  </select>
                </label>
                {form.scope === 'individual' && (
                  <label className="text-xs text-gray-500 uppercase tracking-wide font-medium">Member
                    <select value={form.memberId} onChange={e => setForm({ ...form, memberId: e.target.value })}
                      className="w-full mt-1 border border-gray-300 rounded px-2 py-1.5 text-sm font-normal normal-case text-gray-800">
                      <option value="">—</option>
                      {ws.members.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                    </select>
                  </label>
                )}
              </div>
              <div className="grid grid-cols-2 gap-2">
                {MC_PULSE_DIM_KEYS.map(k => (
                  <label key={k} className="text-xs text-gray-500 uppercase tracking-wide font-medium">{MC_PULSE_DIM_LABELS[k]}
                    <input type="number" min="0" max="10" step="0.5" value={form.scores?.[k] ?? ''} onChange={e => setForm({ ...form, scores: { ...form.scores, [k]: e.target.value } })}
                      className="w-full mt-1 border border-gray-300 rounded px-2 py-1.5 text-sm font-normal normal-case text-gray-800" />
                  </label>
                ))}
              </div>
              <label className="block text-xs text-gray-500 uppercase tracking-wide font-medium">Notes
                <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={3}
                  className="w-full mt-1 border border-gray-300 rounded px-2 py-1.5 text-sm font-normal normal-case text-gray-800" />
              </label>
            </div>
            <div className="px-5 py-3 border-t border-gray-100 flex justify-end gap-2">
              <button onClick={closeForm} className="px-3 py-1.5 text-sm text-gray-600">Cancel</button>
              <button onClick={submit} className="px-4 py-1.5 bg-pink-600 text-white text-sm rounded-lg hover:bg-pink-700">{editingId ? 'Save' : 'Add'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Parse a Skills Matrix markdown file. Format:
//   ## Skill area name
//   - expert: Karen Jimenez
//   - proficient: Jessie Ramos, Junaire Buico
//   - basic: Cee Mendoza
//
// Also accepts:
//   - Karen Jimenez (expert)         → in-line level
//   - Karen Jimenez                  → defaults to "proficient"
//   And synonyms: beginner/novice → basic, intermediate/advanced → proficient, -/— → none
// If a file has no `## headings`, top-level bullets are treated as skill area names (no ratings).
const parseSkillMatrixMarkdown = (md, members) => {
  const lines = (md || '').split(/\r?\n/);
  const areas = [];
  const seenAreas = new Set();
  const unknownNames = new Set();
  let currentArea = null;

  const normalized = (members || []).map(m => ({
    id: m.id, name: (m.name || '').trim(), lc: (m.name || '').toLowerCase().replace(/\s+/g, ' ').trim(),
  }));
  const findMember = (raw) => {
    const lc = (raw || '').toLowerCase().replace(/^["'`]|["'`]$/g, '').replace(/\s+/g, ' ').trim();
    if (!lc) return null;
    return normalized.find(m => m.lc === lc) || null;
  };
  const normalizeLevel = (s) => {
    const lc = (s || '').toLowerCase().trim();
    if (!lc) return null;
    if (lc.startsWith('expert')) return 'expert';
    if (lc.startsWith('profi') || lc === 'intermediate' || lc === 'advanced') return 'proficient';
    if (lc.startsWith('basic') || lc === 'beginner' || lc === 'novice') return 'basic';
    if (lc === 'none' || lc === '-' || lc === '—' || lc === '–') return 'none';
    return null;
  };
  const isMetaHeading = (s) => /^(scoring|source|notes?|legend|guide|levels?|proficiency|coverage|editing|template|rules?|how\s+to|usage|about|introduction|intro|overview|key|index|toc|references?|examples?)\b/i.test(s);
  const isAreaListHeading = (s) => /^(default\s+)?(skill[\s_-]?(areas?|matrix)|areas?)\s*$/i.test(s.trim());
  const ensureArea = (name) => {
    const key = name.toLowerCase();
    if (seenAreas.has(key)) return areas.find(a => a.name.toLowerCase() === key);
    const a = { name, ratings: {} };
    areas.push(a); seenAreas.add(key);
    return a;
  };

  // Mode tracks what to do with bullets/numbered items in the current section.
  //   'idle'  → before any ## heading: bullets are treated as area names if they aren't team members
  //   'skip'  → meta heading (notes, scoring, legend, rules, etc.): bullets ignored
  //   'list'  → "Skill areas" / "Default skill areas" section: bullets ARE area names
  //   'area'  → a regular ## section that names a skill area: bullets are ratings
  let mode = 'idle';

  for (const raw of lines) {
    const trimmed = raw.trim();
    if (!trimmed) continue;

    const areaMatch = trimmed.match(/^##\s+(?!#)(.+?)\s*$/);
    if (areaMatch) {
      const name = areaMatch[1].trim();
      if (isMetaHeading(name))      { currentArea = null; mode = 'skip'; continue; }
      if (isAreaListHeading(name))  { currentArea = null; mode = 'list'; continue; }
      currentArea = ensureArea(name);
      mode = 'area';
      continue;
    }

    if (mode === 'skip') continue;

    // Support both bulleted (- * +) and numbered (1. or 1)) lists.
    const bullet = trimmed.match(/^(?:[-*+]|\d+[.)])\s+(.+)$/);
    if (!bullet) continue;
    const content = bullet[1].trim();

    if (mode === 'list') {
      if (content) ensureArea(content);
      continue;
    }

    if (mode === 'idle' || !currentArea) {
      if (!findMember(content)) ensureArea(content);
      continue;
    }

    // "level: name1, name2"
    const levelColon = content.match(/^([^:]+):\s*(.+)$/);
    if (levelColon) {
      const level = normalizeLevel(levelColon[1]);
      if (!level) continue;
      const names = levelColon[2].split(/[,;]/).map(n => n.trim()).filter(Boolean);
      for (const n of names) {
        const clean = n.replace(/\s*\([^)]*\)\s*$/, '').trim();
        const m = findMember(clean);
        if (m) currentArea.ratings[m.id] = level;
        else unknownNames.add(clean);
      }
      continue;
    }
    // "name (level)" or just "name"
    const inline = content.match(/^(.+?)\s*\(([^)]+)\)\s*$/);
    const nameRaw = inline ? inline[1] : content;
    const level = inline ? (normalizeLevel(inline[2]) || 'proficient') : 'proficient';
    const m = findMember(nameRaw);
    if (m) currentArea.ratings[m.id] = level;
    else unknownNames.add(nameRaw);
  }

  return { areas, unknownNames: Array.from(unknownNames) };
};

// ---- Skills Matrix ----
const MC_SKILL_LEVELS = ['none', 'basic', 'proficient', 'expert'];
const MC_SKILL_LEVEL_STYLE = {
  none:       { bg: '#f1f5f9', color: '#94a3b8', label: '—' },
  basic:      { bg: '#dbeafe', color: '#1d4ed8', label: 'Basic' },
  proficient: { bg: '#dcfce7', color: '#166534', label: 'Proficient' },
  expert:     { bg: '#fce7f3', color: '#9d174d', label: 'Expert ★' },
};

const ManagerSkillsMatrixView = ({ ws, onSave }) => {
  const [draft, setDraft] = useState(() => ({
    areas: [...(ws.skills?.areas || [])],
    grid: JSON.parse(JSON.stringify(ws.skills?.grid || {})),
  }));
  const [newArea, setNewArea] = useState('');
  const saveTimer = useRef(null);

  useEffect(() => {
    setDraft({
      areas: [...(ws.skills?.areas || [])],
      grid: JSON.parse(JSON.stringify(ws.skills?.grid || {})),
    });
  }, [ws.skills]);

  const schedule = (next) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => onSave(next), 400);
  };

  const members = ws.members;

  const addArea = () => {
    const name = newArea.trim();
    if (!name || draft.areas.includes(name)) return;
    const next = { ...draft, areas: [...draft.areas, name] };
    setDraft(next); schedule(next); setNewArea('');
  };
  const renameArea = (idx, value) => {
    const old = draft.areas[idx];
    const areas = draft.areas.map((a, i) => i === idx ? value : a);
    const grid = {};
    for (const [mid, cells] of Object.entries(draft.grid)) {
      const copy = {};
      for (const [k, v] of Object.entries(cells)) copy[k === old ? value : k] = v;
      grid[mid] = copy;
    }
    const next = { areas, grid };
    setDraft(next); schedule(next);
  };
  const removeArea = (idx) => {
    const area = draft.areas[idx];
    const areas = draft.areas.filter((_, i) => i !== idx);
    const grid = {};
    for (const [mid, cells] of Object.entries(draft.grid)) {
      const copy = { ...cells };
      delete copy[area];
      grid[mid] = copy;
    }
    const next = { areas, grid };
    setDraft(next); schedule(next);
  };
  const moveArea = (idx, delta) => {
    const j = idx + delta;
    if (j < 0 || j >= draft.areas.length) return;
    const areas = [...draft.areas];
    [areas[idx], areas[j]] = [areas[j], areas[idx]];
    const next = { ...draft, areas };
    setDraft(next); schedule(next);
  };
  const setCell = (memberId, area, level) => {
    const cells = { ...(draft.grid[memberId] || {}) };
    if (level === 'none') delete cells[area];
    else cells[area] = level;
    const grid = { ...draft.grid, [memberId]: cells };
    const next = { ...draft, grid };
    setDraft(next); schedule(next);
  };

  const handleUploadSkillsMd = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const { areas: parsed, unknownNames } = parseSkillMatrixMarkdown(text, members);
      if (parsed.length === 0) {
        alert('No skill areas found.\n\nExpected:\n  ## Skill area\n  - level: Member Name\n\nOr a top-level bullet list of areas.');
        return;
      }
      const existingLc = new Set(draft.areas.map(a => a.toLowerCase()));
      const newCount = parsed.filter(a => !existingLc.has(a.name.toLowerCase())).length;
      const updatedCount = parsed.length - newCount;
      const ratingCount = parsed.reduce((s, a) => s + Object.keys(a.ratings).length, 0);

      let msg = `Found ${parsed.length} skill area${parsed.length === 1 ? '' : 's'} in "${file.name}":\n`;
      msg += `  • ${newCount} new\n`;
      msg += `  • ${updatedCount} match existing (cells overwritten only where the file specifies)\n`;
      msg += `  • ${ratingCount} member rating${ratingCount === 1 ? '' : 's'} matched\n`;
      if (unknownNames.length > 0) msg += `\n⚠ Skipped ${unknownNames.length} unknown name${unknownNames.length === 1 ? '' : 's'}: ${unknownNames.join(', ')}\n`;
      msg += `\nContinue?`;
      if (!confirm(msg)) return;

      const nextAreas = [...draft.areas];
      for (const a of parsed) {
        if (!nextAreas.some(x => x.toLowerCase() === a.name.toLowerCase())) nextAreas.push(a.name);
      }
      const nextGrid = JSON.parse(JSON.stringify(draft.grid));
      for (const a of parsed) {
        const canonicalArea = nextAreas.find(x => x.toLowerCase() === a.name.toLowerCase()) || a.name;
        for (const [memberId, level] of Object.entries(a.ratings)) {
          const cells = { ...(nextGrid[memberId] || {}) };
          if (level === 'none') delete cells[canonicalArea];
          else cells[canonicalArea] = level;
          nextGrid[memberId] = cells;
        }
      }
      const next = { areas: nextAreas, grid: nextGrid };
      setDraft(next);
      schedule(next);
    } catch (err) {
      alert('Could not read the file: ' + (err.message || err));
    } finally {
      e.target.value = '';
    }
  };

  const countLevel = (area, level) => members.filter(m => (draft.grid[m.id] || {})[area] === level).length;
  const gapRows = draft.areas.filter(a => {
    const covered = members.filter(m => {
      const lvl = (draft.grid[m.id] || {})[a];
      return lvl === 'proficient' || lvl === 'expert';
    }).length;
    return covered < 2;
  });

  if (members.length === 0) {
    return <div className="bg-white rounded-lg border border-gray-200 p-12 text-center text-gray-400">Add team members before building the skills matrix.</div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Skills Matrix</h2>
          <p className="text-sm text-gray-500">{draft.areas.length} skill area{draft.areas.length === 1 ? '' : 's'} · {members.length} member{members.length === 1 ? '' : 's'}</p>
        </div>
        <div className="flex items-center gap-1">
          <label className="px-3 py-1.5 border border-gray-300 text-gray-700 text-sm rounded cursor-pointer hover:bg-gray-50">
            ⬆ Upload .md
            <input type="file" accept=".md,.markdown,text/markdown,text/plain" onChange={handleUploadSkillsMd} className="hidden" />
          </label>
          <input value={newArea} onChange={e => setNewArea(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') addArea(); }}
            placeholder="Add skill area…" className="border border-gray-300 rounded px-2 py-1.5 text-sm" />
          <button onClick={addArea} disabled={!newArea.trim()} className="px-3 py-1.5 bg-pink-600 text-white text-sm rounded hover:bg-pink-700 disabled:opacity-50 font-medium">+ Add</button>
        </div>
      </div>

      {gapRows.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
          <div className="text-xs font-semibold text-amber-800 uppercase tracking-wide mb-1">Bus-factor risk</div>
          <p className="text-sm text-amber-900">
            {gapRows.length} skill area{gapRows.length === 1 ? '' : 's'} with fewer than 2 proficient+ members:
          </p>
          <div className="flex flex-wrap gap-1 mt-1">
            {gapRows.map(a => <span key={a} className="text-xs px-2 py-0.5 rounded bg-amber-100 text-amber-900 font-medium">{a}</span>)}
          </div>
        </div>
      )}

      {draft.areas.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <p className="text-gray-400 mb-3">No skill areas yet. Add what matters for your team — technical, product, tools, or soft skills.</p>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto">
          <table className="text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase sticky left-0 bg-gray-50 z-10 min-w-[200px]">Skill area</th>
                {members.map(m => (
                  <th key={m.id} className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase min-w-[140px] whitespace-nowrap">{m.name}</th>
                ))}
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase whitespace-nowrap">Coverage</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {draft.areas.map((area, idx) => {
                const expertN = countLevel(area, 'expert');
                const profN = countLevel(area, 'proficient');
                const risky = (expertN + profN) < 2;
                return (
                  <tr key={area + idx} className="hover:bg-gray-50/50">
                    <td className="px-3 py-2 sticky left-0 bg-white z-10 min-w-[200px]">
                      <div className="flex items-center gap-1 group">
                        <input value={area} onChange={e => renameArea(idx, e.target.value)}
                          className="flex-1 border border-transparent hover:border-gray-200 focus:border-gray-300 rounded px-1 py-0.5 text-sm font-medium" />
                        <button onClick={() => moveArea(idx, -1)} disabled={idx === 0} className="opacity-0 group-hover:opacity-100 px-1 text-xs text-gray-400 hover:text-gray-800 disabled:opacity-20">▲</button>
                        <button onClick={() => moveArea(idx, 1)} disabled={idx === draft.areas.length - 1} className="opacity-0 group-hover:opacity-100 px-1 text-xs text-gray-400 hover:text-gray-800 disabled:opacity-20">▼</button>
                        <button onClick={() => requestConfirm(`Remove "${area}"?`, () => removeArea(idx))} className="opacity-0 group-hover:opacity-100 px-1 text-xs text-red-500 hover:bg-red-50 rounded">✕</button>
                      </div>
                    </td>
                    {members.map(m => {
                      const lvl = (draft.grid[m.id] || {})[area] || 'none';
                      const st = MC_SKILL_LEVEL_STYLE[lvl];
                      return (
                        <td key={m.id} className="px-3 py-2">
                          <select value={lvl} onChange={e => setCell(m.id, area, e.target.value)}
                            className="text-xs rounded px-2 py-1 border-0 font-semibold w-full" style={{ background: st.bg, color: st.color }}>
                            {MC_SKILL_LEVELS.map(l => <option key={l} value={l}>{MC_SKILL_LEVEL_STYLE[l].label}</option>)}
                          </select>
                        </td>
                      );
                    })}
                    <td className="px-3 py-2 text-xs whitespace-nowrap">
                      <span className={risky ? 'text-amber-700 font-semibold' : 'text-gray-600'}>
                        {expertN}★ expert + {profN} proficient
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ---- Succession Map ----
const mcSuccessionRag = (entry) => {
  const n = (entry.backupIds || []).length;
  if (n >= 2) return { label: `${n} backups`, bg: 'bg-green-100', color: 'text-green-700' };
  if (n === 1) return { label: '1 backup',   bg: 'bg-amber-100', color: 'text-amber-700' };
  return { label: 'no backup', bg: 'bg-red-100', color: 'text-red-700' };
};

const ManagerSuccessionEditor = ({ entry, members, onSave, onDelete, onClose }) => {
  const editing = !!entry?.id;
  const [form, setForm] = useState({
    role: entry?.role || '',
    primaryId: entry?.primaryId || '',
    backupIds: entry?.backupIds || [],
    notes: entry?.notes || '',
  });
  const toggleBackup = (id) => {
    setForm(f => f.backupIds.includes(id)
      ? { ...f, backupIds: f.backupIds.filter(x => x !== id) }
      : { ...f, backupIds: [...f.backupIds, id] });
  };
  const handleSave = async () => {
    if (!form.role.trim()) return;
    await onSave({
      role: form.role.trim(),
      primaryId: form.primaryId ? parseInt(form.primaryId) : null,
      backupIds: form.backupIds.filter(id => id !== (form.primaryId ? parseInt(form.primaryId) : null)),
      notes: form.notes,
    });
    onClose();
  };
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-base font-semibold">{editing ? 'Edit Role' : 'New Role'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">×</button>
        </div>
        <div className="p-5 space-y-3">
          <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide">Role / responsibility
            <input autoFocus value={form.role} onChange={e => setForm({ ...form, role: e.target.value })}
              placeholder="e.g. Payments codebase owner, Key account: Acme" className="w-full mt-1 border border-gray-300 rounded-lg px-3 py-2 text-sm font-normal normal-case text-gray-800" />
          </label>
          <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide">Primary
            <select value={form.primaryId || ''} onChange={e => setForm({ ...form, primaryId: e.target.value })}
              className="w-full mt-1 border border-gray-300 rounded-lg px-3 py-2 text-sm font-normal normal-case text-gray-800">
              <option value="">— unassigned —</option>
              {members.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
          </label>
          <div>
            <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide block mb-1">Backups</label>
            {members.length === 0 ? <p className="text-xs text-gray-400">No members.</p> : (
              <div className="flex flex-wrap gap-1">
                {members.map(m => {
                  const isPrimary = parseInt(form.primaryId) === m.id;
                  return (
                    <button key={m.id} onClick={() => toggleBackup(m.id)} disabled={isPrimary}
                      className={`px-2 py-1 text-xs rounded border ${isPrimary ? 'bg-gray-100 border-gray-200 text-gray-400' : form.backupIds.includes(m.id) ? 'bg-pink-100 border-pink-400 text-pink-800' : 'bg-white border-gray-300 text-gray-600 hover:border-pink-300'}`}
                      title={isPrimary ? 'Already primary' : ''}>
                      {m.name}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide">Notes
            <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={3}
              className="w-full mt-1 border border-gray-300 rounded-lg px-3 py-2 text-sm font-normal normal-case text-gray-800" />
          </label>
        </div>
        <div className="px-5 py-3 border-t border-gray-100 flex justify-between items-center">
          <div>
            {editing && <button onClick={() => requestConfirm(`Delete role "${form.role}"?`, async () => { await onDelete(entry.id); onClose(); })}
              className="text-xs text-red-600 hover:underline">Delete</button>}
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-3 py-1.5 text-sm text-gray-600">Cancel</button>
            <button onClick={handleSave} disabled={!form.role.trim()} className="px-4 py-1.5 bg-pink-600 text-white text-sm rounded-lg hover:bg-pink-700 disabled:opacity-50">{editing ? 'Save' : 'Add'}</button>
          </div>
        </div>
      </div>
    </div>
  );
};

const ManagerSuccessionView = ({ ws, onAdd, onUpdate, onDelete }) => {
  const [editing, setEditing] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const entries = ws.succession || [];
  const nameOf = (id) => ws.members.find(m => m.id === id)?.name || '';
  const noBackup = entries.filter(e => (e.backupIds || []).length === 0);

  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Succession Map</h2>
          <p className="text-sm text-gray-500">{entries.length} role{entries.length === 1 ? '' : 's'} mapped</p>
        </div>
        <button onClick={() => setShowAdd(true)} className="px-3 py-1.5 bg-pink-600 text-white text-sm rounded-lg hover:bg-pink-700 font-medium">+ Role</button>
      </div>

      {noBackup.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
          <div className="text-xs font-semibold text-red-800 uppercase tracking-wide mb-1">Continuity risk</div>
          <p className="text-sm text-red-900">
            {noBackup.length} role{noBackup.length === 1 ? ' has' : 's have'} no backup:
          </p>
          <div className="flex flex-wrap gap-1 mt-1">
            {noBackup.map(e => <span key={e.id} className="text-xs px-2 py-0.5 rounded bg-red-100 text-red-900 font-medium">{e.role}</span>)}
          </div>
        </div>
      )}

      {entries.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <p className="text-gray-400 mb-3">No roles mapped yet. Start with anything critical — code areas, key accounts, vendor relationships, on-call knowledge.</p>
          <button onClick={() => setShowAdd(true)} className="px-4 py-2 bg-pink-600 text-white text-sm rounded-lg hover:bg-pink-700">+ Add First Role</button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {entries.map(e => {
            const rag = mcSuccessionRag(e);
            return (
              <div key={e.id} onClick={() => setEditing(e)} className="bg-white border border-gray-200 rounded-lg p-4 cursor-pointer hover:border-pink-300 hover:shadow-sm">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="font-semibold text-gray-900 min-w-0 truncate">{e.role}</div>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${rag.bg} ${rag.color}`}>{rag.label}</span>
                </div>
                <div className="text-sm text-gray-600 mb-1">
                  <span className="text-xs text-gray-500 uppercase tracking-wide font-semibold">Primary: </span>
                  {e.primaryId ? (
                    <span className="text-gray-800">{nameOf(e.primaryId)}</span>
                  ) : (
                    <span className="text-amber-700 italic">— unassigned —</span>
                  )}
                </div>
                <div className="text-sm text-gray-600">
                  <span className="text-xs text-gray-500 uppercase tracking-wide font-semibold">Backups: </span>
                  {(e.backupIds || []).length === 0 ? (
                    <span className="text-red-700 italic">none</span>
                  ) : (
                    (e.backupIds || []).map(id => nameOf(id)).filter(Boolean).join(', ')
                  )}
                </div>
                {e.notes && <p className="text-xs text-gray-500 mt-2 whitespace-pre-wrap">{e.notes}</p>}
              </div>
            );
          })}
        </div>
      )}

      {showAdd && <ManagerSuccessionEditor entry={null} members={ws.members}
        onSave={onAdd} onDelete={onDelete} onClose={() => setShowAdd(false)} />}
      {editing && <ManagerSuccessionEditor entry={editing} members={ws.members}
        onSave={(data) => onUpdate(editing.id, data)} onDelete={onDelete} onClose={() => setEditing(null)} />}
    </div>
  );
};

// ---- Compensation ----
const MC_PROMOTION_READINESS = [
  { key: 'not_ready', label: 'Not ready',  bg: 'bg-gray-100',  color: 'text-gray-700' },
  { key: 'emerging',  label: 'Emerging',   bg: 'bg-blue-100',  color: 'text-blue-700' },
  { key: 'ready',     label: 'Ready',      bg: 'bg-green-100', color: 'text-green-700' },
  { key: 'promoting', label: 'Promoting',  bg: 'bg-pink-100',  color: 'text-pink-700' },
];
const mcFormatCurrency = (amount, currency) => {
  if (amount == null || amount === '') return '—';
  try { return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency || 'USD', maximumFractionDigits: 0 }).format(Number(amount)); }
  catch { return `${currency || ''} ${Number(amount).toLocaleString()}`; }
};
const mcMaskedCurrency = (amount, currency) => {
  if (amount == null || amount === '') return '—';
  const sym = currency === 'USD' || !currency ? '$' : `${currency} `;
  return `${sym}••••••`;
};
const mcDaysUntil = (iso) => {
  if (!iso) return null;
  const d = new Date(iso + 'T12:00:00Z');
  const now = new Date(); now.setUTCHours(12, 0, 0, 0);
  return Math.round((d - now) / 86400000);
};
const mcFormatAttribution = (by, at) => {
  if (!by && !at) return '';
  const when = at ? new Date(at).toLocaleDateString() : '';
  return `Last edited${by ? ` by ${by}` : ''}${when ? ` on ${when}` : ''}`;
};

const ManagerCompView = ({ ws, onSave }) => {
  const [selectedId, setSelectedId] = useState(ws.members[0]?.id || null);
  const [showSalary, setShowSalary] = useState(false);
  useEffect(() => {
    if (!ws.members.find(m => m.id === selectedId)) setSelectedId(ws.members[0]?.id || null);
  }, [ws.members, selectedId]);

  const empty = { currentLevel: '', salary: '', currency: 'USD', lastRaiseDate: '', lastRaisePct: '',
    nextReviewDate: '', promotionReadiness: 'not_ready', targetLevel: '', delegateNotes: '', managerNotes: '' };
  const [draft, setDraft] = useState(empty);
  const saveTimer = useRef(null);

  useEffect(() => {
    if (!selectedId) { setDraft(empty); return; }
    const c = ws.comp?.[selectedId];
    setDraft(c ? { ...empty, ...c, salary: c.salary ?? '', lastRaisePct: c.lastRaisePct ?? '' } : empty);
  }, [selectedId, ws.comp]);

  const schedule = (next) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => onSave(selectedId, next), 500);
  };
  const set = (patch) => { const next = { ...draft, ...patch }; setDraft(next); schedule(next); };

  const reviewsDue = ws.members.map(m => {
    const c = ws.comp?.[m.id];
    return { m, d: mcDaysUntil(c?.nextReviewDate) };
  }).filter(x => x.d != null && x.d <= 90 && x.d >= 0).sort((a, b) => a.d - b.d);

  if (ws.members.length === 0) {
    return <div className="bg-white rounded-lg border border-gray-200 p-12 text-center text-gray-400">Add team members to manage compensation.</div>;
  }

  const active = ws.comp?.[selectedId];
  const currentReadiness = MC_PROMOTION_READINESS.find(r => r.key === draft.promotionReadiness) || MC_PROMOTION_READINESS[0];
  const daysUntilNext = mcDaysUntil(draft.nextReviewDate);

  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">🔒 Compensation & Leveling</h2>
          <p className="text-sm text-gray-500">Private — scoped to this workspace.</p>
        </div>
        <label className="text-xs text-gray-600 flex items-center gap-1 cursor-pointer">
          <input type="checkbox" checked={showSalary} onChange={e => setShowSalary(e.target.checked)} /> Show salary
        </label>
      </div>

      {reviewsDue.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3">
          <div className="text-xs font-semibold text-amber-800 uppercase tracking-wide mb-1">Reviews due within 90 days</div>
          <div className="flex flex-wrap gap-1">
            {reviewsDue.map(({ m, d }) => (
              <button key={m.id} onClick={() => setSelectedId(m.id)}
                className="text-xs px-2 py-0.5 rounded bg-amber-100 text-amber-900 font-medium hover:bg-amber-200">
                {m.name} — {d}d
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="flex gap-4">
        <aside className="w-64 bg-white rounded-lg border border-gray-200 p-2 flex-shrink-0 h-max">
          {ws.members.map(m => {
            const c = ws.comp?.[m.id];
            const r = MC_PROMOTION_READINESS.find(x => x.key === (c?.promotionReadiness || 'not_ready')) || MC_PROMOTION_READINESS[0];
            return (
              <button key={m.id} onClick={() => setSelectedId(m.id)}
                className={`w-full flex flex-col items-start text-left px-3 py-2 rounded text-sm ${selectedId === m.id ? 'bg-pink-50 text-pink-800' : 'text-gray-700 hover:bg-gray-50'}`}>
                <span className="font-medium truncate">{m.name}</span>
                <span className="text-xs text-gray-500 flex items-center gap-1">
                  {c?.currentLevel || 'No level'}
                  <span className={`text-[10px] px-1.5 py-0.5 rounded ${r.bg} ${r.color}`}>{r.label}</span>
                </span>
              </button>
            );
          })}
        </aside>

        <div className="flex-1 min-w-0 space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <h3 className="text-lg font-bold text-gray-900">{ws.members.find(m => m.id === selectedId)?.name || '—'}</h3>
              {active && <p className="text-xs text-gray-400">{mcFormatAttribution(active.lastUpdatedBy, active.lastUpdatedAt)}</p>}
            </div>
            <span className={`text-xs px-2 py-1 rounded-full font-semibold ${currentReadiness.bg} ${currentReadiness.color}`}>
              {currentReadiness.label}
            </span>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">Current</div>
            <div className="grid grid-cols-3 gap-2">
              <label className="text-xs text-gray-500 uppercase tracking-wide font-medium">Level
                <input value={draft.currentLevel} onChange={e => set({ currentLevel: e.target.value })} placeholder="e.g. IC3, M1, P2"
                  className="w-full mt-1 border border-gray-300 rounded-lg px-2 py-1.5 text-sm font-normal normal-case text-gray-800" />
              </label>
              <label className="text-xs text-gray-500 uppercase tracking-wide font-medium">Salary
                {showSalary ? (
                  <input type="number" value={draft.salary} onChange={e => set({ salary: e.target.value })}
                    className="w-full mt-1 border border-gray-300 rounded-lg px-2 py-1.5 text-sm font-normal normal-case text-gray-800" />
                ) : (
                  <div className="w-full mt-1 border border-gray-300 rounded-lg px-2 py-1.5 text-sm bg-gray-50 text-gray-500 font-normal normal-case">
                    {mcMaskedCurrency(draft.salary, draft.currency)}
                  </div>
                )}
              </label>
              <label className="text-xs text-gray-500 uppercase tracking-wide font-medium">Currency
                <input value={draft.currency} onChange={e => set({ currency: e.target.value.toUpperCase().slice(0, 4) })}
                  className="w-full mt-1 border border-gray-300 rounded-lg px-2 py-1.5 text-sm font-normal normal-case text-gray-800" />
              </label>
            </div>
            {showSalary && draft.salary !== '' && (
              <div className="text-xs text-gray-500 mt-2">Formatted: {mcFormatCurrency(draft.salary, draft.currency)}</div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">Last raise</div>
              <div className="grid grid-cols-2 gap-2">
                <label className="text-xs text-gray-500 uppercase tracking-wide font-medium">Date
                  <input type="date" value={draft.lastRaiseDate || ''} onChange={e => set({ lastRaiseDate: e.target.value })}
                    className="w-full mt-1 border border-gray-300 rounded-lg px-2 py-1.5 text-sm font-normal normal-case text-gray-800" />
                </label>
                <label className="text-xs text-gray-500 uppercase tracking-wide font-medium">Percent
                  <input type="number" step="0.1" value={draft.lastRaisePct} onChange={e => set({ lastRaisePct: e.target.value })}
                    className="w-full mt-1 border border-gray-300 rounded-lg px-2 py-1.5 text-sm font-normal normal-case text-gray-800" />
                </label>
              </div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">Next review</div>
              <input type="date" value={draft.nextReviewDate || ''} onChange={e => set({ nextReviewDate: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm font-normal normal-case text-gray-800" />
              {daysUntilNext != null && (
                <div className={`text-xs mt-1 ${daysUntilNext <= 30 ? 'text-amber-700 font-semibold' : 'text-gray-500'}`}>
                  {daysUntilNext < 0 ? `Overdue by ${-daysUntilNext}d` : daysUntilNext === 0 ? 'Today' : `In ${daysUntilNext}d`}
                </div>
              )}
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">Promotion track</div>
            <div className="grid grid-cols-2 gap-2 items-end">
              <label className="text-xs text-gray-500 uppercase tracking-wide font-medium">Target level
                <input value={draft.targetLevel} onChange={e => set({ targetLevel: e.target.value })}
                  className="w-full mt-1 border border-gray-300 rounded-lg px-2 py-1.5 text-sm font-normal normal-case text-gray-800" />
              </label>
              <label className="text-xs text-gray-500 uppercase tracking-wide font-medium">Readiness
                <select value={draft.promotionReadiness} onChange={e => set({ promotionReadiness: e.target.value })}
                  className="w-full mt-1 border border-gray-300 rounded-lg px-2 py-1.5 text-sm font-normal normal-case text-gray-800">
                  {MC_PROMOTION_READINESS.map(r => <option key={r.key} value={r.key}>{r.label}</option>)}
                </select>
              </label>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-1">
                <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Delegate's notes / HR context</div>
                <span className="text-[10px] bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded font-medium">delegate</span>
              </div>
              <textarea value={draft.delegateNotes} onChange={e => set({ delegateNotes: e.target.value })} rows={4}
                className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm" />
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-1">
                <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Manager's observations</div>
                <span className="text-[10px] bg-pink-100 text-pink-700 px-1.5 py-0.5 rounded font-medium">manager</span>
              </div>
              <textarea value={draft.managerNotes} onChange={e => set({ managerNotes: e.target.value })} rows={4}
                className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ---- Retention Risk ----
const MC_RISK_STYLE = {
  low:    { bg: 'bg-green-100',  color: 'text-green-700', label: 'Low' },
  medium: { bg: 'bg-amber-100',  color: 'text-amber-700', label: 'Medium' },
  high:   { bg: 'bg-red-100',    color: 'text-red-700',   label: 'High' },
};

const ManagerRetentionView = ({ ws, onSave }) => {
  const [selectedId, setSelectedId] = useState(ws.members[0]?.id || null);
  useEffect(() => {
    if (!ws.members.find(m => m.id === selectedId)) setSelectedId(ws.members[0]?.id || null);
  }, [ws.members, selectedId]);

  const defaults = ws.retentionSignalsDefault || [];
  const empty = { riskLevel: 'low', signals: [], stayScore: '', mitigations: '', delegateNotes: '', managerNotes: '' };
  const [draft, setDraft] = useState(empty);
  const [customSignal, setCustomSignal] = useState('');
  const saveTimer = useRef(null);

  useEffect(() => {
    if (!selectedId) { setDraft(empty); return; }
    const r = ws.retention?.[selectedId];
    setDraft(r ? { ...empty, ...r, stayScore: r.stayScore ?? '' } : empty);
  }, [selectedId, ws.retention]);

  const schedule = (next) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => onSave(selectedId, next), 500);
  };
  const set = (patch) => { const next = { ...draft, ...patch }; setDraft(next); schedule(next); };
  const toggleSignal = (s) => {
    const has = draft.signals.includes(s);
    set({ signals: has ? draft.signals.filter(x => x !== s) : [...draft.signals, s] });
  };
  const addCustom = () => {
    const v = customSignal.trim();
    if (!v || draft.signals.includes(v)) return;
    set({ signals: [...draft.signals, v] });
    setCustomSignal('');
  };

  // Stat cards
  const counts = { high: 0, medium: 0, low: 0 };
  ws.members.forEach(m => {
    const r = ws.retention?.[m.id];
    counts[(r?.riskLevel || 'low')] = (counts[(r?.riskLevel || 'low')] || 0) + 1;
  });
  const highRisk = ws.members.filter(m => (ws.retention?.[m.id]?.riskLevel) === 'high');

  if (ws.members.length === 0) {
    return <div className="bg-white rounded-lg border border-gray-200 p-12 text-center text-gray-400">Add team members to track retention risk.</div>;
  }

  const active = ws.retention?.[selectedId];
  const activeStyle = MC_RISK_STYLE[draft.riskLevel] || MC_RISK_STYLE.low;

  return (
    <div>
      <div className="mb-4">
        <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">🔒 Retention Risk</h2>
        <p className="text-sm text-gray-500">Private — your read of 12-month retention likelihood.</p>
      </div>

      <div className="grid grid-cols-3 gap-2 mb-3">
        <div className="bg-white rounded-lg border border-gray-200 px-3 py-2">
          <div className="text-[10px] text-gray-500 uppercase tracking-wide">High risk</div>
          <div className="text-lg font-bold text-red-600">{counts.high || 0}</div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 px-3 py-2">
          <div className="text-[10px] text-gray-500 uppercase tracking-wide">Medium</div>
          <div className="text-lg font-bold text-amber-600">{counts.medium || 0}</div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 px-3 py-2">
          <div className="text-[10px] text-gray-500 uppercase tracking-wide">Low</div>
          <div className="text-lg font-bold text-green-700">{counts.low || 0}</div>
        </div>
      </div>

      {highRisk.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-3">
          <div className="text-xs font-semibold text-red-800 uppercase tracking-wide mb-1">High-risk members</div>
          <div className="flex flex-wrap gap-1">
            {highRisk.map(m => (
              <button key={m.id} onClick={() => setSelectedId(m.id)}
                className="text-xs px-2 py-0.5 rounded bg-red-100 text-red-900 font-medium hover:bg-red-200">{m.name}</button>
            ))}
          </div>
        </div>
      )}

      <div className="flex gap-4">
        <aside className="w-64 bg-white rounded-lg border border-gray-200 p-2 flex-shrink-0 h-max">
          {ws.members.map(m => {
            const r = ws.retention?.[m.id];
            const st = MC_RISK_STYLE[r?.riskLevel || 'low'];
            return (
              <button key={m.id} onClick={() => setSelectedId(m.id)}
                className={`w-full flex items-center justify-between text-left px-3 py-2 rounded text-sm ${selectedId === m.id ? 'bg-pink-50 text-pink-800' : 'text-gray-700 hover:bg-gray-50'}`}>
                <span className="font-medium truncate">{m.name}</span>
                <span className={`text-[10px] px-1.5 py-0.5 rounded ${st.bg} ${st.color}`}>{st.label}</span>
              </button>
            );
          })}
        </aside>

        <div className="flex-1 min-w-0 space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <h3 className="text-lg font-bold text-gray-900">{ws.members.find(m => m.id === selectedId)?.name || '—'}</h3>
              {active && <p className="text-xs text-gray-400">{mcFormatAttribution(active.lastUpdatedBy, active.lastReviewed)}</p>}
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              {Object.entries(MC_RISK_STYLE).map(([k, st]) => (
                <button key={k} onClick={() => set({ riskLevel: k })}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium border ${draft.riskLevel === k ? `${st.bg} ${st.color} border-current` : 'bg-white border-gray-300 text-gray-600 hover:border-gray-400'}`}>
                  {st.label}
                </button>
              ))}
            </div>
            <div>
              <label className="text-xs text-gray-500 uppercase tracking-wide font-medium">Stay-score (12-month gut estimate): <span className="text-sm text-gray-800 normal-case font-semibold">{draft.stayScore || '—'}{draft.stayScore ? '/10' : ''}</span></label>
              <input type="range" min="1" max="10" step="1" value={draft.stayScore || 5}
                onChange={e => set({ stayScore: parseInt(e.target.value) })} className="w-full mt-1 accent-pink-600" />
              <div className="flex justify-between text-[10px] text-gray-400"><span>1 · likely leaving</span><span>10 · firmly staying</span></div>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">Signals</div>
            <div className="flex flex-wrap gap-1 mb-2">
              {[...defaults, ...draft.signals.filter(s => !defaults.includes(s))].map(s => (
                <button key={s} onClick={() => toggleSignal(s)}
                  className={`text-xs px-2 py-1 rounded border ${draft.signals.includes(s) ? 'bg-pink-100 border-pink-400 text-pink-800' : 'bg-white border-gray-300 text-gray-600 hover:border-pink-300'}`}>
                  {s}
                </button>
              ))}
            </div>
            <div className="flex gap-1">
              <input value={customSignal} onChange={e => setCustomSignal(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') addCustom(); }}
                placeholder="Add custom signal…" className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm" />
              <button onClick={addCustom} disabled={!customSignal.trim()} className="px-3 py-1 bg-pink-600 text-white text-sm rounded hover:bg-pink-700 disabled:opacity-50">+ Add</button>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">Mitigation plan</div>
            <textarea value={draft.mitigations} onChange={e => set({ mitigations: e.target.value })} rows={4}
              className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-1">
                <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Delegate's notes / HR context</div>
                <span className="text-[10px] bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded font-medium">delegate</span>
              </div>
              <textarea value={draft.delegateNotes} onChange={e => set({ delegateNotes: e.target.value })} rows={4}
                className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm" />
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-1">
                <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Manager's observations</div>
                <span className="text-[10px] bg-pink-100 text-pink-700 px-1.5 py-0.5 rounded font-medium">manager</span>
              </div>
              <textarea value={draft.managerNotes} onChange={e => set({ managerNotes: e.target.value })} rows={4}
                className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ---- Executive View ----
const ManagerExecutiveView = ({ ws, activeQuarter, onNavigate }) => {
  const months = mcQuarterMonths(activeQuarter);
  const tpl = ws.okrTemplate || [];
  const today = mcTodayIso();

  // Team OKR avg & trend (per month)
  const monthlyTeamAvgs = months.map((mo, idx) => {
    const scores = ws.members.map(m => {
      const e = ws.evaluations[m.id]?.[mo] || {};
      return mcScoreOverall(tpl, e, idx);
    }).filter(s => s != null);
    return scores.length ? scores.reduce((a, b) => a + b, 0) / scores.length : null;
  });
  const teamAvg = (() => {
    const defined = monthlyTeamAvgs.filter(s => s != null);
    return defined.length ? defined[defined.length - 1] : null;
  })();
  const teamRag = mcRag(teamAvg);
  const trendDir = (() => {
    const defined = monthlyTeamAvgs.filter(s => s != null);
    if (defined.length < 2) return 'steady';
    const diff = defined[defined.length - 1] - defined[defined.length - 2];
    if (diff > 0.05) return 'improving';
    if (diff < -0.05) return 'declining';
    return 'steady';
  })();

  // Active onboarding + reviews due
  const activeHires = (ws.hires || []).filter(h => h.status === 'active');
  const reviewsDue = activeHires.filter(h => {
    const prog = ws.hireProgress?.[h.id] || {};
    const startKey = ws.onboardingTemplate.phases[ws.onboardingTemplate.phases.length - 1]?.key;
    return prog[startKey] && !prog.review90?.decision;
  }).length;

  // Retention total
  const retentionCounts = { high: 0, medium: 0, low: 0 };
  ws.members.forEach(m => { const r = ws.retention?.[m.id]; retentionCounts[r?.riskLevel || 'low'] = (retentionCounts[r?.riskLevel || 'low'] || 0) + 1; });
  const retentionTotal = retentionCounts.high + retentionCounts.medium;

  // Latest pulse
  const latestPulse = (ws.pulse || [])[0] || null;
  const latestPulseAvg = latestPulse ? mcPulseAvg(latestPulse.scores) : null;

  // Needs attention
  const skillGaps = ((ws.skills?.areas) || []).filter(a => {
    const covered = ws.members.filter(m => {
      const lvl = ws.skills.grid?.[m.id]?.[a];
      return lvl === 'proficient' || lvl === 'expert';
    }).length;
    return covered < 2;
  }).length;
  const succNoBackup = (ws.succession || []).filter(e => (e.backupIds || []).length === 0).length;
  const compDue60 = ws.members.filter(m => {
    const c = ws.comp?.[m.id];
    const d = mcDaysUntil(c?.nextReviewDate);
    return d != null && d >= 0 && d <= 60;
  }).length;
  const ninetyDayDue2w = activeHires.filter(h => {
    const startDate = h.startDate;
    if (!startDate) return false;
    const target = mcAddDays(startDate, 90);
    const d = mcDaysUntil(target);
    const prog = ws.hireProgress?.[h.id] || {};
    const done = !!prog.review90?.decision;
    return !done && d != null && d >= 0 && d <= 14;
  }).length;
  const thisWeekMon = mcCurrentMonday();
  const thisWeekOncall = (ws.oncall || []).find(r => r.weekStartDate === thisWeekMon) || null;
  const noOncallThisWeek = ws.settings.showOncall && (!thisWeekOncall || !thisWeekOncall.primaryId);

  // Who's out today
  const outToday = [];
  Object.entries(ws.pto || {}).forEach(([mid, arr]) => {
    arr.forEach(e => { if (e.startDate <= today && e.endDate >= today) outToday.push({ ...e, memberId: parseInt(mid) }); });
  });

  // Coaching activity count (last 7 days)
  const sevenAgo = mcAddDays(today, -7);
  let coachingActivity = 0;
  Object.values(ws.coachingLogs || {}).forEach(arr => { arr.forEach(l => { if ((l.date || '') >= sevenAgo) coachingActivity++; }); });

  // IDPs in progress
  const idpsInProgress = Object.values(ws.idps || {}).filter(i => i.status === 'active' || i.status === 'under_review').length;

  // Per-member snapshot
  const snapshot = ws.members.map(m => {
    const scores = months.map((mo, idx) => {
      const e = ws.evaluations[m.id]?.[mo] || {};
      return mcScoreOverall(tpl, e, idx);
    });
    const latest = [...scores].reverse().find(s => s != null);
    const latestPct = latest == null ? null : Math.round(latest * 100);
    const r = ws.retention?.[m.id]?.riskLevel || 'low';
    const c = ws.comp?.[m.id];
    const idp = ws.idps?.[m.id];
    const focus = idp?.growthAreas || idp?.careerGoals || m.notes || '';
    return { m, latestPct, risk: r, level: c?.currentLevel || '—', focus };
  });

  // Monthly team avgs as bars
  const barMax = 1.2;
  const compReviewsCount = compDue60;

  // Hiring/Growth/Risk counts for Manager time allocation
  const hiringCount = (ws.candidates || []).filter(c => c.stage !== 'Hired' && c.stage !== 'Declined').length + activeHires.length;
  const growthCount = idpsInProgress + Object.values(ws.oneOnOnes || {}).filter(oo => (oo.topics || []).some(t => !t.done)).length;
  const riskCount = retentionCounts.high + retentionCounts.medium + skillGaps + succNoBackup;

  const tileCls = 'bg-white rounded-lg border border-gray-200 p-4 hover:border-pink-300 hover:shadow-sm cursor-pointer';

  const nameOf = (id) => ws.members.find(m => m.id === id)?.name || '—';
  const flagBtn = 'w-full flex items-center justify-between px-3 py-2 text-sm rounded hover:bg-gray-50 text-left';

  return (
    <div>
      <div className="mb-4 flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Executive View</h2>
          <p className="text-sm text-gray-500">Read-only rollup — {mcQuarterLabel(activeQuarter)}</p>
        </div>
      </div>

      {/* Hero tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <div className={tileCls} onClick={() => onNavigate('dashboard')}>
          <div className="text-xs text-gray-500 uppercase tracking-wide">Team OKR avg</div>
          <div className="flex items-baseline gap-2">
            <span className="text-3xl font-bold" style={{ color: teamRag.color }}>{teamAvg == null ? '—' : teamAvg.toFixed(2)}</span>
            <span className="text-xs text-gray-500">
              {trendDir === 'improving' && '↑ improving'}
              {trendDir === 'declining' && '↓ declining'}
              {trendDir === 'steady' && '→ steady'}
            </span>
          </div>
        </div>
        <div className={tileCls} onClick={() => onNavigate('onboarding')}>
          <div className="text-xs text-gray-500 uppercase tracking-wide">Onboarding</div>
          <div className="text-3xl font-bold text-gray-900">{activeHires.length}</div>
          <div className="text-xs text-gray-500">{reviewsDue} review{reviewsDue === 1 ? '' : 's'} due</div>
        </div>
        <div className={tileCls} onClick={() => onNavigate('retention')}>
          <div className="text-xs text-gray-500 uppercase tracking-wide">Retention risk (H+M)</div>
          <div className="text-3xl font-bold" style={{ color: retentionTotal > 0 ? '#dc2626' : '#16a34a' }}>{retentionTotal}</div>
          <div className="text-xs text-gray-500">{retentionCounts.high} high · {retentionCounts.medium} medium</div>
        </div>
        <div className={tileCls} onClick={() => onNavigate('pulse')}>
          <div className="text-xs text-gray-500 uppercase tracking-wide">Latest pulse</div>
          <div className="flex items-baseline gap-2">
            <span className="text-3xl font-bold" style={{ color: mcPulseScoreStyle(latestPulseAvg).color }}>{latestPulseAvg == null ? '—' : latestPulseAvg.toFixed(1)}</span>
          </div>
          <div className="text-xs text-gray-500">{latestPulse ? mcFmtShort(latestPulse.date) : 'none yet'}</div>
        </div>
      </div>

      {/* Needs attention */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4">
        <div className="text-sm font-semibold text-gray-800 mb-2">Needs attention</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
          {retentionCounts.high > 0 && (
            <button onClick={() => onNavigate('retention')} className={flagBtn}>
              <span>🚨 High retention risk</span><span className="text-red-600 font-semibold">{retentionCounts.high}</span>
            </button>
          )}
          {skillGaps > 0 && (
            <button onClick={() => onNavigate('skills')} className={flagBtn}>
              <span>🧠 Skill coverage gaps</span><span className="text-amber-600 font-semibold">{skillGaps}</span>
            </button>
          )}
          {succNoBackup > 0 && (
            <button onClick={() => onNavigate('succession')} className={flagBtn}>
              <span>🔗 Succession roles without backup</span><span className="text-red-600 font-semibold">{succNoBackup}</span>
            </button>
          )}
          {ninetyDayDue2w > 0 && (
            <button onClick={() => onNavigate('onboarding')} className={flagBtn}>
              <span>🧭 90-day reviews due within 2 weeks</span><span className="text-amber-600 font-semibold">{ninetyDayDue2w}</span>
            </button>
          )}
          {compReviewsCount > 0 && (
            <button onClick={() => onNavigate('comp')} className={flagBtn}>
              <span>💰 Comp reviews due within 60 days</span><span className="text-amber-600 font-semibold">{compReviewsCount}</span>
            </button>
          )}
          {noOncallThisWeek && (
            <button onClick={() => onNavigate('oncall')} className={flagBtn}>
              <span>🚨 No on-call scheduled this week</span><span className="text-red-600 font-semibold">!</span>
            </button>
          )}
          {(retentionCounts.high + skillGaps + succNoBackup + ninetyDayDue2w + compReviewsCount + (noOncallThisWeek ? 1 : 0)) === 0 && (
            <div className="text-sm text-gray-400 italic px-3 py-2">Nothing pressing.</div>
          )}
        </div>
      </div>

      {/* This week */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4 grid grid-cols-2 md:grid-cols-4 gap-3">
        {ws.settings.showOncall && (
          <div>
            <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">This week on-call</div>
            <div className="text-sm text-gray-800 font-medium">{thisWeekOncall?.primaryId ? nameOf(thisWeekOncall.primaryId) : <span className="text-amber-700">— none —</span>}</div>
            {thisWeekOncall?.secondaryId && <div className="text-xs text-gray-500">Secondary: {nameOf(thisWeekOncall.secondaryId)}</div>}
          </div>
        )}
        <div>
          <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Out today</div>
          {outToday.length === 0 ? <div className="text-sm text-gray-400">—</div> : (
            <div className="text-sm text-gray-800">{outToday.map(e => nameOf(e.memberId)).join(', ')}</div>
          )}
        </div>
        <div>
          <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Coaching (7d)</div>
          <div className="text-lg font-bold text-gray-800">{coachingActivity}</div>
        </div>
        <div>
          <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">IDPs in progress</div>
          <div className="text-lg font-bold text-gray-800">{idpsInProgress}</div>
        </div>
      </div>

      {/* Quarter progression */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4">
        <div className="text-sm font-semibold text-gray-800 mb-3">Quarter progression</div>
        <div className="flex items-end gap-3 h-24">
          {months.map((m, i) => {
            const s = monthlyTeamAvgs[i];
            const h = s == null ? 4 : Math.max(6, (s / barMax) * 80);
            const rag = mcRag(s);
            return (
              <div key={m} className="flex-1 flex flex-col items-center gap-1">
                <div className="w-full rounded-t" style={{ height: `${h}px`, background: s == null ? '#e5e7eb' : rag.color }} title={s == null ? '—' : s.toFixed(2)} />
                <span className="text-[11px] text-gray-500">{mcMonthLabel(m).split(' ')[0]}</span>
                <span className="text-[10px] font-semibold" style={{ color: rag.color }}>{rag.label}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Team snapshot */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden mb-4">
        <div className="px-4 py-2 border-b border-gray-100 text-sm font-semibold text-gray-800">Team snapshot</div>
        {snapshot.length === 0 ? (
          <div className="px-4 py-6 text-center text-gray-400">No team members.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                {['Member','Role','Latest OKR %','Retention','Level','Focus'].map(h =>
                  <th key={h} className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">{h}</th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {snapshot.map(s => {
                const riskSt = MC_RISK_STYLE[s.risk];
                return (
                  <tr key={s.m.id}>
                    <td className="px-3 py-2 font-medium text-gray-900">{s.m.name}</td>
                    <td className="px-3 py-2 text-gray-600 text-xs">{s.m.role || '—'}</td>
                    <td className="px-3 py-2 text-gray-800">{s.latestPct == null ? '—' : `${s.latestPct}%`}</td>
                    <td className="px-3 py-2"><span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${riskSt.bg} ${riskSt.color}`}>{riskSt.label}</span></td>
                    <td className="px-3 py-2 text-gray-700">{s.level}</td>
                    <td className="px-3 py-2 text-gray-500 text-xs truncate max-w-sm">{s.focus || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Manager time allocation */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="text-xs text-gray-500 uppercase tracking-wide">Hiring</div>
          <div className="text-3xl font-bold text-gray-900">{hiringCount}</div>
          <div className="text-xs text-gray-500 mt-1">Candidates in pipeline + active hires</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="text-xs text-gray-500 uppercase tracking-wide">Growth</div>
          <div className="text-3xl font-bold text-gray-900">{growthCount}</div>
          <div className="text-xs text-gray-500 mt-1">Active IDPs + open 1:1 topics</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="text-xs text-gray-500 uppercase tracking-wide">Risk</div>
          <div className="text-3xl font-bold" style={{ color: riskCount > 0 ? '#dc2626' : '#16a34a' }}>{riskCount}</div>
          <div className="text-xs text-gray-500 mt-1">Retention + skill + succession gaps</div>
        </div>
      </div>
    </div>
  );
};

// ---- Module Shell ----
const ManagerConsoleModule = ({ user }) => {
  const [ws, setWs] = useState(null);
  const [view, setView] = useState('executive');
  const [showSettings, setShowSettings] = useState(false);

  const refresh = async () => {
    const data = await ModulesModel.getManagerConsole();
    setWs(data);
  };
  useEffect(() => { refresh(); }, []);

  if (!ws) return <div className="p-6 text-gray-400">Loading workspace…</div>;

  const activeQuarter = ws.settings.activeQuarter || mcCurrentQuarter();

  const onQuarterChange = async (q) => {
    setWs(prev => ({ ...prev, settings: { ...prev.settings, activeQuarter: q } }));
    await ModulesModel.mcUpdateSettings({ activeQuarter: q });
  };
  const onSaveSettings = async (patch) => {
    await ModulesModel.mcUpdateSettings(patch);
    await refresh();
  };
  const onAddMember = async (data) => { const res = await ModulesModel.mcAddMember(data); if (res?.success === false) alert(res.error || 'Failed'); await refresh(); };
  const onUpdateMember = async (id, data) => { await ModulesModel.mcUpdateMember(id, data); await refresh(); };
  const onRemoveMember = async (id) => { await ModulesModel.mcRemoveMember(id); await refresh(); };
  const onSyncMembersFromTeams = async () => { const r = await ModulesModel.mcSyncMembersFromTeams(); await refresh(); return r; };
  const onSaveOkr = async (okrTemplate) => { await ModulesModel.mcSaveOkrTemplate(okrTemplate); await refresh(); };
  const onSaveEvaluation = async (memberId, month, data) => {
    await ModulesModel.mcSaveEvaluation(memberId, month, data);
    setWs(prev => {
      const next = { ...prev, evaluations: { ...prev.evaluations } };
      next.evaluations[memberId] = { ...(next.evaluations[memberId] || {}), [month]: data };
      return next;
    });
  };

  const onAddCandidate = async (data) => { const r = await ModulesModel.mcAddCandidate(data); if (r?.success === false) alert(r.error || 'Failed'); await refresh(); };
  const onUpdateCandidate = async (id, data) => { const r = await ModulesModel.mcUpdateCandidate(id, data); if (r?.success === false) alert(r.error || 'Failed'); await refresh(); };
  const onDeleteCandidate = async (id) => { await ModulesModel.mcDeleteCandidate(id); await refresh(); };
  const onSavePipelineStages = async (stages) => { const r = await ModulesModel.mcSavePipelineStages(stages); await refresh(); return r; };

  const onAddHire = async (data) => { const r = await ModulesModel.mcAddHire(data); if (r?.success === false) alert(r.error || 'Failed'); await refresh(); };
  const onUpdateHire = async (id, data) => { await ModulesModel.mcUpdateHire(id, data); await refresh(); };
  const onDeleteHire = async (id) => { await ModulesModel.mcDeleteHire(id); await refresh(); };
  const onSaveHireProgress = async (hireId, progress) => {
    await ModulesModel.mcSaveHireProgress(hireId, progress);
    setWs(prev => ({ ...prev, hireProgress: { ...prev.hireProgress, [hireId]: progress } }));
  };
  const onConvertHire = async (hireId) => { const r = await ModulesModel.mcConvertHireToMember(hireId); if (r?.success === false) alert(r.error || 'Failed'); await refresh(); };
  const onSaveOnboardingTemplate = async (template) => { await ModulesModel.mcSaveOnboardingTemplate(template); await refresh(); };

  const onAddCoachingLog = async (data) => { const r = await ModulesModel.mcAddCoachingLog(data); if (r?.success === false) alert(r.error || 'Failed'); await refresh(); };
  const onUpdateCoachingLog = async (id, data) => { const r = await ModulesModel.mcUpdateCoachingLog(id, data); if (r?.success === false) alert(r.error || 'Failed'); await refresh(); };
  const onDeleteCoachingLog = async (id) => { await ModulesModel.mcDeleteCoachingLog(id); await refresh(); };

  const onSaveOneOnOne = async (memberId, data) => {
    await ModulesModel.mcSaveOneOnOne(memberId, data);
    setWs(prev => ({ ...prev, oneOnOnes: { ...prev.oneOnOnes, [memberId]: data } }));
  };
  const onSaveIdp = async (memberId, data) => {
    await ModulesModel.mcSaveIdp(memberId, data);
    setWs(prev => ({ ...prev, idps: { ...prev.idps, [memberId]: { ...data, updatedAt: new Date().toISOString() } } }));
  };

  const onUpsertOncall = async (data) => { const r = await ModulesModel.mcUpsertOncall(data); if (r?.success === false) alert(r.error || 'Failed'); await refresh(); };
  const onDeleteOncall = async (id) => { await ModulesModel.mcDeleteOncall(id); await refresh(); };
  const onAutoFillOncall = async (data) => { const r = await ModulesModel.mcAutoFillOncall(data); await refresh(); return r; };
  const onAddPto = async (data) => { const r = await ModulesModel.mcAddPto(data); if (r?.success === false) alert(r.error || 'Failed'); await refresh(); };
  const onDeletePto = async (id) => { await ModulesModel.mcDeletePto(id); await refresh(); };
  const onAddPulse = async (data) => { const r = await ModulesModel.mcAddPulse(data); if (r?.success === false) alert(r.error || 'Failed'); await refresh(); };
  const onUpdatePulse = async (id, data) => { const r = await ModulesModel.mcUpdatePulse(id, data); if (r?.success === false) alert(r.error || 'Failed'); await refresh(); };
  const onDeletePulse = async (id) => { await ModulesModel.mcDeletePulse(id); await refresh(); };

  const onSaveSkills = async (skills) => {
    await ModulesModel.mcSaveSkills(skills);
    setWs(prev => ({ ...prev, skills }));
  };
  const onAddSuccession = async (data) => { const r = await ModulesModel.mcAddSuccession(data); if (r?.success === false) alert(r.error || 'Failed'); await refresh(); };
  const onUpdateSuccession = async (id, data) => { const r = await ModulesModel.mcUpdateSuccession(id, data); if (r?.success === false) alert(r.error || 'Failed'); await refresh(); };
  const onDeleteSuccession = async (id) => { await ModulesModel.mcDeleteSuccession(id); await refresh(); };

  const onSaveComp = async (memberId, data) => {
    const r = await ModulesModel.mcSaveComp(memberId, data);
    if (r?.success === false) { alert(r?.error || 'Failed'); return; }
    setWs(prev => ({ ...prev, comp: { ...prev.comp, [memberId]: { ...data, memberId, lastUpdatedBy: r.lastUpdatedBy, lastUpdatedAt: r.lastUpdatedAt } } }));
  };
  const onSaveRetention = async (memberId, data) => {
    const r = await ModulesModel.mcSaveRetention(memberId, data);
    if (r?.success === false) { alert(r?.error || 'Failed'); return; }
    setWs(prev => ({ ...prev, retention: { ...prev.retention, [memberId]: { ...data, memberId, lastUpdatedBy: r.lastUpdatedBy, lastReviewed: r.lastReviewed } } }));
  };

  return (
    <div className="max-w-[1600px] mx-auto flex">
      <ManagerConsoleSidebar view={view} onView={setView} teamLabel={ws.settings.teamLabel} showOncall={ws.settings.showOncall} onOpenSettings={() => setShowSettings(true)} />
      <main className="flex-1 px-6 py-5 min-w-0">
        <div className="mb-4">
          <div className="text-xs text-gray-400 uppercase tracking-wide">Manager Console</div>
        </div>
        {view === 'dashboard' && <ManagerDashboardView ws={ws} activeQuarter={activeQuarter} onQuarterChange={onQuarterChange} />}
        {view === 'monthly' && <ManagerMonthlyReviewView ws={ws} activeQuarter={activeQuarter} onQuarterChange={onQuarterChange} onSaveEvaluation={onSaveEvaluation} />}
        {view === 'team' && <ManagerTeamView ws={ws} onAdd={onAddMember} onUpdate={onUpdateMember} onRemove={onRemoveMember} onSyncFromTeams={onSyncMembersFromTeams} />}
        {view === 'okr' && <ManagerOkrView ws={ws} activeQuarter={activeQuarter} onEdit={() => setView('edit-okr')} />}
        {view === 'edit-okr' && <ManagerEditOkrView ws={ws} onSave={onSaveOkr} />}
        {view === 'pipeline' && <ManagerCandidatePipelineView ws={ws} onAdd={onAddCandidate} onUpdate={onUpdateCandidate} onDelete={onDeleteCandidate} onSaveStages={onSavePipelineStages} />}
        {view === 'onboarding' && <ManagerOnboardingView ws={ws} onAddHire={onAddHire} onUpdateHire={onUpdateHire} onDeleteHire={onDeleteHire} onSaveProgress={onSaveHireProgress} onConvert={onConvertHire} onSaveTemplate={onSaveOnboardingTemplate} user={user} />}
        {view === 'coaching' && <ManagerCoachingLogsView ws={ws} onAdd={onAddCoachingLog} onUpdate={onUpdateCoachingLog} onDelete={onDeleteCoachingLog} />}
        {view === 'one-on-one' && <ManagerOneOnOneView ws={ws} onSaveOneOnOne={onSaveOneOnOne} />}
        {view === 'idp' && <ManagerIdpView ws={ws} onSaveIdp={onSaveIdp} />}
        {view === 'oncall' && ws.settings.showOncall && <ManagerOncallView ws={ws} onUpsert={onUpsertOncall} onDelete={onDeleteOncall} onAutoFill={onAutoFillOncall} />}
        {view === 'pto' && <ManagerPtoView ws={ws} onAdd={onAddPto} onDelete={onDeletePto} />}
        {view === 'pulse' && <ManagerPulseView ws={ws} onAdd={onAddPulse} onUpdate={onUpdatePulse} onDelete={onDeletePulse} />}
        {view === 'skills' && <ManagerSkillsMatrixView ws={ws} onSave={onSaveSkills} />}
        {view === 'succession' && <ManagerSuccessionView ws={ws} onAdd={onAddSuccession} onUpdate={onUpdateSuccession} onDelete={onDeleteSuccession} />}
        {view === 'comp' && <ManagerCompView ws={ws} onSave={onSaveComp} />}
        {view === 'retention' && <ManagerRetentionView ws={ws} onSave={onSaveRetention} />}
        {view === 'executive' && <ManagerExecutiveView ws={ws} activeQuarter={activeQuarter} onNavigate={setView} />}
      </main>
      {showSettings && <ManagerWorkspaceSettings ws={ws} onSave={onSaveSettings} onClose={() => setShowSettings(false)} />}
    </div>
  );
};

// ============================================================================
// AUTOMATIONBOARD MODULE
// ============================================================================

const TRIGGER_OPTIONS = [
  { module: 'quicktrack',   event: 'task_completed',  label: 'QuickTrack — Task Completed' },
  { module: 'quicktrack',   event: 'task_created',    label: 'QuickTrack — Task Created' },
  { module: 'quality',      event: 'bug_created',     label: 'Quality — Bug Created' },
  { module: 'devops',       event: 'task_completed',  label: 'DevOps — Sprint Task Completed' },
  { module: 'leaveboard',   event: 'leave_approved',  label: 'LeaveBoard — Leave Approved' },
  { module: 'incidentboard',event: 'incident_created',label: 'IncidentBoard — Incident Created' },
  { module: 'requestboard', event: 'request_created', label: 'RequestBoard — New Request Submitted' },
  { module: 'supportboard', event: 'ticket_created',  label: 'SupportBoard — Ticket Created' },
];

const ACTION_OPTIONS = [
  { module: 'quicktrack',   type: 'create_quick_task',  label: 'Create QuickTrack Task' },
  { module: 'timeboard',    type: 'log_hours',           label: 'Log Hours to TimeBoard' },
  { module: 'incidentboard',type: 'create_incident',     label: 'Create Incident' },
  { module: 'system',       type: 'send_notification',   label: 'Send Notification' },
  { module: 'devops',       type: 'update_task_status',  label: 'Update Task Status' },
];

const CONDITION_FIELDS = {
  quicktrack:    ['priority', 'assignee', 'title'],
  quality:       ['severity', 'assignee', 'reporter'],
  devops:        ['priority', 'assignee'],
  leaveboard:    ['leaveType', 'totalDays', 'userName'],
  incidentboard: ['severity', 'team', 'assignee'],
  requestboard:  ['requestType', 'priority', 'department', 'requestedBy'],
  supportboard:  ['priority', 'team', 'ticketNumber'],
};

const CONDITION_OPERATORS = [
  { value: 'equals',       label: 'equals' },
  { value: 'not_equals',   label: 'not equals' },
  { value: 'contains',     label: 'contains' },
  { value: 'greater_than', label: 'greater than' },
  { value: 'less_than',    label: 'less than' },
];

const autoStatusColors = { active: 'bg-green-100 text-green-700', paused: 'bg-yellow-100 text-yellow-700' };
const autoExecColors   = { success: 'bg-green-100 text-green-700', failed: 'bg-red-100 text-red-700' };
const autoModuleColors = {
  quicktrack: 'bg-purple-100 text-purple-700', quality: 'bg-teal-100 text-teal-700',
  devops: 'bg-blue-100 text-blue-700', timeboard: 'bg-orange-100 text-orange-700',
  leaveboard: 'bg-pink-100 text-pink-700', incidentboard: 'bg-red-100 text-red-700',
  system: 'bg-gray-100 text-gray-700', plantrack: 'bg-green-100 text-green-700',
  requestboard: 'bg-emerald-100 text-emerald-700', supportboard: 'bg-cyan-100 text-cyan-700',
};

function RuleBuilderModal({ rule, onSave, onClose }) {
  const isEdit = !!rule?.id;
  const [form, setForm] = React.useState({
    name: rule?.name || '',
    description: rule?.description || '',
    triggerModule: rule?.trigger_module || '',
    triggerEvent: rule?.trigger_event || '',
    hasCondition: !!(rule?.condition),
    conditionField: rule?.condition?.field || '',
    conditionOperator: rule?.condition?.operator || 'equals',
    conditionValue: rule?.condition?.value || '',
    actionModule: rule?.action_module || '',
    actionType: rule?.action_type || '',
    actionConfig: rule?.actionConfig || {},
    status: rule?.status || 'active',
  });
  const [saving, setSaving] = React.useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const selectedTrigger = TRIGGER_OPTIONS.find(t => t.module === form.triggerModule && t.event === form.triggerEvent);
  const selectedAction  = ACTION_OPTIONS.find(a => a.type === form.actionType);
  const condFields = CONDITION_FIELDS[form.triggerModule] || [];

  const handleTriggerChange = (val) => {
    const [mod, ...evtParts] = val.split('|');
    set('triggerModule', mod); set('triggerEvent', evtParts.join('|'));
    set('conditionField', '');
  };
  const handleActionChange = (val) => {
    const opt = ACTION_OPTIONS.find(a => a.type === val);
    if (opt) { set('actionType', val); set('actionModule', opt.module); set('actionConfig', {}); }
  };

  const updateConfig = (k, v) => setForm(f => ({ ...f, actionConfig: { ...f.actionConfig, [k]: v } }));

  const handleSave = async () => {
    if (!form.name.trim() || !form.triggerModule || !form.actionType) return;
    setSaving(true);
    const payload = {
      name: form.name, description: form.description,
      triggerModule: form.triggerModule, triggerEvent: form.triggerEvent,
      condition: form.hasCondition && form.conditionField ? { field: form.conditionField, operator: form.conditionOperator, value: form.conditionValue } : null,
      actionModule: form.actionModule, actionType: form.actionType,
      actionConfig: form.actionConfig, status: form.status,
    };
    await onSave(payload);
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <h2 className="text-lg font-semibold text-gray-900">{isEdit ? 'Edit Automation Rule' : 'New Automation Rule'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
        </div>
        <div className="p-6 space-y-6">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Rule Name *</label>
            <input value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Auto-log hours on task complete" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea value={form.description} onChange={e => set('description', e.target.value)} rows={2} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400" />
          </div>

          {/* Trigger */}
          <div className="bg-indigo-50 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <span className="w-7 h-7 rounded-full bg-indigo-600 text-white text-xs flex items-center justify-center font-bold">1</span>
              <span className="font-semibold text-indigo-800">Trigger — When this happens</span>
            </div>
            <select value={form.triggerModule && form.triggerEvent ? `${form.triggerModule}|${form.triggerEvent}` : ''} onChange={e => handleTriggerChange(e.target.value)} className="w-full border border-indigo-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-400">
              <option value="">Select trigger event...</option>
              {TRIGGER_OPTIONS.map(t => <option key={t.module + t.event} value={`${t.module}|${t.event}`}>{t.label}</option>)}
            </select>
          </div>

          {/* Condition */}
          {form.triggerModule && (
            <div className="bg-amber-50 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-3">
                <span className="w-7 h-7 rounded-full bg-amber-500 text-white text-xs flex items-center justify-center font-bold">2</span>
                <span className="font-semibold text-amber-800">Condition — Optional filter</span>
                <label className="ml-auto flex items-center gap-2 text-sm text-amber-700 cursor-pointer">
                  <input type="checkbox" checked={form.hasCondition} onChange={e => set('hasCondition', e.target.checked)} className="rounded" />
                  Add condition
                </label>
              </div>
              {form.hasCondition && (
                <div className="grid grid-cols-3 gap-2">
                  <select value={form.conditionField} onChange={e => set('conditionField', e.target.value)} className="border border-amber-200 rounded-lg px-2 py-2 text-sm bg-white focus:outline-none">
                    <option value="">Field...</option>
                    {condFields.map(f => <option key={f} value={f}>{f}</option>)}
                  </select>
                  <select value={form.conditionOperator} onChange={e => set('conditionOperator', e.target.value)} className="border border-amber-200 rounded-lg px-2 py-2 text-sm bg-white focus:outline-none">
                    {CONDITION_OPERATORS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                  <input value={form.conditionValue} onChange={e => set('conditionValue', e.target.value)} placeholder="Value..." className="border border-amber-200 rounded-lg px-2 py-2 text-sm focus:outline-none" />
                </div>
              )}
              {!form.hasCondition && <p className="text-sm text-amber-600">No condition — rule runs for every trigger.</p>}
            </div>
          )}

          {/* Action */}
          <div className="bg-green-50 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <span className="w-7 h-7 rounded-full bg-green-600 text-white text-xs flex items-center justify-center font-bold">3</span>
              <span className="font-semibold text-green-800">Action — Then do this</span>
            </div>
            <select value={form.actionType} onChange={e => handleActionChange(e.target.value)} className="w-full border border-green-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-green-400 mb-3">
              <option value="">Select action...</option>
              {ACTION_OPTIONS.map(a => <option key={a.type} value={a.type}>{a.label}</option>)}
            </select>

            {form.actionType === 'create_quick_task' && (
              <div className="space-y-2">
                <div>
                  <label className="text-xs text-gray-500 font-medium">Task Title</label>
                  <input value={form.actionConfig.title || ''} onChange={e => updateConfig('title', e.target.value)}
                    placeholder="e.g. Review request: {{title}} from {{department}}" className="w-full border border-green-200 rounded-lg px-3 py-2 text-sm focus:outline-none" />
                  <p className="text-[10px] text-gray-400 mt-0.5">Use {'{{title}}'}, {'{{department}}'}, {'{{requestedBy}}'}, {'{{priority}}'}, {'{{requestType}}'} as placeholders</p>
                </div>
                <div>
                  <label className="text-xs text-gray-500 font-medium">Description</label>
                  <textarea value={form.actionConfig.description || ''} onChange={e => updateConfig('description', e.target.value)}
                    placeholder="e.g. Request from {{requestedBy}} ({{department}}): {{title}}" rows={2} className="w-full border border-green-200 rounded-lg px-3 py-2 text-sm focus:outline-none" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 font-medium">Assignment</label>
                  <select value={form.actionConfig.auto_assign || ''} onChange={e => { updateConfig('auto_assign', e.target.value); if (e.target.value) updateConfig('assigned_to', ''); }}
                    className="w-full border border-green-200 rounded-lg px-2 py-2 text-sm bg-white focus:outline-none">
                    <option value="">Manual — assign to specific user</option>
                    <option value="dept_manager">Auto — Department/Team Manager (team lead)</option>
                    <option value="team_lead">Auto — Team Lead (by team name/module)</option>
                  </select>
                </div>
                {!form.actionConfig.auto_assign && (
                  <input value={form.actionConfig.assigned_to || ''} onChange={e => updateConfig('assigned_to', e.target.value)}
                    placeholder="Username (leave blank for trigger's assignee)" className="w-full border border-green-200 rounded-lg px-3 py-2 text-sm focus:outline-none" />
                )}
                {form.actionConfig.auto_assign && (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 text-xs text-emerald-700">
                    Task will be auto-assigned to the <strong>{form.actionConfig.auto_assign === 'dept_manager' ? 'team lead of the department' : 'team lead'}</strong> from the trigger data. Falls back to any active manager if no match.
                  </div>
                )}
                <div>
                  <label className="text-xs text-gray-500 font-medium">Priority</label>
                  <select value={form.actionConfig.priority || 'medium'} onChange={e => updateConfig('priority', e.target.value)} className="w-full border border-green-200 rounded-lg px-2 py-2 text-sm bg-white focus:outline-none">
                    {['low','medium','high','urgent'].map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
              </div>
            )}
            {form.actionType === 'log_hours' && (
              <div className="space-y-2">
                <input type="number" min="0.5" step="0.5" value={form.actionConfig.hours || ''} onChange={e => updateConfig('hours', e.target.value)} placeholder="Hours to log" className="w-full border border-green-200 rounded-lg px-3 py-2 text-sm focus:outline-none" />
                <input value={form.actionConfig.user_name || ''} onChange={e => updateConfig('user_name', e.target.value)} placeholder="Username (leave blank to use trigger's assignee)" className="w-full border border-green-200 rounded-lg px-3 py-2 text-sm focus:outline-none" />
              </div>
            )}
            {form.actionType === 'send_notification' && (
              <div className="space-y-2">
                <input value={form.actionConfig.notify_user || ''} onChange={e => updateConfig('notify_user', e.target.value)} placeholder="Notify username" className="w-full border border-green-200 rounded-lg px-3 py-2 text-sm focus:outline-none" />
                <input value={form.actionConfig.title || ''} onChange={e => updateConfig('title', e.target.value)} placeholder="Notification title" className="w-full border border-green-200 rounded-lg px-3 py-2 text-sm focus:outline-none" />
                <textarea value={form.actionConfig.message || ''} onChange={e => updateConfig('message', e.target.value)} placeholder="Notification message" rows={2} className="w-full border border-green-200 rounded-lg px-3 py-2 text-sm focus:outline-none" />
              </div>
            )}
            {form.actionType === 'create_incident' && (
              <div className="space-y-2">
                <input value={form.actionConfig.title || ''} onChange={e => updateConfig('title', e.target.value)} placeholder="Incident title" className="w-full border border-green-200 rounded-lg px-3 py-2 text-sm focus:outline-none" />
                <select value={form.actionConfig.severity || 'Medium'} onChange={e => updateConfig('severity', e.target.value)} className="w-full border border-green-200 rounded-lg px-2 py-2 text-sm bg-white focus:outline-none">
                  {['Low','Medium','High','Critical'].map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            )}
            {form.actionType === 'update_task_status' && (
              <select value={form.actionConfig.status || 'in_progress'} onChange={e => updateConfig('status', e.target.value)} className="w-full border border-green-200 rounded-lg px-2 py-2 text-sm bg-white focus:outline-none">
                {['backlog','ready','in_progress','review','needs_rework','done'].map(s => <option key={s} value={s}>{s === 'needs_rework' ? 'Needs Rework' : s}</option>)}
              </select>
            )}
          </div>

          {/* Status */}
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-gray-700">Status:</span>
            {['active','paused'].map(s => (
              <button key={s} onClick={() => set('status', s)} className={`px-3 py-1 rounded-full text-sm font-medium border ${form.status === s ? (s === 'active' ? 'bg-green-100 border-green-400 text-green-700' : 'bg-yellow-100 border-yellow-400 text-yellow-700') : 'bg-white border-gray-300 text-gray-500'}`}>
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </button>
            ))}
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3 sticky bottom-0 bg-white">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
          <button onClick={handleSave} disabled={saving || !form.name.trim() || !form.triggerModule || !form.actionType} className="px-5 py-2 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 disabled:opacity-50">
            {saving ? 'Saving…' : isEdit ? 'Update Rule' : 'Create Rule'}
          </button>
        </div>
      </div>
    </div>
  );
}

const AutomationBoardModule = ({ user }) => {
  const [rules, setRules] = React.useState([]);
  const [logs, setLogs] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [view, setView] = React.useState('dashboard'); // dashboard | logs
  const [showBuilder, setShowBuilder] = React.useState(false);
  const [editingRule, setEditingRule] = React.useState(null);
  const [deleting, setDeleting] = React.useState(null);

  const canManage = ['admin', 'manager', 'devops_manager'].includes(user?.role || user?.user_role);

  const load = React.useCallback(async () => {
    setLoading(true);
    const [r, l] = await Promise.all([ModulesModel.getAutomationRules(), ModulesModel.getAutomationLogs()]);
    setRules(r || []);
    setLogs(l || []);
    setLoading(false);
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const handleSave = async (data) => {
    if (editingRule?.id) {
      await ModulesModel.updateAutomationRule(editingRule.id, data);
    } else {
      await ModulesModel.createAutomationRule(data);
    }
    setShowBuilder(false);
    setEditingRule(null);
    load();
  };

  const handleToggle = async (rule) => {
    await ModulesModel.updateAutomationRule(rule.id, { status: rule.status === 'active' ? 'paused' : 'active' });
    load();
  };

  const handleDelete = async (id) => {
    setDeleting(id);
    await ModulesModel.deleteAutomationRule(id);
    setDeleting(null);
    load();
  };

  const handleEdit = (rule) => { setEditingRule(rule); setShowBuilder(true); };

  const activeRules  = rules.filter(r => r.status === 'active');
  const pausedRules  = rules.filter(r => r.status === 'paused');
  const recentLogs   = logs.slice(0, 20);
  const failedCount  = logs.filter(l => l.execution_status === 'failed').length;
  const successCount = logs.filter(l => l.execution_status === 'success').length;

  const getTriggerLabel = (r) => TRIGGER_OPTIONS.find(t => t.module === r.trigger_module && t.event === r.trigger_event)?.label || `${r.trigger_module} / ${r.trigger_event}`;
  const getActionLabel  = (r) => ACTION_OPTIONS.find(a => a.type === r.action_type)?.label || r.action_type;

  const RuleCard = ({ rule }) => (
    <div className="bg-white border border-gray-200 rounded-xl p-4 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-semibold text-gray-900 truncate">{rule.name}</span>
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${autoStatusColors[rule.status] || 'bg-gray-100 text-gray-600'}`}>{rule.status}</span>
          </div>
          {rule.description && <p className="text-xs text-gray-500 mb-2 line-clamp-1">{rule.description}</p>}
        </div>
        {canManage && (
          <div className="flex items-center gap-1 shrink-0">
            <button onClick={() => handleToggle(rule)} title={rule.status === 'active' ? 'Pause' : 'Activate'} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 text-sm">
              {rule.status === 'active' ? '⏸' : '▶'}
            </button>
            <button onClick={() => handleEdit(rule)} className="p-1.5 rounded-lg hover:bg-indigo-50 text-indigo-500 text-sm">✏️</button>
            <button onClick={() => handleDelete(rule.id)} disabled={deleting === rule.id} className="p-1.5 rounded-lg hover:bg-red-50 text-red-400 text-sm">
              {deleting === rule.id ? '…' : '🗑'}
            </button>
          </div>
        )}
      </div>

      {/* Flow: Trigger → Condition → Action */}
      <div className="flex items-center gap-2 flex-wrap text-xs">
        <div className="flex items-center gap-1.5 bg-indigo-50 border border-indigo-100 rounded-lg px-2.5 py-1.5">
          <span className="text-indigo-400">⚡</span>
          <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${autoModuleColors[rule.trigger_module] || 'bg-gray-100 text-gray-600'}`}>{rule.trigger_module}</span>
          <span className="text-indigo-700 font-medium">{rule.trigger_event?.replace(/_/g, ' ')}</span>
        </div>
        {rule.condition && (
          <>
            <span className="text-gray-400">→</span>
            <div className="flex items-center gap-1 bg-amber-50 border border-amber-100 rounded-lg px-2.5 py-1.5">
              <span className="text-amber-500">⚙</span>
              <span className="text-amber-700">{rule.condition.field} {rule.condition.operator?.replace(/_/g, ' ')} <strong>{rule.condition.value}</strong></span>
            </div>
          </>
        )}
        <span className="text-gray-400">→</span>
        <div className="flex items-center gap-1.5 bg-green-50 border border-green-100 rounded-lg px-2.5 py-1.5">
          <span className="text-green-400">✓</span>
          <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${autoModuleColors[rule.action_module] || 'bg-gray-100 text-gray-600'}`}>{rule.action_module}</span>
          <span className="text-green-700 font-medium">{rule.action_type?.replace(/_/g, ' ')}</span>
        </div>
      </div>
      <div className="mt-2 text-xs text-gray-400">Created by {rule.created_by || 'System'}</div>
    </div>
  );

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <span className="text-2xl">🤖</span> AutomationBoard
          </h1>
          <p className="text-sm text-gray-500 mt-1">Build workflows that connect Orbit modules automatically</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex bg-gray-100 rounded-lg p-1">
            {[{ id: 'dashboard', label: 'Rules' }, { id: 'logs', label: 'Execution Logs' }].map(v => (
              <button key={v.id} onClick={() => setView(v.id)} className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${view === v.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>{v.label}</button>
            ))}
          </div>
          {canManage && (
            <button onClick={() => { setEditingRule(null); setShowBuilder(true); }} className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700">
              + New Rule
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40 text-gray-400">Loading…</div>
      ) : view === 'dashboard' ? (
        <>
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            {[
              { label: 'Active Rules',    value: activeRules.length,  color: 'text-green-600',  bg: 'bg-green-50' },
              { label: 'Paused Rules',    value: pausedRules.length,  color: 'text-yellow-600', bg: 'bg-yellow-50' },
              { label: 'Total Executions',value: logs.length,          color: 'text-indigo-600', bg: 'bg-indigo-50' },
              { label: 'Failed',          value: failedCount,          color: 'text-red-600',    bg: 'bg-red-50' },
            ].map(s => (
              <div key={s.label} className={`${s.bg} rounded-xl p-4`}>
                <div className={`text-2xl font-bold ${s.color}`}>{s.value}</div>
                <div className="text-sm text-gray-600 mt-1">{s.label}</div>
              </div>
            ))}
          </div>

          {/* Active Rules */}
          <div className="mb-6">
            <h2 className="text-base font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-green-500 inline-block"></span> Active Rules ({activeRules.length})
            </h2>
            {activeRules.length === 0 ? (
              <div className="bg-white border border-dashed border-gray-300 rounded-xl p-8 text-center text-gray-400">
                No active rules yet.{canManage ? ' Click "+ New Rule" to create one.' : ''}
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {activeRules.map(r => <RuleCard key={r.id} rule={r} />)}
              </div>
            )}
          </div>

          {/* Paused Rules */}
          {pausedRules.length > 0 && (
            <div className="mb-6">
              <h2 className="text-base font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-yellow-400 inline-block"></span> Paused Rules ({pausedRules.length})
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {pausedRules.map(r => <RuleCard key={r.id} rule={r} />)}
              </div>
            </div>
          )}

          {/* Recent Executions */}
          <div>
            <h2 className="text-base font-semibold text-gray-800 mb-3">Recent Executions</h2>
            {recentLogs.length === 0 ? (
              <div className="bg-white border border-dashed border-gray-300 rounded-xl p-6 text-center text-gray-400 text-sm">No executions yet. Rules will fire automatically when trigger events occur.</div>
            ) : (
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>{['Rule', 'Trigger', 'Result', 'Status', 'Time'].map(h => <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">{h}</th>)}</tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {recentLogs.map(log => (
                      <tr key={log.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium text-gray-900 max-w-[180px] truncate">{log.rule_name || '—'}</td>
                        <td className="px-4 py-3 text-gray-500">
                          <span className={`px-1.5 py-0.5 rounded text-xs ${autoModuleColors[log.trigger_module] || 'bg-gray-100 text-gray-600'}`}>{log.trigger_module}</span>
                          <span className="ml-1 text-xs">{log.trigger_event?.replace(/_/g,' ')}</span>
                        </td>
                        <td className="px-4 py-3 text-gray-600 max-w-[200px] truncate">{log.execution_result || '—'}</td>
                        <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${autoExecColors[log.execution_status] || 'bg-gray-100 text-gray-600'}`}>{log.execution_status}</span></td>
                        <td className="px-4 py-3 text-gray-400 text-xs whitespace-nowrap">{log.executed_at ? new Date(log.executed_at).toLocaleString() : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      ) : (
        /* Logs View */
        <div>
          <div className="flex items-center gap-4 mb-4 text-sm text-gray-600">
            <span>Total: <strong>{logs.length}</strong></span>
            <span className="text-green-600">Success: <strong>{successCount}</strong></span>
            <span className="text-red-600">Failed: <strong>{failedCount}</strong></span>
          </div>
          {logs.length === 0 ? (
            <div className="bg-white border border-dashed border-gray-300 rounded-xl p-10 text-center text-gray-400">No execution logs yet.</div>
          ) : (
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>{['#', 'Rule', 'Trigger Module', 'Trigger Event', 'Result', 'Status', 'Executed At'].map(h => <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">{h}</th>)}</tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {logs.map(log => (
                    <tr key={log.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-gray-400 text-xs">{log.id}</td>
                      <td className="px-4 py-3 font-medium text-gray-900">{log.rule_name || '—'}</td>
                      <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded text-xs ${autoModuleColors[log.trigger_module] || 'bg-gray-100 text-gray-600'}`}>{log.trigger_module}</span></td>
                      <td className="px-4 py-3 text-gray-600 text-xs">{log.trigger_event?.replace(/_/g,' ')}</td>
                      <td className="px-4 py-3 text-gray-600 max-w-[220px] truncate">{log.execution_result || '—'}</td>
                      <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${autoExecColors[log.execution_status] || 'bg-gray-100 text-gray-600'}`}>{log.execution_status}</span></td>
                      <td className="px-4 py-3 text-gray-400 text-xs whitespace-nowrap">{log.executed_at ? new Date(log.executed_at).toLocaleString() : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Rule Builder Modal */}
      {showBuilder && (
        <RuleBuilderModal
          rule={editingRule}
          onSave={handleSave}
          onClose={() => { setShowBuilder(false); setEditingRule(null); }}
        />
      )}
    </div>
  );
};

const IncidentHeatmapView = ({ analytics }) => {
  if (!analytics) return <div className="py-20 text-center text-gray-400">Loading analytics...</div>;
  const max = (arr) => arr.length ? Math.max(...arr.map(x => x.count || x.total || 0)) : 1;
  const Bar = ({ label, count, maxVal, color = 'bg-red-400' }) => (
    <div className="flex items-center gap-2 mb-1.5">
      <span className="text-xs text-gray-600 w-36 truncate flex-shrink-0">{label}</span>
      <div className="flex-1 h-5 bg-gray-100 rounded overflow-hidden">
        <div className={`h-full ${color} rounded transition-all`} style={{ width: `${maxVal ? (count/maxVal)*100 : 0}%` }}></div>
      </div>
      <span className="text-xs font-semibold text-gray-700 w-6 text-right">{count}</span>
    </div>
  );
  return (
    <div className="space-y-6">
      {/* MTTR Banner */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Total Incidents', value: analytics.total, icon: '📊', color: 'text-gray-700', bg: 'bg-gray-50' },
          { label: 'Avg. Resolution Time', value: analytics.mttrHours ? `${analytics.mttrHours}h` : '—', icon: '⏱️', color: 'text-blue-700', bg: 'bg-blue-50' },
          { label: 'Services Affected', value: analytics.topServices?.length || 0, icon: '⚡', color: 'text-orange-700', bg: 'bg-orange-50' },
          { label: 'Categories', value: analytics.byCategory?.length || 0, icon: '🗂️', color: 'text-purple-700', bg: 'bg-purple-50' },
        ].map(c => (
          <div key={c.label} className={`${c.bg} rounded-xl p-4 border border-gray-100`}>
            <div className="flex items-center gap-2 mb-1"><span>{c.icon}</span><span className="text-xs text-gray-500">{c.label}</span></div>
            <div className={`text-2xl font-bold ${c.color}`}>{c.value}</div>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Top Services */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">🔥 Most Affected Services</h3>
          {analytics.topServices?.length === 0 ? <p className="text-xs text-gray-400">No data yet.</p> :
            analytics.topServices?.map(s => <Bar key={s.name} label={s.name} count={s.count} maxVal={max(analytics.topServices)} color="bg-red-400" />)
          }
        </div>
        {/* By Category */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">🗂️ Incidents by Category</h3>
          {analytics.byCategory?.length === 0 ? <p className="text-xs text-gray-400">No data yet.</p> :
            analytics.byCategory?.map(c => <Bar key={c.name} label={c.name} count={c.count} maxVal={max(analytics.byCategory)} color="bg-purple-400" />)
          }
        </div>
        {/* By Team */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">👥 Incidents by Team</h3>
          {analytics.byTeam?.length === 0 ? <p className="text-xs text-gray-400">No data yet.</p> :
            analytics.byTeam?.map(t => <Bar key={t.name} label={t.name} count={t.count} maxVal={max(analytics.byTeam)} color="bg-blue-400" />)
          }
        </div>
        {/* Monthly Trend */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">📈 Monthly Trend (6 months)</h3>
          {!analytics.trend?.length ? <p className="text-xs text-gray-400">No data yet.</p> : (
            <div className="space-y-1.5">
              {analytics.trend?.map(t => (
                <div key={t.month} className="flex items-center gap-2">
                  <span className="text-xs text-gray-500 w-16 flex-shrink-0">{t.month}</span>
                  <div className="flex-1 flex gap-0.5 h-5">
                    <div className="bg-red-400 rounded-l" style={{ width: `${max(analytics.trend.map(x=>({count:x.total}))) ? (t.total/max(analytics.trend.map(x=>({count:x.total}))))*100 : 0}%`, minWidth: t.total > 0 ? '4px' : '0' }}></div>
                  </div>
                  <span className="text-xs font-semibold text-gray-700 w-6 text-right">{t.total}</span>
                  {t.critical > 0 && <span className="text-[10px] text-red-600 font-semibold">{t.critical}🔴</span>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const IncidentBoardModule = ({
  data, user, filters, viewingIncident, showCreate,
  onFilterChange, onViewIncident, onCreateIncident, onUpdateIncident,
  onAddUpdate, onDeleteIncident, onBack, onShowCreate, onCloseCreate,
  analytics, onLoadAnalytics
}) => {
  const [view, setView] = React.useState('list');
  const incidents = data?.incidents || [];
  const metrics = data?.metrics || {};
  const allUsers = data?.allUsers || [];

  if (viewingIncident) {
    return <IncidentDetailView incident={viewingIncident} user={user} onBack={onBack} onUpdateIncident={onUpdateIncident} onAddUpdate={onAddUpdate} onDeleteIncident={onDeleteIncident} />;
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">🚨 IncidentBoard</h1>
          <p className="text-gray-500 mt-1 text-sm">Enterprise incident tracking — detection, response, and post-incident review</p>
        </div>
        <button onClick={onShowCreate} className="px-4 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 font-semibold whitespace-nowrap shadow-sm">+ Report Incident</button>
      </div>

      {/* Metric cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        {[
          { label: 'Active', value: metrics.open || 0, icon: '🔥', color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200' },
          { label: 'Critical', value: metrics.critical || 0, icon: '🚨', color: 'text-red-800', bg: 'bg-red-100', border: 'border-red-300' },
          { label: 'Investigating', value: metrics.byStatus?.Investigating || 0, icon: '🔍', color: 'text-orange-700', bg: 'bg-orange-50', border: 'border-orange-200' },
          { label: 'Resolved Today', value: metrics.resolvedToday || 0, icon: '✅', color: 'text-green-700', bg: 'bg-green-50', border: 'border-green-200' },
          { label: 'Total', value: metrics.total || 0, icon: '📊', color: 'text-gray-700', bg: 'bg-gray-50', border: 'border-gray-200' },
        ].map(c => (
          <div key={c.label} className={`${c.bg} rounded-xl p-4 border ${c.border}`}>
            <div className="flex items-center gap-1.5 mb-1"><span>{c.icon}</span><span className="text-xs text-gray-500">{c.label}</span></div>
            <div className={`text-2xl font-bold ${c.color}`}>{c.value}</div>
          </div>
        ))}
      </div>

      {/* View tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-lg p-1 mb-4 w-fit">
        {[{id:'list',label:'📋 Incidents'},{id:'analytics',label:'📊 Analytics'}].map(t => (
          <button key={t.id} onClick={() => { setView(t.id); if(t.id==='analytics') onLoadAnalytics(); }}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${view===t.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {view === 'analytics' ? (
        <IncidentHeatmapView analytics={analytics} />
      ) : (<>
        {/* Filters */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4 flex flex-wrap gap-3 items-center">
          <div className="flex items-center gap-2 flex-1 min-w-40">
            <Search size={14} className="text-gray-400 flex-shrink-0" />
            <input value={filters.search || ''} onChange={e => onFilterChange({ search: e.target.value })} placeholder="Search incidents, services..."
              className="flex-1 text-sm border-0 outline-none bg-transparent text-gray-700 placeholder-gray-400" />
          </div>
          <select value={filters.severity || ''} onChange={e => onFilterChange({ severity: e.target.value })} className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 text-gray-700">
            <option value="">All Severities</option>
            {INCIDENT_SEVERITIES.map(s => <option key={s}>{s}</option>)}
          </select>
          <select value={filters.status || ''} onChange={e => onFilterChange({ status: e.target.value })} className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 text-gray-700">
            <option value="">All Statuses</option>
            {INCIDENT_STATUSES.map(s => <option key={s}>{s}</option>)}
          </select>
          <select value={filters.category || ''} onChange={e => onFilterChange({ category: e.target.value })} className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 text-gray-700">
            <option value="">All Categories</option>
            {INCIDENT_CATEGORIES.map(c => <option key={c}>{c}</option>)}
          </select>
          <select value={filters.team || ''} onChange={e => onFilterChange({ team: e.target.value })} className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 text-gray-700">
            <option value="">All Teams</option>
            {INCIDENT_TEAMS.map(t => <option key={t}>{t}</option>)}
          </select>
          {(filters.severity || filters.status || filters.team || filters.search || filters.category) && (
            <button onClick={() => onFilterChange({ severity:'', status:'', team:'', search:'', category:'' })} className="text-xs text-gray-400 hover:text-red-500 underline">Clear</button>
          )}
        </div>

        {/* Incident list */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {incidents.length === 0 ? (
            <div className="py-16 text-center">
              <div className="text-5xl mb-3">🟢</div>
              <p className="text-gray-500 font-medium">No incidents found</p>
              <p className="text-gray-400 text-sm mt-1">All systems operational</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {/* Header */}
              <div className="grid grid-cols-12 gap-2 px-5 py-2 bg-gray-50 text-[10px] font-semibold text-gray-400 uppercase tracking-wide">
                <div className="col-span-1">Sev</div>
                <div className="col-span-4">Incident</div>
                <div className="col-span-2">Service</div>
                <div className="col-span-1">Category</div>
                <div className="col-span-1">Team</div>
                <div className="col-span-1">Status</div>
                <div className="col-span-2">Created</div>
              </div>
              {incidents.map(inc => (
                <div key={inc.id} onClick={() => onViewIncident(inc.id)}
                  className={`grid grid-cols-12 gap-2 px-5 py-3.5 hover:bg-gray-50 cursor-pointer transition-colors items-center border-l-4 ${incSeverityBorder(inc.severity)}`}>
                  <div className="col-span-1">
                    <span className={`inline-block text-[10px] font-bold px-1.5 py-0.5 rounded ${incSeverityColor(inc.severity)}`}>{inc.severity?.[0]}</span>
                  </div>
                  <div className="col-span-4 min-w-0">
                    <div className="flex items-center gap-1.5 mb-0.5">
                      <span className="text-[10px] text-gray-400 font-mono">INC-{String(inc.id).padStart(4,'0')}</span>
                    </div>
                    <p className="text-sm font-semibold text-gray-900 truncate">{inc.title}</p>
                    {inc.incident_commander && <p className="text-[10px] text-gray-400 mt-0.5">🎖️ {inc.incident_commander}</p>}
                  </div>
                  <div className="col-span-2 text-xs text-gray-600 truncate">{inc.service_affected || '—'}</div>
                  <div className="col-span-1">
                    {inc.category && <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${incCategoryColor(inc.category)}`}>{inc.category}</span>}
                  </div>
                  <div className="col-span-1 text-xs text-gray-500">{inc.team || '—'}</div>
                  <div className="col-span-1">
                    <span className={`inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full ${incStatusColor(inc.status)}`}>
                      <span className={`w-1 h-1 rounded-full ${incStatusDot(inc.status)}`}></span>
                      {inc.status}
                    </span>
                  </div>
                  <div className="col-span-2 text-[10px] text-gray-400">
                    {inc.created_at ? new Date(inc.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : ''}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </>)}

      {showCreate && <IncidentCreateModal onSave={onCreateIncident} onClose={onCloseCreate} allUsers={allUsers} user={user} />}
    </div>
  );
};

// ============================================================
// KNOWLEDGEBOARD COMPONENTS
// ============================================================

const KB_CATEGORIES_PRESET = [
  { name: 'Engineering', icon: '⚙️', color: '#0366d6' },
  { name: 'DevOps',      icon: '🔧', color: '#6f42c1' },
  { name: 'DataOps',     icon: '📊', color: '#17a2b8' },
  { name: 'QA',          icon: '✓',  color: '#20c997' },
  { name: 'Operations',  icon: '🏢', color: '#fd7e14' },
  { name: 'HR',          icon: '👥', color: '#e83e8c' },
  { name: 'Vendor',      icon: '📡', color: '#e74c3c' },
  { name: 'General',     icon: '📄', color: '#6c757d' },
];

// Article editor — docs-style with sticky toolbar, status pill, word count
const KnowledgeArticleEditor = ({ article, categories, user, onSave, onCancel }) => {
  const initial = {
    title: article?.title || '',
    categoryId: article?.category_id || '',
    content: article?.content || '',
    status: article?.status || 'draft',
  };
  const [form, setForm] = useState(initial);
  const [tab, setTab] = useState('write');
  const textareaRef = useRef(null);
  const isEdit = !!article;
  const original = useMemo(() => JSON.stringify(initial), [article?.id]);
  const dirty = JSON.stringify(form) !== original;

  const insertAtCursor = (before, after = '', placeholder = '') => {
    const ta = textareaRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const text = ta.value;
    const selected = text.slice(start, end) || placeholder;
    const next = text.slice(0, start) + before + selected + after + text.slice(end);
    setForm(f => ({ ...f, content: next }));
    setTimeout(() => {
      ta.focus();
      ta.selectionStart = start + before.length;
      ta.selectionEnd = start + before.length + selected.length;
    }, 0);
  };

  const insertBlock = (block) => {
    const ta = textareaRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const text = ta.value;
    const before = text.slice(0, start);
    const after = text.slice(start);
    const leading = before && !before.endsWith('\n') ? '\n' : '';
    const trailing = after && !after.startsWith('\n') ? '\n' : '';
    const next = before + leading + block + trailing + after;
    setForm(f => ({ ...f, content: next }));
    setTimeout(() => {
      ta.focus();
      const pos = (before + leading + block).length;
      ta.selectionStart = ta.selectionEnd = pos;
    }, 0);
  };

  const tools = [
    { label: 'B', tip: 'Bold', cls: 'font-bold', action: () => insertAtCursor('**', '**', 'bold text') },
    { label: 'I', tip: 'Italic', cls: 'italic', action: () => insertAtCursor('*', '*', 'italic') },
    { label: '</>', tip: 'Inline code', cls: 'font-mono text-[11px]', action: () => insertAtCursor('`', '`', 'code') },
    { divider: true },
    { label: 'H2', tip: 'Heading 2', cls: 'font-bold text-[11px]', action: () => insertBlock('## Heading') },
    { label: 'H3', tip: 'Heading 3', cls: 'font-bold text-[11px]', action: () => insertBlock('### Heading') },
    { divider: true },
    { label: '•', tip: 'Bullet list', action: () => insertBlock('- Item') },
    { label: '1.', tip: 'Numbered list', cls: 'text-[11px]', action: () => insertBlock('1. Item') },
    { label: '☐', tip: 'Task list', action: () => insertBlock('- [ ] Task') },
    { divider: true },
    { label: '🔗', tip: 'Link', action: () => insertAtCursor('[', '](https://)', 'link text') },
    { label: '🖼', tip: 'Image', action: () => insertBlock('![alt text](https://example.com/image.png)') },
    { divider: true },
    { label: '"', tip: 'Quote', action: () => insertBlock('> Quote') },
    { label: '💡', tip: 'Callout', action: () => insertBlock('> [!tip] Tip\n> Helpful tip here') },
    { divider: true },
    { label: '{ }', tip: 'Code block', cls: 'font-mono text-[11px]', action: () => insertBlock('```js\n// code\n```') },
    { label: '⊞', tip: 'Table', action: () => insertBlock('| Column 1 | Column 2 |\n|---|---|\n| Value | Value |') },
    { label: '—', tip: 'Divider', action: () => insertBlock('---') },
  ];

  const wordCount = kbWordCount(form.content);
  const readTime = kbReadingTime(form.content);

  const handleSaveAs = (status) => {
    if (!form.title.trim()) return alert('Title is required');
    onSave({ title: form.title, categoryId: form.categoryId || null, content: form.content, status, updatedBy: user?.user_name, updatedById: user?.user_id || user?.id });
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
        {/* Top bar */}
        <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
          <div className="flex items-center gap-2 min-w-0">
            <button onClick={onCancel} className="w-9 h-9 hover:bg-white border border-transparent hover:border-gray-200 rounded-lg text-gray-500 flex items-center justify-center transition-colors" aria-label="Back">
              <ChevronRight size={18} className="rotate-180" />
            </button>
            <nav className="flex items-center gap-1.5 text-sm text-gray-500 min-w-0">
              <span className="font-medium">KnowledgeBoard</span>
              <span className="text-gray-300">/</span>
              <span className="text-gray-900 font-medium truncate">{isEdit ? 'Edit' : 'New article'}</span>
            </nav>
          </div>
          <div className="flex items-center gap-2.5">
            <span className={`text-xs px-2.5 py-1 rounded-full font-medium inline-flex items-center gap-1.5 ${form.status === 'published' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${form.status === 'published' ? 'bg-green-500' : 'bg-amber-500'}`} />
              {form.status === 'published' ? 'Published' : 'Draft'}
            </span>
            {dirty ? (
              <span className="text-xs text-amber-600 inline-flex items-center gap-1">● Unsaved changes</span>
            ) : (
              <span className="text-xs text-gray-400 inline-flex items-center gap-1">✓ Up to date</span>
            )}
          </div>
        </div>

        {/* Metadata card */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 sm:p-6 mb-4 space-y-4">
          <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} required
            placeholder="Article title…"
            className="w-full text-2xl sm:text-3xl font-bold tracking-tight text-gray-900 placeholder:text-gray-300 border-0 focus:ring-0 focus:outline-none p-0 bg-transparent" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Category</label>
              <select value={form.categoryId} onChange={e => setForm(f => ({ ...f, categoryId: e.target.value }))}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-gray-50 focus:bg-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition">
                <option value="">— No category —</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.icon} {c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Status</label>
              <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-gray-50 focus:bg-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition">
                <option value="draft">Draft</option>
                <option value="published">Published</option>
              </select>
            </div>
          </div>
        </div>

        {/* Editor card with sticky toolbar */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
          {/* Write/Preview tabs */}
          <div className="flex items-center border-b border-gray-100 px-2 bg-gray-50/40">
            {['write', 'preview'].map(t => (
              <button key={t} type="button" onClick={() => setTab(t)}
                className={`px-4 py-2.5 text-sm font-medium transition-colors relative ${tab === t ? 'text-indigo-700' : 'text-gray-500 hover:text-gray-700'}`}>
                {t === 'write' ? '✏️ Write' : '👁 Preview'}
                {tab === t && <span className="absolute left-2 right-2 -bottom-px h-0.5 bg-indigo-600 rounded-t" />}
              </button>
            ))}
            <div className="ml-auto text-xs text-gray-400 pr-2">Markdown supported · {wordCount} word{wordCount === 1 ? '' : 's'} · {readTime}</div>
          </div>

          {/* Sticky formatting toolbar */}
          {tab === 'write' && (
            <div className="sticky top-0 z-10 bg-white border-b border-gray-100 px-3 py-2 flex items-center gap-0.5 overflow-x-auto">
              {tools.map((t, i) => t.divider ? (
                <span key={i} className="w-px h-5 bg-gray-200 mx-1 flex-shrink-0" />
              ) : (
                <button key={i} type="button" onClick={t.action} title={t.tip}
                  className={`min-w-[32px] h-8 px-2 rounded-md text-gray-600 hover:bg-indigo-50 hover:text-indigo-700 transition-colors flex items-center justify-center text-sm flex-shrink-0 ${t.cls || ''}`}>
                  {t.label}
                </button>
              ))}
            </div>
          )}

          {tab === 'write' ? (
            <textarea ref={textareaRef} value={form.content} onChange={e => setForm(f => ({ ...f, content: e.target.value }))}
              placeholder={`# Welcome\n\nStart writing in Markdown.\n\nUse the toolbar above to insert headings, lists, code blocks, callouts, and more.\n\n> [!tip] Pro tip\n> Tap "Preview" to see rendered output.`}
              className="w-full min-h-[480px] px-6 py-5 text-[0.92rem] font-mono text-gray-800 leading-relaxed resize-y focus:outline-none"
            />
          ) : (
            <div className="min-h-[480px] px-6 sm:px-10 py-8 overflow-auto">
              <div className="kb-article-body max-w-3xl mx-auto text-[0.95rem] leading-7 text-gray-700"
                dangerouslySetInnerHTML={{ __html: renderMarkdown(form.content) || '<p class="text-gray-400 italic">Nothing to preview yet.</p>' }}
              />
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div className="mt-5 flex items-center justify-between gap-3 flex-wrap">
          <div className="text-xs text-gray-400">
            {form.content.length.toLocaleString()} characters · {wordCount.toLocaleString()} words · {readTime}
          </div>
          <div className="flex gap-2 items-center">
            <button type="button" onClick={onCancel} className="px-3.5 py-2 text-sm text-gray-600 hover:text-gray-900 font-medium">Cancel</button>
            <button type="button" onClick={() => handleSaveAs('draft')}
              className="px-4 py-2 text-sm bg-white border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50 hover:border-gray-300 font-medium shadow-sm flex items-center gap-1.5">
              💾 Save draft
            </button>
            <button type="button" onClick={() => handleSaveAs('published')}
              className="px-5 py-2 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 font-medium shadow-sm flex items-center gap-1.5">
              🚀 {isEdit && form.status === 'published' ? 'Update' : 'Publish'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Article view — docs-style with TOC, reading progress, related articles, anchors
const KnowledgeArticleView = ({ article, versions, showVersions, allArticles = [], categories = [], user, isAdmin, onEdit, onDelete, onPublish, onBack, onViewVersions, onRestoreVersion, onCloseVersions, onViewArticle }) => {
  const role = user?.role || user?.user_role;
  const canManage = isAdmin || ['manager', 'devops_manager'].includes(role);
  const canEdit = canManage || (article && article.created_by === (user?.name || user?.user_name));

  const [progress, setProgress] = useState(0);
  const [showFab, setShowFab] = useState(false);
  const [activeTocId, setActiveTocId] = useState(null);
  const [bookmarked, setBookmarked] = useState(false);
  const [toast, setToast] = useState('');
  const [theme, setThemeState] = useState(() => {
    try { return localStorage.getItem('kb_theme') || 'light'; } catch { return 'light'; }
  });
  const dark = theme === 'dark';
  const setTheme = (t) => {
    setThemeState(t);
    try { localStorage.setItem('kb_theme', t); } catch {}
  };
  // cn(light, dark) — pick className based on theme
  const cn = (l, d) => dark ? d : l;
  const contentRef = useRef(null);

  // Reading progress + back-to-top FAB visibility
  useEffect(() => {
    if (!article) return;
    const onScroll = () => {
      const el = contentRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const elTop = rect.top + window.scrollY;
      const elHeight = el.offsetHeight;
      const scrolled = Math.max(0, window.scrollY - elTop);
      const total = Math.max(1, elHeight - window.innerHeight);
      const pct = Math.min(100, Math.max(0, (scrolled / total) * 100));
      setProgress(pct);
      setShowFab(window.scrollY > 600);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, [article?.id]);

  // Bookmark state from localStorage
  useEffect(() => {
    if (!article) return;
    try {
      const bms = JSON.parse(localStorage.getItem('kb_bookmarks') || '[]');
      setBookmarked(bms.includes(article.id));
    } catch {}
  }, [article?.id]);

  // Scroll to a heading id. If the heading lives in a different tab, switch first then scroll.
  const scrollToHeading = (id) => {
    if (hasTabs) {
      const currentIds = new Set(kbExtractTOC(tabs[activeTab]?.content || '').map(h => h.id));
      if (!currentIds.has(id)) {
        const otherIdx = tabs.findIndex(t => kbExtractTOC(t.content).some(h => h.id === id));
        if (otherIdx >= 0) {
          setActiveTab(otherIdx);
          // Wait for the new tab to render, then scroll
          setTimeout(() => {
            const el = document.getElementById(id);
            if (!el) return;
            const y = el.getBoundingClientRect().top + window.scrollY - 80;
            window.scrollTo({ top: y, behavior: 'smooth' });
          }, 80);
          try { history.pushState(null, '', `#${id}`); } catch {}
          return;
        }
      }
    }
    const el = document.getElementById(id);
    if (!el) return;
    const y = el.getBoundingClientRect().top + window.scrollY - 80;
    window.scrollTo({ top: y, behavior: 'smooth' });
    try { history.pushState(null, '', `#${id}`); } catch {}
  };

  // Delegated click handler: intercept in-article anchors → smooth scroll + copy link.
  const handleArticleClick = (e) => {
    const a = e.target.closest('a[href^="#"]');
    if (!a) return;
    const id = a.getAttribute('href').slice(1);
    if (!id) return;
    e.preventDefault();
    scrollToHeading(id);
    try {
      navigator.clipboard.writeText(window.location.href.split('#')[0] + '#' + id);
      showToast('Link copied');
    } catch {}
  };

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 1800);
  };

  const toggleBookmark = () => {
    if (!article) return;
    try {
      const bms = JSON.parse(localStorage.getItem('kb_bookmarks') || '[]');
      const has = bms.includes(article.id);
      const next = has ? bms.filter(id => id !== article.id) : [...bms, article.id];
      localStorage.setItem('kb_bookmarks', JSON.stringify(next));
      setBookmarked(!has);
      showToast(has ? 'Removed from bookmarks' : 'Bookmarked');
    } catch {}
  };

  const copyLink = () => {
    try {
      navigator.clipboard.writeText(window.location.href);
      showToast('Link copied');
    } catch { showToast('Could not copy'); }
  };

  // Split article into H1 tabs (returns 1 entry when there are fewer than 2 H1s)
  const tabs = useMemo(() => kbSplitByH1(article?.content), [article?.content]);
  const hasTabs = tabs.length > 1;
  const [activeTab, setActiveTab] = useState(0);
  // When the article changes, reset to tab 0 (or resolve from hash)
  useEffect(() => {
    if (!hasTabs) { setActiveTab(0); return; }
    const hash = (typeof window !== 'undefined' ? window.location.hash : '').replace(/^#/, '');
    if (!hash) { setActiveTab(0); return; }
    // 1) hash matches a tab slug → select that tab
    const tabBySlug = tabs.findIndex(t => t.slug === hash);
    if (tabBySlug >= 0) { setActiveTab(tabBySlug); return; }
    // 2) hash matches a heading id inside one of the tabs → select that tab
    const inTab = tabs.findIndex(t => kbExtractTOC(t.content).some(h => h.id === hash));
    if (inTab >= 0) { setActiveTab(inTab); return; }
    setActiveTab(0);
  }, [article?.id, hasTabs, tabs.length]);

  const activeContent = hasTabs ? (tabs[activeTab]?.content || '') : (article?.content || '');

  // Active TOC highlighting — observe rendered headings in the currently visible content
  useEffect(() => {
    if (!activeContent) return;
    const handle = requestAnimationFrame(() => {
      const ids = kbExtractTOC(activeContent).map(t => t.id);
      const elements = ids.map(id => document.getElementById(id)).filter(Boolean);
      if (!elements.length) return;
      const visible = new Map();
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach(e => {
            if (e.isIntersecting) visible.set(e.target.id, e.boundingClientRect.top);
            else visible.delete(e.target.id);
          });
          if (visible.size > 0) {
            const top = [...visible.entries()].sort((a, b) => a[1] - b[1])[0][0];
            setActiveTocId(top);
          }
        },
        { rootMargin: '-88px 0px -65% 0px', threshold: 0 }
      );
      elements.forEach(el => observer.observe(el));
      cleanup = () => observer.disconnect();
    });
    let cleanup = null;
    return () => {
      cancelAnimationFrame(handle);
      if (cleanup) cleanup();
    };
  }, [activeContent]);

  const toc = useMemo(() => kbExtractTOC(activeContent), [activeContent]);
  const readTime = useMemo(() => kbReadingTime(article?.content), [article?.content]); // whole-article read time stays in hero
  const category = useMemo(() => categories.find(c => c.id === article?.category_id), [categories, article?.category_id]);
  const related = useMemo(() => {
    if (!allArticles?.length || !article) return [];
    return allArticles
      .filter(a => a.id !== article.id && a.status === 'published' && a.category_id === article.category_id)
      .slice(0, 4);
  }, [allArticles, article?.id, article?.category_id]);

  // Activate a tab + scroll to top of body
  const selectTab = (idx) => {
    setActiveTab(idx);
    setActiveTocId(null);
    if (tabs[idx]?.slug) {
      try { history.pushState(null, '', `#${tabs[idx].slug}`); } catch {}
    }
    // Scroll the body card into view so the tab change feels intentional
    setTimeout(() => {
      const el = contentRef.current;
      if (!el) return;
      const y = el.getBoundingClientRect().top + window.scrollY - 80;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }, 0);
  };

  if (!article) return null;

  const author = article.created_by || 'Unknown';
  const initials = author.split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]?.toUpperCase() || '').join('') || '?';
  const accent = category?.color || '#6366f1';
  const formattedDate = article.updated_at ? new Date(article.updated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

  return (
    <div className={`min-h-screen ${cn('bg-gray-50', 'bg-zinc-950 kb-theme-dark')}`}>
      {/* Reading progress bar */}
      <div className={`fixed top-0 left-0 right-0 z-40 h-[3px] pointer-events-none ${cn('bg-gray-200/60', 'bg-zinc-800/60')}`}>
        <div className="h-full bg-gradient-to-r from-indigo-500 via-violet-500 to-purple-500 transition-[width] duration-150 ease-out" style={{ width: `${progress}%` }} />
      </div>

      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-4 py-2 text-sm rounded-lg shadow-xl ${cn('bg-gray-900 text-white', 'bg-zinc-100 text-zinc-900')}`}>{toast}</div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* Breadcrumbs + top actions */}
        <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
          <nav className={`flex items-center gap-1.5 text-sm min-w-0 ${cn('text-gray-500', 'text-zinc-400')}`}>
            <button onClick={onBack} className={`font-medium transition-colors ${cn('hover:text-gray-900', 'hover:text-zinc-100')}`}>KnowledgeBoard</button>
            {category && (<><span className={cn('text-gray-300', 'text-zinc-600')}>/</span><span className={cn('text-gray-600', 'text-zinc-400')}>{category.name}</span></>)}
            <span className={cn('text-gray-300', 'text-zinc-600')}>/</span>
            <span className={`font-medium truncate max-w-[200px] sm:max-w-md ${cn('text-gray-900', 'text-zinc-100')}`}>{article.title}</span>
          </nav>
          <div className="flex items-center gap-2">
            <button onClick={() => onViewVersions(article.id)} className={`px-3 py-1.5 text-sm rounded-lg flex items-center gap-1.5 transition-colors shadow-sm ${cn('bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-gray-300', 'bg-zinc-900 border border-zinc-800 text-zinc-300 hover:bg-zinc-800 hover:border-zinc-700')}`}>
              <span>🕐</span> History
            </button>
            {canEdit && (
              <>
                {article.status === 'draft' && (
                  <button onClick={() => onPublish(article)} className="px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium flex items-center gap-1.5 shadow-sm">🚀 Publish</button>
                )}
                <button onClick={() => onEdit(article)} className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium flex items-center gap-1.5 shadow-sm">✏️ Edit</button>
                {canManage && <button onClick={() => onDelete(article.id)} className={`px-3 py-1.5 text-sm rounded-lg font-medium shadow-sm ${cn('bg-white border border-red-200 text-red-600 hover:bg-red-50', 'bg-zinc-900 border border-red-900/40 text-red-400 hover:bg-red-950/50')}`}>Delete</button>}
              </>
            )}
          </div>
        </div>

        {/* Main grid: article body + TOC */}
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_260px] gap-6 lg:gap-10 items-start">
          <article ref={contentRef} onClick={handleArticleClick} className="min-w-0">
            {/* Hero header */}
            <div className={`relative rounded-2xl overflow-hidden mb-6 ${cn('bg-white border border-gray-200 shadow-[0_1px_3px_rgba(0,0,0,0.04),0_8px_24px_-12px_rgba(139,92,246,0.14)]', 'bg-zinc-900 border border-zinc-800 shadow-[0_1px_3px_rgba(0,0,0,0.4),0_8px_30px_-12px_rgba(139,92,246,0.25)]')}`}>
              {/* Soft pastel gradient backdrop */}
              <div className="absolute inset-0 pointer-events-none" style={{ background: dark
                ? `radial-gradient(ellipse 90% 70% at 50% 0%, rgba(139,92,246,0.14), transparent 60%), radial-gradient(ellipse 70% 60% at 100% 30%, rgba(167,139,250,0.10), transparent 60%)`
                : `linear-gradient(180deg, #faf5ff 0%, #fdfaff 60%, #ffffff 100%), radial-gradient(ellipse 70% 60% at 100% 30%, rgba(167,139,250,0.20), transparent 60%)` }} />
              {/* Top accent bar */}
              <div className="h-1.5 relative" style={{ background: `linear-gradient(90deg, ${accent}, #8b5cf6 60%, ${accent}80)` }} />

              <div className="relative px-6 sm:px-12 py-10 sm:py-12 flex items-start gap-6">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-6 flex-wrap">
                    {category && (
                      <span className={`text-xs px-2.5 py-1 rounded-full font-semibold inline-flex items-center gap-1.5 ${cn('bg-violet-100 text-violet-700', 'bg-violet-500/15 text-violet-300')}`}>
                        <span>{category.icon || '📄'}</span>{category.name}
                      </span>
                    )}
                    {article.status === 'draft' && (
                      <span className={`text-xs px-2.5 py-1 rounded-full font-medium inline-flex items-center gap-1.5 ${cn('bg-amber-100 text-amber-700', 'bg-amber-500/15 text-amber-300')}`}>
                        <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />Draft
                      </span>
                    )}
                    <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${cn('text-gray-500 bg-white/80 border-gray-200', 'text-zinc-400 bg-zinc-800/70 border-zinc-700')}`}>v{article.version_number}</span>
                  </div>

                  <h1 className={`text-3xl sm:text-[2.75rem] font-bold mb-6 tracking-tight leading-[1.05] ${cn('text-gray-900', 'text-zinc-50')}`}>{article.title}</h1>

                  <div className="flex items-center gap-x-5 gap-y-3 flex-wrap text-sm">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold text-white shadow-sm flex-shrink-0 ring-2 ${cn('ring-white', 'ring-zinc-900')}`} style={{ background: accent }}>{initials}</div>
                      <div className="leading-tight">
                        <div className={`text-sm font-medium ${cn('text-gray-900', 'text-zinc-100')}`}>{author}</div>
                        <div className={`text-xs ${cn('text-gray-500', 'text-zinc-400')}`}>Updated {formattedDate}</div>
                      </div>
                    </div>
                    <span className={`flex items-center gap-1.5 text-xs ${cn('text-gray-600', 'text-zinc-300')}`}>
                      <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>
                      {readTime}
                    </span>
                    {article.views > 0 && (
                      <span className={`flex items-center gap-1.5 text-xs ${cn('text-gray-600', 'text-zinc-300')}`}>
                        <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                        {article.views.toLocaleString()} views
                      </span>
                    )}
                  </div>
                </div>

                {/* Decorative book + sparkles illustration (hidden on mobile) */}
                <div className="hidden md:block flex-shrink-0 self-center" aria-hidden="true">
                  <svg viewBox="0 0 220 200" className="w-44 h-40">
                    {/* Sparkles */}
                    <g fill={dark ? '#c4b5fd' : '#a78bfa'}>
                      <path d="M30 30 l1.5 4.5 l4.5 1.5 l-4.5 1.5 l-1.5 4.5 l-1.5 -4.5 l-4.5 -1.5 l4.5 -1.5 z" opacity="0.85"/>
                      <path d="M180 20 l2 6 l6 2 l-6 2 l-2 6 l-2 -6 l-6 -2 l6 -2 z" opacity="0.65"/>
                      <path d="M195 70 l1.2 3.5 l3.5 1.2 l-3.5 1.2 l-1.2 3.5 l-1.2 -3.5 l-3.5 -1.2 l3.5 -1.2 z" opacity="0.55"/>
                    </g>
                    {/* Cloud shadow */}
                    <ellipse cx="115" cy="178" rx="62" ry="6" fill={dark ? 'rgba(139,92,246,0.18)' : 'rgba(139,92,246,0.14)'}/>
                    {/* Book — isometric stack */}
                    <g transform="translate(40, 50)">
                      {/* Back face (darkest) */}
                      <path d="M55 18 L130 0 L130 88 L55 110 Z" fill="#5b21b6"/>
                      {/* Top face */}
                      <path d="M0 30 L75 13 L130 0 L55 18 Z" fill="#a78bfa"/>
                      {/* Front face (mid) */}
                      <path d="M0 30 L55 18 L55 110 L0 122 Z" fill="#8b5cf6"/>
                      {/* Spine highlight */}
                      <path d="M0 30 L55 18 L55 24 L0 36 Z" fill="#c4b5fd" opacity="0.55"/>
                      {/* Gear emblem on front face */}
                      <g transform="translate(15, 55)" fill={dark ? '#ddd6fe' : '#ffffff'} opacity="0.92">
                        <circle cx="14" cy="14" r="9" />
                        <circle cx="14" cy="14" r="4" fill="#7c3aed"/>
                        <g>
                          <rect x="12.5" y="2"  width="3" height="4" rx="1"/>
                          <rect x="12.5" y="22" width="3" height="4" rx="1"/>
                          <rect x="2"  y="12.5" width="4" height="3" rx="1"/>
                          <rect x="22" y="12.5" width="4" height="3" rx="1"/>
                        </g>
                      </g>
                    </g>
                  </svg>
                </div>
              </div>
            </div>

            {/* Tab strip — only when 2+ H1 sections */}
            {hasTabs && (
              <div className={`rounded-xl mb-4 shadow-sm overflow-hidden ${cn('bg-white border border-gray-200', 'bg-zinc-900 border border-zinc-800')}`}>
                <div className="flex items-center gap-1 p-1.5 overflow-x-auto">
                  {tabs.map((t, i) => {
                    const isActive = activeTab === i;
                    return (
                      <button key={i} onClick={() => selectTab(i)}
                        className={`px-3.5 py-1.5 text-sm font-medium rounded-lg whitespace-nowrap transition-all flex-shrink-0 ${isActive
                          ? cn('bg-indigo-600 text-white shadow-sm', 'bg-indigo-500 text-white shadow-sm')
                          : cn('text-gray-600 hover:bg-gray-100 hover:text-gray-900', 'text-zinc-400 hover:bg-zinc-800/70 hover:text-zinc-100')}`}>
                        <span className={`mr-1.5 text-xs font-mono ${isActive ? 'opacity-70' : 'opacity-50'}`}>{String(i + 1).padStart(2, '0')}</span>
                        {t.title}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Body */}
            <div className={`rounded-2xl overflow-hidden shadow-sm ${cn('bg-white border border-gray-200', 'bg-zinc-900 border border-zinc-800')}`}>
              <div className="px-6 sm:px-12 py-10">
                {activeContent ? (
                  <div className={`kb-article-body max-w-[820px] mx-auto text-[0.95rem] leading-[1.75] ${cn('text-gray-700', 'text-zinc-300')}`} dangerouslySetInnerHTML={{ __html: renderMarkdown(activeContent) }} />
                ) : (
                  <p className={`italic max-w-[820px] mx-auto ${cn('text-gray-400', 'text-zinc-500')}`}>This article has no content yet.</p>
                )}
              </div>

              {/* Helpful-feedback footer */}
              <div className={`border-t px-6 sm:px-12 py-6 ${cn('border-gray-100 bg-gradient-to-b from-gray-50/40 to-gray-50/80', 'border-zinc-800 bg-gradient-to-b from-zinc-800/30 to-zinc-800/50')}`}>
                <div className="max-w-[820px] mx-auto flex items-center justify-between gap-4 flex-wrap">
                  <span className={`text-sm ${cn('text-gray-600', 'text-zinc-400')}`}>Was this article helpful?</span>
                  <div className="flex gap-2">
                    <button onClick={() => showToast('Thanks for your feedback!')} className={`px-3.5 py-1.5 text-sm rounded-lg transition-all ${cn('bg-white border border-gray-200 hover:border-green-300 hover:bg-green-50/50 text-gray-600 hover:text-green-700', 'bg-zinc-900 border border-zinc-800 hover:border-green-700/50 hover:bg-green-900/20 text-zinc-300 hover:text-green-400')}`}>👍 Yes</button>
                    <button onClick={() => showToast('Thanks — we will improve it')} className={`px-3.5 py-1.5 text-sm rounded-lg transition-all ${cn('bg-white border border-gray-200 hover:border-red-300 hover:bg-red-50/50 text-gray-600 hover:text-red-700', 'bg-zinc-900 border border-zinc-800 hover:border-red-700/50 hover:bg-red-900/20 text-zinc-300 hover:text-red-400')}`}>👎 No</button>
                  </div>
                </div>
              </div>
            </div>

            {/* Related articles */}
            {related.length > 0 && (
              <div className="mt-8">
                <h3 className={`text-xs font-semibold uppercase tracking-wider mb-3 px-1 ${cn('text-gray-500', 'text-zinc-400')}`}>Related articles</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {related.map(r => {
                    const rCat = categories.find(c => c.id === r.category_id);
                    const rAccent = rCat?.color || '#6366f1';
                    return (
                      <button key={r.id} onClick={() => onViewArticle?.(r.id)} className={`text-left rounded-xl p-4 group transition-all hover:-translate-y-0.5 hover:shadow-md ${cn('bg-white border border-gray-200 hover:border-indigo-300', 'bg-zinc-900 border border-zinc-800 hover:border-indigo-500/40')}`}>
                        <div className="flex items-center gap-1.5 mb-2">
                          {rCat && (
                            <span className="text-[10px] px-2 py-0.5 rounded-full font-medium" style={{ background: rAccent + (dark ? '22' : '15'), color: dark ? '#c7d2fe' : rAccent }}>{rCat.icon} {rCat.name}</span>
                          )}
                        </div>
                        <div className={`text-sm font-semibold line-clamp-2 leading-snug transition-colors ${cn('text-gray-900 group-hover:text-indigo-700', 'text-zinc-100 group-hover:text-indigo-300')}`}>{r.title}</div>
                        <div className={`text-xs mt-2 ${cn('text-gray-400', 'text-zinc-500')}`}>{r.created_by}</div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </article>

          {/* TOC + Actions sidebar (desktop) */}
          <aside className="hidden lg:block">
            <div className="sticky top-6 space-y-3">
              {toc.length > 0 && (
                <div className={`rounded-xl shadow-sm overflow-hidden ${cn('bg-white border border-gray-200', 'bg-zinc-900 border border-zinc-800')}`}>
                  <div className={`px-4 py-3 border-b ${cn('border-gray-100 bg-gradient-to-b from-gray-50/60 to-white', 'border-zinc-800 bg-gradient-to-b from-zinc-800/40 to-zinc-900')}`}>
                    <h3 className={`text-[11px] font-semibold uppercase tracking-[0.08em] flex items-center gap-1.5 ${cn('text-gray-500', 'text-zinc-400')}`}>
                      <svg viewBox="0 0 16 16" className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 4h10M3 8h10M3 12h7"/></svg>
                      On this page
                    </h3>
                  </div>
                  <nav className="px-2 py-2 max-h-[60vh] overflow-y-auto">
                    {toc.map((item, idx) => {
                      const isActive = activeTocId === item.id;
                      const indent = item.level === 3 ? 'pl-7' : 'pl-3';
                      const weight = item.level === 1 ? 'font-semibold' : item.level === 2 ? 'font-medium' : '';
                      const size = item.level === 3 ? 'text-[13px]' : 'text-sm';
                      const activeCls = isActive
                        ? cn('text-indigo-700 bg-indigo-50', 'text-indigo-300 bg-indigo-500/10')
                        : cn('text-gray-600 hover:bg-gray-50 hover:text-gray-900', 'text-zinc-400 hover:bg-zinc-800/60 hover:text-zinc-100');
                      const markerCls = isActive
                        ? cn('text-indigo-500', 'text-indigo-400')
                        : cn('text-gray-300', 'text-zinc-600');
                      // Detect a leading emoji in the heading — use it as the marker so the TOC matches the body icon.
                      const emojiMatch = item.text.match(/^([\p{Emoji_Presentation}\p{Extended_Pictographic}])\s*/u);
                      const displayText = emojiMatch ? item.text.slice(emojiMatch[0].length) : item.text;
                      const Marker = () => emojiMatch ? (
                        <span className="flex-shrink-0 text-sm">{emojiMatch[1]}</span>
                      ) : item.level === 3 ? (
                        <span className={`flex-shrink-0 w-1.5 h-1.5 rounded-full ${isActive ? cn('bg-indigo-500', 'bg-indigo-400') : cn('bg-gray-300', 'bg-zinc-600')}`} />
                      ) : (
                        <svg viewBox="0 0 24 24" className={`w-3.5 h-3.5 flex-shrink-0 ${markerCls}`} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                          <polyline points="14 2 14 8 20 8"/>
                        </svg>
                      );
                      return (
                        <a key={idx} href={`#${item.id}`} onClick={(e) => { e.preventDefault(); scrollToHeading(item.id); }}
                          className={`relative flex items-center gap-2 py-1.5 pr-3 rounded-md leading-snug transition-all duration-200 ${indent} ${weight} ${size} ${activeCls}`}>
                          {isActive && <span className={`absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-4 rounded-r ${cn('bg-indigo-600', 'bg-indigo-400')}`} />}
                          <Marker />
                          <span className="flex-1 truncate">{displayText}</span>
                        </a>
                      );
                    })}
                  </nav>
                </div>
              )}

              <div className={`rounded-xl shadow-sm p-1.5 ${cn('bg-white border border-gray-200', 'bg-zinc-900 border border-zinc-800')}`}>
                <button onClick={copyLink} className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm rounded-lg transition-colors ${cn('text-gray-600 hover:bg-gray-50 hover:text-gray-900', 'text-zinc-300 hover:bg-zinc-800/60 hover:text-zinc-100')}`}>
                  <svg viewBox="0 0 24 24" className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>
                  <span>Copy link</span>
                </button>
                <button onClick={toggleBookmark} className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm rounded-lg transition-colors ${bookmarked
                  ? cn('bg-indigo-50 text-indigo-700', 'bg-indigo-500/10 text-indigo-300')
                  : cn('text-gray-600 hover:bg-gray-50 hover:text-gray-900', 'text-zinc-300 hover:bg-zinc-800/60 hover:text-zinc-100')}`}>
                  <svg viewBox="0 0 24 24" className="w-4 h-4 flex-shrink-0" fill={bookmarked ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                  <span>{bookmarked ? 'Bookmarked' : 'Bookmark'}</span>
                </button>
                <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm rounded-lg transition-colors ${cn('text-gray-600 hover:bg-gray-50 hover:text-gray-900', 'text-zinc-300 hover:bg-zinc-800/60 hover:text-zinc-100')}`}>
                  <svg viewBox="0 0 24 24" className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="19" x2="12" y2="5"/><polyline points="5 12 12 5 19 12"/></svg>
                  <span>Back to top</span>
                </button>
                <button onClick={() => onViewVersions(article.id)} className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm rounded-lg transition-colors ${cn('text-gray-600 hover:bg-gray-50 hover:text-gray-900', 'text-zinc-300 hover:bg-zinc-800/60 hover:text-zinc-100')}`}>
                  <svg viewBox="0 0 24 24" className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                  <span>Version history</span>
                </button>
                <div className={`my-1 h-px ${cn('bg-gray-100', 'bg-zinc-800')}`} />
                <button onClick={() => setTheme(dark ? 'light' : 'dark')} className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm rounded-lg transition-colors ${cn('text-gray-600 hover:bg-gray-50 hover:text-gray-900', 'text-zinc-300 hover:bg-zinc-800/60 hover:text-zinc-100')}`}>
                  {dark ? (
                    <svg viewBox="0 0 24 24" className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>
                  ) : (
                    <svg viewBox="0 0 24 24" className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>
                  )}
                  <span>{dark ? 'Light reader' : 'Dark reader'}</span>
                </button>
              </div>
            </div>
          </aside>
        </div>
      </div>

      {/* Floating back-to-top FAB */}
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className={`fixed bottom-6 right-6 z-40 w-11 h-11 rounded-full shadow-lg hover:shadow-xl flex items-center justify-center transition-all duration-200 hover:-translate-y-0.5 ${cn(
          'bg-white border border-gray-200 text-gray-600 hover:text-indigo-600 hover:border-indigo-300',
          'bg-zinc-900 border border-zinc-700 text-zinc-300 hover:text-indigo-300 hover:border-indigo-500/50'
        )} ${showFab ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2 pointer-events-none'}`}
        aria-label="Back to top">
        <span className="text-lg leading-none">↑</span>
      </button>

      {/* Version history slide-out */}
      {showVersions && (
        <div className="fixed inset-0 z-40 flex justify-end">
          <div className="absolute inset-0 bg-black/30 backdrop-blur-[2px]" onClick={onCloseVersions} />
          <div className={`relative w-full max-w-md shadow-2xl h-full overflow-y-auto ${cn('bg-white', 'bg-zinc-900')}`}>
            <div className={`sticky top-0 backdrop-blur border-b px-5 py-4 flex items-center justify-between z-10 ${cn('bg-white/95 border-gray-200', 'bg-zinc-900/95 border-zinc-800')}`}>
              <div>
                <h3 className={`font-semibold ${cn('text-gray-900', 'text-zinc-100')}`}>Version History</h3>
                <p className={`text-xs mt-0.5 ${cn('text-gray-500', 'text-zinc-400')}`}>{(versions?.length || 0) + 1} version{(versions?.length || 0) === 0 ? '' : 's'}</p>
              </div>
              <button onClick={onCloseVersions} className={`w-8 h-8 rounded-lg flex items-center justify-center ${cn('text-gray-400 hover:text-gray-600 hover:bg-gray-100', 'text-zinc-500 hover:text-zinc-200 hover:bg-zinc-800')}`}><X size={18} /></button>
            </div>
            <div className="px-5 py-5">
              <div className="relative pl-6">
                <div className={`absolute left-2 top-2 bottom-2 w-px ${cn('bg-gradient-to-b from-indigo-300 via-gray-200 to-transparent', 'bg-gradient-to-b from-indigo-400/60 via-zinc-700 to-transparent')}`} />

                {/* Current */}
                <div className="relative mb-5">
                  <div className={`absolute -left-[18px] top-1.5 w-3.5 h-3.5 rounded-full ring-4 ${cn('bg-indigo-600 ring-indigo-100', 'bg-indigo-400 ring-indigo-500/20')}`} />
                  <div className={`rounded-lg p-3.5 ${cn('bg-indigo-50/70 border border-indigo-100', 'bg-indigo-500/10 border border-indigo-500/30')}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className={`text-xs font-bold ${cn('text-indigo-700', 'text-indigo-300')}`}>v{article.version_number} · Current</span>
                      <span className={`text-[10px] uppercase tracking-wide font-semibold ${cn('text-indigo-500', 'text-indigo-400')}`}>Live</span>
                    </div>
                    <div className={`text-xs font-medium ${cn('text-gray-700', 'text-zinc-300')}`}>{article.updated_by || article.created_by}</div>
                    <div className={`text-xs ${cn('text-gray-500', 'text-zinc-400')}`}>{article.updated_at ? new Date(article.updated_at).toLocaleString() : '—'}</div>
                  </div>
                </div>

                {(versions || []).length === 0 ? (
                  <div className={`relative pl-1 text-sm italic ${cn('text-gray-400', 'text-zinc-500')}`}>No previous versions yet</div>
                ) : (versions || []).map(v => (
                  <div key={v.id} className="relative mb-4 last:mb-0">
                    <div className={`absolute -left-[15px] top-2.5 w-2.5 h-2.5 rounded-full ring-4 ${cn('bg-gray-300 ring-white', 'bg-zinc-600 ring-zinc-900')}`} />
                    <div className={`rounded-lg p-3.5 transition-all ${cn('bg-white border border-gray-200 hover:border-indigo-200 hover:shadow-sm', 'bg-zinc-900 border border-zinc-800 hover:border-indigo-500/40')}`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className={`text-xs font-semibold ${cn('text-gray-700', 'text-zinc-200')}`}>v{v.version_number}</span>
                        {canEdit && (
                          <button onClick={() => onRestoreVersion(article.id, v.id)} className={`text-xs font-medium ${cn('text-indigo-600 hover:text-indigo-800', 'text-indigo-400 hover:text-indigo-300')}`}>Restore</button>
                        )}
                      </div>
                      <div className={`text-xs font-medium ${cn('text-gray-600', 'text-zinc-300')}`}>{v.edited_by}</div>
                      <div className={`text-xs ${cn('text-gray-400', 'text-zinc-500')}`}>{v.edited_at ? new Date(v.edited_at).toLocaleString() : '—'}</div>
                      {v.title && v.title !== article.title && <div className={`text-xs mt-1 italic truncate ${cn('text-gray-500', 'text-zinc-400')}`}>"{v.title}"</div>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Category management modal
const KnowledgeCategoryModal = ({ onSave, onClose }) => {
  const [form, setForm] = React.useState({ name: '', description: '', icon: '📄', color: '#6366f1' });
  const handleSubmit = (e) => { e.preventDefault(); if (!form.name.trim()) return; onSave(form); };
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">New Category</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-5 gap-2">
            {KB_CATEGORIES_PRESET.map(p => (
              <button key={p.name} type="button" onClick={() => setForm(f => ({ ...f, name: p.name, icon: p.icon, color: p.color }))}
                className="flex flex-col items-center p-2 rounded-lg border border-gray-200 hover:border-indigo-400 text-xs gap-1">
                <span className="text-lg">{p.icon}</span><span className="text-gray-600 truncate w-full text-center">{p.name}</span>
              </button>
            ))}
          </div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
            <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500" /></div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Icon</label>
              <input value={form.icon} onChange={e => setForm(f => ({ ...f, icon: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Color</label>
              <input type="color" value={form.color} onChange={e => setForm(f => ({ ...f, color: e.target.value }))}
                className="w-full h-10 border border-gray-300 rounded-lg px-2 cursor-pointer" /></div>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
            <button type="submit" className="px-5 py-2 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 font-medium">Create Category</button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Main KnowledgeBoard module
// Stripped excerpt + initials helper (used by list + featured)
const kbExcerpt = (raw, len = 180) => {
  const s = (raw || '')
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/!\[[^\]]*\]\([^)]*\)/g, ' ')
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
    .replace(/[#>*_`~]+/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  return s.length > len ? s.slice(0, len) + '…' : s;
};
const kbInitials = (name) => (name || '?').split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]?.toUpperCase() || '').join('') || '?';

const KnowledgeBoardModule = ({
  data, user, search, selectedCategory, viewingArticle, showEditor, editingArticle,
  showVersions, articleVersions, showCategoryModal,
  onSearch, onSelectCategory, onViewArticle, onCreateArticle, onEditArticle,
  onSaveArticle, onPublishArticle, onDeleteArticle, onCloseEditor, onViewVersions, onRestoreVersion,
  onCloseVersions, onShowCategoryModal, onCreateCategory, onCloseCategoryModal, onBackToList
}) => {
  const isAdmin = (user?.role || user?.user_role) === 'admin';
  const categories = data?.categories || [];
  const articles = data?.articles || [];
  const metrics = data?.metrics || {};

  const [sortBy, setSortBy] = useState('recent'); // recent | popular | a-z
  const [viewMode, setViewMode] = useState(() => {
    try { return localStorage.getItem('kb_view_mode') || 'grid'; } catch { return 'grid'; }
  });
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const setMode = (m) => {
    setViewMode(m);
    try { localStorage.setItem('kb_view_mode', m); } catch {}
  };

  // Show editor
  if (showEditor) {
    return <KnowledgeArticleEditor article={editingArticle} categories={categories} user={user} onSave={onSaveArticle} onCancel={onCloseEditor} />;
  }

  // Show article view
  if (viewingArticle) {
    return (
      <>
        <KnowledgeArticleView
          article={viewingArticle} versions={articleVersions} showVersions={showVersions}
          allArticles={articles} categories={categories}
          user={user} isAdmin={isAdmin}
          onEdit={onEditArticle} onDelete={onDeleteArticle} onPublish={onPublishArticle} onBack={onBackToList}
          onViewArticle={onViewArticle}
          onViewVersions={onViewVersions} onRestoreVersion={onRestoreVersion} onCloseVersions={onCloseVersions}
        />
        {showCategoryModal && <KnowledgeCategoryModal onSave={onCreateCategory} onClose={onCloseCategoryModal} />}
      </>
    );
  }

  // Sort
  const sorted = [...articles];
  if (sortBy === 'popular') sorted.sort((a, b) => (b.views || 0) - (a.views || 0));
  else if (sortBy === 'a-z') sorted.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
  else sorted.sort((a, b) => new Date(b.updated_at || 0) - new Date(a.updated_at || 0));

  // Featured: top 3 published by views, only on the unfiltered "All Articles" view
  const showFeatured = !search && !selectedCategory && sorted.length > 3;
  const featured = showFeatured ? [...sorted].filter(a => a.status === 'published').sort((a, b) => (b.views || 0) - (a.views || 0)).slice(0, 3) : [];
  const featuredIds = new Set(featured.map(a => a.id));
  const restList = showFeatured ? sorted.filter(a => !featuredIds.has(a.id)) : sorted;

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      <div className="px-4 py-5 border-b border-gray-100">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-xl">📚</span>
          <h2 className="font-bold text-gray-900 text-base tracking-tight">KnowledgeBoard</h2>
        </div>
        <p className="text-xs text-gray-500">Internal knowledge base</p>
      </div>

      <div className="grid grid-cols-2 gap-2 px-3 py-3 border-b border-gray-100">
        <div className="bg-indigo-50 rounded-lg p-2 text-center">
          <div className="text-lg font-bold text-indigo-700">{metrics.published || 0}</div>
          <div className="text-[11px] text-gray-500 uppercase tracking-wide">Published</div>
        </div>
        <div className="bg-amber-50 rounded-lg p-2 text-center">
          <div className="text-lg font-bold text-amber-700">{metrics.drafts || 0}</div>
          <div className="text-[11px] text-gray-500 uppercase tracking-wide">Drafts</div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto py-2 px-1">
        <div className="px-3 mb-1 flex items-center justify-between">
          <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Categories</span>
          {isAdmin && (
            <button onClick={onShowCategoryModal} className="text-gray-400 hover:text-indigo-600 w-6 h-6 rounded hover:bg-indigo-50 inline-flex items-center justify-center" title="New category">
              <span className="text-base leading-none">+</span>
            </button>
          )}
        </div>
        <button
          onClick={() => { onSelectCategory(null); setMobileNavOpen(false); }}
          className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 rounded-lg ${!selectedCategory ? 'bg-indigo-50 text-indigo-700 font-medium' : 'text-gray-700 hover:bg-gray-50'}`}
        >
          <span>📋</span> All Articles
          <span className="ml-auto text-xs text-gray-400">{metrics.total || 0}</span>
        </button>
        {categories.map(cat => (
          <button
            key={cat.id}
            onClick={() => { onSelectCategory(cat.id); setMobileNavOpen(false); }}
            className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 rounded-lg ${selectedCategory === cat.id ? 'bg-indigo-50 text-indigo-700 font-medium' : 'text-gray-700 hover:bg-gray-50'}`}
          >
            <span>{cat.icon || '📄'}</span>
            <span className="truncate flex-1">{cat.name}</span>
            <span className="text-xs text-gray-400 flex-shrink-0">{cat.article_count || 0}</span>
          </button>
        ))}
      </div>
    </div>
  );

  // Render an article card (used by list + featured)
  const ArticleCard = ({ article, featuredCard = false }) => {
    const cat = categories.find(c => c.id === article.category_id);
    const accent = cat?.color || '#6366f1';
    const excerpt = kbExcerpt(article.content_preview, featuredCard ? 220 : 180);
    const author = article.created_by || 'Unknown';
    const initials = kbInitials(author);
    const date = article.updated_at ? new Date(article.updated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
    return (
      <button onClick={() => onViewArticle(article.id)}
        className={`group relative text-left bg-white rounded-2xl border border-gray-200 shadow-sm hover:shadow-lg hover:border-indigo-300 hover:-translate-y-0.5 transition-all duration-200 overflow-hidden flex flex-col w-full ${featuredCard ? 'p-0' : ''}`}>
        <div className="h-1" style={{ background: `linear-gradient(90deg, ${accent}, ${accent}80)` }} />
        <div className="p-5 flex-1 flex flex-col">
          <div className="flex items-center gap-1.5 mb-3 flex-wrap">
            {cat && (
              <span className="text-xs px-2 py-0.5 rounded-full font-medium inline-flex items-center gap-1" style={{ background: accent + '15', color: accent }}>
                <span>{cat.icon || '📄'}</span>{cat.name}
              </span>
            )}
            {article.status !== 'published' && <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-amber-100 text-amber-700">Draft</span>}
            <span className="text-[10px] uppercase tracking-wide text-gray-400 font-semibold ml-auto">v{article.version_number}</span>
          </div>
          <h3 className={`font-semibold text-gray-900 mb-2 line-clamp-2 leading-snug group-hover:text-indigo-700 transition-colors ${featuredCard ? 'text-lg' : 'text-base'}`}>{article.title}</h3>
          {excerpt && <p className="text-sm text-gray-600 line-clamp-3 leading-relaxed mb-4">{excerpt}</p>}
          <div className="mt-auto pt-3 border-t border-gray-100 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-semibold text-white flex-shrink-0" style={{ background: accent }}>{initials}</div>
              <div className="min-w-0">
                <div className="text-xs font-medium text-gray-700 truncate">{author}</div>
                <div className="text-[11px] text-gray-400">{date} · {kbReadingTime(article.content_preview)}</div>
              </div>
            </div>
            {article.views > 0 && (
              <span className="text-xs text-gray-400 inline-flex items-center gap-1 flex-shrink-0">👁 {article.views}</span>
            )}
          </div>
        </div>
      </button>
    );
  };

  // List-mode row
  const ArticleRow = ({ article }) => {
    const cat = categories.find(c => c.id === article.category_id);
    const accent = cat?.color || '#6366f1';
    const author = article.created_by || 'Unknown';
    const initials = kbInitials(author);
    const date = article.updated_at ? new Date(article.updated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
    const excerpt = kbExcerpt(article.content_preview, 140);
    return (
      <button onClick={() => onViewArticle(article.id)}
        className="group w-full text-left flex items-start gap-4 bg-white rounded-xl border border-gray-200 hover:border-indigo-300 hover:shadow-sm transition-all px-5 py-4">
        <div className="w-1 self-stretch rounded-full" style={{ background: accent }} />
        <div className="w-10 h-10 rounded-lg flex items-center justify-center text-sm font-semibold text-white flex-shrink-0" style={{ background: accent }}>{initials}</div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            {cat && <span className="text-[11px] px-2 py-0.5 rounded-full font-medium inline-flex items-center gap-1" style={{ background: accent + '15', color: accent }}><span>{cat.icon || '📄'}</span>{cat.name}</span>}
            {article.status !== 'published' && <span className="text-[11px] px-2 py-0.5 rounded-full font-medium bg-amber-100 text-amber-700">Draft</span>}
          </div>
          <h3 className="text-base font-semibold text-gray-900 group-hover:text-indigo-700 truncate transition-colors">{article.title}</h3>
          {excerpt && <p className="text-sm text-gray-500 line-clamp-1 mt-0.5">{excerpt}</p>}
          <div className="flex items-center gap-3 text-xs text-gray-400 mt-2">
            <span>{author}</span><span>·</span><span>{date}</span><span>·</span><span>{kbReadingTime(article.content_preview)}</span>
            {article.views > 0 && (<><span>·</span><span>👁 {article.views}</span></>)}
          </div>
        </div>
        <ChevronRight size={16} className="text-gray-300 flex-shrink-0 mt-1 group-hover:text-indigo-500 transition-colors" />
      </button>
    );
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 py-6">
        <div className="flex bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden" style={{ minHeight: '80vh' }}>
          {/* Sidebar (desktop) */}
          <div className="hidden lg:flex w-64 flex-shrink-0 bg-white border-r border-gray-200">
            <SidebarContent />
          </div>

          {/* Mobile drawer */}
          {mobileNavOpen && (
            <div className="fixed inset-0 z-40 lg:hidden">
              <div className="absolute inset-0 bg-black/40" onClick={() => setMobileNavOpen(false)} />
              <div className="absolute left-0 top-0 bottom-0 w-72 bg-white shadow-2xl">
                <SidebarContent />
              </div>
            </div>
          )}

          {/* Main panel */}
          <div className="flex-1 flex flex-col min-w-0">
            {/* Sticky top bar */}
            <div className="bg-white/95 backdrop-blur border-b border-gray-200 px-4 sm:px-6 py-3.5 flex items-center gap-2 sm:gap-3 sticky top-0 z-20">
              <button onClick={() => setMobileNavOpen(true)} className="lg:hidden w-9 h-9 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-600 flex-shrink-0" aria-label="Open menu">
                <span className="text-lg">☰</span>
              </button>
              <div className="flex-1 relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                <input
                  value={search} onChange={e => onSearch(e.target.value)}
                  placeholder="Search articles by title or content…"
                  className="w-full pl-9 pr-16 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-gray-50 transition"
                />
                <span className="hidden sm:flex absolute right-3 top-1/2 -translate-y-1/2 items-center gap-1 text-[11px] text-gray-400 font-mono bg-white border border-gray-200 rounded px-1.5 py-0.5 pointer-events-none">/</span>
              </div>
              <button onClick={onCreateArticle}
                className="px-3.5 sm:px-4 py-2.5 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 font-medium flex items-center gap-1.5 flex-shrink-0 shadow-sm">
                <span className="text-base leading-none">+</span> <span className="hidden sm:inline">New Article</span>
              </button>
            </div>

            {/* Content scroll area */}
            <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-5 bg-gradient-to-b from-gray-50/40 to-white">
              {/* Section header */}
              <div className="mb-5 flex items-end justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <h2 className="text-2xl font-bold text-gray-900 tracking-tight">
                    {selectedCategory ? (categories.find(c => c.id === selectedCategory)?.name || 'Category') : 'All Articles'}
                  </h2>
                  {search && <p className="text-sm text-gray-500 mt-0.5">Showing results for <span className="font-medium text-gray-700">"{search}"</span></p>}
                  {!search && <p className="text-sm text-gray-500 mt-0.5">{sorted.length} article{sorted.length !== 1 ? 's' : ''}</p>}
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex items-center bg-white border border-gray-200 rounded-lg overflow-hidden text-sm">
                    <select value={sortBy} onChange={e => setSortBy(e.target.value)} className="px-3 py-2 text-sm bg-white border-0 focus:outline-none cursor-pointer text-gray-700">
                      <option value="recent">Latest</option>
                      <option value="popular">Most viewed</option>
                      <option value="a-z">A → Z</option>
                    </select>
                  </div>
                  <div className="inline-flex bg-white border border-gray-200 rounded-lg p-0.5">
                    <button onClick={() => setMode('grid')} aria-label="Grid view"
                      className={`w-8 h-8 rounded-md flex items-center justify-center transition-colors ${viewMode === 'grid' ? 'bg-indigo-600 text-white' : 'text-gray-500 hover:text-gray-800'}`}>
                      <span className="text-sm">▦</span>
                    </button>
                    <button onClick={() => setMode('list')} aria-label="List view"
                      className={`w-8 h-8 rounded-md flex items-center justify-center transition-colors ${viewMode === 'list' ? 'bg-indigo-600 text-white' : 'text-gray-500 hover:text-gray-800'}`}>
                      <span className="text-sm">≡</span>
                    </button>
                  </div>
                </div>
              </div>

              {/* Featured strip */}
              {showFeatured && featured.length > 0 && (
                <div className="mb-7">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-xs font-semibold text-indigo-600 uppercase tracking-wider inline-flex items-center gap-1">
                      ⭐ Featured
                    </span>
                    <span className="h-px flex-1 bg-gradient-to-r from-indigo-200 to-transparent" />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {featured.map(a => <ArticleCard key={a.id} article={a} featuredCard />)}
                  </div>
                </div>
              )}

              {sorted.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 text-center">
                  <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-indigo-50 to-indigo-100 flex items-center justify-center mb-4 shadow-sm">
                    <span className="text-4xl">📄</span>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">No articles yet</h3>
                  <p className="text-gray-500 text-sm mb-6 max-w-xs">
                    {search ? `No results for "${search}". Try a different search.` : 'Create your first article to start building your knowledge base.'}
                  </p>
                  {!search && <button onClick={onCreateArticle} className="px-5 py-2 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 font-medium shadow-sm">+ New Article</button>}
                </div>
              ) : viewMode === 'grid' ? (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {restList.map(article => <ArticleCard key={article.id} article={article} />)}
                </div>
              ) : (
                <div className="space-y-3">
                  {restList.map(article => <ArticleRow key={article.id} article={article} />)}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {showCategoryModal && <KnowledgeCategoryModal onSave={onCreateCategory} onClose={onCloseCategoryModal} />}
    </div>
  );
};

// ============================================================
// TIMEBOARD COMPONENTS
// ============================================================

const dayLabels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const categoryLabels = { project: 'Project Work', adhoc: 'Ad Hoc', meeting: 'Meetings', admin: 'Admin', training: 'Training', leave: 'Leave' };
const categoryColors = { project: '#0366d6', adhoc: '#6f42c1', meeting: '#e36209', admin: '#586069', training: '#fd7e14', leave: '#e83e8c' };

const MyTimesheetView = ({ taskDays, weekDates, leaveDays, schedule }) => {
  const [expanded, setExpanded] = React.useState({});
  const toggle = (date) => setExpanded(prev => ({ ...prev, [date]: !prev[date] }));

  // Map a date string to the scheduled hours for that weekday (per the user's schedule).
  // Falls back to 8h on weekdays / 0h on weekends if no schedule is provided.
  const scheduleDayMap = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
  const getScheduledHoursForDate = (dateStr) => {
    const d = new Date(dateStr + 'T00:00:00');
    if (isNaN(d.getTime())) return 0;
    if (schedule) return Number(schedule[scheduleDayMap[d.getDay()]] || 0);
    const dow = d.getDay();
    return (dow === 0 || dow === 6) ? 0 : 8;
  };

  // Build a set of leave dates for display
  const leaveDates = new Set();
  if (leaveDays) {
    leaveDays.forEach(leave => {
      const s = new Date(leave.startDate + 'T00:00:00'), e = new Date(leave.endDate + 'T00:00:00');
      for (let d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) leaveDates.add(d.toISOString().split('T')[0]);
    });
  }

  // Map taskDays by date for quick lookup
  const dayMap = {};
  (taskDays || []).forEach(d => { dayMap[d.date] = d; });

  return (
    <div className="mb-6 bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
      {/* Header row */}
      <div className="grid grid-cols-[1fr_auto_auto] items-center px-5 py-3 bg-gray-50 border-b border-gray-200 text-xs font-semibold text-gray-500 uppercase tracking-wide">
        <span>Day</span>
        <span className="pr-6">Tasks</span>
        <span className="w-20 text-right">Hours</span>
      </div>

      {weekDates.map((date, i) => {
        const day = dayMap[date] || { date, tasks: [], totalHours: 0 };
        const scheduledHours = getScheduledHoursForDate(date);
        const isNonWorking = scheduledHours === 0;
        const isLeave = leaveDates.has(date);
        const isOpen = !!expanded[date];
        const hasTasks = day.tasks.length > 0;
        const dateLabel = new Date(date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        const interactive = hasTasks && !isNonWorking;

        return (
          <div key={date} className={`border-b border-gray-100 last:border-0 ${isNonWorking ? 'bg-gray-50' : ''}`}>
            {/* Day summary row */}
            <div
              className={`grid grid-cols-[1fr_auto_auto] items-center px-5 py-3 ${interactive ? 'cursor-pointer hover:bg-blue-50 transition-colors' : ''}`}
              onClick={() => interactive && toggle(date)}
            >
              <div className="flex items-center gap-3">
                {interactive ? (
                  <ChevronRight size={15} className={`text-gray-400 flex-shrink-0 transition-transform duration-150 ${isOpen ? 'rotate-90' : ''}`} />
                ) : (
                  <span className="w-[15px]" />
                )}
                <div>
                  <span className={`text-sm font-semibold ${isNonWorking ? 'text-gray-400' : 'text-gray-800'}`}>
                    {dayLabels[i]}
                  </span>
                  <span className={`ml-2 text-sm ${isNonWorking ? 'text-gray-300' : 'text-gray-500'}`}>{dateLabel}</span>
                  {!isNonWorking && scheduledHours > 0 && (
                    <span className="ml-2 text-[10px] text-gray-400 font-medium">Scheduled: {scheduledHours}h</span>
                  )}
                  {isLeave && <span className="ml-2 text-xs bg-pink-100 text-pink-600 px-2 py-0.5 rounded-full font-medium">Leave</span>}
                </div>
              </div>
              <span className={`pr-6 text-xs ${hasTasks ? 'text-gray-400' : 'text-transparent'}`}>
                {hasTasks ? `${day.tasks.length} task${day.tasks.length !== 1 ? 's' : ''}` : '—'}
              </span>
              <span className={`w-20 text-right text-sm font-bold ${
                isNonWorking ? 'text-gray-300' :
                day.totalHours === 0 ? 'text-gray-300' :
                day.totalHours >= scheduledHours ? 'text-green-600' : 'text-yellow-600'
              }`}>
                {isLeave ? `${scheduledHours}h` : day.totalHours > 0 ? `${day.totalHours}h` : '0h'}
              </span>
            </div>

            {/* Expanded task list */}
            {isOpen && hasTasks && (
              <div className="border-t border-gray-100 bg-gray-50">
                {day.tasks.map((task, ti) => (
                  <div key={ti} className={`flex items-center gap-3 px-5 py-2.5 pl-12 ${ti < day.tasks.length - 1 ? 'border-b border-gray-100' : ''}`}>
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full whitespace-nowrap flex-shrink-0 ${
                      task.source === 'QuickTrack' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                    }`}>
                      {task.source}
                    </span>
                    <span className={`text-xs font-mono px-1.5 py-0.5 rounded flex-shrink-0 ${
                      task.source === 'QuickTrack' ? 'bg-purple-50 text-purple-500' : 'bg-blue-50 text-blue-500'
                    }`}>
                      {task.id}
                    </span>
                    <span className="flex-1 text-sm text-gray-700 truncate">{task.title}</span>
                    <span className="text-sm font-semibold text-gray-800 whitespace-nowrap">{task.hours}h</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const TimeEntryModal = ({ date, category, onClose, onSubmit }) => {
  const [form, setForm] = useState({ hours: '', category: category || 'project', projectId: '', description: '', isBillable: true });
  const handleSubmit = (e) => { e.preventDefault(); onSubmit({ ...form, entryDate: date, hours: parseFloat(form.hours) || 0 }); };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-lg font-semibold">Add Time Entry — {date}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Hours *</label>
              <input required type="number" step="0.5" min="0.5" max="24" value={form.hours} onChange={e => setForm({...form, hours: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <select value={form.category} onChange={e => setForm({...form, category: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                <option value="project">Project Work</option>
                <option value="adhoc">Ad-hoc</option>
                <option value="meeting">Meeting</option>
                <option value="admin">Admin</option>
                <option value="training">Training</option>
              </select>
            </div>
          </div>
          {form.category === 'project' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Project</label>
              <select value={form.projectId} onChange={e => setForm({...form, projectId: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                <option value="">Select project...</option>
                <option value="PROJ-001">Settlement Vault API Modernization</option>
                <option value="PROJ-002">Negotiator Worklist Dashboard</option>
                <option value="PROJ-003">Customer Portal Redesign</option>
                <option value="PROJ-004">Data Warehouse Migration</option>
              </select>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <input value={form.description} onChange={e => setForm({...form, description: e.target.value})}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
          </div>
          <div className="flex items-center gap-2">
            <input type="checkbox" id="billable" checked={form.isBillable} onChange={e => setForm({...form, isBillable: e.target.checked})} className="rounded" />
            <label htmlFor="billable" className="text-sm text-gray-700">Billable</label>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
            <button type="submit" className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">Add Entry</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const TimeBoardModule = ({ data, weekStart, user, onWeekChange, onSubmitTimesheet, onApproveTimesheet }) => {
  const weekDates = getWeekDates(weekStart);
  const weekEnd = weekDates[6];

  const formatWeekLabel = (start, end) => {
    const s = new Date(start + 'T00:00:00');
    const e = new Date(end + 'T00:00:00');
    return `${s.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} — ${e.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
  };

  // Task-derived totals (source of truth)
  const totalHours = data?.weekTaskTotal || 0;
  const leaveHours = data?.myTimesheet?.leaveHours || 0;
  const accountedHours = Math.round((totalHours + leaveHours) * 10) / 10;
  // User's own weekly capacity comes from their schedule; falls back to 40 if missing
  const myWeeklyTotal = data?.myWeeklyTotal || 40;
  const mySchedule = data?.mySchedule || null;
  const availableHours = data?.myTimesheet?.availableHours ?? Math.max(0, myWeeklyTotal - leaveHours);
  // Thresholds for the "Accounted" card — scaled to the user's actual capacity, not a flat 40
  const fullThreshold    = myWeeklyTotal;
  const partialThreshold = Math.round(myWeeklyTotal * 0.8 * 10) / 10;
  const estimatedHours = data?.myTimesheet?.estimatedHours || 0;
  const timesheetStatus = data?.myTimesheet?.status || 'draft';
  const projectComparison = data?.projectComparison || [];

  // Leave days for this user this week
  const leaveDaysThisWeek = (data?.leaveDays || []).filter(l => l.userId === (user?.id || user?.user_id));

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">TimeBoard</h1>
          <p className="text-gray-600 mt-1">Time tracking & timesheet management</p>
        </div>
        <div className="flex items-center gap-2">
          {/* Timesheet status */}
          {timesheetStatus === 'draft' && data?.myTimesheet && (
            <button onClick={() => onSubmitTimesheet(data.myTimesheet.id)} className="px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 font-medium">Submit for Approval</button>
          )}
          {timesheetStatus === 'submitted' && (
            <span className="px-3 py-1.5 bg-yellow-100 text-yellow-700 rounded-lg text-sm font-semibold">Awaiting Approval</span>
          )}
          {timesheetStatus === 'approved' && (
            <span className="px-3 py-1.5 bg-green-100 text-green-700 rounded-lg text-sm font-semibold">Approved</span>
          )}
        </div>
      </div>

      {/* Week Navigation */}
      <div className="flex items-center gap-4 mb-6">
        <button onClick={() => onWeekChange(-1)} className="p-2 hover:bg-gray-100 rounded-lg text-gray-600">
          <ChevronRight size={20} className="rotate-180" />
        </button>
        <div className="text-lg font-semibold text-gray-900">{formatWeekLabel(weekStart, weekEnd)}</div>
        <button onClick={() => onWeekChange(1)} className="p-2 hover:bg-gray-100 rounded-lg text-gray-600">
          <ChevronRight size={20} />
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-5">
          <div className="text-sm text-gray-600 mb-1">Estimated</div>
          <div className="text-3xl font-bold text-purple-700">{estimatedHours}h</div>
          <div className="text-xs text-gray-500 mt-1">Active task estimates</div>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-5">
          <div className="text-sm text-gray-600 mb-1">Completed This Week</div>
          <div className="text-3xl font-bold text-blue-700">{totalHours}h</div>
          <div className="text-xs text-gray-500 mt-1">From finished tasks</div>
        </div>
        <div className="bg-pink-50 border border-pink-200 rounded-lg p-5">
          <div className="text-sm text-gray-600 mb-1">Leave Hours</div>
          <div className="text-3xl font-bold text-pink-700">{leaveHours}h</div>
          <div className="text-xs text-gray-500 mt-1">From approved leave</div>
        </div>
        <div className={`rounded-lg p-5 border ${accountedHours >= fullThreshold ? 'bg-green-50 border-green-200' : accountedHours >= partialThreshold ? 'bg-yellow-50 border-yellow-200' : 'bg-orange-50 border-orange-200'}`}>
          <div className="text-sm text-gray-600 mb-1">Accounted</div>
          <div className={`text-3xl font-bold ${accountedHours >= fullThreshold ? 'text-green-700' : accountedHours >= partialThreshold ? 'text-yellow-700' : 'text-orange-700'}`}>{accountedHours}h</div>
          <div className="text-xs text-gray-500 mt-1">Tasks + leave / {availableHours}h avail<span className="opacity-70"> ({myWeeklyTotal}h/wk)</span></div>
        </div>
      </div>

      {/* My Timesheet — auto-generated from completed tasks */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold text-gray-900">My Timesheet</h2>
        <span className="text-xs text-gray-400 bg-gray-100 px-3 py-1 rounded-full">Auto-generated from completed tasks</span>
      </div>
      <MyTimesheetView
        taskDays={data?.taskDays || []}
        weekDates={weekDates}
        leaveDays={leaveDaysThisWeek}
        schedule={mySchedule}
      />

      {/* Estimate vs Actual by Project */}
      {projectComparison.length > 0 && (
        <>
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Estimate vs Actual — by Project</h2>
          <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden mb-6">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Project</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Estimated</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Actual (This Week)</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Variance</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Progress</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {projectComparison.map(p => {
                  const est = p.estimated_hours || 0;
                  const act = p.actual_hours || 0;
                  const variance = act - est;
                  const pct = est > 0 ? Math.min(100, Math.round((act / est) * 100)) : null;
                  return (
                    <tr key={p.project_id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="text-sm font-medium text-gray-900">{p.project_name}</div>
                        <div className="text-xs text-gray-400">{p.project_id}</div>
                      </td>
                      <td className="px-4 py-3 text-right text-sm font-semibold text-purple-700">{est}h</td>
                      <td className="px-4 py-3 text-right text-sm font-semibold text-blue-700">{act}h</td>
                      <td className="px-4 py-3 text-right text-sm font-semibold">
                        {est > 0 ? (
                          <span className={variance > 0 ? 'text-red-600' : variance < 0 ? 'text-green-600' : 'text-gray-500'}>
                            {variance > 0 ? '+' : ''}{variance.toFixed(1)}h
                          </span>
                        ) : <span className="text-gray-400">—</span>}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {pct !== null ? (
                          <div className="flex items-center justify-end gap-2">
                            <div className="w-20 h-2 bg-gray-200 rounded-full overflow-hidden">
                              <div className="h-full rounded-full" style={{ width: `${pct}%`, background: pct > 100 ? '#dc2626' : pct > 75 ? '#f59e0b' : '#10b981' }} />
                            </div>
                            <span className="text-xs text-gray-600">{pct}%</span>
                          </div>
                        ) : <span className="text-xs text-gray-400">—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Team Timesheets — visible to admin/manager (all) and *_manager roles (their team only) */}
      {data?.teamScope?.canSeeTeam && (
      <>
      <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
        <h2 className="text-xl font-semibold text-gray-900">Team Overview</h2>
        {data?.teamScope?.label && (
          <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-indigo-50 text-indigo-700 border border-indigo-100">
            {data.teamScope.label}
          </span>
        )}
      </div>
      <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Team Member</th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Estimated</th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Actual Logged</th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Leave</th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Accounted</th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Billable</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Status</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {(data?.teamTimesheets || []).map(ts => {
              const ss = getStatusStyle(ts.status === 'draft' ? 'backlog' : ts.status === 'submitted' ? 'review' : ts.status === 'approved' ? 'completed' : 'blocked');
              const tsEst = ts.estimatedHours || 0;
              const tsAct = ts.totalHours || 0;
              const tsLeave = ts.leaveHours || 0;
              const tsAccounted = ts.accountedHours ?? (tsAct + tsLeave);
              const tsVariance = tsAct - tsEst;
              const tsWeekly = ts.weeklyTotal || 40;
              const tsPartial = Math.round(tsWeekly * 0.8 * 10) / 10;
              return (
                <tr key={ts.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-sm font-bold">{ts.userName.charAt(0)}</div>
                      <span className="text-sm font-medium text-gray-900">{ts.userName}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right text-sm font-semibold text-purple-700">{tsEst}h</td>
                  <td className="px-4 py-3 text-right">
                    <div className="text-sm font-semibold text-blue-700">{tsAct}h</div>
                    {tsEst > 0 && (
                      <div className={`text-xs font-medium ${tsVariance > 0 ? 'text-red-500' : tsVariance < 0 ? 'text-green-500' : 'text-gray-400'}`}>
                        {tsVariance > 0 ? '+' : ''}{tsVariance.toFixed(1)}h
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right text-sm font-semibold text-pink-600">{tsLeave > 0 ? `${tsLeave}h` : '—'}</td>
                  <td className="px-4 py-3 text-right">
                    <div className={`text-sm font-semibold ${tsAccounted >= tsWeekly ? 'text-green-700' : tsAccounted >= tsPartial ? 'text-yellow-700' : 'text-orange-700'}`}>{tsAccounted}h</div>
                    <div className="text-xs text-gray-400">{tsLeave > 0 ? `${tsAct}w + ${tsLeave}l` : `of ${tsWeekly}h`}</div>
                  </td>
                  <td className="px-4 py-3 text-right text-sm text-gray-600">{ts.billableHours}h</td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-0.5 rounded text-xs font-semibold capitalize" style={{ background: ss.bg, color: ss.color }}>{ts.status}</span>
                  </td>
                  <td className="px-4 py-3">
                    {ts.status === 'submitted' && (
                      <button onClick={() => onApproveTimesheet(ts.id)} className="px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700">Approve</button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {(!data?.teamTimesheets || data.teamTimesheets.length === 0) && (
          <div className="p-8 text-center text-gray-400">No timesheets for this week.</div>
        )}
      </div>
      </>
      )}
    </div>
  );
};

// ---- LeaveBoard Components ----

const getLeaveTypeBadge = (type) => {
  const badges = {
    vacation: { color: '#0366d6', bg: '#d4e4ff', label: 'Vacation', icon: '🏖️' },
    sick: { color: '#dc3545', bg: '#ffe5e9', label: 'Sick', icon: '🤒' },
    personal: { color: '#6f42c1', bg: '#f3e8ff', label: 'Personal', icon: '👤' },
    wfh: { color: '#28a745', bg: '#dcffe4', label: 'WFH', icon: '🏠' }
  };
  return badges[type] || { color: '#586069', bg: '#f6f8fa', label: type, icon: '📅' };
};

const getLeaveStatusBadge = (status) => {
  const badges = {
    pending: { color: '#e36209', bg: '#fff5cc', label: 'Pending' },
    approved: { color: '#28a745', bg: '#dcffe4', label: 'Approved' },
    rejected: { color: '#dc3545', bg: '#ffe5e9', label: 'Rejected' },
    cancelled: { color: '#586069', bg: '#f6f8fa', label: 'Cancelled' }
  };
  return badges[status] || { color: '#586069', bg: '#f6f8fa', label: status };
};

const LeaveBalanceCards = ({ balance, pastProbation, daysUntilProbation, policy }) => {
  const TIMEOFF_TOTAL = policy?.TIMEOFF_HOURS || 80;
  const SICK_TOTAL = policy?.SICK_HOURS || 48;
  // Prefer the new hour columns; fall back to legacy day×8 if hours haven't been backfilled yet
  const timeoffTotal     = balance.timeoff_hours_total     ?? ((balance.vacation_total || 0) + (balance.personal_total || 0)) * 8;
  const timeoffUsed      = balance.timeoff_hours_used      ?? ((balance.vacation_used  || 0) + (balance.personal_used  || 0)) * 8;
  const timeoffRemaining = balance.timeoff_hours_remaining ?? Math.max(0, timeoffTotal - timeoffUsed);
  const sickTotal     = balance.sick_hours_total     ?? (balance.sick_total || 0) * 8;
  const sickUsed      = balance.sick_hours_used      ?? (balance.sick_used  || 0) * 8;
  const sickRemaining = balance.sick_hours_remaining ?? Math.max(0, sickTotal - sickUsed);

  const cards = [
    {
      label: 'Time Off',
      total: timeoffTotal, used: timeoffUsed, remaining: timeoffRemaining,
      color: '#6366f1', icon: '🏖️',
      sub: pastProbation ? 'Vacation, personal & other' : `Unlocks after 90-day probation${daysUntilProbation != null ? ` (${daysUntilProbation} day${daysUntilProbation === 1 ? '' : 's'} left)` : ''}`,
      locked: !pastProbation,
    },
    {
      label: 'Sick Leave',
      total: sickTotal, used: sickUsed, remaining: sickRemaining,
      color: '#dc3545', icon: '🤒',
      sub: 'Available to everyone',
    },
  ];

  return (
    <>
      {!pastProbation && (
        <div className="mb-4 bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-3">
          <span className="text-base">⏳</span>
          <div className="text-sm">
            <span className="font-semibold text-amber-800">On probation.</span>
            <span className="text-amber-700 ml-1">
              {daysUntilProbation != null
                ? `${daysUntilProbation} day${daysUntilProbation === 1 ? '' : 's'} until your ${TIMEOFF_TOTAL}h Time Off allowance unlocks.`
                : 'Set your hire date in Settings to track probation progress.'}
              {' '}Sick leave ({SICK_TOTAL}h) is available now.
            </span>
          </div>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        {cards.map(card => {
          const usedPercent = card.total > 0 ? Math.round((card.used / card.total) * 100) : 0;
          return (
            <div key={card.label} className={`bg-white rounded-lg shadow border p-5 ${card.locked ? 'border-amber-200' : 'border-gray-200'}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-medium text-gray-600 flex items-center gap-1.5">
                  <span>{card.icon}</span>
                  <span>{card.label}</span>
                  {card.locked && <span className="text-[10px] uppercase tracking-wider font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">Locked</span>}
                </div>
                <div className="text-2xl font-bold" style={{ color: card.locked ? '#92400e' : card.color }}>{card.remaining}<span className="text-xs font-medium text-gray-400 ml-1">h</span></div>
              </div>
              <div className="text-xs text-gray-500 mb-2">{card.used}h used of {card.total}h</div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${usedPercent}%`, background: card.color }} />
              </div>
              {card.sub && <div className="text-[11px] text-gray-400 mt-2">{card.sub}</div>}
            </div>
          );
        })}
      </div>
    </>
  );
};

const RequestLeaveModal = ({ onClose, onSubmit, balance, schedule }) => {
  const [form, setForm] = useState({
    leaveType: 'vacation', startDate: '', endDate: '', reason: '',
    isRetro: false, retroStatus: 'approved', approvedBy: '',
  });
  const [hoursOverride, setHoursOverride] = useState(null); // null = use computed

  const calculateDays = (start, end) => {
    if (!start || !end) return 0;
    const s = new Date(start + 'T00:00:00');
    const e = new Date(end + 'T00:00:00');
    let days = 0;
    for (let d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) {
      const dow = d.getDay();
      if (dow !== 0 && dow !== 6) days++;
    }
    return days;
  };

  const effectiveSchedule = schedule && Object.keys(schedule).length ? schedule : DEFAULT_SCHEDULE_CLIENT;
  const today = new Date().toISOString().slice(0, 10);
  const totalDays = calculateDays(form.startDate, form.endDate);
  const computedHours = computeLeaveHoursClient(form.startDate, form.endDate, effectiveSchedule);
  // Reset override when dates change
  useEffect(() => { setHoursOverride(null); }, [form.startDate, form.endDate]);
  const totalHours = hoursOverride !== null ? hoursOverride : computedHours;

  // Per-day breakdown for the dates picked — used for the explanation tooltip + list
  const dayMap = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
  const dayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const breakdown = (() => {
    if (!form.startDate || !form.endDate) return [];
    const s = new Date(form.startDate + 'T00:00:00');
    const e = new Date(form.endDate + 'T00:00:00');
    if (isNaN(s.getTime()) || isNaN(e.getTime()) || e < s) return [];
    const out = [];
    for (const d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) {
      const dowIdx = d.getDay();
      const key = dayMap[dowIdx];
      const h = Number(effectiveSchedule[key] || 0);
      out.push({
        date: new Date(d).toISOString().slice(0, 10),
        label: dayLabels[dowIdx],
        hours: h,
        working: h > 0,
      });
      if (out.length > 14) break; // soft cap
    }
    return out;
  })();

  // Prefer hour-based balance; fall back to legacy days×8
  const isSick = form.leaveType === 'sick';
  const isWfh = form.leaveType === 'wfh';
  const hoursRemaining = isSick
    ? (balance?.sick_hours_remaining ?? (balance?.sick_remaining || 0) * 8)
    : (balance?.timeoff_hours_remaining ?? ((balance?.vacation_remaining || 0) + (balance?.personal_remaining || 0)) * 8);
  const checksBalance = !isWfh && !(form.isRetro && form.retroStatus !== 'approved');
  const insufficient = checksBalance && totalHours > hoursRemaining;
  const zeroHours = totalDays > 0 && totalHours === 0;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (insufficient || zeroHours) return;
    const payload = {
      leaveType: form.leaveType,
      startDate: form.startDate,
      endDate: form.endDate,
      reason: form.reason,
      totalDays,
      totalHours,
    };
    if (form.isRetro) {
      payload.status = form.retroStatus;
      if (form.approvedBy.trim()) payload.approvedBy = form.approvedBy.trim();
    }
    onSubmit(payload);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-xl font-semibold">Request Leave</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Retro toggle — when on, the form accepts past dates and asks for the prior approval status */}
          <label className={`flex items-center gap-3 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${form.isRetro ? 'border-amber-300 bg-amber-50' : 'border-gray-200 hover:bg-gray-50'}`}>
            <input type="checkbox" checked={form.isRetro} onChange={e => setForm({...form, isRetro: e.target.checked})}
              className="rounded text-amber-600 focus:ring-amber-500" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-gray-900">Retroactive entry</div>
              <div className="text-xs text-gray-500">Log a past leave I've already taken — and record whether it was approved.</div>
            </div>
          </label>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Leave Type</label>
            <select value={form.leaveType} onChange={e => setForm({...form, leaveType: e.target.value})}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
              <option value="vacation">Vacation / Time Off</option>
              <option value="sick">Sick Leave</option>
              <option value="personal">Personal (Time Off)</option>
              <option value="wfh">Work from Home (no balance impact)</option>
            </select>
            {!isWfh && (
              <div className="mt-1 text-xs text-gray-500">
                {isSick ? 'Sick pool' : 'Time Off pool'}: <span className="font-semibold text-gray-700">{hoursRemaining}h remaining</span>
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date *</label>
              <input required type="date" value={form.startDate}
                max={form.isRetro ? today : undefined}
                onChange={e => setForm({...form, startDate: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Date *</label>
              <input required type="date" value={form.endDate}
                max={form.isRetro ? today : undefined}
                onChange={e => setForm({...form, endDate: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
            </div>
          </div>
          {totalDays > 0 && (
            <div className={`rounded-lg border ${insufficient ? 'bg-red-50 border-red-200' : zeroHours ? 'bg-amber-50 border-amber-200' : 'bg-blue-50 border-blue-200'}`}>
              <div className={`px-3 py-2 text-sm flex items-center justify-between gap-3 flex-wrap ${insufficient ? 'text-red-700' : zeroHours ? 'text-amber-700' : 'text-blue-700'}`}>
                <span>
                  <span className="font-semibold">{totalHours}h</span>
                  <span className="opacity-70"> · {totalDays} day{totalDays > 1 ? 's' : ''} in range</span>
                  {hoursOverride === null && breakdown.length && breakdown.length <= 7 && breakdown.some(b => b.working) && (
                    <span className="opacity-60 ml-1">({breakdown.filter(b => b.working).map(b => `${b.label} ${b.hours}h`).join(' + ')})</span>
                  )}
                </span>
                {hoursOverride === null ? (
                  <button type="button" onClick={() => setHoursOverride(computedHours)} className="text-xs font-medium underline opacity-80 hover:opacity-100">
                    Adjust hours
                  </button>
                ) : (
                  <div className="flex items-center gap-2">
                    <input type="number" step="0.5" min="0" max="200" value={hoursOverride}
                      onChange={e => setHoursOverride(Number(e.target.value) || 0)}
                      className="w-20 border border-current/40 rounded px-2 py-0.5 text-sm bg-white/70 text-gray-900" />
                    <span className="text-xs">h</span>
                    <button type="button" onClick={() => setHoursOverride(null)} className="text-xs font-medium underline opacity-80 hover:opacity-100">Reset</button>
                  </div>
                )}
              </div>
              {insufficient && <div className="px-3 pb-2 text-xs text-red-600">Insufficient balance — you have {hoursRemaining}h available.</div>}
              {zeroHours && <div className="px-3 pb-2 text-xs text-amber-700">No working hours in this range (per your schedule). Pick different dates or adjust hours manually.</div>}
            </div>
          )}

          {/* Retro-only: prior approval status */}
          {form.isRetro && (
            <div className="space-y-2 px-3 py-3 rounded-lg bg-amber-50/60 border border-amber-200">
              <div className="text-xs font-semibold text-amber-900 uppercase tracking-wider">Approval status at the time</div>
              <div className="flex gap-2">
                {[
                  { value: 'approved', label: '✓ Was approved',  cls: 'border-emerald-300 text-emerald-700 bg-emerald-50/50' },
                  { value: 'rejected', label: '✕ Was rejected',  cls: 'border-red-300 text-red-700 bg-red-50/50' },
                ].map(opt => (
                  <button key={opt.value} type="button" onClick={() => setForm({...form, retroStatus: opt.value})}
                    className={`flex-1 px-3 py-2 text-sm font-medium rounded-lg border-2 transition-all ${form.retroStatus === opt.value ? opt.cls + ' ring-2 ring-offset-1 ring-amber-300' : 'border-gray-200 text-gray-600 hover:bg-white'}`}>
                    {opt.label}
                  </button>
                ))}
              </div>
              {form.retroStatus === 'approved' && (
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1 mt-2">Approved by (optional)</label>
                  <input type="text" value={form.approvedBy} onChange={e => setForm({...form, approvedBy: e.target.value})}
                    placeholder="e.g. Jim Manager"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500" />
                </div>
              )}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
            <textarea value={form.reason} onChange={e => setForm({...form, reason: e.target.value})} rows={2}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
            <button type="submit" disabled={insufficient || totalDays === 0 || zeroHours}
              className={`px-4 py-2 text-sm rounded-lg disabled:opacity-50 text-white ${form.isRetro ? 'bg-amber-600 hover:bg-amber-700' : 'bg-blue-600 hover:bg-blue-700'}`}>
              {form.isRetro ? `Log Retro ${form.retroStatus === 'approved' ? 'Approved' : 'Rejected'} Leave` : 'Submit Request'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const TeamAbsenceCalendar = ({ upcomingLeave }) => {
  // Build a simple month view
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDay = new Date(year, month, 1).getDay();

  const monthName = new Date(year, month).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  const calDayLabels = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

  // Map leave days
  const leaveMap = {};
  (upcomingLeave || []).forEach(leave => {
    const s = new Date(leave.startDate + 'T00:00:00');
    const e = new Date(leave.endDate + 'T00:00:00');
    for (let d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) {
      const dateStr = d.toISOString().split('T')[0];
      if (!leaveMap[dateStr]) leaveMap[dateStr] = [];
      leaveMap[dateStr].push(leave);
    }
  });

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  return (
    <div className="bg-white rounded-lg shadow border border-gray-200 p-5">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">{monthName}</h3>
      <div className="grid grid-cols-7 gap-1">
        {calDayLabels.map(d => (
          <div key={d} className="text-center text-xs font-semibold text-gray-500 py-1">{d}</div>
        ))}
        {cells.map((day, i) => {
          if (day === null) return <div key={`empty-${i}`} />;
          const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
          const leaveForDay = leaveMap[dateStr] || [];
          const isToday = day === now.getDate();
          const isWeekend = new Date(year, month, day).getDay() === 0 || new Date(year, month, day).getDay() === 6;

          return (
            <div key={day} className={`text-center py-1.5 rounded text-xs relative ${isToday ? 'bg-blue-600 text-white font-bold' : isWeekend ? 'text-gray-300' : 'text-gray-700'} ${leaveForDay.length > 0 && !isToday ? 'bg-pink-100' : ''}`}
              title={leaveForDay.map(l => `${l.userName} (${l.leaveType})`).join(', ')}>
              {day}
              {leaveForDay.length > 0 && (
                <div className="absolute bottom-0 left-1/2 -translate-x-1/2 flex gap-0.5">
                  {leaveForDay.slice(0, 3).map((l, j) => (
                    <div key={j} className="w-1 h-1 rounded-full" style={{ background: getLeaveTypeBadge(l.leaveType).color }} />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {/* Legend */}
      <div className="flex flex-wrap gap-3 mt-4 pt-3 border-t border-gray-200">
        {upcomingLeave && [...new Set(upcomingLeave.map(l => l.userName))].map(name => {
          const leaves = upcomingLeave.filter(l => l.userName === name);
          return (
            <div key={name} className="text-xs text-gray-600">
              <span className="font-medium">{name}</span>: {leaves.map(l => {
                const badge = getLeaveTypeBadge(l.leaveType);
                return <span key={l.startDate} className="ml-1 px-1.5 py-0.5 rounded" style={{ background: badge.bg, color: badge.color }}>{badge.label} {l.startDate.slice(5)}</span>;
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Inline hire-date editor surfaced on LeaveBoard so every user can set theirs without
// leaving the page. Renders prominent if unset; compact summary + edit when set.
const HireDateInlineEditor = ({ hireDate, pastProbation, daysUntilProbation, onSaved }) => {
  const [editing, setEditing] = useState(!hireDate); // auto-open when unset
  const [draft, setDraft] = useState(hireDate || '');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    setDraft(hireDate || '');
    setEditing(!hireDate);
  }, [hireDate]);

  const handleSave = async () => {
    if (!draft) { setErr('Please pick a date'); return; }
    setSaving(true); setErr('');
    const r = await ModulesModel.updateMyHireDate(draft);
    setSaving(false);
    if (r?.success) {
      setEditing(false);
      if (onSaved) await onSaved();
    } else {
      setErr(r?.error || 'Could not save');
    }
  };
  const handleCancel = () => { setDraft(hireDate || ''); setEditing(false); setErr(''); };

  // Compute display state
  const statusLabel = pastProbation
    ? 'Probation passed'
    : hireDate
      ? `On probation — ${daysUntilProbation ?? '?'} day${daysUntilProbation === 1 ? '' : 's'} left`
      : 'Hire date not set';
  const statusTone = pastProbation
    ? { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', dot: 'bg-emerald-500' }
    : hireDate
      ? { bg: 'bg-amber-50',   border: 'border-amber-200',   text: 'text-amber-700',   dot: 'bg-amber-500' }
      : { bg: 'bg-indigo-50',  border: 'border-indigo-200',  text: 'text-indigo-700',  dot: 'bg-indigo-500' };

  // Unset / editing — prominent card
  if (editing) {
    return (
      <div className={`mb-6 rounded-lg border ${hireDate ? 'border-gray-200 bg-white' : 'border-indigo-300 bg-gradient-to-br from-indigo-50 to-violet-50/50'} p-4 sm:p-5 shadow-sm`}>
        <div className="flex items-start gap-3 mb-3">
          <span className="text-2xl">📅</span>
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-gray-900">
              {hireDate ? 'Edit your hire date' : 'Set your hire date'}
            </h3>
            <p className="text-xs text-gray-600 mt-0.5">
              Required to compute your 90-day probation and unlock your 80-hour Time Off allowance. Sick leave (48h) is available regardless.
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex-1 min-w-[160px]">
            <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1">Hire date</label>
            <input type="date" value={draft} onChange={e => { setDraft(e.target.value); setErr(''); }}
              max={new Date().toISOString().slice(0, 10)}
              className="w-full sm:w-56 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
          </div>
          <div className="flex items-center gap-2">
            {hireDate && (
              <button onClick={handleCancel} className="px-3 py-2 text-sm text-gray-600 hover:text-gray-900 font-medium">Cancel</button>
            )}
            <button onClick={handleSave} disabled={saving || !draft}
              className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 font-medium shadow-sm">
              {saving ? 'Saving…' : 'Save hire date'}
            </button>
          </div>
        </div>
        {err && <div className="mt-2 text-xs text-red-600">{err}</div>}
      </div>
    );
  }

  // Compact summary — hire date set, not editing
  return (
    <div className={`mb-6 rounded-lg border ${statusTone.border} ${statusTone.bg} p-3 sm:p-4 flex items-center justify-between gap-3 flex-wrap`}>
      <div className="flex items-center gap-3 min-w-0">
        <span className="text-lg flex-shrink-0">📅</span>
        <div className="min-w-0">
          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Hire date</div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-gray-900">
              {new Date(hireDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </span>
            <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium ${statusTone.text}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${statusTone.dot}`} />
              {statusLabel}
            </span>
          </div>
        </div>
      </div>
      <button onClick={() => setEditing(true)}
        className="px-3 py-1.5 text-xs font-medium text-gray-600 hover:text-gray-900 border border-gray-300 bg-white rounded-lg hover:bg-gray-50">
        Edit
      </button>
    </div>
  );
};

const DAYS_OF_WEEK = [
  { key: 'mon', label: 'Mon' },
  { key: 'tue', label: 'Tue' },
  { key: 'wed', label: 'Wed' },
  { key: 'thu', label: 'Thu' },
  { key: 'fri', label: 'Fri' },
  { key: 'sat', label: 'Sat' },
  { key: 'sun', label: 'Sun' },
];
const DEFAULT_SCHEDULE_CLIENT = { mon: 8, tue: 8, wed: 8, thu: 8, fri: 8, sat: 0, sun: 0 };

// Self-service weekly schedule editor — surfaced on LeaveBoard so every user can set their own hours per weekday.
const ScheduleInlineEditor = ({ schedule, onSaved }) => {
  const initial = schedule && Object.keys(schedule).length ? schedule : DEFAULT_SCHEDULE_CLIENT;
  const [draft, setDraft] = useState(initial);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    setDraft(schedule && Object.keys(schedule).length ? schedule : DEFAULT_SCHEDULE_CLIENT);
  }, [schedule]);

  const totalWeekly = DAYS_OF_WEEK.reduce((s, d) => s + (Number(draft[d.key]) || 0), 0);
  const isDefault = DAYS_OF_WEEK.every(d => Number(initial[d.key] || 0) === DEFAULT_SCHEDULE_CLIENT[d.key]);

  const set = (key, val) => {
    const num = val === '' ? 0 : Number(val);
    if (!Number.isFinite(num) || num < 0 || num > 24) return;
    setDraft({ ...draft, [key]: num });
  };
  const handleSave = async () => {
    setSaving(true); setErr('');
    const r = await ModulesModel.updateMySchedule(draft);
    setSaving(false);
    if (r?.success) {
      setEditing(false);
      if (onSaved) await onSaved();
    } else {
      setErr(r?.error || 'Could not save schedule');
    }
  };
  const handleCancel = () => {
    setDraft(schedule && Object.keys(schedule).length ? schedule : DEFAULT_SCHEDULE_CLIENT);
    setEditing(false); setErr('');
  };

  // Compact summary line used in the read-only state
  const summaryLine = (() => {
    if (isDefault) return 'Standard week — 8h Mon–Fri (40h)';
    // Group by hour count to compress: "Mon–Thu 8.5 · Fri 6"
    return DAYS_OF_WEEK
      .filter(d => Number(initial[d.key] || 0) > 0)
      .map(d => `${d.label} ${initial[d.key]}h`)
      .join(' · ');
  })();

  if (!editing) {
    return (
      <div className="mb-6 rounded-lg border border-gray-200 bg-white p-3 sm:p-4 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <span className="text-lg flex-shrink-0">🗓️</span>
          <div className="min-w-0">
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Weekly schedule</div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-semibold text-gray-900">{totalWeekly}h / week</span>
              <span className="text-xs text-gray-500">{summaryLine}</span>
            </div>
          </div>
        </div>
        <button onClick={() => setEditing(true)}
          className="px-3 py-1.5 text-xs font-medium text-gray-600 hover:text-gray-900 border border-gray-300 bg-white rounded-lg hover:bg-gray-50">
          Edit
        </button>
      </div>
    );
  }

  // Editing — show 7 inputs
  return (
    <div className="mb-6 rounded-lg border border-indigo-200 bg-gradient-to-br from-indigo-50/40 to-white p-4 sm:p-5 shadow-sm">
      <div className="flex items-start gap-3 mb-4">
        <span className="text-xl">🗓️</span>
        <div className="flex-1">
          <h3 className="text-sm font-semibold text-gray-900">Your weekly schedule</h3>
          <p className="text-xs text-gray-600 mt-0.5">Hours you typically work per day. Used to calculate leave duration — a Friday off counts for whatever you'd normally work that day. Use 0 for non-working days.</p>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-1.5 mb-3">
        {DAYS_OF_WEEK.map(d => {
          const v = draft[d.key] ?? 0;
          const isWorking = v > 0;
          return (
            <div key={d.key} className="text-center">
              <label className={`block text-[10px] font-semibold uppercase tracking-wider mb-1 ${isWorking ? 'text-gray-700' : 'text-gray-400'}`}>{d.label}</label>
              <input type="number" step="0.5" min="0" max="24" value={v}
                onChange={e => set(d.key, e.target.value)}
                className={`w-full text-center border rounded-lg px-1 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 ${isWorking ? 'border-gray-300 text-gray-900 bg-white' : 'border-gray-200 text-gray-400 bg-gray-50'}`} />
            </div>
          );
        })}
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap pt-2 border-t border-gray-100">
        <div className="text-xs text-gray-500">
          Weekly total: <span className="font-semibold text-gray-800">{Math.round(totalWeekly * 10) / 10}h</span>
        </div>
        <div className="flex items-center gap-2">
          {err && <span className="text-xs text-red-600">{err}</span>}
          <button onClick={handleCancel} className="px-3 py-1.5 text-xs text-gray-600 hover:text-gray-900 font-medium">Cancel</button>
          <button onClick={handleSave} disabled={saving}
            className="px-4 py-1.5 text-xs bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 font-medium shadow-sm">
            {saving ? 'Saving…' : 'Save schedule'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Compute leave hours from a schedule across an inclusive date range. Mirrors the backend helper.
const computeLeaveHoursClient = (startDate, endDate, schedule) => {
  if (!startDate || !endDate || !schedule) return 0;
  const s = new Date(startDate + 'T00:00:00');
  const e = new Date(endDate + 'T00:00:00');
  if (isNaN(s.getTime()) || isNaN(e.getTime()) || e < s) return 0;
  const dayMap = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
  let hours = 0;
  for (const d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) {
    hours += Number(schedule[dayMap[d.getDay()]] || 0);
  }
  return Math.round(hours * 100) / 100;
};

// One leave request — compact by default (identity/dates/status on one line), the
// approval chain and reason revealed on click, auto-expanded only when there's a
// pending step actually waiting on someone so reviewers still see it immediately.
const LeaveRequestRow = ({ req, user, onApproveRequest, onRejectRequest, onDeleteRequest, formatDate }) => {
  const typeBadge = getLeaveTypeBadge(req.leaveType);
  const statusBadge = getLeaveStatusBadge(req.status);
  const role = user?.role || user?.user_role;
  const userName = user?.user_name || user?.name || '';
  const isOwner = req.userId === (user?.id || user?.user_id) || req.userName === userName;
  const isAdmin = role === 'admin';
  const isManager = ['admin', 'manager'].includes(role);
  const canDelete = (isOwner || isManager) && onDeleteRequest;
  const approvals = req.approvals || [];
  const hasChain = approvals.length > 0;
  const currentStep = approvals.find(a => a.decision === 'pending');
  // Approve/Reject buttons appear only for the person currently assigned (or admin),
  // and NEVER for the requester (mirrors CRF self-approval block).
  const canReview = req.status === 'pending' && !isOwner && (
    hasChain
      ? (isAdmin || (currentStep?.assignedToUserName && currentStep.assignedToUserName === userName))
      : ['admin','manager','devops_manager','dataops_manager','service_desk_manager','revops_manager'].includes(role)
  );
  const [expanded, setExpanded] = useState(!!(hasChain && currentStep));
  const hasDetail = hasChain || req.reason;

  const handleDelete = () => {
    if (!confirm(`Delete this ${req.status} leave request${req.status === 'approved' ? '? The balance will be restored.' : '?'}`)) return;
    onDeleteRequest(req.id);
  };
  const handleReject = () => {
    const notes = prompt('Reason for rejection (required):');
    if (notes == null) return;
    if (!notes.trim()) { alert('Rejection notes are required.'); return; }
    onRejectRequest(req.id, notes.trim());
  };

  return (
    <div className="px-4 py-2.5 hover:bg-gray-50 transition-colors">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <div className="w-7 h-7 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-bold flex-shrink-0">{(req.userName || '?').charAt(0)}</div>
          <span className="font-medium text-sm text-gray-900 truncate shrink-0">{req.userName}</span>
          <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold shrink-0" style={{ background: typeBadge.bg, color: typeBadge.color }}>{typeBadge.icon} {typeBadge.label}</span>
          <span className="text-xs text-gray-500 truncate">📅 {formatDate(req.startDate)} – {formatDate(req.endDate)} ({req.totalHours || (req.totalDays || 0) * 8}h · {req.totalDays}d)</span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold" style={{ background: statusBadge.bg, color: statusBadge.color }}>{statusBadge.label}</span>
          {hasDetail && (
            <button onClick={() => setExpanded(e => !e)} title={expanded ? 'Hide details' : 'Show details'} className="text-gray-400 hover:text-gray-600">
              <ChevronDown size={14} className={`transition-transform ${expanded ? 'rotate-180' : ''}`} />
            </button>
          )}
        </div>
      </div>

      {expanded && (req.reason || hasChain) && (
        <div className="mt-2 ml-9 space-y-2">
          {req.reason && <p className="text-sm text-gray-500">{req.reason}</p>}
          {/* Approval chain — shows each step's status. Highlights the current pending step. */}
          {hasChain && (
            <div className="p-2 bg-slate-50 border border-slate-200 rounded text-xs">
              <div className="font-semibold text-slate-600 mb-1">Approval chain</div>
              <ol className="space-y-1">
                {approvals.map(a => {
                  const dec = (a.decision || 'pending').toLowerCase();
                  const isCurrent = dec === 'pending' && a.stepNumber === currentStep?.stepNumber;
                  const color = dec === 'approved' ? 'text-emerald-700' : dec === 'rejected' ? 'text-rose-700' : isCurrent ? 'text-indigo-700 font-semibold' : 'text-slate-500';
                  const icon = dec === 'approved' ? '✓' : dec === 'rejected' ? '✗' : isCurrent ? '→' : '·';
                  return (
                    <li key={a.stepNumber} className={`flex items-center gap-2 ${color}`}>
                      <span className="w-4 text-center">{icon}</span>
                      <span>Step {a.stepNumber}{a.approverLabel ? ` — ${a.approverLabel}` : ''}: <strong>{a.assignedToUserName}</strong></span>
                      {dec !== 'pending' && <span className="text-slate-400">({dec})</span>}
                      {a.decisionNotes && <span className="text-slate-500 italic truncate">— {a.decisionNotes}</span>}
                    </li>
                  );
                })}
              </ol>
            </div>
          )}
        </div>
      )}

      {(req.status === 'pending' || req.status === 'approved' || canDelete) && (
        <div className="flex gap-2 pt-2 mt-2 border-t border-gray-100 items-center flex-wrap ml-9">
          {canReview && (
            <>
              <button onClick={() => onApproveRequest(req.id)} className="px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700">Approve</button>
              <button onClick={handleReject} className="px-3 py-1 bg-red-100 text-red-700 text-xs rounded hover:bg-red-200">Reject</button>
            </>
          )}
          {req.status === 'pending' && !canReview && isOwner && (
            <span className="text-xs text-gray-500 italic">You can't review your own request. Use Delete to withdraw.</span>
          )}
          {req.status === 'pending' && !canReview && !isOwner && currentStep?.assignedToUserName && (
            <span className="text-xs text-gray-500 italic">Waiting on {currentStep.assignedToUserName} (Step {currentStep.stepNumber})</span>
          )}
          {req.status === 'approved' && req.approvedBy && (
            <span className="text-xs text-gray-400">Approved by {req.approvedBy}</span>
          )}
          {canDelete && (
            <button onClick={handleDelete}
              className="ml-auto px-3 py-1 text-xs text-red-600 border border-red-200 rounded hover:bg-red-50">
              Delete
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const LeaveBoardModule = ({ data, user, onRequestLeave, onApproveRequest, onRejectRequest, onDeleteRequest, onRefresh }) => {
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Manager-only profile editor (hire date + weekly schedule) for team members
  const role = user?.role || user?.user_role;
  const canEditTeam = role === 'admin' || role === 'manager' || (typeof role === 'string' && role.endsWith('_manager'));
  const [editingTeamMember, setEditingTeamMember] = useState(null); // the tb row being edited

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">LeaveBoard</h1>
          <p className="text-gray-600 mt-1">Leave management & absence calendar</p>
        </div>
        <button onClick={onRequestLeave} className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 font-medium">+ Request Leave</button>
      </div>

      {/* Hire date — every user can set this here so their probation + Time Off allowance is computed correctly */}
      <HireDateInlineEditor
        hireDate={data?.myHireDate || null}
        pastProbation={data?.pastProbation}
        daysUntilProbation={data?.daysUntilProbation}
        onSaved={onRefresh}
      />

      {/* Weekly schedule — used to compute leave hours from per-day working hours */}
      <ScheduleInlineEditor
        schedule={data?.mySchedule}
        onSaved={onRefresh}
      />

      {/* Leave Balances */}
      <h2 className="text-xl font-semibold text-gray-900 mb-4">My Leave Balance</h2>
      <LeaveBalanceCards
        balance={data?.myBalance || {}}
        pastProbation={data?.pastProbation}
        daysUntilProbation={data?.daysUntilProbation}
        policy={data?.policy}
      />

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Leave Requests */}
        <div className="lg:col-span-2">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Leave Requests</h2>
          {(!data?.requests || data.requests.length === 0) ? (
            <div className="orbit-card p-8 text-center text-gray-400">No leave requests found.</div>
          ) : (
            <div className="orbit-card divide-y divide-gray-100 overflow-hidden">
              {data.requests.map(req => (
                <LeaveRequestRow key={req.id} req={req} user={user}
                  onApproveRequest={onApproveRequest} onRejectRequest={onRejectRequest} onDeleteRequest={onDeleteRequest}
                  formatDate={formatDate} />
              ))}
            </div>
          )}

          {/* Team Balances — visible to admin/manager (all members) and *_manager roles (their team) */}
          {data?.teamScope?.canSeeTeam && (
            <>
              <div className="flex items-baseline justify-between mt-6 mb-4 flex-wrap gap-2">
                <h2 className="text-xl font-semibold text-gray-900">
                  Team Balances <span className="text-xs font-normal text-gray-400">(hours remaining)</span>
                </h2>
                {data?.teamScope?.label && (
                  <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-indigo-50 text-indigo-700 border border-indigo-100">
                    {data.teamScope.label}
                  </span>
                )}
              </div>
              <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Team Member</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600">Time Off</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600">Sick</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600">Hire date / Status</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600">Schedule</th>
                      {canEditTeam && <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Action</th>}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {(data?.teamBalances || []).length === 0 ? (
                      <tr><td colSpan={canEditTeam ? 6 : 5} className="px-4 py-6 text-center text-sm text-gray-400 italic">No team members found</td></tr>
                    ) : (data?.teamBalances || []).map(tb => {
                      const timeoff = tb.timeoffRemaining ?? (((tb.vacationRemaining || 0) + (tb.personalRemaining || 0)) * 8);
                      const sick    = tb.sickRemaining    ?? ((tb.legacySickRemaining || 0) * 8);
                      const onProbation = tb.pastProbation === false;
                      const weeklyHours = tb.weeklyHours ?? 40;
                      return (
                        <tr key={tb.userId || tb.userName} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm font-medium text-gray-900">{tb.userName}</td>
                          <td className={`px-4 py-3 text-center text-sm font-semibold ${onProbation ? 'text-gray-400' : 'text-indigo-700'}`}>
                            {timeoff}h
                            {onProbation && <span className="ml-1 text-[10px] uppercase tracking-wider font-bold text-amber-700 bg-amber-100 px-1 py-0.5 rounded">Locked</span>}
                          </td>
                          <td className="px-4 py-3 text-center text-sm text-red-600 font-semibold">{sick}h</td>
                          <td className="px-4 py-3 text-center text-xs">
                            {tb.hireDate ? (
                              <div className="flex flex-col items-center gap-0.5">
                                <span className="text-gray-700">{new Date(tb.hireDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                                {tb.pastProbation
                                  ? <span className="text-emerald-700 font-medium">✓ Probation passed</span>
                                  : <span className="text-amber-700 font-medium">⏳ On probation</span>}
                              </div>
                            ) : <span className="text-gray-400 italic">Hire date not set</span>}
                          </td>
                          <td className="px-4 py-3 text-center text-xs text-gray-700">{weeklyHours}h / week</td>
                          {canEditTeam && (
                            <td className="px-4 py-3 text-right">
                              <button onClick={() => setEditingTeamMember(tb)}
                                className="px-2.5 py-1 text-xs text-indigo-600 hover:text-indigo-800 hover:underline font-medium">
                                Edit profile
                              </button>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        {/* Calendar */}
        <div>
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Team Calendar</h2>
          <TeamAbsenceCalendar upcomingLeave={data?.upcomingLeave} />
        </div>
      </div>

      {/* Manager: edit a team member's profile (hire date + schedule) */}
      {editingTeamMember && (
        <TeamMemberProfileModal
          member={editingTeamMember}
          onClose={() => setEditingTeamMember(null)}
          onSaved={async () => { setEditingTeamMember(null); if (onRefresh) await onRefresh(); }}
        />
      )}
    </div>
  );
};

const TeamMemberProfileModal = ({ member, onClose, onSaved }) => {
  const [hireDate, setHireDate] = useState(member.hireDate || '');
  const [schedule, setSchedule] = useState(member.schedule && Object.keys(member.schedule).length
    ? { ...member.schedule }
    : { ...DEFAULT_SCHEDULE_CLIENT });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const setDay = (key, val) => {
    const num = val === '' ? 0 : Number(val);
    if (!Number.isFinite(num) || num < 0 || num > 24) return;
    setSchedule({ ...schedule, [key]: num });
  };

  const weeklyTotal = Math.round(DAYS_OF_WEEK.reduce((s, d) => s + Number(schedule[d.key] || 0), 0) * 10) / 10;
  const initialHireDate = member.hireDate || '';
  const initialScheduleJson = JSON.stringify(member.schedule || DEFAULT_SCHEDULE_CLIENT);
  const currentScheduleJson = JSON.stringify(schedule);
  const dirty = hireDate !== initialHireDate || currentScheduleJson !== initialScheduleJson;

  const probationLabel = (() => {
    if (!hireDate) return { tone: 'gray', text: 'No hire date set' };
    const hire = new Date(hireDate + 'T00:00:00');
    if (isNaN(hire.getTime())) return { tone: 'amber', text: 'Invalid date' };
    const days = Math.floor((Date.now() - hire.getTime()) / 86400000);
    if (days >= 90) return { tone: 'emerald', text: `Probation passed (${days} days)` };
    return { tone: 'amber', text: `On probation — ${90 - days} day${90 - days === 1 ? '' : 's'} left` };
  })();
  const tone = {
    gray:    { bg: 'bg-gray-50',    border: 'border-gray-200',    text: 'text-gray-600',    dot: 'bg-gray-400' },
    amber:   { bg: 'bg-amber-50',   border: 'border-amber-200',   text: 'text-amber-700',   dot: 'bg-amber-500' },
    emerald: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', dot: 'bg-emerald-500' },
  }[probationLabel.tone];

  const handleSave = async () => {
    setSaving(true); setErr('');
    const payload = {};
    if (hireDate !== initialHireDate) payload.hireDate = hireDate || null;
    if (currentScheduleJson !== initialScheduleJson) payload.schedule = schedule;
    if (Object.keys(payload).length === 0) { setSaving(false); onClose(); return; }
    const r = await ModulesModel.adminUpdateUserProfile(member.userId, payload);
    setSaving(false);
    if (r?.success) { if (onSaved) await onSaved(); }
    else setErr(r?.error || 'Could not save changes');
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Edit profile</h2>
            <p className="text-xs text-gray-500 mt-0.5">{member.userName}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">×</button>
        </div>

        <div className="px-6 py-5 space-y-5 max-h-[70vh] overflow-y-auto">
          {/* Hire date */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Hire date</label>
            <input type="date" value={hireDate || ''}
              max={new Date().toISOString().slice(0, 10)}
              onChange={e => setHireDate(e.target.value)}
              className="w-full sm:w-64 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
            <div className={`mt-2 inline-flex items-center gap-2 px-2.5 py-1 rounded-full text-xs font-medium border ${tone.bg} ${tone.border} ${tone.text}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${tone.dot}`} />
              {probationLabel.text}
            </div>
          </div>

          {/* Schedule */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Weekly schedule</label>
            <p className="text-xs text-gray-500 mb-3">Hours worked per day. 0 means non-working.</p>
            <div className="grid grid-cols-7 gap-1.5 mb-2">
              {DAYS_OF_WEEK.map(d => {
                const v = schedule[d.key] ?? 0;
                const isWorking = v > 0;
                return (
                  <div key={d.key} className="text-center">
                    <label className={`block text-[10px] font-semibold uppercase tracking-wider mb-1 ${isWorking ? 'text-gray-700' : 'text-gray-400'}`}>{d.label}</label>
                    <input type="number" step="0.5" min="0" max="24" value={v}
                      onChange={e => setDay(d.key, e.target.value)}
                      className={`w-full text-center border rounded-lg px-1 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 ${isWorking ? 'border-gray-300 text-gray-900 bg-white' : 'border-gray-200 text-gray-400 bg-gray-50'}`} />
                  </div>
                );
              })}
            </div>
            <div className="text-xs text-gray-500">
              Weekly total: <span className="font-semibold text-gray-800">{weeklyTotal}h</span>
            </div>
          </div>

          {err && <div className="text-xs text-red-600">{err}</div>}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2 bg-gray-50/50">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 font-medium">Cancel</button>
          <button onClick={handleSave} disabled={saving || !dirty}
            className="px-5 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 font-medium shadow-sm">
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// MEETINGBOARD MODULE VIEWS
// ============================================================================

const getMeetingStatusBadge = (status) => {
  const badges = {
    scheduled: { color: '#0366d6', bg: '#d4e4ff', label: 'Scheduled' },
    ongoing:   { color: '#28a745', bg: '#dcffe4', label: 'Live' },
    ended:     { color: '#586069', bg: '#f6f8fa', label: 'Ended' },
    cancelled: { color: '#dc3545', bg: '#ffe5e9', label: 'Cancelled' },
  };
  return badges[status] || badges.scheduled;
};

const ScheduleMeetingModal = ({ onClose, onSubmit, users = [] }) => {
  const now = new Date();
  const localIso = new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
  const [form, setForm] = useState({
    title: '', description: '', scheduledAt: localIso,
    durationMinutes: 60, meetingType: 'scheduled', participants: [], agenda: ''
  });
  const [errors, setErrors] = useState({});

  const toggleParticipant = (u) => {
    setForm(f => {
      const exists = f.participants.find(p => p.userId === u.user_id);
      return { ...f, participants: exists
        ? f.participants.filter(p => p.userId !== u.user_id)
        : [...f.participants, { userId: u.user_id, name: u.user_name }]
      };
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.title.trim()) errs.title = 'Title is required';
    if (Object.keys(errs).length) { setErrors(errs); return; }
    onSubmit(form);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white">
          <h2 className="text-xl font-semibold">Schedule Meeting</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Meeting Type</label>
            <div className="flex gap-3">
              {[['scheduled', '📅 Scheduled'], ['instant', '⚡ Start Now']].map(([val, label]) => (
                <button key={val} type="button"
                  onClick={() => setForm(f => ({ ...f, meetingType: val }))}
                  className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-colors ${form.meetingType === val ? 'bg-blue-600 text-white border-blue-600' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}>
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title <span className="text-red-500">*</span></label>
            <input type="text" value={form.title} onChange={e => { setForm(f => ({ ...f, title: e.target.value })); setErrors(ev => ({ ...ev, title: '' })); }}
              placeholder="e.g. Weekly Standup"
              className={`w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${errors.title ? 'border-red-400' : 'border-gray-300'}`} />
            {errors.title && <p className="text-xs text-red-500 mt-1">{errors.title}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={2}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
          </div>
          {form.meetingType === 'scheduled' && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date & Time</label>
                <input type="datetime-local" value={form.scheduledAt} onChange={e => setForm(f => ({ ...f, scheduledAt: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Duration</label>
                <select value={form.durationMinutes} onChange={e => setForm(f => ({ ...f, durationMinutes: parseInt(e.target.value) }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                  {[15, 30, 45, 60, 90, 120].map(m => <option key={m} value={m}>{m < 60 ? `${m} min` : `${m/60}h`}</option>)}
                </select>
              </div>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Agenda</label>
            <MiniWysiwyg value={form.agenda} onChange={v => setForm(f => ({ ...f, agenda: v }))}
              placeholder="Meeting agenda or topics..." minHeight="70px" />
          </div>
          {users.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Invite Participants</label>
              <div className="border border-gray-200 rounded-lg p-3 max-h-40 overflow-y-auto space-y-1">
                {users.map(u => {
                  const checked = !!form.participants.find(p => p.userId === u.user_id);
                  return (
                    <label key={u.user_id} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded">
                      <input type="checkbox" checked={checked} onChange={() => toggleParticipant(u)} className="rounded" />
                      <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-bold shrink-0">
                        {(u.user_name || '?').charAt(0).toUpperCase()}
                      </div>
                      <span className="text-sm text-gray-700">{u.user_name}</span>
                      {u.is_active ? <span className="w-2 h-2 rounded-full bg-green-500 ml-auto shrink-0" /> : <span className="w-2 h-2 rounded-full bg-gray-300 ml-auto shrink-0" />}
                    </label>
                  );
                })}
              </div>
              {form.participants.length > 0 && (
                <p className="text-xs text-blue-600 mt-1">{form.participants.length} participant{form.participants.length > 1 ? 's' : ''} selected</p>
              )}
            </div>
          )}
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
            <button type="submit" className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">
              {form.meetingType === 'instant' ? '⚡ Start Meeting' : '📅 Schedule'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const MeetingNotesModal = ({ meeting, user, onClose, onSaved }) => {
  const [summary, setSummary] = React.useState(meeting.notesSummary || '');
  const [actionItems, setActionItems] = React.useState(meeting.notesActionItems || '');
  const [keyDecisions, setKeyDecisions] = React.useState(meeting.notesKeyDecisions || '');
  const [saving, setSaving] = React.useState(false);
  const [saved, setSaved] = React.useState(false);
  const editorKey = meeting.id;

  const handleSave = async () => {
    setSaving(true);
    await ModulesModel.saveMeetingNotes(meeting.id, { summary, actionItems, keyDecisions });
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
    onSaved?.({ ...meeting, notesSummary: summary, notesActionItems: actionItems, notesKeyDecisions: keyDecisions, notesUpdatedBy: user?.user_name });
  };

  const fmtDate = (iso) => iso ? new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';

  const sanitize = sanitizeHtml;

  const NoteEditor = ({ label, value, onChange, edKey, placeholder }) => {
    const ref = React.useRef(null);
    const mountedRef = React.useRef(false);
    const [empty, setEmpty] = React.useState(!value);
    React.useEffect(() => {
      if (ref.current && !mountedRef.current) {
        mountedRef.current = true;
        if (value) { ref.current.innerHTML = value; setEmpty(false); }
      }
    }, []);
    const sync = () => {
      const html = ref.current?.innerHTML || '';
      const isEmpty = !html || html === '<br>' || html === '<div><br></div>';
      setEmpty(isEmpty);
      onChange(isEmpty ? '' : html);
    };
    const exec = (cmd) => { document.execCommand(cmd, false, null); ref.current?.focus(); sync(); };
    const tbtn = (cmd, content, title) => (
      <button type="button" title={title} key={cmd} onMouseDown={e => { e.preventDefault(); exec(cmd); }}
        className="px-2 py-1 text-xs rounded hover:bg-gray-200 text-gray-600 select-none">{content}</button>
    );
    return (
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-1">{label}</label>
        <div className="border border-gray-300 rounded-xl overflow-hidden focus-within:ring-2 focus-within:ring-blue-400 bg-white">
          <div className="flex items-center gap-0.5 px-2 py-1 bg-gray-50 border-b border-gray-200 flex-wrap">
            {tbtn('bold', <strong>B</strong>, 'Bold')}
            {tbtn('italic', <em>I</em>, 'Italic')}
            {tbtn('underline', <u>U</u>, 'Underline')}
            <div className="w-px h-3.5 bg-gray-300 mx-0.5" />
            {tbtn('insertUnorderedList', '• List', 'Bullet list')}
            {tbtn('insertOrderedList', '1. List', 'Numbered list')}
            <div className="w-px h-3.5 bg-gray-300 mx-0.5" />
            {tbtn('removeFormat', 'Aa✕', 'Clear')}
          </div>
          <div className="relative">
            {empty && <div className="absolute top-2 left-3 text-sm text-gray-400 pointer-events-none select-none">{placeholder}</div>}
            <div ref={ref} contentEditable onInput={sync}
              onPaste={e => { e.preventDefault(); document.execCommand('insertText', false, e.clipboardData.getData('text/plain')); }}
              className="min-h-[80px] max-h-52 overflow-y-auto px-3 py-2 text-sm text-gray-800 focus:outline-none prose prose-sm max-w-none [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5"
              suppressContentEditableWarning />
          </div>
        </div>
      </div>
    );
  };

  const hasNotes = meeting.notesSummary || meeting.notesActionItems || meeting.notesKeyDecisions;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-lg">📝</span>
              <h2 className="text-lg font-semibold text-gray-900 truncate">Meeting Notes</h2>
            </div>
            <p className="text-sm text-gray-500 mt-0.5 truncate">{meeting.title}</p>
            <p className="text-xs text-gray-400 mt-0.5">📅 {fmtDate(meeting.scheduledAt)} · 👤 {meeting.hostName}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 shrink-0 text-xl">✕</button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {meeting.notesUpdatedBy && (
            <div className="text-xs text-gray-400 bg-gray-50 rounded-lg px-3 py-2">
              Last updated by <span className="font-medium text-gray-600">{meeting.notesUpdatedBy}</span>
              {meeting.notesUpdatedAt && <> · {fmtDate(meeting.notesUpdatedAt)}</>}
            </div>
          )}
          <NoteEditor key={`sum-${editorKey}`} label="📋 Summary" value={summary} onChange={setSummary} placeholder="Summarize what was discussed…" />
          <NoteEditor key={`act-${editorKey}`} label="✅ Action Items" value={actionItems} onChange={setActionItems} placeholder="List action items, owners, deadlines…" />
          <NoteEditor key={`dec-${editorKey}`} label="🔑 Key Decisions" value={keyDecisions} onChange={setKeyDecisions} placeholder="Record important decisions made…" />
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between gap-3">
          <span className="text-xs text-gray-400">Notes are visible to all participants</span>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">Close</button>
            <button onClick={handleSave} disabled={saving}
              className={`px-5 py-2 text-sm rounded-lg font-medium text-white transition-colors ${saved ? 'bg-green-600' : 'bg-blue-600 hover:bg-blue-700'} disabled:opacity-50`}>
              {saving ? 'Saving…' : saved ? '✓ Saved' : 'Save Notes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const MeetingCard = ({ meeting: initialMeeting, user, onEnd, onStart, onCancel, onDelete }) => {
  const [meeting, setMeeting] = React.useState(initialMeeting);
  const [showNotes, setShowNotes] = React.useState(false);
  const badge = getMeetingStatusBadge(meeting.status);
  const isHost = Number(meeting.hostId) === Number(user?.id);
  const isOngoing = meeting.status === 'ongoing';
  const isScheduled = meeting.status === 'scheduled';
  const isPast = meeting.status === 'ended' || meeting.status === 'cancelled';
  const joinUrl = `https://meet.jit.si/${meeting.roomCode}`;
  const hasNotes = meeting.notesSummary || meeting.notesActionItems || meeting.notesKeyDecisions;

  const fmtDate = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const sanitize = sanitizeHtml;

  return (
    <>
    {showNotes && (
      <MeetingNotesModal meeting={meeting} user={user}
        onClose={() => setShowNotes(false)}
        onSaved={(updated) => setMeeting(updated)} />
    )}
    <div className={`bg-white rounded-lg shadow border p-4 ${isOngoing ? 'border-green-400 ring-1 ring-green-300' : 'border-gray-200'}`}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-gray-900 text-sm">{meeting.title}</span>
            <span className="px-2 py-0.5 rounded text-xs font-semibold" style={{ background: badge.bg, color: badge.color }}>{badge.label}</span>
            {isOngoing && <span className="flex items-center gap-1 text-xs text-green-600 font-medium animate-pulse">● Live</span>}
            {hasNotes && <span className="px-2 py-0.5 rounded text-xs bg-blue-50 text-blue-600 font-medium">📝 Notes</span>}
          </div>
          {meeting.description && <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{meeting.description}</p>}
        </div>
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500 mb-3">
        <span>👤 {meeting.hostName}</span>
        <span>📅 {fmtDate(meeting.scheduledAt)}</span>
        <span>⏱ {meeting.durationMinutes}m</span>
        {meeting.participants?.length > 0 && <span>👥 {meeting.participants.length} invited</span>}
      </div>
      {meeting.agenda && (
        <div className="text-xs text-gray-500 bg-gray-50 rounded p-2 mb-3 leading-relaxed line-clamp-3 prose prose-sm max-w-none [&_ul]:list-disc [&_ul]:pl-4 [&_ol]:list-decimal [&_ol]:pl-4 [&_p]:m-0">
          <span className="font-medium text-gray-700">Agenda: </span>
          <span dangerouslySetInnerHTML={{ __html: sanitize(meeting.agenda) }} />
        </div>
      )}
      {!isPast && (
        <div className="flex flex-wrap gap-2">
          <a href={joinUrl} target="_blank" rel="noreferrer"
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold text-white flex items-center gap-1.5 ${isOngoing ? 'bg-green-600 hover:bg-green-700' : 'bg-blue-600 hover:bg-blue-700'}`}>
            📹 {isOngoing ? 'Join Now' : 'Join'}
          </a>
          <button onClick={() => navigator.clipboard?.writeText(joinUrl)}
            className="px-3 py-1.5 rounded-lg text-xs font-medium border border-gray-300 text-gray-600 hover:bg-gray-50">
            🔗 Copy Link
          </button>
          {isHost && isScheduled && (
            <button onClick={() => onStart(meeting.id)}
              className="px-3 py-1.5 rounded-lg text-xs font-medium bg-green-100 text-green-700 hover:bg-green-200">
              ▶ Start
            </button>
          )}
          {isHost && isOngoing && (
            <button onClick={() => onEnd(meeting.id)}
              className="px-3 py-1.5 rounded-lg text-xs font-medium bg-gray-100 text-gray-600 hover:bg-gray-200">
              ■ End
            </button>
          )}
          {isHost && (
            <button onClick={() => onCancel(meeting.id)}
              className="px-3 py-1.5 rounded-lg text-xs font-medium text-red-500 hover:bg-red-50">
              Cancel
            </button>
          )}
          {isHost && (
            <button onClick={() => { if (window.confirm('Delete this meeting permanently?')) onDelete(meeting.id); }}
              className="px-3 py-1.5 rounded-lg text-xs font-medium text-red-400 hover:bg-red-50 hover:text-red-600">
              🗑 Delete
            </button>
          )}
          <button onClick={() => setShowNotes(true)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1 ${hasNotes ? 'bg-blue-100 text-blue-700 hover:bg-blue-200' : 'border border-gray-300 text-gray-600 hover:bg-gray-50'}`}>
            📝 {hasNotes ? 'View Notes' : 'Take Notes'}
          </button>
        </div>
      )}
      {isPast && (
        <div className="flex items-center gap-2 mt-1">
          {isHost && <button onClick={() => { if (window.confirm('Delete this meeting permanently?')) onDelete(meeting.id); }} className="text-xs text-gray-400 hover:text-red-500">🗑 Delete</button>}
          <button onClick={() => setShowNotes(true)}
            className={`text-xs font-medium flex items-center gap-1 ${hasNotes ? 'text-blue-600 hover:text-blue-700' : 'text-gray-500 hover:text-gray-700'}`}>
            📝 {hasNotes ? 'View Notes' : 'Take Notes'}
          </button>
        </div>
      )}
      {hasNotes && isPast && (
        <div className="mt-3 pt-3 border-t border-gray-100 space-y-2">
          {meeting.notesSummary && (
            <div>
              <p className="text-xs font-semibold text-gray-500 mb-0.5">📋 Summary</p>
              <div className="text-xs text-gray-700 prose prose-sm max-w-none line-clamp-3" dangerouslySetInnerHTML={{ __html: sanitize(meeting.notesSummary) }} />
            </div>
          )}
          {meeting.notesActionItems && (
            <div>
              <p className="text-xs font-semibold text-gray-500 mb-0.5">✅ Action Items</p>
              <div className="text-xs text-gray-700 prose prose-sm max-w-none line-clamp-2" dangerouslySetInnerHTML={{ __html: sanitize(meeting.notesActionItems) }} />
            </div>
          )}
        </div>
      )}
    </div>
    </>
  );
};

const MeetingBoardModule = ({ data, user, onSchedule, onStart, onEnd, onCancel, onDelete }) => {
  const [tab, setTab] = useState('upcoming');
  const stats = data?.stats || {};
  const statCards = [
    { label: 'Total', value: stats.total || 0, icon: '📹', color: '#0ea5e9' },
    { label: 'Upcoming', value: stats.upcoming || 0, icon: '📅', color: '#6f42c1' },
    { label: 'Completed', value: stats.completed || 0, icon: '✅', color: '#28a745' },
    { label: 'Cancelled', value: stats.cancelled || 0, icon: '❌', color: '#dc3545' },
  ];

  const meetings = tab === 'upcoming' ? (data?.upcoming || []) : (data?.past || []);

  return (
    <div className="max-w-5xl mx-auto px-4 py-6">
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">MeetingBoard</h1>
          <p className="text-gray-600 mt-1">Video conferencing & team huddles powered by Jitsi Meet</p>
        </div>
        <button onClick={onSchedule} className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 font-medium whitespace-nowrap">
          + New Meeting
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        {statCards.map(c => (
          <div key={c.label} className="bg-white rounded-lg shadow border border-gray-200 p-4 text-center">
            <div className="text-2xl mb-1">{c.icon}</div>
            <div className="text-2xl font-bold" style={{ color: c.color }}>{c.value}</div>
            <div className="text-xs text-gray-500 mt-0.5">{c.label}</div>
          </div>
        ))}
      </div>

      {/* Info banner */}
      <div className="bg-sky-50 border border-sky-200 rounded-lg p-3 mb-6 flex items-start gap-3 text-sm text-sky-800">
        <span className="text-xl shrink-0">📹</span>
        <div>
          <span className="font-semibold">Powered by Jitsi Meet — </span>
          free, browser-based video conferencing. No account or install needed. Clicking <strong>Join</strong> opens the meeting in a new tab.
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-4 bg-gray-100 rounded-lg p-1 w-fit">
        {[['upcoming', 'Upcoming & Live'], ['past', 'Past Meetings']].map(([val, label]) => (
          <button key={val} onClick={() => setTab(val)}
            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${tab === val ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
            {label} {val === 'upcoming' && data?.upcoming?.length > 0 && <span className="ml-1 px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs">{data.upcoming.length}</span>}
          </button>
        ))}
      </div>

      {/* Meeting list */}
      {meetings.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <div className="text-5xl mb-3">📹</div>
          <p className="text-lg font-medium">{tab === 'upcoming' ? 'No upcoming meetings' : 'No past meetings'}</p>
          {tab === 'upcoming' && <p className="text-sm mt-1">Schedule a meeting or start an instant huddle</p>}
        </div>
      ) : (
        <div className="space-y-3">
          {meetings.map(m => (
            <MeetingCard key={m.id} meeting={m} user={user}
              onStart={onStart} onEnd={onEnd} onCancel={onCancel} onDelete={onDelete} />
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// DATAOPS MODULE VIEWS
// ============================================================================

const getDataOpsPipelineStatusBadge = (status) => {
  const badges = {
    running: { bg: '#d4e4ff', color: '#0366d6', label: 'Running' },
    idle: { bg: '#f6f8fa', color: '#586069', label: 'Idle' },
    queued: { bg: '#f3e8ff', color: '#6f42c1', label: 'Queued' },
    completed: { bg: '#dcffe4', color: '#28a745', label: 'Completed' },
    failed: { bg: '#ffe5e9', color: '#dc3545', label: 'Failed' }
  };
  return badges[status] || badges.idle;
};

const formatPipelineDuration = (seconds) => {
  if (!seconds) return '-';
  const hours = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
};

const formatDataOpsDateTime = (dateStr) => {
  if (!dateStr) return 'Never';
  return new Date(dateStr).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

const DataOpsMetricsCards = ({ metrics }) => {
  const cards = [
    { label: 'Active Pipelines', value: metrics.activePipelines, icon: '🔧', color: '#6f42c1' },
    { label: 'Data Processed', value: metrics.dataProcessed, icon: '💾', color: '#17a2b8' },
    { label: 'Reports Generated', value: metrics.reportsGenerated, icon: '📊', color: '#28a745' },
    { label: 'Success Rate', value: metrics.successRate, icon: '✓', color: '#20c997' }
  ];
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {cards.map(card => (
        <div key={card.label} className="bg-white rounded-lg shadow border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-600">{card.label}</span>
            <span className="text-2xl">{card.icon}</span>
          </div>
          <div className="text-2xl font-bold" style={{ color: card.color }}>{card.value}</div>
        </div>
      ))}
    </div>
  );
};

const DataOpsActivePipelines = ({ pipelines, onTrigger }) => (
  <div className="bg-white rounded-lg shadow border border-gray-200">
    <div className="px-5 py-4 border-b border-gray-200">
      <h2 className="text-lg font-semibold text-gray-900">Active Pipelines</h2>
    </div>
    <div className="p-5 space-y-3">
      {(!pipelines || pipelines.length === 0) ? (
        <div className="text-center text-gray-400 py-8">No active pipelines</div>
      ) : pipelines.map((p, i) => {
        const badge = getDataOpsPipelineStatusBadge(p.status);
        return (
          <div key={i} className="border border-gray-200 rounded-lg p-3">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className="font-medium text-sm text-gray-900">{p.name}</span>
                <span className="px-2 py-0.5 rounded text-xs font-semibold" style={{ background: badge.bg, color: badge.color }}>{badge.label}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500">{p.type}</span>
                {onTrigger && p.status !== 'running' && (
                  <button onClick={() => onTrigger(p.pipelineId)} className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded hover:bg-blue-200">▶</button>
                )}
              </div>
            </div>
            {p.progress !== undefined && (
              <>
                <div className="flex justify-between text-xs text-gray-600 mb-1">
                  <span>Progress</span><span>{p.progress}%</span>
                </div>
                <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden mb-2">
                  <div className="h-full rounded-full transition-all" style={{ width: `${p.progress}%`, background: badge.color }} />
                </div>
              </>
            )}
            <div className="text-xs text-gray-500">{p.records?.toLocaleString()} records</div>
          </div>
        );
      })}
    </div>
  </div>
);

const DataOpsPowerBIReports = ({ reports }) => (
  <div className="bg-white rounded-lg shadow border border-gray-200">
    <div className="px-5 py-4 border-b border-gray-200">
      <h2 className="text-lg font-semibold text-gray-900">Power BI Reports</h2>
    </div>
    <div className="p-5 space-y-3">
      {(!reports || reports.length === 0) ? (
        <div className="text-center text-gray-400 py-8">No reports available</div>
      ) : reports.map((r, i) => (
        <div key={i} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
          <div className="flex items-center gap-3">
            <span className="text-2xl">{r.icon}</span>
            <div>
              <div className="font-medium text-sm text-gray-900">{r.title}</div>
              <div className="text-xs text-gray-500">Updated {r.lastUpdated}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">👁 {r.views}</span>
            <span className={`px-2 py-0.5 rounded text-xs font-semibold ${r.status === 'Published' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>{r.status}</span>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const DataOpsPipelinesView = ({ pipelines, onAdd, onPipelineClick, onTrigger }) => (
  <div>
    <div className="flex justify-between items-center mb-4">
      <h2 className="text-lg font-semibold text-gray-900">All Pipelines</h2>
      <button onClick={onAdd} className="px-4 py-2 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 font-medium">+ New Pipeline</button>
    </div>
    <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
      <table className="w-full">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Pipeline</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Type</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Status</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Last Run</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Duration</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Records</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Progress</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {(pipelines || []).map(p => {
            const badge = getDataOpsPipelineStatusBadge(p.status);
            return (
              <tr key={p.pipelineId} className="hover:bg-gray-50 cursor-pointer" onClick={() => onPipelineClick(p.pipelineId)}>
                <td className="px-4 py-3">
                  <div className="text-sm font-medium text-gray-900">{p.pipelineName}</div>
                  <div className="text-xs font-mono text-gray-500">{p.pipelineId}</div>
                </td>
                <td className="px-4 py-3 text-sm text-gray-600">{p.pipelineType}</td>
                <td className="px-4 py-3">
                  <span className="px-2 py-0.5 rounded text-xs font-semibold" style={{ background: badge.bg, color: badge.color }}>{badge.label}</span>
                </td>
                <td className="px-4 py-3 text-xs text-gray-600">{formatDataOpsDateTime(p.lastRun)}</td>
                <td className="px-4 py-3 text-xs text-gray-600">{formatPipelineDuration(p.durationSeconds)}</td>
                <td className="px-4 py-3 text-xs text-gray-600">{p.recordsProcessed?.toLocaleString() || 0}</td>
                <td className="px-4 py-3">
                  {p.progress != null ? (
                    <div className="flex items-center gap-2">
                      <div className="w-16 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${p.progress}%`, background: badge.color }} />
                      </div>
                      <span className="text-xs text-gray-500">{p.progress}%</span>
                    </div>
                  ) : '-'}
                </td>
                <td className="px-4 py-3">
                  <button onClick={(e) => { e.stopPropagation(); onTrigger(p.pipelineId); }}
                    disabled={p.status === 'running'}
                    className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded hover:bg-blue-200 disabled:opacity-50 disabled:cursor-not-allowed">
                    ▶ Run
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {(!pipelines || pipelines.length === 0) && (
        <div className="p-8 text-center text-gray-400">No pipelines found. Create your first pipeline to get started.</div>
      )}
    </div>
  </div>
);

const DataOpsReportsView = ({ reports, onAdd, onReportClick, onToggleFavorite }) => (
  <div>
    <div className="flex justify-between items-center mb-4">
      <h2 className="text-lg font-semibold text-gray-900">Power BI Reports</h2>
      <button onClick={onAdd} className="px-4 py-2 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 font-medium">+ New Report</button>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {(reports || []).map(r => {
        const published = r.status === 'Published';
        return (
          <div key={r.reportId} className="bg-white rounded-lg shadow border border-gray-200 p-4 hover:shadow-lg transition-shadow cursor-pointer" onClick={() => onReportClick(r.reportId)}>
            <div className="flex items-start justify-between mb-3">
              <span className="text-3xl">{r.reportIcon}</span>
              <button onClick={(e) => { e.stopPropagation(); onToggleFavorite(r.reportId, !r.isFavorite); }} className="text-xl">{r.isFavorite ? '⭐' : '☆'}</button>
            </div>
            <h3 className="text-base font-semibold text-gray-900 mb-1">{r.reportName}</h3>
            <div className="flex items-center gap-2 mb-2">
              <span className={`px-2 py-0.5 rounded text-xs font-semibold ${published ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>{r.status}</span>
              {r.category && <span className="text-xs text-gray-500">{r.category}</span>}
            </div>
            <div className="flex items-center justify-between text-xs text-gray-500">
              <span>👁 {r.viewCount} views</span>
              <span>{formatDataOpsDateTime(r.lastUpdated)}</span>
            </div>
          </div>
        );
      })}
    </div>
    {(!reports || reports.length === 0) && (
      <div className="bg-white rounded-lg shadow border border-gray-200 p-12 text-center text-gray-400">No reports found.</div>
    )}
  </div>
);

// ---- DataOps Kanban Board (QuickTrack tasks for DataOps team) ----
const DataOpsKanbanCard = ({ task, onStatusChange, onDelete, canDelete, onEdit }) => {
  const [showActualTime, setShowActualTime] = useState(false);
  const priorityColors = { urgent: { bg: '#fef2f2', color: '#dc2626', label: 'Urgent' }, high: { bg: '#fff7ed', color: '#ea580c', label: 'High' }, medium: { bg: '#fefce8', color: '#ca8a04', label: 'Medium' }, low: { bg: '#f0fdf4', color: '#16a34a', label: 'Low' } };
  const p = priorityColors[task.priority] || priorityColors.medium;
  const isOverdue = task.status !== 'done' && task.dueDate && task.dueDate < new Date().toISOString().split('T')[0];
  const nextStatus = { open: 'in_progress', in_progress: 'done', done: 'open' };
  const nextLabel = { open: '▶ Start', in_progress: '✓ Done', done: '↺ Reopen' };
  const handleStatusClick = () => {
    if (nextStatus[task.status] === 'done') { setShowActualTime(true); return; }
    onStatusChange(task.id, { status: nextStatus[task.status] });
  };
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-3 shadow-sm hover:shadow-md transition-shadow">
      {showActualTime && (
        <ActualTimeModal
          taskTitle={task.title}
          onConfirm={mins => { setShowActualTime(false); onStatusChange(task.id, { status: 'done', actualMinutes: mins }); }}
          onCancel={() => setShowActualTime(false)}
        />
      )}
      <div className="flex items-start justify-between gap-2 mb-2">
        <span className="text-xs font-semibold px-2 py-0.5 rounded" style={{ background: p.bg, color: p.color }}>{p.label}</span>
        <div className="flex items-center gap-1">
          {task.category && <span className="text-xs text-gray-400 capitalize">{task.category}</span>}
          {canDelete && (
            <button onClick={() => requestConfirm(`Delete "${task.title}"?`, () => onDelete(task.id))}
              className="text-gray-300 hover:text-red-500 text-base leading-none ml-1" title="Delete task">×</button>
          )}
        </div>
      </div>
      <p onClick={onEdit ? () => onEdit(task.id) : undefined}
        className={`text-sm font-medium text-gray-900 mb-1 leading-snug ${onEdit ? 'cursor-pointer hover:text-purple-700' : ''}`}>{task.title}</p>
      {task.description && <p className="text-xs text-gray-500 mb-2 truncate">{task.description}</p>}
      {task.projectName && <p className="text-xs text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded inline-block mb-2">{task.projectName}</p>}
      <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100">
        <div className="text-xs text-gray-500">
          {task.assignedTo && <span className="font-medium text-gray-700">{task.assignedTo}</span>}
          {task.dueDate && (
            <span className={`ml-2 ${isOverdue ? 'text-red-600 font-semibold' : 'text-gray-400'}`}>
              {isOverdue ? '⚠ ' : ''}{new Date(task.dueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            </span>
          )}
        </div>
        {onStatusChange && (
          <button onClick={handleStatusClick} className="text-xs text-purple-600 hover:text-purple-800 font-medium whitespace-nowrap">
            {nextLabel[task.status]}
          </button>
        )}
      </div>
    </div>
  );
};

const DataOpsKanbanBoard = ({ kanban, sprintBoard, onStatusChange, onDelete, userRole, onEdit, onRefresh }) => {
  const canDelete = userRole === 'admin' || userRole === 'dataops_manager' || userRole === 'manager';
  const canEdit = canDelete;
  const todayStr = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Los_Angeles' });

  const [source, setSource] = useState('all');
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [filterAssignee, setFilterAssignee] = useState('');
  const [filterProject, setFilterProject] = useState('');

  // Normalize QT tasks into flat list
  const qtTasks = [
    ...(kanban?.open || []).map(t => ({ ...t, _col: 'open', _source: 'quicktrack' })),
    ...(kanban?.in_progress || []).map(t => ({ ...t, _col: 'in_progress', _source: 'quicktrack' })),
    ...(kanban?.done || []).filter(t =>
      !t.completedAt || new Date(t.completedAt).toLocaleDateString('en-CA', { timeZone: 'America/Los_Angeles' }) === todayStr
    ).map(t => ({ ...t, _col: 'done', _source: 'quicktrack' })),
  ];

  // Normalize sprint tasks into flat list (map sprint cols to 3-col system)
  const sprintColMap = { backlog: 'open', ready: 'open', inProgress: 'in_progress', in_progress: 'in_progress', review: 'in_progress', done: 'done', completed: 'done' };
  const sprintTasks = Object.entries(sprintBoard || {}).flatMap(([key, items]) =>
    (items || []).map(t => ({
      id: t.id,
      title: t.title,
      description: t.description,
      priority: t.priority,
      status: t.status || key,
      assignedTo: t.assignee,
      projectName: t.projectName,
      estimatedMinutes: t.estimatedMinutes,
      actualMinutes: t.actualMinutes,
      isBug: t.isBug,
      bugCode: t.bugCode,
      severity: t.severity,
      _col: sprintColMap[key] || 'open',
      _source: 'sprint',
    }))
  );

  const allTasks = source === 'quicktrack' ? qtTasks : source === 'sprint' ? sprintTasks : [...qtTasks, ...sprintTasks];

  // Derive filter options from combined data
  const assignees = [...new Set(allTasks.map(t => t.assignedTo).filter(Boolean))].sort();
  const projects = [...new Set(allTasks.map(t => t.projectName).filter(Boolean))].sort();

  // Apply filters
  const filtered = allTasks.filter(t => {
    if (search && !t.title?.toLowerCase().includes(search.toLowerCase())) return false;
    if (filterStatus && t._col !== filterStatus) return false;
    if (filterPriority && t.priority !== filterPriority) return false;
    if (filterAssignee && t.assignedTo !== filterAssignee) return false;
    if (filterProject && t.projectName !== filterProject) return false;
    return true;
  });

  const clearFilters = () => { setSearch(''); setFilterStatus(''); setFilterPriority(''); setFilterAssignee(''); setFilterProject(''); };
  const hasFilter = search || filterStatus || filterPriority || filterAssignee || filterProject;

  const colDef = [
    { key: 'open', label: 'Open', color: 'bg-blue-500' },
    { key: 'in_progress', label: 'In Progress', color: 'bg-amber-500' },
    { key: 'done', label: 'Done', color: 'bg-green-500' },
  ];

  const selectCls = 'border border-gray-200 rounded-lg px-3 py-1.5 text-sm text-gray-700 bg-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500 cursor-pointer';

  return (
    <div>
      {/* Filter Bar */}
      <div className="bg-white border border-gray-200 rounded-xl px-4 py-3 mb-4 flex flex-wrap items-center gap-3">
        {/* Source toggle */}
        <div className="flex items-center gap-1">
          <span className="text-xs font-semibold text-gray-500 mr-1">Source</span>
          {[['all','All'],['sprint','Sprint'],['quicktrack','QuickTrack']].map(([val, lbl]) => (
            <button key={val} onClick={() => setSource(val)}
              className={`px-3 py-1.5 text-sm rounded-lg font-medium transition-colors ${source === val ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
              {lbl}
            </button>
          ))}
        </div>
        <div className="w-px h-6 bg-gray-200" />
        {/* Search */}
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search tasks..."
          className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm text-gray-700 bg-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500 w-44" />
        {/* Status */}
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className={selectCls}>
          <option value="">All Statuses</option>
          <option value="open">Open</option>
          <option value="in_progress">In Progress</option>
          <option value="done">Done</option>
        </select>
        {/* Priority */}
        <select value={filterPriority} onChange={e => setFilterPriority(e.target.value)} className={selectCls}>
          <option value="">All Priorities</option>
          <option value="P1">P1 – Critical</option>
          <option value="P2">P2 – High</option>
          <option value="P3">P3 – Normal</option>
          <option value="urgent">Urgent</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </select>
        {/* Assignee */}
        <select value={filterAssignee} onChange={e => setFilterAssignee(e.target.value)} className={selectCls}>
          <option value="">All Assignees</option>
          {assignees.map(a => <option key={a} value={a}>{a}</option>)}
        </select>
        {/* Project */}
        <select value={filterProject} onChange={e => setFilterProject(e.target.value)} className={selectCls}>
          <option value="">All Projects</option>
          {projects.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
        <div className="ml-auto flex items-center gap-2">
          {hasFilter && (
            <button onClick={clearFilters} className="px-3 py-1.5 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">Clear</button>
          )}
          {onRefresh && (
            <button onClick={onRefresh} className="px-3 py-1.5 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 flex items-center gap-1">
              <span className="text-base leading-none">↻</span> Refresh
            </button>
          )}
        </div>
      </div>

      {/* Board */}
      {filtered.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center text-gray-400">
          <div className="text-4xl mb-3">📋</div>
          <p className="text-sm font-medium">{hasFilter ? 'No tasks match your filters.' : 'No tasks for the DataOps team yet.'}</p>
          {!hasFilter && <p className="text-xs mt-1">Add a QuickTrack task and assign it to a DataOps team member.</p>}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {colDef.map(col => {
            const colTasks = filtered.filter(t => t._col === col.key);
            return (
              <div key={col.key} className="bg-gray-50 rounded-lg border border-gray-200">
                <div className="px-4 py-3 border-b border-gray-200 flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${col.color}`} />
                  <span className="font-semibold text-sm text-gray-800">{col.label}</span>
                  <span className="ml-auto text-xs bg-white border border-gray-200 text-gray-600 rounded-full px-2 py-0.5">{colTasks.length}</span>
                </div>
                <div className="p-3 space-y-2 min-h-32">
                  {colTasks.length === 0 ? (
                    <div className="text-center py-8 text-gray-400 text-xs">No tasks</div>
                  ) : (
                    colTasks.map(t => (
                      <div key={`${t._source}-${t.id}`} className="relative">
                        {t._source === 'sprint' && (
                          <span className="absolute top-2 right-2 text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded font-medium z-10">Sprint</span>
                        )}
                        <DataOpsKanbanCard task={t} onStatusChange={t._source === 'quicktrack' ? onStatusChange : undefined} onDelete={t._source === 'quicktrack' ? onDelete : undefined} canDelete={canDelete && t._source === 'quicktrack'} onEdit={canEdit ? onEdit : undefined} />
                      </div>
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const DataOpsModule = ({ data, viewMode, onViewModeChange, onAddPipeline, onAddReport, onPipelineClick, onReportClick, onTriggerPipeline, onToggleFavorite, onTaskClick, onStatusChange, onAddTask, onSendEod, onEodSettings, userRole, kanban, onKanbanStatusChange, onKanbanDelete, onRefresh }) => {
  const canManageEod = ['admin', 'manager', 'dataops_manager', 'devops_manager'].includes(userRole);
  const canEodSettings = ['admin', 'devops_manager'].includes(userRole);
  return (
  <div className="max-w-7xl mx-auto px-4 py-6">
    <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">DataOps</h1>
        <p className="text-gray-600 mt-1">Data pipeline orchestration & Power BI integration</p>
      </div>
      <div className="flex gap-2">
        {canEodSettings && <button onClick={onEodSettings} className="px-3 py-2 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200 font-medium border border-gray-300">⚙️ EOD Settings</button>}
        {canManageEod && <button onClick={onSendEod} className="px-3 py-2 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200 font-medium border border-gray-300">📤 Send EOD</button>}
        {viewMode === 'sprint' && onAddTask && (
          <button onClick={onAddTask} className="px-4 py-2 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 font-medium">+ New Task</button>
        )}
      </div>
    </div>
    <div className="flex gap-2 mb-6 flex-wrap">
      {['dashboard', 'sprint', 'kanban', 'pipelines', 'reports'].map(mode => (
        <button key={mode} onClick={() => onViewModeChange(mode)}
          className={`px-4 py-2 text-sm rounded-lg font-medium ${viewMode === mode ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
          {mode === 'dashboard' ? '📊 Dashboard' : mode === 'sprint' ? '🗂️ Sprint Board' : mode === 'kanban' ? '🗃️ Kanban' : mode === 'pipelines' ? '🔧 Pipelines' : '📈 Reports'}
        </button>
      ))}
    </div>
    {viewMode === 'dashboard' && data && (
      <>
        <DataOpsMetricsCards metrics={data.metrics} />
        <div className="grid lg:grid-cols-2 gap-6 mt-6">
          <DataOpsActivePipelines pipelines={data.activePipelines} onTrigger={onTriggerPipeline} />
          <DataOpsPowerBIReports reports={data.powerbiReports} />
        </div>
      </>
    )}
    {viewMode === 'sprint' && (
      <>
        {data?.sprint ? (
          <div className="bg-white rounded-lg shadow border border-gray-200 p-5 mb-6">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <h2 className="text-xl font-bold text-gray-900">{data.sprint.name}</h2>
                <p className="text-sm text-gray-600">{data.sprint.goal}</p>
                <div className="text-xs text-gray-500 mt-1">📅 {data.sprint.startDate} → {data.sprint.endDate}</div>
              </div>
              <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs font-semibold uppercase">{data.sprint.status}</span>
            </div>
          </div>
        ) : (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6 text-sm text-yellow-800">No active sprint found.</div>
        )}
        {data?.sprintBoard && (
          <DevOpsPipelineBoard pipeline={data.sprintBoard} title="DataOps Sprint Board" onTaskClick={onTaskClick} onStatusChange={onStatusChange} />
        )}
      </>
    )}
    {viewMode === 'kanban' && (
      <DataOpsKanbanBoard kanban={kanban} sprintBoard={data?.sprintBoard} onStatusChange={onKanbanStatusChange} onDelete={onKanbanDelete} userRole={userRole} onEdit={onTaskClick} onRefresh={onRefresh} />
    )}
    {viewMode === 'pipelines' && (
      <DataOpsPipelinesView pipelines={data?.allPipelines} onAdd={onAddPipeline} onPipelineClick={onPipelineClick} onTrigger={onTriggerPipeline} />
    )}
    {viewMode === 'reports' && (
      <DataOpsReportsView reports={data?.allReports} onAdd={onAddReport} onReportClick={onReportClick} onToggleFavorite={onToggleFavorite} />
    )}
  </div>
  );
};

// ============================================================================
// DATAOPS MODALS
// ============================================================================

const AddPipelineModal = ({ onClose, onSubmit }) => {
  const [form, setForm] = useState({ pipelineName: '', pipelineType: 'ETL', description: '', scheduleCron: '', maxRetries: 3 });
  const handleSubmit = (e) => { e.preventDefault(); onSubmit(form); };
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-xl font-semibold">New Data Pipeline</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">×</button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Pipeline Name *</label>
            <input required value={form.pipelineName} onChange={e => setForm({...form, pipelineName: e.target.value})}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500" placeholder="e.g., Daily Sales ETL" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Pipeline Type</label>
            <select value={form.pipelineType} onChange={e => setForm({...form, pipelineType: e.target.value})}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500">
              <option value="ETL">ETL</option><option value="Report">Report</option><option value="Dashboard">Dashboard</option><option value="Power BI">Power BI</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={3}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500" placeholder="Describe what this pipeline does..." />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Schedule (Cron Expression)</label>
            <input value={form.scheduleCron} onChange={e => setForm({...form, scheduleCron: e.target.value})}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500" placeholder="e.g., 0 2 * * * (2 AM daily)" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Max Retries</label>
            <input type="number" value={form.maxRetries} onChange={e => setForm({...form, maxRetries: parseInt(e.target.value)})}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500" min="0" max="10" />
          </div>
          <div className="flex gap-3 pt-4">
            <button type="submit" className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium">Create Pipeline</button>
            <button type="button" onClick={onClose} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const PipelineDetailModal = ({ pipeline, onClose, onUpdate, onDelete, onTrigger }) => {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    pipelineName: pipeline.pipelineName || '', pipelineType: pipeline.pipelineType || 'ETL',
    description: pipeline.description || '', scheduleCron: pipeline.scheduleCron || '', maxRetries: pipeline.maxRetries || 3
  });
  const handleUpdate = (e) => { e.preventDefault(); onUpdate(pipeline.pipelineId, form); setEditing(false); };
  const handleDelete = () => requestConfirm(`Delete pipeline "${pipeline.pipelineName}"?`, () => onDelete(pipeline.pipelineId));
  const badge = getDataOpsPipelineStatusBadge(pipeline.status);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <div>
            <h2 className="text-xl font-semibold">{pipeline.pipelineName}</h2>
            <p className="text-xs font-mono text-gray-500 mt-1">{pipeline.pipelineId}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">×</button>
        </div>
        {!editing ? (
          <div className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <span className="px-3 py-1 rounded text-sm font-semibold" style={{ background: badge.bg, color: badge.color }}>{badge.label}</span>
              <span className="text-sm text-gray-600">{pipeline.pipelineType}</span>
            </div>
            {pipeline.description && <p className="text-sm text-gray-600 mb-4">{pipeline.description}</p>}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div><h3 className="text-xs font-semibold text-gray-500 mb-1">Last Run</h3><p className="text-sm text-gray-900">{pipeline.lastRun ? new Date(pipeline.lastRun).toLocaleString() : 'Never'}</p></div>
              <div><h3 className="text-xs font-semibold text-gray-500 mb-1">Duration</h3><p className="text-sm text-gray-900">{formatPipelineDuration(pipeline.durationSeconds)}</p></div>
              <div><h3 className="text-xs font-semibold text-gray-500 mb-1">Records Processed</h3><p className="text-sm text-gray-900">{pipeline.recordsProcessed?.toLocaleString() || 0}</p></div>
              <div><h3 className="text-xs font-semibold text-gray-500 mb-1">Schedule</h3><p className="text-sm text-gray-900 font-mono">{pipeline.scheduleCron || 'Manual'}</p></div>
              <div><h3 className="text-xs font-semibold text-gray-500 mb-1">Data Processed</h3><p className="text-sm text-gray-900">{pipeline.dataProcessedGb ? `${pipeline.dataProcessedGb} GB` : '-'}</p></div>
              <div><h3 className="text-xs font-semibold text-gray-500 mb-1">Retries</h3><p className="text-sm text-gray-900">{pipeline.retryCount || 0} / {pipeline.maxRetries || 3}</p></div>
            </div>
            {pipeline.progress != null && (
              <div className="mb-4">
                <div className="flex justify-between text-sm text-gray-600 mb-2"><span>Progress</span><span>{pipeline.progress}%</span></div>
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div className="h-full rounded-full transition-all" style={{ width: `${pipeline.progress}%`, background: badge.color }} />
                </div>
              </div>
            )}
            {pipeline.errorMessage && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <h3 className="text-xs font-semibold text-red-700 mb-1">Error</h3>
                <p className="text-sm text-red-600">{pipeline.errorMessage}</p>
              </div>
            )}
            <div className="flex gap-2 pt-4 border-t border-gray-200">
              <button onClick={() => onTrigger(pipeline.pipelineId)} disabled={pipeline.status === 'running'}
                className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed">▶ Trigger Run</button>
              <button onClick={() => setEditing(true)} className="px-4 py-2 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200">Edit</button>
              <button onClick={handleDelete} className="px-4 py-2 bg-red-100 text-red-700 text-sm rounded-lg hover:bg-red-200">Delete</button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleUpdate} className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Pipeline Name</label>
              <input required value={form.pipelineName} onChange={e => setForm({...form, pipelineName: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
              <select value={form.pipelineType} onChange={e => setForm({...form, pipelineType: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500">
                <option value="ETL">ETL</option><option value="Report">Report</option><option value="Dashboard">Dashboard</option><option value="Power BI">Power BI</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={3}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Schedule (Cron)</label>
              <input value={form.scheduleCron} onChange={e => setForm({...form, scheduleCron: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500" />
            </div>
            <div className="flex gap-2 pt-4">
              <button type="submit" className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700">Save Changes</button>
              <button type="button" onClick={() => setEditing(false)} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">Cancel</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

const AddReportModal = ({ onClose, onSubmit }) => {
  const [form, setForm] = useState({ reportName: '', reportIcon: '📊', workspaceId: '', datasetId: '', description: '', category: 'General', refreshSchedule: '' });
  const handleSubmit = (e) => { e.preventDefault(); onSubmit(form); };
  const iconOptions = ['📊', '💰', '👥', '📈', '⚙️', '📢', '🎯', '💡', '📉', '🔍'];
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-xl font-semibold">New Power BI Report</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">×</button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Report Name *</label>
            <input required value={form.reportName} onChange={e => setForm({...form, reportName: e.target.value})}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500" placeholder="e.g., Sales Performance Dashboard" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Icon</label>
            <div className="flex gap-2 flex-wrap">
              {iconOptions.map(icon => (
                <button key={icon} type="button" onClick={() => setForm({...form, reportIcon: icon})}
                  className={`w-10 h-10 text-xl rounded border-2 hover:border-purple-500 ${form.reportIcon === icon ? 'border-purple-600 bg-purple-50' : 'border-gray-200'}`}>{icon}</button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Workspace ID</label>
              <input value={form.workspaceId} onChange={e => setForm({...form, workspaceId: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500" placeholder="Optional" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Dataset ID</label>
              <input value={form.datasetId} onChange={e => setForm({...form, datasetId: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500" placeholder="Optional" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
            <select value={form.category} onChange={e => setForm({...form, category: e.target.value})}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500">
              <option value="General">General</option><option value="Executive">Executive</option><option value="Sales">Sales</option>
              <option value="Finance">Finance</option><option value="Operations">Operations</option><option value="Marketing">Marketing</option><option value="Analytics">Analytics</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={3}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500" placeholder="Describe what this report shows..." />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Refresh Schedule</label>
            <input value={form.refreshSchedule} onChange={e => setForm({...form, refreshSchedule: e.target.value})}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500" placeholder="e.g., Daily at 6 AM" />
          </div>
          <div className="flex gap-3 pt-4">
            <button type="submit" className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium">Create Report</button>
            <button type="button" onClick={onClose} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const ReportDetailModal = ({ report, onClose, onUpdate, onDelete, onToggleFavorite }) => {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    reportName: report.reportName || '', reportIcon: report.reportIcon || '📊',
    description: report.description || '', category: report.category || 'General',
    status: report.status || 'draft', refreshSchedule: report.refreshSchedule || ''
  });
  const handleUpdate = (e) => { e.preventDefault(); onUpdate(report.reportId, form); setEditing(false); };
  const handleDelete = () => requestConfirm(`Delete report "${report.reportName}"?`, () => onDelete(report.reportId));
  const published = report.status === 'Published';
  const iconOptions = ['📊', '💰', '👥', '📈', '⚙️', '📢', '🎯', '💡', '📉', '🔍'];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <span className="text-3xl">{report.reportIcon}</span>
            <div>
              <h2 className="text-xl font-semibold">{report.reportName}</h2>
              <p className="text-xs font-mono text-gray-500 mt-1">{report.reportId}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">×</button>
        </div>
        {!editing ? (
          <div className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <span className={`px-3 py-1 rounded text-sm font-semibold ${published ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>{report.status}</span>
              {report.category && <span className="text-sm text-gray-600">{report.category}</span>}
              <button onClick={() => onToggleFavorite(report.reportId, !report.isFavorite)} className="text-2xl ml-auto">{report.isFavorite ? '⭐' : '☆'}</button>
            </div>
            {report.description && <p className="text-sm text-gray-600 mb-4">{report.description}</p>}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div><h3 className="text-xs font-semibold text-gray-500 mb-1">View Count</h3><p className="text-sm text-gray-900">{report.viewCount?.toLocaleString() || 0}</p></div>
              <div><h3 className="text-xs font-semibold text-gray-500 mb-1">Last Updated</h3><p className="text-sm text-gray-900">{report.lastUpdated ? new Date(report.lastUpdated).toLocaleString() : 'Never'}</p></div>
              {report.workspaceId && <div><h3 className="text-xs font-semibold text-gray-500 mb-1">Workspace ID</h3><p className="text-sm font-mono text-gray-900">{report.workspaceId}</p></div>}
              {report.datasetId && <div><h3 className="text-xs font-semibold text-gray-500 mb-1">Dataset ID</h3><p className="text-sm font-mono text-gray-900">{report.datasetId}</p></div>}
              {report.refreshSchedule && <div className="col-span-2"><h3 className="text-xs font-semibold text-gray-500 mb-1">Refresh Schedule</h3><p className="text-sm text-gray-900">{report.refreshSchedule}</p></div>}
            </div>
            <div className="flex gap-2 pt-4 border-t border-gray-200">
              <button onClick={() => setEditing(true)} className="px-4 py-2 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200">Edit</button>
              <button onClick={handleDelete} className="px-4 py-2 bg-red-100 text-red-700 text-sm rounded-lg hover:bg-red-200">Delete</button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleUpdate} className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Report Name</label>
              <input required value={form.reportName} onChange={e => setForm({...form, reportName: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Icon</label>
              <div className="flex gap-2 flex-wrap">
                {iconOptions.map(icon => (
                  <button key={icon} type="button" onClick={() => setForm({...form, reportIcon: icon})}
                    className={`w-10 h-10 text-xl rounded border-2 hover:border-purple-500 ${form.reportIcon === icon ? 'border-purple-600 bg-purple-50' : 'border-gray-200'}`}>{icon}</button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <select value={form.category} onChange={e => setForm({...form, category: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500">
                <option value="General">General</option><option value="Executive">Executive</option><option value="Sales">Sales</option>
                <option value="Finance">Finance</option><option value="Operations">Operations</option><option value="Marketing">Marketing</option><option value="Analytics">Analytics</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select value={form.status} onChange={e => setForm({...form, status: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500">
                <option value="draft">Draft</option><option value="Published">Published</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={3}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500" />
            </div>
            <div className="flex gap-2 pt-4">
              <button type="submit" className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700">Save Changes</button>
              <button type="button" onClick={() => setEditing(false)} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">Cancel</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// LEARNTRACK MODULE VIEWS
// ============================================================================

const getDifficultyBadge = (d) => {
  const badges = { beginner: { bg: '#dcffe4', color: '#28a745', label: 'Beginner' }, intermediate: { bg: '#fff3cd', color: '#856404', label: 'Intermediate' }, advanced: { bg: '#ffe5e9', color: '#dc3545', label: 'Advanced' } };
  return badges[d] || badges.beginner;
};

const getGroupLabel = (g) => {
  const labels = { all: 'All Users', admin: 'Admins', developer: 'Developers' };
  return labels[g] || g;
};

const getEnrollmentStatusBadge = (s) => {
  const badges = {
    assigned: { bg: '#f6f8fa', color: '#586069', label: 'Assigned' },
    in_progress: { bg: '#d4e4ff', color: '#0366d6', label: 'In Progress' },
    completed: { bg: '#dcffe4', color: '#28a745', label: 'Completed' },
    dropped: { bg: '#ffe5e9', color: '#dc3545', label: 'Dropped' }
  };
  return badges[s] || badges.assigned;
};

const LearnTrackMetrics = ({ data }) => {
  const totalCourses = data?.courses?.length || 0;
  const myEnrolled = data?.myEnrollments?.length || 0;
  const myCompleted = (data?.myEnrollments || []).filter(e => e.status === 'completed').length;
  const myBadges = data?.myBadges?.length || 0;
  const cards = [
    { label: 'Total Courses', value: totalCourses, icon: '📚', color: '#fd7e14' },
    { label: 'My Enrollments', value: myEnrolled, icon: '📝', color: '#0366d6' },
    { label: 'Completed', value: myCompleted, icon: '✅', color: '#28a745' },
    { label: 'Badges Earned', value: myBadges, icon: '🏅', color: '#6f42c1' }
  ];
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      {cards.map(c => (
        <div key={c.label} className="bg-white rounded-lg shadow border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-600">{c.label}</span>
            <span className="text-2xl">{c.icon}</span>
          </div>
          <div className="text-2xl font-bold" style={{ color: c.color }}>{c.value}</div>
        </div>
      ))}
    </div>
  );
};

const SkillsOverview = ({ skills, courses }) => (
  <div className="mb-6">
    <h2 className="text-lg font-semibold text-gray-900 mb-3">Skills</h2>
    <div className="flex flex-wrap gap-3">
      {(skills || []).map(s => {
        const count = (courses || []).filter(c => c.skillName === s.skillName).length;
        const categoryColors = { technical: '#0366d6', soft: '#fd7e14', leadership: '#6f42c1', compliance: '#dc3545' };
        return (
          <div key={s.id} className="bg-white rounded-lg shadow border border-gray-200 px-4 py-3 flex items-center gap-3">
            <div className="w-2 h-8 rounded-full" style={{ background: categoryColors[s.category] || '#586069' }} />
            <div>
              <div className="text-sm font-semibold text-gray-900">{s.skillName}</div>
              <div className="text-xs text-gray-500">{count} course{count !== 1 ? 's' : ''} · {s.category}</div>
            </div>
          </div>
        );
      })}
    </div>
  </div>
);

const TeamProgressTable = ({ teamProgress }) => (
  <div>
    <h2 className="text-lg font-semibold text-gray-900 mb-3">Team Progress</h2>
    <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
      <table className="w-full">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Team Member</th>
            <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600">Enrolled</th>
            <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600">In Progress</th>
            <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600">Completed</th>
            <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600">Badges</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {(teamProgress || []).map(tp => (
            <tr key={tp.userId} className="hover:bg-gray-50">
              <td className="px-4 py-3 text-sm font-medium text-gray-900">{tp.userName}</td>
              <td className="px-4 py-3 text-center text-sm text-gray-600">{tp.totalEnrollments}</td>
              <td className="px-4 py-3 text-center text-sm text-blue-600 font-semibold">{tp.inProgressCourses}</td>
              <td className="px-4 py-3 text-center text-sm text-green-600 font-semibold">{tp.completedCourses}</td>
              <td className="px-4 py-3 text-center text-sm text-purple-600 font-semibold">{tp.badgeCount}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const CourseCatalog = ({ courses, skills, users = [], onAdd, onCourseClick, onEnroll, filterSkill, filterGroup, onFilterSkill, onFilterGroup }) => {
  const [enrollCourse, setEnrollCourse] = useState(null);
  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
        <h2 className="text-lg font-semibold text-gray-900">Course Catalog</h2>
        <div className="flex items-center gap-2 flex-wrap">
          <select value={filterSkill} onChange={e => onFilterSkill(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm">
            <option value="">All Skills</option>
            {(skills || []).map(s => <option key={s.id} value={s.skillName}>{s.skillName}</option>)}
          </select>
          <select value={filterGroup} onChange={e => onFilterGroup(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm">
            <option value="">All Groups</option>
            <option value="all">All Users</option>
            <option value="developer">Developers</option>
            <option value="admin">Admins</option>
          </select>
          <button onClick={onAdd} className="px-4 py-1.5 bg-orange-500 text-white text-sm rounded-lg hover:bg-orange-600 font-medium">+ New Course</button>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {(courses || []).filter(c => (!filterSkill || c.skillName === filterSkill) && (!filterGroup || c.targetGroup === filterGroup)).map(c => {
          const diff = getDifficultyBadge(c.difficulty);
          return (
            <div key={c.id} className="bg-white rounded-lg shadow border border-gray-200 p-4 hover:shadow-lg transition-shadow cursor-pointer" onClick={() => onCourseClick(c.id)}>
              <div className="flex items-start justify-between mb-3">
                <span className="text-3xl">{c.badgeIcon || '📚'}</span>
                <span className="px-2 py-0.5 rounded text-xs font-semibold" style={{ background: diff.bg, color: diff.color }}>{diff.label}</span>
              </div>
              <h3 className="text-base font-semibold text-gray-900 mb-1">{c.courseName}</h3>
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                {c.skillName && <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs font-medium">{c.skillName}</span>}
                <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">{getGroupLabel(c.targetGroup)}</span>
              </div>
              <div className="text-xs text-gray-500 mb-3">
                {c.durationHours}h · {c.instructor || 'TBD'}
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-500">{c.enrollmentCount} enrolled · {c.completedCount} completed</span>
                <button onClick={(e) => { e.stopPropagation(); setEnrollCourse(c); }}
                  className="px-2 py-1 bg-orange-100 text-orange-700 text-xs rounded hover:bg-orange-200 font-medium">Enroll</button>
              </div>
            </div>
          );
        })}
      </div>
      {(!courses || courses.length === 0) && <div className="bg-white rounded-lg shadow border border-gray-200 p-12 text-center text-gray-400">No courses found.</div>}
      {enrollCourse && (
        <EnrollUserModal course={enrollCourse} users={users}
          excludeUserIds={new Set((enrollCourse.enrolledUsers || []).map(eu => eu.userId))}
          onEnroll={onEnroll} onClose={() => setEnrollCourse(null)} />
      )}
    </div>
  );
};

const MyEnrollments = ({ enrollments, onUpdateProgress }) => (
  <div className="mb-6">
    <h2 className="text-lg font-semibold text-gray-900 mb-3">My Courses</h2>
    <div className="space-y-3">
      {(enrollments || []).map(e => {
        const badge = getEnrollmentStatusBadge(e.status);
        return (
          <div key={e.id} className="bg-white rounded-lg shadow border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-3">
                <span className="text-2xl">{e.badgeIcon || '📚'}</span>
                <div>
                  <div className="font-medium text-sm text-gray-900">{e.courseName}</div>
                  <div className="text-xs text-gray-500">{e.skillName} · {e.difficulty} · {e.durationHours}h</div>
                </div>
              </div>
              <span className="px-2 py-0.5 rounded text-xs font-semibold" style={{ background: badge.bg, color: badge.color }}>{badge.label}</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex-1">
                <div className="flex justify-between text-xs text-gray-600 mb-1">
                  <span>Progress</span><span>{e.progress}%</span>
                </div>
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div className="h-full rounded-full transition-all" style={{ width: `${e.progress}%`, background: e.status === 'completed' ? '#28a745' : '#0366d6' }} />
                </div>
              </div>
              {e.score != null && <span className="text-sm font-bold text-purple-600">Score: {e.score}%</span>}
              {e.status === 'assigned' && (
                <button onClick={() => onUpdateProgress(e.id, { status: 'in_progress' })} className="px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700">Start</button>
              )}
              {e.status === 'in_progress' && (
                <button onClick={() => onUpdateProgress(e.id, { status: 'completed' })} className="px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700">Complete</button>
              )}
            </div>
          </div>
        );
      })}
      {(!enrollments || enrollments.length === 0) && <div className="bg-white rounded-lg shadow border border-gray-200 p-8 text-center text-gray-400">No enrollments yet. Browse the course catalog to get started.</div>}
    </div>
  </div>
);

const MyBadges = ({ badges }) => (
  <div>
    <h2 className="text-lg font-semibold text-gray-900 mb-3">My Badges</h2>
    {(!badges || badges.length === 0) ? (
      <div className="bg-white rounded-lg shadow border border-gray-200 p-8 text-center text-gray-400">No badges earned yet. Complete courses to earn badges!</div>
    ) : (
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {badges.map(b => (
          <div key={b.id} className="bg-white rounded-lg shadow border border-gray-200 p-4 text-center">
            <div className="text-4xl mb-2">{b.badgeIcon}</div>
            <div className="text-sm font-semibold text-gray-900">{b.badgeName}</div>
            <div className="text-xs text-gray-500 mt-1">{b.description}</div>
            <div className="text-xs text-gray-400 mt-2">{b.awardedAt ? new Date(b.awardedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}</div>
          </div>
        ))}
      </div>
    )}
  </div>
);

const LearnTrackModule = ({ data, users = [], user, viewMode, onViewModeChange, onAddCourse, onCourseClick, onEnroll, onUpdateProgress, filterSkill, filterGroup, onFilterSkill, onFilterGroup }) => (
  <div className="max-w-7xl mx-auto px-4 py-6">
    <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">LearnTrack</h1>
        <p className="text-gray-600 mt-1">Learning management, courses & skill development</p>
      </div>
    </div>
    <div className="flex gap-2 mb-6 flex-wrap">
      {[
        { id: 'dashboard',  label: '📊 Dashboard' },
        { id: 'paths',      label: '🗺️ Learning Paths' },
        { id: 'courses',    label: '📚 Courses' },
        { id: 'mylearning', label: '🎓 My Learning' },
      ].map(t => (
        <button key={t.id} onClick={() => onViewModeChange(t.id)}
          className={`px-4 py-2 text-sm rounded-lg font-medium transition-colors ${viewMode === t.id ? 'bg-gradient-to-r from-indigo-500 to-violet-500 text-white shadow-sm' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
          {t.label}
        </button>
      ))}
    </div>
    {viewMode === 'dashboard' && (
      <>
        <LearnTrackMetrics data={data} />
        <SkillsOverview skills={data?.skills} courses={data?.courses} />
        <TeamProgressTable teamProgress={data?.teamProgress} />
      </>
    )}
    {viewMode === 'paths' && (
      <LearningPathsSection user={user} users={users} courses={data?.courses || []} />
    )}
    {viewMode === 'courses' && (
      <CourseCatalog courses={data?.courses} skills={data?.skills} users={users} onAdd={onAddCourse} onCourseClick={onCourseClick} onEnroll={onEnroll}
        filterSkill={filterSkill} filterGroup={filterGroup} onFilterSkill={onFilterSkill} onFilterGroup={onFilterGroup} />
    )}
    {viewMode === 'mylearning' && (
      <>
        <MyEnrollments enrollments={data?.myEnrollments} onUpdateProgress={onUpdateProgress} />
        <MyBadges badges={data?.myBadges} />
      </>
    )}
  </div>
);

// ============================================================================
// LEARNING PATHS — section, list, detail, roadmap, admin modals
// All state is self-contained inside LearningPathsSection so the parent App's
// existing LearnTrack flow doesn't need any new wiring.
// ============================================================================

const PATH_DIFFICULTY_STYLE = {
  beginner:     { label: 'Beginner',     bg: 'bg-emerald-100', text: 'text-emerald-700', dot: 'bg-emerald-500' },
  intermediate: { label: 'Intermediate', bg: 'bg-indigo-100',  text: 'text-indigo-700',  dot: 'bg-indigo-500' },
  advanced:     { label: 'Advanced',     bg: 'bg-rose-100',    text: 'text-rose-700',    dot: 'bg-rose-500' },
};

const PATH_ICON_OPTIONS = ['🎓', '🚀', '⚙️', '🔧', '☁️', '🐍', '⚛️', '📊', '🛡️', '🔐', '🤖', '📡', '💡', '🎯', '🧩', '📦'];
const PATH_ACCENT_OPTIONS = ['#6366f1', '#8b5cf6', '#a855f7', '#ec4899', '#f43f5e', '#f97316', '#eab308', '#22c55e', '#14b8a6', '#06b6d4', '#3b82f6'];

const LearningPathsSection = ({ user, users = [], courses = [] }) => {
  const role = user?.role || user?.user_role;
  const isManager = ['admin', 'manager', 'devops_manager', 'dataops_manager'].includes(role);
  const [paths, setPaths] = useState(null); // null = loading, [] = empty
  const [selectedPathId, setSelectedPathId] = useState(null);
  const [selectedDetail, setSelectedDetail] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showManageCourses, setShowManageCourses] = useState(false);
  const [showAssign, setShowAssign] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [toast, setToast] = useState('');
  const showToast = (m) => { setToast(m); setTimeout(() => setToast(''), 1800); };

  const reload = async () => {
    const list = await ModulesModel.getLearningPaths();
    setPaths(Array.isArray(list) ? list : []);
  };
  const reloadDetail = async (id) => {
    const d = await ModulesModel.getLearningPath(id);
    setSelectedDetail(d);
  };
  useEffect(() => { reload(); }, []);
  useEffect(() => {
    if (selectedPathId) reloadDetail(selectedPathId);
    else setSelectedDetail(null);
  }, [selectedPathId]);

  const handleCreate = async (form) => {
    const r = await ModulesModel.createLearningPath(form);
    if (r?.success) { showToast('Path created'); setShowCreate(false); reload(); }
    else alert(r?.error || 'Could not create path');
  };
  const handleEdit = async (form) => {
    const r = await ModulesModel.updateLearningPath(selectedPathId, form);
    if (r?.success) { showToast('Path updated'); setShowEdit(false); reload(); reloadDetail(selectedPathId); }
  };
  const handleDelete = async () => {
    if (!confirm(`Permanently delete this learning path? Enrolled users keep their course progress.`)) return;
    const r = await ModulesModel.deleteLearningPath(selectedPathId);
    if (r?.success) { showToast('Path deleted'); setSelectedPathId(null); reload(); }
    else alert(r?.error || 'Could not delete');
  };
  const handleSaveCourses = async (courseIds) => {
    const r = await ModulesModel.setPathCourses(selectedPathId, courseIds);
    if (r?.success) { showToast('Curriculum updated'); setShowManageCourses(false); reloadDetail(selectedPathId); reload(); }
  };
  const handleAssign = async (userIds) => {
    const r = await ModulesModel.enrollInPath(selectedPathId, userIds);
    if (r?.success) { showToast(`Assigned ${r.added || userIds.length} user(s)`); setShowAssign(false); reloadDetail(selectedPathId); reload(); }
  };
  const handleSelfEnroll = async (pathId) => {
    const r = await ModulesModel.enrollInPath(pathId);
    if (r?.success) { showToast('Enrolled — your first course is unlocked'); reload(); if (selectedPathId === pathId) reloadDetail(pathId); }
  };
  const handleUnenroll = async (pathId, userId) => {
    const r = await ModulesModel.unenrollFromPath(pathId, userId);
    if (r?.success) { reloadDetail(pathId); reload(); }
  };

  if (paths === null) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map(i => (
          <div key={i} className="bg-white rounded-2xl border border-gray-200 p-6 animate-pulse">
            <div className="h-5 bg-gray-200 rounded w-1/3 mb-3" />
            <div className="h-3 bg-gray-100 rounded w-2/3 mb-2" />
            <div className="h-3 bg-gray-100 rounded w-1/2" />
          </div>
        ))}
      </div>
    );
  }

  // Detail view
  if (selectedPathId && selectedDetail) {
    return (
      <>
        {toast && <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-4 py-2 bg-gray-900 text-white text-sm rounded-lg shadow-xl">{toast}</div>}
        <LearningPathDetail
          path={selectedDetail}
          isManager={isManager}
          user={user}
          onBack={() => setSelectedPathId(null)}
          onSelfEnroll={() => handleSelfEnroll(selectedPathId)}
          onEdit={() => setShowEdit(true)}
          onManageCourses={() => setShowManageCourses(true)}
          onAssign={() => setShowAssign(true)}
          onDelete={handleDelete}
          onUnenroll={handleUnenroll}
        />
        {showEdit && (
          <CreatePathModal initial={selectedDetail} onSave={handleEdit} onClose={() => setShowEdit(false)} title="Edit Learning Path" />
        )}
        {showManageCourses && (
          <ManagePathCoursesModal
            path={selectedDetail}
            allCourses={courses}
            onSave={handleSaveCourses}
            onClose={() => setShowManageCourses(false)}
          />
        )}
        {showAssign && (
          <AssignPathUsersModal
            path={selectedDetail}
            users={users}
            currentEnrollments={selectedDetail.enrolledUsers || []}
            onAssign={handleAssign}
            onClose={() => setShowAssign(false)}
          />
        )}
      </>
    );
  }

  // List view
  return (
    <>
      {toast && <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-4 py-2 bg-gray-900 text-white text-sm rounded-lg shadow-xl">{toast}</div>}
      <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Learning Paths</h2>
          <p className="text-sm text-gray-500">{paths.length} path{paths.length === 1 ? '' : 's'} · Structured curricula with sequential progression</p>
        </div>
        {isManager && (
          <button onClick={() => setShowCreate(true)}
            className="px-4 py-2 text-sm rounded-lg bg-gradient-to-r from-indigo-600 to-violet-600 text-white font-medium hover:shadow-md hover:-translate-y-0.5 transition-all flex items-center gap-1.5 shadow-sm">
            <span>+</span> New Path
          </button>
        )}
      </div>

      {paths.length === 0 ? (
        <div className="bg-gradient-to-br from-indigo-50/50 to-violet-50/40 border border-indigo-100 rounded-2xl p-12 text-center">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-500 flex items-center justify-center text-3xl text-white shadow-md mb-4">🗺️</div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No learning paths yet</h3>
          <p className="text-sm text-gray-500 max-w-sm mx-auto mb-5">Group existing courses into ordered curricula. Learners unlock the next course as they complete the previous one.</p>
          {isManager && (
            <button onClick={() => setShowCreate(true)} className="px-5 py-2 rounded-lg bg-gradient-to-r from-indigo-600 to-violet-600 text-white text-sm font-medium shadow-sm">+ Create the first path</button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {paths.map(p => <PathCard key={p.id} path={p} onOpen={() => setSelectedPathId(p.id)} onEnroll={() => handleSelfEnroll(p.id)} />)}
        </div>
      )}

      {showCreate && (
        <CreatePathModal onSave={handleCreate} onClose={() => setShowCreate(false)} title="New Learning Path" />
      )}
    </>
  );
};

const PathCard = ({ path, onOpen, onEnroll }) => {
  const diff = PATH_DIFFICULTY_STYLE[path.difficulty] || PATH_DIFFICULTY_STYLE.beginner;
  const accent = path.accentColor || '#6366f1';
  const isEnrolled = !!path.isEnrolled;
  const myProgress = path.myProgress || 0;
  const completionRate = path.enrolledCount > 0 ? Math.round((path.completedCount / path.enrolledCount) * 100) : 0;
  return (
    <div onClick={onOpen}
      className="relative group bg-white/80 backdrop-blur-sm border border-gray-200 rounded-2xl overflow-hidden shadow-sm hover:shadow-xl hover:border-indigo-300 hover:-translate-y-1 transition-all duration-200 cursor-pointer">
      {/* Gradient accent strip */}
      <div className="h-1.5" style={{ background: `linear-gradient(90deg, ${accent}, ${accent}80 50%, ${accent}40)` }} />
      {/* Soft gradient backdrop */}
      <div className="absolute inset-0 pointer-events-none opacity-60" style={{ background: `radial-gradient(circle at 100% 0%, ${accent}15, transparent 50%)` }} />

      <div className="relative p-5">
        <div className="flex items-start gap-3 mb-3">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl shadow-sm flex-shrink-0" style={{ background: `linear-gradient(135deg, ${accent}, ${accent}cc)` }}>
            <span className="drop-shadow-sm">{path.icon || '🎓'}</span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold inline-flex items-center gap-1 ${diff.bg} ${diff.text}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${diff.dot}`} />{diff.label}
              </span>
              {path.status === 'draft' && <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold bg-amber-100 text-amber-700">Draft</span>}
            </div>
            <h3 className="text-base font-bold text-gray-900 leading-snug line-clamp-2 group-hover:text-indigo-700 transition-colors">{path.title}</h3>
          </div>
        </div>

        {path.description && <p className="text-sm text-gray-600 line-clamp-2 mb-4 leading-relaxed">{path.description}</p>}

        {/* Meta row */}
        <div className="flex items-center gap-3 text-xs text-gray-500 mb-4 flex-wrap">
          <span className="inline-flex items-center gap-1"><span>📚</span>{path.courseCount} course{path.courseCount === 1 ? '' : 's'}</span>
          {(path.estimatedHours || 0) > 0 && <span className="inline-flex items-center gap-1"><span>⏱</span>{path.estimatedHours}h</span>}
          {path.enrolledCount > 0 && <span className="inline-flex items-center gap-1"><span>👥</span>{path.enrolledCount} learner{path.enrolledCount === 1 ? '' : 's'}</span>}
        </div>

        {/* Progress (enrolled user) OR enroll CTA */}
        {isEnrolled ? (
          <div>
            <div className="flex items-center justify-between text-xs mb-1.5">
              <span className="font-medium text-gray-700">Your progress</span>
              <span className="font-semibold" style={{ color: accent }}>{myProgress}%</span>
            </div>
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
              <div className="h-full rounded-full transition-all" style={{ width: `${myProgress}%`, background: `linear-gradient(90deg, ${accent}, ${accent}cc)` }} />
            </div>
            {path.myStatus === 'completed' && (
              <div className="mt-2 text-xs font-semibold inline-flex items-center gap-1 text-emerald-600">✓ Path completed</div>
            )}
          </div>
        ) : path.status === 'published' ? (
          <button onClick={(e) => { e.stopPropagation(); onEnroll(); }}
            className="w-full mt-1 px-3 py-1.5 text-xs font-semibold rounded-lg border-2 transition-all hover:shadow-sm"
            style={{ borderColor: accent + '40', color: accent, background: accent + '0a' }}>
            Enroll in this path →
          </button>
        ) : (
          <div className="text-xs text-gray-400 italic">Draft — not yet open for enrollment</div>
        )}
      </div>
    </div>
  );
};

const LearningPathDetail = ({ path, isManager, user, onBack, onSelfEnroll, onEdit, onManageCourses, onAssign, onDelete, onUnenroll }) => {
  const accent = path.accentColor || '#6366f1';
  const diff = PATH_DIFFICULTY_STYLE[path.difficulty] || PATH_DIFFICULTY_STYLE.beginner;
  const isEnrolled = !!path.myEnrollment;
  const myProgress = path.myProgress || 0;
  return (
    <div>
      {/* Back + admin actions */}
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <button onClick={onBack} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900 font-medium">
          <ChevronRight size={16} className="rotate-180" /> Back to Paths
        </button>
        {isManager && (
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={onAssign} className="px-3 py-1.5 text-xs font-medium rounded-lg bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 hover:border-gray-300 shadow-sm">👥 Assign users</button>
            <button onClick={onManageCourses} className="px-3 py-1.5 text-xs font-medium rounded-lg bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 hover:border-gray-300 shadow-sm">📚 Manage courses</button>
            <button onClick={onEdit} className="px-3 py-1.5 text-xs font-medium rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 shadow-sm">Edit</button>
            <button onClick={onDelete} className="px-3 py-1.5 text-xs font-medium rounded-lg bg-white border border-red-200 text-red-600 hover:bg-red-50 shadow-sm">Delete</button>
          </div>
        )}
      </div>

      {/* Hero card */}
      <div className="relative bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden mb-6">
        <div className="absolute inset-0 pointer-events-none" style={{ background: `radial-gradient(ellipse 70% 50% at 0% 0%, ${accent}18, transparent 60%), radial-gradient(ellipse 50% 40% at 100% 0%, rgba(139,92,246,0.12), transparent 60%)` }} />
        <div className="h-1.5 relative" style={{ background: `linear-gradient(90deg, ${accent}, #8b5cf6 50%, ${accent}80)` }} />
        <div className="relative px-6 sm:px-10 py-8 flex flex-col sm:flex-row sm:items-center gap-5">
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center text-3xl shadow-md flex-shrink-0" style={{ background: `linear-gradient(135deg, ${accent}, ${accent}cc)` }}>
            <span className="drop-shadow-sm">{path.icon || '🎓'}</span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <span className={`text-xs px-2 py-1 rounded-full font-semibold inline-flex items-center gap-1.5 ${diff.bg} ${diff.text}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${diff.dot}`} />{diff.label}
              </span>
              <span className="text-xs px-2 py-1 rounded-full font-medium bg-white/70 border border-gray-200 text-gray-600">{path.courseCount} courses</span>
              {(path.totalHours || 0) > 0 && <span className="text-xs px-2 py-1 rounded-full font-medium bg-white/70 border border-gray-200 text-gray-600">⏱ {path.totalHours}h</span>}
              {path.status === 'draft' && <span className="text-xs px-2 py-1 rounded-full font-semibold bg-amber-100 text-amber-700">Draft</span>}
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight leading-tight mb-2">{path.title}</h1>
            {path.description && <p className="text-sm text-gray-600 leading-relaxed">{path.description}</p>}
          </div>
        </div>
        {/* Personal progress band */}
        {isEnrolled && (
          <div className="relative border-t border-gray-100 bg-gradient-to-r from-indigo-50/50 to-violet-50/40 px-6 sm:px-10 py-4">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div>
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Your progress</div>
                <div className="text-sm text-gray-700">{path.completedCount} of {path.courseCount} course{path.courseCount === 1 ? '' : 's'} complete</div>
              </div>
              <div className="text-2xl font-bold" style={{ color: accent }}>{myProgress}%</div>
            </div>
            <div className="mt-3 h-2 bg-white/60 rounded-full overflow-hidden">
              <div className="h-full rounded-full transition-all" style={{ width: `${myProgress}%`, background: `linear-gradient(90deg, ${accent}, ${accent}cc)` }} />
            </div>
          </div>
        )}
        {!isEnrolled && path.status === 'published' && (
          <div className="relative border-t border-gray-100 px-6 sm:px-10 py-4 flex items-center justify-between gap-4 flex-wrap">
            <div className="text-sm text-gray-600">Enroll to unlock the first course in this path.</div>
            <button onClick={onSelfEnroll}
              className="px-4 py-2 text-sm font-semibold rounded-lg text-white shadow-sm hover:shadow-md transition-all"
              style={{ background: `linear-gradient(135deg, ${accent}, #8b5cf6)` }}>
              Enroll in path →
            </button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_280px] gap-6 items-start">
        {/* Roadmap */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 sm:p-8">
          <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-5">Curriculum roadmap</h2>
          {path.courses.length === 0 ? (
            <div className="text-center py-10 text-sm text-gray-400">
              No courses added yet.{isManager ? ' Click "Manage courses" to build the curriculum.' : ''}
            </div>
          ) : (
            <PathRoadmap courses={path.courses} accent={accent} isEnrolled={isEnrolled} />
          )}
        </div>

        {/* Enrolled learners sidebar */}
        <aside className="space-y-4">
          {isManager && (
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100 bg-gradient-to-b from-gray-50/60 to-white">
                <h3 className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Enrolled learners ({path.enrolledUsers.length})</h3>
              </div>
              <div className="divide-y divide-gray-100 max-h-[60vh] overflow-y-auto">
                {path.enrolledUsers.length === 0 ? (
                  <div className="px-4 py-6 text-xs text-gray-400 italic text-center">No one assigned yet</div>
                ) : path.enrolledUsers.map(u => {
                  const initials = (u.userName || '?').split(/\s+/).slice(0, 2).map(w => w[0]?.toUpperCase() || '').join('');
                  return (
                    <div key={u.id} className="px-4 py-3 flex items-center gap-3 group hover:bg-gray-50/60">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-semibold text-white flex-shrink-0" style={{ background: accent }}>{initials}</div>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-semibold text-gray-800 truncate">{u.userName}</div>
                        <div className="flex items-center gap-2 mt-0.5">
                          <div className="flex-1 h-1 bg-gray-100 rounded-full overflow-hidden">
                            <div className="h-full rounded-full" style={{ width: `${u.progress}%`, background: u.status === 'completed' ? '#10b981' : accent }} />
                          </div>
                          <span className={`text-[10px] font-semibold ${u.status === 'completed' ? 'text-emerald-600' : 'text-gray-500'}`}>{u.progress}%</span>
                        </div>
                      </div>
                      <button onClick={() => onUnenroll(path.id, u.userId)} className="opacity-0 group-hover:opacity-100 text-[10px] text-red-500 hover:text-red-700 font-medium px-2 py-1 rounded">Remove</button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {/* Path stats card */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
            <h3 className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-3">Path stats</h3>
            <div className="space-y-2.5 text-sm">
              <div className="flex justify-between"><span className="text-gray-500">Courses</span><span className="font-semibold text-gray-900">{path.courseCount}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Total hours</span><span className="font-semibold text-gray-900">{path.totalHours || 0}h</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Enrolled</span><span className="font-semibold text-gray-900">{path.enrolledUsers.length}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Completed</span><span className="font-semibold text-emerald-600">{path.enrolledUsers.filter(u => u.status === 'completed').length}</span></div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
};

const PathRoadmap = ({ courses, accent, isEnrolled }) => (
  <div className="relative">
    {/* Vertical connector line */}
    <div className="absolute left-[19px] top-2 bottom-2 w-0.5 rounded-full" style={{ background: `linear-gradient(180deg, ${accent}40, ${accent}10)` }} />
    <div className="space-y-3">
      {courses.map((c, i) => {
        const locked = !isEnrolled || c.locked;
        const completed = c.completed;
        const active = !locked && !completed && (c.status === 'in_progress' || c.progress > 0);
        const NodeIcon = () => {
          if (completed) return (
            <svg viewBox="0 0 24 24" className="w-5 h-5 text-white" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="4 12 9 17 20 6"/></svg>
          );
          if (locked) return (
            <svg viewBox="0 0 24 24" className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2"><rect x="5" y="11" width="14" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/></svg>
          );
          return <span className="text-white text-xs font-bold">{i + 1}</span>;
        };
        const nodeBg = completed ? 'linear-gradient(135deg, #10b981, #059669)' : (locked ? '#f3f4f6' : `linear-gradient(135deg, ${accent}, #8b5cf6)`);
        return (
          <div key={c.id} className="relative flex items-start gap-4">
            <div className={`relative w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 shadow-sm ${active ? 'ring-4 ring-indigo-100' : ''}`} style={{ background: nodeBg }}>
              <NodeIcon />
              {active && <span className="absolute inset-0 rounded-full animate-ping opacity-30" style={{ background: accent }} />}
            </div>
            <div className={`flex-1 min-w-0 rounded-xl border transition-all p-4 ${locked
              ? 'bg-gray-50/60 border-gray-200 opacity-70'
              : completed
                ? 'bg-emerald-50/40 border-emerald-200'
                : active
                  ? 'bg-white border-indigo-200 shadow-md'
                  : 'bg-white border-gray-200 hover:border-gray-300'}`}>
              <div className="flex items-start justify-between gap-3 mb-1 flex-wrap">
                <h3 className="text-sm font-bold text-gray-900 leading-tight">
                  {c.badgeIcon && <span className="mr-1.5">{c.badgeIcon}</span>}
                  {c.courseName}
                </h3>
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase tracking-wide flex-shrink-0 ${
                  completed ? 'bg-emerald-100 text-emerald-700' :
                  locked    ? 'bg-gray-100 text-gray-500' :
                  active    ? 'bg-indigo-100 text-indigo-700' :
                              'bg-white border border-gray-200 text-gray-600'}`}>
                  {completed ? 'Completed' : locked ? 'Locked' : active ? 'In progress' : 'Unlocked'}
                </span>
              </div>
              {c.description && <p className="text-xs text-gray-600 line-clamp-2 mb-2">{c.description}</p>}
              <div className="flex items-center gap-3 text-[11px] text-gray-500 flex-wrap">
                {c.difficulty && <span className="capitalize">{c.difficulty}</span>}
                {c.durationHours > 0 && <><span className="text-gray-300">·</span><span>⏱ {c.durationHours}h</span></>}
                {c.instructor && <><span className="text-gray-300">·</span><span>By {c.instructor}</span></>}
                {c.skillName && <><span className="text-gray-300">·</span><span className="text-indigo-600 font-medium">{c.skillName}</span></>}
              </div>
              {/* Per-course progress when in progress */}
              {!locked && c.progress > 0 && c.progress < 100 && (
                <div className="mt-3">
                  <div className="flex justify-between text-[10px] text-gray-500 mb-1"><span>Progress</span><span className="font-semibold">{c.progress}%</span></div>
                  <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${c.progress}%`, background: `linear-gradient(90deg, ${accent}, #8b5cf6)` }} />
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  </div>
);

const CreatePathModal = ({ initial = null, onSave, onClose, title = 'New Learning Path' }) => {
  const [form, setForm] = useState({
    title:       initial?.title || '',
    description: initial?.description || '',
    difficulty:  initial?.difficulty || 'beginner',
    icon:        initial?.icon || '🎓',
    accentColor: initial?.accentColor || '#6366f1',
    targetAudience: initial?.targetAudience || '',
    status:      initial?.status || 'draft',
  });
  const [saving, setSaving] = useState(false);
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    setSaving(true);
    await onSave(form);
    setSaving(false);
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-[60] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="h-1.5" style={{ background: `linear-gradient(90deg, ${form.accentColor}, #8b5cf6 60%, ${form.accentColor}80)` }} />
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-bold text-gray-900">{title}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">×</button>
        </div>
        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Title *</label>
            <input required value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              placeholder="e.g. Cloudflare Workers Mastery"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Description</label>
            <textarea rows={3} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder="Who's this for and what will they learn?"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Difficulty</label>
              <select value={form.difficulty} onChange={e => setForm(f => ({ ...f, difficulty: e.target.value }))}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500">
                <option value="beginner">Beginner</option>
                <option value="intermediate">Intermediate</option>
                <option value="advanced">Advanced</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Status</label>
              <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500">
                <option value="draft">Draft</option>
                <option value="published">Published</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Icon</label>
            <div className="grid grid-cols-8 gap-1.5">
              {PATH_ICON_OPTIONS.map(ic => (
                <button key={ic} type="button" onClick={() => setForm(f => ({ ...f, icon: ic }))}
                  className={`w-9 h-9 rounded-lg text-xl flex items-center justify-center transition-all ${form.icon === ic ? 'bg-indigo-100 ring-2 ring-indigo-500' : 'bg-gray-50 hover:bg-gray-100'}`}>
                  {ic}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Accent color</label>
            <div className="flex flex-wrap gap-1.5">
              {PATH_ACCENT_OPTIONS.map(c => (
                <button key={c} type="button" onClick={() => setForm(f => ({ ...f, accentColor: c }))}
                  className={`w-7 h-7 rounded-full transition-all ${form.accentColor === c ? 'ring-2 ring-offset-2 ring-gray-400 scale-110' : 'hover:scale-110'}`}
                  style={{ background: c }} />
              ))}
            </div>
          </div>
        </form>
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2 bg-gray-50/50">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 font-medium">Cancel</button>
          <button type="button" onClick={handleSubmit} disabled={saving || !form.title.trim()}
            className="px-5 py-2 text-sm rounded-lg bg-gradient-to-r from-indigo-600 to-violet-600 text-white font-medium shadow-sm hover:shadow-md disabled:opacity-50">
            {saving ? 'Saving…' : initial ? 'Save changes' : 'Create path'}
          </button>
        </div>
      </div>
    </div>
  );
};

const ManagePathCoursesModal = ({ path, allCourses, onSave, onClose }) => {
  const initialIds = (path.courses || []).map(c => c.id);
  const [selected, setSelected] = useState(initialIds);
  const [search, setSearch] = useState('');
  const move = (idx, delta) => {
    const j = idx + delta;
    if (j < 0 || j >= selected.length) return;
    const next = [...selected];
    [next[idx], next[j]] = [next[j], next[idx]];
    setSelected(next);
  };
  const remove = (id) => setSelected(selected.filter(x => x !== id));
  const add = (id) => { if (!selected.includes(id)) setSelected([...selected, id]); };
  const courseById = Object.fromEntries(allCourses.map(c => [c.id, c]));
  const available = allCourses.filter(c => !selected.includes(c.id) && (!search || (c.courseName || '').toLowerCase().includes(search.toLowerCase())));
  return (
    <div className="fixed inset-0 bg-black/40 z-[60] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full overflow-hidden flex flex-col max-h-[90vh]" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Manage curriculum</h2>
            <p className="text-xs text-gray-500 mt-0.5">Drag-order courses in the path. The first course unlocks immediately; each next one unlocks when the previous completes.</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">×</button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-gray-100 flex-1 overflow-hidden">
          {/* Selected (ordered) */}
          <div className="p-5 overflow-y-auto">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">In this path ({selected.length})</h3>
            {selected.length === 0 ? (
              <div className="text-sm text-gray-400 italic">No courses yet — add some from the right.</div>
            ) : (
              <div className="space-y-2">
                {selected.map((id, i) => {
                  const c = courseById[id];
                  if (!c) return null;
                  return (
                    <div key={id} className="flex items-center gap-2 p-2 bg-gray-50 border border-gray-200 rounded-lg">
                      <div className="w-6 h-6 rounded bg-indigo-100 text-indigo-700 text-xs font-bold flex items-center justify-center flex-shrink-0">{i + 1}</div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-900 truncate">{c.badgeIcon || '📘'} {c.courseName}</div>
                        <div className="text-[10px] text-gray-500">{c.difficulty} · {c.durationHours}h</div>
                      </div>
                      <button onClick={() => move(i, -1)} disabled={i === 0} className="px-1 text-gray-400 hover:text-gray-700 disabled:opacity-30">▲</button>
                      <button onClick={() => move(i, 1)} disabled={i === selected.length - 1} className="px-1 text-gray-400 hover:text-gray-700 disabled:opacity-30">▼</button>
                      <button onClick={() => remove(id)} className="text-xs text-red-500 hover:text-red-700 px-1.5">✕</button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          {/* Available */}
          <div className="p-5 overflow-y-auto bg-gray-50/30">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Add from catalog</h3>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search courses…"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm mb-3 focus:ring-2 focus:ring-indigo-500" />
            <div className="space-y-1.5">
              {available.length === 0 ? (
                <div className="text-sm text-gray-400 italic">{search ? 'No matches' : 'All catalog courses are in the path'}</div>
              ) : available.map(c => (
                <button key={c.id} onClick={() => add(c.id)}
                  className="w-full text-left flex items-center gap-2 p-2 bg-white border border-gray-200 rounded-lg hover:border-indigo-300 hover:shadow-sm transition-all">
                  <span className="text-base">{c.badgeIcon || '📘'}</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-800 truncate">{c.courseName}</div>
                    <div className="text-[10px] text-gray-500">{c.difficulty} · {c.durationHours}h</div>
                  </div>
                  <span className="text-indigo-600 text-lg">+</span>
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2 bg-gray-50/50">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 font-medium">Cancel</button>
          <button type="button" onClick={() => onSave(selected)}
            className="px-5 py-2 text-sm rounded-lg bg-gradient-to-r from-indigo-600 to-violet-600 text-white font-medium shadow-sm hover:shadow-md">
            Save curriculum
          </button>
        </div>
      </div>
    </div>
  );
};

const AssignPathUsersModal = ({ path, users, currentEnrollments, onAssign, onClose }) => {
  const enrolledIds = new Set(currentEnrollments.map(e => e.userId));
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [search, setSearch] = useState('');
  const toggle = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedIds(next);
  };
  const available = users.filter(u => !enrolledIds.has(u.user_id) && (!search ||
    (u.user_name || '').toLowerCase().includes(search.toLowerCase()) ||
    (u.user_role || '').toLowerCase().includes(search.toLowerCase())));
  return (
    <div className="fixed inset-0 bg-black/40 z-[60] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden flex flex-col max-h-[90vh]" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Assign users to path</h2>
            <p className="text-xs text-gray-500 mt-0.5">They'll be auto-enrolled in every course in the curriculum.</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">×</button>
        </div>
        <div className="px-6 py-4 border-b border-gray-100">
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name or role…"
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500" />
        </div>
        <div className="flex-1 overflow-y-auto px-2 py-2">
          {available.length === 0 ? (
            <div className="text-center py-8 text-sm text-gray-400 italic">{users.length === 0 ? 'No users in the directory' : 'Everyone is already enrolled'}</div>
          ) : available.map(u => {
            const checked = selectedIds.has(u.user_id);
            return (
              <label key={u.user_id} className={`flex items-center gap-3 px-3 py-2 mx-1 rounded-lg cursor-pointer ${checked ? 'bg-indigo-50' : 'hover:bg-gray-50'}`}>
                <input type="checkbox" checked={checked} onChange={() => toggle(u.user_id)} className="rounded text-indigo-600 focus:ring-indigo-500" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-800 truncate">{u.user_name}</div>
                  <div className="text-[11px] text-gray-500">{u.user_role}</div>
                </div>
              </label>
            );
          })}
        </div>
        <div className="px-6 py-4 border-t border-gray-200 flex justify-between items-center bg-gray-50/50">
          <span className="text-xs text-gray-500">{selectedIds.size} selected</span>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 font-medium">Cancel</button>
            <button onClick={() => onAssign([...selectedIds])} disabled={selectedIds.size === 0}
              className="px-5 py-2 text-sm rounded-lg bg-gradient-to-r from-indigo-600 to-violet-600 text-white font-medium shadow-sm disabled:opacity-50">
              Assign {selectedIds.size > 0 ? `${selectedIds.size}` : ''}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// LEARNTRACK MODALS
// ============================================================================

const EnrollUserModal = ({ course, users = [], excludeUserIds = new Set(), onEnroll, onClose }) => {
  const [search, setSearch] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const filtered = users.filter(u => {
    if (excludeUserIds.has(u.user_id)) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return (u.user_name || '').toLowerCase().includes(q) || (u.user_role || '').toLowerCase().includes(q);
  });
  const handlePick = async (u) => {
    if (submitting) return;
    setSubmitting(true);
    await onEnroll({ courseId: course.id, userId: u.user_id, userName: u.user_name });
    onClose();
  };
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-gray-200 flex justify-between items-center">
          <div>
            <h2 className="text-base font-semibold">Enroll User</h2>
            <p className="text-xs text-gray-500 mt-0.5 truncate">{course.courseName}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">×</button>
        </div>
        <div className="p-3 border-b border-gray-100">
          <input autoFocus value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name or role…"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500" />
        </div>
        <div className="flex-1 overflow-y-auto">
          {filtered.length === 0 ? (
            <div className="p-6 text-center text-gray-400 text-sm">
              {users.length === 0 ? 'No users found.' : excludeUserIds.size > 0 && search === '' ? 'All active users are already enrolled.' : 'No matches.'}
            </div>
          ) : filtered.map(u => (
            <button key={u.user_id} disabled={submitting} onClick={() => handlePick(u)}
              className="w-full flex items-center justify-between gap-3 px-4 py-2.5 hover:bg-orange-50 border-b border-gray-100 text-left disabled:opacity-50">
              <div className="min-w-0">
                <div className="text-sm font-medium text-gray-900 truncate">{u.user_name}</div>
                {u.user_role && <div className="text-xs text-gray-500 capitalize">{u.user_role.replace(/_/g, ' ')}</div>}
              </div>
              <span className="text-xs text-orange-600 font-medium shrink-0">Enroll →</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

const AddCourseModal = ({ onClose, onSubmit, skills }) => {
  const [form, setForm] = useState({
    courseName: '', description: '', skillId: '', difficulty: 'beginner',
    durationHours: 1, instructor: '', targetGroup: 'all', badgeName: '', badgeIcon: '🏅'
  });
  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({ ...form, skillId: form.skillId ? parseInt(form.skillId) : null });
  };
  const badgeIcons = ['🏅', '⚛️', '🟨', '🐍', '🚀', '👑', '🛡️', '⭐', '🎯', '💎', '🔥', '🌟'];
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-xl font-semibold">New Course</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">×</button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Course Name *</label>
            <input required value={form.courseName} onChange={e => setForm({...form, courseName: e.target.value})}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500" placeholder="e.g., React Advanced Patterns" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Skill</label>
              <select value={form.skillId} onChange={e => setForm({...form, skillId: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500">
                <option value="">Select skill...</option>
                {(skills || []).map(s => <option key={s.id} value={s.id}>{s.skillName}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Difficulty</label>
              <select value={form.difficulty} onChange={e => setForm({...form, difficulty: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500">
                <option value="beginner">Beginner</option><option value="intermediate">Intermediate</option><option value="advanced">Advanced</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Duration (hours)</label>
              <input type="number" min="0.5" step="0.5" value={form.durationHours} onChange={e => setForm({...form, durationHours: parseFloat(e.target.value)})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Target Group</label>
              <select value={form.targetGroup} onChange={e => setForm({...form, targetGroup: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500">
                <option value="all">All Users</option><option value="developer">Developers</option><option value="admin">Admins</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Instructor</label>
            <input value={form.instructor} onChange={e => setForm({...form, instructor: e.target.value})}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500" placeholder="Instructor name" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={3}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500" placeholder="Course description..." />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Badge Name (awarded on completion)</label>
            <input value={form.badgeName} onChange={e => setForm({...form, badgeName: e.target.value})}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500" placeholder="e.g., React Pro" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Badge Icon</label>
            <div className="flex gap-2 flex-wrap">
              {badgeIcons.map(icon => (
                <button key={icon} type="button" onClick={() => setForm({...form, badgeIcon: icon})}
                  className={`w-10 h-10 text-xl rounded border-2 hover:border-orange-500 ${form.badgeIcon === icon ? 'border-orange-600 bg-orange-50' : 'border-gray-200'}`}>{icon}</button>
              ))}
            </div>
          </div>
          <div className="flex gap-3 pt-4">
            <button type="submit" className="flex-1 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 font-medium">Create Course</button>
            <button type="button" onClick={onClose} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const CourseDetailModal = ({ course, users = [], user, onClose, onUpdate, onDelete, onEnroll, onUpdateEnrollment, onDeleteEnrollment }) => {
  const [editing, setEditing] = useState(false);
  const [showEnroll, setShowEnroll] = useState(false);
  const [reassigningId, setReassigningId] = useState(null);
  const enrolledIds = new Set((course.enrolledUsers || []).map(eu => eu.userId));
  const role = user?.role || user?.user_role || '';
  const canManageEnrollments = role === 'admin' || role === 'manager' || role.endsWith('_manager');
  const [form, setForm] = useState({
    courseName: course.courseName || '', description: course.description || '',
    difficulty: course.difficulty || 'beginner', durationHours: course.durationHours || 1,
    instructor: course.instructor || '', targetGroup: course.targetGroup || 'all',
    status: course.status || 'published'
  });
  const handleUpdate = (e) => { e.preventDefault(); onUpdate(course.id, form); setEditing(false); };
  const handleDelete = () => requestConfirm(`Delete course "${course.courseName}"?`, () => onDelete(course.id));
  const diff = getDifficultyBadge(course.difficulty);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <span className="text-3xl">{course.badgeIcon || '📚'}</span>
            <div>
              <h2 className="text-xl font-semibold">{course.courseName}</h2>
              {course.skillName && <span className="text-xs text-blue-600">{course.skillName}</span>}
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">×</button>
        </div>
        {!editing ? (
          <div className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <span className="px-2 py-0.5 rounded text-xs font-semibold" style={{ background: diff.bg, color: diff.color }}>{diff.label}</span>
              <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">{getGroupLabel(course.targetGroup)}</span>
              <span className={`px-2 py-0.5 rounded text-xs font-semibold ${course.status === 'published' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>{course.status}</span>
            </div>
            {course.description && <p className="text-sm text-gray-600 mb-4">{course.description}</p>}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div><h3 className="text-xs font-semibold text-gray-500 mb-1">Duration</h3><p className="text-sm text-gray-900">{course.durationHours} hours</p></div>
              <div><h3 className="text-xs font-semibold text-gray-500 mb-1">Instructor</h3><p className="text-sm text-gray-900">{course.instructor || 'TBD'}</p></div>
              {course.badgeName && <div><h3 className="text-xs font-semibold text-gray-500 mb-1">Badge</h3><p className="text-sm text-gray-900">{course.badgeIcon} {course.badgeName}</p></div>}
            </div>

            {/* Enrolled Users */}
            <h3 className="text-sm font-semibold text-gray-900 mb-2">Enrolled Users ({(course.enrolledUsers || []).length})</h3>
            <div className="bg-gray-50 rounded-lg border border-gray-200 mb-4">
              {(course.enrolledUsers || []).length === 0 ? (
                <div className="p-4 text-center text-gray-400 text-sm">No enrollments yet</div>
              ) : (
                <table className="w-full">
                  <thead><tr>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">User</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Status</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Progress</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Score</th>
                    {canManageEnrollments && (onUpdateEnrollment || onDeleteEnrollment) && <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600">Actions</th>}
                  </tr></thead>
                  <tbody className="divide-y divide-gray-200">
                    {(course.enrolledUsers || []).map(eu => {
                      const sb = getEnrollmentStatusBadge(eu.status);
                      return (
                        <tr key={eu.id}>
                          <td className="px-3 py-2 text-sm text-gray-900">{eu.userName}</td>
                          <td className="px-3 py-2"><span className="px-2 py-0.5 rounded text-xs font-semibold" style={{ background: sb.bg, color: sb.color }}>{sb.label}</span></td>
                          <td className="px-3 py-2">
                            <div className="flex items-center gap-2">
                              <div className="w-16 h-1.5 bg-gray-200 rounded-full overflow-hidden"><div className="h-full rounded-full bg-blue-500" style={{ width: `${eu.progress}%` }} /></div>
                              <span className="text-xs text-gray-500">{eu.progress}%</span>
                            </div>
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-600">{eu.score != null ? `${eu.score}%` : '-'}</td>
                          {canManageEnrollments && (onUpdateEnrollment || onDeleteEnrollment) && (
                            <td className="px-3 py-2 text-right">
                              <div className="inline-flex gap-1">
                                {onUpdateEnrollment && (
                                  <button onClick={() => setReassigningId(eu.id)}
                                    className="px-2 py-0.5 text-xs text-blue-700 bg-blue-50 rounded hover:bg-blue-100">Reassign</button>
                                )}
                                {onDeleteEnrollment && (
                                  <button onClick={() => requestConfirm(`Remove ${eu.userName} from this course?`, () => onDeleteEnrollment(eu.id))}
                                    className="px-2 py-0.5 text-xs text-red-700 bg-red-50 rounded hover:bg-red-100">Remove</button>
                                )}
                              </div>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
            <div className="flex gap-2 mb-4">
              <button onClick={() => setShowEnroll(true)} className="px-3 py-1.5 bg-orange-500 text-white text-xs rounded hover:bg-orange-600 font-medium">+ Enroll User</button>
            </div>
            {showEnroll && (
              <EnrollUserModal course={course} users={users}
                excludeUserIds={enrolledIds}
                onEnroll={onEnroll} onClose={() => setShowEnroll(false)} />
            )}
            {canManageEnrollments && reassigningId && (() => {
              const current = (course.enrolledUsers || []).find(eu => eu.id === reassigningId);
              if (!current) return null;
              // Allow all users except those already enrolled in this course
              const exclude = new Set([...enrolledIds].filter(id => id !== current.userId));
              return (
                <EnrollUserModal
                  course={{ ...course, courseName: `Reassign: ${current.userName} → ?` }}
                  users={users}
                  excludeUserIds={exclude}
                  onEnroll={async ({ userId, userName }) => {
                    await onUpdateEnrollment(reassigningId, { userId, userName });
                  }}
                  onClose={() => setReassigningId(null)} />
              );
            })()}
            <div className="flex gap-2 pt-4 border-t border-gray-200">
              <button onClick={() => setEditing(true)} className="px-4 py-2 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200">Edit</button>
              <button onClick={handleDelete} className="px-4 py-2 bg-red-100 text-red-700 text-sm rounded-lg hover:bg-red-200">Delete</button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleUpdate} className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Course Name</label>
              <input required value={form.courseName} onChange={e => setForm({...form, courseName: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Difficulty</label>
                <select value={form.difficulty} onChange={e => setForm({...form, difficulty: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500">
                  <option value="beginner">Beginner</option><option value="intermediate">Intermediate</option><option value="advanced">Advanced</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Target Group</label>
                <select value={form.targetGroup} onChange={e => setForm({...form, targetGroup: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500">
                  <option value="all">All Users</option><option value="developer">Developers</option><option value="admin">Admins</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select value={form.status} onChange={e => setForm({...form, status: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500">
                <option value="draft">Draft</option><option value="published">Published</option><option value="archived">Archived</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={3}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500" />
            </div>
            <div className="flex gap-2 pt-4">
              <button type="submit" className="flex-1 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600">Save Changes</button>
              <button type="button" onClick={() => setEditing(false)} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">Cancel</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// QMS (QUALITY MANAGEMENT SYSTEM) VIEWS
// ============================================================================

const qmsSeverityStyle = (s) => {
  const m = { critical: { bg: '#fef2f2', color: '#dc2626' }, high: { bg: '#fff7ed', color: '#ea580c' }, medium: { bg: '#fffbeb', color: '#d97706' }, low: { bg: '#eff6ff', color: '#2563eb' } };
  return m[s] || m.medium;
};
const qmsStatusStyle = (s) => {
  const m = { pending: { bg: '#fefce8', color: '#ca8a04' }, approved: { bg: '#f0fdf4', color: '#16a34a' }, rejected: { bg: '#fef2f2', color: '#dc2626' }, pass: { bg: '#f0fdf4', color: '#16a34a' }, fail: { bg: '#fef2f2', color: '#dc2626' }, open: { bg: '#eff6ff', color: '#2563eb' }, in_progress: { bg: '#fefce8', color: '#ca8a04' }, for_qa: { bg: '#fdf2f8', color: '#be185d' }, resolved: { bg: '#f0fdf4', color: '#16a34a' }, closed: { bg: '#f3f4f6', color: '#6b7280' } };
  return m[s] || m.pending;
};
const qmsStageLabels = { requirements: 'Review Requirements', testplan: 'Create Test Plan', testcases: 'Prepare Test Cases', testing: 'Execute Testing', uat: 'UAT Sign-off', verification: 'Production Verification' };

const QmsWorkflowSteps = ({ currentStage, onStageChange }) => {
  const stages = ['requirements', 'testplan', 'testcases', 'testing', 'uat', 'verification'];
  const icons = ['📋', '📝', '✅', '🧪', '✍️', '🚀'];
  return (
    <div className="bg-white rounded-lg shadow border border-gray-200 p-5 mb-6">
      <h2 className="text-sm font-semibold text-gray-700 mb-4">QA Workflow Stages</h2>
      <div className="flex gap-2 overflow-x-auto pb-2">
        {stages.map((s, i) => (
          <button key={s} onClick={() => onStageChange(s)}
            className={`flex-1 min-w-[130px] px-3 py-3 rounded-lg text-center text-xs font-semibold transition-all ${currentStage === s ? 'bg-teal-600 text-white shadow-md' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
            <span className="text-lg block mb-1">{icons[i]}</span>
            {qmsStageLabels[s]}
            {i < stages.length - 1 && <span className="hidden sm:inline text-gray-300 ml-1">→</span>}
          </button>
        ))}
      </div>
    </div>
  );
};

const QmsMetrics = ({ metrics, stage }) => {
  let cards = [];
  if (stage === 'requirements') {
    cards = [
      { label: 'Total Requirements', value: metrics?.requirements?.total || 0, sub: metrics?.requirements?.topLevel != null ? `${metrics.requirements.topLevel} top-level` : null, icon: '📋', color: '#20c997' },
      { label: 'Approved', value: metrics?.requirements?.approved || 0, icon: '✅', color: '#16a34a' },
      { label: 'Pending Review', value: metrics?.requirements?.pending || 0, icon: '⏳', color: '#d97706' }
    ];
  } else if (stage === 'testing' || stage === 'testcases') {
    cards = [
      { label: 'Total Test Cases', value: metrics?.testCases?.total || 0, icon: '🧪', color: '#20c997' },
      { label: 'Passed', value: metrics?.testCases?.passed || 0, icon: '✅', color: '#16a34a' },
      { label: 'Failed', value: metrics?.testCases?.failed || 0, icon: '❌', color: '#dc2626' }
    ];
  } else if (stage === 'uat' || stage === 'verification') {
    cards = [
      { label: 'Pass Rate', value: `${metrics?.passRate || 0}%`, icon: '📊', color: '#16a34a' },
      { label: 'Open Defects', value: metrics?.bugs?.open || 0, icon: '🐛', color: '#ea580c' },
      { label: 'Resolved', value: metrics?.bugs?.resolved || 0, icon: '✅', color: '#20c997' }
    ];
  } else {
    cards = [
      { label: 'Requirements', value: metrics?.requirements?.total || 0, icon: '📋', color: '#20c997' },
      { label: 'Test Cases', value: metrics?.testCases?.total || 0, icon: '🧪', color: '#2563eb' },
      { label: 'Bugs', value: metrics?.bugs?.total || 0, icon: '🐛', color: '#ea580c' }
    ];
  }
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
      {cards.map(c => (
        <div key={c.label} className="bg-white rounded-lg shadow border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-600">{c.label}</span>
            <span className="text-2xl">{c.icon}</span>
          </div>
          <div className="text-3xl font-bold" style={{ color: c.color }}>{c.value}</div>
          {c.sub && <div className="text-xs text-gray-400 mt-1">{c.sub}</div>}
        </div>
      ))}
    </div>
  );
};

const REQ_TYPE_COLOR_PALETTE = {
  teal:   { header: '#0f766e', light: '#f0fdfa', border: '#99f6e4', text: '#0f766e' },
  red:    { header: '#b91c1c', light: '#fef2f2', border: '#fecaca', text: '#b91c1c' },
  blue:   { header: '#1d4ed8', light: '#eff6ff', border: '#bfdbfe', text: '#1d4ed8' },
  purple: { header: '#7e22ce', light: '#faf5ff', border: '#e9d5ff', text: '#7e22ce' },
  orange: { header: '#c2410c', light: '#fff7ed', border: '#fed7aa', text: '#c2410c' },
  green:  { header: '#15803d', light: '#f0fdf4', border: '#bbf7d0', text: '#15803d' },
  amber:  { header: '#b45309', light: '#fffbeb', border: '#fde68a', text: '#b45309' },
  pink:   { header: '#be185d', light: '#fdf2f8', border: '#fbcfe8', text: '#be185d' },
  slate:  { header: '#334155', light: '#f8fafc', border: '#cbd5e1', text: '#334155' },
};

const DEFAULT_REQ_TYPES = [
  { id: 1, name: 'functional',  label: 'Functional Requirements',  color: 'teal',   sort_order: 1 },
  { id: 2, name: 'security',    label: 'Security Requirements',    color: 'red',    sort_order: 2 },
  { id: 3, name: 'performance', label: 'Performance Requirements', color: 'blue',   sort_order: 3 },
  { id: 4, name: 'usability',   label: 'Usability Requirements',   color: 'purple', sort_order: 4 },
];

const ReqTypeColorSwatch = ({ selected, onChange }) => (
  <div className="flex gap-1.5 flex-wrap">
    {Object.keys(REQ_TYPE_COLOR_PALETTE).map(c => (
      <button key={c} type="button" onClick={() => onChange(c)}
        className={`w-6 h-6 rounded-full border-2 transition-transform hover:scale-110 ${selected === c ? 'border-gray-800 scale-110' : 'border-transparent'}`}
        style={{ background: REQ_TYPE_COLOR_PALETTE[c].header }} title={c} />
    ))}
  </div>
);

const ManageReqTypesModal = ({ onClose, onRefresh }) => {
  const [types, setTypes] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ label: '', color: 'teal' });
  const [newForm, setNewForm] = useState({ label: '', color: 'teal' });
  const [showAdd, setShowAdd] = useState(false);
  const [error, setError] = useState('');

  const load = () => ModulesModel.getReqTypes().then(ts => setTypes(Array.isArray(ts) ? ts : []));
  useEffect(() => { load(); }, []);

  const startEdit = t => { setEditingId(t.id); setEditForm({ label: t.label, color: t.color || 'teal' }); setError(''); };
  const cancelEdit = () => { setEditingId(null); setError(''); };

  const saveEdit = async () => {
    if (!editForm.label.trim()) { setError('Label is required'); return; }
    const r = await ModulesModel.updateReqType(editingId, editForm);
    if (r?.success === false) { setError(r.error || 'Failed to save'); return; }
    setEditingId(null); setError(''); load(); onRefresh();
  };

  const handleDelete = async (id) => {
    const r = await ModulesModel.deleteReqType(id);
    if (r?.success === false) { setError(r.error || 'Cannot delete'); return; }
    setError(''); load(); onRefresh();
  };

  const handleAdd = async () => {
    if (!newForm.label.trim()) { setError('Label is required'); return; }
    const r = await ModulesModel.createReqType(newForm);
    if (r?.success === false) { setError(r.error || 'Failed to add'); return; }
    setNewForm({ label: '', color: 'teal' }); setShowAdd(false); setError(''); load(); onRefresh();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg">
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-bold text-gray-900">Manage Requirement Types</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl font-bold">&times;</button>
        </div>
        <div className="p-6 space-y-2 max-h-[70vh] overflow-y-auto">
          {error && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-3">{error}</p>}
          {types.map(t => {
            const st = REQ_TYPE_COLOR_PALETTE[t.color] || REQ_TYPE_COLOR_PALETTE.teal;
            if (editingId === t.id) {
              return (
                <div key={t.id} className="border border-blue-200 bg-blue-50 rounded-lg p-4 space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Label</label>
                    <input value={editForm.label} onChange={e => setEditForm(f => ({ ...f, label: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-2">Color</label>
                    <ReqTypeColorSwatch selected={editForm.color} onChange={c => setEditForm(f => ({ ...f, color: c }))} />
                  </div>
                  <div className="flex gap-2">
                    <button onClick={saveEdit} className="px-3 py-1.5 bg-teal-600 text-white rounded-lg text-xs font-medium hover:bg-teal-700">Save</button>
                    <button onClick={cancelEdit} className="px-3 py-1.5 border border-gray-300 text-gray-600 rounded-lg text-xs font-medium hover:bg-gray-50">Cancel</button>
                  </div>
                </div>
              );
            }
            return (
              <div key={t.id} className="flex items-center gap-3 px-4 py-3 border border-gray-200 rounded-lg group hover:bg-gray-50">
                <span className="w-3 h-3 rounded-full shrink-0" style={{ background: st.header }} />
                <span className="flex-1 text-sm font-medium text-gray-800">{t.label}</span>
                <span className="text-xs text-gray-400 font-mono">{t.name}</span>
                <button onClick={() => startEdit(t)} className="p-1.5 text-gray-300 hover:text-blue-500 rounded opacity-0 group-hover:opacity-100 transition-opacity"><Pencil size={14} /></button>
                <button onClick={() => requestConfirm(`Delete type "${t.label}"?`, () => handleDelete(t.id))} className="p-1.5 text-gray-300 hover:text-red-500 rounded opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 size={14} /></button>
              </div>
            );
          })}

          {/* Add new type */}
          {showAdd ? (
            <div className="border border-teal-200 bg-teal-50 rounded-lg p-4 space-y-3 mt-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Label *</label>
                <input value={newForm.label} onChange={e => setNewForm(f => ({ ...f, label: e.target.value }))}
                  placeholder="e.g. Business Requirements, Compliance Requirements…"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-2">Color</label>
                <ReqTypeColorSwatch selected={newForm.color} onChange={c => setNewForm(f => ({ ...f, color: c }))} />
              </div>
              <div className="flex gap-2">
                <button onClick={handleAdd} className="px-3 py-1.5 bg-teal-600 text-white rounded-lg text-xs font-medium hover:bg-teal-700">Add Type</button>
                <button onClick={() => { setShowAdd(false); setNewForm({ label: '', color: 'teal' }); setError(''); }}
                  className="px-3 py-1.5 border border-gray-300 text-gray-600 rounded-lg text-xs font-medium hover:bg-gray-50">Cancel</button>
              </div>
            </div>
          ) : (
            <button onClick={() => { setShowAdd(true); setEditingId(null); }}
              className="w-full mt-2 px-4 py-2.5 border-2 border-dashed border-gray-300 text-gray-500 rounded-lg text-sm font-medium hover:border-teal-400 hover:text-teal-600 transition-colors">
              + Add New Requirement Type
            </button>
          )}
        </div>
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
          <button onClick={onClose} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200">Close</button>
        </div>
      </div>
    </div>
  );
};

const QmsReqEditForm = ({ editForm: initialForm, reqTypes, onSave, onCancel }) => {
  const [form, setForm] = React.useState(initialForm);
  const update = (field, value) => setForm(f => ({ ...f, [field]: value }));
  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-2">
      <div className="space-y-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Title</label>
          <input value={form.title || ''} onChange={e => update('title', e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400" />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Type</label>
            <select value={form.reqType || ''} onChange={e => update('reqType', e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:ring-2 focus:ring-teal-400">
              {reqTypes.map(t => <option key={t.name} value={t.name}>{t.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Priority</label>
            <select value={form.priority || 'medium'} onChange={e => update('priority', e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:ring-2 focus:ring-teal-400">
              <option value="critical">Critical</option><option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Category</label>
            <input value={form.parentCategory || ''} onChange={e => update('parentCategory', e.target.value)}
              placeholder="Category…" className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:ring-2 focus:ring-teal-400" />
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
          <MiniWysiwyg value={form.description || ''} onChange={v => update('description', v)} placeholder="Describe the requirement…" minHeight="60px" />
        </div>
        <div className="flex gap-2">
          <button onClick={() => onSave(form)} className="px-3 py-1.5 bg-teal-600 text-white rounded-lg text-xs font-medium hover:bg-teal-700">Save</button>
          <button onClick={onCancel} className="px-3 py-1.5 border border-gray-300 text-gray-600 rounded-lg text-xs font-medium hover:bg-gray-50">Cancel</button>
        </div>
      </div>
    </div>
  );
};

// ---- Environment Promotion Components ----
const ENV_STYLES = {
  DEV:        { bg: '#dbeafe', color: '#1d4ed8', border: '#93c5fd' },
  Staging:    { bg: '#fef3c7', color: '#b45309', border: '#fcd34d' },
  UAT:        { bg: '#ede9fe', color: '#7c3aed', border: '#c4b5fd' },
  Production: { bg: '#dcfce7', color: '#15803d', border: '#86efac' },
};
const ENV_ORDER = ['DEV', 'Staging', 'UAT', 'Production'];

const EnvBadge = ({ env }) => {
  const s = ENV_STYLES[env] || { bg: '#f3f4f6', color: '#6b7280', border: '#d1d5db' };
  return (
    <span className="px-1.5 py-0.5 rounded text-[10px] font-bold leading-none"
      style={{ background: s.bg, color: s.color }}>{env}</span>
  );
};

const PromoteModal = ({ artifacts, artifactType, onClose, onPromote, projectId }) => {
  const [targetEnvs, setTargetEnvs] = useState([]);
  const [includeLinked, setIncludeLinked] = useState(true);
  const [promoting, setPromoting] = useState(false);
  const [done, setDone] = useState(null);

  const toggleEnv = (env) => setTargetEnvs(prev => prev.includes(env) ? prev.filter(e => e !== env) : [...prev, env]);

  const handlePromote = async () => {
    if (!targetEnvs.length) return;
    setPromoting(true);
    const allResults = [];
    for (const env of targetEnvs) {
      const res = await onPromote({ artifactType, artifactIds: artifacts.map(a => a.id), targetEnvironment: env, projectId, includeLinked });
      allResults.push({ env, promoted: res?.promoted || 0, success: res?.success });
    }
    setDone(allResults);
    setPromoting(false);
  };

  const currentEnvs = [...new Set(artifacts.map(a => a.environment || 'DEV'))];
  const availableEnvs = ENV_ORDER.filter(e => !currentEnvs.includes(e));

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-gray-100">
          <div>
            <h2 className="text-base font-bold text-gray-900">Promote to Environment</h2>
            <p className="text-xs text-gray-500 mt-0.5">{artifacts.length} {artifactType.replace('_', ' ')}{artifacts.length !== 1 ? 's' : ''} selected</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">&times;</button>
        </div>

        {done ? (
          <div className="p-6">
            <div className="text-center mb-5">
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-3">
                <span className="text-green-600 text-xl">✓</span>
              </div>
              <h3 className="font-semibold text-gray-900">Promotion Complete</h3>
            </div>
            <div className="space-y-2 mb-5">
              {done.map(r => (
                <div key={r.env} className="flex items-center justify-between py-2 px-3 rounded-lg" style={{ background: ENV_STYLES[r.env]?.bg || '#f9fafb' }}>
                  <EnvBadge env={r.env} />
                  <span className="text-sm font-medium" style={{ color: ENV_STYLES[r.env]?.color || '#374151' }}>
                    {r.promoted} artifact{r.promoted !== 1 ? 's' : ''} created
                  </span>
                </div>
              ))}
            </div>
            <button onClick={onClose} className="w-full py-2.5 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700">Done</button>
          </div>
        ) : (
          <>
            <div className="p-6 space-y-5">
              {/* Preview */}
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Selected Artifacts</p>
                <div className="max-h-28 overflow-y-auto space-y-1 rounded-lg border border-gray-100 p-2 bg-gray-50">
                  {artifacts.slice(0, 8).map(a => (
                    <div key={a.id} className="flex items-center gap-2 text-xs py-0.5">
                      <span className="font-mono text-gray-400 shrink-0">{a.reqCode || a.tcCode || `#${a.id}`}</span>
                      <span className="text-gray-700 truncate flex-1">{a.title}</span>
                      <EnvBadge env={a.environment || 'DEV'} />
                    </div>
                  ))}
                  {artifacts.length > 8 && <p className="text-xs text-gray-400 px-1">+{artifacts.length - 8} more</p>}
                </div>
              </div>

              {/* Target environments */}
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Promote to</p>
                {availableEnvs.length === 0 ? (
                  <p className="text-sm text-gray-400 italic">All environments already covered.</p>
                ) : (
                  <div className="grid grid-cols-2 gap-2">
                    {availableEnvs.map(env => {
                      const s = ENV_STYLES[env];
                      const sel = targetEnvs.includes(env);
                      return (
                        <button key={env} onClick={() => toggleEnv(env)}
                          className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg border-2 text-sm font-medium transition-all text-left`}
                          style={sel ? { borderColor: s.color, background: s.bg, color: s.color } : { borderColor: '#e5e7eb', color: '#374151' }}>
                          <span className={`w-4 h-4 rounded border-2 flex items-center justify-center shrink-0 text-xs`}
                            style={sel ? { borderColor: s.color, background: s.color, color: '#fff' } : { borderColor: '#d1d5db' }}>
                            {sel && '✓'}
                          </span>
                          {env}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Include linked */}
              {(artifactType === 'requirement' || artifactType === 'test_plan') && (
                <label className="flex items-center gap-2.5 cursor-pointer">
                  <input type="checkbox" checked={includeLinked} onChange={e => setIncludeLinked(e.target.checked)}
                    className="w-4 h-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500" />
                  <span className="text-sm text-gray-700">Include linked test cases</span>
                </label>
              )}
            </div>

            <div className="flex items-center justify-end gap-3 px-6 pb-6">
              <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50">Cancel</button>
              <button onClick={handlePromote} disabled={!targetEnvs.length || promoting || availableEnvs.length === 0}
                className="px-5 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 disabled:opacity-40 flex items-center gap-2">
                {promoting ? <><span className="animate-spin inline-block">⟳</span> Promoting…</> : <>↑ Promote</>}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const QmsRequirementsStage = ({ requirements, projectId, onApprove, onReject, onAdd, onNext, onEdit, onDelete, onRefresh, onPromote }) => {
  const [collapsed, setCollapsed] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [reqTypes, setReqTypes] = useState(DEFAULT_REQ_TYPES);
  const [showManageTypes, setShowManageTypes] = useState(false);
  const [editingCat, setEditingCat] = useState(null); // { type, cat }
  const [editCatVal, setEditCatVal] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showPromote, setShowPromote] = useState(false);
  const [envFilter, setEnvFilter] = useState('all');

  const loadTypes = () => ModulesModel.getReqTypes().then(ts => { if (Array.isArray(ts) && ts.length > 0) setReqTypes(ts); });
  useEffect(() => { loadTypes(); }, []);

  const TYPE_LABELS = Object.fromEntries(reqTypes.map(t => [t.name, t.label]));
  const TYPE_STYLES = Object.fromEntries(reqTypes.map(t => [t.name, REQ_TYPE_COLOR_PALETTE[t.color] || REQ_TYPE_COLOR_PALETTE.teal]));
  const TYPE_ORDER = reqTypes.map(t => t.name);

  const allReqs = requirements || [];
  const activeEnvs = [...new Set(allReqs.map(r => r.environment || 'DEV'))].sort((a, b) => ENV_ORDER.indexOf(a) - ENV_ORDER.indexOf(b));
  const visibleReqs = envFilter === 'all' ? allReqs : allReqs.filter(r => (r.environment || 'DEV') === envFilter);
  const allReqIds = new Set(visibleReqs.map(r => r.id));
  // Top-level: no parent, OR parent was deleted (orphaned sub-req)
  const topLevel = visibleReqs.filter(r => !r.parentRequirementId || !allReqIds.has(r.parentRequirementId));
  const subReqsMap = {};
  visibleReqs.filter(r => r.parentRequirementId && allReqIds.has(r.parentRequirementId)).forEach(r => {
    if (!subReqsMap[r.parentRequirementId]) subReqsMap[r.parentRequirementId] = [];
    subReqsMap[r.parentRequirementId].push(r);
  });

  // Group top-level: reqType → parentCategory → requirements
  const byType = {};
  topLevel.forEach(r => {
    const type = r.reqType || 'functional';
    if (!byType[type]) byType[type] = {};
    const cat = r.parentCategory || '';
    if (!byType[type][cat]) byType[type][cat] = [];
    byType[type][cat].push(r);
  });
  const types = Object.keys(byType).sort((a, b) => (TYPE_ORDER.indexOf(a) + 1 || 99) - (TYPE_ORDER.indexOf(b) + 1 || 99));

  const toggleKey = k => setCollapsed(c => ({ ...c, [k]: !c[k] }));

  const startEdit = r => {
    setEditingId(r.id);
    setEditForm({ title: r.title, description: r.description || '', reqType: r.reqType, priority: r.priority, parentCategory: r.parentCategory || '' });
  };
  const saveEdit = async (formData) => { await onEdit(editingId, formData ?? editForm); setEditingId(null); };


  // Level 4: sub-requirement
  const renderSubReq = r => {
    const ss = qmsStatusStyle(r.status);
    const ps = qmsSeverityStyle(r.priority);
    if (editingId === r.id) return <div key={r.id}><QmsReqEditForm editForm={editForm} reqTypes={reqTypes} onSave={saveEdit} onCancel={() => setEditingId(null)} /></div>;
    return (
      <div key={r.id} className="flex items-start gap-2 py-2 px-3 bg-white border border-gray-100 rounded-lg mb-1.5 group hover:border-gray-200">
        <span className="text-gray-300 font-mono text-xs mt-0.5 select-none shrink-0">&gt;&gt;&gt;</span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <span className="text-sm font-medium text-gray-800">{r.reqCode}{r.title ? `: ${r.title}` : ''}</span>
            <div className="flex items-center gap-1.5">
              <span className="px-2 py-0.5 rounded-full text-xs font-semibold capitalize" style={{ background: ss.bg, color: ss.color }}>{r.status}</span>
              <span className="px-2 py-0.5 rounded text-xs font-semibold capitalize" style={{ background: ps.bg, color: ps.color }}>{r.priority}</span>
              <button onClick={() => startEdit(r)} className="p-1 text-gray-300 hover:text-blue-500 rounded opacity-0 group-hover:opacity-100 transition-opacity"><Pencil size={11} /></button>
              <button onClick={() => requestConfirm(`Delete "${r.reqCode}"?`, () => onDelete(r.id))} className="p-1 text-gray-300 hover:text-red-500 rounded opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 size={11} /></button>
            </div>
          </div>
          {r.description && <p className="text-xs text-gray-400 mt-0.5">{r.description}</p>}
          {r.status === 'pending' && (
            <div className="flex gap-1.5 mt-1.5">
              <button onClick={() => onApprove(r.id)} className="px-2 py-1 bg-green-600 text-white rounded text-xs font-medium hover:bg-green-700">✓ Approve</button>
              <button onClick={() => onReject(r.id)} className="px-2 py-1 bg-gray-200 text-gray-700 rounded text-xs font-medium hover:bg-gray-300">✗ Reject</button>
            </div>
          )}
        </div>
      </div>
    );
  };

  const toggleSelect = (id) => setSelectedIds(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });

  // Level 3: requirement card
  const renderReq = r => {
    const ss = qmsStatusStyle(r.status);
    const ps = qmsSeverityStyle(r.priority);
    const children = subReqsMap[r.id] || [];
    const isSelected = selectedIds.has(r.id);
    if (editingId === r.id) return <div key={r.id} className="mb-3"><QmsReqEditForm editForm={editForm} reqTypes={reqTypes} onSave={saveEdit} onCancel={() => setEditingId(null)} /></div>;
    return (
      <div key={r.id} className="mb-3">
        <div className={`bg-gray-50 border rounded-lg p-4 group transition-colors ${isSelected ? 'border-teal-400 bg-teal-50' : 'border-gray-200'}`} style={{ borderLeftWidth: '3px', borderLeftColor: isSelected ? '#0d9488' : ps.color }}>
          <div className="flex items-start justify-between gap-2 mb-1">
            <div className="flex items-center gap-2 min-w-0">
              {onPromote && <input type="checkbox" checked={isSelected} onChange={() => toggleSelect(r.id)} className="w-3.5 h-3.5 rounded border-gray-300 text-teal-600 focus:ring-teal-500 shrink-0 cursor-pointer" onClick={e => e.stopPropagation()} />}
              <span className="text-gray-300 font-mono text-xs shrink-0 select-none">&gt;&gt;</span>
              <h4 className="font-semibold text-gray-900 text-sm">{r.reqCode}: {r.title}</h4>
              <EnvBadge env={r.environment || 'DEV'} />
              {(r.allEnvironments || []).filter(e => e !== (r.environment || 'DEV')).map(e => <EnvBadge key={e} env={e} />)}
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              <span className="px-2 py-0.5 rounded-full text-xs font-semibold capitalize" style={{ background: ss.bg, color: ss.color }}>{r.status}</span>
              <span className="px-2 py-0.5 rounded text-xs font-semibold capitalize" style={{ background: ps.bg, color: ps.color }}>{r.priority}</span>
              {onPromote && <button onClick={() => { setSelectedIds(new Set([r.id])); setShowPromote(true); }} className="p-1 text-gray-300 hover:text-teal-500 rounded opacity-0 group-hover:opacity-100 transition-opacity" title="Promote to environment">↑</button>}
              <button onClick={() => startEdit(r)} className="p-1 text-gray-300 hover:text-blue-500 rounded opacity-0 group-hover:opacity-100 transition-opacity"><Pencil size={13} /></button>
              <button onClick={() => requestConfirm(`Delete "${r.reqCode}${r.title ? ': ' + r.title : ''}"?`, () => onDelete(r.id))} className="p-1 text-gray-300 hover:text-red-500 rounded opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 size={13} /></button>
            </div>
          </div>
          {r.description && <p className="text-xs text-gray-500 ml-5 mb-2">{r.description}</p>}
          <div className="flex items-center gap-2 ml-5 flex-wrap">
            {r.status === 'pending' && (
              <>
                <button onClick={() => onApprove(r.id)} className="px-2.5 py-1 bg-green-600 text-white rounded text-xs font-medium hover:bg-green-700">✓ Approve</button>
                <button onClick={() => onReject(r.id)} className="px-2.5 py-1 bg-gray-200 text-gray-700 rounded text-xs font-medium hover:bg-gray-300">✗ Reject</button>
              </>
            )}
            <button onClick={() => onAdd({ parentRequirementId: r.id, reqType: r.reqType, parentCategory: r.parentCategory })}
              className="px-2.5 py-1 border border-dashed border-gray-300 text-gray-400 rounded text-xs hover:border-teal-400 hover:text-teal-600 ml-auto">
              + Sub-requirement
            </button>
          </div>
        </div>
        {children.length > 0 && (
          <div className="ml-6 mt-1 border-l-2 border-gray-100 pl-3 space-y-0">
            {children.map(sub => renderSubReq(sub))}
          </div>
        )}
      </div>
    );
  };

  const selectedArtifacts = allReqs.filter(r => selectedIds.has(r.id));

  return (
    <div>
      {showManageTypes && <ManageReqTypesModal onClose={() => setShowManageTypes(false)} onRefresh={loadTypes} />}
      {showPromote && onPromote && (
        <PromoteModal
          artifacts={selectedArtifacts}
          artifactType="requirement"
          projectId={projectId}
          onClose={() => { setShowPromote(false); setSelectedIds(new Set()); onRefresh?.(); }}
          onPromote={onPromote}
        />
      )}
      <div className="flex justify-between items-center mb-3">
        <h3 className="text-lg font-bold text-gray-900">Requirements</h3>
        <div className="flex items-center gap-2">
          {onPromote && selectedIds.size > 0 && (
            <button onClick={() => setShowPromote(true)}
              className="px-3 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 flex items-center gap-1.5">
              ↑ Promote Selected ({selectedIds.size})
            </button>
          )}
          {onPromote && selectedIds.size > 0 && (
            <button onClick={() => setSelectedIds(new Set())} className="px-3 py-2 border border-gray-300 text-gray-500 rounded-lg text-sm hover:bg-gray-50">Clear</button>
          )}
          <button onClick={() => setShowManageTypes(true)}
            className="px-3 py-2 border border-gray-300 text-gray-600 rounded-lg text-sm font-medium hover:bg-gray-50 flex items-center gap-1.5">
            <Settings size={14} /> Manage Types
          </button>
          <button onClick={() => onAdd({})} className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 flex items-center gap-2">
            <span className="text-lg leading-none">+</span> Add Requirement
          </button>
        </div>
      </div>

      {/* Environment filter */}
      {activeEnvs.length > 1 && (
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <span className="text-xs text-gray-400 font-medium">Show:</span>
          {['all', ...activeEnvs].map(env => (
            <button key={env} onClick={() => { setEnvFilter(env); setSelectedIds(new Set()); }}
              className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-colors ${envFilter === env ? 'border-teal-500 bg-teal-50 text-teal-700' : 'border-gray-200 text-gray-500 hover:border-gray-300'}`}>
              {env === 'all' ? 'All Environments' : env}
            </button>
          ))}
        </div>
      )}

      {types.length === 0 && <p className="text-sm text-gray-400 py-4">No requirements yet.</p>}

      {types.map(type => {
        const st = TYPE_STYLES[type] || TYPE_STYLES.functional;
        const cats = byType[type];
        const totalCount = Object.values(cats).reduce((s, a) => s + a.length, 0);
        const isTypeCollapsed = collapsed[`type:${type}`];
        return (
          <div key={type} className="mb-6">
            {/* Level 1: req_type section */}
            <button onClick={() => toggleKey(`type:${type}`)}
              className="w-full flex items-center justify-between px-4 py-3 rounded-xl text-white font-bold text-sm uppercase tracking-wide mb-3"
              style={{ background: st.header }}>
              <span>{TYPE_LABELS[type] || type}</span>
              <span className="flex items-center gap-2">
                <span className="bg-white bg-opacity-20 px-2 py-0.5 rounded-full text-xs font-medium">{totalCount}</span>
                <ChevronDown size={16} className={`transition-transform ${isTypeCollapsed ? '-rotate-90' : ''}`} />
              </span>
            </button>

            {!isTypeCollapsed && (
              <div className="ml-2">
                {Object.keys(cats).sort((a, b) => { if (a === '') return 1; if (b === '') return -1; return a.localeCompare(b); }).map(cat => {
                  const items = cats[cat];
                  const hasCat = cat !== '';
                  const isCatCollapsed = collapsed[`cat:${type}:${cat}`];
                  return (
                    <div key={cat || '__none__'} className="mb-4">
                      {/* Level 2: category / feature */}
                      {hasCat ? (
                        editingCat?.type === type && editingCat?.cat === cat ? (
                          <div className="flex items-center gap-2 mb-2 px-1">
                            <span className="font-mono text-xs text-gray-400 shrink-0">&gt;</span>
                            <input autoFocus value={editCatVal} onChange={e => setEditCatVal(e.target.value)}
                              onKeyDown={async e => {
                                if (e.key === 'Enter') { await ModulesModel.renameReqCategory(projectId, cat, editCatVal); setEditingCat(null); onRefresh && onRefresh(); }
                                if (e.key === 'Escape') setEditingCat(null);
                              }}
                              className="flex-1 border border-gray-300 rounded-lg px-2 py-1 text-sm focus:ring-2 focus:ring-teal-400" />
                            <button onClick={async () => { await ModulesModel.renameReqCategory(projectId, cat, editCatVal); setEditingCat(null); onRefresh && onRefresh(); }}
                              className="px-2.5 py-1 bg-teal-600 text-white rounded text-xs font-medium hover:bg-teal-700">Save</button>
                            <button onClick={() => setEditingCat(null)}
                              className="px-2.5 py-1 border border-gray-200 text-gray-500 rounded text-xs hover:bg-gray-50">Cancel</button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1 mb-2 group/cat">
                            <button onClick={() => toggleKey(`cat:${type}:${cat}`)}
                              className="flex-1 flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-semibold hover:opacity-80"
                              style={{ background: st.light, borderColor: st.border, color: st.text }}>
                              <ChevronRight size={14} className={`transition-transform ${!isCatCollapsed ? 'rotate-90' : ''}`} />
                              <span className="font-mono text-xs opacity-50">&gt;</span>
                              {cat}
                              <span className="ml-1 px-1.5 py-0.5 rounded-full text-xs font-medium" style={{ background: st.border, color: st.text }}>{items.length}</span>
                            </button>
                            <button onClick={() => { setEditingCat({ type, cat }); setEditCatVal(cat); }}
                              className="p-1.5 text-gray-300 hover:text-blue-500 rounded opacity-0 group-hover/cat:opacity-100 transition-opacity shrink-0">
                              <Pencil size={13} />
                            </button>
                          </div>
                        )
                      ) : (
                        <div className="flex items-center gap-2 mb-2 text-xs font-semibold text-gray-400 uppercase tracking-wide px-1">
                          <span>Uncategorized</span>
                          <span className="px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded-full">{items.length}</span>
                        </div>
                      )}
                      {/* Level 3+4: requirements and their sub-requirements */}
                      {!isCatCollapsed && (
                        <div className="ml-4 border-l-2 pl-3" style={{ borderColor: st.border }}>
                          {items.map(r => renderReq(r))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}

      <button onClick={onNext} className="mt-4 px-5 py-2.5 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700">Proceed to Test Plan →</button>
    </div>
  );
};

const QmsTestPlanStage = ({ project, onUpdate, onNext, onBack, onCreateTestPlan, onUpdateTestPlan, onDeleteTestPlan, testPlans = [], onPromote, user }) => {
  const [qaLeads, setQaLeads] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingPlanId, setEditingPlanId] = useState(null);
  const [saving, setSaving] = useState(false);
  const emptyForm = { title: '', objectives: '', scope: '', environment: 'UAT', qaLeadId: '', targetStartDate: '', targetEndDate: '', status: 'draft' };
  const [form, setForm] = useState(emptyForm);
  const [promotingPlan, setPromotingPlan] = useState(null);

  useEffect(() => { ModulesModel.getQaLeads().then(leads => setQaLeads(leads)); }, []);

  const role = user?.role || user?.user_role;
  const canManage = ['admin', 'qa_lead', 'manager'].includes(role) || (typeof role === 'string' && role.endsWith('_manager'));

  const planStatusStyle = (s) => ({ draft: 'bg-gray-100 text-gray-600', active: 'bg-teal-100 text-teal-700', completed: 'bg-green-100 text-green-700', archived: 'bg-red-50 text-red-500' }[s] || 'bg-gray-100 text-gray-600');

  const openEdit = (plan) => {
    setEditingPlanId(plan.id);
    setForm({
      title:           plan.title || '',
      objectives:      plan.objectives || '',
      scope:           plan.scope || '',
      environment:     plan.environment || 'UAT',
      qaLeadId:        plan.qa_lead_id ? String(plan.qa_lead_id) : '',
      targetStartDate: plan.target_start_date || '',
      targetEndDate:   plan.target_end_date || '',
      status:          plan.status || 'draft',
    });
    setShowForm(true);
  };
  const cancelForm = () => { setShowForm(false); setEditingPlanId(null); setForm(emptyForm); };

  const handleSave = async () => {
    if (!form.title.trim()) return;
    if (!project?.projectId) { alert('No project selected — cannot save the test plan.'); return; }
    setSaving(true);
    const payload = { ...form, projectId: project.projectId, qaLeadId: form.qaLeadId || null };
    const r = editingPlanId
      ? await onUpdateTestPlan(editingPlanId, payload)
      : await onCreateTestPlan(payload);
    setSaving(false);
    if (r?.success) {
      setShowForm(false);
      setEditingPlanId(null);
      setForm(emptyForm);
    }
  };

  const handleDelete = async (plan) => {
    if (!onDeleteTestPlan) return;
    // Try a quick path first — for plans with no dependencies the backend deletes immediately
    // and only prompts when something would be cascaded. Either way the handler shows the right confirm.
    await onDeleteTestPlan(plan);
  };

  const hasActivePlan = testPlans.some(p => p.status === 'active' || p.status === 'draft');

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <h3 className="text-lg font-bold text-gray-900">Test Plan</h3>
        <button onClick={() => { if (showForm && editingPlanId) cancelForm(); else { setShowForm(v => !v); setEditingPlanId(null); setForm(emptyForm); } }}
          className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700">
          {showForm ? 'Cancel' : '+ New Test Plan'}
        </button>
      </div>

      {showForm && (
        <div className="bg-teal-50 border border-teal-200 rounded-xl p-5 mb-5 space-y-3">
          <h4 className="font-semibold text-teal-800 text-sm mb-1">{editingPlanId ? 'Edit Test Plan' : 'New Test Plan'}</h4>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Title *</label>
            <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="e.g. Login Feature Test Plan…"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Environment</label>
              <select value={form.environment} onChange={e => setForm(f => ({ ...f, environment: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400">
                <option value="DEV">DEV</option><option value="UAT">UAT</option><option value="Staging">Staging</option><option value="Production">Production</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">QA Lead</label>
              <select value={form.qaLeadId} onChange={e => setForm(f => ({ ...f, qaLeadId: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400">
                <option value="">— Select —</option>
                {qaLeads.map(u => <option key={u.id} value={u.id}>{u.user_name}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Target Start</label>
              <input type="date" value={form.targetStartDate} onChange={e => setForm(f => ({ ...f, targetStartDate: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Target End</label>
              <input type="date" value={form.targetEndDate} onChange={e => setForm(f => ({ ...f, targetEndDate: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Objectives</label>
            <textarea value={form.objectives} onChange={e => setForm(f => ({ ...f, objectives: e.target.value }))} rows={2} placeholder="What this test plan aims to validate…"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Scope</label>
            <textarea value={form.scope} onChange={e => setForm(f => ({ ...f, scope: e.target.value }))} rows={2} placeholder="Areas and features included / excluded…"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400" />
          </div>
          {editingPlanId && (
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
              <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400">
                <option value="draft">Draft</option>
                <option value="active">Active</option>
                <option value="completed">Completed</option>
                <option value="archived">Archived</option>
              </select>
            </div>
          )}
          <div className="flex gap-2 pt-1">
            <button onClick={handleSave} disabled={saving || !form.title.trim()} className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 disabled:opacity-50">{saving ? 'Saving…' : (editingPlanId ? 'Save Changes' : 'Save Plan')}</button>
            <button onClick={cancelForm} className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50">Cancel</button>
          </div>
        </div>
      )}

      {testPlans.length === 0 && !showForm && (
        <div className="text-center py-10 text-gray-400">
          <p className="text-sm">No test plans yet. Create one to get started.</p>
        </div>
      )}

      {promotingPlan && onPromote && (
        <PromoteModal
          artifacts={[promotingPlan]}
          artifactType="test_plan"
          projectId={project?.projectId}
          onClose={() => setPromotingPlan(null)}
          onPromote={onPromote}
        />
      )}

      {testPlans.length > 0 && (
        <div className="space-y-3 mb-5">
          {testPlans.map(plan => (
            <div key={plan.id} className="bg-white border border-gray-200 rounded-xl p-4 flex items-start justify-between gap-3 group">
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className={`px-2 py-0.5 rounded text-xs font-semibold capitalize ${planStatusStyle(plan.status)}`}>{plan.status}</span>
                  <EnvBadge env={plan.environment || 'DEV'} />
                </div>
                <p className="font-semibold text-gray-900 text-sm">{plan.title}</p>
                {plan.objectives && <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{plan.objectives}</p>}
                <div className="flex items-center gap-4 mt-2">
                  <span className="text-xs text-gray-500">{plan.total_cases || 0} cases</span>
                  {plan.total_cases > 0 && <span className="text-xs text-green-600">{plan.passed || 0} passed</span>}
                  {plan.failed > 0 && <span className="text-xs text-red-600">{plan.failed} failed</span>}
                </div>
              </div>
              <div className="flex flex-col items-end gap-2">
                <span className="text-xs text-gray-400">{plan.created_at ? new Date(plan.created_at).toLocaleDateString() : ''}</span>
                <div className="flex flex-wrap gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                  {onPromote && (
                    <button onClick={() => setPromotingPlan(plan)}
                      className="px-2.5 py-1 text-xs font-medium text-indigo-600 border border-indigo-200 rounded-lg hover:bg-indigo-50">
                      ↑ Promote
                    </button>
                  )}
                  {canManage && onUpdateTestPlan && (
                    <button onClick={() => openEdit(plan)}
                      className="px-2.5 py-1 text-xs font-medium text-teal-700 border border-teal-200 rounded-lg hover:bg-teal-50">
                      ✏️ Edit
                    </button>
                  )}
                  {canManage && onDeleteTestPlan && (
                    <button onClick={() => handleDelete(plan)}
                      className="px-2.5 py-1 text-xs font-medium text-red-600 border border-red-200 rounded-lg hover:bg-red-50">
                      🗑 Delete
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="flex gap-3 mt-4">
        <button onClick={onNext} disabled={!hasActivePlan && testPlans.length === 0}
          className="px-5 py-2.5 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 disabled:opacity-40 disabled:cursor-not-allowed">
          Proceed to Test Cases →
        </button>
        <button onClick={onBack} className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50">← Back</button>
      </div>
    </div>
  );
};

const QmsTcEditForm = ({ editForm, setEditForm, tcModuleList, requirements, onManageMods, onSave, onCancel }) => {
  const updateStep = (i, field, val) => {
    const steps = [...(editForm.steps || [])];
    steps[i] = { ...steps[i], [field]: val };
    setEditForm(f => ({ ...f, steps }));
  };
  const addStep = () => setEditForm(f => ({ ...f, steps: [...(f.steps || []), { action: '', expected: '' }] }));
  const removeStep = (i) => setEditForm(f => ({ ...f, steps: (f.steps || []).filter((_, idx) => idx !== i) }));

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
      <div className="space-y-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Title</label>
          <input value={editForm.title || ''} onChange={e => setEditForm(f => ({ ...f, title: e.target.value }))}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs font-medium text-gray-600">Module</label>
              <button type="button" onClick={onManageMods} className="text-xs text-teal-600 hover:text-teal-700 font-medium">⚙ Manage</button>
            </div>
            <select value={editForm.module || ''} onChange={e => setEditForm(f => ({ ...f, module: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400">
              <option value="">— Select module —</option>
              {tcModuleList.map(m => <option key={m.id} value={m.name}>{m.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Priority</label>
            <select value={editForm.priority || ''} onChange={e => setEditForm(f => ({ ...f, priority: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400">
              <option value="critical">Critical</option><option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option>
            </select>
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Prerequisites</label>
          <textarea value={editForm.prerequisites || ''} onChange={e => setEditForm(f => ({ ...f, prerequisites: e.target.value }))} rows={2}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400" />
        </div>
        {requirements.length > 0 && (
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Linked Requirement</label>
            <select value={editForm.requirementId || ''} onChange={e => setEditForm(f => ({ ...f, requirementId: e.target.value ? parseInt(e.target.value) : null }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400">
              <option value="">— None —</option>
              {requirements.map(r => <option key={r.id} value={r.id}>{r.reqCode}: {r.title}</option>)}
            </select>
          </div>
        )}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-2">Test Steps</label>
          {(editForm.steps || []).map((step, i) => (
            <div key={i} className="bg-white border border-gray-200 rounded-lg p-3 mb-2">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-gray-500">Step {i + 1}</span>
                <button type="button" onClick={() => removeStep(i)} className="text-gray-300 hover:text-red-500 text-xs">✕</button>
              </div>
              <input type="text" placeholder="Action" value={step.action || ''} onChange={e => updateStep(i, 'action', e.target.value)}
                className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm mb-2 focus:ring-2 focus:ring-teal-400" />
              <input type="text" placeholder="Expected Result" value={step.expected || ''} onChange={e => updateStep(i, 'expected', e.target.value)}
                className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm focus:ring-2 focus:ring-teal-400" />
            </div>
          ))}
          <button type="button" onClick={addStep} className="text-xs text-teal-600 font-medium hover:text-teal-700">+ Add Step</button>
        </div>
        <div className="flex gap-2">
          <button onClick={() => onSave(editForm)} className="px-3 py-1.5 bg-teal-600 text-white rounded-lg text-xs font-medium hover:bg-teal-700">Save</button>
          <button onClick={onCancel} className="px-3 py-1.5 border border-gray-300 text-gray-600 rounded-lg text-xs font-medium hover:bg-gray-50">Cancel</button>
        </div>
      </div>
    </div>
  );
};

const QmsTestCasesStage = ({ testCases, requirements = [], onAdd, onEdit, onDelete, onNext, onBack, onPromote, projectId }) => {
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [collapsed, setCollapsed] = useState({});
  const [tcModuleList, setTcModuleList] = useState([]);
  const [showManageMods, setShowManageMods] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showPromote, setShowPromote] = useState(false);
  const [envFilter, setEnvFilter] = useState('all');

  const loadTcModules = async () => { setTcModuleList(await ModulesModel.getTcModules()); };
  useEffect(() => { loadTcModules(); }, []);

  const toggleKey = k => setCollapsed(c => ({ ...c, [k]: !c[k] }));

  const startEdit = (tc) => {
    setEditingId(tc.id);
    const steps = Array.isArray(tc.steps) ? tc.steps : (tc.steps ? (() => { try { return JSON.parse(tc.steps); } catch { return []; } })() : []);
    setEditForm({ title: tc.title, module: tc.module || '', priority: tc.priority, prerequisites: tc.prerequisites || '', requirementId: tc.requirementId || null, steps });
  };
  const saveEdit = async (formData) => { await onEdit(editingId, formData ?? editForm); setEditingId(null); };

  const allTcs = testCases || [];
  const activeEnvsTc = [...new Set(allTcs.map(t => t.environment || 'DEV'))].sort((a, b) => ENV_ORDER.indexOf(a) - ENV_ORDER.indexOf(b));
  const visibleTcs = envFilter === 'all' ? allTcs : allTcs.filter(t => (t.environment || 'DEV') === envFilter);
  const toggleSelectTc = (id) => setSelectedIds(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });

  // Group by module (like req_type grouping)
  const byModule = {};
  visibleTcs.forEach(tc => {
    const mod = tc.module || 'Unassigned';
    if (!byModule[mod]) byModule[mod] = [];
    byModule[mod].push(tc);
  });
  const modules = Object.keys(byModule).sort((a, b) => a === 'Unassigned' ? 1 : b === 'Unassigned' ? -1 : a.localeCompare(b));

  // Module header color (cycles through a small palette)
  const MODULE_COLORS = [
    { header: '#0f766e', light: '#f0fdfa', border: '#99f6e4', text: '#0f766e' },
    { header: '#1d4ed8', light: '#eff6ff', border: '#bfdbfe', text: '#1d4ed8' },
    { header: '#7c3aed', light: '#f5f3ff', border: '#ddd6fe', text: '#7c3aed' },
    { header: '#b45309', light: '#fffbeb', border: '#fde68a', text: '#b45309' },
    { header: '#be123c', light: '#fff1f2', border: '#fecdd3', text: '#be123c' },
  ];

  const renderStep = (step, idx) => (
    <div key={idx} className="flex items-start gap-2 py-2 px-3 bg-white border border-gray-100 rounded-lg mb-1.5 group">
      <span className="text-gray-300 font-mono text-xs mt-0.5 select-none shrink-0">&gt;&gt;&gt;</span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-semibold text-gray-500 shrink-0">Step {idx + 1}</span>
          <span className="text-sm text-gray-800 whitespace-pre-wrap">{step.action || <span className="italic text-gray-400">No action</span>}</span>
        </div>
        {step.expected && (
          <p className="text-xs text-teal-700 mt-0.5 whitespace-pre-wrap">
            <span className="font-medium">Expected:</span> {step.expected}
          </p>
        )}
      </div>
    </div>
  );

  const renderTc = (tc) => {
    const ss = qmsStatusStyle(tc.status);
    const ps = qmsSeverityStyle(tc.priority);
    const steps = Array.isArray(tc.steps) ? tc.steps : (tc.steps ? (() => { try { return JSON.parse(tc.steps); } catch { return []; } })() : []);
    const stepsCollapsed = collapsed[`steps:${tc.id}`];
    const linkedReq = tc.requirementId ? requirements.find(r => r.id === tc.requirementId) : null;
    const isSelected = selectedIds.has(tc.id);

    if (editingId === tc.id) {
      return (
        <div key={tc.id} className="p-3">
          <QmsTcEditForm editForm={editForm} setEditForm={setEditForm} tcModuleList={tcModuleList} requirements={requirements} onManageMods={() => setShowManageMods(true)} onSave={saveEdit} onCancel={() => setEditingId(null)} />
        </div>
      );
    }

    // Prerequisites + linked requirement collapsed into one truncated meta line
    // instead of two separate rows — cuts a chunk of the old per-item height.
    const metaBits = [];
    if (tc.prerequisites) metaBits.push(tc.prerequisites);
    if (linkedReq) metaBits.push(`Req: ${linkedReq.reqCode} ${linkedReq.title}`);

    return (
      <div key={tc.id} className={`px-3 py-2 group transition-colors ${isSelected ? 'bg-teal-50' : 'hover:bg-gray-50'}`} style={{ borderLeftWidth: '3px', borderLeftColor: isSelected ? '#0d9488' : ps.color }}>
        <div className="flex items-start justify-between gap-2 flex-wrap sm:flex-nowrap">
          <div className="flex items-center gap-2 min-w-0">
            {onPromote && <input type="checkbox" checked={isSelected} onChange={() => toggleSelectTc(tc.id)} className="w-3.5 h-3.5 rounded border-gray-300 text-teal-600 focus:ring-teal-500 shrink-0 cursor-pointer" onClick={e => e.stopPropagation()} />}
            <h4 className="font-semibold text-gray-900 text-sm truncate">{tc.tcCode}: {tc.title}</h4>
            <EnvBadge env={tc.environment || 'DEV'} />
            {(tc.allEnvironments || []).filter(e => e !== (tc.environment || 'DEV')).map(e => <EnvBadge key={e} env={e} />)}
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <span className="px-2 py-0.5 rounded-full text-xs font-semibold capitalize" style={{ background: ss.bg, color: ss.color }}>{tc.status}</span>
            <span className="px-2 py-0.5 rounded text-xs font-semibold capitalize" style={{ background: ps.bg, color: ps.color }}>{tc.priority}</span>
            {onPromote && <button onClick={() => { setSelectedIds(new Set([tc.id])); setShowPromote(true); }} className="p-1 text-gray-300 hover:text-teal-500 rounded opacity-0 group-hover:opacity-100 transition-opacity" title="Promote to environment">↑</button>}
            <button onClick={() => startEdit(tc)} className="p-1 text-gray-300 hover:text-blue-500 rounded opacity-0 group-hover:opacity-100 transition-opacity"><Pencil size={13} /></button>
            <button onClick={() => requestConfirm(`Delete test case "${tc.tcCode}"?`, () => onDelete(tc.id))} className="p-1 text-gray-300 hover:text-red-500 rounded opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 size={13} /></button>
          </div>
        </div>
        <div className="flex items-center gap-3 mt-0.5 ml-5">
          {metaBits.length > 0 && <span className="text-xs text-gray-400 truncate">{metaBits.join(' · ')}</span>}
          {steps.length > 0 && (
            <button onClick={() => toggleKey(`steps:${tc.id}`)}
              className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 font-medium shrink-0">
              <ChevronRight size={12} className={`transition-transform ${!stepsCollapsed ? 'rotate-90' : ''}`} />
              {steps.length} step{steps.length !== 1 ? 's' : ''}
            </button>
          )}
        </div>
        {/* Steps — mirrors sub-requirements (Level 4) */}
        {steps.length > 0 && !stepsCollapsed && (
          <div className="ml-6 mt-1.5 border-l-2 border-gray-100 pl-3">
            {steps.map((step, i) => renderStep(step, i))}
          </div>
        )}
      </div>
    );
  };

  const selectedTcArtifacts = allTcs.filter(t => selectedIds.has(t.id));

  return (
    <div>
      {showManageMods && (
        <ManageTcModulesModal onClose={() => setShowManageMods(false)} onSaved={loadTcModules} />
      )}
      {showPromote && onPromote && (
        <PromoteModal
          artifacts={selectedTcArtifacts}
          artifactType="test_case"
          projectId={projectId}
          onClose={() => { setShowPromote(false); setSelectedIds(new Set()); }}
          onPromote={onPromote}
        />
      )}
      <div className="flex justify-between items-center mb-3">
        <h3 className="text-lg font-bold text-gray-900">Test Cases</h3>
        <div className="flex items-center gap-2">
          {onPromote && selectedIds.size > 0 && (
            <button onClick={() => setShowPromote(true)}
              className="px-3 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 flex items-center gap-1.5">
              ↑ Promote Selected ({selectedIds.size})
            </button>
          )}
          {onPromote && selectedIds.size > 0 && (
            <button onClick={() => setSelectedIds(new Set())} className="px-3 py-2 border border-gray-300 text-gray-500 rounded-lg text-sm hover:bg-gray-50">Clear</button>
          )}
          <button onClick={() => setShowManageMods(true)} className="px-3 py-2 border border-gray-300 text-gray-600 rounded-lg text-sm font-medium hover:bg-gray-50 flex items-center gap-1.5">
            ⚙ Manage Modules
          </button>
          <button onClick={onAdd} className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 flex items-center gap-2">
            <span className="text-lg leading-none">+</span> Add Test Case
          </button>
        </div>
      </div>

      {/* Environment filter */}
      {activeEnvsTc.length > 1 && (
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <span className="text-xs text-gray-400 font-medium">Show:</span>
          {['all', ...activeEnvsTc].map(env => (
            <button key={env} onClick={() => { setEnvFilter(env); setSelectedIds(new Set()); }}
              className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-colors ${envFilter === env ? 'border-teal-500 bg-teal-50 text-teal-700' : 'border-gray-200 text-gray-500 hover:border-gray-300'}`}>
              {env === 'all' ? 'All Environments' : env}
            </button>
          ))}
        </div>
      )}

      {modules.length === 0 && <p className="text-sm text-gray-400 py-4">No test cases yet.</p>}

      {modules.map((mod, modIdx) => {
        const items = byModule[mod];
        const modColor = MODULE_COLORS[modIdx % MODULE_COLORS.length];
        const isModCollapsed = collapsed[`mod:${mod}`];
        return (
          <div key={mod} className="mb-6">
            {/* Level 1: module section header */}
            <button onClick={() => toggleKey(`mod:${mod}`)}
              className="w-full flex items-center justify-between px-4 py-3 rounded-xl text-white font-bold text-sm uppercase tracking-wide mb-3"
              style={{ background: modColor.header }}>
              <span>{mod}</span>
              <span className="flex items-center gap-2">
                <span className="bg-white bg-opacity-20 px-2 py-0.5 rounded-full text-xs font-medium">{items.length}</span>
                <ChevronDown size={16} className={`transition-transform ${isModCollapsed ? '-rotate-90' : ''}`} />
              </span>
            </button>
            {!isModCollapsed && (
              <div className="orbit-card divide-y divide-gray-100 overflow-hidden ml-2 border-l-2" style={{ borderLeftColor: modColor.border }}>
                {items.map(tc => renderTc(tc))}
              </div>
            )}
          </div>
        );
      })}

      <div className="flex gap-3 mt-4">
        <button onClick={onNext} className="px-5 py-2.5 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700">Start Testing →</button>
        <button onClick={onBack} className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50">← Back</button>
      </div>
    </div>
  );
};

const QmsTestingStage = ({ testCases, bugs, metrics, project, onPass, onFail, onReset, onReportBug, onEditBug, onNext, onBack, onRecordExecution, testPlans = [], user }) => {
  const canEditBugs = ['admin', 'qa_lead', 'qa', 'devops'].includes(user?.role || user?.user_role);
  const activePlan = testPlans.find(p => p.status === 'active' || p.status === 'draft') || testPlans[0];
  const [execSummary, setExecSummary] = useState(null);
  const [recording, setRecording] = useState({});
  const [showActualResult, setShowActualResult] = useState({});
  const [actualResults, setActualResults] = useState({});

  useEffect(() => {
    if (activePlan?.id) {
      ModulesModel.getExecutionSummary(activePlan.id).then(s => { if (s) setExecSummary(s); });
    }
  }, [activePlan?.id]);

  const execCases = execSummary?.cases || testCases || [];
  const total = execSummary?.total ?? (testCases?.length || 0);
  const passed = execSummary?.passed ?? (metrics?.testCases?.passed || 0);
  const failed = execSummary?.failed ?? (metrics?.testCases?.failed || 0);
  const blocked = execSummary?.blocked ?? 0;
  const executed = execSummary?.executed ?? (passed + failed);
  const progress = total > 0 ? Math.round((executed / total) * 100) : 0;
  const passRate = total > 0 ? Math.round((passed / total) * 100) : 0;

  const record = async (tc, status) => {
    const key = `${tc.id}-${status}`;
    setRecording(r => ({ ...r, [key]: true }));
    if (activePlan?.id && onRecordExecution) {
      await onRecordExecution({ testPlanId: activePlan.id, testCaseId: tc.id, projectId: project?.projectId, status, actualResult: actualResults[tc.id] || '', createBug: status === 'fail' });
      const s = await ModulesModel.getExecutionSummary(activePlan.id);
      if (s) setExecSummary(s);
    } else {
      if (status === 'pass') await onPass(tc.id);
      else if (status === 'fail') await onFail(tc.id);
      else if (status === 'pending' && onReset) await onReset(tc.id);
    }
    setRecording(r => ({ ...r, [key]: false }));
    setShowActualResult(s => ({ ...s, [tc.id]: false }));
  };

  const getExecStatus = (tc) => tc.exec_status || tc.status || 'pending';

  const execStatusStyle = (s) => ({
    pass: 'bg-green-100 text-green-700', fail: 'bg-red-100 text-red-700',
    blocked: 'bg-orange-100 text-orange-700', skip: 'bg-gray-100 text-gray-500',
    pending: 'bg-gray-100 text-gray-500', draft: 'bg-gray-100 text-gray-500',
  }[s] || 'bg-gray-100 text-gray-500');

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-gray-900">Execute Testing</h3>
        {activePlan && <span className="text-xs bg-teal-50 text-teal-700 border border-teal-200 px-2 py-1 rounded-lg font-medium">{activePlan.title}</span>}
      </div>

      {/* Progress */}
      <div className="grid grid-cols-4 gap-3 mb-5">
        {[['Total', total, 'text-gray-900'], ['Passed', passed, 'text-green-600'], ['Failed', failed, 'text-red-600'], ['Blocked', blocked, 'text-orange-600']].map(([label, val, cls]) => (
          <div key={label} className="bg-white border border-gray-200 rounded-xl p-3 text-center">
            <p className={`text-2xl font-bold ${cls}`}>{val}</p>
            <p className="text-xs text-gray-500 mt-0.5">{label}</p>
          </div>
        ))}
      </div>
      <div className="mb-5">
        <div className="flex justify-between text-xs text-gray-500 mb-1"><span>Progress: {executed}/{total} executed</span><span className="font-medium text-teal-700">{progress}% · Pass rate: {passRate}%</span></div>
        <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden flex">
          <div className="h-full bg-green-500 transition-all" style={{ width: `${total > 0 ? (passed / total) * 100 : 0}%` }} />
          <div className="h-full bg-red-400 transition-all" style={{ width: `${total > 0 ? (failed / total) * 100 : 0}%` }} />
          <div className="h-full bg-orange-300 transition-all" style={{ width: `${total > 0 ? (blocked / total) * 100 : 0}%` }} />
        </div>
      </div>

      {/* Test case execution table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-5">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Test Case</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Priority</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Result</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {execCases.map(tc => {
              const status = getExecStatus(tc);
              const ps = qmsSeverityStyle(tc.priority);
              const notDone = status === 'pending' || status === 'draft';
              return (
                <React.Fragment key={tc.id}>
                  <tr className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <p className="text-sm font-medium text-gray-900">{tc.tcCode || `TC-${tc.id}`}: {tc.title}</p>
                      {tc.executed_by && <p className="text-xs text-gray-400 mt-0.5">by {tc.executed_by}</p>}
                    </td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-0.5 rounded text-xs font-semibold capitalize" style={{ background: ps.bg, color: ps.color }}>{tc.priority}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold capitalize ${execStatusStyle(status)}`}>{status}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1.5">
                        {notDone && <>
                          <button onClick={() => record(tc, 'pass')} disabled={recording[`${tc.id}-pass`]} className="px-2 py-1 bg-green-50 text-green-700 border border-green-200 rounded text-xs font-medium hover:bg-green-100 disabled:opacity-50">✓ Pass</button>
                          <button onClick={() => setShowActualResult(s => ({ ...s, [tc.id]: !s[tc.id] }))} className="px-2 py-1 bg-red-50 text-red-700 border border-red-200 rounded text-xs font-medium hover:bg-red-100">✗ Fail</button>
                          <button onClick={() => record(tc, 'blocked')} disabled={recording[`${tc.id}-blocked`]} className="px-2 py-1 bg-orange-50 text-orange-700 border border-orange-200 rounded text-xs font-medium hover:bg-orange-100 disabled:opacity-50">⊘ Block</button>
                        </>}
                        {status === 'fail' && <button onClick={() => onReportBug(tc)} className="px-2 py-1 bg-blue-50 text-blue-700 border border-blue-200 rounded text-xs font-medium hover:bg-blue-100">🐛 Bug</button>}
                        {!notDone && <button onClick={() => record(tc, 'pending')} className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs font-medium hover:bg-gray-200">↺ Reset</button>}
                      </div>
                    </td>
                  </tr>
                  {showActualResult[tc.id] && (
                    <tr className="bg-red-50">
                      <td colSpan={4} className="px-4 py-3">
                        <div className="flex items-end gap-2">
                          <div className="flex-1">
                            <label className="block text-xs font-medium text-red-700 mb-1">Actual Result (required for Fail)</label>
                            <textarea value={actualResults[tc.id] || ''} onChange={e => setActualResults(r => ({ ...r, [tc.id]: e.target.value }))}
                              rows={2} placeholder="Describe what actually happened…"
                              className="w-full border border-red-200 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-red-400 bg-white" />
                          </div>
                          <div className="flex gap-1.5 shrink-0 pb-0.5">
                            <button onClick={() => record(tc, 'fail')} disabled={recording[`${tc.id}-fail`]} className="px-3 py-1.5 bg-red-600 text-white rounded-lg text-xs font-medium hover:bg-red-700 disabled:opacity-50">Mark Fail</button>
                            <button onClick={() => setShowActualResult(s => ({ ...s, [tc.id]: false }))} className="px-3 py-1.5 border border-gray-300 rounded-lg text-xs text-gray-600 hover:bg-gray-50">Cancel</button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            {execCases.length === 0 && <tr><td colSpan={4} className="px-4 py-8 text-center text-sm text-gray-400">No test cases linked to active plan. Go back and add test cases first.</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Open bugs summary */}
      {bugs && bugs.filter(b => b.status === 'open' || b.status === 'in_progress').length > 0 && (
        <div className="mb-5">
          <h4 className="text-sm font-semibold text-gray-700 mb-3">Open Bugs</h4>
          {bugs.filter(b => b.status === 'open' || b.status === 'in_progress').map(b => {
            const bs = qmsSeverityStyle(b.severity);
            return (
              <div key={b.id} className="bg-white border border-gray-200 rounded-lg p-3 mb-2 flex justify-between items-center" style={{ borderLeftWidth: '3px', borderLeftColor: bs.color }}>
                <div>
                  <span className="font-semibold text-sm text-gray-900">{b.bugCode}: {b.title}</span>
                  {b.assignedTo && <span className="text-xs text-gray-500 ml-2">· {b.assignedTo}</span>}
                </div>
                <div className="flex items-center gap-2">
                  <span className="px-2 py-0.5 rounded text-xs font-semibold capitalize" style={{ background: bs.bg, color: bs.color }}>{b.severity}</span>
                  {canEditBugs && <button onClick={() => onEditBug(b)} className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs font-medium hover:bg-gray-200">Edit</button>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="flex gap-3 flex-wrap">
        <button onClick={onNext} className="px-5 py-2.5 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700">Proceed to UAT →</button>
        <button onClick={onBack} className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50">← Back</button>
        <button onClick={() => onReportBug(null)} className="ml-auto px-4 py-2 bg-red-50 text-red-700 border border-red-200 rounded-lg text-sm font-medium hover:bg-red-100">🐛 Report Bug</button>
      </div>
    </div>
  );
};

const QmsUatStage = ({ metrics, bugs, project, onUpdateProject, onNext, onBack, onSubmitUatSignoff, uatSignoffs = [], testPlans = [], user }) => {
  const noBlockers = !(bugs || []).some(b => b.severity === 'critical' && b.status === 'open');
  const passRate = metrics?.passRate || 0;
  const isReady = noBlockers && passRate >= 80;
  const activePlan = testPlans.find(p => p.status === 'active' || p.status === 'draft') || testPlans[0];
  const [form, setForm] = useState({ notes: '', status: 'approved', conditions: '' });
  const [saving, setSaving] = useState(false);
  const latestSignoff = uatSignoffs[0];

  const signoffStatusStyle = (s) => ({ approved: 'bg-green-100 text-green-700', conditional: 'bg-yellow-100 text-yellow-700', rejected: 'bg-red-100 text-red-700' }[s] || 'bg-gray-100 text-gray-600');

  const handleSubmit = async () => {
    setSaving(true);
    await onSubmitUatSignoff({ projectId: project?.projectId, testPlanId: activePlan?.id || null, passRateAtSignoff: passRate, openBugsAtSignoff: metrics?.bugs?.open || 0, ...form });
    setSaving(false);
    if (form.status !== 'rejected') onNext();
  };

  return (
    <div>
      <h3 className="text-lg font-bold text-gray-900 mb-4">UAT Sign-off</h3>

      {/* Readiness banner */}
      <div className={`p-4 rounded-xl mb-5 border-l-4 ${isReady ? 'bg-green-50 border-green-500' : 'bg-yellow-50 border-yellow-400'}`}>
        <h4 className={`font-semibold mb-1 ${isReady ? 'text-green-800' : 'text-yellow-800'}`}>
          {isReady ? '✓ Ready for UAT Sign-off' : '⚠ Not Ready — Issues Remain'}
        </h4>
        <p className={`text-sm ${isReady ? 'text-green-700' : 'text-yellow-700'}`}>
          {isReady ? 'Pass rate meets threshold. No critical open bugs.' : `Pass rate: ${passRate}% (need ≥80%). Open critical bugs: ${(bugs || []).filter(b => b.severity === 'critical' && b.status === 'open').length}`}
        </p>
      </div>

      {/* Metrics summary */}
      <div className="grid grid-cols-4 gap-3 mb-5">
        {[['QA Lead', project?.qaLead || '—', 'text-gray-900'], ['Test Cases', metrics?.testCases?.total || 0, 'text-gray-900'], ['Pass Rate', `${passRate}%`, 'text-green-600'], ['Open Bugs', metrics?.bugs?.open || 0, 'text-orange-600']].map(([label, val, cls]) => (
          <div key={label} className="bg-white border border-gray-200 rounded-xl p-3 text-center">
            <p className={`text-lg font-bold ${cls}`}>{val}</p>
            <p className="text-xs text-gray-500 mt-0.5">{label}</p>
          </div>
        ))}
      </div>

      {/* Previous sign-offs */}
      {uatSignoffs.length > 0 && (
        <div className="mb-5">
          <h4 className="text-sm font-semibold text-gray-700 mb-3">Sign-off History</h4>
          <div className="space-y-2">
            {uatSignoffs.map(s => (
              <div key={s.id} className="bg-white border border-gray-200 rounded-xl p-4 flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`px-2 py-0.5 rounded text-xs font-semibold capitalize ${signoffStatusStyle(s.status)}`}>{s.status}</span>
                    <span className="text-xs text-gray-500">{s.signed_off_by}</span>
                  </div>
                  {s.notes && <p className="text-xs text-gray-600">{s.notes}</p>}
                  {s.conditions && <p className="text-xs text-yellow-700 mt-0.5">Conditions: {s.conditions}</p>}
                </div>
                <span className="text-xs text-gray-400 shrink-0">{s.created_at ? new Date(s.created_at).toLocaleDateString() : ''}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Sign-off form */}
      <div className="bg-gray-50 border border-gray-200 rounded-xl p-5 space-y-4 mb-5">
        <h4 className="font-semibold text-gray-800 text-sm">Submit UAT Sign-off</h4>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Decision</label>
          <div className="flex gap-2">
            {['approved', 'conditional', 'rejected'].map(s => (
              <button key={s} onClick={() => setForm(f => ({ ...f, status: s }))}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold capitalize border transition-all ${form.status === s ? { approved: 'bg-green-600 text-white border-green-600', conditional: 'bg-yellow-500 text-white border-yellow-500', rejected: 'bg-red-600 text-white border-red-600' }[s] : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}>
                {s}
              </button>
            ))}
          </div>
        </div>
        {form.status === 'conditional' && (
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Conditions</label>
            <input value={form.conditions} onChange={e => setForm(f => ({ ...f, conditions: e.target.value }))} placeholder="Specify conditions for approval…"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400" />
          </div>
        )}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Notes</label>
          <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={3}
            placeholder="Any comments, observations, or conditions for sign-off…"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400" />
        </div>
      </div>

      <div className="flex gap-3">
        <button onClick={handleSubmit} disabled={saving || (!isReady && form.status === 'approved')}
          className="px-5 py-2.5 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 disabled:opacity-40 disabled:cursor-not-allowed">
          {saving ? 'Submitting…' : form.status === 'approved' ? '✓ Submit & Proceed to Verification →' : form.status === 'conditional' ? 'Submit Conditional Sign-off →' : 'Submit Rejection'}
        </button>
        <button onClick={onBack} className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50">← Back</button>
      </div>
    </div>
  );
};

const QmsVerificationStage = ({ project, onUpdateProject, onBack, onToggleProdVerification, onCompleteProdVerification, prodVerifications = [], user }) => {
  const [items, setItems] = useState([]);
  const [completing, setCompleting] = useState(false);
  const [localNotes, setLocalNotes] = useState({});

  useEffect(() => {
    if (project?.projectId) {
      ModulesModel.getProdVerifications(project.projectId).then(rows => { if (rows?.length) setItems(rows); });
    }
  }, [project?.projectId]);

  // Merge server state with prop state
  const displayItems = items.length > 0 ? items : prodVerifications;
  const allChecked = displayItems.length > 0 && displayItems.every(i => i.is_checked);
  const checkedCount = displayItems.filter(i => i.is_checked).length;

  const toggle = async (item) => {
    const newVal = !item.is_checked;
    setItems(prev => prev.map(i => i.id === item.id ? { ...i, is_checked: newVal } : i));
    if (onToggleProdVerification) {
      await onToggleProdVerification(item.id, { isChecked: newVal, checkedBy: user?.user_name || user?.name });
    }
  };

  const saveNote = async (item) => {
    if (onToggleProdVerification) {
      await onToggleProdVerification(item.id, { notes: localNotes[item.id] ?? item.notes });
    }
  };

  const handleComplete = async () => {
    setCompleting(true);
    if (onCompleteProdVerification) await onCompleteProdVerification(project?.projectId);
    else await onUpdateProject(project?.projectId, { status: 'completed' });
    setCompleting(false);
  };

  return (
    <div>
      <h3 className="text-lg font-bold text-gray-900 mb-4">Production Verification</h3>
      <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded-xl mb-5">
        <h4 className="font-semibold text-yellow-800 mb-1">⚠ Production Deployment Checklist</h4>
        <p className="text-sm text-yellow-700">Verify all critical functionality after deployment. {checkedCount}/{displayItems.length} checks complete.</p>
      </div>

      {/* Progress bar */}
      <div className="mb-5">
        <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
          <div className="h-full bg-green-500 rounded-full transition-all" style={{ width: displayItems.length > 0 ? `${(checkedCount / displayItems.length) * 100}%` : '0%' }} />
        </div>
      </div>

      <div className="space-y-3 mb-6">
        {displayItems.map(item => (
          <div key={item.id || item.check_key} className={`bg-white border rounded-xl p-4 transition-all ${item.is_checked ? 'border-green-200 bg-green-50' : 'border-gray-200 hover:border-teal-300'}`}>
            <div className="flex items-start gap-3">
              <button onClick={() => toggle(item)} className={`mt-0.5 h-5 w-5 rounded border-2 flex-shrink-0 flex items-center justify-center transition-all ${item.is_checked ? 'bg-green-500 border-green-500' : 'border-gray-300 hover:border-teal-400'}`}>
                {item.is_checked && <span className="text-white text-xs font-bold">✓</span>}
              </button>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h4 className={`font-semibold text-sm ${item.is_checked ? 'text-green-800 line-through' : 'text-gray-900'}`}>{item.check_label}</h4>
                  {item.is_checked && item.checked_by && <span className="text-xs text-green-600">· {item.checked_by}</span>}
                </div>
                <input
                  type="text" placeholder="Add notes…"
                  value={localNotes[item.id] ?? (item.notes || '')}
                  onChange={e => setLocalNotes(n => ({ ...n, [item.id]: e.target.value }))}
                  onBlur={() => saveNote(item)}
                  className="mt-1.5 w-full border-0 border-b border-gray-200 px-0 py-0.5 text-xs text-gray-500 bg-transparent focus:outline-none focus:border-teal-400 placeholder-gray-300"
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="flex gap-3">
        <button onClick={handleComplete} disabled={!allChecked || completing}
          className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-all ${allChecked ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}>
          {completing ? 'Completing…' : '✓ Confirm Verification Complete'}
        </button>
        <button onClick={onBack} className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50">← Back</button>
      </div>
    </div>
  );
};

const AddRequirementModal = ({ onClose, onSubmit, projectId, existingCategories = [], requirements = [], defaultValues = {} }) => {
  const isSubReq = !!defaultValues.parentRequirementId;
  const [form, setForm] = useState({
    title: '', description: '',
    reqType: defaultValues.reqType || 'functional',
    priority: 'medium',
    parentCategory: defaultValues.parentCategory || '',
    parentRequirementId: defaultValues.parentRequirementId || null,
  });
  const [newCategory, setNewCategory] = useState('');
  const [categoryMode, setCategoryMode] = useState('existing');
  const [reqTypes, setReqTypes] = useState(DEFAULT_REQ_TYPES);
  useEffect(() => { ModulesModel.getReqTypes().then(ts => { if (Array.isArray(ts) && ts.length > 0) setReqTypes(ts); }); }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    const cat = categoryMode === 'new' ? newCategory.trim() : form.parentCategory;
    onSubmit({ ...form, parentCategory: cat || null, projectId });
  };

  const parentReq = isSubReq ? requirements.find(r => r.id === defaultValues.parentRequirementId) : null;
  const topLevelReqs = requirements.filter(r => !r.parentRequirementId);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-bold text-gray-900">{isSubReq ? 'Add Sub-requirement' : 'Add Requirement'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl font-bold">&times;</button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Sub-requirement context banner */}
          {parentReq && (
            <div className="flex items-start gap-2 px-3 py-2.5 bg-teal-50 border border-teal-200 rounded-lg">
              <span className="text-teal-500 font-mono text-xs mt-0.5 shrink-0">&gt;&gt;&gt;</span>
              <div className="min-w-0">
                <p className="text-xs font-semibold text-teal-700 mb-0.5">Sub-requirement of:</p>
                <p className="text-sm text-teal-900 font-medium">{parentReq.reqCode}: {parentReq.title}</p>
              </div>
            </div>
          )}

          {/* Optional parent picker (only when not pre-filled) */}
          {!isSubReq && topLevelReqs.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Parent Requirement <span className="text-gray-400 font-normal">(optional — makes this a sub-requirement)</span></label>
              <select value={form.parentRequirementId || ''}
                onChange={e => setForm(f => ({ ...f, parentRequirementId: e.target.value ? parseInt(e.target.value) : null }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400">
                <option value="">— Top-level requirement —</option>
                {topLevelReqs.map(r => <option key={r.id} value={r.id}>{r.reqCode}: {r.title}</option>)}
              </select>
            </div>
          )}

          {/* Category (hide for sub-reqs — inherits from parent) */}
          {!isSubReq && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Feature Group / Category</label>
              <div className="flex gap-2 mb-2">
                <button type="button" onClick={() => setCategoryMode('existing')}
                  className={`px-3 py-1 text-xs rounded-full border ${categoryMode === 'existing' ? 'bg-teal-600 text-white border-teal-600' : 'text-gray-600 border-gray-300 hover:bg-gray-50'}`}>
                  Existing
                </button>
                <button type="button" onClick={() => setCategoryMode('new')}
                  className={`px-3 py-1 text-xs rounded-full border ${categoryMode === 'new' ? 'bg-teal-600 text-white border-teal-600' : 'text-gray-600 border-gray-300 hover:bg-gray-50'}`}>
                  + New Group
                </button>
              </div>
              {categoryMode === 'existing' ? (
                <select value={form.parentCategory} onChange={e => setForm({ ...form, parentCategory: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400">
                  <option value="">— No Group —</option>
                  {existingCategories.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              ) : (
                <input type="text" value={newCategory} onChange={e => setNewCategory(e.target.value)}
                  placeholder="e.g. Login Feature, Payment Module…"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400" />
              )}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
            <input type="text" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400 focus:border-teal-400" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
              <select value={form.reqType} onChange={e => setForm({ ...form, reqType: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400">
                {reqTypes.map(t => <option key={t.name} value={t.name}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
              <select value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400">
                <option value="critical">Critical</option><option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <MiniWysiwyg value={form.description} onChange={v => setForm({ ...form, description: v })} placeholder="Describe the requirement…" minHeight="70px" />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</button>
            <button type="submit" className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700">{isSubReq ? 'Add Sub-requirement' : 'Add Requirement'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const ManageTcModulesModal = ({ onClose, onSaved }) => {
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editVal, setEditVal] = useState('');
  const [error, setError] = useState('');

  const load = async () => { setLoading(true); setModules(await ModulesModel.getTcModules()); setLoading(false); };
  useEffect(() => { load(); }, []);

  const handleAdd = async () => {
    if (!newName.trim()) return;
    setAdding(true); setError('');
    const r = await ModulesModel.createTcModule({ name: newName.trim() });
    if (r?.success === false) { setError(r.error || 'Failed to add'); }
    else { setNewName(''); await load(); onSaved?.(); }
    setAdding(false);
  };

  const handleRename = async (id) => {
    if (!editVal.trim()) return;
    setError('');
    const r = await ModulesModel.updateTcModule(id, { name: editVal.trim() });
    if (r?.success === false) { setError(r.error || 'Failed to rename'); }
    else { setEditingId(null); await load(); onSaved?.(); }
  };

  const handleDelete = (m) => {
    requestConfirm(`Delete module "${m.name}"?`, async () => {
      const r = await ModulesModel.deleteTcModule(m.id);
      if (r?.success === false) { setError(r.error || 'Failed to delete'); }
      else { await load(); onSaved?.(); }
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm">
        <div className="flex justify-between items-center px-5 py-4 border-b border-gray-200">
          <h3 className="text-base font-semibold text-gray-900">Manage Modules</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          {error && <p className="text-xs text-red-600 bg-red-50 rounded px-3 py-2">{error}</p>}
          {loading ? <p className="text-sm text-gray-400 text-center py-4">Loading…</p> : (
            <ul className="space-y-1 max-h-64 overflow-y-auto">
              {modules.map(m => (
                <li key={m.id} className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-50 group">
                  {editingId === m.id ? (
                    <>
                      <input autoFocus value={editVal} onChange={e => setEditVal(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') handleRename(m.id); if (e.key === 'Escape') setEditingId(null); }}
                        className="flex-1 border border-teal-400 rounded px-2 py-1 text-sm focus:ring-2 focus:ring-teal-400 outline-none" />
                      <button onClick={() => handleRename(m.id)} className="text-xs text-teal-600 font-medium hover:text-teal-700">Save</button>
                      <button onClick={() => setEditingId(null)} className="text-xs text-gray-400 hover:text-gray-600">Cancel</button>
                    </>
                  ) : (
                    <>
                      <span className="flex-1 text-sm text-gray-800">{m.name}</span>
                      <button onClick={() => { setEditingId(m.id); setEditVal(m.name); setError(''); }}
                        className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-teal-600 rounded transition-opacity"><Pencil size={13} /></button>
                      <button onClick={() => handleDelete(m)}
                        className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-red-500 rounded transition-opacity"><Trash2 size={13} /></button>
                    </>
                  )}
                </li>
              ))}
            </ul>
          )}
          <div className="flex gap-2 pt-1 border-t border-gray-100">
            <input value={newName} onChange={e => setNewName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleAdd()}
              placeholder="New module name…"
              className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400 outline-none" />
            <button onClick={handleAdd} disabled={adding || !newName.trim()}
              className="px-3 py-2 bg-teal-600 text-white text-sm rounded-lg hover:bg-teal-700 disabled:opacity-50">
              {adding ? '…' : 'Add'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const AddTestCaseModal = ({ onClose, onSubmit, projectId, requirements = [] }) => {
  const [form, setForm] = useState({ title: '', module: '', priority: 'high', prerequisites: '', steps: [{ action: '', expected: '' }], requirementId: null });
  const [modules, setModules] = useState([]);
  const [showManageModules, setShowManageModules] = useState(false);

  const loadModules = async () => {
    const list = await ModulesModel.getTcModules();
    setModules(list);
    if (list.length > 0 && !form.module) setForm(f => ({ ...f, module: list[0].name }));
  };
  useEffect(() => { loadModules(); }, []);

  const addStep = () => setForm({ ...form, steps: [...form.steps, { action: '', expected: '' }] });
  const updateStep = (i, field, val) => { const s = [...form.steps]; s[i][field] = val; setForm({ ...form, steps: s }); };
  const handleSubmit = (e) => { e.preventDefault(); if (!form.title.trim()) return; onSubmit({ ...form, projectId }); };

  return (
    <>
    {showManageModules && (
      <ManageTcModulesModal
        onClose={() => setShowManageModules(false)}
        onSaved={async () => { await loadModules(); }}
      />
    )}
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 sticky top-0 bg-white z-10">
          <h2 className="text-lg font-bold text-gray-900">Add Test Case</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl font-bold">&times;</button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
            <input type="text" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400" required />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-sm font-medium text-gray-700">Module</label>
                <button type="button" onClick={() => setShowManageModules(true)}
                  className="text-xs text-teal-600 hover:text-teal-700 font-medium">⚙ Manage</button>
              </div>
              <select value={form.module} onChange={e => setForm({ ...form, module: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400">
                {modules.map(m => <option key={m.id} value={m.name}>{m.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
              <select value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400">
                <option value="critical">Critical</option><option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Prerequisites</label>
            <textarea value={form.prerequisites} onChange={e => setForm({ ...form, prerequisites: e.target.value })} rows={2}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400" />
          </div>
          {requirements.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Linked Requirement <span className="text-gray-400 font-normal">(optional)</span></label>
              <select value={form.requirementId || ''} onChange={e => setForm({ ...form, requirementId: e.target.value ? parseInt(e.target.value) : null })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400">
                <option value="">— None —</option>
                {requirements.map(r => <option key={r.id} value={r.id}>{r.reqCode}: {r.title}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Test Steps</label>
            {form.steps.map((step, i) => (
              <div key={i} className="bg-gray-50 rounded-lg p-3 mb-2">
                <h5 className="text-xs font-semibold text-gray-600 mb-2">Step {i + 1}</h5>
                <input type="text" placeholder="Action" value={step.action} onChange={e => updateStep(i, 'action', e.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm mb-2 focus:ring-2 focus:ring-teal-400" />
                <input type="text" placeholder="Expected Result" value={step.expected} onChange={e => updateStep(i, 'expected', e.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm focus:ring-2 focus:ring-teal-400" />
              </div>
            ))}
            <button type="button" onClick={addStep} className="text-xs text-teal-600 font-medium hover:text-teal-700">+ Add Step</button>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</button>
            <button type="submit" className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700">Add Test Case</button>
          </div>
        </form>
      </div>
    </div>
    </>
  );
};

// Local-only attachment manager (used inside ReportBugModal before bug exists)
const LocalAttachmentPicker = ({ files, onChange }) => {
  const inputRef = React.useRef();
  const handleFiles = async (selected) => {
    const incoming = Array.from(selected);
    const errors = [];
    const valid = [];
    for (const f of incoming) {
      if (!isAllowedFile(f)) { errors.push(`${f.name}: unsupported type`); continue; }
      if (f.size > MAX_ATTACHMENT_SIZE) { errors.push(`${f.name}: exceeds 2 MB limit`); continue; }
      if (files.length + valid.length >= MAX_ATTACHMENTS) { errors.push('Max 5 attachments reached'); break; }
      valid.push(await readFileAsBase64(f));
    }
    if (errors.length) alert(errors.join('\n'));
    if (valid.length) onChange([...files, ...valid]);
  };
  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <button type="button" onClick={() => inputRef.current?.click()}
          className="px-3 py-1.5 text-xs border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700 font-medium">
          + Attach File
        </button>
        <span className="text-xs text-gray-400">Images, PDF, DOCX, XLSX, CSV · max 2 MB · up to 5 files</span>
        <input ref={inputRef} type="file" multiple accept={ACCEPT_ATTR} className="hidden"
          onChange={e => { handleFiles(e.target.files); e.target.value = ''; }} />
      </div>
      {files.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {files.map((f, i) => (
            <div key={i} className="relative group">
              {f.fileType.startsWith('image/') ? (
                <img src={f.fileData} alt={f.fileName} className="w-16 h-16 object-cover rounded border border-gray-200" />
              ) : (() => { const d = getFileDisplay(f.fileName, f.fileType); return (
                <div className={`w-16 h-16 flex flex-col items-center justify-center ${d.bg} border ${d.border} rounded ${d.text} text-xs font-medium`}>
                  {d.label}<span className="text-gray-400 text-[10px] truncate max-w-[56px] mt-0.5">{f.fileName}</span>
                </div>
              ); })()}
              <button type="button" onClick={() => onChange(files.filter((_, j) => j !== i))}
                className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white rounded-full text-[10px] hidden group-hover:flex items-center justify-center leading-none">×</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// API-backed attachment manager for PlanTrack projects
const ProjectAttachmentManager = ({ projectId, user }) => {
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const inputRef = React.useRef();

  const load = async () => { setLoading(true); setAttachments(await ModulesModel.getProjectAttachments(projectId)); setLoading(false); };
  useEffect(() => { load(); }, [projectId]);

  const handleFiles = async (selected) => {
    const incoming = Array.from(selected);
    const errors = [];
    setUploading(true);
    for (const f of incoming) {
      if (!isAllowedFile(f)) { errors.push(`${f.name}: unsupported type`); continue; }
      if (f.size > MAX_ATTACHMENT_SIZE) { errors.push(`${f.name}: exceeds 2 MB limit`); continue; }
      if (attachments.length >= MAX_ATTACHMENTS) { errors.push('Max 5 attachments reached'); break; }
      const att = await readFileAsBase64(f);
      await ModulesModel.addProjectAttachment(projectId, att);
    }
    if (errors.length) alert(errors.join('\n'));
    setUploading(false);
    await load();
  };

  const handleDelete = async (id) => {
    requestConfirm('Remove this attachment?', async () => {
      await ModulesModel.deleteProjectAttachment(id);
      await load();
    });
  };

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm font-medium text-gray-700">
          {attachments.length} / {MAX_ATTACHMENTS} files
        </span>
        {attachments.length < MAX_ATTACHMENTS && (
          <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading}
            className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
            {uploading ? 'Uploading…' : '+ Upload File'}
          </button>
        )}
        <input ref={inputRef} type="file" multiple accept={ACCEPT_ATTR} className="hidden"
          onChange={e => { handleFiles(e.target.files); e.target.value = ''; }} />
      </div>
      <p className="text-xs text-gray-400 mb-3">Accepted: JPG, PNG, GIF, WebP, PDF, DOCX, XLSX, CSV · Max 2 MB per file</p>
      {loading ? <p className="text-sm text-gray-400">Loading…</p> : attachments.length === 0 ? (
        <div className="text-center py-10 text-gray-400">
          <div className="text-3xl mb-2">📎</div>
          <p className="text-sm">No artifacts yet.</p>
          <p className="text-xs mt-1">Upload images, PDFs, or documents related to this project.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {attachments.map(a => (
            <div key={a.id} className="relative group border border-gray-200 rounded-lg overflow-hidden bg-gray-50">
              {a.fileType.startsWith('image/') ? (
                <a href={a.fileData} target="_blank" rel="noreferrer">
                  <img src={a.fileData} alt={a.fileName} className="w-full h-24 object-cover hover:opacity-80 transition-opacity" />
                </a>
              ) : (() => { const d = getFileDisplay(a.fileName, a.fileType); return (
                <a href={a.fileData} download={a.fileName}
                  className={`w-full h-24 flex flex-col items-center justify-center ${d.bg} ${d.hover} transition-colors ${d.text}`}>
                  <span className="text-2xl">{d.emoji}</span>
                  <span className="text-[10px] text-gray-500 truncate px-1 max-w-full mt-1">{a.fileName}</span>
                </a>
              ); })()}
              <div className="px-2 py-1 bg-white border-t border-gray-100">
                <p className="text-[10px] text-gray-600 truncate">{a.fileName}</p>
                <p className="text-[10px] text-gray-400">{a.uploadedBy || 'Unknown'} · {a.createdAt ? new Date(a.createdAt).toLocaleDateString() : ''}</p>
              </div>
              {(user?.user_name || user?.name) === a.uploadedBy && (
                <button type="button" onClick={() => requestConfirm('Delete this attachment?', () => handleDelete(a.id))}
                  className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full text-xs hidden group-hover:flex items-center justify-center">×</button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const ReportBugModal = ({ onClose, onSubmit, projectId, testCases, prefillTc }) => {
  const [form, setForm] = useState({
    title: '', testCaseId: prefillTc?.id || '', severity: 'medium',
    module: prefillTc?.module || 'Authentication', stepsToReproduce: '',
    expectedResult: '', actualResult: '', environment: 'UAT',
  });
  const [attachments, setAttachments] = useState([]);
  const [activeTab, setActiveTab] = useState('details');
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    onSubmit({ ...form, projectId, testCaseId: form.testCaseId || null, attachments });
  };
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-50 rounded-xl shadow-2xl w-full max-w-5xl max-h-[92vh] flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 bg-white rounded-t-xl flex-shrink-0">
          <div className="flex items-center gap-3">
            <span className="text-lg">🐛</span>
            <h2 className="text-base font-bold text-gray-900">Report Bug</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl font-bold leading-none">&times;</button>
        </div>

        {/* Scrollable Body */}
        <div className="overflow-y-auto flex-1">
          <form onSubmit={handleSubmit}>
            <div className="p-6 grid grid-cols-3 gap-5">

              {/* ── Main Column (2/3) ── */}
              <div className="col-span-2 space-y-4">

                {/* Severity pill + Title card */}
                <div className="bg-white rounded-xl border border-gray-200 p-5">
                  <div className="flex items-center gap-3 mb-4 flex-wrap">
                    <select value={form.severity} onChange={e => setForm({ ...form, severity: e.target.value })}
                      className={`text-xs font-semibold px-3 py-1 rounded-full border-0 cursor-pointer focus:ring-2 focus:ring-teal-400 capitalize ${bugSevStyle(form.severity)}`}>
                      <option value="critical">Critical</option>
                      <option value="high">High</option>
                      <option value="medium">Medium</option>
                      <option value="low">Low</option>
                    </select>
                  </div>
                  <input
                    type="text" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} required
                    placeholder="Bug title…"
                    className="w-full text-xl font-bold text-gray-900 border border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-teal-400 focus:border-teal-400"
                  />
                </div>

                {/* Tabbed card */}
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                  <div className="flex border-b border-gray-200 px-4 pt-3 gap-1">
                    {[
                      { key: 'details', label: '📋 Details' },
                      { key: 'attachments', label: '📎 Attachments' },
                    ].map(t => (
                      <button key={t.key} type="button" onClick={() => setActiveTab(t.key)}
                        className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${activeTab === t.key ? 'bg-teal-50 text-teal-700 border-b-2 border-teal-600' : 'text-gray-500 hover:text-gray-700'}`}>
                        {t.label}
                      </button>
                    ))}
                  </div>
                  <div className="p-5">
                    {activeTab === 'details' && (
                      <div className="space-y-5">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Steps to Reproduce</label>
                          <BugFieldEditor placeholder="1. Navigate to…&#10;2. Click on…&#10;3. Observe…" onChange={v => setForm(f => ({ ...f, stepsToReproduce: v }))} minHeight="80px" />
                        </div>
                        <div className="border-t border-gray-100" />
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Expected Result</label>
                          <BugFieldEditor placeholder="What should happen?" onChange={v => setForm(f => ({ ...f, expectedResult: v }))} minHeight="60px" />
                        </div>
                        <div className="border-t border-gray-100" />
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Actual Result</label>
                          <BugFieldEditor placeholder="What actually happened?" onChange={v => setForm(f => ({ ...f, actualResult: v }))} minHeight="60px" />
                        </div>
                      </div>
                    )}
                    {activeTab === 'attachments' && (
                      <LocalAttachmentPicker files={attachments} onChange={setAttachments} />
                    )}
                  </div>
                </div>
              </div>

              {/* ── Sidebar (1/3) ── */}
              <div className="space-y-4">
                <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-4">
                  <h3 className="font-semibold text-gray-700 text-sm border-b border-gray-100 pb-2">Bug Details</h3>

                  {/* Test Case */}
                  <div>
                    <p className="text-xs text-gray-400 mb-0.5">Linked Test Case</p>
                    <select value={form.testCaseId} onChange={e => setForm({ ...form, testCaseId: e.target.value })}
                      className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:ring-2 focus:ring-teal-400">
                      <option value="">— None —</option>
                      {(testCases || []).map(tc => <option key={tc.id} value={tc.id}>{tc.tcCode} – {tc.title}</option>)}
                    </select>
                  </div>

                  {/* Module */}
                  <div>
                    <p className="text-xs text-gray-400 mb-0.5">Module</p>
                    <select value={form.module} onChange={e => setForm({ ...form, module: e.target.value })}
                      className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:ring-2 focus:ring-teal-400">
                      <option value="">None</option>
                      <option>Authentication</option><option>Settlement Calculator</option><option>Payment Processing</option>
                      <option>Reporting</option><option>User Management</option><option>UI</option>
                    </select>
                  </div>

                  {/* Environment */}
                  <div>
                    <p className="text-xs text-gray-400 mb-0.5">Environment</p>
                    <select value={form.environment} onChange={e => setForm({ ...form, environment: e.target.value })}
                      className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:ring-2 focus:ring-teal-400">
                      <option>DEV</option><option>UAT</option><option>Staging</option><option>Production</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end items-center gap-3 px-6 pb-6">
              <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</button>
              <button type="submit" className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700">Submit Bug Report</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

const EditBugModal = ({ bug, onClose, onSave, onDelete, user, allUsers = [], teamMembers = [], allProjects = [] }) => {
  const [form, setForm] = useState({
    title: bug.title || '', severity: bug.severity || 'medium', status: bug.status || 'open',
    module: bug.module || '', assignedTo: bug.assignedTo || '', environment: bug.environment || 'DEV',
    stepsToReproduce: bug.stepsToReproduce || '', expectedResult: bug.expectedResult || '', actualResult: bug.actualResult || '',
    projectId: bug.projectId || '',
    estimatedMinutes: bug.estimatedMinutes != null ? String(bug.estimatedMinutes) : '',
    actualMinutes: bug.actualMinutes != null ? String(bug.actualMinutes) : '',
  });
  const [activeTab, setActiveTab] = useState('details');
  const [timeErrors, setTimeErrors] = useState({});
  const needsEstimate = form.status === 'in_progress' || form.status === 'ready';
  const needsActual = form.status === 'done';
  const fmtDate = (d) => d ? new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—';

  const handleSubmit = (e) => {
    e.preventDefault();
    const errs = {};
    if (needsEstimate && (!form.estimatedMinutes || parseInt(form.estimatedMinutes) <= 0)) errs.estimatedMinutes = 'Est. Time is required for Ready / In Progress';
    if (needsActual && (!form.actualMinutes || parseInt(form.actualMinutes) <= 0)) errs.actualMinutes = 'Actual Time is required for Done';
    if (Object.keys(errs).length) { setTimeErrors(errs); return; }
    onSave(bug.id, {
      ...form,
      estimatedMinutes: form.estimatedMinutes !== '' ? parseInt(form.estimatedMinutes) : null,
      actualMinutes: form.actualMinutes !== '' ? parseInt(form.actualMinutes) : null,
    });
  };

  // Role-based permissions
  const role = user?.role || user?.user_role;
  const canReassignProject = role === 'admin' || role === 'qa_lead' || role === 'qa';
  const isManager = role?.endsWith('_manager') || role === 'manager';
  const managerModule = (isManager && role !== 'manager') ? role.replace('_manager', '') : null;
  const assigneeOptions = (() => {
    if (isManager && managerModule) return teamMembers.filter(m => m.team_module === managerModule);
    if (isManager || role === 'admin') return allUsers;
    return allUsers;
  })();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-50 rounded-xl shadow-2xl w-full max-w-5xl max-h-[92vh] flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 bg-white rounded-t-xl flex-shrink-0">
          <div className="flex items-center gap-3">
            <span className="font-mono text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded">{bug.bugCode}</span>
            <h2 className="text-base font-bold text-gray-900">Edit Bug</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl font-bold leading-none">&times;</button>
        </div>

        {/* Scrollable Body */}
        <div className="overflow-y-auto flex-1">
          <form onSubmit={handleSubmit}>
            <div className="p-6 grid grid-cols-3 gap-5">

              {/* ── Main Column (2/3) ── */}
              <div className="col-span-2 space-y-4">

                {/* Title + Severity + Status card */}
                <div className="bg-white rounded-xl border border-gray-200 p-5">
                  <div className="flex items-center gap-3 mb-4 flex-wrap">
                    <select value={form.severity} onChange={e => setForm({ ...form, severity: e.target.value })}
                      className={`text-xs font-semibold px-3 py-1 rounded-full border-0 cursor-pointer focus:ring-2 focus:ring-teal-400 capitalize ${bugSevStyle(form.severity)}`}>
                      <option value="critical">Critical</option>
                      <option value="high">High</option>
                      <option value="medium">Medium</option>
                      <option value="low">Low</option>
                    </select>
                    <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}
                      className={`text-xs font-medium px-3 py-1 rounded-full border-0 cursor-pointer focus:ring-2 focus:ring-teal-400 ${bugStatStyle(form.status)}`}>
                      {BUG_STATUSES.map(s => <option key={s} value={s}>{bugStatLabel(s)}</option>)}
                    </select>
                  </div>
                  <input
                    type="text" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} required
                    placeholder="Bug title…"
                    className="w-full text-xl font-bold text-gray-900 border border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-teal-400 focus:border-teal-400"
                  />
                </div>

                {/* Tabbed card */}
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                  <div className="flex border-b border-gray-200 px-4 pt-3 gap-1">
                    {[
                      { key: 'details', label: '📋 Details' },
                      { key: 'comments', label: '💬 Comments' },
                      { key: 'attachments', label: '📎 Attachments' },
                    ].map(t => (
                      <button key={t.key} type="button" onClick={() => setActiveTab(t.key)}
                        className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${activeTab === t.key ? 'bg-teal-50 text-teal-700 border-b-2 border-teal-600' : 'text-gray-500 hover:text-gray-700'}`}>
                        {t.label}
                      </button>
                    ))}
                  </div>
                  <div className="p-5">
                    {activeTab === 'details' && (
                      <div className="space-y-5">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Steps to Reproduce</label>
                          <BugFieldEditor key={`str-${bug.id}`} initialValue={form.stepsToReproduce} onChange={v => setForm(f => ({ ...f, stepsToReproduce: v }))} minHeight="80px" />
                        </div>
                        <div className="border-t border-gray-100" />
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Expected Result</label>
                          <BugFieldEditor key={`exp-${bug.id}`} initialValue={form.expectedResult} onChange={v => setForm(f => ({ ...f, expectedResult: v }))} minHeight="60px" />
                        </div>
                        <div className="border-t border-gray-100" />
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Actual Result</label>
                          <BugFieldEditor key={`act-${bug.id}`} initialValue={form.actualResult} onChange={v => setForm(f => ({ ...f, actualResult: v }))} minHeight="60px" />
                        </div>
                      </div>
                    )}
                    {activeTab === 'comments' && (
                      <TaskComments taskRef={bug.id} taskSource="bug" userName={user?.user_name || user?.name} />
                    )}
                    {activeTab === 'attachments' && (
                      <ApiAttachmentManager bugId={bug.id} uploadedBy={user?.name} />
                    )}
                  </div>
                </div>
              </div>

              {/* ── Sidebar (1/3) ── */}
              <div className="space-y-4">
                <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-4">
                  <h3 className="font-semibold text-gray-700 text-sm border-b border-gray-100 pb-2">Bug Details</h3>

                  {/* Reported By (read-only) */}
                  <div>
                    <p className="text-xs text-gray-400 mb-0.5">Reported By</p>
                    <p className="text-sm font-medium text-gray-800">{bug.reportedBy || '—'}</p>
                  </div>

                  {/* Project */}
                  {canReassignProject && allProjects.length > 0 && (
                    <div>
                      <p className="text-xs text-gray-400 mb-0.5">Project</p>
                      <select value={form.projectId} onChange={e => setForm({ ...form, projectId: e.target.value })}
                        className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:ring-2 focus:ring-teal-400">
                        {allProjects.map(p => <option key={p.projectId || p.id} value={p.projectId || p.id}>{p.projectName || p.name}</option>)}
                      </select>
                    </div>
                  )}
                  {(!canReassignProject || allProjects.length === 0) && bug.projectName && (
                    <div>
                      <p className="text-xs text-gray-400 mb-0.5">Project</p>
                      <p className="text-sm font-medium text-gray-800">{bug.projectName}</p>
                    </div>
                  )}

                  {/* Assigned To */}
                  <div>
                    <p className="text-xs text-gray-400 mb-0.5">Assigned To</p>
                    <select value={form.assignedTo} onChange={e => setForm({ ...form, assignedTo: e.target.value })}
                      className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:ring-2 focus:ring-teal-400">
                      <option value="">— Unassigned —</option>
                      {assigneeOptions.map(u => (
                        <option key={u.user_id} value={u.user_name}>{u.user_name}</option>
                      ))}
                    </select>
                  </div>

                  {/* Environment */}
                  <div>
                    <p className="text-xs text-gray-400 mb-0.5">Environment</p>
                    <select value={form.environment} onChange={e => setForm({ ...form, environment: e.target.value })}
                      className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:ring-2 focus:ring-teal-400">
                      <option>DEV</option><option>UAT</option><option>Staging</option><option>Production</option>
                    </select>
                  </div>

                  {/* Module */}
                  <div>
                    <p className="text-xs text-gray-400 mb-0.5">Module</p>
                    <select value={form.module} onChange={e => setForm({ ...form, module: e.target.value })}
                      className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:ring-2 focus:ring-teal-400">
                      <option value="">None</option>
                      <option>Authentication</option><option>Settlement Calculator</option><option>Payment Processing</option>
                      <option>Reporting</option><option>User Management</option><option>UI</option>
                    </select>
                  </div>

                  {/* Est. Time */}
                  <div>
                    <p className="text-xs text-gray-400 mb-0.5">Est. Time (min){needsEstimate && <span className="text-red-500"> *</span>}</p>
                    <input type="number" min="1" placeholder="e.g. 60" value={form.estimatedMinutes}
                      onChange={e => { setForm(f => ({ ...f, estimatedMinutes: e.target.value })); setTimeErrors(ev => ({ ...ev, estimatedMinutes: '' })); }}
                      className={`w-full border rounded-lg px-2 py-1.5 text-sm focus:ring-2 focus:ring-teal-400 ${timeErrors.estimatedMinutes ? 'border-red-400' : 'border-gray-200'}`} />
                    {timeErrors.estimatedMinutes && <p className="text-xs text-red-500 mt-1">{timeErrors.estimatedMinutes}</p>}
                  </div>

                  {/* Actual Time */}
                  <div>
                    <p className="text-xs text-gray-400 mb-0.5">Actual Time (min){needsActual && <span className="text-red-500"> *</span>}</p>
                    <input type="number" min="0" placeholder="0" value={form.actualMinutes}
                      onChange={e => { setForm(f => ({ ...f, actualMinutes: e.target.value })); setTimeErrors(ev => ({ ...ev, actualMinutes: '' })); }}
                      className={`w-full border rounded-lg px-2 py-1.5 text-sm focus:ring-2 focus:ring-teal-400 ${timeErrors.actualMinutes ? 'border-red-400' : 'border-gray-200'}`} />
                    {timeErrors.actualMinutes && <p className="text-xs text-red-500 mt-1">{timeErrors.actualMinutes}</p>}
                  </div>

                  {/* Linked Test Case (read-only) */}
                  {bug.testCaseCode && (
                    <div>
                      <p className="text-xs text-gray-400 mb-0.5">Linked TC</p>
                      <p className="text-sm font-medium text-gray-800">{bug.testCaseCode}{bug.testCaseTitle ? ` — ${bug.testCaseTitle}` : ''}</p>
                    </div>
                  )}

                  {/* Created / Updated (read-only) */}
                  <div>
                    <p className="text-xs text-gray-400 mb-0.5">Created</p>
                    <p className="text-sm text-gray-600">{fmtDate(bug.createdAt)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 mb-0.5">Last Updated</p>
                    <p className="text-sm text-gray-600">{fmtDate(bug.updatedAt)}</p>
                  </div>
                </div>

                {/* Quick Status */}
                <div className="bg-white rounded-xl border border-gray-200 p-4">
                  <h3 className="font-semibold text-gray-700 text-sm mb-2">Quick Status</h3>
                  <div className="space-y-1">
                    {BUG_STATUSES.map(s => (
                      <button key={s} type="button" onClick={() => setForm(f => ({ ...f, status: s }))}
                        className={`w-full text-left px-3 py-1.5 text-xs rounded-lg flex items-center gap-2 capitalize ${form.status === s ? bugStatStyle(s) + ' font-semibold' : 'text-gray-600 hover:bg-gray-50'}`}>
                        <span className={`w-2 h-2 rounded-full ${bugStatDot(s)}`}></span>{bugStatLabel(s)}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-between items-center px-6 pb-6">
              <button type="button" onClick={() => requestConfirm('Delete this bug?', () => onDelete(bug.id))}
                className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg text-sm font-medium">Delete Bug</button>
              <div className="flex gap-3">
                <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</button>
                <button type="submit" className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700">Save Changes</button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

const QmsSidebar = ({ data, projects, currentProjectId, onProjectChange, onReportBug, onEditBug, onViewBug }) => (
  <div className="space-y-6">
    <div className="bg-white rounded-lg shadow border border-gray-200 p-5">
      <h3 className="text-sm font-semibold text-gray-700 mb-3">Project</h3>
      <select value={currentProjectId || ''} onChange={e => onProjectChange(e.target.value)}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mb-3 focus:ring-2 focus:ring-teal-400">
        {(projects || []).map(p => <option key={p.projectId} value={p.projectId}>{p.projectName}</option>)}
      </select>
      {data?.project && (
        <div className="space-y-2 text-sm">
          <div className="flex justify-between"><span className="text-gray-500">QA Lead</span><span className="font-medium">{data.project.qaLead || '-'}</span></div>
          <div className="flex justify-between"><span className="text-gray-500">Environment</span><span className="font-medium">{data.project.environment || '-'}</span></div>
          <div className="flex justify-between"><span className="text-gray-500">Stage</span><span className="font-medium capitalize">{data.project.currentStage || '-'}</span></div>
          <div className="flex justify-between"><span className="text-gray-500">Status</span><span className="font-medium capitalize">{data.project.status || '-'}</span></div>
        </div>
      )}
    </div>
    <div className="bg-white rounded-lg shadow border border-gray-200 p-5">
      <h3 className="text-sm font-semibold text-gray-700 mb-3">Recent Bugs</h3>
      {(data?.bugs || []).slice(0, 3).map(b => {
        const bs = qmsSeverityStyle(b.severity);
        return (
          <div key={b.id} className="bg-gray-50 rounded-lg p-3 mb-2 cursor-pointer hover:bg-gray-100" onClick={() => onViewBug ? onViewBug(b.id) : onEditBug(b)} style={{ borderLeftWidth: '3px', borderLeftColor: bs.color }}>
            <div className="flex justify-between items-center">
              <div className="font-semibold text-xs text-gray-900">{b.bugCode}</div>
              <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold capitalize" style={{ background: bs.bg, color: bs.color }}>{b.severity}</span>
            </div>
            <div className="text-xs text-gray-500 mt-0.5">{b.title}</div>
            <div className="text-[10px] text-gray-400 mt-1 capitalize">{b.status}</div>
          </div>
        );
      })}
      <div className="flex gap-2 mt-2">
        <button onClick={() => onReportBug(null)} className="flex-1 px-3 py-2 bg-red-100 text-red-700 rounded-lg text-xs font-medium hover:bg-red-200">+ Report Bug</button>
        {onViewBug && <button onClick={() => onViewBug('list')} className="flex-1 px-3 py-2 bg-gray-100 text-gray-600 rounded-lg text-xs font-medium hover:bg-gray-200">View All →</button>}
      </div>
    </div>
    <div className="bg-white rounded-lg shadow border border-gray-200 p-5">
      <h3 className="text-sm font-semibold text-gray-700 mb-3">Quick Stats</h3>
      <div className="space-y-2">
        {[
          { label: 'Test Cases', value: data?.metrics?.testCases?.total || 0 },
          { label: 'Bugs Reported', value: data?.metrics?.bugs?.total || 0 },
          { label: 'Bugs Resolved', value: data?.metrics?.bugs?.resolved || 0 },
          { label: 'Pass Rate', value: `${data?.metrics?.passRate || 0}%`, color: '#20c997' }
        ].map(s => (
          <div key={s.label} className="bg-gray-50 rounded-lg p-3">
            <div className="text-xs text-gray-500 mb-1">{s.label}</div>
            <div className="text-lg font-bold" style={{ color: s.color || '#1f2937' }}>{s.value}</div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const SendQaEodModal = ({ onClose }) => {
  const [userName, setUserName] = React.useState('');
  const [sending, setSending] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    ModulesModel.getQaLeads().then(leads => {
      if (leads.length > 0 && !userName) setUserName(leads[0].user_name);
    });
  }, []);

  const handleSend = async () => {
    if (!userName.trim()) { setError('Enter the QA Lead name'); return; }
    setSending(true); setError('');
    const res = await ModulesModel.sendQualityEodSummary(userName.trim());
    setSending(false);
    if (res?.success) setResult(res);
    else setError(res?.error || 'Failed to send QA EOD summary');
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-900">📤 QA EOD Summary</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
        </div>
        {result ? (
          <div className="text-center py-4">
            <div className="text-4xl mb-3">✅</div>
            <p className="font-medium text-gray-800 mb-3">QA EOD summary sent to Slack!</p>
            <div className="bg-gray-50 rounded-lg p-4 text-sm text-left space-y-1">
              <div className="flex justify-between"><span className="text-gray-500">Bugs reported + TC executed</span><span className="font-medium text-green-600">{result.totals?.total_completed}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Open bugs assigned</span><span className="font-medium text-blue-600">{result.totals?.total_in_progress}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Pending (TC + requirements)</span><span className="font-medium text-gray-600">{result.totals?.total_pending}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Slack channel</span><span className="font-medium">{result.slackChannel}</span></div>
            </div>
            <button onClick={onClose} className="mt-4 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200">Close</button>
          </div>
        ) : (
          <>
            <p className="text-sm text-gray-600 mb-4">
              Sends today's QA activity — bugs reported, test cases executed, requirements created,
              open bugs assigned, and pending test cases.
            </p>
            <label className="block text-sm font-medium text-gray-700 mb-1">QA Lead Name</label>
            <input
              type="text" value={userName} onChange={e => setUserName(e.target.value)}
              placeholder="Auto-filled from QA Lead role"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
            />
            <p className="text-xs text-gray-400 mt-1">Auto-filled from the assigned QA Lead. Edit if needed.</p>
            {error && <p className="text-red-500 text-sm mt-2 bg-red-50 p-2 rounded-lg">{error}</p>}
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900">Cancel</button>
              <button onClick={handleSend} disabled={sending} className="px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 disabled:opacity-50">
                {sending ? 'Sending…' : 'Send QA EOD'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// ALL BUGS PAGE + BUG DETAIL PAGE
// ============================================================================

const BUG_SEVERITIES = ['critical', 'high', 'medium', 'low'];
const BUG_STATUSES = ['open', 'ready', 'in_progress', 'for_qa', 'awaiting_vendor', 'needs_rework', 'resolved', 'done', 'closed'];
const BUG_STATUS_LABELS = { open: 'Open', ready: 'Ready', in_progress: 'In Progress', for_qa: 'For QA', awaiting_vendor: 'Awaiting Vendor', needs_rework: 'Needs Rework', resolved: 'Resolved', done: 'Done', closed: 'Closed' };
const bugStatLabel = (s) => BUG_STATUS_LABELS[s] || s.replace(/_/g, ' ');

const bugSevStyle = (s) => ({ critical: 'bg-red-100 text-red-700', high: 'bg-orange-100 text-orange-700', medium: 'bg-yellow-100 text-yellow-700', low: 'bg-green-100 text-green-700' }[s] || 'bg-gray-100 text-gray-600');
const bugStatStyle = (s) => ({ open: 'bg-red-50 text-red-600', ready: 'bg-purple-50 text-purple-700', in_progress: 'bg-blue-50 text-blue-700', for_qa: 'bg-pink-50 text-pink-700', awaiting_vendor: 'bg-amber-50 text-amber-700', needs_rework: 'bg-orange-50 text-orange-700', resolved: 'bg-green-50 text-green-700', done: 'bg-teal-50 text-teal-700', closed: 'bg-gray-100 text-gray-500' }[s] || 'bg-gray-100 text-gray-600');
const bugStatDot = (s) => ({ open: 'bg-red-500', ready: 'bg-purple-500', in_progress: 'bg-blue-500', for_qa: 'bg-pink-500', awaiting_vendor: 'bg-amber-500', needs_rework: 'bg-orange-500', resolved: 'bg-green-500', done: 'bg-teal-500', closed: 'bg-gray-400' }[s] || 'bg-gray-400');

const BugRow = ({ bug, onViewBug, onEditBug, canEdit, showProject = false }) => (
  <div className="grid gap-x-4 px-4 py-3 hover:bg-teal-50 items-center transition-colors border-b border-gray-100 last:border-0"
    style={{ gridTemplateColumns: canEdit ? '5rem 1fr auto auto auto auto auto' : '5rem 1fr auto auto auto auto' }}>
    <span className="text-xs font-mono text-gray-400 cursor-pointer" onClick={() => onViewBug(bug.id)}>{bug.bugCode}</span>
    <div className="min-w-0 cursor-pointer" onClick={() => onViewBug(bug.id)}>
      <p className="text-sm font-medium text-gray-900 truncate">{bug.title}</p>
      <p className="text-xs text-gray-400 truncate">{[bug.module, showProject && bug.projectName].filter(Boolean).join(' · ')}</p>
    </div>
    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full capitalize ${bugSevStyle(bug.severity)}`}>{bug.severity}</span>
    <span className={`text-xs font-medium px-2 py-0.5 rounded-full capitalize ${bugStatStyle(bug.status)}`}>{bug.status.replace('_', ' ')}</span>
    <span className="text-xs text-gray-500 truncate max-w-28">{bug.assignedTo || '—'}</span>
    <span className="text-xs text-gray-400 whitespace-nowrap">
      {bug.estimatedMinutes != null ? `Est: ${bug.estimatedMinutes}m` : ''}
      {bug.estimatedMinutes != null && bug.actualMinutes != null ? ' · ' : ''}
      {bug.actualMinutes != null ? `Act: ${bug.actualMinutes}m` : ''}
      {bug.estimatedMinutes == null && bug.actualMinutes == null ? '—' : ''}
    </span>
    {canEdit && (
      <button onClick={e => { e.stopPropagation(); onEditBug(bug); }}
        className="flex items-center gap-1 text-xs text-gray-400 hover:text-teal-600 hover:bg-teal-50 px-2 py-1 rounded-lg border border-transparent hover:border-teal-200 transition-colors whitespace-nowrap">
        <Pencil size={11} /> Edit
      </button>
    )}
  </div>
);

const ProjectBugGroup = ({ projectName, projectId, bugs, onViewBug, onEditBug, onFilterToProject, canEdit, defaultOpen = true }) => {
  const [open, setOpen] = React.useState(defaultOpen);
  const openCount = bugs.filter(b => b.status === 'open').length;
  const criticalCount = bugs.filter(b => b.severity === 'critical').length;

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-3">
      {/* Project header row */}
      <div className="flex items-center gap-3 px-4 py-3 bg-gray-50 border-b border-gray-200 cursor-pointer select-none"
        onClick={() => setOpen(o => !o)}>
        <ChevronDown size={15} className={`text-gray-400 transition-transform flex-shrink-0 ${open ? '' : '-rotate-90'}`} />
        <span className="font-semibold text-gray-800 text-sm flex-1">{projectName || 'Unknown Project'}</span>
        <div className="flex items-center gap-2">
          {criticalCount > 0 && (
            <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-red-100 text-red-700">{criticalCount} critical</span>
          )}
          {openCount > 0 && (
            <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-orange-50 text-orange-600">{openCount} open</span>
          )}
          <span className="text-xs text-gray-400">{bugs.length} bug{bugs.length !== 1 ? 's' : ''}</span>
          <button onClick={e => { e.stopPropagation(); onFilterToProject(projectId); }}
            className="text-xs text-teal-600 hover:text-teal-800 hover:underline ml-1">Filter →</button>
        </div>
      </div>
      {/* Bugs */}
      {open && (
        bugs.length === 0 ? (
          <div className="px-4 py-6 text-center text-sm text-gray-400">No bugs match current filters</div>
        ) : (
          <div>
            <div className="gap-x-4 px-4 py-1.5 text-xs font-semibold text-gray-400 uppercase tracking-wide border-b border-gray-100 bg-white"
              style={{ display: 'grid', gridTemplateColumns: canEdit ? '5rem 1fr auto auto auto auto' : '5rem 1fr auto auto auto' }}>
              <span>ID</span><span>Title</span><span>Severity</span><span>Status</span><span>Assigned</span>
              {canEdit && <span></span>}
            </div>
            {bugs.map(bug => <BugRow key={bug.id} bug={bug} onViewBug={onViewBug} onEditBug={onEditBug} canEdit={canEdit} />)}
          </div>
        )
      )}
    </div>
  );
};

const AllBugsView = ({ bugsData, filters, onFilterChange, onViewBug, onEditBug, canEdit, onReportBug }) => {
  const bugs = bugsData?.bugs || [];
  const metrics = bugsData?.metrics || {};
  const allProjects = bugsData?.projects || [];

  // Group bugs by project
  const grouped = React.useMemo(() => {
    const map = {};
    bugs.forEach(b => {
      const key = b.projectId || '__none__';
      if (!map[key]) map[key] = { projectId: b.projectId, projectName: b.projectName || 'No Project', bugs: [] };
      map[key].bugs.push(b);
    });
    // Sort groups: most bugs first
    return Object.values(map).sort((a, b) => b.bugs.length - a.bugs.length);
  }, [bugs]);

  const isFiltered = !!(filters.severity || filters.status || filters.projectId || filters.search);

  return (
    <div>
      {/* Metric cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
        {[
          { label: 'Total Bugs', value: metrics.total || 0, icon: '🐛', color: 'text-gray-800', bg: 'bg-gray-50' },
          { label: 'Open', value: metrics.open || 0, icon: '🔴', color: 'text-red-700', bg: 'bg-red-50' },
          { label: 'In Progress', value: metrics.inProgress || 0, icon: '🔵', color: 'text-blue-700', bg: 'bg-blue-50' },
          { label: 'Resolved', value: (metrics.resolved || 0) + (metrics.closed || 0), icon: '✅', color: 'text-green-700', bg: 'bg-green-50' },
        ].map(c => (
          <div key={c.label} className={`${c.bg} rounded-xl p-4 border border-gray-100`}>
            <div className="flex items-center gap-2 mb-1"><span className="text-lg">{c.icon}</span><span className="text-xs text-gray-500">{c.label}</span></div>
            <div className={`text-2xl font-bold ${c.color}`}>{c.value}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-3 mb-4 flex flex-wrap gap-2 items-center">
        <div className="flex items-center gap-2 flex-1 min-w-36">
          <Search size={14} className="text-gray-400 flex-shrink-0" />
          <input value={filters.search} onChange={e => onFilterChange({ search: e.target.value })}
            placeholder="Search bugs..." className="flex-1 text-sm border-0 outline-none bg-transparent placeholder-gray-400" />
        </div>
        <select value={filters.severity} onChange={e => onFilterChange({ severity: e.target.value })} className="text-sm border border-gray-200 rounded-lg px-2 py-1.5 text-gray-700">
          <option value="">All Severities</option>
          {BUG_SEVERITIES.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
        </select>
        <select value={filters.status} onChange={e => onFilterChange({ status: e.target.value })} className="text-sm border border-gray-200 rounded-lg px-2 py-1.5 text-gray-700">
          <option value="">All Statuses</option>
          {BUG_STATUSES.map(s => <option key={s} value={s}>{bugStatLabel(s)}</option>)}
        </select>
        {allProjects.length > 1 && (
          <select value={filters.projectId} onChange={e => onFilterChange({ projectId: e.target.value })} className="text-sm border border-gray-200 rounded-lg px-2 py-1.5 text-gray-700">
            <option value="">All Projects</option>
            {allProjects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        )}
        <select value={`${filters.sort}_${filters.order}`} onChange={e => { const [sort, order] = e.target.value.split('_'); onFilterChange({ sort, order }); }} className="text-sm border border-gray-200 rounded-lg px-2 py-1.5 text-gray-700">
          <option value="created_at_desc">Newest first</option>
          <option value="created_at_asc">Oldest first</option>
          <option value="updated_at_desc">Recently updated</option>
          <option value="severity_asc">Severity (high→low)</option>
        </select>
        {isFiltered && (
          <button onClick={() => onFilterChange({ severity: '', status: '', projectId: '', search: '' })} className="text-xs text-gray-400 hover:text-red-500 underline">Clear</button>
        )}
        <button onClick={onReportBug} className="ml-auto px-3 py-1.5 bg-teal-600 text-white text-sm rounded-lg hover:bg-teal-700 font-medium whitespace-nowrap">+ Report Bug</button>
      </div>

      {/* Active project filter banner */}
      {filters.projectId && (
        <div className="flex items-center gap-2 mb-3 px-3 py-2 bg-teal-50 border border-teal-200 rounded-lg text-sm text-teal-800">
          <span>Showing bugs for: <strong>{allProjects.find(p => p.id === filters.projectId)?.name || filters.projectId}</strong></span>
          <button onClick={() => onFilterChange({ projectId: '' })} className="ml-auto text-teal-500 hover:text-teal-800 text-xs underline">Show all projects</button>
        </div>
      )}

      {/* Grouped bug list */}
      {bugs.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 py-14 text-center">
          <div className="text-4xl mb-2">🎉</div>
          <p className="text-gray-500 font-medium">No bugs found</p>
          <p className="text-gray-400 text-sm mt-1">{isFiltered ? 'Try adjusting your filters' : 'All systems clean!'}</p>
        </div>
      ) : (
        <div>
          {grouped.map((group, i) => (
            <ProjectBugGroup
              key={group.projectId || i}
              projectName={group.projectName}
              projectId={group.projectId}
              bugs={group.bugs}
              onViewBug={onViewBug}
              onEditBug={onEditBug}
              canEdit={canEdit}
              onFilterToProject={(pid) => onFilterChange({ projectId: pid })}
              defaultOpen={i === 0 || grouped.length <= 3}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const BugFieldEditor = ({ initialValue = '', onChange, placeholder = '', minHeight = '80px' }) => {
  const editorRef = React.useRef(null);
  const [isEmpty, setIsEmpty] = React.useState(!initialValue);
  const mountedRef = React.useRef(false);

  React.useEffect(() => {
    if (editorRef.current && !mountedRef.current) {
      mountedRef.current = true;
      if (initialValue) { editorRef.current.innerHTML = initialValue; setIsEmpty(false); }
    }
  }, []);

  const syncValue = () => {
    const html = editorRef.current?.innerHTML || '';
    const empty = !html || html === '<br>' || html === '<div><br></div>' || html === '<p><br></p>';
    setIsEmpty(empty);
    onChange(empty ? '' : html);
  };

  const exec = (cmd, val = null) => { document.execCommand(cmd, false, val); editorRef.current?.focus(); syncValue(); };

  const insertHtmlAtCursor = (html) => {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    range.deleteContents();
    const div = document.createElement('div');
    div.innerHTML = html;
    const frag = document.createDocumentFragment();
    let lastNode = null;
    while (div.firstChild) { lastNode = frag.appendChild(div.firstChild); }
    range.insertNode(frag);
    if (lastNode) {
      const r = range.cloneRange();
      r.setStartAfter(lastNode);
      r.collapse(true);
      sel.removeAllRanges();
      sel.addRange(r);
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const items = Array.from(e.clipboardData?.items || []);

    // Priority 1: raw image binary → convert to base64 and embed
    const imageItem = items.find(item => item.type.startsWith('image/'));
    if (imageItem) {
      const blob = imageItem.getAsFile();
      if (blob) {
        // D1 caps a single text value at ~1MB. Base64 inflates binary ~33%, so 700KB binary → ~933KB base64, still under the cap.
        if (blob.size > 700 * 1024) {
          alert('Pasted image is over 700 KB. Use a smaller screenshot or attach it via the Attachments section instead.');
          return;
        }
        const reader = new FileReader();
        reader.onload = (ev) => {
          insertHtmlAtCursor(`<img src="${ev.target.result}" style="max-width:100%;border-radius:4px;margin:4px 0;" alt="pasted image"/>`);
          syncValue();
        };
        reader.readAsDataURL(blob);
        return;
      }
    }

    // Priority 2: HTML clipboard with <img> tags (e.g. copy image from browser)
    const htmlItem = items.find(item => item.type === 'text/html');
    if (htmlItem) {
      htmlItem.getAsString((html) => {
        if (/<img[\s>]/i.test(html)) {
          // Sanitize: strip scripts and event handlers, keep img/formatting
          const clean = html
            .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
            .replace(/on\w+\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]*)/gi, '')
            .replace(/javascript\s*:/gi, '');
          insertHtmlAtCursor(clean);
        } else {
          document.execCommand('insertText', false, e.clipboardData.getData('text/plain'));
        }
        syncValue();
      });
      return;
    }

    // Priority 3: plain text fallback
    document.execCommand('insertText', false, e.clipboardData.getData('text/plain'));
  };

  const tbtn = (cmd, content, title) => (
    <button type="button" title={title} key={cmd} onMouseDown={e => { e.preventDefault(); exec(cmd); }}
      className="px-2 py-1 text-xs rounded hover:bg-gray-200 transition-colors text-gray-700 select-none">{content}</button>
  );

  return (
    <div className="border border-gray-300 rounded-xl overflow-hidden focus-within:ring-2 focus-within:ring-teal-400 focus-within:border-teal-400 bg-white">
      <div className="flex items-center gap-0.5 px-2 py-1.5 bg-gray-50 border-b border-gray-200 flex-wrap">
        {tbtn('bold', <strong className="font-bold">B</strong>, 'Bold')}
        {tbtn('italic', <em>I</em>, 'Italic')}
        {tbtn('underline', <u>U</u>, 'Underline')}
        {tbtn('strikeThrough', <s>S</s>, 'Strikethrough')}
        <div className="w-px h-4 bg-gray-300 mx-1" />
        {tbtn('insertUnorderedList', '• List', 'Bullet list')}
        {tbtn('insertOrderedList', '1. List', 'Numbered list')}
        <div className="w-px h-4 bg-gray-300 mx-1" />
        {tbtn('removeFormat', 'Aa✕', 'Clear formatting')}
      </div>
      <div className="relative">
        {isEmpty && <div className="absolute top-2.5 left-3 text-sm text-gray-400 pointer-events-none select-none">{placeholder}</div>}
        <div ref={editorRef} contentEditable onInput={syncValue} onPaste={handlePaste}
          style={{ minHeight }}
          className="max-h-48 overflow-y-auto px-3 py-2.5 text-sm text-gray-800 focus:outline-none prose prose-sm max-w-none [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5"
          suppressContentEditableWarning />
      </div>
    </div>
  );
};

const BugCommentEditor = ({ onChange, placeholder = 'Add a comment…' }) => {
  const editorRef = React.useRef(null);
  const [isEmpty, setIsEmpty] = React.useState(true);

  const syncValue = () => {
    const html = editorRef.current?.innerHTML || '';
    const empty = !html || html === '<br>' || html === '<div><br></div>' || html === '<p><br></p>';
    setIsEmpty(empty);
    onChange(empty ? '' : html);
  };

  const exec = (cmd, val = null) => {
    document.execCommand(cmd, false, val);
    editorRef.current?.focus();
    syncValue();
  };

  const handlePaste = (e) => {
    const items = Array.from(e.clipboardData?.items || []);
    const imageItem = items.find(item => item.type.startsWith('image/'));
    if (imageItem) {
      e.preventDefault();
      const blob = imageItem.getAsFile();
      if (!blob) return;
      if (blob.size > 2 * 1024 * 1024) { alert('Image too large. Max 2MB.'); return; }
      const reader = new FileReader();
      reader.onload = (ev) => {
        document.execCommand('insertHTML', false, `<img src="${ev.target.result}" style="max-width:100%;border-radius:4px;margin:4px 0;" alt="pasted image"/>`);
        syncValue();
      };
      reader.readAsDataURL(blob);
    } else {
      e.preventDefault();
      const text = e.clipboardData.getData('text/plain');
      document.execCommand('insertText', false, text);
    }
  };

  const tbtn = (cmd, content, title) => (
    <button type="button" title={title} key={cmd}
      onMouseDown={e => { e.preventDefault(); exec(cmd); }}
      className="px-2 py-1 text-xs rounded hover:bg-gray-200 transition-colors text-gray-700 select-none">
      {content}
    </button>
  );

  return (
    <div className="border border-gray-300 rounded-xl overflow-hidden focus-within:ring-2 focus-within:ring-teal-400 focus-within:border-teal-400 bg-white">
      {/* Toolbar */}
      <div className="flex items-center gap-0.5 px-2 py-1.5 bg-gray-50 border-b border-gray-200 flex-wrap">
        {tbtn('bold', <strong className="font-bold">B</strong>, 'Bold')}
        {tbtn('italic', <em>I</em>, 'Italic')}
        {tbtn('underline', <u>U</u>, 'Underline')}
        {tbtn('strikeThrough', <s>S</s>, 'Strikethrough')}
        <div className="w-px h-4 bg-gray-300 mx-1" />
        {tbtn('insertUnorderedList', '• List', 'Bullet list')}
        {tbtn('insertOrderedList', '1. List', 'Numbered list')}
        <div className="w-px h-4 bg-gray-300 mx-1" />
        {tbtn('removeFormat', 'Aa✕', 'Clear formatting')}
      </div>
      {/* Editable area */}
      <div className="relative">
        {isEmpty && (
          <div className="absolute top-2.5 left-3 text-sm text-gray-400 pointer-events-none select-none">{placeholder}</div>
        )}
        <div
          ref={editorRef}
          contentEditable
          onInput={syncValue}
          onPaste={handlePaste}
          className="min-h-[80px] max-h-48 overflow-y-auto px-3 py-2.5 text-sm text-gray-800 focus:outline-none prose prose-sm max-w-none [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5"
          suppressContentEditableWarning
        />
      </div>
    </div>
  );
};

const BugPageView = ({ bug, user, onBack, onUpdate, onAddComment, onDeleteComment }) => {
  const [activeTab, setActiveTab] = React.useState('details');
  const [editField, setEditField] = React.useState(null);
  const [editValue, setEditValue] = React.useState('');
  const [commentHtml, setCommentHtml] = React.useState('');
  const [commentKey, setCommentKey] = React.useState(0);
  const [submitting, setSubmitting] = React.useState(false);

  if (!bug) return null;

  const userRole = user?.role || user?.user_role;
  const isAdmin = userRole === 'admin';
  const canEdit = ['admin', 'qa_lead', 'qa', 'devops', 'dataops'].includes(userRole) || user?.user_name === bug.reportedBy || user?.user_name === bug.assignedTo;

  const startEdit = (field, val) => { setEditField(field); setEditValue(val || ''); };
  const saveField = async (field) => {
    const keyMap = { status: 'status', severity: 'severity', assignedTo: 'assignedTo', module: 'module', environment: 'environment', title: 'title', stepsToReproduce: 'stepsToReproduce', expectedResult: 'expectedResult', actualResult: 'actualResult', estimatedMinutes: 'estimatedMinutes', actualMinutes: 'actualMinutes' };
    const val = (field === 'estimatedMinutes' || field === 'actualMinutes') ? (parseInt(editValue) || null) : editValue;
    await onUpdate(bug.id, { [keyMap[field]]: val });
    setEditField(null);
  };

  const handleComment = async () => {
    const stripped = commentHtml.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
    const hasImage = commentHtml.includes('<img');
    if (!stripped && !hasImage) return;
    setSubmitting(true);
    await onAddComment(bug.id, commentHtml);
    setCommentHtml('');
    setCommentKey(k => k + 1);
    setSubmitting(false);
  };

  const fmtDate = (d) => d ? new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—';

  const inlineSelect = (field, options) => (
    editField === field ? (
      <div className="flex items-center gap-2">
        <select value={editValue} onChange={e => setEditValue(e.target.value)} autoFocus className="text-xs border border-teal-400 rounded-full px-2 py-0.5 focus:ring-2 focus:ring-teal-400">
          {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <button onClick={() => saveField(field)} className="text-xs px-2 py-0.5 bg-teal-600 text-white rounded-full">Save</button>
        <button onClick={() => setEditField(null)} className="text-xs text-gray-500 hover:text-gray-800">✕</button>
      </div>
    ) : (
      <div className="flex items-center gap-1.5 group cursor-pointer" onClick={() => canEdit && startEdit(field, bug[field])}>
        <span className={field === 'severity' ? `text-xs font-semibold px-3 py-1 rounded-full capitalize ${bugSevStyle(bug[field])}` : `text-xs font-medium px-3 py-1 rounded-full capitalize flex items-center gap-1 ${bugStatStyle(bug[field])}`}>
          {field === 'status' && <span className={`w-1.5 h-1.5 rounded-full ${bugStatDot(bug[field])}`}></span>}
          {(bug[field] || '—').replace('_', ' ')}
        </span>
        {canEdit && <Pencil size={11} className="text-gray-300 group-hover:text-teal-500 opacity-0 group-hover:opacity-100 transition-opacity" />}
      </div>
    )
  );

  return (
    <div className="max-w-6xl mx-auto px-4 py-6">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 mb-5 text-sm">
        <button onClick={onBack} className="flex items-center gap-1 text-gray-500 hover:text-teal-700 font-medium"><ArrowLeft size={15} /> All Bugs</button>
        <ChevronRight size={14} className="text-gray-300" />
        <span className="font-mono text-gray-400">{bug.bugCode}</span>
        <span className="ml-auto text-xs text-gray-400">{bug.projectName || ''}</span>
      </div>

      <div className="grid grid-cols-3 gap-6">

          {/* Main */}
          <div className="col-span-2 space-y-4">
            {/* Title */}
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <div className="flex items-center gap-3 mb-4 flex-wrap">
                  {inlineSelect('severity', BUG_SEVERITIES.map(s => ({ value: s, label: s })))}
                  {inlineSelect('status', BUG_STATUSES.map(s => ({ value: s, label: bugStatLabel(s) })))}
                </div>
                {editField === 'title' ? (
                  <div className="space-y-2">
                    <input value={editValue} onChange={e => setEditValue(e.target.value)} autoFocus
                      className="w-full text-xl font-bold border border-teal-400 rounded-lg px-3 py-2 focus:ring-2 focus:ring-teal-400" />
                    <div className="flex gap-2">
                      <button onClick={() => saveField('title')} className="px-3 py-1.5 bg-teal-600 text-white text-sm rounded-lg">Save</button>
                      <button onClick={() => setEditField(null)} className="text-sm text-gray-500">Cancel</button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start gap-2 group cursor-pointer" onClick={() => canEdit && startEdit('title', bug.title)}>
                    <h1 className="text-xl font-bold text-gray-900 flex-1">{bug.title}</h1>
                    {canEdit && <Pencil size={14} className="text-gray-300 group-hover:text-teal-500 opacity-0 group-hover:opacity-100 mt-1 flex-shrink-0" />}
                  </div>
                )}
              </div>

              {/* Tabbed card */}
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="flex border-b border-gray-200 px-4 pt-3 gap-1">
                  {[
                    { key: 'details', label: '📋 Details' },
                    { key: 'comments', label: `💬 Comments${bug.comments?.length ? ` (${bug.comments.length})` : ''}` },
                    { key: 'history', label: `⚡ History${bug.history?.length ? ` (${bug.history.length})` : ''}` },
                  ].map(t => (
                    <button key={t.key} onClick={() => setActiveTab(t.key)}
                      className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${activeTab === t.key ? 'bg-teal-50 text-teal-700 border-b-2 border-teal-600' : 'text-gray-500 hover:text-gray-700'}`}>
                      {t.label}
                    </button>
                  ))}
                </div>

                <div className="p-5">
                  {/* Details Tab */}
                  {activeTab === 'details' && (
                    <div className="space-y-4">
                      {['stepsToReproduce', 'expectedResult', 'actualResult'].map(field => {
                        const labels = { stepsToReproduce: 'Steps to Reproduce', expectedResult: 'Expected Result', actualResult: 'Actual Result' };
                        return (
                          <div key={field}>
                            <div className="flex items-center justify-between mb-2">
                              <h3 className="text-sm font-semibold text-gray-700">{labels[field]}</h3>
                              {canEdit && editField !== field && <button onClick={() => startEdit(field, bug[field])} className="text-gray-300 hover:text-teal-500"><Pencil size={13} /></button>}
                            </div>
                            {editField === field ? (
                              <div className="space-y-2">
                                <BugFieldEditor key={`${field}-${bug.id}`} initialValue={editValue} onChange={setEditValue} minHeight="80px" />
                                <div className="flex gap-2">
                                  <button onClick={() => saveField(field)} className="px-3 py-1.5 bg-teal-600 text-white text-sm rounded-lg">Save</button>
                                  <button onClick={() => setEditField(null)} className="text-sm text-gray-500">Cancel</button>
                                </div>
                              </div>
                            ) : (
                              bug[field]
                                ? <div className="text-sm text-gray-700 leading-relaxed prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: sanitizeBugHtml(hydrateScreenshotPlaceholders(bug[field], bug.attachments)) }} />
                                : <span className="text-gray-400 italic text-sm">Not provided</span>
                            )}
                            {field !== 'actualResult' && <div className="border-b border-gray-100 mt-4" />}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Comments Tab */}
                  {activeTab === 'comments' && (
                    <div className="space-y-4">
                      {(bug.comments || []).length === 0 && (
                        <p className="text-sm text-gray-400 text-center py-4">No comments yet. Be the first to comment!</p>
                      )}
                      {(bug.comments || []).map(item => (
                        <div key={item.id} className="flex gap-3">
                          <div className="w-7 h-7 rounded-full bg-teal-100 flex items-center justify-center text-xs font-bold text-teal-700 flex-shrink-0">
                            {(item.userName || '?')[0].toUpperCase()}
                          </div>
                          <div className="flex-1 bg-gray-50 rounded-lg px-3 py-2">
                            <div className="flex items-center justify-between gap-2 mb-1">
                              <span className="text-xs font-semibold text-gray-700">{item.userName}</span>
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-gray-400">{fmtDate(item.createdAt)}</span>
                                {(isAdmin || user?.user_name === item.userName) && (
                                  <button onClick={() => onDeleteComment(bug.id, item.id)} className="text-gray-300 hover:text-red-500"><Trash2 size={11} /></button>
                                )}
                              </div>
                            </div>
                            <div className="text-sm text-gray-700 prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: sanitizeBugHtml(item.content) }} />
                          </div>
                        </div>
                      ))}
                      <div className="border-t border-gray-100 pt-4 space-y-2">
                        <BugCommentEditor key={commentKey} onChange={setCommentHtml} placeholder="Add a comment… (supports bold, italic, lists)" />
                        <div className="flex items-center justify-between">
                          <p className="text-xs text-gray-400">Ctrl+B Bold · Ctrl+I Italic · Ctrl+U Underline</p>
                          <button onClick={handleComment} disabled={!commentHtml || submitting}
                            className="px-4 py-2 bg-teal-600 text-white text-sm rounded-lg hover:bg-teal-700 disabled:opacity-40 font-medium">
                            {submitting ? 'Posting…' : 'Post'}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* History Tab */}
                  {activeTab === 'history' && (
                    <div className="space-y-3">
                      {(bug.history || []).length === 0 && <p className="text-sm text-gray-400 text-center py-4">No history yet.</p>}
                      {(bug.history || []).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).map(item => (
                        <div key={item.id} className="flex gap-3 items-start">
                          <div className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center text-xs text-gray-500 flex-shrink-0">⚡</div>
                          <div className="flex-1 py-1">
                            <p className="text-xs text-gray-500">
                              <span className="font-medium text-gray-700">{item.changedBy || 'System'}</span>
                              {' changed '}<span className="font-medium">{item.field.replace(/([A-Z])/g, ' $1').toLowerCase()}</span>
                              {item.oldValue && <> from <span className="font-mono bg-gray-100 px-1 rounded">{item.oldValue}</span></>}
                              {' → '}<span className="font-mono bg-teal-50 text-teal-700 px-1 rounded">{item.newValue}</span>
                              <span className="ml-2 text-gray-400">{fmtDate(item.createdAt)}</span>
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

          {/* Sidebar */}
          <div className="space-y-4">
              <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-4">
                <h3 className="font-semibold text-gray-700 text-sm border-b border-gray-100 pb-2">Bug Details</h3>
                {[
                  { label: 'Reported By', value: bug.reportedBy },
                  { label: 'Assigned To', value: bug.assignedTo, field: 'assignedTo', editable: true, type: 'text' },
                  { label: 'Environment', value: bug.environment, field: 'environment', editable: true, type: 'select', options: ['DEV', 'QA', 'UAT', 'PROD'] },
                  { label: 'Module', value: bug.module, field: 'module', editable: true, type: 'text' },
                  { label: 'Est. Time (min)', value: bug.estimatedMinutes != null ? String(bug.estimatedMinutes) : null, field: 'estimatedMinutes', editable: true, type: 'number' },
                  { label: 'Actual Time (min)', value: bug.actualMinutes != null ? String(bug.actualMinutes) : null, field: 'actualMinutes', editable: true, type: 'number' },
                  { label: 'Project', value: bug.projectName },
                  { label: 'Linked TC', value: bug.testCaseCode ? `${bug.testCaseCode} — ${bug.testCaseTitle || ''}` : null },
                  { label: 'Created', value: fmtDate(bug.createdAt) },
                  { label: 'Last Updated', value: fmtDate(bug.updatedAt) },
                ].filter(r => r.value !== undefined).map(row => (
                  <div key={row.label}>
                    <p className="text-xs text-gray-400 mb-0.5">{row.label}</p>
                    {row.editable && canEdit && editField === row.field ? (
                      <div className="flex gap-1">
                        {row.type === 'select' ? (
                          <select value={editValue} onChange={e => setEditValue(e.target.value)} className="flex-1 text-sm border border-teal-400 rounded px-2 py-1">
                            {row.options.map(o => <option key={o}>{o}</option>)}
                          </select>
                        ) : (
                          <input type={row.type === 'number' ? 'number' : 'text'} min={row.type === 'number' ? '0' : undefined} value={editValue} onChange={e => setEditValue(e.target.value)} autoFocus className="flex-1 text-sm border border-teal-400 rounded px-2 py-1" />
                        )}
                        <button onClick={() => saveField(row.field)} className="px-2 py-1 bg-teal-600 text-white text-xs rounded">✓</button>
                        <button onClick={() => setEditField(null)} className="text-gray-400 text-xs px-1">✕</button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1 group">
                        <p className="text-sm text-gray-800 font-medium flex-1">{row.value || <span className="text-gray-400 italic">—</span>}</p>
                        {row.editable && canEdit && <button onClick={() => startEdit(row.field, bug[row.field])} className="opacity-0 group-hover:opacity-100 text-gray-300 hover:text-teal-500"><Pencil size={11} /></button>}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Quick Status */}
              {canEdit && (
                <div className="bg-white rounded-xl border border-gray-200 p-4">
                  <h3 className="font-semibold text-gray-700 text-sm mb-2">Quick Status</h3>
                  <div className="space-y-1">
                    {BUG_STATUSES.map(s => (
                      <button key={s} onClick={() => onUpdate(bug.id, { status: s })}
                        className={`w-full text-left px-3 py-1.5 text-xs rounded-lg flex items-center gap-2 capitalize ${bug.status === s ? bugStatStyle(s) + ' font-semibold' : 'text-gray-600 hover:bg-gray-50'}`}>
                        <span className={`w-2 h-2 rounded-full ${bugStatDot(s)}`}></span>{bugStatLabel(s)}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Attachments */}
              {bug.attachments?.length > 0 && (
                <div className="bg-white rounded-xl border border-gray-200 p-4">
                  <h3 className="font-semibold text-gray-700 text-sm mb-2">📎 Attachments ({bug.attachments.length})</h3>
                  <div className="space-y-1">
                    {bug.attachments.map(a => (
                      <div key={a.id} className="flex items-center gap-2 text-xs text-gray-600">
                        <span>📄</span><span className="truncate flex-1">{a.fileName}</span>
                        <span className="text-gray-400">{a.fileSize ? `${Math.round(a.fileSize / 1024)}KB` : ''}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
    </div>
  );
};

// ---- QMS Coverage Badge ----
const QmsCoverageBadge = ({ coverage, totalTcs }) => {
  if (totalTcs === 0) return <span className="px-2 py-0.5 rounded text-xs font-semibold bg-gray-100 text-gray-400">No Tests</span>;
  if (coverage >= 90) return <span className="px-2 py-0.5 rounded text-xs font-semibold bg-green-100 text-green-700">✓ Fully Tested {coverage}%</span>;
  if (coverage >= 60) return <span className="px-2 py-0.5 rounded text-xs font-semibold bg-yellow-100 text-yellow-700">⚡ Partial {coverage}%</span>;
  return <span className="px-2 py-0.5 rounded text-xs font-semibold bg-red-100 text-red-700">⚠ Risky {coverage}%</span>;
};

// ---- QMS Requirements View (standalone) ----
const QmsRequirementsView = ({ projectId, requirements = [], onAdd, onEdit, onViewDetail, onQaStatusChange, onTestResultChange }) => {
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [collapsed, setCollapsed] = useState({});
  const [reqTypes, setReqTypes] = useState(DEFAULT_REQ_TYPES);
  useEffect(() => { ModulesModel.getReqTypes().then(ts => { if (Array.isArray(ts) && ts.length > 0) setReqTypes(ts); }); }, []);

  const qaStatusStyles = {
    not_tested: 'bg-gray-100 text-gray-500',
    pending_review: 'bg-yellow-100 text-yellow-700',
    approved_for_test_plan: 'bg-teal-100 text-teal-700',
    needs_clarification: 'bg-orange-100 text-orange-700',
  };
  const qaStatusLabels = { not_tested: 'Not Tested', pending_review: 'Pending Review', approved_for_test_plan: 'Approved', needs_clarification: 'Needs Clarification' };

  const testResultStyles = {
    pass:  'bg-green-100 text-green-700',
    fail:  'bg-red-100 text-red-700',
    block: 'bg-amber-100 text-amber-800',
  };
  const testResultLabels = { pass: 'Pass', fail: 'Fail', block: 'Block' };

  const TYPE_LABELS = Object.fromEntries(reqTypes.map(t => [t.name, t.label]));
  const TYPE_STYLES = Object.fromEntries(reqTypes.map(t => [t.name, REQ_TYPE_COLOR_PALETTE[t.color] || REQ_TYPE_COLOR_PALETTE.teal]));
  const TYPE_ORDER = reqTypes.map(t => t.name);

  // Apply search + status filter. For sub-requirements, also keep them when their parent matches.
  const matchesFilters = (r) => {
    if (filter !== 'all' && (r.qaStatus || r.qa_status || 'not_tested') !== filter) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!r.title.toLowerCase().includes(q) && !(r.reqCode || '').toLowerCase().includes(q)) return false;
    }
    return true;
  };
  const byId = new Map(requirements.map(r => [r.id, r]));
  const matched = new Set(requirements.filter(matchesFilters).map(r => r.id));
  // Also include ancestors of matched sub-reqs so their parents render.
  for (const id of [...matched]) {
    let cur = byId.get(id);
    while (cur?.parentRequirementId && byId.has(cur.parentRequirementId)) {
      matched.add(cur.parentRequirementId);
      cur = byId.get(cur.parentRequirementId);
    }
  }
  const visibleReqs = requirements.filter(r => matched.has(r.id));

  // Group: top-level (no parent or orphaned sub-req) vs sub-reqs
  const visibleIds = new Set(visibleReqs.map(r => r.id));
  const topLevel = visibleReqs.filter(r => !r.parentRequirementId || !visibleIds.has(r.parentRequirementId));
  const subReqsMap = {};
  visibleReqs.filter(r => r.parentRequirementId && visibleIds.has(r.parentRequirementId)).forEach(r => {
    if (!subReqsMap[r.parentRequirementId]) subReqsMap[r.parentRequirementId] = [];
    subReqsMap[r.parentRequirementId].push(r);
  });

  // Group top-level by reqType → parentCategory
  const byType = {};
  topLevel.forEach(r => {
    const type = r.reqType || 'functional';
    if (!byType[type]) byType[type] = {};
    const cat = r.parentCategory || '';
    if (!byType[type][cat]) byType[type][cat] = [];
    byType[type][cat].push(r);
  });
  const types = Object.keys(byType).sort((a, b) => (TYPE_ORDER.indexOf(a) + 1 || 99) - (TYPE_ORDER.indexOf(b) + 1 || 99));
  const toggleKey = k => setCollapsed(c => ({ ...c, [k]: !c[k] }));

  // Compact single row instead of a tall padded card — a project can accumulate
  // dozens/hundreds of requirements, and the previous card (title, description,
  // separate "Result:" bar) was ~130-150px per item.
  const renderReqCard = (r, isSubReq = false) => {
    const qaStatus = r.qaStatus || r.qa_status || 'not_tested';
    const testResult = r.testResult || r.test_result || null;
    const ps = getPriorityStyle(r.priority);
    const setResult = (val) => onTestResultChange && onTestResultChange(r.id, testResult === val ? null : val);
    const btnBase = 'w-6 h-6 flex items-center justify-center text-xs rounded border transition-colors shrink-0';
    const resultBtn = (val, activeCls, idleCls) => `${btnBase} ${testResult === val ? activeCls : idleCls}`;
    return (
      <div key={r.id} className={`flex items-start justify-between gap-3 px-4 py-2.5 hover:bg-gray-50 transition-colors flex-wrap sm:flex-nowrap ${isSubReq ? 'pl-8' : ''}`}>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 flex-wrap">
            {isSubReq && <span className="text-gray-300 font-mono text-xs select-none">&gt;&gt;&gt;</span>}
            <span className="text-[11px] font-mono text-gray-400 shrink-0">{r.reqCode}</span>
            <span className="text-sm font-semibold text-gray-900 truncate">{r.title}</span>
            <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold capitalize shrink-0" style={{ background: ps.bg, color: ps.text || ps.color }}>{r.priority}</span>
            <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold shrink-0 ${qaStatusStyles[qaStatus] || 'bg-gray-100 text-gray-500'}`}>{qaStatusLabels[qaStatus] || qaStatus}</span>
            {testResult && (
              <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold shrink-0 ${testResultStyles[testResult]}`}>{testResultLabels[testResult]}</span>
            )}
          </div>
          {r.description && <div className="text-xs text-gray-400 truncate mt-0.5">{stripHtml(r.description)}</div>}
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {onTestResultChange && (
            <>
              <button onClick={() => setResult('pass')} title="Pass"
                className={resultBtn('pass', 'bg-green-600 text-white border-green-600', 'bg-white text-green-700 border-green-200 hover:bg-green-50')}>✓</button>
              <button onClick={() => setResult('fail')} title="Fail"
                className={resultBtn('fail', 'bg-red-600 text-white border-red-600', 'bg-white text-red-700 border-red-200 hover:bg-red-50')}>✗</button>
              <button onClick={() => setResult('block')} title="Block"
                className={resultBtn('block', 'bg-amber-600 text-white border-amber-600', 'bg-white text-amber-800 border-amber-200 hover:bg-amber-50')}>⛔</button>
            </>
          )}
          <select value={qaStatus} onChange={e => onQaStatusChange(r.id, e.target.value)}
            className="text-xs border border-gray-200 rounded px-1.5 py-1 focus:ring-1 focus:ring-teal-400">
            <option value="not_tested">Not Tested</option>
            <option value="pending_review">Pending Review</option>
            <option value="approved_for_test_plan">Approved</option>
            <option value="needs_clarification">Needs Clarification</option>
          </select>
          <button onClick={() => onViewDetail(r)} className="text-xs text-teal-700 hover:text-teal-800 font-medium px-1 shrink-0">View →</button>
        </div>
      </div>
    );
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-5">
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search requirements…"
          className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400" />
        <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
          {['all', 'not_tested', 'pending_review', 'approved_for_test_plan', 'needs_clarification'].map(f => (
            <button key={f} onClick={() => setFilter(f)}
              className={`px-2 py-1 text-xs rounded font-medium transition-colors ${filter === f ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
              {f === 'all' ? 'All' : qaStatusLabels[f]}
            </button>
          ))}
        </div>
        <button onClick={onAdd} className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 shrink-0">+ Requirement</button>
      </div>

      {types.length === 0 && <div className="text-center py-12 text-gray-400 text-sm">No requirements found.</div>}

      {types.map(type => {
        const st = TYPE_STYLES[type] || TYPE_STYLES.functional || REQ_TYPE_COLOR_PALETTE.teal;
        const cats = byType[type];
        const totalCount = Object.values(cats).reduce((s, a) => s + a.length, 0);
        const isTypeCollapsed = collapsed[`type:${type}`];
        return (
          <div key={type} className="mb-6">
            <button onClick={() => toggleKey(`type:${type}`)}
              className="w-full flex items-center justify-between px-4 py-3 rounded-xl text-white font-bold text-sm uppercase tracking-wide mb-3"
              style={{ background: st.header }}>
              <span>{TYPE_LABELS[type] || type}</span>
              <span className="flex items-center gap-2">
                <span className="bg-white bg-opacity-20 px-2 py-0.5 rounded-full text-xs font-medium">{totalCount}</span>
                <ChevronDown size={16} className={`transition-transform ${isTypeCollapsed ? '-rotate-90' : ''}`} />
              </span>
            </button>
            {!isTypeCollapsed && (
              <div className="ml-2">
                {Object.keys(cats).sort((a, b) => { if (a === '') return 1; if (b === '') return -1; return a.localeCompare(b); }).map(cat => {
                  const items = cats[cat];
                  const hasCat = cat !== '';
                  const isCatCollapsed = collapsed[`cat:${type}:${cat}`];
                  return (
                    <div key={cat || '__none__'} className="mb-4">
                      {hasCat ? (
                        <button onClick={() => toggleKey(`cat:${type}:${cat}`)}
                          className="w-full flex items-center gap-2 px-3 py-2 mb-2 rounded-lg border text-sm font-semibold hover:opacity-80"
                          style={{ background: st.light, borderColor: st.border, color: st.text }}>
                          <ChevronRight size={14} className={`transition-transform ${!isCatCollapsed ? 'rotate-90' : ''}`} />
                          <span className="font-mono text-xs opacity-50">&gt;</span>
                          {cat}
                          <span className="ml-1 px-1.5 py-0.5 rounded-full text-xs font-medium" style={{ background: st.border, color: st.text }}>{items.length}</span>
                        </button>
                      ) : (
                        <div className="flex items-center gap-2 mb-2 text-xs font-semibold text-gray-400 uppercase tracking-wide px-1">
                          <span>Uncategorized</span>
                          <span className="px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded-full">{items.length}</span>
                        </div>
                      )}
                      {!isCatCollapsed && (
                        <div className="orbit-card divide-y divide-gray-100 overflow-hidden ml-1">
                          {items.map(r => (
                            <React.Fragment key={r.id}>
                              {renderReqCard(r, false)}
                              {(subReqsMap[r.id] || []).map(sub => renderReqCard(sub, true))}
                            </React.Fragment>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ---- Generate Sprint Tasks Modal ----
const SPRINT_TASK_TEMPLATES = {
  backend_feature:  ['Design API Endpoint', 'Implement Backend Logic', 'Write Unit Tests', 'Create Documentation', 'Prepare Integration Tests'],
  frontend_feature: ['Design UI Component', 'Implement Frontend Component', 'Connect API Integration', 'Write UI Tests', 'Perform Accessibility Validation'],
  integration:      ['Design Integration Architecture', 'Implement Integration Layer', 'Write Integration Tests', 'Create API Contracts', 'Document Integration Flow'],
  bug_fix:          ['Investigate Root Cause', 'Implement Fix', 'Write Regression Tests', 'Update Documentation', 'Verify in Staging'],
  security:         ['Security Requirements Analysis', 'Implement Security Controls', 'Penetration Testing', 'Security Documentation', 'Compliance Review'],
  default:          ['Analyze Requirements', 'Implement Feature', 'Write Tests', 'Code Review', 'Documentation'],
};

function resolveTaskTemplate(reqType, parentCategory) {
  const raw = ((reqType || '') + ' ' + (parentCategory || '')).toLowerCase();
  if (raw.includes('backend')) return SPRINT_TASK_TEMPLATES.backend_feature;
  if (raw.includes('frontend') || raw.includes('ui')) return SPRINT_TASK_TEMPLATES.frontend_feature;
  if (raw.includes('integration') || raw.includes('api')) return SPRINT_TASK_TEMPLATES.integration;
  if (raw.includes('bug') || raw.includes('fix')) return SPRINT_TASK_TEMPLATES.bug_fix;
  if (raw.includes('security') || raw.includes('auth')) return SPRINT_TASK_TEMPLATES.security;
  return SPRINT_TASK_TEMPLATES.default;
}

// Parse requirement description into candidate task titles.
// Returns { items: string[], isStructured: boolean }
function parseDescriptionIntoTasks(description) {
  if (!description) return { items: [], isStructured: false };

  // 1. Try <li> elements (WYSIWYG lists)
  const liMatches = [...description.matchAll(/<li[^>]*>([\s\S]*?)<\/li>/gi)];
  if (liMatches.length >= 1) {
    const items = liMatches
      .map(m => m[1].replace(/<[^>]*>/g, '').replace(/&[a-z#\d]+;/gi, ' ').replace(/\s+/g, ' ').trim())
      .filter(t => t.length > 2);
    if (items.length >= 1) return { items, isStructured: true };
  }

  // 2. Strip HTML and try bullet/numbered line parsing
  const plain = description
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|li|h[1-6])>/gi, '\n')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, ' ').replace(/&[a-z#\d]+;/gi, ' ');

  const lines = plain.split('\n')
    .map(l => l.replace(/^[\s\u2022\-\*\•\d\.\)\:]+\s*/, '').trim())
    .filter(l => l.length > 2);
  const unique = [...new Set(lines)];
  if (unique.length >= 2) return { items: unique, isStructured: true };

  // 3. Prose paragraph — return the plain text so we can show it as context
  const prose = plain.replace(/\n+/g, ' ').replace(/\s+/g, ' ').trim();
  return { items: [], isStructured: false, prose };
}

const GenerateSprintTasksModal = ({ requirement, teamMembers = [], onClose, onGenerated }) => {
  const templateTitles = resolveTaskTemplate(requirement?.req_type || requirement?.reqType, requirement?.parent_category || requirement?.parentCategory);
  const rawDesc = requirement?.description || '';
  const descParsed = parseDescriptionIntoTasks(rawDesc);
  const hasDescription = rawDesc.replace(/<[^>]*>/g, '').trim().length > 5;
  const reqDescriptionPlain = stripHtml(rawDesc);

  const makeTasksFromTitles = (titles, withDesc = '') =>
    titles.map(t => ({ title: t, description: withDesc, assignee: '', priority: 'P2' }));

  const [reqTestCases, setReqTestCases] = useState([]);
  useEffect(() => {
    if (requirement?.id) {
      ModulesModel.getRequirementDetail(requirement.id).then(d => {
        if (d?.testCases?.length) {
          setReqTestCases(d.testCases);
          setMode('testcases');
          setTasks(d.testCases.map(tc => ({ title: tc.title || '', description: stripHtml(tc.description || tc.steps || ''), assignee: '', priority: 'P2' })));
        }
      });
    }
  }, [requirement?.id]);

  const hasTestCases = reqTestCases.length > 0;

  const [mode, setMode] = useState(() => hasDescription ? 'description' : 'template');
  const [tasks, setTasks] = useState(() => {
    if (hasDescription && descParsed.isStructured)
      return makeTasksFromTitles(descParsed.items, reqDescriptionPlain);
    // Prose description: use template titles with requirement description as context
    return makeTasksFromTitles(templateTitles, reqDescriptionPlain);
  });
  const [globalAssignee, setGlobalAssignee] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const switchMode = (id) => {
    setMode(id);
    setError('');
    if (id === 'testcases') {
      setTasks(reqTestCases.map(tc => ({ title: tc.title || '', description: stripHtml(tc.description || tc.steps || ''), assignee: '', priority: 'P2' })));
    } else if (id === 'template') {
      setTasks(makeTasksFromTitles(templateTitles));
    } else if (id === 'description') {
      if (descParsed.isStructured)
        setTasks(makeTasksFromTitles(descParsed.items, reqDescriptionPlain));
      else
        setTasks(makeTasksFromTitles(templateTitles, reqDescriptionPlain));
    } else {
      setTasks([{ title: '', description: '', assignee: '', priority: 'P2' }]);
    }
  };

  const setTaskField = (i, field, val) => setTasks(prev => prev.map((t, idx) => idx === i ? { ...t, [field]: val } : t));
  const addTask = () => setTasks(prev => [...prev, { title: '', description: '', assignee: '', priority: 'P2' }]);
  const removeTask = (i) => setTasks(prev => prev.filter((_, idx) => idx !== i));
  const applyGlobalAssignee = () => {
    if (globalAssignee) setTasks(prev => prev.map(t => ({ ...t, assignee: globalAssignee })));
  };

  const handleGenerate = async () => {
    const validTasks = tasks.filter(t => t.title.trim());
    if (!validTasks.length) { setError('Add at least one task title.'); return; }
    setSaving(true); setError('');
    const result = await ModulesModel.generateSprintTasks(requirement.id, validTasks);
    setSaving(false);
    if (result.success) onGenerated(result);
    else setError(result.error || 'Failed to generate tasks.');
  };

  const priorityOpts = ['P1', 'P2', 'P3', 'P4'];

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Generate Sprint Tasks</h2>
            <p className="text-xs text-gray-500 mt-0.5">{requirement?.req_code || requirement?.reqCode} — {requirement?.title}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl font-bold leading-none">×</button>
        </div>

        {/* Mode selector */}
        <div className="px-6 pt-4 flex flex-wrap gap-2 items-center">
          {[
            ['testcases', '🧪 From Test Cases'],
            ['description', '📄 From Description'],
            ['template', '📋 Use Template'],
            ['manual', '✏️ Manual Entry'],
          ].map(([id, label]) => (
            (id !== 'description' || hasDescription) &&
            (id !== 'testcases' || hasTestCases) && (
              <button key={id} onClick={() => switchMode(id)}
                className={`px-4 py-2 text-sm rounded-lg font-medium border transition-colors ${mode === id ? 'bg-teal-600 text-white border-teal-600' : 'bg-white text-gray-600 border-gray-300 hover:border-teal-400'}`}>
                {label}
              </button>
            )
          ))}
          <div className="ml-auto flex items-center gap-2">
            <select value={globalAssignee} onChange={e => setGlobalAssignee(e.target.value)}
              className="text-xs border border-gray-300 rounded px-2 py-1.5 focus:ring-1 focus:ring-teal-400">
              <option value="">Assign all to…</option>
              {teamMembers.map(m => <option key={m.id || m.user_name} value={m.user_name}>{m.user_name}</option>)}
            </select>
            <button onClick={applyGlobalAssignee} className="text-xs px-2 py-1.5 border border-gray-300 rounded hover:bg-gray-50">Apply</button>
          </div>
        </div>

        {/* Mode hint */}
        <div className="px-6 pt-2 pb-1">
          {mode === 'description' && descParsed.isStructured && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-xs text-blue-700">
              {descParsed.items.length} item(s) extracted from the description. Each task's description is pre-filled with the requirement context. Edit before generating.
            </div>
          )}
          {mode === 'description' && !descParsed.isStructured && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-700">
              Description is a prose paragraph — task titles are from the <strong>{requirement?.req_type || 'default'}</strong> template. The requirement description is pre-filled in each task so developers have the full context. Edit titles as needed.
            </div>
          )}
          {mode === 'testcases' && (
            <div className="bg-teal-50 border border-teal-200 rounded-lg px-3 py-2 text-xs text-teal-700">
              {reqTestCases.length} test case(s) linked to this requirement. Each sprint task uses the test case title and description. Edit before generating.
            </div>
          )}
          {mode === 'template' && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-xs text-gray-600">
              Template based on requirement type: <span className="font-semibold">{requirement?.req_type || requirement?.reqType || 'default'}</span>. Edit titles before generating.
            </div>
          )}
          {mode === 'manual' && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-xs text-gray-600">
              Enter task titles manually. Click <strong>+ Add Task</strong> to add more rows.
            </div>
          )}
        </div>

        {/* Task list */}
        <div className="flex-1 overflow-y-auto px-6 py-3 space-y-2">
          {tasks.map((t, i) => (
            <div key={i} className="bg-gray-50 border border-gray-200 rounded-xl p-3">
              <div className="flex gap-2 mb-2">
                <input value={t.title} onChange={e => setTaskField(i, 'title', e.target.value)}
                  placeholder="Task title *"
                  className="flex-1 border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-teal-400" />
                <select value={t.priority} onChange={e => setTaskField(i, 'priority', e.target.value)}
                  className="border border-gray-300 rounded-lg px-2 py-1.5 text-xs focus:ring-1 focus:ring-teal-400">
                  {priorityOpts.map(v => <option key={v} value={v}>{v}</option>)}
                </select>
                <select value={t.assignee} onChange={e => setTaskField(i, 'assignee', e.target.value)}
                  className="border border-gray-300 rounded-lg px-2 py-1.5 text-xs focus:ring-1 focus:ring-teal-400 min-w-[120px]">
                  <option value="">Unassigned</option>
                  {teamMembers.map(m => <option key={m.id || m.user_name} value={m.user_name}>{m.user_name}</option>)}
                </select>
                <button onClick={() => removeTask(i)} className="text-red-400 hover:text-red-600 text-lg leading-none px-1">×</button>
              </div>
              <input value={t.description} onChange={e => setTaskField(i, 'description', e.target.value)}
                placeholder="Description (optional)"
                className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-xs text-gray-600 focus:ring-1 focus:ring-teal-400" />
            </div>
          ))}
          <button onClick={addTask} className="w-full py-2 border border-dashed border-gray-300 rounded-xl text-sm text-gray-400 hover:border-teal-400 hover:text-teal-600 transition-colors">+ Add Task</button>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t flex items-center justify-between">
          {error ? <p className="text-sm text-red-600">{error}</p> : <p className="text-sm text-gray-400">{tasks.filter(t => t.title.trim()).length} task(s) will be created in PlanTrack</p>}
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50">Cancel</button>
            <button onClick={handleGenerate} disabled={saving}
              className="px-5 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 disabled:opacity-50">
              {saving ? 'Generating…' : '⚡ Generate Tasks'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ---- QMS Requirement Detail View ----
const QmsRequirementDetailView = ({ requirementId, onBack, onEditBug, onReportBug, onNavigateToPlanTrack, teamMembers = [] }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showGenerate, setShowGenerate] = useState(false);

  const loadDetail = () => {
    if (!requirementId) return;
    setLoading(true);
    ModulesModel.getRequirementDetail(requirementId).then(d => { setDetail(d); setLoading(false); });
  };

  useEffect(() => { loadDetail(); }, [requirementId]);

  if (loading) return <div className="py-12 text-center text-gray-400 text-sm">Loading…</div>;
  if (!detail) return <div className="py-12 text-center text-gray-400 text-sm">Requirement not found.</div>;

  const { requirement: req, tasks = [], testCases = [], bugs = [], coverage, totalTcs, passedTcs, taskProgress = 0, closedTasks = 0, taskStatus = 'open' } = detail;
  const ps = getPriorityStyle(req.priority);

  const reviewStatusStyle = (s) => ({ ready_for_qa: 'bg-teal-100 text-teal-700', approved: 'bg-green-100 text-green-700', rejected: 'bg-red-100 text-red-700', none: 'bg-gray-100 text-gray-500' }[s] || 'bg-gray-100 text-gray-500');
  const reviewStatusLabel = (s) => ({ ready_for_qa: 'Ready for QA', approved: 'Approved', rejected: 'Rejected', none: 'None' }[s] || s || 'None');
  const execStatusStyle = (s) => ({ pass: 'bg-green-100 text-green-700', fail: 'bg-red-100 text-red-700', blocked: 'bg-orange-100 text-orange-700' }[s] || 'bg-gray-100 text-gray-500');
  const taskStatusStyle = { open: 'bg-gray-100 text-gray-600', in_progress: 'bg-blue-100 text-blue-700', completed: 'bg-green-100 text-green-700' };
  const taskStatusLabel = { open: 'Open', in_progress: 'In Progress', completed: 'Completed' };

  return (
    <div>
      {showGenerate && (
        <GenerateSprintTasksModal
          requirement={req}
          teamMembers={teamMembers}
          onClose={() => setShowGenerate(false)}
          onGenerated={() => { setShowGenerate(false); loadDetail(); }}
        />
      )}

      <button onClick={onBack} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800 mb-4">← Back to Requirements</button>

      {/* Requirement header */}
      <div className="bg-white border border-gray-200 rounded-xl p-5 mb-5">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs font-mono text-gray-400">{req.req_code}</span>
              <span className="px-2 py-0.5 rounded text-xs font-semibold capitalize" style={{ background: ps.bg, color: ps.text || ps.color }}>{req.priority}</span>
              {req.req_type && <span className="px-2 py-0.5 rounded text-xs bg-purple-50 text-purple-700 border border-purple-200">{req.req_type}</span>}
            </div>
            <h2 className="text-xl font-bold text-gray-900">{req.title}</h2>
          </div>
          <QmsCoverageBadge coverage={coverage} totalTcs={totalTcs} />
        </div>
        {req.description && <p className="text-sm text-gray-600 mb-3">{stripHtml(req.description)}</p>}
        <div className="grid grid-cols-4 gap-3">
          {[['Sprint Tasks', tasks.length, 'text-blue-600'], ['Test Cases', totalTcs, 'text-teal-600'], ['Open Bugs', bugs.length, 'text-red-600'], ['Progress', `${taskProgress}%`, 'text-indigo-600']].map(([label, val, cls]) => (
            <div key={label} className="bg-gray-50 rounded-lg p-3 text-center">
              <p className={`text-2xl font-bold ${cls}`}>{val}</p>
              <p className="text-xs text-gray-500 mt-0.5">{label}</p>
            </div>
          ))}
        </div>
        {/* Task progress bar */}
        {tasks.length > 0 && (
          <div className="mt-3">
            <div className="flex justify-between text-xs text-gray-500 mb-1">
              <span className="flex items-center gap-1.5">
                Development Progress
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${taskStatusStyle[taskStatus]}`}>{taskStatusLabel[taskStatus]}</span>
              </span>
              <span>{closedTasks}/{tasks.length} closed</span>
            </div>
            <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden"><div className="h-full bg-indigo-500 rounded-full transition-all" style={{ width: `${taskProgress}%` }} /></div>
          </div>
        )}
        {totalTcs > 0 && (
          <div className="mt-2">
            <div className="flex justify-between text-xs text-gray-500 mb-1"><span>QA Coverage</span><span>{passedTcs}/{totalTcs} passed</span></div>
            <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden"><div className="h-full bg-teal-500 rounded-full transition-all" style={{ width: `${coverage}%` }} /></div>
          </div>
        )}
      </div>

      {/* Sprint Tasks section */}
      <div className="bg-white border border-gray-200 rounded-xl p-5 mb-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-800 flex items-center gap-2">
            🔧 Sprint Tasks
            <span className="text-xs bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded-full">{tasks.length}</span>
          </h3>
          <div className="flex gap-2">
            {onNavigateToPlanTrack && (
              <button onClick={onNavigateToPlanTrack} className="px-3 py-1.5 text-xs border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50 font-medium">
                View Sprint →
              </button>
            )}
            <button onClick={() => setShowGenerate(true)} className="px-3 py-1.5 text-xs bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium">
              ⚡ Generate Sprint Tasks
            </button>
          </div>
        </div>

        {tasks.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-sm text-gray-400 mb-3">No sprint tasks linked to this requirement.</p>
            <button onClick={() => setShowGenerate(true)} className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700">
              ⚡ Generate Sprint Tasks
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left text-xs font-semibold text-gray-500 pb-2 pr-3">Task ID</th>
                  <th className="text-left text-xs font-semibold text-gray-500 pb-2 pr-3">Title</th>
                  <th className="text-left text-xs font-semibold text-gray-500 pb-2 pr-3">Assignee</th>
                  <th className="text-left text-xs font-semibold text-gray-500 pb-2 pr-3">Status</th>
                  <th className="text-left text-xs font-semibold text-gray-500 pb-2">Review Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {tasks.map(t => (
                  <tr key={t.taskId} className="hover:bg-gray-50 transition-colors">
                    <td className="py-2 pr-3">
                      <span className="text-xs font-mono text-gray-400">{t.taskId}</span>
                    </td>
                    <td className="py-2 pr-3">
                      <span className="font-medium text-gray-800 text-sm">{t.title}</span>
                    </td>
                    <td className="py-2 pr-3">
                      {t.assignee ? <span className="text-xs text-gray-600">👤 {t.assignee}</span> : <span className="text-xs text-gray-300">—</span>}
                    </td>
                    <td className="py-2 pr-3">
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 capitalize">{t.status}</span>
                    </td>
                    <td className="py-2">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${reviewStatusStyle(t.reviewStatus || 'none')}`}>
                        {reviewStatusLabel(t.reviewStatus || 'none')}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Test Cases */}
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <h3 className="font-semibold text-gray-800 text-sm mb-3 flex items-center gap-2">🧪 Test Cases <span className="text-xs bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded-full">{testCases.length}</span></h3>
          {testCases.length === 0 ? <p className="text-xs text-gray-400 py-3">No test cases linked.</p> : (
            <div className="space-y-2">
              {testCases.map(tc => (
                <div key={tc.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg text-xs">
                  <p className="font-medium text-gray-800 truncate">{tc.tcCode}: {tc.title}</p>
                  <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${tc.execution ? execStatusStyle(tc.execution.status) : 'bg-gray-100 text-gray-400'}`}>
                    {tc.execution ? tc.execution.status : 'pending'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Bugs */}
        {bugs.length > 0 ? (
          <div className="bg-white border border-gray-200 rounded-xl p-4">
            <h3 className="font-semibold text-gray-800 text-sm mb-3 flex items-center gap-2">🐛 Linked Bugs <span className="text-xs bg-red-100 text-red-600 px-1.5 py-0.5 rounded-full">{bugs.length}</span></h3>
            <div className="space-y-2">
              {bugs.map(b => {
                const bs = qmsSeverityStyle(b.severity);
                return (
                  <div key={b.id} className="flex items-center justify-between p-3 bg-red-50 border border-red-100 rounded-lg" style={{ borderLeftWidth: '3px', borderLeftColor: bs.color }}>
                    <div>
                      <span className="text-xs font-mono text-gray-400 mr-2">{b.bugCode}</span>
                      <span className="text-sm font-medium text-gray-900">{b.title}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-0.5 rounded text-xs font-semibold" style={{ background: bs.bg, color: bs.color }}>{b.severity}</span>
                      <button onClick={() => onEditBug(b)} className="text-xs text-blue-600 hover:underline">Edit</button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="bg-white border border-gray-200 rounded-xl p-4 flex items-center justify-center">
            <p className="text-xs text-gray-300 py-3">No open bugs linked.</p>
          </div>
        )}
      </div>
    </div>
  );
};

// ---- QMS RTM View ----
const QmsRtmView = ({ projectId }) => {
  const [rtm, setRtm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCoverage, setFilterCoverage] = useState('all');

  useEffect(() => {
    if (!projectId) return;
    setLoading(true);
    ModulesModel.getRtmData(projectId).then(d => { setRtm(d); setLoading(false); });
  }, [projectId]);

  if (loading) return <div className="py-12 text-center text-gray-400 text-sm">Building traceability matrix…</div>;
  if (!rtm?.requirements?.length) return <div className="py-12 text-center text-gray-400 text-sm">No requirements found for traceability matrix.</div>;

  const { requirements, summary } = rtm;

  const filtered = requirements.filter(r => {
    if (search && !r.title.toLowerCase().includes(search.toLowerCase()) && !(r.reqCode || '').toLowerCase().includes(search.toLowerCase())) return false;
    if (filterCoverage === 'full' && r.coverage < 90) return false;
    if (filterCoverage === 'partial' && (r.coverage < 60 || r.coverage >= 90)) return false;
    if (filterCoverage === 'risky' && (r.totalTcs === 0 || r.coverage >= 60)) return false;
    if (filterCoverage === 'untested' && r.totalTcs > 0) return false;
    return true;
  });

  const coverageBar = (pct) => (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden min-w-[60px]">
        <div className={`h-full rounded-full ${pct >= 90 ? 'bg-green-500' : pct >= 60 ? 'bg-yellow-400' : 'bg-red-400'}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs font-medium text-gray-600 w-8">{pct}%</span>
    </div>
  );

  return (
    <div>
      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        {[
          ['Total Reqs', summary.total, 'text-gray-900'],
          ['Fully Tested', summary.fullyTested, 'text-green-600'],
          ['Partial Coverage', summary.partial, 'text-yellow-600'],
          ['Risky Release', summary.risky, 'text-red-600'],
        ].map(([label, val, cls]) => (
          <div key={label} className="bg-white border border-gray-200 rounded-xl p-3 text-center">
            <p className={`text-2xl font-bold ${cls}`}>{val}</p>
            <p className="text-xs text-gray-500 mt-0.5">{label}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search requirements…"
          className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400" />
        <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
          {[['all','All'],['full','Fully Tested'],['partial','Partial'],['risky','Risky'],['untested','Untested']].map(([val, label]) => (
            <button key={val} onClick={() => setFilterCoverage(val)}
              className={`px-2 py-1 text-xs rounded font-medium transition-colors ${filterCoverage === val ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>{label}</button>
          ))}
        </div>
      </div>

      {/* RTM Table */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase min-w-[220px]">Requirement</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Tasks</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Test Cases</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Open Bugs</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase min-w-[160px]">QA Coverage</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map(r => {
                const ps = getPriorityStyle(r.priority);
                return (
                  <tr key={r.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-start gap-2">
                        <span className="text-xs font-mono text-gray-400 shrink-0 mt-0.5">{r.reqCode}</span>
                        <div>
                          <p className="font-medium text-gray-900 leading-snug">{r.title}</p>
                          <span className="text-xs capitalize" style={{ color: ps.text || ps.color }}>{r.priority}</span>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`text-sm font-semibold ${r.tasks.length > 0 ? 'text-blue-600' : 'text-gray-300'}`}>{r.tasks.length}</span>
                      {r.tasks.some(t => t.reviewStatus === 'ready_for_qa') && (
                        <div className="text-xs text-teal-600 font-medium">Ready for QA</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="text-sm font-semibold text-gray-700">{r.totalTcs}</div>
                      {r.totalTcs > 0 && <div className="text-xs text-green-600">{r.passedTcs} passed</div>}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`text-sm font-semibold ${r.openBugs.length > 0 ? 'text-red-600' : 'text-gray-300'}`}>{r.openBugs.length}</span>
                      {r.openBugs.some(b => b.severity === 'critical') && (
                        <div className="text-xs text-red-600 font-medium">Critical</div>
                      )}
                    </td>
                    <td className="px-4 py-3">{coverageBar(r.coverage)}</td>
                    <td className="px-4 py-3"><QmsCoverageBadge coverage={r.coverage} totalTcs={r.totalTcs} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {filtered.length === 0 && <p className="text-center py-8 text-sm text-gray-400">No requirements match the current filter.</p>}
        </div>
      </div>
    </div>
  );
};

const QmsModule = ({ data, stage, onStageChange, onApproveReq, onRejectReq, onAddReq, onEditReq, onDeleteReq, onRefreshReq, onUpdateProject, onAddTestCase, onEditTestCase, onDeleteTestCase, onPassTC, onFailTC, onResetTC, onReportBug, onEditBug, onProjectChange, onSendEod, onEodSettings, userRole, user,
  qmsView, onQmsViewChange, allBugsData, allBugsFilters, viewingBugDetail, onAllBugsFilterChange, onViewBugDetail, onBugDetailUpdate, onBugDetailComment, onDeleteBugComment, onBackFromBugDetail, onReportBugFromList,
  testPlans, onCreateTestPlan, onUpdateTestPlan, onDeleteTestPlan, onRecordExecution, uatSignoffs, onSubmitUatSignoff, prodVerifications, onToggleProdVerification, onCompleteProdVerification,
  viewingRequirementDetail, onViewRequirementDetail, onBackFromReqDetail, onQaStatusChange, onTestResultChange,
  onNavigateToPlanTrack, teamMembers, onPromoteArtifacts
}) => {
  const effectiveRole = userRole || user?.role || user?.user_role;
  const canManageEod = ['admin', 'manager', 'dataops_manager', 'devops_manager'].includes(effectiveRole);
  const canEodSettings = ['admin', 'devops_manager'].includes(effectiveRole);
  const canEditBugs = ['admin', 'qa_lead', 'qa', 'devops'].includes(effectiveRole);
  const canPromote = ['admin', 'qa_lead', 'manager'].includes(effectiveRole) || effectiveRole?.endsWith('_manager');
  const projectId = data?.project?.projectId;
  const renderStage = () => {
    switch (stage) {
      case 'requirements': return <QmsRequirementsStage requirements={data?.requirements} projectId={projectId} onApprove={onApproveReq} onReject={onRejectReq} onAdd={onAddReq} onNext={() => onStageChange('testplan')} onEdit={onEditReq} onDelete={onDeleteReq} onRefresh={onRefreshReq} onPromote={canPromote ? onPromoteArtifacts : undefined} />;
      case 'testplan': return <QmsTestPlanStage project={data?.project} onUpdate={onUpdateProject} onNext={() => onStageChange('testcases')} onBack={() => onStageChange('requirements')} onCreateTestPlan={onCreateTestPlan} onUpdateTestPlan={onUpdateTestPlan} onDeleteTestPlan={onDeleteTestPlan} testPlans={testPlans || []} onPromote={canPromote ? onPromoteArtifacts : undefined} user={user} />;
      case 'testcases': return <QmsTestCasesStage testCases={data?.testCases} requirements={data?.requirements || []} onAdd={onAddTestCase} onEdit={onEditTestCase} onDelete={onDeleteTestCase} onNext={() => onStageChange('testing')} onBack={() => onStageChange('testplan')} onPromote={canPromote ? onPromoteArtifacts : undefined} projectId={projectId} />;
      case 'testing': return <QmsTestingStage testCases={data?.testCases} bugs={data?.bugs} metrics={data?.metrics} project={data?.project} onPass={onPassTC} onFail={onFailTC} onReset={onResetTC} onReportBug={onReportBug} onEditBug={onEditBug} onNext={() => onStageChange('uat')} onBack={() => onStageChange('testcases')} onRecordExecution={onRecordExecution} testPlans={testPlans || []} user={user} />;
      case 'uat': return <QmsUatStage metrics={data?.metrics} bugs={data?.bugs} project={data?.project} onUpdateProject={onUpdateProject} onEditBug={onEditBug} onNext={() => onStageChange('verification')} onBack={() => onStageChange('testing')} onSubmitUatSignoff={onSubmitUatSignoff} uatSignoffs={uatSignoffs || []} testPlans={testPlans || []} user={user} />;
      case 'verification': return <QmsVerificationStage project={data?.project} onUpdateProject={onUpdateProject} onBack={() => onStageChange('uat')} onToggleProdVerification={onToggleProdVerification} onCompleteProdVerification={onCompleteProdVerification} prodVerifications={prodVerifications || []} user={user} />;
      default: return null;
    }
  };
  // If viewing a specific bug detail page
  if (viewingBugDetail) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-6">
        <BugPageView
          bug={viewingBugDetail}
          user={user}
          onBack={onBackFromBugDetail}
          onUpdate={onBugDetailUpdate}
          onAddComment={onBugDetailComment}
          onDeleteComment={onDeleteBugComment}
        />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="mb-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">QA Management System</h1>
          <p className="text-gray-600 mt-1">Complete QA workflow management — requirements to production</p>
        </div>
        <div className="flex gap-2">
          {canEodSettings && <button onClick={onEodSettings} className="px-3 py-2 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200 font-medium border border-gray-300">⚙️ EOD Settings</button>}
          {canManageEod && <button onClick={onSendEod} className="px-3 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 font-medium">📤 QA EOD</button>}
        </div>
      </div>

      {/* Tab bar */}
      <div className="flex gap-1 mb-5 bg-gray-100 rounded-xl p-1 flex-wrap">
        {[
          { id: 'pipeline', label: '⚙️ Pipeline', count: null },
          { id: 'requirements', label: '📋 Requirements', count: data?.requirements?.length },
          { id: 'rtm', label: '🔗 Traceability', count: null },
          { id: 'bugs', label: '🐛 All Bugs', count: allBugsData?.metrics?.total },
        ].map(tab => (
          <button key={tab.id} onClick={() => onQmsViewChange(tab.id)}
            className={`px-4 py-2 text-sm rounded-lg font-medium flex items-center gap-2 transition-colors ${qmsView === tab.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
            {tab.label}
            {tab.count !== null && tab.count !== undefined && (
              <span className={`text-xs px-1.5 py-0.5 rounded-full ${qmsView === tab.id ? 'bg-teal-100 text-teal-700' : 'bg-gray-200 text-gray-500'}`}>{tab.count}</span>
            )}
          </button>
        ))}
      </div>

      {qmsView === 'bugs' ? (
        <AllBugsView
          bugsData={allBugsData}
          filters={allBugsFilters}
          onFilterChange={onAllBugsFilterChange}
          onViewBug={onViewBugDetail}
          onEditBug={onEditBug}
          canEdit={canEditBugs}
          onReportBug={onReportBugFromList}
        />
      ) : qmsView === 'requirements' ? (
        viewingRequirementDetail ? (
          <QmsRequirementDetailView
            requirementId={viewingRequirementDetail}
            onBack={onBackFromReqDetail}
            onEditBug={onEditBug}
            onReportBug={onReportBug}
            onNavigateToPlanTrack={onNavigateToPlanTrack}
            teamMembers={teamMembers || []}
          />
        ) : (
          <QmsRequirementsView
            projectId={data?.project?.projectId}
            requirements={data?.requirements || []}
            onAdd={onAddReq}
            onEdit={onEditReq}
            onViewDetail={(r) => onViewRequirementDetail(r.id)}
            onQaStatusChange={onQaStatusChange}
            onTestResultChange={onTestResultChange}
          />
        )
      ) : qmsView === 'rtm' ? (
        <QmsRtmView projectId={data?.project?.projectId} />
      ) : (
        <>
          <QmsWorkflowSteps currentStage={stage} onStageChange={onStageChange} />
          <QmsMetrics metrics={data?.metrics} stage={stage} />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">{renderStage()}</div>
            <div><QmsSidebar data={data} projects={data?.projects} currentProjectId={data?.project?.projectId} onProjectChange={onProjectChange} onReportBug={onReportBug} onEditBug={onEditBug} onViewBug={onViewBugDetail} /></div>
          </div>
          {data?.project?.projectId && (
            <div className="mt-8">
              <div className="flex items-center gap-2 mb-4">
                <AlertCircle size={18} className="text-orange-500" />
                <h2 className="text-lg font-semibold text-gray-900">Risk Register</h2>
                <span className="text-sm text-gray-400">— {data.project.projectName || data.project.projectId}</span>
              </div>
              <ProjectRisksTab projectId={data.project.projectId} user={{ role: userRole }} />
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ============================================================================
// QUICKTRACK VIEWS
// ============================================================================

const getQtPriorityStyle = (p) => {
  const styles = { urgent: { bg: '#fef2f2', color: '#dc2626' }, high: { bg: '#fff7ed', color: '#ea580c' }, medium: { bg: '#fffbeb', color: '#d97706' }, low: { bg: '#f0fdf4', color: '#16a34a' } };
  return styles[p] || styles.medium;
};
const getQtStatusStyle = (s) => {
  const styles = { open: { bg: '#eff6ff', color: '#2563eb' }, in_progress: { bg: '#fefce8', color: '#ca8a04' }, done: { bg: '#f0fdf4', color: '#16a34a' } };
  return styles[s] || styles.open;
};
const getQtCategoryStyle = (c) => {
  const styles = { bug: { bg: '#fef2f2', color: '#dc2626' }, feature: { bg: '#f0f9ff', color: '#0284c7' }, chore: { bg: '#f5f3ff', color: '#7c3aed' }, general: { bg: '#f9fafb', color: '#6b7280' } };
  return styles[c] || styles.general;
};
const QuickTrackMetrics = ({ metrics }) => {
  const cards = [
    { label: 'Total Tasks', value: metrics?.total || 0, icon: '📋', color: '#667eea' },
    { label: 'Open', value: metrics?.open || 0, icon: '🔵', color: '#2563eb' },
    { label: 'In Progress', value: metrics?.inProgress || 0, icon: '🟡', color: '#ca8a04' },
    { label: 'Done', value: metrics?.done || 0, icon: '🟢', color: '#16a34a' }
  ];
  return (
    <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-6">
      {cards.map(c => (
        <div key={c.label} className="bg-white rounded-lg shadow border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-600">{c.label}</span>
            <span className="text-2xl">{c.icon}</span>
          </div>
          <div className="text-3xl font-bold" style={{ color: c.color }}>{c.value}</div>
        </div>
      ))}
    </div>
  );
};

const QuickTrackFilters = ({ filterStatus, filterPriority, filterCategory, filterDateFrom, filterDateTo, onFilterStatus, onFilterPriority, onFilterCategory, onFilterDateFrom, onFilterDateTo, onReset, onAddTask, onBackfillTask }) => {
  // YYYY-MM-DD in Pacific — used for date-input shortcuts and the "To" max attribute.
  const pacificToday = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/Los_Angeles' });
  const pacificDateShifted = (days) => {
    const d = new Date();
    d.setUTCDate(d.getUTCDate() + days);
    return d.toLocaleDateString('en-CA', { timeZone: 'America/Los_Angeles' });
  };
  const setRange = (from, to) => { onFilterDateFrom(from); onFilterDateTo(to); };
  const dateInvalid = filterDateFrom && filterDateTo && filterDateFrom > filterDateTo;
  return (
    <div className="bg-white rounded-lg shadow border border-gray-200 p-5 mb-6">
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1">
          <label className="text-xs font-semibold text-gray-500 uppercase">Status</label>
          <select value={filterStatus} onChange={e => onFilterStatus(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm min-w-[140px] focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400">
            <option value="">All Statuses</option>
            <option value="open">Open</option>
            <option value="in_progress">In Progress</option>
            <option value="done">Done</option>
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs font-semibold text-gray-500 uppercase">Priority</label>
          <select value={filterPriority} onChange={e => onFilterPriority(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm min-w-[140px] focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400">
            <option value="">All Priorities</option>
            <option value="urgent">Urgent</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs font-semibold text-gray-500 uppercase">Category</label>
          <select value={filterCategory} onChange={e => onFilterCategory(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm min-w-[140px] focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400">
            <option value="">All Categories</option>
            <option value="bug">Bug</option>
            <option value="feature">Feature</option>
            <option value="chore">Chore</option>
            <option value="general">General</option>
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs font-semibold text-gray-500 uppercase">From <span className="font-normal normal-case text-gray-400">(PT)</span></label>
          <input
            type="date"
            value={filterDateFrom}
            max={filterDateTo || pacificToday()}
            onChange={e => onFilterDateFrom(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs font-semibold text-gray-500 uppercase">To <span className="font-normal normal-case text-gray-400">(PT)</span></label>
          <input
            type="date"
            value={filterDateTo}
            min={filterDateFrom || undefined}
            max={pacificToday()}
            onChange={e => onFilterDateTo(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400"
          />
        </div>
        <button onClick={onReset} className="px-4 py-2 bg-gray-100 border border-gray-300 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-200">Reset</button>
        <div className="flex-1" />
        {onBackfillTask && (
          <button onClick={onBackfillTask} title="Log work completed on an earlier date — e.g. while Orbit was down"
            className="px-4 py-2 bg-white border border-amber-300 text-amber-700 rounded-lg text-sm font-medium hover:bg-amber-50 flex items-center gap-2">
            <span className="text-base leading-none">🕓</span> Backfill Past Task
          </button>
        )}
        <button onClick={onAddTask} className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 flex items-center gap-2">
          <span className="text-lg leading-none">+</span> New Task
        </button>
      </div>
      <div className="flex flex-wrap items-center gap-2 mt-3">
        <span className="text-xs text-gray-500 uppercase font-semibold">Quick range:</span>
        <button type="button" onClick={() => setRange(pacificToday(), pacificToday())} className="px-2.5 py-1 text-xs bg-white border border-gray-300 rounded hover:bg-gray-50">Today</button>
        <button type="button" onClick={() => setRange(pacificDateShifted(-1), pacificDateShifted(-1))} className="px-2.5 py-1 text-xs bg-white border border-gray-300 rounded hover:bg-gray-50">Yesterday</button>
        <button type="button" onClick={() => setRange(pacificDateShifted(-6), pacificToday())} className="px-2.5 py-1 text-xs bg-white border border-gray-300 rounded hover:bg-gray-50">Last 7 days</button>
        <button type="button" onClick={() => setRange(pacificDateShifted(-29), pacificToday())} className="px-2.5 py-1 text-xs bg-white border border-gray-300 rounded hover:bg-gray-50">Last 30 days</button>
        {dateInvalid && <span className="text-xs text-rose-600 font-medium">⚠ From must be on or before To</span>}
      </div>
    </div>
  );
};

const QuickTrackTable = ({ tasks, onStatusChange, onDelete, onEdit, user, teamMembers = [] }) => {
  const role = user?.user_role || user?.role || '';
  const isAdmin = role === 'admin';
  const isManager = !isAdmin && (role.endsWith('_manager') || role === 'manager');
  const managerModule = (isManager && role !== 'manager') ? role.replace('_manager', '') : null;
  const myTeamNames = isManager
    ? new Set((managerModule ? teamMembers.filter(m => m.team_module?.toLowerCase() === managerModule?.toLowerCase()) : teamMembers).map(m => m.user_name))
    : null;
  const canDelete = (t) => isAdmin || (isManager && myTeamNames?.has(t.assignedTo)) || (user?.user_name || user?.name) === t.assignedTo;
  const [pendingDone, setPendingDone] = useState(null); // { id, title }

  const handleStatusChange = (task, newStatus) => {
    if (newStatus === 'done') { setPendingDone({ id: task.id, title: task.title }); return; }
    onStatusChange(task.id, { status: newStatus });
  };

  return (
    <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
      {pendingDone && (
        <ActualTimeModal
          taskTitle={pendingDone.title}
          onConfirm={mins => { onStatusChange(pendingDone.id, { status: 'done', actualMinutes: mins }); setPendingDone(null); }}
          onCancel={() => setPendingDone(null)}
        />
      )}
      <div className="flex justify-between items-center px-5 py-4 border-b border-gray-200">
        <h2 className="text-sm font-semibold text-gray-700">Tasks</h2>
        <span className="text-xs text-gray-500 bg-gray-100 px-3 py-1 rounded-full">{(tasks || []).length} items</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Title</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Project</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Priority</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Category</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Assigned To</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Time (min)</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase w-16"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {(tasks || []).map(t => {
              const ps = getQtPriorityStyle(t.priority);
              const cs = getQtCategoryStyle(t.category);
              return (
                <tr key={t.id} className={`hover:bg-gray-50 ${t.status === 'done' ? 'opacity-60' : ''}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      <div className={`text-sm font-semibold text-gray-900 ${t.status === 'done' ? 'line-through' : ''}`}>{t.title}</div>
                      {(t.isBackfilled === 1 || t.isBackfilled === true) && (
                        <span className="shrink-0 px-1.5 py-0.5 text-[10px] font-semibold rounded bg-amber-100 text-amber-700" title={`Entered retroactively${t.backfilledBy ? ` by ${t.backfilledBy}` : ''}`}>
                          Backfilled
                        </span>
                      )}
                    </div>
                    {t.description && <div className="text-xs text-gray-500 mt-0.5 truncate max-w-xs">{t.description}</div>}
                    {(t.isBackfilled === 1 || t.isBackfilled === true) && t.completedAt && (
                      <div className="text-[10px] text-amber-600 mt-0.5">
                        Completed {new Date((t.completedAt.replace ? t.completedAt.replace(' ', 'T') : t.completedAt) + 'Z').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {t.projectName ? <span className="text-xs font-medium text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded">{t.projectName}</span> : <span className="text-xs text-gray-400">-</span>}
                  </td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-0.5 rounded text-xs font-semibold capitalize" style={{ background: ps.bg, color: ps.color }}>{t.priority}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-0.5 rounded text-xs font-medium capitalize" style={{ background: cs.bg, color: cs.color }}>{t.category}</span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700">{t.assignedTo || '-'}</td>
                  <td className="px-4 py-3">
                    {(t.estimatedMinutes > 0 || t.actualMinutes > 0) ? (
                      <div className="text-xs space-y-0.5">
                        <div className="text-gray-500">Est: <span className="font-medium text-gray-700">{t.estimatedMinutes || 0}m</span></div>
                        <div className="text-gray-500">Act: <span className={`font-medium ${t.actualMinutes > t.estimatedMinutes && t.estimatedMinutes > 0 ? 'text-red-600' : 'text-gray-700'}`}>{t.actualMinutes || 0}m</span></div>
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <select value={t.status} onChange={e => handleStatusChange(t, e.target.value)}
                      className="border border-gray-300 rounded px-2 py-1 text-xs font-medium focus:ring-2 focus:ring-indigo-400"
                      style={{ background: getQtStatusStyle(t.status).bg, color: getQtStatusStyle(t.status).color }}>
                      <option value="open">Open</option>
                      <option value="in_progress">In Progress</option>
                      <option value="done">Done</option>
                    </select>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <button onClick={() => onEdit(t)} className="text-xs text-indigo-500 hover:text-indigo-700 font-medium" title="Edit task">Edit</button>
                      {canDelete(t) && (
                        <button onClick={() => requestConfirm(`Delete "${t.title}"?`, () => onDelete(t.id))} className="text-gray-400 hover:text-red-500 text-sm" title="Delete task">&times;</button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {(!tasks || tasks.length === 0) && (
          <div className="p-12 text-center text-gray-400">No tasks match the selected filters.</div>
        )}
      </div>
    </div>
  );
};

const ActualTimeModal = ({ taskTitle, onConfirm, onCancel }) => {
  const [minutes, setMinutes] = useState('');
  const [error, setError] = useState('');
  const handleConfirm = () => {
    const val = parseInt(minutes);
    if (!minutes || isNaN(val) || val <= 0) { setError('Actual time is required and must be greater than 0.'); return; }
    onConfirm(val);
  };
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-base font-bold text-gray-900">Log Actual Time</h2>
          <p className="text-sm text-gray-500 mt-1 truncate">"{taskTitle}"</p>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Actual Time (min) <span className="text-red-500">*</span></label>
            <input
              type="number" min="1" value={minutes} onChange={e => { setMinutes(e.target.value); setError(''); }}
              placeholder="e.g. 60" autoFocus
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400"
            />
            {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
          </div>
          <p className="text-xs text-gray-400">Required before marking this task as Done.</p>
        </div>
        <div className="flex justify-end gap-2 px-6 py-4 border-t border-gray-100">
          <button onClick={onCancel} className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
          <button onClick={handleConfirm} className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700">Mark as Done</button>
        </div>
      </div>
    </div>
  );
};

const AddQuickTaskModal = ({ onClose, onSubmit, projects, teamMembers = [], user, backfillMode = false }) => {
  const role = user?.user_role || user?.role || '';
  const isAdmin = role === 'admin';
  const isManager = !isAdmin && (role.endsWith('_manager') || role === 'manager');
  const managerModule = isManager
    ? (role !== 'manager'
      ? role.replace('_manager', '')
      : teamMembers.find(m => m.user_id === user?.user_id || m.user_name === (user?.user_name || user?.name))?.team_module || null)
    : null;
  const assignableMembers = isAdmin
    ? teamMembers
    : isManager
    ? (managerModule ? teamMembers.filter(m => m.team_module?.toLowerCase() === managerModule?.toLowerCase()) : teamMembers)
    : [];
  const selfAssign = !isAdmin && !isManager;
  const defaultAssignee = selfAssign ? (user?.user_name || user?.name || '') : '';

  // Backfill entries only make sense as completed work — there's no such thing as an
  // "open" or "in progress" task from a date that's already past.
  const [form, setForm] = useState({ title: '', description: '', priority: 'medium', category: 'general', assignedTo: defaultAssignee, projectId: '', estimatedMinutes: '', actualMinutes: '', status: backfillMode ? 'done' : 'open', completedDate: '' });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const isDone = backfillMode || ['done', 'completed'].includes(form.status);

  // YYYY-MM-DD in Pacific — matches the convention used by the QuickTrack date filters,
  // and is what the backend's getPacificDateStr() compares against for "not the future".
  const pacificToday = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/Los_Angeles' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    const errs = {};
    if (!form.estimatedMinutes || parseInt(form.estimatedMinutes) <= 0) errs.estimatedMinutes = 'Estimated Time is required';
    if (isDone && (!form.actualMinutes || parseInt(form.actualMinutes) <= 0)) errs.actualMinutes = 'Actual Time is required when status is Done';
    if (backfillMode) {
      if (!form.completedDate) errs.completedDate = 'Completed date is required';
      else if (form.completedDate > pacificToday()) errs.completedDate = 'Completed date cannot be in the future';
    }
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSubmitting(true);
    const payload = backfillMode ? { ...form, status: 'done', isBackfill: true } : form;
    const result = await onSubmit(payload);
    setSubmitting(false);
    // A validation failure (e.g. rejected by the backend) surfaces here instead of the
    // modal silently closing as if it had worked.
    if (result?.success === false) setErrors(prev => ({ ...prev, _general: result.error || 'Failed to save task' }));
  };

  const priorityPill = { low: 'bg-gray-100 text-gray-600', medium: 'bg-blue-100 text-blue-700', high: 'bg-orange-100 text-orange-700', urgent: 'bg-red-100 text-red-700' };
  const statusPill = { open: 'bg-gray-100 text-gray-700', in_progress: 'bg-yellow-100 text-yellow-800', done: 'bg-green-100 text-green-700' };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-50 rounded-xl shadow-2xl w-full max-w-4xl max-h-[92vh] flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 bg-white rounded-t-xl flex-shrink-0">
          <h2 className="text-base font-bold text-gray-900">{backfillMode ? '🕓 Backfill Past Task' : 'New Quick Task'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl font-bold leading-none">&times;</button>
        </div>

        {backfillMode && (
          <div className="px-6 pt-4 flex-shrink-0">
            <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-xs text-amber-900">
              Orbit was unavailable Sept 1–2, 2026. Use this to log work you already completed on an earlier date —
              it'll be dated and counted for that day (including TimeBoard hours), not today.
            </div>
          </div>
        )}

        {/* Scrollable Body */}
        <div className="overflow-y-auto flex-1">
          <form onSubmit={handleSubmit}>
            <div className="p-6 grid grid-cols-3 gap-5">

              {/* ── Main Column (2/3) ── */}
              <div className="col-span-2 space-y-4">
                {/* Priority + Category pills + Title */}
                <div className="bg-white rounded-xl border border-gray-200 p-5">
                  <div className="flex items-center gap-2 mb-4 flex-wrap">
                    <select value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })}
                      className={`text-xs font-semibold px-3 py-1 rounded-full border-0 cursor-pointer focus:ring-2 focus:ring-indigo-400 ${priorityPill[form.priority] || 'bg-gray-100 text-gray-600'}`}>
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="urgent">Urgent</option>
                    </select>
                    <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}
                      className="text-xs font-medium px-3 py-1 rounded-full border border-gray-200 cursor-pointer focus:ring-2 focus:ring-indigo-400 bg-white text-gray-600">
                      <option value="general">General</option>
                      <option value="bug">Bug</option>
                      <option value="feature">Feature</option>
                      <option value="chore">Chore</option>
                    </select>
                    {backfillMode ? (
                      // Backfill entries are always Done — there's no such thing as an
                      // "open" task from a date that's already past.
                      <span className={`text-xs font-medium px-3 py-1 rounded-full ${statusPill.done}`}>Done</span>
                    ) : (
                      <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}
                        className={`text-xs font-medium px-3 py-1 rounded-full border-0 cursor-pointer focus:ring-2 focus:ring-indigo-400 ${statusPill[form.status] || 'bg-gray-100 text-gray-700'}`}>
                        <option value="open">Open</option>
                        <option value="in_progress">In Progress</option>
                        <option value="done">Done</option>
                      </select>
                    )}
                  </div>
                  {backfillMode && (
                    <div className="mb-4">
                      <p className="text-xs text-gray-500 mb-1">Completed Date <span className="text-red-500">*</span></p>
                      <div className="flex items-center gap-2 flex-wrap">
                        <input type="date" value={form.completedDate} max={pacificToday()}
                          onChange={e => { setForm({ ...form, completedDate: e.target.value }); setErrors(ev => ({ ...ev, completedDate: '' })); }}
                          className={`border rounded-lg px-2 py-1.5 text-sm focus:ring-2 focus:ring-indigo-400 ${errors.completedDate ? 'border-red-400' : 'border-gray-200'}`} />
                        <span className="text-xs text-gray-400">Quick:</span>
                        <button type="button" onClick={() => setForm({ ...form, completedDate: '2026-09-01' })}
                          className="px-2.5 py-1 text-xs bg-white border border-gray-300 rounded hover:bg-gray-50">Sep 1</button>
                        <button type="button" onClick={() => setForm({ ...form, completedDate: '2026-09-02' })}
                          className="px-2.5 py-1 text-xs bg-white border border-gray-300 rounded hover:bg-gray-50">Sep 2</button>
                      </div>
                      {errors.completedDate && <p className="text-xs text-red-500 mt-1">{errors.completedDate}</p>}
                    </div>
                  )}
                  <input type="text" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} required
                    placeholder="Task title…"
                    className="w-full text-xl font-bold text-gray-900 border border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400" />
                </div>

                {/* Description */}
                <div className="bg-white rounded-xl border border-gray-200 p-5">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Description</label>
                  <MiniWysiwyg value={form.description} onChange={v => setForm({ ...form, description: v })} placeholder="Add a description…" minHeight={160} />
                </div>
              </div>

              {/* ── Sidebar (1/3) ── */}
              <div className="space-y-4">
                <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-4">
                  <h3 className="font-semibold text-gray-700 text-sm border-b border-gray-100 pb-2">Task Details</h3>

                  {/* Assign To */}
                  <div>
                    <p className="text-xs text-gray-400 mb-0.5">Assign To</p>
                    {selfAssign ? (
                      <p className="text-sm font-medium text-gray-800">{form.assignedTo || '—'}</p>
                    ) : (
                      <select value={form.assignedTo} onChange={e => setForm({ ...form, assignedTo: e.target.value })}
                        className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:ring-2 focus:ring-indigo-400">
                        <option value="">— Unassigned —</option>
                        {assignableMembers.map(m => <option key={m.user_id} value={m.user_name}>{m.user_name}</option>)}
                      </select>
                    )}
                  </div>

                  {/* Est. Time */}
                  <div>
                    <p className="text-xs text-gray-400 mb-0.5">Est. Time (min) <span className="text-red-500">*</span></p>
                    <input type="number" min="1" placeholder="e.g. 60" value={form.estimatedMinutes}
                      onChange={e => { setForm({ ...form, estimatedMinutes: e.target.value }); setErrors(ev => ({ ...ev, estimatedMinutes: '' })); }}
                      className={`w-full border rounded-lg px-2 py-1.5 text-sm focus:ring-2 focus:ring-indigo-400 ${errors.estimatedMinutes ? 'border-red-400' : 'border-gray-200'}`} />
                    {errors.estimatedMinutes && <p className="text-xs text-red-500 mt-1">{errors.estimatedMinutes}</p>}
                  </div>

                  {/* Actual Time */}
                  <div>
                    <p className="text-xs text-gray-400 mb-0.5">Actual Time (min){isDone && <span className="text-red-500"> *</span>}</p>
                    <input type="number" min="0" placeholder="0" value={form.actualMinutes}
                      onChange={e => { setForm({ ...form, actualMinutes: e.target.value }); setErrors(ev => ({ ...ev, actualMinutes: '' })); }}
                      className={`w-full border rounded-lg px-2 py-1.5 text-sm focus:ring-2 focus:ring-indigo-400 ${errors.actualMinutes ? 'border-red-400' : 'border-gray-200'}`} />
                    {errors.actualMinutes && <p className="text-xs text-red-500 mt-1">{errors.actualMinutes}</p>}
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-between items-center gap-3 px-6 pb-6">
              <span className="text-xs text-red-600 font-medium">{errors._general}</span>
              <div className="flex items-center gap-3 ml-auto">
                <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</button>
                <button type="submit" disabled={submitting} className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50">
                  {submitting ? 'Saving…' : backfillMode ? 'Backfill Task' : 'Create Task'}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

const EditQuickTaskModal = ({ task, onClose, onSubmit, projects, user, teamMembers = [] }) => {
  const role = user?.user_role || user?.role || '';
  const isAdmin = role === 'admin';
  const isManager = !isAdmin && (role.endsWith('_manager') || role === 'manager');
  const managerModule = isManager
    ? (role !== 'manager'
      ? role.replace('_manager', '')
      : teamMembers.find(m => m.user_id === user?.user_id || m.user_name === (user?.user_name || user?.name))?.team_module || null)
    : null;
  const selfAssign = !isAdmin && !isManager;
  const assignableMembers = isAdmin
    ? teamMembers
    : isManager
    ? (managerModule ? teamMembers.filter(m => m.team_module?.toLowerCase() === managerModule?.toLowerCase()) : teamMembers)
    : [];
  const [form, setForm] = useState({
    title: task.title || '',
    description: task.description || '',
    priority: task.priority || 'medium',
    category: task.category || 'general',
    assignedTo: task.assignedTo || '',
    projectId: task.projectId || '',
    estimatedMinutes: task.estimatedMinutes || task.estimated_minutes || '',
    actualMinutes: task.actualMinutes || task.actual_minutes || ''
  });
  const [activeTab, setActiveTab] = useState('details');

  const handleSubmit = (e) => { e.preventDefault(); if (!form.title.trim()) return; onSubmit(task.id, form); };

  const priorityPill = { low: 'bg-gray-100 text-gray-600', medium: 'bg-blue-100 text-blue-700', high: 'bg-orange-100 text-orange-700', urgent: 'bg-red-100 text-red-700' };
  // task.createdAt is a raw SQLite UTC string ("YYYY-MM-DD HH:MM:SS", no 'T'/'Z'), which
  // `new Date(...)` parses as browser-local time rather than UTC if left as-is — normalize
  // to an explicit UTC ISO string first, then render in Pacific per this app's convention.
  const fmtDate = (d) => {
    if (!d) return '—';
    const iso = String(d).includes('T') ? d : String(d).replace(' ', 'T') + 'Z';
    const dt = new Date(iso);
    return isNaN(dt.getTime()) ? '—' : dt.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: 'America/Los_Angeles' });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-50 rounded-xl shadow-2xl w-full max-w-5xl max-h-[92vh] flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 bg-white rounded-t-xl flex-shrink-0">
          <h2 className="text-base font-bold text-gray-900">Edit Task</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl font-bold leading-none">&times;</button>
        </div>

        {/* Scrollable Body */}
        <div className="overflow-y-auto flex-1">
          <form onSubmit={handleSubmit}>
            <div className="p-6 grid grid-cols-3 gap-5">

              {/* ── Main Column (2/3) ── */}
              <div className="col-span-2 space-y-4">
                {/* Priority + Category pills + Title */}
                <div className="bg-white rounded-xl border border-gray-200 p-5">
                  <div className="flex items-center gap-2 mb-4 flex-wrap">
                    <select value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })}
                      className={`text-xs font-semibold px-3 py-1 rounded-full border-0 cursor-pointer focus:ring-2 focus:ring-indigo-400 ${priorityPill[form.priority] || 'bg-gray-100 text-gray-600'}`}>
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="urgent">Urgent</option>
                    </select>
                    <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}
                      className="text-xs font-medium px-3 py-1 rounded-full border border-gray-200 cursor-pointer focus:ring-2 focus:ring-indigo-400 bg-white text-gray-600">
                      <option value="general">General</option>
                      <option value="bug">Bug</option>
                      <option value="feature">Feature</option>
                      <option value="chore">Chore</option>
                    </select>
                  </div>
                  <input type="text" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} required
                    placeholder="Task title…"
                    className="w-full text-xl font-bold text-gray-900 border border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400" />
                </div>

                {/* Tabbed card */}
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                  <div className="flex border-b border-gray-200 px-4 pt-3 gap-1">
                    {[{ key: 'details', label: '📋 Details' }, { key: 'comments', label: '💬 Comments' }].map(t => (
                      <button key={t.key} type="button" onClick={() => setActiveTab(t.key)}
                        className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${activeTab === t.key ? 'bg-indigo-50 text-indigo-700 border-b-2 border-indigo-600' : 'text-gray-500 hover:text-gray-700'}`}>
                        {t.label}
                      </button>
                    ))}
                  </div>
                  <div className="p-5">
                    {activeTab === 'details' && (
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Description</label>
                        <MiniWysiwyg value={form.description} onChange={v => setForm({ ...form, description: v })} placeholder="Add a description…" minHeight="120px" />
                      </div>
                    )}
                    {activeTab === 'comments' && (
                      <TaskComments taskRef={task.id} taskSource="quicktrack" userName={user?.user_name || user?.name} />
                    )}
                  </div>
                </div>
              </div>

              {/* ── Sidebar (1/3) ── */}
              <div className="space-y-4">
                <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-4">
                  <h3 className="font-semibold text-gray-700 text-sm border-b border-gray-100 pb-2">Task Details</h3>

                  {/* Assign To */}
                  <div>
                    <p className="text-xs text-gray-400 mb-0.5">Assign To</p>
                    {selfAssign ? (
                      <p className="text-sm font-medium text-gray-800">{form.assignedTo || '—'}</p>
                    ) : (
                      <select value={form.assignedTo} onChange={e => setForm({ ...form, assignedTo: e.target.value })}
                        className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:ring-2 focus:ring-indigo-400">
                        <option value="">— Unassigned —</option>
                        {assignableMembers.map(m => <option key={m.user_id} value={m.user_name}>{m.user_name}</option>)}
                      </select>
                    )}
                  </div>


                  {/* Est. Time */}
                  <div>
                    <p className="text-xs text-gray-400 mb-0.5">Est. Time (min)</p>
                    <input type="number" min="0" placeholder="0" value={form.estimatedMinutes}
                      onChange={e => setForm({ ...form, estimatedMinutes: e.target.value })}
                      className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:ring-2 focus:ring-indigo-400" />
                  </div>

                  {/* Actual Time */}
                  <div>
                    <p className="text-xs text-gray-400 mb-0.5">Actual Time (min)</p>
                    <input type="number" min="0" placeholder="0" value={form.actualMinutes}
                      onChange={e => setForm({ ...form, actualMinutes: e.target.value })}
                      className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:ring-2 focus:ring-indigo-400" />
                  </div>

                  {/* Created */}
                  {task.createdAt && (
                    <div>
                      <p className="text-xs text-gray-400 mb-0.5">Created</p>
                      <p className="text-sm text-gray-600">{fmtDate(task.createdAt)}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end items-center gap-3 px-6 pb-6">
              <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</button>
              <button type="submit" className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700">Save Changes</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

const QuickTrackModule = ({ data, filterStatus, filterPriority, filterCategory, filterDateFrom, filterDateTo, onFilterStatus, onFilterPriority, onFilterCategory, onFilterDateFrom, onFilterDateTo, onResetFilters, onAddTask, onBackfillTask, onStatusChange, onDelete, onEdit, user, teamMembers = [] }) => {
  // Client-side filtering
  let tasks = data?.tasks || [];
  if (filterStatus) tasks = tasks.filter(t => t.status === filterStatus);
  if (filterPriority) tasks = tasks.filter(t => t.priority === filterPriority);
  if (filterCategory) tasks = tasks.filter(t => t.category === filterCategory);
  if (filterDateFrom || filterDateTo) {
    // Backend aliases qt.created_at AS createdAt — camelCase on the wire.
    // Stored as UTC datetime "YYYY-MM-DD HH:MM:SS"; convert to Pacific calendar date for comparison.
    tasks = tasks.filter(t => {
      const raw = t.createdAt || t.created_at; // tolerate either shape just in case
      if (!raw) return false;
      const createdUtc = new Date(String(raw).replace(' ', 'T') + (String(raw).endsWith('Z') ? '' : 'Z'));
      if (isNaN(createdUtc.getTime())) return false;
      const createdPt = createdUtc.toLocaleDateString('en-CA', { timeZone: 'America/Los_Angeles' });
      if (filterDateFrom && createdPt < filterDateFrom) return false;
      if (filterDateTo && createdPt > filterDateTo) return false;
      return true;
    });
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">QuickTrack</h1>
        <p className="text-gray-600 mt-1">Ad-hoc task tracker for quick turnaround items</p>
      </div>
      <QuickTrackMetrics metrics={data?.metrics} />
      <QuickTrackFilters
        filterStatus={filterStatus} filterPriority={filterPriority} filterCategory={filterCategory}
        filterDateFrom={filterDateFrom} filterDateTo={filterDateTo}
        onFilterStatus={onFilterStatus} onFilterPriority={onFilterPriority} onFilterCategory={onFilterCategory}
        onFilterDateFrom={onFilterDateFrom} onFilterDateTo={onFilterDateTo}
        onReset={onResetFilters} onAddTask={onAddTask} onBackfillTask={onBackfillTask}
      />
      <QuickTrackTable tasks={tasks} onStatusChange={onStatusChange} onDelete={onDelete} onEdit={onEdit} user={user} teamMembers={teamMembers} />
    </div>
  );
};

// ============================================================================
// VENDOR EVENT OBSERVABILITY VIEWS
// ============================================================================

const getVendorEventStatusStyle = (status) => {
  const styles = {
    Accepted: { bg: '#dcfce7', color: '#166534' },
    Rejected: { bg: '#fff7ed', color: '#9a3412' },
    Failed: { bg: '#fee2e2', color: '#991b1b' },
    Pending: { bg: '#f1f5f9', color: '#475569' }
  };
  return styles[status] || styles.Pending;
};

const getWorkflowStyle = (wf) => {
  const styles = {
    Creation: { bg: '#dbeafe', color: '#1e40af' },
    Reattribution: { bg: '#f3e8ff', color: '#6b21a8' },
    Rejection: { bg: '#fef3c7', color: '#92400e' },
    Reengagement: { bg: '#d1fae5', color: '#065f46' }
  };
  return styles[wf] || { bg: '#f1f5f9', color: '#475569' };
};

const VendorEventsMetrics = ({ metrics }) => {
  const cards = [
    { label: 'Total Leads', value: metrics?.totalLeads ?? 0, sub: 'Matching current filters', color: '#3b82f6' },
    { label: "Today's Leads", value: metrics?.todayLeads ?? 0, sub: 'Pacific Time', color: '#22c55e' },
    { label: 'Active Partners', value: metrics?.uniquePartners ?? 0, sub: 'Distinct partner names', color: '#f59e0b' },
    { label: 'Top Partner', value: metrics?.topPartner || '—', sub: 'Highest lead volume', color: '#8b5cf6' },
  ];
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      {cards.map(c => (
        <div key={c.label} className="bg-white rounded-lg shadow border border-gray-200 p-5">
          <div className="text-xs font-semibold text-gray-500 uppercase mb-2">{c.label}</div>
          <div className="text-3xl font-bold mb-1" style={{ color: c.color }}>{c.value}</div>
          <div className="text-xs text-gray-400">{c.sub}</div>
        </div>
      ))}
    </div>
  );
};

const VendorEventsFilters = ({ events, filterVendor, filterWorkflow, filterStatus, onFilterVendor, onFilterWorkflow, onFilterStatus, onReset }) => {
  const allEvents = events || [];
  const uniqueWorkflows = [...new Set(allEvents.map(e => e.workflow).filter(Boolean))].sort();
  const uniqueStatuses  = [...new Set(allEvents.map(e => e.status).filter(Boolean))].sort();
  const uniqueVendors   = [...new Set(allEvents.map(e => e.partner || e.vendor).filter(Boolean))].sort();
  return (
    <div className="bg-white rounded-lg shadow border border-gray-200 p-5 mb-6">
      <h2 className="text-sm font-semibold text-gray-700 mb-3">Filters</h2>
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1">
          <label className="text-xs font-semibold text-gray-500 uppercase">Partner</label>
          <select value={filterVendor} onChange={e => onFilterVendor(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm min-w-[170px] focus:ring-2 focus:ring-red-400 focus:border-red-400">
            <option value="">All Partners</option>
            {uniqueVendors.map(v => <option key={v} value={v}>{v}</option>)}
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs font-semibold text-gray-500 uppercase">Workflow</label>
          <select value={filterWorkflow} onChange={e => onFilterWorkflow(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm min-w-[170px] focus:ring-2 focus:ring-red-400 focus:border-red-400">
            <option value="">All Workflows</option>
            {uniqueWorkflows.map(w => <option key={w} value={w}>{w}</option>)}
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs font-semibold text-gray-500 uppercase">Status</label>
          <select value={filterStatus} onChange={e => onFilterStatus(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm min-w-[170px] focus:ring-2 focus:ring-red-400 focus:border-red-400">
            <option value="">All Statuses</option>
            {uniqueStatuses.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <button onClick={onReset} className="px-4 py-2 bg-gray-100 border border-gray-300 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-200">Reset</button>
      </div>
    </div>
  );
};

const VendorEventsTable = ({ events }) => {
  const [expandedId, setExpandedId] = useState(null);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 10;
  const allEvents = events || [];
  const totalPages = Math.max(1, Math.ceil(allEvents.length / PAGE_SIZE));
  const pageEvents = allEvents.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  useEffect(() => { setPage(1); setExpandedId(null); }, [events]);
  const utmSourceColor = (src) => {
    if (!src) return { bg: '#f1f5f9', color: '#64748b' };
    const s = src.toLowerCase();
    if (s.includes('google')) return { bg: '#dbeafe', color: '#1e40af' };
    if (s.includes('facebook') || s.includes('meta')) return { bg: '#ede9fe', color: '#5b21b6' };
    if (s.includes('email') || s.includes('mail')) return { bg: '#dcfce7', color: '#166534' };
    if (s.includes('organic') || s.includes('seo')) return { bg: '#fef9c3', color: '#854d0e' };
    return { bg: '#f1f5f9', color: '#374151' };
  };
  const dash = <span className="text-gray-300">—</span>;

  return (
    <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
      <div className="flex justify-between items-center px-5 py-4 border-b border-gray-200">
        <h2 className="text-sm font-semibold text-gray-700">Lead Events</h2>
        <span className="text-xs text-gray-500 bg-gray-100 px-3 py-1 rounded-full">{(events || []).length} records</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase whitespace-nowrap">Contact ID</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase whitespace-nowrap">Partner</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase whitespace-nowrap">Workflow</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase whitespace-nowrap">Date</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase whitespace-nowrap">Time</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase whitespace-nowrap">UTM Source</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase whitespace-nowrap">UTM Campaign</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase whitespace-nowrap">Lead Src ID</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {pageEvents.map(ev => {
              const ws = getWorkflowStyle(ev.workflow);
              const utmSrc = utmSourceColor(ev.utmSource);
              const rowKey = ev.id || ev.leadId;
              const isExpanded = expandedId === rowKey;
              const hasExtra = ev.utmMedium || ev.utmContent || ev.utmNetwork || ev.clickId;
              return (
                <React.Fragment key={rowKey}>
                  <tr className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-semibold text-gray-900 whitespace-nowrap">{ev.leadId || dash}</td>
                    <td className="px-4 py-3 text-gray-700 whitespace-nowrap">{ev.partner || ev.vendor || dash}</td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap" style={{ background: ws.bg, color: ws.color }}>{ev.workflow}</span>
                    </td>
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{ev.date || dash}</td>
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{ev.time || dash}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {ev.utmSource
                        ? <span className="px-2 py-0.5 rounded text-xs font-medium" style={{ background: utmSrc.bg, color: utmSrc.color }}>{ev.utmSource}</span>
                        : dash}
                    </td>
                    <td className="px-4 py-3 text-gray-600 max-w-[160px]">
                      {ev.utmCampaign ? <span title={ev.utmCampaign} className="block truncate">{ev.utmCampaign}</span> : dash}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {ev.leadSourceId
                        ? <span className="px-2 py-0.5 rounded bg-gray-100 text-gray-700 text-xs font-mono">{ev.leadSourceId}</span>
                        : dash}
                    </td>
                    <td className="px-4 py-3">
                      {hasExtra && (
                        <button onClick={() => setExpandedId(isExpanded ? null : rowKey)}
                          className="text-xs text-blue-600 hover:text-blue-800 font-medium whitespace-nowrap">
                          {isExpanded ? 'Less ▲' : 'More ▼'}
                        </button>
                      )}
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr className="bg-blue-50">
                      <td colSpan={10} className="px-6 py-3">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                          {ev.utmMedium && (
                            <div><span className="text-gray-500 uppercase font-semibold block">UTM Medium</span><span className="text-gray-800">{ev.utmMedium}</span></div>
                          )}
                          {ev.utmContent && (
                            <div><span className="text-gray-500 uppercase font-semibold block">UTM Content</span><span className="text-gray-800">{ev.utmContent}</span></div>
                          )}
                          {ev.utmNetwork && (
                            <div><span className="text-gray-500 uppercase font-semibold block">UTM Network</span><span className="text-gray-800">{ev.utmNetwork}</span></div>
                          )}
                          {ev.clickId && (
                            <div><span className="text-gray-500 uppercase font-semibold block">Click ID</span><span className="text-gray-800 font-mono">{ev.clickId}</span></div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
        {allEvents.length === 0 && (
          <div className="p-12 text-center text-gray-400">No records match the selected filters.</div>
        )}
        {allEvents.length > 0 && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-gray-200 bg-gray-50">
            <span className="text-xs text-gray-500">
              Showing {Math.min((page - 1) * PAGE_SIZE + 1, allEvents.length)}–{Math.min(page * PAGE_SIZE, allEvents.length)} of {allEvents.length} records
            </span>
            <div className="flex items-center gap-1">
              <button onClick={() => setPage(1)} disabled={page === 1}
                className="px-2 py-1 text-xs rounded border border-gray-300 text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed">«</button>
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                className="px-2 py-1 text-xs rounded border border-gray-300 text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed">‹</button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
                .reduce((acc, p, idx, arr) => {
                  if (idx > 0 && p - arr[idx - 1] > 1) acc.push('...');
                  acc.push(p);
                  return acc;
                }, [])
                .map((p, i) => p === '...'
                  ? <span key={`ellipsis-${i}`} className="px-2 py-1 text-xs text-gray-400">…</span>
                  : <button key={p} onClick={() => setPage(p)}
                      className={`px-2.5 py-1 text-xs rounded border font-medium ${page === p ? 'bg-red-600 text-white border-red-600' : 'border-gray-300 text-gray-600 hover:bg-gray-100'}`}>{p}</button>
                )}
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                className="px-2 py-1 text-xs rounded border border-gray-300 text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed">›</button>
              <button onClick={() => setPage(totalPages)} disabled={page === totalPages}
                className="px-2 py-1 text-xs rounded border border-gray-300 text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed">»</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};


const VENDOR_WORKFLOWS = [
  { key: 'New Contact',    color: 'bg-blue-100 text-blue-700' },
  { key: 'Reattribution',  color: 'bg-purple-100 text-purple-700' },
  { key: 'Reengagement',   color: 'bg-amber-100 text-amber-700' },
  { key: 'Reject',         color: 'bg-red-100 text-red-700' },
];

const VendorList = ({ vendors, events }) => {
  // Count events per vendor per workflow
  const counts = {};
  (events || []).forEach(ev => {
    const name = ev.partner || ev.vendor;
    if (!name) return;
    if (!counts[name]) counts[name] = {};
    const wf = ev.workflow || '';
    counts[name][wf] = (counts[name][wf] || 0) + 1;
  });
  // Match workflow key loosely (e.g. "Reject" matches "Rejection")
  const getCount = (vendorName, wfKey) => {
    const vc = counts[vendorName] || {};
    return Object.entries(vc).reduce((sum, [k, n]) => sum + (k.toLowerCase().startsWith(wfKey.toLowerCase()) ? n : 0), 0);
  };
  return (
  <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden mb-6">
    <div className="flex justify-between items-center px-5 py-4 border-b border-gray-200">
      <h2 className="text-sm font-semibold text-gray-700">Registered Vendors</h2>
      <span className="text-xs text-gray-500 bg-gray-100 px-3 py-1 rounded-full">{(vendors || []).length} vendors</span>
    </div>
    {(!vendors || vendors.length === 0) ? (
      <div className="p-8 text-center text-gray-400 text-sm">No vendors registered yet. Click "+ New Vendor" to add one.</div>
    ) : (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Partner Name</th>
              {VENDOR_WORKFLOWS.map(w => (
                <th key={w.key} className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase whitespace-nowrap">{w.key}</th>
              ))}
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {(vendors || []).map(v => (
              <tr key={v.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 font-semibold text-gray-900">{v.vendorName}</td>
                {VENDOR_WORKFLOWS.map(w => {
                  const n = getCount(v.vendorName, w.key);
                  return (
                    <td key={w.key} className="px-4 py-3">
                      {n > 0
                        ? <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${w.color}`}>{n}</span>
                        : <span className="text-gray-300 text-xs">—</span>}
                    </td>
                  );
                })}
                <td className="px-4 py-3">
                  <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${v.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                    {v.status || 'active'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
  );
};

const downloadCsv = (filename, headers, rows) => {
  const escape = (val) => {
    const s = String(val ?? '');
    return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = [headers.map(escape).join(','), ...rows.map(r => r.map(escape).join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
};


const toPacificDateTime = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleString('en-US', { timeZone: 'America/Los_Angeles', year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
};

const VendorEventsModule = ({ data, filterVendor, filterWorkflow, filterStatus, onFilterVendor, onFilterWorkflow, onFilterStatus, onResetFilters }) => {
  const today = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Los_Angeles' });
  const [exportOpen, setExportOpen] = React.useState(false);

  const handleDownloadEvents = () => {
    const events = data?.events || [];
    const headers = ['Contact ID', 'Partner', 'Workflow', 'Date', 'Time', 'UTM Source', 'UTM Campaign', 'Lead Src ID', 'Status', 'UTM Medium', 'UTM Content', 'UTM Network', 'Click ID'];
    const rows = events.map(ev => [
      ev.leadId, ev.partner || ev.vendor || '', ev.workflow, ev.date || '', ev.time || '',
      ev.utmSource || '', ev.utmCampaign || '', ev.leadSourceId || '', ev.status || '',
      ev.utmMedium || '', ev.utmContent || '', ev.utmNetwork || '', ev.clickId || '',
    ]);
    downloadCsv(`vendor-events-${today}.csv`, headers, rows);
    setExportOpen(false);
  };

  const handleDownloadVendors = () => {
    const vendors = data?.vendors || [];
    const headers = ['Vendor Name', 'URL', 'Contact Email', 'Status', 'Last Check Status', 'Last Checked At (PT)'];
    const rows = vendors.map(v => [v.vendorName, v.vendorUrl || '', v.contactEmail || '', v.status, v.lastCheckStatus ?? '', v.lastCheckAt ? toPacificDateTime(v.lastCheckAt) : '']);
    downloadCsv(`vendors-${today}.csv`, headers, rows);
    setExportOpen(false);
  };

  return (
  <div className="max-w-7xl mx-auto px-4 py-6">
    <div className="flex justify-between items-start mb-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Vendor Event Observability</h1>
        <p className="text-gray-600 mt-1">Real-time monitoring of vendor lead events across all workflow lines</p>
      </div>
      <div className="flex gap-2">
        <div className="relative">
          <button onClick={() => setExportOpen(o => !o)} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 flex items-center gap-2 whitespace-nowrap border border-gray-300">
            Export CSV
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9"/></svg>
          </button>
          {exportOpen && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setExportOpen(false)} />
              <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 min-w-[220px]">
                <button onClick={handleDownloadEvents} className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 rounded-t-lg">
                  Lead Events ({(data?.events || []).length})
                </button>
                <button onClick={handleDownloadVendors} className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 rounded-b-lg border-t border-gray-100">
                  Vendor List ({(data?.vendors || []).length})
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
    <VendorEventsMetrics metrics={data?.metrics} />
    <VendorList vendors={data?.vendors} events={data?.events} />
    <VendorEventsFilters
      events={data?.events}
      vendors={data?.vendors}
      filterVendor={filterVendor} filterWorkflow={filterWorkflow} filterStatus={filterStatus}
      onFilterVendor={onFilterVendor} onFilterWorkflow={onFilterWorkflow} onFilterStatus={onFilterStatus}
      onReset={onResetFilters}
    />
    <VendorEventsTable events={data?.events} />
  </div>
  );
};

// ============================================================================
// LOGIN VIEW
// ============================================================================

const AuthInput = ({ label, type = 'text', value, onChange, placeholder, required, autoComplete }) => (
  <div>
    <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
    <input
      type={type} value={value} onChange={onChange} placeholder={placeholder}
      required={required} autoComplete={autoComplete}
      className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
    />
  </div>
);

const AuthAlert = ({ type, message }) => type === 'error' ? (
  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-start gap-2">
    <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />{message}
  </div>
) : (
  <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">{message}</div>
);

const LoginBranding = () => (
  <div className="text-center mb-8">
    <div className="text-5xl mb-3">🛰️</div>
    <h1 className="text-3xl font-bold text-white tracking-wide">ORBIT</h1>
    <p className="text-blue-200 mt-1 text-sm">Enterprise Operations Hub</p>
  </div>
);

// ---- Forgot Password panel ----
const ForgotPasswordPanel = ({ onBack }) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch('/api/auth/forgot-password', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      const data = await res.json();
      setResult({ ok: true, data });
    } catch { setResult({ ok: false, msg: 'Network error. Please try again.' }); }
    setLoading(false);
  };

  if (result?.ok) {
    return (
      <div className="p-8 space-y-4">
        <div className="text-center">
          <div className="text-4xl mb-3">✉️</div>
          <h3 className="font-semibold text-gray-900">Reset Link Generated</h3>
          <p className="text-sm text-gray-500 mt-1">{result.data.message}</p>
        </div>
        {result.data.resetUrl && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-xs font-medium text-blue-700 mb-1">Reset URL (share securely):</p>
            <p className="text-xs text-blue-600 break-all font-mono">{result.data.resetUrl}</p>
            <p className="text-xs text-blue-500 mt-1">Expires in: {result.data.expiresIn}</p>
          </div>
        )}
        <button onClick={onBack} className="w-full text-sm text-blue-600 hover:text-blue-800 font-medium">← Back to Sign In</button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="p-8 space-y-4">
      <div className="text-center mb-2">
        <h3 className="font-semibold text-gray-900">Reset your password</h3>
        <p className="text-xs text-gray-500 mt-1">Enter your email and we'll generate a secure reset link</p>
      </div>
      {result && !result.ok && <AuthAlert type="error" message={result.msg} />}
      <AuthInput label="Work Email" type="email" value={email} onChange={e => setEmail(e.target.value)}
        placeholder="you@company.com" required autoComplete="email" />
      <button type="submit" disabled={loading}
        className="w-full bg-blue-600 text-white py-3 rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:opacity-60 transition-colors">
        {loading ? 'Generating…' : 'Send Reset Link'}
      </button>
      <button type="button" onClick={onBack} className="w-full text-sm text-gray-500 hover:text-gray-700">← Back to Sign In</button>
    </form>
  );
};

// ---- Reset Password page (standalone, shown when ?reset_token= in URL) ----
const ResetPasswordView = ({ token, onDone }) => {
  const [form, setForm] = useState({ newPassword: '', confirmPassword: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch('/api/auth/reset-password', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, newPassword: form.newPassword, confirmPassword: form.confirmPassword })
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Reset failed'); }
      else { setSuccess(true); }
    } catch { setError('Network error. Please try again.'); }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <LoginBranding />
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          {success ? (
            <div className="p-8 text-center space-y-4">
              <div className="text-4xl">✅</div>
              <h3 className="font-semibold text-gray-900">Password Reset Successfully</h3>
              <p className="text-sm text-gray-500">You can now sign in with your new password.</p>
              <button onClick={onDone} className="w-full bg-blue-600 text-white py-3 rounded-lg text-sm font-semibold hover:bg-blue-700">
                Go to Sign In
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="p-8 space-y-4">
              <div className="text-center mb-2">
                <h3 className="font-semibold text-gray-900">Set new password</h3>
                <p className="text-xs text-gray-500 mt-1">Choose a strong password of at least 8 characters</p>
              </div>
              {error && <AuthAlert type="error" message={error} />}
              <AuthInput label="New Password" type="password" value={form.newPassword}
                onChange={e => set('newPassword', e.target.value)} placeholder="At least 8 characters"
                required autoComplete="new-password" />
              <AuthInput label="Confirm New Password" type="password" value={form.confirmPassword}
                onChange={e => set('confirmPassword', e.target.value)} placeholder="••••••••"
                required autoComplete="new-password" />
              <button type="submit" disabled={loading}
                className="w-full bg-blue-600 text-white py-3 rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:opacity-60 transition-colors">
                {loading ? 'Saving…' : 'Reset Password'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

// ---- Main Login View ----
const LoginView = ({ onLogin }) => {
  const [mode, setMode] = useState('login');        // 'login' | 'register' | 'forgot'
  const [authMethod, setAuthMethod] = useState('password');
  const [form, setForm] = useState({ email: '', password: '', name: '', confirmPassword: '' });
  const [ssoIssuer, setSsoIssuer] = useState('');
  const [ssoEmail, setSsoEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const clearMessages = () => setError('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    clearMessages(); setLoading(true);
    try {
      const endpoint = mode === 'register' ? '/api/auth/register' : '/api/auth/login';
      const body = mode === 'register'
        ? { email: form.email, name: form.name, password: form.password, confirmPassword: form.confirmPassword }
        : { email: form.email, password: form.password };
      const res = await fetch(endpoint, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      const data = await res.json();
      if (!res.ok) setError(data.error || 'Authentication failed');
      else onLogin(data.token, data.user);
    } catch { setError('Network error. Please try again.'); }
    setLoading(false);
  };

  const handleGoogleLogin = async () => {
    clearMessages(); setLoading(true);
    try {
      const redirectUri = window.location.origin;
      const res = await fetch(`/api/auth/google/url?redirectUri=${encodeURIComponent(redirectUri)}`);
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Google sign-in is not configured'); setLoading(false); return; }
      sessionStorage.setItem('oauth_provider', 'google');
      sessionStorage.setItem('oauth_redirect_uri', redirectUri);
      window.location.href = data.url;
    } catch { setError('Failed to initialize Google sign-in'); }
    setLoading(false);
  };

  const handleSsoLogin = async () => {
    clearMessages();
    if (!ssoIssuer && !ssoEmail) { setError('Enter your work email or SSO provider URL'); return; }
    setLoading(true);
    try {
      const redirectUri = window.location.origin;
      const params = new URLSearchParams({ redirectUri });
      if (ssoIssuer) params.set('issuerUrl', ssoIssuer);
      if (ssoEmail) params.set('email', ssoEmail);
      const res = await fetch(`/api/auth/sso/url?${params}`);
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'SSO is not configured'); setLoading(false); return; }
      sessionStorage.setItem('oauth_provider', 'sso');
      sessionStorage.setItem('oauth_redirect_uri', redirectUri);
      sessionStorage.setItem('sso_issuer', ssoIssuer);
      window.location.href = data.url;
    } catch { setError('Failed to initialize SSO sign-in'); }
    setLoading(false);
  };

  const handleDemoLogin = async () => {
    clearMessages(); setLoading(true);
    try {
      const res = await fetch('/api/auth/demo', { method: 'POST' });
      const data = await res.json();
      if (!res.ok) setError(data.error || 'Demo login failed');
      else onLogin(data.token, data.user);
    } catch { setError('Demo login failed. Make sure the database is seeded.'); }
    setLoading(false);
  };

  const methodTab = (id, label, icon) => (
    <button key={id} onClick={() => { setAuthMethod(id); clearMessages(); }}
      className={`flex-1 py-2.5 text-sm font-medium rounded-lg transition-all ${authMethod === id ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
      <span className="mr-1.5">{icon}</span>{label}
    </button>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <LoginBranding />

        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">

          {/* Forgot Password panel */}
          {mode === 'forgot' ? (
            <ForgotPasswordPanel onBack={() => { setMode('login'); clearMessages(); }} />
          ) : (
            <>
              {/* Sign In / Create Account tabs */}
              <div className="flex border-b border-gray-100">
                {[['login', 'Sign In'], ['register', 'Create Account']].map(([m, label]) => (
                  <button key={m} onClick={() => { setMode(m); clearMessages(); if (m === 'register') setAuthMethod('password'); }}
                    className={`flex-1 py-4 text-sm font-semibold transition-colors ${mode === m ? 'text-blue-700 border-b-2 border-blue-600 bg-blue-50/40' : 'text-gray-400 hover:text-gray-600'}`}>
                    {label}
                  </button>
                ))}
              </div>

              <div className="p-8">
                {/* Auth method tabs (Sign In only) */}
                {mode === 'login' && (
                  <div className="flex bg-gray-100 rounded-xl p-1 mb-6">
                    {methodTab('password', 'Password', '🔑')}
                    {methodTab('google', 'Google', '🔵')}
                    {methodTab('sso', 'SSO', '🏢')}
                  </div>
                )}

                {error && <AuthAlert type="error" message={error} />}

                {/* PASSWORD FORM */}
                {(authMethod === 'password' || mode === 'register') && (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    {mode === 'register' && (
                      <AuthInput label="Full Name" value={form.name} onChange={e => set('name', e.target.value)}
                        placeholder="John Smith" required autoComplete="name" />
                    )}
                    <AuthInput label="Work Email" type="email" value={form.email} onChange={e => set('email', e.target.value)}
                      placeholder="you@company.com" required autoComplete="email" />
                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <label className="text-xs font-medium text-gray-600">Password</label>
                        {mode === 'login' && (
                          <button type="button" onClick={() => { setMode('forgot'); clearMessages(); }}
                            className="text-xs text-blue-600 hover:text-blue-800 font-medium">
                            Forgot password?
                          </button>
                        )}
                      </div>
                      <input type="password" required value={form.password} onChange={e => set('password', e.target.value)}
                        placeholder={mode === 'register' ? 'At least 8 characters' : '••••••••'}
                        autoComplete={mode === 'register' ? 'new-password' : 'current-password'}
                        className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
                    </div>
                    {mode === 'register' && (
                      <AuthInput label="Confirm Password" type="password" value={form.confirmPassword}
                        onChange={e => set('confirmPassword', e.target.value)} placeholder="••••••••"
                        required autoComplete="new-password" />
                    )}
                    <button type="submit" disabled={loading}
                      className="w-full bg-blue-600 text-white py-3 rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:opacity-60 transition-colors">
                      {loading ? 'Please wait…' : mode === 'register' ? 'Create Account' : 'Sign In'}
                    </button>
                  </form>
                )}

                {/* GOOGLE */}
                {authMethod === 'google' && mode === 'login' && (
                  <div className="space-y-4">
                    <p className="text-sm text-gray-500 text-center">Sign in using your Google Workspace account.</p>
                    <button onClick={handleGoogleLogin} disabled={loading}
                      className="w-full flex items-center justify-center gap-3 border-2 border-gray-200 rounded-lg py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-300 disabled:opacity-60 transition-colors">
                      <svg width="18" height="18" viewBox="0 0 18 18">
                        <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"/>
                        <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"/>
                        <path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"/>
                        <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 6.29C4.672 4.163 6.656 3.58 9 3.58z"/>
                      </svg>
                      {loading ? 'Redirecting…' : 'Continue with Google'}
                    </button>
                  </div>
                )}

                {/* SSO */}
                {authMethod === 'sso' && mode === 'login' && (
                  <div className="space-y-4">
                    <p className="text-sm text-gray-500 text-center">Sign in via Azure AD, Okta, Ping, or any OIDC provider.</p>
                    <AuthInput label="Work Email" type="email" value={ssoEmail} onChange={e => setSsoEmail(e.target.value)} placeholder="you@company.com" />
                    <div className="flex items-center gap-2 text-xs text-gray-400">
                      <div className="flex-1 h-px bg-gray-200" />or<div className="flex-1 h-px bg-gray-200" />
                    </div>
                    <AuthInput label="SSO Provider URL" type="url" value={ssoIssuer} onChange={e => setSsoIssuer(e.target.value)} placeholder="https://login.microsoftonline.com/{tenant-id}/v2.0" />
                    <button onClick={handleSsoLogin} disabled={loading}
                      className="w-full bg-indigo-600 text-white py-3 rounded-lg text-sm font-semibold hover:bg-indigo-700 disabled:opacity-60 transition-colors">
                      {loading ? 'Redirecting…' : 'Continue with SSO'}
                    </button>
                  </div>
                )}

                {/* Demo */}
                <div className="mt-6 pt-5 border-t border-gray-100">
                  <button onClick={handleDemoLogin} disabled={loading}
                    className="w-full py-2.5 rounded-lg text-xs font-medium text-gray-500 hover:text-gray-700 hover:bg-gray-50 border border-dashed border-gray-200 transition-colors">
                    {loading ? 'Loading…' : '⚡ Continue with Demo Account'}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        <p className="text-center text-blue-200 text-xs mt-6 opacity-70">ORBIT Enterprise © {new Date().getFullYear()}</p>
      </div>
    </div>
  );
};

// ---- Change Password Modal ----
const ChangePasswordModal = ({ authToken, onClose }) => {
  const [form, setForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const res = await fetch('/api/auth/change-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify(form)
      });
      const data = await res.json();
      if (!res.ok) setError(data.error || 'Failed to change password');
      else setSuccess(true);
    } catch { setError('Network error. Please try again.'); }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="font-semibold text-gray-900 flex items-center gap-2">
            <Shield size={16} className="text-blue-600" /> Change Password
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
        </div>
        {success ? (
          <div className="p-6 text-center space-y-3">
            <div className="text-3xl">✅</div>
            <p className="font-medium text-gray-900">Password changed successfully</p>
            <button onClick={onClose} className="w-full bg-blue-600 text-white py-2.5 rounded-lg text-sm font-medium hover:bg-blue-700">Done</button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            {error && <AuthAlert type="error" message={error} />}
            <AuthInput label="Current Password" type="password" value={form.currentPassword}
              onChange={e => set('currentPassword', e.target.value)} placeholder="••••••••"
              required autoComplete="current-password" />
            <AuthInput label="New Password" type="password" value={form.newPassword}
              onChange={e => set('newPassword', e.target.value)} placeholder="At least 8 characters"
              required autoComplete="new-password" />
            <AuthInput label="Confirm New Password" type="password" value={form.confirmPassword}
              onChange={e => set('confirmPassword', e.target.value)} placeholder="••••••••"
              required autoComplete="new-password" />
            <div className="flex gap-3 pt-1">
              <button type="button" onClick={onClose} className="flex-1 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50">Cancel</button>
              <button type="submit" disabled={loading} className="flex-1 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-60">
                {loading ? 'Saving…' : 'Update Password'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// AUDIT TRAIL VIEW
// ============================================================================

const ACTION_BADGE = {
  CREATE:  { bg: 'bg-green-100',  text: 'text-green-800',  label: 'Create' },
  UPDATE:  { bg: 'bg-blue-100',   text: 'text-blue-800',   label: 'Update' },
  DELETE:  { bg: 'bg-red-100',    text: 'text-red-800',    label: 'Delete' },
  LOGIN:   { bg: 'bg-purple-100', text: 'text-purple-800', label: 'Login' },
  LOGOUT:  { bg: 'bg-gray-100',   text: 'text-gray-700',   label: 'Logout' },
  VIEW:    { bg: 'bg-sky-100',    text: 'text-sky-800',    label: 'View' },
  READ:    { bg: 'bg-sky-100',    text: 'text-sky-800',    label: 'Read' },
  EXPORT:  { bg: 'bg-amber-100',  text: 'text-amber-800',  label: 'Export' },
  IMPORT:  { bg: 'bg-teal-100',   text: 'text-teal-800',   label: 'Import' },
  APPROVE: { bg: 'bg-emerald-100',text: 'text-emerald-800',label: 'Approve' },
  REJECT:  { bg: 'bg-orange-100', text: 'text-orange-800', label: 'Reject' },
};

const MODULE_BADGE = {
  plantrack:    { bg: 'bg-indigo-100',  text: 'text-indigo-800' },
  devops:       { bg: 'bg-emerald-100', text: 'text-emerald-800' },
  quality:      { bg: 'bg-pink-100',    text: 'text-pink-800' },
  timeboard:    { bg: 'bg-orange-100',  text: 'text-orange-800' },
  leaveboard:   { bg: 'bg-teal-100',    text: 'text-teal-800' },
  system:       { bg: 'bg-gray-100',    text: 'text-gray-700' },
  dashboard:    { bg: 'bg-violet-100',  text: 'text-violet-800' },
  dataops:      { bg: 'bg-cyan-100',    text: 'text-cyan-800' },
  learntrack:   { bg: 'bg-rose-100',    text: 'text-rose-800' },
  quicktrack:   { bg: 'bg-yellow-100',  text: 'text-yellow-800' },
  vendorevents: { bg: 'bg-fuchsia-100', text: 'text-fuchsia-800' },
};

const AuditActionBadge = ({ action }) => {
  const a = (action || '').toUpperCase();
  const style = ACTION_BADGE[a] || { bg: 'bg-yellow-100', text: 'text-yellow-800', label: action };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${style.bg} ${style.text}`}>
      {style.label || action}
    </span>
  );
};

const AuditModuleBadge = ({ module }) => {
  const key = (module || '').toLowerCase();
  const style = MODULE_BADGE[key] || { bg: 'bg-blue-100', text: 'text-blue-800' };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${style.bg} ${style.text} capitalize`}>
      {module || 'system'}
    </span>
  );
};

const AuditTrailView = ({ logs, stats, filters, onFilterChange, onLoadMore, hasMore, loading }) => {
  const [localFilters, setLocalFilters] = useState({ ...filters });

  const handleFilter = (key, value) => {
    const updated = { ...localFilters, [key]: value };
    setLocalFilters(updated);
  };

  const applyFilters = () => {
    onFilterChange({ ...localFilters, offset: 0 });
  };

  const resetFilters = () => {
    const empty = { module: '', action: '', entityType: '', dateFrom: '', dateTo: '', search: '' };
    setLocalFilters(empty);
    onFilterChange({ ...empty, offset: 0 });
  };

  const formatTime = (ts) => {
    if (!ts) return '—';
    try {
      const d = new Date(ts.endsWith('Z') ? ts : ts + 'Z');
      return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false });
    } catch { return ts; }
  };

  const maxModuleCount = stats?.byModule?.length ? Math.max(...stats.byModule.map(m => m.count)) : 1;

  const knownModules = ['plantrack', 'devops', 'quality', 'timeboard', 'leaveboard', 'meetingboard', 'dataops', 'learntrack', 'quicktrack', 'vendorevents', 'changerequest', 'marketingworkflow', 'troubleshootinglogs', 'deployments', 'system', 'dashboard'];
  const knownActions = ['CREATE', 'UPDATE', 'DELETE', 'LOGIN', 'LOGOUT', 'VIEW', 'READ', 'EXPORT', 'IMPORT', 'APPROVE', 'REJECT'];

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Shield size={24} className="text-blue-600" /> Audit Trail
          </h1>
          <p className="text-gray-500 text-sm mt-1">Comprehensive system-wide activity logging and monitoring</p>
        </div>
        <button onClick={applyFilters} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700">
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Total Events', value: (stats?.total || 0).toLocaleString(), icon: <Activity size={18} className="text-blue-500" />, color: 'border-blue-200' },
          { label: "Today's Events", value: (stats?.today || 0).toLocaleString(), icon: <Clock size={18} className="text-green-500" />, color: 'border-green-200' },
          { label: 'Unique Users', value: (stats?.uniqueUsers || 0).toLocaleString(), icon: <Users size={18} className="text-purple-500" />, color: 'border-purple-200' },
          { label: 'Last 7 Days', value: (stats?.lastWeek || 0).toLocaleString(), icon: <BarChart3 size={18} className="text-orange-500" />, color: 'border-orange-200' },
        ].map(card => (
          <div key={card.label} className={`bg-white rounded-lg shadow-sm border-l-4 ${card.color} p-4`}>
            <div className="flex items-center justify-between">
              <p className="text-xs text-gray-500 font-medium">{card.label}</p>
              {card.icon}
            </div>
            <p className="text-2xl font-bold text-gray-900 mt-1">{card.value}</p>
          </div>
        ))}
      </div>

      {/* Module Breakdown + Action Breakdown */}
      {stats && (stats.byModule?.length > 0 || stats.byAction?.length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {/* By Module */}
          {stats.byModule?.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm p-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Events by Module</h3>
              <div className="space-y-2">
                {stats.byModule.map(m => (
                  <div key={m.module} className="flex items-center gap-2">
                    <span className="text-xs text-gray-600 w-24 truncate capitalize">{m.module || 'system'}</span>
                    <div className="flex-1 bg-gray-100 rounded-full h-2">
                      <div
                        className="bg-blue-500 h-2 rounded-full"
                        style={{ width: `${Math.round((m.count / maxModuleCount) * 100)}%` }}
                      />
                    </div>
                    <span className="text-xs font-medium text-gray-700 w-8 text-right">{m.count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {/* By Action */}
          {stats.byAction?.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm p-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Events by Action</h3>
              <div className="flex flex-wrap gap-2">
                {stats.byAction.map(a => (
                  <div key={a.action_type} className="flex items-center gap-1">
                    <AuditActionBadge action={a.action_type} />
                    <span className="text-xs text-gray-500">{a.count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Filter Bar */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          <div className="relative">
            <Search size={14} className="absolute left-2 top-2.5 text-gray-400" />
            <input
              type="text"
              placeholder="Search..."
              value={localFilters.search || ''}
              onChange={e => handleFilter('search', e.target.value)}
              onKeyDown={e => e.key === 'Enter' && applyFilters()}
              className="w-full pl-7 pr-2 py-2 border border-gray-200 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
            />
          </div>
          <select
            value={localFilters.module || ''}
            onChange={e => handleFilter('module', e.target.value)}
            className="border border-gray-200 rounded px-2 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
          >
            <option value="">All Modules</option>
            {knownModules.map(m => <option key={m} value={m} className="capitalize">{m}</option>)}
          </select>
          <select
            value={localFilters.action || ''}
            onChange={e => handleFilter('action', e.target.value)}
            className="border border-gray-200 rounded px-2 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
          >
            <option value="">All Actions</option>
            {knownActions.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
          <input
            type="date"
            value={localFilters.dateFrom || ''}
            onChange={e => handleFilter('dateFrom', e.target.value)}
            className="border border-gray-200 rounded px-2 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
            title="From date"
          />
          <input
            type="date"
            value={localFilters.dateTo || ''}
            onChange={e => handleFilter('dateTo', e.target.value)}
            className="border border-gray-200 rounded px-2 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
            title="To date"
          />
          <div className="flex gap-2">
            <button onClick={applyFilters} className="flex-1 bg-blue-600 text-white rounded px-3 py-2 text-xs hover:bg-blue-700">Apply</button>
            <button onClick={resetFilters} className="flex-1 bg-gray-100 text-gray-600 rounded px-3 py-2 text-xs hover:bg-gray-200">Reset</button>
          </div>
        </div>
      </div>

      {/* Log Table */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-400 text-sm">Loading audit logs...</div>
        ) : logs.length === 0 ? (
          <div className="p-8 text-center text-gray-400">
            <Shield size={32} className="mx-auto mb-2 opacity-30" />
            <p className="text-sm">No audit events found matching your filters.</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left px-4 py-3 text-gray-600 font-semibold w-36">Timestamp</th>
                    <th className="text-left px-4 py-3 text-gray-600 font-semibold">User</th>
                    <th className="text-left px-4 py-3 text-gray-600 font-semibold">Action</th>
                    <th className="text-left px-4 py-3 text-gray-600 font-semibold">Module</th>
                    <th className="text-left px-4 py-3 text-gray-600 font-semibold">Entity</th>
                    <th className="text-left px-4 py-3 text-gray-600 font-semibold">Details</th>
                    <th className="text-left px-4 py-3 text-gray-600 font-semibold w-24">IP</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {logs.map((log, i) => (
                    <tr key={log.id || i} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 text-gray-500 whitespace-nowrap font-mono text-xs">
                        {formatTime(log.created_at)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-800">{log.user_name || '—'}</div>
                        {log.user_id && <div className="text-gray-400 text-xs">ID: {log.user_id}</div>}
                      </td>
                      <td className="px-4 py-3">
                        <AuditActionBadge action={log.action_type} />
                      </td>
                      <td className="px-4 py-3">
                        <AuditModuleBadge module={log.module} />
                      </td>
                      <td className="px-4 py-3">
                        {log.entity_name && <div className="font-medium text-gray-700">{log.entity_name}</div>}
                        {log.entity_type && <div className="text-gray-400">{log.entity_type}{log.entity_id ? ` #${log.entity_id}` : ''}</div>}
                      </td>
                      <td className="px-4 py-3 text-gray-600 max-w-xs truncate" title={log.details || ''}>
                        {log.details || '—'}
                      </td>
                      <td className="px-4 py-3 text-gray-400 font-mono whitespace-nowrap">
                        {log.ip_address && log.ip_address !== 'unknown' ? log.ip_address : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {hasMore && (
              <div className="p-4 text-center border-t border-gray-100">
                <button
                  onClick={onLoadMore}
                  className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200 font-medium"
                >
                  Load More Events
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// ---- Setup View (first-run admin creation wizard) ----
const SetupView = ({ onSetupComplete }) => {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ orgName: '', name: '', email: '', password: '', confirmPassword: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirmPassword) { setError('Passwords do not match'); return; }
    if (form.password.length < 8) { setError('Password must be at least 8 characters'); return; }
    setLoading(true); setError('');
    try {
      const res = await fetch('/api/auth/setup', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ orgName: form.orgName, name: form.name, email: form.email, password: form.password, confirmPassword: form.confirmPassword })
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Setup failed'); }
      else {
        localStorage.setItem('orbit_token', data.token);
        localStorage.setItem('orbit_user', JSON.stringify(data.user));
        onSetupComplete(data.token, data.user);
      }
    } catch { setError('Network error. Please try again.'); }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 to-indigo-800 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-8 text-white text-center">
          <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">🚀</span>
          </div>
          <h1 className="text-2xl font-bold">Welcome to ORBIT</h1>
          <p className="text-blue-100 mt-1 text-sm">Let's set up your workspace</p>
          <div className="flex items-center justify-center gap-2 mt-4">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${step >= 1 ? 'bg-white text-blue-600' : 'bg-white/30 text-white'}`}>1</div>
            <div className={`h-1 w-8 rounded ${step >= 2 ? 'bg-white' : 'bg-white/30'}`} />
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${step >= 2 ? 'bg-white text-blue-600' : 'bg-white/30 text-white'}`}>2</div>
          </div>
        </div>
        <div className="p-8">
          {step === 1 ? (
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Organization Setup</h2>
              <p className="text-gray-500 text-sm mb-6">Enter your organization name to get started.</p>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Organization Name *</label>
                <input type="text" value={form.orgName} onChange={e => setForm(f => ({ ...f, orgName: e.target.value }))}
                  placeholder="e.g. Acme Corp" autoFocus
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none" />
              </div>
              <button onClick={() => { if (!form.orgName.trim()) return; setStep(2); }} disabled={!form.orgName.trim()}
                className="w-full py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed">
                Continue
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Create Admin Account</h2>
              <p className="text-gray-500 text-sm mb-6">This will be the primary administrator account.</p>
              {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                  <input type="text" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required autoFocus
                    placeholder="John Doe" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
                  <input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} required
                    placeholder="admin@company.com" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Password *</label>
                  <input type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} required
                    placeholder="Minimum 8 characters" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password *</label>
                  <input type="password" value={form.confirmPassword} onChange={e => setForm(f => ({ ...f, confirmPassword: e.target.value }))} required
                    placeholder="Repeat password" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none" />
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <button type="button" onClick={() => setStep(1)} className="px-4 py-3 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 text-sm font-medium">Back</button>
                <button type="submit" disabled={loading}
                  className="flex-1 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2">
                  {loading ? 'Creating...' : 'Create Admin Account & Launch ORBIT'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// POLARIS MODULE
// ============================================================================

const POLARIS_STATUS_META = {
  dev:      { label: 'In Development',     color: '#2563eb', bg: 'rgba(37,99,235,0.10)' },
  qa:       { label: 'QA',                 color: '#db2777', bg: 'rgba(219,39,119,0.10)' },
  qaip:     { label: 'QA in Progress',     color: '#be185d', bg: 'rgba(190,24,93,0.10)' },
  readyuat: { label: 'Ready for UAT',      color: '#a855f7', bg: 'rgba(168,85,247,0.10)' },
  inuat:    { label: 'In UAT',             color: '#c026d3', bg: 'rgba(192,38,211,0.10)' },
  fordep:   { label: 'For Deploy',         color: '#65a30d', bg: 'rgba(101,163,13,0.10)' },
  dep:      { label: 'Deployed',           color: '#059669', bg: 'rgba(5,150,105,0.10)' },
  partial:  { label: 'Partially Deployed', color: '#7c3aed', bg: 'rgba(124,58,237,0.10)' },
  hypercare:{ label: 'Hypercare',          color: '#14b8a6', bg: 'rgba(20,184,166,0.10)' },
  hold:     { label: 'On Hold',            color: '#d97706', bg: 'rgba(217,119,6,0.10)' },
  track:    { label: 'On Track',           color: '#0891b2', bg: 'rgba(8,145,178,0.10)' },
  kick:     { label: 'To Kickoff',         color: '#ea580c', bg: 'rgba(234,88,12,0.10)' },
  ns:       { label: 'Not Started',        color: '#64748b', bg: 'rgba(100,116,139,0.10)' },
  wait:     { label: 'Dependency',         color: '#ca8a04', bg: 'rgba(202,138,4,0.10)' },
  blocked:  { label: 'Blocked',            color: '#dc2626', bg: 'rgba(220,38,38,0.10)' },
  parked:   { label: 'Parked',             color: '#475569', bg: 'rgba(71,85,105,0.10)' },
  cancel:   { label: 'Cancelled',          color: '#52525b', bg: 'rgba(82,82,91,0.10)' },
};

const POLARIS_BULLET_META = {
  normal: { color: '#334155', dot: '#94a3b8' },
  info:   { color: '#1d4ed8', dot: '#60a5fa' },
  warn:   { color: '#b45309', dot: '#f59e0b' },
  good:   { color: '#047857', dot: '#10b981' },
};

// Subsection labels are free-text. We auto-color the heading based on keywords
// in the label so common buckets (DataOps, DevOps, QA, Forth) get a consistent look.
const getPolarisSubsectionStyle = (label) => {
  const s = String(label || '').toLowerCase();
  if (s.includes('dataops')) return { color: '#0d9488' }; // teal
  if (s.includes('devops'))  return { color: '#7c3aed' }; // violet
  if (s.includes('qa'))      return { color: '#db2777' }; // pink
  if (s.includes('forth'))   return { color: '#d97706' }; // amber
  if (s.includes('design'))  return { color: '#0891b2' }; // cyan
  if (s.includes('product')) return { color: '#4f46e5' }; // indigo
  return { color: '#64748b' }; // slate (default)
};

const POLARIS_EMOJI_CATEGORIES = {
  Faces:   ['😀','😃','😄','😁','😆','😅','🤣','😂','🙂','😊','😇','🥳','😍','🤩','😘','😜','🤔','😐','🥶','😎','🤖','😱','😴','🥱'],
  Work:    ['✅','❌','⚠️','🚧','💡','📌','📍','📁','📂','📊','📈','📉','🗓️','🔔','💬','📧','📝','📋','📎','🔗','🔒','🔑','🔨','⚙️','🔧','🎯','🚀','💻','📱','🧪','🏢','🛠️','🧭'],
  Hands:   ['👍','👎','👌','✌️','🤞','🤝','👈','👉','👆','👇','👋','✋','🤚','🙏','✍️','💪'],
  Awards:  ['🏆','🥇','🥈','🥉','🏅','🎖️','🎉','🎊','🎁','⭐','🌟','💫','✨'],
  Nature:  ['🌍','🌎','🌏','🌐','🌳','🌲','🌿','☘️','🌷','🌸','🌼','⚡','🔥','❄️','🌊','☁️','☀️','🌧️'],
};

const PolarisEmojiPicker = ({ onSelect, onClose }) => {
  const [cat, setCat] = useState('Faces');
  const [search, setSearch] = useState('');
  const ref = useRef(null);
  useEffect(() => {
    const onMouse = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', onMouse);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onMouse); document.removeEventListener('keydown', onKey); };
  }, [onClose]);
  const all = Object.values(POLARIS_EMOJI_CATEGORIES).flat();
  const list = search ? all : POLARIS_EMOJI_CATEGORIES[cat];
  return (
    <div ref={ref} className="absolute z-30 top-full left-0 mt-1 w-72 bg-white rounded-lg shadow-xl p-2" style={{ border: '1px solid #e2e8f0' }} onClick={(e) => e.stopPropagation()}>
      <input autoFocus value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search…" className="w-full bg-slate-50 border border-slate-200 rounded px-2 py-1 text-xs mb-1.5" />
      <div className="flex gap-1 mb-1.5">
        {Object.entries(POLARIS_EMOJI_CATEGORIES).map(([k, arr]) => (
          <button key={k} onClick={() => { setCat(k); setSearch(''); }} title={k}
            className={`text-base px-1.5 py-0.5 rounded ${cat === k && !search ? 'bg-indigo-100 ring-1 ring-indigo-300' : 'hover:bg-slate-100'}`}>
            {arr[0]}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-8 gap-0.5 max-h-44 overflow-y-auto">
        {list.map((em, i) => (
          <button key={`${em}-${i}`} onClick={() => { onSelect(em); onClose(); }} className="w-8 h-8 hover:bg-slate-100 rounded text-lg leading-none">
            {em}
          </button>
        ))}
      </div>
    </div>
  );
};

const PolarisCompass = ({ size = 32 }) => (
  <svg width={size} height={size} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <defs>
      <linearGradient id="polaris-grad" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#818cf8" />
        <stop offset="100%" stopColor="#a78bfa" />
      </linearGradient>
    </defs>
    <circle cx="32" cy="32" r="28" stroke="url(#polaris-grad)" strokeWidth="2" fill="rgba(124,58,237,0.08)" />
    <path d="M32 8 L36 30 L58 32 L36 34 L32 56 L28 34 L6 32 L28 30 Z" fill="url(#polaris-grad)" />
    <circle cx="32" cy="32" r="3" fill="#fff" />
  </svg>
);

const PolarisStatusBadge = ({ status }) => {
  const meta = POLARIS_STATUS_META[status] || POLARIS_STATUS_META.dev;
  return (
    <span
      className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold"
      style={{ color: meta.color, backgroundColor: meta.bg, border: `1px solid ${meta.color}44` }}
    >
      {meta.label}
    </span>
  );
};

const PolarisScopeTag = ({ scope }) => (
  <span
    className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider"
    style={
      scope === 'external'
        ? { color: '#e11d48', backgroundColor: 'rgba(244,63,94,0.10)', border: '1px solid #fda4af' }
        : { color: '#4f46e5', backgroundColor: 'rgba(99,102,241,0.10)', border: '1px solid #a5b4fc' }
    }
  >
    {scope === 'external' ? 'External' : 'Internal'}
  </span>
);

const PolarisFilterChip = ({ active, onClick, children, color }) => (
  <button
    onClick={onClick}
    className="px-3 py-1.5 rounded-full text-xs font-semibold transition-all border"
    style={{
      borderColor: active ? color : '#e2e8f0',
      backgroundColor: active ? `${color}1A` : '#ffffff',
      color: active ? color : '#64748b',
    }}
  >
    {children}
  </button>
);

const PolarisHighlightsCard = ({ highlights, meta, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(() => Array.isArray(highlights) && highlights.length ? highlights : [{ text: '', children: [] }]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!editing) setDraft(Array.isArray(highlights) && highlights.length ? highlights : [{ text: '', children: [] }]);
  }, [highlights, editing]);

  const updateBullet = (idx, text) => setDraft(d => d.map((b, i) => i === idx ? { ...b, text } : b));
  const addBullet = () => setDraft(d => [...d, { text: '', children: [] }]);
  const removeBullet = (idx) => setDraft(d => d.filter((_, i) => i !== idx));
  const addSub = (idx) => setDraft(d => d.map((b, i) => i === idx ? { ...b, children: [...(b.children || []), { text: '' }] } : b));
  const updateSub = (idx, subIdx, text) => setDraft(d => d.map((b, i) => i === idx ? { ...b, children: b.children.map((c, j) => j === subIdx ? { ...c, text } : c) } : b));
  const removeSub = (idx, subIdx) => setDraft(d => d.map((b, i) => i === idx ? { ...b, children: b.children.filter((_, j) => j !== subIdx) } : b));

  const save = async () => {
    setSaving(true);
    const cleaned = draft.filter(b => b.text && b.text.trim()).map(b => ({
      text: b.text.trim(),
      children: (b.children || []).filter(c => c.text && c.text.trim()).map(c => ({ text: c.text.trim() })),
    }));
    await onSave(cleaned);
    setSaving(false);
    setEditing(false);
  };

  return (
    <div className="rounded-2xl p-5 mb-6 shadow-sm" style={{ background: 'linear-gradient(135deg, #eef2ff, #f5f3ff)', border: '1px solid #e0e7ff' }}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-lg">✨</span>
          <h3 className="text-sm font-bold uppercase tracking-wider text-indigo-700">Highlights</h3>
          {meta?.updatedBy && <span className="text-xs text-slate-500">— last edit by {meta.updatedBy}</span>}
        </div>
        {!editing ? (
          <button onClick={() => setEditing(true)} className="text-xs text-indigo-600 hover:text-indigo-700 font-medium">Edit</button>
        ) : (
          <div className="flex gap-2">
            <button onClick={() => setEditing(false)} className="text-xs text-slate-500 hover:text-slate-700">Cancel</button>
            <button onClick={save} disabled={saving} className="text-xs font-semibold px-3 py-1 rounded bg-indigo-600 hover:bg-indigo-700 text-white disabled:opacity-50">{saving ? 'Saving…' : 'Save'}</button>
          </div>
        )}
      </div>
      {!editing ? (
        <ul className="space-y-1.5">
          {(highlights || []).length === 0 && <li className="text-sm text-slate-400 italic">No highlights yet — click Edit to add some.</li>}
          {(highlights || []).map((b, i) => (
            <li key={i} className="text-sm text-slate-800">
              <div className="flex gap-2"><span className="text-indigo-500">•</span><span>{b.text}</span></div>
              {(b.children || []).length > 0 && (
                <ul className="ml-6 mt-1 space-y-1">
                  {(b.children || []).map((c, j) => (
                    <li key={j} className="text-xs text-slate-600 flex gap-2"><span className="text-violet-500">◦</span><span>{c.text}</span></li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <div className="space-y-2">
          {draft.map((b, i) => (
            <div key={i} className="space-y-1">
              <div className="flex gap-2 items-center">
                <span className="text-indigo-500">•</span>
                <input value={b.text || ''} onChange={(e) => updateBullet(i, e.target.value)} placeholder="Highlight…" className="flex-1 bg-white border border-slate-300 rounded px-2 py-1 text-sm text-slate-800" />
                <button onClick={() => addSub(i)} className="text-xs text-slate-500 hover:text-indigo-600">+sub</button>
                <button onClick={() => removeBullet(i)} className="text-xs text-rose-600 hover:text-rose-700">×</button>
              </div>
              {(b.children || []).map((c, j) => (
                <div key={j} className="flex gap-2 items-center ml-6">
                  <span className="text-violet-500">◦</span>
                  <input value={c.text || ''} onChange={(e) => updateSub(i, j, e.target.value)} placeholder="Sub-bullet…" className="flex-1 bg-white border border-slate-300 rounded px-2 py-1 text-xs text-slate-800" />
                  <button onClick={() => removeSub(i, j)} className="text-xs text-rose-600 hover:text-rose-700">×</button>
                </div>
              ))}
            </div>
          ))}
          <button onClick={addBullet} className="text-xs text-indigo-600 hover:text-indigo-700 font-medium">+ Add highlight</button>
        </div>
      )}
    </div>
  );
};

const PolarisLiveSection = ({ title, accent, items, emptyHint }) => {
  if (!items || items.length === 0) return null;
  return (
    <div className="rounded-lg p-3" style={{ background: `${accent}0D`, border: `1px solid ${accent}33` }}>
      <h5 className="text-[11px] font-bold uppercase tracking-wider mb-2" style={{ color: accent }}>{title}</h5>
      <ul className="space-y-1">
        {items.map((it, i) => (
          <li key={i} className="text-sm flex items-start gap-2">
            <span className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: accent }} />
            <span className="text-slate-700">{it}</span>
          </li>
        ))}
        {items.length === 0 && emptyHint && <li className="text-xs text-slate-400 italic">{emptyHint}</li>}
      </ul>
    </div>
  );
};

const PolarisLiveData = ({ projectId, live }) => {
  if (!live) return null;
  const bugs = live.bugs || {};
  const tasks = live.tasks || {};
  const milestone = live.nextMilestone;
  const updates = live.latestUpdates || [];

  const bucketAssignees = (bucket) =>
    Object.values(bugs.byAssignee || {})
      .filter(a => a.bucket === bucket)
      .sort((a, b) => b.count - a.count);

  const ROLE_ACCENTS = { devops: '#2563eb', dataops: '#7c3aed', qa: '#db2777', other: '#64748b' };
  const renderRoleSection = (label, bucket) => {
    const assignees = bucketAssignees(bucket);
    if (assignees.length === 0) return null;
    return (
      <PolarisLiveSection
        key={bucket}
        title={label}
        accent={ROLE_ACCENTS[bucket]}
        items={assignees.map(a => `${a.name}: ${a.count} active bug${a.count === 1 ? '' : 's'}`)}
      />
    );
  };

  const summaryLine =
    bugs.total > 0
      ? `${bugs.total} active bug${bugs.total === 1 ? '' : 's'}`
      : null;

  // Task-completion and next-milestone lines were removed from this card by request —
  // that detail lives in PlanTrack itself. `tasks` / `milestone` still arrive in the
  // payload for other consumers, so nothing needs changing server-side.
  const hasAnything = summaryLine || updates.length > 0
    || ['devops','dataops','qa','other'].some(b => bucketAssignees(b).length > 0);

  if (!hasAnything) return null;

  return (
    <div className="rounded-lg p-3 space-y-2" style={{ background: '#f8fafc', border: '1px dashed #c7d2fe' }}>
      <div className="flex items-center gap-2">
        <span className="text-[10px] font-bold uppercase tracking-widest text-indigo-600">Live from PlanTrack</span>
        <span className="text-[10px] text-slate-400 font-mono">{projectId}</span>
      </div>
      {summaryLine && (
        <ul className="space-y-1 pl-1">
          <li className="text-sm text-slate-700 flex gap-2"><span className="text-amber-600">⚠</span>{summaryLine}</li>
        </ul>
      )}
      {renderRoleSection('DataOps', 'dataops')}
      {renderRoleSection('DevOps', 'devops')}
      {renderRoleSection('QA', 'qa')}
      {/* Anything not in a team bucket (admin/manager assignees, unknown or unassigned names)
          still counts toward the total above — render it so the count always reconciles. */}
      {renderRoleSection('Other / Unassigned', 'other')}
      {updates.length > 0 && (
        <div className="rounded-lg p-3" style={{ background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.25)' }}>
          <h5 className="text-[11px] font-bold uppercase tracking-wider mb-2 text-emerald-700">Recent Updates</h5>
          <ul className="space-y-1.5">
            {updates.map((u, i) => (
              <li key={i} className="text-xs text-slate-700">
                <span className="text-slate-400 mr-2">{new Date(u.createdAt).toLocaleDateString()}</span>
                <span className="text-slate-800">{u.content}</span>
                {u.userName && <span className="text-slate-400 ml-1">— {u.userName}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

// ---- Inline edit helper: click-to-edit text/textarea/date ----
// `html`: the value is rich-text HTML (from MiniWysiwyg elsewhere, e.g. the Polaris note
// field) — the collapsed display renders it as sanitized markup instead of a raw string.
// The inline quick-edit itself stays a plain textarea even in that mode (the full "Edit
// Project" form is the place for careful formatting); this only fixes the read view so
// formatting doesn't show up as literal HTML tags.
const PolarisInlineEdit = ({ value, onSave, placeholder, kind = 'text', displayClass = '', editClass = '', stopPropagation = true, html = false }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');
  useEffect(() => { if (!editing) setDraft(value || ''); }, [value, editing]);

  const commit = () => {
    setEditing(false);
    const trimmed = (draft ?? '').trim();
    if (trimmed !== (value || '').trim()) onSave(trimmed);
  };
  const cancel = () => { setDraft(value || ''); setEditing(false); };
  const startEdit = (e) => { if (stopPropagation) e.stopPropagation(); setEditing(true); };

  if (!editing) {
    const isEmpty = !value || !String(stripHtml(value)).trim();
    if (html && !isEmpty) {
      return (
        <span
          onClick={startEdit}
          className={`${displayClass} cursor-text rounded px-1 -mx-1 hover:bg-slate-100 transition-colors inline-block prose prose-sm max-w-none [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:m-0`}
          title="Click to edit"
          dangerouslySetInnerHTML={{ __html: sanitizeBugHtml(value) }}
        />
      );
    }
    return (
      <span
        onClick={startEdit}
        className={`${displayClass} cursor-text rounded px-1 -mx-1 hover:bg-slate-100 transition-colors ${isEmpty ? 'text-slate-400 italic' : ''}`}
        title="Click to edit"
      >
        {isEmpty ? (placeholder || '—') : value}
      </span>
    );
  }

  const sharedProps = {
    autoFocus: true,
    value: draft,
    onChange: (e) => setDraft(e.target.value),
    onBlur: commit,
    onClick: (e) => stopPropagation && e.stopPropagation(),
    onKeyDown: (e) => {
      if (e.key === 'Escape') { e.preventDefault(); cancel(); }
      if (kind !== 'textarea' && e.key === 'Enter') { e.preventDefault(); commit(); }
    },
  };
  const base = 'bg-white border border-indigo-400 rounded px-1.5 py-0.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-200';
  if (kind === 'textarea') return <textarea {...sharedProps} rows={2} className={`${base} w-full ${editClass}`} />;
  if (kind === 'date') return <input type="date" {...sharedProps} className={`${base} ${editClass}`} />;
  return <input type="text" {...sharedProps} className={`${base} ${editClass}`} />;
};

// ---- Status popover: click status badge -> 16-option picker ----
const PolarisStatusPopover = ({ value, onChange, onClose }) => {
  const ref = useRef(null);
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    const esc = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', handler);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', handler); document.removeEventListener('keydown', esc); };
  }, [onClose]);
  return (
    <div
      ref={ref}
      className="absolute z-20 mt-1 right-0 rounded-lg bg-white shadow-lg p-2 grid grid-cols-2 gap-1 min-w-[280px]"
      style={{ border: '1px solid #e2e8f0' }}
      onClick={(e) => e.stopPropagation()}
    >
      {Object.entries(POLARIS_STATUS_META).map(([k, meta]) => (
        <button
          key={k}
          onClick={() => { onChange(k); onClose(); }}
          className={`flex items-center gap-2 px-2 py-1.5 rounded text-left text-xs hover:bg-slate-50 ${k === value ? 'bg-indigo-50 ring-1 ring-indigo-300' : ''}`}
        >
          <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: meta.color }} />
          <span className="text-slate-700 truncate">{meta.label}</span>
        </button>
      ))}
    </div>
  );
};

// ---- Archive confirm modal: required reason ----
const PolarisArchiveConfirmModal = ({ project, onConfirm, onCancel }) => {
  const [reason, setReason] = useState('');
  const meta = POLARIS_STATUS_META[project?.status] || POLARIS_STATUS_META.dev;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(15,23,42,0.45)' }}>
      <div className="w-full max-w-md rounded-2xl p-6 bg-white shadow-xl" style={{ border: '1px solid #e2e8f0' }}>
        <h2 className="text-lg font-bold text-slate-900 mb-1">📦 Archive Project</h2>
        <p className="text-xs text-slate-500 mb-3">Hides the project from Polaris Active view. PlanTrack is not affected. A reason is required for audit.</p>
        <div className="mb-3 p-3 rounded-lg bg-slate-50 border border-slate-200">
          <div className="text-xs uppercase font-bold text-slate-500 mb-1">Project</div>
          <div className="text-sm font-semibold text-slate-900">{project?.emoji} {project?.name}</div>
          <div className="flex items-center gap-2 mt-2">
            <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full" style={{ color: meta.color, backgroundColor: meta.bg, border: `1px solid ${meta.color}44` }}>{meta.label}</span>
            <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded text-slate-500 bg-white border border-slate-200">{project?.scope}</span>
          </div>
        </div>
        <label className="text-xs font-bold text-slate-700 mb-1 block">
          Reason for archiving <span className="text-rose-600">*</span>
        </label>
        <textarea
          autoFocus
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. Project completed, stakeholder decision, superseded…"
          className="w-full bg-white border border-slate-300 rounded px-2 py-1.5 text-sm text-slate-900"
        />
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onCancel} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-900">Cancel</button>
          <button onClick={() => onConfirm(reason.trim())} disabled={!reason.trim()} className="px-4 py-2 text-sm font-semibold rounded bg-amber-500 hover:bg-amber-600 text-white disabled:opacity-50">📦 Confirm Archive</button>
        </div>
      </div>
    </div>
  );
};

// ---- Archive detail modal: read-only view of archived project ----
const PolarisArchiveDetailModal = ({ project, onRestore, onDelete, onClose }) => {
  if (!project) return null;
  const meta = POLARIS_STATUS_META[project.status] || POLARIS_STATUS_META.dev;
  const bullets = project.bullets || [];
  const mainBullets = bullets.filter(b => !b.subsection);
  // Dynamic subsection grouping
  const groupsMap = new Map();
  for (const b of bullets) {
    if (!b.subsection) continue;
    if (!groupsMap.has(b.subsection)) groupsMap.set(b.subsection, []);
    groupsMap.get(b.subsection).push(b);
  }
  const subBuckets = Array.from(groupsMap.entries()).map(([label, items]) => ({ label, items }));

  const renderBullet = (b) => {
    const bm = POLARIS_BULLET_META[b.bulletType] || POLARIS_BULLET_META.normal;
    return (
      <li key={b.id} className="text-sm flex items-start gap-2">
        <span className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: bm.dot }} />
        <span style={{ color: bm.color }}>{b.text}</span>
      </li>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(15,23,42,0.45)' }}>
      <div className="w-full max-w-2xl rounded-2xl bg-white shadow-xl flex flex-col max-h-[90vh]" style={{ border: '1px solid #e2e8f0' }}>
        <div className="px-6 pt-5 pb-3 border-b border-slate-200 flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-2xl">{project.emoji || '🚀'}</span>
              <h2 className="text-lg font-bold text-slate-900">{project.name}</h2>
            </div>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full" style={{ color: meta.color, backgroundColor: meta.bg, border: `1px solid ${meta.color}44` }}>{meta.label}</span>
              <PolarisScopeTag scope={project.scope} />
              {project.archivedAt && <span className="text-[10px] text-slate-500 bg-slate-50 border border-slate-200 px-2 py-0.5 rounded">Archived {new Date(project.archivedAt).toLocaleDateString()}</span>}
              <span className="text-[10px] font-mono text-slate-400">{project.id}</span>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-900 text-xl leading-none">×</button>
        </div>

        <div className="px-6 py-4 overflow-y-auto flex-1 space-y-3">
          {project.archiveReason && (
            <div className="rounded-lg p-3" style={{ background: '#fffbeb', border: '1px solid #fde68a' }}>
              <div className="text-[10px] font-bold uppercase text-amber-700 mb-1">Archive reason</div>
              <div className="text-sm text-amber-900 italic">{project.archiveReason}</div>
            </div>
          )}
          {project.uatDate && (
            <div className="inline-flex items-center gap-2 text-xs px-2 py-1 rounded" style={{ background: 'rgba(59,130,246,0.10)', color: '#1d4ed8', border: '1px solid #bfdbfe' }}>
              🎯 Target UAT: <strong>{project.uatDate}</strong>
            </div>
          )}
          {project.note && (
            <div className="px-3 py-2 rounded text-sm italic prose prose-sm max-w-none [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5" style={{ background: '#eff6ff', borderLeft: '3px solid #60a5fa', color: '#1e40af' }} dangerouslySetInnerHTML={{ __html: sanitizeBugHtml(project.note) }} />
          )}
          {project.dep && (
            <div className="px-3 py-2 rounded text-sm prose prose-sm max-w-none [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5" style={{ background: '#fffbeb', borderLeft: '3px solid #f59e0b', color: '#92400e' }}>
              <span className="font-semibold text-amber-700 mr-2">Dep:</span>
              <span dangerouslySetInnerHTML={{ __html: sanitizeBugHtml(project.dep) }} />
            </div>
          )}
          {mainBullets.length > 0 && (
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">Updates</div>
              <ul className="space-y-1">{mainBullets.map(renderBullet)}</ul>
            </div>
          )}
          {subBuckets.map(s => {
            const style = getPolarisSubsectionStyle(s.label);
            return (
              <div key={s.label} className="rounded-lg p-3 bg-slate-50 border border-slate-200">
                <div className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: style.color }}>{s.label}</div>
                <ul className="space-y-1">{s.items.map(renderBullet)}</ul>
              </div>
            );
          })}
          {!mainBullets.length && subBuckets.length === 0 && !project.note && !project.dep && (
            <div className="text-center text-sm text-slate-400 italic py-6">No details recorded.</div>
          )}
        </div>

        <div className="px-6 py-3 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-900">Close</button>
          <button onClick={onDelete} className="px-4 py-2 text-sm font-semibold rounded bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200">Delete</button>
          <button onClick={onRestore} className="px-4 py-2 text-sm font-semibold rounded bg-emerald-600 hover:bg-emerald-700 text-white">↻ Restore</button>
        </div>
      </div>
    </div>
  );
};

const PolarisProjectCard = ({
  project, index, total, onToggleCollapse, onEditProject, onDeleteProject, onArchive,
  onAddBullet, onEditBullet, onDeleteBullet, onUpdateProject, onChangeStatus,
  onRenameSubsection, onDeleteSubsection,
  onDragStart, onDragOver, onDrop, onDragEnd, isDragging, isDragTarget,
}) => {
  const [adding, setAdding] = useState(null); // {subsection, bulletType}
  const [newText, setNewText] = useState('');
  const [editingBulletId, setEditingBulletId] = useState(null);
  const [editingBulletText, setEditingBulletText] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [addingSection, setAddingSection] = useState(false);
  const [newSectionName, setNewSectionName] = useState('');

  const collapsed = !!project.collapsed;
  const bullets = project.bullets || [];
  const mainBullets = bullets.filter(b => !b.subsection);
  // Dynamic subsection grouping — preserves insertion order from the bullets array
  const subBuckets = useMemo(() => {
    const groups = new Map();
    for (const b of bullets) {
      if (!b.subsection) continue;
      if (!groups.has(b.subsection)) groups.set(b.subsection, []);
      groups.get(b.subsection).push(b);
    }
    return Array.from(groups.entries()).map(([label, items]) => ({ label, items }));
  }, [bullets]);

  const renderBullet = (b) => {
    const meta = POLARIS_BULLET_META[b.bulletType] || POLARIS_BULLET_META.normal;
    const isEditing = editingBulletId === b.id;
    return (
      <li key={b.id} className="text-sm flex items-start gap-2 group">
        <span className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: meta.dot }} />
        {isEditing ? (
          <>
            <input
              autoFocus value={editingBulletText} onChange={(e) => setEditingBulletText(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { onEditBullet(b.id, { text: editingBulletText }); setEditingBulletId(null); } if (e.key === 'Escape') setEditingBulletId(null); }}
              className="flex-1 bg-white border border-slate-300 rounded px-2 py-0.5 text-sm text-slate-800"
            />
            <button onClick={() => { onEditBullet(b.id, { text: editingBulletText }); setEditingBulletId(null); }} className="text-xs text-emerald-600">✓</button>
            <button onClick={() => setEditingBulletId(null)} className="text-xs text-slate-500">×</button>
          </>
        ) : (
          <>
            <span className="flex-1" style={{ color: meta.color }}>{b.text}</span>
            <span className="opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
              <select
                value={b.bulletType}
                onChange={(e) => onEditBullet(b.id, { bulletType: e.target.value })}
                className="bg-white border border-slate-300 rounded text-[10px] text-slate-600 px-1"
              >
                <option value="normal">·</option>
                <option value="info">i</option>
                <option value="warn">!</option>
                <option value="good">✓</option>
              </select>
              <button onClick={() => { setEditingBulletId(b.id); setEditingBulletText(b.text || ''); }} className="text-xs text-slate-500 hover:text-indigo-600">edit</button>
              <button onClick={() => onDeleteBullet(b.id)} className="text-xs text-rose-600 hover:text-rose-700">×</button>
            </span>
          </>
        )}
      </li>
    );
  };

  const addBulletInline = async (subsection) => {
    if (!newText.trim()) { setAdding(null); return; }
    await onAddBullet({ subsection: subsection || null, bulletType: adding?.bulletType || 'normal', text: newText.trim() });
    setNewText('');
    setAdding(null);
  };

  const [showStatusPopover, setShowStatusPopover] = useState(false);
  const scopeBorder = project.scope === 'external' ? '#fb923c' : '#6366f1';
  const cardStyle = {
    border: '1px solid #e2e8f0',
    borderLeft: `3px solid ${scopeBorder}`,
    opacity: isDragging ? 0.4 : 1,
    boxShadow: isDragTarget ? '0 0 0 2px #818cf8' : undefined,
    transition: 'box-shadow .15s, opacity .15s',
  };

  return (
    <div
      className="rounded-xl mb-3 overflow-visible bg-white shadow-sm"
      style={cardStyle}
      onDragOver={onDragOver}
      onDrop={onDrop}
    >
      <div
        className="flex items-center gap-3 px-4 py-3"
        style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}
      >
        <span
          draggable
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
          className="cursor-grab active:cursor-grabbing text-slate-300 hover:text-indigo-500 px-1 select-none"
          title="Drag to reorder"
        >⋮⋮</span>
        <div className="text-xs font-bold text-slate-500 w-6 text-center">#{index + 1}</div>
        <button onClick={onToggleCollapse} className="text-slate-400 hover:text-slate-700">{collapsed ? '▶' : '▼'}</button>
        <div className="relative">
          <button
            onClick={(e) => { e.stopPropagation(); setShowEmojiPicker(v => !v); }}
            className="text-2xl px-1 rounded hover:bg-slate-100 transition-colors leading-none"
            title="Click to change emoji"
          >
            {project.emoji || '🚀'}
          </button>
          {showEmojiPicker && (
            <PolarisEmojiPicker
              onSelect={(em) => onUpdateProject({ emoji: em })}
              onClose={() => setShowEmojiPicker(false)}
            />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            {project.number && <span className="text-xs font-mono text-slate-400">{project.number}</span>}
            <PolarisInlineEdit
              value={project.name}
              onSave={(v) => v && v.trim() && onUpdateProject({ name: v.trim() })}
              displayClass="font-bold text-slate-900 truncate"
              editClass="font-bold min-w-[200px]"
            />
            <button
              onClick={(e) => { e.stopPropagation(); onUpdateProject({ scope: project.scope === 'external' ? 'internal' : 'external' }); }}
              title="Click to flip scope"
              className="cursor-pointer"
            >
              <PolarisScopeTag scope={project.scope} />
            </button>
            <div className="relative">
              <button onClick={(e) => { e.stopPropagation(); setShowStatusPopover(v => !v); }} title="Click to change status">
                <PolarisStatusBadge status={project.status} />
              </button>
              {showStatusPopover && (
                <PolarisStatusPopover
                  value={project.status}
                  onChange={(s) => onChangeStatus(s)}
                  onClose={() => setShowStatusPopover(false)}
                />
              )}
            </div>
            {project.uatDate ? (
              <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full" style={{ backgroundColor: 'rgba(59,130,246,0.10)', color: '#1d4ed8', border: '1px solid #bfdbfe' }}>
                UAT
                <PolarisInlineEdit
                  value={project.uatDate}
                  kind="date"
                  onSave={(v) => onUpdateProject({ uatDate: v || null })}
                  displayClass="font-semibold"
                />
                <button onClick={(e) => { e.stopPropagation(); onUpdateProject({ uatDate: null }); }} className="text-blue-400 hover:text-rose-600 ml-0.5" title="Remove UAT date">×</button>
              </span>
            ) : null}
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={onEditProject} title="Open full edit form (emoji, description)" className="text-xs text-slate-400 hover:text-indigo-600 px-2 py-1">⚙</button>
          <button onClick={onArchive} className="text-xs text-slate-500 hover:text-amber-600 px-2 py-1">{project.archived ? 'Restore' : 'Archive'}</button>
          <button onClick={onDeleteProject} className="text-xs text-rose-600 hover:text-rose-700 px-2 py-1">Delete</button>
        </div>
      </div>

      {!collapsed && (
        <div className="px-5 py-4 space-y-3">
          {project.note != null && (
            <div className="px-3 py-2 rounded text-sm italic relative group" style={{ background: '#eff6ff', borderLeft: '3px solid #60a5fa', color: '#1e40af' }}>
              <PolarisInlineEdit
                value={project.note}
                kind="textarea"
                onSave={(v) => onUpdateProject({ note: v || null })}
                placeholder="Click to add a note…"
                displayClass="block"
                html
              />
              <button onClick={() => onUpdateProject({ note: null })} className="absolute top-1 right-2 text-blue-400 hover:text-rose-600 opacity-0 group-hover:opacity-100 text-xs" title="Remove note">×</button>
            </div>
          )}
          {project.dep != null && (
            <div className="px-3 py-2 rounded text-sm relative group" style={{ background: '#fffbeb', borderLeft: '3px solid #f59e0b', color: '#92400e' }}>
              <span className="font-semibold text-amber-700 mr-2">Dep:</span>
              <PolarisInlineEdit
                value={project.dep}
                kind="textarea"
                onSave={(v) => onUpdateProject({ dep: v || null })}
                placeholder="Click to add a dependency…"
                html
              />
              <button onClick={() => onUpdateProject({ dep: null })} className="absolute top-1 right-2 text-amber-400 hover:text-rose-600 opacity-0 group-hover:opacity-100 text-xs" title="Remove dep">×</button>
            </div>
          )}
          <div className="flex gap-2 flex-wrap text-xs">
            {!project.uatDate && <button onClick={() => onUpdateProject({ uatDate: project.liveData?.nextMilestone?.dueDate || new Date().toISOString().slice(0,10) })} className="text-slate-500 hover:text-indigo-600 border border-dashed border-slate-300 px-2 py-0.5 rounded">+ UAT date</button>}
            {project.note == null && <button onClick={() => onUpdateProject({ note: '' })} className="text-slate-500 hover:text-indigo-600 border border-dashed border-slate-300 px-2 py-0.5 rounded">+ note</button>}
            {project.dep == null && <button onClick={() => onUpdateProject({ dep: '' })} className="text-slate-500 hover:text-amber-600 border border-dashed border-slate-300 px-2 py-0.5 rounded">+ dep note</button>}
          </div>

          <PolarisLiveData projectId={project.id} live={project.liveData} />

          {mainBullets.length > 0 && <ul className="space-y-1.5">{mainBullets.map(renderBullet)}</ul>}

          {adding && adding.subsection === null && (
            <div className="flex gap-2 items-center pl-3">
              <input autoFocus value={newText} onChange={(e) => setNewText(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') addBulletInline(null); if (e.key === 'Escape') setAdding(null); }} placeholder="New bullet…" className="flex-1 bg-white border border-slate-300 rounded px-2 py-1 text-sm text-slate-800" />
              <select value={adding.bulletType} onChange={(e) => setAdding({ ...adding, bulletType: e.target.value })} className="bg-white border border-slate-300 rounded text-xs text-slate-700 px-1 py-1">
                <option value="normal">Normal</option>
                <option value="info">Info</option>
                <option value="warn">Warn</option>
                <option value="good">Good</option>
              </select>
              <button onClick={() => addBulletInline(null)} className="text-xs text-emerald-600">Add</button>
              <button onClick={() => setAdding(null)} className="text-xs text-slate-500">Cancel</button>
            </div>
          )}
          {(!adding || adding.subsection !== null) && (
            <button onClick={() => { setAdding({ subsection: null, bulletType: 'normal' }); setNewText(''); }} className="text-xs text-indigo-600 hover:text-indigo-700 font-medium">+ Add bullet</button>
          )}

          {subBuckets.map(s => {
            const style = getPolarisSubsectionStyle(s.label);
            return (
              <div key={s.label} className="mt-3 pt-3 border-t border-slate-200">
                <div className="flex items-center justify-between mb-2 group/sec">
                  <PolarisInlineEdit
                    value={s.label}
                    onSave={(v) => v && v.trim() && v.trim() !== s.label && onRenameSubsection(s.label, v.trim())}
                    displayClass="text-xs font-bold uppercase tracking-wider"
                    editClass="text-xs font-bold uppercase tracking-wider min-w-[140px]"
                  />
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold uppercase tracking-wider" style={{ color: style.color }}>·</span>
                    <button
                      onClick={() => onDeleteSubsection(s.label)}
                      className="text-xs text-rose-500 hover:text-rose-700 opacity-0 group-hover/sec:opacity-100"
                      title="Delete this section and its bullets"
                    >Delete section</button>
                  </div>
                </div>
                <ul className="space-y-1.5" style={{ borderLeft: `2px solid ${style.color}33`, paddingLeft: '10px' }}>
                  {s.items.map(renderBullet)}
                </ul>
                {adding && adding.subsection === s.label && (
                  <div className="flex gap-2 items-center pl-3 mt-2">
                    <input autoFocus value={newText} onChange={(e) => setNewText(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') addBulletInline(s.label); if (e.key === 'Escape') setAdding(null); }} placeholder={`New ${s.label} note…`} className="flex-1 bg-white border border-slate-300 rounded px-2 py-1 text-sm text-slate-800" />
                    <select value={adding.bulletType} onChange={(e) => setAdding({ ...adding, bulletType: e.target.value })} className="bg-white border border-slate-300 rounded text-xs text-slate-700 px-1 py-1">
                      <option value="normal">Normal</option>
                      <option value="info">Info</option>
                      <option value="warn">Warn</option>
                      <option value="good">Good</option>
                    </select>
                    <button onClick={() => addBulletInline(s.label)} className="text-xs text-emerald-600">Add</button>
                    <button onClick={() => setAdding(null)} className="text-xs text-slate-500">Cancel</button>
                  </div>
                )}
                {(!adding || adding.subsection !== s.label) && (
                  <button onClick={() => { setAdding({ subsection: s.label, bulletType: 'normal' }); setNewText(''); }} className="text-xs text-indigo-600 hover:text-indigo-700 mt-1.5">+ bullet</button>
                )}
              </div>
            );
          })}

          <div className="pt-2 flex gap-2 items-center text-xs">
            {addingSection ? (
              <>
                <input
                  autoFocus
                  value={newSectionName}
                  onChange={(e) => setNewSectionName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && newSectionName.trim()) {
                      onAddBullet({ subsection: newSectionName.trim(), bulletType: 'normal', text: 'New item' });
                      setNewSectionName('');
                      setAddingSection(false);
                    }
                    if (e.key === 'Escape') { setNewSectionName(''); setAddingSection(false); }
                  }}
                  placeholder='Section name (e.g. "DataOps Joy", "QA Mylene", "Forth Tickets")'
                  className="flex-1 bg-white border border-slate-300 rounded px-2 py-1 text-sm text-slate-800"
                />
                <button
                  onClick={() => {
                    if (!newSectionName.trim()) { setAddingSection(false); return; }
                    onAddBullet({ subsection: newSectionName.trim(), bulletType: 'normal', text: 'New item' });
                    setNewSectionName('');
                    setAddingSection(false);
                  }}
                  className="text-xs text-emerald-600 hover:text-emerald-700 font-medium"
                >Add</button>
                <button onClick={() => { setNewSectionName(''); setAddingSection(false); }} className="text-xs text-slate-500">Cancel</button>
              </>
            ) : (
              <button onClick={() => setAddingSection(true)} className="text-slate-500 hover:text-indigo-600 border border-dashed border-slate-300 px-2 py-0.5 rounded">+ Add section</button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const PolarisProjectFormModal = ({ project, onClose, onSave }) => {
  const isEdit = !!project;
  const [form, setForm] = useState({
    name: project?.name || '',
    description: project?.description || '',
    emoji: project?.emoji || '🚀',
    scope: project?.scope || 'internal',
    status: project?.status || 'dev',
    uatDate: project?.uatDate || '',
    note: project?.note || '',
    dep: project?.dep || '',
  });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    await onSave(form);
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(15,23,42,0.45)' }}>
      <div className="w-full max-w-lg rounded-2xl p-6 max-h-[90vh] overflow-y-auto bg-white shadow-xl" style={{ border: '1px solid #e2e8f0' }}>
        <div className="mb-4">
          <h2 className="text-lg font-bold text-slate-900">{isEdit ? 'Edit Project' : 'New Project'}</h2>
          {isEdit && project?.id && <p className="text-xs text-slate-500 font-mono mt-1">{project.id} · synced with PlanTrack</p>}
          {!isEdit && <p className="text-xs text-slate-500 mt-1">Creates a PlanTrack project and pins it to Polaris.</p>}
        </div>
        <div className="space-y-3">
          <div className="grid grid-cols-4 gap-3">
            <div>
              <label className="text-xs text-slate-600 mb-1 block">Emoji</label>
              <input value={form.emoji} onChange={(e) => setForm({ ...form, emoji: e.target.value })} className="w-full bg-white border border-slate-300 rounded px-2 py-1.5 text-center text-lg text-slate-900" />
            </div>
            <div className="col-span-3">
              <label className="text-xs text-slate-600 mb-1 block">Name *</label>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full bg-white border border-slate-300 rounded px-2 py-1.5 text-sm text-slate-900" />
            </div>
          </div>
          <div>
            <label className="text-xs text-slate-600 mb-1 block">Description {isEdit && <span className="text-slate-400">(read-only here — edit in PlanTrack)</span>}</label>
            <textarea
              rows={2}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              disabled={isEdit}
              className="w-full bg-white border border-slate-300 rounded px-2 py-1.5 text-sm text-slate-900 disabled:bg-slate-50 disabled:text-slate-500"
            />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs text-slate-600 mb-1 block">Scope</label>
              <select value={form.scope} onChange={(e) => setForm({ ...form, scope: e.target.value })} className="w-full bg-white border border-slate-300 rounded px-2 py-1.5 text-sm text-slate-900">
                <option value="internal">Internal</option>
                <option value="external">External</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-600 mb-1 block">Status</label>
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="w-full bg-white border border-slate-300 rounded px-2 py-1.5 text-sm text-slate-900">
                {Object.entries(POLARIS_STATUS_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-600 mb-1 block">UAT Date</label>
              <input type="date" value={form.uatDate || ''} onChange={(e) => setForm({ ...form, uatDate: e.target.value })} className="w-full bg-white border border-slate-300 rounded px-2 py-1.5 text-sm text-slate-900" />
            </div>
          </div>
          <div>
            <label className="text-xs text-slate-600 mb-1 block">Note (italic blue block)</label>
            <MiniWysiwyg value={form.note} onChange={(v) => setForm({ ...form, note: v })} placeholder="Add a note…" minHeight="60px" />
          </div>
          <div>
            <label className="text-xs text-slate-600 mb-1 block">Dependency (amber block)</label>
            <MiniWysiwyg value={form.dep} onChange={(v) => setForm({ ...form, dep: v })} placeholder="Add a dependency…" minHeight="60px" />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-900">Cancel</button>
          <button onClick={submit} disabled={saving || !form.name.trim()} className="px-4 py-2 text-sm font-semibold rounded bg-indigo-600 hover:bg-indigo-700 text-white disabled:opacity-50">{saving ? 'Saving…' : (isEdit ? 'Save Changes' : 'Create Project')}</button>
        </div>
      </div>
    </div>
  );
};

const PolarisSummaryModal = ({ projects, onClose }) => {
  const byStatus = {};
  for (const p of projects) {
    byStatus[p.status] = byStatus[p.status] || [];
    byStatus[p.status].push(p);
  }
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(15,23,42,0.45)' }}>
      <div className="w-full max-w-3xl rounded-2xl p-6 max-h-[85vh] overflow-y-auto bg-white shadow-xl" style={{ border: '1px solid #e2e8f0' }}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-slate-900">Status Summary</h2>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900">✕</button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {Object.entries(POLARIS_STATUS_META).map(([k, meta]) => {
            const items = byStatus[k] || [];
            if (items.length === 0) return null;
            return (
              <div
                key={k}
                className="rounded-xl p-3 transition-all duration-150 hover:-translate-y-0.5 hover:shadow-md cursor-default"
                style={{ background: meta.bg, border: `1px solid ${meta.color}44` }}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-bold uppercase tracking-wider" style={{ color: meta.color }}>{meta.label}</span>
                  <span className="text-xs font-bold text-slate-700">{items.length}</span>
                </div>
                <ul className="space-y-1">
                  {items.map(p => <li key={p.id} className="text-xs text-slate-700 truncate">{p.emoji} {p.name}</li>)}
                </ul>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const PolarisReorderReasonModal = ({ onConfirm, onCancel, fromPos, toPos, projectName }) => {
  const [reason, setReason] = useState('');
  const MAX = 500;
  const MIN = 5;
  const tooLong = reason.length > MAX;
  const tooShort = reason.trim().length < MIN;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(15,23,42,0.45)' }}>
      <div className="w-full max-w-md rounded-2xl p-6 bg-white shadow-xl" style={{ border: '1px solid #e2e8f0' }}>
        <h2 className="text-lg font-bold text-slate-900 mb-1">📋 Priority change</h2>
        <p className="text-xs text-slate-500 mb-3">Logged to the priority log so future-you knows why this moved.</p>
        {(projectName || (fromPos != null && toPos != null)) && (
          <div className="mb-3 p-3 rounded-lg bg-slate-50 border border-slate-200 space-y-1">
            {projectName && <div className="text-sm font-semibold text-slate-900">{projectName}</div>}
            {fromPos != null && toPos != null && (
              <div className="flex items-center gap-2 text-xs">
                <span className="font-semibold text-slate-500">Priority:</span>
                <span className="font-bold text-slate-700 bg-white border border-slate-200 px-2 py-0.5 rounded">#{fromPos}</span>
                <span className="text-indigo-500">→</span>
                <span className="font-bold text-indigo-700 bg-indigo-50 border border-indigo-200 px-2 py-0.5 rounded">#{toPos}</span>
              </div>
            )}
          </div>
        )}
        <textarea
          autoFocus rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          maxLength={MAX}
          className={`w-full bg-white border rounded px-2 py-1.5 text-sm text-slate-900 ${tooLong ? 'border-rose-400' : 'border-slate-300'}`}
          placeholder="e.g. Customer escalation, deadline shift… (min 5 chars)"
        />
        <div className={`text-right text-[11px] mt-1 ${reason.length > 450 ? 'text-rose-600 font-semibold' : 'text-slate-400'}`}>
          {reason.length} / {MAX}
        </div>
        <div className="flex justify-end gap-2 mt-3">
          <button onClick={onCancel} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-900">Cancel</button>
          <button
            onClick={() => onConfirm(reason.trim())}
            disabled={tooShort || tooLong}
            className="px-4 py-2 text-sm font-semibold rounded bg-indigo-600 hover:bg-indigo-700 text-white disabled:opacity-50"
            title={tooShort ? `At least ${MIN} characters required` : ''}
          >Confirm Move</button>
        </div>
      </div>
    </div>
  );
};

// ---- Polaris email helpers ----
const POLARIS_EMAIL_BULLET_DOTS = {
  good:   { icon: '✓', color: '#16a34a' },
  warn:   { icon: '⚠', color: '#d97706' },
  info:   { icon: '►', color: '#2563eb' },
  normal: { icon: '•', color: '#6b7280' },
};

const escHtml = (s) => String(s == null ? '' : s)
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

const buildPolarisEmailHTML = ({ projects, highlights, subject, updatedLabel, includeLiveData }) => {
  const bulletLi = (b, indent) => {
    const dot = POLARIS_EMAIL_BULLET_DOTS[b.bulletType] || POLARIS_EMAIL_BULLET_DOTS.normal;
    const pad = indent ? 'padding-left:20px;' : '';
    return `<tr><td style="padding:2px 0;${pad}font-size:13px;color:#374151;line-height:1.6">`
      + `<span style="color:${dot.color};font-weight:bold;margin-right:6px">${dot.icon}</span>`
      + escHtml(b.text) + '</td></tr>';
  };

  const liveBlock = (live) => {
    if (!includeLiveData || !live) return '';
    const bugs = live.bugs || {};
    const tasks = live.tasks || {};
    const milestone = live.nextMilestone;
    const updates = live.latestUpdates || [];
    const summaryRows = [];
    if (bugs.total > 0) summaryRows.push(`<tr><td style="padding:2px 0;font-size:12px;color:#92400e">⚠ ${bugs.total} active bug${bugs.total === 1 ? '' : 's'}</td></tr>`);
    if (tasks.total > 0) summaryRows.push(`<tr><td style="padding:2px 0;font-size:12px;color:#1e40af">▤ ${tasks.done}/${tasks.total} tasks complete${tasks.inProgress > 0 ? ` · ${tasks.inProgress} in progress` : ''}</td></tr>`);
    if (milestone) summaryRows.push(`<tr><td style="padding:2px 0;font-size:12px;color:#6d28d9">◆ Next milestone: ${escHtml(milestone.title)}${milestone.dueDate ? ` (due ${escHtml(milestone.dueDate)})` : ''}${milestone.completion ? ` — ${milestone.completion}%` : ''}</td></tr>`);
    const byAssignee = bugs.byAssignee ? Object.values(bugs.byAssignee) : [];
    const roleSection = (label, bucket, color) => {
      const items = byAssignee.filter(a => a.bucket === bucket).sort((a, b) => b.count - a.count);
      if (items.length === 0) return '';
      const rows = items.map(a => `<tr><td style="padding:2px 0;font-size:12px;color:#374151"><span style="color:${color};margin-right:6px">•</span>${escHtml(a.name)}: ${a.count} active bug${a.count === 1 ? '' : 's'}</td></tr>`).join('');
      return `<tr><td style="padding:6px 0 2px;font-size:10px;font-weight:700;text-transform:uppercase;color:${color};letter-spacing:.5px">${label}</td></tr>${rows}`;
    };
    const updateRows = updates.map(u => `<tr><td style="padding:2px 0;font-size:12px;color:#374151"><span style="color:#6b7280;margin-right:6px">· ${new Date(u.createdAt).toLocaleDateString()}</span>${escHtml(u.content)}${u.userName ? ` <span style=\"color:#9ca3af\">— ${escHtml(u.userName)}</span>` : ''}</td></tr>`).join('');
    const hasAnything = summaryRows.length || updateRows
      || ['dataops','devops','qa','other'].some(b => byAssignee.some(a => a.bucket === b));
    if (!hasAnything) return '';
    return `<tr><td style="padding:8px 14px;background:#f8fafc;border-bottom:1px solid #e5e7eb">`
      + `<div style="font-size:10px;font-weight:700;text-transform:uppercase;color:#4f46e5;letter-spacing:.6px;margin-bottom:4px">Live from PlanTrack</div>`
      + `<table role="presentation" width="100%" cellpadding="0" cellspacing="0">${summaryRows.join('')}`
      + roleSection('DataOps', 'dataops', '#7c3aed')
      + roleSection('DevOps', 'devops', '#2563eb')
      + roleSection('QA', 'qa', '#db2777')
      + roleSection('Other / Unassigned', 'other', '#64748b')
      + (updateRows ? `<tr><td style="padding:6px 0 2px;font-size:10px;font-weight:700;text-transform:uppercase;color:#047857;letter-spacing:.5px">Recent Updates</td></tr>${updateRows}` : '')
      + `</table></td></tr>`;
  };

  const projectRows = projects.map(p => {
    const meta = POLARIS_STATUS_META[p.status] || POLARIS_STATUS_META.dev;
    const color = meta.color;
    const buls = [];
    if (p.note) buls.push(`<tr><td style="padding:2px 0;font-size:13px;color:#1e40af;font-style:italic;line-height:1.6;background:#eff6ff;border-left:3px solid #60a5fa;padding-left:10px;border-radius:0 4px 4px 0">${sanitizeBugHtml(p.note)}</td></tr>`);
    if (p.dep) buls.push(`<tr><td style="padding:2px 0;font-size:13px;color:#92400e;line-height:1.6;background:#fffbeb;border-left:3px solid #f59e0b;padding-left:10px;border-radius:0 4px 4px 0"><strong>Dep:</strong> ${sanitizeBugHtml(p.dep)}</td></tr>`);
    const bullets = p.bullets || [];
    const mainBullets = bullets.filter(b => !b.subsection);
    for (const b of mainBullets) buls.push(bulletLi(b, false));
    // Dynamic subsection grouping (any free-text label, preserved in insertion order)
    const subGroups = new Map();
    for (const b of bullets) {
      if (!b.subsection) continue;
      if (!subGroups.has(b.subsection)) subGroups.set(b.subsection, []);
      subGroups.get(b.subsection).push(b);
    }
    for (const [label, items] of subGroups) {
      const style = getPolarisSubsectionStyle(label);
      buls.push(`<tr><td style="padding:6px 0 2px;font-size:11px;font-weight:700;text-transform:uppercase;color:${style.color};letter-spacing:.5px">${escHtml(label)}</td></tr>`);
      for (const b of items) buls.push(bulletLi(b, true));
    }

    return `<tr><td style="padding:0 0 14px 0">`
      + `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="border:1px solid #e5e7eb;border-radius:10px;overflow:hidden;border-left:4px solid ${color}">`
      + `<tr><td style="padding:10px 14px;background:#f9fafb;border-bottom:1px solid #e5e7eb">`
      + `<table role="presentation" width="100%" cellpadding="0" cellspacing="0"><tr>`
      + `<td style="font-size:14px;font-weight:bold;color:#111827">${p.emoji || ''} ${escHtml(p.name)}</td>`
      + `<td align="right"><span style="background:${color}22;border:1px solid ${color}66;color:${color};padding:2px 9px;border-radius:999px;font-size:11px;font-weight:700">${escHtml(meta.label)}</span>`
      + (p.uatDate ? `<span style="margin-left:5px;background:#eff6ff;border:1px solid #bfdbfe;color:#1d4ed8;padding:2px 9px;border-radius:999px;font-size:11px">UAT: ${escHtml(p.uatDate)}</span>` : '')
      + `</td></tr></table></td></tr>`
      + liveBlock(p.liveData)
      + (buls.length ? `<tr><td style="padding:10px 14px"><table role="presentation" width="100%" cellpadding="0" cellspacing="0">${buls.join('')}</table></td></tr>` : '')
      + `</table></td></tr>`;
  }).join('');

  const highlightRows = (highlights || []).map(item => {
    let row = `<li style="margin-bottom:4px">${escHtml(item.text)}`;
    if ((item.children || []).length > 0) {
      row += `<ul style="margin:4px 0 0;padding-left:16px;color:#6b7280">`;
      for (const c of item.children) row += `<li style="margin-bottom:2px">↳ ${escHtml(c.text)}</li>`;
      row += `</ul>`;
    }
    return row + `</li>`;
  }).join('');

  return `<!DOCTYPE html><html><head><meta charset="utf-8"></head>`
    + `<body style="margin:0;padding:0;background:#f5f7fb;font-family:Arial,sans-serif;color:#1f2937">`
    + `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background:#f5f7fb">`
    + `<tr><td align="center" style="padding:24px">`
    + `<table role="presentation" width="680" cellpadding="0" cellspacing="0" style="max-width:680px;background:#fff;border-radius:12px;border:1px solid #e5e7eb;overflow:hidden">`
    + `<tr><td style="background:linear-gradient(135deg,#4f46e5,#7c3aed);color:#fff;padding:20px 26px">`
    + `<div style="font-size:18px;font-weight:bold">${escHtml(subject || 'Polaris Status Update')}</div>`
    + `<div style="margin-top:5px;font-size:12px;opacity:.9">Last updated: <strong>${escHtml(updatedLabel)}</strong></div></td></tr>`
    + (highlightRows ? `<tr><td style="padding:16px 26px 0"><div style="background:#eef2ff;border:1px solid #c7d2fe;border-left:4px solid #4f46e5;border-radius:8px;padding:12px 14px">`
        + `<div style="font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:1px;color:#4338ca;margin-bottom:8px">Highlights</div>`
        + `<ul style="margin:0;padding-left:18px;font-size:13px;color:#3730a3;line-height:1.6">${highlightRows}</ul></div></td></tr>` : '')
    + `<tr><td style="padding:16px 26px 22px">`
    + `<div style="font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:1px;color:#6b7280;margin-bottom:12px">Project Status Update</div>`
    + `<table role="presentation" width="100%" cellpadding="0" cellspacing="0">${projectRows}</table></td></tr>`
    + `<tr><td style="padding:12px 26px 20px;background:#f9fafb;border-top:1px solid #e5e7eb">`
    + `<div style="font-size:12px;color:#6b7280">Generated by <strong>Polaris</strong> · Orbit Project Status Board</div></td></tr>`
    + `</table></td></tr></table></body></html>`;
};

const buildPolarisEmailText = ({ projects, highlights, subject, updatedLabel, includeLiveData }) => {
  let body = `${subject || 'Polaris Status Update'}\nLast updated: ${updatedLabel}\n\n`;
  if ((highlights || []).length) {
    body += 'HIGHLIGHTS\n';
    for (const h of highlights) {
      body += `- ${h.text}\n`;
      for (const c of (h.children || [])) body += `  ↳ ${c.text}\n`;
    }
    body += '\n';
  }
  for (const p of projects) {
    const meta = POLARIS_STATUS_META[p.status] || POLARIS_STATUS_META.dev;
    body += `${p.emoji || ''} ${p.name} - ${meta.label}${p.uatDate ? ` | UAT: ${p.uatDate}` : ''}\n`;
    if (p.note) body += `  ${stripHtml(p.note)}\n`;
    if (p.dep) body += `  Dep: ${stripHtml(p.dep)}\n`;
    if (includeLiveData && p.liveData) {
      const live = p.liveData;
      if (live.bugs?.total > 0) body += `  - ${live.bugs.total} active bug${live.bugs.total === 1 ? '' : 's'} across teams\n`;
      if (live.tasks?.total > 0) body += `  - ${live.tasks.done}/${live.tasks.total} tasks complete${live.tasks.inProgress > 0 ? ` (${live.tasks.inProgress} in progress)` : ''}\n`;
      if (live.nextMilestone) body += `  - Next milestone: ${live.nextMilestone.title}${live.nextMilestone.dueDate ? ` (due ${live.nextMilestone.dueDate})` : ''}\n`;
    }
    const bullets = (p.bullets || []).filter(b => !b.subsection);
    for (const b of bullets) body += `  - ${b.text}\n`;
    // Dynamic subsection grouping
    const subGroups = new Map();
    for (const b of (p.bullets || [])) {
      if (!b.subsection) continue;
      if (!subGroups.has(b.subsection)) subGroups.set(b.subsection, []);
      subGroups.get(b.subsection).push(b);
    }
    for (const [label, items] of subGroups) {
      body += `  ${label}:\n`;
      for (const b of items) body += `    - ${b.text}\n`;
    }
    body += '\n';
  }
  body += '---\nGenerated by Polaris · Orbit Project Status Board';
  return body;
};

const PolarisEmailModal = ({ data, userId, onClose }) => {
  const projects = data?.projects || [];
  const highlights = data?.highlights || [];
  const recipientsStorageKey = `polaris_recipients_${userId || 'anon'}`;

  const [subject, setSubject] = useState(`Polaris Status Update - ${new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`);
  const [selectedIds, setSelectedIds] = useState(() => new Set(projects.map(p => p.id)));
  const [recipients, setRecipients] = useState(() => {
    try { return JSON.parse(localStorage.getItem(recipientsStorageKey) || '[]'); }
    catch { return []; }
  });
  const [recipInput, setRecipInput] = useState('');
  const [recipError, setRecipError] = useState(false);
  const [includeLiveData, setIncludeLiveData] = useState(true);
  const [copyMsg, setCopyMsg] = useState('');
  const [gmailUrl, setGmailUrl] = useState('');
  const [linkCopyMsg, setLinkCopyMsg] = useState('');

  const persistRecipients = (next) => {
    setRecipients(next);
    try { localStorage.setItem(recipientsStorageKey, JSON.stringify(next)); } catch {}
  };

  const addRecipient = () => {
    const v = recipInput.trim().toLowerCase();
    if (!v || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) {
      setRecipError(true);
      setTimeout(() => setRecipError(false), 1500);
      return;
    }
    if (!recipients.includes(v)) persistRecipients([...recipients, v]);
    setRecipInput('');
  };

  const removeRecipient = (email) => persistRecipients(recipients.filter(r => r !== email));

  const toggleProject = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    setSelectedIds(prev => prev.size === projects.length ? new Set() : new Set(projects.map(p => p.id)));
  };

  const selectedProjects = projects.filter(p => selectedIds.has(p.id));
  const updatedLabel = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  const flashCopyMsg = (msg) => {
    setCopyMsg(msg);
    setTimeout(() => setCopyMsg(''), 3500);
  };

  const copyForGmail = async () => {
    const html = buildPolarisEmailHTML({ projects: selectedProjects, highlights, subject, updatedLabel, includeLiveData });
    const text = buildPolarisEmailText({ projects: selectedProjects, highlights, subject, updatedLabel, includeLiveData });
    try {
      if (window.ClipboardItem && navigator.clipboard?.write) {
        const item = new ClipboardItem({
          'text/html': new Blob([html], { type: 'text/html' }),
          'text/plain': new Blob([text], { type: 'text/plain' }),
        });
        await navigator.clipboard.write([item]);
        flashCopyMsg('✓ Formatted email copied. Paste into Gmail compose with Ctrl+V.');
        return;
      }
    } catch {}
    // Fallback: hidden div + execCommand
    try {
      const div = document.createElement('div');
      div.style.cssText = 'position:fixed;left:-9999px;top:0;opacity:0;pointer-events:none';
      div.innerHTML = html;
      document.body.appendChild(div);
      const range = document.createRange(); range.selectNodeContents(div);
      const sel = window.getSelection(); sel.removeAllRanges(); sel.addRange(range);
      document.execCommand('copy');
      sel.removeAllRanges(); document.body.removeChild(div);
      flashCopyMsg('✓ Copied. Paste into Gmail compose with Ctrl+V.');
    } catch {
      flashCopyMsg('Could not copy automatically — try the Gmail link option.');
    }
  };

  const buildGmailUrl = () => {
    const text = buildPolarisEmailText({ projects: selectedProjects, highlights, subject, updatedLabel, includeLiveData });
    const to = recipients.join(',');
    const url = `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(to)}&su=${encodeURIComponent(subject)}&body=${encodeURIComponent(text)}`;
    setGmailUrl(prev => prev === url ? '' : url);
  };

  const copyGmailLink = async () => {
    try {
      await navigator.clipboard.writeText(gmailUrl);
      setLinkCopyMsg('✓ Link copied. Paste into a new browser tab.');
      setTimeout(() => setLinkCopyMsg(''), 3000);
    } catch {
      setLinkCopyMsg('Select the URL and copy manually.');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(15,23,42,0.45)' }}>
      <div className="w-full max-w-2xl rounded-2xl bg-white shadow-xl flex flex-col max-h-[92vh]" style={{ border: '1px solid #e2e8f0' }}>
        <div className="px-6 pt-5 pb-4 border-b border-slate-200">
          <h2 className="text-lg font-bold text-slate-900">📧 Send Project Update Email</h2>
          <p className="text-xs text-slate-500 mt-1">Compose and copy a formatted status update to paste into Gmail.</p>
        </div>

        <div className="px-6 py-4 overflow-y-auto flex-1 space-y-4">
          {/* Subject */}
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5 block">Subject</label>
            <input value={subject} onChange={(e) => setSubject(e.target.value)} className="w-full bg-white border border-slate-300 rounded px-3 py-2 text-sm text-slate-900" />
          </div>

          {/* Recipients */}
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5 block">Recipients (for Gmail link)</label>
            <div className="flex gap-2">
              <input
                value={recipInput}
                onChange={(e) => setRecipInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addRecipient(); } }}
                placeholder="email@example.com"
                className={`flex-1 bg-white border rounded px-3 py-2 text-sm text-slate-900 ${recipError ? 'border-rose-500' : 'border-slate-300'}`}
              />
              <button onClick={addRecipient} className="px-3 py-2 text-xs font-semibold rounded bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-300">Add</button>
            </div>
            {recipients.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {recipients.map(r => (
                  <span key={r} className="inline-flex items-center gap-1 bg-indigo-50 text-indigo-700 text-xs px-2.5 py-1 rounded-full border border-indigo-200">
                    {r}
                    <button onClick={() => removeRecipient(r)} className="text-indigo-500 hover:text-rose-600 ml-1">×</button>
                  </span>
                ))}
              </div>
            )}
            <p className="text-[11px] text-slate-400 mt-1">Saved on this device for next time.</p>
          </div>

          {/* Project picker */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-bold uppercase tracking-wider text-slate-500">Projects ({selectedIds.size} of {projects.length} selected)</label>
              <button onClick={toggleAll} className="text-xs text-indigo-600 hover:text-indigo-700 font-medium underline">
                {selectedIds.size === projects.length ? 'Deselect All' : 'Select All'}
              </button>
            </div>
            <div className="border border-slate-200 rounded-lg max-h-48 overflow-y-auto">
              {projects.length === 0 ? (
                <p className="text-xs text-slate-400 italic p-3">No active projects to include.</p>
              ) : projects.map(p => {
                const meta = POLARIS_STATUS_META[p.status] || POLARIS_STATUS_META.dev;
                const checked = selectedIds.has(p.id);
                return (
                  <label key={p.id} className={`flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-slate-50 border-b border-slate-100 last:border-b-0 ${checked ? 'bg-indigo-50/50' : ''}`}>
                    <input type="checkbox" checked={checked} onChange={() => toggleProject(p.id)} className="accent-indigo-600" />
                    <span className="text-sm text-slate-700 flex-1 truncate">{p.emoji} {p.name}</span>
                    <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{ color: meta.color, backgroundColor: meta.bg, border: `1px solid ${meta.color}44` }}>{meta.label}</span>
                    {p.uatDate && <span className="text-[10px] text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded-full border border-indigo-200">UAT {p.uatDate}</span>}
                  </label>
                );
              })}
            </div>
          </div>

          {/* Auto-data toggle */}
          <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
            <input type="checkbox" checked={includeLiveData} onChange={(e) => setIncludeLiveData(e.target.checked)} className="accent-indigo-600" />
            Include "Live from PlanTrack" data (bugs by role, milestones, recent updates)
          </label>

          {/* Preview */}
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5 block">Preview</label>
            <div className="border border-slate-200 rounded-lg overflow-hidden bg-slate-50" style={{ maxHeight: '280px', overflowY: 'auto' }}>
              <iframe
                title="Email preview"
                style={{ width: '100%', height: '260px', border: 'none', display: 'block', background: '#fff' }}
                srcDoc={buildPolarisEmailHTML({ projects: selectedProjects, highlights, subject, updatedLabel, includeLiveData })}
              />
            </div>
          </div>

          {/* Gmail URL fallback (collapsed) */}
          {gmailUrl && (
            <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
              <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Gmail compose link</div>
              <p className="text-xs text-slate-500 mb-2">Plain-text body only. Paste this URL into a new browser tab to open Gmail with the email prefilled.</p>
              <div className="flex gap-2">
                <input readOnly value={gmailUrl} className="flex-1 bg-white border border-slate-300 rounded px-2 py-1.5 text-[10px] text-indigo-700 font-mono" onClick={(e) => e.target.select()} />
                <button onClick={copyGmailLink} className="px-3 py-1.5 text-xs font-semibold rounded bg-indigo-100 hover:bg-indigo-200 text-indigo-700 border border-indigo-300">Copy Link</button>
              </div>
              {linkCopyMsg && <p className="text-xs text-emerald-700 mt-2">{linkCopyMsg}</p>}
            </div>
          )}
        </div>

        <div className="px-6 py-3 border-t border-slate-200 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <span className={`text-xs font-semibold transition-opacity ${copyMsg ? 'opacity-100' : 'opacity-0'} text-emerald-700 truncate`}>{copyMsg}</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={buildGmailUrl} className="px-3 py-2 text-xs font-semibold rounded bg-white border border-slate-300 text-slate-700 hover:border-indigo-400 hover:text-indigo-700">
              {gmailUrl ? 'Hide Gmail link' : 'Generate Gmail link'}
            </button>
            <button onClick={copyForGmail} disabled={selectedProjects.length === 0} className="px-4 py-2 text-xs font-semibold rounded bg-gradient-to-r from-indigo-600 to-violet-600 text-white hover:from-indigo-700 hover:to-violet-700 disabled:opacity-50 shadow-sm">✨ Copy for Gmail</button>
            <button onClick={onClose} className="px-3 py-2 text-xs font-semibold text-slate-600 hover:text-slate-900">Close</button>
          </div>
        </div>
      </div>
    </div>
  );
};

const PolarisModule = ({ data, user, onRefresh, showArchive, onToggleShowArchive }) => {
  const [scopeFilter, setScopeFilter] = useState(new Set(['internal','external']));
  const [statusFilter, setStatusFilter] = useState(new Set(Object.keys(POLARIS_STATUS_META)));
  const [sortMode, setSortMode] = useState('priority');
  const [showSummary, setShowSummary] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEmail, setShowEmail] = useState(false);
  const [editProject, setEditProject] = useState(null);
  const [pendingMove, setPendingMove] = useState(null); // {ids, fromIdx, toIdx}
  const [pendingArchive, setPendingArchive] = useState(null); // project pending archive-with-reason
  const [archiveDetail, setArchiveDetail] = useState(null); // archived project being viewed
  const [draggingId, setDraggingId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const [logFilter, setLogFilter] = useState(null); // project name string, or null = all

  // ---- Undo stack (session-scoped, last 20 inverse operations) ----
  const [undoStack, setUndoStack] = useState([]);
  const [savedFlash, setSavedFlash] = useState(false);
  const UNDO_LIMIT = 20;

  const recordAction = (label, inverse) => {
    setUndoStack(s => {
      const next = [...s, { label, inverse, t: Date.now() }];
      if (next.length > UNDO_LIMIT) next.shift();
      return next;
    });
    setSavedFlash(true);
    setTimeout(() => setSavedFlash(false), 2200);
  };

  const handleUndo = async () => {
    if (!undoStack.length) return;
    const top = undoStack[undoStack.length - 1];
    setUndoStack(s => s.slice(0, -1));
    try { await top.inverse(); }
    catch (e) { console.error('Undo failed:', e); alert(`Undo failed: ${e.message || 'unknown error'}`); }
    await onRefresh();
  };

  // Helper to find a bullet by id across all projects (active + archived)
  const findBullet = (bid) => {
    for (const p of [...(data?.projects || []), ...(data?.archived || [])]) {
      const b = (p.bullets || []).find(x => x.id === bid);
      if (b) return { bullet: b, projectId: p.id };
    }
    return null;
  };

  const allProjects = useMemo(() => showArchive ? (data?.archived || []) : (data?.projects || []), [data, showArchive]);

  const visible = useMemo(() => {
    let list = allProjects.filter(p => scopeFilter.has(p.scope) && statusFilter.has(p.status));
    if (sortMode === 'name') list = [...list].sort((a, b) => a.name.localeCompare(b.name));
    else if (sortMode === 'status') list = [...list].sort((a, b) => (a.status || '').localeCompare(b.status || ''));
    else if (sortMode === 'uat') list = [...list].sort((a, b) => (a.uatDate || '￿').localeCompare(b.uatDate || '￿'));
    else if (sortMode === 'scope-int') list = [...list].sort((a, b) => (a.scope === 'internal' ? 0 : 1) - (b.scope === 'internal' ? 0 : 1));
    else if (sortMode === 'scope-ext') list = [...list].sort((a, b) => (a.scope === 'external' ? 0 : 1) - (b.scope === 'external' ? 0 : 1));
    return list;
  }, [allProjects, scopeFilter, statusFilter, sortMode]);

  const toggleSet = (setter, key) => setter(prev => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const handleDropOn = (targetProject) => {
    if (!draggingId || draggingId === targetProject.id) { setDraggingId(null); setDragOverId(null); return; }
    const ordered = [...(data?.projects || [])];
    const fromIdx = ordered.findIndex(p => p.id === draggingId);
    const toIdx = ordered.findIndex(p => p.id === targetProject.id);
    if (fromIdx < 0 || toIdx < 0 || fromIdx === toIdx) { setDraggingId(null); setDragOverId(null); return; }
    const movedProject = ordered[fromIdx];
    setPendingMove({ project: movedProject, fromIdx, toIdx, ordered });
    setDraggingId(null);
    setDragOverId(null);
  };

  const confirmMove = async (reason) => {
    if (!pendingMove) return;
    const { ordered, fromIdx, toIdx, project } = pendingMove;
    const prevOrderIds = ordered.map(p => p.id);
    const next = [...ordered];
    const [moved] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, moved);
    await ModulesModel.reorderPolarisProjects(next.map(p => p.id), reason);
    recordAction(`Reorder "${project?.name || moved?.name}" #${fromIdx + 1} → #${toIdx + 1}`, async () => {
      await ModulesModel.reorderPolarisProjects(prevOrderIds, `Undo: ${reason}`);
    });
    setPendingMove(null);
    await onRefresh();
  };

  const handleSaveProject = async (form) => {
    if (editProject) {
      // Capture prev values for undo (only fields present in the patch)
      const prevPatch = {};
      for (const k of Object.keys(form)) {
        if (k in editProject) prevPatch[k] = editProject[k];
      }
      const projectId = editProject.id;
      const projectName = editProject.name;
      try {
        await ModulesModel.updatePolarisProject(projectId, form);
      } catch (e) {
        alert(`Could not save "${projectName}": ${e.message || 'unknown error'}`);
        return;
      }
      recordAction(`Edit ${projectName}`, async () => {
        await ModulesModel.updatePolarisProject(projectId, prevPatch);
      });
      setEditProject(null);
    } else {
      // Create — not undoable (would orphan PlanTrack project)
      await ModulesModel.createPolarisProject(form);
      setShowAddForm(false);
    }
    await onRefresh();
  };

  const handleDeleteProject = async (project) => {
    if (!window.confirm(`Delete "${project.name}"? This removes the project from PlanTrack as well, including its tasks, milestones, comments, and Polaris bullets. This cannot be undone.`)) return;
    const res = await ModulesModel.deletePolarisProject(project.id);
    if (res?.success === false) {
      alert(`Could not delete: ${res.error || 'unknown error'}`);
      return;
    }
    await onRefresh();
  };

  const handleArchiveProject = async (project) => {
    if (project.archived) {
      // Restore — no reason required, but captureable for undo
      const prevReason = project.archiveReason || '';
      const id = project.id;
      const name = project.name;
      await ModulesModel.setPolarisArchive(id, false, '');
      recordAction(`Restore ${name}`, async () => {
        await ModulesModel.setPolarisArchive(id, true, prevReason);
      });
      await onRefresh();
      return;
    }
    // Archive — open reason modal
    setPendingArchive(project);
  };

  const confirmArchive = async (reason) => {
    if (!pendingArchive) return;
    const id = pendingArchive.id;
    const name = pendingArchive.name;
    await ModulesModel.setPolarisArchive(id, true, reason);
    recordAction(`Archive ${name}`, async () => {
      await ModulesModel.setPolarisArchive(id, false, '');
    });
    setPendingArchive(null);
    await onRefresh();
  };

  const handleUpdateProject = async (project, patch) => {
    // Capture prev values for any keys being updated
    const prevPatch = {};
    for (const k of Object.keys(patch)) {
      if (k in project) prevPatch[k] = project[k];
    }
    const id = project.id;
    const name = project.name;
    try {
      await ModulesModel.updatePolarisProject(id, patch);
    } catch (e) {
      alert(`Could not update "${name}": ${e.message || 'unknown error'}`);
      return;
    }
    // Build a short label for the undo entry from the patched keys
    const keys = Object.keys(patch);
    const labelHint = keys.length === 1
      ? `${keys[0]} of ${name}`
      : `${keys.length} fields on ${name}`;
    recordAction(`Edit ${labelHint}`, async () => {
      await ModulesModel.updatePolarisProject(id, prevPatch);
    });
    await onRefresh();
  };

  const handleRenameSubsection = async (project, from, to) => {
    const id = project.id;
    await ModulesModel.renamePolarisSubsection(id, from, to);
    recordAction(`Rename section "${from}" → "${to}"`, async () => {
      await ModulesModel.renamePolarisSubsection(id, to, from);
    });
    await onRefresh();
  };

  const handleDeleteSubsection = async (project, label) => {
    const bulletsInSection = (project.bullets || []).filter(b => b.subsection === label).map(b => ({ ...b }));
    const count = bulletsInSection.length;
    if (!window.confirm(`Delete section "${label}"? This will remove ${count} bullet${count === 1 ? '' : 's'} in this section.`)) return;
    const id = project.id;
    await ModulesModel.deletePolarisSubsection(id, label);
    recordAction(`Delete section "${label}" (${count} bullet${count === 1 ? '' : 's'})`, async () => {
      // Re-add each bullet under the same label (positions may differ — acceptable for undo)
      for (const b of bulletsInSection) {
        await ModulesModel.addPolarisBullet(id, {
          text: b.text,
          bulletType: b.bulletType,
          subsection: label,
        });
      }
    });
    await onRefresh();
  };

  const handleAddBullet = async (project, payload) => {
    const result = await ModulesModel.addPolarisBullet(project.id, payload);
    if (result?.success && result.id) {
      const newId = result.id;
      const labelHint = payload.subsection ? `bullet in "${payload.subsection}"` : 'bullet';
      recordAction(`Add ${labelHint}`, async () => {
        await ModulesModel.deletePolarisBullet(newId);
      });
    }
    await onRefresh();
  };

  const handleEditBullet = async (bulletId, patch) => {
    const found = findBullet(bulletId);
    const prevPatch = {};
    if (found) {
      for (const k of Object.keys(patch)) {
        if (k in found.bullet) prevPatch[k] = found.bullet[k];
      }
    }
    await ModulesModel.updatePolarisBullet(bulletId, patch);
    if (found) {
      recordAction(`Edit bullet`, async () => {
        await ModulesModel.updatePolarisBullet(bulletId, prevPatch);
      });
    }
    await onRefresh();
  };

  const handleDeleteBullet = async (bulletId) => {
    const found = findBullet(bulletId);
    await ModulesModel.deletePolarisBullet(bulletId);
    if (found) {
      const { bullet: b, projectId } = found;
      recordAction(`Delete bullet "${(b.text || '').slice(0, 40)}…"`, async () => {
        await ModulesModel.addPolarisBullet(projectId, {
          text: b.text,
          bulletType: b.bulletType,
          subsection: b.subsection,
        });
      });
    }
    await onRefresh();
  };

  // Collapse/expand is excluded from undo — would just be noise
  const handleToggleCollapse = async (project) => {
    try {
      await ModulesModel.updatePolarisProject(project.id, { collapsed: project.collapsed ? 0 : 1 });
    } catch (e) {
      console.error('Toggle collapse failed:', e);
      return;
    }
    await onRefresh();
  };

  const handleSaveHighlights = async (content) => {
    const prev = data?.highlights || [];
    await ModulesModel.setPolarisHighlights(content);
    recordAction(`Edit highlights`, async () => {
      await ModulesModel.setPolarisHighlights(prev);
    });
    await onRefresh();
  };

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)' }}>
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <PolarisCompass size={44} />
            <div>
              <h1 className="text-3xl font-extrabold" style={{ background: 'linear-gradient(90deg, #4f46e5, #7c3aed, #db2777)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>Polaris</h1>
              <p className="text-xs text-slate-500">Your true-north priority board</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span
              className={`text-xs font-semibold text-emerald-600 transition-opacity duration-300 ${savedFlash ? 'opacity-100' : 'opacity-0'}`}
              aria-live="polite"
            >✓ Saved</span>
            <button
              onClick={handleUndo}
              disabled={undoStack.length === 0}
              title={undoStack.length ? `Undo: ${undoStack[undoStack.length - 1].label}` : 'Nothing to undo'}
              className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-white border border-slate-300 text-slate-700 hover:border-violet-400 hover:text-violet-700 disabled:opacity-40 disabled:cursor-not-allowed"
            >↶ Undo{undoStack.length > 0 ? ` (${undoStack.length})` : ''}</button>
            <button onClick={onToggleShowArchive} className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-white border border-slate-300 text-slate-700 hover:border-amber-400 hover:text-amber-700">
              {showArchive ? '← Active' : 'Archive'}
            </button>
            <button onClick={() => setShowSummary(true)} className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-white border border-slate-300 text-slate-700 hover:border-indigo-400 hover:text-indigo-700">Summary</button>
            <button onClick={() => setShowEmail(true)} className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-white border border-slate-300 text-slate-700 hover:border-emerald-400 hover:text-emerald-700">📧 Email</button>
            <button onClick={() => setShowAddForm(true)} className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-gradient-to-r from-indigo-600 to-violet-600 text-white hover:from-indigo-700 hover:to-violet-700 shadow-sm">+ Add Project</button>
          </div>
        </div>

        {/* Legend strip (status + scope key) */}
        {!showArchive && (
          <div className="mb-5 px-4 py-2.5 rounded-xl bg-white shadow-sm flex items-center gap-2 flex-wrap" style={{ border: '1px solid #e2e8f0' }}>
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Status</span>
            {Object.entries(POLARIS_STATUS_META).map(([k, meta]) => (
              <span key={k} className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold" style={{ color: meta.color, backgroundColor: meta.bg, border: `1px solid ${meta.color}44` }}>
                {meta.label}
              </span>
            ))}
            <span className="w-px h-4 bg-slate-200 mx-1" />
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Scope</span>
            <PolarisScopeTag scope="internal" />
            <PolarisScopeTag scope="external" />
          </div>
        )}

        {/* Highlights (only on active view) */}
        {!showArchive && (
          <PolarisHighlightsCard highlights={data?.highlights || []} meta={data?.highlightsMeta} onSave={handleSaveHighlights} />
        )}

        {/* Filters */}
        {!showArchive && (
          <div className="mb-4 p-4 rounded-xl bg-white shadow-sm" style={{ border: '1px solid #e2e8f0' }}>
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <span className="text-xs text-slate-500 font-semibold uppercase mr-2">Scope</span>
              <PolarisFilterChip active={scopeFilter.has('internal')} onClick={() => toggleSet(setScopeFilter, 'internal')} color="#4f46e5">Internal</PolarisFilterChip>
              <PolarisFilterChip active={scopeFilter.has('external')} onClick={() => toggleSet(setScopeFilter, 'external')} color="#e11d48">External</PolarisFilterChip>
            </div>
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <span className="text-xs text-slate-500 font-semibold uppercase mr-2">Status</span>
              {Object.entries(POLARIS_STATUS_META).map(([k, meta]) => (
                <PolarisFilterChip key={k} active={statusFilter.has(k)} onClick={() => toggleSet(setStatusFilter, k)} color={meta.color}>{meta.label}</PolarisFilterChip>
              ))}
            </div>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500 font-semibold uppercase">Sort</span>
                <select value={sortMode} onChange={(e) => setSortMode(e.target.value)} className="bg-white border border-slate-300 rounded text-xs text-slate-700 px-2 py-1">
                  <option value="priority">Priority (manual)</option>
                  <option value="name">Name (A→Z)</option>
                  <option value="status">Status</option>
                  <option value="uat">UAT Date</option>
                  <option value="scope-int">Scope (Int → Ext)</option>
                  <option value="scope-ext">Scope (Ext → Int)</option>
                </select>
              </div>
              <div className="flex items-center gap-3">
                {(scopeFilter.size < 2 || statusFilter.size < Object.keys(POLARIS_STATUS_META).length || sortMode !== 'priority') && (
                  <button
                    onClick={() => {
                      setScopeFilter(new Set(['internal','external']));
                      setStatusFilter(new Set(Object.keys(POLARIS_STATUS_META)));
                      setSortMode('priority');
                    }}
                    className="text-[11px] px-2 py-0.5 rounded-full bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200 font-semibold"
                  >× Clear filters</button>
                )}
                <span className="text-xs text-slate-500">{visible.length} of {allProjects.length} projects</span>
              </div>
            </div>
          </div>
        )}

        {/* Project list */}
        {visible.length === 0 ? (
          <div className="text-center py-16 text-slate-400">
            {showArchive ? 'No archived projects.' : 'No projects match your filters. Try widening the chips above or add a new project.'}
          </div>
        ) : showArchive ? (
          // Archived view — cards open a detail modal on click
          <div className="space-y-2">
            {visible.map(p => {
              const meta = POLARIS_STATUS_META[p.status] || POLARIS_STATUS_META.dev;
              return (
                <button
                  key={p.id}
                  onClick={() => setArchiveDetail(p)}
                  className="w-full text-left flex items-center gap-3 px-4 py-2.5 rounded-xl bg-white border border-slate-200 hover:border-amber-400 hover:shadow-sm transition-all"
                >
                  <span className="text-xl">{p.emoji || '🚀'}</span>
                  <span className="font-semibold text-slate-900 flex-1 truncate">{p.name}</span>
                  <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full" style={{ color: meta.color, backgroundColor: meta.bg, border: `1px solid ${meta.color}44` }}>{meta.label}</span>
                  {p.archivedAt && <span className="text-[10px] text-slate-400">{new Date(p.archivedAt).toLocaleDateString()}</span>}
                  {p.archiveReason && <span className="text-[11px] text-slate-500 italic truncate max-w-xs">— {p.archiveReason}</span>}
                  <span className="text-xs text-slate-400">View →</span>
                </button>
              );
            })}
          </div>
        ) : (
          visible.map((p, i) => (
            <PolarisProjectCard
              key={p.id}
              project={p}
              index={i}
              total={visible.length}
              isDragging={draggingId === p.id}
              isDragTarget={dragOverId === p.id && draggingId && draggingId !== p.id}
              onToggleCollapse={() => handleToggleCollapse(p)}
              onEditProject={() => setEditProject(p)}
              onDeleteProject={() => handleDeleteProject(p)}
              onArchive={() => handleArchiveProject(p)}
              onAddBullet={(data) => handleAddBullet(p, data)}
              onEditBullet={handleEditBullet}
              onDeleteBullet={handleDeleteBullet}
              onUpdateProject={(patch) => handleUpdateProject(p, patch)}
              onChangeStatus={(s) => handleUpdateProject(p, { status: s })}
              onRenameSubsection={(from, to) => handleRenameSubsection(p, from, to)}
              onDeleteSubsection={(label) => handleDeleteSubsection(p, label)}
              onDragStart={(e) => { setDraggingId(p.id); e.dataTransfer.effectAllowed = 'move'; }}
              onDragEnd={() => { setDraggingId(null); setDragOverId(null); }}
              onDragOver={(e) => { if (sortMode === 'priority' && draggingId && draggingId !== p.id) { e.preventDefault(); setDragOverId(p.id); } }}
              onDrop={(e) => { e.preventDefault(); if (sortMode === 'priority') handleDropOn(p); }}
            />
          ))
        )}

        {/* Priority log */}
        {!showArchive && (data?.priorityLog || []).length > 0 && (() => {
          const allEntries = data?.priorityLog || [];
          const projectNames = Array.from(new Set(allEntries.map(e => e.projectName))).sort();
          const validFilter = logFilter && projectNames.includes(logFilter) ? logFilter : null;
          const filtered = validFilter ? allEntries.filter(e => e.projectName === validFilter) : allEntries;
          return (
            <div className="mt-8 rounded-xl p-5 bg-white shadow-sm" style={{ border: '1px solid #e2e8f0' }}>
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-bold uppercase tracking-wider text-slate-600">📝 Priority Log</h3>
                  <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200">
                    {validFilter ? `${filtered.length} of ${allEntries.length}` : `${allEntries.length} ${allEntries.length === 1 ? 'entry' : 'entries'}`}
                  </span>
                </div>
                <button
                  onClick={async () => {
                    if (!window.confirm(`Clear all ${allEntries.length} priority log entries? This cannot be undone.`)) return;
                    await ModulesModel.clearPolarisPriorityLog();
                    await onRefresh();
                    setLogFilter(null);
                  }}
                  className="px-2.5 py-1 text-[10px] font-semibold rounded bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200"
                >Clear All</button>
              </div>
              <div className="flex flex-wrap items-center gap-1.5 mb-3 pb-3 border-b border-slate-100">
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mr-1">Project</span>
                <button
                  onClick={() => setLogFilter(null)}
                  className={`text-[11px] px-2 py-0.5 rounded-full border ${!validFilter ? 'bg-indigo-100 border-indigo-300 text-indigo-700' : 'bg-white border-slate-200 text-slate-500 hover:border-indigo-300'}`}
                >All</button>
                {projectNames.map(name => (
                  <button
                    key={name}
                    onClick={() => setLogFilter(name)}
                    className={`text-[11px] px-2 py-0.5 rounded-full border max-w-[200px] truncate ${validFilter === name ? 'bg-indigo-100 border-indigo-300 text-indigo-700' : 'bg-white border-slate-200 text-slate-600 hover:border-indigo-300'}`}
                    title={name}
                  >{name}</button>
                ))}
              </div>
              {filtered.length === 0 ? (
                <p className="text-xs text-slate-400 italic text-center py-4">No entries match this filter.</p>
              ) : (
                <ul className="space-y-2 max-h-72 overflow-y-auto">
                  {filtered.map(entry => (
                    <li key={entry.id} className="text-xs text-slate-600 flex gap-3 items-start group hover:bg-slate-50 rounded px-2 py-1.5">
                      <span className="text-slate-400 w-36 flex-shrink-0">{new Date(entry.createdAt).toLocaleString()}</span>
                      <span className="text-slate-900 font-medium flex-shrink-0">{entry.projectName}</span>
                      <span className="text-indigo-600 flex-shrink-0">#{entry.fromPosition} → #{entry.toPosition}</span>
                      {entry.reason && <span className="text-slate-500 italic flex-1">— {entry.reason}</span>}
                      {entry.userName && <span className="text-slate-400 flex-shrink-0">{entry.userName}</span>}
                      <button
                        onClick={async () => {
                          if (!window.confirm('Delete this priority log entry?')) return;
                          await ModulesModel.deletePolarisPriorityLogEntry(entry.id);
                          await onRefresh();
                        }}
                        className="text-rose-500 hover:text-rose-700 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                        title="Delete entry"
                      >×</button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })()}
      </div>

      {showAddForm && (
        <PolarisProjectFormModal onClose={() => setShowAddForm(false)} onSave={handleSaveProject} />
      )}
      {editProject && (
        <PolarisProjectFormModal project={editProject} onClose={() => setEditProject(null)} onSave={handleSaveProject} />
      )}
      {showSummary && (
        <PolarisSummaryModal projects={data?.projects || []} onClose={() => setShowSummary(false)} />
      )}
      {pendingMove && (
        <PolarisReorderReasonModal
          onConfirm={confirmMove}
          onCancel={() => setPendingMove(null)}
          projectName={pendingMove.project?.name}
          fromPos={pendingMove.fromIdx + 1}
          toPos={pendingMove.toIdx + 1}
        />
      )}
      {showEmail && (
        <PolarisEmailModal data={data} userId={user?.user_id} onClose={() => setShowEmail(false)} />
      )}
      {pendingArchive && (
        <PolarisArchiveConfirmModal
          project={pendingArchive}
          onConfirm={confirmArchive}
          onCancel={() => setPendingArchive(null)}
        />
      )}
      {archiveDetail && (
        <PolarisArchiveDetailModal
          project={archiveDetail}
          onClose={() => setArchiveDetail(null)}
          onRestore={async () => {
            const p = archiveDetail;
            setArchiveDetail(null);
            await ModulesModel.setPolarisArchive(p.id, false, '');
            await onRefresh();
          }}
          onDelete={async () => {
            const p = archiveDetail;
            if (!window.confirm(`Permanently delete "${p.name}"? This removes the project from PlanTrack as well, including all tasks, milestones, comments, and Polaris bullets. This cannot be undone.`)) return;
            setArchiveDetail(null);
            const res = await ModulesModel.deletePolarisProject(p.id);
            if (res?.success === false) { alert(`Could not delete: ${res.error || 'unknown error'}`); return; }
            await onRefresh();
          }}
        />
      )}
    </div>
  );
};

// ============================================================================
// CHANGE REQUEST FORM MODULE
// ============================================================================

const CR_STATUS_META = {
  Draft:     { color: '#64748b', bg: '#f1f5f9' },
  Submitted: { color: '#2563eb', bg: '#eff6ff' },
  Approved:  { color: '#16a34a', bg: '#dcfce7' },
  Rejected:  { color: '#dc2626', bg: '#fee2e2' },
  Completed: { color: '#047857', bg: '#d1fae5' },
  Cancelled: { color: '#71717a', bg: '#f4f4f5' },
};

const CR_URGENCY_META = {
  '1-High':   { color: '#dc2626', bg: '#fee2e2', label: '1 — High' },
  '2-Medium': { color: '#d97706', bg: '#fef3c7', label: '2 — Medium' },
  '3-Low':    { color: '#16a34a', bg: '#dcfce7', label: '3 — Low' },
};

const CR_TARGET_SYSTEMS_LIST = ['CoreApp', 'Forth', 'Snowflake', 'Power BI', 'Pipedream', 'AWS', 'Cloudflare', 'Other'];
const CR_CHANGE_TYPES_LIST = ['Normal', 'Emergency'];
const CR_TEAM_OWNERS_LIST = ['DevOps', 'DataOps', 'QA', 'Service Desk', 'Marketing', 'Product', 'Other'];
const CR_REASONS_LIST = ['Enhancement', 'Bug Fix', 'New Feature', 'Compliance', 'Security', 'Infrastructure', 'Other'];

const ChangeRequestStatusBadge = ({ status }) => {
  const meta = CR_STATUS_META[status] || CR_STATUS_META.Draft;
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider"
      style={{ color: meta.color, backgroundColor: meta.bg, border: `1px solid ${meta.color}44` }}>{status}</span>
  );
};

const ChangeRequestUrgencyBadge = ({ urgency }) => {
  const meta = CR_URGENCY_META[urgency] || CR_URGENCY_META['2-Medium'];
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold"
      style={{ color: meta.color, backgroundColor: meta.bg, border: `1px solid ${meta.color}44` }}>{meta.label}</span>
  );
};

const ChangeRequestFormModal = ({ initial, users, settings, onSave, onClose }) => {
  const targetSystems = settings?.targetSystems || CR_TARGET_SYSTEMS_LIST;
  const teamOwners = settings?.teamOwners || CR_TEAM_OWNERS_LIST;
  const reasons = settings?.reasons || CR_REASONS_LIST;
  const isEdit = !!initial?.id;
  const [form, setForm] = useState({
    title: initial?.title || '',
    description: initial?.description || '',
    targetSystem: initial?.targetSystem || 'CoreApp',
    changeType: initial?.changeType || 'Normal',
    teamOwner: initial?.teamOwner || 'DevOps',
    itContactName: initial?.itContactName || '',
    itContactId: initial?.itContactId || null,
    reason: initial?.reason || 'Enhancement',
    businessJustification: initial?.businessJustification || '',
    urgency: initial?.urgency || '2-Medium',
    implementationPlan: initial?.implementationPlan || '',
    implementationDate: initial?.implementationDate || '',
    rollbackPlan: initial?.rollbackPlan || '',
    catchAll: initial?.catchAll || '',
  });
  const [saving, setSaving] = useState(false);
  const [stagedFiles, setStagedFiles] = useState([]); // newly attached files (not yet uploaded)
  const [stagedTcLinks, setStagedTcLinks] = useState([]); // TCMS test cases to link after save
  const [pickerOpen, setPickerOpen] = useState(false);
  const [uploadingMsg, setUploadingMsg] = useState('');

  const handleStageFile = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    const allowed = 3 - stagedFiles.length - (initial?.attachments?.filter(a => (a.category || 'proof_of_testing') === 'proof_of_testing').length || 0);
    if (allowed <= 0) { alert('Maximum 3 proof-of-testing files per CR'); return; }
    const accepted = files.slice(0, allowed);
    const next = [];
    for (const file of accepted) {
      if (file.size > 10 * 1024 * 1024) { alert(`${file.name} exceeds 10 MB`); continue; }
      const fileData = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result);
        r.onerror = reject;
        r.readAsDataURL(file);
      });
      next.push({ fileName: file.name, fileType: file.type, fileSize: file.size, fileData });
    }
    if (files.length > allowed) alert(`Only ${allowed} more file(s) allowed — extra files were dropped.`);
    setStagedFiles(s => [...s, ...next]);
  };

  const validate = () => {
    if (!form.title.trim()) return 'Change title is required';
    if (!form.description.trim()) return 'Change description is required';
    if (!form.implementationPlan.trim()) return 'Implementation Plan is required';
    if (!form.implementationDate) return 'Implementation Date is required';
    return null;
  };

  const handleSubmit = async (shouldSubmit) => {
    const err = validate();
    if (err) { alert(err); return; }
    setSaving(true);
    const payload = { ...form };
    if (!isEdit && shouldSubmit) payload.submit = true;
    const res = await onSave(payload);
    if (res?.success === false) { setSaving(false); alert(res.error || 'Failed to save'); return; }

    // Upload any staged proof-of-testing files + TCMS links now that we have a CR id
    const crId = isEdit ? initial.id : res?.id;
    if (crId && stagedFiles.length > 0) {
      let done = 0;
      for (const f of stagedFiles) {
        setUploadingMsg(`Uploading proof of testing ${++done}/${stagedFiles.length}…`);
        const ur = await ModulesModel.addChangeRequestAttachment(crId, { ...f, category: 'proof_of_testing' });
        if (ur?.success === false) alert(`${f.fileName}: ${ur.error || 'upload failed'}`);
      }
      setUploadingMsg('');
    }
    if (crId && stagedTcLinks.length > 0) {
      setUploadingMsg(`Linking ${stagedTcLinks.length} TCMS test case${stagedTcLinks.length === 1 ? '' : 's'}…`);
      const lr = await ModulesModel.addChangeRequestTestLinks(crId, stagedTcLinks.map(c => c.id));
      if (lr?.success === false) alert(`TCMS link failed: ${lr.error || 'unknown error'}`);
      setUploadingMsg('');
    }

    if (isEdit && shouldSubmit) {
      const sres = await ModulesModel.submitChangeRequest(initial.id);
      if (sres?.success === false) { setSaving(false); alert(sres.error || 'Failed to submit'); return; }
    }
    setSaving(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(15,23,42,0.45)' }}>
      <div className="w-full max-w-3xl bg-white rounded-2xl shadow-xl flex flex-col max-h-[92vh]" style={{ border: '1px solid #e2e8f0' }}>
        <div className="px-6 pt-5 pb-3 border-b border-slate-200">
          <h2 className="text-lg font-bold text-slate-900">{isEdit ? `Edit ${initial.crNumber}` : 'New Change Request'}</h2>
          <p className="text-xs text-slate-500 mt-1">Structured form to formally request modifications to IT systems, infrastructure, or processes.</p>
        </div>
        <div className="px-6 py-4 overflow-y-auto flex-1 space-y-4">
          {/* 1. Title */}
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1 block">Change title <span className="text-rose-600">*</span></label>
            <p className="text-[11px] text-slate-500 mb-1">A concise, high-level label (e.g. "Enable MFA for VPN Users").</p>
            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full bg-white border border-slate-300 rounded px-2 py-1.5 text-sm text-slate-900" />
          </div>
          {/* 2. Description */}
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1 block">Change description <span className="text-rose-600">*</span></label>
            <p className="text-[11px] text-slate-500 mb-1">What component, service, or system is being altered? Addition, modification, or removal?</p>
            <MiniWysiwyg value={form.description} onChange={(v) => setForm({ ...form, description: v })} minHeight="80px" placeholder="Describe the change…" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            {/* 3. Target System */}
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1 block">Target System <span className="text-rose-600">*</span></label>
              <select value={form.targetSystem} onChange={(e) => setForm({ ...form, targetSystem: e.target.value })} className="w-full bg-white border border-slate-300 rounded px-2 py-1.5 text-sm text-slate-900">
                {targetSystems.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            {/* 4. Change type */}
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1 block">Change type <span className="text-rose-600">*</span></label>
              <select value={form.changeType} onChange={(e) => setForm({ ...form, changeType: e.target.value })} className="w-full bg-white border border-slate-300 rounded px-2 py-1.5 text-sm text-slate-900">
                {CR_CHANGE_TYPES_LIST.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <p className="text-[10px] text-slate-500 mt-1">
                <strong>Normal:</strong> needs risk assessment + approval. <strong>Emergency:</strong> expedited for outages/security.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {/* 5. Team owner */}
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1 block">Team owner <span className="text-rose-600">*</span></label>
              <select value={form.teamOwner} onChange={(e) => setForm({ ...form, teamOwner: e.target.value })} className="w-full bg-white border border-slate-300 rounded px-2 py-1.5 text-sm text-slate-900">
                {teamOwners.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            {/* 6. IT Point of contact */}
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1 block">IT Point of contact <span className="text-rose-600">*</span></label>
              <select
                value={form.itContactId || ''}
                onChange={(e) => {
                  const id = e.target.value ? parseInt(e.target.value) : null;
                  const u = (users || []).find(x => x.user_id === id || x.id === id);
                  setForm({ ...form, itContactId: id, itContactName: u ? (u.user_name || u.name) : '' });
                }}
                className="w-full bg-white border border-slate-300 rounded px-2 py-1.5 text-sm text-slate-900"
              >
                <option value="">— Select contact —</option>
                {(users || []).map(u => (
                  <option key={u.user_id || u.id} value={u.user_id || u.id}>{u.user_name || u.name}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {/* 7. Reason */}
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1 block">Reason for change <span className="text-rose-600">*</span></label>
              <select value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className="w-full bg-white border border-slate-300 rounded px-2 py-1.5 text-sm text-slate-900">
                {reasons.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            {/* 9. Urgency */}
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1 block">Urgency <span className="text-rose-600">*</span></label>
              <select value={form.urgency} onChange={(e) => setForm({ ...form, urgency: e.target.value })} className="w-full bg-white border border-slate-300 rounded px-2 py-1.5 text-sm text-slate-900">
                <option value="1-High">1 — High</option>
                <option value="2-Medium">2 — Medium</option>
                <option value="3-Low">3 — Low</option>
              </select>
              <p className="text-[10px] text-slate-500 mt-1">Emergency change type → set to <strong>1-High</strong>.</p>
            </div>
          </div>
          {/* 8. Business Justification */}
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1 block">Business justification</label>
            <p className="text-[11px] text-slate-500 mb-1">Why is this change necessary from a business value / risk / strategic alignment perspective?</p>
            <textarea rows={2} value={form.businessJustification} onChange={(e) => setForm({ ...form, businessJustification: e.target.value })} className="w-full bg-white border border-slate-300 rounded px-2 py-1.5 text-sm text-slate-900" />
          </div>
          {/* 10. Implementation Plan */}
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1 block">Implementation plan <span className="text-rose-600">*</span></label>
            <p className="text-[11px] text-slate-500 mb-1">Detailed, step-by-step outline of how the approved change will be executed in a controlled, safe manner.</p>
            <textarea rows={4} value={form.implementationPlan} onChange={(e) => setForm({ ...form, implementationPlan: e.target.value })} className="w-full bg-white border border-slate-300 rounded px-2 py-1.5 text-sm text-slate-900 font-mono" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            {/* 11. Implementation Date */}
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1 block">Implementation date <span className="text-rose-600">*</span></label>
              <input type="date" value={form.implementationDate || ''} onChange={(e) => setForm({ ...form, implementationDate: e.target.value })} className="w-full bg-white border border-slate-300 rounded px-2 py-1.5 text-sm text-slate-900" />
            </div>
          </div>
          {/* 12. Rollback Plan */}
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1 block">Rollback plan <span className="text-slate-400 font-normal normal-case tracking-normal">(mandatory for DevOps team)</span></label>
            <p className="text-[11px] text-slate-500 mb-1">Step-by-step procedure for reverting if the change fails or introduces unacceptable risk.</p>
            <textarea rows={2} value={form.rollbackPlan} onChange={(e) => setForm({ ...form, rollbackPlan: e.target.value })} className="w-full bg-white border border-slate-300 rounded px-2 py-1.5 text-sm text-slate-900" />
          </div>
          {/* 13. Proof of Testing */}
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1 block">Proof of testing</label>
            <p className="text-[11px] text-slate-500 mb-2">Attach evidence that the change was tested in lower environments — files (screenshots, test reports, recordings) or a link to TCMS test cases. Max 3 files, 10 MB each.</p>
            {/* Existing TCMS links (edit mode) */}
            {isEdit && (initial.testLinks || []).map(l => {
              const meta = CR_TC_STATUS_META[l.tcStatus || 'pending'] || CR_TC_STATUS_META.pending;
              return (
                <div key={`existing-tc-${l.id}`} className="flex items-center gap-2 text-xs text-slate-600 px-2 py-1 bg-slate-50 rounded mb-1">
                  <span className="font-mono text-slate-500 w-16 flex-shrink-0">{l.tcCode || `#${l.testCaseId}`}</span>
                  <span className="flex-1 truncate">🔗 {l.tcTitle || 'TCMS test case'}</span>
                  <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold" style={{ color: meta.color, backgroundColor: meta.bg, border: `1px solid ${meta.color}44` }}>{meta.icon} {meta.label}</span>
                  <span className="text-slate-400 text-[10px]">already linked</span>
                </div>
              );
            })}
            {/* Staged TCMS links (new in this session) */}
            {stagedTcLinks.map(c => {
              const meta = CR_TC_STATUS_META[c.status || 'pending'] || CR_TC_STATUS_META.pending;
              return (
                <div key={`staged-tc-${c.id}`} className="flex items-center gap-2 text-xs text-slate-700 px-2 py-1 bg-indigo-50 border border-indigo-100 rounded mb-1">
                  <span className="font-mono text-slate-500 w-16 flex-shrink-0">{c.tcCode}</span>
                  <span className="flex-1 truncate">🔗 {c.title}</span>
                  <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold" style={{ color: meta.color, backgroundColor: meta.bg, border: `1px solid ${meta.color}44` }}>{meta.icon} {meta.label}</span>
                  <span className="text-slate-400 text-[10px]">ready to link</span>
                  <button onClick={() => setStagedTcLinks(s => s.filter(x => x.id !== c.id))} className="text-rose-500 hover:text-rose-700">×</button>
                </div>
              );
            })}
            {/* Existing files (edit mode) */}
            {isEdit && (initial.attachments || []).filter(a => (a.category || 'proof_of_testing') === 'proof_of_testing').map(a => (
              <div key={a.id} className="flex items-center gap-2 text-xs text-slate-600 px-2 py-1 bg-slate-50 rounded mb-1">
                <span className="flex-1 truncate">📎 {a.fileName}</span>
                <span className="text-slate-400">{(a.fileSize/1024).toFixed(0)} KB · already uploaded</span>
              </div>
            ))}
            {/* Staged files */}
            {stagedFiles.map((f, i) => (
              <div key={i} className="flex items-center gap-2 text-xs text-slate-700 px-2 py-1 bg-indigo-50 border border-indigo-100 rounded mb-1">
                <span className="flex-1 truncate">📎 {f.fileName}</span>
                <span className="text-slate-400">{(f.fileSize/1024).toFixed(0)} KB · ready to upload</span>
                <button onClick={() => setStagedFiles(s => s.filter((_, j) => j !== i))} className="text-rose-500 hover:text-rose-700">×</button>
              </div>
            ))}
            <div className="flex flex-wrap gap-2 items-center mt-1">
              {((isEdit ? (initial.attachments || []).filter(a => (a.category || 'proof_of_testing') === 'proof_of_testing').length : 0) + stagedFiles.length) < 3 && (
                <label className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded bg-white border border-dashed border-slate-300 text-slate-700 hover:border-indigo-400 hover:text-indigo-700 cursor-pointer">
                  ↑ Upload file
                  <input type="file" multiple onChange={handleStageFile} className="hidden" />
                </label>
              )}
              <button
                type="button"
                onClick={() => setPickerOpen(true)}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded bg-white border border-dashed border-slate-300 text-slate-700 hover:border-indigo-400 hover:text-indigo-700"
              >🔗 Link TCMS</button>
            </div>
            <p className="text-[10px] text-slate-400 mt-1">Files: Word, Excel, PPT, PDF, Image, Video, Audio. TCMS: any test case whose pass status proves the change works.</p>
          </div>
          {/* 14. Catch-all */}
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1 block">Catch-all</label>
            <p className="text-[11px] text-slate-500 mb-1">Any other remarks for this change.</p>
            <input value={form.catchAll} onChange={(e) => setForm({ ...form, catchAll: e.target.value })} className="w-full bg-white border border-slate-300 rounded px-2 py-1.5 text-sm text-slate-900" />
          </div>
        </div>
        <div className="px-6 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
          {uploadingMsg && <span className="text-xs text-indigo-600 mr-auto">{uploadingMsg}</span>}
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-900">Cancel</button>
          <button onClick={() => handleSubmit(false)} disabled={saving} className="px-4 py-2 text-sm font-semibold rounded bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50">
            {saving ? 'Saving…' : 'Save Draft'}
          </button>
          <button onClick={() => handleSubmit(true)} disabled={saving} className="px-4 py-2 text-sm font-semibold rounded bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50">
            {saving ? 'Saving…' : 'Save & Submit'}
          </button>
        </div>
      </div>
      {pickerOpen && (
        <ChangeRequestTcmsLinkModal
          crId={initial?.id}
          alreadyLinkedIds={[
            ...((initial?.testLinks || []).map(l => l.testCaseId)),
            ...stagedTcLinks.map(c => c.id),
          ]}
          onClose={() => setPickerOpen(false)}
          onSave={async (cases) => {
            setStagedTcLinks(s => [...s, ...cases]);
            return { success: true };
          }}
        />
      )}
    </div>
  );
};

const ChangeRequestCompleteModal = ({ cr, onSubmit, onClose }) => {
  const [closureNotes, setClosureNotes] = useState('');
  const [actualCompletionDate, setActualCompletionDate] = useState(new Date().toISOString().slice(0, 10));
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(15,23,42,0.45)' }}>
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-6" style={{ border: '1px solid #e2e8f0' }}>
        <h2 className="text-lg font-bold text-slate-900 mb-1">🎉 Mark as Completed</h2>
        <p className="text-sm text-slate-600 mb-3">{cr.crNumber}: <strong>{cr.title}</strong></p>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-bold text-slate-700 mb-1 block">Actual completion date</label>
            <input type="date" value={actualCompletionDate} onChange={(e) => setActualCompletionDate(e.target.value)} className="w-full bg-white border border-slate-300 rounded px-2 py-1.5 text-sm text-slate-900" />
          </div>
          <div>
            <label className="text-xs font-bold text-slate-700 mb-1 block">Closure notes <span className="text-slate-400 font-normal">(what happened, issues, follow-ups)</span></label>
            <MiniWysiwyg value={closureNotes} onChange={setClosureNotes} minHeight="90px" placeholder="Implementation completed successfully without issues. Verified in PROD. No rollback needed." />
            <p className="text-[11px] text-slate-400 mt-1">Strongly recommended for audit and post-implementation review.</p>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-900">Cancel</button>
          <button onClick={() => onSubmit({ closureNotes: closureNotes.trim(), actualCompletionDate })} className="px-4 py-2 text-sm font-semibold rounded bg-emerald-600 hover:bg-emerald-700 text-white">Mark Completed</button>
        </div>
      </div>
    </div>
  );
};

const ChangeRequestComments = ({ crId, comments, user, onRefresh }) => {
  const [draft, setDraft] = useState('');
  const [posting, setPosting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingText, setEditingText] = useState('');
  // Normalize user shape — `name`/`role` from auth/me, falls back to user_name/user_role
  const userName = user?.user_name || user?.name || '';
  const userRole = user?.user_role || user?.role || '';

  const post = async () => {
    if (!draft.trim()) return;
    setPosting(true);
    const r = await ModulesModel.addChangeRequestComment(crId, draft.trim());
    setPosting(false);
    if (r?.success === false) { alert(r.error || 'Failed to post'); return; }
    setDraft('');
    await onRefresh();
  };
  const saveEdit = async (id) => {
    if (!editingText.trim()) return;
    const r = await ModulesModel.updateChangeRequestComment(id, editingText.trim());
    if (r?.success === false) { alert(r.error || 'Failed to edit'); return; }
    setEditingId(null); setEditingText('');
    await onRefresh();
  };
  const remove = async (id) => {
    if (!window.confirm('Delete this comment?')) return;
    const r = await ModulesModel.deleteChangeRequestComment(id);
    if (r?.success === false) { alert(r.error || 'Failed to delete'); return; }
    await onRefresh();
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-5 mb-4" style={{ border: '1px solid #e2e8f0' }}>
      <h3 className="text-sm font-bold uppercase tracking-wider text-slate-700 mb-3">💬 Comments ({comments?.length || 0})</h3>
      {(!comments || comments.length === 0) && <p className="text-xs text-slate-400 italic mb-3">No comments yet. Start the conversation below.</p>}
      <ul className="space-y-3 mb-4">
        {(comments || []).map(c => {
          const canEditOwn = c.userName === userName || userRole === 'admin';
          const isEditing = editingId === c.id;
          return (
            <li key={c.id} className="flex gap-3 group">
              <div className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-xs font-bold flex-shrink-0">
                {(c.userName || '?').slice(0, 1).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-semibold text-slate-900">{c.userName}</span>
                  <span className="text-[11px] text-slate-400">{new Date(c.createdAt).toLocaleString()}</span>
                  {canEditOwn && !isEditing && (
                    <span className="opacity-0 group-hover:opacity-100 transition-opacity flex gap-2 ml-auto">
                      <button onClick={() => { setEditingId(c.id); setEditingText(c.content); }} className="text-xs text-slate-500 hover:text-indigo-600">edit</button>
                      <button onClick={() => remove(c.id)} className="text-xs text-rose-500 hover:text-rose-700">delete</button>
                    </span>
                  )}
                </div>
                {isEditing ? (
                  <>
                    <textarea autoFocus value={editingText} onChange={(e) => setEditingText(e.target.value)} rows={3} className="w-full bg-white border border-slate-300 rounded px-2 py-1.5 text-sm text-slate-900" />
                    <div className="flex gap-2 mt-1">
                      <button onClick={() => saveEdit(c.id)} className="text-xs text-emerald-600 font-semibold">Save</button>
                      <button onClick={() => { setEditingId(null); setEditingText(''); }} className="text-xs text-slate-500">Cancel</button>
                    </div>
                  </>
                ) : (
                  <p className="text-sm text-slate-700 whitespace-pre-wrap">{c.content}</p>
                )}
              </div>
            </li>
          );
        })}
      </ul>
      <div className="flex gap-2 items-end pt-3 border-t border-slate-100">
        <div className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-xs font-bold flex-shrink-0">
          {(userName || '?').slice(0, 1).toUpperCase()}
        </div>
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') { e.preventDefault(); post(); } }}
          placeholder="Add a comment… (Ctrl+Enter to post)"
          rows={2}
          className="flex-1 bg-white border border-slate-300 rounded px-2 py-1.5 text-sm text-slate-900"
        />
        <button onClick={post} disabled={posting || !draft.trim()} className="px-3 py-1.5 text-xs font-semibold rounded bg-indigo-600 hover:bg-indigo-700 text-white disabled:opacity-50">
          {posting ? 'Posting…' : 'Post'}
        </button>
      </div>
    </div>
  );
};

const ChangeRequestReviewModal = ({ cr, decision, onSubmit, onClose }) => {
  const [notes, setNotes] = useState('');
  const isReject = decision === 'Rejected';
  const isApprove = decision === 'Approved';
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(15,23,42,0.45)' }}>
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-6" style={{ border: '1px solid #e2e8f0' }}>
        <h2 className="text-lg font-bold text-slate-900 mb-1">{isApprove ? '✅ Approve' : '❌ Reject'} change request</h2>
        <p className="text-sm text-slate-600 mb-3">{cr.crNumber}: <strong>{cr.title}</strong></p>
        <label className="text-xs font-bold text-slate-700 mb-1 block">
          Review notes {isReject && <span className="text-rose-600">*</span>}
        </label>
        <textarea
          autoFocus rows={4}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder={isReject ? 'Why is this being rejected?' : 'Optional notes for the requester'}
          className="w-full bg-white border border-slate-300 rounded px-2 py-1.5 text-sm text-slate-900"
        />
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-900">Cancel</button>
          <button
            onClick={() => onSubmit(notes.trim())}
            disabled={isReject && !notes.trim()}
            className={`px-4 py-2 text-sm font-semibold rounded text-white disabled:opacity-50 ${isApprove ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-rose-600 hover:bg-rose-700'}`}
          >Confirm {decision}</button>
        </div>
      </div>
    </div>
  );
};

const ChangeRequestDetailView = ({ detail, user, onBack, onRefresh, onEdit, onDelete }) => {
  const [review, setReview] = useState(null); // 'Approved' | 'Rejected'
  const [completing, setCompleting] = useState(false);
  const [linkingTcms, setLinkingTcms] = useState(false);
  const cr = detail;
  // The auth/me endpoint returns `{ name, role, ... }`. Some places in the codebase
  // also stash `user_name`/`user_role`. Normalize so the permission gates work
  // regardless of which shape the parent passed in.
  const userName = user?.user_name || user?.name || '';
  const userRole = user?.user_role || user?.role || '';
  const approvals = cr.approvals || [];
  const currentStep = approvals.find(a => a.decision === 'pending');
  const hasChain = approvals.length > 0;
  const isAdmin = userRole === 'admin';
  const isOwner = cr.requestedBy === userName;
  // If the CR has an approval chain, only the current pending step's assigned approver
  // can review (admin always allowed as override). Otherwise legacy: any admin/manager.
  // Requesters can never review their own CR — they must use Cancel to withdraw it,
  // and the chain (incl. final approver) must run for every submission regardless of role.
  const canReview = cr.status === 'Submitted' && !isOwner && (
    hasChain
      ? (isAdmin || (currentStep?.assignedToUserName && currentStep.assignedToUserName === userName))
      : ['admin','manager','devops_manager','dataops_manager','service_desk_manager'].includes(userRole)
  );
  const canEdit = (isAdmin || userRole === 'manager' || isOwner) && ['Draft','Submitted','Rejected'].includes(cr.status);
  const canDelete = isAdmin || (isOwner && cr.status === 'Draft');
  const canSubmit = isOwner && ['Draft','Rejected'].includes(cr.status);
  const canComplete = ['admin','manager','devops_manager','dataops_manager','service_desk_manager'].includes(userRole) && cr.status === 'Approved';

  const handleReview = async (notes) => {
    const res = await ModulesModel.reviewChangeRequest(cr.id, review, notes);
    if (res?.success === false) { alert(res.error || 'Failed'); return; }
    setReview(null);
    await onRefresh();
  };
  const handleSubmit = async () => {
    const res = await ModulesModel.submitChangeRequest(cr.id);
    if (res?.success === false) { alert(res.error || 'Failed'); return; }
    await onRefresh();
  };
  const handleCompleteSubmit = async (payload) => {
    const res = await ModulesModel.completeChangeRequest(cr.id, payload);
    if (res?.success === false) { alert(res.error || 'Failed'); return; }
    setCompleting(false);
    await onRefresh();
  };
  const handleCancel = async () => {
    if (!window.confirm('Cancel this change request?')) return;
    const res = await ModulesModel.cancelChangeRequest(cr.id);
    if (res?.success === false) { alert(res.error || 'Failed'); return; }
    await onRefresh();
  };
  const handleAttachUpload = async (e, category = 'proof_of_testing') => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    const existing = (cr.attachments || []).filter(a => (a.category || 'proof_of_testing') === category).length;
    if (existing + files.length > 3) { alert(`Maximum 3 ${category === 'implementation_report' ? 'implementation report files' : 'proof-of-testing files'} per CR`); return; }
    for (const file of files) {
      if (file.size > 10 * 1024 * 1024) { alert(`${file.name} exceeds 10 MB`); continue; }
      const fileData = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result);
        r.onerror = reject;
        r.readAsDataURL(file);
      });
      const res = await ModulesModel.addChangeRequestAttachment(cr.id, {
        fileName: file.name, fileType: file.type, fileSize: file.size, fileData, category,
      });
      if (res?.success === false) { alert(`${file.name}: ${res.error}`); }
    }
    await onRefresh();
  };
  const handleAttachDelete = async (attId) => {
    if (!window.confirm('Delete this attachment?')) return;
    await ModulesModel.deleteChangeRequestAttachment(attId);
    await onRefresh();
  };
  const handleAttachDownload = async (att) => {
    const d = await ModulesModel.downloadChangeRequestAttachment(att.id);
    if (!d?.fileData) { alert('Could not load file'); return; }
    const a = document.createElement('a');
    a.href = d.fileData; a.download = d.fileName || att.fileName;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-6">
      <button onClick={onBack} className="text-sm text-slate-500 hover:text-slate-900 mb-4">← Back to list</button>
      <div className="bg-white rounded-xl shadow-sm p-6 mb-4" style={{ border: '1px solid #e2e8f0' }}>
        <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs font-mono text-slate-500">{cr.crNumber}</span>
              <ChangeRequestStatusBadge status={cr.status} />
              <ChangeRequestUrgencyBadge urgency={cr.urgency} />
              {cr.changeType === 'Emergency' && (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold bg-rose-100 text-rose-700 border border-rose-300">🚨 EMERGENCY</span>
              )}
            </div>
            <h1 className="text-xl font-bold text-slate-900">{cr.title}</h1>
            <p className="text-xs text-slate-500 mt-1">
              Requested by <strong>{cr.requestedBy || '—'}</strong> · {new Date(cr.createdAt).toLocaleDateString()}
              {cr.reviewedBy && <> · {cr.status} by <strong>{cr.reviewedBy}</strong></>}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {canEdit && <button onClick={onEdit} className="px-3 py-1.5 text-xs font-semibold rounded bg-white border border-slate-300 text-slate-700 hover:border-indigo-400">Edit</button>}
            {canSubmit && <button onClick={handleSubmit} className="px-3 py-1.5 text-xs font-semibold rounded bg-blue-600 hover:bg-blue-700 text-white">Submit for Review</button>}
            {canReview && <>
              <button onClick={() => setReview('Approved')} className="px-3 py-1.5 text-xs font-semibold rounded bg-emerald-600 hover:bg-emerald-700 text-white">Approve</button>
              <button onClick={() => setReview('Rejected')} className="px-3 py-1.5 text-xs font-semibold rounded bg-rose-600 hover:bg-rose-700 text-white">Reject</button>
            </>}
            {canComplete && <button onClick={() => setCompleting(true)} className="px-3 py-1.5 text-xs font-semibold rounded bg-emerald-600 hover:bg-emerald-700 text-white">Mark Completed</button>}
            {!['Completed','Cancelled'].includes(cr.status) && (isOwner || ['admin','manager'].includes(userRole)) && (
              <button onClick={handleCancel} className="px-3 py-1.5 text-xs font-semibold rounded bg-white border border-slate-300 text-slate-600 hover:border-amber-400 hover:text-amber-700">Cancel CR</button>
            )}
            <button onClick={() => printChangeRequest(cr)} className="px-3 py-1.5 text-xs font-semibold rounded bg-white border border-slate-300 text-slate-700 hover:border-indigo-400" title="Print or Save as PDF">🖨 Print / PDF</button>
            <button onClick={() => exportChangeRequestWord(cr)} className="px-3 py-1.5 text-xs font-semibold rounded bg-white border border-slate-300 text-slate-700 hover:border-blue-400" title="Download as .doc">📄 Export Word</button>
            {canDelete && <button onClick={onDelete} className="px-3 py-1.5 text-xs font-semibold rounded bg-rose-50 border border-rose-200 text-rose-700 hover:bg-rose-100">Delete</button>}
          </div>
        </div>

        {hasChain && (
          <div className="mb-4 p-3 rounded-lg bg-slate-50 border border-slate-200">
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2">Approval Chain</div>
            <div className="flex flex-wrap items-stretch gap-2">
              {approvals.map((step, i) => {
                let bg = '#f8fafc', border = '#e2e8f0', color = '#64748b', icon = '○';
                if (step.decision === 'approved') { bg = '#dcfce7'; border = '#86efac'; color = '#166534'; icon = '✓'; }
                else if (step.decision === 'rejected') { bg = '#fee2e2'; border = '#fca5a5'; color = '#991b1b'; icon = '✗'; }
                else if (step.decision === 'pending' && step.id === currentStep?.id) { bg = '#fef3c7'; border = '#fcd34d'; color = '#92400e'; icon = '⏳'; }
                return (
                  <React.Fragment key={step.id}>
                    <div className="rounded-lg px-3 py-2 flex-1 min-w-[180px]" style={{ background: bg, border: `1px solid ${border}` }}>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-base leading-none" style={{ color }}>{icon}</span>
                        <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color }}>Step {step.stepNumber}{step.approverLabel ? ` · ${step.approverLabel}` : ''}</span>
                      </div>
                      <div className="text-sm font-semibold text-slate-900">{step.assignedToUserName}</div>
                      {step.decision !== 'pending' && step.decidedAt && (
                        <div className="text-[10px] text-slate-500 mt-0.5">{step.decision} · {new Date(step.decidedAt).toLocaleDateString()}</div>
                      )}
                      {step.decision === 'pending' && step.id === currentStep?.id && (
                        <div className="text-[10px] text-amber-700 mt-0.5 font-semibold">Awaiting decision</div>
                      )}
                      {step.decisionNotes && (
                        <div className="text-[11px] text-slate-600 italic mt-1 truncate" title={step.decisionNotes}>— {step.decisionNotes}</div>
                      )}
                    </div>
                    {i < approvals.length - 1 && <div className="self-center text-slate-300">→</div>}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        )}
        {cr.reviewNotes && (
          <div className={`mb-4 px-3 py-2 rounded text-sm border ${cr.status === 'Rejected' ? 'bg-rose-50 border-rose-200 text-rose-800' : 'bg-emerald-50 border-emerald-200 text-emerald-800'}`}>
            <div className="text-[10px] font-bold uppercase mb-1">Final review notes</div>
            {cr.reviewNotes}
          </div>
        )}
        {cr.status === 'Completed' && (cr.closureNotes || cr.actualCompletionDate) && (
          <div className="mb-4 px-3 py-2 rounded text-sm border bg-emerald-50 border-emerald-200 text-emerald-900">
            <div className="text-[10px] font-bold uppercase mb-1 text-emerald-700">🎉 Closure {cr.actualCompletionDate && <span className="font-normal normal-case tracking-normal text-emerald-700">· Completed on {cr.actualCompletionDate}</span>}</div>
            {cr.closureNotes ? <div className="prose prose-sm max-w-none [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5" dangerouslySetInnerHTML={{ __html: sanitizeBugHtml(cr.closureNotes) }} /> : <p className="italic text-emerald-700">No closure notes recorded.</p>}
          </div>
        )}

        <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
          <div><div className="text-[10px] font-bold uppercase text-slate-500">Target System</div><div className="text-slate-900">{cr.targetSystem}</div></div>
          <div><div className="text-[10px] font-bold uppercase text-slate-500">Change Type</div><div className="text-slate-900">{cr.changeType}</div></div>
          <div><div className="text-[10px] font-bold uppercase text-slate-500">Team Owner</div><div className="text-slate-900">{cr.teamOwner}</div></div>
          <div><div className="text-[10px] font-bold uppercase text-slate-500">IT Point of Contact</div><div className="text-slate-900">{cr.itContactName || '—'}</div></div>
          <div><div className="text-[10px] font-bold uppercase text-slate-500">Reason</div><div className="text-slate-900">{cr.reason}</div></div>
          <div><div className="text-[10px] font-bold uppercase text-slate-500">Implementation Date</div><div className="text-slate-900">{cr.implementationDate || '—'}</div></div>
        </div>

        <div className="mt-5 space-y-4">
          <div>
            <div className="text-[10px] font-bold uppercase text-slate-500 mb-1">Description</div>
            <div className="text-sm text-slate-700 prose prose-sm max-w-none [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5" dangerouslySetInnerHTML={{ __html: sanitizeBugHtml(cr.description) }} />
          </div>
          {cr.businessJustification && (
            <div>
              <div className="text-[10px] font-bold uppercase text-slate-500 mb-1">Business Justification</div>
              <p className="text-sm text-slate-700 whitespace-pre-wrap">{cr.businessJustification}</p>
            </div>
          )}
          <div>
            <div className="text-[10px] font-bold uppercase text-slate-500 mb-1">Implementation Plan</div>
            <pre className="text-sm text-slate-700 whitespace-pre-wrap bg-slate-50 rounded p-3 border border-slate-200 font-mono">{cr.implementationPlan}</pre>
          </div>
          {cr.rollbackPlan && (
            <div>
              <div className="text-[10px] font-bold uppercase text-slate-500 mb-1">Rollback Plan</div>
              <p className="text-sm text-slate-700 whitespace-pre-wrap">{cr.rollbackPlan}</p>
            </div>
          )}
          {cr.catchAll && (
            <div>
              <div className="text-[10px] font-bold uppercase text-slate-500 mb-1">Remarks</div>
              <p className="text-sm text-slate-700 whitespace-pre-wrap">{cr.catchAll}</p>
            </div>
          )}
        </div>
      </div>

      {/* Attachments — two categories + linked TCMS in Proof of Testing */}
      {(() => {
        const proof = (cr.attachments || []).filter(a => (a.category || 'proof_of_testing') === 'proof_of_testing');
        const reports = (cr.attachments || []).filter(a => a.category === 'implementation_report');
        const tcLinks = cr.testLinks || [];

        return (
          <>
            <div className="bg-white rounded-xl shadow-sm p-5 mb-4" style={{ border: '1px solid #e2e8f0' }}>
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <h3 className="text-sm font-bold uppercase tracking-wider text-slate-700">📎 Proof of Testing</h3>
                <div className="flex items-center gap-2">
                  <button onClick={() => setLinkingTcms(true)} className="text-xs font-semibold text-indigo-600 hover:text-indigo-700">🔗 Link TCMS</button>
                  {proof.length < 3 && (
                    <label className="text-xs font-semibold text-indigo-600 hover:text-indigo-700 cursor-pointer">
                      + Upload file
                      <input type="file" multiple onChange={(e) => handleAttachUpload(e, 'proof_of_testing')} className="hidden" />
                    </label>
                  )}
                </div>
              </div>

              {/* Linked TCMS test cases */}
              {tcLinks.length > 0 && (
                <div className="mb-3">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">🔗 Linked TCMS test cases</div>
                  <ul className="space-y-1.5">
                    {tcLinks.map(l => {
                      const meta = CR_TC_STATUS_META[l.tcStatus || 'pending'] || CR_TC_STATUS_META.pending;
                      return (
                        <li key={l.id} className="flex items-center gap-2 text-sm px-2 py-1.5 rounded bg-slate-50 border border-slate-100">
                          <span className="text-xs font-mono text-slate-500 w-20 flex-shrink-0">{l.tcCode || `TC #${l.testCaseId}`}</span>
                          <div className="flex-1 min-w-0">
                            <div className="text-slate-900 truncate">{l.tcTitle || <span className="italic text-slate-400">case not found</span>}</div>
                            <div className="text-[11px] text-slate-500 truncate">
                              {l.projectName || '—'}
                              {l.executedBy && <> · last run by {l.executedBy}</>}
                              {l.executedAt && <> · {new Date(l.executedAt).toLocaleDateString()}</>}
                            </div>
                          </div>
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold flex-shrink-0" style={{ color: meta.color, backgroundColor: meta.bg, border: `1px solid ${meta.color}44` }}>
                            {meta.icon} {meta.label}
                          </span>
                          <button
                            onClick={async () => {
                              if (!window.confirm(`Unlink ${l.tcCode || 'this test case'}?`)) return;
                              const r = await ModulesModel.removeChangeRequestTestLink(l.id);
                              if (r?.success === false) { alert(r.error || 'Failed'); return; }
                              await onRefresh();
                            }}
                            className="text-rose-500 hover:text-rose-700 text-xs flex-shrink-0"
                          >×</button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}

              {/* Uploaded files */}
              <div>
                {tcLinks.length > 0 && proof.length > 0 && (
                  <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">📂 Uploaded files ({proof.length}/3)</div>
                )}
                {!proof.length && !tcLinks.length ? (
                  <p className="text-xs text-slate-400 italic">No proof of testing yet. Upload files (max 3 × 10 MB) or link TCMS test cases.</p>
                ) : proof.length > 0 ? (
                  <ul className="space-y-1.5">
                    {proof.map(a => (
                      <li key={a.id} className="flex items-center gap-2 text-sm">
                        <button onClick={() => handleAttachDownload(a)} className="text-indigo-600 hover:underline truncate flex-1">{a.fileName}</button>
                        <span className="text-xs text-slate-400">{(a.fileSize/1024).toFixed(0)} KB · {a.uploadedBy} · {new Date(a.createdAt).toLocaleDateString()}</span>
                        <button onClick={() => handleAttachDelete(a.id)} className="text-rose-500 hover:text-rose-700 text-xs">×</button>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
            </div>

            {/* Implementation Report */}
            <div className="bg-white rounded-xl shadow-sm p-5 mb-4" style={{ border: '1px solid #e2e8f0' }}>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold uppercase tracking-wider text-slate-700">📑 Implementation Report ({reports.length}/3)</h3>
                {reports.length < 3 && (
                  <label className="text-xs font-semibold text-indigo-600 hover:text-indigo-700 cursor-pointer">
                    + Upload file
                    <input type="file" multiple onChange={(e) => handleAttachUpload(e, 'implementation_report')} className="hidden" />
                  </label>
                )}
              </div>
              {!reports.length ? (
                <p className="text-xs text-slate-400 italic">No files yet. Max 3 files, 10 MB each.</p>
              ) : (
                <ul className="space-y-1.5">
                  {reports.map(a => (
                    <li key={a.id} className="flex items-center gap-2 text-sm">
                      <button onClick={() => handleAttachDownload(a)} className="text-indigo-600 hover:underline truncate flex-1">{a.fileName}</button>
                      <span className="text-xs text-slate-400">{(a.fileSize/1024).toFixed(0)} KB · {a.uploadedBy} · {new Date(a.createdAt).toLocaleDateString()}</span>
                      <button onClick={() => handleAttachDelete(a.id)} className="text-rose-500 hover:text-rose-700 text-xs">×</button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </>
        );
      })()}

      {/* Comments */}
      <ChangeRequestComments crId={cr.id} comments={cr.comments} user={user} onRefresh={onRefresh} />

      {/* Timeline */}
      <div className="bg-white rounded-xl shadow-sm p-5" style={{ border: '1px solid #e2e8f0' }}>
        <h3 className="text-sm font-bold uppercase tracking-wider text-slate-700 mb-3">📜 Activity</h3>
        <ul className="space-y-2">
          {(cr.history || []).map(h => (
            <li key={h.id} className="text-xs text-slate-600 flex gap-3">
              <span className="text-slate-400 w-32 flex-shrink-0">{new Date(h.createdAt).toLocaleString()}</span>
              <span className="text-slate-700 font-medium capitalize">{h.action}</span>
              {h.newValue && <span className="text-indigo-600">{h.oldValue ? `${h.oldValue} → ` : ''}{h.newValue}</span>}
              {h.notes && <span className="italic text-slate-500">— {h.notes}</span>}
              <span className="text-slate-400 ml-auto">{h.userName || ''}</span>
            </li>
          ))}
        </ul>
      </div>

      {review && <ChangeRequestReviewModal cr={cr} decision={review} onSubmit={handleReview} onClose={() => setReview(null)} />}
      {completing && <ChangeRequestCompleteModal cr={cr} onSubmit={handleCompleteSubmit} onClose={() => setCompleting(false)} />}
      {linkingTcms && (
        <ChangeRequestTcmsLinkModal
          crId={cr.id}
          alreadyLinkedIds={(cr.testLinks || []).map(l => l.testCaseId)}
          onClose={() => setLinkingTcms(false)}
          onSave={async (cases) => {
            const r = await ModulesModel.addChangeRequestTestLinks(cr.id, cases.map(c => c.id));
            if (r?.success !== false) await onRefresh();
            return r;
          }}
        />
      )}
    </div>
  );
};

const CR_TC_STATUS_META = {
  pass:    { color: '#059669', bg: '#d1fae5', icon: '✓', label: 'Pass' },
  fail:    { color: '#dc2626', bg: '#fee2e2', icon: '✗', label: 'Fail' },
  blocked: { color: '#d97706', bg: '#fef3c7', icon: '🚫', label: 'Blocked' },
  pending: { color: '#64748b', bg: '#f1f5f9', icon: '○', label: 'Pending' },
};

const ChangeRequestTcmsLinkModal = ({ crId, alreadyLinkedIds, onSave, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [testCases, setTestCases] = useState([]);
  const [projects, setProjects] = useState([]);
  const [search, setSearch] = useState('');
  const [projectFilter, setProjectFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selected, setSelected] = useState(() => new Set());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const d = await ModulesModel.getTcmsForLinking();
      if (cancelled) return;
      setTestCases(d?.testCases || []);
      setProjects(d?.projects || []);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  const visible = useMemo(() => {
    const linked = new Set(alreadyLinkedIds || []);
    let l = testCases.filter(tc => !linked.has(tc.id));
    if (projectFilter) l = l.filter(tc => tc.projectId === projectFilter);
    if (statusFilter) l = l.filter(tc => (tc.status || 'pending') === statusFilter);
    if (search.trim()) {
      const s = search.trim().toLowerCase();
      l = l.filter(tc => (tc.tcCode || '').toLowerCase().includes(s) || (tc.title || '').toLowerCase().includes(s) || (tc.module || '').toLowerCase().includes(s));
    }
    return l;
  }, [testCases, projectFilter, statusFilter, search, alreadyLinkedIds]);

  const toggle = (id) => setSelected(s => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  const submit = async () => {
    if (selected.size === 0) return;
    setSaving(true);
    // Pass full test case objects so callers can either stage them (form modal,
    // pre-save) or extract IDs and call the backend (detail view, post-save).
    const selectedCases = testCases.filter(tc => selected.has(tc.id));
    const r = await onSave(selectedCases);
    setSaving(false);
    if (r?.success === false) { alert(r.error || 'Failed'); return; }
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(15,23,42,0.45)' }}>
      <div className="w-full max-w-3xl bg-white rounded-2xl shadow-xl flex flex-col max-h-[88vh]" style={{ border: '1px solid #e2e8f0' }}>
        <div className="px-6 pt-5 pb-3 border-b border-slate-200">
          <h2 className="text-lg font-bold text-slate-900">🔗 Link TCMS test cases</h2>
          <p className="text-xs text-slate-500 mt-1">Select test cases that prove this change was tested. Status reflects the latest execution. Already-linked cases are filtered out.</p>
        </div>
        <div className="px-6 py-3 border-b border-slate-200 flex flex-wrap gap-2 items-center">
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search code, title, module…" className="flex-1 min-w-[200px] bg-white border border-slate-300 rounded px-3 py-1.5 text-sm" />
          <select value={projectFilter} onChange={(e) => setProjectFilter(e.target.value)} className="bg-white border border-slate-300 rounded px-2 py-1.5 text-sm text-slate-700">
            <option value="">All projects</option>
            {projects.map(p => <option key={p.projectId} value={p.projectId}>{p.projectName}</option>)}
          </select>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="bg-white border border-slate-300 rounded px-2 py-1.5 text-sm text-slate-700">
            <option value="">All statuses</option>
            <option value="pass">Pass</option>
            <option value="fail">Fail</option>
            <option value="blocked">Blocked</option>
            <option value="pending">Pending</option>
          </select>
          <span className="text-xs text-slate-500 ml-auto">{selected.size} of {visible.length} selected</span>
        </div>
        <div className="overflow-y-auto flex-1">
          {loading ? (
            <div className="text-center py-12 text-slate-400">Loading test cases…</div>
          ) : visible.length === 0 ? (
            <div className="text-center py-12 text-slate-400 text-sm">No test cases match your filters.</div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {visible.map(tc => {
                const status = CR_TC_STATUS_META[tc.status || 'pending'] || CR_TC_STATUS_META.pending;
                const checked = selected.has(tc.id);
                return (
                  <li key={tc.id}>
                    <label className={`flex items-center gap-3 px-6 py-2.5 cursor-pointer hover:bg-slate-50 ${checked ? 'bg-indigo-50/40' : ''}`}>
                      <input type="checkbox" checked={checked} onChange={() => toggle(tc.id)} className="accent-indigo-600" />
                      <span className="text-xs font-mono text-slate-500 w-20">{tc.tcCode}</span>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-slate-900 truncate">{tc.title}</div>
                        <div className="text-[11px] text-slate-500 truncate">{tc.projectName || '—'}{tc.module ? ` · ${tc.module}` : ''}</div>
                      </div>
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ color: status.color, backgroundColor: status.bg, border: `1px solid ${status.color}44` }}>
                        {status.icon} {status.label}
                      </span>
                    </label>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        <div className="px-6 py-3 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-900">Cancel</button>
          <button onClick={submit} disabled={saving || selected.size === 0} className="px-4 py-2 text-sm font-semibold rounded bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50">
            {saving ? 'Linking…' : `Link ${selected.size || ''}`.trim()}
          </button>
        </div>
      </div>
    </div>
  );
};

const DPL_REQ_STATUS_META = {
  Approved:      { color: '#059669', bg: '#d1fae5', icon: '✓', label: 'Approved' },
  Completed:     { color: '#059669', bg: '#d1fae5', icon: '✓', label: 'Completed' },
  Submitted:     { color: '#64748b', bg: '#f1f5f9', icon: '○', label: 'Submitted' },
  'Under Review':{ color: '#d97706', bg: '#fef3c7', icon: '⏳', label: 'Under Review' },
  'In Progress': { color: '#d97706', bg: '#fef3c7', icon: '⏳', label: 'In Progress' },
  Rejected:      { color: '#dc2626', bg: '#fee2e2', icon: '✗', label: 'Rejected' },
};
const DPL_REQ_STATUS_DEFAULT = { color: '#64748b', bg: '#f1f5f9', icon: '○', label: 'Unknown' };

const DeploymentRequirementLinkModal = ({ alreadyLinkedIds, onSave, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [requirements, setRequirements] = useState([]);
  const [projects, setProjects] = useState([]);
  const [search, setSearch] = useState('');
  const [projectFilter, setProjectFilter] = useState('');
  const [selected, setSelected] = useState(() => new Set());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const d = await ModulesModel.getRequirementsForLinking();
      if (cancelled) return;
      setRequirements(d?.requirements || []);
      setProjects(d?.projects || []);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  const visible = useMemo(() => {
    const linked = new Set(alreadyLinkedIds || []);
    let l = requirements.filter(r => !linked.has(r.id));
    if (projectFilter) l = l.filter(r => r.projectId === projectFilter);
    if (search.trim()) {
      const s = search.trim().toLowerCase();
      l = l.filter(r => (r.reqCode || '').toLowerCase().includes(s) || (r.title || '').toLowerCase().includes(s));
    }
    return l;
  }, [requirements, projectFilter, search, alreadyLinkedIds]);

  const toggle = (id) => setSelected(s => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  const submit = async () => {
    if (selected.size === 0) return;
    setSaving(true);
    // Pass full requirement objects so callers can either stage them (form modal,
    // pre-save) or extract IDs and call the backend (detail view, post-save).
    const selectedReqs = requirements.filter(r => selected.has(r.id));
    const r = await onSave(selectedReqs);
    setSaving(false);
    if (r?.success === false) { alert(r.error || 'Failed'); return; }
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(15,23,42,0.45)' }}>
      <div className="w-full max-w-3xl bg-white rounded-2xl shadow-xl flex flex-col max-h-[88vh]" style={{ border: '1px solid #e2e8f0' }}>
        <div className="px-6 pt-5 pb-3 border-b border-slate-200">
          <h2 className="text-lg font-bold text-slate-900">🔗 Link QMS requirements</h2>
          <p className="text-xs text-slate-500 mt-1">Select requirements this deployment fulfills. Already-linked requirements are filtered out.</p>
        </div>
        <div className="px-6 py-3 border-b border-slate-200 flex flex-wrap gap-2 items-center">
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search code, title…" className="flex-1 min-w-[200px] bg-white border border-slate-300 rounded px-3 py-1.5 text-sm" />
          <select value={projectFilter} onChange={(e) => setProjectFilter(e.target.value)} className="bg-white border border-slate-300 rounded px-2 py-1.5 text-sm text-slate-700">
            <option value="">All projects</option>
            {projects.map(p => <option key={p.projectId} value={p.projectId}>{p.projectName}</option>)}
          </select>
          <span className="text-xs text-slate-500 ml-auto">{selected.size} of {visible.length} selected</span>
        </div>
        <div className="overflow-y-auto flex-1">
          {loading ? (
            <div className="text-center py-12 text-slate-400">Loading requirements…</div>
          ) : visible.length === 0 ? (
            <div className="text-center py-12 text-slate-400 text-sm">No requirements match your filters.</div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {visible.map(r => {
                const status = DPL_REQ_STATUS_META[r.status] || DPL_REQ_STATUS_DEFAULT;
                const checked = selected.has(r.id);
                return (
                  <li key={r.id}>
                    <label className={`flex items-center gap-3 px-6 py-2.5 cursor-pointer hover:bg-slate-50 ${checked ? 'bg-indigo-50/40' : ''}`}>
                      <input type="checkbox" checked={checked} onChange={() => toggle(r.id)} className="accent-indigo-600" />
                      <span className="text-xs font-mono text-slate-500 w-20">{r.reqCode}</span>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-slate-900 truncate">{r.title}</div>
                        <div className="text-[11px] text-slate-500 truncate">{r.projectName || '—'}{r.reqType ? ` · ${r.reqType}` : ''}</div>
                      </div>
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ color: status.color, backgroundColor: status.bg, border: `1px solid ${status.color}44` }}>
                        {status.icon} {status.label}
                      </span>
                    </label>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        <div className="px-6 py-3 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-900">Cancel</button>
          <button onClick={submit} disabled={saving || selected.size === 0} className="px-4 py-2 text-sm font-semibold rounded bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50">
            {saving ? 'Linking…' : `Link ${selected.size || ''}`.trim()}
          </button>
        </div>
      </div>
    </div>
  );
};

const ChangeRequestSettingsModal = ({ initial, onSave, onClose }) => {
  const [target, setTarget] = useState((initial?.targetSystems || CR_TARGET_SYSTEMS_LIST).join('\n'));
  const [teams, setTeams] = useState((initial?.teamOwners || CR_TEAM_OWNERS_LIST).join('\n'));
  const [reasons, setReasons] = useState((initial?.reasons || CR_REASONS_LIST).join('\n'));
  const [chainsJson, setChainsJson] = useState(() => JSON.stringify(initial?.approvalChains || {
    DevOps: [
      { type: 'role', value: 'devops_manager', label: 'Team Manager' },
      { type: 'user', value: 'Anthony Zoblescin', label: 'Final Approval' },
    ],
    DataOps: [
      { type: 'role', value: 'dataops_manager', label: 'Team Manager' },
      { type: 'user', value: 'Anthony Zoblescin', label: 'Final Approval' },
    ],
    'Service Desk': [
      { type: 'role', value: 'service_desk_manager', label: 'Team Manager' },
      { type: 'user', value: 'Max Simon', label: 'Second Approval' },
      { type: 'user', value: 'Anthony Zoblescin', label: 'Final Approval' },
    ],
  }, null, 2));
  const [chainsError, setChainsError] = useState('');
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    let approvalChains = null;
    try {
      approvalChains = JSON.parse(chainsJson || '{}');
      setChainsError('');
    } catch (e) {
      setChainsError('Invalid JSON: ' + e.message);
      setSaving(false);
      return;
    }
    const r = await onSave({
      targetSystems: target.split('\n').map(s => s.trim()).filter(Boolean),
      teamOwners: teams.split('\n').map(s => s.trim()).filter(Boolean),
      reasons: reasons.split('\n').map(s => s.trim()).filter(Boolean),
      approvalChains,
    });
    setSaving(false);
    if (r?.success === false) { alert(r.error || 'Failed'); return; }
    onClose();
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(15,23,42,0.45)' }}>
      <div className="w-full max-w-xl bg-white rounded-2xl shadow-xl p-6 max-h-[90vh] overflow-y-auto" style={{ border: '1px solid #e2e8f0' }}>
        <h2 className="text-lg font-bold text-slate-900 mb-1">⚙ Change Request — Dropdown Settings</h2>
        <p className="text-sm text-slate-500 mb-4">One value per line. Changes apply to all future and edited CRs.</p>
        <div className="space-y-4">
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1 block">Target Systems</label>
            <textarea rows={6} value={target} onChange={(e) => setTarget(e.target.value)} className="w-full bg-white border border-slate-300 rounded px-2 py-1.5 text-sm font-mono text-slate-900" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1 block">Team Owners</label>
            <textarea rows={5} value={teams} onChange={(e) => setTeams(e.target.value)} className="w-full bg-white border border-slate-300 rounded px-2 py-1.5 text-sm font-mono text-slate-900" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1 block">Reasons for Change</label>
            <textarea rows={5} value={reasons} onChange={(e) => setReasons(e.target.value)} className="w-full bg-white border border-slate-300 rounded px-2 py-1.5 text-sm font-mono text-slate-900" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1 block">Approval Chains (per team owner)</label>
            <p className="text-[11px] text-slate-500 mb-2">
              JSON map: team name → ordered list of approval steps. Each step is <code>{`{ type, value, label }`}</code>.
              <code>type: "role"</code> resolves to the active user with that <code>user_role</code> (e.g. <code>devops_manager</code>).
              <code>type: "user"</code> matches a literal <code>user_name</code>. Teams not listed here fall back to legacy single-step approval (any admin/manager).
            </p>
            <textarea rows={12} value={chainsJson} onChange={(e) => setChainsJson(e.target.value)} className="w-full bg-white border border-slate-300 rounded px-2 py-1.5 text-xs font-mono text-slate-900" />
            {chainsError && <p className="text-xs text-rose-600 mt-1">{chainsError}</p>}
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-900">Cancel</button>
          <button onClick={submit} disabled={saving} className="px-4 py-2 text-sm font-semibold rounded bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50">{saving ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
};

// ---- CR export helpers (Print/PDF + Word .doc download) ----
const buildCrExportHTML = (cr) => {
  const esc = (s) => String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const meta = CR_STATUS_META[cr.status] || CR_STATUS_META.Draft;
  const sec = (label, value) => value ? `<div class="row"><div class="label">${label}</div><div class="value">${esc(value)}</div></div>` : '';
  const block = (title, body) => body ? `<h2>${title}</h2><div class="block">${esc(body).replace(/\n/g, '<br/>')}</div>` : '';
  // Description and Closure notes are rich-text (MiniWysiwyg) fields — already HTML, so they
  // go through sanitizeBugHtml instead of esc()+\n->br, unlike the plain-text fields above.
  const blockHtml = (title, html) => html ? `<h2>${title}</h2><div class="block">${sanitizeBugHtml(html)}</div>` : '';
  const comments = (cr.comments || []).map(c => `<div class="comment"><strong>${esc(c.userName)}</strong> <span class="muted">· ${new Date(c.createdAt).toLocaleString()}</span><div>${esc(c.content).replace(/\n/g, '<br/>')}</div></div>`).join('');
  const history = (cr.history || []).map(h => `<div class="hist">${new Date(h.createdAt).toLocaleString()} · <strong>${esc(h.action)}</strong>${h.newValue ? ' — ' + esc(h.newValue) : ''}${h.notes ? ' — <em>' + esc(h.notes) + '</em>' : ''}${h.userName ? ' · ' + esc(h.userName) : ''}</div>`).join('');
  const reviewBox = cr.reviewNotes
    ? `<div class="callout ${cr.status === 'Rejected' ? 'reject' : 'approve'}"><strong>Review notes:</strong><br/>${esc(cr.reviewNotes).replace(/\n/g, '<br/>')}</div>` : '';
  const closureBox = (cr.status === 'Completed' && (cr.closureNotes || cr.actualCompletionDate))
    ? `<div class="callout approve"><strong>Closure ${cr.actualCompletionDate ? `(${esc(cr.actualCompletionDate)})` : ''}:</strong><br/>${sanitizeBugHtml(cr.closureNotes || '')}</div>` : '';
  return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${esc(cr.crNumber)} — ${esc(cr.title)}</title>
<style>
  body { font-family: 'Segoe UI', Arial, sans-serif; color: #111827; padding: 24px; max-width: 800px; margin: auto; }
  h1 { font-size: 22px; margin: 0 0 4px; color: #111827; }
  h2 { font-size: 13px; text-transform: uppercase; letter-spacing: 1px; color: #6b7280; margin: 18px 0 6px; border-bottom: 1px solid #e5e7eb; padding-bottom: 3px; }
  .meta { color: #6b7280; font-size: 12px; margin-bottom: 16px; }
  .badge { display: inline-block; padding: 2px 9px; border-radius: 999px; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: .5px; color: ${meta.color}; background: ${meta.bg}; border: 1px solid ${meta.color}66; }
  .row { display: flex; padding: 4px 0; border-bottom: 1px solid #f3f4f6; }
  .label { width: 180px; font-weight: 600; color: #4b5563; font-size: 12px; }
  .value { flex: 1; color: #111827; font-size: 13px; }
  .block { background: #f9fafb; border-left: 3px solid #d1d5db; padding: 10px 14px; font-size: 13px; line-height: 1.6; white-space: pre-wrap; }
  .callout { padding: 10px 14px; border-radius: 6px; font-size: 13px; margin: 12px 0; border: 1px solid; }
  .callout.approve { background: #d1fae5; border-color: #6ee7b7; color: #065f46; }
  .callout.reject { background: #fee2e2; border-color: #fca5a5; color: #991b1b; }
  .comment { padding: 8px 0; border-bottom: 1px solid #f3f4f6; font-size: 13px; }
  .muted { color: #9ca3af; font-size: 11px; }
  .hist { font-size: 11px; color: #6b7280; padding: 2px 0; }
  .footer { text-align: center; color: #9ca3af; font-size: 11px; margin-top: 28px; padding-top: 10px; border-top: 1px solid #e5e7eb; }
  @media print { body { padding: 8px; } }
</style></head><body>
<h1>${esc(cr.title)}</h1>
<div class="meta">${esc(cr.crNumber)} · <span class="badge">${esc(cr.status)}</span> · Urgency <strong>${esc(cr.urgency)}</strong>${cr.changeType === 'Emergency' ? ' · 🚨 EMERGENCY' : ''}</div>
${sec('Requested by', cr.requestedBy)}
${sec('Submitted at', cr.submittedAt && new Date(cr.submittedAt).toLocaleString())}
${sec('Target System', cr.targetSystem)}
${sec('Change Type', cr.changeType)}
${sec('Team Owner', cr.teamOwner)}
${sec('IT Point of Contact', cr.itContactName)}
${sec('Reason', cr.reason)}
${sec('Urgency', cr.urgency)}
${sec('Implementation Date', cr.implementationDate)}
${cr.reviewedBy ? sec(cr.status === 'Approved' ? 'Approved by' : 'Reviewed by', `${cr.reviewedBy}${cr.reviewedAt ? ` (${new Date(cr.reviewedAt).toLocaleString()})` : ''}`) : ''}
${reviewBox}
${closureBox}
${blockHtml('Description', cr.description)}
${block('Business Justification', cr.businessJustification)}
${block('Implementation Plan', cr.implementationPlan)}
${block('Rollback Plan', cr.rollbackPlan)}
${block('Remarks', cr.catchAll)}
${(cr.attachments || []).length ? `<h2>Attachments</h2><ul>${(cr.attachments || []).map(a => `<li>${esc(a.fileName)} <span class="muted">(${a.category === 'implementation_report' ? 'Implementation Report' : 'Proof of Testing'} · ${(a.fileSize/1024).toFixed(0)} KB · ${esc(a.uploadedBy || '')})</span></li>`).join('')}</ul>` : ''}
${comments ? `<h2>Comments</h2>${comments}` : ''}
${history ? `<h2>Activity Log</h2>${history}` : ''}
<div class="footer">Generated by Orbit · Change Request Form · ${new Date().toLocaleString()}</div>
</body></html>`;
};

const printChangeRequest = (cr) => {
  const html = buildCrExportHTML(cr);
  const w = window.open('', '_blank');
  if (!w) { alert('Pop-up blocked. Please allow pop-ups for this site to print.'); return; }
  w.document.open();
  w.document.write(html);
  w.document.close();
  // Wait one tick for the document to parse before triggering print
  setTimeout(() => { w.focus(); w.print(); }, 250);
};

const exportChangeRequestWord = (cr) => {
  const html = buildCrExportHTML(cr);
  // .doc files in HTML format — Word opens these natively
  const wordHtml = `<html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word' xmlns='http://www.w3.org/TR/REC-html40'>${html.slice(html.indexOf('<head>'))}`;
  const blob = new Blob([wordHtml], { type: 'application/msword' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${cr.crNumber || 'change-request'}.doc`;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const ChangeRequestModule = ({ data, user, users, settings, onRefresh, onOpenDetail, onShowForm, onShowSettings }) => {
  const [tab, setTab] = useState('all'); // 'all' | 'log'
  const [statusFilter, setStatusFilter] = useState('');
  const [mineOnly, setMineOnly] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkPrompt, setBulkPrompt] = useState(null); // { action, label }
  const [bulkNotes, setBulkNotes] = useState('');
  // Approval log state (Approved + Completed only — rejected has its own tab)
  const [logRows, setLogRows] = useState(null);
  const [logLoading, setLogLoading] = useState(false);
  const [logStatus, setLogStatus] = useState(''); // '' | Approved | Completed
  const [logTeam, setLogTeam] = useState('');
  const [logFrom, setLogFrom] = useState('');
  const [logTo, setLogTo] = useState('');
  const [logSearch, setLogSearch] = useState('');

  // Rejected log state
  const [rejRows, setRejRows] = useState(null);
  const [rejLoading, setRejLoading] = useState(false);
  const [rejTeam, setRejTeam] = useState('');
  const [rejFrom, setRejFrom] = useState('');
  const [rejTo, setRejTo] = useState('');
  const [rejSearch, setRejSearch] = useState('');

  const loadLog = async () => {
    setLogLoading(true);
    const filters = { view: 'log' };
    if (logStatus) filters.status = logStatus;
    else filters.statusIn = 'Approved,Completed';
    if (logTeam) filters.teamOwner = logTeam;
    if (logFrom) filters.reviewedFrom = logFrom;
    if (logTo) filters.reviewedTo = logTo;
    if (logSearch.trim()) filters.search = logSearch.trim();
    const r = await ModulesModel.getChangeRequests(filters);
    setLogRows(r?.changeRequests || []);
    setLogLoading(false);
  };

  const loadRejected = async () => {
    setRejLoading(true);
    const filters = { view: 'log', status: 'Rejected' };
    if (rejTeam) filters.teamOwner = rejTeam;
    if (rejFrom) filters.reviewedFrom = rejFrom;
    if (rejTo) filters.reviewedTo = rejTo;
    if (rejSearch.trim()) filters.search = rejSearch.trim();
    const r = await ModulesModel.getChangeRequests(filters);
    setRejRows(r?.changeRequests || []);
    setRejLoading(false);
  };

  useEffect(() => { if (tab === 'log') loadLog(); }, [tab, logStatus, logTeam, logFrom, logTo, logSearch]);
  useEffect(() => { if (tab === 'rejected') loadRejected(); }, [tab, rejTeam, rejFrom, rejTo, rejSearch]);

  const list = data?.changeRequests || [];
  const stats = data?.stats || {};
  // Normalize user shape — auth/me ships `name`/`role`, some callers use user_name/user_role
  const userName = user?.user_name || user?.name || '';
  const userRole = user?.user_role || user?.role || '';
  const isManager = ['admin','manager','devops_manager','dataops_manager','service_desk_manager'].includes(userRole);
  const isAdmin = ['admin','manager'].includes(userRole);

  const csvDownload = (filename, headers, rows) => {
    const csv = [headers, ...rows].map(r => r.map(cell => `"${String(cell == null ? '' : cell).replace(/"/g, '""')}"`).join(',')).join('\r\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const csvExportLog = () => {
    csvDownload(
      `change-request-approval-log-${new Date().toISOString().slice(0, 10)}.csv`,
      ['CR Number','Title','Final Status','Decision Date','Approved By','Approval Notes','Team Owner','Target System','Change Type','Reason','Urgency','Requested By','Submitted At','Planned Date','Actual Completion','Closure Notes'],
      (logRows || []).map(c => [
        c.crNumber, c.title, c.status,
        c.reviewedAt ? new Date(c.reviewedAt).toLocaleString() : '',
        c.reviewedBy || '', c.reviewNotes || '',
        c.teamOwner, c.targetSystem, c.changeType, c.reason, c.urgency,
        c.requestedBy || '',
        c.submittedAt ? new Date(c.submittedAt).toLocaleString() : '',
        c.implementationDate || '',
        c.actualCompletionDate || '',
        stripHtml(c.closureNotes || ''),
      ]),
    );
  };

  const csvExportRejected = () => {
    csvDownload(
      `change-request-rejected-${new Date().toISOString().slice(0, 10)}.csv`,
      ['CR Number','Title','Rejected Date','Rejected By','Rejection Reason','Team Owner','Target System','Change Type','Original Reason','Urgency','Requested By','Submitted At','Description'],
      (rejRows || []).map(c => [
        c.crNumber, c.title,
        c.reviewedAt ? new Date(c.reviewedAt).toLocaleString() : '',
        c.reviewedBy || '', c.reviewNotes || '',
        c.teamOwner, c.targetSystem, c.changeType, c.reason, c.urgency,
        c.requestedBy || '',
        c.submittedAt ? new Date(c.submittedAt).toLocaleString() : '',
        c.description || '',
      ]),
    );
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const runBulk = async () => {
    if (!bulkPrompt) return;
    const r = await ModulesModel.bulkChangeRequestAction(bulkPrompt.action, Array.from(selectedIds), bulkNotes.trim());
    if (r?.success === false) { alert(r.error || 'Failed'); return; }
    if (r?.errors?.length) {
      alert(`${r.succeeded} succeeded, ${r.failed} failed:\n\n${r.errors.slice(0, 5).join('\n')}`);
    }
    setBulkPrompt(null);
    setBulkNotes('');
    setSelectedIds(new Set());
    await onRefresh();
  };

  const filtered = useMemo(() => {
    let l = list;
    if (statusFilter) l = l.filter(c => c.status === statusFilter);
    if (mineOnly && userName) l = l.filter(c => c.requestedBy === userName || c.itContactName === userName);
    if (search.trim()) {
      const s = search.trim().toLowerCase();
      l = l.filter(c => (c.title || '').toLowerCase().includes(s) || (c.crNumber || '').toLowerCase().includes(s) || (c.description || '').toLowerCase().includes(s));
    }
    return l;
  }, [list, statusFilter, mineOnly, search, user]);

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)' }}>
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-extrabold text-slate-900 flex items-center gap-2">📋 Change Request Form</h1>
            <p className="text-sm text-slate-500 mt-1">Formally request modifications to IT systems, infrastructure, or processes.</p>
          </div>
          <div className="flex items-center gap-2">
            {isAdmin && (
              <button onClick={onShowSettings} className="px-3 py-2 text-xs font-semibold rounded-lg bg-white border border-slate-300 text-slate-700 hover:border-indigo-400">⚙ Settings</button>
            )}
            <button onClick={onShowForm} className="px-4 py-2 text-sm font-semibold rounded-lg bg-blue-600 hover:bg-blue-700 text-white shadow-sm">+ New Change Request</button>
          </div>
        </div>

        {/* Tab nav */}
        <div className="flex gap-1 border-b border-slate-200 mb-5">
          {[
            ['all', '📋 All Requests', 'text-blue-600 border-blue-600'],
            ['log', '📚 Approval Log', 'text-emerald-600 border-emerald-600'],
            ['rejected', '📕 Rejected Log', 'text-rose-600 border-rose-600'],
          ].map(([id, label, active]) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              className={`px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors ${tab === id ? active : 'text-slate-500 border-transparent hover:text-slate-700'}`}
            >{label}</button>
          ))}
        </div>

        {tab === 'rejected' ? (
          <div>
            <div className="mb-4 p-3 rounded-xl bg-white shadow-sm flex items-center gap-2 flex-wrap" style={{ border: '1px solid #e2e8f0' }}>
              <input value={rejSearch} onChange={(e) => setRejSearch(e.target.value)} placeholder="Search title, number…" className="flex-1 min-w-[160px] bg-white border border-slate-300 rounded px-3 py-1.5 text-sm" />
              <select value={rejTeam} onChange={(e) => setRejTeam(e.target.value)} className="bg-white border border-slate-300 rounded px-2 py-1.5 text-sm text-slate-700">
                <option value="">All teams</option>
                {(settings?.teamOwners || CR_TEAM_OWNERS_LIST).map(t => <option key={t} value={t}>{t}</option>)}
              </select>
              <div className="flex items-center gap-1 text-xs text-slate-500">
                <span>Rejected</span>
                <input type="date" value={rejFrom} onChange={(e) => setRejFrom(e.target.value)} className="bg-white border border-slate-300 rounded px-2 py-1 text-xs" />
                <span>→</span>
                <input type="date" value={rejTo} onChange={(e) => setRejTo(e.target.value)} className="bg-white border border-slate-300 rounded px-2 py-1 text-xs" />
              </div>
              {(rejTeam || rejFrom || rejTo || rejSearch) && (
                <button onClick={() => { setRejTeam(''); setRejFrom(''); setRejTo(''); setRejSearch(''); }} className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 border border-slate-200">× Clear</button>
              )}
              <button onClick={csvExportRejected} disabled={!rejRows?.length} className="ml-auto text-xs font-semibold px-3 py-1.5 rounded bg-white border border-slate-300 text-slate-700 hover:border-rose-400 hover:text-rose-700 disabled:opacity-50">📤 Export CSV</button>
              <span className="text-xs text-slate-500">{rejRows ? `${rejRows.length}` : '…'} rejected</span>
            </div>

            <div className="bg-white rounded-xl overflow-hidden shadow-sm" style={{ border: '1px solid #e2e8f0' }}>
              {rejLoading ? (
                <div className="p-10 text-center text-slate-400">Loading rejected log…</div>
              ) : (rejRows || []).length === 0 ? (
                <div className="p-10 text-center text-slate-400">No rejected change requests match your filters.</div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-rose-50 border-b border-rose-200">
                    <tr className="text-[10px] uppercase tracking-wider text-rose-700">
                      <th className="text-left px-3 py-2 font-bold">CR #</th>
                      <th className="text-left px-3 py-2 font-bold">Title</th>
                      <th className="text-left px-3 py-2 font-bold">Rejection reason</th>
                      <th className="text-left px-3 py-2 font-bold">Rejected by</th>
                      <th className="text-left px-3 py-2 font-bold">Rejected on</th>
                      <th className="text-left px-3 py-2 font-bold">Team</th>
                      <th className="text-left px-3 py-2 font-bold">Requested by</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rejRows.map((cr, i) => (
                      <tr
                        key={cr.id}
                        onClick={() => onOpenDetail(cr)}
                        className={`cursor-pointer hover:bg-rose-50/40 transition-colors ${i % 2 ? 'bg-slate-50/30' : ''}`}
                        style={{ borderTop: '1px solid #f1f5f9' }}
                      >
                        <td className="px-3 py-2 font-mono text-[11px] text-slate-500 whitespace-nowrap">{cr.crNumber}</td>
                        <td className="px-3 py-2">
                          <div className="text-slate-900 font-medium truncate max-w-[260px]" title={cr.title}>{cr.title}</div>
                          <div className="text-[11px] text-slate-500 truncate max-w-[260px]" title={`${cr.targetSystem} · ${cr.changeType} · ${cr.reason}`}>
                            {cr.targetSystem} · {cr.changeType} · {cr.reason}
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          <div className="text-rose-700 text-sm font-medium truncate max-w-[320px]" title={cr.reviewNotes}>{cr.reviewNotes || <span className="italic text-slate-400">no reason recorded</span>}</div>
                        </td>
                        <td className="px-3 py-2 text-slate-700 whitespace-nowrap">{cr.reviewedBy || '—'}</td>
                        <td className="px-3 py-2 text-slate-500 text-[11px] whitespace-nowrap">{cr.reviewedAt ? new Date(cr.reviewedAt).toLocaleDateString() : '—'}</td>
                        <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{cr.teamOwner}</td>
                        <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{cr.requestedBy || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        ) : tab === 'log' ? (
          <div>
            <div className="mb-4 p-3 rounded-xl bg-white shadow-sm flex items-center gap-2 flex-wrap" style={{ border: '1px solid #e2e8f0' }}>
              <input value={logSearch} onChange={(e) => setLogSearch(e.target.value)} placeholder="Search title, number…" className="flex-1 min-w-[160px] bg-white border border-slate-300 rounded px-3 py-1.5 text-sm" />
              <select value={logStatus} onChange={(e) => setLogStatus(e.target.value)} className="bg-white border border-slate-300 rounded px-2 py-1.5 text-sm text-slate-700">
                <option value="">Approved + Completed</option>
                <option value="Approved">Approved (in flight)</option>
                <option value="Completed">Completed</option>
              </select>
              <select value={logTeam} onChange={(e) => setLogTeam(e.target.value)} className="bg-white border border-slate-300 rounded px-2 py-1.5 text-sm text-slate-700">
                <option value="">All teams</option>
                {(settings?.teamOwners || CR_TEAM_OWNERS_LIST).map(t => <option key={t} value={t}>{t}</option>)}
              </select>
              <div className="flex items-center gap-1 text-xs text-slate-500">
                <span>Reviewed</span>
                <input type="date" value={logFrom} onChange={(e) => setLogFrom(e.target.value)} className="bg-white border border-slate-300 rounded px-2 py-1 text-xs" />
                <span>→</span>
                <input type="date" value={logTo} onChange={(e) => setLogTo(e.target.value)} className="bg-white border border-slate-300 rounded px-2 py-1 text-xs" />
              </div>
              {(logStatus || logTeam || logFrom || logTo || logSearch) && (
                <button onClick={() => { setLogStatus(''); setLogTeam(''); setLogFrom(''); setLogTo(''); setLogSearch(''); }} className="text-xs px-2 py-0.5 rounded-full bg-rose-50 text-rose-700 border border-rose-200">× Clear</button>
              )}
              <button onClick={csvExportLog} disabled={!logRows?.length} className="ml-auto text-xs font-semibold px-3 py-1.5 rounded bg-white border border-slate-300 text-slate-700 hover:border-emerald-400 hover:text-emerald-700 disabled:opacity-50">📤 Export CSV</button>
              <span className="text-xs text-slate-500">{logRows ? `${logRows.length}` : '…'} entries</span>
            </div>

            <div className="bg-white rounded-xl overflow-hidden shadow-sm" style={{ border: '1px solid #e2e8f0' }}>
              {logLoading ? (
                <div className="p-10 text-center text-slate-400">Loading audit log…</div>
              ) : (logRows || []).length === 0 ? (
                <div className="p-10 text-center text-slate-400">No reviewed change requests match your filters.</div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr className="text-[10px] uppercase tracking-wider text-slate-500">
                      <th className="text-left px-3 py-2 font-bold">CR #</th>
                      <th className="text-left px-3 py-2 font-bold">Title</th>
                      <th className="text-left px-3 py-2 font-bold">Decision</th>
                      <th className="text-left px-3 py-2 font-bold">By</th>
                      <th className="text-left px-3 py-2 font-bold">Reviewed</th>
                      <th className="text-left px-3 py-2 font-bold">Team</th>
                      <th className="text-left px-3 py-2 font-bold">Planned</th>
                      <th className="text-left px-3 py-2 font-bold">Done</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logRows.map((cr, i) => (
                      <tr
                        key={cr.id}
                        onClick={() => onOpenDetail(cr)}
                        className={`cursor-pointer hover:bg-indigo-50/40 transition-colors ${i % 2 ? 'bg-slate-50/30' : ''}`}
                        style={{ borderTop: '1px solid #f1f5f9' }}
                      >
                        <td className="px-3 py-2 font-mono text-[11px] text-slate-500 whitespace-nowrap">{cr.crNumber}</td>
                        <td className="px-3 py-2">
                          <div className="text-slate-900 font-medium truncate max-w-[260px]" title={cr.title}>{cr.title}</div>
                          {cr.reviewNotes && <div className="text-[11px] text-slate-500 italic truncate max-w-[260px]" title={cr.reviewNotes}>— {cr.reviewNotes}</div>}
                        </td>
                        <td className="px-3 py-2"><ChangeRequestStatusBadge status={cr.status} /></td>
                        <td className="px-3 py-2 text-slate-700 whitespace-nowrap">{cr.reviewedBy || '—'}</td>
                        <td className="px-3 py-2 text-slate-500 text-[11px] whitespace-nowrap">{cr.reviewedAt ? new Date(cr.reviewedAt).toLocaleDateString() : '—'}</td>
                        <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{cr.teamOwner}</td>
                        <td className="px-3 py-2 text-slate-500 text-[11px] whitespace-nowrap">{cr.implementationDate || '—'}</td>
                        <td className="px-3 py-2 text-slate-500 text-[11px] whitespace-nowrap">{cr.actualCompletionDate || (cr.completedAt ? new Date(cr.completedAt).toLocaleDateString() : '—')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        ) : (
        <>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-5">
          {Object.entries(CR_STATUS_META).map(([k, meta]) => (
            <button
              key={k}
              onClick={() => setStatusFilter(statusFilter === k ? '' : k)}
              className={`rounded-xl p-3 text-left transition-all ${statusFilter === k ? 'ring-2 ring-offset-1' : 'hover:-translate-y-0.5 hover:shadow-md'}`}
              style={{ background: meta.bg, border: `1px solid ${meta.color}44`, ringColor: meta.color }}
            >
              <div className="text-[10px] font-bold uppercase tracking-wider" style={{ color: meta.color }}>{k}</div>
              <div className="text-xl font-bold text-slate-900">{stats[k] || 0}</div>
            </button>
          ))}
        </div>

        {/* Filter row */}
        <div className="mb-4 p-3 rounded-xl bg-white shadow-sm flex items-center gap-2 flex-wrap" style={{ border: '1px solid #e2e8f0' }}>
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by title, number, or description…" className="flex-1 min-w-[200px] bg-white border border-slate-300 rounded px-3 py-1.5 text-sm" />
          <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer">
            <input type="checkbox" checked={mineOnly} onChange={(e) => setMineOnly(e.target.checked)} className="accent-indigo-600" />
            My CRs only
          </label>
          {(statusFilter || mineOnly || search) && (
            <button onClick={() => { setStatusFilter(''); setMineOnly(false); setSearch(''); }} className="text-xs px-2 py-0.5 rounded-full bg-rose-50 text-rose-700 border border-rose-200">× Clear</button>
          )}
          <span className="text-xs text-slate-500 ml-auto">{filtered.length} of {list.length}</span>
        </div>

        {/* Bulk action bar (visible when 1+ selected) */}
        {selectedIds.size > 0 && (
          <div className="sticky top-0 z-10 mb-3 px-4 py-2.5 rounded-xl bg-indigo-50 border border-indigo-200 flex items-center gap-2 flex-wrap shadow-sm">
            <span className="text-sm font-semibold text-indigo-900">{selectedIds.size} selected</span>
            <button onClick={() => setSelectedIds(new Set())} className="text-xs text-indigo-600 hover:text-indigo-800 underline">Clear</button>
            <div className="flex items-center gap-1 ml-auto flex-wrap">
              {isManager && <button onClick={() => setBulkPrompt({ action: 'approve', label: 'Approve' })} className="px-3 py-1 text-xs font-semibold rounded bg-emerald-600 hover:bg-emerald-700 text-white">Approve selected</button>}
              {isManager && <button onClick={() => setBulkPrompt({ action: 'reject', label: 'Reject' })} className="px-3 py-1 text-xs font-semibold rounded bg-rose-600 hover:bg-rose-700 text-white">Reject selected</button>}
              {isManager && <button onClick={() => setBulkPrompt({ action: 'complete', label: 'Mark Completed' })} className="px-3 py-1 text-xs font-semibold rounded bg-emerald-700 hover:bg-emerald-800 text-white">Complete selected</button>}
              <button onClick={() => setBulkPrompt({ action: 'cancel', label: 'Cancel' })} className="px-3 py-1 text-xs font-semibold rounded bg-white border border-slate-300 text-slate-700 hover:border-amber-400">Cancel selected</button>
              {userRole === 'admin' && <button onClick={() => setBulkPrompt({ action: 'delete', label: 'Delete' })} className="px-3 py-1 text-xs font-semibold rounded bg-rose-50 border border-rose-200 text-rose-700 hover:bg-rose-100">Delete selected</button>}
            </div>
          </div>
        )}

        {/* List */}
        {filtered.length === 0 ? (
          <div className="bg-white rounded-xl text-center py-16 text-slate-400" style={{ border: '1px dashed #e2e8f0' }}>
            {list.length === 0 ? 'No change requests yet. Click "+ New Change Request" to create the first one.' : 'No CRs match your filters.'}
          </div>
        ) : (
          <div className="space-y-2">
            {filtered.map(cr => {
              const isSelected = selectedIds.has(cr.id);
              return (
                <div
                  key={cr.id}
                  className={`bg-white rounded-xl shadow-sm hover:shadow-md transition-all px-3 py-3 flex items-center gap-3 ${isSelected ? 'ring-2 ring-indigo-400' : ''}`}
                  style={{ border: '1px solid #e2e8f0', borderLeft: `3px solid ${(CR_STATUS_META[cr.status] || CR_STATUS_META.Draft).color}` }}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleSelect(cr.id)}
                    className="accent-indigo-600 flex-shrink-0"
                    onClick={(e) => e.stopPropagation()}
                  />
                  <button
                    onClick={() => onOpenDetail(cr)}
                    className="flex-1 text-left flex items-center gap-3 min-w-0"
                  >
                    <div className="text-xs font-mono text-slate-400 w-24 flex-shrink-0">{cr.crNumber}</div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-slate-900 truncate">{cr.title}</div>
                      <div className="text-xs text-slate-500 truncate">
                        {cr.teamOwner} · {cr.targetSystem} · {cr.reason}
                        {cr.requestedBy && <> · by {cr.requestedBy}</>}
                      </div>
                    </div>
                    <ChangeRequestUrgencyBadge urgency={cr.urgency} />
                    {cr.changeType === 'Emergency' && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-rose-100 text-rose-700 border border-rose-300">🚨</span>
                    )}
                    <ChangeRequestStatusBadge status={cr.status} />
                    {cr.implementationDate && <span className="text-[10px] text-slate-400 hidden md:inline">→ {cr.implementationDate}</span>}
                  </button>
                </div>
              );
            })}
          </div>
        )}
        </>
        )}
      </div>

      {bulkPrompt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(15,23,42,0.45)' }}>
          <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-6" style={{ border: '1px solid #e2e8f0' }}>
            <h2 className="text-lg font-bold text-slate-900 mb-1">{bulkPrompt.label} {selectedIds.size} CR{selectedIds.size === 1 ? '' : 's'}</h2>
            <p className="text-sm text-slate-600 mb-3">This action will be applied to every selected change request. Failures are reported individually.</p>
            {(bulkPrompt.action === 'approve' || bulkPrompt.action === 'reject' || bulkPrompt.action === 'complete') && (
              <>
                <label className="text-xs font-bold text-slate-700 mb-1 block">
                  Notes {bulkPrompt.action === 'reject' && <span className="text-rose-600">*</span>}
                </label>
                <textarea autoFocus rows={3} value={bulkNotes} onChange={(e) => setBulkNotes(e.target.value)} className="w-full bg-white border border-slate-300 rounded px-2 py-1.5 text-sm text-slate-900" placeholder={bulkPrompt.action === 'reject' ? 'Why are these being rejected?' : 'Optional notes (applied to each)'} />
              </>
            )}
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => { setBulkPrompt(null); setBulkNotes(''); }} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-900">Cancel</button>
              <button
                onClick={runBulk}
                disabled={bulkPrompt.action === 'reject' && !bulkNotes.trim()}
                className={`px-4 py-2 text-sm font-semibold rounded text-white disabled:opacity-50 ${
                  bulkPrompt.action === 'approve' || bulkPrompt.action === 'complete' ? 'bg-emerald-600 hover:bg-emerald-700' :
                  bulkPrompt.action === 'delete' ? 'bg-rose-700 hover:bg-rose-800' : 'bg-rose-600 hover:bg-rose-700'
                }`}
              >Confirm {bulkPrompt.label}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ---- Role Permissions Section ----
const ALL_MODULES_LIST = [
  { id: 'devops',         name: 'DevOps' },
  { id: 'dataops',        name: 'DataOps' },
  { id: 'plantrack',      name: 'PlanTrack' },
  { id: 'polaris',        name: 'Polaris' },
  { id: 'quality',        name: 'Quality' },
  { id: 'quicktrack',     name: 'QuickTrack' },
  { id: 'learntrack',     name: 'LearnTrack' },
  { id: 'timeboard',      name: 'TimeBoard' },
  { id: 'leaveboard',     name: 'LeaveBoard' },
  { id: 'meetingboard',   name: 'MeetingBoard' },
  { id: 'knowledgeboard', name: 'KnowledgeBoard' },
  { id: 'incidentboard',  name: 'IncidentBoard' },
  { id: 'automationboard', name: 'AutomationBoard' },
  { id: 'assetboard',      name: 'AssetBoard' },
  { id: 'requestboard',   name: 'RequestBoard' },
  { id: 'vendorevents',   name: 'Vendor Events' },
  { id: 'leaderboard',    name: 'LeaderBoard' },
  { id: 'supportboard', name: 'SupportBoard' },
  { id: 'tcms',         name: 'TCMS' },
  { id: 'manager-console', name: 'Manager Console' },
  { id: 'techdebt',     name: 'Tech Debt Review' },
  { id: 'product',      name: 'Product' },
  { id: 'changerequest', name: 'Change Request Form' },
  { id: 'marketingworkflow', name: 'Marketing Workflow' },
  { id: 'troubleshootinglogs', name: 'Workflow Troubleshooting Logs' },
  { id: 'deployments', name: 'Deployments' },
  { id: 'apm',         name: 'APM' },
  { id: 'datafeed',    name: 'DataFeed' },
  { id: 'workflows',   name: 'Workflows' },
];

const ROLE_DISPLAY_NAMES = {
  admin:           'Admin',
  developer:       'Developer',
  dataops:         'DataOps',
  project:         'Project Manager',
  qa:              'QA',
  qa_lead:         'QA Lead',
  devops_manager:  'DevOps Manager',
  dataops_manager: 'DataOps Manager',
  manager:         'Manager',
  marketing:       'Marketing',
  service_desk:    'Service Desk',
  service_desk_manager: 'Service Desk Manager',
  uat_tester:          'UAT Tester',
  revops:              'RevOps',
  revops_manager:      'RevOps Manager',
  user:                'User',
};

const RolePermissionsSection = ({ authToken }) => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRole, setSelectedRole] = useState(null);
  // drafts: { [roleId]: Set of moduleIds }
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    const res = await fetch('/api/settings/roles', { headers: { Authorization: `Bearer ${authToken}` } });
    if (res.ok) {
      const data = await res.json();
      const rolesData = data.roles || [];
      setRoles(rolesData);
      const init = {};
      for (const r of rolesData) init[r.id] = new Set(r.modules || []);
      setDrafts(init);
      if (rolesData.length > 0 && !selectedRole) setSelectedRole(rolesData[0].id);
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const toggleModule = (roleId, moduleId) => {
    setDrafts(d => {
      const current = new Set(d[roleId] || []);
      if (current.has(moduleId)) current.delete(moduleId); else current.add(moduleId);
      return { ...d, [roleId]: current };
    });
    setSaved(false);
  };

  const save = async () => {
    if (!selectedRole) return;
    const role = roles.find(r => r.id === selectedRole);
    if (role?.name === 'admin') return; // admin always unrestricted
    setSaving(true); setSaved(false); setError('');
    const moduleIds = Array.from(drafts[selectedRole] || []);
    const res = await fetch(`/api/settings/roles/${selectedRole}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
      body: JSON.stringify({ moduleIds }),
    });
    setSaving(false);
    if (res.ok) { setSaved(true); setTimeout(() => setSaved(false), 2500); }
    else { const d = await res.json().catch(() => ({})); setError(d.error || 'Failed to save'); }
  };

  if (loading) return <div className="mt-6 bg-white rounded-lg shadow p-6 text-sm text-gray-500">Loading permissions…</div>;

  const activeRole = roles.find(r => r.id === selectedRole);
  const isAdmin = activeRole?.name === 'admin';
  const activeModules = drafts[selectedRole] || new Set();

  return (
    <div className="mt-6 bg-white rounded-lg shadow overflow-hidden">
      <div className="px-6 pt-6 pb-4 border-b border-gray-100">
        <div className="flex items-center gap-3 mb-1">
          <Shield className="w-6 h-6 text-indigo-600" />
          <h2 className="text-xl font-semibold text-gray-900">Role Permissions</h2>
        </div>
        <p className="text-sm text-gray-500">
          Select a role to configure which modules its members can access. Changes take effect on next login.
        </p>
      </div>

      <div className="flex min-h-[340px]">
        {/* Left: role list */}
        <div className="w-52 border-r border-gray-100 flex-shrink-0">
          {roles.map(role => (
            <button
              key={role.id}
              onClick={() => { setSelectedRole(role.id); setSaved(false); setError(''); }}
              className={`w-full text-left px-5 py-3 text-sm font-medium transition-colors border-b border-gray-50 ${
                selectedRole === role.id
                  ? 'bg-indigo-50 text-indigo-700 border-l-4 border-l-indigo-500'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              {ROLE_DISPLAY_NAMES[role.name] || role.name}
              {role.name === 'admin' && (
                <span className="ml-2 text-xs text-indigo-400 font-normal">(all access)</span>
              )}
            </button>
          ))}
        </div>

        {/* Right: module toggles */}
        <div className="flex-1 px-6 py-5">
          {!activeRole ? (
            <p className="text-sm text-gray-400 italic">Select a role to configure.</p>
          ) : isAdmin ? (
            <div className="flex flex-col items-center justify-center h-full text-center text-gray-400 gap-2 py-10">
              <Shield className="w-8 h-8 text-indigo-300" />
              <p className="text-sm font-medium text-indigo-500">Admin role has unrestricted access to all modules.</p>
              <p className="text-xs text-gray-400">Module restrictions cannot be applied to the Admin role.</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3 mb-6">
                {ALL_MODULES_LIST.map(mod => {
                  const enabled = activeModules.has(mod.id);
                  return (
                    <button
                      key={mod.id}
                      onClick={() => toggleModule(selectedRole, mod.id)}
                      className={`flex items-center gap-3 px-4 py-3 rounded-xl border-2 text-left transition-all ${
                        enabled
                          ? 'border-indigo-400 bg-indigo-50 text-indigo-800'
                          : 'border-gray-200 bg-white text-gray-500 hover:border-gray-300'
                      }`}
                    >
                      <div className={`w-4 h-4 rounded flex-shrink-0 border-2 flex items-center justify-center ${
                        enabled ? 'bg-indigo-500 border-indigo-500' : 'border-gray-300'
                      }`}>
                        {enabled && <span className="text-white text-[10px] leading-none font-bold">✓</span>}
                      </div>
                      <span className="text-sm font-medium">{mod.name}</span>
                    </button>
                  );
                })}
              </div>

              <div className="flex items-center gap-4">
                <button
                  onClick={save}
                  disabled={saving}
                  className="px-5 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
                >
                  {saving ? 'Saving…' : saved ? '✓ Saved' : 'Save Changes'}
                </button>
                {error && <p className="text-sm text-red-500">{error}</p>}
                {saved && !saving && <p className="text-sm text-green-600">Permissions updated successfully.</p>}
              </div>
            </>
          )}
        </div>
      </div>

      <div className="px-6 py-3 bg-gray-50 border-t border-gray-100">
        <p className="text-xs text-gray-400">
          All granted modules give <strong>full access</strong> (create, edit, delete). Fine-grained permission levels are not used in this release.
        </p>
      </div>
    </div>
  );
};

const TeamManagementSection = ({ authToken }) => {
  const [teams, setTeams] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedTeam, setExpandedTeam] = useState(null);
  const [showAddTeam, setShowAddTeam] = useState(false);
  const [addMemberTeamId, setAddMemberTeamId] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [actionMsg, setActionMsg] = useState('');
  const [teamForm, setTeamForm] = useState({ teamName: '', description: '', department: '', color: '#667eea', module: '' });
  const [editingTeam, setEditingTeam] = useState(null); // { id, teamName, description, department, color, module }

  const COLORS = ['#667eea', '#48bb78', '#ed8936', '#f56565', '#4299e1', '#9f7aea', '#38b2ac', '#e53e3e'];

  const load = async () => {
    setLoading(true);
    try {
      const [teamsRes, usersRes] = await Promise.all([
        fetch('/api/teams', { headers: { Authorization: `Bearer ${authToken}` } }),
        fetch('/api/users', { headers: { Authorization: `Bearer ${authToken}` } })
      ]);
      const teamsData = await teamsRes.json();
      const usersData = await usersRes.json();
      setTeams(Array.isArray(teamsData) ? teamsData : []);
      setUsers(Array.isArray(usersData) ? usersData : []);
    } catch { }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleAddTeam = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch('/api/teams', {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify(teamForm)
      });
      if (res.ok) {
        setShowAddTeam(false);
        setTeamForm({ teamName: '', description: '', department: '', color: '#667eea', module: '' });
        load();
      }
    } catch { }
  };

  const handleDeleteTeam = async (teamId) => {
    if (!confirm('Delete this team?')) return;
    try {
      await fetch(`/api/teams/${teamId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${authToken}` } });
      setActionMsg('Team deleted');
      setTimeout(() => setActionMsg(''), 3000);
      load();
    } catch { }
  };

  const handleUpdateTeam = async (e) => {
    e.preventDefault();
    try {
      await fetch(`/api/teams/${editingTeam.id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({
          teamName: editingTeam.teamName, description: editingTeam.description,
          department: editingTeam.department, color: editingTeam.color, module: editingTeam.module
        })
      });
      setEditingTeam(null);
      setActionMsg('Team updated');
      setTimeout(() => setActionMsg(''), 3000);
      load();
    } catch { }
  };

  const handleAddMember = async (teamId) => {
    if (!selectedUserId) return;
    try {
      await fetch(`/api/teams/${teamId}/members`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({ userId: parseInt(selectedUserId) })
      });
      setSelectedUserId('');
      setAddMemberTeamId(null);
      load();
    } catch { }
  };

  const handleRemoveMember = async (teamId, userId) => {
    try {
      await fetch(`/api/teams/${teamId}/members/${userId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${authToken}` } });
      load();
    } catch { }
  };

  const getMemberNames = (team) => {
    if (!team.members) return [];
    try { return typeof team.members === 'string' ? JSON.parse(team.members) : team.members; }
    catch { return []; }
  };

  if (loading) return <div className="mt-6 bg-white rounded-lg shadow p-6 text-center text-gray-500">Loading teams...</div>;

  return (
    <div className="mt-6 bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Users className="w-6 h-6 text-indigo-600" />
          <h2 className="text-xl font-semibold text-gray-900">Team Management</h2>
        </div>
        <button onClick={() => setShowAddTeam(!showAddTeam)}
          className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 flex items-center gap-2">
          <Plus className="w-4 h-4" /> New Team
        </button>
      </div>

      {actionMsg && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">{actionMsg}</div>
      )}

      {showAddTeam && (
        <form onSubmit={handleAddTeam} className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <h3 className="font-medium text-gray-900 mb-3">Create New Team</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
            <input type="text" placeholder="Team Name *" required value={teamForm.teamName}
              onChange={e => setTeamForm(f => ({ ...f, teamName: e.target.value }))}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none" />
            <input type="text" placeholder="Department" value={teamForm.department}
              onChange={e => setTeamForm(f => ({ ...f, department: e.target.value }))}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none" />
          </div>
          <input type="text" placeholder="Description" value={teamForm.description}
            onChange={e => setTeamForm(f => ({ ...f, description: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none mb-3" />
          <select value={teamForm.module} onChange={e => setTeamForm(f => ({ ...f, module: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none mb-3">
            <option value="">— Module (for EOD reports) —</option>
            <option value="devops">DevOps</option>
            <option value="dataops">DataOps</option>
            <option value="quality">Quality</option>
            <option value="plantrack">PlanTrack</option>
            <option value="quicktrack">QuickTrack</option>
          </select>
          <div className="flex items-center gap-2 mb-4">
            <span className="text-sm text-gray-600">Color:</span>
            {COLORS.map(c => (
              <button key={c} type="button" onClick={() => setTeamForm(f => ({ ...f, color: c }))}
                className={`w-6 h-6 rounded-full transition-transform ${teamForm.color === c ? 'scale-125 ring-2 ring-offset-1 ring-gray-400' : 'hover:scale-110'}`}
                style={{ backgroundColor: c }} />
            ))}
          </div>
          <div className="flex gap-2">
            <button type="submit" className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700">Create Team</button>
            <button type="button" onClick={() => setShowAddTeam(false)} className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg text-sm font-medium hover:bg-gray-300">Cancel</button>
          </div>
        </form>
      )}

      {teams.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p>No teams yet. Create your first team above.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {teams.map(team => {
            const members = getMemberNames(team);
            const isExpanded = expandedTeam === team.id;
            return (
              <div key={team.id} className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="flex items-center gap-3 p-4 cursor-pointer hover:bg-gray-50" onClick={() => setExpandedTeam(isExpanded ? null : team.id)}>
                  <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: team.color || '#667eea' }} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900">{team.team_name}</span>
                      {team.department && <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">{team.department}</span>}
                      {team.module && <span className="text-xs text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded capitalize">{team.module}</span>}
                      <span className="text-xs text-gray-400">{members.length} member{members.length !== 1 ? 's' : ''}</span>
                    </div>
                    {team.description && <p className="text-sm text-gray-500 truncate">{team.description}</p>}
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={e => { e.stopPropagation(); setEditingTeam({ id: team.id, teamName: team.team_name, description: team.description || '', department: team.department || '', color: team.color || '#667eea', module: team.module || '' }); setExpandedTeam(team.id); }}
                      className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded" title="Edit team">
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button onClick={e => { e.stopPropagation(); requestConfirm(`Delete team "${team.name}"?`, () => handleDeleteTeam(team.id)); }}
                      className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded">
                      <Trash2 className="w-4 h-4" />
                    </button>
                    <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                  </div>
                </div>
                {isExpanded && (
                  <div className="border-t border-gray-200 p-4 bg-gray-50">
                    {editingTeam?.id === team.id && (
                      <form onSubmit={handleUpdateTeam} className="mb-4 p-3 bg-white rounded-lg border border-indigo-200">
                        <h4 className="text-sm font-medium text-gray-800 mb-2">Edit Team</h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
                          <input type="text" required placeholder="Team Name *" value={editingTeam.teamName}
                            onChange={e => setEditingTeam(t => ({ ...t, teamName: e.target.value }))}
                            className="px-3 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-indigo-500 outline-none" />
                          <input type="text" placeholder="Department" value={editingTeam.department}
                            onChange={e => setEditingTeam(t => ({ ...t, department: e.target.value }))}
                            className="px-3 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-indigo-500 outline-none" />
                        </div>
                        <input type="text" placeholder="Description" value={editingTeam.description}
                          onChange={e => setEditingTeam(t => ({ ...t, description: e.target.value }))}
                          className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-indigo-500 outline-none mb-2" />
                        <select value={editingTeam.module} onChange={e => setEditingTeam(t => ({ ...t, module: e.target.value }))}
                          className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-indigo-500 outline-none mb-2">
                          <option value="">— Module (for EOD reports) —</option>
                          <option value="devops">DevOps</option>
                          <option value="dataops">DataOps</option>
                          <option value="quality">Quality</option>
                          <option value="plantrack">PlanTrack</option>
                          <option value="quicktrack">QuickTrack</option>
                        </select>
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-xs text-gray-600">Color:</span>
                          {COLORS.map(c => (
                            <button key={c} type="button" onClick={() => setEditingTeam(t => ({ ...t, color: c }))}
                              className={`w-5 h-5 rounded-full transition-transform ${editingTeam.color === c ? 'scale-125 ring-2 ring-offset-1 ring-gray-400' : 'hover:scale-110'}`}
                              style={{ backgroundColor: c }} />
                          ))}
                        </div>
                        <div className="flex gap-2">
                          <button type="submit" className="px-3 py-1.5 bg-indigo-600 text-white rounded text-sm hover:bg-indigo-700">Save</button>
                          <button type="button" onClick={() => setEditingTeam(null)} className="px-3 py-1.5 text-gray-600 bg-gray-100 rounded text-sm hover:bg-gray-200">Cancel</button>
                        </div>
                      </form>
                    )}
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Members</h4>
                    {members.length === 0 ? (
                      <p className="text-sm text-gray-400 mb-2">No members yet.</p>
                    ) : (
                      <div className="space-y-1 mb-2">
                        {members.map(m => (
                          <div key={m.user_id} className="flex items-center justify-between p-2 bg-white rounded border border-gray-100">
                            <div className="flex items-center gap-2">
                              <div className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center text-xs font-semibold text-indigo-700">
                                {(m.user_name || m.email || '?').charAt(0).toUpperCase()}
                              </div>
                              <div>
                                <span className="text-sm font-medium text-gray-900">{m.user_name || m.email}</span>
                                {m.member_role === 'lead' && (
                                  <span className="ml-2 text-xs text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">Lead</span>
                                )}
                              </div>
                            </div>
                            <button onClick={() => handleRemoveMember(team.id, m.user_id)} className="p-1 text-gray-400 hover:text-red-500">
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    {addMemberTeamId === team.id ? (
                      <div className="flex items-center gap-2 mt-2">
                        <select value={selectedUserId} onChange={e => setSelectedUserId(e.target.value)}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none">
                          <option value="">Select user...</option>
                          {users.filter(u => !members.some(m => m.user_id === u.id)).map(u => (
                            <option key={u.id} value={u.id}>{u.user_name || u.email}</option>
                          ))}
                        </select>
                        <button onClick={() => handleAddMember(team.id)} disabled={!selectedUserId}
                          className="px-3 py-2 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700 disabled:opacity-50">Add</button>
                        <button onClick={() => { setAddMemberTeamId(null); setSelectedUserId(''); }}
                          className="px-3 py-2 text-gray-600 bg-gray-200 rounded-lg text-sm hover:bg-gray-300">Cancel</button>
                      </div>
                    ) : (
                      <button onClick={() => setAddMemberTeamId(team.id)}
                        className="text-sm text-indigo-600 hover:text-indigo-800 flex items-center gap-1">
                        <Plus className="w-3.5 h-3.5" /> Add Member
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ---- User Management Section (admin only) ----
const UserManagementSection = ({ authToken }) => {
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddUser, setShowAddUser] = useState(false);
  const [addForm, setAddForm] = useState({ email: '', name: '', role: 'user', password: '' });
  const [addLoading, setAddLoading] = useState(false);
  const [addResult, setAddResult] = useState(null);
  const [actionMsg, setActionMsg] = useState('');
  const [assigningUserId, setAssigningUserId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [usersRes, teamsRes] = await Promise.all([
        fetch('/api/users', { headers: { Authorization: `Bearer ${authToken}` } }),
        fetch('/api/teams', { headers: { Authorization: `Bearer ${authToken}` } })
      ]);
      const usersData = await usersRes.json();
      const teamsData = await teamsRes.json();
      setUsers(Array.isArray(usersData) ? usersData : []);
      setTeams(Array.isArray(teamsData) ? teamsData : []);
    } catch { setUsers([]); setTeams([]); }
    setLoading(false);
  };

  const getUserTeams = (userId) =>
    teams.filter(t => (t.members || []).some(m => m.user_id === userId));

  const getUnassignedTeams = (userId) =>
    teams.filter(t => !(t.members || []).some(m => m.user_id === userId));

  const handleAssignTeam = async (userId, teamId) => {
    try {
      await fetch(`/api/teams/${teamId}/members`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({ userId })
      });
      setAssigningUserId(null);
      load();
    } catch { /* ignore */ }
  };

  const handleRemoveFromTeam = async (userId, teamId) => {
    try {
      await fetch(`/api/teams/${teamId}/members/${userId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${authToken}` } });
      load();
    } catch { /* ignore */ }
  };

  useEffect(() => { load(); }, []);

  const handleAddUser = async (e) => {
    e.preventDefault();
    setAddLoading(true); setAddResult(null);
    try {
      const res = await fetch('/api/users', {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify(addForm)
      });
      const data = await res.json();
      if (!res.ok) setAddResult({ type: 'error', message: data.error || 'Failed to create user' });
      else {
        setAddResult({ type: 'success', message: 'User created.', resetUrl: data.resetUrl });
        setAddForm({ email: '', name: '', role: 'user', password: '' });
        load();
      }
    } catch { setAddResult({ type: 'error', message: 'Network error' }); }
    setAddLoading(false);
  };

  const handleToggleActive = async (u) => {
    try {
      await fetch(`/api/users/${u.id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({ isActive: !u.is_active })
      });
      setActionMsg(`User ${u.is_active ? 'deactivated' : 'activated'} successfully`);
      setTimeout(() => setActionMsg(''), 3000);
      load();
    } catch { /* ignore */ }
  };

  const handleRoleChange = async (u, newRole) => {
    try {
      await fetch(`/api/users/${u.id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({ role: newRole })
      });
      setActionMsg('Role updated');
      setTimeout(() => setActionMsg(''), 3000);
      load();
    } catch { /* ignore */ }
  };

  const handleAdminReset = async (u) => {
    try {
      const res = await fetch(`/api/users/${u.id}/reset-password`, {
        method: 'POST', headers: { Authorization: `Bearer ${authToken}` }
      });
      const data = await res.json();
      if (data.resetUrl) {
        setActionMsg(`Reset link: ${data.resetUrl}`);
      } else {
        setActionMsg('Reset link generated and sent');
        setTimeout(() => setActionMsg(''), 4000);
      }
    } catch { /* ignore */ }
  };

  const fmt = (ts) => ts ? new Date(ts.endsWith('Z') ? ts : ts + 'Z').toLocaleDateString() : 'Never';

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Users size={20} className="text-blue-600" />
          <h2 className="text-xl font-semibold text-gray-900">User Management</h2>
        </div>
        <button onClick={() => { setShowAddUser(v => !v); setAddResult(null); }}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700">
          + Add User
        </button>
      </div>
      <p className="text-gray-500 text-sm mb-4">Manage user accounts, roles, and password resets for your organization.</p>

      {actionMsg && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-700 break-all">{actionMsg}</div>
      )}

      {showAddUser && (
        <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
          <h3 className="font-medium text-gray-800 mb-3 text-sm">Add New User</h3>
          {addResult && (
            <div className={`mb-3 p-2 rounded text-xs ${addResult.type === 'error' ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
              {addResult.message}
              {addResult.resetUrl && <div className="mt-1 font-mono break-all">Invite URL: {addResult.resetUrl}</div>}
            </div>
          )}
          <form onSubmit={handleAddUser} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input required placeholder="Full Name" value={addForm.name} onChange={e => setAddForm(f => ({ ...f, name: e.target.value }))}
              className="border border-gray-200 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400" />
            <input required type="email" placeholder="Email" value={addForm.email} onChange={e => setAddForm(f => ({ ...f, email: e.target.value }))}
              className="border border-gray-200 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400" />
            <select value={addForm.role} onChange={e => setAddForm(f => ({ ...f, role: e.target.value }))}
              className="border border-gray-200 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400">
              {Object.entries(ROLE_DISPLAY_NAMES)
                .sort((a, b) => a[1].localeCompare(b[1]))
                .map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
            <input type="password" placeholder="Password (leave blank for invite link)" value={addForm.password}
              onChange={e => setAddForm(f => ({ ...f, password: e.target.value }))}
              className="border border-gray-200 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400" />
            <div className="sm:col-span-2 flex gap-2">
              <button type="submit" disabled={addLoading}
                className="px-4 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-60">
                {addLoading ? 'Creating…' : 'Create User'}
              </button>
              <button type="button" onClick={() => setShowAddUser(false)}
                className="px-4 py-2 border border-gray-200 text-gray-600 rounded text-sm hover:bg-gray-50">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div className="py-8 text-center text-gray-400 text-sm">Loading users…</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-600">User</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-600">Role</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-600">Teams</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-600">Status</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-600">Last Login</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {users.map(u => {
                const userTeams = getUserTeams(u.id);
                const unassigned = getUnassignedTeams(u.id);
                const isAssigning = assigningUserId === u.id;
                return (
                  <tr key={u.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-800">{u.user_name}</div>
                      <div className="text-xs text-gray-400">{u.email}</div>
                    </td>
                    <td className="px-4 py-3">
                      <select value={u.user_role} onChange={e => handleRoleChange(u, e.target.value)}
                        className="border border-gray-200 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400">
                        {Object.entries(ROLE_DISPLAY_NAMES)
                          .sort((a, b) => a[1].localeCompare(b[1]))
                          .map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                        {/* Render the current role even if it's not in ROLE_DISPLAY_NAMES (e.g. legacy custom roles) */}
                        {u.user_role && !ROLE_DISPLAY_NAMES[u.user_role] && (
                          <option value={u.user_role}>{u.user_role}</option>
                        )}
                      </select>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col gap-1.5 min-w-[180px]">
                        {/* Current team pills */}
                        {userTeams.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {userTeams.map(t => (
                              <span key={t.id} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium text-white"
                                style={{ backgroundColor: t.color || '#667eea' }}>
                                {t.team_name}
                                <button onClick={() => handleRemoveFromTeam(u.id, t.id)}
                                  className="hover:opacity-70 ml-0.5 leading-none" title="Remove from team">
                                  <X className="w-3 h-3" />
                                </button>
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400 italic">No team assigned</span>
                        )}
                        {/* Assign team control */}
                        {teams.length === 0 ? null : isAssigning ? (
                          <div className="flex items-center gap-1.5">
                            <select autoFocus defaultValue=""
                              onChange={e => { if (e.target.value) handleAssignTeam(u.id, parseInt(e.target.value)); }}
                              className="border border-indigo-300 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-400 bg-white">
                              <option value="" disabled>Select a team…</option>
                              {unassigned.map(t => (
                                <option key={t.id} value={t.id}>{t.team_name}</option>
                              ))}
                            </select>
                            <button onClick={() => setAssigningUserId(null)}
                              className="px-2 py-1 text-xs text-gray-500 bg-gray-100 rounded hover:bg-gray-200">
                              Cancel
                            </button>
                          </div>
                        ) : unassigned.length > 0 ? (
                          <button onClick={() => setAssigningUserId(u.id)}
                            className="w-fit inline-flex items-center gap-1 px-2.5 py-1 rounded bg-indigo-50 border border-indigo-200 text-xs font-medium text-indigo-600 hover:bg-indigo-100 hover:border-indigo-400">
                            <Plus className="w-3.5 h-3.5" /> Assign Team
                          </button>
                        ) : null}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${u.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                        {u.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">{fmt(u.last_login_at)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button onClick={() => handleAdminReset(u)}
                          className="text-xs text-blue-600 hover:text-blue-800 font-medium">
                          Reset PW
                        </button>
                        <span className="text-gray-300">|</span>
                        <button onClick={() => handleToggleActive(u)}
                          className={`text-xs font-medium ${u.is_active ? 'text-red-500 hover:text-red-700' : 'text-green-600 hover:text-green-800'}`}>
                          {u.is_active ? 'Deactivate' : 'Activate'}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {users.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-6 text-center text-gray-400 text-sm">No users found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const ORGCHART_URL = 'https://script.google.com/a/macros/pacificdebt.com/s/AKfycbzKd-SculTwbBE2peaAtmi9HjkDiyrtYs87SVFWSP_hBilLdbwcjqgrtPbr2TmjZ_KkCA/exec';

const OrgChartView = () => {
  const [frameStatus, setFrameStatus] = React.useState('loading'); // loading | loaded | blocked

  // Apps Script may refuse to render in an iframe (X-Frame-Options) or redirect the user to a
  // Google login. We can't read the inner state cross-origin, so we use a load timeout to detect
  // a likely-blocked frame and offer the open-in-new-tab fallback.
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setFrameStatus(prev => (prev === 'loading' ? 'blocked' : prev));
    }, 8000);
    return () => clearTimeout(timer);
  }, []);

  const openInTab = () => window.open(ORGCHART_URL, '_blank', 'noopener,noreferrer');

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Org Chart</h1>
          <p className="text-gray-600 mt-1 text-sm">Pacific Debt organizational hierarchy (loaded from Google Apps Script).</p>
        </div>
        <button
          onClick={openInTab}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
        >
          Open in new tab ↗
        </button>
      </div>

      {frameStatus === 'blocked' && (
        <div className="mb-4 p-4 rounded-lg bg-amber-50 border border-amber-200 text-amber-900 text-sm">
          The org chart didn't load inside Orbit. This usually means Google blocked the frame, or your
          browser is not signed in to a <strong>@pacificdebt.com</strong> Google account. Use
          <strong> Open in new tab</strong> above, or sign in to Google and refresh this page.
        </div>
      )}

      <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden" style={{ height: 'calc(100vh - 220px)', minHeight: 520 }}>
        {/* allow-same-origin + allow-scripts is required for Apps Script to render; the Google origin is trusted. */}
        <iframe
          src={ORGCHART_URL}
          title="Pacific Debt Org Chart"
          onLoad={() => setFrameStatus('loaded')}
          className="w-full h-full bg-white"
          referrerPolicy="no-referrer-when-downgrade"
          sandbox="allow-same-origin allow-scripts allow-popups allow-forms allow-top-navigation-by-user-activation"
        />
      </div>
    </div>
  );
};

// ============================================================================
// MARKETING WORKFLOW — Code review + deployment progress tracker
// One parent task per change initiative; many vendor rows underneath, each with
// QA / Dev / Lead remarks and overall status.
// ============================================================================

// Task-level tag (New / Enhancement). Deliberately distinct from the status palettes
// so a tag never reads as a workflow state.
const MWF_TAG_COLORS = {
  'New':         { bg: '#ede9fe', color: '#5b21b6', border: '#c4b5fd' },
  'Enhancement': { bg: '#cffafe', color: '#155e75', border: '#67e8f9' },
};
const MWFTagPill = ({ tag, size = 'sm' }) => {
  const c = MWF_TAG_COLORS[tag] || { bg: '#f3f4f6', color: '#374151', border: '#d1d5db' };
  return (
    <span
      className={`${size === 'xs' ? 'px-1.5 py-0.5 text-[9px]' : 'px-2 py-0.5 text-[10px]'} rounded font-bold uppercase tracking-wide whitespace-nowrap`}
      style={{ background: c.bg, color: c.color, border: `1px solid ${c.border}` }}
    >{tag || 'New'}</span>
  );
};

const MWF_QA_STATUS_COLORS = {
  'Not Yet Tested': { bg: '#fef3c7', color: '#92400e', border: '#fde68a' },
  'In Progress':    { bg: '#dbeafe', color: '#1e40af', border: '#93c5fd' },
  'Passed':         { bg: '#d1fae5', color: '#065f46', border: '#6ee7b7' },
  'Failed':         { bg: '#fee2e2', color: '#991b1b', border: '#fca5a5' },
};
const MWF_BUILD_STATUS_COLORS = {
  'Not Started': { bg: '#f3f4f6', color: '#374151', border: '#d1d5db' },
  'In Progress': { bg: '#fef3c7', color: '#92400e', border: '#fcd34d' },
  'Blocked':     { bg: '#fee2e2', color: '#991b1b', border: '#fca5a5' },
  'Completed':   { bg: '#d1fae5', color: '#065f46', border: '#6ee7b7' },
  'Cancelled':   { bg: '#e5e7eb', color: '#6b7280', border: '#d1d5db' },
};
const MWF_DEPLOYMENT_STATUS_COLORS = {
  'Pending Deployment': { bg: '#fef3c7', color: '#92400e', border: '#fcd34d' },
  'Deployed':           { bg: '#d1fae5', color: '#065f46', border: '#6ee7b7' },
};
// Legacy alias used by the older "Row status" summary
const MWF_OVERALL_STATUS_COLORS = MWF_BUILD_STATUS_COLORS;

const MarketingWorkflowStatusPill = ({ value, colors }) => {
  const s = colors[value] || { bg: '#f3f4f6', color: '#374151', border: '#d1d5db' };
  return (
    <span className="px-2 py-0.5 rounded text-[11px] font-semibold whitespace-nowrap" style={{ background: s.bg, color: s.color, border: `1px solid ${s.border}` }}>{value || '—'}</span>
  );
};

const MWFNewTaskModal = ({ onClose, onCreate }) => {
  const [form, setForm] = React.useState({ title: '', description: '', requestedBy: '', startDate: '', endDate: '', tag: 'New' });
  const [saving, setSaving] = React.useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    setSaving(true);
    const res = await ModulesModel.createMarketingTask(form);
    setSaving(false);
    if (res?.success === false) { alert(res.error || 'Failed to create task'); return; }
    onCreate(res.id);
  };
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <form onSubmit={submit} className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-xl font-semibold">New Marketing Workflow Task</h2>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
            <input required autoFocus value={form.title} onChange={e => setForm(s => ({ ...s, title: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-pink-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tag</label>
            <div className="flex gap-2">
              {['New', 'Enhancement'].map(t => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setForm(s => ({ ...s, tag: t }))}
                  className={`flex-1 px-3 py-2 text-sm rounded-lg border transition-colors ${
                    form.tag === t ? 'border-pink-500 bg-pink-50 text-pink-700 font-semibold' : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
                  }`}
                >{t}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea value={form.description} onChange={e => setForm(s => ({ ...s, description: e.target.value }))} rows={3} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-pink-500" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Requested By</label>
              <input value={form.requestedBy} onChange={e => setForm(s => ({ ...s, requestedBy: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-pink-500" />
            </div>
            <div></div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
              <input type="date" value={form.startDate} onChange={e => setForm(s => ({ ...s, startDate: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-pink-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
              <input type="date" value={form.endDate} onChange={e => setForm(s => ({ ...s, endDate: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-pink-500" />
            </div>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
          <button type="submit" disabled={saving || !form.title.trim()} className="px-4 py-2 bg-pink-600 text-white text-sm rounded-lg hover:bg-pink-700 disabled:opacity-50">{saving ? 'Creating…' : 'Create Task'}</button>
        </div>
      </form>
    </div>
  );
};

const MWFRowEditor = ({ row, buildStatuses, deploymentStatuses, onSave, onDelete, onDuplicate, onView }) => {
  // Backwards-compat: if the row predates the field rename, fall back to legacy keys.
  const initial = React.useMemo(() => ({
    ...row,
    buildStatus: row.buildStatus ?? row.overallStatus ?? 'Not Started',
    qaRemarks: row.qaRemarks ?? row.kamilRemarks ?? '',
    deploymentStatus: row.deploymentStatus ?? 'Pending Deployment',
  }), [row]);
  const [draft, setDraft] = React.useState(initial);
  const [saving, setSaving] = React.useState(false);
  React.useEffect(() => { setDraft(initial); }, [initial]);
  const set = (k, v) => setDraft(d => ({ ...d, [k]: v }));
  // Compare only the top-level fields (the modal handles the inner ones independently).
  const topFields = ['vendorPartner', 'assignedTo', 'remarks', 'buildStatus', 'deploymentStatus', 'deploymentDate', 'deployedBy'];
  const dirty = topFields.some(k => (draft[k] || '') !== (initial[k] || ''));
  const save = async () => {
    if (!dirty) return;
    setSaving(true);
    try {
      // Only send the top-level fields — the modal owns the inner ones.
      const patch = Object.fromEntries(topFields.map(k => [k, draft[k] ?? '']));
      await onSave(row.id, patch);
    } catch (e) { alert(`Save failed: ${e.message || 'unknown error'}`); }
    setSaving(false);
  };
  const ipt = "w-full border border-gray-200 rounded px-2 py-1 text-xs bg-white focus:outline-none focus:ring-1 focus:ring-pink-400";
  const ta = "w-full border border-gray-200 rounded px-2 py-1 text-xs bg-white focus:outline-none focus:ring-1 focus:ring-pink-400 leading-snug";
  return (
    <tr className="border-b border-gray-200 hover:bg-gray-50 align-top">
      <td className="p-2 min-w-[140px]"><input className={ipt} value={draft.vendorPartner || ''} onChange={e => set('vendorPartner', e.target.value)} placeholder="Vendor" /></td>
      <td className="p-2 min-w-[120px]"><input className={ipt} value={draft.assignedTo || ''} onChange={e => set('assignedTo', e.target.value)} placeholder="Assignee" /></td>
      <td className="p-2 min-w-[180px]"><textarea className={ta} rows={3} value={draft.remarks || ''} onChange={e => set('remarks', e.target.value)} placeholder="Remarks" /></td>
      <td className="p-2 min-w-[140px]">
        <select className={ipt} value={draft.buildStatus || 'Not Started'} onChange={e => set('buildStatus', e.target.value)}>
          {buildStatuses.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <div className="mt-1"><MarketingWorkflowStatusPill value={draft.buildStatus} colors={MWF_BUILD_STATUS_COLORS} /></div>
      </td>
      <td className="p-2 min-w-[160px]">
        <select className={ipt} value={draft.deploymentStatus || 'Pending Deployment'} onChange={e => set('deploymentStatus', e.target.value)}>
          {deploymentStatuses.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <div className="mt-1"><MarketingWorkflowStatusPill value={draft.deploymentStatus} colors={MWF_DEPLOYMENT_STATUS_COLORS} /></div>
      </td>
      <td className="p-2 min-w-[130px]"><input type="date" className={ipt} value={draft.deploymentDate || ''} onChange={e => set('deploymentDate', e.target.value)} /></td>
      <td className="p-2 min-w-[120px]"><input className={ipt} value={draft.deployedBy || ''} onChange={e => set('deployedBy', e.target.value)} placeholder="Deployed by" /></td>
      <td className="p-2 whitespace-nowrap">
        <div className="flex flex-col gap-1 min-w-[80px]">
          <button onClick={() => onView(row.id)} className="px-2 py-1 text-xs text-indigo-700 border border-indigo-200 rounded hover:bg-indigo-50">View</button>
          <button onClick={save} disabled={!dirty || saving} className="px-2 py-1 text-xs bg-pink-600 text-white rounded disabled:opacity-40 hover:bg-pink-700">{saving ? '…' : 'Save'}</button>
          <button onClick={() => onDuplicate(row.id)} className="px-2 py-1 text-xs text-amber-700 border border-amber-200 rounded hover:bg-amber-50">Duplicate</button>
          <button onClick={() => { if (confirm('Delete this row?')) onDelete(row.id); }} className="px-2 py-1 text-xs text-red-600 border border-red-200 rounded hover:bg-red-50">Delete</button>
        </div>
      </td>
    </tr>
  );
};

// Modal for the inner row details. Saves Task Description / Reference / Dev Remarks /
// QA Status / QA Remarks independently of the inline row's top-level fields.
const MWFRowDetailModal = ({ row, qaStatuses, taskStats, onClose, onSave, onDelete }) => {
  // Backwards-compat: fall back to the pre-rename legacy key.
  const initial = React.useMemo(() => ({
    taskDescription: row.taskDescription || '',
    reference: row.reference || '',
    devRemarks: row.devRemarks || '',
    qaStatus: row.qaStatus || 'Not Yet Tested',
    qaRemarks: row.qaRemarks ?? row.kamilRemarks ?? '',
  }), [row]);
  const [draft, setDraft] = React.useState(initial);
  const [saving, setSaving] = React.useState(false);
  // Resync if the row is refreshed underneath us (e.g. the inline editor saved).
  React.useEffect(() => { setDraft(initial); }, [initial]);
  const dirty = Object.keys(initial).some(k => draft[k] !== initial[k]);
  const set = (k, v) => setDraft(d => ({ ...d, [k]: v }));

  // Esc closes — but not while a save is in flight or edits would be lost silently.
  React.useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape' || saving) return;
      if (dirty && !confirm('Discard unsaved changes to this row?')) return;
      onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [dirty, saving, onClose]);
  const save = async () => {
    if (!dirty) return;
    setSaving(true);
    try { await onSave(row.id, draft); onClose(); }
    catch (e) { alert(`Save failed: ${e.message || 'unknown error'}`); }
    setSaving(false);
  };
  const handleDelete = () => {
    if (!confirm(`Delete row${row.vendorPartner ? ` for "${row.vendorPartner}"` : ''}?`)) return;
    onDelete(row.id);
    onClose();
  };
  // Task-wide QA tally (across every vendor row), shown as count + rate.
  const totalRows = taskStats?.totalRows ?? 0;
  const passed = taskStats?.qaPassed ?? 0;
  const failed = taskStats?.qaFailed ?? 0;
  const rate = (n) => (totalRows > 0 ? Math.round((n / totalRows) * 100) : 0);
  const requestClose = () => {
    if (saving) return;
    if (dirty && !confirm('Discard unsaved changes to this row?')) return;
    onClose();
  };
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={requestClose}>
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex justify-between items-start gap-3">
            <div className="min-w-0">
              <h2 className="text-lg font-semibold truncate">{row.vendorPartner || 'Row Detail'}</h2>
              <div className="text-xs text-gray-500">{row.assignedTo ? `Assigned to ${row.assignedTo}` : 'Unassigned'}</div>
            </div>
            <button onClick={requestClose} className="text-gray-400 hover:text-gray-600 shrink-0"><X size={20} /></button>
          </div>

          {/* This row's own status — read-only here; edited inline in the rows table. */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-3">
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] font-semibold text-gray-400 uppercase">Build</span>
              <MarketingWorkflowStatusPill value={row.buildStatus ?? row.overallStatus ?? 'Not Started'} colors={MWF_BUILD_STATUS_COLORS} />
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] font-semibold text-gray-400 uppercase">Deployment</span>
              <MarketingWorkflowStatusPill value={row.deploymentStatus || 'Pending Deployment'} colors={MWF_DEPLOYMENT_STATUS_COLORS} />
            </div>
            {row.deploymentDate && <div className="text-[11px] text-gray-500">Deployed {row.deploymentDate}{row.deployedBy ? ` by ${row.deployedBy}` : ''}</div>}
          </div>

          {/* Task-wide QA pass/fail — count AND rate, so one row's review has context. */}
          <div className="mt-3 pt-3 border-t border-gray-100">
            <div className="text-[10px] font-semibold text-gray-400 uppercase mb-1.5">
              QA across all {totalRows} row{totalRows === 1 ? '' : 's'} in this task
            </div>
            <div className="flex flex-wrap gap-2 text-xs">
              <span className="px-2.5 py-1 rounded font-semibold" style={{ background: '#d1fae5', color: '#065f46' }}>
                ✓ Passed: {passed} <span className="font-normal opacity-75">({rate(passed)}%)</span>
              </span>
              <span className="px-2.5 py-1 rounded font-semibold" style={{ background: '#fee2e2', color: '#991b1b' }}>
                ✗ Failed: {failed} <span className="font-normal opacity-75">({rate(failed)}%)</span>
              </span>
              {(taskStats?.qaInProgress ?? 0) > 0 && (
                <span className="px-2.5 py-1 rounded font-semibold" style={{ background: '#dbeafe', color: '#1e40af' }}>
                  ⏳ In Progress: {taskStats.qaInProgress} <span className="font-normal opacity-75">({rate(taskStats.qaInProgress)}%)</span>
                </span>
              )}
              {(taskStats?.qaNotYetTested ?? 0) > 0 && (
                <span className="px-2.5 py-1 rounded font-semibold" style={{ background: '#fef3c7', color: '#92400e' }}>
                  · Not Yet Tested: {taskStats.qaNotYetTested} <span className="font-normal opacity-75">({rate(taskStats.qaNotYetTested)}%)</span>
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Task Description</label>
            <textarea value={draft.taskDescription} onChange={e => set('taskDescription', e.target.value)} rows={6} className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-pink-500 font-mono leading-snug" placeholder={'STEP 1: ...\nSTEP 2: ...'} />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Reference</label>
            <textarea value={draft.reference} onChange={e => set('reference', e.target.value)} rows={2} className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-pink-500" placeholder="Link or doc reference" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Dev Remarks</label>
            <textarea value={draft.devRemarks} onChange={e => set('devRemarks', e.target.value)} rows={3} className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-pink-500" placeholder="Notes from the developer" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-start">
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">QA / Code Review Status</label>
              <select value={draft.qaStatus} onChange={e => set('qaStatus', e.target.value)} className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm bg-white focus:ring-2 focus:ring-pink-500">
                {qaStatuses.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <div className="mt-1"><MarketingWorkflowStatusPill value={draft.qaStatus} colors={MWF_QA_STATUS_COLORS} /></div>
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">QA Remarks</label>
              <textarea value={draft.qaRemarks} onChange={e => set('qaRemarks', e.target.value)} rows={3} className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-pink-500" placeholder="Reviewer notes" />
            </div>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-gray-200 flex justify-between items-center gap-2">
          <button onClick={handleDelete} className="px-3 py-1.5 text-sm text-red-600 border border-red-300 rounded-lg hover:bg-red-50">Delete</button>
          <div className="flex items-center gap-2">
            {dirty && <span className="text-xs text-amber-600 font-medium">Unsaved changes</span>}
            <button onClick={requestClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Close</button>
            <button onClick={save} disabled={!dirty || saving} className="px-4 py-2 bg-pink-600 text-white text-sm rounded-lg hover:bg-pink-700 disabled:opacity-50">{saving ? 'Saving…' : 'Save'}</button>
          </div>
        </div>
      </div>
    </div>
  );
};

const MarketingWorkflowTaskDetail = ({ taskId, onBack }) => {
  const [task, setTask] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [meta, setMeta] = React.useState({ qaStatuses: [], buildStatuses: [], deploymentStatuses: [] });
  const [editingTask, setEditingTask] = React.useState(false);
  const [taskDraft, setTaskDraft] = React.useState({});
  const [viewingRowId, setViewingRowId] = React.useState(null);
  const [taskStats, setTaskStats] = React.useState({});

  const load = React.useCallback(async () => {
    setLoading(true);
    const [detail, list] = await Promise.all([
      ModulesModel.getMarketingTaskDetail(taskId),
      ModulesModel.getMarketingWorkflow({}),
    ]);
    setTask(detail);
    // Pull this task's stats out of the list response so we have qaPassed/qaFailed etc.
    const matching = (list.tasks || []).find(t => t.id === taskId);
    setTaskStats(matching?.stats || {});
    setMeta({
      qaStatuses: list.qaStatuses || [],
      buildStatuses: list.buildStatuses || list.overallStatuses || [],
      deploymentStatuses: list.deploymentStatuses || ['Pending Deployment', 'Deployed'],
    });
    setLoading(false);
  }, [taskId]);
  React.useEffect(() => { load(); }, [load]);

  if (loading) return <div className="max-w-7xl mx-auto px-4 py-6 text-sm text-gray-500">Loading task…</div>;
  if (!task) return <div className="max-w-7xl mx-auto px-4 py-6 text-sm text-gray-500">Task not found. <button onClick={onBack} className="text-pink-600 underline">Back</button></div>;

  const addRow = async () => {
    const res = await ModulesModel.addMarketingRow(taskId, {});
    if (res?.success === false) { alert(res.error || 'Failed to add row'); return; }
    await load();
  };
  const updateRow = async (rowId, data) => {
    await ModulesModel.updateMarketingRow(rowId, data);
    await load();
  };
  const deleteRow = async (rowId) => {
    await ModulesModel.deleteMarketingRow(rowId);
    await load();
  };
  const duplicateRow = async (rowId) => {
    const res = await ModulesModel.duplicateMarketingRow(rowId);
    if (res?.success === false) { alert(res.error || 'Failed to duplicate'); return; }
    await load();
  };

  // Build-status counts (the "Row status" summary, per the spec — still computes overall counts).
  const statusCounts = {};
  (task.rows || []).forEach(r => {
    const k = r.buildStatus || r.overallStatus || 'Not Started';
    statusCounts[k] = (statusCounts[k] || 0) + 1;
  });
  const totalRows = task.rows?.length || 0;
  const qaPassed = taskStats?.qaPassed ?? (task.rows || []).filter(r => r.qaStatus === 'Passed').length;
  const qaFailed = taskStats?.qaFailed ?? (task.rows || []).filter(r => r.qaStatus === 'Failed').length;
  // Single normalized tally used by both the header cards and the row modal, so a
  // failed/stale list fetch can't leave either showing 0 while rows clearly exist.
  const qaTally = {
    totalRows,
    qaPassed,
    qaFailed,
    qaInProgress:   taskStats?.qaInProgress   ?? (task.rows || []).filter(r => r.qaStatus === 'In Progress').length,
    qaNotYetTested: taskStats?.qaNotYetTested ?? (task.rows || []).filter(r => !r.qaStatus || r.qaStatus === 'Not Yet Tested').length,
  };
  const viewingRow = (task.rows || []).find(r => r.id === viewingRowId);

  const beginEditTask = () => {
    setTaskDraft({
      title: task.title,
      description: task.description || '',
      requestedBy: task.requestedBy || '',
      startDate: task.startDate || '',
      endDate: task.endDate || '',
      tag: task.tag || 'New',
    });
    setEditingTask(true);
  };
  const saveTask = async () => {
    try {
      await ModulesModel.updateMarketingTask(taskId, taskDraft);
      setEditingTask(false);
      await load();
    } catch (e) { alert(`Save failed: ${e.message}`); }
  };
  const deleteTask = async () => {
    if (!confirm(`Delete "${task.title}" and all its rows? This cannot be undone.`)) return;
    await ModulesModel.deleteMarketingTask(taskId);
    onBack();
  };

  return (
    <div className="max-w-[100rem] mx-auto px-4 py-6">
      <button onClick={onBack} className="text-sm text-pink-600 hover:text-pink-700 mb-3 flex items-center gap-1"><ArrowLeft size={14} /> Back to list</button>
      <div className="bg-white rounded-lg shadow border border-gray-200 p-5 mb-4">
        {editingTask ? (
          <div className="space-y-3">
            <input value={taskDraft.title} onChange={e => setTaskDraft(d => ({ ...d, title: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-lg font-semibold" />
            <textarea value={taskDraft.description} onChange={e => setTaskDraft(d => ({ ...d, description: e.target.value }))} rows={2} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="Description" />
            <div>
              <label className="block text-xs text-gray-500 mb-1">Tag</label>
              <div className="flex gap-2">
                {['New', 'Enhancement'].map(t => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setTaskDraft(d => ({ ...d, tag: t }))}
                    className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                      (taskDraft.tag || 'New') === t ? 'border-pink-500 bg-pink-50 text-pink-700 font-semibold' : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
                    }`}
                  >{t}</button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><label className="block text-xs text-gray-500 mb-1">Requested By</label><input value={taskDraft.requestedBy} onChange={e => setTaskDraft(d => ({ ...d, requestedBy: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm" /></div>
              <div><label className="block text-xs text-gray-500 mb-1">Start Date</label><input type="date" value={taskDraft.startDate} onChange={e => setTaskDraft(d => ({ ...d, startDate: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm" /></div>
              <div><label className="block text-xs text-gray-500 mb-1">End Date</label><input type="date" value={taskDraft.endDate} onChange={e => setTaskDraft(d => ({ ...d, endDate: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm" /></div>
            </div>
            <div className="flex gap-2">
              <button onClick={saveTask} className="px-4 py-1.5 bg-pink-600 text-white text-sm rounded-lg hover:bg-pink-700">Save</button>
              <button onClick={() => setEditingTask(false)} className="px-4 py-1.5 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-start justify-between gap-3 mb-2">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <div className="text-xs font-mono text-gray-400">{task.taskCode}</div>
                  <MWFTagPill tag={task.tag} />
                </div>
                <h1 className="text-2xl font-bold text-gray-900 break-words">{task.title}</h1>
                {task.description && <p className="text-sm text-gray-600 mt-1 break-words whitespace-pre-wrap">{task.description}</p>}
              </div>
              <div className="flex gap-2 shrink-0">
                <button onClick={beginEditTask} className="px-3 py-1.5 text-xs border border-gray-300 rounded-lg hover:bg-gray-50">Edit</button>
                <button onClick={deleteTask} className="px-3 py-1.5 text-xs text-red-600 border border-red-300 rounded-lg hover:bg-red-50">Delete</button>
              </div>
            </div>

            {/* Main-row roll-ups — derived from the rows below, not editable directly. */}
            <div className="flex flex-wrap items-center gap-4 mt-3 pt-3 border-t border-gray-100">
              <div>
                <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Build Status</div>
                <MarketingWorkflowStatusPill value={task.buildStatus || 'Not Started'} colors={MWF_BUILD_STATUS_COLORS} />
              </div>
              <div>
                <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">QA Status</div>
                <MarketingWorkflowStatusPill value={task.qaStatus || 'Not Yet Tested'} colors={MWF_QA_STATUS_COLORS} />
              </div>
              <div>
                <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Deployment Status</div>
                <MarketingWorkflowStatusPill value={task.deploymentStatus || 'Pending Deployment'} colors={MWF_DEPLOYMENT_STATUS_COLORS} />
              </div>
              <div className="text-[10px] text-gray-400 self-end pb-0.5">
                Rolled up from {totalRows} row{totalRows === 1 ? '' : 's'} · QA shows Passed only when every active row has passed
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 text-xs text-gray-600 mt-3">
              <div><span className="font-semibold text-gray-500 uppercase text-[10px]">Requested by</span><div className="text-sm text-gray-900">{task.requestedBy || '—'}</div></div>
              <div><span className="font-semibold text-gray-500 uppercase text-[10px]">Start</span><div className="text-sm text-gray-900">{task.startDate || '—'}</div></div>
              <div><span className="font-semibold text-gray-500 uppercase text-[10px]">End</span><div className="text-sm text-gray-900">{task.endDate || '—'}</div></div>
              <div><span className="font-semibold text-gray-500 uppercase text-[10px]">Created by</span><div className="text-sm text-gray-900">{task.createdBy || '—'}</div></div>
              <div>
                <span className="font-semibold text-gray-500 uppercase text-[10px]">Updated by</span>
                <div className="text-sm text-gray-900">{task.updatedBy || '—'}</div>
                {task.updatedAt && task.updatedAt !== task.createdAt && (
                  <div className="text-[10px] text-gray-400">{new Date(task.updatedAt.replace(' ', 'T') + 'Z').toLocaleString()}</div>
                )}
              </div>
              <div>
                <span className="font-semibold text-gray-500 uppercase text-[10px]">Created on</span>
                <div className="text-sm text-gray-900">{task.createdAt ? new Date(task.createdAt.replace(' ', 'T') + 'Z').toLocaleDateString() : '—'}</div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Status summary — Row Status still computes the total count of Task Build Status across rows. */}
      {totalRows > 0 && (
        <div className="bg-white rounded-lg shadow border border-gray-200 p-4 mb-4 space-y-3">
          <div>
            <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Row status ({totalRows} total)</div>
            <div className="flex flex-wrap gap-2">
              {Object.keys(MWF_BUILD_STATUS_COLORS).map(s => {
                const n = statusCounts[s] || 0;
                const pct = totalRows > 0 ? Math.round((n / totalRows) * 100) : 0;
                const c = MWF_BUILD_STATUS_COLORS[s];
                return (
                  <div key={s} className="flex-1 min-w-[140px] px-3 py-2 rounded border" style={{ background: c.bg, borderColor: c.border, color: c.color }}>
                    <div className="text-[11px] font-semibold uppercase tracking-wide opacity-80">{s}</div>
                    <div className="text-xl font-bold">{n} <span className="text-xs font-normal opacity-70">({pct}%)</span></div>
                  </div>
                );
              })}
            </div>
          </div>
          {/* QA Passed / Failed counts surfaced explicitly per spec. */}
          <div>
            <div className="text-xs font-semibold text-gray-500 uppercase mb-2">QA / Code Review</div>
            <div className="flex flex-wrap gap-2">
              <div className="flex-1 min-w-[140px] px-3 py-2 rounded border" style={{ background: '#d1fae5', borderColor: '#6ee7b7', color: '#065f46' }}>
                <div className="text-[11px] font-semibold uppercase tracking-wide opacity-80">Passed</div>
                <div className="text-xl font-bold">{qaPassed} <span className="text-xs font-normal opacity-70">({totalRows > 0 ? Math.round((qaPassed / totalRows) * 100) : 0}%)</span></div>
              </div>
              <div className="flex-1 min-w-[140px] px-3 py-2 rounded border" style={{ background: '#fee2e2', borderColor: '#fca5a5', color: '#991b1b' }}>
                <div className="text-[11px] font-semibold uppercase tracking-wide opacity-80">Failed</div>
                <div className="text-xl font-bold">{qaFailed} <span className="text-xs font-normal opacity-70">({totalRows > 0 ? Math.round((qaFailed / totalRows) * 100) : 0}%)</span></div>
              </div>
              <div className="flex-1 min-w-[140px] px-3 py-2 rounded border" style={{ background: '#dbeafe', borderColor: '#93c5fd', color: '#1e40af' }}>
                <div className="text-[11px] font-semibold uppercase tracking-wide opacity-80">In Progress</div>
                <div className="text-xl font-bold">{qaTally.qaInProgress} <span className="text-xs font-normal opacity-70">({totalRows > 0 ? Math.round((qaTally.qaInProgress / totalRows) * 100) : 0}%)</span></div>
              </div>
              <div className="flex-1 min-w-[140px] px-3 py-2 rounded border" style={{ background: '#fef3c7', borderColor: '#fde68a', color: '#92400e' }}>
                <div className="text-[11px] font-semibold uppercase tracking-wide opacity-80">Not Yet Tested</div>
                <div className="text-xl font-bold">{qaTally.qaNotYetTested} <span className="text-xs font-normal opacity-70">({totalRows > 0 ? Math.round((qaTally.qaNotYetTested / totalRows) * 100) : 0}%)</span></div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Rows table */}
      <div className="bg-white rounded-lg shadow border border-gray-200">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-sm font-semibold text-gray-700">Rows ({totalRows})</h2>
          <button onClick={addRow} className="px-3 py-1.5 bg-pink-600 text-white text-sm rounded-lg hover:bg-pink-700">+ Add Row</button>
        </div>
        {totalRows === 0 ? (
          <div className="p-12 text-center text-sm text-gray-400">No rows yet. Click <strong>+ Add Row</strong> to add the first vendor.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 text-left">
                <tr className="text-[10px] uppercase tracking-wide text-gray-500">
                  <th className="p-2 font-semibold">Vendor / Partner</th>
                  <th className="p-2 font-semibold">Assigned To</th>
                  <th className="p-2 font-semibold">Remarks</th>
                  <th className="p-2 font-semibold">Build Status</th>
                  <th className="p-2 font-semibold">Deployment Status</th>
                  <th className="p-2 font-semibold">Deployment Date</th>
                  <th className="p-2 font-semibold">Deployed By</th>
                  <th className="p-2 font-semibold">Action</th>
                </tr>
              </thead>
              <tbody>
                {task.rows.map(row => (
                  <MWFRowEditor
                    key={row.id}
                    row={row}
                    buildStatuses={meta.buildStatuses}
                    deploymentStatuses={meta.deploymentStatuses}
                    onSave={updateRow}
                    onDelete={deleteRow}
                    onDuplicate={duplicateRow}
                    onView={setViewingRowId}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {viewingRow && (
        <MWFRowDetailModal
          row={viewingRow}
          qaStatuses={meta.qaStatuses}
          taskStats={qaTally}
          onClose={() => setViewingRowId(null)}
          onSave={updateRow}
          onDelete={deleteRow}
        />
      )}
    </div>
  );
};

const MarketingWorkflowModule = () => {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [search, setSearch] = React.useState('');
  const [filterRequester, setFilterRequester] = React.useState('');
  const [filterCreator, setFilterCreator] = React.useState('');
  const [filterStatus, setFilterStatus] = React.useState('');
  const [filterVendor, setFilterVendor] = React.useState('');
  const [filterFrom, setFilterFrom] = React.useState('');
  const [filterTo, setFilterTo] = React.useState('');
  const [sort, setSort] = React.useState('newest');
  const [tagTab, setTagTab] = React.useState('ALL');
  const [showNew, setShowNew] = React.useState(false);
  const [selectedTaskId, setSelectedTaskId] = React.useState(null);

  const pacificToday = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/Los_Angeles' });
  const pacificDateShifted = (days) => {
    const d = new Date();
    d.setUTCDate(d.getUTCDate() + days);
    return d.toLocaleDateString('en-CA', { timeZone: 'America/Los_Angeles' });
  };
  const setRange = (from, to) => { setFilterFrom(from); setFilterTo(to); };

  const load = React.useCallback(async () => {
    setLoading(true);
    const d = await ModulesModel.getMarketingWorkflow({
      search,
      requestedBy: filterRequester,
      createdBy:   filterCreator,
      status:      filterStatus,
      vendor:      filterVendor,
      createdFrom: filterFrom,
      createdTo:   filterTo,
      // 'ALL' is the absence of a tag filter — the backend ignores unknown values anyway.
      tag:         tagTab === 'ALL' ? '' : tagTab,
      sort,
    });
    setData(d);
    setLoading(false);
  }, [search, filterRequester, filterCreator, filterStatus, filterVendor, filterFrom, filterTo, tagTab, sort]);
  React.useEffect(() => { load(); }, [load]);

  if (selectedTaskId) {
    return <MarketingWorkflowTaskDetail taskId={selectedTaskId} onBack={() => { setSelectedTaskId(null); load(); }} />;
  }

  const activeFilterCount = [filterRequester, filterCreator, filterStatus, filterVendor, filterFrom, filterTo, search].filter(Boolean).length;
  const dateInvalid = filterFrom && filterTo && filterFrom > filterTo;
  const resetAll = () => {
    setSearch(''); setFilterRequester(''); setFilterCreator('');
    setFilterStatus(''); setFilterVendor(''); setFilterFrom(''); setFilterTo('');
    setSort('newest'); setTagTab('ALL');
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Marketing Workflow</h1>
          <p className="text-gray-600 mt-1 text-sm">Code Review &amp; Deployment Progress tracker — one task per change, one row per vendor.</p>
        </div>
        <button onClick={() => setShowNew(true)} className="px-4 py-2 bg-pink-600 text-white text-sm rounded-lg hover:bg-pink-700 font-medium">+ New Task</button>
      </div>

      <div className="bg-white rounded-lg shadow border border-gray-200 p-4 mb-4 space-y-3">
        {/* Row 1: search + sort + reset */}
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[220px]">
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Search</label>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Title, description, task code (e.g. MWF-2026-…)" className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-pink-500" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Sort</label>
            <select value={sort} onChange={e => setSort(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm min-w-[160px] focus:ring-2 focus:ring-pink-500">
              <option value="newest">Newest first</option>
              <option value="oldest">Oldest first</option>
              <option value="recent">Recently updated</option>
              <option value="alpha">Title A→Z</option>
            </select>
          </div>
          <button onClick={resetAll} disabled={activeFilterCount === 0 && sort === 'newest'} className="px-3 py-1.5 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40">
            Reset {activeFilterCount > 0 ? `(${activeFilterCount})` : ''}
          </button>
        </div>

        {/* Row 2: dropdown filters */}
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Requested By</label>
            <select value={filterRequester} onChange={e => setFilterRequester(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm min-w-[160px] focus:ring-2 focus:ring-pink-500">
              <option value="">All</option>
              {(data?.requesters || []).map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Created By</label>
            <select value={filterCreator} onChange={e => setFilterCreator(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm min-w-[160px] focus:ring-2 focus:ring-pink-500">
              <option value="">All</option>
              {(data?.creators || []).map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Has row with build status</label>
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm min-w-[160px] focus:ring-2 focus:ring-pink-500">
              <option value="">Any</option>
              {(data?.buildStatuses || data?.overallStatuses || []).map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="flex-1 min-w-[160px]">
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Vendor / Partner</label>
            <input list="mwf-vendor-list" value={filterVendor} onChange={e => setFilterVendor(e.target.value)} placeholder="e.g. Ascend" className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-pink-500" />
            <datalist id="mwf-vendor-list">
              {(data?.vendors || []).map(v => <option key={v} value={v} />)}
            </datalist>
          </div>
        </div>

        {/* Row 3: date range + quick presets */}
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Created From</label>
            <input type="date" value={filterFrom} max={filterTo || pacificToday()} onChange={e => setFilterFrom(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-pink-500" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Created To</label>
            <input type="date" value={filterTo} min={filterFrom || undefined} max={pacificToday()} onChange={e => setFilterTo(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-pink-500" />
          </div>
          <div className="flex flex-wrap items-center gap-1.5 pb-0.5">
            <span className="text-xs text-gray-500 uppercase font-semibold">Quick:</span>
            <button type="button" onClick={() => setRange(pacificToday(), pacificToday())} className="px-2.5 py-1 text-xs bg-white border border-gray-300 rounded hover:bg-gray-50">Today</button>
            <button type="button" onClick={() => setRange(pacificDateShifted(-6), pacificToday())} className="px-2.5 py-1 text-xs bg-white border border-gray-300 rounded hover:bg-gray-50">Last 7 days</button>
            <button type="button" onClick={() => setRange(pacificDateShifted(-29), pacificToday())} className="px-2.5 py-1 text-xs bg-white border border-gray-300 rounded hover:bg-gray-50">Last 30 days</button>
            <button type="button" onClick={() => setRange(pacificToday().slice(0,8) + '01', pacificToday())} className="px-2.5 py-1 text-xs bg-white border border-gray-300 rounded hover:bg-gray-50">This month</button>
            <button type="button" onClick={() => setRange(pacificDateShifted(-89), pacificToday())} className="px-2.5 py-1 text-xs bg-white border border-gray-300 rounded hover:bg-gray-50">Last 90 days</button>
          </div>
          {dateInvalid && <span className="text-xs text-rose-600 font-medium">⚠ Created From must be on or before Created To</span>}
        </div>
      </div>

      {/* Active filter chips — gives a glance of what's narrowing the list */}
      {activeFilterCount > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 mb-3 text-xs">
          <span className="text-gray-500 font-semibold">Active:</span>
          {search && <span className="px-2 py-0.5 bg-pink-50 border border-pink-200 text-pink-700 rounded">search: "{search}" <button onClick={() => setSearch('')} className="ml-1 hover:text-pink-900">×</button></span>}
          {filterRequester && <span className="px-2 py-0.5 bg-pink-50 border border-pink-200 text-pink-700 rounded">requested by: {filterRequester} <button onClick={() => setFilterRequester('')} className="ml-1 hover:text-pink-900">×</button></span>}
          {filterCreator && <span className="px-2 py-0.5 bg-pink-50 border border-pink-200 text-pink-700 rounded">created by: {filterCreator} <button onClick={() => setFilterCreator('')} className="ml-1 hover:text-pink-900">×</button></span>}
          {filterStatus && <span className="px-2 py-0.5 bg-pink-50 border border-pink-200 text-pink-700 rounded">status: {filterStatus} <button onClick={() => setFilterStatus('')} className="ml-1 hover:text-pink-900">×</button></span>}
          {filterVendor && <span className="px-2 py-0.5 bg-pink-50 border border-pink-200 text-pink-700 rounded">vendor: {filterVendor} <button onClick={() => setFilterVendor('')} className="ml-1 hover:text-pink-900">×</button></span>}
          {(filterFrom || filterTo) && <span className="px-2 py-0.5 bg-pink-50 border border-pink-200 text-pink-700 rounded">created {filterFrom || '…'} → {filterTo || '…'} <button onClick={() => { setFilterFrom(''); setFilterTo(''); }} className="ml-1 hover:text-pink-900">×</button></span>}
        </div>
      )}

      {/* Tag tabs. Counts come from the backend and ignore the active tab, so the
          other tabs still show their totals while one is selected. */}
      <div className="border-b border-gray-200 mb-4 flex gap-1">
        {['ALL', ...((data?.tags) || ['New', 'Enhancement'])].map(t => {
          const n = data?.tagCounts?.[t];
          const active = tagTab === t;
          return (
            <button
              key={t}
              onClick={() => setTagTab(t)}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                active ? 'text-pink-700 border-pink-600 bg-pink-50/40' : 'text-gray-500 border-transparent hover:text-gray-700'
              }`}
            >
              {t === 'ALL' ? 'All' : t}
              {n !== undefined && (
                <span className={`ml-1.5 px-1.5 py-0.5 rounded text-[10px] font-semibold ${active ? 'bg-pink-100 text-pink-700' : 'bg-gray-100 text-gray-500'}`}>{n}</span>
              )}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="text-sm text-gray-500">Loading…</div>
      ) : (data?.tasks || []).length === 0 ? (
        (activeFilterCount > 0 || tagTab !== 'ALL') ? (
          <div className="bg-white rounded-lg shadow border border-gray-200 p-8 text-center text-sm text-gray-500">
            No {tagTab !== 'ALL' ? <strong>{tagTab}</strong> : null} tasks match the current filters.{' '}
            <button onClick={resetAll} className="text-pink-600 hover:text-pink-700 underline">Clear all filters</button> to see everything.
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow border border-gray-200 p-8 text-center text-sm text-gray-500">No marketing workflow tasks yet. Click <strong>+ New Task</strong> to create the first one.</div>
        )
      ) : (
        <>
        <div className="text-xs text-gray-500 mb-2">{data.tasks.length} task{data.tasks.length === 1 ? '' : 's'} {activeFilterCount > 0 ? 'match current filters' : 'total'}</div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {(data?.tasks || []).map(t => {
            const total = t.stats?.totalRows || 0;
            const completedPct = total > 0 ? Math.round((t.stats.completed / total) * 100) : 0;
            return (
              <button key={t.id} onClick={() => setSelectedTaskId(t.id)} className="text-left bg-white rounded-lg shadow border border-gray-200 hover:shadow-md hover:border-pink-300 transition-all p-4">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <div className="text-[11px] font-mono text-gray-400">{t.taskCode}</div>
                    <MWFTagPill tag={t.tag} size="xs" />
                  </div>
                  <div className="text-[11px] text-gray-400 shrink-0">{t.startDate || '—'} → {t.endDate || '—'}</div>
                </div>
                <h3 className="font-semibold text-gray-900 break-words mb-1">{t.title}</h3>
                {t.description && <p className="text-xs text-gray-500 line-clamp-2 mb-3">{t.description}</p>}
                <div className="flex items-center justify-between text-xs text-gray-600 mb-2">
                  <span>Requested by <strong>{t.requestedBy || '—'}</strong></span>
                  <span className="text-gray-400">{total} row{total === 1 ? '' : 's'}</span>
                </div>

                {/* Main-row roll-ups — one Build / QA / Deployment status for the whole task. */}
                <div className="grid grid-cols-3 gap-1.5 mb-3">
                  <div>
                    <div className="text-[9px] font-semibold text-gray-400 uppercase tracking-wide mb-0.5">Build</div>
                    <MarketingWorkflowStatusPill value={t.buildStatus || 'Not Started'} colors={MWF_BUILD_STATUS_COLORS} />
                  </div>
                  <div>
                    <div className="text-[9px] font-semibold text-gray-400 uppercase tracking-wide mb-0.5">QA</div>
                    <MarketingWorkflowStatusPill value={t.qaStatus || 'Not Yet Tested'} colors={MWF_QA_STATUS_COLORS} />
                  </div>
                  <div>
                    <div className="text-[9px] font-semibold text-gray-400 uppercase tracking-wide mb-0.5">Deployment</div>
                    <MarketingWorkflowStatusPill value={t.deploymentStatus || 'Pending Deployment'} colors={MWF_DEPLOYMENT_STATUS_COLORS} />
                  </div>
                </div>

                {total > 0 && (
                  <>
                    <div className="flex items-center justify-between text-[11px] text-gray-600 mb-1">
                      <span className="font-semibold">Build completion</span>
                      <span className="font-bold text-emerald-700">{completedPct}%</span>
                    </div>
                    <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden mb-2">
                      <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${completedPct}%` }} />
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {(() => {
                        const buckets = [
                          { key: 'completed',  n: t.stats.completed,  icon: '✓', bg: '#d1fae5', color: '#065f46' },
                          { key: 'inProgress', n: t.stats.inProgress, icon: '⏳', bg: '#fef3c7', color: '#92400e' },
                          { key: 'blocked',    n: t.stats.blocked,    icon: '⛔', bg: '#fee2e2', color: '#991b1b' },
                          { key: 'notStarted', n: t.stats.notStarted, icon: '·', bg: '#f3f4f6', color: '#374151' },
                          { key: 'cancelled',  n: t.stats.cancelled,  icon: '✕', bg: '#e5e7eb', color: '#6b7280' },
                        ];
                        return buckets.filter(b => b.n > 0).map(b => {
                          const pct = total > 0 ? Math.round((b.n / total) * 100) : 0;
                          return (
                            <span key={b.key} className="px-2 py-0.5 text-[10px] font-semibold rounded" style={{ background: b.bg, color: b.color }}>
                              {b.icon} {b.n} <span className="opacity-70 font-normal">({pct}%)</span>
                            </span>
                          );
                        });
                      })()}
                    </div>
                  </>
                )}
              </button>
            );
          })}
        </div>
        </>
      )}

      {showNew && <MWFNewTaskModal onClose={() => setShowNew(false)} onCreate={(id) => { setShowNew(false); setSelectedTaskId(id); }} />}
    </div>
  );
};

// ============================================================================
// WORKFLOW TROUBLESHOOTING LOGS — FAQ-style reference entries for devs.
// Anyone can read; only the creator (or an admin) can edit/delete via the backend gate.
// ============================================================================

const TSL_ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
const TSL_MAX_IMAGE_BYTES = 2 * 1024 * 1024;
const TSL_MAX_IMAGES = 10;
const TSL_STATUS_OPTIONS = ['Draft', 'Published', 'Archived'];
const TSL_SEVERITY_OPTIONS = ['Critical', 'High', 'Medium', 'Low'];
const TSL_ENVIRONMENT_OPTIONS = ['DEV', 'UAT', 'Staging', 'PROD', 'All'];

const TSL_STATUS_COLORS = {
  Draft:     { bg: '#fef3c7', color: '#92400e' },
  Published: { bg: '#d1fae5', color: '#065f46' },
  Archived:  { bg: '#e5e7eb', color: '#6b7280' },
};
const TSL_SEVERITY_COLORS = {
  Critical: { bg: '#fee2e2', color: '#991b1b' },
  High:     { bg: '#ffedd5', color: '#9a3412' },
  Medium:   { bg: '#fef9c3', color: '#854d0e' },
  Low:      { bg: '#dcfce7', color: '#166534' },
};

const TSLBadge = ({ value, colors, prefix = '' }) => {
  if (!value) return null;
  const c = colors[value] || { bg: '#f3f4f6', color: '#374151' };
  return <span className="px-2 py-0.5 text-[11px] font-semibold rounded whitespace-nowrap" style={{ background: c.bg, color: c.color }}>{prefix}{value}</span>;
};

const fmtMinutes = (m) => {
  if (m == null || m === '') return '—';
  const n = Number(m);
  if (!Number.isFinite(n) || n < 0) return '—';
  if (n < 60) return `${n}m`;
  const h = Math.floor(n / 60);
  const mm = n % 60;
  return mm > 0 ? `${h}h ${mm}m` : `${h}h`;
};

// Convert a File from <input type="file"> to the base64 payload the API expects.
const tslReadFileAsBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => {
    const result = reader.result || '';
    const base64 = String(result).split(',')[1] || '';
    resolve({ fileName: file.name, fileType: file.type, fileSize: file.size, fileData: base64 });
  };
  reader.onerror = () => reject(reader.error || new Error('Failed to read file'));
  reader.readAsDataURL(file);
});

const TSLImageThumb = ({ image, onDelete }) => {
  const src = `data:${image.fileType};base64,${image.fileData}`;
  return (
    <div className="relative group">
      <a href={src} target="_blank" rel="noopener noreferrer" className="block">
        <img src={src} alt={image.fileName} className="w-full h-32 object-cover rounded border border-gray-200 hover:border-teal-400" />
      </a>
      {onDelete && (
        <button
          onClick={() => { if (confirm(`Delete "${image.fileName}"?`)) onDelete(image.id); }}
          className="absolute top-1 right-1 bg-white border border-gray-300 rounded-full w-6 h-6 text-xs text-gray-500 hover:text-red-600 hover:border-red-300 opacity-0 group-hover:opacity-100"
          title="Delete image"
        >×</button>
      )}
      <div className="text-[10px] text-gray-500 mt-1 truncate" title={image.fileName}>{image.fileName}</div>
    </div>
  );
};

const TSLLogModal = ({ initial, onClose, onSave, user }) => {
  const isEdit = !!initial;
  const [form, setForm] = React.useState({
    title: initial?.title || '',
    description: initial?.description || '',
    tags: initial?.tags || '',
    status: initial?.status || 'Published',
    category: initial?.category || '',
    severity: initial?.severity || '',
    environment: initial?.environment || '',
    issueDate: initial?.issueDate || '',
    resolvedBy: initial?.resolvedBy || (user?.user_name || user?.name || ''),
    resolutionMinutes: initial?.resolutionMinutes ?? '',
  });
  const [pendingImages, setPendingImages] = React.useState([]); // [{fileName, fileType, fileSize, fileData}]
  const [error, setError] = React.useState('');
  const [saving, setSaving] = React.useState(false);

  const onPick = async (e) => {
    setError('');
    const files = Array.from(e.target.files || []);
    e.target.value = ''; // allow re-picking the same file
    const next = [...pendingImages];
    for (const f of files) {
      if (!TSL_ALLOWED_IMAGE_TYPES.includes(f.type)) { setError(`"${f.name}" is not a supported image type.`); continue; }
      if (f.size > TSL_MAX_IMAGE_BYTES) { setError(`"${f.name}" is larger than 2 MB.`); continue; }
      if (next.length >= TSL_MAX_IMAGES) { setError(`Max ${TSL_MAX_IMAGES} images per log.`); break; }
      try { next.push(await tslReadFileAsBase64(f)); }
      catch (err) { setError(err?.message || 'Failed to read file'); }
    }
    setPendingImages(next);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { setError('Title is required'); return; }
    setSaving(true); setError('');
    try {
      if (isEdit) {
        await ModulesModel.updateTroubleshootingLog(initial.id, form);
        // Upload any newly attached images
        for (const img of pendingImages) {
          const r = await ModulesModel.addTroubleshootingLogImage(initial.id, img);
          if (r?.success === false) { setError(r.error || 'Image upload failed'); break; }
        }
        onSave(initial.id);
      } else {
        const res = await ModulesModel.createTroubleshootingLog({ ...form, images: pendingImages });
        if (res?.success === false) { setError(res.error || 'Save failed'); setSaving(false); return; }
        onSave(res.id);
      }
    } catch (err) {
      setError(err?.message || 'Save failed');
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <form onSubmit={submit} className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-xl font-semibold">{isEdit ? 'Edit Troubleshooting Log' : 'New Troubleshooting Log'}</h2>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title <span className="text-red-500">*</span></label>
            <input required autoFocus value={form.title} onChange={e => setForm(s => ({ ...s, title: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500" placeholder="e.g. 502 from /api/dataops on cold-start" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <MiniWysiwyg value={form.description} onChange={v => setForm(s => ({ ...s, description: v }))} minHeight="180px" placeholder={'Symptom: ...\nRoot cause: ...\nFix: ...\nVerified by: ...'} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tags <span className="text-gray-400 font-normal">(comma-separated, for search)</span></label>
            <input value={form.tags} onChange={e => setForm(s => ({ ...s, tags: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500" placeholder="dataops, snowflake, 502" />
          </div>

          {/* Tracking metadata — optional but recommended */}
          <div className="border border-gray-200 rounded-lg p-3 space-y-3 bg-gray-50">
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Tracking metadata</div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Status</label>
                <select value={form.status} onChange={e => setForm(s => ({ ...s, status: e.target.value }))} className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm bg-white focus:ring-2 focus:ring-teal-500">
                  {TSL_STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Severity</label>
                <select value={form.severity} onChange={e => setForm(s => ({ ...s, severity: e.target.value }))} className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm bg-white focus:ring-2 focus:ring-teal-500">
                  <option value="">—</option>
                  {TSL_SEVERITY_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Environment</label>
                <select value={form.environment} onChange={e => setForm(s => ({ ...s, environment: e.target.value }))} className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm bg-white focus:ring-2 focus:ring-teal-500">
                  <option value="">—</option>
                  {TSL_ENVIRONMENT_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Category</label>
                <input value={form.category} onChange={e => setForm(s => ({ ...s, category: e.target.value }))} placeholder="e.g. DevOps, DataOps, Forth" className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm bg-white focus:ring-2 focus:ring-teal-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Issue Date</label>
                <input type="date" value={form.issueDate} onChange={e => setForm(s => ({ ...s, issueDate: e.target.value }))} className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm bg-white focus:ring-2 focus:ring-teal-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Resolution time <span className="text-gray-400 font-normal">(minutes)</span></label>
                <input type="number" min="0" value={form.resolutionMinutes} onChange={e => setForm(s => ({ ...s, resolutionMinutes: e.target.value }))} placeholder="e.g. 45" className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm bg-white focus:ring-2 focus:ring-teal-500" />
              </div>
              <div className="col-span-2 md:col-span-3">
                <label className="block text-xs font-medium text-gray-700 mb-1">Resolved By</label>
                <input value={form.resolvedBy} onChange={e => setForm(s => ({ ...s, resolvedBy: e.target.value }))} placeholder="Who actually fixed the issue" className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm bg-white focus:ring-2 focus:ring-teal-500" />
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Images <span className="text-gray-400 font-normal">(JPEG / PNG / GIF / WebP, max 2 MB each, up to {TSL_MAX_IMAGES})</span></label>
            <input type="file" accept="image/jpeg,image/png,image/gif,image/webp" multiple onChange={onPick} className="block text-sm" />
            {pendingImages.length > 0 && (
              <div className="grid grid-cols-3 gap-2 mt-3">
                {pendingImages.map((img, i) => (
                  <div key={i} className="relative group">
                    <img src={`data:${img.fileType};base64,${img.fileData}`} alt={img.fileName} className="w-full h-24 object-cover rounded border border-gray-200" />
                    <button type="button" onClick={() => setPendingImages(p => p.filter((_, idx) => idx !== i))} className="absolute top-1 right-1 bg-white border border-gray-300 rounded-full w-5 h-5 text-xs text-gray-500 hover:text-red-600">×</button>
                    <div className="text-[10px] text-gray-500 mt-0.5 truncate" title={img.fileName}>{img.fileName}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
          {error && <p className="text-red-600 text-xs font-medium">{error}</p>}
        </div>
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
          <button type="submit" disabled={saving || !form.title.trim()} className="px-4 py-2 bg-teal-600 text-white text-sm rounded-lg hover:bg-teal-700 disabled:opacity-50">{saving ? 'Saving…' : (isEdit ? 'Save Changes' : 'Create Log')}</button>
        </div>
      </form>
    </div>
  );
};

const TSLDetailView = ({ logId, user, onBack, onEdit, onDeleted }) => {
  const [log, setLog] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const userName = user?.user_name || user?.name || '';
  const userId = user?.user_id || user?.id;
  const role = user?.user_role || user?.role || '';
  const isAdmin = role === 'admin';

  const load = React.useCallback(async () => {
    setLoading(true);
    setLog(await ModulesModel.getTroubleshootingLogDetail(logId));
    setLoading(false);
  }, [logId]);
  React.useEffect(() => { load(); }, [load]);

  if (loading) return <div className="max-w-4xl mx-auto px-4 py-6 text-sm text-gray-500">Loading…</div>;
  if (!log) return <div className="max-w-4xl mx-auto px-4 py-6 text-sm text-gray-500">Log not found. <button onClick={onBack} className="text-teal-600 underline">Back</button></div>;

  const canEdit = isAdmin || log.createdBy === userName || log.createdById === userId;

  const handleDelete = async () => {
    if (!confirm(`Delete "${log.title}"? This removes the log and its images.`)) return;
    const res = await ModulesModel.deleteTroubleshootingLog(logId);
    if (res?.success === false) { alert(res.error || 'Delete failed'); return; }
    onDeleted();
  };
  const handleDeleteImage = async (imageId) => {
    const res = await ModulesModel.deleteTroubleshootingLogImage(imageId);
    if (res?.success === false) { alert(res.error || 'Delete failed'); return; }
    await load();
  };
  const handleVerify = async () => {
    const res = await ModulesModel.verifyTroubleshootingLog(logId);
    if (res?.success === false) { alert(res.error || 'Verify failed'); return; }
    await load();
  };

  const fmtTs = (s) => s ? new Date(s.replace(' ', 'T') + 'Z').toLocaleString() : '—';

  return (
    <div className="max-w-4xl mx-auto px-4 py-6">
      <button onClick={onBack} className="text-sm text-teal-600 hover:text-teal-700 mb-3 flex items-center gap-1"><ArrowLeft size={14} /> Back to list</button>
      <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl font-bold text-gray-900 break-words">{log.title}</h1>
            <div className="flex flex-wrap items-center gap-1.5 mt-2">
              <TSLBadge value={log.status} colors={TSL_STATUS_COLORS} />
              <TSLBadge value={log.severity} colors={TSL_SEVERITY_COLORS} />
              {log.environment && <span className="px-2 py-0.5 text-[11px] font-semibold rounded bg-indigo-50 text-indigo-700 whitespace-nowrap">{log.environment}</span>}
              {log.category && <span className="px-2 py-0.5 text-[11px] font-semibold rounded bg-blue-50 text-blue-700 whitespace-nowrap">{log.category}</span>}
            </div>
          </div>
          <div className="flex gap-2 shrink-0">
            <button onClick={handleVerify} className="px-3 py-1.5 text-sm border border-emerald-300 text-emerald-700 rounded-lg hover:bg-emerald-50" title="Mark this log as verified-current so others know it still works">✓ Mark Verified</button>
            {canEdit && (
              <>
                <button onClick={() => onEdit(log)} className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">Edit</button>
                <button onClick={handleDelete} className="px-3 py-1.5 text-sm text-red-600 border border-red-300 rounded-lg hover:bg-red-50">Delete</button>
              </>
            )}
          </div>
        </div>
        {log.tags && (
          <div className="flex flex-wrap gap-1 mb-3">
            {log.tags.split(',').map(t => t.trim()).filter(Boolean).map(t => (
              <span key={t} className="px-2 py-0.5 text-[11px] font-semibold bg-teal-50 text-teal-700 rounded">#{t}</span>
            ))}
          </div>
        )}

        {/* Tracking metadata block */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-2 text-xs text-gray-600 border border-gray-200 rounded-lg p-3 mb-4 bg-gray-50">
          <div><span className="text-gray-500 uppercase font-semibold text-[10px] block">Created</span><span className="text-gray-900">{log.createdBy || 'Unknown'}</span><span className="text-gray-400 block">{fmtTs(log.createdAt)}</span></div>
          <div><span className="text-gray-500 uppercase font-semibold text-[10px] block">Last Updated</span><span className="text-gray-900">{log.updatedBy || '—'}</span><span className="text-gray-400 block">{log.updatedAt && log.updatedAt !== log.createdAt ? fmtTs(log.updatedAt) : '—'}</span></div>
          <div><span className="text-gray-500 uppercase font-semibold text-[10px] block">Issue Date</span><span className="text-gray-900">{log.issueDate || '—'}</span></div>
          <div><span className="text-gray-500 uppercase font-semibold text-[10px] block">Resolved By</span><span className="text-gray-900">{log.resolvedBy || '—'}</span></div>
          <div><span className="text-gray-500 uppercase font-semibold text-[10px] block">Resolution Time</span><span className="text-gray-900">{fmtMinutes(log.resolutionMinutes)}</span></div>
          <div><span className="text-gray-500 uppercase font-semibold text-[10px] block">Views</span><span className="text-gray-900">{log.viewCount || 0}</span></div>
          <div className="col-span-2"><span className="text-gray-500 uppercase font-semibold text-[10px] block">Last Verified</span><span className="text-gray-900">{log.lastVerifiedBy || '—'}</span><span className="text-gray-400 block">{log.lastVerifiedAt ? fmtTs(log.lastVerifiedAt) : 'Never'}</span></div>
        </div>
        {log.description ? (
          <div className="text-sm text-gray-800 break-words border-l-4 border-teal-200 bg-teal-50/40 p-4 rounded mb-4 prose prose-sm max-w-none [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5" dangerouslySetInnerHTML={{ __html: sanitizeBugHtml(log.description) }} />
        ) : (
          <div className="text-sm text-gray-400 italic mb-4">No description provided.</div>
        )}
        {(log.images || []).length > 0 && (
          <div>
            <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Attachments ({log.images.length})</div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {log.images.map(img => (
                <TSLImageThumb key={img.id} image={img} onDelete={canEdit ? handleDeleteImage : null} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const TroubleshootingLogsModule = ({ user }) => {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [search, setSearch] = React.useState('');
  const [filterCreator, setFilterCreator] = React.useState('');
  const [filterStatus, setFilterStatus] = React.useState('');
  const [filterSeverity, setFilterSeverity] = React.useState('');
  const [filterEnvironment, setFilterEnvironment] = React.useState('');
  const [filterCategory, setFilterCategory] = React.useState('');
  const [showModal, setShowModal] = React.useState(false);
  const [editingLog, setEditingLog] = React.useState(null);
  const [selectedLogId, setSelectedLogId] = React.useState(null);
  const [logPage, setLogPage] = React.useState(0);
  const LOG_PAGE_SIZE = 10;

  const load = React.useCallback(async () => {
    setLoading(true);
    const d = await ModulesModel.getTroubleshootingLogs({
      search,
      createdBy: filterCreator,
      status: filterStatus,
      severity: filterSeverity,
      environment: filterEnvironment,
      category: filterCategory,
    });
    setData(d);
    setLoading(false);
  }, [search, filterCreator, filterStatus, filterSeverity, filterEnvironment, filterCategory]);
  React.useEffect(() => { load(); }, [load]);
  React.useEffect(() => { setLogPage(0); }, [search, filterCreator, filterStatus, filterSeverity, filterEnvironment, filterCategory]);
  // Also clamp whenever the list itself changes (e.g. a delete or edit shrinks it) —
  // otherwise a user sitting on the last page can end up stranded on an
  // out-of-range page with an empty list and no visible way back.
  React.useEffect(() => {
    const total = Math.ceil((data?.logs || []).length / LOG_PAGE_SIZE);
    if (logPage > 0 && logPage >= total) setLogPage(Math.max(0, total - 1));
  }, [data]);

  if (selectedLogId) {
    return (
      <TSLDetailView
        logId={selectedLogId}
        user={user}
        onBack={() => { setSelectedLogId(null); load(); }}
        onEdit={(log) => { setEditingLog(log); setShowModal(true); }}
        onDeleted={() => { setSelectedLogId(null); load(); }}
      />
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Workflow Troubleshooting Logs</h1>
          <p className="text-gray-600 mt-1 text-sm">Dev FAQ — quick lookups for past issues, root causes, and fixes.</p>
        </div>
        <button onClick={() => { setEditingLog(null); setShowModal(true); }} className="px-4 py-2 bg-teal-600 text-white text-sm rounded-lg hover:bg-teal-700 font-medium">+ New Log</button>
      </div>

      <div className="bg-white rounded-lg shadow border border-gray-200 p-4 mb-4 space-y-3">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[220px]">
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Search</label>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search title, description, or tags…" className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-teal-500" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Author</label>
            <select value={filterCreator} onChange={e => setFilterCreator(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm min-w-[140px] focus:ring-2 focus:ring-teal-500">
              <option value="">All</option>
              {(data?.creators || []).map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <button onClick={() => { setSearch(''); setFilterCreator(''); setFilterStatus(''); setFilterSeverity(''); setFilterEnvironment(''); setFilterCategory(''); }} className="px-3 py-1.5 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">Reset</button>
        </div>
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Status</label>
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm min-w-[120px] focus:ring-2 focus:ring-teal-500">
              <option value="">All</option>
              {TSL_STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Severity</label>
            <select value={filterSeverity} onChange={e => setFilterSeverity(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm min-w-[120px] focus:ring-2 focus:ring-teal-500">
              <option value="">All</option>
              {TSL_SEVERITY_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Environment</label>
            <select value={filterEnvironment} onChange={e => setFilterEnvironment(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm min-w-[120px] focus:ring-2 focus:ring-teal-500">
              <option value="">All</option>
              {TSL_ENVIRONMENT_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Category</label>
            <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm min-w-[140px] focus:ring-2 focus:ring-teal-500">
              <option value="">All</option>
              {(data?.categories || []).map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-gray-500">Loading…</div>
      ) : (data?.logs || []).length === 0 ? (
        <div className="bg-white rounded-lg shadow border border-gray-200 p-8 text-center text-sm text-gray-500">
          {search || filterCreator
            ? <>No logs match the current filters. <button onClick={() => { setSearch(''); setFilterCreator(''); }} className="text-teal-600 hover:text-teal-700 underline">Clear</button>.</>
            : <>No troubleshooting logs yet. Click <strong>+ New Log</strong> to add the first one.</>}
        </div>
      ) : (() => {
        const allLogs = data?.logs || [];
        const totalPages = Math.ceil(allLogs.length / LOG_PAGE_SIZE);
        const pagedLogs = allLogs.slice(logPage * LOG_PAGE_SIZE, (logPage + 1) * LOG_PAGE_SIZE);
        return (
          <div className="orbit-card overflow-hidden">
            <div className="divide-y divide-gray-100">
              {pagedLogs.map(l => (
                <button key={l.id} onClick={() => setSelectedLogId(l.id)} className="w-full text-left px-4 py-2.5 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="font-semibold text-gray-900 text-sm break-words">{l.title}</h3>
                    <div className="flex items-center gap-1.5 shrink-0">
                      {l.viewCount > 0 && <span className="text-[11px] text-gray-400">👁 {l.viewCount}</span>}
                      {l.imageCount > 0 && <span className="px-1.5 py-0.5 text-[10px] font-semibold bg-teal-50 text-teal-700 rounded">📎 {l.imageCount}</span>}
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5 mt-1">
                    <TSLBadge value={l.status} colors={TSL_STATUS_COLORS} />
                    <TSLBadge value={l.severity} colors={TSL_SEVERITY_COLORS} />
                    {l.environment && <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-indigo-50 text-indigo-700">{l.environment}</span>}
                    {l.category && <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-blue-50 text-blue-700">{l.category}</span>}
                  </div>
                  {l.description && <p className="text-xs text-gray-500 line-clamp-1 mt-1 break-words">{stripHtml(l.description)}</p>}
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-gray-400 mt-1">
                    <span>by <strong className="text-gray-600">{l.createdBy || 'Unknown'}</strong></span>
                    <span>{l.createdAt ? new Date(l.createdAt.replace(' ', 'T') + 'Z').toLocaleDateString() : '—'}</span>
                    {l.updatedAt && l.updatedAt !== l.createdAt && <span>· updated {new Date(l.updatedAt.replace(' ', 'T') + 'Z').toLocaleDateString()}</span>}
                    {l.issueDate && <span>· issue {l.issueDate}</span>}
                    {l.resolutionMinutes != null && l.resolutionMinutes !== '' && <span>· fixed in {fmtMinutes(l.resolutionMinutes)}</span>}
                    {l.tags && (
                      <span className="flex flex-wrap gap-1">
                        {l.tags.split(',').map(t => t.trim()).filter(Boolean).map(t => (
                          <span key={t} className="px-1.5 py-0.5 text-[10px] font-semibold bg-gray-100 text-gray-600 rounded">#{t}</span>
                        ))}
                      </span>
                    )}
                  </div>
                </button>
              ))}
            </div>
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-2.5 border-t border-gray-100">
                <span className="text-xs text-gray-400">
                  {logPage * LOG_PAGE_SIZE + 1}–{Math.min((logPage + 1) * LOG_PAGE_SIZE, allLogs.length)} of {allLogs.length}
                </span>
                <div className="flex gap-2">
                  <button onClick={() => setLogPage(p => p - 1)} disabled={logPage === 0}
                    className="px-3 py-1 text-xs font-medium border border-gray-200 rounded-md hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">Prev</button>
                  <button onClick={() => setLogPage(p => p + 1)} disabled={logPage >= totalPages - 1}
                    className="px-3 py-1 text-xs font-medium border border-gray-200 rounded-md hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">Next</button>
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {showModal && (
        <TSLLogModal
          initial={editingLog}
          user={user}
          onClose={() => { setShowModal(false); setEditingLog(null); }}
          onSave={(id) => {
            setShowModal(false);
            setEditingLog(null);
            if (id && !selectedLogId) setSelectedLogId(id);
            else load();
          }}
        />
      )}
    </div>
  );
};

// ============================================================================
// DEPLOYMENTS — pre-deploy approval chain + deploy/verify/completion lifecycle.
// Mirrors CRF mechanics; shares the buildChainForRequester routing.
// ============================================================================

// Status names the stage the deployment is waiting on. The legacy values at the bottom
// are kept so pre-resequence records still render with a sensible colour.
const DPL_STATUS_COLORS = {
  Draft:                                        { bg: '#f3f4f6', color: '#374151' },
  'Ready for QA':                               { bg: '#fce7f3', color: '#9d174d' },
  'Pending Technical/DevOps Manager Approval':  { bg: '#dbeafe', color: '#1e40af' },
  'Pending PM Approval':                        { bg: '#e0e7ff', color: '#3730a3' },
  'Pending Final Approval':                     { bg: '#fef3c7', color: '#92400e' },
  'Approved for Deployment':                    { bg: '#d1fae5', color: '#065f46' },
  'Deployed \u2013 Pending Validation':          { bg: '#ede9fe', color: '#5b21b6' },
  Completed:                                    { bg: '#cffafe', color: '#155e75' },
  'Rollback Required':                          { bg: '#ffedd5', color: '#9a3412' },
  'Deployment Failed':                          { bg: '#fee2e2', color: '#991b1b' },
  Rejected:                                     { bg: '#fee2e2', color: '#991b1b' },
  Cancelled:                                    { bg: '#e5e7eb', color: '#6b7280' },
  // legacy
  Submitted: { bg: '#dbeafe', color: '#1e40af' },
  Approved:  { bg: '#d1fae5', color: '#065f46' },
  Deployed:  { bg: '#ede9fe', color: '#5b21b6' },
  Verified:  { bg: '#dcfce7', color: '#15803d' },
};

// Summary-card buckets for the Deployments list header. The resequenced approval flow has
// 12 real statuses (shown individually via DeploymentStatusPill), which is too many for an
// at-a-glance row, so each one — plus the legacy pre-resequence statuses still on old
// records — is folded into the bucket it corresponds to.
const DPL_CARD_GROUPS = [
  { label: 'Draft',       statuses: ['Draft'], color: DPL_STATUS_COLORS.Draft },
  { label: 'In Approval', statuses: ['Submitted', 'Ready for QA', 'Pending Technical/DevOps Manager Approval', 'Pending PM Approval', 'Pending Final Approval'], color: DPL_STATUS_COLORS.Submitted },
  { label: 'Approved',    statuses: ['Approved', 'Approved for Deployment'], color: DPL_STATUS_COLORS.Approved },
  { label: 'Deployed',    statuses: ['Deployed', 'Deployed – Pending Validation', 'Verified'], color: DPL_STATUS_COLORS.Deployed },
  { label: 'Completed',   statuses: ['Completed'], color: DPL_STATUS_COLORS.Completed },
  { label: 'Failed',      statuses: ['Rollback Required', 'Deployment Failed'], color: DPL_STATUS_COLORS['Deployment Failed'] },
  { label: 'Rejected',    statuses: ['Rejected'], color: DPL_STATUS_COLORS.Rejected },
  { label: 'Cancelled',   statuses: ['Cancelled'], color: DPL_STATUS_COLORS.Cancelled },
];

// Approval sequence, mirrored from DEPLOYMENT_STAGE_DEFS on the server. Used for the
// "who is this waiting on" summary; the stage list itself still comes from the API.
const DPL_APPROVAL_SEQUENCE = [
  { key: 'qa_verify',      label: 'QA Approval',                        who: 'QA Lead' },
  { key: 'devops_review',  label: 'Technical/DevOps Manager Approval',  who: 'DevOps Manager' },
  { key: 'pm_verify',      label: 'PM Approval',                        who: 'Project Manager' },
  { key: 'final_approval', label: 'Final Approval',                     who: 'Anthony Zoblescin' },
  // DevOps still performs the release; QA owns the sign-off because they run the
  // smoke tests. The PM gives the final confirmation at the end.
  { key: 'execute',        label: 'Production Deployment & Smoke Testing', who: 'QA' },
  { key: 'post_verify',    label: 'Post-Deployment Validation',         who: 'Project Manager' },
];
const DPL_VALIDATION_OUTCOMES = ['Completed', 'Rollback Required', 'Deployment Failed'];

const DeploymentStatusPill = ({ status }) => {
  const c = DPL_STATUS_COLORS[status] || { bg: '#f3f4f6', color: '#374151' };
  return <span className="px-2 py-0.5 text-xs font-semibold rounded whitespace-nowrap" style={{ background: c.bg, color: c.color }}>{status || '—'}</span>;
};

// v2 staged-workflow constants
const DPL_CHANGE_TYPES = ['Normal', 'Emergency', 'Standard'];
const DPL_ATTACHMENT_CATEGORIES = [
  { key: 'stakeholder_signoff', label: 'Stakeholder sign-off' },
  { key: 'project_documentation', label: 'Project documentation' },
  { key: 'release_notes', label: 'Release notes' },
  { key: 'deployment_plan', label: 'Deployment plan' },
  { key: 'rollback_plan', label: 'Rollback plan' },
  { key: 'tcms_results', label: 'TCMS results' },
  { key: 'regression_results', label: 'Regression results' },
  { key: 'uat_results', label: 'UAT results' },
  { key: 'security_scan', label: 'Security scan' },
  { key: 'compliance_check', label: 'Compliance check' },
  { key: 'deployment_package', label: 'Deployment package' },
  { key: 'db_migration', label: 'DB migration' },
  { key: 'monitoring_config', label: 'Monitoring config' },
  { key: 'cab_minutes', label: 'CAB minutes' },
  { key: 'deployment_log', label: 'Deployment log' },
  { key: 'prod_verification_evidence', label: 'Prod verification evidence' },
  { key: 'pir_document', label: 'PIR document' },
  { key: 'other', label: 'Other' },
];
const dplCategoryLabel = (key) => DPL_ATTACHMENT_CATEGORIES.find(c => c.key === key)?.label || key;
const DPL_STAGE_STATUS_COLORS = {
  pending:     { bg: '#f3f4f6', color: '#6b7280', border: '#d1d5db', icon: '·' },
  in_progress: { bg: '#dbeafe', color: '#1e40af', border: '#93c5fd', icon: '●' },
  complete:    { bg: '#d1fae5', color: '#065f46', border: '#6ee7b7', icon: '✓' },
  rejected:    { bg: '#fee2e2', color: '#991b1b', border: '#fca5a5', icon: '✗' },
  returned:    { bg: '#fef3c7', color: '#92400e', border: '#fcd34d', icon: '↺' },
  // Stage retired from the chain after this deployment was submitted. Kept visible so
  // the history reads correctly; it can never become the active stage again.
  skipped:     { bg: '#f1f5f9', color: '#94a3b8', border: '#e2e8f0', icon: '–' },
};

// Required attachment categories per stage — mirrors backend stage templates.
// v1.9 — every stage's attachments are optional now; nothing here gates stage completion,
// but the map stays (all empty) since other code keys off its shape.
const DPL_REQUIRED_ATTACHMENTS_PER_STAGE = {
  pm_verify:        [],
  qa_verify:        [],
  security_review:  [],
  devops_review:    [],
  cab_review:       [],
  final_approval:   [],
  execute:          [],
  post_verify:      [],
  pir:              [],
};

// Read a File from <input type=file> into base64 for the API
const dplReadFileAsBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => {
    const result = reader.result || '';
    const base64 = String(result).split(',')[1] || '';
    resolve({ fileName: file.name, fileType: file.type, fileSize: file.size, fileData: base64 });
  };
  reader.onerror = () => reject(reader.error || new Error('Failed to read file'));
  reader.readAsDataURL(file);
});

// Format an SLA countdown to a short human label: "due in 3h", "due in 2d 4h", or
// "overdue by 6h" when breached. Returns null if no SLA data available.
const dplFormatSla = (msUntilDue, breached) => {
  if (msUntilDue == null) return null;
  const abs = Math.abs(msUntilDue);
  const h = Math.floor(abs / 3600000);
  const m = Math.floor((abs % 3600000) / 60000);
  const d = Math.floor(h / 24);
  const remH = h - d * 24;
  const txt = d > 0 ? `${d}d ${remH}h` : (h > 0 ? `${h}h ${m}m` : `${m}m`);
  return breached ? `Overdue by ${txt}` : `Due in ${txt}`;
};

// Visual horizontal stepper of all stages with their current status.
const DeploymentStageStepper = ({ stages, currentStageId }) => {
  if (!stages || stages.length === 0) return null;
  return (
    <div className="overflow-x-auto bg-white rounded-lg border border-gray-200 p-3 mb-4">
      <div className="flex items-center gap-1 min-w-max">
        {stages.map((s, i) => {
          const c = DPL_STAGE_STATUS_COLORS[s.status] || DPL_STAGE_STATUS_COLORS.pending;
          const isCurrent = s.id === currentStageId;
          const slaLabel = dplFormatSla(s.slaMsUntilDue, s.slaBreached);
          return (
            <React.Fragment key={s.id}>
              <div className={`flex flex-col items-center px-3 py-2 rounded ${isCurrent ? 'ring-2 ring-blue-400' : ''} ${s.slaBreached ? 'ring-2 ring-rose-500' : ''}`} style={{ background: c.bg, borderColor: c.border }}>
                <div className="flex items-center gap-1.5">
                  <span className="text-base font-bold" style={{ color: c.color }}>{c.icon}</span>
                  <span className="text-xs font-semibold whitespace-nowrap" style={{ color: c.color }}>{s.stageLabel}</span>
                  {s.slaBreached && <span className="text-[9px] font-bold px-1 rounded bg-rose-600 text-white" title={`SLA: ${s.slaHours || '?'}h`}>SLA!</span>}
                </div>
                <div className="text-[10px] text-gray-500 mt-0.5 whitespace-nowrap">{s.assignedToUserName || '—'}</div>
                {slaLabel && s.status === 'in_progress' && (
                  <div className={`text-[9px] mt-0.5 whitespace-nowrap ${s.slaBreached ? 'text-rose-600 font-semibold' : 'text-gray-500'}`}>{slaLabel}</div>
                )}
                {s.decidedAt && <div className="text-[9px] text-gray-400">{new Date(s.decidedAt.replace ? s.decidedAt.replace(' ', 'T') + 'Z' : s.decidedAt).toLocaleDateString()}</div>}
              </div>
              {i < stages.length - 1 && <div className="w-4 h-px bg-gray-300 shrink-0" />}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

// One row of a stage's checklist — controlled checkbox with auto-stamping
const DPLChecklistItem = ({ item, locked, onToggle }) => {
  const [busy, setBusy] = React.useState(false);
  const toggle = async () => {
    if (locked || busy) return;
    setBusy(true);
    try { await onToggle(item.id, !item.checked); }
    catch (e) { alert(`Could not toggle: ${e.message}`); }
    setBusy(false);
  };
  return (
    <li className={`flex items-start gap-2 py-1.5 px-2 rounded ${locked ? 'opacity-70' : 'hover:bg-gray-50 cursor-pointer'}`} onClick={toggle}>
      <input
        type="checkbox"
        checked={!!item.checked}
        readOnly
        disabled={locked || busy}
        className={`mt-0.5 ${item.isRequired ? 'accent-rose-600' : 'accent-blue-600'}`}
      />
      <div className="flex-1 text-sm">
        <span className={item.checked ? 'text-gray-500 line-through' : 'text-gray-900'}>{item.itemText}</span>
        {item.isRequired === 1 && <span className="text-rose-600 ml-1" title="Required">*</span>}
        {item.checked && item.checkedBy && (
          <div className="text-[10px] text-gray-400 mt-0.5">{item.checkedBy} · {item.checkedAt ? new Date(item.checkedAt.replace ? item.checkedAt.replace(' ', 'T') + 'Z' : item.checkedAt).toLocaleString() : ''}</div>
        )}
      </div>
    </li>
  );
};

// Multi-file uploader for a specific stage + category. Required categories show a badge.
const DPLAttachmentUploader = ({ stageId, category, deploymentId, locked, onUploaded }) => {
  const [busy, setBusy] = React.useState(false);
  const onPick = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    setBusy(true);
    for (const f of files) {
      if (f.size > 5 * 1024 * 1024) { alert(`"${f.name}" exceeds 5 MB`); continue; }
      try {
        const payload = await dplReadFileAsBase64(f);
        const res = await ModulesModel.addDeploymentAttachment(deploymentId, { ...payload, category, stageId });
        if (res?.success === false) { alert(`Upload failed for ${f.name}: ${res.error || ''}`); break; }
      } catch (err) { alert(err?.message || 'Read failed'); }
    }
    setBusy(false);
    onUploaded?.();
  };
  return (
    <input type="file" multiple disabled={locked || busy} onChange={onPick} className="text-xs" />
  );
};

// General-purpose deployment-level attachment uploader. Used on the "Attachments" tab
// for documents, emails, screenshots, anything not tied to a specific stage's required
// category. stageId is null; category is user-picked (defaults to 'other'). 5 MB per file.
const DPLGeneralAttachmentUploader = ({ deploymentId, onUploaded }) => {
  const [category, setCategory] = React.useState('other');
  const [busy, setBusy] = React.useState(false);
  const [progress, setProgress] = React.useState('');
  const fileInputRef = React.useRef(null);
  const onPick = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    setBusy(true);
    let done = 0;
    for (const f of files) {
      if (f.size > 5 * 1024 * 1024) { alert(`"${f.name}" exceeds 5 MB`); continue; }
      setProgress(`Uploading ${f.name} (${++done}/${files.length})…`);
      try {
        const payload = await dplReadFileAsBase64(f);
        const res = await ModulesModel.addDeploymentAttachment(deploymentId, { ...payload, category, stageId: null });
        if (res?.success === false) { alert(`Upload failed for ${f.name}: ${res.error || ''}`); break; }
      } catch (err) { alert(err?.message || 'Read failed'); }
    }
    setBusy(false);
    setProgress('');
    onUploaded?.();
  };
  return (
    <div className="bg-blue-50/40 border border-blue-200 rounded-lg p-3 mb-2">
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[200px]">
          <label className="block text-[10px] uppercase font-semibold text-gray-500 mb-1">Add an attachment</label>
          <div className="text-xs text-gray-600 mb-2">Documents, emails (.eml/.msg), screenshots, PDFs — anything supporting this deployment. Max 5 MB per file.</div>
        </div>
        <div>
          <label className="block text-[10px] uppercase font-semibold text-gray-500 mb-1">Category</label>
          <select
            value={category}
            onChange={e => setCategory(e.target.value)}
            disabled={busy}
            className="border border-gray-300 rounded px-2 py-1.5 text-sm bg-white"
          >
            {DPL_ATTACHMENT_CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[10px] uppercase font-semibold text-gray-500 mb-1">File(s)</label>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            disabled={busy}
            onChange={onPick}
            className="text-xs"
          />
        </div>
      </div>
      {busy && <div className="text-xs text-blue-700 font-medium mt-2">{progress || 'Uploading…'}</div>}
    </div>
  );
};

// Modal: return-to-previous-stage with mandatory reason + target picker
const DPLReturnModal = ({ stages, currentStage, onClose, onConfirm }) => {
  const earlier = stages.filter(s => s.sequenceOrder < currentStage.sequenceOrder);
  const [targetKey, setTargetKey] = React.useState(earlier[earlier.length - 1]?.stageKey || '');
  const [reason, setReason] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const submit = async () => {
    if (!targetKey || !reason.trim()) { alert('Pick a target stage and provide a reason.'); return; }
    setBusy(true);
    await onConfirm(targetKey, reason.trim());
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-lg font-semibold">Return to Previous Stage</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-3">
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Return to stage</label>
            <select value={targetKey} onChange={e => setTargetKey(e.target.value)} className="w-full border border-gray-300 rounded px-3 py-2 text-sm">
              {earlier.map(s => <option key={s.id} value={s.stageKey}>{s.stageLabel}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Reason <span className="text-rose-600">*</span></label>
            <textarea rows={4} value={reason} onChange={e => setReason(e.target.value)} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" placeholder="Why return to this stage? The original approver will be notified." />
          </div>
          <p className="text-xs text-gray-500">Checklist items and attachments on intermediate stages are preserved. The target stage's approver can amend without redoing all their work.</p>
        </div>
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600">Cancel</button>
          <button onClick={submit} disabled={busy} className="px-4 py-2 bg-amber-600 text-white text-sm rounded-lg hover:bg-amber-700 disabled:opacity-50">{busy ? 'Returning…' : 'Confirm Return'}</button>
        </div>
      </div>
    </div>
  );
};

const DPLRequestInfoModal = ({ onClose, onConfirm }) => {
  const [question, setQuestion] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const submit = async () => {
    if (!question.trim()) return;
    setBusy(true);
    await onConfirm(question.trim());
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-lg font-semibold">Request More Info</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
        </div>
        <div className="p-6">
          <label className="block text-xs font-semibold text-gray-700 mb-1">What do you need from the requester?</label>
          <textarea rows={5} value={question} onChange={e => setQuestion(e.target.value)} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" placeholder="e.g. Please attach the load test results before I can approve." autoFocus />
          <p className="text-xs text-gray-500 mt-2">Status will change to <strong>Info Needed</strong>. Requester will be notified and can respond + add files. When ready, they click "Resume" to return the stage to your queue.</p>
        </div>
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600">Cancel</button>
          <button onClick={submit} disabled={busy || !question.trim()} className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50">{busy ? 'Sending…' : 'Send Question'}</button>
        </div>
      </div>
    </div>
  );
};

const DeploymentCreateModal = ({ onClose, onSave, lookups, initial }) => {
  const isEdit = !!initial;
  const [form, setForm] = React.useState({
    projectApplication: initial?.projectApplication || '',
    projectName: initial?.projectName || '',
    projectId: initial?.projectId || '',
    description: initial?.description || '',
    linkedCrId: initial?.linkedCrId ? String(initial.linkedCrId) : '',
    linkedTestPlanId: initial?.linkedTestPlanId ? String(initial.linkedTestPlanId) : '',
    plannedDeploymentDate: initial?.plannedDeploymentDate || '',
    changeType: initial?.changeType || 'Normal',
    // 'project' = pick an existing PlanTrack project; 'manual' = nominate a free-text
    // title for a hotfix or other work with no project behind it.
    entryMode: initial?.entryMode || 'project',
  });
  // CRFs staged before the deployment exists. One may be flagged 'original'; the rest
  // are addenda. Persisted on save (create) or immediately (edit).
  const [stagedCrLinks, setStagedCrLinks] = React.useState([]);
  const [crPickerId, setCrPickerId] = React.useState('');
  const existingCrLinks = initial?.crLinks || [];
  const linkedCrIdSet = React.useMemo(
    () => new Set([...existingCrLinks.map(l => String(l.crId)), ...stagedCrLinks.map(l => String(l.crId))]),
    [existingCrLinks, stagedCrLinks]
  );
  const addStagedCr = (crId) => {
    if (!crId || linkedCrIdSet.has(String(crId))) return;
    const cr = (lookups?.crs || []).find(c => String(c.id) === String(crId));
    if (!cr) return;
    // First CRF on the deployment is the original; anything after it is an addendum.
    const isFirst = existingCrLinks.length === 0 && stagedCrLinks.length === 0;
    setStagedCrLinks(l => [...l, { crId: cr.id, crNumber: cr.crNumber, title: cr.title, status: cr.status, linkType: isFirst ? 'original' : 'addendum' }]);
    setCrPickerId('');
  };
  const setStagedCrType = (crId, linkType) => setStagedCrLinks(l => l.map(x => {
    if (String(x.crId) !== String(crId)) return linkType === 'original' ? { ...x, linkType: 'addendum' } : x;
    return { ...x, linkType };
  }));
  // Pick a PlanTrack project — sets both projectId AND projectName from the lookup.
  // Also clears any previously-linked TCMS test plan, since test plans are project-scoped
  // and the old selection almost certainly belongs to a different project now.
  const onPickProject = (id) => {
    const p = (lookups?.projects || []).find(x => String(x.id) === String(id));
    setForm(s => ({ ...s, projectId: id, projectName: p?.name || '', linkedTestPlanId: '' }));
  };
  // Switching modes clears the other mode's inputs so a stale project can't ride along
  // on a manual deployment (or vice versa).
  const onSwitchMode = (mode) => setForm(s => (
    mode === 'manual'
      ? { ...s, entryMode: 'manual', projectId: '', projectName: '', linkedTestPlanId: '' }
      : { ...s, entryMode: 'project', projectName: '' }
  ));
  // TCMS test case linkage — multi-select, mirrors the CRF pattern. `stagedTcLinks` holds
  // newly-selected cases that haven't hit the backend yet; they're submitted right after
  // the deployment is created/updated. Already-linked cases (edit mode) are read from
  // initial.testLinks and shown alongside.
  const [stagedTcLinks, setStagedTcLinks] = React.useState([]);
  const [showLinkModal, setShowLinkModal] = React.useState(false);
  const existingLinkIds = React.useMemo(() => (initial?.testLinks || []).map(l => l.testCaseId), [initial]);
  const stagedLinkIds = React.useMemo(() => stagedTcLinks.map(c => c.id), [stagedTcLinks]);
  const allLinkedIds = React.useMemo(() => [...existingLinkIds, ...stagedLinkIds], [existingLinkIds, stagedLinkIds]);
  // QMS requirement linkage — mirrors the TCMS staging pattern directly above.
  const [stagedReqLinks, setStagedReqLinks] = React.useState([]);
  const [showReqLinkModal, setShowReqLinkModal] = React.useState(false);
  const existingReqLinkIds = React.useMemo(() => (initial?.requirementLinks || []).map(l => l.requirementId), [initial]);
  const stagedReqLinkIds = React.useMemo(() => stagedReqLinks.map(r => r.id), [stagedReqLinks]);
  const allReqLinkedIds = React.useMemo(() => [...existingReqLinkIds, ...stagedReqLinkIds], [existingReqLinkIds, stagedReqLinkIds]);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState('');
  // v1.6 — advisory conflict check. Reruns when plannedDate / application / changeType
  // change. Debounced 350ms so we don't spam the API on every keystroke.
  const [conflictCheck, setConflictCheck] = React.useState({ freezeWindows: [], conflictingDeployments: [] });
  React.useEffect(() => {
    if (!form.plannedDeploymentDate) {
      setConflictCheck({ freezeWindows: [], conflictingDeployments: [] });
      return;
    }
    const t = setTimeout(async () => {
      const r = await ModulesModel.checkDeploymentConflicts({
        plannedDate: form.plannedDeploymentDate,
        application: form.projectApplication,
        changeType: form.changeType,
        excludeId: initial?.id || 0,
      });
      setConflictCheck(r || { freezeWindows: [], conflictingDeployments: [] });
    }, 350);
    return () => clearTimeout(t);
  }, [form.plannedDeploymentDate, form.projectApplication, form.changeType, initial?.id]);

  const submit = async (e, withSubmit) => {
    e.preventDefault();
    if (form.entryMode === 'manual') {
      if (!form.projectName.trim()) { setError('Deployment Title is required'); return; }
    } else {
      if (!form.projectId) { setError('Select a PlanTrack project, or switch to a manual deployment title'); return; }
      if (!form.projectName.trim()) { setError('Project Name is required'); return; }
    }
    if (!form.projectApplication.trim()) { setError('Project Application is required'); return; }
    setSaving(true);
    try {
      let res;
      if (isEdit) {
        res = await ModulesModel.updateDeployment(initial.id, form);
      } else {
        res = await ModulesModel.createDeployment({
          ...form,
          submit: !!withSubmit,
          crLinks: stagedCrLinks.map(l => ({ crId: l.crId, linkType: l.linkType })),
        });
      }
      if (res?.success === false) { setError(res.error || 'Failed'); setSaving(false); return; }
      if (res?.warning) { alert(`Saved as Draft — could not start approval chain: ${res.warning}`); }
      // Post any staged TCMS test case links now that we have a deployment id
      const dplId = isEdit ? initial.id : res?.id;
      // On edit the deployment already exists, so newly staged CRFs are posted here.
      if (dplId && isEdit && stagedCrLinks.length > 0) {
        const cr = await ModulesModel.addDeploymentCrLinks(dplId, stagedCrLinks.map(l => ({ crId: l.crId, linkType: l.linkType })));
        if (cr?.success === false) alert(`CRF linking failed: ${cr.error || 'unknown error'}`);
      }
      if (dplId && stagedTcLinks.length > 0) {
        const lr = await ModulesModel.addDeploymentTestLinks(dplId, stagedTcLinks.map(c => c.id));
        if (lr?.success === false) alert(`Test case linking failed: ${lr.error || 'unknown error'}`);
      }
      if (dplId && stagedReqLinks.length > 0) {
        const rr = await ModulesModel.addDeploymentRequirementLinks(dplId, stagedReqLinks.map(r => r.id));
        if (rr?.success === false) alert(`Requirement linking failed: ${rr.error || 'unknown error'}`);
      }
      onSave(dplId);
    } catch (err) {
      setError(err?.message || 'Failed to save');
    }
    setSaving(false);
  };
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <form className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-xl font-semibold">{isEdit ? `Edit Deployment · ${initial.deploymentCode || ''}` : 'New Deployment'}</h2>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <div className="p-6 space-y-4">
          {/* Two ways in: an existing PlanTrack project, or a nominated title for a
              hotfix / one-off with no project behind it. */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Deployment for <span className="text-red-500">*</span></label>
            <div className="flex gap-2 mb-2">
              {[
                { key: 'project', label: 'Existing project', hint: 'Link to a PlanTrack project' },
                { key: 'manual', label: 'Manual / Hotfix', hint: 'Nominate a title instead' },
              ].map(m => (
                <button
                  key={m.key}
                  type="button"
                  onClick={() => onSwitchMode(m.key)}
                  title={m.hint}
                  className={`flex-1 px-3 py-2 text-sm rounded-lg border text-left transition-colors ${
                    form.entryMode === m.key
                      ? 'border-blue-500 bg-blue-50 text-blue-800 font-semibold'
                      : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <div>{m.label}</div>
                  <div className="text-[11px] font-normal opacity-70">{m.hint}</div>
                </button>
              ))}
            </div>

            {form.entryMode === 'project' ? (
              <>
                <select value={form.projectId} onChange={e => onPickProject(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500">
                  <option value="">— Select a PlanTrack project —</option>
                  {(lookups?.projects || []).map(p => (
                    <option key={p.id} value={p.id}>
                      {p.id} — {p.name}{p.status ? ` (${p.status})` : ''}{(p.isArchived === 1 || p.isArchived === true) ? ' · ARCHIVED' : ''}
                    </option>
                  ))}
                </select>
                {form.projectId && form.projectName && (() => {
                  const picked = (lookups?.projects || []).find(x => String(x.id) === String(form.projectId));
                  const archived = picked && (picked.isArchived === 1 || picked.isArchived === true);
                  return (
                    <div className="text-xs text-gray-500 mt-1">
                      Linked to <strong className="text-gray-700">{form.projectName}</strong> ({form.projectId})
                      {archived && <span className="ml-2 px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-semibold">archived project</span>}
                    </div>
                  );
                })()}
                {(lookups?.projects || []).length === 0 && (
                  <div className="text-xs text-amber-600 mt-1">No PlanTrack projects available. Create one in PlanTrack, or switch to Manual / Hotfix.</div>
                )}
              </>
            ) : (
              <>
                <input
                  type="text"
                  value={form.projectName}
                  onChange={e => setForm(s => ({ ...s, projectName: e.target.value }))}
                  placeholder="e.g. Hotfix — settlement rounding correction"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                />
                <div className="text-xs text-gray-500 mt-1">No PlanTrack project is linked. This deployment is tracked under the title above.</div>
              </>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Project Application <span className="text-red-500">*</span></label>
              <select value={form.projectApplication} onChange={e => setForm(s => ({ ...s, projectApplication: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500">
                <option value="">Select…</option>
                {['CoreApp', 'Pipedream Workflows', 'Forth', 'Snowflake', 'Power BI', 'AWS', 'Cloudflare', 'Other'].map(a => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Change Type</label>
              <select value={form.changeType} onChange={e => setForm(s => ({ ...s, changeType: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500">
                {DPL_CHANGE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
              <div className="text-xs text-gray-500 mt-1">
                {form.changeType === 'Emergency' ? 'Fast-track: CAB stage is skipped.' :
                 form.changeType === 'Standard' ? 'Pre-approved type: CAB stage is skipped.' :
                 'Full 9-stage workflow (PM → QA → Security → DevOps → CAB → Final → Execute → PostVerify → PIR).'}
              </div>
            </div>
          </div>
          {/* Multiple CRFs: one original plus any addenda raised against it. */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Linked CRFs <span className="text-gray-400 font-normal text-xs">(original + any addendum)</span>
            </label>
            <select
              value={crPickerId}
              onChange={e => addStagedCr(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
            >
              <option value="">— Add a CRF —</option>
              {(lookups?.crs || []).filter(c => !linkedCrIdSet.has(String(c.id))).map(c => (
                <option key={c.id} value={c.id}>{c.crNumber} — {c.title} ({c.status})</option>
              ))}
            </select>

            {(existingCrLinks.length > 0 || stagedCrLinks.length > 0) && (
              <div className="mt-2 space-y-1">
                {existingCrLinks.map(l => (
                  <div key={`e-${l.id}`} className="flex items-center gap-2 text-xs bg-gray-50 border border-gray-200 rounded px-2 py-1.5">
                    <span className={`px-1.5 py-0.5 rounded font-semibold ${l.linkType === 'original' ? 'bg-blue-100 text-blue-800' : 'bg-violet-100 text-violet-800'}`}>
                      {l.linkType === 'original' ? 'ORIGINAL' : 'ADDENDUM'}
                    </span>
                    <span className="font-mono text-gray-600">{l.crNumber}</span>
                    <span className="text-gray-700 truncate flex-1">{l.title}</span>
                    <span className="text-gray-400">{l.status}</span>
                    <span className="text-[10px] text-gray-400 italic">saved</span>
                  </div>
                ))}
                {stagedCrLinks.map(l => (
                  <div key={`s-${l.crId}`} className="flex items-center gap-2 text-xs bg-blue-50/50 border border-blue-200 rounded px-2 py-1.5">
                    <select
                      value={l.linkType}
                      onChange={e => setStagedCrType(l.crId, e.target.value)}
                      className="border border-blue-200 rounded px-1 py-0.5 text-[10px] font-semibold bg-white"
                    >
                      <option value="original">ORIGINAL</option>
                      <option value="addendum">ADDENDUM</option>
                    </select>
                    <span className="font-mono text-gray-600">{l.crNumber}</span>
                    <span className="text-gray-700 truncate flex-1">{l.title}</span>
                    <button
                      type="button"
                      onClick={() => setStagedCrLinks(x => x.filter(y => String(y.crId) !== String(l.crId)))}
                      className="text-rose-500 hover:text-rose-700 font-bold px-1"
                      title="Remove"
                    >×</button>
                  </div>
                ))}
              </div>
            )}
            {existingCrLinks.length === 0 && stagedCrLinks.length === 0 && (
              <div className="text-xs text-gray-400 mt-1">No CRFs linked. Optional — leave empty for deployments not driven by a change request.</div>
            )}
          </div>
          {/* Linked TCMS test cases — multi-select, mirrors the CRF "Proof of Testing" pattern.
              Cases are staged until the deployment is saved, then submitted in one batch. */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-sm font-medium text-gray-700">Linked TCMS test cases <span className="text-gray-400 font-normal text-xs">(test evidence)</span></label>
              <button
                type="button"
                onClick={() => setShowLinkModal(true)}
                className="text-xs font-semibold text-blue-600 hover:text-blue-700"
              >🔗 Link test cases</button>
            </div>
            {/* Existing links (edit mode) — read from initial.testLinks */}
            {isEdit && (initial.testLinks || []).map(l => {
              const meta = CR_TC_STATUS_META[l.tcStatus || 'pending'] || CR_TC_STATUS_META.pending;
              return (
                <div key={`existing-tc-${l.id}`} className="flex items-center gap-2 text-xs text-slate-600 px-2 py-1 bg-slate-50 rounded mb-1 border border-slate-100">
                  <span className="font-mono text-slate-500 w-16 flex-shrink-0">{l.tcCode || `#${l.testCaseId}`}</span>
                  <span className="flex-1 truncate">🔗 {l.tcTitle || 'TCMS test case'}</span>
                  <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold" style={{ color: meta.color, backgroundColor: meta.bg, border: `1px solid ${meta.color}44` }}>{meta.icon} {meta.label}</span>
                  <span className="text-slate-400 text-[10px]">already linked</span>
                </div>
              );
            })}
            {/* Staged links (newly selected in this session) */}
            {stagedTcLinks.map(c => {
              const meta = CR_TC_STATUS_META[c.status || 'pending'] || CR_TC_STATUS_META.pending;
              return (
                <div key={`staged-tc-${c.id}`} className="flex items-center gap-2 text-xs text-slate-700 px-2 py-1 bg-indigo-50 border border-indigo-100 rounded mb-1">
                  <span className="font-mono text-slate-500 w-16 flex-shrink-0">{c.tcCode || `#${c.id}`}</span>
                  <span className="flex-1 truncate">🔗 {c.title || 'TCMS test case'}</span>
                  <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold" style={{ color: meta.color, backgroundColor: meta.bg, border: `1px solid ${meta.color}44` }}>{meta.icon} {meta.label}</span>
                  <button
                    type="button"
                    onClick={() => setStagedTcLinks(s => s.filter(x => x.id !== c.id))}
                    className="text-rose-500 hover:text-rose-700"
                  >×</button>
                </div>
              );
            })}
            {(!isEdit || !(initial?.testLinks || []).length) && stagedTcLinks.length === 0 && (
              <div className="text-xs text-gray-400 italic px-2 py-1">No test cases linked yet — click "Link test cases" to choose evidence from TCMS.</div>
            )}
          </div>
          {/* Linked QMS requirements — mirrors the Linked TCMS test cases block above. */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-sm font-medium text-gray-700">Linked QMS requirements <span className="text-gray-400 font-normal text-xs">(optional)</span></label>
              <button
                type="button"
                onClick={() => setShowReqLinkModal(true)}
                className="text-xs font-semibold text-blue-600 hover:text-blue-700"
              >🔗 Link requirements</button>
            </div>
            {/* Existing links (edit mode) — read from initial.requirementLinks */}
            {isEdit && (initial.requirementLinks || []).map(l => {
              const meta = DPL_REQ_STATUS_META[l.reqStatus] || DPL_REQ_STATUS_DEFAULT;
              return (
                <div key={`existing-req-${l.id}`} className="flex items-center gap-2 text-xs text-slate-600 px-2 py-1 bg-slate-50 rounded mb-1 border border-slate-100">
                  <span className="font-mono text-slate-500 w-16 flex-shrink-0">{l.reqCode || `#${l.requirementId}`}</span>
                  <span className="flex-1 truncate">🔗 {l.reqTitle || 'QMS requirement'}</span>
                  <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold" style={{ color: meta.color, backgroundColor: meta.bg, border: `1px solid ${meta.color}44` }}>{meta.icon} {meta.label}</span>
                  <span className="text-slate-400 text-[10px]">already linked</span>
                </div>
              );
            })}
            {/* Staged links (newly selected in this session) */}
            {stagedReqLinks.map(r => {
              const meta = DPL_REQ_STATUS_META[r.status] || DPL_REQ_STATUS_DEFAULT;
              return (
                <div key={`staged-req-${r.id}`} className="flex items-center gap-2 text-xs text-slate-700 px-2 py-1 bg-indigo-50 border border-indigo-100 rounded mb-1">
                  <span className="font-mono text-slate-500 w-16 flex-shrink-0">{r.reqCode || `#${r.id}`}</span>
                  <span className="flex-1 truncate">🔗 {r.title || 'QMS requirement'}</span>
                  <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold" style={{ color: meta.color, backgroundColor: meta.bg, border: `1px solid ${meta.color}44` }}>{meta.icon} {meta.label}</span>
                  <button
                    type="button"
                    onClick={() => setStagedReqLinks(s => s.filter(x => x.id !== r.id))}
                    className="text-rose-500 hover:text-rose-700"
                  >×</button>
                </div>
              );
            })}
            {(!isEdit || !(initial?.requirementLinks || []).length) && stagedReqLinks.length === 0 && (
              <div className="text-xs text-gray-400 italic px-2 py-1">No requirements linked yet — click "Link requirements" to choose from QMS.</div>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Planned Deployment Date</label>
            <input type="date" value={form.plannedDeploymentDate} onChange={e => setForm(s => ({ ...s, plannedDeploymentDate: e.target.value }))} className="w-full md:w-1/2 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description / Scope</label>
            <MiniWysiwyg value={form.description} onChange={v => setForm(s => ({ ...s, description: v }))} minHeight="100px" placeholder="What's being deployed, why, rollback plan…" />
          </div>
          {/* v1.6 — surface freeze windows + same-day same-app conflicts BEFORE the user submits */}
          {(conflictCheck.freezeWindows || []).length > 0 && (
            <div className="bg-rose-50 border border-rose-300 rounded-lg p-3 text-sm">
              <div className="font-semibold text-rose-800 mb-1">🚫 Change freeze in effect on this date</div>
              <ul className="space-y-1 text-rose-900">
                {conflictCheck.freezeWindows.map(w => (
                  <li key={w.id}>
                    <strong>{w.name}</strong> ({w.startDate} → {w.endDate}){w.reason ? ` — ${w.reason}` : ''}
                    {!w.allowAdminOverride && <span className="ml-1 text-xs">(no override allowed)</span>}
                  </li>
                ))}
              </ul>
              <div className="text-xs text-rose-700 mt-2">Submission will be blocked. Pick a different date, or contact an admin if your change qualifies for an exception.</div>
            </div>
          )}
          {(conflictCheck.conflictingDeployments || []).length > 0 && (
            <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 text-sm">
              <div className="font-semibold text-amber-800 mb-1">⚠ Other deployments share this window</div>
              <ul className="space-y-1 text-amber-900">
                {conflictCheck.conflictingDeployments.map(c => (
                  <li key={c.id}>
                    <strong className="font-mono text-xs">{c.deploymentCode}</strong> — {c.projectName} ({c.status})
                    {c.requestedBy && <span className="text-xs text-amber-700 ml-1">· requested by {c.requestedBy}</span>}
                  </li>
                ))}
              </ul>
              <div className="text-xs text-amber-700 mt-2">Same application, same day. Coordinate with the other requesters before submitting — concurrent deployments often surface conflicts.</div>
            </div>
          )}
          {error && <p className="text-red-600 text-xs font-medium">{error}</p>}
        </div>
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
          {isEdit ? (
            <button type="button" onClick={(e) => submit(e, false)} disabled={saving} className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50">{saving ? 'Saving…' : 'Save Changes'}</button>
          ) : (
            <>
              <button type="button" onClick={(e) => submit(e, false)} disabled={saving} className="px-4 py-2 text-sm text-blue-700 border border-blue-300 rounded-lg hover:bg-blue-50 disabled:opacity-50">{saving ? 'Saving…' : 'Save as Draft'}</button>
              <button type="button" onClick={(e) => submit(e, true)} disabled={saving} className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50">{saving ? 'Submitting…' : 'Save & Submit for Approval'}</button>
            </>
          )}
        </div>
      </form>
      {showLinkModal && (
        <ChangeRequestTcmsLinkModal
          crId={null}
          alreadyLinkedIds={allLinkedIds}
          onClose={() => setShowLinkModal(false)}
          onSave={async (cases) => {
            // Pre-save: stage them in local state instead of hitting the backend.
            setStagedTcLinks(s => {
              const seen = new Set(s.map(x => x.id));
              return [...s, ...cases.filter(c => !seen.has(c.id))];
            });
            return { success: true };
          }}
        />
      )}
      {showReqLinkModal && (
        <DeploymentRequirementLinkModal
          alreadyLinkedIds={allReqLinkedIds}
          onClose={() => setShowReqLinkModal(false)}
          onSave={async (reqs) => {
            // Pre-save: stage them in local state instead of hitting the backend.
            setStagedReqLinks(s => {
              const seen = new Set(s.map(x => x.id));
              return [...s, ...reqs.filter(r => !seen.has(r.id))];
            });
            return { success: true };
          }}
        />
      )}
    </div>
  );
};

// v1.8 — risk level pill for a deployment. Color + label reflect the score bucket.
const DPL_RISK_META = {
  low:    { color: '#065f46', bg: '#d1fae5', border: '#6ee7b7', icon: '●', label: 'Low risk' },
  medium: { color: '#92400e', bg: '#fef3c7', border: '#fcd34d', icon: '▲', label: 'Medium risk' },
  high:   { color: '#991b1b', bg: '#fee2e2', border: '#fca5a5', icon: '⚠', label: 'High risk' },
};

const DeploymentRiskBadge = ({ level, score, factors }) => {
  if (!level) return null;
  const meta = DPL_RISK_META[level] || DPL_RISK_META.low;
  const list = Array.isArray(factors) ? factors : [];
  return (
    <details className="relative inline-block group">
      <summary
        className="cursor-pointer list-none inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border"
        style={{ color: meta.color, background: meta.bg, borderColor: meta.border }}
        title="Click for risk breakdown"
      >
        <span className="text-sm">{meta.icon}</span>
        <span>{meta.label}</span>
        {score != null && <span className="opacity-70 font-mono">({score})</span>}
      </summary>
      <div className="absolute right-0 top-full mt-1 z-20 w-72 bg-white rounded-lg shadow-xl border border-gray-200 p-3">
        <div className="text-[10px] font-bold uppercase text-gray-500 mb-1.5">Risk factors</div>
        {list.length === 0 ? (
          <div className="text-xs text-gray-500 italic">No specific risk factors — score = 0.</div>
        ) : (
          <ul className="space-y-1 text-xs">
            {list.map((f, i) => (
              <li key={i} className="flex items-center gap-2">
                <span className="flex-1 text-gray-700">{f.label}</span>
                <span className="font-mono font-semibold text-gray-900">+{f.points}</span>
              </li>
            ))}
          </ul>
        )}
        {level === 'high' && (
          <div className="mt-2 pt-2 border-t border-gray-100 text-[11px] text-rose-700">
            ⚠ Final Approval requires password re-verification for this deployment.
          </div>
        )}
      </div>
    </details>
  );
};

// v1.8 — MFA re-auth modal shown before completing a high-risk Final Approval. Captures
// the current user's password, verifies it, and hands the resulting nonce back to the
// caller so completeStage can validate it server-side.
const DPLMfaReauthModal = ({ deploymentId, deploymentCode, onCancel, onVerified }) => {
  const [password, setPassword] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState('');
  const submit = async (e) => {
    e?.preventDefault();
    if (!password) { setError('Enter your password'); return; }
    setBusy(true); setError('');
    const r = await ModulesModel.verifyDeploymentMfa(deploymentId, password);
    setBusy(false);
    if (r?.success === false || !r?.nonce) {
      setError(r?.error || 'Verification failed');
      return;
    }
    onVerified(r.nonce);
  };
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <form onSubmit={submit} className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="px-5 py-3 border-b flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-gray-900">⚠ Final Approval — password required</h3>
            <div className="text-xs text-gray-500">High-risk deployment · {deploymentCode || ''}</div>
          </div>
          <button type="button" onClick={onCancel} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-sm text-gray-700">
            This deployment is flagged <strong className="text-rose-700">high risk</strong>. Please re-enter your password to confirm you intend to approve this change.
          </p>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Your password</label>
            <input
              type="password"
              autoFocus
              value={password}
              onChange={e => setPassword(e.target.value)}
              disabled={busy}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
              placeholder="•••••••••"
            />
          </div>
          {error && <div className="text-xs text-rose-600 font-medium">{error}</div>}
          <p className="text-[11px] text-gray-500">Your password is never stored beyond the standard Orbit login hash. This verification issues a single-use approval token valid for 5 minutes.</p>
        </div>
        <div className="px-5 py-3 border-t flex justify-end gap-2">
          <button type="button" onClick={onCancel} className="px-3 py-1.5 text-sm text-gray-600">Cancel</button>
          <button type="submit" disabled={busy || !password} className="px-3 py-1.5 text-sm bg-rose-600 text-white rounded hover:bg-rose-700 disabled:opacity-50">
            {busy ? 'Verifying…' : 'Verify & Continue'}
          </button>
        </div>
      </form>
    </div>
  );
};

const DeploymentDetailView = ({ deploymentId, user, onBack, onRefresh }) => {
  const [d, setD] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [editing, setEditing] = React.useState(false);
  const [lookups, setLookups] = React.useState(null);
  const [activeTab, setActiveTab] = React.useState('stages');
  const [showReturnModal, setShowReturnModal] = React.useState(false);
  const [showRmiModal, setShowRmiModal] = React.useState(false);
  const [showTcmsLinkModal, setShowTcmsLinkModal] = React.useState(false);
  const [showReqLinkModal, setShowReqLinkModal] = React.useState(false);
  // v1.8 — MFA state must live here (before any early returns) so hook count is stable
  // across the loading→loaded transition. Previously placed lower in the function, which
  // triggered React error #310 ("rendered more hooks than during the previous render").
  const [pendingMfaStageId, setPendingMfaStageId] = React.useState(null);
  const [pendingMfaNotes, setPendingMfaNotes] = React.useState('');
  const userName = user?.user_name || user?.name || '';
  const userId = user?.user_id || user?.id;
  const role = user?.user_role || user?.role || '';
  const isAdmin = role === 'admin';
  const isManagerRole = role === 'manager' || (typeof role === 'string' && role.endsWith('_manager'));

  const load = React.useCallback(async () => {
    // `loading` still gets set on every call (checklist toggle, stage approve, attachment
    // upload/delete — everything on this page funnels through load()), but the full-page
    // "Loading…" placeholder below is gated on `!d` too, so it only ever appears on the
    // true first load. Without that gate, every one of those actions unmounted the whole
    // page down to a few lines of placeholder text and remounted it once data came back —
    // the DOM collapsing and reappearing is what reset the scroll position to the top on
    // every checklist click, approval, or attachment upload.
    setLoading(true);
    setD(await ModulesModel.getDeploymentDetail(deploymentId));
    setLoading(false);
  }, [deploymentId]);
  React.useEffect(() => { load(); }, [load]);

  const openEditor = async () => {
    if (!lookups) setLookups(await ModulesModel.getDeploymentLookups());
    setEditing(true);
  };

  if (loading && !d) return <div className="max-w-5xl mx-auto px-4 py-6 text-sm text-gray-500">Loading…</div>;
  if (!d) return <div className="max-w-5xl mx-auto px-4 py-6 text-sm text-gray-500">Not found. <button onClick={onBack} className="text-blue-600 underline">Back</button></div>;

  const isOwner = d.requestedBy === userName || d.requestedById === user?.user_id;
  const stages = d.stages || [];
  const hasStages = stages.length > 0;
  const currentStage = stages.find(s => s.status === 'in_progress');
  const isAssignedToCurrentStage = currentStage && currentStage.assignedToUserId === userId;
  // PM Approval — the requester IS the natural approver when they hold the PM role, so the
  // self-approval block is lifted as long as they're the assigned approver. Other stages
  // keep the strict block (requester cannot approve their own QA/Security/etc.).
  const isSelfApprovableStage = currentStage && ['pm_verify'].includes(currentStage.stageKey);
  // Approve buttons appear when (a) stage is in_progress, (b) viewer is admin or assigned,
  // and (c) viewer is not the requester — UNLESS the current stage is PM-owned, in which
  // case requester self-approval is allowed (they must still be the assigned approver).
  const canActOnCurrentStage = hasStages && currentStage && (
    isAdmin || (isAssignedToCurrentStage && (!isOwner || isSelfApprovableStage))
  );
  const canSubmit = isOwner && ['Draft', 'Rejected'].includes(d.status);
  const canProvideInfo = isOwner && d.status === 'Info Needed';
  // Legacy view fallback for pre-v2 deployments still using the simple approval-chain
  const approvals = d.approvals || [];
  const currentStep = approvals.find(a => a.decision === 'pending');
  const hasLegacyChain = !hasStages && approvals.length > 0;
  const canReview = !hasStages && d.status === 'Submitted' && !isOwner && (
    hasLegacyChain ? (isAdmin || (currentStep?.assignedToUserName === userName)) : ['admin','manager','devops_manager','dataops_manager'].includes(role)
  );
  // Edit permission — relaxed: creator, any module-access user ("editor"), manager, or admin
  // can edit while the deployment is in Draft/Rejected. Past that, only admins/managers can
  // amend out of band.
  const isEditableState = ['Draft', 'Rejected'].includes(d.status);
  const canEdit = (isEditableState) || isAdmin || isManagerRole;
  const canDeploy = ['admin','manager','devops_manager','dataops_manager','developer','dataops'].includes(role) && d.status === 'Approved';
  const canVerify = ['admin','manager','devops_manager','dataops_manager','qa','qa_lead'].includes(role) && d.status === 'Deployed';
  const canComplete = ['admin','manager','devops_manager','dataops_manager'].includes(role) && ['Verified', 'Deployed'].includes(d.status);
  // Mirrors the backend's actual policy in cancelDeployment: admin/manager can cancel
  // from any non-terminal status, but an owner loses that once the deployment is
  // Approved for Deployment or already Deployed — cancelling past that point needs an
  // elevated role. Previously this button showed as available for the owner at those
  // two statuses too, so clicking it always got rejected server-side.
  const canCancel = !['Completed', 'Cancelled'].includes(d.status) && (
    isAdmin || role === 'manager' ||
    (isOwner && !['Approved for Deployment', 'Deployed – Pending Validation'].includes(d.status))
  );
  const canDelete = isAdmin || (isOwner && ['Draft', 'Cancelled'].includes(d.status));
  // Surface why this was rejected right on the resubmit screen, so the requester can't
  // hit "Submit for Approval" again without seeing what needs to be addressed first.
  // Covers both the v2 staged workflow (history row, action 'stage_rejected') and the
  // legacy single-chain path (reviewNotes/reviewedBy/reviewedAt columns on the record).
  const latestRejection = d.status === 'Rejected'
    ? (d.history || []).find(h => h.action === 'stage_rejected')
      || (d.reviewNotes ? { notes: d.reviewNotes, actorName: d.reviewedBy, createdAt: d.reviewedAt } : null)
    : null;

  const handleSubmit = async () => {
    if (!confirm('Submit this deployment for approval?')) return;
    const r = await ModulesModel.submitDeployment(deploymentId);
    if (r?.success === false) { alert(r.error); return; }
    await load(); onRefresh?.();
  };
  const handleReview = async (decision) => {
    let notes = '';
    if (decision === 'Rejected') {
      const v = prompt('Reason for rejection (required):');
      if (v == null) return;
      if (!v.trim()) { alert('Rejection notes are required.'); return; }
      notes = v.trim();
    }
    const r = await ModulesModel.reviewDeployment(deploymentId, decision, notes);
    if (r?.success === false) { alert(r.error); return; }
    await load(); onRefresh?.();
  };
  const handleStageAction = async (kind) => {
    const labels = { deployed: 'Mark as Deployed', verified: 'Mark as Verified', completed: 'Mark as Completed' };
    const notes = prompt(`${labels[kind]} — optional notes:`) || '';
    if (notes === null) return;
    let r;
    if (kind === 'deployed') r = await ModulesModel.markDeploymentDeployed(deploymentId, notes);
    if (kind === 'verified') r = await ModulesModel.markDeploymentVerified(deploymentId, notes);
    if (kind === 'completed') r = await ModulesModel.completeDeployment(deploymentId, notes);
    if (r?.success === false) { alert(r.error); return; }
    await load(); onRefresh?.();
  };
  const handleCancel = async () => {
    if (!confirm('Cancel this deployment?')) return;
    const r = await ModulesModel.cancelDeployment(deploymentId);
    if (r?.success === false) { alert(r.error); return; }
    await load(); onRefresh?.();
  };
  const handleDelete = async () => {
    if (!confirm(`Delete ${d.deploymentCode}? This cannot be undone.`)) return;
    const r = await ModulesModel.deleteDeployment(deploymentId);
    if (r?.success === false) { alert(r.error); return; }
    onBack();
  };

  // v2 staged-workflow actions
  // v1.8 — Final Approval on high-risk deployments requires an MFA nonce. We intercept
  // the click, open the password re-auth modal, and only proceed once the server has
  // issued a valid nonce. State is declared at the top of the component (above the early
  // returns) — see the pendingMfa* useState calls up there.
  const handleStageComplete = async (stageId) => {
    const stage = (d?.stages || []).find(s => s.id === stageId);
    const isFinal = stage?.stageKey === 'final_approval';
    const isHighRisk = d?.riskLevel === 'high';

    // Post-Deployment Validation closes the deployment out, so it must declare how it
    // ended. Anything other than Completed requires an explanation.
    let validationOutcome;
    if (stage?.stageKey === 'post_verify') {
      const choice = prompt(
        'Post-Deployment Validation outcome:\n\n'
        + '  1 = Completed\n'
        + '  2 = Rollback Required\n'
        + '  3 = Deployment Failed\n\n'
        + 'Enter 1, 2 or 3:',
        '1'
      );
      if (choice == null) return;
      validationOutcome = DPL_VALIDATION_OUTCOMES[parseInt(String(choice).trim(), 10) - 1];
      if (!validationOutcome) { alert('Enter 1, 2 or 3.'); return; }
    }

    const notes = prompt(
      validationOutcome && validationOutcome !== 'Completed'
        ? `Notes explaining "${validationOutcome}" (required):`
        : 'Optional decision notes:'
    );
    if (notes == null) return;
    if (validationOutcome && validationOutcome !== 'Completed' && !notes.trim()) {
      alert('Notes are required for this outcome.'); return;
    }

    if (isFinal && isHighRisk) {
      // Open MFA modal — completion continues in onVerified below
      setPendingMfaNotes(notes);
      setPendingMfaStageId(stageId);
      return;
    }
    const r = await ModulesModel.completeDeploymentStage(deploymentId, stageId, notes, validationOutcome);
    if (r?.success === false) { alert(r.error); return; }
    await load(); onRefresh?.();
  };
  const handleMfaVerified = async (mfaNonce) => {
    const stageId = pendingMfaStageId;
    const notes = pendingMfaNotes;
    setPendingMfaStageId(null);
    setPendingMfaNotes('');
    const r = await ModulesModel.completeDeploymentStageWithMfa(deploymentId, stageId, notes, mfaNonce);
    if (r?.success === false) { alert(r.error); return; }
    await load(); onRefresh?.();
  };
  const handleStageReject = async (stageId) => {
    const notes = prompt('Rejection reason (required):');
    if (notes == null) return;
    if (!notes.trim()) { alert('Reason is required.'); return; }
    const r = await ModulesModel.rejectDeploymentStage(deploymentId, stageId, notes.trim());
    if (r?.success === false) { alert(r.error); return; }
    await load(); onRefresh?.();
  };
  const handleStageReturn = async (targetStageKey, reason) => {
    const r = await ModulesModel.returnDeploymentStage(deploymentId, currentStage.id, targetStageKey, reason);
    if (r?.success === false) { alert(r.error); return; }
    setShowReturnModal(false);
    await load(); onRefresh?.();
  };
  const handleRequestInfo = async (question) => {
    const r = await ModulesModel.requestDeploymentStageInfo(deploymentId, currentStage.id, question);
    if (r?.success === false) { alert(r.error); return; }
    setShowRmiModal(false);
    await load(); onRefresh?.();
  };
  const handleProvideInfo = async () => {
    const response = prompt('Provide your response / additional info:') || '';
    const r = await ModulesModel.provideDeploymentInfo(deploymentId, response);
    if (r?.success === false) { alert(r.error); return; }
    await load(); onRefresh?.();
  };
  const handleToggleChecklist = async (itemId, checked) => {
    await ModulesModel.toggleDeploymentChecklist(deploymentId, itemId, checked);
    await load();
  };
  const handleDeleteAttachment = async (attId) => {
    if (!confirm('Delete this attachment?')) return;
    const r = await ModulesModel.deleteDeploymentAttachment(attId);
    if (r?.success === false) { alert(r.error); return; }
    await load();
  };

  // Formats a timestamp string as local. Handles TWO input shapes:
  //   - SQLite datetime('now') → "2026-07-03 00:07:08" (space, no zone)  → treat as UTC
  //   - JS new Date().toISOString() → "2026-07-03T00:07:08.390Z" (already ISO)
  // Old logic blindly appended 'Z' after replacing space, which produced "...ZZ" for
  // already-ISO strings → Invalid Date. Now: only normalize when the string looks like
  // SQLite format; leave ISO strings alone.
  const fmt = (s) => {
    if (!s) return '—';
    const str = String(s);
    const iso = str.includes('T') ? str : str.replace(' ', 'T') + 'Z';
    const d = new Date(iso);
    return isNaN(d.getTime()) ? '—' : d.toLocaleString();
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-6">
      <button onClick={onBack} className="text-sm text-blue-600 hover:text-blue-700 mb-3 flex items-center gap-1"><ArrowLeft size={14} /> Back to list</button>
      <div className="bg-white rounded-lg shadow border border-gray-200 p-6 mb-4">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="min-w-0">
            <div className="text-xs font-mono text-gray-400">{d.deploymentCode}</div>
            <h1 className="text-2xl font-bold text-gray-900 break-words">{d.projectName}</h1>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <DeploymentStatusPill status={d.status} />
              {d.projectApplication && <span className="px-2 py-0.5 text-xs font-semibold rounded bg-indigo-50 text-indigo-700">{d.projectApplication}</span>}
              {d.plannedDeploymentDate && <span className="px-2 py-0.5 text-xs font-semibold rounded bg-blue-50 text-blue-700">📅 {d.plannedDeploymentDate}</span>}
              {d.changeType && <span className="px-2 py-0.5 text-xs font-semibold rounded bg-purple-50 text-purple-700">{d.changeType}</span>}
              {/* v1.8 — risk badge with click-to-expand factor breakdown */}
              {d.riskLevel && <DeploymentRiskBadge level={d.riskLevel} score={d.riskScore} factors={d.riskFactors} />}
            </div>
          </div>
          <div className="flex flex-wrap gap-2 shrink-0 justify-end print:hidden">
            <button onClick={() => window.print()} className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50" title="Print or save as PDF for compliance audits">🖨 Print / Save PDF</button>
            {canEdit && <button onClick={openEditor} className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">Edit</button>}
            {canSubmit && <button onClick={handleSubmit} className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700">Submit for Approval</button>}
            {canProvideInfo && <button onClick={handleProvideInfo} className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700">Resume — Provide Info</button>}
            {/* Legacy fallback for deployments created before v2 staged workflow */}
            {canReview && <button onClick={() => handleReview('Approved')} className="px-3 py-1.5 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700">Approve</button>}
            {canReview && <button onClick={() => handleReview('Rejected')} className="px-3 py-1.5 text-sm bg-rose-600 text-white rounded-lg hover:bg-rose-700">Reject</button>}
            {!hasStages && canDeploy && <button onClick={() => handleStageAction('deployed')} className="px-3 py-1.5 text-sm bg-violet-600 text-white rounded-lg hover:bg-violet-700">Mark Deployed</button>}
            {!hasStages && canVerify && <button onClick={() => handleStageAction('verified')} className="px-3 py-1.5 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700">Mark Verified</button>}
            {!hasStages && canComplete && <button onClick={() => handleStageAction('completed')} className="px-3 py-1.5 text-sm bg-cyan-700 text-white rounded-lg hover:bg-cyan-800">Mark Completed</button>}
            {canCancel && <button onClick={handleCancel} className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>}
            {canDelete && <button onClick={handleDelete} className="px-3 py-1.5 text-sm text-red-600 border border-red-300 rounded-lg hover:bg-red-50">Delete</button>}
          </div>
        </div>

        {latestRejection && (
          <div className="border-l-4 border-rose-300 bg-rose-50 p-3 rounded mb-4">
            <div className="text-xs font-semibold text-rose-700 uppercase">Rejected{latestRejection.actorName ? ` by ${latestRejection.actorName}` : ''}{latestRejection.createdAt ? ` · ${fmt(latestRejection.createdAt)}` : ''}</div>
            <div className="text-sm text-rose-900 mt-1 break-words">{latestRejection.notes || 'No reason provided.'}</div>
            {canSubmit && <div className="text-xs text-rose-700 mt-1.5">Address this before resubmitting — the full approval chain will restart from QA.</div>}
          </div>
        )}

        {d.description && <div className="text-sm text-gray-700 break-words border-l-4 border-blue-200 bg-blue-50/40 p-3 rounded mb-4 prose prose-sm max-w-none [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5" dangerouslySetInnerHTML={{ __html: sanitizeBugHtml(d.description) }} />}

        {/* Linked records */}
        {(d.linkedProject || (d.crLinks || []).length > 0 || d.linkedCr || d.linkedTestPlan || d.entryMode === 'manual') && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
            {d.linkedProject && (
              <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                <div className="text-[10px] font-semibold text-gray-500 uppercase">Linked PlanTrack Project</div>
                <div className="text-sm text-gray-900 mt-1"><span className="font-mono text-gray-500">{d.linkedProject.id}</span> · {d.linkedProject.name}</div>
                {d.linkedProject.status && <div className="text-xs text-gray-500">Status: {d.linkedProject.status}</div>}
              </div>
            )}
            {d.entryMode === 'manual' && (
              <div className="border border-amber-200 rounded-lg p-3 bg-amber-50">
                <div className="text-[10px] font-semibold text-amber-700 uppercase">Manual Deployment</div>
                <div className="text-sm text-gray-900 mt-1">{d.projectName}</div>
                <div className="text-xs text-amber-700">No PlanTrack project linked — tracked by title.</div>
              </div>
            )}
            {/* All CRFs on this deployment: the original plus any addenda. Falls back to
                the legacy single link for rows created before multi-CRF support. */}
            {((d.crLinks || []).length > 0 || d.linkedCr) && (
              <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                <div className="text-[10px] font-semibold text-gray-500 uppercase">
                  Linked CRF{((d.crLinks || []).length > 1) ? `s (${d.crLinks.length})` : ''}
                </div>
                {(d.crLinks || []).length > 0 ? (
                  <div className="space-y-1 mt-1">
                    {d.crLinks.map(l => (
                      <div key={l.id} className="flex items-start gap-1.5">
                        <span className={`px-1 py-0.5 rounded text-[9px] font-bold shrink-0 mt-0.5 ${l.linkType === 'original' ? 'bg-blue-100 text-blue-800' : 'bg-violet-100 text-violet-800'}`}>
                          {l.linkType === 'original' ? 'ORIG' : 'ADD'}
                        </span>
                        <div className="min-w-0">
                          <div className="text-sm text-gray-900"><span className="font-mono text-gray-500">{l.crNumber}</span> · {l.title}</div>
                          <div className="text-xs text-gray-500">Status: {l.status}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <>
                    <div className="text-sm text-gray-900 mt-1"><span className="font-mono text-gray-500">{d.linkedCr.crNumber}</span> · {d.linkedCr.title}</div>
                    <div className="text-xs text-gray-500">Status: {d.linkedCr.status}</div>
                  </>
                )}
              </div>
            )}
            {d.linkedTestPlan && (
              <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                <div className="text-[10px] font-semibold text-gray-500 uppercase">Linked TCMS Test Plan <span className="text-gray-400 normal-case">(legacy)</span></div>
                <div className="text-sm text-gray-900 mt-1">{d.linkedTestPlan.title}</div>
                <div className="text-xs text-gray-500">Status: {d.linkedTestPlan.status || 'draft'}{d.linkedTestPlan.projectId ? ` · Project ${d.linkedTestPlan.projectId}` : ''}</div>
              </div>
            )}
          </div>
        )}

        {/* Linked TCMS test cases — multi-link, mirrors the CRF "Proof of Testing" pattern.
            Admins, the requester, or anyone managing the deployment can link/unlink. */}
        <div className="border border-gray-200 rounded-lg p-3 bg-white mb-4 print:hidden">
          <div className="flex items-center justify-between mb-2">
            <div>
              <div className="text-[10px] font-semibold text-gray-500 uppercase">🔗 Linked TCMS test cases</div>
              <div className="text-xs text-gray-500">Test evidence for this deployment — pulled from QMS.</div>
            </div>
            <button onClick={() => setShowTcmsLinkModal(true)} className="text-xs font-semibold text-blue-600 hover:text-blue-700">+ Link test cases</button>
          </div>
          {(d.testLinks || []).length === 0 ? (
            <div className="text-xs text-gray-400 italic">No test cases linked yet.</div>
          ) : (
            <ul className="space-y-1">
              {(d.testLinks || []).map(l => {
                const meta = CR_TC_STATUS_META[l.tcStatus || 'pending'] || CR_TC_STATUS_META.pending;
                return (
                  <li key={l.id} className="flex items-center gap-2 text-sm px-2 py-1.5 rounded bg-slate-50 border border-slate-100">
                    <span className="text-xs font-mono text-slate-500 w-20 flex-shrink-0">{l.tcCode || `TC #${l.testCaseId}`}</span>
                    <div className="flex-1 min-w-0">
                      <div className="text-slate-900 truncate">{l.tcTitle || <span className="italic text-slate-400">case not found</span>}</div>
                      <div className="text-[11px] text-slate-500 truncate">
                        {l.projectName || '—'}
                        {l.executedBy && <> · last run by {l.executedBy}</>}
                        {l.executedAt && <> · {new Date(l.executedAt).toLocaleDateString()}</>}
                      </div>
                    </div>
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold flex-shrink-0" style={{ color: meta.color, backgroundColor: meta.bg, border: `1px solid ${meta.color}44` }}>
                      {meta.icon} {meta.label}
                    </span>
                    <button
                      onClick={async () => {
                        if (!window.confirm(`Unlink ${l.tcCode || 'this test case'}?`)) return;
                        const r = await ModulesModel.removeDeploymentTestLink(l.id);
                        if (r?.success === false) { alert(r.error || 'Failed'); return; }
                        load();
                      }}
                      className="text-rose-500 hover:text-rose-700 text-xs flex-shrink-0"
                    >×</button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Linked QMS requirements — mirrors the Linked TCMS test cases block above. */}
        <div className="border border-gray-200 rounded-lg p-3 bg-white mb-4 print:hidden">
          <div className="flex items-center justify-between mb-2">
            <div>
              <div className="text-[10px] font-semibold text-gray-500 uppercase">🔗 Linked QMS requirements</div>
              <div className="text-xs text-gray-500">Requirements this deployment fulfills.</div>
            </div>
            <button onClick={() => setShowReqLinkModal(true)} className="text-xs font-semibold text-blue-600 hover:text-blue-700">+ Link requirements</button>
          </div>
          {(d.requirementLinks || []).length === 0 ? (
            <div className="text-xs text-gray-400 italic">No requirements linked yet.</div>
          ) : (
            <ul className="space-y-1">
              {(d.requirementLinks || []).map(l => {
                const meta = DPL_REQ_STATUS_META[l.reqStatus] || DPL_REQ_STATUS_DEFAULT;
                return (
                  <li key={l.id} className="flex items-center gap-2 text-sm px-2 py-1.5 rounded bg-slate-50 border border-slate-100">
                    <span className="text-xs font-mono text-slate-500 w-20 flex-shrink-0">{l.reqCode || `REQ #${l.requirementId}`}</span>
                    <div className="flex-1 min-w-0">
                      <div className="text-slate-900 truncate">{l.reqTitle || <span className="italic text-slate-400">requirement not found</span>}</div>
                      <div className="text-[11px] text-slate-500 truncate">{l.projectName || '—'}{l.reqType ? ` · ${l.reqType}` : ''}</div>
                    </div>
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold flex-shrink-0" style={{ color: meta.color, backgroundColor: meta.bg, border: `1px solid ${meta.color}44` }}>
                      {meta.icon} {meta.label}
                    </span>
                    <button
                      onClick={async () => {
                        if (!window.confirm(`Unlink ${l.reqCode || 'this requirement'}?`)) return;
                        const r = await ModulesModel.removeDeploymentRequirementLink(l.id);
                        if (r?.success === false) { alert(r.error || 'Failed'); return; }
                        load();
                      }}
                      className="text-rose-500 hover:text-rose-700 text-xs flex-shrink-0"
                    >×</button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Timeline metadata */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
          <div><span className="text-[10px] uppercase text-gray-500 font-semibold">Requested by</span><div className="text-gray-900">{d.requestedBy || '—'}</div><div className="text-gray-400">{fmt(d.createdAt)}</div></div>
          <div><span className="text-[10px] uppercase text-gray-500 font-semibold">Submitted</span><div className="text-gray-900">{d.submittedAt ? fmt(d.submittedAt) : '—'}</div></div>
          <div><span className="text-[10px] uppercase text-gray-500 font-semibold">Final Approval</span><div className="text-gray-900">{d.reviewedBy || '—'}</div><div className="text-gray-400">{d.reviewedAt ? fmt(d.reviewedAt) : '—'}</div></div>
          <div><span className="text-[10px] uppercase text-gray-500 font-semibold">Deployed</span><div className="text-gray-900">{d.deployedBy || '—'}</div><div className="text-gray-400">{d.deployedAt ? fmt(d.deployedAt) : '—'}</div></div>
          <div><span className="text-[10px] uppercase text-gray-500 font-semibold">Verified</span><div className="text-gray-900">{d.verifiedBy || '—'}</div><div className="text-gray-400">{d.verifiedAt ? fmt(d.verifiedAt) : '—'}</div></div>
          <div><span className="text-[10px] uppercase text-gray-500 font-semibold">Completed</span><div className="text-gray-900">{d.completedAt ? fmt(d.completedAt) : '—'}</div></div>
        </div>
      </div>

      {/* v2 Staged workflow — stepper + tabs + per-stage content */}
      {hasStages && (
        <>
          {/* Who the deployment is waiting on right now — the approval sequence is strict,
              so exactly one stage is ever in_progress. */}
          {currentStage && (
            <div className="flex flex-wrap items-center gap-2 mb-2 px-3 py-2 rounded-lg border border-blue-200 bg-blue-50 text-sm">
              <span className="text-[10px] font-bold uppercase tracking-wide text-blue-700">Awaiting</span>
              <span className="font-semibold text-gray-900">{currentStage.stageLabel}</span>
              <span className="text-gray-400">/</span>
              <span className="text-gray-700">{currentStage.assignedToUserName || 'unassigned'}</span>
              <span className="ml-auto text-xs text-gray-500">
                Step {(stages || []).findIndex(x => x.id === currentStage.id) + 1} of {(stages || []).length}
              </span>
            </div>
          )}
          <DeploymentStageStepper stages={stages} currentStageId={d.currentStageId || currentStage?.id} />

          {/* Info-needed banner */}
          {d.status === 'Info Needed' && (
            <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 mb-4">
              <div className="text-xs font-semibold text-amber-900 uppercase mb-1">Info Requested</div>
              <div className="text-sm text-amber-900 whitespace-pre-wrap">{d.infoRequestNotes || d.info_request_notes}</div>
              {canProvideInfo && <p className="text-xs text-amber-700 mt-2">Click <strong>Resume — Provide Info</strong> above when you've added the requested files/info.</p>}
            </div>
          )}

          {/* Tabs */}
          <div className="bg-white rounded-lg shadow border border-gray-200 mb-4">
            <div className="flex border-b border-gray-200">
              {[
                { key: 'stages', label: `Stages (${stages.length})` },
                { key: 'attachments', label: `Attachments (${(d.attachments || []).length})` },
                { key: 'history', label: `History (${(d.history || []).length})` },
              ].map(t => (
                <button
                  key={t.key}
                  onClick={() => setActiveTab(t.key)}
                  className={`px-4 py-2.5 text-sm font-medium border-b-2 ${activeTab === t.key ? 'text-blue-700 border-blue-600 bg-blue-50/40' : 'text-gray-500 border-transparent hover:text-gray-700'}`}
                >{t.label}</button>
              ))}
            </div>

            {/* Stages tab — list each stage with its checklist + attachments + actions */}
            {activeTab === 'stages' && (
              <div className="p-4 space-y-3">
                {stages.map(s => {
                  const sc = DPL_STAGE_STATUS_COLORS[s.status] || DPL_STAGE_STATUS_COLORS.pending;
                  const isCurrent = s.status === 'in_progress';
                  const locked = s.status !== 'in_progress';
                  const requiredCats = DPL_REQUIRED_ATTACHMENTS_PER_STAGE[s.stageKey] || [];
                  const stageAtts = (d.attachments || []).filter(a => a.stageId === s.id);
                  const totalChecklist = s.checklist?.length || 0;
                  const checkedCount = (s.checklist || []).filter(c => c.checked).length;
                  const requiredUnchecked = (s.checklist || []).filter(c => c.isRequired === 1 && !c.checked).length;
                  const presentCats = new Set(stageAtts.map(a => a.category));
                  // Stage 1 (QA verify): the backend auto-generates the tcms_results attachment
                  // at approval time when every linked TCMS test case is passing, so don't block
                  // the Approve button on a manual upload that will never come in that case.
                  const linkedTcAllPassing = s.stageKey === 'qa_verify' && (d.testLinks || []).length > 0 && (d.testLinks || []).every(l => l.tcStatus === 'pass');
                  const missingRequiredCats = requiredCats.filter(c => !presentCats.has(c) && !(c === 'tcms_results' && linkedTcAllPassing));
                  const canCompleteHere = isCurrent && canActOnCurrentStage && requiredUnchecked === 0 && missingRequiredCats.length === 0;

                  return (
                    <div key={s.id} className={`border rounded-lg p-3 ${s.slaBreached ? 'border-rose-400 bg-rose-50/30' : isCurrent ? 'border-blue-300 bg-blue-50/30' : 'border-gray-200 bg-white'}`}>
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-base font-bold" style={{ color: sc.color }}>{sc.icon}</span>
                            <span className="text-sm font-semibold text-gray-900">Stage {s.sequenceOrder} — {s.stageLabel}</span>
                            <span className="px-2 py-0.5 text-[10px] font-semibold rounded uppercase" style={{ background: sc.bg, color: sc.color }}>{s.status.replace('_', ' ')}</span>
                            {s.slaHours && isCurrent && (
                              <span className={`px-2 py-0.5 text-[10px] font-semibold rounded ${s.slaBreached ? 'bg-rose-600 text-white' : 'bg-amber-100 text-amber-800'}`} title={`SLA: ${s.slaHours}h from start`}>
                                {s.slaBreached ? '⚠ SLA BREACHED' : 'SLA: '} {dplFormatSla(s.slaMsUntilDue, s.slaBreached)}
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-gray-500 mt-0.5">
                            Assigned to <strong>{s.assignedToUserName || '—'}</strong>
                            {s.decidedBy && <> · Decided by <strong>{s.decidedBy}</strong> · {s.decidedAt ? new Date(s.decidedAt.replace ? s.decidedAt.replace(' ', 'T') + 'Z' : s.decidedAt).toLocaleString() : ''}</>}
                          </div>
                          {s.decisionNotes && <div className="text-xs text-slate-600 italic mt-1">"{s.decisionNotes}"</div>}
                        </div>
                        {isCurrent && canActOnCurrentStage && (
                          <div className="flex flex-wrap gap-1.5 shrink-0">
                            <button
                              onClick={() => handleStageComplete(s.id)}
                              disabled={!canCompleteHere}
                              title={!canCompleteHere ? `Missing: ${[requiredUnchecked > 0 ? `${requiredUnchecked} checklist item(s)` : null, ...missingRequiredCats.map(c => `${dplCategoryLabel(c)} attachment`)].filter(Boolean).join(' · ')}` : (s.stageKey === 'final_approval' && d.riskLevel === 'high' ? 'High-risk approval — you will be asked to re-enter your password' : '')}
                              className="px-3 py-1 text-xs bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                              {s.stageKey === 'final_approval' && d.riskLevel === 'high' && <span className="mr-1">🔒</span>}
                              ✓ {s.stageKey === 'final_approval' ? 'Final Approve'
                                 : s.stageKey === 'post_verify' ? 'Record Outcome'
                                 : s.stageKey === 'execute' ? 'Mark Deployed'
                                 : 'Approve'}
                            </button>
                            {/* Reject / Return / Request Info are blocked for the requester
                                even on PM-owned stages — the requester should Cancel the
                                deployment instead of rejecting/returning their own work. */}
                            {(!isOwner || isAdmin) && (
                              <>
                                <button onClick={() => handleStageReject(s.id)} className="px-3 py-1 text-xs bg-rose-600 text-white rounded hover:bg-rose-700">✗ Reject</button>
                                {s.sequenceOrder > 1 && <button onClick={() => setShowReturnModal(true)} className="px-3 py-1 text-xs border border-amber-300 text-amber-700 rounded hover:bg-amber-50">↺ Return to…</button>}
                                <button onClick={() => setShowRmiModal(true)} className="px-3 py-1 text-xs border border-gray-300 text-gray-700 rounded hover:bg-gray-50">? Request Info</button>
                              </>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Checklist for this stage */}
                      {totalChecklist > 0 && (
                        <div className="mt-2">
                          <div className="flex items-center justify-between text-[11px] text-gray-600 mb-1">
                            <span className="font-semibold uppercase">Checklist ({checkedCount}/{totalChecklist} · {requiredUnchecked} required missing)</span>
                          </div>
                          <ul className="border border-gray-200 rounded bg-white">
                            {s.checklist.map(item => (
                              <DPLChecklistItem key={item.id} item={item} locked={locked || !canActOnCurrentStage} onToggle={handleToggleChecklist} />
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Required + uploaded attachments for this stage */}
                      {(requiredCats.length > 0 || stageAtts.length > 0) && (
                        <div className="mt-3">
                          <div className="text-[11px] font-semibold uppercase text-gray-600 mb-1">Attachments</div>
                          <div className="space-y-1.5">
                            {requiredCats.map(cat => {
                              const autoSatisfied = cat === 'tcms_results' && linkedTcAllPassing && !presentCats.has(cat);
                              const has = presentCats.has(cat) || autoSatisfied;
                              return (
                                <div key={cat} className={`flex items-center gap-2 text-xs ${has ? 'text-gray-600' : 'text-rose-700'}`}>
                                  <span className="w-4 text-center">{has ? '✓' : '⚠'}</span>
                                  <span className="flex-1">
                                    {dplCategoryLabel(cat)} {!has && <span className="font-semibold">(required)</span>}
                                    {autoSatisfied && <span className="text-emerald-600"> (auto-generates from linked TCMS results on approval)</span>}
                                  </span>
                                  {isCurrent && canActOnCurrentStage && (
                                    <DPLAttachmentUploader stageId={s.id} category={cat} deploymentId={deploymentId} locked={locked} onUploaded={load} />
                                  )}
                                </div>
                              );
                            })}
                            {stageAtts.filter(a => !requiredCats.includes(a.category)).length > 0 && (
                              <div className="mt-1 pt-1 border-t border-gray-100">
                                <div className="text-[10px] text-gray-500 uppercase">Additional uploads</div>
                                {stageAtts.filter(a => !requiredCats.includes(a.category)).map(a => (
                                  <div key={a.id} className="flex items-center gap-2 text-xs text-gray-700 mt-1">
                                    <span>📎</span>
                                    <span className="flex-1 truncate">{a.fileName} <span className="text-gray-400">({dplCategoryLabel(a.category)})</span></span>
                                    <button onClick={() => handleDeleteAttachment(a.id)} className="text-gray-400 hover:text-rose-600">×</button>
                                  </div>
                                ))}
                              </div>
                            )}
                            {isCurrent && canActOnCurrentStage && (
                              <div className="mt-2 pt-2 border-t border-gray-100">
                                <div className="text-[10px] text-gray-500 mb-1">Upload to optional category:</div>
                                <div className="flex flex-wrap gap-1">
                                  {DPL_ATTACHMENT_CATEGORIES.filter(c => !requiredCats.includes(c.key)).map(c => (
                                    <details key={c.key} className="inline-block">
                                      <summary className="px-2 py-0.5 text-[11px] border border-gray-300 rounded cursor-pointer hover:bg-gray-50">{c.label}</summary>
                                      <div className="mt-1"><DPLAttachmentUploader stageId={s.id} category={c.key} deploymentId={deploymentId} locked={false} onUploaded={load} /></div>
                                    </details>
                                  ))}
                                </div>
                              </div>
                            )}
                            {stageAtts.length === 0 && requiredCats.length === 0 && (
                              <div className="text-xs text-gray-400 italic">No attachments yet.</div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {activeTab === 'attachments' && (
              <div className="p-4">
                {/* General-purpose uploader for documents, emails, anything not tied to a
                    specific stage's required category. stageId is null on the backend; the
                    attachment shows up as "—" in the Stage column. */}
                <DPLGeneralAttachmentUploader deploymentId={deploymentId} onUploaded={load} />
                {(d.attachments || []).length === 0 ? (
                  <div className="text-sm text-gray-400 mt-4">No attachments yet.</div>
                ) : (
                  <table className="w-full text-sm mt-4">
                    <thead className="bg-gray-50">
                      <tr className="text-left text-[10px] uppercase text-gray-500">
                        <th className="p-2">File</th>
                        <th className="p-2">Category</th>
                        <th className="p-2">Stage</th>
                        <th className="p-2">Uploaded by</th>
                        <th className="p-2">When</th>
                        <th className="p-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {(d.attachments || []).map(a => {
                        const s = stages.find(x => x.id === a.stageId);
                        const downloadAttachment = async () => {
                          const r = await ModulesModel.downloadDeploymentAttachment(a.id);
                          if (!r?.fileData) { alert(`Download failed: ${r?.error || 'no data'}`); return; }
                          // Trigger a browser download from the base64 payload
                          const link = document.createElement('a');
                          link.href = `data:${r.fileType || 'application/octet-stream'};base64,${r.fileData}`;
                          link.download = r.fileName || `attachment-${a.id}`;
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                        };
                        return (
                          <tr key={a.id} className="border-t border-gray-100">
                            <td className="p-2 break-all">
                              <button onClick={downloadAttachment} className="text-blue-600 hover:underline text-left" title="Download">
                                {a.fileName}
                              </button>
                              {a.fileSize ? <span className="text-[10px] text-gray-400 ml-1">({Math.round(a.fileSize / 1024)} KB)</span> : null}
                            </td>
                            <td className="p-2 text-gray-600">{dplCategoryLabel(a.category)}</td>
                            <td className="p-2 text-gray-600">{s?.stageLabel || <span className="text-gray-400 italic">deployment-level</span>}</td>
                            <td className="p-2 text-gray-600">{a.uploadedBy || '—'}</td>
                            <td className="p-2 text-xs text-gray-500">{a.createdAt ? new Date(a.createdAt.replace ? a.createdAt.replace(' ', 'T') + 'Z' : a.createdAt).toLocaleString() : '—'}</td>
                            <td className="p-2 text-right">
                              <button onClick={() => handleDeleteAttachment(a.id)} className="text-xs text-rose-600 hover:underline">Delete</button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {activeTab === 'history' && (
              <div className="p-4">
                {(d.history || []).length === 0 ? (
                  <div className="text-sm text-gray-400">No history yet.</div>
                ) : (
                  <ol className="space-y-2 text-sm">
                    {d.history.map(h => (
                      <li key={h.id} className="flex items-start gap-3 border-l-2 border-blue-200 pl-3 py-1">
                        <div className="flex-1">
                          <div className="font-semibold text-gray-800">{h.action.replace(/_/g, ' ')}</div>
                          <div className="text-xs text-gray-500"><strong>{h.actorName || 'system'}</strong> · {h.createdAt ? new Date(h.createdAt.replace ? h.createdAt.replace(' ', 'T') + 'Z' : h.createdAt).toLocaleString() : '—'}</div>
                          {h.notes && <div className="text-xs text-gray-600 italic mt-0.5">"{h.notes}"</div>}
                        </div>
                      </li>
                    ))}
                  </ol>
                )}
              </div>
            )}
          </div>
        </>
      )}

      {/* Legacy approval chain (deployments created before v2 staged workflow) */}
      {!hasStages && hasLegacyChain && (
        <div className="bg-white rounded-lg shadow border border-gray-200 p-4 mb-4">
          <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Pre-Deployment Approval Chain <span className="font-normal normal-case text-gray-400">(legacy — created before staged workflow)</span></div>
          <ol className="space-y-1.5 text-sm">
            {approvals.map(a => {
              const dec = (a.decision || 'pending').toLowerCase();
              const isCurrent = dec === 'pending' && a.stepNumber === currentStep?.stepNumber;
              const color = dec === 'approved' ? 'text-emerald-700' : dec === 'rejected' ? 'text-rose-700' : isCurrent ? 'text-indigo-700 font-semibold' : 'text-slate-500';
              const icon = dec === 'approved' ? '✓' : dec === 'rejected' ? '✗' : isCurrent ? '→' : '·';
              return (
                <li key={a.stepNumber} className={`flex items-start gap-2 ${color}`}>
                  <span className="w-5 text-center shrink-0">{icon}</span>
                  <div className="flex-1">
                    <span>Step {a.stepNumber}{a.approverLabel ? ` — ${a.approverLabel}` : ''}: <strong>{a.assignedToUserName}</strong></span>
                    {dec !== 'pending' && <span className="text-slate-400 ml-2">({a.decision})</span>}
                    {a.decisionNotes && <div className="text-xs text-slate-600 italic mt-0.5">{a.decisionNotes}</div>}
                  </div>
                </li>
              );
            })}
          </ol>
        </div>
      )}

      {/* Action modals */}
      {showReturnModal && currentStage && (
        <DPLReturnModal
          stages={stages}
          currentStage={currentStage}
          onClose={() => setShowReturnModal(false)}
          onConfirm={handleStageReturn}
        />
      )}
      {showRmiModal && (
        <DPLRequestInfoModal
          onClose={() => setShowRmiModal(false)}
          onConfirm={handleRequestInfo}
        />
      )}
      {showTcmsLinkModal && (
        <ChangeRequestTcmsLinkModal
          crId={null}
          alreadyLinkedIds={(d.testLinks || []).map(l => l.testCaseId)}
          onClose={() => setShowTcmsLinkModal(false)}
          onSave={async (cases) => {
            const r = await ModulesModel.addDeploymentTestLinks(deploymentId, cases.map(c => c.id));
            if (r?.success === false) return r;
            await load();
            return { success: true };
          }}
        />
      )}
      {showReqLinkModal && (
        <DeploymentRequirementLinkModal
          alreadyLinkedIds={(d.requirementLinks || []).map(l => l.requirementId)}
          onClose={() => setShowReqLinkModal(false)}
          onSave={async (reqs) => {
            const r = await ModulesModel.addDeploymentRequirementLinks(deploymentId, reqs.map(rq => rq.id));
            if (r?.success === false) return r;
            await load();
            return { success: true };
          }}
        />
      )}
      {/* v1.8 — password re-auth for high-risk final approvals */}
      {pendingMfaStageId && (
        <DPLMfaReauthModal
          deploymentId={deploymentId}
          deploymentCode={d.deploymentCode}
          onCancel={() => { setPendingMfaStageId(null); setPendingMfaNotes(''); }}
          onVerified={handleMfaVerified}
        />
      )}

      {/* Stage notes */}
      {(d.reviewNotes || d.deploymentNotes || d.verificationNotes || d.completionNotes) && (
        <div className="bg-white rounded-lg shadow border border-gray-200 p-4 space-y-3">
          {d.reviewNotes && <div><div className="text-[10px] uppercase text-gray-500 font-semibold mb-1">Review Notes</div><div className="text-sm text-gray-700 whitespace-pre-wrap">{d.reviewNotes}</div></div>}
          {d.deploymentNotes && <div><div className="text-[10px] uppercase text-gray-500 font-semibold mb-1">Deployment Notes</div><div className="text-sm text-gray-700 whitespace-pre-wrap">{d.deploymentNotes}</div></div>}
          {d.verificationNotes && <div><div className="text-[10px] uppercase text-gray-500 font-semibold mb-1">Verification Notes</div><div className="text-sm text-gray-700 whitespace-pre-wrap">{d.verificationNotes}</div></div>}
          {d.completionNotes && <div><div className="text-[10px] uppercase text-gray-500 font-semibold mb-1">Completion Notes</div><div className="text-sm text-gray-700 whitespace-pre-wrap">{d.completionNotes}</div></div>}
        </div>
      )}

      {editing && (
        <DeploymentCreateModal
          initial={d}
          lookups={lookups}
          onClose={() => setEditing(false)}
          onSave={() => { setEditing(false); load(); onRefresh?.(); }}
        />
      )}
    </div>
  );
};

// Format minutes → human "Xh Ym" or "Nd Xh"
const dplFmtMinutes = (m) => {
  if (m == null || !Number.isFinite(m)) return '—';
  if (m < 60) return `${Math.round(m)}m`;
  if (m < 1440) return `${Math.floor(m / 60)}h ${Math.round(m % 60)}m`;
  const days = Math.floor(m / 1440);
  const hrs = Math.round((m % 1440) / 60);
  return `${days}d ${hrs}h`;
};

// DORA-style KPI dashboard for Deployments. Rendered when user clicks "View KPIs"
// in the Deployments list page.
const DeploymentsKpiView = ({ onBack }) => {
  const [kpis, setKpis] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [rangeDays, setRangeDays] = React.useState(90);

  const load = React.useCallback(async () => {
    setLoading(true);
    setKpis(await ModulesModel.getDeploymentKpis(rangeDays));
    setLoading(false);
  }, [rangeDays]);
  React.useEffect(() => { load(); }, [load]);

  if (loading) return <div className="max-w-7xl mx-auto px-4 py-6 text-sm text-gray-500">Loading deployment KPIs…</div>;
  if (!kpis) return <div className="max-w-7xl mx-auto px-4 py-6 text-sm text-gray-500">No data. <button onClick={onBack} className="text-blue-600 underline">Back</button></div>;

  const cfr = kpis.changeFailureRate || { rate: 0, failed: 0, total: 0 };
  const lt = kpis.leadTimeMinutes || {};
  const totalDeployed = (kpis.statusCounts?.Deployed || 0) + (kpis.statusCounts?.Verified || 0) + (kpis.statusCounts?.Completed || 0);
  const maxFreq = Math.max(1, ...(kpis.frequency || []).map(f => f.count));

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <div className="flex items-center justify-between mb-4">
        <button onClick={onBack} className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"><ArrowLeft size={14} /> Back to Deployments</button>
        <div className="flex items-center gap-2">
          <label className="text-xs text-gray-600">Range:</label>
          <select value={rangeDays} onChange={e => setRangeDays(parseInt(e.target.value, 10))} className="border border-gray-300 rounded px-2 py-1 text-sm">
            <option value="30">Last 30 days</option>
            <option value="60">Last 60 days</option>
            <option value="90">Last 90 days</option>
            <option value="180">Last 180 days</option>
            <option value="365">Last 365 days</option>
          </select>
        </div>
      </div>

      <h1 className="text-3xl font-bold text-gray-900 mb-1">Deployment KPIs</h1>
      <p className="text-gray-600 mb-6 text-sm">DORA metrics + approval cycle insights — last {rangeDays} days unless noted.</p>

      {/* Headline KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <div className="bg-white rounded-lg shadow border border-gray-200 p-4">
          <div className="text-[10px] uppercase font-semibold text-gray-500">Deployment Frequency</div>
          <div className="text-3xl font-bold text-gray-900 mt-1">{totalDeployed}</div>
          <div className="text-xs text-gray-500 mt-1">deployments live (last {rangeDays}d)</div>
        </div>
        <div className="bg-white rounded-lg shadow border border-gray-200 p-4">
          <div className="text-[10px] uppercase font-semibold text-gray-500">Lead Time (median)</div>
          <div className="text-3xl font-bold text-gray-900 mt-1">{dplFmtMinutes(lt.median)}</div>
          <div className="text-xs text-gray-500 mt-1">submit → deployed · p95 {dplFmtMinutes(lt.p95)}</div>
        </div>
        <div className="bg-white rounded-lg shadow border border-gray-200 p-4">
          <div className="text-[10px] uppercase font-semibold text-gray-500">Change Failure Rate</div>
          <div className={`text-3xl font-bold mt-1 ${cfr.rate > 0.2 ? 'text-rose-600' : cfr.rate > 0.1 ? 'text-amber-600' : 'text-emerald-600'}`}>{Math.round(cfr.rate * 1000) / 10}%</div>
          <div className="text-xs text-gray-500 mt-1">{cfr.failed} / {cfr.total} rejected or cancelled</div>
        </div>
        <div className="bg-white rounded-lg shadow border border-gray-200 p-4">
          <div className="text-[10px] uppercase font-semibold text-gray-500">Open Approval Backlog</div>
          <div className="text-3xl font-bold text-gray-900 mt-1">{(kpis.backlog || []).reduce((a, b) => a + b.pending, 0)}</div>
          <div className="text-xs text-gray-500 mt-1">pending stages across {(kpis.backlog || []).length} approvers</div>
        </div>
        {(() => {
          const sla = kpis.sla || {};
          const rate = sla.complianceRate;
          const openCount = (sla.openBreaches || []).length;
          const cls = openCount > 0 ? 'text-rose-600' : rate == null ? 'text-gray-400' : rate >= 0.9 ? 'text-emerald-600' : rate >= 0.75 ? 'text-amber-600' : 'text-rose-600';
          return (
            <div className="bg-white rounded-lg shadow border border-gray-200 p-4">
              <div className="text-[10px] uppercase font-semibold text-gray-500">SLA Compliance</div>
              <div className={`text-3xl font-bold mt-1 ${cls}`}>{rate == null ? '—' : `${Math.round(rate * 1000) / 10}%`}</div>
              <div className="text-xs text-gray-500 mt-1">
                {sla.completedOnTime || 0} / {sla.totalCompleted || 0} on-time
                {openCount > 0 && <span className="ml-1 text-rose-600 font-semibold">· {openCount} open breach{openCount === 1 ? '' : 'es'}</span>}
              </div>
            </div>
          );
        })()}
      </div>

      {/* Open SLA breaches list — only renders when there are active breaches */}
      {(kpis.sla?.openBreaches || []).length > 0 && (
        <div className="bg-rose-50 border border-rose-200 rounded-lg p-4 mb-6">
          <div className="text-sm font-semibold text-rose-800 mb-2">⏰ {kpis.sla.openBreaches.length} open SLA breach{kpis.sla.openBreaches.length === 1 ? '' : 'es'} right now</div>
          <ul className="space-y-1.5 text-sm">
            {kpis.sla.openBreaches.slice(0, 10).map(b => (
              <li key={b.stageId} className="flex items-center gap-2 text-rose-900">
                <span className="font-mono text-xs shrink-0">{b.deploymentCode}</span>
                <span className="truncate flex-1">{b.projectName} — {b.stageLabel}</span>
                <span className="text-xs">{b.approver || '—'}</span>
                <span className="font-semibold text-rose-700 shrink-0">{Math.round(b.hoursOverdue)}h over</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Status distribution + frequency chart */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow border border-gray-200 p-4">
          <div className="text-sm font-semibold text-gray-700 mb-3">Status distribution</div>
          <div className="grid grid-cols-2 gap-2">
            {['Draft','Submitted','Info Needed','Approved','Deployed','Verified','Completed','Rejected','Cancelled'].map(s => {
              const n = kpis.statusCounts?.[s] || 0;
              const c = DPL_STATUS_COLORS[s] || { bg: '#f3f4f6', color: '#374151' };
              return (
                <div key={s} className="flex items-center gap-2 text-sm">
                  <span className="inline-block w-3 h-3 rounded-sm" style={{ background: c.color }} />
                  <span className="flex-1 text-gray-700">{s}</span>
                  <span className="font-semibold text-gray-900">{n}</span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow border border-gray-200 p-4">
          <div className="text-sm font-semibold text-gray-700 mb-3">Weekly deployment frequency</div>
          {(kpis.frequency || []).length === 0 ? (
            <div className="text-xs text-gray-400">No deployments in this range yet.</div>
          ) : (
            <div className="space-y-1.5">
              {(kpis.frequency || []).map(f => (
                <div key={f.week} className="flex items-center gap-2 text-xs">
                  <span className="w-16 text-gray-500 font-mono">{f.week}</span>
                  <div className="flex-1 h-4 bg-gray-100 rounded overflow-hidden">
                    <div className="h-full bg-blue-500" style={{ width: `${(f.count / maxFreq) * 100}%` }} />
                  </div>
                  <span className="w-8 text-right font-semibold text-gray-700">{f.count}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Per-stage cycle time */}
      <div className="bg-white rounded-lg shadow border border-gray-200 p-4 mb-6">
        <div className="text-sm font-semibold text-gray-700 mb-3">Average time per stage</div>
        {(kpis.stageCycle || []).length === 0 ? (
          <div className="text-xs text-gray-400">No completed stages yet in this range.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase text-gray-500">
                <th className="pb-2">Stage</th>
                <th className="pb-2">Average duration</th>
                <th className="pb-2">Samples</th>
              </tr>
            </thead>
            <tbody>
              {kpis.stageCycle.map(s => (
                <tr key={s.stageKey} className="border-t border-gray-100">
                  <td className="py-2">{s.stageLabel}</td>
                  <td className="py-2 font-mono">{dplFmtMinutes(s.avgMinutes)}</td>
                  <td className="py-2 text-gray-500">{s.samples}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Backlog + top rejections + by application */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg shadow border border-gray-200 p-4">
          <div className="text-sm font-semibold text-gray-700 mb-3">Approval backlog by approver</div>
          {(kpis.backlog || []).length === 0 ? (
            <div className="text-xs text-gray-400">No open approvals 🎉</div>
          ) : (
            <ul className="space-y-1.5 text-sm">
              {kpis.backlog.map(b => (
                <li key={b.approver} className="flex items-center gap-2">
                  <span className="flex-1 truncate">{b.approver || '—'}</span>
                  <span className="text-xs text-gray-500">{b.avgHoursOpen ? `~${Math.round(b.avgHoursOpen)}h avg` : ''}</span>
                  <span className="font-semibold text-gray-900">{b.pending}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="bg-white rounded-lg shadow border border-gray-200 p-4">
          <div className="text-sm font-semibold text-gray-700 mb-3">Top rejection reasons</div>
          {(kpis.topRejections || []).length === 0 ? (
            <div className="text-xs text-gray-400">No rejections in this range.</div>
          ) : (
            <ul className="space-y-2 text-sm">
              {kpis.topRejections.map((r, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="font-semibold text-rose-600 shrink-0">{r.c}×</span>
                  <span className="text-gray-700 italic truncate">"{r.reasonPrefix}…"</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="bg-white rounded-lg shadow border border-gray-200 p-4">
          <div className="text-sm font-semibold text-gray-700 mb-3">By application</div>
          {(kpis.byApplication || []).length === 0 ? (
            <div className="text-xs text-gray-400">No data.</div>
          ) : (
            <ul className="space-y-2 text-sm">
              {kpis.byApplication.map(a => (
                <li key={a.application || '—'} className="flex items-center gap-2">
                  <span className="flex-1 truncate">{a.application || '—'}</span>
                  <span className="text-xs text-gray-500">{a.deployedCount} live</span>
                  <span className="font-semibold text-gray-900">{a.c}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

// Admin-only Settings view for the Deployments module. Hosts stage templates +
// freeze windows so they live with the module rather than in global Settings.
// Approval-chain editor. Mirrors the CRF chain settings: JSON keyed by the requester's
// role, each step naming a stage plus who approves it (a role or a named user).
// Anthony is appended as the final step server-side regardless of what is saved, so the
// production gate can't be edited away.
const DPL_CHAIN_STAGE_LABELS = {
  qa_verify: 'QA Approval',
  devops_review: 'Technical/DevOps Manager Approval',
  pm_verify: 'PM Approval',
  security_review: 'Security & Compliance Review',
  cab_review: 'Change Advisory Board',
  final_approval: 'Final Approval',
  execute: 'Production Deployment & Smoke Testing',
  post_verify: 'Post-Deployment Validation',
};

const DeploymentApprovalChainsSection = () => {
  const [data, setData] = React.useState(null);
  const [draft, setDraft] = React.useState('');
  const [previewRole, setPreviewRole] = React.useState('');
  const [preview, setPreview] = React.useState(null);
  const [saving, setSaving] = React.useState(false);
  const [msg, setMsg] = React.useState('');

  const load = React.useCallback(async (role = '') => {
    const d = await ModulesModel.getDeploymentApprovalChains(role);
    if (!d) return;
    setData(d);
    setPreview(d.preview || null);
    setDraft(JSON.stringify(d.approvalChains, null, 2));
  }, []);
  React.useEffect(() => { load(''); }, [load]);

  const save = async () => {
    let parsed;
    try { parsed = JSON.parse(draft); }
    catch (e) { setMsg(`Invalid JSON: ${e.message}`); return; }
    setSaving(true);
    const r = await ModulesModel.saveDeploymentApprovalChains(parsed);
    setSaving(false);
    if (r?.success === false) { setMsg(r.error || 'Save failed'); return; }
    setMsg('Saved. New deployments will use this chain; in-flight ones keep the chain they were submitted under.');
    await load(previewRole);
  };

  if (!data) return <div className="bg-white rounded-lg shadow border border-gray-200 p-4 mb-4 text-sm text-gray-500">Loading approval chains…</div>;

  return (
    <div className="bg-white rounded-lg shadow border border-gray-200 p-4 mb-4">
      <h2 className="text-lg font-semibold text-gray-900 mb-1">Approval Chain</h2>
      <p className="text-xs text-gray-600 mb-3">
        Keyed by the <strong>requester's role</strong>; <code className="bg-gray-100 px-1 rounded">*</code> is the fallback used when a role has no entry.
        Each step is <code className="bg-gray-100 px-1 rounded">{'{ stageKey, type: "role"|"user", value, label }'}</code>.
        <strong> {data.finalApprover}</strong> is always appended as the final approver, and
        <strong> {(data.executionStageKeys || []).join(' → ')}</strong> always follow the chain — neither can be removed here.
      </p>

      <div className="text-[11px] text-gray-500 mb-2">
        Valid stageKey values: {(data.approvalStageKeys || []).map(k => (
          <code key={k} className="bg-gray-100 px-1 rounded mr-1">{k}</code>
        ))}
      </div>

      <textarea
        value={draft}
        onChange={e => { setDraft(e.target.value); setMsg(''); }}
        rows={14}
        spellCheck={false}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-xs font-mono focus:ring-2 focus:ring-blue-500"
      />

      <div className="flex flex-wrap items-center gap-2 mt-2">
        <button onClick={save} disabled={saving} className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50">
          {saving ? 'Saving…' : 'Save chain'}
        </button>
        <button
          onClick={() => setDraft(JSON.stringify(data.defaults, null, 2))}
          className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
        >Reset to default</button>
        <div className="flex items-center gap-1 ml-auto">
          <label className="text-xs text-gray-500">Preview for role</label>
          <input
            value={previewRole}
            onChange={e => setPreviewRole(e.target.value)}
            placeholder="e.g. developer"
            className="border border-gray-300 rounded px-2 py-1 text-xs w-36"
          />
          <button onClick={() => load(previewRole)} className="px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-50">Preview</button>
        </div>
      </div>
      {msg && <div className="text-xs mt-2 text-gray-700 bg-gray-50 border border-gray-200 rounded p-2">{msg}</div>}

      {preview && (
        <div className="mt-3 border-t border-gray-100 pt-3">
          <div className="text-xs font-semibold text-gray-600 mb-1.5">
            Resolved chain{preview.requesterRole ? ` for "${preview.requesterRole}"` : ' (fallback)'}
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            {(preview.chain || []).map((st, i) => (
              <React.Fragment key={`${st.stageKey}-${i}`}>
                <div className={`px-2 py-1 rounded border text-xs ${st.resolvable ? 'bg-blue-50 border-blue-200 text-blue-900' : 'bg-rose-50 border-rose-300 text-rose-800'}`}>
                  <div className="font-semibold">{i + 1}. {DPL_CHAIN_STAGE_LABELS[st.stageKey] || st.stageKey}</div>
                  <div className="opacity-80">
                    {st.type}: {st.value}
                    {st.resolvable ? ` → ${st.resolvedUserName}` : ' → UNRESOLVED'}
                  </div>
                </div>
                {i < preview.chain.length - 1 && <span className="text-gray-300">→</span>}
              </React.Fragment>
            ))}
            {(preview.executionStages || []).map(k => (
              <React.Fragment key={k}>
                <span className="text-gray-300">→</span>
                <div className="px-2 py-1 rounded border border-gray-200 bg-gray-50 text-xs text-gray-600">
                  <div className="font-semibold">{k}</div>
                  <div className="opacity-70">fixed</div>
                </div>
              </React.Fragment>
            ))}
          </div>
          {(preview.chain || []).some(st => !st.resolvable) && (
            <div className="text-xs text-rose-700 mt-2">
              ⚠ One or more steps resolve to no active user. Submissions using this chain fall back to the stage's default approver.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const DeploymentsSettingsView = ({ onBack }) => {
  return (
    <div className="max-w-5xl mx-auto px-4 py-6">
      <button onClick={onBack} className="mb-4 text-sm text-blue-600 hover:underline">← Back to Deployments</button>
      <h1 className="text-3xl font-bold text-gray-900 mb-1">Deployments Settings</h1>
      <p className="text-gray-600 mb-6 text-sm">Approval chain, stage workflow templates and change-freeze windows. Admin-managed.</p>
      <DeploymentApprovalChainsSection />
      <DeploymentStageTemplatesSection />
      <DeploymentFreezeWindowsSection />
    </div>
  );
};

// ============================================================================
// DATAFEED — read-only API management module
// Landing page has three tabs: API Keys (create/revoke), Schema (entity docs),
// and Access Log (recent requests). Admin only.
// ============================================================================

const DataFeedModule = ({ user }) => {
  const [tab, setTab] = React.useState('keys');
  const [keys, setKeys] = React.useState([]);
  const [schema, setSchema] = React.useState({ entities: [] });
  const [accessLog, setAccessLog] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [showNew, setShowNew] = React.useState(false);
  const [newlyCreatedKey, setNewlyCreatedKey] = React.useState(null);
  const [revealed, setRevealed] = React.useState(null);   // { key, prefix, name, mode }
  const [busyKeyId, setBusyKeyId] = React.useState(null);
  // Roles allowed to manage DataFeed API keys — mirrors the backend's DATAFEED_KEY_MANAGER_ROLES.
  // Admin plus manager/dataops/dataops_manager/developer/devops_manager can create/list/
  // revoke keys and view the access log. `developer` is the DevOps IC role in Orbit.
  const role = String(user?.user_role || user?.role || '').toLowerCase();
  const canManage = ['admin', 'manager', 'dataops', 'dataops_manager', 'developer', 'devops_manager'].includes(role);

  const loadAll = React.useCallback(async () => {
    setLoading(true);
    const [k, s, l] = await Promise.all([
      canManage ? ModulesModel.listDataFeedKeys() : Promise.resolve({ keys: [] }),
      ModulesModel.getDataFeedSchema(),
      canManage ? ModulesModel.getDataFeedAccessLog(50) : Promise.resolve({ log: [] }),
    ]);
    setKeys(k?.keys || []);
    setSchema(s || { entities: [] });
    setAccessLog(l?.log || []);
    setLoading(false);
  }, [canManage]);
  React.useEffect(() => { loadAll(); }, [loadAll]);

  const handleReveal = async (key) => {
    setBusyKeyId(key.id);
    const r = await ModulesModel.revealDataFeedKey(key.id);
    setBusyKeyId(null);
    if (!r?.success || !r.key) { alert(r?.error || 'Could not retrieve this key.'); return; }
    setRevealed({ ...r, mode: 'reveal' });
    loadAll(); // refresh last-revealed metadata
  };
  const handleRotate = async (key) => {
    if (!window.confirm(
      `Rotate key "${key.name}"?\n\nA new secret is issued and shown once. The current secret stops working immediately, `
      + `so every external system using it must be updated or it will start getting 401 responses.`
    )) return;
    setBusyKeyId(key.id);
    const r = await ModulesModel.rotateDataFeedKey(key.id);
    setBusyKeyId(null);
    if (!r?.success || !r.key) { alert(r?.error || 'Could not rotate this key.'); return; }
    setRevealed({ ...r, mode: 'rotate' });
    loadAll();
  };

  const handleRevoke = async (key) => {
    if (!window.confirm(`Revoke key "${key.name}"? External systems using this key will start getting 401 responses immediately.`)) return;
    await ModulesModel.updateDataFeedKey(key.id, { isActive: false });
    loadAll();
  };
  const handleDelete = async (key) => {
    if (!window.confirm(`Permanently DELETE key "${key.name}"? This can't be undone. Prefer Revoke if you might want to audit it later.`)) return;
    await ModulesModel.deleteDataFeedKey(key.id);
    loadAll();
  };

  if (!canManage) {
    return (
      <div className="max-w-5xl mx-auto px-4 py-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-1">DataFeed</h1>
        <p className="text-gray-600 mb-6 text-sm">Read-only Orbit data export API. Key management is restricted to admin, manager, DataOps, and DevOps roles; the schema below is public.</p>
        <DataFeedSchemaPanel schema={schema} />
      </div>
    );
  }

  const apiBase = typeof window !== 'undefined' ? `${window.location.origin}/api/datafeed` : '/api/datafeed';

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">DataFeed</h1>
          <p className="text-gray-600 mt-1 text-sm">Read-only API endpoints for external systems to ingest Orbit data into their own warehouses (Snowflake, BigQuery, Postgres, etc.).</p>
        </div>
        <button onClick={() => setShowNew(true)} className="px-4 py-2 bg-cyan-700 text-white text-sm rounded-lg hover:bg-cyan-800 font-medium">+ Create API Key</button>
      </div>

      <div className="bg-cyan-50/40 border border-cyan-200 rounded-lg p-3 mb-4 text-sm">
        <div className="text-xs font-bold uppercase text-cyan-800 mb-1">Base URL</div>
        <code className="font-mono text-xs bg-white px-2 py-1 rounded border border-cyan-100 select-all">{apiBase}</code>
        <div className="text-xs text-cyan-900 mt-1">Every data endpoint requires the <code className="bg-white px-1 rounded">X-API-Key</code> HTTP header. Schema doc at <code className="bg-white px-1 rounded">GET {apiBase}/schema</code> is public (no auth).</div>
      </div>

      <div className="border-b border-gray-200 mb-4 flex gap-1">
        {[
          { key: 'keys', label: `🔑 API Keys (${keys.length})` },
          { key: 'schema', label: `📖 Schema (${(schema.entities || []).length} entities)` },
          { key: 'log', label: `📊 Access Log (${accessLog.length})` },
        ].map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 ${tab === t.key ? 'text-cyan-700 border-cyan-600 bg-cyan-50/40' : 'text-gray-500 border-transparent hover:text-gray-700'}`}
          >{t.label}</button>
        ))}
      </div>

      {loading ? (
        <div className="text-sm text-gray-400">Loading…</div>
      ) : tab === 'keys' ? (
        <DataFeedKeysPanel
          keys={keys}
          busyKeyId={busyKeyId}
          onReveal={handleReveal}
          onRotate={handleRotate}
          onRevoke={handleRevoke}
          onDelete={handleDelete}
          onReload={loadAll}
        />
      ) : tab === 'schema' ? (
        <DataFeedSchemaPanel schema={schema} apiBase={apiBase} />
      ) : (
        <DataFeedAccessLogPanel log={accessLog} onRefresh={loadAll} />
      )}

      {showNew && (
        <DataFeedCreateKeyModal
          entities={schema.entities || []}
          onClose={() => setShowNew(false)}
          onCreated={(result) => {
            setShowNew(false);
            setNewlyCreatedKey(result);
            loadAll();
          }}
        />
      )}
      {newlyCreatedKey && (
        <DataFeedShowKeyModal secret={newlyCreatedKey} mode="create" onClose={() => setNewlyCreatedKey(null)} />
      )}
      {revealed && (
        <DataFeedShowKeyModal secret={revealed} mode={revealed.mode} onClose={() => setRevealed(null)} />
      )}
    </div>
  );
};

// Setup/status guidance intentionally lives on the row controls (the "Rotate to view"
// button and its tooltip) rather than in banners above the table. The DATAFEED_KEY_SECRET
// hardening step is documented in .dev.vars.example and next to encryptDataFeedKey.
const DataFeedKeysPanel = ({ keys, busyKeyId, onReveal, onRotate, onRevoke, onDelete, onReload }) => {
  if (keys.length === 0) {
    return <div className="bg-white rounded-lg shadow border border-gray-200 p-8 text-center text-sm text-gray-500">No API keys yet. Click <strong>+ Create API Key</strong> to issue one.</div>;
  }
  return (
    <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 text-[10px] uppercase text-gray-500">
          <tr>
            <th className="text-left px-4 py-2">Name</th>
            <th className="text-left px-4 py-2">Prefix</th>
            <th className="text-left px-4 py-2">Scope</th>
            <th className="text-left px-4 py-2">Created</th>
            <th className="text-left px-4 py-2">Last used</th>
            <th className="text-left px-4 py-2">Requests</th>
            <th className="text-left px-4 py-2">Status</th>
            <th className="text-right px-4 py-2">Actions</th>
          </tr>
        </thead>
        <tbody>
          {keys.map(k => {
            const active = k.isActive === 1 || k.isActive === true;
            const scope = k.allowedEntities === '*' ? 'All entities' : String(k.allowedEntities || '').split(',').filter(Boolean).join(', ') || '—';
            return (
              <tr key={k.id} className={`border-t border-gray-100 ${!active ? 'bg-gray-50 opacity-70' : ''}`}>
                <td className="px-4 py-2 font-medium text-gray-900">{k.name}{k.notes && <div className="text-xs text-gray-500 font-normal italic">{k.notes}</div>}</td>
                <td className="px-4 py-2 font-mono text-xs text-gray-500">
                  <span className="select-all">{k.keyPrefix}</span>…
                  {k.rotatedAt && <div className="text-[10px] text-indigo-500 font-sans">rotated {new Date(k.rotatedAt.replace ? k.rotatedAt.replace(' ', 'T') + 'Z' : k.rotatedAt).toLocaleDateString()}{k.rotatedBy ? ` by ${k.rotatedBy}` : ''}</div>}
                  {k.lastRevealedAt && <div className="text-[10px] text-gray-400 font-sans">viewed {new Date(k.lastRevealedAt.replace ? k.lastRevealedAt.replace(' ', 'T') + 'Z' : k.lastRevealedAt).toLocaleDateString()}{k.lastRevealedBy ? ` by ${k.lastRevealedBy}` : ''}</div>}
                </td>
                <td className="px-4 py-2 text-xs text-gray-700 max-w-xs truncate" title={scope}>{scope}</td>
                <td className="px-4 py-2 text-xs text-gray-500">{k.createdAt ? new Date(k.createdAt.replace ? k.createdAt.replace(' ', 'T') + 'Z' : k.createdAt).toLocaleDateString() : '—'}<div className="text-[10px] text-gray-400">{k.createdBy}</div></td>
                <td className="px-4 py-2 text-xs text-gray-500">{k.lastUsedAt ? new Date(k.lastUsedAt.replace ? k.lastUsedAt.replace(' ', 'T') + 'Z' : k.lastUsedAt).toLocaleString() : <em className="text-gray-400 not-italic">never</em>}</td>
                <td className="px-4 py-2 text-xs font-mono text-gray-700">{(k.requestCount || 0).toLocaleString()}</td>
                <td className="px-4 py-2">
                  {active
                    ? <span className="px-2 py-0.5 text-[10px] font-semibold rounded bg-emerald-100 text-emerald-700">ACTIVE</span>
                    : <span className="px-2 py-0.5 text-[10px] font-semibold rounded bg-rose-100 text-rose-700">REVOKED</span>}
                </td>
                <td className="px-4 py-2 text-right whitespace-nowrap">
                  {(() => {
                    const canReveal = k.canReveal === 1 || k.canReveal === true;
                    const busy = busyKeyId === k.id;
                    return (
                      <>
                        {canReveal ? (
                          <button onClick={() => onReveal(k)} disabled={busy}
                            className="text-xs text-cyan-700 font-medium hover:underline mr-2 disabled:opacity-40">
                            {busy ? '…' : 'View'}
                          </button>
                        ) : active ? (
                          // Not a dead end: this key predates stored retrieval, and rotating
                          // is the only way to get a viewable one. Offer that directly.
                          <button onClick={() => onRotate(k)} disabled={busy}
                            className="text-xs text-cyan-700 font-medium hover:underline mr-2 disabled:opacity-40"
                            title="Only a hash exists for this key, so it can't be shown. Rotate issues a viewable replacement and invalidates the current secret.">
                            {busy ? '…' : 'Rotate to view'}
                          </button>
                        ) : (
                          <span className="text-xs text-gray-300 mr-2 cursor-help"
                            title="Revoked keys cannot be viewed or rotated.">View</span>
                        )}
                        {active && (
                          <button onClick={() => onRotate(k)} disabled={busy}
                            className="text-xs text-indigo-600 hover:underline mr-2 disabled:opacity-40">Rotate</button>
                        )}
                        {active && <button onClick={() => onRevoke(k)} className="text-xs text-amber-600 hover:underline mr-2">Revoke</button>}
                        <button onClick={() => onDelete(k)} className="text-xs text-rose-600 hover:underline">Delete</button>
                      </>
                    );
                  })()}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const DataFeedSchemaPanel = ({ schema, apiBase }) => {
  const entities = schema?.entities || [];
  if (entities.length === 0) return <div className="text-sm text-gray-400">Loading schema…</div>;
  const base = apiBase || (typeof window !== 'undefined' ? `${window.location.origin}/api/datafeed` : '/api/datafeed');
  return (
    <div className="space-y-2">
      <div className="text-xs text-gray-600 mb-2">
        <strong>Pagination:</strong> pass <code className="bg-gray-100 px-1 rounded">?since=&lt;ISO timestamp&gt;</code> to fetch only rows updated after that time. Use the <code className="bg-gray-100 px-1 rounded">nextSince</code> field of the previous response as your next <code>?since</code>. Default limit 500, max 1000.
      </div>
      {entities.map(e => (
        <details key={e.entity} className="bg-white rounded-lg shadow border border-gray-200">
          <summary className="cursor-pointer px-4 py-3 flex items-center gap-3">
            <span className="font-mono font-semibold text-cyan-700">{e.entity}</span>
            <span className="text-xs text-gray-600 flex-1">{e.description}</span>
            <span className="text-[10px] text-gray-400">updated column: <code className="font-mono">{e.updatedColumn}</code></span>
          </summary>
          <div className="px-4 py-3 border-t border-gray-100 space-y-2 text-xs">
            <div>
              <div className="font-semibold text-gray-700 mb-1">Endpoint</div>
              <code className="font-mono bg-gray-50 px-2 py-1 rounded border border-gray-100 block select-all">GET {base}/{e.entity}?since=2026-01-01T00:00:00Z&amp;limit=500</code>
            </div>
            <div>
              <div className="font-semibold text-gray-700 mb-1">curl example</div>
              <pre className="font-mono bg-gray-900 text-emerald-200 px-3 py-2 rounded text-[11px] whitespace-pre-wrap overflow-x-auto">
{`curl -H "X-API-Key: $ORBIT_DF_KEY" \\
  "${base}/${e.entity}?since=2026-01-01T00:00:00Z&limit=500"`}
              </pre>
            </div>
            <div>
              <div className="font-semibold text-gray-700 mb-1">Response shape</div>
              <pre className="font-mono bg-gray-50 border border-gray-100 rounded px-3 py-2 text-[11px] whitespace-pre-wrap">
{`{
  "entity": "${e.entity}",
  "count": 500,
  "hasMore": true,
  "nextSince": "2026-03-15T14:22:01Z",
  "updatedColumn": "${e.updatedColumn}",
  "rows": [ /* row objects */ ]
}`}
              </pre>
            </div>
          </div>
        </details>
      ))}
    </div>
  );
};

// Robust timestamp parser — handles both SQLite ('2026-07-06 14:32:01') and ISO
// ('2026-07-06T14:32:01.000Z') formats. Returns '—' on invalid instead of "Invalid Date".
const dfFmtTs = (s) => {
  if (!s) return '—';
  const str = String(s);
  const iso = str.includes('T') ? str : str.replace(' ', 'T') + 'Z';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '—' : d.toLocaleString();
};

// Short relative-time helper for the access log — "just now / 2m ago / 3h ago / 5d ago"
const dfFmtRelative = (s) => {
  if (!s) return '';
  const str = String(s);
  const iso = str.includes('T') ? str : str.replace(' ', 'T') + 'Z';
  const t = new Date(iso).getTime();
  if (!isFinite(t)) return '';
  const diff = Math.max(0, Date.now() - t);
  const sec = Math.floor(diff / 1000);
  if (sec < 10) return 'just now';
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  return `${day}d ago`;
};

const DF_STATUS_META = {
  200: { bg: 'bg-emerald-100', color: 'text-emerald-700', label: 'OK' },
  400: { bg: 'bg-amber-100', color: 'text-amber-700', label: 'Bad Request' },
  401: { bg: 'bg-rose-100', color: 'text-rose-700', label: 'Unauthorized' },
  403: { bg: 'bg-rose-100', color: 'text-rose-700', label: 'Forbidden' },
  404: { bg: 'bg-amber-100', color: 'text-amber-700', label: 'Not Found' },
  429: { bg: 'bg-amber-100', color: 'text-amber-700', label: 'Rate Limited' },
  500: { bg: 'bg-rose-100', color: 'text-rose-700', label: 'Server Error' },
};

const DataFeedAccessLogPanel = ({ log, onRefresh }) => {
  const [autoRefresh, setAutoRefresh] = React.useState(false);
  const [filterKey, setFilterKey] = React.useState('');
  const [filterStatus, setFilterStatus] = React.useState('');
  const [expanded, setExpanded] = React.useState(null);

  // Auto-refresh — poll every 15s while enabled. Cleans up on toggle or unmount.
  React.useEffect(() => {
    if (!autoRefresh) return;
    const iv = setInterval(() => { onRefresh?.(); }, 15000);
    return () => clearInterval(iv);
  }, [autoRefresh, onRefresh]);

  // Unique keys/entities/statuses for filter dropdowns
  const uniqueKeys = React.useMemo(() => {
    const s = new Map();
    for (const l of log) {
      const k = l.keyName || `(invalid: ${l.keyPrefix || 'unknown'}…)`;
      if (!s.has(k)) s.set(k, l.keyPrefix || '');
    }
    return Array.from(s.entries()); // [name, prefix]
  }, [log]);
  const uniqueStatuses = React.useMemo(() => Array.from(new Set(log.map(l => l.statusCode))).sort((a, b) => a - b), [log]);

  const filtered = log.filter(l => {
    if (filterKey && (l.keyName || `(invalid: ${l.keyPrefix || 'unknown'}…)`) !== filterKey) return false;
    if (filterStatus && String(l.statusCode) !== filterStatus) return false;
    return true;
  });

  // Rollup counts by status for the header — instant sense of traffic health
  const counts = { ok: 0, auth: 0, other: 0 };
  for (const l of log) {
    if (l.statusCode === 200) counts.ok++;
    else if (l.statusCode === 401 || l.statusCode === 403) counts.auth++;
    else counts.other++;
  }

  return (
    <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
      <div className="px-4 py-2 border-b border-gray-100 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3 text-xs">
          <span className="text-gray-500">Most recent <strong className="text-gray-900">{log.length}</strong></span>
          {log.length > 0 && (
            <>
              <span className="text-emerald-700">✓ {counts.ok} OK</span>
              {counts.auth > 0 && <span className="text-rose-700">🔒 {counts.auth} auth failures</span>}
              {counts.other > 0 && <span className="text-amber-700">⚠ {counts.other} other</span>}
            </>
          )}
        </div>
        <div className="flex items-center gap-2">
          {uniqueKeys.length > 1 && (
            <select value={filterKey} onChange={e => setFilterKey(e.target.value)} className="text-xs border border-gray-300 rounded px-2 py-1">
              <option value="">All keys</option>
              {uniqueKeys.map(([name]) => <option key={name} value={name}>{name}</option>)}
            </select>
          )}
          {uniqueStatuses.length > 1 && (
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="text-xs border border-gray-300 rounded px-2 py-1">
              <option value="">All statuses</option>
              {uniqueStatuses.map(s => <option key={s} value={s}>{s} — {DF_STATUS_META[s]?.label || 'Other'}</option>)}
            </select>
          )}
          <label className="text-xs flex items-center gap-1 cursor-pointer text-gray-600">
            <input type="checkbox" checked={autoRefresh} onChange={e => setAutoRefresh(e.target.checked)} />
            Auto-refresh
          </label>
          <button onClick={onRefresh} className="text-xs text-blue-600 hover:underline">↻ Refresh</button>
        </div>
      </div>
      {filtered.length === 0 ? (
        <div className="p-8 text-center">
          <div className="text-sm text-gray-400 mb-1">{log.length === 0 ? 'No requests yet.' : 'No requests match your filters.'}</div>
          {log.length === 0 && (
            <div className="text-xs text-gray-400 max-w-md mx-auto">
              Once external systems start hitting <code className="bg-gray-100 px-1 rounded font-mono">/api/datafeed/*</code>, every request appears here — including invalid-key attempts (great for spotting misconfigured clients or unauthorized probes).
            </div>
          )}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-[10px] uppercase text-gray-500">
              <tr>
                <th className="text-left px-3 py-2 w-8"></th>
                <th className="text-left px-3 py-2">Time</th>
                <th className="text-left px-3 py-2">Key</th>
                <th className="text-left px-3 py-2">Entity</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-right px-3 py-2">Rows</th>
                <th className="text-right px-3 py-2">Duration</th>
                <th className="text-left px-3 py-2">IP</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(l => {
                const meta = DF_STATUS_META[l.statusCode] || { bg: 'bg-gray-100', color: 'text-gray-700', label: 'Other' };
                const isOpen = expanded === l.id;
                return (
                  <React.Fragment key={l.id}>
                    <tr className={`border-t border-gray-100 hover:bg-gray-50 cursor-pointer ${l.statusCode >= 400 ? 'bg-rose-50/20' : ''}`} onClick={() => setExpanded(isOpen ? null : l.id)}>
                      <td className="px-3 py-2 text-gray-400 text-xs">{isOpen ? '▾' : '▸'}</td>
                      <td className="px-3 py-2 text-xs text-gray-600" title={dfFmtTs(l.createdAt)}>
                        <div>{dfFmtRelative(l.createdAt)}</div>
                        <div className="text-[10px] text-gray-400">{dfFmtTs(l.createdAt)}</div>
                      </td>
                      <td className="px-3 py-2 text-xs">
                        <div>{l.keyName || <em className="text-rose-500 not-italic">(invalid key)</em>}</div>
                        <div className="text-[10px] font-mono text-gray-400">{l.keyPrefix ? `${l.keyPrefix}…` : '—'}</div>
                      </td>
                      <td className="px-3 py-2 font-mono text-xs text-cyan-700">{l.entity || <span className="text-gray-400">—</span>}</td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <span className={`px-1.5 py-0.5 text-[10px] font-semibold rounded font-mono ${meta.bg} ${meta.color}`}>{l.statusCode}</span>
                        <span className={`ml-1 text-[10px] ${meta.color}`}>{meta.label}</span>
                      </td>
                      <td className="px-3 py-2 font-mono text-xs text-right">{(l.rowCount || 0).toLocaleString()}</td>
                      <td className="px-3 py-2 font-mono text-xs text-gray-500 text-right">{l.durationMs != null ? `${l.durationMs}ms` : '—'}</td>
                      <td className="px-3 py-2 font-mono text-[10px] text-gray-400">{l.ipAddress || '—'}</td>
                    </tr>
                    {isOpen && (
                      <tr className="border-t border-gray-100 bg-blue-50/30">
                        <td></td>
                        <td colSpan={7} className="px-3 py-3">
                          <div className="grid grid-cols-2 gap-3 text-xs">
                            <div>
                              <div className="text-[10px] uppercase font-bold text-gray-500 mb-0.5">Since (incremental cursor)</div>
                              <code className="font-mono bg-white px-2 py-1 rounded border border-gray-100 block break-all">{l.since || <em className="text-gray-400 not-italic">(none — full pull)</em>}</code>
                            </div>
                            <div>
                              <div className="text-[10px] uppercase font-bold text-gray-500 mb-0.5">Key id</div>
                              <code className="font-mono text-gray-700">{l.keyId ?? <em className="text-rose-500 not-italic">(no matching key row)</em>}</code>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const DataFeedCreateKeyModal = ({ entities, onClose, onCreated }) => {
  const [form, setForm] = React.useState({ name: '', notes: '', scope: 'all', selectedEntities: [] });
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState('');
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setError('Name is required'); return; }
    setSaving(true);
    const allowedEntities = form.scope === 'all' ? '*' : form.selectedEntities;
    if (form.scope === 'restricted' && (!allowedEntities || allowedEntities.length === 0)) {
      setSaving(false); setError('Pick at least one entity, or switch to "All entities"'); return;
    }
    const r = await ModulesModel.createDataFeedKey({ name: form.name.trim(), notes: form.notes.trim(), allowedEntities });
    setSaving(false);
    if (r?.success === false || !r?.key) { setError(r?.error || 'Failed to create key'); return; }
    onCreated(r);
  };
  const toggleEntity = (e) => setForm(s => ({
    ...s,
    selectedEntities: s.selectedEntities.includes(e) ? s.selectedEntities.filter(x => x !== e) : [...s.selectedEntities, e],
  }));
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <form onSubmit={submit} className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="px-5 py-3 border-b flex justify-between items-center">
          <h3 className="text-lg font-semibold">Create DataFeed API Key</h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Name <span className="text-rose-500">*</span></label>
            <input type="text" value={form.name} onChange={e => setForm(s => ({ ...s, name: e.target.value }))} placeholder="Snowflake sync — production" className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Notes</label>
            <input type="text" value={form.notes} onChange={e => setForm(s => ({ ...s, notes: e.target.value }))} placeholder="Used by nightly ETL job in Airflow" className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Scope</label>
            <div className="space-y-1">
              <label className="flex items-center gap-2 text-sm">
                <input type="radio" checked={form.scope === 'all'} onChange={() => setForm(s => ({ ...s, scope: 'all' }))} />
                <span>All entities (unrestricted)</span>
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="radio" checked={form.scope === 'restricted'} onChange={() => setForm(s => ({ ...s, scope: 'restricted' }))} />
                <span>Restrict to specific entities</span>
              </label>
            </div>
            {form.scope === 'restricted' && (
              <div className="mt-2 max-h-56 overflow-y-auto border border-gray-200 rounded p-2 space-y-1">
                {entities.map(e => (
                  <label key={e.entity} className="flex items-center gap-2 text-xs hover:bg-gray-50 px-1 py-0.5 rounded cursor-pointer">
                    <input type="checkbox" checked={form.selectedEntities.includes(e.entity)} onChange={() => toggleEntity(e.entity)} />
                    <span className="font-mono">{e.entity}</span>
                    <span className="text-gray-500 truncate">{e.description}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
          {error && <div className="text-xs text-rose-600 font-medium">{error}</div>}
        </div>
        <div className="px-5 py-3 border-t flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm text-gray-600">Cancel</button>
          <button type="submit" disabled={saving} className="px-3 py-1.5 text-sm bg-cyan-700 text-white rounded hover:bg-cyan-800 disabled:opacity-50">{saving ? 'Creating…' : 'Create Key'}</button>
        </div>
      </form>
    </div>
  );
};

// Shows a full API key. `mode` drives the copy only — the key itself is handled the same
// way in all three cases. 'create'/'rotate' are show-once unless retrievable storage is
// on; 'reveal' means it came back out of storage, so no "last chance" warning is honest.
const DataFeedShowKeyModal = ({ secret, mode = 'create', onClose }) => {
  const [copied, setCopied] = React.useState(false);
  const [visible, setVisible] = React.useState(mode !== 'reveal');
  const copy = async () => {
    try { await navigator.clipboard.writeText(secret.key); setCopied(true); setTimeout(() => setCopied(false), 2000); }
    catch { alert('Copy failed — please select and copy manually.'); }
  };
  const storable = secret.canReveal === true || secret.canReveal === 1;
  const title = mode === 'reveal' ? '🔑 API Key' : mode === 'rotate' ? '🔄 API Key Rotated' : '🔑 New API Key Created';
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
        <div className="px-5 py-3 border-b flex items-center justify-between">
          <h3 className="text-lg font-semibold">{title}</h3>
          {secret.name && <span className="text-xs text-gray-500 truncate ml-3">{secret.name}</span>}
        </div>
        <div className="p-5 space-y-3">
          {mode === 'reveal' ? (
            <div className="bg-cyan-50 border border-cyan-300 rounded p-3 text-sm text-cyan-900">
              This is a live credential. Anyone holding it can read every entity in this key's scope — treat it like a password, and <strong>Rotate</strong> if you believe it leaked. This view is recorded in the audit log.
            </div>
          ) : (
            <div className="bg-amber-50 border border-amber-300 rounded p-3 text-sm text-amber-900">
              {mode === 'rotate' && <><strong>⚠ The previous secret no longer works.</strong> Update every external system using this key or it will start getting 401 responses.<br /></>}
              {storable
                ? <>You can view this key again later from the API Keys tab.</>
                : <><strong>⚠ Save this key now.</strong> This is the only time it will be shown — Orbit keeps only a hash, so the plain text is gone once you close this dialog.</>}
            </div>
          )}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">API Key</label>
            <div className="flex gap-2">
              <input readOnly type={visible ? 'text' : 'password'} value={secret.key}
                className="flex-1 border border-gray-300 rounded px-3 py-2 text-sm font-mono select-all"
                onClick={e => e.target.select()} />
              <button onClick={() => setVisible(v => !v)} className="px-3 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50 whitespace-nowrap">
                {visible ? 'Hide' : 'Show'}
              </button>
              <button onClick={copy} className="px-3 py-2 text-sm bg-cyan-700 text-white rounded hover:bg-cyan-800 whitespace-nowrap">{copied ? '✓ Copied' : 'Copy'}</button>
            </div>
          </div>
          <div className="text-xs text-gray-600">
            <strong>How to use:</strong> Include this as an HTTP header on every request:<br />
            <code className="bg-gray-100 px-1 py-0.5 rounded font-mono block mt-1">X-API-Key: {secret.prefix}…</code>
          </div>
        </div>
        <div className="px-5 py-3 border-t flex justify-end">
          <button onClick={onClose} className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700">
            {mode === 'reveal' ? 'Close' : "I've saved it — Close"}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// APM — SOC 2 REPORTING PIPELINE
// Landing page: list of reports + create button.
// Detail view: per-control cards with evidence list, AI-generated narrative,
// and Ingest / Generate / Regenerate / Approve actions.
// ============================================================================

const APM_STATUS_COLORS = {
  Draft:     { bg: '#f3f4f6', color: '#6b7280', border: '#d1d5db' },
  Ingested:  { bg: '#dbeafe', color: '#1e40af', border: '#93c5fd' },
  Reviewing: { bg: '#fef3c7', color: '#92400e', border: '#fcd34d' },
  Published: { bg: '#d1fae5', color: '#065f46', border: '#6ee7b7' },
};

const ApmModule = ({ user }) => {
  const [reports, setReports] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [selectedId, setSelectedId] = React.useState(null);
  const [showNew, setShowNew] = React.useState(false);
  const load = React.useCallback(async () => {
    setLoading(true);
    const r = await ModulesModel.listSoc2Reports();
    setReports(r?.reports || []);
    setLoading(false);
  }, []);
  React.useEffect(() => { load(); }, [load]);

  if (selectedId) {
    return <ApmSoc2ReportDetail
      reportId={selectedId}
      user={user}
      onBack={() => { setSelectedId(null); load(); }}
      onRefresh={load}
    />;
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">APM — Audit Program Management</h1>
          <p className="text-gray-600 mt-1 text-sm">Ingest audit data and use the Claude API to draft SOC 2 report narratives. Configure <code className="text-xs bg-gray-100 px-1 rounded">ANTHROPIC_API_KEY</code> in Cloudflare Pages secrets to enable AI generation.</p>
        </div>
        <button onClick={() => setShowNew(true)} className="px-4 py-2 bg-blue-700 text-white text-sm rounded-lg hover:bg-blue-800 font-medium">+ New SOC 2 Report</button>
      </div>

      {loading ? (
        <div className="text-sm text-gray-400">Loading reports…</div>
      ) : reports.length === 0 ? (
        <div className="bg-white rounded-lg shadow border border-gray-200 p-8 text-center text-sm text-gray-500">
          No SOC 2 reports yet. Click <strong>+ New SOC 2 Report</strong> to create the first one.
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-[10px] uppercase text-gray-500">
              <tr>
                <th className="text-left px-4 py-2">Code</th>
                <th className="text-left px-4 py-2">Title</th>
                <th className="text-left px-4 py-2">Type</th>
                <th className="text-left px-4 py-2">Period</th>
                <th className="text-left px-4 py-2">Controls</th>
                <th className="text-left px-4 py-2">Evidence</th>
                <th className="text-left px-4 py-2">Narratives</th>
                <th className="text-left px-4 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {reports.map(r => {
                const meta = APM_STATUS_COLORS[r.status] || APM_STATUS_COLORS.Draft;
                return (
                  <tr key={r.id} className="border-t border-gray-100 hover:bg-blue-50/40 cursor-pointer" onClick={() => setSelectedId(r.id)}>
                    <td className="px-4 py-2 font-mono text-xs text-gray-500">{r.reportCode}</td>
                    <td className="px-4 py-2 font-medium text-gray-900">{r.title}</td>
                    <td className="px-4 py-2 text-gray-600">{r.reportType}</td>
                    <td className="px-4 py-2 text-xs text-gray-500">{r.periodStart} → {r.periodEnd}</td>
                    <td className="px-4 py-2 text-gray-700">{r.controlsInScope}</td>
                    <td className="px-4 py-2 text-gray-700">{r.evidenceCount}</td>
                    <td className="px-4 py-2 text-gray-700">{r.narrativeCount}</td>
                    <td className="px-4 py-2">
                      <span className="px-2 py-0.5 text-[10px] font-semibold rounded" style={{ background: meta.bg, color: meta.color }}>{r.status}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {showNew && <ApmSoc2CreateModal onClose={() => setShowNew(false)} onSave={(id) => { setShowNew(false); load(); setSelectedId(id); }} />}
    </div>
  );
};

const ApmSoc2CreateModal = ({ onClose, onSave }) => {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = React.useState({
    title: '',
    reportType: 'Type II',
    periodStart: new Date(new Date().setMonth(new Date().getMonth() - 3)).toISOString().slice(0, 10),
    periodEnd: today,
    notes: '',
  });
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState('');
  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { setError('Title is required'); return; }
    setSaving(true);
    const r = await ModulesModel.createSoc2Report(form);
    setSaving(false);
    if (r?.success === false || !r?.id) { setError(r?.error || 'Failed'); return; }
    onSave(r.id);
  };
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <form onSubmit={submit} className="bg-white rounded-lg shadow-xl max-w-lg w-full">
        <div className="px-5 py-3 border-b flex justify-between items-center">
          <h3 className="text-lg font-semibold">New SOC 2 Report</h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Report title <span className="text-rose-500">*</span></label>
            <input type="text" value={form.title} onChange={e => setForm(s => ({ ...s, title: e.target.value }))} placeholder="Q3 2026 SOC 2 Type II" className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Report type</label>
              <select value={form.reportType} onChange={e => setForm(s => ({ ...s, reportType: e.target.value }))} className="w-full border border-gray-300 rounded px-3 py-2 text-sm">
                <option>Type I</option>
                <option>Type II</option>
                <option>Readiness</option>
              </select>
            </div>
            <div />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Period start</label>
              <input type="date" value={form.periodStart} onChange={e => setForm(s => ({ ...s, periodStart: e.target.value }))} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Period end</label>
              <input type="date" value={form.periodEnd} onChange={e => setForm(s => ({ ...s, periodEnd: e.target.value }))} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Notes</label>
            <textarea rows={2} value={form.notes} onChange={e => setForm(s => ({ ...s, notes: e.target.value }))} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
          </div>
          <p className="text-xs text-gray-500">Every active SOC 2 control is included in scope by default — you can toggle individual controls off from the report detail view.</p>
          {error && <div className="text-xs text-rose-600 font-medium">{error}</div>}
        </div>
        <div className="px-5 py-3 border-t flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm text-gray-600">Cancel</button>
          <button type="submit" disabled={saving} className="px-3 py-1.5 text-sm bg-blue-700 text-white rounded hover:bg-blue-800 disabled:opacity-50">{saving ? 'Creating…' : 'Create Report'}</button>
        </div>
      </form>
    </div>
  );
};

const ApmSoc2ReportDetail = ({ reportId, user, onBack, onRefresh }) => {
  const [report, setReport] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [ingesting, setIngesting] = React.useState(false);
  const [generatingFor, setGeneratingFor] = React.useState(null);
  const [expandedControl, setExpandedControl] = React.useState(null);
  const [evidenceCache, setEvidenceCache] = React.useState({});
  const load = React.useCallback(async () => {
    setLoading(true);
    const r = await ModulesModel.getSoc2Report(reportId);
    setReport(r);
    setLoading(false);
  }, [reportId]);
  React.useEffect(() => { load(); }, [load]);

  const handleIngest = async () => {
    setIngesting(true);
    const r = await ModulesModel.ingestSoc2AuditData(reportId);
    setIngesting(false);
    if (r?.success === false) { alert(r.error || 'Ingest failed'); return; }
    // Build a rich message so admins can see exactly what happened. Zero-result outcomes
    // are the confusing ones — surface the diagnostic hint from the backend inline.
    let msg = `Ingested ${r.evidenceCreated || 0} new evidence rows from ${r.eventsScanned || 0} audit events across ${r.controls || 0} controls.`;
    if (r.perControlHits && Object.keys(r.perControlHits).length > 0) {
      const hits = Object.entries(r.perControlHits).map(([code, n]) => `${code}: ${n}`).join(', ');
      msg += `\n\nBy control: ${hits}`;
    }
    if (r.diagnostics) {
      msg += `\n\n⚠ ${r.diagnostics.reason}\n${r.diagnostics.hint || ''}`;
      if (r.diagnostics.earliestAuditEvent) {
        msg += `\n\nAudit log spans: ${r.diagnostics.earliestAuditEvent} → ${r.diagnostics.latestAuditEvent} (${r.diagnostics.auditEventsInDb} events total)`;
      }
      if (r.diagnostics.uniqueActionsSeen) {
        msg += `\n\nActions seen in period: ${r.diagnostics.uniqueActionsSeen.join(', ')}`;
        msg += `\nModules seen in period: ${r.diagnostics.uniqueModulesSeen.join(', ')}`;
      }
    }
    alert(msg);
    await load();
    onRefresh?.();
  };
  const handleGenerate = async (controlId) => {
    setGeneratingFor(controlId);
    const r = await ModulesModel.generateSoc2Narrative(reportId, controlId);
    setGeneratingFor(null);
    if (r?.success === false) { alert(r.error || 'Generation failed'); return; }
    await load();
  };
  const handleApprove = async (narrativeId) => {
    const r = await ModulesModel.updateSoc2Narrative(narrativeId, { status: 'approved' });
    if (r?.success === false) { alert(r.error || 'Failed'); return; }
    await load();
  };
  const toggleExpand = async (controlId) => {
    if (expandedControl === controlId) { setExpandedControl(null); return; }
    setExpandedControl(controlId);
    if (!evidenceCache[controlId]) {
      const r = await ModulesModel.listSoc2Evidence(reportId, controlId);
      setEvidenceCache(c => ({ ...c, [controlId]: r?.evidence || [] }));
    }
  };

  if (loading) return <div className="max-w-6xl mx-auto px-4 py-6 text-sm text-gray-500">Loading report…</div>;
  if (!report) return <div className="max-w-6xl mx-auto px-4 py-6 text-sm text-gray-500">Report not found. <button onClick={onBack} className="text-blue-600 underline">Back</button></div>;

  const meta = APM_STATUS_COLORS[report.status] || APM_STATUS_COLORS.Draft;
  const controls = report.controls || [];
  const controlsWithNarrative = controls.filter(c => c.latestNarrativeId).length;
  const controlsApproved = controls.filter(c => c.latestNarrativeStatus === 'approved').length;

  return (
    <div className="max-w-6xl mx-auto px-4 py-6">
      <button onClick={onBack} className="text-sm text-blue-600 hover:underline mb-3">← Back to APM</button>
      <div className="bg-white rounded-lg shadow border border-gray-200 p-6 mb-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-xs font-mono text-gray-400">{report.reportCode}</div>
            <h1 className="text-2xl font-bold text-gray-900">{report.title}</h1>
            <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
              <span className="px-2 py-0.5 font-semibold rounded" style={{ background: meta.bg, color: meta.color }}>{report.status}</span>
              <span className="text-gray-500">{report.reportType}</span>
              <span className="text-gray-500">·</span>
              <span className="text-gray-700">{report.periodStart} → {report.periodEnd}</span>
              <span className="text-gray-500">·</span>
              <span className="text-gray-700">{controls.length} controls · {controlsWithNarrative} narratives drafted · {controlsApproved} approved</span>
            </div>
            {report.notes && <div className="text-sm text-gray-600 mt-3 italic">"{report.notes}"</div>}
          </div>
          <div className="flex gap-2 shrink-0 print:hidden">
            <button onClick={handleIngest} disabled={ingesting} className="px-3 py-1.5 text-sm border border-blue-300 text-blue-700 rounded hover:bg-blue-50 disabled:opacity-50">
              {ingesting ? 'Ingesting…' : '📥 Ingest Audit Data'}
            </button>
            <button onClick={() => window.print()} className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50">🖨 Print / Save PDF</button>
          </div>
        </div>
      </div>

      <div className="bg-blue-50/30 border border-blue-100 rounded-lg p-3 mb-4 text-xs text-blue-900">
        <strong>How this works:</strong> Click <strong>Ingest Audit Data</strong> to pull relevant events from Orbit's audit log into each control's evidence. Then click <strong>✨ Generate</strong> on any control to have Claude draft a compliance narrative from that evidence. Review, edit if needed, and approve when ready.
      </div>

      <div className="space-y-3">
        {controls.map(c => {
          const isExpanded = expandedControl === c.id;
          const evidence = evidenceCache[c.id] || [];
          const hasNarrative = c.latestNarrativeId != null;
          const isApproved = c.latestNarrativeStatus === 'approved';
          return (
            <div key={c.id} className={`bg-white rounded-lg shadow border ${isApproved ? 'border-emerald-300' : hasNarrative ? 'border-blue-200' : 'border-gray-200'}`}>
              <div className="p-4 cursor-pointer" onClick={() => toggleExpand(c.id)}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs font-bold text-blue-700">{c.code}</span>
                      <span className="text-sm font-semibold text-gray-900">{c.title}</span>
                      {isApproved && <span className="px-2 py-0.5 text-[10px] font-semibold rounded bg-emerald-100 text-emerald-700">✓ APPROVED</span>}
                      {hasNarrative && !isApproved && <span className="px-2 py-0.5 text-[10px] font-semibold rounded bg-blue-100 text-blue-700">DRAFT</span>}
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">{c.category} · <span className="text-gray-700">{c.evidenceCount || 0}</span> evidence entries</div>
                    <div className="text-xs text-gray-600 mt-1 line-clamp-2">{c.description}</div>
                  </div>
                  <div className="flex gap-1.5 shrink-0 print:hidden" onClick={e => e.stopPropagation()}>
                    <button
                      onClick={() => handleGenerate(c.id)}
                      disabled={generatingFor === c.id || (c.evidenceCount || 0) === 0}
                      className="px-3 py-1.5 text-xs bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-40 disabled:cursor-not-allowed"
                      title={(c.evidenceCount || 0) === 0 ? 'Ingest audit data first' : hasNarrative ? 'Regenerate narrative from current evidence' : 'Generate narrative from evidence'}
                    >
                      {generatingFor === c.id ? '✨ Calling Claude…' : hasNarrative ? '✨ Regenerate' : '✨ Generate'}
                    </button>
                    {hasNarrative && !isApproved && (
                      <button onClick={() => handleApprove(c.latestNarrativeId)} className="px-3 py-1.5 text-xs bg-emerald-600 text-white rounded hover:bg-emerald-700">
                        ✓ Approve
                      </button>
                    )}
                    <span className="text-gray-400 text-xs px-2">{isExpanded ? '▾' : '▸'}</span>
                  </div>
                </div>
              </div>
              {isExpanded && (
                <div className="border-t border-gray-100 p-4 space-y-3">
                  {hasNarrative && (
                    <div>
                      <div className="text-[10px] uppercase font-bold text-gray-500 mb-1">
                        AI-Generated Narrative
                        <span className="ml-2 font-normal normal-case text-gray-400">
                          {c.latestNarrativeModel} · {c.latestNarrativeGeneratedAt ? new Date(c.latestNarrativeGeneratedAt.replace ? c.latestNarrativeGeneratedAt.replace(' ', 'T') + 'Z' : c.latestNarrativeGeneratedAt).toLocaleString() : ''}
                          {c.latestNarrativeGeneratedBy ? ` · triggered by ${c.latestNarrativeGeneratedBy}` : ''}
                        </span>
                      </div>
                      <div className="text-sm text-gray-800 whitespace-pre-wrap bg-purple-50/40 border-l-4 border-purple-300 p-3 rounded">{c.latestNarrative}</div>
                    </div>
                  )}
                  <div>
                    <div className="text-[10px] uppercase font-bold text-gray-500 mb-1">Evidence ({evidence.length})</div>
                    {evidence.length === 0 ? (
                      <div className="text-xs text-gray-400 italic">No evidence loaded. Use "Ingest Audit Data" above to pull matching audit events.</div>
                    ) : (
                      <ul className="space-y-1 text-xs max-h-64 overflow-y-auto border border-gray-100 rounded p-2">
                        {evidence.map(e => (
                          <li key={e.id} className="flex items-start gap-2 py-1 border-b border-gray-50 last:border-0">
                            <span className="font-mono text-gray-400 shrink-0">{e.occurredAt ? new Date(e.occurredAt.replace ? e.occurredAt.replace(' ', 'T') + 'Z' : e.occurredAt).toLocaleString() : ''}</span>
                            <span className="text-gray-500 shrink-0">[{e.source}{e.sourceRef ? `:${e.sourceRef}` : ''}]</span>
                            <span className="flex-1 text-gray-700">{e.description}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Deployment volume totals — this month / this year, with a monthly trend for the
// current year. All buckets are Pacific-calendar per the app-wide convention; the
// backend does the UTC->Pacific conversion so nothing here re-derives dates.
const MONTH_ABBR = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const DeploymentsTotalsView = ({ onBack }) => {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [basis, setBasis] = React.useState('raised'); // 'raised' = created, 'deployed' = actually shipped

  React.useEffect(() => {
    (async () => {
      setLoading(true);
      setData(await ModulesModel.getDeploymentVolume());
      setLoading(false);
    })();
  }, []);

  if (loading) return <div className="max-w-7xl mx-auto px-4 py-6 text-sm text-gray-500">Loading totals…</div>;
  if (!data) return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <button onClick={onBack} className="text-sm text-blue-600 hover:text-blue-700 mb-3">← Back to deployments</button>
      <div className="text-sm text-gray-500">Could not load deployment totals.</div>
    </div>
  );

  const month = data.thisMonth?.[basis] || {};
  const year = data.thisYear?.[basis] || {};
  const monthly = data.monthly || [];
  const peak = Math.max(1, ...monthly.map(m => Math.max(m.raised || 0, m.deployed || 0)));
  const monthLabel = MONTH_ABBR[parseInt((data.currentMonth || '').slice(5, 7), 10) - 1] || '';

  const Card = ({ label, value, sub, tone = 'blue' }) => {
    const tones = {
      blue:  { bg: '#eff6ff', border: '#bfdbfe', color: '#1e40af' },
      green: { bg: '#ecfdf5', border: '#a7f3d0', color: '#065f46' },
      slate: { bg: '#f8fafc', border: '#e2e8f0', color: '#334155' },
      amber: { bg: '#fffbeb', border: '#fde68a', color: '#92400e' },
    };
    const t = tones[tone] || tones.blue;
    return (
      <div className="rounded-lg border p-4" style={{ background: t.bg, borderColor: t.border, color: t.color }}>
        <div className="text-[11px] font-semibold uppercase tracking-wide opacity-80">{label}</div>
        <div className="text-3xl font-bold mt-0.5">{value}</div>
        {sub && <div className="text-xs opacity-75 mt-0.5">{sub}</div>}
      </div>
    );
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <button onClick={onBack} className="text-sm text-blue-600 hover:text-blue-700 mb-3">← Back to deployments</button>
      <div className="flex items-start justify-between gap-3 flex-wrap mb-5">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Deployment Totals</h1>
          <p className="text-gray-600 mt-1 text-sm">
            Volume for {monthLabel} {data.currentYear} and year-to-date · Pacific calendar (as of {data.pacificToday})
          </p>
        </div>
        <div className="flex rounded-lg border border-gray-300 overflow-hidden text-sm">
          {[
            { key: 'raised', label: 'Raised', hint: 'Counted by date created' },
            { key: 'deployed', label: 'Deployed', hint: 'Counted by date actually deployed' },
          ].map(b => (
            <button
              key={b.key}
              onClick={() => setBasis(b.key)}
              title={b.hint}
              className={`px-4 py-2 ${basis === b.key ? 'bg-blue-600 text-white font-semibold' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
            >{b.label}</button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <Card label={`This Month (${monthLabel})`} value={month.total || 0} sub={`${month.completed || 0} completed`} tone="blue" />
        <Card label={`This Year (${data.currentYear})`} value={year.total || 0} sub={`${year.completed || 0} completed`} tone="green" />
        <Card label="Manual / Hotfix (YTD)" value={year.manual || 0} sub={`${year.project || 0} from projects`} tone="amber" />
        <Card label="All Time" value={data.allTime || 0} sub="every deployment on record" tone="slate" />
      </div>

      {/* Monthly trend — plain bars, no chart library needed */}
      <div className="bg-white rounded-lg shadow border border-gray-200 p-4 mb-5">
        <div className="text-sm font-semibold text-gray-700 mb-3">Monthly volume · {data.currentYear}</div>
        {monthly.length === 0 ? (
          <div className="text-sm text-gray-400">No deployments recorded this year yet.</div>
        ) : (
          <div className="space-y-1.5">
            {monthly.map(m => {
              const v = basis === 'deployed' ? (m.deployed || 0) : (m.raised || 0);
              const isCurrent = m.month === data.currentMonth;
              return (
                <div key={m.month} className="flex items-center gap-3">
                  <div className={`w-12 text-xs shrink-0 ${isCurrent ? 'font-bold text-blue-700' : 'text-gray-500'}`}>
                    {MONTH_ABBR[parseInt(m.month.slice(5, 7), 10) - 1]}
                  </div>
                  <div className="flex-1 h-5 bg-gray-100 rounded overflow-hidden">
                    <div
                      className={`h-full rounded ${isCurrent ? 'bg-blue-600' : 'bg-blue-400'}`}
                      style={{ width: `${Math.round((v / peak) * 100)}%`, minWidth: v > 0 ? '2px' : 0 }}
                    />
                  </div>
                  <div className={`w-16 text-right text-xs ${isCurrent ? 'font-bold text-blue-700' : 'text-gray-600'}`}>
                    {v}{m.completed > 0 && basis === 'raised' ? <span className="text-gray-400"> · {m.completed}✓</span> : null}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <div className="text-[11px] text-gray-400 mt-3">
          {basis === 'raised'
            ? 'Counted by the date the deployment was created. ✓ = reached Completed.'
            : 'Counted by the date the deployment was actually marked Deployed.'}
        </div>
      </div>

      {(data.byApplication || []).length > 0 && (
        <div className="bg-white rounded-lg shadow border border-gray-200 p-4">
          <div className="text-sm font-semibold text-gray-700 mb-3">By application · {data.currentYear} (raised)</div>
          <div className="space-y-1.5">
            {data.byApplication.map(a => {
              const top = Math.max(1, ...data.byApplication.map(x => x.count));
              return (
                <div key={a.app} className="flex items-center gap-3">
                  <div className="w-40 text-xs text-gray-600 truncate shrink-0" title={a.app}>{a.app}</div>
                  <div className="flex-1 h-4 bg-gray-100 rounded overflow-hidden">
                    <div className="h-full bg-violet-400 rounded" style={{ width: `${Math.round((a.count / top) * 100)}%` }} />
                  </div>
                  <div className="w-10 text-right text-xs text-gray-600">{a.count}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const DeploymentsModule = ({ user, deepLinkDeploymentId, onDeepLinkConsumed }) => {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [search, setSearch] = React.useState('');
  const [filterStatus, setFilterStatus] = React.useState('');
  const [filterApp, setFilterApp] = React.useState('');
  const [filterRequester, setFilterRequester] = React.useState('');
  const [showNew, setShowNew] = React.useState(false);
  const [lookups, setLookups] = React.useState(null);
  const [selectedId, setSelectedId] = React.useState(null);
  // Opens straight to a specific deployment when arriving from a notification click.
  // Reacts to changes (not just mount) so clicking a second, different deployment
  // notification while already on this page still jumps to the new one.
  React.useEffect(() => {
    if (deepLinkDeploymentId) {
      setSelectedId(deepLinkDeploymentId);
      onDeepLinkConsumed?.();
    }
  }, [deepLinkDeploymentId, onDeepLinkConsumed]);
  const [showKpis, setShowKpis] = React.useState(false);
  const [showTotals, setShowTotals] = React.useState(false);
  const [showSettings, setShowSettings] = React.useState(false);
  const role = user?.user_role || user?.role || '';
  const isAdmin = role === 'admin';
  const isManager = role === 'manager' || (typeof role === 'string' && role.endsWith('_manager'));

  const load = React.useCallback(async () => {
    setLoading(true);
    const d = await ModulesModel.getDeployments({ search, status: filterStatus, application: filterApp, requestedBy: filterRequester });
    setData(d);
    setLoading(false);
  }, [search, filterStatus, filterApp, filterRequester]);
  React.useEffect(() => { load(); }, [load]);

  const openNew = async () => {
    if (!lookups) setLookups(await ModulesModel.getDeploymentLookups());
    setShowNew(true);
  };

  if (showKpis) {
    return <DeploymentsKpiView onBack={() => setShowKpis(false)} />;
  }
  if (showTotals) {
    return <DeploymentsTotalsView onBack={() => setShowTotals(false)} />;
  }
  if (showSettings) {
    return <DeploymentsSettingsView onBack={() => setShowSettings(false)} />;
  }
  if (selectedId) {
    return <DeploymentDetailView deploymentId={selectedId} user={user} onBack={() => { setSelectedId(null); load(); }} onRefresh={load} />;
  }

  const stats = data?.stats || {};
  const groupCounts = DPL_CARD_GROUPS.map(g => ({ ...g, count: g.statuses.reduce((sum, s) => sum + (stats[s] || 0), 0) }));
  const active = groupCounts.filter(g => ['In Approval', 'Approved', 'Deployed'].includes(g.label)).reduce((sum, g) => sum + g.count, 0);
  const total = Object.values(stats).reduce((a, b) => a + b, 0);

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Deployments</h1>
          <p className="text-gray-600 mt-1 text-sm">Track workflow / CoreApp / CR deployments with pre-deploy approval, deploy, verify, and completion.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowTotals(true)} className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 font-medium text-gray-700" title="Total deployments this month and year">🗓 Totals</button>
          <button onClick={() => setShowKpis(true)} className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 font-medium text-gray-700">📊 View KPIs</button>
          {isAdmin && (
            <button onClick={() => setShowSettings(true)} className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 font-medium text-gray-700" title="Stage templates + freeze windows">⚙ Settings</button>
          )}
          <button onClick={openNew} className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 font-medium">+ New Deployment</button>
        </div>
      </div>

      {/* Status summary */}
      {total > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2 mb-4">
          {groupCounts.map(({ label, count, color }) => {
            const c = color || { bg: '#f3f4f6', color: '#374151' };
            return (
              <div key={label} className="px-3 py-2 rounded border text-center" style={{ background: c.bg, color: c.color, borderColor: c.color + '40' }}>
                <div className="text-[10px] uppercase font-semibold opacity-80">{label}</div>
                <div className="text-xl font-bold">{count}</div>
              </div>
            );
          })}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow border border-gray-200 p-4 mb-4">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[220px]">
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Search</label>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Code, project, description…" className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Status</label>
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm min-w-[140px]">
              <option value="">All</option>
              {(data?.statuses || []).map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Application</label>
            <select value={filterApp} onChange={e => setFilterApp(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm min-w-[140px]">
              <option value="">All</option>
              {(data?.applications || []).map(a => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Requested By</label>
            <select value={filterRequester} onChange={e => setFilterRequester(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm min-w-[140px]">
              <option value="">All</option>
              {(data?.requesters || []).map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <button onClick={() => { setSearch(''); setFilterStatus(''); setFilterApp(''); setFilterRequester(''); }} className="px-3 py-1.5 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">Reset</button>
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-gray-500">Loading…</div>
      ) : (data?.deployments || []).length === 0 ? (
        <div className="bg-white rounded-lg shadow border border-gray-200 p-8 text-center text-sm text-gray-500">No deployments yet. Click <strong>+ New Deployment</strong> to create the first one.</div>
      ) : (
        <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-[10px] uppercase tracking-wide text-gray-500">
              <tr>
                <th className="p-3 font-semibold">Code</th>
                <th className="p-3 font-semibold">Project</th>
                <th className="p-3 font-semibold">Application</th>
                <th className="p-3 font-semibold">Status</th>
                <th className="p-3 font-semibold">Planned Date</th>
                <th className="p-3 font-semibold">Requested By</th>
                <th className="p-3 font-semibold">Updated</th>
              </tr>
            </thead>
            <tbody>
              {(data?.deployments || []).map(d => (
                <tr key={d.id} onClick={() => setSelectedId(d.id)} className="border-t border-gray-100 hover:bg-blue-50 cursor-pointer">
                  <td className="p-3 font-mono text-xs text-gray-500">{d.deploymentCode}</td>
                  <td className="p-3 break-words">{d.projectName}</td>
                  <td className="p-3 text-gray-600">{d.projectApplication || '—'}</td>
                  <td className="p-3"><DeploymentStatusPill status={d.status} /></td>
                  <td className="p-3 text-gray-600">{d.plannedDeploymentDate || '—'}</td>
                  <td className="p-3 text-gray-600">{d.requestedBy || '—'}</td>
                  <td className="p-3 text-xs text-gray-500">{d.updatedAt ? new Date(d.updatedAt.replace(' ', 'T') + 'Z').toLocaleDateString() : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showNew && (
        <DeploymentCreateModal
          lookups={lookups}
          onClose={() => setShowNew(false)}
          onSave={(id) => { setShowNew(false); if (id) setSelectedId(id); else load(); }}
        />
      )}
    </div>
  );
};

// ============================================================================
// WORKFLOWS — in-house Pipedream-style automation: trigger (manual or an
// inbound webhook) -> ordered chain of steps (http_request, delay, filter,
// transform) -> run history with per-step results.
// ============================================================================

const WORKFLOW_STEP_META = {
  http_request: { label: 'HTTP Request', icon: '🌐', color: '#2563eb', description: 'Call any URL — method, headers, body all support {{templates}}.', defaultConfig: { method: 'POST', url: '', headers: {}, body: '' } },
  delay:        { label: 'Delay',        icon: '⏱️', color: '#d97706', description: 'Pause the run for up to 5 seconds.', defaultConfig: { ms: 1000 } },
  filter:       { label: 'Filter',       icon: '🔎', color: '#db2777', description: 'Stop the run here (not a failure) unless a JS condition is true.', defaultConfig: { condition: 'trigger.status !== undefined' } },
  transform:    { label: 'Transform',    icon: '🔧', color: '#7c3aed', description: 'Reshape data by mapping fields from trigger/prior steps.', defaultConfig: { mappings: [{ from: '', to: '' }] } },
  code:         { label: 'Run Code',     icon: '💻', color: '#4f46e5', description: 'Real JavaScript/TypeScript, sandboxed — no network access.', defaultConfig: { language: 'javascript', code: '// steps, trigger, and console are available.\n// No fetch/await here — use an HTTP Request step for network calls.\nreturn { ok: true };' } },
  respond:      { label: 'HTTP Response', icon: '↩️', color: '#0d9488', description: 'Set the exact HTTP response a webhook trigger returns.', defaultConfig: { status: 200, contentType: 'application/json', body: '{"ok": true}', headers: {} } },
  end:          { label: 'End Workflow', icon: '🏁', color: '#64748b', description: 'Stop the run here, successfully — nothing after this executes.', defaultConfig: { reason: '' } },
  if_else:      { label: 'If/Else',     icon: '🔀', color: '#0891b2', description: 'Branch on a JS condition — true and false each continue independently.', defaultConfig: { condition: 'trigger.status !== undefined', trueNext: 'continue', falseNext: 'continue' } },
};
const WORKFLOW_RUN_STATUS_COLORS = {
  running: { bg: '#dbeafe', color: '#1e40af' },
  success: { bg: '#d1fae5', color: '#065f46' },
  failed:  { bg: '#fee2e2', color: '#991b1b' },
  pending: { bg: '#f3f4f6', color: '#374151' },
  skipped: { bg: '#f3f4f6', color: '#6b7280' },
};
const WorkflowStatusPill = ({ status }) => {
  const c = WORKFLOW_RUN_STATUS_COLORS[status] || { bg: '#f3f4f6', color: '#374151' };
  return <span className="px-2 py-0.5 text-xs font-semibold rounded whitespace-nowrap" style={{ background: c.bg, color: c.color }}>{status || '—'}</span>;
};

// One-line, config-aware description shown on each canvas node (distinct from the
// static per-type `description` in WORKFLOW_STEP_META, which is used by the picker).
function wfStepSummary(step) {
  const c = step.config || {};
  let base;
  switch (step.stepType) {
    case 'http_request': base = `${(c.method || 'POST')} ${c.url || '(no URL set)'}`; break;
    case 'delay': base = `${c.ms ?? 1000}ms`; break;
    case 'filter': base = c.condition || '(no condition)'; break;
    case 'transform': base = `${(c.mappings || []).length} mapping(s)`; break;
    case 'code': base = c.language === 'typescript' ? 'TypeScript' : 'JavaScript'; break;
    case 'respond': base = `${c.status ?? 200} response`; break;
    case 'end': base = c.reason || 'Ends the workflow'; break;
    case 'if_else': base = `if ${c.condition || '(no condition)'}`; break;
    default: base = ''; break;
  }
  // Flag when this step's default fallthrough (to whatever's next in the list) has been
  // explicitly overridden — otherwise two branches that share a step_order boundary can
  // silently re-merge with no visible cue on the node itself.
  if (step.stepType !== 'if_else' && step.stepType !== 'end' && c.__next && c.__next !== 'continue') base += ' ⇥ redirected';
  return base;
}

// Position is purely cosmetic (canvas layout only — execution order is the steps
// array index, same as before), so it rides inside each step's own config under a
// reserved key rather than needing a schema change. The execution engine only ever
// reads the specific fields it expects per step type, so this extra key is inert.
function wfStepPosition(step, index) {
  return step.config?.__position || { x: 40, y: 20 + 140 * (index + 1) };
}

function buildWorkflowNodes(steps, triggerType, selectedIndex, onDeleteIndex) {
  const nodes = [
    { id: 'trigger', type: 'wfTriggerNode', position: { x: 40, y: 20 }, data: { triggerType }, draggable: false, selectable: false },
  ];
  steps.forEach((step, i) => {
    nodes.push({
      id: `step-${i}`,
      type: 'wfStepNode',
      position: wfStepPosition(step, i),
      selected: i === selectedIndex,
      data: {
        stepType: step.stepType,
        name: step.name,
        isEnabled: step.isEnabled,
        summary: wfStepSummary(step),
        onDelete: () => onDeleteIndex(i),
      },
    });
  });
  return nodes;
}

// Each step emits its own outgoing edge(s) — non-branching steps always emit exactly one
// edge to the next array index (identical to the old pure-position logic), while an
// if_else step emits two, one per named handle, each resolved from its config (`'continue'`
// falls through to i+1; a step key jumps to that step, but only forward — a stale/backward
// key after reordering is treated as invalid and falls back to `continue`, same rule the
// engine uses). Jump edges are dashed so a step reachable via ordinary fallthrough AND via
// a distant jump doesn't read as an unexplained tangle.
function buildWorkflowEdges(steps) {
  const keyToIndex = new Map();
  steps.forEach((s, i) => { const k = s.config?.__key; if (k) keyToIndex.set(k, i); });
  const resolveTarget = (fromIndex, spec) => {
    if (spec && spec !== 'continue') {
      const idx = keyToIndex.get(spec);
      if (idx !== undefined && idx > fromIndex) return { index: idx, isJump: true };
    }
    return fromIndex + 1 < steps.length ? { index: fromIndex + 1, isJump: false } : null;
  };
  const edges = [];
  // The trigger's edge is automatic (always trigger -> step-0) — not user-editable.
  if (steps.length > 0) edges.push({ id: 'e-trigger', source: 'trigger', target: 'step-0', type: 'smoothstep', reconnectable: false, selectable: false });
  steps.forEach((step, i) => {
    if (step.stepType === 'if_else') {
      const c = step.config || {};
      [['true', c.trueNext, 'True', 'trueNext'], ['false', c.falseNext, 'False', 'falseNext']].forEach(([handle, spec, label, field]) => {
        const t = resolveTarget(i, spec);
        if (!t) return;
        edges.push({
          id: `e-${i}-${handle}`,
          source: `step-${i}`,
          sourceHandle: handle,
          target: `step-${t.index}`,
          type: 'smoothstep',
          label,
          style: t.isJump ? { strokeDasharray: '5,5' } : undefined,
          data: { sourceIndex: i, field, isJump: t.isJump },
          // Every edge (not just explicit jumps) can be grabbed and dragged to a different
          // target step — including the plain "continue to next" line, which otherwise
          // looked like fixed layout rather than an editable connection.
          reconnectable: 'target',
          // React Flow's default "selected" edge style is a heavy black stroke that
          // overrides the dashed/colored styling above and makes a clicked edge look like
          // an unrelated new connection — clicking still fires onEdgeClick/reconnect just
          // fine without this, so there's no reason to let edges visually select at all.
          selectable: false,
        });
      });
    } else {
      // config.__next generalizes if_else's explicit-target mechanism to every step type —
      // without it, a step always falls through to i+1 regardless of which branch it's
      // visually dragged into, which is exactly what makes two branches silently re-merge
      // if their steps happen to be adjacent in array order.
      const t = resolveTarget(i, step.config?.__next);
      if (t) edges.push({ id: `e-${i}`, source: `step-${i}`, target: `step-${t.index}`, type: 'smoothstep', style: t.isJump ? { strokeDasharray: '5,5' } : undefined, data: { sourceIndex: i, field: '__next', isJump: t.isJump }, reconnectable: 'target', selectable: false });
    }
  });
  return edges;
}

const WORKFLOW_TRIGGER_NODE_META = {
  webhook:  { icon: '🪝', label: 'Webhook' },
  schedule: { icon: '🕐', label: 'Schedule' },
  manual:   { icon: '▶', label: 'Manual' },
};
const WorkflowTriggerNode = ({ data }) => {
  const meta = WORKFLOW_TRIGGER_NODE_META[data.triggerType] || WORKFLOW_TRIGGER_NODE_META.manual;
  return (
    <div className="rounded-lg border border-blue-200 bg-blue-50 shadow-sm w-56 p-2.5">
      <div className="flex items-center gap-1.5">
        <span>{meta.icon}</span>
        <span className="text-sm font-semibold text-blue-900">{meta.label} Trigger</span>
      </div>
      <div className="text-[11px] text-blue-700 mt-0.5">Starts the workflow</div>
      <Handle type="source" position={Position.Bottom} />
    </div>
  );
};

const WorkflowStepNode = ({ data, selected }) => {
  const meta = WORKFLOW_STEP_META[data.stepType] || {};
  return (
    <div className={`rounded-lg border bg-white shadow-sm w-56 overflow-hidden ${selected ? 'border-blue-400 ring-2 ring-blue-100' : 'border-gray-200'} ${data.isEnabled === false ? 'opacity-50' : ''}`}>
      <Handle type="target" position={Position.Top} />
      <div className="h-1" style={{ background: meta.color || '#94a3b8' }} />
      <div className="p-2.5">
        <div className="flex items-center gap-1.5">
          <span>{meta.icon}</span>
          <span className="text-sm font-semibold text-gray-900 truncate flex-1">{data.name || meta.label}</span>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); data.onDelete?.(); }}
            className="text-gray-300 hover:text-rose-600 text-xs shrink-0"
            title="Delete step"
          >✕</button>
        </div>
        <div className="text-[11px] text-gray-500 truncate mt-0.5 font-mono">{data.summary}</div>
      </div>
      {data.stepType === 'if_else' ? (
        <>
          <Handle type="source" id="true" position={Position.Bottom} style={{ left: '30%' }} />
          <Handle type="source" id="false" position={Position.Bottom} style={{ left: '70%' }} />
        </>
      ) : (
        <Handle type="source" position={Position.Bottom} />
      )}
    </div>
  );
};

const WORKFLOW_NODE_TYPES = { wfTriggerNode: WorkflowTriggerNode, wfStepNode: WorkflowStepNode };

// The visual canvas — trigger + one node per step, connected as a strict linear chain
// (matches the execution model exactly: no branching). Dragging repositions a node
// cosmetically only; add/remove/select go through the parent's steps array, same as
// the previous card-list UI, just via canvas interactions instead of list buttons.
//
// Nodes are recomputed from `steps` (the external source of truth) whenever it
// structurally changes, via useNodesState + a syncing effect — the standard React
// Flow pattern for "externally controlled data, but still smooth to drag." Dragging
// itself is handled by React Flow's own internal state in between renders; we only
// report the final position back up to the parent on drop.
const WorkflowCanvas = ({ steps, triggerType, selectedIndex, onSelect, onPositionChange, onDeleteIndex, onAddClick, onConnectSteps, onResetConnection, onReorderSteps }) => {
  const [nodes, setNodes, onNodesChangeInternal] = useNodesState([]);
  const edges = React.useMemo(() => buildWorkflowEdges(steps), [steps]);
  const rfInstanceRef = React.useRef(null);
  const stepCount = steps.length;

  React.useEffect(() => {
    setNodes(buildWorkflowNodes(steps, triggerType, selectedIndex, onDeleteIndex));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [steps, triggerType, selectedIndex]);

  // `fitView` on <ReactFlow> only runs once at mount — without this, newly added steps
  // can end up below the initially-fitted viewport as the chain grows.
  React.useEffect(() => {
    const id = setTimeout(() => rfInstanceRef.current?.fitView({ padding: 0.2, duration: 300 }), 50);
    return () => clearTimeout(id);
  }, [stepCount]);

  const handleNodesChange = (changes) => {
    onNodesChangeInternal(changes);
    for (const change of changes) {
      if (change.type === 'position' && change.position && change.dragging === false && String(change.id).startsWith('step-')) {
        onPositionChange(parseInt(change.id.replace('step-', ''), 10), change.position);
      }
    }
    // Dropping a node is also how you reorder steps, not just reposition them — a step's
    // default "continue" line always goes to whatever's next in execution order, so if
    // dragging a box around didn't also change that order, the lines you see would stop
    // matching what actually runs (exactly the "why is this still connected to that"
    // confusion this replaces). Compute the post-drop layout synchronously via
    // applyNodeChanges rather than reading `nodes` — that state update from
    // onNodesChangeInternal above hasn't committed yet within this same call.
    const dropped = changes.some(c => c.type === 'position' && c.dragging === false && String(c.id).startsWith('step-'));
    if (dropped && onReorderSteps) {
      const updated = applyNodeChanges(changes, nodes);
      const order = updated
        .filter(n => n.id.startsWith('step-'))
        .sort((a, b) => a.position.y - b.position.y)
        .map(n => steps[parseInt(n.id.replace('step-', ''), 10)]?.config?.__key)
        .filter(Boolean);
      onReorderSteps(order);
    }
  };
  const handleNodeClick = (_e, node) => {
    if (node.id === 'trigger') return;
    onSelect(parseInt(node.id.replace('step-', ''), 10));
  };

  // Dragging from a step's handle to another node is an alternative to the config panel's
  // "go to" dropdowns — both write to the exact same config fields, so either one just
  // works with the other. Forward-only, same rule the engine and dropdowns already enforce.
  const isValidConnection = (conn) => {
    if (!conn.source?.startsWith('step-') || !conn.target?.startsWith('step-')) return false;
    const sourceIndex = parseInt(conn.source.replace('step-', ''), 10);
    const targetIndex = parseInt(conn.target.replace('step-', ''), 10);
    return targetIndex > sourceIndex;
  };
  const handleConnect = (conn) => {
    if (!isValidConnection(conn)) return;
    const sourceIndex = parseInt(conn.source.replace('step-', ''), 10);
    const targetIndex = parseInt(conn.target.replace('step-', ''), 10);
    onConnectSteps(sourceIndex, conn.sourceHandle, targetIndex);
  };
  // Only jump edges (dashed) represent an actual override to remove — the plain fallthrough
  // line to i+1 isn't a "connection" the user made, so clicking it does nothing.
  const handleEdgeClick = (_e, edge) => {
    if (!edge.data?.isJump) return;
    onResetConnection(edge.data.sourceIndex, edge.data.field);
  };
  // Grabbing an EXISTING line's endpoint and dragging it to a different step — every edge is
  // reconnectable this way (see buildWorkflowEdges' `reconnectable: 'target'`), not just the
  // ones already representing an override, so a plain "continue to next" line is just as
  // editable as a jump. React Flow hands back the same {source, sourceHandle, target} shape
  // a fresh connection would, so this reuses handleConnect's validation and write path as-is.
  const handleReconnect = (_oldEdge, newConnection) => handleConnect(newConnection);

  return (
    <div className="wf-canvas relative border border-gray-200 rounded-lg overflow-hidden bg-gray-50" style={{ height: 440 }}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={WORKFLOW_NODE_TYPES}
        onNodesChange={handleNodesChange}
        onNodeClick={handleNodeClick}
        onPaneClick={() => onSelect(null)}
        onConnect={handleConnect}
        isValidConnection={isValidConnection}
        onEdgeClick={handleEdgeClick}
        onReconnect={handleReconnect}
        edgesReconnectable
        onInit={(instance) => { rfInstanceRef.current = instance; }}
        deleteKeyCode={null}
        fitView
        minZoom={0.4}
        maxZoom={1.5}
        proOptions={{ hideAttribution: true }}
      >
        <Background gap={16} size={1} color="#e5e7eb" />
        <Controls showInteractive={false} />
      </ReactFlow>
      <button
        type="button"
        onClick={onAddClick}
        className="absolute top-3 right-3 px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-lg shadow hover:bg-blue-700 z-10"
      >
        + Add Step
      </button>
    </div>
  );
};

const WorkflowStepTypePicker = ({ onPick, onClose }) => (
  <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50" onClick={onClose}>
    <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-5" onClick={e => e.stopPropagation()}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Add a step</h3>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">✕</button>
      </div>
      <div className="grid grid-cols-2 gap-2">
        {Object.entries(WORKFLOW_STEP_META).map(([key, meta]) => (
          <button
            key={key} type="button" onClick={() => onPick(key)}
            className="flex items-start gap-2 p-3 border border-gray-200 rounded-lg text-left hover:border-blue-300 hover:bg-blue-50"
          >
            <span className="text-lg">{meta.icon}</span>
            <div>
              <div className="text-sm font-semibold text-gray-900">{meta.label}</div>
              <div className="text-xs text-gray-500">{meta.description}</div>
            </div>
          </button>
        ))}
      </div>
    </div>
  </div>
);

// Slide-in right panel — the per-type config forms are unchanged from the old inline
// card, just re-laid-out for a taller vertical panel instead of a compact card.
const WorkflowStepConfigPanel = ({ step, index, total, steps, onChange, onRemove, onMoveUp, onMoveDown, onTest, testing, testResult, onClose }) => {
  const meta = WORKFLOW_STEP_META[step.stepType] || {};
  const setConfig = (patch) => onChange({ ...step, config: { ...step.config, ...patch } });
  // Steps an if_else branch is allowed to jump to — later array positions only (forward-only,
  // matches the engine's own rule that a backward/stale key falls back to 'continue').
  const laterSteps = (steps || []).map((s, i) => ({ ...s, __idx: i })).filter(s => s.__idx > index);

  return (
    <div className="fixed inset-y-0 right-0 w-96 max-w-full bg-white border-l border-gray-200 shadow-xl z-40 flex flex-col">
      <div className="p-4 border-b border-gray-100" style={{ borderTop: `3px solid ${meta.color || '#94a3b8'}` }}>
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className="text-lg">{meta.icon}</span>
            <select
              value={step.stepType}
              onChange={e => {
                const stepType = e.target.value;
                // Preserve __key/__position/__next — other steps may reference this step's key
                // as a branch/redirect target, and it'd otherwise be silently dropped here.
                onChange({ ...step, stepType, config: { ...(WORKFLOW_STEP_META[stepType]?.defaultConfig || {}), __key: step.config?.__key, __position: step.config?.__position, __next: step.config?.__next } });
              }}
              className="border border-gray-300 rounded px-2 py-1 text-sm"
            >
              {Object.entries(WORKFLOW_STEP_META).map(([key, m]) => <option key={key} value={key}>{m.label}</option>)}
            </select>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">✕</button>
        </div>
        <input
          type="text" value={step.name || ''} onChange={e => onChange({ ...step, name: e.target.value })}
          placeholder="Step name (optional, used as {{steps.name...}})"
          className="mt-2 w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
        />
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {step.stepType === 'http_request' && (
          <div className="space-y-1.5">
            <div className="flex gap-1.5">
              <select value={step.config.method || 'POST'} onChange={e => setConfig({ method: e.target.value })} className="border border-gray-300 rounded px-2 py-1 text-xs w-24">
                {['GET', 'POST', 'PUT', 'DELETE', 'PATCH'].map(m => <option key={m} value={m}>{m}</option>)}
              </select>
              <input type="text" value={step.config.url || ''} onChange={e => setConfig({ url: e.target.value })} placeholder="https://example.com/webhook or {{trigger.callbackUrl}}" className="flex-1 border border-gray-300 rounded px-2 py-1 text-xs font-mono" />
            </div>
            {step.config.method !== 'GET' && (
              <textarea value={step.config.body || ''} onChange={e => setConfig({ body: e.target.value })} placeholder='Body — use {{trigger.field}} or {{steps.stepName.field}} to interpolate' rows={4} className="w-full border border-gray-300 rounded px-2 py-1 text-xs font-mono" />
            )}
          </div>
        )}
        {step.stepType === 'delay' && (
          <div>
            <label className="block text-xs text-gray-500 mb-1">Wait (ms, max 5000)</label>
            <input type="number" min={0} max={5000} value={step.config.ms ?? 1000} onChange={e => setConfig({ ms: Math.max(0, Math.min(5000, parseInt(e.target.value) || 0)) })} className="border border-gray-300 rounded px-2 py-1 text-xs w-24" />
          </div>
        )}
        {step.stepType === 'filter' && (
          <div className="space-y-1">
            <input
              type="text" value={step.config.condition || ''} onChange={e => setConfig({ condition: e.target.value })}
              placeholder="trigger.status === 'approved'" spellCheck={false}
              className="w-full border border-gray-300 rounded px-2 py-1 text-xs font-mono bg-gray-900 text-emerald-300"
            />
            <div className="text-[11px] text-gray-500">
              A real JS expression, evaluated in the sandbox — <code className="px-1 bg-gray-100 rounded">trigger</code> and <code className="px-1 bg-gray-100 rounded">steps</code> are in scope. Stops the run here (not a failure) if it's falsy.
            </div>
          </div>
        )}
        {step.stepType === 'transform' && (
          <div className="space-y-1">
            {(step.config.mappings || []).map((m, i) => (
              <div key={i} className="flex gap-1.5 items-center">
                <input type="text" value={m.from} onChange={e => {
                  const mappings = [...step.config.mappings]; mappings[i] = { ...m, from: e.target.value }; setConfig({ mappings });
                }} placeholder="from: trigger.field" className="border border-gray-300 rounded px-2 py-1 text-xs font-mono flex-1" />
                <span className="text-gray-400 text-xs">→</span>
                <input type="text" value={m.to} onChange={e => {
                  const mappings = [...step.config.mappings]; mappings[i] = { ...m, to: e.target.value }; setConfig({ mappings });
                }} placeholder="to: outputField" className="border border-gray-300 rounded px-2 py-1 text-xs font-mono flex-1" />
                <button type="button" onClick={() => setConfig({ mappings: step.config.mappings.filter((_, j) => j !== i) })} className="text-rose-500 text-xs px-1">✕</button>
              </div>
            ))}
            <button type="button" onClick={() => setConfig({ mappings: [...(step.config.mappings || []), { from: '', to: '' }] })} className="text-xs text-blue-600 hover:underline">+ Add mapping</button>
          </div>
        )}
        {step.stepType === 'code' && (
          <div className="space-y-1">
            <select value={step.config.language || 'javascript'} onChange={e => setConfig({ language: e.target.value })} className="border border-gray-300 rounded px-2 py-1 text-xs">
              <option value="javascript">JavaScript</option>
              <option value="typescript">TypeScript</option>
            </select>
            <textarea
              value={step.config.code || ''} onChange={e => setConfig({ code: e.target.value })}
              rows={12} spellCheck={false} placeholder="return { ok: true };"
              className="w-full border border-gray-300 rounded px-2 py-1.5 text-xs font-mono bg-gray-900 text-emerald-300"
            />
            <div className="text-[11px] text-gray-500">
              Runs in a real JS sandbox (QuickJS/WASM, not <code className="px-1 bg-gray-100 rounded">eval</code> — Cloudflare Workers don't allow that). <code className="px-1 bg-gray-100 rounded">steps</code>, <code className="px-1 bg-gray-100 rounded">trigger</code>, and <code className="px-1 bg-gray-100 rounded">console</code> are available; no <code className="px-1 bg-gray-100 rounded">fetch</code>/<code className="px-1 bg-gray-100 rounded">await</code> inside — use an HTTP Request step for network calls, then read its output here. Use <code className="px-1 bg-gray-100 rounded">return</code> to produce this step's output.
            </div>
          </div>
        )}
        {step.stepType === 'respond' && (
          <div className="space-y-1.5">
            <div className="flex gap-1.5">
              <input type="number" min={100} max={599} value={step.config.status ?? 200} onChange={e => setConfig({ status: parseInt(e.target.value) || 200 })} placeholder="200" className="w-20 border border-gray-300 rounded px-2 py-1 text-xs" />
              <input type="text" value={step.config.contentType || 'application/json'} onChange={e => setConfig({ contentType: e.target.value })} placeholder="Content-Type" className="flex-1 border border-gray-300 rounded px-2 py-1 text-xs font-mono" />
            </div>
            <textarea value={step.config.body || ''} onChange={e => setConfig({ body: e.target.value })} rows={4} placeholder='{"ok": true} — use {{trigger.field}} or {{steps.stepName.field}}' className="w-full border border-gray-300 rounded px-2 py-1 text-xs font-mono" />
            <div className="text-[11px] text-gray-500">Only takes effect for webhook-triggered runs — sets the actual HTTP response the caller receives. If more than one Respond step runs, the last one wins.</div>
          </div>
        )}
        {step.stepType === 'end' && (
          <div className="space-y-1.5">
            <input type="text" value={step.config.reason || ''} onChange={e => setConfig({ reason: e.target.value })} placeholder="Reason (optional, shown in run history)" className="w-full border border-gray-300 rounded px-2 py-1 text-xs" />
            <div className="text-[11px] text-gray-500">Ends the run right here — always, not conditionally. Nothing after this step executes, and the run is still marked <strong>success</strong>. Useful as an explicit stopping point instead of just letting the chain run out.</div>
          </div>
        )}
        {step.stepType === 'if_else' && (
          <div className="space-y-2">
            <input
              type="text" value={step.config.condition || ''} onChange={e => setConfig({ condition: e.target.value })}
              placeholder="trigger.status === 'approved'" spellCheck={false}
              className="w-full border border-gray-300 rounded px-2 py-1 text-xs font-mono bg-gray-900 text-emerald-300"
            />
            <div className="text-[11px] text-gray-500">
              Same sandboxed JS expression as Filter — <code className="px-1 bg-gray-100 rounded">trigger</code> and <code className="px-1 bg-gray-100 rounded">steps</code> are in scope. True and false each pick where the run continues.
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">If true, go to</label>
              <select value={step.config.trueNext || 'continue'} onChange={e => setConfig({ trueNext: e.target.value })} className="w-full border border-gray-300 rounded px-2 py-1 text-xs">
                <option value="continue">Continue to next step</option>
                {laterSteps.map(s => <option key={s.config?.__key} value={s.config?.__key}>{s.name || WORKFLOW_STEP_META[s.stepType]?.label || s.stepType}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">If false, go to</label>
              <select value={step.config.falseNext || 'continue'} onChange={e => setConfig({ falseNext: e.target.value })} className="w-full border border-gray-300 rounded px-2 py-1 text-xs">
                <option value="continue">Continue to next step</option>
                {laterSteps.map(s => <option key={s.config?.__key} value={s.config?.__key}>{s.name || WORKFLOW_STEP_META[s.stepType]?.label || s.stepType}</option>)}
              </select>
            </div>
            <div className="text-[11px] text-gray-500">Only later steps can be picked, to keep the run from looping forever. To stop a branch entirely, point it at an <strong>End Workflow</strong> step.</div>
          </div>
        )}
        {step.stepType !== 'if_else' && step.stepType !== 'end' && (
          <div className="pt-2 border-t border-gray-100">
            <label className="block text-xs text-gray-500 mb-1">After this step, go to</label>
            <select value={step.config.__next || 'continue'} onChange={e => setConfig({ __next: e.target.value })} className="w-full border border-gray-300 rounded px-2 py-1 text-xs">
              <option value="continue">Continue to next step</option>
              {laterSteps.map(s => <option key={s.config?.__key} value={s.config?.__key}>{s.name || WORKFLOW_STEP_META[s.stepType]?.label || s.stepType}</option>)}
            </select>
            <div className="text-[11px] text-gray-500 mt-1">
              Defaults to whatever's next in this list — override it when this step is the last one in a branch, so it doesn't silently fall into steps that belong to a different branch.
            </div>
          </div>
        )}
      </div>

      <div className="p-4 border-t border-gray-100 space-y-2">
        {testResult && (
          <div className={`text-xs rounded p-2 border ${testResult.status === 'failed' ? 'bg-rose-50 border-rose-200 text-rose-700' : testResult.status === 'skipped' ? 'bg-gray-50 border-gray-200 text-gray-600' : 'bg-emerald-50 border-emerald-200 text-emerald-800'}`}>
            {testResult.status === 'failed' ? (
              <>✗ {testResult.error}</>
            ) : testResult.status === 'skipped' ? (
              <>○ Not reached — an earlier branch skipped past this step.</>
            ) : (
              <>
                ✓ Success{testResult.durationMs != null && <span className="text-gray-500"> · {testResult.durationMs}ms</span>}{testResult.stopped && <span className="text-gray-500"> · run stopped here</span>}
                {step.stepType === 'if_else' && testResult.output?.next && testResult.output.next !== 'continue' && (
                  <span className="text-gray-500"> · jumped to '{(steps || []).find(s => s.config?.__key === testResult.output.next)?.name || (steps || []).find(s => s.config?.__key === testResult.output.next)?.stepType || testResult.output.next}'</span>
                )}
                {step.stepType !== 'if_else' && step.config?.__next && step.config.__next !== 'continue' && (
                  <span className="text-gray-500"> · redirected to '{(steps || []).find(s => s.config?.__key === step.config.__next)?.name || (steps || []).find(s => s.config?.__key === step.config.__next)?.stepType || step.config.__next}'</span>
                )}
                <div className="font-mono mt-1 break-words text-gray-700">{JSON.stringify(testResult.output)}</div>
              </>
            )}
          </div>
        )}
        <div className="flex gap-2">
          <button onClick={onTest} disabled={testing} className="flex-1 px-3 py-1.5 text-xs border border-emerald-300 text-emerald-700 rounded-lg hover:bg-emerald-50 disabled:opacity-50">{testing ? 'Testing…' : '▶ Test'}</button>
          <button onClick={onMoveUp} disabled={index === 0} className="px-2 py-1.5 text-xs border border-gray-300 rounded-lg disabled:opacity-30 hover:bg-gray-50">↑</button>
          <button onClick={onMoveDown} disabled={index === total - 1} className="px-2 py-1.5 text-xs border border-gray-300 rounded-lg disabled:opacity-30 hover:bg-gray-50">↓</button>
        </div>
        <label className="flex items-center gap-2 text-xs text-gray-600">
          <input type="checkbox" checked={step.isEnabled !== false} onChange={e => onChange({ ...step, isEnabled: e.target.checked })} />
          Enabled
        </label>
        <button onClick={onRemove} className="w-full px-3 py-1.5 text-xs text-rose-600 border border-rose-300 rounded-lg hover:bg-rose-50">Delete step</button>
      </div>
    </div>
  );
};

// Recursive tree renderer for the run-detail view's Exports/Inputs tabs — object/array
// nodes show a collapse triangle + {N}/[N] count, primitives are colored by type. Root and
// its immediate children start expanded; deeper nodes start collapsed, matching how the
// data is normally scanned (top-level shape first, drill in from there).
const JsonTreeNode = ({ label, value, depth }) => {
  const [collapsed, setCollapsed] = React.useState(depth >= 2);
  const isObj = value !== null && typeof value === 'object';
  const pad = { paddingLeft: depth * 14 };
  if (!isObj) {
    const color = value === null || value === undefined ? '#94a3b8'
      : typeof value === 'string' ? '#b45309'
      : typeof value === 'number' ? '#1d4ed8'
      : typeof value === 'boolean' ? '#7c3aed' : '#111827';
    const display = value === null || value === undefined ? 'null' : typeof value === 'string' ? `"${value}"` : String(value);
    return (
      <div className="font-mono text-xs py-0.5" style={pad}>
        {label != null && <span className="text-gray-500">{label}: </span>}
        <span style={{ color }} className="break-all">{display}</span>
      </div>
    );
  }
  const isArray = Array.isArray(value);
  const entries = isArray ? value.map((v, i) => [i, v]) : Object.entries(value);
  return (
    <div className="font-mono text-xs">
      <div style={pad} className="py-0.5 cursor-pointer select-none flex items-center gap-1 text-gray-700 hover:bg-gray-50 rounded" onClick={() => setCollapsed(c => !c)}>
        <span className="text-gray-400 w-3 inline-block shrink-0">{entries.length ? (collapsed ? '▶' : '▼') : ' '}</span>
        {label != null && <span className="text-gray-500">{label}: </span>}
        <span className="text-gray-400">{isArray ? `[${entries.length}]` : `{${entries.length}}`}</span>
      </div>
      {!collapsed && entries.map(([k, v]) => <JsonTreeNode key={k} label={k} value={v} depth={depth + 1} />)}
    </div>
  );
};
const JsonTreeView = ({ json }) => {
  const data = React.useMemo(() => { try { return json ? JSON.parse(json) : null; } catch { return null; } }, [json]);
  if (data === null || (typeof data === 'object' && Object.keys(data).length === 0)) {
    return <div className="text-xs text-gray-400 italic p-2">No data.</div>;
  }
  return <div className="p-2 overflow-x-auto max-h-72 overflow-y-auto">{Object.entries(data).map(([k, v]) => <JsonTreeNode key={k} label={k} value={v} depth={0} />)}</div>;
};

const WORKFLOW_RUN_TABS = ['Exports', 'Inputs', 'Logs', 'Details'];

// One-line "what actually ran" summary for a run step's collapsed header — sourced from
// the resolved inputsJson (post-{{template}}), unlike the editor canvas's wfStepSummary
// which reads the raw unresolved config. A skipped step has no inputs to summarize.
function wfRunStepSummary(step) {
  let c; try { c = JSON.parse(step.inputsJson || '{}'); } catch { c = {}; }
  switch (step.stepType) {
    case 'http_request': return `${c.method || 'POST'} ${c.url || ''}`;
    case 'delay': return `${c.ms ?? ''}ms`;
    case 'filter': return c.condition || '';
    case 'if_else': return `if ${c.condition || ''}`;
    case 'transform': return `${(c.mappings || []).length} mapping(s)`;
    case 'code': return c.language === 'typescript' ? 'TypeScript' : 'JavaScript';
    case 'respond': return `${c.status ?? ''} response`;
    case 'end': return c.reason || 'Ends the workflow';
    default: return '';
  }
}

// One step's outcome within a completed (or in-progress) run — icon/name/status header,
// a one-line config summary, and the Exports/Inputs/Logs/Details tab strip.
const WorkflowRunStepCard = React.forwardRef(({ step }, ref) => {
  const [tab, setTab] = React.useState('Exports');
  const meta = WORKFLOW_STEP_META[step.stepType] || {};
  const logs = React.useMemo(() => { try { return JSON.parse(step.logsJson || '[]'); } catch { return []; } }, [step.logsJson]);
  const isSuccess = step.status === 'success';
  const respondOutput = step.stepType === 'respond' && isSuccess
    ? (() => { try { return JSON.parse(step.outputJson || '{}'); } catch { return null; } })()
    : null;

  return (
    <div ref={ref} className="border border-gray-200 rounded-lg bg-white overflow-hidden">
      <div className="flex items-center gap-2 p-3 border-b border-gray-100" style={{ borderTop: `3px solid ${meta.color || '#94a3b8'}` }}>
        <span>{meta.icon}</span>
        <span className="text-sm font-semibold text-gray-900 flex-1 truncate">{step.name || meta.label || step.stepType}</span>
        {step.durationMs != null && <span className="text-xs text-gray-400">{step.durationMs}ms</span>}
        <WorkflowStatusPill status={step.status} />
      </div>
      <div className="px-3 pt-2 text-[11px] text-gray-400 font-mono truncate">{wfRunStepSummary(step) || step.stepType}</div>
      {step.status === 'skipped' ? (
        <div className="p-3 text-xs text-gray-400 italic">Skipped — an earlier branch didn't lead here.</div>
      ) : step.error ? (
        <div className="p-3 text-xs text-rose-700 bg-rose-50 border-t border-rose-100 break-words">✗ {step.error}</div>
      ) : (
        <>
          {respondOutput && (
            <div className="mx-3 mt-2 px-2 py-1.5 rounded bg-indigo-50 border border-indigo-100 text-xs text-indigo-800 flex items-center gap-1.5">
              <span>ℹ</span> Responded successfully with status {respondOutput.status}
            </div>
          )}
          <div className="flex border-b border-gray-100 mt-2">
            {WORKFLOW_RUN_TABS.map(t => (
              <button key={t} onClick={() => setTab(t)} className={`px-3 py-1.5 text-xs font-medium border-b-2 -mb-px ${tab === t ? 'text-blue-700 border-blue-600' : 'text-gray-500 border-transparent hover:text-gray-700'}`}>{t}</button>
            ))}
          </div>
          {tab === 'Exports' && <JsonTreeView json={step.outputJson} />}
          {tab === 'Inputs' && <JsonTreeView json={step.inputsJson} />}
          {tab === 'Logs' && (
            logs.length ? (
              <div className="p-2 font-mono text-xs text-gray-700 space-y-0.5 max-h-72 overflow-y-auto">
                {logs.map((l, i) => <div key={i} className="break-all">{typeof l === 'string' ? l : JSON.stringify(l)}</div>)}
              </div>
            ) : <div className="text-xs text-gray-400 italic p-2">No logs.</div>
          )}
          {tab === 'Details' && (
            <div className="p-2 text-xs text-gray-700 space-y-1">
              <div><span className="text-gray-400">Type:</span> {step.stepType}</div>
              <div><span className="text-gray-400">Status:</span> {step.status}</div>
              <div><span className="text-gray-400">Started:</span> {step.startedAt ? new Date(step.startedAt.replace(' ', 'T') + 'Z').toLocaleString() : '—'}</div>
              <div><span className="text-gray-400">Completed:</span> {step.completedAt ? new Date(step.completedAt.replace(' ', 'T') + 'Z').toLocaleString() : '—'}</div>
              <div><span className="text-gray-400">Duration:</span> {step.durationMs != null ? `${step.durationMs}ms` : '—'}</div>
            </div>
          )}
        </>
      )}
    </div>
  );
});

// Trigger kind + a short display label, derived from `triggered_by`. Manual runs store the
// triggering user's name there instead of a literal 'manual', so anything that isn't the two
// known system values is treated as a manual run by that person.
const WF_TRIGGER_KIND_META = {
  webhook:  { icon: '🪝', label: 'Webhook', color: '#7c3aed' },
  schedule: { icon: '⏰', label: 'Schedule', color: '#0891b2' },
  manual:   { icon: '▶️', label: 'Manual', color: '#2563eb' },
};
function wfRunTriggerKind(triggeredBy) {
  if (triggeredBy === 'webhook') return 'webhook';
  if (triggeredBy === 'schedule') return 'schedule';
  return 'manual';
}

// The payload a run actually received when it started — same card chrome as
// WorkflowRunStepCard so it reads as the first "step" in the chain, just without the
// Exports/Inputs/Logs tab split since a trigger only ever has one thing to show.
const WorkflowRunTriggerCard = React.forwardRef(({ run }, ref) => {
  const kind = wfRunTriggerKind(run?.triggeredBy);
  const meta = WF_TRIGGER_KIND_META[kind];
  // triggeredBy holds the triggering user's name for a manual run, but falls back to
  // the literal 'manual' when no user_name resolved (see runWorkflowManual) — showing
  // that string as if it were a name reads as "Manually run by manual", so treat it
  // the same as no name at all.
  const manualBy = run?.triggeredBy && run.triggeredBy !== 'manual' ? run.triggeredBy : 'a user';
  const subtitle = kind === 'webhook' ? 'Inbound webhook call'
    : kind === 'schedule' ? 'Scheduled interval'
    : `Manually run by ${manualBy}`;
  return (
    <div ref={ref} className="border border-gray-200 rounded-lg bg-white overflow-hidden">
      <div className="flex items-center gap-2 p-3 border-b border-gray-100" style={{ borderTop: `3px solid ${meta.color}` }}>
        <span>{meta.icon}</span>
        <span className="text-sm font-semibold text-gray-900 flex-1 truncate">Trigger</span>
        <span className="text-xs text-gray-400">{meta.label}</span>
      </div>
      <div className="px-3 pt-2 text-[11px] text-gray-400 font-mono truncate">{subtitle}</div>
      <div className="px-3 py-1.5 mt-1 text-[11px] font-medium uppercase tracking-wide text-gray-400 border-b border-gray-100">Payload received</div>
      <JsonTreeView json={run?.triggerData} />
    </div>
  );
});

// Read-only mini chain mirroring the run's actual step order, colored by outcome instead
// of step type — clicking a node scrolls the matching card into view. Deliberately not the
// full editing canvas (no drag/connect/position persistence): a completed run has nothing
// left to edit, just a chain of outcomes to review.
const WorkflowRunMiniCanvas = ({ steps, triggeredBy, onSelect }) => (
  <div className="w-48 shrink-0 space-y-0">
    {(() => {
      const kind = wfRunTriggerKind(triggeredBy);
      const meta = WF_TRIGGER_KIND_META[kind];
      return (
        <button
          type="button"
          onClick={() => onSelect('trigger')}
          className="w-full flex items-center gap-1.5 px-2 py-1.5 rounded border text-left text-xs bg-white hover:bg-gray-50 truncate"
          style={{ borderColor: meta.color + '55' }}
        >
          <span>{meta.icon}</span>
          <span className="truncate flex-1">Trigger</span>
        </button>
      );
    })()}
    {steps.length > 0 && <div className="h-3 w-px bg-gray-200 ml-4" />}
    {steps.map((s, i) => {
      const meta = WORKFLOW_STEP_META[s.stepType] || {};
      const c = WORKFLOW_RUN_STATUS_COLORS[s.status === 'success' ? 'success' : s.status === 'failed' ? 'failed' : s.status === 'skipped' ? 'skipped' : 'pending'];
      return (
        <React.Fragment key={s.id}>
          {i > 0 && <div className="h-3 w-px bg-gray-200 ml-4" />}
          <button
            type="button"
            onClick={() => onSelect(s.id)}
            className="w-full flex items-center gap-1.5 px-2 py-1.5 rounded border text-left text-xs bg-white hover:bg-gray-50 truncate"
            style={{ borderColor: c.color + '55' }}
          >
            <span>{meta.icon}</span>
            <span className="truncate flex-1">{s.name || meta.label || s.stepType}</span>
          </button>
        </React.Fragment>
      );
    })}
  </div>
);

// Run history for one workflow — table + expandable per-step detail.
const WorkflowRunsPanel = ({ workflowId }) => {
  const [runs, setRuns] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [expanded, setExpanded] = React.useState(null);
  const [detail, setDetail] = React.useState(null);
  const cardRefs = React.useRef({});

  const load = React.useCallback(async () => {
    setLoading(true);
    const r = await ModulesModel.getWorkflowRuns(workflowId);
    setRuns(r?.runs || []);
    setLoading(false);
  }, [workflowId]);
  React.useEffect(() => { load(); }, [load]);

  const toggleExpand = async (runId) => {
    if (expanded === runId) { setExpanded(null); return; }
    setExpanded(runId);
    setDetail(null);
    const d = await ModulesModel.getWorkflowRunDetail(runId);
    setDetail(d);
  };

  const scrollToStep = (stepId) => cardRefs.current[stepId]?.scrollIntoView({ behavior: 'smooth', block: 'center' });

  if (loading) return <div className="text-sm text-gray-500 p-4">Loading runs…</div>;
  if (runs.length === 0) return <div className="text-sm text-gray-500 p-4">No runs yet.</div>;

  return (
    <div className="space-y-2">
      {runs.map(r => {
        const isExpandedRun = expanded === r.id;
        const d = isExpandedRun ? detail : null;
        const durationMs = d?.run?.startedAt && d?.run?.completedAt
          ? new Date(d.run.completedAt.replace(' ', 'T') + 'Z') - new Date(d.run.startedAt.replace(' ', 'T') + 'Z')
          : null;
        return (
          <div key={r.id} className="border border-gray-200 rounded-lg overflow-hidden">
            <button onClick={() => toggleExpand(r.id)} className="w-full flex items-center justify-between gap-3 p-3 text-left hover:bg-gray-50">
              <div className="flex items-center gap-3">
                <WorkflowStatusPill status={r.status} />
                <span className="text-sm text-gray-700">Triggered by <strong>{r.triggeredBy || '—'}</strong></span>
                <span className="text-xs text-gray-400">{r.startedAt ? new Date(r.startedAt.replace(' ', 'T') + 'Z').toLocaleString() : ''}</span>
              </div>
              <span className="text-xs text-gray-400">{isExpandedRun ? '▲' : '▼'}</span>
            </button>
            {isExpandedRun && (
              <div className="border-t border-gray-100 p-3 bg-gray-50 space-y-3">
                <div className={`rounded-lg p-3 text-sm font-medium flex items-center gap-2 ${r.status === 'failed' ? 'bg-rose-50 text-rose-800 border border-rose-200' : 'bg-emerald-50 text-emerald-800 border border-emerald-200'}`}>
                  <span>{r.status === 'failed' ? '✗' : '✓'}</span>
                  <span>{r.status === 'failed' ? 'Failed' : 'Success'}{durationMs != null && <> — Workflow executed in {durationMs.toLocaleString()} ms</>}</span>
                </div>
                {r.error && <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded p-2">{r.error}</div>}
                {!d ? <div className="text-xs text-gray-400">Loading steps…</div> : (
                  <div className="flex gap-3 items-start">
                    <WorkflowRunMiniCanvas steps={d.steps || []} triggeredBy={d.run?.triggeredBy} onSelect={scrollToStep} />
                    <div className="flex-1 min-w-0 space-y-2">
                      <WorkflowRunTriggerCard run={d.run} ref={el => { cardRefs.current['trigger'] = el; }} />
                      {(d.steps || []).map(s => (
                        <WorkflowRunStepCard key={s.id} step={s} ref={el => { cardRefs.current[s.id] = el; }} />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// Create/edit screen — trigger config + ordered step list.
const WorkflowEditorView = ({ workflowId, onBack, onSaved }) => {
  const isNew = !workflowId;
  const [name, setName] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [triggerType, setTriggerType] = React.useState('manual');
  const [status, setStatus] = React.useState('active');
  const [webhookToken, setWebhookToken] = React.useState('');
  const [steps, setSteps] = React.useState([]);
  const [loading, setLoading] = React.useState(!isNew);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState('');
  const [activeTab, setActiveTab] = React.useState('edit');
  const [sampleTrigger, setSampleTrigger] = React.useState('{}');
  const [sampleTriggerError, setSampleTriggerError] = React.useState('');
  const [testResults, setTestResults] = React.useState({});
  const [testingIndex, setTestingIndex] = React.useState(null);
  const [selectedIndex, setSelectedIndex] = React.useState(null);
  const [showPicker, setShowPicker] = React.useState(false);
  const [httpResponseMode, setHttpResponseMode] = React.useState('custom');
  const [staticStatus, setStaticStatus] = React.useState(200);
  const [staticContentType, setStaticContentType] = React.useState('application/json');
  const [staticBody, setStaticBody] = React.useState('{"ok": true}');
  const [intervalMinutes, setIntervalMinutes] = React.useState(60);
  const [nextRunAt, setNextRunAt] = React.useState('');

  React.useEffect(() => {
    if (isNew) return;
    ModulesModel.getWorkflow(workflowId).then(d => {
      if (!d) { setError('Workflow not found'); setLoading(false); return; }
      setName(d.workflow.name); setDescription(d.workflow.description || '');
      setTriggerType(d.workflow.triggerType); setStatus(d.workflow.status);
      setWebhookToken(d.workflow.webhookToken || '');
      const tc = d.workflow.triggerConfig || {};
      setHttpResponseMode(tc.httpResponseMode || 'custom');
      setStaticStatus(tc.staticStatus ?? 200);
      setStaticContentType(tc.staticContentType || 'application/json');
      setStaticBody(tc.staticBody ?? '{"ok": true}');
      setIntervalMinutes(tc.intervalMinutes || 60);
      setNextRunAt(d.workflow.nextRunAt || '');
      setSteps((d.steps || []).map(s => {
        const config = JSON.parse(s.configJson || '{}');
        // Every step needs a stable key so if_else steps elsewhere can reference it as a
        // branch target — backfilled eagerly here (not lazily on first use) since it must
        // already exist by the time the config panel's branch-target dropdowns render.
        // Steps saved before this feature existed won't have one yet.
        if (!config.__key) config.__key = crypto.randomUUID();
        return { stepType: s.stepType, name: s.name || '', config, isEnabled: s.isEnabled !== 0 };
      }));
      setLoading(false);
    });
  }, [workflowId, isNew]);

  const addStep = (stepType) => {
    setSteps(s => {
      const config = { ...(WORKFLOW_STEP_META[stepType]?.defaultConfig || {}), __key: crypto.randomUUID() };
      const next = [...s, { stepType, name: '', config, isEnabled: true }];
      setSelectedIndex(next.length - 1);
      return next;
    });
    setShowPicker(false);
  };
  const updateStep = (i, updated) => setSteps(s => s.map((x, idx) => idx === i ? updated : x));
  const removeStep = (i) => {
    setSteps(s => {
      const removedKey = s[i]?.config?.__key;
      return s.filter((_, idx) => idx !== i).map(x => {
        if (!removedKey) return x;
        // Don't leave a dangling reference to a step that no longer exists — if_else's two
        // branch targets, or any other step type's generic __next override.
        const patch = {};
        if (x.config?.trueNext === removedKey) patch.trueNext = 'continue';
        if (x.config?.falseNext === removedKey) patch.falseNext = 'continue';
        if (x.config?.__next === removedKey) patch.__next = 'continue';
        return Object.keys(patch).length ? { ...x, config: { ...x.config, ...patch } } : x;
      });
    });
    setSelectedIndex(sel => (sel === i ? null : sel != null && sel > i ? sel - 1 : sel));
  };
  const moveStep = (i, dir) => {
    setSteps(s => {
      const arr = [...s]; const j = i + dir;
      if (j < 0 || j >= arr.length) return arr;
      [arr[i], arr[j]] = [arr[j], arr[i]];
      return arr;
    });
    setSelectedIndex(sel => (sel === i ? i + dir : sel === i + dir ? i : sel));
  };
  const updateStepPosition = (i, position) => setSteps(s => s.map((x, idx) => idx === i ? { ...x, config: { ...x.config, __position: position } } : x));

  // Dragging a connection on the canvas writes to the exact same config fields the config
  // panel's "go to" dropdowns do — trueNext/falseNext for if_else, __next for everything
  // else — so the two stay interchangeable and never fight each other. `sourceHandle` is
  // 'true'/'false' for an if_else source, null for every other step type's single handle.
  const connectSteps = (sourceIndex, sourceHandle, targetIndex) => {
    setSteps(s => {
      if (targetIndex <= sourceIndex || targetIndex >= s.length) return s;
      // Connecting straight to the immediate next step is indistinguishable from the
      // default, so store it as 'continue' rather than a literal key — keeps the edge
      // solid instead of dashed, and keeps the canvas summary's "redirected" flag accurate.
      const value = targetIndex === sourceIndex + 1 ? 'continue' : s[targetIndex]?.config?.__key;
      if (!value) return s;
      return s.map((x, idx) => {
        if (idx !== sourceIndex) return x;
        if (x.stepType === 'if_else') {
          const field = sourceHandle === 'false' ? 'falseNext' : 'trueNext';
          return { ...x, config: { ...x.config, [field]: value } };
        }
        return { ...x, config: { ...x.config, __next: value } };
      });
    });
  };
  const resetConnection = (sourceIndex, field) => setSteps(s => s.map((x, idx) => idx === sourceIndex ? { ...x, config: { ...x.config, [field]: 'continue' } } : x));

  // Dragging a node above/below others reorders the underlying execution order to match —
  // keyed by config.__key (stable across reorders), never by index, so this is safe to call
  // with whatever order the canvas just observed. Explicit trueNext/falseNext/__next
  // references keep pointing at the same step by key regardless of where it moves to; only
  // steps still on the 'continue' default are affected, which is exactly the point.
  const reorderSteps = (orderedKeys) => {
    setSteps(s => {
      const byKey = new Map(s.map(x => [x.config?.__key, x]));
      const reordered = orderedKeys.map(k => byKey.get(k)).filter(Boolean);
      const missing = s.filter(x => !orderedKeys.includes(x.config?.__key));
      const next = reordered.length ? [...reordered, ...missing] : s;
      if (next !== s && selectedIndex != null) {
        const prevKey = s[selectedIndex]?.config?.__key;
        if (prevKey) {
          const newIdx = next.findIndex(x => x.config?.__key === prevKey);
          if (newIdx >= 0 && newIdx !== selectedIndex) setSelectedIndex(newIdx);
        }
      }
      return next;
    });
  };

  // Runs steps[0..index] against the sample trigger data — ephemeral, nothing is saved.
  // Uses whatever's currently in the editor, so it works before the workflow is saved.
  const runTest = async (index) => {
    let triggerData;
    try { triggerData = JSON.parse(sampleTrigger || '{}'); setSampleTriggerError(''); }
    catch { setSampleTriggerError('Sample trigger data is not valid JSON'); return; }
    setTestingIndex(index);
    const payloadSteps = steps.map(s => ({ stepType: s.stepType, name: s.name, config: s.config, isEnabled: s.isEnabled }));
    const res = await ModulesModel.testWorkflowSteps(payloadSteps, index, triggerData);
    // Look up by index, not "last element" — with if_else jumps, the walk can finish on a
    // different index than the one under test (an earlier branch may skip past it).
    const forIndex = res?.results?.find(r => r.index === index) || { status: 'failed', error: 'No result returned' };
    setTestResults(r => ({ ...r, [index]: forIndex }));
    setTestingIndex(null);
  };

  const save = async () => {
    if (!name.trim()) { setError('Name is required'); return; }
    setSaving(true); setError('');
    const payload = {
      name, description, triggerType, status,
      triggerConfig: { httpResponseMode, staticStatus, staticContentType, staticBody, intervalMinutes },
      steps: steps.map(s => ({ stepType: s.stepType, name: s.name, config: s.config, isEnabled: s.isEnabled })),
    };
    const result = isNew ? await ModulesModel.createWorkflow(payload) : await ModulesModel.updateWorkflow(workflowId, payload);
    setSaving(false);
    if (result?.success === false) { setError(result.error || 'Save failed'); return; }
    if (result?.webhookToken) setWebhookToken(result.webhookToken);
    onSaved(isNew ? result.id : workflowId);
  };

  const regenerateHook = async () => {
    if (!confirm('Rotate the webhook URL? The old one will stop working immediately.')) return;
    const r = await ModulesModel.regenerateWorkflowHook(workflowId);
    if (r?.webhookToken) setWebhookToken(r.webhookToken);
  };

  const runNow = async () => {
    const r = await ModulesModel.runWorkflow(workflowId, {});
    alert(r?.success !== false ? `Run started — status: ${r.status}` : `Run failed: ${r.error}`);
    setActiveTab('runs');
  };

  if (loading) return <div className="max-w-4xl mx-auto px-4 py-6 text-sm text-gray-500">Loading…</div>;

  const hookUrl = webhookToken ? `${window.location.origin}/api/workflows/hooks/${webhookToken}` : '';

  return (
    <div className="max-w-4xl mx-auto px-4 py-6">
      <button onClick={onBack} className="text-sm text-blue-600 hover:underline mb-4">← Back to Workflows</button>
      <h1 className="text-2xl font-bold text-gray-900 mb-4">{isNew ? 'New Workflow' : 'Edit Workflow'}</h1>

      {!isNew && (
        <div className="flex border-b border-gray-200 mb-4">
          {['edit', 'runs'].map(t => (
            <button key={t} onClick={() => setActiveTab(t)} className={`px-4 py-2 text-sm font-medium border-b-2 ${activeTab === t ? 'text-blue-700 border-blue-600' : 'text-gray-500 border-transparent'}`}>{t === 'edit' ? 'Edit' : 'Runs'}</button>
          ))}
        </div>
      )}

      {activeTab === 'runs' ? <WorkflowRunsPanel workflowId={workflowId} /> : (
        <div className="bg-white rounded-lg shadow border border-gray-200 p-5 space-y-4">
          {error && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded p-2">{error}</div>}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Name</label>
              <input type="text" value={name} onChange={e => setName(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Status</label>
              <select value={status} onChange={e => setStatus(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm">
                <option value="active">Active</option>
                <option value="paused">Paused</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Description</label>
            <input type="text" value={description} onChange={e => setDescription(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Trigger</label>
            <div className="flex gap-2 mb-2">
              {['manual', 'webhook', 'schedule'].map(t => (
                <button key={t} type="button" onClick={() => setTriggerType(t)} className={`px-3 py-1.5 text-sm rounded-lg border ${triggerType === t ? 'bg-blue-600 text-white border-blue-600' : 'border-gray-300 text-gray-700'}`}>{t === 'manual' ? '▶ Manual' : t === 'webhook' ? '🪝 Webhook' : '🕐 Schedule'}</button>
              ))}
            </div>
            {triggerType === 'schedule' && (
              <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg text-xs space-y-1.5">
                <label className="block text-[11px] font-semibold text-gray-500 uppercase mb-1">Run every</label>
                <select value={intervalMinutes} onChange={e => setIntervalMinutes(parseInt(e.target.value))} className="border border-gray-300 rounded px-2 py-1.5 text-xs">
                  {[
                    { v: 5, l: '5 minutes' }, { v: 15, l: '15 minutes' }, { v: 30, l: '30 minutes' },
                    { v: 60, l: '1 hour' }, { v: 180, l: '3 hours' }, { v: 360, l: '6 hours' },
                    { v: 720, l: '12 hours' }, { v: 1440, l: '1 day' },
                  ].map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
                </select>
                {nextRunAt ? (
                  <div className="text-gray-500">Next run: {new Date(nextRunAt.replace(' ', 'T') + 'Z').toLocaleString()}</div>
                ) : (
                  <div className="text-gray-500">The countdown starts once you save.</div>
                )}
                <div className="text-gray-500">Fires with an empty trigger payload — there's no inbound data, so <code className="px-1 bg-gray-100 rounded">trigger.*</code> is always empty here.</div>
              </div>
            )}
            {triggerType === 'webhook' && (
              <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg text-xs space-y-1.5">
                {hookUrl ? (
                  <>
                    <div className="font-mono break-all">{hookUrl}</div>
                    <div className="flex gap-2">
                      <button type="button" onClick={() => navigator.clipboard?.writeText(hookUrl)} className="text-blue-600 hover:underline">Copy</button>
                      {!isNew && <button type="button" onClick={regenerateHook} className="text-rose-600 hover:underline">Regenerate</button>}
                    </div>
                    <div className="text-gray-500">POST any JSON to this URL to run the workflow — the token in the URL is the only auth.</div>
                  </>
                ) : <div className="text-gray-500">A URL will be generated once you save.</div>}

                <div className="pt-2 mt-2 border-t border-gray-200">
                  <div className="text-[11px] font-semibold text-gray-500 uppercase mb-1.5">HTTP Response</div>
                  <div className="space-y-1.5">
                    {[
                      { key: '200', label: 'Return HTTP 200 OK' },
                      { key: '204', label: 'Return HTTP 204 No Content' },
                      { key: 'static', label: 'Return a static response' },
                      { key: 'custom', label: 'Return a custom response from your workflow', hint: 'Add an HTTP Response step to control status/body — without one, a default run summary is returned instead.' },
                    ].map(opt => (
                      <label key={opt.key} className="flex items-start gap-2 cursor-pointer">
                        <input type="radio" name="httpResponseMode" checked={httpResponseMode === opt.key} onChange={() => setHttpResponseMode(opt.key)} className="mt-0.5" />
                        <div>
                          <div className="text-gray-800">{opt.label}</div>
                          {opt.hint && <div className="text-gray-500">{opt.hint}</div>}
                        </div>
                      </label>
                    ))}
                  </div>
                  {httpResponseMode === 'static' && (
                    <div className="mt-2 pl-5 space-y-1.5">
                      <div className="flex gap-1.5">
                        <input type="number" min={100} max={599} value={staticStatus} onChange={e => setStaticStatus(parseInt(e.target.value) || 200)} className="w-20 border border-gray-300 rounded px-2 py-1 text-xs" />
                        <input type="text" value={staticContentType} onChange={e => setStaticContentType(e.target.value)} placeholder="Content-Type" className="flex-1 border border-gray-300 rounded px-2 py-1 text-xs font-mono" />
                      </div>
                      <textarea value={staticBody} onChange={e => setStaticBody(e.target.value)} rows={3} placeholder='{"ok": true} — {{trigger.field}} is supported' className="w-full border border-gray-300 rounded px-2 py-1 text-xs font-mono" />
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Sample trigger data (for testing)</label>
            <textarea
              value={sampleTrigger}
              onChange={e => setSampleTrigger(e.target.value)}
              rows={2}
              spellCheck={false}
              placeholder='{"example": "value"}'
              className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-xs font-mono"
            />
            {sampleTriggerError && <div className="text-xs text-rose-600 mt-1">{sampleTriggerError}</div>}
            <div className="text-[11px] text-gray-500 mt-1">Used only by each step's <strong>Test</strong> button below — never saved with the workflow.</div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-2">Steps</label>
            <WorkflowCanvas
              steps={steps}
              triggerType={triggerType}
              selectedIndex={selectedIndex}
              onSelect={setSelectedIndex}
              onPositionChange={updateStepPosition}
              onDeleteIndex={removeStep}
              onAddClick={() => setShowPicker(true)}
              onConnectSteps={connectSteps}
              onResetConnection={resetConnection}
              onReorderSteps={reorderSteps}
            />
            <div className="text-[11px] text-gray-500 mt-1">Click a step to edit it. Drag a step above/below another to reorder it — the lines update to match. Drag from a step's handle — or drag an existing line's end — to a different step to set where it goes next (only forward — click a dashed connection to remove it).</div>
          </div>

          <div className="flex gap-2 pt-2">
            <button onClick={save} disabled={saving} className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50">{saving ? 'Saving…' : 'Save'}</button>
            {!isNew && <button onClick={runNow} className="px-4 py-2 border border-gray-300 text-sm rounded-lg hover:bg-gray-50">▶ Run Now</button>}
          </div>
        </div>
      )}

      {selectedIndex != null && steps[selectedIndex] && (
        <WorkflowStepConfigPanel
          step={steps[selectedIndex]}
          index={selectedIndex}
          total={steps.length}
          steps={steps}
          onChange={updated => updateStep(selectedIndex, updated)}
          onRemove={() => removeStep(selectedIndex)}
          onMoveUp={() => moveStep(selectedIndex, -1)}
          onMoveDown={() => moveStep(selectedIndex, 1)}
          onTest={() => runTest(selectedIndex)}
          testing={testingIndex === selectedIndex}
          testResult={testResults[selectedIndex]}
          onClose={() => setSelectedIndex(null)}
        />
      )}
      {showPicker && <WorkflowStepTypePicker onPick={addStep} onClose={() => setShowPicker(false)} />}
    </div>
  );
};

const WorkflowsModule = ({ user }) => {
  const [workflows, setWorkflows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [selectedId, setSelectedId] = React.useState(null);
  const [creating, setCreating] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    const r = await ModulesModel.getWorkflows();
    setWorkflows(r?.workflows || []);
    setLoading(false);
  }, []);
  React.useEffect(() => { load(); }, [load]);

  const handleDelete = async (id, name) => {
    if (!confirm(`Delete workflow "${name}"? This also deletes its run history.`)) return;
    await ModulesModel.deleteWorkflow(id);
    load();
  };
  const runNow = async (id) => {
    const r = await ModulesModel.runWorkflow(id, {});
    alert(r?.success !== false ? `Run started — status: ${r.status}` : `Run failed: ${r.error}`);
    load();
  };

  if (selectedId || creating) {
    return (
      <WorkflowEditorView
        workflowId={creating ? null : selectedId}
        onBack={() => { setSelectedId(null); setCreating(false); load(); }}
        onSaved={(id) => { setCreating(false); setSelectedId(id); load(); }}
      />
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Workflows</h1>
          <p className="text-gray-600 mt-1 text-sm">Build automated pipelines — trigger on a webhook or run manually, then chain HTTP calls, filters, and transforms.</p>
        </div>
        <button onClick={() => setCreating(true)} className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 font-medium">+ New Workflow</button>
      </div>

      {loading ? (
        <div className="text-sm text-gray-500">Loading…</div>
      ) : workflows.length === 0 ? (
        <div className="bg-white rounded-lg shadow border border-gray-200 p-8 text-center text-sm text-gray-500">No workflows yet. Click <strong>+ New Workflow</strong> to create the first one.</div>
      ) : (
        <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-[10px] uppercase tracking-wide text-gray-500">
              <tr>
                <th className="p-3 font-semibold">Name</th>
                <th className="p-3 font-semibold">Trigger</th>
                <th className="p-3 font-semibold">Status</th>
                <th className="p-3 font-semibold">Last Run</th>
                <th className="p-3 font-semibold">Runs</th>
                <th className="p-3 font-semibold"></th>
              </tr>
            </thead>
            <tbody>
              {workflows.map(w => (
                <tr key={w.id} className="border-t border-gray-100 hover:bg-blue-50">
                  <td className="p-3 cursor-pointer" onClick={() => setSelectedId(w.id)}>
                    <div className="font-medium text-gray-900">{w.name}</div>
                    {w.description && <div className="text-xs text-gray-500">{w.description}</div>}
                  </td>
                  <td className="p-3 text-gray-600">{w.triggerType === 'webhook' ? '🪝 Webhook' : w.triggerType === 'schedule' ? '🕐 Schedule' : '▶ Manual'}</td>
                  <td className="p-3">
                    <span className={`px-2 py-0.5 text-xs font-semibold rounded ${w.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600'}`}>{w.status}</span>
                  </td>
                  <td className="p-3">
                    {w.lastRunAt ? (<><WorkflowStatusPill status={w.lastRunStatus} /> <span className="text-xs text-gray-400 ml-1">{new Date(w.lastRunAt.replace(' ', 'T') + 'Z').toLocaleString()}</span></>) : <span className="text-gray-400">—</span>}
                  </td>
                  <td className="p-3 text-gray-600">{w.runCount || 0}</td>
                  <td className="p-3 text-right whitespace-nowrap">
                    <button onClick={() => runNow(w.id)} className="text-xs text-emerald-600 hover:underline mr-3">Run</button>
                    <button onClick={() => setSelectedId(w.id)} className="text-xs text-blue-600 hover:underline mr-3">Edit</button>
                    <button onClick={() => handleDelete(w.id, w.name)} className="text-xs text-rose-600 hover:underline">Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const FASTDTR_URL = 'https://www.fastdtr.com/';

// ============================================================================
// PROJECT DASHBOARD
// ============================================================================

const PROJECT_STATUS_COLORS = {
  active:    { color: '#0366d6', label: 'Active' },
  planning:  { color: '#6f42c1', label: 'Planning' },
  on_hold:   { color: '#e36209', label: 'On Hold' },
  completed: { color: '#28a745', label: 'Completed' },
};
const projectStatusMeta = (s) => PROJECT_STATUS_COLORS[s] || { color: '#94a3b8', label: s || 'Unknown' };

// Donut chart used for milestone-based project progress. Renders an arc proportional
// to `percentage` (0-100) and centers the number inside.
const ProgressDonut = ({ percentage = 0, size = 128, strokeWidth = 14, color = '#3b82f6', bgColor = '#e5e7eb' }) => {
  const pct = Math.max(0, Math.min(100, Math.round(percentage)));
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (pct / 100) * circumference;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={size/2} cy={size/2} r={radius} stroke={bgColor} strokeWidth={strokeWidth} fill="none" />
      <circle
        cx={size/2} cy={size/2} r={radius}
        stroke={color} strokeWidth={strokeWidth} fill="none"
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        strokeLinecap="round"
        transform={`rotate(-90 ${size/2} ${size/2})`}
        style={{ transition: 'stroke-dashoffset 400ms ease' }}
      />
      <text x={size/2} y={size/2 - 4} textAnchor="middle" dominantBaseline="central" fontSize={size * 0.22} fontWeight="700" fill="#111827">{pct}%</text>
      <text x={size/2} y={size/2 + size * 0.14} textAnchor="middle" dominantBaseline="central" fontSize={size * 0.085} fill="#6b7280">complete</text>
    </svg>
  );
};

// Two-segment pie: completed vs pending. Background circle is the pending color;
// foreground arc shows the completed portion. Renders an empty-state ring when total is 0.
const TwoSegmentPie = ({ completed = 0, pending = 0, size = 128, completedColor = '#10b981', pendingColor = '#cbd5e1' }) => {
  const total = completed + pending;
  const cx = size / 2, cy = size / 2;
  if (total === 0) {
    return (
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={cx} cy={cy} r={size/2 - 4} fill="none" stroke="#e5e7eb" strokeWidth="2" strokeDasharray="4 4" />
        <text x={cx} y={cy} textAnchor="middle" dominantBaseline="central" fontSize="11" fill="#9ca3af">No tasks</text>
      </svg>
    );
  }
  const r = size / 2 - 4;
  if (completed === 0) {
    return <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}><circle cx={cx} cy={cy} r={r} fill={pendingColor} /></svg>;
  }
  if (pending === 0) {
    return <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}><circle cx={cx} cy={cy} r={r} fill={completedColor} /></svg>;
  }
  const frac = completed / total;
  const startAngle = -Math.PI / 2;
  const endAngle = startAngle + frac * 2 * Math.PI;
  const x1 = cx + r * Math.cos(startAngle);
  const y1 = cy + r * Math.sin(startAngle);
  const x2 = cx + r * Math.cos(endAngle);
  const y2 = cy + r * Math.sin(endAngle);
  const largeArc = frac > 0.5 ? 1 : 0;
  const completedPath = `M ${cx} ${cy} L ${x1.toFixed(2)} ${y1.toFixed(2)} A ${r} ${r} 0 ${largeArc} 1 ${x2.toFixed(2)} ${y2.toFixed(2)} Z`;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={cx} cy={cy} r={r} fill={pendingColor} />
      <path d={completedPath} fill={completedColor} />
    </svg>
  );
};

// Multi-segment pie used for the overall status distribution at the top of the page.
const StatusDistributionPie = ({ data, size = 220 }) => {
  const total = data.reduce((a, d) => a + (d.count || 0), 0);
  const cx = size / 2, cy = size / 2;
  const r = size / 2 - 4;
  if (total === 0) {
    return (
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="#e5e7eb" strokeWidth="2" strokeDasharray="4 4" />
        <text x={cx} y={cy} textAnchor="middle" dominantBaseline="central" fontSize="13" fill="#9ca3af">No projects</text>
      </svg>
    );
  }
  let cursor = -Math.PI / 2;
  const slices = data.map((d, i) => {
    const frac = d.count / total;
    const start = cursor;
    const end = cursor + frac * 2 * Math.PI;
    cursor = end;
    if (frac >= 0.999) {
      return <circle key={i} cx={cx} cy={cy} r={r} fill={projectStatusMeta(d.status).color} />;
    }
    const x1 = cx + r * Math.cos(start), y1 = cy + r * Math.sin(start);
    const x2 = cx + r * Math.cos(end), y2 = cy + r * Math.sin(end);
    const largeArc = frac > 0.5 ? 1 : 0;
    const path = `M ${cx} ${cy} L ${x1.toFixed(2)} ${y1.toFixed(2)} A ${r} ${r} 0 ${largeArc} 1 ${x2.toFixed(2)} ${y2.toFixed(2)} Z`;
    return <path key={i} d={path} fill={projectStatusMeta(d.status).color} />;
  });
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>{slices}</svg>
  );
};

const ProjectDashboardView = () => {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');

  const load = React.useCallback(() => {
    setLoading(true); setError('');
    ModulesModel.getProjectDashboard()
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { setError(e?.message || 'Failed to load dashboard'); setLoading(false); });
  }, []);
  React.useEffect(() => { load(); }, [load]);

  if (loading) return <div className="max-w-7xl mx-auto px-4 py-6 text-sm text-gray-500">Loading project dashboard…</div>;
  if (error) return <div className="max-w-7xl mx-auto px-4 py-6 text-sm text-red-600">{error}</div>;

  const statusDistribution = data?.statusDistribution || [];
  const projects = data?.projects || [];
  const totals = data?.totals || {};
  const overallProgress = totals.milestones > 0 ? Math.round((totals.milestonesCompleted / totals.milestones) * 100) : 0;
  const overallTaskCompletion = totals.tasks > 0 ? Math.round((totals.tasksCompleted / totals.tasks) * 100) : 0;

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Project Dashboard</h1>
          <p className="text-gray-600 mt-1 text-sm">Status, milestone progress, and task completion across all active projects.</p>
        </div>
        <button onClick={load} className="px-4 py-2 bg-gray-100 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-200">Refresh</button>
      </div>

      {/* Overview — status distribution + roll-up numbers */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow border border-gray-200 p-5">
          <h2 className="text-sm font-semibold text-gray-700 mb-4">Projects by status</h2>
          <div className="flex items-center gap-6">
            <StatusDistributionPie data={statusDistribution} />
            <div className="flex-1 space-y-2">
              {statusDistribution.length === 0 && <div className="text-sm text-gray-500">No active projects.</div>}
              {statusDistribution.map(d => {
                const meta = projectStatusMeta(d.status);
                const pct = totals.projects > 0 ? Math.round((d.count / totals.projects) * 100) : 0;
                return (
                  <div key={d.status} className="flex items-center gap-3">
                    <span className="inline-block w-3 h-3 rounded-sm shrink-0" style={{ background: meta.color }} />
                    <span className="text-sm text-gray-700 flex-1">{meta.label}</span>
                    <span className="text-sm font-semibold text-gray-900">{d.count}</span>
                    <span className="text-xs text-gray-500 w-10 text-right">{pct}%</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow border border-gray-200 p-5">
          <h2 className="text-sm font-semibold text-gray-700 mb-4">Overall</h2>
          <div className="grid grid-cols-2 gap-4">
            <div className="text-center p-3 rounded-lg bg-gray-50 border border-gray-200">
              <div className="text-3xl font-bold text-gray-900">{totals.projects || 0}</div>
              <div className="text-xs text-gray-500 mt-1">Active projects</div>
            </div>
            <div className="text-center p-3 rounded-lg bg-gray-50 border border-gray-200">
              <div className="text-3xl font-bold text-gray-900">{totals.milestones || 0}</div>
              <div className="text-xs text-gray-500 mt-1">Milestones ({totals.milestonesCompleted || 0} done)</div>
            </div>
            <div className="text-center p-3 rounded-lg bg-blue-50 border border-blue-200">
              <div className="text-3xl font-bold text-blue-700">{overallProgress}%</div>
              <div className="text-xs text-blue-700 mt-1">Avg milestone completion</div>
            </div>
            <div className="text-center p-3 rounded-lg bg-emerald-50 border border-emerald-200">
              <div className="text-3xl font-bold text-emerald-700">{overallTaskCompletion}%</div>
              <div className="text-xs text-emerald-700 mt-1">Tasks completed ({totals.tasksCompleted || 0}/{totals.tasks || 0})</div>
            </div>
          </div>
        </div>
      </div>

      {/* Per-project cards — each with two pies */}
      <h2 className="text-sm font-semibold text-gray-700 mb-3">Per project</h2>
      {projects.length === 0 ? (
        <div className="bg-white rounded-lg shadow border border-gray-200 p-8 text-center text-sm text-gray-500">No active projects to display.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {projects.map(p => {
            const sMeta = projectStatusMeta(p.status);
            return (
              <div key={p.id} className="bg-white rounded-lg shadow border border-gray-200 p-4">
                <div className="flex items-start justify-between gap-2 mb-3">
                  <div className="min-w-0">
                    <div className="text-xs font-mono text-gray-400">{p.id}</div>
                    <div className="font-semibold text-gray-900 truncate" title={p.name}>{p.name}</div>
                    {p.owner && <div className="text-xs text-gray-500 mt-0.5">Owner: {p.owner}</div>}
                  </div>
                  <span className="px-2 py-0.5 text-xs font-semibold rounded-full whitespace-nowrap" style={{ background: `${sMeta.color}1a`, color: sMeta.color }}>
                    {sMeta.label}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-3 mt-2">
                  <div className="flex flex-col items-center">
                    <ProgressDonut percentage={p.progress || 0} size={120} />
                    <div className="text-xs text-gray-600 mt-1 text-center">
                      <div className="font-semibold">Milestone progress</div>
                      <div className="text-gray-500">{p.milestonesCompleted}/{p.milestonesTotal} done</div>
                    </div>
                  </div>
                  <div className="flex flex-col items-center">
                    <TwoSegmentPie completed={p.tasksCompleted || 0} pending={p.tasksPending || 0} size={120} />
                    <div className="text-xs text-gray-600 mt-1 text-center">
                      <div className="font-semibold">Tasks</div>
                      <div className="text-gray-500">
                        <span className="inline-flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-sm bg-emerald-500" /> {p.tasksCompleted} done</span>
                        <span className="mx-1">·</span>
                        <span className="inline-flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-sm bg-slate-300" /> {p.tasksPending} pending</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const FastDtrView = () => {
  const [frameStatus, setFrameStatus] = React.useState('loading'); // loading | loaded | blocked

  // Many SaaS sites refuse to render in an iframe (X-Frame-Options / CSP frame-ancestors).
  // We can't read inner state cross-origin, so use a load timeout as the blocked-frame heuristic
  // and surface the open-in-new-tab fallback.
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setFrameStatus(prev => (prev === 'loading' ? 'blocked' : prev));
    }, 8000);
    return () => clearTimeout(timer);
  }, []);

  const openInTab = () => window.open(FASTDTR_URL, '_blank', 'noopener,noreferrer');

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">FastDTR</h1>
          <p className="text-gray-600 mt-1 text-sm">Daily Time Report — log your time at fastdtr.com.</p>
        </div>
        <button
          onClick={openInTab}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
        >
          Open in new tab ↗
        </button>
      </div>

      {frameStatus === 'blocked' && (
        <div className="mb-4 p-4 rounded-lg bg-amber-50 border border-amber-200 text-amber-900 text-sm">
          FastDTR didn't load inside Orbit. The site may block embedding in iframes. Use
          <strong> Open in new tab</strong> above to access it directly.
        </div>
      )}

      <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden" style={{ height: 'calc(100vh - 220px)', minHeight: 520 }}>
        {/* allow-same-origin + allow-scripts is required for the embedded site to function; trusted vendor. */}
        <iframe
          src={FASTDTR_URL}
          title="FastDTR"
          onLoad={() => setFrameStatus('loaded')}
          className="w-full h-full bg-white"
          referrerPolicy="no-referrer-when-downgrade"
          sandbox="allow-same-origin allow-scripts allow-popups allow-forms allow-top-navigation-by-user-activation"
        />
      </div>
    </div>
  );
};

// v1.4 — Admin-managed deployment stage templates. Tunable per stage: label, SLA hours,
// required/optional attachment categories, checklist items (add/edit/remove/reorder).
// Routing (sequence, required role, change-type skip rules) stays in code — admins can't
// brick the approval flow from this UI. "Reset to defaults" reverts a stage or all stages
// to the hardcoded code defaults.
const DeploymentStageTemplatesSection = () => {
  const [templates, setTemplates] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [expandedKey, setExpandedKey] = React.useState(null);
  const [editingTpl, setEditingTpl] = React.useState(null); // stageKey being edited
  const [newItemText, setNewItemText] = React.useState({}); // { [stageKey]: 'text' }
  const [saving, setSaving] = React.useState(false);
  const reload = async () => {
    setLoading(true);
    const r = await ModulesModel.listStageTemplates();
    setTemplates(r?.templates || []);
    setLoading(false);
  };
  React.useEffect(() => { reload(); }, []);

  const saveTpl = async (tpl, patch) => {
    setSaving(true);
    const r = await ModulesModel.updateStageTemplate(tpl.stageKey, patch);
    setSaving(false);
    if (r?.success === false || r?.error) alert(r?.error || 'Save failed');
    else reload();
  };
  const toggleEnabled = (tpl) => saveTpl(tpl, { isEnabled: !(tpl.isEnabled === 1 || tpl.isEnabled === true) });
  const addItem = async (stageKey) => {
    const text = (newItemText[stageKey] || '').trim();
    if (!text) return;
    const r = await ModulesModel.addStageChecklistItem(stageKey, { itemText: text, isRequired: true });
    if (r?.success === false || r?.error) { alert(r?.error || 'Add failed'); return; }
    setNewItemText(s => ({ ...s, [stageKey]: '' }));
    reload();
  };
  const toggleItemRequired = (item) => ModulesModel.updateStageChecklistItem(item.id, { isRequired: !(item.isRequired === 1 || item.isRequired === true) }).then(reload);
  const toggleItemActive = (item) => ModulesModel.updateStageChecklistItem(item.id, { isActive: !(item.isActive === 1 || item.isActive === true) }).then(reload);
  const renameItem = async (item) => {
    const next = prompt('Edit checklist item text:', item.itemText);
    if (next == null) return;
    const txt = String(next).trim();
    if (!txt) return;
    await ModulesModel.updateStageChecklistItem(item.id, { itemText: txt });
    reload();
  };
  const deleteItem = async (item) => {
    if (!confirm(`Delete checklist item "${item.itemText}"?`)) return;
    await ModulesModel.deleteStageChecklistItem(item.id);
    reload();
  };
  const moveItem = async (item, items, dir) => {
    const idx = items.findIndex(i => i.id === item.id);
    const targetIdx = idx + dir;
    if (targetIdx < 0 || targetIdx >= items.length) return;
    const swap = items[targetIdx];
    await Promise.all([
      ModulesModel.updateStageChecklistItem(item.id, { sortOrder: swap.sortOrder }),
      ModulesModel.updateStageChecklistItem(swap.id, { sortOrder: item.sortOrder }),
    ]);
    reload();
  };
  const resetTpl = async (stageKey) => {
    if (!confirm(`Reset stage "${stageKey}" to code defaults? Existing checklist customizations will be lost.`)) return;
    await ModulesModel.resetStageTemplate(stageKey);
    reload();
  };
  const resetAll = async () => {
    if (!confirm('Reset ALL deployment stage templates back to code defaults? Every checklist customization will be lost.')) return;
    await ModulesModel.resetStageTemplate(null);
    reload();
  };

  return (
    <div className="bg-white rounded-lg shadow p-6 mt-6">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Deployment Stage Templates</h2>
          <p className="text-xs text-gray-500 mt-0.5">Edit checklist items, SLA hours, required attachments, and labels. Routing (sequence, approver role, change-type skip rules) stays in code.</p>
        </div>
        <button onClick={resetAll} className="px-3 py-1.5 text-xs border border-rose-300 text-rose-700 rounded hover:bg-rose-50">Reset all to defaults</button>
      </div>
      {loading ? (
        <div className="text-sm text-gray-400">Loading…</div>
      ) : templates.length === 0 ? (
        <div className="text-sm text-gray-500 italic">No templates yet. (Migration creates them automatically on first deployment activity.)</div>
      ) : (
        <div className="space-y-2">
          {templates.map(tpl => {
            const isExpanded = expandedKey === tpl.stageKey;
            const isEditing = editingTpl === tpl.stageKey;
            const enabled = tpl.isEnabled === 1 || tpl.isEnabled === true;
            const checklist = tpl.checklist || [];
            const skipped = (tpl.skippedForChangeTypes || []);
            return (
              <div key={tpl.stageKey} className={`border rounded-lg overflow-hidden ${enabled ? 'border-gray-200' : 'border-amber-300 bg-amber-50/40'}`}>
                <div className="flex items-center gap-3 px-3 py-2 bg-gray-50 cursor-pointer hover:bg-gray-100" onClick={() => setExpandedKey(isExpanded ? null : tpl.stageKey)}>
                  <span className="text-gray-400">{isExpanded ? '▾' : '▸'}</span>
                  <span className="font-mono text-xs text-gray-500 w-8">{tpl.sequence}.</span>
                  <span className="font-semibold text-gray-900 flex-1">{tpl.label}</span>
                  <span className="text-xs font-mono text-gray-400">{tpl.stageKey}</span>
                  {tpl.slaHours != null && <span className="text-xs px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-medium">{tpl.slaHours}h SLA</span>}
                  {skipped.length > 0 && <span className="text-xs text-gray-500" title={`Skipped for ${skipped.join(', ')}`}>skip: {skipped.join(',')}</span>}
                  {!enabled && <span className="text-xs px-1.5 py-0.5 rounded bg-amber-200 text-amber-900 font-medium">DISABLED</span>}
                  <span className="text-xs text-gray-500">{checklist.length} item{checklist.length === 1 ? '' : 's'}</span>
                </div>
                {isExpanded && (
                  <div className="p-4 space-y-4">
                    {/* Stage-level edits */}
                    <div className="grid grid-cols-3 gap-3 items-end">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Label</label>
                        {isEditing ? (
                          <input type="text" defaultValue={tpl.label} id={`tpl-label-${tpl.stageKey}`} className="w-full border border-gray-300 rounded px-2 py-1 text-sm" />
                        ) : (
                          <div className="text-sm text-gray-900">{tpl.label}</div>
                        )}
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">SLA hours <span className="text-gray-400">(blank = none)</span></label>
                        {isEditing ? (
                          <input type="number" defaultValue={tpl.slaHours ?? ''} id={`tpl-sla-${tpl.stageKey}`} min="0" className="w-full border border-gray-300 rounded px-2 py-1 text-sm" />
                        ) : (
                          <div className="text-sm text-gray-900 font-mono">{tpl.slaHours ?? '—'}</div>
                        )}
                      </div>
                      <div className="text-right space-x-2">
                        {isEditing ? (
                          <>
                            <button onClick={() => setEditingTpl(null)} className="text-xs text-gray-500 hover:underline">Cancel</button>
                            <button
                              disabled={saving}
                              onClick={() => {
                                const label = document.getElementById(`tpl-label-${tpl.stageKey}`)?.value || tpl.label;
                                const slaVal = document.getElementById(`tpl-sla-${tpl.stageKey}`)?.value;
                                const reqVal = document.getElementById(`tpl-req-${tpl.stageKey}`)?.value;
                                const optVal = document.getElementById(`tpl-opt-${tpl.stageKey}`)?.value;
                                const slaHours = slaVal === '' ? null : parseInt(slaVal, 10);
                                saveTpl(tpl, {
                                  label,
                                  slaHours,
                                  requiredAttachments: reqVal ? reqVal.split(',').map(s => s.trim()).filter(Boolean) : [],
                                  optionalAttachments: optVal ? optVal.split(',').map(s => s.trim()).filter(Boolean) : [],
                                });
                                setEditingTpl(null);
                              }}
                              className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                            >Save</button>
                          </>
                        ) : (
                          <>
                            <button onClick={() => toggleEnabled(tpl)} className="text-xs text-amber-600 hover:underline">{enabled ? 'Disable' : 'Enable'}</button>
                            <button onClick={() => resetTpl(tpl.stageKey)} className="text-xs text-rose-600 hover:underline">Reset</button>
                            <button onClick={() => setEditingTpl(tpl.stageKey)} className="text-xs text-blue-600 hover:underline">Edit stage</button>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Required attachments <span className="text-gray-400">(comma-separated keys)</span></label>
                        {isEditing ? (
                          <input type="text" defaultValue={tpl.requiredAttachments || ''} id={`tpl-req-${tpl.stageKey}`} className="w-full border border-gray-300 rounded px-2 py-1 text-sm font-mono" placeholder="e.g. release_notes,rollback_plan" />
                        ) : (
                          <div className="text-xs text-gray-700 font-mono">{tpl.requiredAttachments || <em className="text-gray-400 not-italic">none</em>}</div>
                        )}
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Optional attachments</label>
                        {isEditing ? (
                          <input type="text" defaultValue={tpl.optionalAttachments || ''} id={`tpl-opt-${tpl.stageKey}`} className="w-full border border-gray-300 rounded px-2 py-1 text-sm font-mono" placeholder="e.g. uat_results" />
                        ) : (
                          <div className="text-xs text-gray-700 font-mono">{tpl.optionalAttachments || <em className="text-gray-400 not-italic">none</em>}</div>
                        )}
                      </div>
                    </div>

                    {/* Checklist editor */}
                    <div>
                      <div className="text-xs font-medium text-gray-600 mb-1">Checklist items <span className="text-gray-400 font-normal">({checklist.filter(i => i.isActive).length} active · {checklist.filter(i => i.isRequired).length} required)</span></div>
                      {checklist.length === 0 ? (
                        <div className="text-xs text-gray-400 italic mb-2">No checklist items yet.</div>
                      ) : (
                        <ul className="space-y-1 mb-2">
                          {checklist.map((item, i) => {
                            const active = item.isActive === 1 || item.isActive === true;
                            const required = item.isRequired === 1 || item.isRequired === true;
                            return (
                              <li key={item.id} className={`flex items-center gap-2 px-2 py-1 rounded text-sm ${active ? 'bg-gray-50' : 'bg-gray-100 opacity-60'}`}>
                                <button onClick={() => moveItem(item, checklist, -1)} disabled={i === 0} className="text-gray-400 disabled:opacity-30 hover:text-gray-700 text-xs">↑</button>
                                <button onClick={() => moveItem(item, checklist, +1)} disabled={i === checklist.length - 1} className="text-gray-400 disabled:opacity-30 hover:text-gray-700 text-xs">↓</button>
                                <span className={`flex-1 ${active ? 'text-gray-900' : 'line-through text-gray-500'}`}>{item.itemText}</span>
                                <button onClick={() => toggleItemRequired(item)} className={`text-[10px] px-1.5 py-0.5 rounded ${required ? 'bg-rose-100 text-rose-700' : 'bg-gray-200 text-gray-600'}`}>{required ? 'Required' : 'Optional'}</button>
                                <button onClick={() => toggleItemActive(item)} className="text-xs text-amber-600 hover:underline">{active ? 'Disable' : 'Enable'}</button>
                                <button onClick={() => renameItem(item)} className="text-xs text-blue-600 hover:underline">Edit</button>
                                <button onClick={() => deleteItem(item)} className="text-xs text-rose-600 hover:underline">Delete</button>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={newItemText[tpl.stageKey] || ''}
                          onChange={e => setNewItemText(s => ({ ...s, [tpl.stageKey]: e.target.value }))}
                          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addItem(tpl.stageKey); } }}
                          placeholder="Add a checklist item…"
                          className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm"
                        />
                        <button onClick={() => addItem(tpl.stageKey)} disabled={!String(newItemText[tpl.stageKey] || '').trim()} className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50">+ Add</button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// v1.6 — Admin-managed change-freeze windows. A window with no blocked_change_types
// blocks ALL change types in its date range. Use "Emergency,Standard" to allow only
// fast-track changes through, etc. The submitDeployment endpoint reads these and
// returns a 400 with the matching window names when a deployment violates them.
const DeploymentFreezeWindowsSection = () => {
  const [windows, setWindows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [editing, setEditing] = React.useState(null); // null | 'new' | <id>
  const [form, setForm] = React.useState({ name: '', startDate: '', endDate: '', reason: '', blockedChangeTypes: '', allowAdminOverride: true });
  const reload = async () => {
    setLoading(true);
    const r = await ModulesModel.listFreezeWindows();
    setWindows(r?.windows || []);
    setLoading(false);
  };
  React.useEffect(() => { reload(); }, []);
  const openNew = () => {
    setForm({ name: '', startDate: '', endDate: '', reason: '', blockedChangeTypes: '', allowAdminOverride: true });
    setEditing('new');
  };
  const openEdit = (w) => {
    setForm({
      name: w.name || '',
      startDate: w.startDate || '',
      endDate: w.endDate || '',
      reason: w.reason || '',
      blockedChangeTypes: w.blockedChangeTypes || '',
      allowAdminOverride: w.allowAdminOverride === 1 || w.allowAdminOverride === true,
    });
    setEditing(w.id);
  };
  const save = async () => {
    if (!form.name.trim() || !form.startDate || !form.endDate) {
      alert('Name, start date, and end date are required'); return;
    }
    if (String(form.endDate) < String(form.startDate)) {
      alert('End date must be on or after start date'); return;
    }
    const payload = {
      name: form.name.trim(),
      startDate: form.startDate, endDate: form.endDate,
      reason: form.reason || null,
      blockedChangeTypes: form.blockedChangeTypes ? form.blockedChangeTypes.split(',').map(s => s.trim()).filter(Boolean) : [],
      allowAdminOverride: !!form.allowAdminOverride,
    };
    const r = editing === 'new'
      ? await ModulesModel.createFreezeWindow(payload)
      : await ModulesModel.updateFreezeWindow(editing, payload);
    if (r?.success === false || r?.error) { alert(r?.error || 'Save failed'); return; }
    setEditing(null);
    reload();
  };
  const toggleActive = async (w) => {
    await ModulesModel.updateFreezeWindow(w.id, { isActive: !(w.isActive === 1 || w.isActive === true) });
    reload();
  };
  const remove = async (w) => {
    if (!confirm(`Delete freeze window "${w.name}"? This cannot be undone.`)) return;
    await ModulesModel.deleteFreezeWindow(w.id);
    reload();
  };
  const todayStr = new Date().toISOString().slice(0, 10);
  return (
    <div className="bg-white rounded-lg shadow p-6 mt-6">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Deployment Freeze Windows</h2>
          <p className="text-xs text-gray-500 mt-0.5">Block deployment submissions during sensitive periods (release cuts, audits, holidays). Admin-managed.</p>
        </div>
        <button onClick={openNew} className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700">+ New Window</button>
      </div>
      {loading ? (
        <div className="text-sm text-gray-400">Loading…</div>
      ) : windows.length === 0 ? (
        <div className="text-sm text-gray-500 italic">No freeze windows defined yet. Submissions are allowed for any planned date.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase text-gray-500 border-b border-gray-200">
                <th className="pb-2">Name</th>
                <th className="pb-2">Window</th>
                <th className="pb-2">Blocks</th>
                <th className="pb-2">Override</th>
                <th className="pb-2">Status</th>
                <th className="pb-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {windows.map(w => {
                const active = w.isActive === 1 || w.isActive === true;
                const isCurrent = active && String(w.startDate) <= todayStr && String(w.endDate) >= todayStr;
                return (
                  <tr key={w.id} className={`border-t border-gray-100 ${isCurrent ? 'bg-rose-50/50' : ''}`}>
                    <td className="py-2 font-medium text-gray-900">{w.name}{w.reason && <div className="text-xs text-gray-500 font-normal">{w.reason}</div>}</td>
                    <td className="py-2 text-xs font-mono">{w.startDate} → {w.endDate}</td>
                    <td className="py-2 text-xs">{w.blockedChangeTypes || <em className="text-gray-400">All types</em>}</td>
                    <td className="py-2 text-xs">{(w.allowAdminOverride === 1 || w.allowAdminOverride === true) ? 'Admin' : <span className="text-rose-700 font-semibold">None</span>}</td>
                    <td className="py-2">
                      {isCurrent ? <span className="px-2 py-0.5 text-[10px] font-semibold rounded bg-rose-600 text-white">ACTIVE NOW</span>
                        : active ? <span className="px-2 py-0.5 text-[10px] font-semibold rounded bg-emerald-100 text-emerald-700">Enabled</span>
                        : <span className="px-2 py-0.5 text-[10px] font-semibold rounded bg-gray-100 text-gray-500">Disabled</span>}
                    </td>
                    <td className="py-2 text-right">
                      <button onClick={() => openEdit(w)} className="text-blue-600 hover:underline text-xs mr-2">Edit</button>
                      <button onClick={() => toggleActive(w)} className="text-amber-600 hover:underline text-xs mr-2">{active ? 'Disable' : 'Enable'}</button>
                      <button onClick={() => remove(w)} className="text-rose-600 hover:underline text-xs">Delete</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {editing && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="px-5 py-3 border-b flex justify-between items-center">
              <h3 className="font-semibold">{editing === 'new' ? 'New Freeze Window' : 'Edit Freeze Window'}</h3>
              <button onClick={() => setEditing(null)} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name <span className="text-rose-500">*</span></label>
                <input type="text" value={form.name} onChange={e => setForm(s => ({ ...s, name: e.target.value }))} placeholder="Holiday freeze" className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start date <span className="text-rose-500">*</span></label>
                  <input type="date" value={form.startDate} onChange={e => setForm(s => ({ ...s, startDate: e.target.value }))} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End date <span className="text-rose-500">*</span></label>
                  <input type="date" value={form.endDate} onChange={e => setForm(s => ({ ...s, endDate: e.target.value }))} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reason / context</label>
                <input type="text" value={form.reason} onChange={e => setForm(s => ({ ...s, reason: e.target.value }))} placeholder="Q4 release cut" className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Change types blocked <span className="text-gray-400 text-xs">(comma-separated; blank = block all)</span></label>
                <input type="text" value={form.blockedChangeTypes} onChange={e => setForm(s => ({ ...s, blockedChangeTypes: e.target.value }))} placeholder="Normal,Standard" className="w-full border border-gray-300 rounded px-3 py-2 text-sm font-mono" />
                <div className="text-xs text-gray-500 mt-1">Available: Normal, Emergency, Standard. Leave blank to block any deployment.</div>
              </div>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input type="checkbox" checked={form.allowAdminOverride} onChange={e => setForm(s => ({ ...s, allowAdminOverride: e.target.checked }))} />
                Admins can override this freeze (otherwise submissions are blocked for everyone)
              </label>
            </div>
            <div className="px-5 py-3 border-t flex justify-end gap-2">
              <button onClick={() => setEditing(null)} className="px-3 py-1.5 text-sm text-gray-600">Cancel</button>
              <button onClick={save} className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded">{editing === 'new' ? 'Create' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const IntegrationsSection = () => {
  const [settings, setSettings] = React.useState({
    eod_enabled: 'true',
    eod_slack_channel: '',
    pipedream_outbound_url: '',
    pipedream_crf_url: '',
    pipedream_leave_url: '',
    pipedream_deployment_url: '',
    pipedream_project_url: '',
    pipedream_backup_url: '',
    pipedream_api_key: '',
    eod_schedule_time: '17:00',
    eod_schedule_days: 'Mon,Tue,Wed,Thu,Fri',
    eod_schedule_modules: '',
    eod_scheduler_secret: '',
    legacy_redirect_enabled: 'false',
    legacy_redirect_url: '',
    legacy_redirect_bypass_key: '',
    legacy_redirect_bypass_key_set: false,
  });
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [saved, setSaved] = React.useState(false);
  const [error, setError] = React.useState('');
  const [testing, setTesting] = React.useState('');
  const [testResults, setTestResults] = React.useState({});
  const [showApiKey, setShowApiKey] = React.useState(false);
  const [showSchedulerSecret, setShowSchedulerSecret] = React.useState(false);

  React.useEffect(() => {
    ModulesModel.getAppSettings()
      .then(s => { setSettings(prev => ({ ...prev, ...(s || {}) })); setLoading(false); })
      .catch(() => { setError('Could not load settings'); setLoading(false); });
  }, []);

  const save = async () => {
    setSaving(true); setError(''); setSaved(false);
    try {
      // legacy_redirect_bypass_key_set is a read-only display flag (the real key is never
      // sent to the client), and an empty legacy_redirect_bypass_key means "left untouched"
      // here, not "clear it" — omit both rather than blank out an existing key on every save.
      const { legacy_redirect_bypass_key_set, legacy_redirect_bypass_key, ...rest } = settings;
      const payload = legacy_redirect_bypass_key ? { ...rest, legacy_redirect_bypass_key } : rest;
      const res = await ModulesModel.saveAppSettings(payload);
      if (res?.success === false) setError(res.error || 'Failed to save');
      else {
        setSaved(true);
        setSettings(s => ({ ...s, legacy_redirect_bypass_key: '', legacy_redirect_bypass_key_set: legacy_redirect_bypass_key ? true : s.legacy_redirect_bypass_key_set }));
        setTimeout(() => setSaved(false), 2500);
      }
    } catch (e) { setError(e?.message || 'Failed to save'); }
    setSaving(false);
  };

  const sendTest = async (target) => {
    setTesting(target);
    setTestResults(prev => ({ ...prev, [target]: null }));
    try {
      const res = await fetch(`${API_BASE}/integrations/test-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...BaseModel.authHeaders() },
        body: JSON.stringify({ target })
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || body?.success === false) {
        setTestResults(prev => ({ ...prev, [target]: { ok: false, msg: body?.error || `Test failed (${res.status})` } }));
      } else {
        setTestResults(prev => ({ ...prev, [target]: { ok: true, msg: body?.message || 'Test email sent. Check your inbox.' } }));
      }
    } catch (e) {
      setTestResults(prev => ({ ...prev, [target]: { ok: false, msg: e?.message || 'Network error' } }));
    }
    setTesting('');
  };

  const WebhookField = ({ target, title, description, fieldKey, placeholder }) => (
    <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
      <div className="flex items-start justify-between gap-3 mb-2">
        <div>
          <div className="text-sm font-semibold text-gray-900">{title}</div>
          <div className="text-xs text-gray-500 mt-0.5">{description}</div>
        </div>
      </div>
      <input
        type="text"
        value={settings[fieldKey] || ''}
        onChange={e => setSettings(s => ({ ...s, [fieldKey]: e.target.value }))}
        placeholder={placeholder}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
      />
      <div className="flex items-center justify-between mt-2">
        <p className={`text-xs font-medium ${testResults[target]?.ok ? 'text-green-600' : testResults[target] ? 'text-red-600' : 'text-transparent'}`}>
          {testResults[target] ? `${testResults[target].ok ? '✓' : '✗'} ${testResults[target].msg}` : '·'}
        </p>
        <button
          type="button"
          onClick={() => sendTest(target)}
          disabled={testing === target || !settings[fieldKey]}
          title={!settings[fieldKey] ? 'Save the URL first' : 'Send a test email through this webhook'}
          className="px-3 py-1.5 bg-white text-gray-700 border border-gray-300 rounded-lg text-xs font-medium hover:bg-gray-100 disabled:opacity-50 whitespace-nowrap"
        >
          {testing === target ? 'Sending…' : '✉️ Send test'}
        </button>
      </div>
    </div>
  );

  if (loading) return <div className="mt-6 bg-white rounded-lg shadow p-6 text-sm text-gray-500">Loading integrations…</div>;

  return (
    <div className="mt-6 bg-white rounded-lg shadow p-6">
      <div className="flex items-center gap-3 mb-1">
        <Settings className="w-6 h-6 text-blue-600" />
        <h2 className="text-xl font-semibold text-gray-900">Integrations</h2>
      </div>
      <p className="text-gray-600 mb-6 text-sm">
        Each outbound channel has its own Pipedream webhook. They are independent — configuring one does not affect the others.
        Use the <strong>Send test</strong> button next to each URL to verify it end-to-end.
      </p>

      <div className="p-4 bg-rose-50 border border-rose-200 rounded-lg space-y-3 mb-4">
        <div>
          <div className="text-sm font-semibold text-rose-900">Legacy Instance Redirect</div>
          <div className="text-xs text-rose-800 mt-0.5">
            For a retired copy of Orbit only. When enabled, every visitor to <strong>this</strong> instance — even
            before logging in — is sent straight to the URL below instead of seeing the app. Leave off on the
            live production instance.
          </div>
        </div>
        <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-rose-200">
          <div>
            <div className="text-sm font-medium text-gray-900">Redirect visitors to the new Orbit</div>
            <div className="text-xs text-gray-500">Master switch — takes effect immediately, no deploy needed.</div>
          </div>
          <button
            type="button"
            onClick={() => setSettings(s => ({ ...s, legacy_redirect_enabled: s.legacy_redirect_enabled === 'true' ? 'false' : 'true' }))}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${settings.legacy_redirect_enabled === 'true' ? 'bg-rose-600' : 'bg-gray-300'}`}
          >
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${settings.legacy_redirect_enabled === 'true' ? 'translate-x-6' : 'translate-x-1'}`} />
          </button>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">New Orbit URL</label>
          <input
            type="text"
            value={settings.legacy_redirect_url || ''}
            onChange={e => setSettings(s => ({ ...s, legacy_redirect_url: e.target.value }))}
            placeholder="https://orbit-1lg.pages.dev/"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-rose-500"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Bypass access key {settings.legacy_redirect_bypass_key_set && <span className="text-emerald-700 font-normal">(currently set)</span>}
          </label>
          <input
            type="text"
            value={settings.legacy_redirect_bypass_key}
            onChange={e => setSettings(s => ({ ...s, legacy_redirect_bypass_key: e.target.value }))}
            placeholder={settings.legacy_redirect_bypass_key_set ? 'Leave blank to keep the current key — type a new one to rotate it' : 'Set a key to let yourself keep using this instance'}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-rose-500"
          />
          <div className="text-[11px] text-gray-500 mt-1">
            Once set, visit this instance with <code className="px-1 bg-gray-100 rounded">?legacy_key=&lt;key&gt;</code> added
            to the URL one time — your browser remembers it after that and skips the redirect. Nobody else who
            just clicks the plain link gets sent here.
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <WebhookField
          target="eod"
          fieldKey="pipedream_outbound_url"
          title="EOD Reports webhook"
          description="Daily EOD digests posted to Slack/email"
          placeholder="https://eod.m.pipedream.net"
        />

        <WebhookField
          target="crf"
          fieldKey="pipedream_crf_url"
          title="Change Request approvals webhook"
          description="Emails approvers when their step is reached, and notifies the requester on approve/reject/complete. Independent from the EOD URL."
          placeholder="https://crf.m.pipedream.net"
        />

        <WebhookField
          target="leave"
          fieldKey="pipedream_leave_url"
          title="Leave approvals webhook"
          description="Emails the next approver in the leave chain and notifies the requester on approve/reject. Uses canned templates per leave type (sick uses a separate template)."
          placeholder="https://leave.m.pipedream.net"
        />

        <WebhookField
          target="deployment"
          fieldKey="pipedream_deployment_url"
          title="Deployment notifications webhook"
          description="Emails the assigned approver when a stage opens, the requester when rejected/returned/info-requested, and milestone notifications (Approved/Deployed/Closed). Independent from the other webhooks."
          placeholder="https://deployment.m.pipedream.net"
        />

        <WebhookField
          target="project"
          fieldKey="pipedream_project_url"
          title="Project Updates webhook"
          description="Outbound notifications for project update reports"
          placeholder="https://project.m.pipedream.net"
        />

        {/* Daily DB backup destination — Pipedream workflow receives the SQL dump and
            forwards it to Google Drive / S3 / Dropbox / etc. See workers/db-backup-cron
            for the companion cron that triggers this daily. */}
        <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg space-y-2">
          <div>
            <div className="text-sm font-semibold text-emerald-900">💾 Daily database backup</div>
            <div className="text-xs text-emerald-800 mt-0.5">
              Companion cron worker (<code className="text-[10px] bg-emerald-100 rounded px-1">workers/db-backup-cron</code>) hits <code className="text-[10px] bg-emerald-100 rounded px-1">/api/system/backup</code> daily. Orbit generates the full SQL dump and POSTs it to the URL below — your Pipedream workflow drops it into Google Drive.
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-emerald-900 mb-1">Backup webhook URL</label>
            <input
              type="url"
              value={settings.pipedream_backup_url || ''}
              onChange={e => setSettings(s => ({ ...s, pipedream_backup_url: e.target.value }))}
              placeholder="https://backup.m.pipedream.net"
              className="w-full px-3 py-2 border border-emerald-300 rounded text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
          </div>
          <button
            type="button"
            disabled={!settings.pipedream_backup_url || !settings.eod_scheduler_secret}
            onClick={async () => {
              if (!confirm('Run a backup now and POST the SQL dump to your Pipedream workflow?')) return;
              try {
                const r = await fetch(`${API_BASE}/system/backup`, {
                  method: 'POST',
                  headers: { 'X-Scheduler-Secret': settings.eod_scheduler_secret, 'Content-Type': 'application/json' },
                  body: '{}',
                });
                const j = await r.json();
                if (j.success) {
                  const skipped = (j.stats?.tablesSkippedByDefault || []).length;
                  const notProcessed = (j.stats?.tablesNotProcessed || []).length;
                  const truncated = (j.stats?.tablesTruncated || []).length;
                  const uncompMB = ((j.stats?.uncompressedBytes || 0) / 1024 / 1024).toFixed(2);
                  const gzKB = ((j.stats?.gzippedBytes || 0) / 1024).toFixed(0);
                  const ratio = j.stats?.uncompressedBytes && j.stats?.gzippedBytes ? (j.stats.uncompressedBytes / j.stats.gzippedBytes).toFixed(1) : '?';
                  let msg = `✓ Backup posted to Pipedream.\n\nFile: ${j.fileName}\nTables processed: ${j.stats?.tablesProcessed}/${j.stats?.tablesTotal}\nRows: ${j.stats?.rows?.toLocaleString() || 0}\nSize: ${uncompMB} MB → ${gzKB} KB gzipped (${ratio}x)\nDuration: ${j.stats?.durationMs}ms`;
                  if (skipped > 0) msg += `\n\nSkipped by default (log/audit tables): ${(j.stats?.tablesSkippedByDefault || []).join(', ')}`;
                  if (notProcessed > 0) msg += `\n\n⚠ Time budget hit — ${notProcessed} table(s) not processed: ${(j.stats?.tablesNotProcessed || []).join(', ')}`;
                  if (truncated > 0) msg += `\n\n⚠ Truncated (data past size cap): ${(j.stats?.tablesTruncated || []).join(', ')}. Use wrangler d1 export for a full dump of these.`;
                  alert(msg);
                } else {
                  alert(`Backup failed: ${j.error || 'unknown error'}${j.uncompressedBytes ? `\n\nGenerated ${((j.uncompressedBytes || 0) / 1024 / 1024).toFixed(2)} MB uncompressed, ${((j.gzippedBytes || 0) / 1024).toFixed(0)} KB gzipped — the Pipedream body limit is ~512 KB. If gzipped exceeds that, exclude more log tables via ?exclude=...` : ''}`);
                }
              } catch (e) { alert(`Request failed: ${e?.message || 'network error'}`); }
            }}
            title={!settings.eod_scheduler_secret ? 'Generate the EOD scheduler secret first (below)' : (!settings.pipedream_backup_url ? 'Enter the backup webhook URL above' : '')}
            className="w-full py-1.5 text-xs bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            🚀 Send Test Backup Now
          </button>
          <div className="text-[10px] text-emerald-800 leading-snug">
            <strong>Reuses the EOD scheduler secret</strong> — no separate secret needed. Configure the Companion Worker with the same <code>SCHEDULER_SECRET</code> as EOD.
          </div>
        </div>

        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
          <div>
            <div className="text-sm font-medium text-gray-900">EOD notifications enabled</div>
            <div className="text-xs text-gray-500">Master switch for EOD scheduled posts only. Does not affect CRF or project webhooks.</div>
          </div>
          <button
            type="button"
            onClick={() => setSettings(s => ({ ...s, eod_enabled: s.eod_enabled === 'true' ? 'false' : 'true' }))}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${settings.eod_enabled === 'true' ? 'bg-blue-600' : 'bg-gray-300'}`}
          >
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${settings.eod_enabled === 'true' ? 'translate-x-6' : 'translate-x-1'}`} />
          </button>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">EOD Slack Channel</label>
          <input
            type="text"
            value={settings.eod_slack_channel || ''}
            onChange={e => setSettings(s => ({ ...s, eod_slack_channel: e.target.value }))}
            placeholder="#eod-reports"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* EOD Scheduler — drives automated daily sends via the companion Worker hitting /api/eod/scheduler-tick */}
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg space-y-3">
          <div>
            <div className="text-sm font-semibold text-amber-900">EOD Scheduler</div>
            <div className="text-xs text-amber-800 mt-0.5">Sends EOD digests automatically when the configured Pacific time is reached on a scheduled day. Requires a companion cron caller (see workers/eod-cron) hitting <code className="px-1 bg-amber-100 rounded">/api/eod/scheduler-tick</code> with the secret below.</div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Send at (Pacific time)</label>
              <input
                type="time"
                value={settings.eod_schedule_time || '17:00'}
                onChange={e => setSettings(s => ({ ...s, eod_schedule_time: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Days</label>
            <div className="flex flex-wrap gap-1">
              {['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].map(d => {
                const days = (settings.eod_schedule_days || '').split(',').map(x => x.trim()).filter(Boolean);
                const active = days.includes(d);
                return (
                  <button
                    key={d}
                    type="button"
                    onClick={() => {
                      const order = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];
                      const next = (active ? days.filter(x => x !== d) : [...days, d]).sort((a, b) => order.indexOf(a) - order.indexOf(b));
                      setSettings(s => ({ ...s, eod_schedule_days: next.join(',') }));
                    }}
                    className={`px-3 py-1 text-xs font-medium rounded ${active ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-50'}`}
                  >
                    {d}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Modules to send <span className="text-gray-400 font-normal">(each is sent at most once per day)</span></label>
            <div className="flex flex-wrap gap-1">
              {['devops','dataops','quality'].map(m => {
                const mods = (settings.eod_schedule_modules || '').split(',').map(x => x.trim()).filter(Boolean);
                const active = mods.includes(m);
                const lastSent = settings[`eod_last_sent_${m}`];
                return (
                  <button
                    key={m}
                    type="button"
                    onClick={() => {
                      const next = active ? mods.filter(x => x !== m) : [...mods, m];
                      setSettings(s => ({ ...s, eod_schedule_modules: next.join(',') }));
                    }}
                    title={lastSent ? `Last sent: ${lastSent}` : 'Never sent yet'}
                    className={`px-3 py-1.5 text-xs font-medium rounded ${active ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-50'}`}
                  >
                    {m}{lastSent ? ` · last ${lastSent}` : ''}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Scheduler secret <span className="text-gray-400 font-normal">(set in the companion Worker)</span></label>
            <div className="flex gap-2">
              <input
                type={showSchedulerSecret ? 'text' : 'password'}
                value={settings.eod_scheduler_secret || ''}
                onChange={e => setSettings(s => ({ ...s, eod_scheduler_secret: e.target.value }))}
                placeholder="Click Generate to create one"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
              />
              <button type="button" onClick={() => setShowSchedulerSecret(v => !v)} className="px-3 py-2 text-xs border border-gray-300 rounded-lg hover:bg-gray-50 bg-white">
                {showSchedulerSecret ? 'Hide' : 'Show'}
              </button>
              <button
                type="button"
                onClick={() => {
                  const fresh = (crypto.randomUUID() + crypto.randomUUID()).replace(/-/g, '');
                  setSettings(s => ({ ...s, eod_scheduler_secret: fresh }));
                  setShowSchedulerSecret(true);
                }}
                className="px-3 py-2 text-xs border border-gray-300 rounded-lg hover:bg-gray-50 bg-white"
              >
                Generate
              </button>
              <button
                type="button"
                onClick={() => settings.eod_scheduler_secret && navigator.clipboard?.writeText(settings.eod_scheduler_secret)}
                disabled={!settings.eod_scheduler_secret}
                className="px-3 py-2 text-xs border border-gray-300 rounded-lg hover:bg-gray-50 bg-white disabled:opacity-50"
              >
                Copy
              </button>
            </div>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Pipedream API Key <span className="text-xs text-gray-400 font-normal">(shared across all webhooks)</span></label>
          <div className="flex gap-2">
            <input
              type={showApiKey ? 'text' : 'password'}
              value={settings.pipedream_api_key || ''}
              onChange={e => setSettings(s => ({ ...s, pipedream_api_key: e.target.value }))}
              placeholder="optional bearer token"
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button type="button" onClick={() => setShowApiKey(v => !v)} className="px-3 py-2 text-xs border border-gray-300 rounded-lg hover:bg-gray-50">
              {showApiKey ? 'Hide' : 'Show'}
            </button>
          </div>
        </div>

        {error && <p className="text-red-600 text-xs font-medium">{error}</p>}
        {saved && <p className="text-green-600 text-xs font-medium">✓ Settings saved</p>}

        <div className="flex flex-wrap gap-3 pt-2 border-t border-gray-100">
          <button onClick={save} disabled={saving} className="px-5 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
            {saving ? 'Saving…' : 'Save Integrations'}
          </button>
        </div>
      </div>
    </div>
  );
};

const SettingsView = ({ onClearData, user, authToken }) => {
  const moduleOptions = [
    { key: 'plantrack', label: 'PlanTrack (Projects, Milestones, Tasks)' },
    { key: 'quicktrack', label: 'QuickTrack (Quick Tasks)' },
    { key: 'dataops', label: 'DataOps (Pipelines, Reports)' },
    { key: 'qms', label: 'Quality / QMS (Requirements, Test Cases, Bugs)' },
    { key: 'timeboard', label: 'TimeBoard (Timesheets, Entries)' },
    { key: 'leaveboard', label: 'LeaveBoard (Leave Requests, Balances)' },
    { key: 'learntrack', label: 'LearnTrack (Courses, Skills, Badges)' },
    { key: 'vendors', label: 'Vendors (Vendors, Events)' }
  ];

  const [selected, setSelected] = useState([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [result, setResult] = useState(null);

  const allSelected = selected.length === moduleOptions.length;

  const toggleModule = (key) => {
    setSelected(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  const toggleAll = () => {
    setSelected(allSelected ? [] : moduleOptions.map(m => m.key));
  };

  const handleClear = async () => {
    setClearing(true);
    setResult(null);
    try {
      const modules = allSelected ? ['all'] : selected;
      const res = await onClearData(modules);
      if (res?.success) {
        setResult({ type: 'success', message: `Cleared ${res.tablesCleared} table(s) successfully. You can now enter your real data.` });
        setSelected([]);
      } else {
        setResult({ type: 'error', message: res?.error || 'Failed to clear data.' });
      }
    } catch {
      setResult({ type: 'error', message: 'Network error. Please try again.' });
    }
    setClearing(false);
    setConfirmOpen(false);
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
        <p className="text-gray-600 mt-1">Manage your system data and configurations.</p>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center gap-3 mb-4">
          <Trash2 className="w-6 h-6 text-red-600" />
          <h2 className="text-xl font-semibold text-gray-900">Data Management</h2>
        </div>
        <p className="text-gray-600 mb-6">
          Clear test/demo data from selected modules so you can replace it with real data.
          This deletes all records from the selected modules but keeps the database structure intact.
        </p>

        {result && (
          <div className={`mb-4 p-4 rounded-lg ${result.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
            {result.message}
          </div>
        )}

        <div className="mb-6">
          <label className="flex items-center gap-3 mb-3 pb-3 border-b border-gray-200 cursor-pointer">
            <input type="checkbox" checked={allSelected} onChange={toggleAll} className="w-4 h-4 text-red-600 rounded" />
            <span className="font-semibold text-gray-900">Select All Modules</span>
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {moduleOptions.map(mod => (
              <label key={mod.key} className="flex items-center gap-3 p-2 rounded hover:bg-gray-50 cursor-pointer">
                <input type="checkbox" checked={selected.includes(mod.key)} onChange={() => toggleModule(mod.key)} className="w-4 h-4 text-red-600 rounded" />
                <span className="text-sm text-gray-700">{mod.label}</span>
              </label>
            ))}
          </div>
        </div>

        <button
          onClick={() => setConfirmOpen(true)}
          disabled={selected.length === 0 || clearing}
          className="px-5 py-2.5 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          <Trash2 className="w-4 h-4" />
          Clear Selected Data
        </button>
      </div>

      {confirmOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Confirm Data Deletion</h3>
            </div>
            <p className="text-gray-600 mb-2">
              This will permanently delete all data from <strong>{selected.length}</strong> module(s):
            </p>
            <ul className="text-sm text-gray-700 mb-4 ml-4 list-disc">
              {selected.map(key => (
                <li key={key}>{moduleOptions.find(m => m.key === key)?.label}</li>
              ))}
            </ul>
            <p className="text-red-600 text-sm font-medium mb-6">This action cannot be undone.</p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setConfirmOpen(false)} disabled={clearing} className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">
                Cancel
              </button>
              <button onClick={handleClear} disabled={clearing} className="px-4 py-2 text-sm text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2">
                {clearing ? 'Clearing...' : 'Delete Permanently'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* User Management — admin only */}
      {user?.role === 'admin' && (
        <div className="mt-6">
          <UserManagementSection authToken={authToken} />
        </div>
      )}

      {/* Role Permissions — admin only */}
      {user?.role === 'admin' && (
        <RolePermissionsSection authToken={authToken} />
      )}

      {/* Team Management — admin only */}
      {user?.role === 'admin' && (
        <TeamManagementSection authToken={authToken} />
      )}

      {/* Integrations (Pipedream / Email / Slack) — admin + devops_manager */}
      {['admin', 'devops_manager'].includes(user?.role) && (
        <IntegrationsSection />
      )}

      {/* Deployment-specific admin settings (stage templates + freeze windows) live inside
          the Deployments module itself — Modules → Deployments → ⚙ Settings. */}
    </div>
  );
};

// ============================================================================
// CONTROLLER (Application Logic)
// ============================================================================

const App = () => {
  const [currentPage, setCurrentPage] = useState('dashboard');
  const [user, setUser] = useState(null);
  const [authToken, setAuthToken] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [burnCharts, setBurnCharts] = useState(null);
  const [dashboardEnhancements, setDashboardEnhancements] = useState(null);
  // Dashboard's read-only Deployment Overview reuses the exact same list the full
  // Deployments module fetches (ModulesModel.getDeployments) — no separate endpoint.
  const [dashboardDeployments, setDashboardDeployments] = useState(null);
  const [dashboardTab, setDashboardTab] = useState('daily');
  const [modules, setModules] = useState([]);
  const [leaderboardData, setLeaderboardData] = useState(null);
  const [leaderboardPeriod, setLeaderboardPeriod] = useState('weekly');
  const [leaderboardRangeStart, setLeaderboardRangeStart] = useState('');
  const [leaderboardRangeEnd, setLeaderboardRangeEnd] = useState('');
  const [devOpsData, setDevOpsData] = useState(null);
  const [devOpsViewMode, setDevOpsViewMode] = useState('sprint');
  const [devOpsGroup, setDevOpsGroup] = useState('all');
  const [showAddDevOpsTask, setShowAddDevOpsTask] = useState(false);
  const [showAddDataOpsTask, setShowAddDataOpsTask] = useState(false);
  const [selectedDevOpsTask, setSelectedDevOpsTask] = useState(null);
  const [selectedBug, setSelectedBug] = useState(null);
  const [planTrackData, setPlanTrackData] = useState(null);
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [projectDetail, setProjectDetail] = useState(null);
  const [showAddProject, setShowAddProject] = useState(false);
  const [showAddSprint, setShowAddSprint] = useState(false);
  const [showEditProject, setShowEditProject] = useState(false);
  const [showSprintsView, setShowSprintsView] = useState(false);
  const [showMilestoneEngine, setShowMilestoneEngine] = useState(false);
  const [showPostUpdateReport, setShowPostUpdateReport] = useState(false);
  const [showSendEod, setShowSendEod] = useState(false);
  const [eodModule, setEodModule] = useState('devops');
  const [showEodSettings, setShowEodSettings] = useState(false);
  const [showQaEod, setShowQaEod] = useState(false);
  const [showAddMilestone, setShowAddMilestone] = useState(false);
  const [editingMilestoneId, setEditingMilestoneId] = useState(null);
  const [editingMilestoneData, setEditingMilestoneData] = useState(null);
  const [dataOpsData, setDataOpsData] = useState(null);
  const [dataOpsViewMode, setDataOpsViewMode] = useState('dashboard');
  const [dataOpsKanban, setDataOpsKanban] = useState(null);
  const [showAddPipeline, setShowAddPipeline] = useState(false);
  const [showAddReport, setShowAddReport] = useState(false);
  const [selectedPipeline, setSelectedPipeline] = useState(null);
  const [selectedReport, setSelectedReport] = useState(null);
  const [learnTrackData, setLearnTrackData] = useState(null);
  const [learnTrackViewMode, setLearnTrackViewMode] = useState('dashboard');
  const [polarisData, setPolarisData] = useState(null);
  const [polarisShowArchive, setPolarisShowArchive] = useState(false);
  const [crData, setCrData] = useState(null);
  const [crDetail, setCrDetail] = useState(null);
  const [crShowForm, setCrShowForm] = useState(false);
  const [crEditing, setCrEditing] = useState(null);
  const [crSettings, setCrSettings] = useState(null);
  const [crShowSettings, setCrShowSettings] = useState(false);
  const [showAddCourse, setShowAddCourse] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [learnFilterSkill, setLearnFilterSkill] = useState('');
  const [learnFilterGroup, setLearnFilterGroup] = useState('');
  const [timeBoardData, setTimeBoardData] = useState(null);
  const [leaveBoardData, setLeaveBoardData] = useState(null);
  const [meetingBoardData, setMeetingBoardData] = useState(null);
  const [showScheduleMeeting, setShowScheduleMeeting] = useState(false);
  const [knowledgeData, setKnowledgeData] = useState(null);
  const [knowledgeSearch, setKnowledgeSearch] = useState('');
  const [knowledgeSelectedCategory, setKnowledgeSelectedCategory] = useState(null);
  const [knowledgeViewingArticle, setKnowledgeViewingArticle] = useState(null);
  const [knowledgeEditingArticle, setKnowledgeEditingArticle] = useState(null);
  const [knowledgeShowEditor, setKnowledgeShowEditor] = useState(false);
  const [knowledgeArticleVersions, setKnowledgeArticleVersions] = useState([]);
  const [knowledgeShowVersions, setKnowledgeShowVersions] = useState(false);
  const [knowledgeShowCategoryModal, setKnowledgeShowCategoryModal] = useState(false);
  const [incidentData, setIncidentData] = useState(null);
  const [incidentFilters, setIncidentFilters] = useState({ severity: '', status: '', team: '', search: '', category: '', environment: '' });
  const [viewingIncident, setViewingIncident] = useState(null);
  const [showCreateIncident, setShowCreateIncident] = useState(false);
  const [incidentAnalytics, setIncidentAnalytics] = useState(null);
  const [currentWeekStart, setCurrentWeekStart] = useState(() => {
    // Use Pacific timezone for week calculation
    const pacificDateStr = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Los_Angeles' });
    const d = new Date(pacificDateStr + 'T12:00:00');
    const dow = d.getDay();
    const diff = d.getDate() - dow + (dow === 0 ? -6 : 1);
    return new Date(d.setDate(diff)).toISOString().split('T')[0];
  });
  const [showRequestLeave, setShowRequestLeave] = useState(false);
  const [showTimeEntry, setShowTimeEntry] = useState(null);
  const [quickTrackData, setQuickTrackData] = useState(null);
  const [qtFilterStatus, setQtFilterStatus] = useState('');
  const [qtFilterPriority, setQtFilterPriority] = useState('');
  const [qtFilterCategory, setQtFilterCategory] = useState('');
  const [qtFilterDateFrom, setQtFilterDateFrom] = useState('');
  const [qtFilterDateTo, setQtFilterDateTo] = useState('');
  const [showAddQuickTask, setShowAddQuickTask] = useState(false);
  const [showBackfillQuickTask, setShowBackfillQuickTask] = useState(false);
  const [editingQuickTask, setEditingQuickTask] = useState(null);
  const [vendorEventsData, setVendorEventsData] = useState(null);
  const [veFilterVendor, setVeFilterVendor] = useState('');
  const [veFilterWorkflow, setVeFilterWorkflow] = useState('');
  const [veFilterStatus, setVeFilterStatus] = useState('');
  const [qualityData, setQualityData] = useState(null);
  const [qmsView, setQmsView] = useState('pipeline'); // 'pipeline' | 'bugs' | 'requirements' | 'rtm'
  const [viewingRequirementDetail, setViewingRequirementDetail] = useState(null);
  const [allBugsData, setAllBugsData] = useState(null);
  const [allBugsFilters, setAllBugsFilters] = useState({ projectId: '', severity: '', status: '', search: '', sort: 'created_at', order: 'desc' });
  const [viewingBugDetail, setViewingBugDetail] = useState(null);
  const [reportsData, setReportsData] = useState(null);
  const [taskManageData, setTaskManageData] = useState(null);
  const [taskManageFilters, setTaskManageFilters] = useState({ source: '', status: [], priority: [], assignee: [], project: [], search: '' });
  const [qmsStage, setQmsStage] = useState('requirements');
  const [qmsProjectId, setQmsProjectId] = useState(null);
  const [testPlans, setTestPlans] = useState([]);
  const [uatSignoffs, setUatSignoffs] = useState([]);
  const [prodVerifications, setProdVerifications] = useState([]);
  const [showAddRequirement, setShowAddRequirement] = useState(false);
  const [addReqDefaults, setAddReqDefaults] = useState({});
  const [showAddTestCase, setShowAddTestCase] = useState(false);
  const [showReportBug, setShowReportBug] = useState(false);
  const [reportBugPrefill, setReportBugPrefill] = useState(null);
  // One-shot deep link: DeploymentsModule owns its own `selectedId` state internally
  // (unlike bugs/CRs, which are lifted to top level), so a notification click has
  // nowhere to route to without this — see handleNotificationNavigate below.
  const [deepLinkDeploymentId, setDeepLinkDeploymentId] = useState(null);
  const [editingBug, setEditingBug] = useState(null);
  const [allProjects, setAllProjects] = useState([]);
  const [allTeamMembers, setAllTeamMembers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditStats, setAuditStats] = useState(null);
  const [auditHasMore, setAuditHasMore] = useState(false);
  const [auditOffset, setAuditOffset] = useState(0);
  const [auditFilters, setAuditFilters] = useState({ module: '', action: '', entityType: '', dateFrom: '', dateTo: '', search: '' });
  const [auditLoading, setAuditLoading] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [resetToken, setResetToken] = useState(null);
  const [needsSetup, setNeedsSetup] = useState(false);
  // Set when the backend can reach the app but not the database. Distinct from
  // needsSetup so a database outage never masquerades as a fresh install.
  const [dbUnavailable, setDbUnavailable] = useState('');
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [showMessaging, setShowMessaging] = useState(false);
  const [msgConversations, setMsgConversations] = useState([]);
  const [msgOpenUserId, setMsgOpenUserId] = useState(null);
  const [msgOpenUserName, setMsgOpenUserName] = useState('');

  const fetchUnreadMessages = async () => {
    const tok = localStorage.getItem('orbit_token');
    if (!tok) return;
    try {
      const [countRes, convRes] = await Promise.all([
        fetch('/api/messages/unread-count', { headers: { Authorization: `Bearer ${tok}` } }),
        fetch('/api/messages/conversations', { headers: { Authorization: `Bearer ${tok}` } }),
      ]);
      if (countRes.ok) { const d = await countRes.json(); setUnreadMessages(d.unread || 0); }
      if (convRes.ok) { const d = await convRes.json(); setMsgConversations(d.conversations || []); }
    } catch { /* ignore */ }
  };

  const fetchNotifications = async () => {
    const token = localStorage.getItem('orbit_token');
    if (!token) return;
    try {
      const res = await fetch('/api/notifications', { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setNotifications(data.notifications || []);
        setUnreadCount(data.unread || 0);
      }
    } catch { /* ignore */ }
  };

  const handleMarkNotificationRead = async (id) => {
    const token = localStorage.getItem('orbit_token');
    setNotifications(n => n.map(x => x.id === id ? { ...x, isRead: 1 } : x));
    setUnreadCount(c => Math.max(0, c - 1));
    await fetch(`/api/notifications/${id}/read`, { method: 'PUT', headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
  };

  const handleMarkAllRead = async () => {
    const token = localStorage.getItem('orbit_token');
    setNotifications(n => n.map(x => ({ ...x, isRead: 1 })));
    setUnreadCount(0);
    await fetch('/api/notifications/read-all', { method: 'PUT', headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
  };

  const handleDismissNotification = async (id) => {
    const token = localStorage.getItem('orbit_token');
    const notif = notifications.find(n => n.id === id);
    setNotifications(n => n.filter(x => x.id !== id));
    if (notif && !notif.isRead) setUnreadCount(c => Math.max(0, c - 1));
    await fetch(`/api/notifications/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
  };

  const handleNotificationNavigate = async (notification) => {
    const { entityType, entityId, type } = notification;
    const numId = entityId ? parseInt(entityId, 10) : null;
    switch (entityType) {
      case 'bug':
        setCurrentPage('quality');
        if (numId) { const bug = await ModulesModel.getBug(numId); if (bug?.id) setSelectedBug(bug); }
        break;
      case 'task':
        setCurrentPage('devops');
        if (entityId) { const task = await ModulesModel.getDevOpsTask(entityId); if (task) setSelectedDevOpsTask(task); }
        break;
      case 'quick_task':
        setCurrentPage('quicktrack');
        if (numId) { const task = await ModulesModel.getQuickTask(numId); if (task) setEditingQuickTask(task); }
        break;
      case 'meeting':
        setCurrentPage('meetingboard');
        break;
      case 'incident':
        setCurrentPage('incidentboard');
        break;
      case 'request':
        setCurrentPage('requestboard');
        break;
      case 'support_ticket':
        setCurrentPage('supportboard');
        break;
      case 'change_request':
        setCurrentPage('changerequest');
        if (numId) {
          const detail = await ModulesModel.getChangeRequest(numId);
          if (detail) setCrDetail(detail);
        }
        break;
      case 'deployment':
        setCurrentPage('deployments');
        if (numId) setDeepLinkDeploymentId(numId);
        break;
      default:
        if (type === 'bug_assigned' || type === 'bug_commented' || type === 'comment_replied') setCurrentPage('quality');
        else if (type === 'meeting_invite') setCurrentPage('meetingboard');
        else if (type === 'task_assigned') setCurrentPage('devops');
        else if (type === 'cr_approval_needed' || type === 'cr_approval_progress' || type === 'cr_approved' || type === 'cr_rejected' || type === 'cr_completed' || type === 'cr_commented' || type === 'cr_submitted') {
          setCurrentPage('changerequest');
          if (numId) {
            const detail = await ModulesModel.getChangeRequest(numId);
            if (detail) setCrDetail(detail);
          }
        }
        break;
    }
  };

  // Auth init: check setup status, localStorage token, handle OAuth callbacks
  useEffect(() => {
    const initAuth = async () => {
      // Check if first-run setup is needed (no users exist), and — before anything else —
      // whether this instance has been retired in favor of a newer Orbit URL (Settings →
      // Integrations → Legacy Instance Redirect). Both come from the same pre-auth check,
      // so fetch it once, up front, and let the redirect fire before OAuth/reset-token
      // handling or any UI renders. `unavailable` means the backend could not read the
      // users table at all — that is NOT the same as "no users exist", and must never open
      // the setup wizard. If the check itself fails (network, etc.), fail open — proceed to
      // normal login rather than risk a redirect loop or a stuck blank page.
      //
      // A one-time ?legacy_key=<key> in the URL lets a specific person keep using this
      // instance while everyone else gets redirected — the key is remembered in
      // localStorage so it only has to be shared/visited once, then stripped from the URL
      // so it never lingers in browser history or gets shared via a copied link.
      const earlyParams = new URLSearchParams(window.location.search);
      const urlLegacyKey = earlyParams.get('legacy_key');
      if (urlLegacyKey) {
        try { localStorage.setItem('orbit_legacy_bypass_key', urlLegacyKey); } catch {}
        earlyParams.delete('legacy_key');
        const qs = earlyParams.toString();
        window.history.replaceState({}, document.title, window.location.pathname + (qs ? `?${qs}` : ''));
      }
      let setupData = null;
      try {
        let storedLegacyKey = '';
        try { storedLegacyKey = localStorage.getItem('orbit_legacy_bypass_key') || ''; } catch {}
        const qp = storedLegacyKey ? `?legacy_key=${encodeURIComponent(storedLegacyKey)}` : '';
        const setupRes = await fetch(`/api/auth/setup-status${qp}`);
        setupData = await setupRes.json();
        if (setupData?.legacyRedirectEnabled && setupData?.legacyRedirectUrl && !setupData?.legacyRedirectBypassed) {
          window.location.href = setupData.legacyRedirectUrl;
          return;
        }
      } catch { /* ignore, proceed normally */ }

      // Check for OAuth callback (code in URL params)
      const params = new URLSearchParams(window.location.search);

      // Check for password reset token
      const rt = params.get('reset_token');
      if (rt) {
        setResetToken(rt);
        window.history.replaceState({}, document.title, '/');
        setAuthChecked(true);
        return;
      }

      if (setupData) {
        if (setupData.unavailable) {
          setDbUnavailable(setupData.error || 'The database is temporarily unavailable.');
          setAuthChecked(true);
          return;
        }
        if (setupData.needsSetup) {
          setNeedsSetup(true);
          setAuthChecked(true);
          return;
        }
      }

      const code = params.get('code');
      const state = params.get('state');
      if (code && state) {
        const provider = sessionStorage.getItem('oauth_provider');
        const redirectUri = sessionStorage.getItem('oauth_redirect_uri') || window.location.origin;
        sessionStorage.removeItem('oauth_provider');
        sessionStorage.removeItem('oauth_redirect_uri');
        try {
          let endpoint = '/api/auth/google/callback';
          const body = { code, redirectUri };
          if (provider === 'sso' || (state && state.startsWith('sso:'))) {
            endpoint = '/api/auth/sso/callback';
            body.issuerUrl = sessionStorage.getItem('sso_issuer') || '';
            sessionStorage.removeItem('sso_issuer');
          }
          const res = await fetch(endpoint, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
          const data = await res.json();
          if (data.token) {
            localStorage.setItem('orbit_token', data.token);
            localStorage.setItem('orbit_user', JSON.stringify(data.user));
            setAuthToken(data.token);
            setUser(data.user);
            setCurrentPage('dashboard');
            window.history.replaceState({}, document.title, '/');
            setAuthChecked(true);
            return;
          }
        } catch { /* fall through to login */ }
        window.history.replaceState({}, document.title, '/');
      }

      // Restore from localStorage
      const storedToken = localStorage.getItem('orbit_token');
      if (storedToken) {
        try {
          const res = await fetch('/api/auth/me', { headers: { Authorization: `Bearer ${storedToken}` } });
          if (res.ok) {
            const u = await res.json();
            setAuthToken(storedToken);
            setUser(u);
            setCurrentPage('dashboard');
            setAuthChecked(true);
            return;
          }
        } catch { /* token invalid */ }
        localStorage.removeItem('orbit_token');
        localStorage.removeItem('orbit_user');
      }
      setAuthChecked(true);
    };
    initAuth();
  }, []);

  // Notification polling — every 30 seconds
  useEffect(() => {
    if (!user) return;
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [user]);

  // Message unread count polling — every 10 seconds
  useEffect(() => {
    if (!user) return;
    fetchUnreadMessages();
    const interval = setInterval(fetchUnreadMessages, 10000);
    return () => clearInterval(interval);
  }, [user]);

  useEffect(() => {
    if (user) loadData();
  }, [currentPage, devOpsViewMode, dataOpsViewMode, currentWeekStart, veFilterVendor, veFilterWorkflow, veFilterStatus, leaderboardPeriod, leaderboardRangeStart, leaderboardRangeEnd, user]);

  useEffect(() => {
    if (user) {
      ModulesModel.getTeamMembers().then(members => setAllTeamMembers(members));
      ModulesModel.getUserDirectory().then(users => setAllUsers(users));
      ModulesModel.getPlanTrackData().then(data => setAllProjects(data?.projects || []));
    }
  }, [user]);

  useEffect(() => {
    if (!user || currentPage !== 'vendorevents') return;
    const interval = setInterval(() => {
      ModulesModel.getVendorEventsData({ vendor: veFilterVendor, workflow: veFilterWorkflow, status: veFilterStatus })
        .then(data => setVendorEventsData(data));
    }, 60000);
    return () => clearInterval(interval);
  }, [user, currentPage, veFilterVendor, veFilterWorkflow, veFilterStatus]);

  const loadData = async () => {
    setLoading(true);
    try {
      if (currentPage === 'dashboard') {
        const [data, charts, enhancements, deployments] = await Promise.all([DashboardModel.getStats(), DashboardModel.getBurnCharts(), DashboardModel.getEnhancements(), ModulesModel.getDeployments()]);
        setDashboardData(data);
        setBurnCharts(charts);
        setDashboardEnhancements(enhancements);
        setDashboardDeployments(deployments);
      } else if (currentPage === 'modules') {
        const data = await ModulesModel.getModules();
        setModules(data);
      } else if (currentPage === 'leaderboard') {
        const data = await ModulesModel.getLeaderboard(leaderboardPeriod, leaderboardRangeStart, leaderboardRangeEnd);
        setLeaderboardData(data);
      } else if (currentPage === 'devops') {
        const devops = await ModulesModel.getDevOpsPipeline();
        if (devOpsViewMode === 'kanban') {
          const kanban = await ModulesModel.getDevOpsKanban();
          devops.kanbanPipeline = kanban.pipeline;
        }
        setDevOpsData(devops);
      } else if (currentPage === 'plantrack') {
        const data = await ModulesModel.getPlanTrackData();
        setPlanTrackData(data);
        if (selectedProjectId) {
          const detail = await ModulesModel.getProjectDetail(selectedProjectId);
          setProjectDetail(detail);
        }
      } else if (currentPage === 'dataops') {
        const data = await ModulesModel.getDataOpsData();
        setDataOpsData(data);
      } else if (currentPage === 'learntrack') {
        const data = await ModulesModel.getLearnTrackData();
        setLearnTrackData(data);
      } else if (currentPage === 'polaris') {
        const data = await ModulesModel.getPolarisData();
        setPolarisData(data);
      } else if (currentPage === 'changerequest') {
        const [data, settings] = await Promise.all([
          ModulesModel.getChangeRequests(),
          ModulesModel.getChangeRequestSettings(),
        ]);
        setCrData(data);
        if (settings) setCrSettings(settings);
      } else if (currentPage === 'timeboard') {
        const data = await ModulesModel.getTimeBoardData(currentWeekStart);
        setTimeBoardData(data);
      } else if (currentPage === 'leaveboard') {
        const data = await ModulesModel.getLeaveBoardData();
        setLeaveBoardData(data);
      } else if (currentPage === 'meetingboard') {
        const data = await ModulesModel.getMeetingBoardData();
        setMeetingBoardData(data);
      } else if (currentPage === 'knowledgeboard') {
        const data = await ModulesModel.getKnowledgeData({ categoryId: knowledgeSelectedCategory, search: knowledgeSearch });
        setKnowledgeData(data);
      } else if (currentPage === 'incidentboard') {
        const data = await ModulesModel.getIncidentsData(incidentFilters);
        setIncidentData(data);
      } else if (currentPage === 'quality') {
        const [data, bugsData] = await Promise.all([
          ModulesModel.getQualityData(qmsProjectId),
          ModulesModel.getAllBugs(allBugsFilters)
        ]);
        setQualityData(data);
        setAllBugsData(bugsData);
        if (data?.project?.currentStage) setQmsStage(data.project.currentStage);
      } else if (currentPage === 'quicktrack') {
        const [data, ptData] = await Promise.all([ModulesModel.getQuickTrackData(), ModulesModel.getPlanTrackData()]);
        setQuickTrackData(data);
        setAllProjects(ptData?.projects || []);
      } else if (currentPage === 'vendorevents') {
        const data = await ModulesModel.getVendorEventsData({ vendor: veFilterVendor, workflow: veFilterWorkflow, status: veFilterStatus });
        setVendorEventsData(data);
      } else if (currentPage === 'reports') {
        const data = await ModulesModel.getReportsData();
        setReportsData(data);
      } else if (currentPage === 'tasks') {
        const data = await ModulesModel.getTaskManageData(taskManageFilters);
        setTaskManageData(data);
      } else if (currentPage === 'audit') {
        setAuditLoading(true);
        const [logsData, statsData] = await Promise.all([
          ModulesModel.getAuditLogs({ ...auditFilters, offset: 0 }),
          ModulesModel.getAuditStats()
        ]);
        setAuditLogs(logsData.logs || []);
        setAuditHasMore(logsData.hasMore || false);
        setAuditOffset(0);
        setAuditStats(statsData);
        setAuditLoading(false);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    }
    setLoading(false);
  };

  const handleSelectProject = async (projectId) => {
    setSelectedProjectId(projectId);
    setLoading(true);
    try {
      const detail = await ModulesModel.getProjectDetail(projectId);
      setProjectDetail(detail);
    } catch (error) {
      console.error('Error loading project detail:', error);
    }
    setLoading(false);
  };

  const handleLogin = (token, userData) => {
    localStorage.setItem('orbit_token', token);
    localStorage.setItem('orbit_user', JSON.stringify(userData));
    setAuthToken(token);
    setUser(userData);
    setCurrentPage('dashboard');
  };

  const handleLogout = async () => {
    try {
      await fetch('/api/auth/logout', { method: 'POST', headers: { Authorization: `Bearer ${authToken}` } });
    } catch { /* ignore */ }
    localStorage.removeItem('orbit_token');
    localStorage.removeItem('orbit_user');
    setAuthToken(null);
    setUser(null);
  };

  const handleBackToProjects = () => {
    setSelectedProjectId(null);
    setProjectDetail(null);
  };

  const handleAuditFilterChange = async (newFilters) => {
    setAuditFilters(newFilters);
    setAuditLoading(true);
    try {
      const logsData = await ModulesModel.getAuditLogs({ ...newFilters, offset: 0 });
      setAuditLogs(logsData.logs || []);
      setAuditHasMore(logsData.hasMore || false);
      setAuditOffset(0);
    } catch { /* ignore */ }
    setAuditLoading(false);
  };

  const handleAuditLoadMore = async () => {
    const nextOffset = auditOffset + 50;
    setAuditLoading(true);
    try {
      const logsData = await ModulesModel.getAuditLogs({ ...auditFilters, offset: nextOffset });
      setAuditLogs(prev => [...prev, ...(logsData.logs || [])]);
      setAuditHasMore(logsData.hasMore || false);
      setAuditOffset(nextOffset);
    } catch { /* ignore */ }
    setAuditLoading(false);
  };

  const handleRefreshProjectDetail = async () => {
    if (!selectedProjectId) return;
    try {
      const detail = await ModulesModel.getProjectDetail(selectedProjectId);
      setProjectDetail(detail);
    } catch (error) {
      console.error('Error refreshing project detail:', error);
    }
  };

  const handleCreateProject = async (formData) => {
    await ModulesModel.createProject(formData);
    setShowAddProject(false);
    const data = await ModulesModel.getPlanTrackData();
    setPlanTrackData(data);
  };

  const handleCreateSprint = async (formData) => {
    await ModulesModel.createSprint(formData);
    setShowAddSprint(false);
    const data = await ModulesModel.getPlanTrackData();
    setPlanTrackData(data);
  };

  const handleUpdateSprint = async (sprintId, data) => {
    await ModulesModel.updateSprint(sprintId, data);
    const refreshed = await ModulesModel.getPlanTrackData();
    setPlanTrackData(refreshed);
  };

  const handleDeleteSprint = async (sprintId) => {
    const result = await ModulesModel.deleteSprint(sprintId);
    if (result?.success) {
      const refreshed = await ModulesModel.getPlanTrackData();
      setPlanTrackData(refreshed);
    } else {
      alert(`Could not delete sprint: ${result?.error || 'Unknown error'}`);
    }
    return result;
  };

  const handleUpdateProject = async (formData) => {
    await ModulesModel.updateProject(selectedProjectId, formData);
    setShowEditProject(false);
    const data = await ModulesModel.getPlanTrackData();
    setPlanTrackData(data);
    const detail = await ModulesModel.getProjectDetail(selectedProjectId);
    setProjectDetail(detail);
  };

  const handleToggleArchive = async (projectId, archived) => {
    await ModulesModel.updateProject(projectId, { archived: archived ? 1 : 0 });
    const data = await ModulesModel.getPlanTrackData();
    setPlanTrackData(data);
    if (selectedProjectId === projectId) {
      const detail = await ModulesModel.getProjectDetail(projectId);
      setProjectDetail(detail);
    }
  };

  const handleDeleteProject = async (projectId, projectName) => {
    // Stage 1 — probe (without force) to count linked records
    const probe = await ModulesModel.deleteProject(projectId, false);
    if (probe?.success) {
      // Empty project — deleted immediately, no confirm needed
      setSelectedProjectId(null);
      setProjectDetail(null);
      const data = await ModulesModel.getPlanTrackData();
      setPlanTrackData(data);
      return;
    }
    if (probe?.needsConfirmation) {
      const c = probe.counts || {};
      const qmsRecords = (c.testPlans || 0) + (c.testExecutions || 0) + (c.uatSignoffs || 0) + (c.prodVerifications || 0) + (c.qaPromotions || 0);
      const polarisRecords = (c.polarisMeta || 0) + (c.polarisBullets || 0);
      const charterStakeholders = (c.charters || 0) + (c.stakeholders || 0);
      const purgeLines = [
        c.tasks ? `  • ${c.tasks} task${c.tasks === 1 ? '' : 's'}` : null,
        c.milestones ? `  • ${c.milestones} milestone${c.milestones === 1 ? '' : 's'}` : null,
        (c.comments + c.updates) ? `  • ${c.comments + c.updates} comment/update entrie${(c.comments + c.updates) === 1 ? '' : 's'}` : null,
        c.attachments ? `  • ${c.attachments} attachment${c.attachments === 1 ? '' : 's'}` : null,
        c.risks ? `  • ${c.risks} risk register entrie${c.risks === 1 ? 'y' : 's'}` : null,
        qmsRecords ? `  • ${qmsRecords} QMS record${qmsRecords === 1 ? '' : 's'} (test plans, executions, sign-offs, verifications)` : null,
        charterStakeholders ? `  • ${charterStakeholders} charter/stakeholder record${charterStakeholders === 1 ? '' : 's'}` : null,
        polarisRecords ? `  • ${polarisRecords} Polaris item${polarisRecords === 1 ? '' : 's'} (overlay + bullets)` : null,
      ].filter(Boolean);
      const unlinkLines = [
        c.bugs ? `  • ${c.bugs} QA bug${c.bugs === 1 ? '' : 's'}` : null,
        c.requirements ? `  • ${c.requirements} requirement${c.requirements === 1 ? '' : 's'}` : null,
        c.testCases ? `  • ${c.testCases} test case${c.testCases === 1 ? '' : 's'}` : null,
      ].filter(Boolean);
      const msg = [
        `PERMANENTLY delete project "${probe.projectName}"?`,
        '',
        purgeLines.length ? 'The following will be DELETED:' : 'No owned records to delete.',
        ...purgeLines,
        unlinkLines.length ? '\nThese QA records will be UNLINKED (preserved):' : '',
        ...unlinkLines,
        '',
        'This cannot be undone. Type the project name to confirm:',
      ].join('\n');
      const typed = prompt(msg);
      if (typed === null) return; // user cancelled
      if (typed.trim() !== probe.projectName) {
        alert('Project name did not match. Deletion cancelled.');
        return;
      }
      const result = await ModulesModel.deleteProject(projectId, true);
      if (result?.success) {
        setSelectedProjectId(null);
        setProjectDetail(null);
        const data = await ModulesModel.getPlanTrackData();
        setPlanTrackData(data);
      } else {
        alert(`Could not delete project: ${result?.error || 'Unknown error'}`);
      }
    } else {
      alert(`Could not delete project: ${probe?.error || 'Unknown error'}`);
    }
  };

  // Milestone Engine handlers
  const refreshPlanTrack = async () => {
    const data = await ModulesModel.getPlanTrackData();
    setPlanTrackData(data);
    if (selectedProjectId) {
      const detail = await ModulesModel.getProjectDetail(selectedProjectId);
      setProjectDetail(detail);
    }
  };

  const handleCreateMilestone = async (formData) => {
    await ModulesModel.createMilestone(formData);
    setShowAddMilestone(false);
    await refreshPlanTrack();
  };

  const handleUpdateMilestone = async (msId, data) => {
    await ModulesModel.updateMilestone(msId, data);
    setEditingMilestoneId(null); setEditingMilestoneData(null);
    await refreshPlanTrack();
  };

  const handleDeleteMilestone = async (msId) => {
    await ModulesModel.deleteMilestone(msId);
    setEditingMilestoneId(null); setEditingMilestoneData(null);
    await refreshPlanTrack();
  };

  const handleCompleteMilestone = async (msId) => {
    const r = await ModulesModel.completeMilestone(msId);
    if (!r.success) alert(r.error || 'Failed to complete milestone');
    await refreshPlanTrack();
  };

  const handleEditMilestone = async (msId) => {
    const detail = await ModulesModel.getMilestoneDetail(msId);
    if (detail) { setEditingMilestoneId(msId); setEditingMilestoneData(detail); }
  };


  // DevOps handlers
  const handleCreateDevOpsTask = async (formData) => {
    await ModulesModel.createDevOpsTask(formData);
    setShowAddDevOpsTask(false);
    const devops = await ModulesModel.getDevOpsPipeline();
    if (devOpsViewMode === 'kanban') {
      const kanban = await ModulesModel.getDevOpsKanban();
      devops.kanbanPipeline = kanban.pipeline;
    }
    setDevOpsData(devops);
  };

  const handleCreateDataOpsTask = async (formData) => {
    await ModulesModel.createDataOpsTask(formData);
    setShowAddDataOpsTask(false);
    await refreshDataOps();
  };

  const handleDevOpsTaskClick = async (taskId) => {
    if (String(taskId).startsWith('QT-')) {
      const role = user?.role || user?.user_role;
      const isManager = role === 'manager' || role === 'devops_manager' || role === 'admin';
      if (!isManager) return;
      const numericId = parseInt(String(taskId).replace('QT-', ''), 10);
      const task = await ModulesModel.getQuickTask(numericId);
      if (task) setEditingQuickTask(task);
      return;
    }
    // Bugs: admins/managers get full edit modal; others get view-only panel
    // QA bugs have IDs like BUG-1, BUG-3 (pure numeric suffix)
    // DevOps bug tasks have IDs like BUG-2026-947 (year-sequence) — treat as regular tasks
    if (/^BUG-\d+$/.test(String(taskId))) {
      const numericId = parseInt(String(taskId).replace('BUG-', ''), 10);
      const bug = await ModulesModel.getBug(numericId);
      if (!bug?.id) return;
      if (user?.role === 'admin' || user?.role?.endsWith('_manager') || user?.role === 'manager') {
        setEditingBug(bug);
      } else {
        setSelectedBug(bug);
      }
      return;
    }
    // Try to find the task in current data first
    let task = null;
    if (devOpsData?.pipeline) {
      for (const items of Object.values(devOpsData.pipeline)) {
        task = items.find(t => t.id === taskId);
        if (task) break;
      }
    }
    if (devOpsData?.kanbanPipeline && !task) {
      for (const items of Object.values(devOpsData.kanbanPipeline)) {
        task = items.find(t => t.id === taskId);
        if (task) break;
      }
    }
    // Fallback: fetch from API
    if (!task) {
      task = await ModulesModel.getDevOpsTask(taskId);
    }
    if (task) setSelectedDevOpsTask(task);
  };

  const handleDevOpsKanbanDelete = async (taskId) => {
    if (!String(taskId).startsWith('QT-')) return;
    const qtId = parseInt(String(taskId).replace('QT-', ''), 10);
    await ModulesModel.deleteQuickTask(qtId);
    const devops = await ModulesModel.getDevOpsPipeline();
    const kanban = await ModulesModel.getDevOpsKanban();
    devops.kanbanPipeline = kanban.pipeline;
    setDevOpsData(devops);
  };

  const handleDevOpsStatusChange = async (taskId, newStatus, extra = {}) => {
    if (String(taskId).startsWith('QT-')) {
      const qtId = parseInt(String(taskId).replace('QT-', ''), 10);
      const qtStatusMap = { backlog: 'open', ready: 'open', in_progress: 'in_progress', review: 'in_progress', done: 'done', completed: 'done' };
      await ModulesModel.updateQuickTask(qtId, { status: qtStatusMap[newStatus] || 'open', ...extra });
    } else if (/^BUG-\d+$/.test(String(taskId))) {
      const bugId = parseInt(String(taskId).replace('BUG-', ''), 10);
      // Sprint column → bug status: backlog→open, in_progress→in_progress, review→resolved, needs_rework→needs_rework, done→closed
      const bugStatusMap = { backlog: 'open', ready: 'open', in_progress: 'in_progress', review: 'resolved', needs_rework: 'needs_rework', done: 'closed', completed: 'closed' };
      await ModulesModel.updateBug(bugId, { status: bugStatusMap[newStatus] || 'open' });
    } else {
      const taskUpdate = { status: newStatus };
      if (extra.actualMinutes) {
        taskUpdate.actualHours = parseFloat((extra.actualMinutes / 60).toFixed(2));
        taskUpdate.actualMinutes = extra.actualMinutes;
      }
      await ModulesModel.updateDevOpsTask(taskId, taskUpdate);
    }
    const devops = await ModulesModel.getDevOpsPipeline();
    if (devOpsViewMode === 'kanban') {
      const kanban = await ModulesModel.getDevOpsKanban();
      devops.kanbanPipeline = kanban.pipeline;
    }
    setDevOpsData(devops);
  };

  const handleDevOpsTaskUpdate = async (taskId, changes) => {
    await ModulesModel.updateDevOpsTask(taskId, changes);
    setSelectedDevOpsTask(null);
    const devops = await ModulesModel.getDevOpsPipeline();
    if (devOpsViewMode === 'kanban') {
      const kanban = await ModulesModel.getDevOpsKanban();
      devops.kanbanPipeline = kanban.pipeline;
    }
    setDevOpsData(devops);
  };

  const handleDeleteDevOpsTask = async (taskId) => {
    await ModulesModel.deleteDevOpsTask(taskId);
    setSelectedDevOpsTask(null);
    const devops = await ModulesModel.getDevOpsPipeline();
    if (devOpsViewMode === 'kanban') {
      const kanban = await ModulesModel.getDevOpsKanban();
      devops.kanbanPipeline = kanban.pipeline;
    }
    setDevOpsData(devops);
  };

  const handleDevOpsBugReassign = async (bugId, data) => {
    await ModulesModel.updateBug(bugId, data);
    const [devops, dataops] = await Promise.all([
      ModulesModel.getDevOpsPipeline(),
      ModulesModel.getDataOpsData(),
    ]);
    if (devOpsViewMode === 'kanban') {
      const kanban = await ModulesModel.getDevOpsKanban();
      devops.kanbanPipeline = kanban.pipeline;
    }
    setDevOpsData(devops);
    setDataOpsData(dataops);
  };

  // LearnTrack handlers
  const refreshLearnTrackData = async () => {
    const data = await ModulesModel.getLearnTrackData();
    setLearnTrackData(data);
  };

  const handleCourseClick = async (courseId) => {
    const course = await ModulesModel.getCourseDetail(courseId);
    if (course) setSelectedCourse(course);
  };

  const handleCreateCourse = async (formData) => {
    await ModulesModel.createCourse(formData);
    setShowAddCourse(false);
    await refreshLearnTrackData();
  };

  const handleUpdateCourse = async (courseId, data) => {
    await ModulesModel.updateCourse(courseId, data);
    setSelectedCourse(null);
    await refreshLearnTrackData();
  };

  const handleDeleteCourse = async (courseId) => {
    await ModulesModel.deleteCourse(courseId);
    setSelectedCourse(null);
    await refreshLearnTrackData();
  };

  const handleEnrollUser = async (data) => {
    await ModulesModel.enrollUser(data);
    await refreshLearnTrackData();
    if (selectedCourse) {
      const updated = await ModulesModel.getCourseDetail(selectedCourse.id);
      setSelectedCourse(updated);
    }
  };

  const handleUpdateEnrollment = async (enrollmentId, data) => {
    const res = await ModulesModel.updateEnrollment(enrollmentId, data);
    if (res?.success === false) { alert(`Failed to update enrollment: ${res?.error || 'Unknown error'}`); return; }
    await refreshLearnTrackData();
    // Also refresh the open course detail modal if present
    if (selectedCourse) {
      const fresh = await ModulesModel.getCourseDetail(selectedCourse.id);
      if (fresh) setSelectedCourse(fresh);
    }
  };

  const handleDeleteEnrollment = async (enrollmentId) => {
    const res = await ModulesModel.deleteEnrollment(enrollmentId);
    if (res?.success === false) { alert(`Failed to remove enrollment: ${res?.error || 'Unknown error'}`); return; }
    await refreshLearnTrackData();
    if (selectedCourse) {
      const fresh = await ModulesModel.getCourseDetail(selectedCourse.id);
      if (fresh) setSelectedCourse(fresh);
    }
  };

  // TimeBoard handlers
  const refreshTimeBoardData = async () => {
    const data = await ModulesModel.getTimeBoardData(currentWeekStart);
    setTimeBoardData(data);
  };

  const handleCreateTimeEntry = async (formData) => {
    await ModulesModel.createTimesheetEntry({ ...formData, weekStart: currentWeekStart });
    setShowTimeEntry(null);
    await refreshTimeBoardData();
  };

  const handleDeleteTimeEntry = async (id) => {
    await ModulesModel.deleteTimesheetEntry(id);
    await refreshTimeBoardData();
  };

  const handleSubmitTimesheet = async (id) => {
    await ModulesModel.submitTimesheet(id);
    await refreshTimeBoardData();
  };

  const handleApproveTimesheet = async (id) => {
    await ModulesModel.approveTimesheet(id);
    await refreshTimeBoardData();
  };

  const handleSyncCompletedTasks = async (defaultHours = 1) => {
    const result = await ModulesModel.syncCompletedTasks(defaultHours);
    await refreshTimeBoardData();
    return result;
  };

  const handleWeekChange = (direction) => {
    const current = new Date(currentWeekStart + 'T12:00:00');
    current.setDate(current.getDate() + (direction > 0 ? 7 : -7));
    setCurrentWeekStart(current.toISOString().split('T')[0]);
  };

  // LeaveBoard handlers
  const refreshLeaveBoardData = async () => {
    const data = await ModulesModel.getLeaveBoardData();
    setLeaveBoardData(data);
  };

  const handleCreateLeaveRequest = async (formData) => {
    await ModulesModel.createLeaveRequest(formData);
    setShowRequestLeave(false);
    await refreshLeaveBoardData();
  };

  const handleUpdateLeaveRequest = async (id, data) => {
    await ModulesModel.updateLeaveRequest(id, data);
    await refreshLeaveBoardData();
  };

  const handleDeleteLeaveRequest = async (id) => {
    const r = await ModulesModel.deleteLeaveRequest(id);
    if (r?.success) {
      await refreshLeaveBoardData();
    } else {
      alert(r?.error || 'Could not delete this leave request.');
    }
  };

  // MeetingBoard handlers
  const refreshMeetingBoardData = async () => {
    const data = await ModulesModel.getMeetingBoardData();
    setMeetingBoardData(data);
  };

  const handleCreateMeeting = async (formData) => {
    await ModulesModel.createMeeting({ ...formData, hostId: user?.id, hostName: user?.name });
    setShowScheduleMeeting(false);
    await refreshMeetingBoardData();
  };

  const handleEndMeeting = async (id) => {
    await ModulesModel.updateMeeting(id, { status: 'ended' });
    await refreshMeetingBoardData();
  };

  const handleStartMeeting = async (id) => {
    await ModulesModel.updateMeeting(id, { status: 'ongoing' });
    await refreshMeetingBoardData();
  };

  const handleCancelMeeting = async (id) => {
    await ModulesModel.updateMeeting(id, { status: 'cancelled' });
    await refreshMeetingBoardData();
  };

  const handleDeleteMeeting = async (id) => {
    await ModulesModel.deleteMeeting(id);
    await refreshMeetingBoardData();
  };

  // KnowledgeBoard handlers
  const refreshKnowledgeData = async (catId, search) => {
    const data = await ModulesModel.getKnowledgeData({ categoryId: catId ?? knowledgeSelectedCategory, search: search ?? knowledgeSearch });
    setKnowledgeData(data);
  };

  const handleKnowledgeCategorySelect = async (catId) => {
    setKnowledgeSelectedCategory(catId);
    setKnowledgeViewingArticle(null);
    const data = await ModulesModel.getKnowledgeData({ categoryId: catId, search: knowledgeSearch });
    setKnowledgeData(data);
  };

  const handleKnowledgeSearch = async (q) => {
    setKnowledgeSearch(q);
    const data = await ModulesModel.getKnowledgeData({ categoryId: knowledgeSelectedCategory, search: q });
    setKnowledgeData(data);
  };

  const handleViewKnowledgeArticle = async (id) => {
    const article = await ModulesModel.getKnowledgeArticle(id);
    setKnowledgeViewingArticle(article);
    setKnowledgeShowVersions(false);
    setKnowledgeArticleVersions([]);
  };

  const handleKnowledgeCreateArticle = () => {
    setKnowledgeEditingArticle(null);
    setKnowledgeShowEditor(true);
  };

  const handleKnowledgeEditArticle = (article) => {
    setKnowledgeEditingArticle(article);
    setKnowledgeShowEditor(true);
  };

  const handleKnowledgeSaveArticle = async (data) => {
    let result;
    if (knowledgeEditingArticle) {
      result = await ModulesModel.updateKnowledgeArticle(knowledgeEditingArticle.id, data);
    } else {
      result = await ModulesModel.createKnowledgeArticle({ ...data, createdBy: user?.name, createdById: user?.id });
    }
    if (!result?.success) {
      alert(result?.error || 'Failed to save article. Please try again.');
      return;
    }
    setKnowledgeShowEditor(false);
    setKnowledgeEditingArticle(null);
    await refreshKnowledgeData();
    if (result.article) setKnowledgeViewingArticle(result.article);
  };

  const handleKnowledgePublishArticle = async (article) => {
    const result = await ModulesModel.updateKnowledgeArticle(article.id, { title: article.title, categoryId: article.category_id, content: article.content, status: 'published', updatedBy: user?.name, updatedById: user?.id });
    if (!result?.success) {
      alert(result?.error || 'Failed to publish article. Please try again.');
      return;
    }
    await refreshKnowledgeData();
    setKnowledgeViewingArticle(result.article);
  };

  const handleKnowledgeDeleteArticle = async (id) => {
    if (!window.confirm('Delete this article? This cannot be undone.')) return;
    await ModulesModel.deleteKnowledgeArticle(id);
    setKnowledgeViewingArticle(null);
    await refreshKnowledgeData();
  };

  const handleViewArticleVersions = async (articleId) => {
    const versions = await ModulesModel.getArticleVersions(articleId);
    setKnowledgeArticleVersions(versions);
    setKnowledgeShowVersions(true);
  };

  const handleRestoreVersion = async (articleId, versionId) => {
    if (!window.confirm('Restore this version? The current content will be saved as a version.')) return;
    const result = await ModulesModel.restoreArticleVersion(articleId, versionId);
    setKnowledgeShowVersions(false);
    if (result?.id) setKnowledgeViewingArticle(result);
    await refreshKnowledgeData();
  };

  const handleCreateKnowledgeCategory = async (data) => {
    await ModulesModel.createKnowledgeCategory({ ...data, createdBy: user?.user_name });
    setKnowledgeShowCategoryModal(false);
    await refreshKnowledgeData();
  };

  // IncidentBoard handlers
  const refreshIncidentData = async () => {
    const data = await ModulesModel.getIncidentsData(incidentFilters);
    setIncidentData(data);
  };

  const handleIncidentFilterChange = async (newFilters) => {
    const merged = { ...incidentFilters, ...newFilters };
    setIncidentFilters(merged);
    const data = await ModulesModel.getIncidentsData(merged);
    setIncidentData(data);
  };

  const handleViewIncident = async (id) => {
    const incident = await ModulesModel.getIncident(id);
    setViewingIncident(incident);
  };

  const handleCreateIncident = async (data) => {
    await ModulesModel.createIncident({ ...data, reportedBy: user?.user_name, reportedById: user?.user_id || user?.id });
    setShowCreateIncident(false);
    await refreshIncidentData();
  };

  const handleUpdateIncident = async (id, data) => {
    const result = await ModulesModel.updateIncident(id, data);
    if (result?.success) {
      setViewingIncident(result.incident);
      await refreshIncidentData();
    }
  };

  const handleAddIncidentUpdate = async (incidentId, message) => {
    const result = await ModulesModel.addIncidentUpdate(incidentId, { update_message: message });
    if (result?.success) {
      setViewingIncident(prev => prev ? { ...prev, updates: result.updates } : prev);
    }
  };

  const handleDeleteIncident = async (id) => {
    await ModulesModel.deleteIncident(id);
    setViewingIncident(null);
    await refreshIncidentData();
  };

  const handleLoadIncidentAnalytics = async () => {
    const data = await ModulesModel.getIncidentAnalytics();
    setIncidentAnalytics(data);
  };

  // QMS handlers
  const refreshQualityData = async () => {
    const data = await ModulesModel.getQualityData(qmsProjectId);
    setQualityData(data);
  };

  const refreshAllBugs = async (filters = allBugsFilters) => {
    const data = await ModulesModel.getAllBugs(filters);
    setAllBugsData(data);
  };

  const handleAllBugsFilterChange = async (newFilters) => {
    const merged = { ...allBugsFilters, ...newFilters };
    setAllBugsFilters(merged);
    await refreshAllBugs(merged);
  };

  const handleViewBugDetail = async (id) => {
    if (id === 'list') { setQmsView('bugs'); setViewingBugDetail(null); return; }
    const bug = await ModulesModel.getBugDetail(id);
    setViewingBugDetail(bug);
    setQmsView('bugs');
  };

  const handleBugDetailUpdate = async (id, changes) => {
    await ModulesModel.updateBug(id, changes);
    const updated = await ModulesModel.getBugDetail(id);
    setViewingBugDetail(updated);
    await refreshAllBugs();
    await refreshQualityData();
  };

  const handleBugDetailComment = async (bugId, comment) => {
    await ModulesModel.addBugComment(bugId, { userName: user?.user_name, content: comment });
    const updated = await ModulesModel.getBugDetail(bugId);
    setViewingBugDetail(updated);
  };

  const handleDeleteBugComment = async (bugId, commentId) => {
    await ModulesModel.deleteBugComment(commentId);
    const updated = await ModulesModel.getBugDetail(bugId);
    setViewingBugDetail(updated);
  };

  const handleQmsStageChange = async (stage) => {
    setQmsStage(stage);
    if (qualityData?.project?.projectId) {
      await ModulesModel.updateQaProject(qualityData.project.projectId, { currentStage: stage });
    }
  };

  const handleQmsProjectChange = async (projectId) => {
    setQmsProjectId(projectId);
    const data = await ModulesModel.getQualityData(projectId);
    setQualityData(data);
    if (data?.project?.currentStage) setQmsStage(data.project.currentStage);
    const [plans, signoffs] = await Promise.all([
      ModulesModel.getTestPlans(projectId),
      ModulesModel.getUatSignoffs(projectId),
    ]);
    setTestPlans(plans);
    setUatSignoffs(signoffs);
  };

  const handleClearData = async (modules) => {
    return await ModulesModel.clearData(modules);
  };

  const handleCreateRequirement = async (data) => {
    const result = await ModulesModel.createRequirement(data);
    if (result?.success === false) { alert('Failed to save requirement: ' + (result.error || 'Unknown error')); return; }
    setShowAddRequirement(false);
    await refreshQualityData();
  };

  const handleApproveRequirement = async (id) => {
    await ModulesModel.updateRequirement(id, { status: 'approved' });
    await refreshQualityData();
  };

  const handleRejectRequirement = async (id) => {
    await ModulesModel.updateRequirement(id, { status: 'rejected' });
    await refreshQualityData();
  };

  const handleEditRequirement = async (id, data) => {
    const result = await ModulesModel.updateRequirement(id, data);
    if (result?.success === false) { alert('Failed to save requirement: ' + (result.error || 'Unknown error')); return; }
    await refreshQualityData();
  };

  const handleDeleteRequirement = async (id) => {
    try {
      const r = await fetch(`${'/api'}/quality/requirements/${id}`, { method: 'DELETE', headers: ModulesModel.authHeaders() });
      if (!r.ok) throw new Error('Delete failed');
    } catch (e) { alert('Failed to delete requirement'); return; }
    await refreshQualityData();
  };

  const handleCreateTestCase = async (data) => {
    const result = await ModulesModel.createTestCase(data);
    if (result?.success === false) { alert('Failed to save test case: ' + (result.error || 'Unknown error')); return; }
    setShowAddTestCase(false);
    await refreshQualityData();
  };

  const handlePassTestCase = async (id) => {
    await ModulesModel.updateTestCase(id, { status: 'pass', executedBy: user?.user_name || user?.name || 'QA Tester' });
    await refreshQualityData();
  };

  const handleFailTestCase = async (id) => {
    await ModulesModel.updateTestCase(id, { status: 'fail', executedBy: user?.user_name || user?.name || 'QA Tester' });
    await refreshQualityData();
  };

  const handleResetTestCase = async (id) => {
    await ModulesModel.updateTestCase(id, { status: 'pending', executedBy: null });
    await refreshQualityData();
  };

  const handleEditTestCase = async (id, data) => {
    await ModulesModel.updateTestCase(id, data);
    await refreshQualityData();
  };

  const handleDeleteTestCase = async (id) => {
    try {
      const r = await fetch(`${'/api'}/quality/testcases/${id}`, { method: 'DELETE', headers: ModulesModel.authHeaders() });
      if (!r.ok) throw new Error('Delete failed');
    } catch (e) { alert('Failed to delete test case'); return; }
    await refreshQualityData();
  };

  const handleReportBug = (tc) => {
    setReportBugPrefill(tc);
    setShowReportBug(true);
  };

  const handleCreateBug = async (data) => {
    if (!data?.projectId) {
      alert('Please select a project in Quality before reporting a bug.');
      return;
    }
    const result = await ModulesModel.createBug({ ...data, reportedBy: user?.name || user?.user_name });
    if (result?.success === false) { alert('Failed to save bug: ' + (result.error || 'Unknown error')); return; }
    if (result?.warning) alert(result.warning);
    setShowReportBug(false);
    setReportBugPrefill(null);
    await refreshQualityData();
  };

  const handleEditBug = async (bug) => {
    const role = user?.role || user?.user_role;
    const canEdit = ['admin', 'qa_lead', 'qa', 'devops', 'manager'].includes(role) || role?.endsWith('_manager');
    if (!canEdit) { setSelectedBug(bug); return; }
    // Bug rows from the All Bugs list don't include steps/expected/actual — fetch full detail first
    if (bug.stepsToReproduce === undefined) {
      const full = await ModulesModel.getBugDetail(bug.id);
      setEditingBug(full || bug);
    } else {
      setEditingBug(bug);
    }
  };

  const handleUpdateBug = async (id, data) => {
    const result = await ModulesModel.updateBug(id, data);
    if (result?.success === false) { alert('Failed to update bug: ' + (result.error || 'Unknown error')); return; }
    if (result?.warning) alert(result.warning);
    setEditingBug(null);
    if (currentPage === 'devops') {
      const devops = await ModulesModel.getDevOpsPipeline();
      if (devOpsViewMode === 'kanban') {
        const kanban = await ModulesModel.getDevOpsKanban();
        devops.kanbanPipeline = kanban.pipeline;
      }
      setDevOpsData(devops);
    } else {
      await refreshQualityData();
      await refreshAllBugs();
    }
  };

  const handlePromoteArtifacts = async ({ artifactType, artifactIds, targetEnvironment, projectId, includeLinked }) => {
    const pid = projectId || qualityData?.project?.projectId;
    const result = await ModulesModel.promoteArtifacts({ artifactType, artifactIds, targetEnvironment, projectId: pid, includeLinked });
    if (result?.success) { await refreshQualityData(); }
    return result;
  };

  const handleDeleteBug = async (id) => {
    try {
      const r = await fetch(`${'/api'}/quality/bugs/${id}`, { method: 'DELETE', headers: ModulesModel.authHeaders() });
      if (!r.ok) throw new Error('Delete failed');
    } catch (e) { alert('Failed to delete bug'); return; }
    setEditingBug(null);
    await refreshQualityData();
  };

  const handleUpdateQaProject = async (id, data) => {
    await ModulesModel.updateQaProject(id, data);
    await refreshQualityData();
  };

  const handleQaStatusChange = async (requirementId, qaStatus) => {
    await ModulesModel.updateRequirement(requirementId, { qaStatus });
    await refreshQualityData();
  };

  const handleTestResultChange = async (requirementId, testResult) => {
    await ModulesModel.updateRequirement(requirementId, { testResult });
    await refreshQualityData();
  };

  const refreshTestPlans = async (projectId) => {
    const pid = projectId || qualityData?.project?.projectId;
    if (!pid) return;
    const plans = await ModulesModel.getTestPlans(pid);
    setTestPlans(plans);
  };

  const handleCreateTestPlan = async (data) => {
    const r = await ModulesModel.createTestPlan(data);
    if (!r?.success) {
      alert(`Could not save the test plan: ${r?.error || 'Unknown error'}`);
      return r;
    }
    await refreshTestPlans(data.projectId);
    return r;
  };

  const handleUpdateTestPlan = async (planId, data) => {
    const r = await ModulesModel.updateTestPlan(planId, data);
    if (!r?.success) {
      alert(`Could not update the test plan: ${r?.error || 'Unknown error'}`);
      return r;
    }
    await refreshTestPlans(data.projectId);
    return r;
  };

  const handleDeleteTestPlan = async (plan) => {
    // Probe first — if dependent rows exist, show counts and ask the user to type the plan title
    const probe = await ModulesModel.deleteTestPlan(plan.id, false);
    if (probe?.success) {
      await refreshTestPlans(plan.project_id);
      return probe;
    }
    if (probe?.needsConfirmation) {
      const c = probe.counts || {};
      const lines = [
        `Permanently delete test plan "${probe.planTitle}"?`,
        '',
        c.executions    ? `  • ${c.executions} test execution${c.executions === 1 ? '' : 's'} (will be DELETED)` : null,
        c.signoffs      ? `  • ${c.signoffs} UAT sign-off${c.signoffs === 1 ? '' : 's'} (will be DELETED)` : null,
        c.prodChecks    ? `  • ${c.prodChecks} prod verification${c.prodChecks === 1 ? '' : 's'} (will be DELETED)` : null,
        c.attachedCases ? `  • ${c.attachedCases} test case${c.attachedCases === 1 ? '' : 's'} (will be DETACHED, not deleted)` : null,
        '',
        'This cannot be undone. Type the plan title to confirm:',
      ].filter(Boolean).join('\n');
      const typed = prompt(lines);
      if (typed === null) return { success: false, cancelled: true };
      if (typed.trim() !== probe.planTitle) {
        alert('Plan title did not match. Deletion cancelled.');
        return { success: false, cancelled: true };
      }
      const result = await ModulesModel.deleteTestPlan(plan.id, true);
      if (result?.success) await refreshTestPlans(plan.project_id);
      else alert(`Could not delete the test plan: ${result?.error || 'Unknown error'}`);
      return result;
    }
    alert(`Could not delete the test plan: ${probe?.error || 'Unknown error'}`);
    return probe;
  };

  const handleRecordExecution = async (data) => {
    await ModulesModel.recordExecution(data);
    await refreshQualityData();
  };

  const handleSubmitUatSignoff = async (data) => {
    await ModulesModel.submitUatSignoff(data);
    const signoffs = await ModulesModel.getUatSignoffs(data.projectId);
    setUatSignoffs(signoffs);
    await refreshQualityData();
  };

  const handleToggleProdVerification = async (id, data) => {
    await ModulesModel.updateProdVerification(id, data);
  };

  const handleCompleteProdVerification = async (projectId) => {
    await ModulesModel.completeProdVerification(projectId);
    await refreshQualityData();
  };

  // QuickTrack handlers
  const refreshQuickTrackData = async () => {
    const data = await ModulesModel.getQuickTrackData();
    setQuickTrackData(data);
  };

  // Shared by both the regular "New Task" modal and the "Backfill Past Task" modal.
  // Returns the result so the modal can show a validation error (e.g. missing/future
  // completed date) instead of closing as if it had succeeded.
  const handleCreateQuickTask = async (data) => {
    const result = await ModulesModel.createQuickTask(data);
    if (result?.success !== false) {
      setShowAddQuickTask(false);
      setShowBackfillQuickTask(false);
      await refreshQuickTrackData();
    }
    return result;
  };

  const handleUpdateQuickTask = async (taskId, data) => {
    await ModulesModel.updateQuickTask(taskId, data);
    await refreshQuickTrackData();
  };

  const handleDeleteQuickTask = async (taskId) => {
    await ModulesModel.deleteQuickTask(taskId);
    await refreshQuickTrackData();
  };

  const handleEditQuickTask = (task) => setEditingQuickTask(task);

  const handleSaveQuickTask = async (taskId, data) => {
    await ModulesModel.updateQuickTask(taskId, data);
    setEditingQuickTask(null);
    await refreshQuickTrackData();
  };

  // Vendor Events handlers
  const handleVeFilterVendor = (v) => setVeFilterVendor(v);
  const handleVeFilterWorkflow = (v) => setVeFilterWorkflow(v);
  const handleVeFilterStatus = (v) => setVeFilterStatus(v);
  const handleVeResetFilters = () => { setVeFilterVendor(''); setVeFilterWorkflow(''); setVeFilterStatus(''); };

  // Task Management handlers
  const handleTaskManageFilter = async (newFilters) => {
    const merged = { ...taskManageFilters, ...newFilters };
    setTaskManageFilters(merged);
    const data = await ModulesModel.getTaskManageData(merged);
    setTaskManageData(data);
  };
  const handleTaskManageBulk = async (tasks, updates) => {
    await ModulesModel.bulkUpdateTasks(tasks, updates);
    const data = await ModulesModel.getTaskManageData(taskManageFilters);
    setTaskManageData(data);
  };
  const handleTaskManageRefresh = async () => {
    const data = await ModulesModel.getTaskManageData(taskManageFilters);
    setTaskManageData(data);
  };


  // DataOps handlers
  const refreshDataOps = async () => {
    const data = await ModulesModel.getDataOpsData();
    setDataOpsData(data);
  };

  const refreshDataOpsKanban = async () => {
    const data = await ModulesModel.getDataOpsKanban();
    setDataOpsKanban(data);
  };

  const handleDataOpsViewModeChange = async (mode) => {
    setDataOpsViewMode(mode);
    if (mode === 'kanban' && !dataOpsKanban) {
      refreshDataOpsKanban();
    }
  };

  const handleDataOpsKanbanStatusChange = async (taskId, update) => {
    await ModulesModel.updateQuickTask(taskId, update);
    refreshDataOpsKanban();
  };

  const handleDataOpsKanbanDelete = async (taskId) => {
    await ModulesModel.deleteQuickTask(taskId);
    refreshDataOpsKanban();
  };

  const handleDataOpsTaskClick = async (taskId) => {
    const isQT = String(taskId).startsWith('QT-') || (!String(taskId).startsWith('BUG-') && /^\d+$/.test(String(taskId)));
    if (isQT) {
      const role = user?.role || user?.user_role;
      const isManager = role === 'manager' || role === 'dataops_manager' || role === 'admin';
      if (!isManager) return;
      const numericId = String(taskId).startsWith('QT-')
        ? parseInt(String(taskId).replace('QT-', ''), 10)
        : parseInt(String(taskId), 10);
      const task = await ModulesModel.getQuickTask(numericId);
      if (task) setEditingQuickTask(task);
      return;
    }
    if (/^BUG-\d+$/.test(String(taskId))) {
      const numericId = parseInt(String(taskId).replace('BUG-', ''), 10);
      const bug = await ModulesModel.getBug(numericId);
      if (!bug?.id) return;
      setSelectedBug(bug);
      return;
    }
    let task = null;
    if (dataOpsData?.sprintBoard) {
      for (const items of Object.values(dataOpsData.sprintBoard)) {
        task = items.find(t => t.id === taskId);
        if (task) break;
      }
    }
    if (!task) task = await ModulesModel.getDevOpsTask(taskId);
    if (task) setSelectedDevOpsTask(task);
  };

  const handleDataOpsStatusChange = async (taskId, newStatus) => {
    await ModulesModel.updateDevOpsTask(taskId, { status: newStatus });
    await refreshDataOps();
  };

  const handleSelectPipeline = async (pipelineId) => {
    const pipeline = await ModulesModel.getPipeline(pipelineId);
    if (pipeline) setSelectedPipeline(pipeline);
  };

  const handleCreatePipeline = async (formData) => {
    await ModulesModel.createPipeline(formData);
    setShowAddPipeline(false);
    await refreshDataOps();
  };

  const handleUpdatePipeline = async (pipelineId, data) => {
    await ModulesModel.updatePipeline(pipelineId, data);
    setSelectedPipeline(null);
    await refreshDataOps();
  };

  const handleDeletePipeline = async (pipelineId) => {
    await ModulesModel.deletePipeline(pipelineId);
    setSelectedPipeline(null);
    await refreshDataOps();
  };

  const handleTriggerPipeline = async (pipelineId) => {
    await ModulesModel.updatePipeline(pipelineId, { action: 'trigger' });
    await refreshDataOps();
  };

  const handleSelectReport = async (reportId) => {
    const report = await ModulesModel.getReport(reportId);
    await ModulesModel.updateReport(reportId, { action: 'view' });
    if (report) setSelectedReport(report);
  };

  const handleCreateReport = async (formData) => {
    await ModulesModel.createReport(formData);
    setShowAddReport(false);
    await refreshDataOps();
  };

  const handleUpdateReport = async (reportId, data) => {
    await ModulesModel.updateReport(reportId, data);
    setSelectedReport(null);
    await refreshDataOps();
  };

  const handleDeleteReport = async (reportId) => {
    await ModulesModel.deleteReport(reportId);
    setSelectedReport(null);
    await refreshDataOps();
  };

  const handleToggleFavorite = async (reportId, isFavorite) => {
    await ModulesModel.updateReport(reportId, { isFavorite });
    await refreshDataOps();
    if (selectedReport && selectedReport.reportId === reportId) {
      const updated = await ModulesModel.getReport(reportId);
      setSelectedReport(updated);
    }
  };

  const renderPage = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="text-4xl mb-4">🛰️</div>
            <div className="text-gray-600">Loading...</div>
          </div>
        </div>
      );
    }

    // Block access to pages the user's role doesn't allow
    if (!canAccessPage(user, currentPage)) {
      if (currentPage !== 'dashboard') setCurrentPage('dashboard');
      return null;
    }

    switch (currentPage) {
      case 'dashboard':
        return (
          <div>
            <div className="max-w-7xl mx-auto px-4 pt-4">
              <div className="flex gap-1 border-b border-gray-200">
                {[['daily', 'Daily Overview'], ['quarterly', 'Quarterly Stats']].map(([id, label]) => (
                  <button key={id} onClick={() => setDashboardTab(id)}
                    className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${dashboardTab === id ? 'text-blue-600 border-blue-600' : 'text-gray-500 border-transparent hover:text-gray-700'}`}>
                    {label}
                  </button>
                ))}
              </div>
            </div>
            {dashboardTab === 'daily' && dashboardData && <DashboardView data={dashboardData} burnCharts={burnCharts} enhancements={dashboardEnhancements} deployments={dashboardDeployments} onNavigate={setCurrentPage} onOpenDeployment={(id) => { setCurrentPage('deployments'); setDeepLinkDeploymentId(id); }} user={user} />}
            {dashboardTab === 'quarterly' && <QuarterlyStatsView user={user} />}
          </div>
        );
      case 'modules':
        return modules.length > 0 ? <ModulesView modules={modules} onNavigate={setCurrentPage} user={user} /> : null;
      case 'plantrack':
        if (showMilestoneEngine) {
          return <MilestoneEngineView
            onBack={() => setShowMilestoneEngine(false)}
            projects={planTrackData?.projects || []}
            user={user}
          />;
        }
        if (showSprintsView) {
          return <SprintsManagementView
            sprints={planTrackData?.sprints || []}
            onBack={() => setShowSprintsView(false)}
            onCreateSprint={handleCreateSprint}
            onUpdateSprint={handleUpdateSprint}
            onDeleteSprint={handleDeleteSprint}
            user={user}
          />;
        }
        if (selectedProjectId && projectDetail) {
          return <ProjectDetailView detail={projectDetail} onBack={handleBackToProjects} onRefresh={handleRefreshProjectDetail} user={user} teamMembers={allTeamMembers} onEdit={() => setShowEditProject(true)}
            onCreateMilestone={() => setShowAddMilestone(true)}
            onEditMilestone={handleEditMilestone}
            onCompleteMilestone={handleCompleteMilestone}
            onToggleArchive={handleToggleArchive}
            onDelete={handleDeleteProject}
          />;
        }
        return planTrackData ? <PlanTrackView data={planTrackData} onSelectProject={handleSelectProject} onShowAddProject={() => setShowAddProject(true)} onShowSprints={() => setShowSprintsView(true)} onShowMilestones={() => setShowMilestoneEngine(true)} onShowPostUpdateReport={() => setShowPostUpdateReport(true)} onToggleArchive={handleToggleArchive} /> : null;
      case 'devops':
        return devOpsData ? (
          <DevOpsModule
            data={devOpsData}
            viewMode={devOpsViewMode}
            group={devOpsGroup}
            onViewModeChange={async (mode) => {
              setDevOpsViewMode(mode);
              if (mode === 'kanban') {
                const devops = await ModulesModel.getDevOpsPipeline();
                const kanban = await ModulesModel.getDevOpsKanban();
                devops.kanbanPipeline = kanban?.pipeline || {};
                setDevOpsData(devops);
              }
            }}
            onGroupChange={setDevOpsGroup}
            onAddTask={() => setShowAddDevOpsTask(true)}
            onTaskClick={handleDevOpsTaskClick}
            onStatusChange={handleDevOpsStatusChange}
            onSendEod={() => { setEodModule('devops'); setShowSendEod(true); }}
            onEodSettings={() => setShowEodSettings(true)}
            userRole={user?.role}
            onDelete={handleDevOpsKanbanDelete}
          />
        ) : null;
      case 'dataops':
        return dataOpsData ? (
          <DataOpsModule
            data={dataOpsData}
            viewMode={dataOpsViewMode}
            onViewModeChange={handleDataOpsViewModeChange}
            onAddPipeline={() => setShowAddPipeline(true)}
            onAddReport={() => setShowAddReport(true)}
            onPipelineClick={handleSelectPipeline}
            onReportClick={handleSelectReport}
            onTriggerPipeline={handleTriggerPipeline}
            onToggleFavorite={handleToggleFavorite}
            onTaskClick={handleDataOpsTaskClick}
            onStatusChange={handleDataOpsStatusChange}
            onAddTask={() => setShowAddDataOpsTask(true)}
            onSendEod={() => { setEodModule('dataops'); setShowSendEod(true); }}
            onEodSettings={() => setShowEodSettings(true)}
            userRole={user?.role}
            kanban={dataOpsKanban}
            onKanbanStatusChange={handleDataOpsKanbanStatusChange}
            onKanbanDelete={handleDataOpsKanbanDelete}
            onRefresh={refreshDataOpsKanban}
          />
        ) : null;
      case 'learntrack':
        return learnTrackData ? (
          <LearnTrackModule
            data={learnTrackData}
            users={allUsers}
            user={user}
            viewMode={learnTrackViewMode}
            onViewModeChange={setLearnTrackViewMode}
            onAddCourse={() => setShowAddCourse(true)}
            onCourseClick={handleCourseClick}
            onEnroll={handleEnrollUser}
            onUpdateProgress={handleUpdateEnrollment}
            filterSkill={learnFilterSkill}
            filterGroup={learnFilterGroup}
            onFilterSkill={setLearnFilterSkill}
            onFilterGroup={setLearnFilterGroup}
          />
        ) : null;
      case 'timeboard':
        return timeBoardData ? (
          <TimeBoardModule
            data={timeBoardData}
            weekStart={currentWeekStart}
            user={user}
            onWeekChange={handleWeekChange}
            onSubmitTimesheet={handleSubmitTimesheet}
            onApproveTimesheet={handleApproveTimesheet}
          />
        ) : null;
      case 'leaveboard':
        return leaveBoardData ? (
          <LeaveBoardModule
            data={leaveBoardData}
            user={user}
            onRequestLeave={() => setShowRequestLeave(true)}
            onApproveRequest={(id, notes) => handleUpdateLeaveRequest(id, { status: 'approved', notes: notes || '' })}
            onRejectRequest={(id, notes) => handleUpdateLeaveRequest(id, { status: 'rejected', notes: notes || '' })}
            onDeleteRequest={handleDeleteLeaveRequest}
            onRefresh={refreshLeaveBoardData}
          />
        ) : null;
      case 'meetingboard':
        return (
          <MeetingBoardModule
            data={meetingBoardData}
            user={user}
            onSchedule={() => setShowScheduleMeeting(true)}
            onStart={handleStartMeeting}
            onEnd={handleEndMeeting}
            onCancel={handleCancelMeeting}
            onDelete={handleDeleteMeeting}
          />
        );
      case 'knowledgeboard':
        return (
          <KnowledgeBoardModule
            data={knowledgeData}
            user={user}
            search={knowledgeSearch}
            selectedCategory={knowledgeSelectedCategory}
            viewingArticle={knowledgeViewingArticle}
            showEditor={knowledgeShowEditor}
            editingArticle={knowledgeEditingArticle}
            showVersions={knowledgeShowVersions}
            articleVersions={knowledgeArticleVersions}
            showCategoryModal={knowledgeShowCategoryModal}
            onSearch={handleKnowledgeSearch}
            onSelectCategory={handleKnowledgeCategorySelect}
            onViewArticle={handleViewKnowledgeArticle}
            onCreateArticle={handleKnowledgeCreateArticle}
            onEditArticle={handleKnowledgeEditArticle}
            onSaveArticle={handleKnowledgeSaveArticle}
            onPublishArticle={handleKnowledgePublishArticle}
            onDeleteArticle={handleKnowledgeDeleteArticle}
            onCloseEditor={() => { setKnowledgeShowEditor(false); setKnowledgeEditingArticle(null); }}
            onViewVersions={handleViewArticleVersions}
            onRestoreVersion={handleRestoreVersion}
            onCloseVersions={() => setKnowledgeShowVersions(false)}
            onShowCategoryModal={() => setKnowledgeShowCategoryModal(true)}
            onCreateCategory={handleCreateKnowledgeCategory}
            onCloseCategoryModal={() => setKnowledgeShowCategoryModal(false)}
            onBackToList={() => setKnowledgeViewingArticle(null)}
          />
        );
      case 'assetboard':
        return <AssetBoardModule user={user} />;
      case 'requestboard':
        return <RequestBoardModule user={user} />;
      case 'supportboard':
        return <SupportBoardModule user={user} />;
      case 'tcms':
        return <TCMSModule user={user} />;
      case 'manager-console':
        return <ManagerConsoleModule user={user} />;
      case 'techdebt':
        return <TechDebtModule user={user} />;
      case 'product':
        return <ProductModule user={user} />;
      case 'polaris':
        return polarisData ? (
          <PolarisModule
            data={polarisData}
            user={user}
            showArchive={polarisShowArchive}
            onToggleShowArchive={() => setPolarisShowArchive(v => !v)}
            onRefresh={async () => {
              const fresh = await ModulesModel.getPolarisData();
              setPolarisData(fresh);
            }}
          />
        ) : null;
      case 'changerequest': {
        const refreshList = async () => {
          const fresh = await ModulesModel.getChangeRequests();
          setCrData(fresh);
        };
        const refreshDetail = async () => {
          if (!crDetail) return;
          const fresh = await ModulesModel.getChangeRequest(crDetail.id);
          if (fresh) setCrDetail(fresh);
          else setCrDetail(null);
          await refreshList();
        };
        const settingsModal = crShowSettings ? (
          <ChangeRequestSettingsModal
            initial={crSettings}
            onClose={() => setCrShowSettings(false)}
            onSave={async (data) => {
              const r = await ModulesModel.saveChangeRequestSettings(data);
              if (r?.success !== false) {
                const fresh = await ModulesModel.getChangeRequestSettings();
                if (fresh) setCrSettings(fresh);
              }
              return r;
            }}
          />
        ) : null;
        if (crDetail) {
          return (
            <>
              <ChangeRequestDetailView
                detail={crDetail}
                user={user}
                onBack={() => setCrDetail(null)}
                onRefresh={refreshDetail}
                onEdit={() => { setCrEditing(crDetail); setCrShowForm(true); }}
                onDelete={async () => {
                  if (!window.confirm('Permanently delete this change request? This cannot be undone.')) return;
                  const r = await ModulesModel.deleteChangeRequest(crDetail.id);
                  if (r?.success === false) { alert(r.error || 'Failed'); return; }
                  setCrDetail(null);
                  await refreshList();
                }}
              />
              {crShowForm && (
                <ChangeRequestFormModal
                  initial={crEditing}
                  users={allTeamMembers || []}
                  settings={crSettings}
                  onClose={() => { setCrShowForm(false); setCrEditing(null); }}
                  onSave={async (payload) => {
                    const r = crEditing
                      ? await ModulesModel.updateChangeRequest(crEditing.id, payload)
                      : await ModulesModel.createChangeRequest(payload);
                    if (r?.success !== false) await refreshDetail();
                    return r;
                  }}
                />
              )}
              {settingsModal}
            </>
          );
        }
        return crData ? (
          <>
            <ChangeRequestModule
              data={crData}
              user={user}
              users={allTeamMembers || []}
              settings={crSettings}
              onRefresh={refreshList}
              onOpenDetail={async (cr) => {
                const detail = await ModulesModel.getChangeRequest(cr.id);
                if (detail) setCrDetail(detail);
              }}
              onShowForm={() => { setCrEditing(null); setCrShowForm(true); }}
              onShowSettings={() => setCrShowSettings(true)}
            />
            {crShowForm && (
              <ChangeRequestFormModal
                initial={crEditing}
                users={allTeamMembers || []}
                settings={crSettings}
                onClose={() => { setCrShowForm(false); setCrEditing(null); }}
                onSave={async (payload) => {
                  const r = crEditing
                    ? await ModulesModel.updateChangeRequest(crEditing.id, payload)
                    : await ModulesModel.createChangeRequest(payload);
                  if (r?.success !== false) await refreshList();
                  return r;
                }}
              />
            )}
            {settingsModal}
          </>
        ) : null;
      }
      case 'automationboard':
        return <AutomationBoardModule user={user} />;
      case 'incidentboard':
        return (
          <IncidentBoardModule
            data={incidentData}
            user={user}
            filters={incidentFilters}
            viewingIncident={viewingIncident}
            showCreate={showCreateIncident}
            analytics={incidentAnalytics}
            onFilterChange={handleIncidentFilterChange}
            onViewIncident={handleViewIncident}
            onCreateIncident={handleCreateIncident}
            onUpdateIncident={handleUpdateIncident}
            onAddUpdate={handleAddIncidentUpdate}
            onDeleteIncident={handleDeleteIncident}
            onBack={() => setViewingIncident(null)}
            onShowCreate={() => setShowCreateIncident(true)}
            onCloseCreate={() => setShowCreateIncident(false)}
            onLoadAnalytics={handleLoadIncidentAnalytics}
          />
        );
      case 'quality':
        return qualityData ? (
          <QmsModule
            data={qualityData}
            stage={qmsStage}
            onStageChange={handleQmsStageChange}
            onApproveReq={handleApproveRequirement}
            onRejectReq={handleRejectRequirement}
            onAddReq={(defaults = {}) => { setAddReqDefaults(defaults); setShowAddRequirement(true); }}
            onEditReq={handleEditRequirement}
            onDeleteReq={handleDeleteRequirement}
            onRefreshReq={refreshQualityData}
            onUpdateProject={handleUpdateQaProject}
            onAddTestCase={() => setShowAddTestCase(true)}
            onEditTestCase={handleEditTestCase}
            onDeleteTestCase={handleDeleteTestCase}
            onPassTC={handlePassTestCase}
            onFailTC={handleFailTestCase}
            onResetTC={handleResetTestCase}
            onReportBug={handleReportBug}
            onEditBug={handleEditBug}
            onProjectChange={handleQmsProjectChange}
            onSendEod={() => setShowQaEod(true)}
            onEodSettings={() => setShowEodSettings(true)}
            userRole={user?.role || user?.user_role}
            user={user}
            qmsView={qmsView}
            onQmsViewChange={(v) => { setQmsView(v); setViewingRequirementDetail(null); if (v === 'bugs') { setViewingBugDetail(null); refreshAllBugs(); } }}
            allBugsData={allBugsData}
            allBugsFilters={allBugsFilters}
            viewingBugDetail={viewingBugDetail}
            onAllBugsFilterChange={handleAllBugsFilterChange}
            onViewBugDetail={handleViewBugDetail}
            onBugDetailUpdate={handleBugDetailUpdate}
            onBugDetailComment={handleBugDetailComment}
            onDeleteBugComment={handleDeleteBugComment}
            onBackFromBugDetail={() => setViewingBugDetail(null)}
            onReportBugFromList={() => { setShowReportBug(true); }}
            testPlans={testPlans}
            onCreateTestPlan={handleCreateTestPlan}
            onUpdateTestPlan={handleUpdateTestPlan}
            onDeleteTestPlan={handleDeleteTestPlan}
            onRecordExecution={handleRecordExecution}
            uatSignoffs={uatSignoffs}
            onSubmitUatSignoff={handleSubmitUatSignoff}
            prodVerifications={prodVerifications}
            onToggleProdVerification={handleToggleProdVerification}
            onCompleteProdVerification={handleCompleteProdVerification}
            viewingRequirementDetail={viewingRequirementDetail}
            onViewRequirementDetail={(id) => setViewingRequirementDetail(id)}
            onBackFromReqDetail={() => setViewingRequirementDetail(null)}
            onQaStatusChange={handleQaStatusChange}
            onTestResultChange={handleTestResultChange}
            onNavigateToPlanTrack={() => setCurrentPage('plantrack')}
            teamMembers={allTeamMembers}
            onPromoteArtifacts={handlePromoteArtifacts}
          />
        ) : null;
      case 'quicktrack':
        return quickTrackData ? (
          <QuickTrackModule
            data={quickTrackData}
            filterStatus={qtFilterStatus}
            filterPriority={qtFilterPriority}
            filterCategory={qtFilterCategory}
            filterDateFrom={qtFilterDateFrom}
            filterDateTo={qtFilterDateTo}
            onFilterStatus={setQtFilterStatus}
            onFilterPriority={setQtFilterPriority}
            onFilterCategory={setQtFilterCategory}
            onFilterDateFrom={setQtFilterDateFrom}
            onFilterDateTo={setQtFilterDateTo}
            onResetFilters={() => { setQtFilterStatus(''); setQtFilterPriority(''); setQtFilterCategory(''); setQtFilterDateFrom(''); setQtFilterDateTo(''); }}
            onAddTask={() => setShowAddQuickTask(true)}
            onBackfillTask={() => setShowBackfillQuickTask(true)}
            onStatusChange={handleUpdateQuickTask}
            onDelete={handleDeleteQuickTask}
            onEdit={handleEditQuickTask}
            projects={allProjects}
            user={user}
            teamMembers={allTeamMembers}
          />
        ) : null;
      case 'vendorevents':
        return vendorEventsData ? (
          <VendorEventsModule
            data={vendorEventsData}
            filterVendor={veFilterVendor}
            filterWorkflow={veFilterWorkflow}
            filterStatus={veFilterStatus}
            onFilterVendor={handleVeFilterVendor}
            onFilterWorkflow={handleVeFilterWorkflow}
            onFilterStatus={handleVeFilterStatus}
            onResetFilters={handleVeResetFilters}
          />
        ) : null;
      case 'tasks':
        return <TaskManagementView
          data={taskManageData}
          filters={taskManageFilters}
          onFilterChange={handleTaskManageFilter}
          onBulkUpdate={handleTaskManageBulk}
          onRefresh={handleTaskManageRefresh}
        />;
      case 'reports':
        return <ReportsView data={reportsData} />;
      case 'audit':
        return <AuditTrailView
          logs={auditLogs}
          stats={auditStats}
          filters={auditFilters}
          onFilterChange={handleAuditFilterChange}
          onLoadMore={handleAuditLoadMore}
          hasMore={auditHasMore}
          loading={auditLoading}
        />;
      case 'leaderboard':
        return <LeaderBoardModule data={leaderboardData || { devops: [], dataops: [] }} period={leaderboardPeriod} onPeriodChange={setLeaderboardPeriod} rangeStart={leaderboardRangeStart} rangeEnd={leaderboardRangeEnd} onRangeChange={(s, e) => { setLeaderboardRangeStart(s); setLeaderboardRangeEnd(e); }} />;
      case 'orgchart':
        return <OrgChartView />;
      case 'projectdashboard':
        return <ProjectDashboardView />;
      case 'marketingworkflow':
        return <MarketingWorkflowModule />;
      case 'troubleshootinglogs':
        return <TroubleshootingLogsModule user={user} />;
      case 'deployments':
        return <DeploymentsModule user={user} deepLinkDeploymentId={deepLinkDeploymentId} onDeepLinkConsumed={() => setDeepLinkDeploymentId(null)} />;
      case 'workflows':
        return <WorkflowsModule user={user} />;
      case 'apm':
        return <ApmModule user={user} />;
      case 'datafeed':
        return <DataFeedModule user={user} />;
      case 'fastdtr':
        return <FastDtrView />;
      case 'messages':
        return <MessagingModule user={user} onUnreadChange={setUnreadMessages} />;
      case 'settings':
        return <SettingsView onClearData={handleClearData} user={user} authToken={authToken} />;
      default:
        // dashboardData starts null until the first load resolves — unlike the
        // primary 'dashboard' case (which waits for it), this fallback must guard
        // too or DashboardView crashes reading data.stats/data.activities on null.
        return dashboardData ? <DashboardView data={dashboardData} burnCharts={burnCharts} enhancements={dashboardEnhancements} deployments={dashboardDeployments} onNavigate={setCurrentPage} onOpenDeployment={(id) => { setCurrentPage('deployments'); setDeepLinkDeploymentId(id); }} user={user} /> : null;
    }
  };

  // Auth guard: show loading spinner, login page, or main app
  if (!authChecked) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-600 to-indigo-800 flex items-center justify-center">
        <div className="text-center text-white">
          <div className="text-5xl mb-4">🛰️</div>
          <div className="w-8 h-8 border-4 border-white border-t-transparent rounded-full animate-spin mx-auto"></div>
        </div>
      </div>
    );
  }

  if (resetToken) {
    return <ResetPasswordView token={resetToken} onDone={() => setResetToken(null)} />;
  }

  // Checked before needsSetup on purpose: when the database is unreachable we must not
  // fall through to the first-run wizard and imply the organization is gone.
  if (dbUnavailable) {
    const isQuota = /row read limit|exceeded .*limit|free tier/i.test(dbUnavailable);
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-lg w-full bg-white rounded-lg shadow border border-gray-200 p-6">
          <h1 className="text-xl font-bold text-gray-900 mb-1">Orbit is temporarily unavailable</h1>
          <p className="text-sm text-gray-600 mb-4">
            The application is running, but it can't reach the database right now.
            <strong> Your data and accounts are safe</strong> — nothing has been deleted and no setup is required.
          </p>
          {isQuota && (
            <div className="bg-amber-50 border border-amber-300 rounded p-3 text-sm text-amber-900 mb-4">
              The database has hit its daily usage limit. Access returns automatically at
              <strong> midnight UTC</strong>, or immediately if the Cloudflare plan is upgraded.
            </div>
          )}
          <details className="text-xs text-gray-500 mb-4">
            <summary className="cursor-pointer">Technical detail</summary>
            <code className="block mt-1 bg-gray-50 border border-gray-200 rounded p-2 break-words">{dbUnavailable}</code>
          </details>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700"
          >Retry</button>
        </div>
      </div>
    );
  }

  if (needsSetup) {
    return <SetupView onSetupComplete={(token, userData) => { setNeedsSetup(false); handleLogin(token, userData); }} />;
  }

  if (!user) {
    return <LoginView onLogin={handleLogin} />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <ConfirmDialog />
      <Header
        user={user} onMenuClick={() => setMenuOpen(true)} onLogout={handleLogout} onChangePassword={() => setShowChangePassword(true)}
        notifications={notifications} unreadCount={unreadCount} onMarkRead={handleMarkNotificationRead} onMarkAllRead={handleMarkAllRead} onDismiss={handleDismissNotification} onNavigate={handleNotificationNavigate}
        unreadMessages={unreadMessages} msgConversations={msgConversations}
        onOpenMessage={(uid, uname) => { setMsgOpenUserId(uid); setMsgOpenUserName(uname); setShowMessaging(true); }}
        onToggleMessages={() => { setMsgOpenUserId(null); setMsgOpenUserName(''); setShowMessaging(s => !s); }}
        onGoTo={(page, channelId, channelData) => {
          if (channelId) {
            if (_openChatChannelFn) {
              // MessagingModule already mounted — open channel directly
              _openChatChannelFn(channelId);
              setCurrentPage(page);
            } else {
              // Will mount fresh — store for pickup on mount
              _pendingChat = { id: channelId, channel: channelData };
              setCurrentPage(page);
            }
          } else {
            setCurrentPage(page);
          }
        }}
      />
      {showMessaging && (
        <MessagingPanel
          user={user}
          onClose={() => { setShowMessaging(false); setMsgOpenUserId(null); setMsgOpenUserName(''); fetchUnreadMessages(); }}
          onUnreadChange={setUnreadMessages}
          initialUserId={msgOpenUserId}
          initialUserName={msgOpenUserName}
        />
      )}
      <Navigation
        currentPage={currentPage}
        onNavigate={(page) => { setCurrentPage(page); setSelectedProjectId(null); setProjectDetail(null); setShowSprintsView(false); setShowMilestoneEngine(false); }}
        isOpen={menuOpen}
        onClose={() => setMenuOpen(false)}
        user={user}
      />
      <main>
        {renderPage()}
      </main>
      {showAddProject && <AddProjectModal onClose={() => setShowAddProject(false)} onSubmit={handleCreateProject} />}
      {showPostUpdateReport && <PostUpdateReportModal onClose={() => setShowPostUpdateReport(false)} />}
      {showSendEod && <SendEodModal module={eodModule} onClose={() => setShowSendEod(false)} />}
      {showQaEod && <SendQaEodModal onClose={() => setShowQaEod(false)} />}
      {showEodSettings && <EodSettingsModal onClose={() => setShowEodSettings(false)} />}
      {showAddSprint && <AddSprintModal onClose={() => setShowAddSprint(false)} onSubmit={handleCreateSprint} />}
      {showEditProject && projectDetail && <EditProjectModal
        project={projectDetail.project}
        sprints={projectDetail.allSprints || planTrackData?.sprints || []}
        onClose={() => setShowEditProject(false)}
        onSubmit={handleUpdateProject}
      />}
      {showAddDevOpsTask && <AddDevOpsTaskModal onClose={() => setShowAddDevOpsTask(false)} onSubmit={handleCreateDevOpsTask} teamMembers={allTeamMembers.filter(m => m.team_module === 'devops')} user={user} allUsers={allUsers} projects={allProjects} />}
      {showAddDataOpsTask && <AddDevOpsTaskModal onClose={() => setShowAddDataOpsTask(false)} onSubmit={handleCreateDataOpsTask} teamMembers={allTeamMembers.filter(m => m.team_module === 'dataops')} user={user} allUsers={allUsers} projects={allProjects} />}
      {selectedDevOpsTask && <TaskDetailPanel key={selectedDevOpsTask.id} task={selectedDevOpsTask} onClose={() => setSelectedDevOpsTask(null)} onUpdate={handleDevOpsTaskUpdate} onDelete={handleDeleteDevOpsTask} user={user} teamMembers={allTeamMembers} allUsers={allUsers} projects={allProjects} />}
      {selectedBug && <BugDetailPanel bug={selectedBug} onClose={() => setSelectedBug(null)} user={user} teamMembers={allTeamMembers} onUpdate={handleDevOpsBugReassign} />}
      {showTimeEntry && <TimeEntryModal date={showTimeEntry.date} category={showTimeEntry.category} onClose={() => setShowTimeEntry(null)} onSubmit={handleCreateTimeEntry} />}
      {showScheduleMeeting && <ScheduleMeetingModal users={allUsers || []} onClose={() => setShowScheduleMeeting(false)} onSubmit={handleCreateMeeting} />}
      {showRequestLeave && <RequestLeaveModal balance={leaveBoardData?.myBalance || {}} schedule={leaveBoardData?.mySchedule} onClose={() => setShowRequestLeave(false)} onSubmit={handleCreateLeaveRequest} />}
      {showAddPipeline && <AddPipelineModal onClose={() => setShowAddPipeline(false)} onSubmit={handleCreatePipeline} />}
      {showAddReport && <AddReportModal onClose={() => setShowAddReport(false)} onSubmit={handleCreateReport} />}
      {selectedPipeline && <PipelineDetailModal pipeline={selectedPipeline} onClose={() => setSelectedPipeline(null)} onUpdate={handleUpdatePipeline} onDelete={handleDeletePipeline} onTrigger={handleTriggerPipeline} />}
      {selectedReport && <ReportDetailModal report={selectedReport} onClose={() => setSelectedReport(null)} onUpdate={handleUpdateReport} onDelete={handleDeleteReport} onToggleFavorite={handleToggleFavorite} />}
      {showAddCourse && <AddCourseModal onClose={() => setShowAddCourse(false)} onSubmit={handleCreateCourse} skills={learnTrackData?.skills} />}
      {selectedCourse && <CourseDetailModal course={selectedCourse} users={allUsers} user={user} onClose={() => setSelectedCourse(null)} onUpdate={handleUpdateCourse} onDelete={handleDeleteCourse} onEnroll={handleEnrollUser} onUpdateEnrollment={handleUpdateEnrollment} onDeleteEnrollment={handleDeleteEnrollment} />}
      {showAddQuickTask && <AddQuickTaskModal onClose={() => setShowAddQuickTask(false)} onSubmit={handleCreateQuickTask} projects={allProjects} teamMembers={allTeamMembers} user={user} />}
      {showBackfillQuickTask && <AddQuickTaskModal backfillMode onClose={() => setShowBackfillQuickTask(false)} onSubmit={handleCreateQuickTask} projects={allProjects} teamMembers={allTeamMembers} user={user} />}
      {editingQuickTask && <EditQuickTaskModal key={editingQuickTask.id} task={editingQuickTask} onClose={() => setEditingQuickTask(null)} onSubmit={handleSaveQuickTask} projects={allProjects} user={user} teamMembers={allTeamMembers} />}
      {showAddRequirement && <AddRequirementModal onClose={() => { setShowAddRequirement(false); setAddReqDefaults({}); }} onSubmit={handleCreateRequirement} projectId={qualityData?.project?.projectId} existingCategories={[...new Set((qualityData?.requirements || []).filter(r => !r.parentRequirementId).map(r => r.parentCategory).filter(Boolean))]} requirements={qualityData?.requirements || []} defaultValues={addReqDefaults} />}
      {showAddTestCase && <AddTestCaseModal onClose={() => setShowAddTestCase(false)} onSubmit={handleCreateTestCase} projectId={qualityData?.project?.projectId} requirements={qualityData?.requirements || []} />}
      {showReportBug && <ReportBugModal onClose={() => { setShowReportBug(false); setReportBugPrefill(null); }} onSubmit={handleCreateBug} projectId={qualityData?.project?.projectId} testCases={qualityData?.testCases} prefillTc={reportBugPrefill} />}
      {editingBug && <EditBugModal bug={editingBug} onClose={() => setEditingBug(null)} onSave={handleUpdateBug} onDelete={handleDeleteBug} user={user} allUsers={allUsers} teamMembers={allTeamMembers} allProjects={allProjects} />}
      {showChangePassword && <ChangePasswordModal authToken={authToken} onClose={() => setShowChangePassword(false)} />}
      {showAddMilestone && <AddMilestoneModal
        onClose={() => setShowAddMilestone(false)}
        onSubmit={handleCreateMilestone}
        projectId={selectedProjectId}
        projects={planTrackData?.projects || []}
        user={user}
      />}
      {editingMilestoneData && <EditMilestoneModal
        milestone={editingMilestoneData}
        allMilestones={planTrackData?.milestones || []}
        onClose={() => { setEditingMilestoneId(null); setEditingMilestoneData(null); }}
        onSubmit={(data) => handleUpdateMilestone(editingMilestoneId, data)}
        onDelete={handleDeleteMilestone}
      />}
      <footer className="text-center py-8 text-gray-600 text-sm">
        ORBIT System v1.0 | React + Cloudflare Pages/Workers MVC Architecture
      </footer>
    </div>
  );
};

export default App;